import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import Highcharts from '../../highcharts';
import XRangeModule from 'highcharts/modules/xrange';
import Header from '../../components/Header';
import AccessibleChart from '../../components/AccessibleChart';
import ResponsiveTableRegion from '../../components/ResponsiveTableRegion';
import Filters from '../../components/Filters';
import { ventrisConfig } from '../../ventris.config';

import ReportPageIcon from '../../icons/ReportPageIcon';
import DownloadIcon from '../../icons/DownloadIcon';
import FilterIcon from '../../icons/FilterIcon';
import { getUseCaseTerminology } from '../../useCase';

XRangeModule(Highcharts);

type ClinicianReportProps = {
};

export const SCREENING_RISK_CUTPOINTS: Record<string, {
  lowerBound: number;
  highRiskCutPoint: number;
  moderateRiskCutPoint: number;
  upperBound: number;
}> = {
  'K|Spring': { lowerBound: 288, highRiskCutPoint: 380, moderateRiskCutPoint: 422, upperBound: 581 },
  '1st|Fall': { lowerBound: 319, highRiskCutPoint: 425, moderateRiskCutPoint: 465, upperBound: 625 },
  '1st|Winter': { lowerBound: 363, highRiskCutPoint: 486, moderateRiskCutPoint: 515, upperBound: 646 },
  '1st|Spring': { lowerBound: 405, highRiskCutPoint: 486, moderateRiskCutPoint: 525, upperBound: 672 },
  '2nd|Fall': { lowerBound: 445, highRiskCutPoint: 536, moderateRiskCutPoint: 562, upperBound: 699 },
  '2nd|Winter': { lowerBound: 457, highRiskCutPoint: 528, moderateRiskCutPoint: 561, upperBound: 753 },
  '2nd|Spring': { lowerBound: 479, highRiskCutPoint: 554, moderateRiskCutPoint: 576, upperBound: 794 },
  '3rd|Fall': { lowerBound: 510, highRiskCutPoint: 582, moderateRiskCutPoint: 594, upperBound: 833 },
  '3rd|Winter': { lowerBound: 558, highRiskCutPoint: 628, moderateRiskCutPoint: 640, upperBound: 851 },
  '3rd|Spring': { lowerBound: 582, highRiskCutPoint: 663, moderateRiskCutPoint: 683, upperBound: 892 },
};

export const SCREENING_RISK_CONTENT: Record<string, {
  title: string;
  description: string;
  meaning: string;
  graphColor: string;
  panelColor: string;
  textColor: string;
}> = {
  High: {
    title: 'High Risk',
    description: 'Word reading skills are far below the level expected for your student’s grade at this time of year.',
    meaning: 'This student is at high risk of having word reading delays at the end of the year. This student needs additional instructional support in foundational reading skills.',
    graphColor: '#2a68ac',
    panelColor: '#2a68ac',
    textColor: '#ffffff',
  },
  Moderate: {
    title: 'Moderate Risk',
    description: 'Word reading skills are below the level expected for your student’s grade at this time of year.',
    meaning: 'This student is at moderate risk of having word reading delays at the end of the year. This student may need additional instructional support in foundational reading skills.',
    graphColor: '#438ad8',
    panelColor: '#438ad8',
    textColor: '#ffffff',
  },
  Low: {
    title: 'Low Risk',
    description: 'Word reading skills are at or above the level expected for your student’s grade at this time of year.',
    meaning: 'Student’s word reading skills are developing as expected for their grade.',
    graphColor: '#a7c8ee',
    panelColor: '#a7c8ee',
    textColor: '#17324d',
  },
};

export const nre: any = [
  {
    "ID": 1,
    "KG Winter_theta_nre": -3.164841012,
    "KG Winter_pr_1_99_nre": 1,
    "KG Winter_ss_nre": 60,
    "KG Winter_dss_nre": 184,
    "KG Spring_theta_nre": -3.235425023,
    "KG Spring_pr_1_99_nre": 1,
    "KG Spring_ss_nre": 55,
    "KG Spring_dss_nre": 176,
    "G1 Fall_theta_nre": -3.25916583,
    "G1 Fall_pr_1_99_nre": 1,
    "G1 Fall_ss_nre": 51,
    "G1 Fall_dss_nre": 174,
    "G1 Winter_theta_nre": -2.718098432,
    "G1 Winter_pr_1_99_nre": 1,
    "G1 Winter_ss_nre": 53,
    "G1 Winter_dss_nre": 228,
    "G1 Spring_theta_nre": -2.147460699,
    "G1 Spring_pr_1_99_nre": 1,
    "G1 Spring_ss_nre": 57,
    "G1 Spring_dss_nre": 285,
    "G2 Fall_theta_nre": -3.183012684,
    "G2 Fall_pr_1_99_nre": 1,
    "G2 Fall_ss_nre": 38,
    "G2 Fall_dss_nre": 182,
    "G2 Winter_theta_nre": -1.787453279,
    "G2 Winter_pr_1_99_nre": 1,
    "G2 Winter_ss_nre": 52,
    "G2 Winter_dss_nre": 321,
    "G2 Spring_theta_nre": -1.520844425,
    "G2 Spring_pr_1_99_nre": 1,
    "G2 Spring_ss_nre": 58,
    "G2 Spring_dss_nre": 348,
    "G3 Fall_theta_nre": -1.328206745,
    "G3 Fall_pr_1_99_nre": 1,
    "G3 Fall_ss_nre": 59,
    "G3 Fall_dss_nre": 367,
    "G3 Winter_theta_nre": -0.874290767,
    "G3 Winter_pr_1_99_nre": 1,
    "G3 Winter_ss_nre": 53,
    "G3 Winter_dss_nre": 413,
    "G3 Spring_theta_nre": -0.144430806,
    "G3 Spring_pr_1_99_nre": 1,
    "G3 Spring_ss_nre": 60,
    "G3 Spring_dss_nre": 486
  },
  {
    "ID": 2,
    "KG Winter_theta_nre": -3.053153083,
    "KG Winter_pr_1_99_nre": 1,
    "KG Winter_ss_nre": 62,
    "KG Winter_dss_nre": 195,
    "KG Spring_theta_nre": -3.159761124,
    "KG Spring_pr_1_99_nre": 1,
    "KG Spring_ss_nre": 57,
    "KG Spring_dss_nre": 184,
    "G1 Fall_theta_nre": -2.657449936,
    "G1 Fall_pr_1_99_nre": 1,
    "G1 Fall_ss_nre": 61,
    "G1 Fall_dss_nre": 234,
    "G1 Winter_theta_nre": -2.452549108,
    "G1 Winter_pr_1_99_nre": 1,
    "G1 Winter_ss_nre": 58,
    "G1 Winter_dss_nre": 255,
    "G1 Spring_theta_nre": -1.991887196,
    "G1 Spring_pr_1_99_nre": 1,
    "G1 Spring_ss_nre": 59,
    "G1 Spring_dss_nre": 301,
    "G2 Fall_theta_nre": -2.136635979,
    "G2 Fall_pr_1_99_nre": 1,
    "G2 Fall_ss_nre": 56,
    "G2 Fall_dss_nre": 286,
    "G2 Winter_theta_nre": -1.405493116,
    "G2 Winter_pr_1_99_nre": 1,
    "G2 Winter_ss_nre": 59,
    "G2 Winter_dss_nre": 359,
    "G2 Spring_theta_nre": -1.116969709,
    "G2 Spring_pr_1_99_nre": 1,
    "G2 Spring_ss_nre": 65,
    "G2 Spring_dss_nre": 388,
    "G3 Fall_theta_nre": -1.281181116,
    "G3 Fall_pr_1_99_nre": 1,
    "G3 Fall_ss_nre": 60,
    "G3 Fall_dss_nre": 372,
    "G3 Winter_theta_nre": -0.383542279,
    "G3 Winter_pr_1_99_nre": 1,
    "G3 Winter_ss_nre": 63,
    "G3 Winter_dss_nre": 462,
    "G3 Spring_theta_nre": -0.12347679,
    "G3 Spring_pr_1_99_nre": 1,
    "G3 Spring_ss_nre": 60,
    "G3 Spring_dss_nre": 488
  },
  {
    "ID": 3,
    "KG Winter_theta_nre": -3.031809869,
    "KG Winter_pr_1_99_nre": 1,
    "KG Winter_ss_nre": 63,
    "KG Winter_dss_nre": 197,
    "KG Spring_theta_nre": -2.872843809,
    "KG Spring_pr_1_99_nre": 1,
    "KG Spring_ss_nre": 62,
    "KG Spring_dss_nre": 213,
    "G1 Fall_theta_nre": -2.498452169,
    "G1 Fall_pr_1_99_nre": 1,
    "G1 Fall_ss_nre": 64,
    "G1 Fall_dss_nre": 250,
    "G1 Winter_theta_nre": -2.38169472,
    "G1 Winter_pr_1_99_nre": 1,
    "G1 Winter_ss_nre": 59,
    "G1 Winter_dss_nre": 262,
    "G1 Spring_theta_nre": -1.878805601,
    "G1 Spring_pr_1_99_nre": 1,
    "G1 Spring_ss_nre": 62,
    "G1 Spring_dss_nre": 312,
    "G2 Fall_theta_nre": -1.688490447,
    "G2 Fall_pr_1_99_nre": 1,
    "G2 Fall_ss_nre": 64,
    "G2 Fall_dss_nre": 331,
    "G2 Winter_theta_nre": -1.161253267,
    "G2 Winter_pr_1_99_nre": 1,
    "G2 Winter_ss_nre": 64,
    "G2 Winter_dss_nre": 384,
    "G2 Spring_theta_nre": -1.063686817,
    "G2 Spring_pr_1_99_nre": 1,
    "G2 Spring_ss_nre": 66,
    "G2 Spring_dss_nre": 394,
    "G3 Fall_theta_nre": -1.248761394,
    "G3 Fall_pr_1_99_nre": 1,
    "G3 Fall_ss_nre": 60,
    "G3 Fall_dss_nre": 375,
    "G3 Winter_theta_nre": -0.378461953,
    "G3 Winter_pr_1_99_nre": 1,
    "G3 Winter_ss_nre": 63,
    "G3 Winter_dss_nre": 462,
    "G3 Spring_theta_nre": -0.005057544,
    "G3 Spring_pr_1_99_nre": 1,
    "G3 Spring_ss_nre": 63,
    "G3 Spring_dss_nre": 499
  },
  {
    "ID": 4,
    "KG Winter_theta_nre": -3.025147002,
    "KG Winter_pr_1_99_nre": 1,
    "KG Winter_ss_nre": 63,
    "KG Winter_dss_nre": 197,
    "KG Spring_theta_nre": -2.772263862,
    "KG Spring_pr_1_99_nre": 1,
    "KG Spring_ss_nre": 63,
    "KG Spring_dss_nre": 223,
    "G1 Fall_theta_nre": -2.377235847,
    "G1 Fall_pr_1_99_nre": 1,
    "G1 Fall_ss_nre": 66,
    "G1 Fall_dss_nre": 262,
    "G1 Winter_theta_nre": -2.381491779,
    "G1 Winter_pr_1_99_nre": 1,
    "G1 Winter_ss_nre": 59,
    "G1 Winter_dss_nre": 262,
    "G1 Spring_theta_nre": -1.549158377,
    "G1 Spring_pr_1_99_nre": 1,
    "G1 Spring_ss_nre": 68,
    "G1 Spring_dss_nre": 345,
    "G2 Fall_theta_nre": -1.543244116,
    "G2 Fall_pr_1_99_nre": 1,
    "G2 Fall_ss_nre": 67,
    "G2 Fall_dss_nre": 346,
    "G2 Winter_theta_nre": -1.127956299,
    "G2 Winter_pr_1_99_nre": 1,
    "G2 Winter_ss_nre": 65,
    "G2 Winter_dss_nre": 387,
    "G2 Spring_theta_nre": -1.011137297,
    "G2 Spring_pr_1_99_nre": 1,
    "G2 Spring_ss_nre": 67,
    "G2 Spring_dss_nre": 399,
    "G3 Fall_theta_nre": -1.165677854,
    "G3 Fall_pr_1_99_nre": 1,
    "G3 Fall_ss_nre": 61,
    "G3 Fall_dss_nre": 383,
    "G3 Winter_theta_nre": -0.361672659,
    "G3 Winter_pr_1_99_nre": 1,
    "G3 Winter_ss_nre": 64,
    "G3 Winter_dss_nre": 464,
    "G3 Spring_theta_nre": 0.1085683,
    "G3 Spring_pr_1_99_nre": 1,
    "G3 Spring_ss_nre": 65,
    "G3 Spring_dss_nre": 511
  },
  {
    "ID": 5,
    "KG Winter_theta_nre": -3.019121889,
    "KG Winter_pr_1_99_nre": 1,
    "KG Winter_ss_nre": 63,
    "KG Winter_dss_nre": 198,
    "KG Spring_theta_nre": -2.70759592,
    "KG Spring_pr_1_99_nre": 1,
    "KG Spring_ss_nre": 64,
    "KG Spring_dss_nre": 229,
    "G1 Fall_theta_nre": -2.376095005,
    "G1 Fall_pr_1_99_nre": 1,
    "G1 Fall_ss_nre": 66,
    "G1 Fall_dss_nre": 262,
    "G1 Winter_theta_nre": -2.16029844,
    "G1 Winter_pr_1_99_nre": 1,
    "G1 Winter_ss_nre": 63,
    "G1 Winter_dss_nre": 284,
    "G1 Spring_theta_nre": -1.546230667,
    "G1 Spring_pr_1_99_nre": 1,
    "G1 Spring_ss_nre": 68,
    "G1 Spring_dss_nre": 345,
    "G2 Fall_theta_nre": -1.53637284,
    "G2 Fall_pr_1_99_nre": 1,
    "G2 Fall_ss_nre": 67,
    "G2 Fall_dss_nre": 346,
    "G2 Winter_theta_nre": -1.094977102,
    "G2 Winter_pr_1_99_nre": 1,
    "G2 Winter_ss_nre": 66,
    "G2 Winter_dss_nre": 391,
    "G2 Spring_theta_nre": -0.964394178,
    "G2 Spring_pr_1_99_nre": 1,
    "G2 Spring_ss_nre": 68,
    "G2 Spring_dss_nre": 404,
    "G3 Fall_theta_nre": -0.847305014,
    "G3 Fall_pr_1_99_nre": 1,
    "G3 Fall_ss_nre": 67,
    "G3 Fall_dss_nre": 415,
    "G3 Winter_theta_nre": -0.361537434,
    "G3 Winter_pr_1_99_nre": 1,
    "G3 Winter_ss_nre": 64,
    "G3 Winter_dss_nre": 464,
    "G3 Spring_theta_nre": 0.151224322,
    "G3 Spring_pr_1_99_nre": 1,
    "G3 Spring_ss_nre": 66,
    "G3 Spring_dss_nre": 515
  },
  {
    "ID": 6,
    "KG Winter_theta_nre": -2.915934798,
    "KG Winter_pr_1_99_nre": 1,
    "KG Winter_ss_nre": 65,
    "KG Winter_dss_nre": 208,
    "KG Spring_theta_nre": -2.531365102,
    "KG Spring_pr_1_99_nre": 1,
    "KG Spring_ss_nre": 67,
    "KG Spring_dss_nre": 247,
    "G1 Fall_theta_nre": -2.310724275,
    "G1 Fall_pr_1_99_nre": 1,
    "G1 Fall_ss_nre": 67,
    "G1 Fall_dss_nre": 269,
    "G1 Winter_theta_nre": -2.054110849,
    "G1 Winter_pr_1_99_nre": 1,
    "G1 Winter_ss_nre": 65,
    "G1 Winter_dss_nre": 295,
    "G1 Spring_theta_nre": -1.433931639,
    "G1 Spring_pr_1_99_nre": 1,
    "G1 Spring_ss_nre": 70,
    "G1 Spring_dss_nre": 357,
    "G2 Fall_theta_nre": -1.536162476,
    "G2 Fall_pr_1_99_nre": 1,
    "G2 Fall_ss_nre": 67,
    "G2 Fall_dss_nre": 346,
    "G2 Winter_theta_nre": -1.088854228,
    "G2 Winter_pr_1_99_nre": 1,
    "G2 Winter_ss_nre": 66,
    "G2 Winter_dss_nre": 391,
    "G2 Spring_theta_nre": -0.95396002,
    "G2 Spring_pr_1_99_nre": 1,
    "G2 Spring_ss_nre": 68,
    "G2 Spring_dss_nre": 405,
    "G3 Fall_theta_nre": -0.844305285,
    "G3 Fall_pr_1_99_nre": 1,
    "G3 Fall_ss_nre": 67,
    "G3 Fall_dss_nre": 416,
    "G3 Winter_theta_nre": -0.318595485,
    "G3 Winter_pr_1_99_nre": 1,
    "G3 Winter_ss_nre": 65,
    "G3 Winter_dss_nre": 468,
    "G3 Spring_theta_nre": 0.165429747,
    "G3 Spring_pr_1_99_nre": 1,
    "G3 Spring_ss_nre": 66,
    "G3 Spring_dss_nre": 517
  },
  {
    "ID": 7,
    "KG Winter_theta_nre": -2.89995077,
    "KG Winter_pr_1_99_nre": 1,
    "KG Winter_ss_nre": 65,
    "KG Winter_dss_nre": 210,
    "KG Spring_theta_nre": -2.459488143,
    "KG Spring_pr_1_99_nre": 1,
    "KG Spring_ss_nre": 69,
    "KG Spring_dss_nre": 254,
    "G1 Fall_theta_nre": -2.274693566,
    "G1 Fall_pr_1_99_nre": 1,
    "G1 Fall_ss_nre": 68,
    "G1 Fall_dss_nre": 273,
    "G1 Winter_theta_nre": -2.050168873,
    "G1 Winter_pr_1_99_nre": 1,
    "G1 Winter_ss_nre": 65,
    "G1 Winter_dss_nre": 295,
    "G1 Spring_theta_nre": -1.416145966,
    "G1 Spring_pr_1_99_nre": 1,
    "G1 Spring_ss_nre": 70,
    "G1 Spring_dss_nre": 358,
    "G2 Fall_theta_nre": -1.525493013,
    "G2 Fall_pr_1_99_nre": 1,
    "G2 Fall_ss_nre": 67,
    "G2 Fall_dss_nre": 347,
    "G2 Winter_theta_nre": -0.982879104,
    "G2 Winter_pr_1_99_nre": 1,
    "G2 Winter_ss_nre": 68,
    "G2 Winter_dss_nre": 402,
    "G2 Spring_theta_nre": -0.928776555,
    "G2 Spring_pr_1_99_nre": 1,
    "G2 Spring_ss_nre": 68,
    "G2 Spring_dss_nre": 407,
    "G3 Fall_theta_nre": -0.741550693,
    "G3 Fall_pr_1_99_nre": 1,
    "G3 Fall_ss_nre": 68,
    "G3 Fall_dss_nre": 426,
    "G3 Winter_theta_nre": -0.303586591,
    "G3 Winter_pr_1_99_nre": 1,
    "G3 Winter_ss_nre": 65,
    "G3 Winter_dss_nre": 470,
    "G3 Spring_theta_nre": 0.324776151,
    "G3 Spring_pr_1_99_nre": 1,
    "G3 Spring_ss_nre": 70,
    "G3 Spring_dss_nre": 532
  },
  {
    "ID": 8,
    "KG Winter_theta_nre": -2.68463619,
    "KG Winter_pr_1_99_nre": 2,
    "KG Winter_ss_nre": 69,
    "KG Winter_dss_nre": 232,
    "KG Spring_theta_nre": -2.451172478,
    "KG Spring_pr_1_99_nre": 2,
    "KG Spring_ss_nre": 69,
    "KG Spring_dss_nre": 255,
    "G1 Fall_theta_nre": -2.20426706,
    "G1 Fall_pr_1_99_nre": 2,
    "G1 Fall_ss_nre": 69,
    "G1 Fall_dss_nre": 280,
    "G1 Winter_theta_nre": -2.026912944,
    "G1 Winter_pr_1_99_nre": 2,
    "G1 Winter_ss_nre": 66,
    "G1 Winter_dss_nre": 297,
    "G1 Spring_theta_nre": -1.391846398,
    "G1 Spring_pr_1_99_nre": 2,
    "G1 Spring_ss_nre": 71,
    "G1 Spring_dss_nre": 361,
    "G2 Fall_theta_nre": -1.500044365,
    "G2 Fall_pr_1_99_nre": 2,
    "G2 Fall_ss_nre": 67,
    "G2 Fall_dss_nre": 350,
    "G2 Winter_theta_nre": -0.942733093,
    "G2 Winter_pr_1_99_nre": 2,
    "G2 Winter_ss_nre": 69,
    "G2 Winter_dss_nre": 406,
    "G2 Spring_theta_nre": -0.899141022,
    "G2 Spring_pr_1_99_nre": 2,
    "G2 Spring_ss_nre": 69,
    "G2 Spring_dss_nre": 410,
    "G3 Fall_theta_nre": -0.698445496,
    "G3 Fall_pr_1_99_nre": 2,
    "G3 Fall_ss_nre": 69,
    "G3 Fall_dss_nre": 430,
    "G3 Winter_theta_nre": -0.245967401,
    "G3 Winter_pr_1_99_nre": 2,
    "G3 Winter_ss_nre": 66,
    "G3 Winter_dss_nre": 475,
    "G3 Spring_theta_nre": 0.354805823,
    "G3 Spring_pr_1_99_nre": 2,
    "G3 Spring_ss_nre": 70,
    "G3 Spring_dss_nre": 535
  },
  {
    "ID": 9,
    "KG Winter_theta_nre": -2.668377027,
    "KG Winter_pr_1_99_nre": 2,
    "KG Winter_ss_nre": 69,
    "KG Winter_dss_nre": 233,
    "KG Spring_theta_nre": -2.426732963,
    "KG Spring_pr_1_99_nre": 2,
    "KG Spring_ss_nre": 69,
    "KG Spring_dss_nre": 257,
    "G1 Fall_theta_nre": -2.200973204,
    "G1 Fall_pr_1_99_nre": 2,
    "G1 Fall_ss_nre": 69,
    "G1 Fall_dss_nre": 280,
    "G1 Winter_theta_nre": -1.9922451,
    "G1 Winter_pr_1_99_nre": 2,
    "G1 Winter_ss_nre": 66,
    "G1 Winter_dss_nre": 301,
    "G1 Spring_theta_nre": -1.304242493,
    "G1 Spring_pr_1_99_nre": 2,
    "G1 Spring_ss_nre": 72,
    "G1 Spring_dss_nre": 370,
    "G2 Fall_theta_nre": -1.467375526,
    "G2 Fall_pr_1_99_nre": 2,
    "G2 Fall_ss_nre": 68,
    "G2 Fall_dss_nre": 353,
    "G2 Winter_theta_nre": -0.92757919,
    "G2 Winter_pr_1_99_nre": 2,
    "G2 Winter_ss_nre": 69,
    "G2 Winter_dss_nre": 407,
    "G2 Spring_theta_nre": -0.868057973,
    "G2 Spring_pr_1_99_nre": 2,
    "G2 Spring_ss_nre": 69,
    "G2 Spring_dss_nre": 413,
    "G3 Fall_theta_nre": -0.678612984,
    "G3 Fall_pr_1_99_nre": 2,
    "G3 Fall_ss_nre": 69,
    "G3 Fall_dss_nre": 432,
    "G3 Winter_theta_nre": -0.240542569,
    "G3 Winter_pr_1_99_nre": 2,
    "G3 Winter_ss_nre": 66,
    "G3 Winter_dss_nre": 476,
    "G3 Spring_theta_nre": 0.407639533,
    "G3 Spring_pr_1_99_nre": 2,
    "G3 Spring_ss_nre": 72,
    "G3 Spring_dss_nre": 541
  },
  {
    "ID": 10,
    "KG Winter_theta_nre": -2.621695901,
    "KG Winter_pr_1_99_nre": 2,
    "KG Winter_ss_nre": 70,
    "KG Winter_dss_nre": 238,
    "KG Spring_theta_nre": -2.422849218,
    "KG Spring_pr_1_99_nre": 2,
    "KG Spring_ss_nre": 69,
    "KG Spring_dss_nre": 258,
    "G1 Fall_theta_nre": -2.111859387,
    "G1 Fall_pr_1_99_nre": 2,
    "G1 Fall_ss_nre": 71,
    "G1 Fall_dss_nre": 289,
    "G1 Winter_theta_nre": -1.91668072,
    "G1 Winter_pr_1_99_nre": 2,
    "G1 Winter_ss_nre": 68,
    "G1 Winter_dss_nre": 308,
    "G1 Spring_theta_nre": -1.287282273,
    "G1 Spring_pr_1_99_nre": 2,
    "G1 Spring_ss_nre": 73,
    "G1 Spring_dss_nre": 371,
    "G2 Fall_theta_nre": -1.322474397,
    "G2 Fall_pr_1_99_nre": 2,
    "G2 Fall_ss_nre": 70,
    "G2 Fall_dss_nre": 368,
    "G2 Winter_theta_nre": -0.851167209,
    "G2 Winter_pr_1_99_nre": 2,
    "G2 Winter_ss_nre": 70,
    "G2 Winter_dss_nre": 415,
    "G2 Spring_theta_nre": -0.766200058,
    "G2 Spring_pr_1_99_nre": 2,
    "G2 Spring_ss_nre": 71,
    "G2 Spring_dss_nre": 423,
    "G3 Fall_theta_nre": -0.652775875,
    "G3 Fall_pr_1_99_nre": 2,
    "G3 Fall_ss_nre": 70,
    "G3 Fall_dss_nre": 435,
    "G3 Winter_theta_nre": -0.238187368,
    "G3 Winter_pr_1_99_nre": 2,
    "G3 Winter_ss_nre": 66,
    "G3 Winter_dss_nre": 476,
    "G3 Spring_theta_nre": 0.434179555,
    "G3 Spring_pr_1_99_nre": 2,
    "G3 Spring_ss_nre": 72,
    "G3 Spring_dss_nre": 543
  },
  {
    "ID": 11,
    "KG Winter_theta_nre": -2.62007356,
    "KG Winter_pr_1_99_nre": 2,
    "KG Winter_ss_nre": 70,
    "KG Winter_dss_nre": 238,
    "KG Spring_theta_nre": -2.417337322,
    "KG Spring_pr_1_99_nre": 2,
    "KG Spring_ss_nre": 69,
    "KG Spring_dss_nre": 258,
    "G1 Fall_theta_nre": -2.108296527,
    "G1 Fall_pr_1_99_nre": 2,
    "G1 Fall_ss_nre": 71,
    "G1 Fall_dss_nre": 289,
    "G1 Winter_theta_nre": -1.902055222,
    "G1 Winter_pr_1_99_nre": 2,
    "G1 Winter_ss_nre": 68,
    "G1 Winter_dss_nre": 310,
    "G1 Spring_theta_nre": -1.285149481,
    "G1 Spring_pr_1_99_nre": 2,
    "G1 Spring_ss_nre": 73,
    "G1 Spring_dss_nre": 371,
    "G2 Fall_theta_nre": -1.290547068,
    "G2 Fall_pr_1_99_nre": 2,
    "G2 Fall_ss_nre": 71,
    "G2 Fall_dss_nre": 371,
    "G2 Winter_theta_nre": -0.845450828,
    "G2 Winter_pr_1_99_nre": 2,
    "G2 Winter_ss_nre": 71,
    "G2 Winter_dss_nre": 415,
    "G2 Spring_theta_nre": -0.726851803,
    "G2 Spring_pr_1_99_nre": 2,
    "G2 Spring_ss_nre": 72,
    "G2 Spring_dss_nre": 427,
    "G3 Fall_theta_nre": -0.587950067,
    "G3 Fall_pr_1_99_nre": 2,
    "G3 Fall_ss_nre": 71,
    "G3 Fall_dss_nre": 441,
    "G3 Winter_theta_nre": -0.208850339,
    "G3 Winter_pr_1_99_nre": 2,
    "G3 Winter_ss_nre": 67,
    "G3 Winter_dss_nre": 479,
    "G3 Spring_theta_nre": 0.436510775,
    "G3 Spring_pr_1_99_nre": 2,
    "G3 Spring_ss_nre": 72,
    "G3 Spring_dss_nre": 544
  },
  {
    "ID": 12,
    "KG Winter_theta_nre": -2.617497667,
    "KG Winter_pr_1_99_nre": 2,
    "KG Winter_ss_nre": 70,
    "KG Winter_dss_nre": 238,
    "KG Spring_theta_nre": -2.346482122,
    "KG Spring_pr_1_99_nre": 2,
    "KG Spring_ss_nre": 71,
    "KG Spring_dss_nre": 265,
    "G1 Fall_theta_nre": -2.095298153,
    "G1 Fall_pr_1_99_nre": 2,
    "G1 Fall_ss_nre": 71,
    "G1 Fall_dss_nre": 290,
    "G1 Winter_theta_nre": -1.893582361,
    "G1 Winter_pr_1_99_nre": 2,
    "G1 Winter_ss_nre": 68,
    "G1 Winter_dss_nre": 311,
    "G1 Spring_theta_nre": -1.247624515,
    "G1 Spring_pr_1_99_nre": 2,
    "G1 Spring_ss_nre": 73,
    "G1 Spring_dss_nre": 375,
    "G2 Fall_theta_nre": -1.276172971,
    "G2 Fall_pr_1_99_nre": 2,
    "G2 Fall_ss_nre": 71,
    "G2 Fall_dss_nre": 372,
    "G2 Winter_theta_nre": -0.844439193,
    "G2 Winter_pr_1_99_nre": 2,
    "G2 Winter_ss_nre": 71,
    "G2 Winter_dss_nre": 416,
    "G2 Spring_theta_nre": -0.712884743,
    "G2 Spring_pr_1_99_nre": 2,
    "G2 Spring_ss_nre": 72,
    "G2 Spring_dss_nre": 429,
    "G3 Fall_theta_nre": -0.583371723,
    "G3 Fall_pr_1_99_nre": 2,
    "G3 Fall_ss_nre": 71,
    "G3 Fall_dss_nre": 442,
    "G3 Winter_theta_nre": -0.193338872,
    "G3 Winter_pr_1_99_nre": 2,
    "G3 Winter_ss_nre": 67,
    "G3 Winter_dss_nre": 481,
    "G3 Spring_theta_nre": 0.445836189,
    "G3 Spring_pr_1_99_nre": 2,
    "G3 Spring_ss_nre": 72,
    "G3 Spring_dss_nre": 545
  },
  {
    "ID": 13,
    "KG Winter_theta_nre": -2.604946112,
    "KG Winter_pr_1_99_nre": 3,
    "KG Winter_ss_nre": 70,
    "KG Winter_dss_nre": 240,
    "KG Spring_theta_nre": -2.317249484,
    "KG Spring_pr_1_99_nre": 3,
    "KG Spring_ss_nre": 71,
    "KG Spring_dss_nre": 268,
    "G1 Fall_theta_nre": -2.076787109,
    "G1 Fall_pr_1_99_nre": 3,
    "G1 Fall_ss_nre": 71,
    "G1 Fall_dss_nre": 292,
    "G1 Winter_theta_nre": -1.699696004,
    "G1 Winter_pr_1_99_nre": 3,
    "G1 Winter_ss_nre": 72,
    "G1 Winter_dss_nre": 330,
    "G1 Spring_theta_nre": -1.241993876,
    "G1 Spring_pr_1_99_nre": 3,
    "G1 Spring_ss_nre": 73,
    "G1 Spring_dss_nre": 376,
    "G2 Fall_theta_nre": -1.221798812,
    "G2 Fall_pr_1_99_nre": 3,
    "G2 Fall_ss_nre": 72,
    "G2 Fall_dss_nre": 378,
    "G2 Winter_theta_nre": -0.83433243,
    "G2 Winter_pr_1_99_nre": 3,
    "G2 Winter_ss_nre": 71,
    "G2 Winter_dss_nre": 417,
    "G2 Spring_theta_nre": -0.702954615,
    "G2 Spring_pr_1_99_nre": 3,
    "G2 Spring_ss_nre": 72,
    "G2 Spring_dss_nre": 430,
    "G3 Fall_theta_nre": -0.574000519,
    "G3 Fall_pr_1_99_nre": 3,
    "G3 Fall_ss_nre": 71,
    "G3 Fall_dss_nre": 443,
    "G3 Winter_theta_nre": -0.139248377,
    "G3 Winter_pr_1_99_nre": 3,
    "G3 Winter_ss_nre": 68,
    "G3 Winter_dss_nre": 486,
    "G3 Spring_theta_nre": 0.451857396,
    "G3 Spring_pr_1_99_nre": 3,
    "G3 Spring_ss_nre": 73,
    "G3 Spring_dss_nre": 545
  },
  {
    "ID": 14,
    "KG Winter_theta_nre": -2.583466416,
    "KG Winter_pr_1_99_nre": 3,
    "KG Winter_ss_nre": 71,
    "KG Winter_dss_nre": 242,
    "KG Spring_theta_nre": -2.310076588,
    "KG Spring_pr_1_99_nre": 3,
    "KG Spring_ss_nre": 71,
    "KG Spring_dss_nre": 269,
    "G1 Fall_theta_nre": -1.976457372,
    "G1 Fall_pr_1_99_nre": 3,
    "G1 Fall_ss_nre": 73,
    "G1 Fall_dss_nre": 302,
    "G1 Winter_theta_nre": -1.688944378,
    "G1 Winter_pr_1_99_nre": 3,
    "G1 Winter_ss_nre": 72,
    "G1 Winter_dss_nre": 331,
    "G1 Spring_theta_nre": -1.232345498,
    "G1 Spring_pr_1_99_nre": 3,
    "G1 Spring_ss_nre": 74,
    "G1 Spring_dss_nre": 377,
    "G2 Fall_theta_nre": -1.206775014,
    "G2 Fall_pr_1_99_nre": 3,
    "G2 Fall_ss_nre": 73,
    "G2 Fall_dss_nre": 379,
    "G2 Winter_theta_nre": -0.818115279,
    "G2 Winter_pr_1_99_nre": 3,
    "G2 Winter_ss_nre": 71,
    "G2 Winter_dss_nre": 418,
    "G2 Spring_theta_nre": -0.687616678,
    "G2 Spring_pr_1_99_nre": 3,
    "G2 Spring_ss_nre": 72,
    "G2 Spring_dss_nre": 431,
    "G3 Fall_theta_nre": -0.562892533,
    "G3 Fall_pr_1_99_nre": 3,
    "G3 Fall_ss_nre": 71,
    "G3 Fall_dss_nre": 444,
    "G3 Winter_theta_nre": -0.067822713,
    "G3 Winter_pr_1_99_nre": 3,
    "G3 Winter_ss_nre": 70,
    "G3 Winter_dss_nre": 493,
    "G3 Spring_theta_nre": 0.462652528,
    "G3 Spring_pr_1_99_nre": 3,
    "G3 Spring_ss_nre": 73,
    "G3 Spring_dss_nre": 546
  },
  {
    "ID": 15,
    "KG Winter_theta_nre": -2.550540961,
    "KG Winter_pr_1_99_nre": 3,
    "KG Winter_ss_nre": 71,
    "KG Winter_dss_nre": 245,
    "KG Spring_theta_nre": -2.298824578,
    "KG Spring_pr_1_99_nre": 3,
    "KG Spring_ss_nre": 71,
    "KG Spring_dss_nre": 270,
    "G1 Fall_theta_nre": -1.974845532,
    "G1 Fall_pr_1_99_nre": 3,
    "G1 Fall_ss_nre": 73,
    "G1 Fall_dss_nre": 303,
    "G1 Winter_theta_nre": -1.649096417,
    "G1 Winter_pr_1_99_nre": 3,
    "G1 Winter_ss_nre": 73,
    "G1 Winter_dss_nre": 335,
    "G1 Spring_theta_nre": -1.230112126,
    "G1 Spring_pr_1_99_nre": 3,
    "G1 Spring_ss_nre": 74,
    "G1 Spring_dss_nre": 377,
    "G2 Fall_theta_nre": -1.200365342,
    "G2 Fall_pr_1_99_nre": 3,
    "G2 Fall_ss_nre": 73,
    "G2 Fall_dss_nre": 380,
    "G2 Winter_theta_nre": -0.77154296,
    "G2 Winter_pr_1_99_nre": 3,
    "G2 Winter_ss_nre": 72,
    "G2 Winter_dss_nre": 423,
    "G2 Spring_theta_nre": -0.6491967,
    "G2 Spring_pr_1_99_nre": 3,
    "G2 Spring_ss_nre": 73,
    "G2 Spring_dss_nre": 435,
    "G3 Fall_theta_nre": -0.541371299,
    "G3 Fall_pr_1_99_nre": 3,
    "G3 Fall_ss_nre": 72,
    "G3 Fall_dss_nre": 446,
    "G3 Winter_theta_nre": -0.061071402,
    "G3 Winter_pr_1_99_nre": 3,
    "G3 Winter_ss_nre": 70,
    "G3 Winter_dss_nre": 494,
    "G3 Spring_theta_nre": 0.465028927,
    "G3 Spring_pr_1_99_nre": 3,
    "G3 Spring_ss_nre": 73,
    "G3 Spring_dss_nre": 547
  },
  {
    "ID": 16,
    "KG Winter_theta_nre": -2.54941424,
    "KG Winter_pr_1_99_nre": 3,
    "KG Winter_ss_nre": 71,
    "KG Winter_dss_nre": 245,
    "KG Spring_theta_nre": -2.282079334,
    "KG Spring_pr_1_99_nre": 3,
    "KG Spring_ss_nre": 72,
    "KG Spring_dss_nre": 272,
    "G1 Fall_theta_nre": -1.969859773,
    "G1 Fall_pr_1_99_nre": 3,
    "G1 Fall_ss_nre": 73,
    "G1 Fall_dss_nre": 303,
    "G1 Winter_theta_nre": -1.62625858,
    "G1 Winter_pr_1_99_nre": 3,
    "G1 Winter_ss_nre": 73,
    "G1 Winter_dss_nre": 337,
    "G1 Spring_theta_nre": -1.216842674,
    "G1 Spring_pr_1_99_nre": 3,
    "G1 Spring_ss_nre": 74,
    "G1 Spring_dss_nre": 378,
    "G2 Fall_theta_nre": -1.174941723,
    "G2 Fall_pr_1_99_nre": 3,
    "G2 Fall_ss_nre": 73,
    "G2 Fall_dss_nre": 383,
    "G2 Winter_theta_nre": -0.75040333,
    "G2 Winter_pr_1_99_nre": 3,
    "G2 Winter_ss_nre": 72,
    "G2 Winter_dss_nre": 425,
    "G2 Spring_theta_nre": -0.631038609,
    "G2 Spring_pr_1_99_nre": 3,
    "G2 Spring_ss_nre": 73,
    "G2 Spring_dss_nre": 437,
    "G3 Fall_theta_nre": -0.536257342,
    "G3 Fall_pr_1_99_nre": 3,
    "G3 Fall_ss_nre": 72,
    "G3 Fall_dss_nre": 446,
    "G3 Winter_theta_nre": 0.019857477,
    "G3 Winter_pr_1_99_nre": 3,
    "G3 Winter_ss_nre": 71,
    "G3 Winter_dss_nre": 502,
    "G3 Spring_theta_nre": 0.488008692,
    "G3 Spring_pr_1_99_nre": 3,
    "G3 Spring_ss_nre": 73,
    "G3 Spring_dss_nre": 549
  },
  {
    "ID": 17,
    "KG Winter_theta_nre": -2.525793585,
    "KG Winter_pr_1_99_nre": 3,
    "KG Winter_ss_nre": 72,
    "KG Winter_dss_nre": 247,
    "KG Spring_theta_nre": -2.262027882,
    "KG Spring_pr_1_99_nre": 3,
    "KG Spring_ss_nre": 72,
    "KG Spring_dss_nre": 274,
    "G1 Fall_theta_nre": -1.951983981,
    "G1 Fall_pr_1_99_nre": 3,
    "G1 Fall_ss_nre": 73,
    "G1 Fall_dss_nre": 305,
    "G1 Winter_theta_nre": -1.61314335,
    "G1 Winter_pr_1_99_nre": 3,
    "G1 Winter_ss_nre": 73,
    "G1 Winter_dss_nre": 339,
    "G1 Spring_theta_nre": -1.216701152,
    "G1 Spring_pr_1_99_nre": 3,
    "G1 Spring_ss_nre": 74,
    "G1 Spring_dss_nre": 378,
    "G2 Fall_theta_nre": -1.155867363,
    "G2 Fall_pr_1_99_nre": 3,
    "G2 Fall_ss_nre": 73,
    "G2 Fall_dss_nre": 384,
    "G2 Winter_theta_nre": -0.746365916,
    "G2 Winter_pr_1_99_nre": 3,
    "G2 Winter_ss_nre": 72,
    "G2 Winter_dss_nre": 425,
    "G2 Spring_theta_nre": -0.621487904,
    "G2 Spring_pr_1_99_nre": 3,
    "G2 Spring_ss_nre": 74,
    "G2 Spring_dss_nre": 438,
    "G3 Fall_theta_nre": -0.536233789,
    "G3 Fall_pr_1_99_nre": 3,
    "G3 Fall_ss_nre": 72,
    "G3 Fall_dss_nre": 446,
    "G3 Winter_theta_nre": 0.036997627,
    "G3 Winter_pr_1_99_nre": 3,
    "G3 Winter_ss_nre": 72,
    "G3 Winter_dss_nre": 504,
    "G3 Spring_theta_nre": 0.488828982,
    "G3 Spring_pr_1_99_nre": 3,
    "G3 Spring_ss_nre": 73,
    "G3 Spring_dss_nre": 549
  },
  {
    "ID": 18,
    "KG Winter_theta_nre": -2.523002976,
    "KG Winter_pr_1_99_nre": 4,
    "KG Winter_ss_nre": 72,
    "KG Winter_dss_nre": 248,
    "KG Spring_theta_nre": -2.245780846,
    "KG Spring_pr_1_99_nre": 4,
    "KG Spring_ss_nre": 72,
    "KG Spring_dss_nre": 275,
    "G1 Fall_theta_nre": -1.951932819,
    "G1 Fall_pr_1_99_nre": 4,
    "G1 Fall_ss_nre": 73,
    "G1 Fall_dss_nre": 305,
    "G1 Winter_theta_nre": -1.578272194,
    "G1 Winter_pr_1_99_nre": 4,
    "G1 Winter_ss_nre": 74,
    "G1 Winter_dss_nre": 342,
    "G1 Spring_theta_nre": -1.21524714,
    "G1 Spring_pr_1_99_nre": 4,
    "G1 Spring_ss_nre": 74,
    "G1 Spring_dss_nre": 378,
    "G2 Fall_theta_nre": -1.148044391,
    "G2 Fall_pr_1_99_nre": 4,
    "G2 Fall_ss_nre": 74,
    "G2 Fall_dss_nre": 385,
    "G2 Winter_theta_nre": -0.690335484,
    "G2 Winter_pr_1_99_nre": 4,
    "G2 Winter_ss_nre": 74,
    "G2 Winter_dss_nre": 431,
    "G2 Spring_theta_nre": -0.582529704,
    "G2 Spring_pr_1_99_nre": 4,
    "G2 Spring_ss_nre": 74,
    "G2 Spring_dss_nre": 442,
    "G3 Fall_theta_nre": -0.471860451,
    "G3 Fall_pr_1_99_nre": 4,
    "G3 Fall_ss_nre": 73,
    "G3 Fall_dss_nre": 453,
    "G3 Winter_theta_nre": 0.089756548,
    "G3 Winter_pr_1_99_nre": 4,
    "G3 Winter_ss_nre": 73,
    "G3 Winter_dss_nre": 509,
    "G3 Spring_theta_nre": 0.493509667,
    "G3 Spring_pr_1_99_nre": 4,
    "G3 Spring_ss_nre": 73,
    "G3 Spring_dss_nre": 549
  },
  {
    "ID": 19,
    "KG Winter_theta_nre": -2.522877721,
    "KG Winter_pr_1_99_nre": 4,
    "KG Winter_ss_nre": 72,
    "KG Winter_dss_nre": 248,
    "KG Spring_theta_nre": -2.158031244,
    "KG Spring_pr_1_99_nre": 4,
    "KG Spring_ss_nre": 74,
    "KG Spring_dss_nre": 284,
    "G1 Fall_theta_nre": -1.92472395,
    "G1 Fall_pr_1_99_nre": 4,
    "G1 Fall_ss_nre": 74,
    "G1 Fall_dss_nre": 308,
    "G1 Winter_theta_nre": -1.55670147,
    "G1 Winter_pr_1_99_nre": 4,
    "G1 Winter_ss_nre": 74,
    "G1 Winter_dss_nre": 344,
    "G1 Spring_theta_nre": -1.212849653,
    "G1 Spring_pr_1_99_nre": 4,
    "G1 Spring_ss_nre": 74,
    "G1 Spring_dss_nre": 379,
    "G2 Fall_theta_nre": -1.106147312,
    "G2 Fall_pr_1_99_nre": 4,
    "G2 Fall_ss_nre": 74,
    "G2 Fall_dss_nre": 389,
    "G2 Winter_theta_nre": -0.673407606,
    "G2 Winter_pr_1_99_nre": 4,
    "G2 Winter_ss_nre": 74,
    "G2 Winter_dss_nre": 433,
    "G2 Spring_theta_nre": -0.57140215,
    "G2 Spring_pr_1_99_nre": 4,
    "G2 Spring_ss_nre": 74,
    "G2 Spring_dss_nre": 443,
    "G3 Fall_theta_nre": -0.469096255,
    "G3 Fall_pr_1_99_nre": 4,
    "G3 Fall_ss_nre": 73,
    "G3 Fall_dss_nre": 453,
    "G3 Winter_theta_nre": 0.151258409,
    "G3 Winter_pr_1_99_nre": 4,
    "G3 Winter_ss_nre": 74,
    "G3 Winter_dss_nre": 515,
    "G3 Spring_theta_nre": 0.496946925,
    "G3 Spring_pr_1_99_nre": 4,
    "G3 Spring_ss_nre": 74,
    "G3 Spring_dss_nre": 550
  },
  {
    "ID": 20,
    "KG Winter_theta_nre": -2.512511963,
    "KG Winter_pr_1_99_nre": 4,
    "KG Winter_ss_nre": 72,
    "KG Winter_dss_nre": 249,
    "KG Spring_theta_nre": -2.124489283,
    "KG Spring_pr_1_99_nre": 4,
    "KG Spring_ss_nre": 74,
    "KG Spring_dss_nre": 288,
    "G1 Fall_theta_nre": -1.923479709,
    "G1 Fall_pr_1_99_nre": 4,
    "G1 Fall_ss_nre": 74,
    "G1 Fall_dss_nre": 308,
    "G1 Winter_theta_nre": -1.555647682,
    "G1 Winter_pr_1_99_nre": 4,
    "G1 Winter_ss_nre": 74,
    "G1 Winter_dss_nre": 344,
    "G1 Spring_theta_nre": -1.204473167,
    "G1 Spring_pr_1_99_nre": 4,
    "G1 Spring_ss_nre": 74,
    "G1 Spring_dss_nre": 380,
    "G2 Fall_theta_nre": -1.103554514,
    "G2 Fall_pr_1_99_nre": 4,
    "G2 Fall_ss_nre": 74,
    "G2 Fall_dss_nre": 390,
    "G2 Winter_theta_nre": -0.658212834,
    "G2 Winter_pr_1_99_nre": 4,
    "G2 Winter_ss_nre": 74,
    "G2 Winter_dss_nre": 434,
    "G2 Spring_theta_nre": -0.564746833,
    "G2 Spring_pr_1_99_nre": 4,
    "G2 Spring_ss_nre": 75,
    "G2 Spring_dss_nre": 444,
    "G3 Fall_theta_nre": -0.43747559,
    "G3 Fall_pr_1_99_nre": 4,
    "G3 Fall_ss_nre": 73,
    "G3 Fall_dss_nre": 456,
    "G3 Winter_theta_nre": 0.179829119,
    "G3 Winter_pr_1_99_nre": 4,
    "G3 Winter_ss_nre": 75,
    "G3 Winter_dss_nre": 518,
    "G3 Spring_theta_nre": 0.503446269,
    "G3 Spring_pr_1_99_nre": 4,
    "G3 Spring_ss_nre": 74,
    "G3 Spring_dss_nre": 550
  },
  {
    "ID": 21,
    "KG Winter_theta_nre": -2.508180879,
    "KG Winter_pr_1_99_nre": 4,
    "KG Winter_ss_nre": 72,
    "KG Winter_dss_nre": 249,
    "KG Spring_theta_nre": -2.118210309,
    "KG Spring_pr_1_99_nre": 4,
    "KG Spring_ss_nre": 74,
    "KG Spring_dss_nre": 288,
    "G1 Fall_theta_nre": -1.866618181,
    "G1 Fall_pr_1_99_nre": 4,
    "G1 Fall_ss_nre": 75,
    "G1 Fall_dss_nre": 313,
    "G1 Winter_theta_nre": -1.543335278,
    "G1 Winter_pr_1_99_nre": 4,
    "G1 Winter_ss_nre": 74,
    "G1 Winter_dss_nre": 346,
    "G1 Spring_theta_nre": -1.169474525,
    "G1 Spring_pr_1_99_nre": 4,
    "G1 Spring_ss_nre": 75,
    "G1 Spring_dss_nre": 383,
    "G2 Fall_theta_nre": -1.094176839,
    "G2 Fall_pr_1_99_nre": 4,
    "G2 Fall_ss_nre": 74,
    "G2 Fall_dss_nre": 391,
    "G2 Winter_theta_nre": -0.657211183,
    "G2 Winter_pr_1_99_nre": 4,
    "G2 Winter_ss_nre": 74,
    "G2 Winter_dss_nre": 434,
    "G2 Spring_theta_nre": -0.53395636,
    "G2 Spring_pr_1_99_nre": 4,
    "G2 Spring_ss_nre": 75,
    "G2 Spring_dss_nre": 447,
    "G3 Fall_theta_nre": -0.434152524,
    "G3 Fall_pr_1_99_nre": 4,
    "G3 Fall_ss_nre": 73,
    "G3 Fall_dss_nre": 457,
    "G3 Winter_theta_nre": 0.190238769,
    "G3 Winter_pr_1_99_nre": 4,
    "G3 Winter_ss_nre": 75,
    "G3 Winter_dss_nre": 519,
    "G3 Spring_theta_nre": 0.51172443,
    "G3 Spring_pr_1_99_nre": 4,
    "G3 Spring_ss_nre": 74,
    "G3 Spring_dss_nre": 551
  },
  {
    "ID": 22,
    "KG Winter_theta_nre": -2.490570987,
    "KG Winter_pr_1_99_nre": 4,
    "KG Winter_ss_nre": 72,
    "KG Winter_dss_nre": 251,
    "KG Spring_theta_nre": -2.088293196,
    "KG Spring_pr_1_99_nre": 4,
    "KG Spring_ss_nre": 75,
    "KG Spring_dss_nre": 291,
    "G1 Fall_theta_nre": -1.863203713,
    "G1 Fall_pr_1_99_nre": 4,
    "G1 Fall_ss_nre": 75,
    "G1 Fall_dss_nre": 314,
    "G1 Winter_theta_nre": -1.540363286,
    "G1 Winter_pr_1_99_nre": 4,
    "G1 Winter_ss_nre": 74,
    "G1 Winter_dss_nre": 346,
    "G1 Spring_theta_nre": -1.158856392,
    "G1 Spring_pr_1_99_nre": 4,
    "G1 Spring_ss_nre": 75,
    "G1 Spring_dss_nre": 384,
    "G2 Fall_theta_nre": -1.089595456,
    "G2 Fall_pr_1_99_nre": 4,
    "G2 Fall_ss_nre": 75,
    "G2 Fall_dss_nre": 391,
    "G2 Winter_theta_nre": -0.652293341,
    "G2 Winter_pr_1_99_nre": 4,
    "G2 Winter_ss_nre": 74,
    "G2 Winter_dss_nre": 435,
    "G2 Spring_theta_nre": -0.517079624,
    "G2 Spring_pr_1_99_nre": 4,
    "G2 Spring_ss_nre": 75,
    "G2 Spring_dss_nre": 448,
    "G3 Fall_theta_nre": -0.423305675,
    "G3 Fall_pr_1_99_nre": 4,
    "G3 Fall_ss_nre": 74,
    "G3 Fall_dss_nre": 458,
    "G3 Winter_theta_nre": 0.218530369,
    "G3 Winter_pr_1_99_nre": 4,
    "G3 Winter_ss_nre": 75,
    "G3 Winter_dss_nre": 522,
    "G3 Spring_theta_nre": 0.517653328,
    "G3 Spring_pr_1_99_nre": 4,
    "G3 Spring_ss_nre": 74,
    "G3 Spring_dss_nre": 552
  },
  {
    "ID": 23,
    "KG Winter_theta_nre": -2.467877184,
    "KG Winter_pr_1_99_nre": 5,
    "KG Winter_ss_nre": 73,
    "KG Winter_dss_nre": 253,
    "KG Spring_theta_nre": -2.081373443,
    "KG Spring_pr_1_99_nre": 5,
    "KG Spring_ss_nre": 75,
    "KG Spring_dss_nre": 292,
    "G1 Fall_theta_nre": -1.848812455,
    "G1 Fall_pr_1_99_nre": 5,
    "G1 Fall_ss_nre": 75,
    "G1 Fall_dss_nre": 315,
    "G1 Winter_theta_nre": -1.529904841,
    "G1 Winter_pr_1_99_nre": 5,
    "G1 Winter_ss_nre": 75,
    "G1 Winter_dss_nre": 347,
    "G1 Spring_theta_nre": -1.133843946,
    "G1 Spring_pr_1_99_nre": 5,
    "G1 Spring_ss_nre": 75,
    "G1 Spring_dss_nre": 387,
    "G2 Fall_theta_nre": -1.076207892,
    "G2 Fall_pr_1_99_nre": 5,
    "G2 Fall_ss_nre": 75,
    "G2 Fall_dss_nre": 392,
    "G2 Winter_theta_nre": -0.647668639,
    "G2 Winter_pr_1_99_nre": 5,
    "G2 Winter_ss_nre": 74,
    "G2 Winter_dss_nre": 435,
    "G2 Spring_theta_nre": -0.515150058,
    "G2 Spring_pr_1_99_nre": 5,
    "G2 Spring_ss_nre": 75,
    "G2 Spring_dss_nre": 448,
    "G3 Fall_theta_nre": -0.418670715,
    "G3 Fall_pr_1_99_nre": 5,
    "G3 Fall_ss_nre": 74,
    "G3 Fall_dss_nre": 458,
    "G3 Winter_theta_nre": 0.229921808,
    "G3 Winter_pr_1_99_nre": 5,
    "G3 Winter_ss_nre": 76,
    "G3 Winter_dss_nre": 523,
    "G3 Spring_theta_nre": 0.53294854,
    "G3 Spring_pr_1_99_nre": 5,
    "G3 Spring_ss_nre": 74,
    "G3 Spring_dss_nre": 553
  },
  {
    "ID": 24,
    "KG Winter_theta_nre": -2.455055575,
    "KG Winter_pr_1_99_nre": 5,
    "KG Winter_ss_nre": 73,
    "KG Winter_dss_nre": 254,
    "KG Spring_theta_nre": -2.076871673,
    "KG Spring_pr_1_99_nre": 5,
    "KG Spring_ss_nre": 75,
    "KG Spring_dss_nre": 292,
    "G1 Fall_theta_nre": -1.84668805,
    "G1 Fall_pr_1_99_nre": 5,
    "G1 Fall_ss_nre": 75,
    "G1 Fall_dss_nre": 315,
    "G1 Winter_theta_nre": -1.522914546,
    "G1 Winter_pr_1_99_nre": 5,
    "G1 Winter_ss_nre": 75,
    "G1 Winter_dss_nre": 348,
    "G1 Spring_theta_nre": -1.114755513,
    "G1 Spring_pr_1_99_nre": 5,
    "G1 Spring_ss_nre": 76,
    "G1 Spring_dss_nre": 389,
    "G2 Fall_theta_nre": -1.055977943,
    "G2 Fall_pr_1_99_nre": 5,
    "G2 Fall_ss_nre": 75,
    "G2 Fall_dss_nre": 394,
    "G2 Winter_theta_nre": -0.618353576,
    "G2 Winter_pr_1_99_nre": 5,
    "G2 Winter_ss_nre": 75,
    "G2 Winter_dss_nre": 438,
    "G2 Spring_theta_nre": -0.513865745,
    "G2 Spring_pr_1_99_nre": 5,
    "G2 Spring_ss_nre": 75,
    "G2 Spring_dss_nre": 449,
    "G3 Fall_theta_nre": -0.396500906,
    "G3 Fall_pr_1_99_nre": 5,
    "G3 Fall_ss_nre": 74,
    "G3 Fall_dss_nre": 460,
    "G3 Winter_theta_nre": 0.230498848,
    "G3 Winter_pr_1_99_nre": 5,
    "G3 Winter_ss_nre": 76,
    "G3 Winter_dss_nre": 523,
    "G3 Spring_theta_nre": 0.537296542,
    "G3 Spring_pr_1_99_nre": 5,
    "G3 Spring_ss_nre": 74,
    "G3 Spring_dss_nre": 554
  },
  {
    "ID": 25,
    "KG Winter_theta_nre": -2.37459503,
    "KG Winter_pr_1_99_nre": 5,
    "KG Winter_ss_nre": 74,
    "KG Winter_dss_nre": 263,
    "KG Spring_theta_nre": -2.076246952,
    "KG Spring_pr_1_99_nre": 5,
    "KG Spring_ss_nre": 75,
    "KG Spring_dss_nre": 292,
    "G1 Fall_theta_nre": -1.834749381,
    "G1 Fall_pr_1_99_nre": 5,
    "G1 Fall_ss_nre": 75,
    "G1 Fall_dss_nre": 317,
    "G1 Winter_theta_nre": -1.516830387,
    "G1 Winter_pr_1_99_nre": 5,
    "G1 Winter_ss_nre": 75,
    "G1 Winter_dss_nre": 348,
    "G1 Spring_theta_nre": -1.102310312,
    "G1 Spring_pr_1_99_nre": 5,
    "G1 Spring_ss_nre": 76,
    "G1 Spring_dss_nre": 390,
    "G2 Fall_theta_nre": -1.038786657,
    "G2 Fall_pr_1_99_nre": 5,
    "G2 Fall_ss_nre": 75,
    "G2 Fall_dss_nre": 396,
    "G2 Winter_theta_nre": -0.594433574,
    "G2 Winter_pr_1_99_nre": 5,
    "G2 Winter_ss_nre": 75,
    "G2 Winter_dss_nre": 441,
    "G2 Spring_theta_nre": -0.513141409,
    "G2 Spring_pr_1_99_nre": 5,
    "G2 Spring_ss_nre": 75,
    "G2 Spring_dss_nre": 449,
    "G3 Fall_theta_nre": -0.313510237,
    "G3 Fall_pr_1_99_nre": 5,
    "G3 Fall_ss_nre": 75,
    "G3 Fall_dss_nre": 469,
    "G3 Winter_theta_nre": 0.233195435,
    "G3 Winter_pr_1_99_nre": 5,
    "G3 Winter_ss_nre": 76,
    "G3 Winter_dss_nre": 523,
    "G3 Spring_theta_nre": 0.548294027,
    "G3 Spring_pr_1_99_nre": 5,
    "G3 Spring_ss_nre": 75,
    "G3 Spring_dss_nre": 555
  },
  {
    "ID": 26,
    "KG Winter_theta_nre": -2.373396458,
    "KG Winter_pr_1_99_nre": 5,
    "KG Winter_ss_nre": 74,
    "KG Winter_dss_nre": 263,
    "KG Spring_theta_nre": -2.030991977,
    "KG Spring_pr_1_99_nre": 5,
    "KG Spring_ss_nre": 76,
    "KG Spring_dss_nre": 297,
    "G1 Fall_theta_nre": -1.780863882,
    "G1 Fall_pr_1_99_nre": 5,
    "G1 Fall_ss_nre": 76,
    "G1 Fall_dss_nre": 322,
    "G1 Winter_theta_nre": -1.489929162,
    "G1 Winter_pr_1_99_nre": 5,
    "G1 Winter_ss_nre": 75,
    "G1 Winter_dss_nre": 351,
    "G1 Spring_theta_nre": -1.081494751,
    "G1 Spring_pr_1_99_nre": 5,
    "G1 Spring_ss_nre": 76,
    "G1 Spring_dss_nre": 392,
    "G2 Fall_theta_nre": -1.016472634,
    "G2 Fall_pr_1_99_nre": 5,
    "G2 Fall_ss_nre": 76,
    "G2 Fall_dss_nre": 398,
    "G2 Winter_theta_nre": -0.585486937,
    "G2 Winter_pr_1_99_nre": 5,
    "G2 Winter_ss_nre": 76,
    "G2 Winter_dss_nre": 441,
    "G2 Spring_theta_nre": -0.509730629,
    "G2 Spring_pr_1_99_nre": 5,
    "G2 Spring_ss_nre": 76,
    "G2 Spring_dss_nre": 449,
    "G3 Fall_theta_nre": -0.312193526,
    "G3 Fall_pr_1_99_nre": 5,
    "G3 Fall_ss_nre": 75,
    "G3 Fall_dss_nre": 469,
    "G3 Winter_theta_nre": 0.25787387,
    "G3 Winter_pr_1_99_nre": 5,
    "G3 Winter_ss_nre": 76,
    "G3 Winter_dss_nre": 526,
    "G3 Spring_theta_nre": 0.58861787,
    "G3 Spring_pr_1_99_nre": 5,
    "G3 Spring_ss_nre": 76,
    "G3 Spring_dss_nre": 559
  },
  {
    "ID": 27,
    "KG Winter_theta_nre": -2.353131669,
    "KG Winter_pr_1_99_nre": 5,
    "KG Winter_ss_nre": 75,
    "KG Winter_dss_nre": 265,
    "KG Spring_theta_nre": -2.005287363,
    "KG Spring_pr_1_99_nre": 5,
    "KG Spring_ss_nre": 76,
    "KG Spring_dss_nre": 299,
    "G1 Fall_theta_nre": -1.768271754,
    "G1 Fall_pr_1_99_nre": 5,
    "G1 Fall_ss_nre": 76,
    "G1 Fall_dss_nre": 323,
    "G1 Winter_theta_nre": -1.489692728,
    "G1 Winter_pr_1_99_nre": 5,
    "G1 Winter_ss_nre": 75,
    "G1 Winter_dss_nre": 351,
    "G1 Spring_theta_nre": -1.079360963,
    "G1 Spring_pr_1_99_nre": 5,
    "G1 Spring_ss_nre": 77,
    "G1 Spring_dss_nre": 392,
    "G2 Fall_theta_nre": -0.986095595,
    "G2 Fall_pr_1_99_nre": 5,
    "G2 Fall_ss_nre": 76,
    "G2 Fall_dss_nre": 401,
    "G2 Winter_theta_nre": -0.579308079,
    "G2 Winter_pr_1_99_nre": 5,
    "G2 Winter_ss_nre": 76,
    "G2 Winter_dss_nre": 442,
    "G2 Spring_theta_nre": -0.504739669,
    "G2 Spring_pr_1_99_nre": 5,
    "G2 Spring_ss_nre": 76,
    "G2 Spring_dss_nre": 450,
    "G3 Fall_theta_nre": -0.296312833,
    "G3 Fall_pr_1_99_nre": 5,
    "G3 Fall_ss_nre": 76,
    "G3 Fall_dss_nre": 470,
    "G3 Winter_theta_nre": 0.264016892,
    "G3 Winter_pr_1_99_nre": 5,
    "G3 Winter_ss_nre": 76,
    "G3 Winter_dss_nre": 526,
    "G3 Spring_theta_nre": 0.590794003,
    "G3 Spring_pr_1_99_nre": 5,
    "G3 Spring_ss_nre": 76,
    "G3 Spring_dss_nre": 559
  },
  {
    "ID": 28,
    "KG Winter_theta_nre": -2.347168388,
    "KG Winter_pr_1_99_nre": 6,
    "KG Winter_ss_nre": 75,
    "KG Winter_dss_nre": 265,
    "KG Spring_theta_nre": -1.970200538,
    "KG Spring_pr_1_99_nre": 6,
    "KG Spring_ss_nre": 77,
    "KG Spring_dss_nre": 303,
    "G1 Fall_theta_nre": -1.76055164,
    "G1 Fall_pr_1_99_nre": 6,
    "G1 Fall_ss_nre": 77,
    "G1 Fall_dss_nre": 324,
    "G1 Winter_theta_nre": -1.466735713,
    "G1 Winter_pr_1_99_nre": 6,
    "G1 Winter_ss_nre": 76,
    "G1 Winter_dss_nre": 353,
    "G1 Spring_theta_nre": -1.070583519,
    "G1 Spring_pr_1_99_nre": 6,
    "G1 Spring_ss_nre": 77,
    "G1 Spring_dss_nre": 393,
    "G2 Fall_theta_nre": -0.985794338,
    "G2 Fall_pr_1_99_nre": 6,
    "G2 Fall_ss_nre": 76,
    "G2 Fall_dss_nre": 401,
    "G2 Winter_theta_nre": -0.576833785,
    "G2 Winter_pr_1_99_nre": 6,
    "G2 Winter_ss_nre": 76,
    "G2 Winter_dss_nre": 442,
    "G2 Spring_theta_nre": -0.48405075,
    "G2 Spring_pr_1_99_nre": 6,
    "G2 Spring_ss_nre": 76,
    "G2 Spring_dss_nre": 452,
    "G3 Fall_theta_nre": -0.253065765,
    "G3 Fall_pr_1_99_nre": 6,
    "G3 Fall_ss_nre": 76,
    "G3 Fall_dss_nre": 475,
    "G3 Winter_theta_nre": 0.274823728,
    "G3 Winter_pr_1_99_nre": 6,
    "G3 Winter_ss_nre": 77,
    "G3 Winter_dss_nre": 527,
    "G3 Spring_theta_nre": 0.594668215,
    "G3 Spring_pr_1_99_nre": 6,
    "G3 Spring_ss_nre": 76,
    "G3 Spring_dss_nre": 559
  },
  {
    "ID": 29,
    "KG Winter_theta_nre": -2.306806232,
    "KG Winter_pr_1_99_nre": 6,
    "KG Winter_ss_nre": 76,
    "KG Winter_dss_nre": 269,
    "KG Spring_theta_nre": -1.944562337,
    "KG Spring_pr_1_99_nre": 6,
    "KG Spring_ss_nre": 77,
    "KG Spring_dss_nre": 306,
    "G1 Fall_theta_nre": -1.75877324,
    "G1 Fall_pr_1_99_nre": 6,
    "G1 Fall_ss_nre": 77,
    "G1 Fall_dss_nre": 324,
    "G1 Winter_theta_nre": -1.461417154,
    "G1 Winter_pr_1_99_nre": 6,
    "G1 Winter_ss_nre": 76,
    "G1 Winter_dss_nre": 354,
    "G1 Spring_theta_nre": -1.062636942,
    "G1 Spring_pr_1_99_nre": 6,
    "G1 Spring_ss_nre": 77,
    "G1 Spring_dss_nre": 394,
    "G2 Fall_theta_nre": -0.981195508,
    "G2 Fall_pr_1_99_nre": 6,
    "G2 Fall_ss_nre": 76,
    "G2 Fall_dss_nre": 402,
    "G2 Winter_theta_nre": -0.561006702,
    "G2 Winter_pr_1_99_nre": 6,
    "G2 Winter_ss_nre": 76,
    "G2 Winter_dss_nre": 444,
    "G2 Spring_theta_nre": -0.482457176,
    "G2 Spring_pr_1_99_nre": 6,
    "G2 Spring_ss_nre": 76,
    "G2 Spring_dss_nre": 452,
    "G3 Fall_theta_nre": -0.2521542,
    "G3 Fall_pr_1_99_nre": 6,
    "G3 Fall_ss_nre": 76,
    "G3 Fall_dss_nre": 475,
    "G3 Winter_theta_nre": 0.305983271,
    "G3 Winter_pr_1_99_nre": 6,
    "G3 Winter_ss_nre": 77,
    "G3 Winter_dss_nre": 531,
    "G3 Spring_theta_nre": 0.597426643,
    "G3 Spring_pr_1_99_nre": 6,
    "G3 Spring_ss_nre": 76,
    "G3 Spring_dss_nre": 560
  },
  {
    "ID": 30,
    "KG Winter_theta_nre": -2.296436846,
    "KG Winter_pr_1_99_nre": 6,
    "KG Winter_ss_nre": 76,
    "KG Winter_dss_nre": 270,
    "KG Spring_theta_nre": -1.937765233,
    "KG Spring_pr_1_99_nre": 6,
    "KG Spring_ss_nre": 78,
    "KG Spring_dss_nre": 306,
    "G1 Fall_theta_nre": -1.739737276,
    "G1 Fall_pr_1_99_nre": 6,
    "G1 Fall_ss_nre": 77,
    "G1 Fall_dss_nre": 326,
    "G1 Winter_theta_nre": -1.448001053,
    "G1 Winter_pr_1_99_nre": 6,
    "G1 Winter_ss_nre": 76,
    "G1 Winter_dss_nre": 355,
    "G1 Spring_theta_nre": -1.059536394,
    "G1 Spring_pr_1_99_nre": 6,
    "G1 Spring_ss_nre": 77,
    "G1 Spring_dss_nre": 394,
    "G2 Fall_theta_nre": -0.946489772,
    "G2 Fall_pr_1_99_nre": 6,
    "G2 Fall_ss_nre": 77,
    "G2 Fall_dss_nre": 405,
    "G2 Winter_theta_nre": -0.537372989,
    "G2 Winter_pr_1_99_nre": 6,
    "G2 Winter_ss_nre": 77,
    "G2 Winter_dss_nre": 446,
    "G2 Spring_theta_nre": -0.468945358,
    "G2 Spring_pr_1_99_nre": 6,
    "G2 Spring_ss_nre": 76,
    "G2 Spring_dss_nre": 453,
    "G3 Fall_theta_nre": -0.236350238,
    "G3 Fall_pr_1_99_nre": 6,
    "G3 Fall_ss_nre": 77,
    "G3 Fall_dss_nre": 476,
    "G3 Winter_theta_nre": 0.311303478,
    "G3 Winter_pr_1_99_nre": 6,
    "G3 Winter_ss_nre": 77,
    "G3 Winter_dss_nre": 531,
    "G3 Spring_theta_nre": 0.668877918,
    "G3 Spring_pr_1_99_nre": 6,
    "G3 Spring_ss_nre": 77,
    "G3 Spring_dss_nre": 567
  },
  {
    "ID": 31,
    "KG Winter_theta_nre": -2.293334923,
    "KG Winter_pr_1_99_nre": 6,
    "KG Winter_ss_nre": 76,
    "KG Winter_dss_nre": 271,
    "KG Spring_theta_nre": -1.935699743,
    "KG Spring_pr_1_99_nre": 6,
    "KG Spring_ss_nre": 78,
    "KG Spring_dss_nre": 306,
    "G1 Fall_theta_nre": -1.729915903,
    "G1 Fall_pr_1_99_nre": 6,
    "G1 Fall_ss_nre": 77,
    "G1 Fall_dss_nre": 327,
    "G1 Winter_theta_nre": -1.415134517,
    "G1 Winter_pr_1_99_nre": 6,
    "G1 Winter_ss_nre": 77,
    "G1 Winter_dss_nre": 358,
    "G1 Spring_theta_nre": -1.055494474,
    "G1 Spring_pr_1_99_nre": 6,
    "G1 Spring_ss_nre": 77,
    "G1 Spring_dss_nre": 394,
    "G2 Fall_theta_nre": -0.944176087,
    "G2 Fall_pr_1_99_nre": 6,
    "G2 Fall_ss_nre": 77,
    "G2 Fall_dss_nre": 406,
    "G2 Winter_theta_nre": -0.521541753,
    "G2 Winter_pr_1_99_nre": 6,
    "G2 Winter_ss_nre": 77,
    "G2 Winter_dss_nre": 448,
    "G2 Spring_theta_nre": -0.465243068,
    "G2 Spring_pr_1_99_nre": 6,
    "G2 Spring_ss_nre": 76,
    "G2 Spring_dss_nre": 453,
    "G3 Fall_theta_nre": -0.234371734,
    "G3 Fall_pr_1_99_nre": 6,
    "G3 Fall_ss_nre": 77,
    "G3 Fall_dss_nre": 477,
    "G3 Winter_theta_nre": 0.321668338,
    "G3 Winter_pr_1_99_nre": 6,
    "G3 Winter_ss_nre": 78,
    "G3 Winter_dss_nre": 532,
    "G3 Spring_theta_nre": 0.675843401,
    "G3 Spring_pr_1_99_nre": 6,
    "G3 Spring_ss_nre": 77,
    "G3 Spring_dss_nre": 568
  },
  {
    "ID": 32,
    "KG Winter_theta_nre": -2.284134149,
    "KG Winter_pr_1_99_nre": 6,
    "KG Winter_ss_nre": 76,
    "KG Winter_dss_nre": 272,
    "KG Spring_theta_nre": -1.931149307,
    "KG Spring_pr_1_99_nre": 6,
    "KG Spring_ss_nre": 78,
    "KG Spring_dss_nre": 307,
    "G1 Fall_theta_nre": -1.722773929,
    "G1 Fall_pr_1_99_nre": 6,
    "G1 Fall_ss_nre": 77,
    "G1 Fall_dss_nre": 328,
    "G1 Winter_theta_nre": -1.392694553,
    "G1 Winter_pr_1_99_nre": 6,
    "G1 Winter_ss_nre": 77,
    "G1 Winter_dss_nre": 361,
    "G1 Spring_theta_nre": -1.049313354,
    "G1 Spring_pr_1_99_nre": 6,
    "G1 Spring_ss_nre": 77,
    "G1 Spring_dss_nre": 395,
    "G2 Fall_theta_nre": -0.90551038,
    "G2 Fall_pr_1_99_nre": 6,
    "G2 Fall_ss_nre": 78,
    "G2 Fall_dss_nre": 409,
    "G2 Winter_theta_nre": -0.510862011,
    "G2 Winter_pr_1_99_nre": 6,
    "G2 Winter_ss_nre": 77,
    "G2 Winter_dss_nre": 449,
    "G2 Spring_theta_nre": -0.459248342,
    "G2 Spring_pr_1_99_nre": 6,
    "G2 Spring_ss_nre": 76,
    "G2 Spring_dss_nre": 454,
    "G3 Fall_theta_nre": -0.227882692,
    "G3 Fall_pr_1_99_nre": 6,
    "G3 Fall_ss_nre": 77,
    "G3 Fall_dss_nre": 477,
    "G3 Winter_theta_nre": 0.327348758,
    "G3 Winter_pr_1_99_nre": 6,
    "G3 Winter_ss_nre": 78,
    "G3 Winter_dss_nre": 533,
    "G3 Spring_theta_nre": 0.689007727,
    "G3 Spring_pr_1_99_nre": 6,
    "G3 Spring_ss_nre": 78,
    "G3 Spring_dss_nre": 569
  },
  {
    "ID": 33,
    "KG Winter_theta_nre": -2.266137681,
    "KG Winter_pr_1_99_nre": 7,
    "KG Winter_ss_nre": 76,
    "KG Winter_dss_nre": 273,
    "KG Spring_theta_nre": -1.928064808,
    "KG Spring_pr_1_99_nre": 7,
    "KG Spring_ss_nre": 78,
    "KG Spring_dss_nre": 307,
    "G1 Fall_theta_nre": -1.716107034,
    "G1 Fall_pr_1_99_nre": 7,
    "G1 Fall_ss_nre": 77,
    "G1 Fall_dss_nre": 328,
    "G1 Winter_theta_nre": -1.379699088,
    "G1 Winter_pr_1_99_nre": 7,
    "G1 Winter_ss_nre": 77,
    "G1 Winter_dss_nre": 362,
    "G1 Spring_theta_nre": -1.04765564,
    "G1 Spring_pr_1_99_nre": 7,
    "G1 Spring_ss_nre": 77,
    "G1 Spring_dss_nre": 395,
    "G2 Fall_theta_nre": -0.900974404,
    "G2 Fall_pr_1_99_nre": 7,
    "G2 Fall_ss_nre": 78,
    "G2 Fall_dss_nre": 410,
    "G2 Winter_theta_nre": -0.485216537,
    "G2 Winter_pr_1_99_nre": 7,
    "G2 Winter_ss_nre": 78,
    "G2 Winter_dss_nre": 451,
    "G2 Spring_theta_nre": -0.45678542,
    "G2 Spring_pr_1_99_nre": 7,
    "G2 Spring_ss_nre": 76,
    "G2 Spring_dss_nre": 454,
    "G3 Fall_theta_nre": -0.223309916,
    "G3 Fall_pr_1_99_nre": 7,
    "G3 Fall_ss_nre": 77,
    "G3 Fall_dss_nre": 478,
    "G3 Winter_theta_nre": 0.331401089,
    "G3 Winter_pr_1_99_nre": 7,
    "G3 Winter_ss_nre": 78,
    "G3 Winter_dss_nre": 533,
    "G3 Spring_theta_nre": 0.69709611,
    "G3 Spring_pr_1_99_nre": 7,
    "G3 Spring_ss_nre": 78,
    "G3 Spring_dss_nre": 570
  },
  {
    "ID": 34,
    "KG Winter_theta_nre": -2.257880435,
    "KG Winter_pr_1_99_nre": 7,
    "KG Winter_ss_nre": 76,
    "KG Winter_dss_nre": 274,
    "KG Spring_theta_nre": -1.907346997,
    "KG Spring_pr_1_99_nre": 7,
    "KG Spring_ss_nre": 78,
    "KG Spring_dss_nre": 309,
    "G1 Fall_theta_nre": -1.678105425,
    "G1 Fall_pr_1_99_nre": 7,
    "G1 Fall_ss_nre": 78,
    "G1 Fall_dss_nre": 332,
    "G1 Winter_theta_nre": -1.360017585,
    "G1 Winter_pr_1_99_nre": 7,
    "G1 Winter_ss_nre": 78,
    "G1 Winter_dss_nre": 364,
    "G1 Spring_theta_nre": -0.994089052,
    "G1 Spring_pr_1_99_nre": 7,
    "G1 Spring_ss_nre": 78,
    "G1 Spring_dss_nre": 401,
    "G2 Fall_theta_nre": -0.858336127,
    "G2 Fall_pr_1_99_nre": 7,
    "G2 Fall_ss_nre": 79,
    "G2 Fall_dss_nre": 414,
    "G2 Winter_theta_nre": -0.485182164,
    "G2 Winter_pr_1_99_nre": 7,
    "G2 Winter_ss_nre": 78,
    "G2 Winter_dss_nre": 451,
    "G2 Spring_theta_nre": -0.448287823,
    "G2 Spring_pr_1_99_nre": 7,
    "G2 Spring_ss_nre": 77,
    "G2 Spring_dss_nre": 455,
    "G3 Fall_theta_nre": -0.220454468,
    "G3 Fall_pr_1_99_nre": 7,
    "G3 Fall_ss_nre": 77,
    "G3 Fall_dss_nre": 478,
    "G3 Winter_theta_nre": 0.334712684,
    "G3 Winter_pr_1_99_nre": 7,
    "G3 Winter_ss_nre": 78,
    "G3 Winter_dss_nre": 533,
    "G3 Spring_theta_nre": 0.699444754,
    "G3 Spring_pr_1_99_nre": 7,
    "G3 Spring_ss_nre": 78,
    "G3 Spring_dss_nre": 570
  },
  {
    "ID": 35,
    "KG Winter_theta_nre": -2.254155703,
    "KG Winter_pr_1_99_nre": 7,
    "KG Winter_ss_nre": 77,
    "KG Winter_dss_nre": 275,
    "KG Spring_theta_nre": -1.90594455,
    "KG Spring_pr_1_99_nre": 7,
    "KG Spring_ss_nre": 78,
    "KG Spring_dss_nre": 309,
    "G1 Fall_theta_nre": -1.662010404,
    "G1 Fall_pr_1_99_nre": 7,
    "G1 Fall_ss_nre": 78,
    "G1 Fall_dss_nre": 334,
    "G1 Winter_theta_nre": -1.356548809,
    "G1 Winter_pr_1_99_nre": 7,
    "G1 Winter_ss_nre": 78,
    "G1 Winter_dss_nre": 364,
    "G1 Spring_theta_nre": -0.990179127,
    "G1 Spring_pr_1_99_nre": 7,
    "G1 Spring_ss_nre": 78,
    "G1 Spring_dss_nre": 401,
    "G2 Fall_theta_nre": -0.855998428,
    "G2 Fall_pr_1_99_nre": 7,
    "G2 Fall_ss_nre": 79,
    "G2 Fall_dss_nre": 414,
    "G2 Winter_theta_nre": -0.477820348,
    "G2 Winter_pr_1_99_nre": 7,
    "G2 Winter_ss_nre": 78,
    "G2 Winter_dss_nre": 452,
    "G2 Spring_theta_nre": -0.447311408,
    "G2 Spring_pr_1_99_nre": 7,
    "G2 Spring_ss_nre": 77,
    "G2 Spring_dss_nre": 455,
    "G3 Fall_theta_nre": -0.205712173,
    "G3 Fall_pr_1_99_nre": 7,
    "G3 Fall_ss_nre": 77,
    "G3 Fall_dss_nre": 479,
    "G3 Winter_theta_nre": 0.343153018,
    "G3 Winter_pr_1_99_nre": 7,
    "G3 Winter_ss_nre": 78,
    "G3 Winter_dss_nre": 534,
    "G3 Spring_theta_nre": 0.701542875,
    "G3 Spring_pr_1_99_nre": 7,
    "G3 Spring_ss_nre": 78,
    "G3 Spring_dss_nre": 570
  },
  {
    "ID": 36,
    "KG Winter_theta_nre": -2.244358921,
    "KG Winter_pr_1_99_nre": 7,
    "KG Winter_ss_nre": 77,
    "KG Winter_dss_nre": 276,
    "KG Spring_theta_nre": -1.886725859,
    "KG Spring_pr_1_99_nre": 7,
    "KG Spring_ss_nre": 78,
    "KG Spring_dss_nre": 311,
    "G1 Fall_theta_nre": -1.657493437,
    "G1 Fall_pr_1_99_nre": 7,
    "G1 Fall_ss_nre": 78,
    "G1 Fall_dss_nre": 334,
    "G1 Winter_theta_nre": -1.322154895,
    "G1 Winter_pr_1_99_nre": 7,
    "G1 Winter_ss_nre": 78,
    "G1 Winter_dss_nre": 368,
    "G1 Spring_theta_nre": -0.964333369,
    "G1 Spring_pr_1_99_nre": 7,
    "G1 Spring_ss_nre": 79,
    "G1 Spring_dss_nre": 404,
    "G2 Fall_theta_nre": -0.820181867,
    "G2 Fall_pr_1_99_nre": 7,
    "G2 Fall_ss_nre": 79,
    "G2 Fall_dss_nre": 418,
    "G2 Winter_theta_nre": -0.471683186,
    "G2 Winter_pr_1_99_nre": 7,
    "G2 Winter_ss_nre": 78,
    "G2 Winter_dss_nre": 453,
    "G2 Spring_theta_nre": -0.438362922,
    "G2 Spring_pr_1_99_nre": 7,
    "G2 Spring_ss_nre": 77,
    "G2 Spring_dss_nre": 456,
    "G3 Fall_theta_nre": -0.193212521,
    "G3 Fall_pr_1_99_nre": 7,
    "G3 Fall_ss_nre": 77,
    "G3 Fall_dss_nre": 481,
    "G3 Winter_theta_nre": 0.353812981,
    "G3 Winter_pr_1_99_nre": 7,
    "G3 Winter_ss_nre": 78,
    "G3 Winter_dss_nre": 535,
    "G3 Spring_theta_nre": 0.723415528,
    "G3 Spring_pr_1_99_nre": 7,
    "G3 Spring_ss_nre": 78,
    "G3 Spring_dss_nre": 572
  },
  {
    "ID": 37,
    "KG Winter_theta_nre": -2.227855901,
    "KG Winter_pr_1_99_nre": 7,
    "KG Winter_ss_nre": 77,
    "KG Winter_dss_nre": 277,
    "KG Spring_theta_nre": -1.880468818,
    "KG Spring_pr_1_99_nre": 7,
    "KG Spring_ss_nre": 78,
    "KG Spring_dss_nre": 312,
    "G1 Fall_theta_nre": -1.622862828,
    "G1 Fall_pr_1_99_nre": 7,
    "G1 Fall_ss_nre": 79,
    "G1 Fall_dss_nre": 338,
    "G1 Winter_theta_nre": -1.321067399,
    "G1 Winter_pr_1_99_nre": 7,
    "G1 Winter_ss_nre": 78,
    "G1 Winter_dss_nre": 368,
    "G1 Spring_theta_nre": -0.955300052,
    "G1 Spring_pr_1_99_nre": 7,
    "G1 Spring_ss_nre": 79,
    "G1 Spring_dss_nre": 404,
    "G2 Fall_theta_nre": -0.819233616,
    "G2 Fall_pr_1_99_nre": 7,
    "G2 Fall_ss_nre": 79,
    "G2 Fall_dss_nre": 418,
    "G2 Winter_theta_nre": -0.462102389,
    "G2 Winter_pr_1_99_nre": 7,
    "G2 Winter_ss_nre": 78,
    "G2 Winter_dss_nre": 454,
    "G2 Spring_theta_nre": -0.387439256,
    "G2 Spring_pr_1_99_nre": 7,
    "G2 Spring_ss_nre": 78,
    "G2 Spring_dss_nre": 461,
    "G3 Fall_theta_nre": -0.176913235,
    "G3 Fall_pr_1_99_nre": 7,
    "G3 Fall_ss_nre": 78,
    "G3 Fall_dss_nre": 482,
    "G3 Winter_theta_nre": 0.374316315,
    "G3 Winter_pr_1_99_nre": 7,
    "G3 Winter_ss_nre": 79,
    "G3 Winter_dss_nre": 537,
    "G3 Spring_theta_nre": 0.73035178,
    "G3 Spring_pr_1_99_nre": 7,
    "G3 Spring_ss_nre": 79,
    "G3 Spring_dss_nre": 573
  },
  {
    "ID": 38,
    "KG Winter_theta_nre": -2.223949047,
    "KG Winter_pr_1_99_nre": 8,
    "KG Winter_ss_nre": 77,
    "KG Winter_dss_nre": 278,
    "KG Spring_theta_nre": -1.87383707,
    "KG Spring_pr_1_99_nre": 8,
    "KG Spring_ss_nre": 79,
    "KG Spring_dss_nre": 313,
    "G1 Fall_theta_nre": -1.62203067,
    "G1 Fall_pr_1_99_nre": 8,
    "G1 Fall_ss_nre": 79,
    "G1 Fall_dss_nre": 338,
    "G1 Winter_theta_nre": -1.295084541,
    "G1 Winter_pr_1_99_nre": 8,
    "G1 Winter_ss_nre": 79,
    "G1 Winter_dss_nre": 370,
    "G1 Spring_theta_nre": -0.952026902,
    "G1 Spring_pr_1_99_nre": 8,
    "G1 Spring_ss_nre": 79,
    "G1 Spring_dss_nre": 405,
    "G2 Fall_theta_nre": -0.813471143,
    "G2 Fall_pr_1_99_nre": 8,
    "G2 Fall_ss_nre": 79,
    "G2 Fall_dss_nre": 419,
    "G2 Winter_theta_nre": -0.457485192,
    "G2 Winter_pr_1_99_nre": 8,
    "G2 Winter_ss_nre": 78,
    "G2 Winter_dss_nre": 454,
    "G2 Spring_theta_nre": -0.383923277,
    "G2 Spring_pr_1_99_nre": 8,
    "G2 Spring_ss_nre": 78,
    "G2 Spring_dss_nre": 462,
    "G3 Fall_theta_nre": -0.156808192,
    "G3 Fall_pr_1_99_nre": 8,
    "G3 Fall_ss_nre": 78,
    "G3 Fall_dss_nre": 484,
    "G3 Winter_theta_nre": 0.376782822,
    "G3 Winter_pr_1_99_nre": 8,
    "G3 Winter_ss_nre": 79,
    "G3 Winter_dss_nre": 538,
    "G3 Spring_theta_nre": 0.746513677,
    "G3 Spring_pr_1_99_nre": 8,
    "G3 Spring_ss_nre": 79,
    "G3 Spring_dss_nre": 575
  },
  {
    "ID": 39,
    "KG Winter_theta_nre": -2.220106361,
    "KG Winter_pr_1_99_nre": 8,
    "KG Winter_ss_nre": 77,
    "KG Winter_dss_nre": 278,
    "KG Spring_theta_nre": -1.848275353,
    "KG Spring_pr_1_99_nre": 8,
    "KG Spring_ss_nre": 79,
    "KG Spring_dss_nre": 315,
    "G1 Fall_theta_nre": -1.613331207,
    "G1 Fall_pr_1_99_nre": 8,
    "G1 Fall_ss_nre": 79,
    "G1 Fall_dss_nre": 339,
    "G1 Winter_theta_nre": -1.279825747,
    "G1 Winter_pr_1_99_nre": 8,
    "G1 Winter_ss_nre": 79,
    "G1 Winter_dss_nre": 372,
    "G1 Spring_theta_nre": -0.950522898,
    "G1 Spring_pr_1_99_nre": 8,
    "G1 Spring_ss_nre": 79,
    "G1 Spring_dss_nre": 405,
    "G2 Fall_theta_nre": -0.811502432,
    "G2 Fall_pr_1_99_nre": 8,
    "G2 Fall_ss_nre": 79,
    "G2 Fall_dss_nre": 419,
    "G2 Winter_theta_nre": -0.440839041,
    "G2 Winter_pr_1_99_nre": 8,
    "G2 Winter_ss_nre": 78,
    "G2 Winter_dss_nre": 456,
    "G2 Spring_theta_nre": -0.37254046,
    "G2 Spring_pr_1_99_nre": 8,
    "G2 Spring_ss_nre": 78,
    "G2 Spring_dss_nre": 463,
    "G3 Fall_theta_nre": -0.151178079,
    "G3 Fall_pr_1_99_nre": 8,
    "G3 Fall_ss_nre": 78,
    "G3 Fall_dss_nre": 485,
    "G3 Winter_theta_nre": 0.380601538,
    "G3 Winter_pr_1_99_nre": 8,
    "G3 Winter_ss_nre": 79,
    "G3 Winter_dss_nre": 538,
    "G3 Spring_theta_nre": 0.76374547,
    "G3 Spring_pr_1_99_nre": 8,
    "G3 Spring_ss_nre": 79,
    "G3 Spring_dss_nre": 576
  },
  {
    "ID": 40,
    "KG Winter_theta_nre": -2.204142984,
    "KG Winter_pr_1_99_nre": 8,
    "KG Winter_ss_nre": 77,
    "KG Winter_dss_nre": 280,
    "KG Spring_theta_nre": -1.832383427,
    "KG Spring_pr_1_99_nre": 8,
    "KG Spring_ss_nre": 79,
    "KG Spring_dss_nre": 317,
    "G1 Fall_theta_nre": -1.612162401,
    "G1 Fall_pr_1_99_nre": 8,
    "G1 Fall_ss_nre": 79,
    "G1 Fall_dss_nre": 339,
    "G1 Winter_theta_nre": -1.267714355,
    "G1 Winter_pr_1_99_nre": 8,
    "G1 Winter_ss_nre": 79,
    "G1 Winter_dss_nre": 373,
    "G1 Spring_theta_nre": -0.927366391,
    "G1 Spring_pr_1_99_nre": 8,
    "G1 Spring_ss_nre": 79,
    "G1 Spring_dss_nre": 407,
    "G2 Fall_theta_nre": -0.807050109,
    "G2 Fall_pr_1_99_nre": 8,
    "G2 Fall_ss_nre": 79,
    "G2 Fall_dss_nre": 419,
    "G2 Winter_theta_nre": -0.426915239,
    "G2 Winter_pr_1_99_nre": 8,
    "G2 Winter_ss_nre": 79,
    "G2 Winter_dss_nre": 457,
    "G2 Spring_theta_nre": -0.360566808,
    "G2 Spring_pr_1_99_nre": 8,
    "G2 Spring_ss_nre": 78,
    "G2 Spring_dss_nre": 464,
    "G3 Fall_theta_nre": -0.147295091,
    "G3 Fall_pr_1_99_nre": 8,
    "G3 Fall_ss_nre": 78,
    "G3 Fall_dss_nre": 485,
    "G3 Winter_theta_nre": 0.382794472,
    "G3 Winter_pr_1_99_nre": 8,
    "G3 Winter_ss_nre": 79,
    "G3 Winter_dss_nre": 538,
    "G3 Spring_theta_nre": 0.796361773,
    "G3 Spring_pr_1_99_nre": 8,
    "G3 Spring_ss_nre": 80,
    "G3 Spring_dss_nre": 580
  },
  {
    "ID": 41,
    "KG Winter_theta_nre": -2.20167875,
    "KG Winter_pr_1_99_nre": 8,
    "KG Winter_ss_nre": 77,
    "KG Winter_dss_nre": 280,
    "KG Spring_theta_nre": -1.830260018,
    "KG Spring_pr_1_99_nre": 8,
    "KG Spring_ss_nre": 79,
    "KG Spring_dss_nre": 317,
    "G1 Fall_theta_nre": -1.610469488,
    "G1 Fall_pr_1_99_nre": 8,
    "G1 Fall_ss_nre": 79,
    "G1 Fall_dss_nre": 339,
    "G1 Winter_theta_nre": -1.249495286,
    "G1 Winter_pr_1_99_nre": 8,
    "G1 Winter_ss_nre": 80,
    "G1 Winter_dss_nre": 375,
    "G1 Spring_theta_nre": -0.911780651,
    "G1 Spring_pr_1_99_nre": 8,
    "G1 Spring_ss_nre": 80,
    "G1 Spring_dss_nre": 409,
    "G2 Fall_theta_nre": -0.801165074,
    "G2 Fall_pr_1_99_nre": 8,
    "G2 Fall_ss_nre": 80,
    "G2 Fall_dss_nre": 420,
    "G2 Winter_theta_nre": -0.426235651,
    "G2 Winter_pr_1_99_nre": 8,
    "G2 Winter_ss_nre": 79,
    "G2 Winter_dss_nre": 457,
    "G2 Spring_theta_nre": -0.358319138,
    "G2 Spring_pr_1_99_nre": 8,
    "G2 Spring_ss_nre": 78,
    "G2 Spring_dss_nre": 464,
    "G3 Fall_theta_nre": -0.129429168,
    "G3 Fall_pr_1_99_nre": 8,
    "G3 Fall_ss_nre": 78,
    "G3 Fall_dss_nre": 487,
    "G3 Winter_theta_nre": 0.384284783,
    "G3 Winter_pr_1_99_nre": 8,
    "G3 Winter_ss_nre": 79,
    "G3 Winter_dss_nre": 538,
    "G3 Spring_theta_nre": 0.805837236,
    "G3 Spring_pr_1_99_nre": 8,
    "G3 Spring_ss_nre": 80,
    "G3 Spring_dss_nre": 581
  },
  {
    "ID": 42,
    "KG Winter_theta_nre": -2.198657077,
    "KG Winter_pr_1_99_nre": 8,
    "KG Winter_ss_nre": 77,
    "KG Winter_dss_nre": 280,
    "KG Spring_theta_nre": -1.828157435,
    "KG Spring_pr_1_99_nre": 8,
    "KG Spring_ss_nre": 79,
    "KG Spring_dss_nre": 317,
    "G1 Fall_theta_nre": -1.59500088,
    "G1 Fall_pr_1_99_nre": 8,
    "G1 Fall_ss_nre": 79,
    "G1 Fall_dss_nre": 340,
    "G1 Winter_theta_nre": -1.22900183,
    "G1 Winter_pr_1_99_nre": 8,
    "G1 Winter_ss_nre": 80,
    "G1 Winter_dss_nre": 377,
    "G1 Spring_theta_nre": -0.893448076,
    "G1 Spring_pr_1_99_nre": 8,
    "G1 Spring_ss_nre": 80,
    "G1 Spring_dss_nre": 411,
    "G2 Fall_theta_nre": -0.799679373,
    "G2 Fall_pr_1_99_nre": 8,
    "G2 Fall_ss_nre": 80,
    "G2 Fall_dss_nre": 420,
    "G2 Winter_theta_nre": -0.426138236,
    "G2 Winter_pr_1_99_nre": 8,
    "G2 Winter_ss_nre": 79,
    "G2 Winter_dss_nre": 457,
    "G2 Spring_theta_nre": -0.354560284,
    "G2 Spring_pr_1_99_nre": 8,
    "G2 Spring_ss_nre": 78,
    "G2 Spring_dss_nre": 465,
    "G3 Fall_theta_nre": -0.116038075,
    "G3 Fall_pr_1_99_nre": 8,
    "G3 Fall_ss_nre": 79,
    "G3 Fall_dss_nre": 488,
    "G3 Winter_theta_nre": 0.38651331,
    "G3 Winter_pr_1_99_nre": 8,
    "G3 Winter_ss_nre": 79,
    "G3 Winter_dss_nre": 539,
    "G3 Spring_theta_nre": 0.811406177,
    "G3 Spring_pr_1_99_nre": 8,
    "G3 Spring_ss_nre": 80,
    "G3 Spring_dss_nre": 581
  },
  {
    "ID": 43,
    "KG Winter_theta_nre": -2.14217274,
    "KG Winter_pr_1_99_nre": 9,
    "KG Winter_ss_nre": 78,
    "KG Winter_dss_nre": 286,
    "KG Spring_theta_nre": -1.826455164,
    "KG Spring_pr_1_99_nre": 9,
    "KG Spring_ss_nre": 79,
    "KG Spring_dss_nre": 317,
    "G1 Fall_theta_nre": -1.573321722,
    "G1 Fall_pr_1_99_nre": 9,
    "G1 Fall_ss_nre": 80,
    "G1 Fall_dss_nre": 343,
    "G1 Winter_theta_nre": -1.227784086,
    "G1 Winter_pr_1_99_nre": 9,
    "G1 Winter_ss_nre": 80,
    "G1 Winter_dss_nre": 377,
    "G1 Spring_theta_nre": -0.891923322,
    "G1 Spring_pr_1_99_nre": 9,
    "G1 Spring_ss_nre": 80,
    "G1 Spring_dss_nre": 411,
    "G2 Fall_theta_nre": -0.779111936,
    "G2 Fall_pr_1_99_nre": 9,
    "G2 Fall_ss_nre": 80,
    "G2 Fall_dss_nre": 422,
    "G2 Winter_theta_nre": -0.394412848,
    "G2 Winter_pr_1_99_nre": 9,
    "G2 Winter_ss_nre": 79,
    "G2 Winter_dss_nre": 461,
    "G2 Spring_theta_nre": -0.347937923,
    "G2 Spring_pr_1_99_nre": 9,
    "G2 Spring_ss_nre": 78,
    "G2 Spring_dss_nre": 465,
    "G3 Fall_theta_nre": -0.113357538,
    "G3 Fall_pr_1_99_nre": 9,
    "G3 Fall_ss_nre": 79,
    "G3 Fall_dss_nre": 489,
    "G3 Winter_theta_nre": 0.415475847,
    "G3 Winter_pr_1_99_nre": 9,
    "G3 Winter_ss_nre": 80,
    "G3 Winter_dss_nre": 542,
    "G3 Spring_theta_nre": 0.812689536,
    "G3 Spring_pr_1_99_nre": 9,
    "G3 Spring_ss_nre": 80,
    "G3 Spring_dss_nre": 581
  },
  {
    "ID": 44,
    "KG Winter_theta_nre": -2.141519217,
    "KG Winter_pr_1_99_nre": 9,
    "KG Winter_ss_nre": 79,
    "KG Winter_dss_nre": 286,
    "KG Spring_theta_nre": -1.816667089,
    "KG Spring_pr_1_99_nre": 9,
    "KG Spring_ss_nre": 80,
    "KG Spring_dss_nre": 318,
    "G1 Fall_theta_nre": -1.570447697,
    "G1 Fall_pr_1_99_nre": 9,
    "G1 Fall_ss_nre": 80,
    "G1 Fall_dss_nre": 343,
    "G1 Winter_theta_nre": -1.222265882,
    "G1 Winter_pr_1_99_nre": 9,
    "G1 Winter_ss_nre": 80,
    "G1 Winter_dss_nre": 378,
    "G1 Spring_theta_nre": -0.891114082,
    "G1 Spring_pr_1_99_nre": 9,
    "G1 Spring_ss_nre": 80,
    "G1 Spring_dss_nre": 411,
    "G2 Fall_theta_nre": -0.769782202,
    "G2 Fall_pr_1_99_nre": 9,
    "G2 Fall_ss_nre": 80,
    "G2 Fall_dss_nre": 423,
    "G2 Winter_theta_nre": -0.39391054,
    "G2 Winter_pr_1_99_nre": 9,
    "G2 Winter_ss_nre": 79,
    "G2 Winter_dss_nre": 461,
    "G2 Spring_theta_nre": -0.341570432,
    "G2 Spring_pr_1_99_nre": 9,
    "G2 Spring_ss_nre": 78,
    "G2 Spring_dss_nre": 466,
    "G3 Fall_theta_nre": -0.078931059,
    "G3 Fall_pr_1_99_nre": 9,
    "G3 Fall_ss_nre": 79,
    "G3 Fall_dss_nre": 492,
    "G3 Winter_theta_nre": 0.416183423,
    "G3 Winter_pr_1_99_nre": 9,
    "G3 Winter_ss_nre": 80,
    "G3 Winter_dss_nre": 542,
    "G3 Spring_theta_nre": 0.824848495,
    "G3 Spring_pr_1_99_nre": 9,
    "G3 Spring_ss_nre": 81,
    "G3 Spring_dss_nre": 582
  },
  {
    "ID": 45,
    "KG Winter_theta_nre": -2.135971227,
    "KG Winter_pr_1_99_nre": 9,
    "KG Winter_ss_nre": 79,
    "KG Winter_dss_nre": 286,
    "KG Spring_theta_nre": -1.812989241,
    "KG Spring_pr_1_99_nre": 9,
    "KG Spring_ss_nre": 80,
    "KG Spring_dss_nre": 319,
    "G1 Fall_theta_nre": -1.565966537,
    "G1 Fall_pr_1_99_nre": 9,
    "G1 Fall_ss_nre": 80,
    "G1 Fall_dss_nre": 343,
    "G1 Winter_theta_nre": -1.219148228,
    "G1 Winter_pr_1_99_nre": 9,
    "G1 Winter_ss_nre": 80,
    "G1 Winter_dss_nre": 378,
    "G1 Spring_theta_nre": -0.881030385,
    "G1 Spring_pr_1_99_nre": 9,
    "G1 Spring_ss_nre": 80,
    "G1 Spring_dss_nre": 412,
    "G2 Fall_theta_nre": -0.757668238,
    "G2 Fall_pr_1_99_nre": 9,
    "G2 Fall_ss_nre": 80,
    "G2 Fall_dss_nre": 424,
    "G2 Winter_theta_nre": -0.389112646,
    "G2 Winter_pr_1_99_nre": 9,
    "G2 Winter_ss_nre": 80,
    "G2 Winter_dss_nre": 461,
    "G2 Spring_theta_nre": -0.305575439,
    "G2 Spring_pr_1_99_nre": 9,
    "G2 Spring_ss_nre": 79,
    "G2 Spring_dss_nre": 469,
    "G3 Fall_theta_nre": -0.047163775,
    "G3 Fall_pr_1_99_nre": 9,
    "G3 Fall_ss_nre": 80,
    "G3 Fall_dss_nre": 495,
    "G3 Winter_theta_nre": 0.416246605,
    "G3 Winter_pr_1_99_nre": 9,
    "G3 Winter_ss_nre": 80,
    "G3 Winter_dss_nre": 542,
    "G3 Spring_theta_nre": 0.825135636,
    "G3 Spring_pr_1_99_nre": 9,
    "G3 Spring_ss_nre": 81,
    "G3 Spring_dss_nre": 583
  },
  {
    "ID": 46,
    "KG Winter_theta_nre": -2.121092234,
    "KG Winter_pr_1_99_nre": 9,
    "KG Winter_ss_nre": 79,
    "KG Winter_dss_nre": 288,
    "KG Spring_theta_nre": -1.812526455,
    "KG Spring_pr_1_99_nre": 9,
    "KG Spring_ss_nre": 80,
    "KG Spring_dss_nre": 319,
    "G1 Fall_theta_nre": -1.564453725,
    "G1 Fall_pr_1_99_nre": 9,
    "G1 Fall_ss_nre": 80,
    "G1 Fall_dss_nre": 344,
    "G1 Winter_theta_nre": -1.208200936,
    "G1 Winter_pr_1_99_nre": 9,
    "G1 Winter_ss_nre": 80,
    "G1 Winter_dss_nre": 379,
    "G1 Spring_theta_nre": -0.880209149,
    "G1 Spring_pr_1_99_nre": 9,
    "G1 Spring_ss_nre": 80,
    "G1 Spring_dss_nre": 412,
    "G2 Fall_theta_nre": -0.757103903,
    "G2 Fall_pr_1_99_nre": 9,
    "G2 Fall_ss_nre": 80,
    "G2 Fall_dss_nre": 424,
    "G2 Winter_theta_nre": -0.384818926,
    "G2 Winter_pr_1_99_nre": 9,
    "G2 Winter_ss_nre": 80,
    "G2 Winter_dss_nre": 462,
    "G2 Spring_theta_nre": -0.301021763,
    "G2 Spring_pr_1_99_nre": 9,
    "G2 Spring_ss_nre": 79,
    "G2 Spring_dss_nre": 470,
    "G3 Fall_theta_nre": -0.021298686,
    "G3 Fall_pr_1_99_nre": 9,
    "G3 Fall_ss_nre": 80,
    "G3 Fall_dss_nre": 498,
    "G3 Winter_theta_nre": 0.428066108,
    "G3 Winter_pr_1_99_nre": 9,
    "G3 Winter_ss_nre": 80,
    "G3 Winter_dss_nre": 543,
    "G3 Spring_theta_nre": 0.827374994,
    "G3 Spring_pr_1_99_nre": 9,
    "G3 Spring_ss_nre": 81,
    "G3 Spring_dss_nre": 583
  },
  {
    "ID": 47,
    "KG Winter_theta_nre": -2.103464773,
    "KG Winter_pr_1_99_nre": 9,
    "KG Winter_ss_nre": 79,
    "KG Winter_dss_nre": 290,
    "KG Spring_theta_nre": -1.798800598,
    "KG Spring_pr_1_99_nre": 9,
    "KG Spring_ss_nre": 80,
    "KG Spring_dss_nre": 320,
    "G1 Fall_theta_nre": -1.557353297,
    "G1 Fall_pr_1_99_nre": 9,
    "G1 Fall_ss_nre": 80,
    "G1 Fall_dss_nre": 344,
    "G1 Winter_theta_nre": -1.20526749,
    "G1 Winter_pr_1_99_nre": 9,
    "G1 Winter_ss_nre": 80,
    "G1 Winter_dss_nre": 379,
    "G1 Spring_theta_nre": -0.877754676,
    "G1 Spring_pr_1_99_nre": 9,
    "G1 Spring_ss_nre": 80,
    "G1 Spring_dss_nre": 412,
    "G2 Fall_theta_nre": -0.749926789,
    "G2 Fall_pr_1_99_nre": 9,
    "G2 Fall_ss_nre": 80,
    "G2 Fall_dss_nre": 425,
    "G2 Winter_theta_nre": -0.377306544,
    "G2 Winter_pr_1_99_nre": 9,
    "G2 Winter_ss_nre": 80,
    "G2 Winter_dss_nre": 462,
    "G2 Spring_theta_nre": -0.287883289,
    "G2 Spring_pr_1_99_nre": 9,
    "G2 Spring_ss_nre": 79,
    "G2 Spring_dss_nre": 471,
    "G3 Fall_theta_nre": -0.013853321,
    "G3 Fall_pr_1_99_nre": 9,
    "G3 Fall_ss_nre": 80,
    "G3 Fall_dss_nre": 499,
    "G3 Winter_theta_nre": 0.429975862,
    "G3 Winter_pr_1_99_nre": 9,
    "G3 Winter_ss_nre": 80,
    "G3 Winter_dss_nre": 543,
    "G3 Spring_theta_nre": 0.841961238,
    "G3 Spring_pr_1_99_nre": 9,
    "G3 Spring_ss_nre": 81,
    "G3 Spring_dss_nre": 584
  },
  {
    "ID": 48,
    "KG Winter_theta_nre": -2.093429849,
    "KG Winter_pr_1_99_nre": 10,
    "KG Winter_ss_nre": 79,
    "KG Winter_dss_nre": 291,
    "KG Spring_theta_nre": -1.788065536,
    "KG Spring_pr_1_99_nre": 10,
    "KG Spring_ss_nre": 80,
    "KG Spring_dss_nre": 321,
    "G1 Fall_theta_nre": -1.556991861,
    "G1 Fall_pr_1_99_nre": 10,
    "G1 Fall_ss_nre": 80,
    "G1 Fall_dss_nre": 344,
    "G1 Winter_theta_nre": -1.199005986,
    "G1 Winter_pr_1_99_nre": 10,
    "G1 Winter_ss_nre": 81,
    "G1 Winter_dss_nre": 380,
    "G1 Spring_theta_nre": -0.870106842,
    "G1 Spring_pr_1_99_nre": 10,
    "G1 Spring_ss_nre": 80,
    "G1 Spring_dss_nre": 413,
    "G2 Fall_theta_nre": -0.748548177,
    "G2 Fall_pr_1_99_nre": 10,
    "G2 Fall_ss_nre": 80,
    "G2 Fall_dss_nre": 425,
    "G2 Winter_theta_nre": -0.372838808,
    "G2 Winter_pr_1_99_nre": 10,
    "G2 Winter_ss_nre": 80,
    "G2 Winter_dss_nre": 463,
    "G2 Spring_theta_nre": -0.284684422,
    "G2 Spring_pr_1_99_nre": 10,
    "G2 Spring_ss_nre": 79,
    "G2 Spring_dss_nre": 472,
    "G3 Fall_theta_nre": -0.009665301,
    "G3 Fall_pr_1_99_nre": 10,
    "G3 Fall_ss_nre": 80,
    "G3 Fall_dss_nre": 499,
    "G3 Winter_theta_nre": 0.440857348,
    "G3 Winter_pr_1_99_nre": 10,
    "G3 Winter_ss_nre": 80,
    "G3 Winter_dss_nre": 544,
    "G3 Spring_theta_nre": 0.843987358,
    "G3 Spring_pr_1_99_nre": 10,
    "G3 Spring_ss_nre": 81,
    "G3 Spring_dss_nre": 584
  },
  {
    "ID": 49,
    "KG Winter_theta_nre": -2.082102205,
    "KG Winter_pr_1_99_nre": 10,
    "KG Winter_ss_nre": 80,
    "KG Winter_dss_nre": 292,
    "KG Spring_theta_nre": -1.780828362,
    "KG Spring_pr_1_99_nre": 10,
    "KG Spring_ss_nre": 80,
    "KG Spring_dss_nre": 322,
    "G1 Fall_theta_nre": -1.543062951,
    "G1 Fall_pr_1_99_nre": 10,
    "G1 Fall_ss_nre": 80,
    "G1 Fall_dss_nre": 346,
    "G1 Winter_theta_nre": -1.177825656,
    "G1 Winter_pr_1_99_nre": 10,
    "G1 Winter_ss_nre": 81,
    "G1 Winter_dss_nre": 382,
    "G1 Spring_theta_nre": -0.864962638,
    "G1 Spring_pr_1_99_nre": 10,
    "G1 Spring_ss_nre": 81,
    "G1 Spring_dss_nre": 414,
    "G2 Fall_theta_nre": -0.747938911,
    "G2 Fall_pr_1_99_nre": 10,
    "G2 Fall_ss_nre": 80,
    "G2 Fall_dss_nre": 425,
    "G2 Winter_theta_nre": -0.357779332,
    "G2 Winter_pr_1_99_nre": 10,
    "G2 Winter_ss_nre": 80,
    "G2 Winter_dss_nre": 464,
    "G2 Spring_theta_nre": -0.280949492,
    "G2 Spring_pr_1_99_nre": 10,
    "G2 Spring_ss_nre": 79,
    "G2 Spring_dss_nre": 472,
    "G3 Fall_theta_nre": -0.008112724,
    "G3 Fall_pr_1_99_nre": 10,
    "G3 Fall_ss_nre": 80,
    "G3 Fall_dss_nre": 499,
    "G3 Winter_theta_nre": 0.446508714,
    "G3 Winter_pr_1_99_nre": 10,
    "G3 Winter_ss_nre": 80,
    "G3 Winter_dss_nre": 545,
    "G3 Spring_theta_nre": 0.856484278,
    "G3 Spring_pr_1_99_nre": 10,
    "G3 Spring_ss_nre": 81,
    "G3 Spring_dss_nre": 586
  },
  {
    "ID": 50,
    "KG Winter_theta_nre": -2.069083123,
    "KG Winter_pr_1_99_nre": 10,
    "KG Winter_ss_nre": 80,
    "KG Winter_dss_nre": 293,
    "KG Spring_theta_nre": -1.770309649,
    "KG Spring_pr_1_99_nre": 10,
    "KG Spring_ss_nre": 80,
    "KG Spring_dss_nre": 323,
    "G1 Fall_theta_nre": -1.536517814,
    "G1 Fall_pr_1_99_nre": 10,
    "G1 Fall_ss_nre": 80,
    "G1 Fall_dss_nre": 346,
    "G1 Winter_theta_nre": -1.174004684,
    "G1 Winter_pr_1_99_nre": 10,
    "G1 Winter_ss_nre": 81,
    "G1 Winter_dss_nre": 383,
    "G1 Spring_theta_nre": -0.85160643,
    "G1 Spring_pr_1_99_nre": 10,
    "G1 Spring_ss_nre": 81,
    "G1 Spring_dss_nre": 415,
    "G2 Fall_theta_nre": -0.731286218,
    "G2 Fall_pr_1_99_nre": 10,
    "G2 Fall_ss_nre": 81,
    "G2 Fall_dss_nre": 427,
    "G2 Winter_theta_nre": -0.341787946,
    "G2 Winter_pr_1_99_nre": 10,
    "G2 Winter_ss_nre": 80,
    "G2 Winter_dss_nre": 466,
    "G2 Spring_theta_nre": -0.279769979,
    "G2 Spring_pr_1_99_nre": 10,
    "G2 Spring_ss_nre": 79,
    "G2 Spring_dss_nre": 472,
    "G3 Fall_theta_nre": -0.00080958,
    "G3 Fall_pr_1_99_nre": 10,
    "G3 Fall_ss_nre": 80,
    "G3 Fall_dss_nre": 500,
    "G3 Winter_theta_nre": 0.446952124,
    "G3 Winter_pr_1_99_nre": 10,
    "G3 Winter_ss_nre": 80,
    "G3 Winter_dss_nre": 545,
    "G3 Spring_theta_nre": 0.859223123,
    "G3 Spring_pr_1_99_nre": 10,
    "G3 Spring_ss_nre": 81,
    "G3 Spring_dss_nre": 586
  },
  {
    "ID": 51,
    "KG Winter_theta_nre": -2.059974106,
    "KG Winter_pr_1_99_nre": 10,
    "KG Winter_ss_nre": 80,
    "KG Winter_dss_nre": 294,
    "KG Spring_theta_nre": -1.769657545,
    "KG Spring_pr_1_99_nre": 10,
    "KG Spring_ss_nre": 80,
    "KG Spring_dss_nre": 323,
    "G1 Fall_theta_nre": -1.517391622,
    "G1 Fall_pr_1_99_nre": 10,
    "G1 Fall_ss_nre": 81,
    "G1 Fall_dss_nre": 348,
    "G1 Winter_theta_nre": -1.170636195,
    "G1 Winter_pr_1_99_nre": 10,
    "G1 Winter_ss_nre": 81,
    "G1 Winter_dss_nre": 383,
    "G1 Spring_theta_nre": -0.827914511,
    "G1 Spring_pr_1_99_nre": 10,
    "G1 Spring_ss_nre": 81,
    "G1 Spring_dss_nre": 417,
    "G2 Fall_theta_nre": -0.718862773,
    "G2 Fall_pr_1_99_nre": 10,
    "G2 Fall_ss_nre": 81,
    "G2 Fall_dss_nre": 428,
    "G2 Winter_theta_nre": -0.340968183,
    "G2 Winter_pr_1_99_nre": 10,
    "G2 Winter_ss_nre": 80,
    "G2 Winter_dss_nre": 466,
    "G2 Spring_theta_nre": -0.25795176,
    "G2 Spring_pr_1_99_nre": 10,
    "G2 Spring_ss_nre": 80,
    "G2 Spring_dss_nre": 474,
    "G3 Fall_theta_nre": 0.034990864,
    "G3 Fall_pr_1_99_nre": 10,
    "G3 Fall_ss_nre": 81,
    "G3 Fall_dss_nre": 503,
    "G3 Winter_theta_nre": 0.455947363,
    "G3 Winter_pr_1_99_nre": 10,
    "G3 Winter_ss_nre": 80,
    "G3 Winter_dss_nre": 546,
    "G3 Spring_theta_nre": 0.890627134,
    "G3 Spring_pr_1_99_nre": 10,
    "G3 Spring_ss_nre": 82,
    "G3 Spring_dss_nre": 589
  },
  {
    "ID": 52,
    "KG Winter_theta_nre": -2.058226424,
    "KG Winter_pr_1_99_nre": 10,
    "KG Winter_ss_nre": 80,
    "KG Winter_dss_nre": 294,
    "KG Spring_theta_nre": -1.746658578,
    "KG Spring_pr_1_99_nre": 10,
    "KG Spring_ss_nre": 81,
    "KG Spring_dss_nre": 325,
    "G1 Fall_theta_nre": -1.510629809,
    "G1 Fall_pr_1_99_nre": 10,
    "G1 Fall_ss_nre": 81,
    "G1 Fall_dss_nre": 349,
    "G1 Winter_theta_nre": -1.166226859,
    "G1 Winter_pr_1_99_nre": 10,
    "G1 Winter_ss_nre": 81,
    "G1 Winter_dss_nre": 383,
    "G1 Spring_theta_nre": -0.819020468,
    "G1 Spring_pr_1_99_nre": 10,
    "G1 Spring_ss_nre": 81,
    "G1 Spring_dss_nre": 418,
    "G2 Fall_theta_nre": -0.709376535,
    "G2 Fall_pr_1_99_nre": 10,
    "G2 Fall_ss_nre": 81,
    "G2 Fall_dss_nre": 429,
    "G2 Winter_theta_nre": -0.32203854,
    "G2 Winter_pr_1_99_nre": 10,
    "G2 Winter_ss_nre": 81,
    "G2 Winter_dss_nre": 468,
    "G2 Spring_theta_nre": -0.194612629,
    "G2 Spring_pr_1_99_nre": 10,
    "G2 Spring_ss_nre": 81,
    "G2 Spring_dss_nre": 481,
    "G3 Fall_theta_nre": 0.045305856,
    "G3 Fall_pr_1_99_nre": 10,
    "G3 Fall_ss_nre": 81,
    "G3 Fall_dss_nre": 505,
    "G3 Winter_theta_nre": 0.470305303,
    "G3 Winter_pr_1_99_nre": 10,
    "G3 Winter_ss_nre": 81,
    "G3 Winter_dss_nre": 547,
    "G3 Spring_theta_nre": 0.899246388,
    "G3 Spring_pr_1_99_nre": 10,
    "G3 Spring_ss_nre": 82,
    "G3 Spring_dss_nre": 590
  },
  {
    "ID": 53,
    "KG Winter_theta_nre": -2.047442914,
    "KG Winter_pr_1_99_nre": 11,
    "KG Winter_ss_nre": 80,
    "KG Winter_dss_nre": 295,
    "KG Spring_theta_nre": -1.743433732,
    "KG Spring_pr_1_99_nre": 11,
    "KG Spring_ss_nre": 81,
    "KG Spring_dss_nre": 326,
    "G1 Fall_theta_nre": -1.507133358,
    "G1 Fall_pr_1_99_nre": 11,
    "G1 Fall_ss_nre": 81,
    "G1 Fall_dss_nre": 349,
    "G1 Winter_theta_nre": -1.151276316,
    "G1 Winter_pr_1_99_nre": 11,
    "G1 Winter_ss_nre": 81,
    "G1 Winter_dss_nre": 385,
    "G1 Spring_theta_nre": -0.804637972,
    "G1 Spring_pr_1_99_nre": 11,
    "G1 Spring_ss_nre": 82,
    "G1 Spring_dss_nre": 420,
    "G2 Fall_theta_nre": -0.703997373,
    "G2 Fall_pr_1_99_nre": 11,
    "G2 Fall_ss_nre": 81,
    "G2 Fall_dss_nre": 430,
    "G2 Winter_theta_nre": -0.301391662,
    "G2 Winter_pr_1_99_nre": 11,
    "G2 Winter_ss_nre": 81,
    "G2 Winter_dss_nre": 470,
    "G2 Spring_theta_nre": -0.183065,
    "G2 Spring_pr_1_99_nre": 11,
    "G2 Spring_ss_nre": 81,
    "G2 Spring_dss_nre": 482,
    "G3 Fall_theta_nre": 0.08240631,
    "G3 Fall_pr_1_99_nre": 11,
    "G3 Fall_ss_nre": 82,
    "G3 Fall_dss_nre": 508,
    "G3 Winter_theta_nre": 0.475789257,
    "G3 Winter_pr_1_99_nre": 11,
    "G3 Winter_ss_nre": 81,
    "G3 Winter_dss_nre": 548,
    "G3 Spring_theta_nre": 0.90709395,
    "G3 Spring_pr_1_99_nre": 11,
    "G3 Spring_ss_nre": 82,
    "G3 Spring_dss_nre": 591
  },
  {
    "ID": 54,
    "KG Winter_theta_nre": -2.034139892,
    "KG Winter_pr_1_99_nre": 11,
    "KG Winter_ss_nre": 80,
    "KG Winter_dss_nre": 297,
    "KG Spring_theta_nre": -1.74205834,
    "KG Spring_pr_1_99_nre": 11,
    "KG Spring_ss_nre": 81,
    "KG Spring_dss_nre": 326,
    "G1 Fall_theta_nre": -1.493752575,
    "G1 Fall_pr_1_99_nre": 11,
    "G1 Fall_ss_nre": 81,
    "G1 Fall_dss_nre": 351,
    "G1 Winter_theta_nre": -1.137957814,
    "G1 Winter_pr_1_99_nre": 11,
    "G1 Winter_ss_nre": 82,
    "G1 Winter_dss_nre": 386,
    "G1 Spring_theta_nre": -0.795654035,
    "G1 Spring_pr_1_99_nre": 11,
    "G1 Spring_ss_nre": 82,
    "G1 Spring_dss_nre": 420,
    "G2 Fall_theta_nre": -0.691996878,
    "G2 Fall_pr_1_99_nre": 11,
    "G2 Fall_ss_nre": 81,
    "G2 Fall_dss_nre": 431,
    "G2 Winter_theta_nre": -0.282521176,
    "G2 Winter_pr_1_99_nre": 11,
    "G2 Winter_ss_nre": 82,
    "G2 Winter_dss_nre": 472,
    "G2 Spring_theta_nre": -0.171746963,
    "G2 Spring_pr_1_99_nre": 11,
    "G2 Spring_ss_nre": 81,
    "G2 Spring_dss_nre": 483,
    "G3 Fall_theta_nre": 0.096194726,
    "G3 Fall_pr_1_99_nre": 11,
    "G3 Fall_ss_nre": 82,
    "G3 Fall_dss_nre": 510,
    "G3 Winter_theta_nre": 0.477068283,
    "G3 Winter_pr_1_99_nre": 11,
    "G3 Winter_ss_nre": 81,
    "G3 Winter_dss_nre": 548,
    "G3 Spring_theta_nre": 0.908835083,
    "G3 Spring_pr_1_99_nre": 11,
    "G3 Spring_ss_nre": 82,
    "G3 Spring_dss_nre": 591
  },
  {
    "ID": 55,
    "KG Winter_theta_nre": -2.026926806,
    "KG Winter_pr_1_99_nre": 11,
    "KG Winter_ss_nre": 81,
    "KG Winter_dss_nre": 297,
    "KG Spring_theta_nre": -1.720425042,
    "KG Spring_pr_1_99_nre": 11,
    "KG Spring_ss_nre": 81,
    "KG Spring_dss_nre": 328,
    "G1 Fall_theta_nre": -1.485374538,
    "G1 Fall_pr_1_99_nre": 11,
    "G1 Fall_ss_nre": 81,
    "G1 Fall_dss_nre": 351,
    "G1 Winter_theta_nre": -1.119499683,
    "G1 Winter_pr_1_99_nre": 11,
    "G1 Winter_ss_nre": 82,
    "G1 Winter_dss_nre": 388,
    "G1 Spring_theta_nre": -0.788995523,
    "G1 Spring_pr_1_99_nre": 11,
    "G1 Spring_ss_nre": 82,
    "G1 Spring_dss_nre": 421,
    "G2 Fall_theta_nre": -0.679150837,
    "G2 Fall_pr_1_99_nre": 11,
    "G2 Fall_ss_nre": 82,
    "G2 Fall_dss_nre": 432,
    "G2 Winter_theta_nre": -0.272979665,
    "G2 Winter_pr_1_99_nre": 11,
    "G2 Winter_ss_nre": 82,
    "G2 Winter_dss_nre": 473,
    "G2 Spring_theta_nre": -0.169552096,
    "G2 Spring_pr_1_99_nre": 11,
    "G2 Spring_ss_nre": 81,
    "G2 Spring_dss_nre": 483,
    "G3 Fall_theta_nre": 0.103472338,
    "G3 Fall_pr_1_99_nre": 11,
    "G3 Fall_ss_nre": 82,
    "G3 Fall_dss_nre": 510,
    "G3 Winter_theta_nre": 0.477765736,
    "G3 Winter_pr_1_99_nre": 11,
    "G3 Winter_ss_nre": 81,
    "G3 Winter_dss_nre": 548,
    "G3 Spring_theta_nre": 0.942459094,
    "G3 Spring_pr_1_99_nre": 11,
    "G3 Spring_ss_nre": 83,
    "G3 Spring_dss_nre": 594
  },
  {
    "ID": 56,
    "KG Winter_theta_nre": -2.019022572,
    "KG Winter_pr_1_99_nre": 11,
    "KG Winter_ss_nre": 81,
    "KG Winter_dss_nre": 298,
    "KG Spring_theta_nre": -1.695134445,
    "KG Spring_pr_1_99_nre": 11,
    "KG Spring_ss_nre": 82,
    "KG Spring_dss_nre": 330,
    "G1 Fall_theta_nre": -1.479732914,
    "G1 Fall_pr_1_99_nre": 11,
    "G1 Fall_ss_nre": 81,
    "G1 Fall_dss_nre": 352,
    "G1 Winter_theta_nre": -1.116403825,
    "G1 Winter_pr_1_99_nre": 11,
    "G1 Winter_ss_nre": 82,
    "G1 Winter_dss_nre": 388,
    "G1 Spring_theta_nre": -0.786742175,
    "G1 Spring_pr_1_99_nre": 11,
    "G1 Spring_ss_nre": 82,
    "G1 Spring_dss_nre": 421,
    "G2 Fall_theta_nre": -0.673604174,
    "G2 Fall_pr_1_99_nre": 11,
    "G2 Fall_ss_nre": 82,
    "G2 Fall_dss_nre": 433,
    "G2 Winter_theta_nre": -0.25395687,
    "G2 Winter_pr_1_99_nre": 11,
    "G2 Winter_ss_nre": 82,
    "G2 Winter_dss_nre": 475,
    "G2 Spring_theta_nre": -0.157450995,
    "G2 Spring_pr_1_99_nre": 11,
    "G2 Spring_ss_nre": 82,
    "G2 Spring_dss_nre": 484,
    "G3 Fall_theta_nre": 0.105783547,
    "G3 Fall_pr_1_99_nre": 11,
    "G3 Fall_ss_nre": 82,
    "G3 Fall_dss_nre": 511,
    "G3 Winter_theta_nre": 0.489413481,
    "G3 Winter_pr_1_99_nre": 11,
    "G3 Winter_ss_nre": 81,
    "G3 Winter_dss_nre": 549,
    "G3 Spring_theta_nre": 0.943963399,
    "G3 Spring_pr_1_99_nre": 11,
    "G3 Spring_ss_nre": 83,
    "G3 Spring_dss_nre": 594
  },
  {
    "ID": 57,
    "KG Winter_theta_nre": -1.995164193,
    "KG Winter_pr_1_99_nre": 11,
    "KG Winter_ss_nre": 81,
    "KG Winter_dss_nre": 300,
    "KG Spring_theta_nre": -1.693180298,
    "KG Spring_pr_1_99_nre": 11,
    "KG Spring_ss_nre": 82,
    "KG Spring_dss_nre": 331,
    "G1 Fall_theta_nre": -1.452569105,
    "G1 Fall_pr_1_99_nre": 11,
    "G1 Fall_ss_nre": 82,
    "G1 Fall_dss_nre": 355,
    "G1 Winter_theta_nre": -1.111760795,
    "G1 Winter_pr_1_99_nre": 11,
    "G1 Winter_ss_nre": 82,
    "G1 Winter_dss_nre": 389,
    "G1 Spring_theta_nre": -0.782735977,
    "G1 Spring_pr_1_99_nre": 11,
    "G1 Spring_ss_nre": 82,
    "G1 Spring_dss_nre": 422,
    "G2 Fall_theta_nre": -0.665489133,
    "G2 Fall_pr_1_99_nre": 11,
    "G2 Fall_ss_nre": 82,
    "G2 Fall_dss_nre": 433,
    "G2 Winter_theta_nre": -0.2493642,
    "G2 Winter_pr_1_99_nre": 11,
    "G2 Winter_ss_nre": 82,
    "G2 Winter_dss_nre": 475,
    "G2 Spring_theta_nre": -0.134305568,
    "G2 Spring_pr_1_99_nre": 11,
    "G2 Spring_ss_nre": 82,
    "G2 Spring_dss_nre": 487,
    "G3 Fall_theta_nre": 0.109382711,
    "G3 Fall_pr_1_99_nre": 11,
    "G3 Fall_ss_nre": 82,
    "G3 Fall_dss_nre": 511,
    "G3 Winter_theta_nre": 0.50039904,
    "G3 Winter_pr_1_99_nre": 11,
    "G3 Winter_ss_nre": 81,
    "G3 Winter_dss_nre": 550,
    "G3 Spring_theta_nre": 0.947016247,
    "G3 Spring_pr_1_99_nre": 11,
    "G3 Spring_ss_nre": 83,
    "G3 Spring_dss_nre": 595
  },
  {
    "ID": 58,
    "KG Winter_theta_nre": -1.992431338,
    "KG Winter_pr_1_99_nre": 12,
    "KG Winter_ss_nre": 81,
    "KG Winter_dss_nre": 301,
    "KG Spring_theta_nre": -1.686141599,
    "KG Spring_pr_1_99_nre": 12,
    "KG Spring_ss_nre": 82,
    "KG Spring_dss_nre": 331,
    "G1 Fall_theta_nre": -1.4505056,
    "G1 Fall_pr_1_99_nre": 12,
    "G1 Fall_ss_nre": 82,
    "G1 Fall_dss_nre": 355,
    "G1 Winter_theta_nre": -1.096154519,
    "G1 Winter_pr_1_99_nre": 12,
    "G1 Winter_ss_nre": 82,
    "G1 Winter_dss_nre": 390,
    "G1 Spring_theta_nre": -0.780412599,
    "G1 Spring_pr_1_99_nre": 12,
    "G1 Spring_ss_nre": 82,
    "G1 Spring_dss_nre": 422,
    "G2 Fall_theta_nre": -0.665401885,
    "G2 Fall_pr_1_99_nre": 12,
    "G2 Fall_ss_nre": 82,
    "G2 Fall_dss_nre": 433,
    "G2 Winter_theta_nre": -0.243584809,
    "G2 Winter_pr_1_99_nre": 12,
    "G2 Winter_ss_nre": 82,
    "G2 Winter_dss_nre": 476,
    "G2 Spring_theta_nre": -0.121691502,
    "G2 Spring_pr_1_99_nre": 12,
    "G2 Spring_ss_nre": 82,
    "G2 Spring_dss_nre": 488,
    "G3 Fall_theta_nre": 0.110562157,
    "G3 Fall_pr_1_99_nre": 12,
    "G3 Fall_ss_nre": 82,
    "G3 Fall_dss_nre": 511,
    "G3 Winter_theta_nre": 0.506135605,
    "G3 Winter_pr_1_99_nre": 12,
    "G3 Winter_ss_nre": 81,
    "G3 Winter_dss_nre": 551,
    "G3 Spring_theta_nre": 0.948377231,
    "G3 Spring_pr_1_99_nre": 12,
    "G3 Spring_ss_nre": 83,
    "G3 Spring_dss_nre": 595
  },
  {
    "ID": 59,
    "KG Winter_theta_nre": -1.985552169,
    "KG Winter_pr_1_99_nre": 12,
    "KG Winter_ss_nre": 81,
    "KG Winter_dss_nre": 301,
    "KG Spring_theta_nre": -1.674647016,
    "KG Spring_pr_1_99_nre": 12,
    "KG Spring_ss_nre": 82,
    "KG Spring_dss_nre": 333,
    "G1 Fall_theta_nre": -1.447928495,
    "G1 Fall_pr_1_99_nre": 12,
    "G1 Fall_ss_nre": 82,
    "G1 Fall_dss_nre": 355,
    "G1 Winter_theta_nre": -1.08784389,
    "G1 Winter_pr_1_99_nre": 12,
    "G1 Winter_ss_nre": 83,
    "G1 Winter_dss_nre": 391,
    "G1 Spring_theta_nre": -0.761265371,
    "G1 Spring_pr_1_99_nre": 12,
    "G1 Spring_ss_nre": 82,
    "G1 Spring_dss_nre": 424,
    "G2 Fall_theta_nre": -0.653671218,
    "G2 Fall_pr_1_99_nre": 12,
    "G2 Fall_ss_nre": 82,
    "G2 Fall_dss_nre": 435,
    "G2 Winter_theta_nre": -0.238916079,
    "G2 Winter_pr_1_99_nre": 12,
    "G2 Winter_ss_nre": 82,
    "G2 Winter_dss_nre": 476,
    "G2 Spring_theta_nre": -0.116238702,
    "G2 Spring_pr_1_99_nre": 12,
    "G2 Spring_ss_nre": 82,
    "G2 Spring_dss_nre": 488,
    "G3 Fall_theta_nre": 0.114470364,
    "G3 Fall_pr_1_99_nre": 12,
    "G3 Fall_ss_nre": 82,
    "G3 Fall_dss_nre": 511,
    "G3 Winter_theta_nre": 0.507395622,
    "G3 Winter_pr_1_99_nre": 12,
    "G3 Winter_ss_nre": 81,
    "G3 Winter_dss_nre": 551,
    "G3 Spring_theta_nre": 0.948591656,
    "G3 Spring_pr_1_99_nre": 12,
    "G3 Spring_ss_nre": 83,
    "G3 Spring_dss_nre": 595
  },
  {
    "ID": 60,
    "KG Winter_theta_nre": -1.971405662,
    "KG Winter_pr_1_99_nre": 12,
    "KG Winter_ss_nre": 82,
    "KG Winter_dss_nre": 303,
    "KG Spring_theta_nre": -1.656036208,
    "KG Spring_pr_1_99_nre": 12,
    "KG Spring_ss_nre": 82,
    "KG Spring_dss_nre": 334,
    "G1 Fall_theta_nre": -1.441445454,
    "G1 Fall_pr_1_99_nre": 12,
    "G1 Fall_ss_nre": 82,
    "G1 Fall_dss_nre": 356,
    "G1 Winter_theta_nre": -1.076958676,
    "G1 Winter_pr_1_99_nre": 12,
    "G1 Winter_ss_nre": 83,
    "G1 Winter_dss_nre": 392,
    "G1 Spring_theta_nre": -0.752876634,
    "G1 Spring_pr_1_99_nre": 12,
    "G1 Spring_ss_nre": 83,
    "G1 Spring_dss_nre": 425,
    "G2 Fall_theta_nre": -0.653119146,
    "G2 Fall_pr_1_99_nre": 12,
    "G2 Fall_ss_nre": 82,
    "G2 Fall_dss_nre": 435,
    "G2 Winter_theta_nre": -0.238502453,
    "G2 Winter_pr_1_99_nre": 12,
    "G2 Winter_ss_nre": 82,
    "G2 Winter_dss_nre": 476,
    "G2 Spring_theta_nre": -0.115118568,
    "G2 Spring_pr_1_99_nre": 12,
    "G2 Spring_ss_nre": 82,
    "G2 Spring_dss_nre": 488,
    "G3 Fall_theta_nre": 0.115204292,
    "G3 Fall_pr_1_99_nre": 12,
    "G3 Fall_ss_nre": 82,
    "G3 Fall_dss_nre": 512,
    "G3 Winter_theta_nre": 0.517747715,
    "G3 Winter_pr_1_99_nre": 12,
    "G3 Winter_ss_nre": 82,
    "G3 Winter_dss_nre": 552,
    "G3 Spring_theta_nre": 0.955956201,
    "G3 Spring_pr_1_99_nre": 12,
    "G3 Spring_ss_nre": 84,
    "G3 Spring_dss_nre": 596
  },
  {
    "ID": 61,
    "KG Winter_theta_nre": -1.961004946,
    "KG Winter_pr_1_99_nre": 12,
    "KG Winter_ss_nre": 82,
    "KG Winter_dss_nre": 304,
    "KG Spring_theta_nre": -1.655960584,
    "KG Spring_pr_1_99_nre": 12,
    "KG Spring_ss_nre": 82,
    "KG Spring_dss_nre": 334,
    "G1 Fall_theta_nre": -1.4245367,
    "G1 Fall_pr_1_99_nre": 12,
    "G1 Fall_ss_nre": 82,
    "G1 Fall_dss_nre": 358,
    "G1 Winter_theta_nre": -1.073767771,
    "G1 Winter_pr_1_99_nre": 12,
    "G1 Winter_ss_nre": 83,
    "G1 Winter_dss_nre": 393,
    "G1 Spring_theta_nre": -0.752639226,
    "G1 Spring_pr_1_99_nre": 12,
    "G1 Spring_ss_nre": 83,
    "G1 Spring_dss_nre": 425,
    "G2 Fall_theta_nre": -0.640772917,
    "G2 Fall_pr_1_99_nre": 12,
    "G2 Fall_ss_nre": 82,
    "G2 Fall_dss_nre": 436,
    "G2 Winter_theta_nre": -0.235342515,
    "G2 Winter_pr_1_99_nre": 12,
    "G2 Winter_ss_nre": 83,
    "G2 Winter_dss_nre": 476,
    "G2 Spring_theta_nre": -0.098787707,
    "G2 Spring_pr_1_99_nre": 12,
    "G2 Spring_ss_nre": 83,
    "G2 Spring_dss_nre": 490,
    "G3 Fall_theta_nre": 0.128162748,
    "G3 Fall_pr_1_99_nre": 12,
    "G3 Fall_ss_nre": 83,
    "G3 Fall_dss_nre": 513,
    "G3 Winter_theta_nre": 0.535791581,
    "G3 Winter_pr_1_99_nre": 12,
    "G3 Winter_ss_nre": 82,
    "G3 Winter_dss_nre": 554,
    "G3 Spring_theta_nre": 0.962349131,
    "G3 Spring_pr_1_99_nre": 12,
    "G3 Spring_ss_nre": 84,
    "G3 Spring_dss_nre": 596
  },
  {
    "ID": 62,
    "KG Winter_theta_nre": -1.940550249,
    "KG Winter_pr_1_99_nre": 12,
    "KG Winter_ss_nre": 82,
    "KG Winter_dss_nre": 306,
    "KG Spring_theta_nre": -1.653015147,
    "KG Spring_pr_1_99_nre": 12,
    "KG Spring_ss_nre": 82,
    "KG Spring_dss_nre": 335,
    "G1 Fall_theta_nre": -1.424025879,
    "G1 Fall_pr_1_99_nre": 12,
    "G1 Fall_ss_nre": 82,
    "G1 Fall_dss_nre": 358,
    "G1 Winter_theta_nre": -1.069862392,
    "G1 Winter_pr_1_99_nre": 12,
    "G1 Winter_ss_nre": 83,
    "G1 Winter_dss_nre": 393,
    "G1 Spring_theta_nre": -0.715772313,
    "G1 Spring_pr_1_99_nre": 12,
    "G1 Spring_ss_nre": 83,
    "G1 Spring_dss_nre": 428,
    "G2 Fall_theta_nre": -0.637449665,
    "G2 Fall_pr_1_99_nre": 12,
    "G2 Fall_ss_nre": 82,
    "G2 Fall_dss_nre": 436,
    "G2 Winter_theta_nre": -0.219653839,
    "G2 Winter_pr_1_99_nre": 12,
    "G2 Winter_ss_nre": 83,
    "G2 Winter_dss_nre": 478,
    "G2 Spring_theta_nre": -0.097402251,
    "G2 Spring_pr_1_99_nre": 12,
    "G2 Spring_ss_nre": 83,
    "G2 Spring_dss_nre": 490,
    "G3 Fall_theta_nre": 0.140474752,
    "G3 Fall_pr_1_99_nre": 12,
    "G3 Fall_ss_nre": 83,
    "G3 Fall_dss_nre": 514,
    "G3 Winter_theta_nre": 0.536500821,
    "G3 Winter_pr_1_99_nre": 12,
    "G3 Winter_ss_nre": 82,
    "G3 Winter_dss_nre": 554,
    "G3 Spring_theta_nre": 0.968710667,
    "G3 Spring_pr_1_99_nre": 12,
    "G3 Spring_ss_nre": 84,
    "G3 Spring_dss_nre": 597
  },
  {
    "ID": 63,
    "KG Winter_theta_nre": -1.939000066,
    "KG Winter_pr_1_99_nre": 13,
    "KG Winter_ss_nre": 82,
    "KG Winter_dss_nre": 306,
    "KG Spring_theta_nre": -1.6394023,
    "KG Spring_pr_1_99_nre": 13,
    "KG Spring_ss_nre": 83,
    "KG Spring_dss_nre": 336,
    "G1 Fall_theta_nre": -1.41991013,
    "G1 Fall_pr_1_99_nre": 13,
    "G1 Fall_ss_nre": 82,
    "G1 Fall_dss_nre": 358,
    "G1 Winter_theta_nre": -1.058669426,
    "G1 Winter_pr_1_99_nre": 13,
    "G1 Winter_ss_nre": 83,
    "G1 Winter_dss_nre": 394,
    "G1 Spring_theta_nre": -0.696385892,
    "G1 Spring_pr_1_99_nre": 13,
    "G1 Spring_ss_nre": 84,
    "G1 Spring_dss_nre": 430,
    "G2 Fall_theta_nre": -0.633552131,
    "G2 Fall_pr_1_99_nre": 13,
    "G2 Fall_ss_nre": 82,
    "G2 Fall_dss_nre": 437,
    "G2 Winter_theta_nre": -0.203892414,
    "G2 Winter_pr_1_99_nre": 13,
    "G2 Winter_ss_nre": 83,
    "G2 Winter_dss_nre": 480,
    "G2 Spring_theta_nre": -0.090875069,
    "G2 Spring_pr_1_99_nre": 13,
    "G2 Spring_ss_nre": 83,
    "G2 Spring_dss_nre": 491,
    "G3 Fall_theta_nre": 0.145573483,
    "G3 Fall_pr_1_99_nre": 13,
    "G3 Fall_ss_nre": 83,
    "G3 Fall_dss_nre": 515,
    "G3 Winter_theta_nre": 0.538438203,
    "G3 Winter_pr_1_99_nre": 13,
    "G3 Winter_ss_nre": 82,
    "G3 Winter_dss_nre": 554,
    "G3 Spring_theta_nre": 0.97670817,
    "G3 Spring_pr_1_99_nre": 13,
    "G3 Spring_ss_nre": 84,
    "G3 Spring_dss_nre": 598
  },
  {
    "ID": 64,
    "KG Winter_theta_nre": -1.933587037,
    "KG Winter_pr_1_99_nre": 13,
    "KG Winter_ss_nre": 82,
    "KG Winter_dss_nre": 307,
    "KG Spring_theta_nre": -1.633428061,
    "KG Spring_pr_1_99_nre": 13,
    "KG Spring_ss_nre": 83,
    "KG Spring_dss_nre": 337,
    "G1 Fall_theta_nre": -1.40825798,
    "G1 Fall_pr_1_99_nre": 13,
    "G1 Fall_ss_nre": 83,
    "G1 Fall_dss_nre": 359,
    "G1 Winter_theta_nre": -1.043221969,
    "G1 Winter_pr_1_99_nre": 13,
    "G1 Winter_ss_nre": 83,
    "G1 Winter_dss_nre": 396,
    "G1 Spring_theta_nre": -0.694392294,
    "G1 Spring_pr_1_99_nre": 13,
    "G1 Spring_ss_nre": 84,
    "G1 Spring_dss_nre": 431,
    "G2 Fall_theta_nre": -0.632993811,
    "G2 Fall_pr_1_99_nre": 13,
    "G2 Fall_ss_nre": 82,
    "G2 Fall_dss_nre": 437,
    "G2 Winter_theta_nre": -0.194331216,
    "G2 Winter_pr_1_99_nre": 13,
    "G2 Winter_ss_nre": 83,
    "G2 Winter_dss_nre": 481,
    "G2 Spring_theta_nre": -0.085558865,
    "G2 Spring_pr_1_99_nre": 13,
    "G2 Spring_ss_nre": 83,
    "G2 Spring_dss_nre": 491,
    "G3 Fall_theta_nre": 0.146662959,
    "G3 Fall_pr_1_99_nre": 13,
    "G3 Fall_ss_nre": 83,
    "G3 Fall_dss_nre": 515,
    "G3 Winter_theta_nre": 0.542333146,
    "G3 Winter_pr_1_99_nre": 13,
    "G3 Winter_ss_nre": 82,
    "G3 Winter_dss_nre": 554,
    "G3 Spring_theta_nre": 0.981466593,
    "G3 Spring_pr_1_99_nre": 13,
    "G3 Spring_ss_nre": 84,
    "G3 Spring_dss_nre": 598
  },
  {
    "ID": 65,
    "KG Winter_theta_nre": -1.928473257,
    "KG Winter_pr_1_99_nre": 13,
    "KG Winter_ss_nre": 82,
    "KG Winter_dss_nre": 307,
    "KG Spring_theta_nre": -1.622340206,
    "KG Spring_pr_1_99_nre": 13,
    "KG Spring_ss_nre": 83,
    "KG Spring_dss_nre": 338,
    "G1 Fall_theta_nre": -1.399192576,
    "G1 Fall_pr_1_99_nre": 13,
    "G1 Fall_ss_nre": 83,
    "G1 Fall_dss_nre": 360,
    "G1 Winter_theta_nre": -1.03707376,
    "G1 Winter_pr_1_99_nre": 13,
    "G1 Winter_ss_nre": 83,
    "G1 Winter_dss_nre": 396,
    "G1 Spring_theta_nre": -0.692662299,
    "G1 Spring_pr_1_99_nre": 13,
    "G1 Spring_ss_nre": 84,
    "G1 Spring_dss_nre": 431,
    "G2 Fall_theta_nre": -0.629569111,
    "G2 Fall_pr_1_99_nre": 13,
    "G2 Fall_ss_nre": 83,
    "G2 Fall_dss_nre": 437,
    "G2 Winter_theta_nre": -0.1929384,
    "G2 Winter_pr_1_99_nre": 13,
    "G2 Winter_ss_nre": 83,
    "G2 Winter_dss_nre": 481,
    "G2 Spring_theta_nre": -0.083045931,
    "G2 Spring_pr_1_99_nre": 13,
    "G2 Spring_ss_nre": 83,
    "G2 Spring_dss_nre": 492,
    "G3 Fall_theta_nre": 0.155926247,
    "G3 Fall_pr_1_99_nre": 13,
    "G3 Fall_ss_nre": 83,
    "G3 Fall_dss_nre": 516,
    "G3 Winter_theta_nre": 0.564732359,
    "G3 Winter_pr_1_99_nre": 13,
    "G3 Winter_ss_nre": 83,
    "G3 Winter_dss_nre": 556,
    "G3 Spring_theta_nre": 0.99273914,
    "G3 Spring_pr_1_99_nre": 13,
    "G3 Spring_ss_nre": 84,
    "G3 Spring_dss_nre": 599
  },
  {
    "ID": 66,
    "KG Winter_theta_nre": -1.923595326,
    "KG Winter_pr_1_99_nre": 13,
    "KG Winter_ss_nre": 82,
    "KG Winter_dss_nre": 308,
    "KG Spring_theta_nre": -1.619288213,
    "KG Spring_pr_1_99_nre": 13,
    "KG Spring_ss_nre": 83,
    "KG Spring_dss_nre": 338,
    "G1 Fall_theta_nre": -1.398102805,
    "G1 Fall_pr_1_99_nre": 13,
    "G1 Fall_ss_nre": 83,
    "G1 Fall_dss_nre": 360,
    "G1 Winter_theta_nre": -1.023556361,
    "G1 Winter_pr_1_99_nre": 13,
    "G1 Winter_ss_nre": 84,
    "G1 Winter_dss_nre": 398,
    "G1 Spring_theta_nre": -0.679406428,
    "G1 Spring_pr_1_99_nre": 13,
    "G1 Spring_ss_nre": 84,
    "G1 Spring_dss_nre": 432,
    "G2 Fall_theta_nre": -0.626569626,
    "G2 Fall_pr_1_99_nre": 13,
    "G2 Fall_ss_nre": 83,
    "G2 Fall_dss_nre": 437,
    "G2 Winter_theta_nre": -0.19286838,
    "G2 Winter_pr_1_99_nre": 13,
    "G2 Winter_ss_nre": 83,
    "G2 Winter_dss_nre": 481,
    "G2 Spring_theta_nre": -0.081425171,
    "G2 Spring_pr_1_99_nre": 13,
    "G2 Spring_ss_nre": 83,
    "G2 Spring_dss_nre": 492,
    "G3 Fall_theta_nre": 0.157957788,
    "G3 Fall_pr_1_99_nre": 13,
    "G3 Fall_ss_nre": 83,
    "G3 Fall_dss_nre": 516,
    "G3 Winter_theta_nre": 0.570651808,
    "G3 Winter_pr_1_99_nre": 13,
    "G3 Winter_ss_nre": 83,
    "G3 Winter_dss_nre": 557,
    "G3 Spring_theta_nre": 0.997241289,
    "G3 Spring_pr_1_99_nre": 13,
    "G3 Spring_ss_nre": 84,
    "G3 Spring_dss_nre": 600
  },
  {
    "ID": 67,
    "KG Winter_theta_nre": -1.87933676,
    "KG Winter_pr_1_99_nre": 13,
    "KG Winter_ss_nre": 83,
    "KG Winter_dss_nre": 312,
    "KG Spring_theta_nre": -1.617355107,
    "KG Spring_pr_1_99_nre": 13,
    "KG Spring_ss_nre": 83,
    "KG Spring_dss_nre": 338,
    "G1 Fall_theta_nre": -1.397286628,
    "G1 Fall_pr_1_99_nre": 13,
    "G1 Fall_ss_nre": 83,
    "G1 Fall_dss_nre": 360,
    "G1 Winter_theta_nre": -1.022122739,
    "G1 Winter_pr_1_99_nre": 13,
    "G1 Winter_ss_nre": 84,
    "G1 Winter_dss_nre": 398,
    "G1 Spring_theta_nre": -0.662770792,
    "G1 Spring_pr_1_99_nre": 13,
    "G1 Spring_ss_nre": 84,
    "G1 Spring_dss_nre": 434,
    "G2 Fall_theta_nre": -0.619582624,
    "G2 Fall_pr_1_99_nre": 13,
    "G2 Fall_ss_nre": 83,
    "G2 Fall_dss_nre": 438,
    "G2 Winter_theta_nre": -0.189947697,
    "G2 Winter_pr_1_99_nre": 13,
    "G2 Winter_ss_nre": 83,
    "G2 Winter_dss_nre": 481,
    "G2 Spring_theta_nre": -0.068177855,
    "G2 Spring_pr_1_99_nre": 13,
    "G2 Spring_ss_nre": 83,
    "G2 Spring_dss_nre": 493,
    "G3 Fall_theta_nre": 0.20146979,
    "G3 Fall_pr_1_99_nre": 13,
    "G3 Fall_ss_nre": 84,
    "G3 Fall_dss_nre": 520,
    "G3 Winter_theta_nre": 0.581317545,
    "G3 Winter_pr_1_99_nre": 13,
    "G3 Winter_ss_nre": 83,
    "G3 Winter_dss_nre": 558,
    "G3 Spring_theta_nre": 1.000311509,
    "G3 Spring_pr_1_99_nre": 13,
    "G3 Spring_ss_nre": 84,
    "G3 Spring_dss_nre": 600
  },
  {
    "ID": 68,
    "KG Winter_theta_nre": -1.873608732,
    "KG Winter_pr_1_99_nre": 14,
    "KG Winter_ss_nre": 83,
    "KG Winter_dss_nre": 313,
    "KG Spring_theta_nre": -1.60361098,
    "KG Spring_pr_1_99_nre": 14,
    "KG Spring_ss_nre": 83,
    "KG Spring_dss_nre": 340,
    "G1 Fall_theta_nre": -1.39257705,
    "G1 Fall_pr_1_99_nre": 14,
    "G1 Fall_ss_nre": 83,
    "G1 Fall_dss_nre": 361,
    "G1 Winter_theta_nre": -1.021878813,
    "G1 Winter_pr_1_99_nre": 14,
    "G1 Winter_ss_nre": 84,
    "G1 Winter_dss_nre": 398,
    "G1 Spring_theta_nre": -0.655726051,
    "G1 Spring_pr_1_99_nre": 14,
    "G1 Spring_ss_nre": 84,
    "G1 Spring_dss_nre": 434,
    "G2 Fall_theta_nre": -0.617615803,
    "G2 Fall_pr_1_99_nre": 14,
    "G2 Fall_ss_nre": 83,
    "G2 Fall_dss_nre": 438,
    "G2 Winter_theta_nre": -0.182440388,
    "G2 Winter_pr_1_99_nre": 14,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 482,
    "G2 Spring_theta_nre": -0.066068332,
    "G2 Spring_pr_1_99_nre": 14,
    "G2 Spring_ss_nre": 83,
    "G2 Spring_dss_nre": 493,
    "G3 Fall_theta_nre": 0.215950835,
    "G3 Fall_pr_1_99_nre": 14,
    "G3 Fall_ss_nre": 84,
    "G3 Fall_dss_nre": 522,
    "G3 Winter_theta_nre": 0.589574853,
    "G3 Winter_pr_1_99_nre": 14,
    "G3 Winter_ss_nre": 83,
    "G3 Winter_dss_nre": 559,
    "G3 Spring_theta_nre": 1.000615615,
    "G3 Spring_pr_1_99_nre": 14,
    "G3 Spring_ss_nre": 84,
    "G3 Spring_dss_nre": 600
  },
  {
    "ID": 69,
    "KG Winter_theta_nre": -1.871474206,
    "KG Winter_pr_1_99_nre": 14,
    "KG Winter_ss_nre": 83,
    "KG Winter_dss_nre": 313,
    "KG Spring_theta_nre": -1.565700563,
    "KG Spring_pr_1_99_nre": 14,
    "KG Spring_ss_nre": 84,
    "KG Spring_dss_nre": 343,
    "G1 Fall_theta_nre": -1.390668488,
    "G1 Fall_pr_1_99_nre": 14,
    "G1 Fall_ss_nre": 83,
    "G1 Fall_dss_nre": 361,
    "G1 Winter_theta_nre": -1.010996197,
    "G1 Winter_pr_1_99_nre": 14,
    "G1 Winter_ss_nre": 84,
    "G1 Winter_dss_nre": 399,
    "G1 Spring_theta_nre": -0.654275837,
    "G1 Spring_pr_1_99_nre": 14,
    "G1 Spring_ss_nre": 84,
    "G1 Spring_dss_nre": 435,
    "G2 Fall_theta_nre": -0.60372504,
    "G2 Fall_pr_1_99_nre": 14,
    "G2 Fall_ss_nre": 83,
    "G2 Fall_dss_nre": 440,
    "G2 Winter_theta_nre": -0.182390782,
    "G2 Winter_pr_1_99_nre": 14,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 482,
    "G2 Spring_theta_nre": -0.044158687,
    "G2 Spring_pr_1_99_nre": 14,
    "G2 Spring_ss_nre": 83,
    "G2 Spring_dss_nre": 496,
    "G3 Fall_theta_nre": 0.230044255,
    "G3 Fall_pr_1_99_nre": 14,
    "G3 Fall_ss_nre": 84,
    "G3 Fall_dss_nre": 523,
    "G3 Winter_theta_nre": 0.594378901,
    "G3 Winter_pr_1_99_nre": 14,
    "G3 Winter_ss_nre": 83,
    "G3 Winter_dss_nre": 559,
    "G3 Spring_theta_nre": 1.013837149,
    "G3 Spring_pr_1_99_nre": 14,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 601
  },
  {
    "ID": 70,
    "KG Winter_theta_nre": -1.868711075,
    "KG Winter_pr_1_99_nre": 14,
    "KG Winter_ss_nre": 83,
    "KG Winter_dss_nre": 313,
    "KG Spring_theta_nre": -1.561040463,
    "KG Spring_pr_1_99_nre": 14,
    "KG Spring_ss_nre": 84,
    "KG Spring_dss_nre": 344,
    "G1 Fall_theta_nre": -1.388988635,
    "G1 Fall_pr_1_99_nre": 14,
    "G1 Fall_ss_nre": 83,
    "G1 Fall_dss_nre": 361,
    "G1 Winter_theta_nre": -1.009291299,
    "G1 Winter_pr_1_99_nre": 14,
    "G1 Winter_ss_nre": 84,
    "G1 Winter_dss_nre": 399,
    "G1 Spring_theta_nre": -0.650193761,
    "G1 Spring_pr_1_99_nre": 14,
    "G1 Spring_ss_nre": 85,
    "G1 Spring_dss_nre": 435,
    "G2 Fall_theta_nre": -0.58967992,
    "G2 Fall_pr_1_99_nre": 14,
    "G2 Fall_ss_nre": 83,
    "G2 Fall_dss_nre": 441,
    "G2 Winter_theta_nre": -0.180589606,
    "G2 Winter_pr_1_99_nre": 14,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 482,
    "G2 Spring_theta_nre": -0.036201326,
    "G2 Spring_pr_1_99_nre": 14,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 496,
    "G3 Fall_theta_nre": 0.261194986,
    "G3 Fall_pr_1_99_nre": 14,
    "G3 Fall_ss_nre": 85,
    "G3 Fall_dss_nre": 526,
    "G3 Winter_theta_nre": 0.604378328,
    "G3 Winter_pr_1_99_nre": 14,
    "G3 Winter_ss_nre": 83,
    "G3 Winter_dss_nre": 560,
    "G3 Spring_theta_nre": 1.014744203,
    "G3 Spring_pr_1_99_nre": 14,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 601
  },
  {
    "ID": 71,
    "KG Winter_theta_nre": -1.86637552,
    "KG Winter_pr_1_99_nre": 14,
    "KG Winter_ss_nre": 83,
    "KG Winter_dss_nre": 313,
    "KG Spring_theta_nre": -1.55808419,
    "KG Spring_pr_1_99_nre": 14,
    "KG Spring_ss_nre": 84,
    "KG Spring_dss_nre": 344,
    "G1 Fall_theta_nre": -1.381572019,
    "G1 Fall_pr_1_99_nre": 14,
    "G1 Fall_ss_nre": 83,
    "G1 Fall_dss_nre": 362,
    "G1 Winter_theta_nre": -1.009136441,
    "G1 Winter_pr_1_99_nre": 14,
    "G1 Winter_ss_nre": 84,
    "G1 Winter_dss_nre": 399,
    "G1 Spring_theta_nre": -0.643441882,
    "G1 Spring_pr_1_99_nre": 14,
    "G1 Spring_ss_nre": 85,
    "G1 Spring_dss_nre": 436,
    "G2 Fall_theta_nre": -0.587875111,
    "G2 Fall_pr_1_99_nre": 14,
    "G2 Fall_ss_nre": 83,
    "G2 Fall_dss_nre": 441,
    "G2 Winter_theta_nre": -0.172349566,
    "G2 Winter_pr_1_99_nre": 14,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 483,
    "G2 Spring_theta_nre": -0.035423841,
    "G2 Spring_pr_1_99_nre": 14,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 496,
    "G3 Fall_theta_nre": 0.262484198,
    "G3 Fall_pr_1_99_nre": 14,
    "G3 Fall_ss_nre": 85,
    "G3 Fall_dss_nre": 526,
    "G3 Winter_theta_nre": 0.615773065,
    "G3 Winter_pr_1_99_nre": 14,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 562,
    "G3 Spring_theta_nre": 1.015664227,
    "G3 Spring_pr_1_99_nre": 14,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 602
  },
  {
    "ID": 72,
    "KG Winter_theta_nre": -1.847841721,
    "KG Winter_pr_1_99_nre": 14,
    "KG Winter_ss_nre": 84,
    "KG Winter_dss_nre": 315,
    "KG Spring_theta_nre": -1.553733132,
    "KG Spring_pr_1_99_nre": 14,
    "KG Spring_ss_nre": 84,
    "KG Spring_dss_nre": 345,
    "G1 Fall_theta_nre": -1.371313913,
    "G1 Fall_pr_1_99_nre": 14,
    "G1 Fall_ss_nre": 83,
    "G1 Fall_dss_nre": 363,
    "G1 Winter_theta_nre": -1.00057117,
    "G1 Winter_pr_1_99_nre": 14,
    "G1 Winter_ss_nre": 84,
    "G1 Winter_dss_nre": 400,
    "G1 Spring_theta_nre": -0.639453793,
    "G1 Spring_pr_1_99_nre": 14,
    "G1 Spring_ss_nre": 85,
    "G1 Spring_dss_nre": 436,
    "G2 Fall_theta_nre": -0.569744745,
    "G2 Fall_pr_1_99_nre": 14,
    "G2 Fall_ss_nre": 84,
    "G2 Fall_dss_nre": 443,
    "G2 Winter_theta_nre": -0.171362957,
    "G2 Winter_pr_1_99_nre": 14,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 483,
    "G2 Spring_theta_nre": -0.033303019,
    "G2 Spring_pr_1_99_nre": 14,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 497,
    "G3 Fall_theta_nre": 0.264872333,
    "G3 Fall_pr_1_99_nre": 14,
    "G3 Fall_ss_nre": 85,
    "G3 Fall_dss_nre": 526,
    "G3 Winter_theta_nre": 0.618616147,
    "G3 Winter_pr_1_99_nre": 14,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 562,
    "G3 Spring_theta_nre": 1.016366407,
    "G3 Spring_pr_1_99_nre": 14,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 602
  },
  {
    "ID": 73,
    "KG Winter_theta_nre": -1.842637789,
    "KG Winter_pr_1_99_nre": 15,
    "KG Winter_ss_nre": 84,
    "KG Winter_dss_nre": 316,
    "KG Spring_theta_nre": -1.536385746,
    "KG Spring_pr_1_99_nre": 15,
    "KG Spring_ss_nre": 84,
    "KG Spring_dss_nre": 346,
    "G1 Fall_theta_nre": -1.363421397,
    "G1 Fall_pr_1_99_nre": 15,
    "G1 Fall_ss_nre": 83,
    "G1 Fall_dss_nre": 364,
    "G1 Winter_theta_nre": -0.99460855,
    "G1 Winter_pr_1_99_nre": 15,
    "G1 Winter_ss_nre": 84,
    "G1 Winter_dss_nre": 401,
    "G1 Spring_theta_nre": -0.631788104,
    "G1 Spring_pr_1_99_nre": 15,
    "G1 Spring_ss_nre": 85,
    "G1 Spring_dss_nre": 437,
    "G2 Fall_theta_nre": -0.568170449,
    "G2 Fall_pr_1_99_nre": 15,
    "G2 Fall_ss_nre": 84,
    "G2 Fall_dss_nre": 443,
    "G2 Winter_theta_nre": -0.167797893,
    "G2 Winter_pr_1_99_nre": 15,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 483,
    "G2 Spring_theta_nre": -0.033080582,
    "G2 Spring_pr_1_99_nre": 15,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 497,
    "G3 Fall_theta_nre": 0.269918241,
    "G3 Fall_pr_1_99_nre": 15,
    "G3 Fall_ss_nre": 85,
    "G3 Fall_dss_nre": 527,
    "G3 Winter_theta_nre": 0.618847212,
    "G3 Winter_pr_1_99_nre": 15,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 562,
    "G3 Spring_theta_nre": 1.020075045,
    "G3 Spring_pr_1_99_nre": 15,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 602
  },
  {
    "ID": 74,
    "KG Winter_theta_nre": -1.836658806,
    "KG Winter_pr_1_99_nre": 15,
    "KG Winter_ss_nre": 84,
    "KG Winter_dss_nre": 316,
    "KG Spring_theta_nre": -1.524034799,
    "KG Spring_pr_1_99_nre": 15,
    "KG Spring_ss_nre": 85,
    "KG Spring_dss_nre": 348,
    "G1 Fall_theta_nre": -1.339549453,
    "G1 Fall_pr_1_99_nre": 15,
    "G1 Fall_ss_nre": 84,
    "G1 Fall_dss_nre": 366,
    "G1 Winter_theta_nre": -0.983563293,
    "G1 Winter_pr_1_99_nre": 15,
    "G1 Winter_ss_nre": 84,
    "G1 Winter_dss_nre": 402,
    "G1 Spring_theta_nre": -0.625311846,
    "G1 Spring_pr_1_99_nre": 15,
    "G1 Spring_ss_nre": 85,
    "G1 Spring_dss_nre": 437,
    "G2 Fall_theta_nre": -0.558284824,
    "G2 Fall_pr_1_99_nre": 15,
    "G2 Fall_ss_nre": 84,
    "G2 Fall_dss_nre": 444,
    "G2 Winter_theta_nre": -0.159105021,
    "G2 Winter_pr_1_99_nre": 15,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 484,
    "G2 Spring_theta_nre": -0.032221437,
    "G2 Spring_pr_1_99_nre": 15,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 497,
    "G3 Fall_theta_nre": 0.276584094,
    "G3 Fall_pr_1_99_nre": 15,
    "G3 Fall_ss_nre": 85,
    "G3 Fall_dss_nre": 528,
    "G3 Winter_theta_nre": 0.621591812,
    "G3 Winter_pr_1_99_nre": 15,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 562,
    "G3 Spring_theta_nre": 1.027560487,
    "G3 Spring_pr_1_99_nre": 15,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 603
  },
  {
    "ID": 75,
    "KG Winter_theta_nre": -1.831097684,
    "KG Winter_pr_1_99_nre": 15,
    "KG Winter_ss_nre": 84,
    "KG Winter_dss_nre": 317,
    "KG Spring_theta_nre": -1.516733443,
    "KG Spring_pr_1_99_nre": 15,
    "KG Spring_ss_nre": 85,
    "KG Spring_dss_nre": 348,
    "G1 Fall_theta_nre": -1.339089966,
    "G1 Fall_pr_1_99_nre": 15,
    "G1 Fall_ss_nre": 84,
    "G1 Fall_dss_nre": 366,
    "G1 Winter_theta_nre": -0.967518194,
    "G1 Winter_pr_1_99_nre": 15,
    "G1 Winter_ss_nre": 85,
    "G1 Winter_dss_nre": 403,
    "G1 Spring_theta_nre": -0.623804125,
    "G1 Spring_pr_1_99_nre": 15,
    "G1 Spring_ss_nre": 85,
    "G1 Spring_dss_nre": 438,
    "G2 Fall_theta_nre": -0.555826057,
    "G2 Fall_pr_1_99_nre": 15,
    "G2 Fall_ss_nre": 84,
    "G2 Fall_dss_nre": 444,
    "G2 Winter_theta_nre": -0.153487197,
    "G2 Winter_pr_1_99_nre": 15,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 485,
    "G2 Spring_theta_nre": -0.031554053,
    "G2 Spring_pr_1_99_nre": 15,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 497,
    "G3 Fall_theta_nre": 0.28920168,
    "G3 Fall_pr_1_99_nre": 15,
    "G3 Fall_ss_nre": 85,
    "G3 Fall_dss_nre": 529,
    "G3 Winter_theta_nre": 0.621715337,
    "G3 Winter_pr_1_99_nre": 15,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 562,
    "G3 Spring_theta_nre": 1.032430767,
    "G3 Spring_pr_1_99_nre": 15,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 603
  },
  {
    "ID": 76,
    "KG Winter_theta_nre": -1.827008119,
    "KG Winter_pr_1_99_nre": 15,
    "KG Winter_ss_nre": 84,
    "KG Winter_dss_nre": 317,
    "KG Spring_theta_nre": -1.496532327,
    "KG Spring_pr_1_99_nre": 15,
    "KG Spring_ss_nre": 85,
    "KG Spring_dss_nre": 350,
    "G1 Fall_theta_nre": -1.328797906,
    "G1 Fall_pr_1_99_nre": 15,
    "G1 Fall_ss_nre": 84,
    "G1 Fall_dss_nre": 367,
    "G1 Winter_theta_nre": -0.96475456,
    "G1 Winter_pr_1_99_nre": 15,
    "G1 Winter_ss_nre": 85,
    "G1 Winter_dss_nre": 404,
    "G1 Spring_theta_nre": -0.606961517,
    "G1 Spring_pr_1_99_nre": 15,
    "G1 Spring_ss_nre": 85,
    "G1 Spring_dss_nre": 439,
    "G2 Fall_theta_nre": -0.538902428,
    "G2 Fall_pr_1_99_nre": 15,
    "G2 Fall_ss_nre": 84,
    "G2 Fall_dss_nre": 446,
    "G2 Winter_theta_nre": -0.14643571,
    "G2 Winter_pr_1_99_nre": 15,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 485,
    "G2 Spring_theta_nre": -0.026477393,
    "G2 Spring_pr_1_99_nre": 15,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 497,
    "G3 Fall_theta_nre": 0.311110767,
    "G3 Fall_pr_1_99_nre": 15,
    "G3 Fall_ss_nre": 85,
    "G3 Fall_dss_nre": 531,
    "G3 Winter_theta_nre": 0.636108693,
    "G3 Winter_pr_1_99_nre": 15,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 564,
    "G3 Spring_theta_nre": 1.033542877,
    "G3 Spring_pr_1_99_nre": 15,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 603
  },
  {
    "ID": 77,
    "KG Winter_theta_nre": -1.826933042,
    "KG Winter_pr_1_99_nre": 15,
    "KG Winter_ss_nre": 84,
    "KG Winter_dss_nre": 317,
    "KG Spring_theta_nre": -1.49119381,
    "KG Spring_pr_1_99_nre": 15,
    "KG Spring_ss_nre": 85,
    "KG Spring_dss_nre": 351,
    "G1 Fall_theta_nre": -1.322566096,
    "G1 Fall_pr_1_99_nre": 15,
    "G1 Fall_ss_nre": 84,
    "G1 Fall_dss_nre": 368,
    "G1 Winter_theta_nre": -0.962656847,
    "G1 Winter_pr_1_99_nre": 15,
    "G1 Winter_ss_nre": 85,
    "G1 Winter_dss_nre": 404,
    "G1 Spring_theta_nre": -0.588156029,
    "G1 Spring_pr_1_99_nre": 15,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 441,
    "G2 Fall_theta_nre": -0.538521307,
    "G2 Fall_pr_1_99_nre": 15,
    "G2 Fall_ss_nre": 84,
    "G2 Fall_dss_nre": 446,
    "G2 Winter_theta_nre": -0.143549268,
    "G2 Winter_pr_1_99_nre": 15,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 486,
    "G2 Spring_theta_nre": -0.026200529,
    "G2 Spring_pr_1_99_nre": 15,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 497,
    "G3 Fall_theta_nre": 0.316445871,
    "G3 Fall_pr_1_99_nre": 15,
    "G3 Fall_ss_nre": 86,
    "G3 Fall_dss_nre": 532,
    "G3 Winter_theta_nre": 0.6421597,
    "G3 Winter_pr_1_99_nre": 15,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 564,
    "G3 Spring_theta_nre": 1.035469908,
    "G3 Spring_pr_1_99_nre": 15,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 604
  },
  {
    "ID": 78,
    "KG Winter_theta_nre": -1.819997879,
    "KG Winter_pr_1_99_nre": 16,
    "KG Winter_ss_nre": 84,
    "KG Winter_dss_nre": 318,
    "KG Spring_theta_nre": -1.488685952,
    "KG Spring_pr_1_99_nre": 16,
    "KG Spring_ss_nre": 85,
    "KG Spring_dss_nre": 351,
    "G1 Fall_theta_nre": -1.316869858,
    "G1 Fall_pr_1_99_nre": 16,
    "G1 Fall_ss_nre": 84,
    "G1 Fall_dss_nre": 368,
    "G1 Winter_theta_nre": -0.955448949,
    "G1 Winter_pr_1_99_nre": 16,
    "G1 Winter_ss_nre": 85,
    "G1 Winter_dss_nre": 404,
    "G1 Spring_theta_nre": -0.584992604,
    "G1 Spring_pr_1_99_nre": 16,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 442,
    "G2 Fall_theta_nre": -0.532386356,
    "G2 Fall_pr_1_99_nre": 16,
    "G2 Fall_ss_nre": 84,
    "G2 Fall_dss_nre": 447,
    "G2 Winter_theta_nre": -0.136993837,
    "G2 Winter_pr_1_99_nre": 16,
    "G2 Winter_ss_nre": 84,
    "G2 Winter_dss_nre": 486,
    "G2 Spring_theta_nre": -0.020274692,
    "G2 Spring_pr_1_99_nre": 16,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 498,
    "G3 Fall_theta_nre": 0.32497405,
    "G3 Fall_pr_1_99_nre": 16,
    "G3 Fall_ss_nre": 86,
    "G3 Fall_dss_nre": 532,
    "G3 Winter_theta_nre": 0.646855117,
    "G3 Winter_pr_1_99_nre": 16,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 565,
    "G3 Spring_theta_nre": 1.03580319,
    "G3 Spring_pr_1_99_nre": 16,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 604
  },
  {
    "ID": 79,
    "KG Winter_theta_nre": -1.799512404,
    "KG Winter_pr_1_99_nre": 16,
    "KG Winter_ss_nre": 85,
    "KG Winter_dss_nre": 320,
    "KG Spring_theta_nre": -1.482761399,
    "KG Spring_pr_1_99_nre": 16,
    "KG Spring_ss_nre": 85,
    "KG Spring_dss_nre": 352,
    "G1 Fall_theta_nre": -1.315820541,
    "G1 Fall_pr_1_99_nre": 16,
    "G1 Fall_ss_nre": 84,
    "G1 Fall_dss_nre": 368,
    "G1 Winter_theta_nre": -0.955188684,
    "G1 Winter_pr_1_99_nre": 16,
    "G1 Winter_ss_nre": 85,
    "G1 Winter_dss_nre": 404,
    "G1 Spring_theta_nre": -0.579739531,
    "G1 Spring_pr_1_99_nre": 16,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 442,
    "G2 Fall_theta_nre": -0.526237449,
    "G2 Fall_pr_1_99_nre": 16,
    "G2 Fall_ss_nre": 84,
    "G2 Fall_dss_nre": 447,
    "G2 Winter_theta_nre": -0.135126549,
    "G2 Winter_pr_1_99_nre": 16,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 486,
    "G2 Spring_theta_nre": -0.019466268,
    "G2 Spring_pr_1_99_nre": 16,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 498,
    "G3 Fall_theta_nre": 0.32600321,
    "G3 Fall_pr_1_99_nre": 16,
    "G3 Fall_ss_nre": 86,
    "G3 Fall_dss_nre": 533,
    "G3 Winter_theta_nre": 0.649837991,
    "G3 Winter_pr_1_99_nre": 16,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 565,
    "G3 Spring_theta_nre": 1.038201856,
    "G3 Spring_pr_1_99_nre": 16,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 604
  },
  {
    "ID": 80,
    "KG Winter_theta_nre": -1.799196626,
    "KG Winter_pr_1_99_nre": 16,
    "KG Winter_ss_nre": 85,
    "KG Winter_dss_nre": 320,
    "KG Spring_theta_nre": -1.481722567,
    "KG Spring_pr_1_99_nre": 16,
    "KG Spring_ss_nre": 85,
    "KG Spring_dss_nre": 352,
    "G1 Fall_theta_nre": -1.306237232,
    "G1 Fall_pr_1_99_nre": 16,
    "G1 Fall_ss_nre": 84,
    "G1 Fall_dss_nre": 369,
    "G1 Winter_theta_nre": -0.953533337,
    "G1 Winter_pr_1_99_nre": 16,
    "G1 Winter_ss_nre": 85,
    "G1 Winter_dss_nre": 405,
    "G1 Spring_theta_nre": -0.576405314,
    "G1 Spring_pr_1_99_nre": 16,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 442,
    "G2 Fall_theta_nre": -0.521471142,
    "G2 Fall_pr_1_99_nre": 16,
    "G2 Fall_ss_nre": 84,
    "G2 Fall_dss_nre": 448,
    "G2 Winter_theta_nre": -0.122668747,
    "G2 Winter_pr_1_99_nre": 16,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 488,
    "G2 Spring_theta_nre": -0.006296491,
    "G2 Spring_pr_1_99_nre": 16,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 499,
    "G3 Fall_theta_nre": 0.333350625,
    "G3 Fall_pr_1_99_nre": 16,
    "G3 Fall_ss_nre": 86,
    "G3 Fall_dss_nre": 533,
    "G3 Winter_theta_nre": 0.650704983,
    "G3 Winter_pr_1_99_nre": 16,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 565,
    "G3 Spring_theta_nre": 1.045149984,
    "G3 Spring_pr_1_99_nre": 16,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 605
  },
  {
    "ID": 81,
    "KG Winter_theta_nre": -1.776369816,
    "KG Winter_pr_1_99_nre": 16,
    "KG Winter_ss_nre": 85,
    "KG Winter_dss_nre": 322,
    "KG Spring_theta_nre": -1.474386074,
    "KG Spring_pr_1_99_nre": 16,
    "KG Spring_ss_nre": 85,
    "KG Spring_dss_nre": 353,
    "G1 Fall_theta_nre": -1.29691486,
    "G1 Fall_pr_1_99_nre": 16,
    "G1 Fall_ss_nre": 84,
    "G1 Fall_dss_nre": 370,
    "G1 Winter_theta_nre": -0.944679345,
    "G1 Winter_pr_1_99_nre": 16,
    "G1 Winter_ss_nre": 85,
    "G1 Winter_dss_nre": 406,
    "G1 Spring_theta_nre": -0.57202795,
    "G1 Spring_pr_1_99_nre": 16,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 443,
    "G2 Fall_theta_nre": -0.503644337,
    "G2 Fall_pr_1_99_nre": 16,
    "G2 Fall_ss_nre": 85,
    "G2 Fall_dss_nre": 450,
    "G2 Winter_theta_nre": -0.117801053,
    "G2 Winter_pr_1_99_nre": 16,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 488,
    "G2 Spring_theta_nre": 0.012706488,
    "G2 Spring_pr_1_99_nre": 16,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 501,
    "G3 Fall_theta_nre": 0.336602401,
    "G3 Fall_pr_1_99_nre": 16,
    "G3 Fall_ss_nre": 86,
    "G3 Fall_dss_nre": 534,
    "G3 Winter_theta_nre": 0.65308254,
    "G3 Winter_pr_1_99_nre": 16,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 565,
    "G3 Spring_theta_nre": 1.046609321,
    "G3 Spring_pr_1_99_nre": 16,
    "G3 Spring_ss_nre": 85,
    "G3 Spring_dss_nre": 605
  },
  {
    "ID": 82,
    "KG Winter_theta_nre": -1.774790262,
    "KG Winter_pr_1_99_nre": 16,
    "KG Winter_ss_nre": 85,
    "KG Winter_dss_nre": 323,
    "KG Spring_theta_nre": -1.470864323,
    "KG Spring_pr_1_99_nre": 16,
    "KG Spring_ss_nre": 85,
    "KG Spring_dss_nre": 353,
    "G1 Fall_theta_nre": -1.283189939,
    "G1 Fall_pr_1_99_nre": 16,
    "G1 Fall_ss_nre": 85,
    "G1 Fall_dss_nre": 372,
    "G1 Winter_theta_nre": -0.943004614,
    "G1 Winter_pr_1_99_nre": 16,
    "G1 Winter_ss_nre": 85,
    "G1 Winter_dss_nre": 406,
    "G1 Spring_theta_nre": -0.56821452,
    "G1 Spring_pr_1_99_nre": 16,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 443,
    "G2 Fall_theta_nre": -0.495460519,
    "G2 Fall_pr_1_99_nre": 16,
    "G2 Fall_ss_nre": 85,
    "G2 Fall_dss_nre": 450,
    "G2 Winter_theta_nre": -0.111637335,
    "G2 Winter_pr_1_99_nre": 16,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 489,
    "G2 Spring_theta_nre": 0.01397726,
    "G2 Spring_pr_1_99_nre": 16,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 501,
    "G3 Fall_theta_nre": 0.341115801,
    "G3 Fall_pr_1_99_nre": 16,
    "G3 Fall_ss_nre": 86,
    "G3 Fall_dss_nre": 534,
    "G3 Winter_theta_nre": 0.655077518,
    "G3 Winter_pr_1_99_nre": 16,
    "G3 Winter_ss_nre": 84,
    "G3 Winter_dss_nre": 566,
    "G3 Spring_theta_nre": 1.052236533,
    "G3 Spring_pr_1_99_nre": 16,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 605
  },
  {
    "ID": 83,
    "KG Winter_theta_nre": -1.771300412,
    "KG Winter_pr_1_99_nre": 17,
    "KG Winter_ss_nre": 85,
    "KG Winter_dss_nre": 323,
    "KG Spring_theta_nre": -1.470413914,
    "KG Spring_pr_1_99_nre": 17,
    "KG Spring_ss_nre": 85,
    "KG Spring_dss_nre": 353,
    "G1 Fall_theta_nre": -1.276483478,
    "G1 Fall_pr_1_99_nre": 17,
    "G1 Fall_ss_nre": 85,
    "G1 Fall_dss_nre": 372,
    "G1 Winter_theta_nre": -0.935052634,
    "G1 Winter_pr_1_99_nre": 17,
    "G1 Winter_ss_nre": 85,
    "G1 Winter_dss_nre": 406,
    "G1 Spring_theta_nre": -0.565718452,
    "G1 Spring_pr_1_99_nre": 17,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 443,
    "G2 Fall_theta_nre": -0.491071831,
    "G2 Fall_pr_1_99_nre": 17,
    "G2 Fall_ss_nre": 85,
    "G2 Fall_dss_nre": 451,
    "G2 Winter_theta_nre": -0.110387477,
    "G2 Winter_pr_1_99_nre": 17,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 489,
    "G2 Spring_theta_nre": 0.014980888,
    "G2 Spring_pr_1_99_nre": 17,
    "G2 Spring_ss_nre": 84,
    "G2 Spring_dss_nre": 501,
    "G3 Fall_theta_nre": 0.361846301,
    "G3 Fall_pr_1_99_nre": 17,
    "G3 Fall_ss_nre": 86,
    "G3 Fall_dss_nre": 536,
    "G3 Winter_theta_nre": 0.671926747,
    "G3 Winter_pr_1_99_nre": 17,
    "G3 Winter_ss_nre": 85,
    "G3 Winter_dss_nre": 567,
    "G3 Spring_theta_nre": 1.054593741,
    "G3 Spring_pr_1_99_nre": 17,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 605
  },
  {
    "ID": 84,
    "KG Winter_theta_nre": -1.769986218,
    "KG Winter_pr_1_99_nre": 17,
    "KG Winter_ss_nre": 85,
    "KG Winter_dss_nre": 323,
    "KG Spring_theta_nre": -1.460680165,
    "KG Spring_pr_1_99_nre": 17,
    "KG Spring_ss_nre": 86,
    "KG Spring_dss_nre": 354,
    "G1 Fall_theta_nre": -1.257264223,
    "G1 Fall_pr_1_99_nre": 17,
    "G1 Fall_ss_nre": 85,
    "G1 Fall_dss_nre": 374,
    "G1 Winter_theta_nre": -0.921257018,
    "G1 Winter_pr_1_99_nre": 17,
    "G1 Winter_ss_nre": 86,
    "G1 Winter_dss_nre": 408,
    "G1 Spring_theta_nre": -0.565131985,
    "G1 Spring_pr_1_99_nre": 17,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 443,
    "G2 Fall_theta_nre": -0.47303805,
    "G2 Fall_pr_1_99_nre": 17,
    "G2 Fall_ss_nre": 85,
    "G2 Fall_dss_nre": 453,
    "G2 Winter_theta_nre": -0.108854683,
    "G2 Winter_pr_1_99_nre": 17,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 489,
    "G2 Spring_theta_nre": 0.046298873,
    "G2 Spring_pr_1_99_nre": 17,
    "G2 Spring_ss_nre": 85,
    "G2 Spring_dss_nre": 505,
    "G3 Fall_theta_nre": 0.36847262,
    "G3 Fall_pr_1_99_nre": 17,
    "G3 Fall_ss_nre": 86,
    "G3 Fall_dss_nre": 537,
    "G3 Winter_theta_nre": 0.683882189,
    "G3 Winter_pr_1_99_nre": 17,
    "G3 Winter_ss_nre": 85,
    "G3 Winter_dss_nre": 568,
    "G3 Spring_theta_nre": 1.063994031,
    "G3 Spring_pr_1_99_nre": 17,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 606
  },
  {
    "ID": 85,
    "KG Winter_theta_nre": -1.767200387,
    "KG Winter_pr_1_99_nre": 17,
    "KG Winter_ss_nre": 85,
    "KG Winter_dss_nre": 323,
    "KG Spring_theta_nre": -1.457551631,
    "KG Spring_pr_1_99_nre": 17,
    "KG Spring_ss_nre": 86,
    "KG Spring_dss_nre": 354,
    "G1 Fall_theta_nre": -1.255405722,
    "G1 Fall_pr_1_99_nre": 17,
    "G1 Fall_ss_nre": 85,
    "G1 Fall_dss_nre": 374,
    "G1 Winter_theta_nre": -0.916375605,
    "G1 Winter_pr_1_99_nre": 17,
    "G1 Winter_ss_nre": 86,
    "G1 Winter_dss_nre": 408,
    "G1 Spring_theta_nre": -0.559068924,
    "G1 Spring_pr_1_99_nre": 17,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 444,
    "G2 Fall_theta_nre": -0.472397634,
    "G2 Fall_pr_1_99_nre": 17,
    "G2 Fall_ss_nre": 85,
    "G2 Fall_dss_nre": 453,
    "G2 Winter_theta_nre": -0.107553133,
    "G2 Winter_pr_1_99_nre": 17,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 489,
    "G2 Spring_theta_nre": 0.046369148,
    "G2 Spring_pr_1_99_nre": 17,
    "G2 Spring_ss_nre": 85,
    "G2 Spring_dss_nre": 505,
    "G3 Fall_theta_nre": 0.370639592,
    "G3 Fall_pr_1_99_nre": 17,
    "G3 Fall_ss_nre": 86,
    "G3 Fall_dss_nre": 537,
    "G3 Winter_theta_nre": 0.699116749,
    "G3 Winter_pr_1_99_nre": 17,
    "G3 Winter_ss_nre": 85,
    "G3 Winter_dss_nre": 570,
    "G3 Spring_theta_nre": 1.068435306,
    "G3 Spring_pr_1_99_nre": 17,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 607
  },
  {
    "ID": 86,
    "KG Winter_theta_nre": -1.755048923,
    "KG Winter_pr_1_99_nre": 17,
    "KG Winter_ss_nre": 85,
    "KG Winter_dss_nre": 324,
    "KG Spring_theta_nre": -1.447685825,
    "KG Spring_pr_1_99_nre": 17,
    "KG Spring_ss_nre": 86,
    "KG Spring_dss_nre": 355,
    "G1 Fall_theta_nre": -1.244969201,
    "G1 Fall_pr_1_99_nre": 17,
    "G1 Fall_ss_nre": 85,
    "G1 Fall_dss_nre": 376,
    "G1 Winter_theta_nre": -0.911102732,
    "G1 Winter_pr_1_99_nre": 17,
    "G1 Winter_ss_nre": 86,
    "G1 Winter_dss_nre": 409,
    "G1 Spring_theta_nre": -0.558194089,
    "G1 Spring_pr_1_99_nre": 17,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 444,
    "G2 Fall_theta_nre": -0.471182218,
    "G2 Fall_pr_1_99_nre": 17,
    "G2 Fall_ss_nre": 85,
    "G2 Fall_dss_nre": 453,
    "G2 Winter_theta_nre": -0.106000496,
    "G2 Winter_pr_1_99_nre": 17,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 489,
    "G2 Spring_theta_nre": 0.05004592,
    "G2 Spring_pr_1_99_nre": 17,
    "G2 Spring_ss_nre": 85,
    "G2 Spring_dss_nre": 505,
    "G3 Fall_theta_nre": 0.371371964,
    "G3 Fall_pr_1_99_nre": 17,
    "G3 Fall_ss_nre": 86,
    "G3 Fall_dss_nre": 537,
    "G3 Winter_theta_nre": 0.700753466,
    "G3 Winter_pr_1_99_nre": 17,
    "G3 Winter_ss_nre": 85,
    "G3 Winter_dss_nre": 570,
    "G3 Spring_theta_nre": 1.078271258,
    "G3 Spring_pr_1_99_nre": 17,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 608
  },
  {
    "ID": 87,
    "KG Winter_theta_nre": -1.751262687,
    "KG Winter_pr_1_99_nre": 17,
    "KG Winter_ss_nre": 85,
    "KG Winter_dss_nre": 325,
    "KG Spring_theta_nre": -1.446433739,
    "KG Spring_pr_1_99_nre": 17,
    "KG Spring_ss_nre": 86,
    "KG Spring_dss_nre": 355,
    "G1 Fall_theta_nre": -1.243401953,
    "G1 Fall_pr_1_99_nre": 17,
    "G1 Fall_ss_nre": 85,
    "G1 Fall_dss_nre": 376,
    "G1 Winter_theta_nre": -0.907569033,
    "G1 Winter_pr_1_99_nre": 17,
    "G1 Winter_ss_nre": 86,
    "G1 Winter_dss_nre": 409,
    "G1 Spring_theta_nre": -0.55753661,
    "G1 Spring_pr_1_99_nre": 17,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 444,
    "G2 Fall_theta_nre": -0.466876466,
    "G2 Fall_pr_1_99_nre": 17,
    "G2 Fall_ss_nre": 85,
    "G2 Fall_dss_nre": 453,
    "G2 Winter_theta_nre": -0.095092362,
    "G2 Winter_pr_1_99_nre": 17,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 490,
    "G2 Spring_theta_nre": 0.051324457,
    "G2 Spring_pr_1_99_nre": 17,
    "G2 Spring_ss_nre": 85,
    "G2 Spring_dss_nre": 505,
    "G3 Fall_theta_nre": 0.384284152,
    "G3 Fall_pr_1_99_nre": 17,
    "G3 Fall_ss_nre": 87,
    "G3 Fall_dss_nre": 538,
    "G3 Winter_theta_nre": 0.730383744,
    "G3 Winter_pr_1_99_nre": 17,
    "G3 Winter_ss_nre": 86,
    "G3 Winter_dss_nre": 573,
    "G3 Spring_theta_nre": 1.079753535,
    "G3 Spring_pr_1_99_nre": 17,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 608
  },
  {
    "ID": 88,
    "KG Winter_theta_nre": -1.746056453,
    "KG Winter_pr_1_99_nre": 18,
    "KG Winter_ss_nre": 86,
    "KG Winter_dss_nre": 325,
    "KG Spring_theta_nre": -1.445662808,
    "KG Spring_pr_1_99_nre": 18,
    "KG Spring_ss_nre": 86,
    "KG Spring_dss_nre": 355,
    "G1 Fall_theta_nre": -1.240938205,
    "G1 Fall_pr_1_99_nre": 18,
    "G1 Fall_ss_nre": 85,
    "G1 Fall_dss_nre": 376,
    "G1 Winter_theta_nre": -0.896623785,
    "G1 Winter_pr_1_99_nre": 18,
    "G1 Winter_ss_nre": 86,
    "G1 Winter_dss_nre": 410,
    "G1 Spring_theta_nre": -0.554746798,
    "G1 Spring_pr_1_99_nre": 18,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 445,
    "G2 Fall_theta_nre": -0.465239394,
    "G2 Fall_pr_1_99_nre": 18,
    "G2 Fall_ss_nre": 85,
    "G2 Fall_dss_nre": 453,
    "G2 Winter_theta_nre": -0.09346117,
    "G2 Winter_pr_1_99_nre": 18,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 491,
    "G2 Spring_theta_nre": 0.057234567,
    "G2 Spring_pr_1_99_nre": 18,
    "G2 Spring_ss_nre": 85,
    "G2 Spring_dss_nre": 506,
    "G3 Fall_theta_nre": 0.388286362,
    "G3 Fall_pr_1_99_nre": 18,
    "G3 Fall_ss_nre": 87,
    "G3 Fall_dss_nre": 539,
    "G3 Winter_theta_nre": 0.731427366,
    "G3 Winter_pr_1_99_nre": 18,
    "G3 Winter_ss_nre": 86,
    "G3 Winter_dss_nre": 573,
    "G3 Spring_theta_nre": 1.084484765,
    "G3 Spring_pr_1_99_nre": 18,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 608
  },
  {
    "ID": 89,
    "KG Winter_theta_nre": -1.742048618,
    "KG Winter_pr_1_99_nre": 18,
    "KG Winter_ss_nre": 86,
    "KG Winter_dss_nre": 326,
    "KG Spring_theta_nre": -1.439941432,
    "KG Spring_pr_1_99_nre": 18,
    "KG Spring_ss_nre": 86,
    "KG Spring_dss_nre": 356,
    "G1 Fall_theta_nre": -1.234637949,
    "G1 Fall_pr_1_99_nre": 18,
    "G1 Fall_ss_nre": 85,
    "G1 Fall_dss_nre": 377,
    "G1 Winter_theta_nre": -0.889404634,
    "G1 Winter_pr_1_99_nre": 18,
    "G1 Winter_ss_nre": 86,
    "G1 Winter_dss_nre": 411,
    "G1 Spring_theta_nre": -0.550071361,
    "G1 Spring_pr_1_99_nre": 18,
    "G1 Spring_ss_nre": 86,
    "G1 Spring_dss_nre": 445,
    "G2 Fall_theta_nre": -0.464088076,
    "G2 Fall_pr_1_99_nre": 18,
    "G2 Fall_ss_nre": 85,
    "G2 Fall_dss_nre": 454,
    "G2 Winter_theta_nre": -0.092033333,
    "G2 Winter_pr_1_99_nre": 18,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 491,
    "G2 Spring_theta_nre": 0.079090032,
    "G2 Spring_pr_1_99_nre": 18,
    "G2 Spring_ss_nre": 86,
    "G2 Spring_dss_nre": 508,
    "G3 Fall_theta_nre": 0.392133432,
    "G3 Fall_pr_1_99_nre": 18,
    "G3 Fall_ss_nre": 87,
    "G3 Fall_dss_nre": 539,
    "G3 Winter_theta_nre": 0.733198563,
    "G3 Winter_pr_1_99_nre": 18,
    "G3 Winter_ss_nre": 86,
    "G3 Winter_dss_nre": 573,
    "G3 Spring_theta_nre": 1.085352577,
    "G3 Spring_pr_1_99_nre": 18,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 609
  },
  {
    "ID": 90,
    "KG Winter_theta_nre": -1.727867949,
    "KG Winter_pr_1_99_nre": 18,
    "KG Winter_ss_nre": 86,
    "KG Winter_dss_nre": 327,
    "KG Spring_theta_nre": -1.438014003,
    "KG Spring_pr_1_99_nre": 18,
    "KG Spring_ss_nre": 86,
    "KG Spring_dss_nre": 356,
    "G1 Fall_theta_nre": -1.228104466,
    "G1 Fall_pr_1_99_nre": 18,
    "G1 Fall_ss_nre": 86,
    "G1 Fall_dss_nre": 377,
    "G1 Winter_theta_nre": -0.875500308,
    "G1 Winter_pr_1_99_nre": 18,
    "G1 Winter_ss_nre": 86,
    "G1 Winter_dss_nre": 412,
    "G1 Spring_theta_nre": -0.541169982,
    "G1 Spring_pr_1_99_nre": 18,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 446,
    "G2 Fall_theta_nre": -0.452831868,
    "G2 Fall_pr_1_99_nre": 18,
    "G2 Fall_ss_nre": 86,
    "G2 Fall_dss_nre": 455,
    "G2 Winter_theta_nre": -0.085749865,
    "G2 Winter_pr_1_99_nre": 18,
    "G2 Winter_ss_nre": 85,
    "G2 Winter_dss_nre": 491,
    "G2 Spring_theta_nre": 0.09212033,
    "G2 Spring_pr_1_99_nre": 18,
    "G2 Spring_ss_nre": 86,
    "G2 Spring_dss_nre": 509,
    "G3 Fall_theta_nre": 0.394571111,
    "G3 Fall_pr_1_99_nre": 18,
    "G3 Fall_ss_nre": 87,
    "G3 Fall_dss_nre": 539,
    "G3 Winter_theta_nre": 0.737954893,
    "G3 Winter_pr_1_99_nre": 18,
    "G3 Winter_ss_nre": 86,
    "G3 Winter_dss_nre": 574,
    "G3 Spring_theta_nre": 1.08689175,
    "G3 Spring_pr_1_99_nre": 18,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 609
  },
  {
    "ID": 91,
    "KG Winter_theta_nre": -1.674403794,
    "KG Winter_pr_1_99_nre": 18,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 333,
    "KG Spring_theta_nre": -1.430214423,
    "KG Spring_pr_1_99_nre": 18,
    "KG Spring_ss_nre": 86,
    "KG Spring_dss_nre": 357,
    "G1 Fall_theta_nre": -1.227266139,
    "G1 Fall_pr_1_99_nre": 18,
    "G1 Fall_ss_nre": 86,
    "G1 Fall_dss_nre": 377,
    "G1 Winter_theta_nre": -0.870121526,
    "G1 Winter_pr_1_99_nre": 18,
    "G1 Winter_ss_nre": 86,
    "G1 Winter_dss_nre": 413,
    "G1 Spring_theta_nre": -0.539719933,
    "G1 Spring_pr_1_99_nre": 18,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 446,
    "G2 Fall_theta_nre": -0.446765821,
    "G2 Fall_pr_1_99_nre": 18,
    "G2 Fall_ss_nre": 86,
    "G2 Fall_dss_nre": 455,
    "G2 Winter_theta_nre": -0.07924023,
    "G2 Winter_pr_1_99_nre": 18,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 492,
    "G2 Spring_theta_nre": 0.093354747,
    "G2 Spring_pr_1_99_nre": 18,
    "G2 Spring_ss_nre": 86,
    "G2 Spring_dss_nre": 509,
    "G3 Fall_theta_nre": 0.394691137,
    "G3 Fall_pr_1_99_nre": 18,
    "G3 Fall_ss_nre": 87,
    "G3 Fall_dss_nre": 539,
    "G3 Winter_theta_nre": 0.741418857,
    "G3 Winter_pr_1_99_nre": 18,
    "G3 Winter_ss_nre": 86,
    "G3 Winter_dss_nre": 574,
    "G3 Spring_theta_nre": 1.087856935,
    "G3 Spring_pr_1_99_nre": 18,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 609
  },
  {
    "ID": 92,
    "KG Winter_theta_nre": -1.672497225,
    "KG Winter_pr_1_99_nre": 18,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 333,
    "KG Spring_theta_nre": -1.430045207,
    "KG Spring_pr_1_99_nre": 18,
    "KG Spring_ss_nre": 86,
    "KG Spring_dss_nre": 357,
    "G1 Fall_theta_nre": -1.223520456,
    "G1 Fall_pr_1_99_nre": 18,
    "G1 Fall_ss_nre": 86,
    "G1 Fall_dss_nre": 378,
    "G1 Winter_theta_nre": -0.867665926,
    "G1 Winter_pr_1_99_nre": 18,
    "G1 Winter_ss_nre": 87,
    "G1 Winter_dss_nre": 413,
    "G1 Spring_theta_nre": -0.536068735,
    "G1 Spring_pr_1_99_nre": 18,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 446,
    "G2 Fall_theta_nre": -0.434021048,
    "G2 Fall_pr_1_99_nre": 18,
    "G2 Fall_ss_nre": 86,
    "G2 Fall_dss_nre": 457,
    "G2 Winter_theta_nre": -0.067421252,
    "G2 Winter_pr_1_99_nre": 18,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 493,
    "G2 Spring_theta_nre": 0.098259129,
    "G2 Spring_pr_1_99_nre": 18,
    "G2 Spring_ss_nre": 86,
    "G2 Spring_dss_nre": 510,
    "G3 Fall_theta_nre": 0.401811253,
    "G3 Fall_pr_1_99_nre": 18,
    "G3 Fall_ss_nre": 87,
    "G3 Fall_dss_nre": 540,
    "G3 Winter_theta_nre": 0.743858582,
    "G3 Winter_pr_1_99_nre": 18,
    "G3 Winter_ss_nre": 86,
    "G3 Winter_dss_nre": 574,
    "G3 Spring_theta_nre": 1.088182659,
    "G3 Spring_pr_1_99_nre": 18,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 609
  },
  {
    "ID": 93,
    "KG Winter_theta_nre": -1.670867226,
    "KG Winter_pr_1_99_nre": 19,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 333,
    "KG Spring_theta_nre": -1.418003809,
    "KG Spring_pr_1_99_nre": 19,
    "KG Spring_ss_nre": 86,
    "KG Spring_dss_nre": 358,
    "G1 Fall_theta_nre": -1.199839685,
    "G1 Fall_pr_1_99_nre": 19,
    "G1 Fall_ss_nre": 86,
    "G1 Fall_dss_nre": 380,
    "G1 Winter_theta_nre": -0.861602578,
    "G1 Winter_pr_1_99_nre": 19,
    "G1 Winter_ss_nre": 87,
    "G1 Winter_dss_nre": 414,
    "G1 Spring_theta_nre": -0.534973603,
    "G1 Spring_pr_1_99_nre": 19,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 447,
    "G2 Fall_theta_nre": -0.424540036,
    "G2 Fall_pr_1_99_nre": 19,
    "G2 Fall_ss_nre": 86,
    "G2 Fall_dss_nre": 458,
    "G2 Winter_theta_nre": -0.066856406,
    "G2 Winter_pr_1_99_nre": 19,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 493,
    "G2 Spring_theta_nre": 0.103886294,
    "G2 Spring_pr_1_99_nre": 19,
    "G2 Spring_ss_nre": 86,
    "G2 Spring_dss_nre": 510,
    "G3 Fall_theta_nre": 0.416558755,
    "G3 Fall_pr_1_99_nre": 19,
    "G3 Fall_ss_nre": 87,
    "G3 Fall_dss_nre": 542,
    "G3 Winter_theta_nre": 0.748327259,
    "G3 Winter_pr_1_99_nre": 19,
    "G3 Winter_ss_nre": 86,
    "G3 Winter_dss_nre": 575,
    "G3 Spring_theta_nre": 1.089656051,
    "G3 Spring_pr_1_99_nre": 19,
    "G3 Spring_ss_nre": 86,
    "G3 Spring_dss_nre": 609
  },
  {
    "ID": 94,
    "KG Winter_theta_nre": -1.669103078,
    "KG Winter_pr_1_99_nre": 19,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 333,
    "KG Spring_theta_nre": -1.409898218,
    "KG Spring_pr_1_99_nre": 19,
    "KG Spring_ss_nre": 87,
    "KG Spring_dss_nre": 359,
    "G1 Fall_theta_nre": -1.187346349,
    "G1 Fall_pr_1_99_nre": 19,
    "G1 Fall_ss_nre": 86,
    "G1 Fall_dss_nre": 381,
    "G1 Winter_theta_nre": -0.859811897,
    "G1 Winter_pr_1_99_nre": 19,
    "G1 Winter_ss_nre": 87,
    "G1 Winter_dss_nre": 414,
    "G1 Spring_theta_nre": -0.5282319,
    "G1 Spring_pr_1_99_nre": 19,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 447,
    "G2 Fall_theta_nre": -0.415041213,
    "G2 Fall_pr_1_99_nre": 19,
    "G2 Fall_ss_nre": 86,
    "G2 Fall_dss_nre": 458,
    "G2 Winter_theta_nre": -0.063534694,
    "G2 Winter_pr_1_99_nre": 19,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 494,
    "G2 Spring_theta_nre": 0.109958705,
    "G2 Spring_pr_1_99_nre": 19,
    "G2 Spring_ss_nre": 86,
    "G2 Spring_dss_nre": 511,
    "G3 Fall_theta_nre": 0.417602431,
    "G3 Fall_pr_1_99_nre": 19,
    "G3 Fall_ss_nre": 87,
    "G3 Fall_dss_nre": 542,
    "G3 Winter_theta_nre": 0.748631249,
    "G3 Winter_pr_1_99_nre": 19,
    "G3 Winter_ss_nre": 86,
    "G3 Winter_dss_nre": 575,
    "G3 Spring_theta_nre": 1.094300673,
    "G3 Spring_pr_1_99_nre": 19,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 609
  },
  {
    "ID": 95,
    "KG Winter_theta_nre": -1.668709259,
    "KG Winter_pr_1_99_nre": 19,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 333,
    "KG Spring_theta_nre": -1.394555385,
    "KG Spring_pr_1_99_nre": 19,
    "KG Spring_ss_nre": 87,
    "KG Spring_dss_nre": 361,
    "G1 Fall_theta_nre": -1.184967684,
    "G1 Fall_pr_1_99_nre": 19,
    "G1 Fall_ss_nre": 86,
    "G1 Fall_dss_nre": 382,
    "G1 Winter_theta_nre": -0.846562046,
    "G1 Winter_pr_1_99_nre": 19,
    "G1 Winter_ss_nre": 87,
    "G1 Winter_dss_nre": 415,
    "G1 Spring_theta_nre": -0.515900247,
    "G1 Spring_pr_1_99_nre": 19,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 448,
    "G2 Fall_theta_nre": -0.409494704,
    "G2 Fall_pr_1_99_nre": 19,
    "G2 Fall_ss_nre": 86,
    "G2 Fall_dss_nre": 459,
    "G2 Winter_theta_nre": -0.050113261,
    "G2 Winter_pr_1_99_nre": 19,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 495,
    "G2 Spring_theta_nre": 0.119735353,
    "G2 Spring_pr_1_99_nre": 19,
    "G2 Spring_ss_nre": 86,
    "G2 Spring_dss_nre": 512,
    "G3 Fall_theta_nre": 0.447503496,
    "G3 Fall_pr_1_99_nre": 19,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 545,
    "G3 Winter_theta_nre": 0.757588436,
    "G3 Winter_pr_1_99_nre": 19,
    "G3 Winter_ss_nre": 87,
    "G3 Winter_dss_nre": 576,
    "G3 Spring_theta_nre": 1.096957822,
    "G3 Spring_pr_1_99_nre": 19,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 610
  },
  {
    "ID": 96,
    "KG Winter_theta_nre": -1.667138104,
    "KG Winter_pr_1_99_nre": 19,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 333,
    "KG Spring_theta_nre": -1.39028102,
    "KG Spring_pr_1_99_nre": 19,
    "KG Spring_ss_nre": 87,
    "KG Spring_dss_nre": 361,
    "G1 Fall_theta_nre": -1.183355036,
    "G1 Fall_pr_1_99_nre": 19,
    "G1 Fall_ss_nre": 86,
    "G1 Fall_dss_nre": 382,
    "G1 Winter_theta_nre": -0.840924797,
    "G1 Winter_pr_1_99_nre": 19,
    "G1 Winter_ss_nre": 87,
    "G1 Winter_dss_nre": 416,
    "G1 Spring_theta_nre": -0.515136502,
    "G1 Spring_pr_1_99_nre": 19,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 448,
    "G2 Fall_theta_nre": -0.406817866,
    "G2 Fall_pr_1_99_nre": 19,
    "G2 Fall_ss_nre": 86,
    "G2 Fall_dss_nre": 459,
    "G2 Winter_theta_nre": -0.048275633,
    "G2 Winter_pr_1_99_nre": 19,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 495,
    "G2 Spring_theta_nre": 0.131658171,
    "G2 Spring_pr_1_99_nre": 19,
    "G2 Spring_ss_nre": 86,
    "G2 Spring_dss_nre": 513,
    "G3 Fall_theta_nre": 0.450988627,
    "G3 Fall_pr_1_99_nre": 19,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 545,
    "G3 Winter_theta_nre": 0.760474533,
    "G3 Winter_pr_1_99_nre": 19,
    "G3 Winter_ss_nre": 87,
    "G3 Winter_dss_nre": 576,
    "G3 Spring_theta_nre": 1.101367209,
    "G3 Spring_pr_1_99_nre": 19,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 610
  },
  {
    "ID": 97,
    "KG Winter_theta_nre": -1.666188665,
    "KG Winter_pr_1_99_nre": 19,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 333,
    "KG Spring_theta_nre": -1.385053247,
    "KG Spring_pr_1_99_nre": 19,
    "KG Spring_ss_nre": 87,
    "KG Spring_dss_nre": 361,
    "G1 Fall_theta_nre": -1.178412744,
    "G1 Fall_pr_1_99_nre": 19,
    "G1 Fall_ss_nre": 86,
    "G1 Fall_dss_nre": 382,
    "G1 Winter_theta_nre": -0.840687067,
    "G1 Winter_pr_1_99_nre": 19,
    "G1 Winter_ss_nre": 87,
    "G1 Winter_dss_nre": 416,
    "G1 Spring_theta_nre": -0.514326047,
    "G1 Spring_pr_1_99_nre": 19,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 449,
    "G2 Fall_theta_nre": -0.405756261,
    "G2 Fall_pr_1_99_nre": 19,
    "G2 Fall_ss_nre": 86,
    "G2 Fall_dss_nre": 459,
    "G2 Winter_theta_nre": -0.046802272,
    "G2 Winter_pr_1_99_nre": 19,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 495,
    "G2 Spring_theta_nre": 0.135114214,
    "G2 Spring_pr_1_99_nre": 19,
    "G2 Spring_ss_nre": 87,
    "G2 Spring_dss_nre": 514,
    "G3 Fall_theta_nre": 0.451972765,
    "G3 Fall_pr_1_99_nre": 19,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 545,
    "G3 Winter_theta_nre": 0.767153695,
    "G3 Winter_pr_1_99_nre": 19,
    "G3 Winter_ss_nre": 87,
    "G3 Winter_dss_nre": 577,
    "G3 Spring_theta_nre": 1.101831051,
    "G3 Spring_pr_1_99_nre": 19,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 610
  },
  {
    "ID": 98,
    "KG Winter_theta_nre": -1.666098407,
    "KG Winter_pr_1_99_nre": 20,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 333,
    "KG Spring_theta_nre": -1.383758429,
    "KG Spring_pr_1_99_nre": 20,
    "KG Spring_ss_nre": 87,
    "KG Spring_dss_nre": 362,
    "G1 Fall_theta_nre": -1.172131135,
    "G1 Fall_pr_1_99_nre": 20,
    "G1 Fall_ss_nre": 86,
    "G1 Fall_dss_nre": 383,
    "G1 Winter_theta_nre": -0.837814903,
    "G1 Winter_pr_1_99_nre": 20,
    "G1 Winter_ss_nre": 87,
    "G1 Winter_dss_nre": 416,
    "G1 Spring_theta_nre": -0.510316334,
    "G1 Spring_pr_1_99_nre": 20,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 449,
    "G2 Fall_theta_nre": -0.401605147,
    "G2 Fall_pr_1_99_nre": 20,
    "G2 Fall_ss_nre": 86,
    "G2 Fall_dss_nre": 460,
    "G2 Winter_theta_nre": -0.043267246,
    "G2 Winter_pr_1_99_nre": 20,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 496,
    "G2 Spring_theta_nre": 0.150320458,
    "G2 Spring_pr_1_99_nre": 20,
    "G2 Spring_ss_nre": 87,
    "G2 Spring_dss_nre": 515,
    "G3 Fall_theta_nre": 0.455742053,
    "G3 Fall_pr_1_99_nre": 20,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 546,
    "G3 Winter_theta_nre": 0.770185976,
    "G3 Winter_pr_1_99_nre": 20,
    "G3 Winter_ss_nre": 87,
    "G3 Winter_dss_nre": 577,
    "G3 Spring_theta_nre": 1.103446041,
    "G3 Spring_pr_1_99_nre": 20,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 610
  },
  {
    "ID": 99,
    "KG Winter_theta_nre": -1.664068722,
    "KG Winter_pr_1_99_nre": 20,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 334,
    "KG Spring_theta_nre": -1.383623642,
    "KG Spring_pr_1_99_nre": 20,
    "KG Spring_ss_nre": 87,
    "KG Spring_dss_nre": 362,
    "G1 Fall_theta_nre": -1.168270693,
    "G1 Fall_pr_1_99_nre": 20,
    "G1 Fall_ss_nre": 87,
    "G1 Fall_dss_nre": 383,
    "G1 Winter_theta_nre": -0.836147997,
    "G1 Winter_pr_1_99_nre": 20,
    "G1 Winter_ss_nre": 87,
    "G1 Winter_dss_nre": 416,
    "G1 Spring_theta_nre": -0.510004486,
    "G1 Spring_pr_1_99_nre": 20,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 449,
    "G2 Fall_theta_nre": -0.400617699,
    "G2 Fall_pr_1_99_nre": 20,
    "G2 Fall_ss_nre": 86,
    "G2 Fall_dss_nre": 460,
    "G2 Winter_theta_nre": -0.042517306,
    "G2 Winter_pr_1_99_nre": 20,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 496,
    "G2 Spring_theta_nre": 0.183786554,
    "G2 Spring_pr_1_99_nre": 20,
    "G2 Spring_ss_nre": 87,
    "G2 Spring_dss_nre": 518,
    "G3 Fall_theta_nre": 0.457435028,
    "G3 Fall_pr_1_99_nre": 20,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 546,
    "G3 Winter_theta_nre": 0.774111985,
    "G3 Winter_pr_1_99_nre": 20,
    "G3 Winter_ss_nre": 87,
    "G3 Winter_dss_nre": 577,
    "G3 Spring_theta_nre": 1.103898544,
    "G3 Spring_pr_1_99_nre": 20,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 610
  },
  {
    "ID": 100,
    "KG Winter_theta_nre": -1.661365406,
    "KG Winter_pr_1_99_nre": 20,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 334,
    "KG Spring_theta_nre": -1.382832707,
    "KG Spring_pr_1_99_nre": 20,
    "KG Spring_ss_nre": 87,
    "KG Spring_dss_nre": 362,
    "G1 Fall_theta_nre": -1.16047882,
    "G1 Fall_pr_1_99_nre": 20,
    "G1 Fall_ss_nre": 87,
    "G1 Fall_dss_nre": 384,
    "G1 Winter_theta_nre": -0.826305266,
    "G1 Winter_pr_1_99_nre": 20,
    "G1 Winter_ss_nre": 87,
    "G1 Winter_dss_nre": 417,
    "G1 Spring_theta_nre": -0.502179277,
    "G1 Spring_pr_1_99_nre": 20,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 450,
    "G2 Fall_theta_nre": -0.395889896,
    "G2 Fall_pr_1_99_nre": 20,
    "G2 Fall_ss_nre": 87,
    "G2 Fall_dss_nre": 460,
    "G2 Winter_theta_nre": -0.040874678,
    "G2 Winter_pr_1_99_nre": 20,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 496,
    "G2 Spring_theta_nre": 0.188298682,
    "G2 Spring_pr_1_99_nre": 20,
    "G2 Spring_ss_nre": 87,
    "G2 Spring_dss_nre": 519,
    "G3 Fall_theta_nre": 0.461110075,
    "G3 Fall_pr_1_99_nre": 20,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 546,
    "G3 Winter_theta_nre": 0.778709041,
    "G3 Winter_pr_1_99_nre": 20,
    "G3 Winter_ss_nre": 87,
    "G3 Winter_dss_nre": 578,
    "G3 Spring_theta_nre": 1.106105747,
    "G3 Spring_pr_1_99_nre": 20,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 611
  },
  {
    "ID": 101,
    "KG Winter_theta_nre": -1.648845028,
    "KG Winter_pr_1_99_nre": 20,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 335,
    "KG Spring_theta_nre": -1.366719938,
    "KG Spring_pr_1_99_nre": 20,
    "KG Spring_ss_nre": 87,
    "KG Spring_dss_nre": 363,
    "G1 Fall_theta_nre": -1.147507904,
    "G1 Fall_pr_1_99_nre": 20,
    "G1 Fall_ss_nre": 87,
    "G1 Fall_dss_nre": 385,
    "G1 Winter_theta_nre": -0.807872575,
    "G1 Winter_pr_1_99_nre": 20,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 419,
    "G1 Spring_theta_nre": -0.495733462,
    "G1 Spring_pr_1_99_nre": 20,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 450,
    "G2 Fall_theta_nre": -0.393126618,
    "G2 Fall_pr_1_99_nre": 20,
    "G2 Fall_ss_nre": 87,
    "G2 Fall_dss_nre": 461,
    "G2 Winter_theta_nre": -0.039450028,
    "G2 Winter_pr_1_99_nre": 20,
    "G2 Winter_ss_nre": 86,
    "G2 Winter_dss_nre": 496,
    "G2 Spring_theta_nre": 0.191616614,
    "G2 Spring_pr_1_99_nre": 20,
    "G2 Spring_ss_nre": 87,
    "G2 Spring_dss_nre": 519,
    "G3 Fall_theta_nre": 0.461458182,
    "G3 Fall_pr_1_99_nre": 20,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 546,
    "G3 Winter_theta_nre": 0.782716494,
    "G3 Winter_pr_1_99_nre": 20,
    "G3 Winter_ss_nre": 87,
    "G3 Winter_dss_nre": 578,
    "G3 Spring_theta_nre": 1.109222703,
    "G3 Spring_pr_1_99_nre": 20,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 611
  },
  {
    "ID": 102,
    "KG Winter_theta_nre": -1.636676233,
    "KG Winter_pr_1_99_nre": 20,
    "KG Winter_ss_nre": 87,
    "KG Winter_dss_nre": 336,
    "KG Spring_theta_nre": -1.356602969,
    "KG Spring_pr_1_99_nre": 20,
    "KG Spring_ss_nre": 87,
    "KG Spring_dss_nre": 364,
    "G1 Fall_theta_nre": -1.14730365,
    "G1 Fall_pr_1_99_nre": 20,
    "G1 Fall_ss_nre": 87,
    "G1 Fall_dss_nre": 385,
    "G1 Winter_theta_nre": -0.807123944,
    "G1 Winter_pr_1_99_nre": 20,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 419,
    "G1 Spring_theta_nre": -0.493482181,
    "G1 Spring_pr_1_99_nre": 20,
    "G1 Spring_ss_nre": 87,
    "G1 Spring_dss_nre": 451,
    "G2 Fall_theta_nre": -0.392968328,
    "G2 Fall_pr_1_99_nre": 20,
    "G2 Fall_ss_nre": 87,
    "G2 Fall_dss_nre": 461,
    "G2 Winter_theta_nre": -0.024564208,
    "G2 Winter_pr_1_99_nre": 20,
    "G2 Winter_ss_nre": 87,
    "G2 Winter_dss_nre": 498,
    "G2 Spring_theta_nre": 0.192089709,
    "G2 Spring_pr_1_99_nre": 20,
    "G2 Spring_ss_nre": 88,
    "G2 Spring_dss_nre": 519,
    "G3 Fall_theta_nre": 0.464143381,
    "G3 Fall_pr_1_99_nre": 20,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 546,
    "G3 Winter_theta_nre": 0.797260698,
    "G3 Winter_pr_1_99_nre": 20,
    "G3 Winter_ss_nre": 87,
    "G3 Winter_dss_nre": 580,
    "G3 Spring_theta_nre": 1.110147957,
    "G3 Spring_pr_1_99_nre": 20,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 611
  },
  {
    "ID": 103,
    "KG Winter_theta_nre": -1.633968851,
    "KG Winter_pr_1_99_nre": 21,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 337,
    "KG Spring_theta_nre": -1.355337197,
    "KG Spring_pr_1_99_nre": 21,
    "KG Spring_ss_nre": 87,
    "KG Spring_dss_nre": 364,
    "G1 Fall_theta_nre": -1.139997149,
    "G1 Fall_pr_1_99_nre": 21,
    "G1 Fall_ss_nre": 87,
    "G1 Fall_dss_nre": 386,
    "G1 Winter_theta_nre": -0.801826227,
    "G1 Winter_pr_1_99_nre": 21,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 420,
    "G1 Spring_theta_nre": -0.492414904,
    "G1 Spring_pr_1_99_nre": 21,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 451,
    "G2 Fall_theta_nre": -0.392957252,
    "G2 Fall_pr_1_99_nre": 21,
    "G2 Fall_ss_nre": 87,
    "G2 Fall_dss_nre": 461,
    "G2 Winter_theta_nre": -0.001633471,
    "G2 Winter_pr_1_99_nre": 21,
    "G2 Winter_ss_nre": 87,
    "G2 Winter_dss_nre": 500,
    "G2 Spring_theta_nre": 0.19432281,
    "G2 Spring_pr_1_99_nre": 21,
    "G2 Spring_ss_nre": 88,
    "G2 Spring_dss_nre": 519,
    "G3 Fall_theta_nre": 0.466892452,
    "G3 Fall_pr_1_99_nre": 21,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 547,
    "G3 Winter_theta_nre": 0.7985955,
    "G3 Winter_pr_1_99_nre": 21,
    "G3 Winter_ss_nre": 87,
    "G3 Winter_dss_nre": 580,
    "G3 Spring_theta_nre": 1.114156947,
    "G3 Spring_pr_1_99_nre": 21,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 611
  },
  {
    "ID": 104,
    "KG Winter_theta_nre": -1.632966867,
    "KG Winter_pr_1_99_nre": 21,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 337,
    "KG Spring_theta_nre": -1.342220517,
    "KG Spring_pr_1_99_nre": 21,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 366,
    "G1 Fall_theta_nre": -1.137582723,
    "G1 Fall_pr_1_99_nre": 21,
    "G1 Fall_ss_nre": 87,
    "G1 Fall_dss_nre": 386,
    "G1 Winter_theta_nre": -0.800141312,
    "G1 Winter_pr_1_99_nre": 21,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 420,
    "G1 Spring_theta_nre": -0.488494581,
    "G1 Spring_pr_1_99_nre": 21,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 451,
    "G2 Fall_theta_nre": -0.374859694,
    "G2 Fall_pr_1_99_nre": 21,
    "G2 Fall_ss_nre": 87,
    "G2 Fall_dss_nre": 463,
    "G2 Winter_theta_nre": -0.000435293,
    "G2 Winter_pr_1_99_nre": 21,
    "G2 Winter_ss_nre": 87,
    "G2 Winter_dss_nre": 500,
    "G2 Spring_theta_nre": 0.199100891,
    "G2 Spring_pr_1_99_nre": 21,
    "G2 Spring_ss_nre": 88,
    "G2 Spring_dss_nre": 520,
    "G3 Fall_theta_nre": 0.469170614,
    "G3 Fall_pr_1_99_nre": 21,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 547,
    "G3 Winter_theta_nre": 0.809263333,
    "G3 Winter_pr_1_99_nre": 21,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 581,
    "G3 Spring_theta_nre": 1.127749069,
    "G3 Spring_pr_1_99_nre": 21,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 613
  },
  {
    "ID": 105,
    "KG Winter_theta_nre": -1.632920472,
    "KG Winter_pr_1_99_nre": 21,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 337,
    "KG Spring_theta_nre": -1.323546153,
    "KG Spring_pr_1_99_nre": 21,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 368,
    "G1 Fall_theta_nre": -1.126926719,
    "G1 Fall_pr_1_99_nre": 21,
    "G1 Fall_ss_nre": 87,
    "G1 Fall_dss_nre": 387,
    "G1 Winter_theta_nre": -0.796245273,
    "G1 Winter_pr_1_99_nre": 21,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 420,
    "G1 Spring_theta_nre": -0.484861199,
    "G1 Spring_pr_1_99_nre": 21,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 452,
    "G2 Fall_theta_nre": -0.366831283,
    "G2 Fall_pr_1_99_nre": 21,
    "G2 Fall_ss_nre": 87,
    "G2 Fall_dss_nre": 463,
    "G2 Winter_theta_nre": -0.0000327,
    "G2 Winter_pr_1_99_nre": 21,
    "G2 Winter_ss_nre": 87,
    "G2 Winter_dss_nre": 500,
    "G2 Spring_theta_nre": 0.199570248,
    "G2 Spring_pr_1_99_nre": 21,
    "G2 Spring_ss_nre": 88,
    "G2 Spring_dss_nre": 520,
    "G3 Fall_theta_nre": 0.47131171,
    "G3 Fall_pr_1_99_nre": 21,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 547,
    "G3 Winter_theta_nre": 0.81031544,
    "G3 Winter_pr_1_99_nre": 21,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 581,
    "G3 Spring_theta_nre": 1.12932919,
    "G3 Spring_pr_1_99_nre": 21,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 613
  },
  {
    "ID": 106,
    "KG Winter_theta_nre": -1.624719198,
    "KG Winter_pr_1_99_nre": 21,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 338,
    "KG Spring_theta_nre": -1.320915266,
    "KG Spring_pr_1_99_nre": 21,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 368,
    "G1 Fall_theta_nre": -1.124692003,
    "G1 Fall_pr_1_99_nre": 21,
    "G1 Fall_ss_nre": 87,
    "G1 Fall_dss_nre": 388,
    "G1 Winter_theta_nre": -0.787091406,
    "G1 Winter_pr_1_99_nre": 21,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 421,
    "G1 Spring_theta_nre": -0.484706492,
    "G1 Spring_pr_1_99_nre": 21,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 452,
    "G2 Fall_theta_nre": -0.360202463,
    "G2 Fall_pr_1_99_nre": 21,
    "G2 Fall_ss_nre": 87,
    "G2 Fall_dss_nre": 464,
    "G2 Winter_theta_nre": 0.002010027,
    "G2 Winter_pr_1_99_nre": 21,
    "G2 Winter_ss_nre": 87,
    "G2 Winter_dss_nre": 500,
    "G2 Spring_theta_nre": 0.207380777,
    "G2 Spring_pr_1_99_nre": 21,
    "G2 Spring_ss_nre": 88,
    "G2 Spring_dss_nre": 521,
    "G3 Fall_theta_nre": 0.475183734,
    "G3 Fall_pr_1_99_nre": 21,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 548,
    "G3 Winter_theta_nre": 0.81108864,
    "G3 Winter_pr_1_99_nre": 21,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 581,
    "G3 Spring_theta_nre": 1.129779887,
    "G3 Spring_pr_1_99_nre": 21,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 613
  },
  {
    "ID": 107,
    "KG Winter_theta_nre": -1.623657513,
    "KG Winter_pr_1_99_nre": 21,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 338,
    "KG Spring_theta_nre": -1.319979211,
    "KG Spring_pr_1_99_nre": 21,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 368,
    "G1 Fall_theta_nre": -1.10486801,
    "G1 Fall_pr_1_99_nre": 21,
    "G1 Fall_ss_nre": 88,
    "G1 Fall_dss_nre": 390,
    "G1 Winter_theta_nre": -0.785664648,
    "G1 Winter_pr_1_99_nre": 21,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 421,
    "G1 Spring_theta_nre": -0.48254587,
    "G1 Spring_pr_1_99_nre": 21,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 452,
    "G2 Fall_theta_nre": -0.355438753,
    "G2 Fall_pr_1_99_nre": 21,
    "G2 Fall_ss_nre": 87,
    "G2 Fall_dss_nre": 464,
    "G2 Winter_theta_nre": 0.006544142,
    "G2 Winter_pr_1_99_nre": 21,
    "G2 Winter_ss_nre": 87,
    "G2 Winter_dss_nre": 501,
    "G2 Spring_theta_nre": 0.218549521,
    "G2 Spring_pr_1_99_nre": 21,
    "G2 Spring_ss_nre": 88,
    "G2 Spring_dss_nre": 522,
    "G3 Fall_theta_nre": 0.480467514,
    "G3 Fall_pr_1_99_nre": 21,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 548,
    "G3 Winter_theta_nre": 0.817892493,
    "G3 Winter_pr_1_99_nre": 21,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 582,
    "G3 Spring_theta_nre": 1.129894144,
    "G3 Spring_pr_1_99_nre": 21,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 613
  },
  {
    "ID": 108,
    "KG Winter_theta_nre": -1.622755359,
    "KG Winter_pr_1_99_nre": 22,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 338,
    "KG Spring_theta_nre": -1.313196015,
    "KG Spring_pr_1_99_nre": 22,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 369,
    "G1 Fall_theta_nre": -1.099300894,
    "G1 Fall_pr_1_99_nre": 22,
    "G1 Fall_ss_nre": 88,
    "G1 Fall_dss_nre": 390,
    "G1 Winter_theta_nre": -0.781898096,
    "G1 Winter_pr_1_99_nre": 22,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 422,
    "G1 Spring_theta_nre": -0.481940278,
    "G1 Spring_pr_1_99_nre": 22,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 452,
    "G2 Fall_theta_nre": -0.339172095,
    "G2 Fall_pr_1_99_nre": 22,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 466,
    "G2 Winter_theta_nre": 0.007674184,
    "G2 Winter_pr_1_99_nre": 22,
    "G2 Winter_ss_nre": 87,
    "G2 Winter_dss_nre": 501,
    "G2 Spring_theta_nre": 0.218818943,
    "G2 Spring_pr_1_99_nre": 22,
    "G2 Spring_ss_nre": 88,
    "G2 Spring_dss_nre": 522,
    "G3 Fall_theta_nre": 0.48137655,
    "G3 Fall_pr_1_99_nre": 22,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 548,
    "G3 Winter_theta_nre": 0.81822156,
    "G3 Winter_pr_1_99_nre": 22,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 582,
    "G3 Spring_theta_nre": 1.130223809,
    "G3 Spring_pr_1_99_nre": 22,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 613
  },
  {
    "ID": 109,
    "KG Winter_theta_nre": -1.621708023,
    "KG Winter_pr_1_99_nre": 22,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 338,
    "KG Spring_theta_nre": -1.310324741,
    "KG Spring_pr_1_99_nre": 22,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 369,
    "G1 Fall_theta_nre": -1.093634738,
    "G1 Fall_pr_1_99_nre": 22,
    "G1 Fall_ss_nre": 88,
    "G1 Fall_dss_nre": 391,
    "G1 Winter_theta_nre": -0.779736358,
    "G1 Winter_pr_1_99_nre": 22,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 422,
    "G1 Spring_theta_nre": -0.477825694,
    "G1 Spring_pr_1_99_nre": 22,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 452,
    "G2 Fall_theta_nre": -0.33886771,
    "G2 Fall_pr_1_99_nre": 22,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 466,
    "G2 Winter_theta_nre": 0.026832485,
    "G2 Winter_pr_1_99_nre": 22,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 503,
    "G2 Spring_theta_nre": 0.222714647,
    "G2 Spring_pr_1_99_nre": 22,
    "G2 Spring_ss_nre": 88,
    "G2 Spring_dss_nre": 522,
    "G3 Fall_theta_nre": 0.485516277,
    "G3 Fall_pr_1_99_nre": 22,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 549,
    "G3 Winter_theta_nre": 0.823200871,
    "G3 Winter_pr_1_99_nre": 22,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 582,
    "G3 Spring_theta_nre": 1.137526835,
    "G3 Spring_pr_1_99_nre": 22,
    "G3 Spring_ss_nre": 87,
    "G3 Spring_dss_nre": 614
  },
  {
    "ID": 110,
    "KG Winter_theta_nre": -1.607389344,
    "KG Winter_pr_1_99_nre": 22,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 339,
    "KG Spring_theta_nre": -1.307553857,
    "KG Spring_pr_1_99_nre": 22,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 369,
    "G1 Fall_theta_nre": -1.056435011,
    "G1 Fall_pr_1_99_nre": 22,
    "G1 Fall_ss_nre": 88,
    "G1 Fall_dss_nre": 394,
    "G1 Winter_theta_nre": -0.774002665,
    "G1 Winter_pr_1_99_nre": 22,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 423,
    "G1 Spring_theta_nre": -0.476450151,
    "G1 Spring_pr_1_99_nre": 22,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 452,
    "G2 Fall_theta_nre": -0.335469106,
    "G2 Fall_pr_1_99_nre": 22,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 466,
    "G2 Winter_theta_nre": 0.033435087,
    "G2 Winter_pr_1_99_nre": 22,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 503,
    "G2 Spring_theta_nre": 0.246195283,
    "G2 Spring_pr_1_99_nre": 22,
    "G2 Spring_ss_nre": 88,
    "G2 Spring_dss_nre": 525,
    "G3 Fall_theta_nre": 0.494383171,
    "G3 Fall_pr_1_99_nre": 22,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 549,
    "G3 Winter_theta_nre": 0.831674586,
    "G3 Winter_pr_1_99_nre": 22,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 583,
    "G3 Spring_theta_nre": 1.140522247,
    "G3 Spring_pr_1_99_nre": 22,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 614
  },
  {
    "ID": 111,
    "KG Winter_theta_nre": -1.604913462,
    "KG Winter_pr_1_99_nre": 22,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 340,
    "KG Spring_theta_nre": -1.305979041,
    "KG Spring_pr_1_99_nre": 22,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 369,
    "G1 Fall_theta_nre": -1.048868804,
    "G1 Fall_pr_1_99_nre": 22,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 395,
    "G1 Winter_theta_nre": -0.772606399,
    "G1 Winter_pr_1_99_nre": 22,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 423,
    "G1 Spring_theta_nre": -0.474043396,
    "G1 Spring_pr_1_99_nre": 22,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 453,
    "G2 Fall_theta_nre": -0.326227846,
    "G2 Fall_pr_1_99_nre": 22,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 467,
    "G2 Winter_theta_nre": 0.037977065,
    "G2 Winter_pr_1_99_nre": 22,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 504,
    "G2 Spring_theta_nre": 0.248509061,
    "G2 Spring_pr_1_99_nre": 22,
    "G2 Spring_ss_nre": 88,
    "G2 Spring_dss_nre": 525,
    "G3 Fall_theta_nre": 0.494755318,
    "G3 Fall_pr_1_99_nre": 22,
    "G3 Fall_ss_nre": 88,
    "G3 Fall_dss_nre": 549,
    "G3 Winter_theta_nre": 0.831851445,
    "G3 Winter_pr_1_99_nre": 22,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 583,
    "G3 Spring_theta_nre": 1.144834789,
    "G3 Spring_pr_1_99_nre": 22,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 614
  },
  {
    "ID": 112,
    "KG Winter_theta_nre": -1.603273941,
    "KG Winter_pr_1_99_nre": 22,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 340,
    "KG Spring_theta_nre": -1.300424927,
    "KG Spring_pr_1_99_nre": 22,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 370,
    "G1 Fall_theta_nre": -1.041901598,
    "G1 Fall_pr_1_99_nre": 22,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 396,
    "G1 Winter_theta_nre": -0.770019195,
    "G1 Winter_pr_1_99_nre": 22,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 423,
    "G1 Spring_theta_nre": -0.469893749,
    "G1 Spring_pr_1_99_nre": 22,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 453,
    "G2 Fall_theta_nre": -0.326142718,
    "G2 Fall_pr_1_99_nre": 22,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 467,
    "G2 Winter_theta_nre": 0.040769378,
    "G2 Winter_pr_1_99_nre": 22,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 504,
    "G2 Spring_theta_nre": 0.255266952,
    "G2 Spring_pr_1_99_nre": 22,
    "G2 Spring_ss_nre": 89,
    "G2 Spring_dss_nre": 526,
    "G3 Fall_theta_nre": 0.505098268,
    "G3 Fall_pr_1_99_nre": 22,
    "G3 Fall_ss_nre": 89,
    "G3 Fall_dss_nre": 551,
    "G3 Winter_theta_nre": 0.832185563,
    "G3 Winter_pr_1_99_nre": 22,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 583,
    "G3 Spring_theta_nre": 1.151028249,
    "G3 Spring_pr_1_99_nre": 22,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 615
  },
  {
    "ID": 113,
    "KG Winter_theta_nre": -1.603074947,
    "KG Winter_pr_1_99_nre": 23,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 340,
    "KG Spring_theta_nre": -1.299482553,
    "KG Spring_pr_1_99_nre": 23,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 370,
    "G1 Fall_theta_nre": -1.036862185,
    "G1 Fall_pr_1_99_nre": 23,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 396,
    "G1 Winter_theta_nre": -0.762212531,
    "G1 Winter_pr_1_99_nre": 23,
    "G1 Winter_ss_nre": 88,
    "G1 Winter_dss_nre": 424,
    "G1 Spring_theta_nre": -0.468318495,
    "G1 Spring_pr_1_99_nre": 23,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 453,
    "G2 Fall_theta_nre": -0.319940511,
    "G2 Fall_pr_1_99_nre": 23,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 468,
    "G2 Winter_theta_nre": 0.053348107,
    "G2 Winter_pr_1_99_nre": 23,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 505,
    "G2 Spring_theta_nre": 0.26506114,
    "G2 Spring_pr_1_99_nre": 23,
    "G2 Spring_ss_nre": 89,
    "G2 Spring_dss_nre": 527,
    "G3 Fall_theta_nre": 0.513504897,
    "G3 Fall_pr_1_99_nre": 23,
    "G3 Fall_ss_nre": 89,
    "G3 Fall_dss_nre": 551,
    "G3 Winter_theta_nre": 0.840777908,
    "G3 Winter_pr_1_99_nre": 23,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 584,
    "G3 Spring_theta_nre": 1.151050996,
    "G3 Spring_pr_1_99_nre": 23,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 615
  },
  {
    "ID": 114,
    "KG Winter_theta_nre": -1.60300999,
    "KG Winter_pr_1_99_nre": 23,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 340,
    "KG Spring_theta_nre": -1.297070607,
    "KG Spring_pr_1_99_nre": 23,
    "KG Spring_ss_nre": 88,
    "KG Spring_dss_nre": 370,
    "G1 Fall_theta_nre": -1.034826267,
    "G1 Fall_pr_1_99_nre": 23,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 397,
    "G1 Winter_theta_nre": -0.74383963,
    "G1 Winter_pr_1_99_nre": 23,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 426,
    "G1 Spring_theta_nre": -0.457396748,
    "G1 Spring_pr_1_99_nre": 23,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 454,
    "G2 Fall_theta_nre": -0.309704776,
    "G2 Fall_pr_1_99_nre": 23,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 469,
    "G2 Winter_theta_nre": 0.058882282,
    "G2 Winter_pr_1_99_nre": 23,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 506,
    "G2 Spring_theta_nre": 0.268486964,
    "G2 Spring_pr_1_99_nre": 23,
    "G2 Spring_ss_nre": 89,
    "G2 Spring_dss_nre": 527,
    "G3 Fall_theta_nre": 0.513672422,
    "G3 Fall_pr_1_99_nre": 23,
    "G3 Fall_ss_nre": 89,
    "G3 Fall_dss_nre": 551,
    "G3 Winter_theta_nre": 0.84128523,
    "G3 Winter_pr_1_99_nre": 23,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 584,
    "G3 Spring_theta_nre": 1.15316792,
    "G3 Spring_pr_1_99_nre": 23,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 615
  },
  {
    "ID": 115,
    "KG Winter_theta_nre": -1.596635072,
    "KG Winter_pr_1_99_nre": 23,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 340,
    "KG Spring_theta_nre": -1.28856228,
    "KG Spring_pr_1_99_nre": 23,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 371,
    "G1 Fall_theta_nre": -1.02978961,
    "G1 Fall_pr_1_99_nre": 23,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 397,
    "G1 Winter_theta_nre": -0.742427655,
    "G1 Winter_pr_1_99_nre": 23,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 426,
    "G1 Spring_theta_nre": -0.454382942,
    "G1 Spring_pr_1_99_nre": 23,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 455,
    "G2 Fall_theta_nre": -0.301821763,
    "G2 Fall_pr_1_99_nre": 23,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 470,
    "G2 Winter_theta_nre": 0.060983517,
    "G2 Winter_pr_1_99_nre": 23,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 506,
    "G2 Spring_theta_nre": 0.274795489,
    "G2 Spring_pr_1_99_nre": 23,
    "G2 Spring_ss_nre": 89,
    "G2 Spring_dss_nre": 527,
    "G3 Fall_theta_nre": 0.519226933,
    "G3 Fall_pr_1_99_nre": 23,
    "G3 Fall_ss_nre": 89,
    "G3 Fall_dss_nre": 552,
    "G3 Winter_theta_nre": 0.8447501,
    "G3 Winter_pr_1_99_nre": 23,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 584,
    "G3 Spring_theta_nre": 1.153365119,
    "G3 Spring_pr_1_99_nre": 23,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 615
  },
  {
    "ID": 116,
    "KG Winter_theta_nre": -1.580329066,
    "KG Winter_pr_1_99_nre": 23,
    "KG Winter_ss_nre": 88,
    "KG Winter_dss_nre": 342,
    "KG Spring_theta_nre": -1.285077758,
    "KG Spring_pr_1_99_nre": 23,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 371,
    "G1 Fall_theta_nre": -1.02657731,
    "G1 Fall_pr_1_99_nre": 23,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 397,
    "G1 Winter_theta_nre": -0.739305257,
    "G1 Winter_pr_1_99_nre": 23,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 426,
    "G1 Spring_theta_nre": -0.444541477,
    "G1 Spring_pr_1_99_nre": 23,
    "G1 Spring_ss_nre": 88,
    "G1 Spring_dss_nre": 456,
    "G2 Fall_theta_nre": -0.301137657,
    "G2 Fall_pr_1_99_nre": 23,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 470,
    "G2 Winter_theta_nre": 0.061244587,
    "G2 Winter_pr_1_99_nre": 23,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 506,
    "G2 Spring_theta_nre": 0.27591195,
    "G2 Spring_pr_1_99_nre": 23,
    "G2 Spring_ss_nre": 89,
    "G2 Spring_dss_nre": 528,
    "G3 Fall_theta_nre": 0.525467176,
    "G3 Fall_pr_1_99_nre": 23,
    "G3 Fall_ss_nre": 89,
    "G3 Fall_dss_nre": 553,
    "G3 Winter_theta_nre": 0.849599484,
    "G3 Winter_pr_1_99_nre": 23,
    "G3 Winter_ss_nre": 88,
    "G3 Winter_dss_nre": 585,
    "G3 Spring_theta_nre": 1.158264081,
    "G3 Spring_pr_1_99_nre": 23,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 616
  },
  {
    "ID": 117,
    "KG Winter_theta_nre": -1.577013985,
    "KG Winter_pr_1_99_nre": 23,
    "KG Winter_ss_nre": 89,
    "KG Winter_dss_nre": 342,
    "KG Spring_theta_nre": -1.283088972,
    "KG Spring_pr_1_99_nre": 23,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 372,
    "G1 Fall_theta_nre": -1.025832211,
    "G1 Fall_pr_1_99_nre": 23,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 397,
    "G1 Winter_theta_nre": -0.73288261,
    "G1 Winter_pr_1_99_nre": 23,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 427,
    "G1 Spring_theta_nre": -0.438674372,
    "G1 Spring_pr_1_99_nre": 23,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 456,
    "G2 Fall_theta_nre": -0.297917825,
    "G2 Fall_pr_1_99_nre": 23,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 470,
    "G2 Winter_theta_nre": 0.06144192,
    "G2 Winter_pr_1_99_nre": 23,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 506,
    "G2 Spring_theta_nre": 0.281622105,
    "G2 Spring_pr_1_99_nre": 23,
    "G2 Spring_ss_nre": 89,
    "G2 Spring_dss_nre": 528,
    "G3 Fall_theta_nre": 0.528087119,
    "G3 Fall_pr_1_99_nre": 23,
    "G3 Fall_ss_nre": 89,
    "G3 Fall_dss_nre": 553,
    "G3 Winter_theta_nre": 0.85593108,
    "G3 Winter_pr_1_99_nre": 23,
    "G3 Winter_ss_nre": 89,
    "G3 Winter_dss_nre": 586,
    "G3 Spring_theta_nre": 1.159331923,
    "G3 Spring_pr_1_99_nre": 23,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 616
  },
  {
    "ID": 118,
    "KG Winter_theta_nre": -1.576053295,
    "KG Winter_pr_1_99_nre": 24,
    "KG Winter_ss_nre": 89,
    "KG Winter_dss_nre": 342,
    "KG Spring_theta_nre": -1.27691956,
    "KG Spring_pr_1_99_nre": 24,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 372,
    "G1 Fall_theta_nre": -1.024679747,
    "G1 Fall_pr_1_99_nre": 24,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 398,
    "G1 Winter_theta_nre": -0.729289129,
    "G1 Winter_pr_1_99_nre": 24,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 427,
    "G1 Spring_theta_nre": -0.433482375,
    "G1 Spring_pr_1_99_nre": 24,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 457,
    "G2 Fall_theta_nre": -0.294265236,
    "G2 Fall_pr_1_99_nre": 24,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 471,
    "G2 Winter_theta_nre": 0.062185156,
    "G2 Winter_pr_1_99_nre": 24,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 506,
    "G2 Spring_theta_nre": 0.286079327,
    "G2 Spring_pr_1_99_nre": 24,
    "G2 Spring_ss_nre": 89,
    "G2 Spring_dss_nre": 529,
    "G3 Fall_theta_nre": 0.540850077,
    "G3 Fall_pr_1_99_nre": 24,
    "G3 Fall_ss_nre": 89,
    "G3 Fall_dss_nre": 554,
    "G3 Winter_theta_nre": 0.85772106,
    "G3 Winter_pr_1_99_nre": 24,
    "G3 Winter_ss_nre": 89,
    "G3 Winter_dss_nre": 586,
    "G3 Spring_theta_nre": 1.159758222,
    "G3 Spring_pr_1_99_nre": 24,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 616
  },
  {
    "ID": 119,
    "KG Winter_theta_nre": -1.555319251,
    "KG Winter_pr_1_99_nre": 24,
    "KG Winter_ss_nre": 89,
    "KG Winter_dss_nre": 344,
    "KG Spring_theta_nre": -1.275918425,
    "KG Spring_pr_1_99_nre": 24,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 372,
    "G1 Fall_theta_nre": -1.015128213,
    "G1 Fall_pr_1_99_nre": 24,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 398,
    "G1 Winter_theta_nre": -0.724159781,
    "G1 Winter_pr_1_99_nre": 24,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 428,
    "G1 Spring_theta_nre": -0.432846902,
    "G1 Spring_pr_1_99_nre": 24,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 457,
    "G2 Fall_theta_nre": -0.286743505,
    "G2 Fall_pr_1_99_nre": 24,
    "G2 Fall_ss_nre": 88,
    "G2 Fall_dss_nre": 471,
    "G2 Winter_theta_nre": 0.062892847,
    "G2 Winter_pr_1_99_nre": 24,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 506,
    "G2 Spring_theta_nre": 0.292181194,
    "G2 Spring_pr_1_99_nre": 24,
    "G2 Spring_ss_nre": 89,
    "G2 Spring_dss_nre": 529,
    "G3 Fall_theta_nre": 0.558235475,
    "G3 Fall_pr_1_99_nre": 24,
    "G3 Fall_ss_nre": 90,
    "G3 Fall_dss_nre": 556,
    "G3 Winter_theta_nre": 0.865634142,
    "G3 Winter_pr_1_99_nre": 24,
    "G3 Winter_ss_nre": 89,
    "G3 Winter_dss_nre": 587,
    "G3 Spring_theta_nre": 1.164305396,
    "G3 Spring_pr_1_99_nre": 24,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 616
  },
  {
    "ID": 120,
    "KG Winter_theta_nre": -1.545417368,
    "KG Winter_pr_1_99_nre": 24,
    "KG Winter_ss_nre": 89,
    "KG Winter_dss_nre": 345,
    "KG Spring_theta_nre": -1.270762949,
    "KG Spring_pr_1_99_nre": 24,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 373,
    "G1 Fall_theta_nre": -1.01436198,
    "G1 Fall_pr_1_99_nre": 24,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 399,
    "G1 Winter_theta_nre": -0.722478132,
    "G1 Winter_pr_1_99_nre": 24,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 428,
    "G1 Spring_theta_nre": -0.430187538,
    "G1 Spring_pr_1_99_nre": 24,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 457,
    "G2 Fall_theta_nre": -0.278861007,
    "G2 Fall_pr_1_99_nre": 24,
    "G2 Fall_ss_nre": 89,
    "G2 Fall_dss_nre": 472,
    "G2 Winter_theta_nre": 0.06360383,
    "G2 Winter_pr_1_99_nre": 24,
    "G2 Winter_ss_nre": 88,
    "G2 Winter_dss_nre": 506,
    "G2 Spring_theta_nre": 0.296972275,
    "G2 Spring_pr_1_99_nre": 24,
    "G2 Spring_ss_nre": 89,
    "G2 Spring_dss_nre": 530,
    "G3 Fall_theta_nre": 0.565243993,
    "G3 Fall_pr_1_99_nre": 24,
    "G3 Fall_ss_nre": 90,
    "G3 Fall_dss_nre": 557,
    "G3 Winter_theta_nre": 0.869729842,
    "G3 Winter_pr_1_99_nre": 24,
    "G3 Winter_ss_nre": 89,
    "G3 Winter_dss_nre": 587,
    "G3 Spring_theta_nre": 1.176867019,
    "G3 Spring_pr_1_99_nre": 24,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 618
  },
  {
    "ID": 121,
    "KG Winter_theta_nre": -1.54109219,
    "KG Winter_pr_1_99_nre": 24,
    "KG Winter_ss_nre": 89,
    "KG Winter_dss_nre": 346,
    "KG Spring_theta_nre": -1.26974148,
    "KG Spring_pr_1_99_nre": 24,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 373,
    "G1 Fall_theta_nre": -1.014248478,
    "G1 Fall_pr_1_99_nre": 24,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 399,
    "G1 Winter_theta_nre": -0.721052584,
    "G1 Winter_pr_1_99_nre": 24,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 428,
    "G1 Spring_theta_nre": -0.429616794,
    "G1 Spring_pr_1_99_nre": 24,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 457,
    "G2 Fall_theta_nre": -0.269653782,
    "G2 Fall_pr_1_99_nre": 24,
    "G2 Fall_ss_nre": 89,
    "G2 Fall_dss_nre": 473,
    "G2 Winter_theta_nre": 0.078329877,
    "G2 Winter_pr_1_99_nre": 24,
    "G2 Winter_ss_nre": 89,
    "G2 Winter_dss_nre": 508,
    "G2 Spring_theta_nre": 0.297860686,
    "G2 Spring_pr_1_99_nre": 24,
    "G2 Spring_ss_nre": 89,
    "G2 Spring_dss_nre": 530,
    "G3 Fall_theta_nre": 0.566351996,
    "G3 Fall_pr_1_99_nre": 24,
    "G3 Fall_ss_nre": 90,
    "G3 Fall_dss_nre": 557,
    "G3 Winter_theta_nre": 0.870337195,
    "G3 Winter_pr_1_99_nre": 24,
    "G3 Winter_ss_nre": 89,
    "G3 Winter_dss_nre": 587,
    "G3 Spring_theta_nre": 1.177727443,
    "G3 Spring_pr_1_99_nre": 24,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 618
  },
  {
    "ID": 122,
    "KG Winter_theta_nre": -1.524074577,
    "KG Winter_pr_1_99_nre": 24,
    "KG Winter_ss_nre": 89,
    "KG Winter_dss_nre": 348,
    "KG Spring_theta_nre": -1.265761377,
    "KG Spring_pr_1_99_nre": 24,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 373,
    "G1 Fall_theta_nre": -1.013400292,
    "G1 Fall_pr_1_99_nre": 24,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 399,
    "G1 Winter_theta_nre": -0.719657294,
    "G1 Winter_pr_1_99_nre": 24,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 428,
    "G1 Spring_theta_nre": -0.428646036,
    "G1 Spring_pr_1_99_nre": 24,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 457,
    "G2 Fall_theta_nre": -0.261650765,
    "G2 Fall_pr_1_99_nre": 24,
    "G2 Fall_ss_nre": 89,
    "G2 Fall_dss_nre": 474,
    "G2 Winter_theta_nre": 0.078477836,
    "G2 Winter_pr_1_99_nre": 24,
    "G2 Winter_ss_nre": 89,
    "G2 Winter_dss_nre": 508,
    "G2 Spring_theta_nre": 0.31846902,
    "G2 Spring_pr_1_99_nre": 24,
    "G2 Spring_ss_nre": 90,
    "G2 Spring_dss_nre": 532,
    "G3 Fall_theta_nre": 0.571305001,
    "G3 Fall_pr_1_99_nre": 24,
    "G3 Fall_ss_nre": 90,
    "G3 Fall_dss_nre": 557,
    "G3 Winter_theta_nre": 0.875813727,
    "G3 Winter_pr_1_99_nre": 24,
    "G3 Winter_ss_nre": 89,
    "G3 Winter_dss_nre": 588,
    "G3 Spring_theta_nre": 1.178397532,
    "G3 Spring_pr_1_99_nre": 24,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 618
  },
  {
    "ID": 123,
    "KG Winter_theta_nre": -1.508958219,
    "KG Winter_pr_1_99_nre": 25,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 349,
    "KG Spring_theta_nre": -1.261718337,
    "KG Spring_pr_1_99_nre": 25,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 374,
    "G1 Fall_theta_nre": -1.010460759,
    "G1 Fall_pr_1_99_nre": 25,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 399,
    "G1 Winter_theta_nre": -0.718719892,
    "G1 Winter_pr_1_99_nre": 25,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 428,
    "G1 Spring_theta_nre": -0.422226788,
    "G1 Spring_pr_1_99_nre": 25,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 458,
    "G2 Fall_theta_nre": -0.250824007,
    "G2 Fall_pr_1_99_nre": 25,
    "G2 Fall_ss_nre": 89,
    "G2 Fall_dss_nre": 475,
    "G2 Winter_theta_nre": 0.088495183,
    "G2 Winter_pr_1_99_nre": 25,
    "G2 Winter_ss_nre": 89,
    "G2 Winter_dss_nre": 509,
    "G2 Spring_theta_nre": 0.321092162,
    "G2 Spring_pr_1_99_nre": 25,
    "G2 Spring_ss_nre": 90,
    "G2 Spring_dss_nre": 532,
    "G3 Fall_theta_nre": 0.573296588,
    "G3 Fall_pr_1_99_nre": 25,
    "G3 Fall_ss_nre": 90,
    "G3 Fall_dss_nre": 557,
    "G3 Winter_theta_nre": 0.899195387,
    "G3 Winter_pr_1_99_nre": 25,
    "G3 Winter_ss_nre": 89,
    "G3 Winter_dss_nre": 590,
    "G3 Spring_theta_nre": 1.180727188,
    "G3 Spring_pr_1_99_nre": 25,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 618
  },
  {
    "ID": 124,
    "KG Winter_theta_nre": -1.503744749,
    "KG Winter_pr_1_99_nre": 25,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 350,
    "KG Spring_theta_nre": -1.259478111,
    "KG Spring_pr_1_99_nre": 25,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 374,
    "G1 Fall_theta_nre": -1.009538654,
    "G1 Fall_pr_1_99_nre": 25,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 399,
    "G1 Winter_theta_nre": -0.717785503,
    "G1 Winter_pr_1_99_nre": 25,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 428,
    "G1 Spring_theta_nre": -0.419679612,
    "G1 Spring_pr_1_99_nre": 25,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 458,
    "G2 Fall_theta_nre": -0.234756659,
    "G2 Fall_pr_1_99_nre": 25,
    "G2 Fall_ss_nre": 89,
    "G2 Fall_dss_nre": 477,
    "G2 Winter_theta_nre": 0.089169813,
    "G2 Winter_pr_1_99_nre": 25,
    "G2 Winter_ss_nre": 89,
    "G2 Winter_dss_nre": 509,
    "G2 Spring_theta_nre": 0.327889502,
    "G2 Spring_pr_1_99_nre": 25,
    "G2 Spring_ss_nre": 90,
    "G2 Spring_dss_nre": 533,
    "G3 Fall_theta_nre": 0.583819772,
    "G3 Fall_pr_1_99_nre": 25,
    "G3 Fall_ss_nre": 90,
    "G3 Fall_dss_nre": 558,
    "G3 Winter_theta_nre": 0.904603539,
    "G3 Winter_pr_1_99_nre": 25,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 590,
    "G3 Spring_theta_nre": 1.183807191,
    "G3 Spring_pr_1_99_nre": 25,
    "G3 Spring_ss_nre": 88,
    "G3 Spring_dss_nre": 618
  },
  {
    "ID": 125,
    "KG Winter_theta_nre": -1.498186661,
    "KG Winter_pr_1_99_nre": 25,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 350,
    "KG Spring_theta_nre": -1.252997986,
    "KG Spring_pr_1_99_nre": 25,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 375,
    "G1 Fall_theta_nre": -1.006913757,
    "G1 Fall_pr_1_99_nre": 25,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 399,
    "G1 Winter_theta_nre": -0.713623559,
    "G1 Winter_pr_1_99_nre": 25,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 429,
    "G1 Spring_theta_nre": -0.413819318,
    "G1 Spring_pr_1_99_nre": 25,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 459,
    "G2 Fall_theta_nre": -0.231515245,
    "G2 Fall_pr_1_99_nre": 25,
    "G2 Fall_ss_nre": 89,
    "G2 Fall_dss_nre": 477,
    "G2 Winter_theta_nre": 0.097962119,
    "G2 Winter_pr_1_99_nre": 25,
    "G2 Winter_ss_nre": 89,
    "G2 Winter_dss_nre": 510,
    "G2 Spring_theta_nre": 0.33582734,
    "G2 Spring_pr_1_99_nre": 25,
    "G2 Spring_ss_nre": 90,
    "G2 Spring_dss_nre": 534,
    "G3 Fall_theta_nre": 0.604101902,
    "G3 Fall_pr_1_99_nre": 25,
    "G3 Fall_ss_nre": 90,
    "G3 Fall_dss_nre": 560,
    "G3 Winter_theta_nre": 0.908126055,
    "G3 Winter_pr_1_99_nre": 25,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 591,
    "G3 Spring_theta_nre": 1.187896162,
    "G3 Spring_pr_1_99_nre": 25,
    "G3 Spring_ss_nre": 89,
    "G3 Spring_dss_nre": 619
  },
  {
    "ID": 126,
    "KG Winter_theta_nre": -1.49536687,
    "KG Winter_pr_1_99_nre": 25,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 350,
    "KG Spring_theta_nre": -1.241701007,
    "KG Spring_pr_1_99_nre": 25,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 376,
    "G1 Fall_theta_nre": -1.001315369,
    "G1 Fall_pr_1_99_nre": 25,
    "G1 Fall_ss_nre": 89,
    "G1 Fall_dss_nre": 400,
    "G1 Winter_theta_nre": -0.709988139,
    "G1 Winter_pr_1_99_nre": 25,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 429,
    "G1 Spring_theta_nre": -0.413599522,
    "G1 Spring_pr_1_99_nre": 25,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 459,
    "G2 Fall_theta_nre": -0.224557708,
    "G2 Fall_pr_1_99_nre": 25,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 478,
    "G2 Winter_theta_nre": 0.101583277,
    "G2 Winter_pr_1_99_nre": 25,
    "G2 Winter_ss_nre": 89,
    "G2 Winter_dss_nre": 510,
    "G2 Spring_theta_nre": 0.342674825,
    "G2 Spring_pr_1_99_nre": 25,
    "G2 Spring_ss_nre": 90,
    "G2 Spring_dss_nre": 534,
    "G3 Fall_theta_nre": 0.607267469,
    "G3 Fall_pr_1_99_nre": 25,
    "G3 Fall_ss_nre": 90,
    "G3 Fall_dss_nre": 561,
    "G3 Winter_theta_nre": 0.912987161,
    "G3 Winter_pr_1_99_nre": 25,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 591,
    "G3 Spring_theta_nre": 1.19655852,
    "G3 Spring_pr_1_99_nre": 25,
    "G3 Spring_ss_nre": 89,
    "G3 Spring_dss_nre": 620
  },
  {
    "ID": 127,
    "KG Winter_theta_nre": -1.482933796,
    "KG Winter_pr_1_99_nre": 25,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 352,
    "KG Spring_theta_nre": -1.236767626,
    "KG Spring_pr_1_99_nre": 25,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 376,
    "G1 Fall_theta_nre": -0.994229788,
    "G1 Fall_pr_1_99_nre": 25,
    "G1 Fall_ss_nre": 90,
    "G1 Fall_dss_nre": 401,
    "G1 Winter_theta_nre": -0.704949225,
    "G1 Winter_pr_1_99_nre": 25,
    "G1 Winter_ss_nre": 89,
    "G1 Winter_dss_nre": 430,
    "G1 Spring_theta_nre": -0.41121762,
    "G1 Spring_pr_1_99_nre": 25,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 459,
    "G2 Fall_theta_nre": -0.208800345,
    "G2 Fall_pr_1_99_nre": 25,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 479,
    "G2 Winter_theta_nre": 0.114275297,
    "G2 Winter_pr_1_99_nre": 25,
    "G2 Winter_ss_nre": 89,
    "G2 Winter_dss_nre": 511,
    "G2 Spring_theta_nre": 0.343927534,
    "G2 Spring_pr_1_99_nre": 25,
    "G2 Spring_ss_nre": 90,
    "G2 Spring_dss_nre": 534,
    "G3 Fall_theta_nre": 0.61561607,
    "G3 Fall_pr_1_99_nre": 25,
    "G3 Fall_ss_nre": 90,
    "G3 Fall_dss_nre": 562,
    "G3 Winter_theta_nre": 0.914329681,
    "G3 Winter_pr_1_99_nre": 25,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 591,
    "G3 Spring_theta_nre": 1.206153811,
    "G3 Spring_pr_1_99_nre": 25,
    "G3 Spring_ss_nre": 89,
    "G3 Spring_dss_nre": 621
  },
  {
    "ID": 128,
    "KG Winter_theta_nre": -1.482413187,
    "KG Winter_pr_1_99_nre": 26,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 352,
    "KG Spring_theta_nre": -1.235100912,
    "KG Spring_pr_1_99_nre": 26,
    "KG Spring_ss_nre": 89,
    "KG Spring_dss_nre": 376,
    "G1 Fall_theta_nre": -0.98100967,
    "G1 Fall_pr_1_99_nre": 26,
    "G1 Fall_ss_nre": 90,
    "G1 Fall_dss_nre": 402,
    "G1 Winter_theta_nre": -0.694679747,
    "G1 Winter_pr_1_99_nre": 26,
    "G1 Winter_ss_nre": 90,
    "G1 Winter_dss_nre": 431,
    "G1 Spring_theta_nre": -0.407253401,
    "G1 Spring_pr_1_99_nre": 26,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 459,
    "G2 Fall_theta_nre": -0.200396148,
    "G2 Fall_pr_1_99_nre": 26,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 480,
    "G2 Winter_theta_nre": 0.114502735,
    "G2 Winter_pr_1_99_nre": 26,
    "G2 Winter_ss_nre": 89,
    "G2 Winter_dss_nre": 511,
    "G2 Spring_theta_nre": 0.344421372,
    "G2 Spring_pr_1_99_nre": 26,
    "G2 Spring_ss_nre": 90,
    "G2 Spring_dss_nre": 534,
    "G3 Fall_theta_nre": 0.620795795,
    "G3 Fall_pr_1_99_nre": 26,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 562,
    "G3 Winter_theta_nre": 0.919296549,
    "G3 Winter_pr_1_99_nre": 26,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 592,
    "G3 Spring_theta_nre": 1.208710564,
    "G3 Spring_pr_1_99_nre": 26,
    "G3 Spring_ss_nre": 89,
    "G3 Spring_dss_nre": 621
  },
  {
    "ID": 129,
    "KG Winter_theta_nre": -1.480880582,
    "KG Winter_pr_1_99_nre": 26,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 352,
    "KG Spring_theta_nre": -1.22770518,
    "KG Spring_pr_1_99_nre": 26,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 377,
    "G1 Fall_theta_nre": -0.980786494,
    "G1 Fall_pr_1_99_nre": 26,
    "G1 Fall_ss_nre": 90,
    "G1 Fall_dss_nre": 402,
    "G1 Winter_theta_nre": -0.691377896,
    "G1 Winter_pr_1_99_nre": 26,
    "G1 Winter_ss_nre": 90,
    "G1 Winter_dss_nre": 431,
    "G1 Spring_theta_nre": -0.395175787,
    "G1 Spring_pr_1_99_nre": 26,
    "G1 Spring_ss_nre": 89,
    "G1 Spring_dss_nre": 460,
    "G2 Fall_theta_nre": -0.19906573,
    "G2 Fall_pr_1_99_nre": 26,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 480,
    "G2 Winter_theta_nre": 0.115390159,
    "G2 Winter_pr_1_99_nre": 26,
    "G2 Winter_ss_nre": 89,
    "G2 Winter_dss_nre": 512,
    "G2 Spring_theta_nre": 0.354145852,
    "G2 Spring_pr_1_99_nre": 26,
    "G2 Spring_ss_nre": 90,
    "G2 Spring_dss_nre": 535,
    "G3 Fall_theta_nre": 0.622228699,
    "G3 Fall_pr_1_99_nre": 26,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 562,
    "G3 Winter_theta_nre": 0.921877675,
    "G3 Winter_pr_1_99_nre": 26,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 592,
    "G3 Spring_theta_nre": 1.213096138,
    "G3 Spring_pr_1_99_nre": 26,
    "G3 Spring_ss_nre": 89,
    "G3 Spring_dss_nre": 621
  },
  {
    "ID": 130,
    "KG Winter_theta_nre": -1.480664588,
    "KG Winter_pr_1_99_nre": 26,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 352,
    "KG Spring_theta_nre": -1.225953737,
    "KG Spring_pr_1_99_nre": 26,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 377,
    "G1 Fall_theta_nre": -0.979829281,
    "G1 Fall_pr_1_99_nre": 26,
    "G1 Fall_ss_nre": 90,
    "G1 Fall_dss_nre": 402,
    "G1 Winter_theta_nre": -0.675659981,
    "G1 Winter_pr_1_99_nre": 26,
    "G1 Winter_ss_nre": 90,
    "G1 Winter_dss_nre": 432,
    "G1 Spring_theta_nre": -0.384822351,
    "G1 Spring_pr_1_99_nre": 26,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 462,
    "G2 Fall_theta_nre": -0.189616217,
    "G2 Fall_pr_1_99_nre": 26,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 481,
    "G2 Winter_theta_nre": 0.118016984,
    "G2 Winter_pr_1_99_nre": 26,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 512,
    "G2 Spring_theta_nre": 0.3724994,
    "G2 Spring_pr_1_99_nre": 26,
    "G2 Spring_ss_nre": 91,
    "G2 Spring_dss_nre": 537,
    "G3 Fall_theta_nre": 0.626747079,
    "G3 Fall_pr_1_99_nre": 26,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 563,
    "G3 Winter_theta_nre": 0.925167043,
    "G3 Winter_pr_1_99_nre": 26,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 593,
    "G3 Spring_theta_nre": 1.213569588,
    "G3 Spring_pr_1_99_nre": 26,
    "G3 Spring_ss_nre": 89,
    "G3 Spring_dss_nre": 621
  },
  {
    "ID": 131,
    "KG Winter_theta_nre": -1.478001258,
    "KG Winter_pr_1_99_nre": 26,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 352,
    "KG Spring_theta_nre": -1.223534814,
    "KG Spring_pr_1_99_nre": 26,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 378,
    "G1 Fall_theta_nre": -0.973027548,
    "G1 Fall_pr_1_99_nre": 26,
    "G1 Fall_ss_nre": 90,
    "G1 Fall_dss_nre": 403,
    "G1 Winter_theta_nre": -0.672898026,
    "G1 Winter_pr_1_99_nre": 26,
    "G1 Winter_ss_nre": 90,
    "G1 Winter_dss_nre": 433,
    "G1 Spring_theta_nre": -0.383287712,
    "G1 Spring_pr_1_99_nre": 26,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 462,
    "G2 Fall_theta_nre": -0.187472505,
    "G2 Fall_pr_1_99_nre": 26,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 481,
    "G2 Winter_theta_nre": 0.120063525,
    "G2 Winter_pr_1_99_nre": 26,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 512,
    "G2 Spring_theta_nre": 0.37479372,
    "G2 Spring_pr_1_99_nre": 26,
    "G2 Spring_ss_nre": 91,
    "G2 Spring_dss_nre": 537,
    "G3 Fall_theta_nre": 0.634187091,
    "G3 Fall_pr_1_99_nre": 26,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 563,
    "G3 Winter_theta_nre": 0.929353069,
    "G3 Winter_pr_1_99_nre": 26,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 593,
    "G3 Spring_theta_nre": 1.214077666,
    "G3 Spring_pr_1_99_nre": 26,
    "G3 Spring_ss_nre": 89,
    "G3 Spring_dss_nre": 621
  },
  {
    "ID": 132,
    "KG Winter_theta_nre": -1.476641505,
    "KG Winter_pr_1_99_nre": 26,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 352,
    "KG Spring_theta_nre": -1.223120649,
    "KG Spring_pr_1_99_nre": 26,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 378,
    "G1 Fall_theta_nre": -0.94786766,
    "G1 Fall_pr_1_99_nre": 26,
    "G1 Fall_ss_nre": 90,
    "G1 Fall_dss_nre": 405,
    "G1 Winter_theta_nre": -0.66974412,
    "G1 Winter_pr_1_99_nre": 26,
    "G1 Winter_ss_nre": 90,
    "G1 Winter_dss_nre": 433,
    "G1 Spring_theta_nre": -0.365150965,
    "G1 Spring_pr_1_99_nre": 26,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 463,
    "G2 Fall_theta_nre": -0.185185413,
    "G2 Fall_pr_1_99_nre": 26,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 481,
    "G2 Winter_theta_nre": 0.128685942,
    "G2 Winter_pr_1_99_nre": 26,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 513,
    "G2 Spring_theta_nre": 0.378746919,
    "G2 Spring_pr_1_99_nre": 26,
    "G2 Spring_ss_nre": 91,
    "G2 Spring_dss_nre": 538,
    "G3 Fall_theta_nre": 0.645020867,
    "G3 Fall_pr_1_99_nre": 26,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 565,
    "G3 Winter_theta_nre": 0.931128067,
    "G3 Winter_pr_1_99_nre": 26,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 593,
    "G3 Spring_theta_nre": 1.221709064,
    "G3 Spring_pr_1_99_nre": 26,
    "G3 Spring_ss_nre": 89,
    "G3 Spring_dss_nre": 622
  },
  {
    "ID": 133,
    "KG Winter_theta_nre": -1.473799034,
    "KG Winter_pr_1_99_nre": 27,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 353,
    "KG Spring_theta_nre": -1.212794651,
    "KG Spring_pr_1_99_nre": 27,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 379,
    "G1 Fall_theta_nre": -0.94265926,
    "G1 Fall_pr_1_99_nre": 27,
    "G1 Fall_ss_nre": 90,
    "G1 Fall_dss_nre": 406,
    "G1 Winter_theta_nre": -0.651290249,
    "G1 Winter_pr_1_99_nre": 27,
    "G1 Winter_ss_nre": 90,
    "G1 Winter_dss_nre": 435,
    "G1 Spring_theta_nre": -0.362054022,
    "G1 Spring_pr_1_99_nre": 27,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 464,
    "G2 Fall_theta_nre": -0.182904023,
    "G2 Fall_pr_1_99_nre": 27,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 482,
    "G2 Winter_theta_nre": 0.1341536,
    "G2 Winter_pr_1_99_nre": 27,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 513,
    "G2 Spring_theta_nre": 0.388597555,
    "G2 Spring_pr_1_99_nre": 27,
    "G2 Spring_ss_nre": 91,
    "G2 Spring_dss_nre": 539,
    "G3 Fall_theta_nre": 0.65575998,
    "G3 Fall_pr_1_99_nre": 27,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 566,
    "G3 Winter_theta_nre": 0.93801745,
    "G3 Winter_pr_1_99_nre": 27,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 594,
    "G3 Spring_theta_nre": 1.237291452,
    "G3 Spring_pr_1_99_nre": 27,
    "G3 Spring_ss_nre": 90,
    "G3 Spring_dss_nre": 624
  },
  {
    "ID": 134,
    "KG Winter_theta_nre": -1.467774223,
    "KG Winter_pr_1_99_nre": 27,
    "KG Winter_ss_nre": 90,
    "KG Winter_dss_nre": 353,
    "KG Spring_theta_nre": -1.21273036,
    "KG Spring_pr_1_99_nre": 27,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 379,
    "G1 Fall_theta_nre": -0.940954224,
    "G1 Fall_pr_1_99_nre": 27,
    "G1 Fall_ss_nre": 90,
    "G1 Fall_dss_nre": 406,
    "G1 Winter_theta_nre": -0.651183028,
    "G1 Winter_pr_1_99_nre": 27,
    "G1 Winter_ss_nre": 90,
    "G1 Winter_dss_nre": 435,
    "G1 Spring_theta_nre": -0.361826336,
    "G1 Spring_pr_1_99_nre": 27,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 464,
    "G2 Fall_theta_nre": -0.169068893,
    "G2 Fall_pr_1_99_nre": 27,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 483,
    "G2 Winter_theta_nre": 0.134507435,
    "G2 Winter_pr_1_99_nre": 27,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 513,
    "G2 Spring_theta_nre": 0.394384412,
    "G2 Spring_pr_1_99_nre": 27,
    "G2 Spring_ss_nre": 91,
    "G2 Spring_dss_nre": 539,
    "G3 Fall_theta_nre": 0.656963154,
    "G3 Fall_pr_1_99_nre": 27,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 566,
    "G3 Winter_theta_nre": 0.939778724,
    "G3 Winter_pr_1_99_nre": 27,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 594,
    "G3 Spring_theta_nre": 1.24330776,
    "G3 Spring_pr_1_99_nre": 27,
    "G3 Spring_ss_nre": 90,
    "G3 Spring_dss_nre": 624
  },
  {
    "ID": 135,
    "KG Winter_theta_nre": -1.458748793,
    "KG Winter_pr_1_99_nre": 27,
    "KG Winter_ss_nre": 91,
    "KG Winter_dss_nre": 354,
    "KG Spring_theta_nre": -1.210917615,
    "KG Spring_pr_1_99_nre": 27,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 379,
    "G1 Fall_theta_nre": -0.931410845,
    "G1 Fall_pr_1_99_nre": 27,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 407,
    "G1 Winter_theta_nre": -0.646431896,
    "G1 Winter_pr_1_99_nre": 27,
    "G1 Winter_ss_nre": 90,
    "G1 Winter_dss_nre": 435,
    "G1 Spring_theta_nre": -0.361703425,
    "G1 Spring_pr_1_99_nre": 27,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 464,
    "G2 Fall_theta_nre": -0.168614526,
    "G2 Fall_pr_1_99_nre": 27,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 483,
    "G2 Winter_theta_nre": 0.138209613,
    "G2 Winter_pr_1_99_nre": 27,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 514,
    "G2 Spring_theta_nre": 0.400662691,
    "G2 Spring_pr_1_99_nre": 27,
    "G2 Spring_ss_nre": 91,
    "G2 Spring_dss_nre": 540,
    "G3 Fall_theta_nre": 0.662080928,
    "G3 Fall_pr_1_99_nre": 27,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 566,
    "G3 Winter_theta_nre": 0.946846556,
    "G3 Winter_pr_1_99_nre": 27,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 595,
    "G3 Spring_theta_nre": 1.244935332,
    "G3 Spring_pr_1_99_nre": 27,
    "G3 Spring_ss_nre": 90,
    "G3 Spring_dss_nre": 624
  },
  {
    "ID": 136,
    "KG Winter_theta_nre": -1.450699049,
    "KG Winter_pr_1_99_nre": 27,
    "KG Winter_ss_nre": 91,
    "KG Winter_dss_nre": 355,
    "KG Spring_theta_nre": -1.206388288,
    "KG Spring_pr_1_99_nre": 27,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 379,
    "G1 Fall_theta_nre": -0.931064473,
    "G1 Fall_pr_1_99_nre": 27,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 407,
    "G1 Winter_theta_nre": -0.644768809,
    "G1 Winter_pr_1_99_nre": 27,
    "G1 Winter_ss_nre": 91,
    "G1 Winter_dss_nre": 436,
    "G1 Spring_theta_nre": -0.352531808,
    "G1 Spring_pr_1_99_nre": 27,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 465,
    "G2 Fall_theta_nre": -0.168260712,
    "G2 Fall_pr_1_99_nre": 27,
    "G2 Fall_ss_nre": 90,
    "G2 Fall_dss_nre": 483,
    "G2 Winter_theta_nre": 0.142185363,
    "G2 Winter_pr_1_99_nre": 27,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 514,
    "G2 Spring_theta_nre": 0.404640491,
    "G2 Spring_pr_1_99_nre": 27,
    "G2 Spring_ss_nre": 91,
    "G2 Spring_dss_nre": 540,
    "G3 Fall_theta_nre": 0.662498807,
    "G3 Fall_pr_1_99_nre": 27,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 566,
    "G3 Winter_theta_nre": 0.953022327,
    "G3 Winter_pr_1_99_nre": 27,
    "G3 Winter_ss_nre": 90,
    "G3 Winter_dss_nre": 595,
    "G3 Spring_theta_nre": 1.249296799,
    "G3 Spring_pr_1_99_nre": 27,
    "G3 Spring_ss_nre": 90,
    "G3 Spring_dss_nre": 625
  },
  {
    "ID": 137,
    "KG Winter_theta_nre": -1.449411062,
    "KG Winter_pr_1_99_nre": 27,
    "KG Winter_ss_nre": 91,
    "KG Winter_dss_nre": 355,
    "KG Spring_theta_nre": -1.204510942,
    "KG Spring_pr_1_99_nre": 27,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 380,
    "G1 Fall_theta_nre": -0.92678606,
    "G1 Fall_pr_1_99_nre": 27,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 407,
    "G1 Winter_theta_nre": -0.637198509,
    "G1 Winter_pr_1_99_nre": 27,
    "G1 Winter_ss_nre": 91,
    "G1 Winter_dss_nre": 436,
    "G1 Spring_theta_nre": -0.351548278,
    "G1 Spring_pr_1_99_nre": 27,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 465,
    "G2 Fall_theta_nre": -0.166251209,
    "G2 Fall_pr_1_99_nre": 27,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 483,
    "G2 Winter_theta_nre": 0.145779542,
    "G2 Winter_pr_1_99_nre": 27,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 515,
    "G2 Spring_theta_nre": 0.410412074,
    "G2 Spring_pr_1_99_nre": 27,
    "G2 Spring_ss_nre": 91,
    "G2 Spring_dss_nre": 541,
    "G3 Fall_theta_nre": 0.665857744,
    "G3 Fall_pr_1_99_nre": 27,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 567,
    "G3 Winter_theta_nre": 0.958802026,
    "G3 Winter_pr_1_99_nre": 27,
    "G3 Winter_ss_nre": 91,
    "G3 Winter_dss_nre": 596,
    "G3 Spring_theta_nre": 1.252739548,
    "G3 Spring_pr_1_99_nre": 27,
    "G3 Spring_ss_nre": 90,
    "G3 Spring_dss_nre": 625
  },
  {
    "ID": 138,
    "KG Winter_theta_nre": -1.442347933,
    "KG Winter_pr_1_99_nre": 28,
    "KG Winter_ss_nre": 91,
    "KG Winter_dss_nre": 356,
    "KG Spring_theta_nre": -1.196873331,
    "KG Spring_pr_1_99_nre": 28,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 380,
    "G1 Fall_theta_nre": -0.920789528,
    "G1 Fall_pr_1_99_nre": 28,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 408,
    "G1 Winter_theta_nre": -0.634337325,
    "G1 Winter_pr_1_99_nre": 28,
    "G1 Winter_ss_nre": 91,
    "G1 Winter_dss_nre": 437,
    "G1 Spring_theta_nre": -0.350255812,
    "G1 Spring_pr_1_99_nre": 28,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 465,
    "G2 Fall_theta_nre": -0.147221996,
    "G2 Fall_pr_1_99_nre": 28,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 485,
    "G2 Winter_theta_nre": 0.147952136,
    "G2 Winter_pr_1_99_nre": 28,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 515,
    "G2 Spring_theta_nre": 0.413856736,
    "G2 Spring_pr_1_99_nre": 28,
    "G2 Spring_ss_nre": 91,
    "G2 Spring_dss_nre": 541,
    "G3 Fall_theta_nre": 0.675348757,
    "G3 Fall_pr_1_99_nre": 28,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 568,
    "G3 Winter_theta_nre": 0.968635525,
    "G3 Winter_pr_1_99_nre": 28,
    "G3 Winter_ss_nre": 91,
    "G3 Winter_dss_nre": 597,
    "G3 Spring_theta_nre": 1.261414224,
    "G3 Spring_pr_1_99_nre": 28,
    "G3 Spring_ss_nre": 90,
    "G3 Spring_dss_nre": 626
  },
  {
    "ID": 139,
    "KG Winter_theta_nre": -1.438856012,
    "KG Winter_pr_1_99_nre": 28,
    "KG Winter_ss_nre": 91,
    "KG Winter_dss_nre": 356,
    "KG Spring_theta_nre": -1.184735621,
    "KG Spring_pr_1_99_nre": 28,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 382,
    "G1 Fall_theta_nre": -0.916598585,
    "G1 Fall_pr_1_99_nre": 28,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 408,
    "G1 Winter_theta_nre": -0.618319974,
    "G1 Winter_pr_1_99_nre": 28,
    "G1 Winter_ss_nre": 91,
    "G1 Winter_dss_nre": 438,
    "G1 Spring_theta_nre": -0.350251846,
    "G1 Spring_pr_1_99_nre": 28,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 465,
    "G2 Fall_theta_nre": -0.145669325,
    "G2 Fall_pr_1_99_nre": 28,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 485,
    "G2 Winter_theta_nre": 0.164729403,
    "G2 Winter_pr_1_99_nre": 28,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 516,
    "G2 Spring_theta_nre": 0.425786317,
    "G2 Spring_pr_1_99_nre": 28,
    "G2 Spring_ss_nre": 91,
    "G2 Spring_dss_nre": 543,
    "G3 Fall_theta_nre": 0.677877947,
    "G3 Fall_pr_1_99_nre": 28,
    "G3 Fall_ss_nre": 91,
    "G3 Fall_dss_nre": 568,
    "G3 Winter_theta_nre": 0.97305579,
    "G3 Winter_pr_1_99_nre": 28,
    "G3 Winter_ss_nre": 91,
    "G3 Winter_dss_nre": 597,
    "G3 Spring_theta_nre": 1.262781283,
    "G3 Spring_pr_1_99_nre": 28,
    "G3 Spring_ss_nre": 90,
    "G3 Spring_dss_nre": 626
  },
  {
    "ID": 140,
    "KG Winter_theta_nre": -1.434002772,
    "KG Winter_pr_1_99_nre": 28,
    "KG Winter_ss_nre": 91,
    "KG Winter_dss_nre": 357,
    "KG Spring_theta_nre": -1.18035322,
    "KG Spring_pr_1_99_nre": 28,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 382,
    "G1 Fall_theta_nre": -0.907574238,
    "G1 Fall_pr_1_99_nre": 28,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 409,
    "G1 Winter_theta_nre": -0.616364833,
    "G1 Winter_pr_1_99_nre": 28,
    "G1 Winter_ss_nre": 91,
    "G1 Winter_dss_nre": 438,
    "G1 Spring_theta_nre": -0.346462223,
    "G1 Spring_pr_1_99_nre": 28,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 465,
    "G2 Fall_theta_nre": -0.143433912,
    "G2 Fall_pr_1_99_nre": 28,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 486,
    "G2 Winter_theta_nre": 0.165474513,
    "G2 Winter_pr_1_99_nre": 28,
    "G2 Winter_ss_nre": 90,
    "G2 Winter_dss_nre": 517,
    "G2 Spring_theta_nre": 0.427116155,
    "G2 Spring_pr_1_99_nre": 28,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 543,
    "G3 Fall_theta_nre": 0.682743436,
    "G3 Fall_pr_1_99_nre": 28,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 568,
    "G3 Winter_theta_nre": 0.976176647,
    "G3 Winter_pr_1_99_nre": 28,
    "G3 Winter_ss_nre": 91,
    "G3 Winter_dss_nre": 598,
    "G3 Spring_theta_nre": 1.263332075,
    "G3 Spring_pr_1_99_nre": 28,
    "G3 Spring_ss_nre": 90,
    "G3 Spring_dss_nre": 626
  },
  {
    "ID": 141,
    "KG Winter_theta_nre": -1.42718009,
    "KG Winter_pr_1_99_nre": 28,
    "KG Winter_ss_nre": 91,
    "KG Winter_dss_nre": 357,
    "KG Spring_theta_nre": -1.176796796,
    "KG Spring_pr_1_99_nre": 28,
    "KG Spring_ss_nre": 90,
    "KG Spring_dss_nre": 382,
    "G1 Fall_theta_nre": -0.902861422,
    "G1 Fall_pr_1_99_nre": 28,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 410,
    "G1 Winter_theta_nre": -0.616314916,
    "G1 Winter_pr_1_99_nre": 28,
    "G1 Winter_ss_nre": 91,
    "G1 Winter_dss_nre": 438,
    "G1 Spring_theta_nre": -0.344751999,
    "G1 Spring_pr_1_99_nre": 28,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 466,
    "G2 Fall_theta_nre": -0.131379729,
    "G2 Fall_pr_1_99_nre": 28,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 487,
    "G2 Winter_theta_nre": 0.170710177,
    "G2 Winter_pr_1_99_nre": 28,
    "G2 Winter_ss_nre": 91,
    "G2 Winter_dss_nre": 517,
    "G2 Spring_theta_nre": 0.431738793,
    "G2 Spring_pr_1_99_nre": 28,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 543,
    "G3 Fall_theta_nre": 0.694935201,
    "G3 Fall_pr_1_99_nre": 28,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 569,
    "G3 Winter_theta_nre": 0.98351535,
    "G3 Winter_pr_1_99_nre": 28,
    "G3 Winter_ss_nre": 91,
    "G3 Winter_dss_nre": 598,
    "G3 Spring_theta_nre": 1.26877684,
    "G3 Spring_pr_1_99_nre": 28,
    "G3 Spring_ss_nre": 90,
    "G3 Spring_dss_nre": 627
  },
  {
    "ID": 142,
    "KG Winter_theta_nre": -1.40571375,
    "KG Winter_pr_1_99_nre": 28,
    "KG Winter_ss_nre": 92,
    "KG Winter_dss_nre": 359,
    "KG Spring_theta_nre": -1.172336038,
    "KG Spring_pr_1_99_nre": 28,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 383,
    "G1 Fall_theta_nre": -0.89893914,
    "G1 Fall_pr_1_99_nre": 28,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 410,
    "G1 Winter_theta_nre": -0.612008424,
    "G1 Winter_pr_1_99_nre": 28,
    "G1 Winter_ss_nre": 91,
    "G1 Winter_dss_nre": 439,
    "G1 Spring_theta_nre": -0.344484483,
    "G1 Spring_pr_1_99_nre": 28,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 466,
    "G2 Fall_theta_nre": -0.128545359,
    "G2 Fall_pr_1_99_nre": 28,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 487,
    "G2 Winter_theta_nre": 0.180691419,
    "G2 Winter_pr_1_99_nre": 28,
    "G2 Winter_ss_nre": 91,
    "G2 Winter_dss_nre": 518,
    "G2 Spring_theta_nre": 0.432303531,
    "G2 Spring_pr_1_99_nre": 28,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 543,
    "G3 Fall_theta_nre": 0.698196398,
    "G3 Fall_pr_1_99_nre": 28,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 570,
    "G3 Winter_theta_nre": 0.986443737,
    "G3 Winter_pr_1_99_nre": 28,
    "G3 Winter_ss_nre": 91,
    "G3 Winter_dss_nre": 599,
    "G3 Spring_theta_nre": 1.271319832,
    "G3 Spring_pr_1_99_nre": 28,
    "G3 Spring_ss_nre": 90,
    "G3 Spring_dss_nre": 627
  },
  {
    "ID": 143,
    "KG Winter_theta_nre": -1.401847312,
    "KG Winter_pr_1_99_nre": 29,
    "KG Winter_ss_nre": 92,
    "KG Winter_dss_nre": 360,
    "KG Spring_theta_nre": -1.171019894,
    "KG Spring_pr_1_99_nre": 29,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 383,
    "G1 Fall_theta_nre": -0.898606569,
    "G1 Fall_pr_1_99_nre": 29,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 410,
    "G1 Winter_theta_nre": -0.604229155,
    "G1 Winter_pr_1_99_nre": 29,
    "G1 Winter_ss_nre": 91,
    "G1 Winter_dss_nre": 440,
    "G1 Spring_theta_nre": -0.337585799,
    "G1 Spring_pr_1_99_nre": 29,
    "G1 Spring_ss_nre": 90,
    "G1 Spring_dss_nre": 466,
    "G2 Fall_theta_nre": -0.128050973,
    "G2 Fall_pr_1_99_nre": 29,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 487,
    "G2 Winter_theta_nre": 0.20154015,
    "G2 Winter_pr_1_99_nre": 29,
    "G2 Winter_ss_nre": 91,
    "G2 Winter_dss_nre": 520,
    "G2 Spring_theta_nre": 0.437586007,
    "G2 Spring_pr_1_99_nre": 29,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 544,
    "G3 Fall_theta_nre": 0.70465552,
    "G3 Fall_pr_1_99_nre": 29,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 570,
    "G3 Winter_theta_nre": 1.004484245,
    "G3 Winter_pr_1_99_nre": 29,
    "G3 Winter_ss_nre": 92,
    "G3 Winter_dss_nre": 600,
    "G3 Spring_theta_nre": 1.28119047,
    "G3 Spring_pr_1_99_nre": 29,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 628
  },
  {
    "ID": 144,
    "KG Winter_theta_nre": -1.397141903,
    "KG Winter_pr_1_99_nre": 29,
    "KG Winter_ss_nre": 92,
    "KG Winter_dss_nre": 360,
    "KG Spring_theta_nre": -1.159434776,
    "KG Spring_pr_1_99_nre": 29,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 384,
    "G1 Fall_theta_nre": -0.897470313,
    "G1 Fall_pr_1_99_nre": 29,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 410,
    "G1 Winter_theta_nre": -0.59601081,
    "G1 Winter_pr_1_99_nre": 29,
    "G1 Winter_ss_nre": 91,
    "G1 Winter_dss_nre": 440,
    "G1 Spring_theta_nre": -0.330654616,
    "G1 Spring_pr_1_99_nre": 29,
    "G1 Spring_ss_nre": 91,
    "G1 Spring_dss_nre": 467,
    "G2 Fall_theta_nre": -0.123146264,
    "G2 Fall_pr_1_99_nre": 29,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 488,
    "G2 Winter_theta_nre": 0.202880972,
    "G2 Winter_pr_1_99_nre": 29,
    "G2 Winter_ss_nre": 91,
    "G2 Winter_dss_nre": 520,
    "G2 Spring_theta_nre": 0.438183613,
    "G2 Spring_pr_1_99_nre": 29,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 544,
    "G3 Fall_theta_nre": 0.709550767,
    "G3 Fall_pr_1_99_nre": 29,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 571,
    "G3 Winter_theta_nre": 1.007200964,
    "G3 Winter_pr_1_99_nre": 29,
    "G3 Winter_ss_nre": 92,
    "G3 Winter_dss_nre": 601,
    "G3 Spring_theta_nre": 1.281834052,
    "G3 Spring_pr_1_99_nre": 29,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 628
  },
  {
    "ID": 145,
    "KG Winter_theta_nre": -1.389467133,
    "KG Winter_pr_1_99_nre": 29,
    "KG Winter_ss_nre": 92,
    "KG Winter_dss_nre": 361,
    "KG Spring_theta_nre": -1.148305162,
    "KG Spring_pr_1_99_nre": 29,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 385,
    "G1 Fall_theta_nre": -0.894431575,
    "G1 Fall_pr_1_99_nre": 29,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 411,
    "G1 Winter_theta_nre": -0.58910901,
    "G1 Winter_pr_1_99_nre": 29,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 441,
    "G1 Spring_theta_nre": -0.31771539,
    "G1 Spring_pr_1_99_nre": 29,
    "G1 Spring_ss_nre": 91,
    "G1 Spring_dss_nre": 468,
    "G2 Fall_theta_nre": -0.122827935,
    "G2 Fall_pr_1_99_nre": 29,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 488,
    "G2 Winter_theta_nre": 0.203837262,
    "G2 Winter_pr_1_99_nre": 29,
    "G2 Winter_ss_nre": 91,
    "G2 Winter_dss_nre": 520,
    "G2 Spring_theta_nre": 0.438579534,
    "G2 Spring_pr_1_99_nre": 29,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 544,
    "G3 Fall_theta_nre": 0.712266792,
    "G3 Fall_pr_1_99_nre": 29,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 571,
    "G3 Winter_theta_nre": 1.008307038,
    "G3 Winter_pr_1_99_nre": 29,
    "G3 Winter_ss_nre": 92,
    "G3 Winter_dss_nre": 601,
    "G3 Spring_theta_nre": 1.282543553,
    "G3 Spring_pr_1_99_nre": 29,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 628
  },
  {
    "ID": 146,
    "KG Winter_theta_nre": -1.388023794,
    "KG Winter_pr_1_99_nre": 29,
    "KG Winter_ss_nre": 92,
    "KG Winter_dss_nre": 361,
    "KG Spring_theta_nre": -1.145449203,
    "KG Spring_pr_1_99_nre": 29,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 385,
    "G1 Fall_theta_nre": -0.882601825,
    "G1 Fall_pr_1_99_nre": 29,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 412,
    "G1 Winter_theta_nre": -0.586150569,
    "G1 Winter_pr_1_99_nre": 29,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 441,
    "G1 Spring_theta_nre": -0.311064473,
    "G1 Spring_pr_1_99_nre": 29,
    "G1 Spring_ss_nre": 91,
    "G1 Spring_dss_nre": 469,
    "G2 Fall_theta_nre": -0.117275453,
    "G2 Fall_pr_1_99_nre": 29,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 488,
    "G2 Winter_theta_nre": 0.216819414,
    "G2 Winter_pr_1_99_nre": 29,
    "G2 Winter_ss_nre": 91,
    "G2 Winter_dss_nre": 522,
    "G2 Spring_theta_nre": 0.456402902,
    "G2 Spring_pr_1_99_nre": 29,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 546,
    "G3 Fall_theta_nre": 0.713397591,
    "G3 Fall_pr_1_99_nre": 29,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 571,
    "G3 Winter_theta_nre": 1.013216595,
    "G3 Winter_pr_1_99_nre": 29,
    "G3 Winter_ss_nre": 92,
    "G3 Winter_dss_nre": 601,
    "G3 Spring_theta_nre": 1.286211269,
    "G3 Spring_pr_1_99_nre": 29,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 629
  },
  {
    "ID": 147,
    "KG Winter_theta_nre": -1.373873299,
    "KG Winter_pr_1_99_nre": 29,
    "KG Winter_ss_nre": 92,
    "KG Winter_dss_nre": 363,
    "KG Spring_theta_nre": -1.139185845,
    "KG Spring_pr_1_99_nre": 29,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 386,
    "G1 Fall_theta_nre": -0.880547833,
    "G1 Fall_pr_1_99_nre": 29,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 412,
    "G1 Winter_theta_nre": -0.57345994,
    "G1 Winter_pr_1_99_nre": 29,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 443,
    "G1 Spring_theta_nre": -0.310843182,
    "G1 Spring_pr_1_99_nre": 29,
    "G1 Spring_ss_nre": 91,
    "G1 Spring_dss_nre": 469,
    "G2 Fall_theta_nre": -0.114637444,
    "G2 Fall_pr_1_99_nre": 29,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 489,
    "G2 Winter_theta_nre": 0.225558181,
    "G2 Winter_pr_1_99_nre": 29,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 523,
    "G2 Spring_theta_nre": 0.456917559,
    "G2 Spring_pr_1_99_nre": 29,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 546,
    "G3 Fall_theta_nre": 0.714701175,
    "G3 Fall_pr_1_99_nre": 29,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 571,
    "G3 Winter_theta_nre": 1.023000778,
    "G3 Winter_pr_1_99_nre": 29,
    "G3 Winter_ss_nre": 92,
    "G3 Winter_dss_nre": 602,
    "G3 Spring_theta_nre": 1.292835194,
    "G3 Spring_pr_1_99_nre": 29,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 629
  },
  {
    "ID": 148,
    "KG Winter_theta_nre": -1.372048422,
    "KG Winter_pr_1_99_nre": 30,
    "KG Winter_ss_nre": 92,
    "KG Winter_dss_nre": 363,
    "KG Spring_theta_nre": -1.137405743,
    "KG Spring_pr_1_99_nre": 30,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 386,
    "G1 Fall_theta_nre": -0.87611234,
    "G1 Fall_pr_1_99_nre": 30,
    "G1 Fall_ss_nre": 91,
    "G1 Fall_dss_nre": 412,
    "G1 Winter_theta_nre": -0.57334777,
    "G1 Winter_pr_1_99_nre": 30,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 443,
    "G1 Spring_theta_nre": -0.309004265,
    "G1 Spring_pr_1_99_nre": 30,
    "G1 Spring_ss_nre": 91,
    "G1 Spring_dss_nre": 469,
    "G2 Fall_theta_nre": -0.113404526,
    "G2 Fall_pr_1_99_nre": 30,
    "G2 Fall_ss_nre": 91,
    "G2 Fall_dss_nre": 489,
    "G2 Winter_theta_nre": 0.225812374,
    "G2 Winter_pr_1_99_nre": 30,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 523,
    "G2 Spring_theta_nre": 0.464582246,
    "G2 Spring_pr_1_99_nre": 30,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 546,
    "G3 Fall_theta_nre": 0.714964006,
    "G3 Fall_pr_1_99_nre": 30,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 571,
    "G3 Winter_theta_nre": 1.028806675,
    "G3 Winter_pr_1_99_nre": 30,
    "G3 Winter_ss_nre": 92,
    "G3 Winter_dss_nre": 603,
    "G3 Spring_theta_nre": 1.298580439,
    "G3 Spring_pr_1_99_nre": 30,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 630
  },
  {
    "ID": 149,
    "KG Winter_theta_nre": -1.342942596,
    "KG Winter_pr_1_99_nre": 30,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 366,
    "KG Spring_theta_nre": -1.134240932,
    "KG Spring_pr_1_99_nre": 30,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 387,
    "G1 Fall_theta_nre": -0.867564178,
    "G1 Fall_pr_1_99_nre": 30,
    "G1 Fall_ss_nre": 92,
    "G1 Fall_dss_nre": 413,
    "G1 Winter_theta_nre": -0.571690682,
    "G1 Winter_pr_1_99_nre": 30,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 443,
    "G1 Spring_theta_nre": -0.307487914,
    "G1 Spring_pr_1_99_nre": 30,
    "G1 Spring_ss_nre": 91,
    "G1 Spring_dss_nre": 469,
    "G2 Fall_theta_nre": -0.110140317,
    "G2 Fall_pr_1_99_nre": 30,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 489,
    "G2 Winter_theta_nre": 0.230563037,
    "G2 Winter_pr_1_99_nre": 30,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 523,
    "G2 Spring_theta_nre": 0.465553493,
    "G2 Spring_pr_1_99_nre": 30,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 547,
    "G3 Fall_theta_nre": 0.715019058,
    "G3 Fall_pr_1_99_nre": 30,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 572,
    "G3 Winter_theta_nre": 1.030495764,
    "G3 Winter_pr_1_99_nre": 30,
    "G3 Winter_ss_nre": 92,
    "G3 Winter_dss_nre": 603,
    "G3 Spring_theta_nre": 1.299402416,
    "G3 Spring_pr_1_99_nre": 30,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 630
  },
  {
    "ID": 150,
    "KG Winter_theta_nre": -1.339684477,
    "KG Winter_pr_1_99_nre": 30,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 366,
    "KG Spring_theta_nre": -1.129950124,
    "KG Spring_pr_1_99_nre": 30,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 387,
    "G1 Fall_theta_nre": -0.861978251,
    "G1 Fall_pr_1_99_nre": 30,
    "G1 Fall_ss_nre": 92,
    "G1 Fall_dss_nre": 414,
    "G1 Winter_theta_nre": -0.571149386,
    "G1 Winter_pr_1_99_nre": 30,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 443,
    "G1 Spring_theta_nre": -0.298613145,
    "G1 Spring_pr_1_99_nre": 30,
    "G1 Spring_ss_nre": 91,
    "G1 Spring_dss_nre": 470,
    "G2 Fall_theta_nre": -0.100618837,
    "G2 Fall_pr_1_99_nre": 30,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 490,
    "G2 Winter_theta_nre": 0.239653422,
    "G2 Winter_pr_1_99_nre": 30,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 524,
    "G2 Spring_theta_nre": 0.468084432,
    "G2 Spring_pr_1_99_nre": 30,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 547,
    "G3 Fall_theta_nre": 0.716214159,
    "G3 Fall_pr_1_99_nre": 30,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 572,
    "G3 Winter_theta_nre": 1.040212171,
    "G3 Winter_pr_1_99_nre": 30,
    "G3 Winter_ss_nre": 92,
    "G3 Winter_dss_nre": 604,
    "G3 Spring_theta_nre": 1.30101678,
    "G3 Spring_pr_1_99_nre": 30,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 630
  },
  {
    "ID": 151,
    "KG Winter_theta_nre": -1.337525995,
    "KG Winter_pr_1_99_nre": 30,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 366,
    "KG Spring_theta_nre": -1.129101969,
    "KG Spring_pr_1_99_nre": 30,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 387,
    "G1 Fall_theta_nre": -0.848754205,
    "G1 Fall_pr_1_99_nre": 30,
    "G1 Fall_ss_nre": 92,
    "G1 Fall_dss_nre": 415,
    "G1 Winter_theta_nre": -0.569184821,
    "G1 Winter_pr_1_99_nre": 30,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 443,
    "G1 Spring_theta_nre": -0.29508026,
    "G1 Spring_pr_1_99_nre": 30,
    "G1 Spring_ss_nre": 91,
    "G1 Spring_dss_nre": 470,
    "G2 Fall_theta_nre": -0.099654575,
    "G2 Fall_pr_1_99_nre": 30,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 490,
    "G2 Winter_theta_nre": 0.241510202,
    "G2 Winter_pr_1_99_nre": 30,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 524,
    "G2 Spring_theta_nre": 0.46954786,
    "G2 Spring_pr_1_99_nre": 30,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 547,
    "G3 Fall_theta_nre": 0.716534848,
    "G3 Fall_pr_1_99_nre": 30,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 572,
    "G3 Winter_theta_nre": 1.065376709,
    "G3 Winter_pr_1_99_nre": 30,
    "G3 Winter_ss_nre": 93,
    "G3 Winter_dss_nre": 607,
    "G3 Spring_theta_nre": 1.306270642,
    "G3 Spring_pr_1_99_nre": 30,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 631
  },
  {
    "ID": 152,
    "KG Winter_theta_nre": -1.333997514,
    "KG Winter_pr_1_99_nre": 30,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 367,
    "KG Spring_theta_nre": -1.128529949,
    "KG Spring_pr_1_99_nre": 30,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 387,
    "G1 Fall_theta_nre": -0.845288684,
    "G1 Fall_pr_1_99_nre": 30,
    "G1 Fall_ss_nre": 92,
    "G1 Fall_dss_nre": 415,
    "G1 Winter_theta_nre": -0.566750727,
    "G1 Winter_pr_1_99_nre": 30,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 443,
    "G1 Spring_theta_nre": -0.294094104,
    "G1 Spring_pr_1_99_nre": 30,
    "G1 Spring_ss_nre": 91,
    "G1 Spring_dss_nre": 471,
    "G2 Fall_theta_nre": -0.096657281,
    "G2 Fall_pr_1_99_nre": 30,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 490,
    "G2 Winter_theta_nre": 0.241703375,
    "G2 Winter_pr_1_99_nre": 30,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 524,
    "G2 Spring_theta_nre": 0.476487954,
    "G2 Spring_pr_1_99_nre": 30,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 548,
    "G3 Fall_theta_nre": 0.720006025,
    "G3 Fall_pr_1_99_nre": 30,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 572,
    "G3 Winter_theta_nre": 1.079697105,
    "G3 Winter_pr_1_99_nre": 30,
    "G3 Winter_ss_nre": 93,
    "G3 Winter_dss_nre": 608,
    "G3 Spring_theta_nre": 1.309498239,
    "G3 Spring_pr_1_99_nre": 30,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 631
  },
  {
    "ID": 153,
    "KG Winter_theta_nre": -1.326609149,
    "KG Winter_pr_1_99_nre": 31,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 367,
    "KG Spring_theta_nre": -1.1248209,
    "KG Spring_pr_1_99_nre": 31,
    "KG Spring_ss_nre": 91,
    "KG Spring_dss_nre": 388,
    "G1 Fall_theta_nre": -0.841216448,
    "G1 Fall_pr_1_99_nre": 31,
    "G1 Fall_ss_nre": 92,
    "G1 Fall_dss_nre": 416,
    "G1 Winter_theta_nre": -0.563026787,
    "G1 Winter_pr_1_99_nre": 31,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 444,
    "G1 Spring_theta_nre": -0.290351419,
    "G1 Spring_pr_1_99_nre": 31,
    "G1 Spring_ss_nre": 91,
    "G1 Spring_dss_nre": 471,
    "G2 Fall_theta_nre": -0.081451247,
    "G2 Fall_pr_1_99_nre": 31,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 492,
    "G2 Winter_theta_nre": 0.253320823,
    "G2 Winter_pr_1_99_nre": 31,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 525,
    "G2 Spring_theta_nre": 0.477523739,
    "G2 Spring_pr_1_99_nre": 31,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 548,
    "G3 Fall_theta_nre": 0.734554405,
    "G3 Fall_pr_1_99_nre": 31,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 573,
    "G3 Winter_theta_nre": 1.08188925,
    "G3 Winter_pr_1_99_nre": 31,
    "G3 Winter_ss_nre": 93,
    "G3 Winter_dss_nre": 608,
    "G3 Spring_theta_nre": 1.311219826,
    "G3 Spring_pr_1_99_nre": 31,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 631
  },
  {
    "ID": 154,
    "KG Winter_theta_nre": -1.324765043,
    "KG Winter_pr_1_99_nre": 31,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 368,
    "KG Spring_theta_nre": -1.091945102,
    "KG Spring_pr_1_99_nre": 31,
    "KG Spring_ss_nre": 92,
    "KG Spring_dss_nre": 391,
    "G1 Fall_theta_nre": -0.836814757,
    "G1 Fall_pr_1_99_nre": 31,
    "G1 Fall_ss_nre": 92,
    "G1 Fall_dss_nre": 416,
    "G1 Winter_theta_nre": -0.562709497,
    "G1 Winter_pr_1_99_nre": 31,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 444,
    "G1 Spring_theta_nre": -0.275061089,
    "G1 Spring_pr_1_99_nre": 31,
    "G1 Spring_ss_nre": 92,
    "G1 Spring_dss_nre": 472,
    "G2 Fall_theta_nre": -0.0789212,
    "G2 Fall_pr_1_99_nre": 31,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 492,
    "G2 Winter_theta_nre": 0.257178926,
    "G2 Winter_pr_1_99_nre": 31,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 526,
    "G2 Spring_theta_nre": 0.480986722,
    "G2 Spring_pr_1_99_nre": 31,
    "G2 Spring_ss_nre": 92,
    "G2 Spring_dss_nre": 548,
    "G3 Fall_theta_nre": 0.735408323,
    "G3 Fall_pr_1_99_nre": 31,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 574,
    "G3 Winter_theta_nre": 1.08550262,
    "G3 Winter_pr_1_99_nre": 31,
    "G3 Winter_ss_nre": 93,
    "G3 Winter_dss_nre": 609,
    "G3 Spring_theta_nre": 1.318247444,
    "G3 Spring_pr_1_99_nre": 31,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 632
  },
  {
    "ID": 155,
    "KG Winter_theta_nre": -1.319425242,
    "KG Winter_pr_1_99_nre": 31,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 368,
    "KG Spring_theta_nre": -1.076904588,
    "KG Spring_pr_1_99_nre": 31,
    "KG Spring_ss_nre": 92,
    "KG Spring_dss_nre": 392,
    "G1 Fall_theta_nre": -0.831954296,
    "G1 Fall_pr_1_99_nre": 31,
    "G1 Fall_ss_nre": 92,
    "G1 Fall_dss_nre": 417,
    "G1 Winter_theta_nre": -0.55727763,
    "G1 Winter_pr_1_99_nre": 31,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 444,
    "G1 Spring_theta_nre": -0.274963424,
    "G1 Spring_pr_1_99_nre": 31,
    "G1 Spring_ss_nre": 92,
    "G1 Spring_dss_nre": 473,
    "G2 Fall_theta_nre": -0.076002133,
    "G2 Fall_pr_1_99_nre": 31,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 492,
    "G2 Winter_theta_nre": 0.257256954,
    "G2 Winter_pr_1_99_nre": 31,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 526,
    "G2 Spring_theta_nre": 0.489025914,
    "G2 Spring_pr_1_99_nre": 31,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 549,
    "G3 Fall_theta_nre": 0.738710847,
    "G3 Fall_pr_1_99_nre": 31,
    "G3 Fall_ss_nre": 92,
    "G3 Fall_dss_nre": 574,
    "G3 Winter_theta_nre": 1.104389522,
    "G3 Winter_pr_1_99_nre": 31,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 610,
    "G3 Spring_theta_nre": 1.320882121,
    "G3 Spring_pr_1_99_nre": 31,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 632
  },
  {
    "ID": 156,
    "KG Winter_theta_nre": -1.31074224,
    "KG Winter_pr_1_99_nre": 31,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 369,
    "KG Spring_theta_nre": -1.073855462,
    "KG Spring_pr_1_99_nre": 31,
    "KG Spring_ss_nre": 92,
    "KG Spring_dss_nre": 393,
    "G1 Fall_theta_nre": -0.808756625,
    "G1 Fall_pr_1_99_nre": 31,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 419,
    "G1 Winter_theta_nre": -0.544635489,
    "G1 Winter_pr_1_99_nre": 31,
    "G1 Winter_ss_nre": 92,
    "G1 Winter_dss_nre": 446,
    "G1 Spring_theta_nre": -0.268439875,
    "G1 Spring_pr_1_99_nre": 31,
    "G1 Spring_ss_nre": 92,
    "G1 Spring_dss_nre": 473,
    "G2 Fall_theta_nre": -0.071299019,
    "G2 Fall_pr_1_99_nre": 31,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 493,
    "G2 Winter_theta_nre": 0.260891246,
    "G2 Winter_pr_1_99_nre": 31,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 526,
    "G2 Spring_theta_nre": 0.490782149,
    "G2 Spring_pr_1_99_nre": 31,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 549,
    "G3 Fall_theta_nre": 0.759805122,
    "G3 Fall_pr_1_99_nre": 31,
    "G3 Fall_ss_nre": 93,
    "G3 Fall_dss_nre": 576,
    "G3 Winter_theta_nre": 1.106559439,
    "G3 Winter_pr_1_99_nre": 31,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 611,
    "G3 Spring_theta_nre": 1.320975748,
    "G3 Spring_pr_1_99_nre": 31,
    "G3 Spring_ss_nre": 91,
    "G3 Spring_dss_nre": 632
  },
  {
    "ID": 157,
    "KG Winter_theta_nre": -1.304392065,
    "KG Winter_pr_1_99_nre": 31,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 370,
    "KG Spring_theta_nre": -1.072329916,
    "KG Spring_pr_1_99_nre": 31,
    "KG Spring_ss_nre": 92,
    "KG Spring_dss_nre": 393,
    "G1 Fall_theta_nre": -0.791558429,
    "G1 Fall_pr_1_99_nre": 31,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 421,
    "G1 Winter_theta_nre": -0.53299854,
    "G1 Winter_pr_1_99_nre": 31,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 447,
    "G1 Spring_theta_nre": -0.26122155,
    "G1 Spring_pr_1_99_nre": 31,
    "G1 Spring_ss_nre": 92,
    "G1 Spring_dss_nre": 474,
    "G2 Fall_theta_nre": -0.068382784,
    "G2 Fall_pr_1_99_nre": 31,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 493,
    "G2 Winter_theta_nre": 0.265131759,
    "G2 Winter_pr_1_99_nre": 31,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 527,
    "G2 Spring_theta_nre": 0.500851216,
    "G2 Spring_pr_1_99_nre": 31,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 550,
    "G3 Fall_theta_nre": 0.768376662,
    "G3 Fall_pr_1_99_nre": 31,
    "G3 Fall_ss_nre": 93,
    "G3 Fall_dss_nre": 577,
    "G3 Winter_theta_nre": 1.111937655,
    "G3 Winter_pr_1_99_nre": 31,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 611,
    "G3 Spring_theta_nre": 1.329692876,
    "G3 Spring_pr_1_99_nre": 31,
    "G3 Spring_ss_nre": 92,
    "G3 Spring_dss_nre": 633
  },
  {
    "ID": 158,
    "KG Winter_theta_nre": -1.304385212,
    "KG Winter_pr_1_99_nre": 32,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 370,
    "KG Spring_theta_nre": -1.071985575,
    "KG Spring_pr_1_99_nre": 32,
    "KG Spring_ss_nre": 92,
    "KG Spring_dss_nre": 393,
    "G1 Fall_theta_nre": -0.791456633,
    "G1 Fall_pr_1_99_nre": 32,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 421,
    "G1 Winter_theta_nre": -0.530072057,
    "G1 Winter_pr_1_99_nre": 32,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 447,
    "G1 Spring_theta_nre": -0.244917952,
    "G1 Spring_pr_1_99_nre": 32,
    "G1 Spring_ss_nre": 92,
    "G1 Spring_dss_nre": 476,
    "G2 Fall_theta_nre": -0.066701614,
    "G2 Fall_pr_1_99_nre": 32,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 493,
    "G2 Winter_theta_nre": 0.267983293,
    "G2 Winter_pr_1_99_nre": 32,
    "G2 Winter_ss_nre": 92,
    "G2 Winter_dss_nre": 527,
    "G2 Spring_theta_nre": 0.509469849,
    "G2 Spring_pr_1_99_nre": 32,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 551,
    "G3 Fall_theta_nre": 0.771029282,
    "G3 Fall_pr_1_99_nre": 32,
    "G3 Fall_ss_nre": 93,
    "G3 Fall_dss_nre": 577,
    "G3 Winter_theta_nre": 1.112901995,
    "G3 Winter_pr_1_99_nre": 32,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 611,
    "G3 Spring_theta_nre": 1.329907677,
    "G3 Spring_pr_1_99_nre": 32,
    "G3 Spring_ss_nre": 92,
    "G3 Spring_dss_nre": 633
  },
  {
    "ID": 159,
    "KG Winter_theta_nre": -1.304117404,
    "KG Winter_pr_1_99_nre": 32,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 370,
    "KG Spring_theta_nre": -1.070458667,
    "KG Spring_pr_1_99_nre": 32,
    "KG Spring_ss_nre": 92,
    "KG Spring_dss_nre": 393,
    "G1 Fall_theta_nre": -0.791118712,
    "G1 Fall_pr_1_99_nre": 32,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 421,
    "G1 Winter_theta_nre": -0.517404334,
    "G1 Winter_pr_1_99_nre": 32,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 448,
    "G1 Spring_theta_nre": -0.242597259,
    "G1 Spring_pr_1_99_nre": 32,
    "G1 Spring_ss_nre": 92,
    "G1 Spring_dss_nre": 476,
    "G2 Fall_theta_nre": -0.058050565,
    "G2 Fall_pr_1_99_nre": 32,
    "G2 Fall_ss_nre": 92,
    "G2 Fall_dss_nre": 494,
    "G2 Winter_theta_nre": 0.271615281,
    "G2 Winter_pr_1_99_nre": 32,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 527,
    "G2 Spring_theta_nre": 0.511966884,
    "G2 Spring_pr_1_99_nre": 32,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 551,
    "G3 Fall_theta_nre": 0.771756811,
    "G3 Fall_pr_1_99_nre": 32,
    "G3 Fall_ss_nre": 93,
    "G3 Fall_dss_nre": 577,
    "G3 Winter_theta_nre": 1.114077239,
    "G3 Winter_pr_1_99_nre": 32,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 611,
    "G3 Spring_theta_nre": 1.330485959,
    "G3 Spring_pr_1_99_nre": 32,
    "G3 Spring_ss_nre": 92,
    "G3 Spring_dss_nre": 633
  },
  {
    "ID": 160,
    "KG Winter_theta_nre": -1.302995471,
    "KG Winter_pr_1_99_nre": 32,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 370,
    "KG Spring_theta_nre": -1.06940796,
    "KG Spring_pr_1_99_nre": 32,
    "KG Spring_ss_nre": 92,
    "KG Spring_dss_nre": 393,
    "G1 Fall_theta_nre": -0.787468761,
    "G1 Fall_pr_1_99_nre": 32,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 421,
    "G1 Winter_theta_nre": -0.515805168,
    "G1 Winter_pr_1_99_nre": 32,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 448,
    "G1 Spring_theta_nre": -0.242353891,
    "G1 Spring_pr_1_99_nre": 32,
    "G1 Spring_ss_nre": 92,
    "G1 Spring_dss_nre": 476,
    "G2 Fall_theta_nre": -0.051940304,
    "G2 Fall_pr_1_99_nre": 32,
    "G2 Fall_ss_nre": 93,
    "G2 Fall_dss_nre": 495,
    "G2 Winter_theta_nre": 0.272951983,
    "G2 Winter_pr_1_99_nre": 32,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 527,
    "G2 Spring_theta_nre": 0.519704595,
    "G2 Spring_pr_1_99_nre": 32,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 552,
    "G3 Fall_theta_nre": 0.771967979,
    "G3 Fall_pr_1_99_nre": 32,
    "G3 Fall_ss_nre": 93,
    "G3 Fall_dss_nre": 577,
    "G3 Winter_theta_nre": 1.116313698,
    "G3 Winter_pr_1_99_nre": 32,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 612,
    "G3 Spring_theta_nre": 1.333459883,
    "G3 Spring_pr_1_99_nre": 32,
    "G3 Spring_ss_nre": 92,
    "G3 Spring_dss_nre": 633
  },
  {
    "ID": 161,
    "KG Winter_theta_nre": -1.300856694,
    "KG Winter_pr_1_99_nre": 32,
    "KG Winter_ss_nre": 93,
    "KG Winter_dss_nre": 370,
    "KG Spring_theta_nre": -1.051726501,
    "KG Spring_pr_1_99_nre": 32,
    "KG Spring_ss_nre": 93,
    "KG Spring_dss_nre": 395,
    "G1 Fall_theta_nre": -0.78304118,
    "G1 Fall_pr_1_99_nre": 32,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 422,
    "G1 Winter_theta_nre": -0.509305042,
    "G1 Winter_pr_1_99_nre": 32,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 449,
    "G1 Spring_theta_nre": -0.231629822,
    "G1 Spring_pr_1_99_nre": 32,
    "G1 Spring_ss_nre": 92,
    "G1 Spring_dss_nre": 477,
    "G2 Fall_theta_nre": -0.045408894,
    "G2 Fall_pr_1_99_nre": 32,
    "G2 Fall_ss_nre": 93,
    "G2 Fall_dss_nre": 495,
    "G2 Winter_theta_nre": 0.273719079,
    "G2 Winter_pr_1_99_nre": 32,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 527,
    "G2 Spring_theta_nre": 0.520004512,
    "G2 Spring_pr_1_99_nre": 32,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 552,
    "G3 Fall_theta_nre": 0.774284813,
    "G3 Fall_pr_1_99_nre": 32,
    "G3 Fall_ss_nre": 93,
    "G3 Fall_dss_nre": 577,
    "G3 Winter_theta_nre": 1.117818352,
    "G3 Winter_pr_1_99_nre": 32,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 612,
    "G3 Spring_theta_nre": 1.334347698,
    "G3 Spring_pr_1_99_nre": 32,
    "G3 Spring_ss_nre": 92,
    "G3 Spring_dss_nre": 633
  },
  {
    "ID": 162,
    "KG Winter_theta_nre": -1.288258045,
    "KG Winter_pr_1_99_nre": 32,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 371,
    "KG Spring_theta_nre": -1.045293059,
    "KG Spring_pr_1_99_nre": 32,
    "KG Spring_ss_nre": 93,
    "KG Spring_dss_nre": 395,
    "G1 Fall_theta_nre": -0.778306513,
    "G1 Fall_pr_1_99_nre": 32,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 422,
    "G1 Winter_theta_nre": -0.50550786,
    "G1 Winter_pr_1_99_nre": 32,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 449,
    "G1 Spring_theta_nre": -0.22748623,
    "G1 Spring_pr_1_99_nre": 32,
    "G1 Spring_ss_nre": 92,
    "G1 Spring_dss_nre": 477,
    "G2 Fall_theta_nre": -0.044485116,
    "G2 Fall_pr_1_99_nre": 32,
    "G2 Fall_ss_nre": 93,
    "G2 Fall_dss_nre": 496,
    "G2 Winter_theta_nre": 0.284566926,
    "G2 Winter_pr_1_99_nre": 32,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 528,
    "G2 Spring_theta_nre": 0.528954568,
    "G2 Spring_pr_1_99_nre": 32,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 553,
    "G3 Fall_theta_nre": 0.775492204,
    "G3 Fall_pr_1_99_nre": 32,
    "G3 Fall_ss_nre": 93,
    "G3 Fall_dss_nre": 578,
    "G3 Winter_theta_nre": 1.120277683,
    "G3 Winter_pr_1_99_nre": 32,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 612,
    "G3 Spring_theta_nre": 1.34408938,
    "G3 Spring_pr_1_99_nre": 32,
    "G3 Spring_ss_nre": 92,
    "G3 Spring_dss_nre": 634
  },
  {
    "ID": 163,
    "KG Winter_theta_nre": -1.286238688,
    "KG Winter_pr_1_99_nre": 33,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 371,
    "KG Spring_theta_nre": -1.036186679,
    "KG Spring_pr_1_99_nre": 33,
    "KG Spring_ss_nre": 93,
    "KG Spring_dss_nre": 396,
    "G1 Fall_theta_nre": -0.776214595,
    "G1 Fall_pr_1_99_nre": 33,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 422,
    "G1 Winter_theta_nre": -0.503036417,
    "G1 Winter_pr_1_99_nre": 33,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 450,
    "G1 Spring_theta_nre": -0.215795205,
    "G1 Spring_pr_1_99_nre": 33,
    "G1 Spring_ss_nre": 93,
    "G1 Spring_dss_nre": 478,
    "G2 Fall_theta_nre": -0.041312175,
    "G2 Fall_pr_1_99_nre": 33,
    "G2 Fall_ss_nre": 93,
    "G2 Fall_dss_nre": 496,
    "G2 Winter_theta_nre": 0.291326591,
    "G2 Winter_pr_1_99_nre": 33,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 529,
    "G2 Spring_theta_nre": 0.532917972,
    "G2 Spring_pr_1_99_nre": 33,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 553,
    "G3 Fall_theta_nre": 0.785322851,
    "G3 Fall_pr_1_99_nre": 33,
    "G3 Fall_ss_nre": 93,
    "G3 Fall_dss_nre": 579,
    "G3 Winter_theta_nre": 1.12956054,
    "G3 Winter_pr_1_99_nre": 33,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 613,
    "G3 Spring_theta_nre": 1.345115313,
    "G3 Spring_pr_1_99_nre": 33,
    "G3 Spring_ss_nre": 92,
    "G3 Spring_dss_nre": 635
  },
  {
    "ID": 164,
    "KG Winter_theta_nre": -1.28034731,
    "KG Winter_pr_1_99_nre": 33,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 372,
    "KG Spring_theta_nre": -1.033770472,
    "KG Spring_pr_1_99_nre": 33,
    "KG Spring_ss_nre": 93,
    "KG Spring_dss_nre": 397,
    "G1 Fall_theta_nre": -0.77385223,
    "G1 Fall_pr_1_99_nre": 33,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 423,
    "G1 Winter_theta_nre": -0.499151405,
    "G1 Winter_pr_1_99_nre": 33,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 450,
    "G1 Spring_theta_nre": -0.215597771,
    "G1 Spring_pr_1_99_nre": 33,
    "G1 Spring_ss_nre": 93,
    "G1 Spring_dss_nre": 478,
    "G2 Fall_theta_nre": -0.027142436,
    "G2 Fall_pr_1_99_nre": 33,
    "G2 Fall_ss_nre": 93,
    "G2 Fall_dss_nre": 497,
    "G2 Winter_theta_nre": 0.291651345,
    "G2 Winter_pr_1_99_nre": 33,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 529,
    "G2 Spring_theta_nre": 0.536805506,
    "G2 Spring_pr_1_99_nre": 33,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 554,
    "G3 Fall_theta_nre": 0.803789064,
    "G3 Fall_pr_1_99_nre": 33,
    "G3 Fall_ss_nre": 93,
    "G3 Fall_dss_nre": 580,
    "G3 Winter_theta_nre": 1.134284305,
    "G3 Winter_pr_1_99_nre": 33,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 613,
    "G3 Spring_theta_nre": 1.360348123,
    "G3 Spring_pr_1_99_nre": 33,
    "G3 Spring_ss_nre": 92,
    "G3 Spring_dss_nre": 636
  },
  {
    "ID": 165,
    "KG Winter_theta_nre": -1.280139016,
    "KG Winter_pr_1_99_nre": 33,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 372,
    "KG Spring_theta_nre": -1.02522734,
    "KG Spring_pr_1_99_nre": 33,
    "KG Spring_ss_nre": 93,
    "KG Spring_dss_nre": 397,
    "G1 Fall_theta_nre": -0.771843209,
    "G1 Fall_pr_1_99_nre": 33,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 423,
    "G1 Winter_theta_nre": -0.497753575,
    "G1 Winter_pr_1_99_nre": 33,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 450,
    "G1 Spring_theta_nre": -0.213110953,
    "G1 Spring_pr_1_99_nre": 33,
    "G1 Spring_ss_nre": 93,
    "G1 Spring_dss_nre": 479,
    "G2 Fall_theta_nre": -0.019481376,
    "G2 Fall_pr_1_99_nre": 33,
    "G2 Fall_ss_nre": 93,
    "G2 Fall_dss_nre": 498,
    "G2 Winter_theta_nre": 0.294137585,
    "G2 Winter_pr_1_99_nre": 33,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 529,
    "G2 Spring_theta_nre": 0.538550806,
    "G2 Spring_pr_1_99_nre": 33,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 554,
    "G3 Fall_theta_nre": 0.805114444,
    "G3 Fall_pr_1_99_nre": 33,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 581,
    "G3 Winter_theta_nre": 1.139404829,
    "G3 Winter_pr_1_99_nre": 33,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 614,
    "G3 Spring_theta_nre": 1.365736232,
    "G3 Spring_pr_1_99_nre": 33,
    "G3 Spring_ss_nre": 92,
    "G3 Spring_dss_nre": 637
  },
  {
    "ID": 166,
    "KG Winter_theta_nre": -1.276784884,
    "KG Winter_pr_1_99_nre": 33,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 372,
    "KG Spring_theta_nre": -1.021135842,
    "KG Spring_pr_1_99_nre": 33,
    "KG Spring_ss_nre": 93,
    "KG Spring_dss_nre": 398,
    "G1 Fall_theta_nre": -0.771185495,
    "G1 Fall_pr_1_99_nre": 33,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 423,
    "G1 Winter_theta_nre": -0.490970287,
    "G1 Winter_pr_1_99_nre": 33,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 451,
    "G1 Spring_theta_nre": -0.211527807,
    "G1 Spring_pr_1_99_nre": 33,
    "G1 Spring_ss_nre": 93,
    "G1 Spring_dss_nre": 479,
    "G2 Fall_theta_nre": -0.012331071,
    "G2 Fall_pr_1_99_nre": 33,
    "G2 Fall_ss_nre": 93,
    "G2 Fall_dss_nre": 499,
    "G2 Winter_theta_nre": 0.295621655,
    "G2 Winter_pr_1_99_nre": 33,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 530,
    "G2 Spring_theta_nre": 0.540180064,
    "G2 Spring_pr_1_99_nre": 33,
    "G2 Spring_ss_nre": 93,
    "G2 Spring_dss_nre": 554,
    "G3 Fall_theta_nre": 0.806149956,
    "G3 Fall_pr_1_99_nre": 33,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 581,
    "G3 Winter_theta_nre": 1.143115062,
    "G3 Winter_pr_1_99_nre": 33,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 614,
    "G3 Spring_theta_nre": 1.365865191,
    "G3 Spring_pr_1_99_nre": 33,
    "G3 Spring_ss_nre": 92,
    "G3 Spring_dss_nre": 637
  },
  {
    "ID": 167,
    "KG Winter_theta_nre": -1.274554292,
    "KG Winter_pr_1_99_nre": 33,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 373,
    "KG Spring_theta_nre": -1.018565963,
    "KG Spring_pr_1_99_nre": 33,
    "KG Spring_ss_nre": 93,
    "KG Spring_dss_nre": 398,
    "G1 Fall_theta_nre": -0.766409934,
    "G1 Fall_pr_1_99_nre": 33,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 423,
    "G1 Winter_theta_nre": -0.488398086,
    "G1 Winter_pr_1_99_nre": 33,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 451,
    "G1 Spring_theta_nre": -0.210253706,
    "G1 Spring_pr_1_99_nre": 33,
    "G1 Spring_ss_nre": 93,
    "G1 Spring_dss_nre": 479,
    "G2 Fall_theta_nre": -0.012004477,
    "G2 Fall_pr_1_99_nre": 33,
    "G2 Fall_ss_nre": 93,
    "G2 Fall_dss_nre": 499,
    "G2 Winter_theta_nre": 0.314472077,
    "G2 Winter_pr_1_99_nre": 33,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 531,
    "G2 Spring_theta_nre": 0.54471743,
    "G2 Spring_pr_1_99_nre": 33,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 554,
    "G3 Fall_theta_nre": 0.806272247,
    "G3 Fall_pr_1_99_nre": 33,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 581,
    "G3 Winter_theta_nre": 1.14499824,
    "G3 Winter_pr_1_99_nre": 33,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 614,
    "G3 Spring_theta_nre": 1.372665095,
    "G3 Spring_pr_1_99_nre": 33,
    "G3 Spring_ss_nre": 93,
    "G3 Spring_dss_nre": 637
  },
  {
    "ID": 168,
    "KG Winter_theta_nre": -1.27179478,
    "KG Winter_pr_1_99_nre": 34,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 373,
    "KG Spring_theta_nre": -1.016547636,
    "KG Spring_pr_1_99_nre": 34,
    "KG Spring_ss_nre": 93,
    "KG Spring_dss_nre": 398,
    "G1 Fall_theta_nre": -0.759074742,
    "G1 Fall_pr_1_99_nre": 34,
    "G1 Fall_ss_nre": 93,
    "G1 Fall_dss_nre": 424,
    "G1 Winter_theta_nre": -0.483634088,
    "G1 Winter_pr_1_99_nre": 34,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 452,
    "G1 Spring_theta_nre": -0.209620534,
    "G1 Spring_pr_1_99_nre": 34,
    "G1 Spring_ss_nre": 93,
    "G1 Spring_dss_nre": 479,
    "G2 Fall_theta_nre": 0.005075082,
    "G2 Fall_pr_1_99_nre": 34,
    "G2 Fall_ss_nre": 93,
    "G2 Fall_dss_nre": 501,
    "G2 Winter_theta_nre": 0.31523072,
    "G2 Winter_pr_1_99_nre": 34,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 532,
    "G2 Spring_theta_nre": 0.549913312,
    "G2 Spring_pr_1_99_nre": 34,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 555,
    "G3 Fall_theta_nre": 0.809527333,
    "G3 Fall_pr_1_99_nre": 34,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 581,
    "G3 Winter_theta_nre": 1.146696452,
    "G3 Winter_pr_1_99_nre": 34,
    "G3 Winter_ss_nre": 94,
    "G3 Winter_dss_nre": 615,
    "G3 Spring_theta_nre": 1.37886087,
    "G3 Spring_pr_1_99_nre": 34,
    "G3 Spring_ss_nre": 93,
    "G3 Spring_dss_nre": 638
  },
  {
    "ID": 169,
    "KG Winter_theta_nre": -1.2699731,
    "KG Winter_pr_1_99_nre": 34,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 373,
    "KG Spring_theta_nre": -1.016355052,
    "KG Spring_pr_1_99_nre": 34,
    "KG Spring_ss_nre": 93,
    "KG Spring_dss_nre": 398,
    "G1 Fall_theta_nre": -0.756101059,
    "G1 Fall_pr_1_99_nre": 34,
    "G1 Fall_ss_nre": 94,
    "G1 Fall_dss_nre": 424,
    "G1 Winter_theta_nre": -0.47948233,
    "G1 Winter_pr_1_99_nre": 34,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 452,
    "G1 Spring_theta_nre": -0.205984307,
    "G1 Spring_pr_1_99_nre": 34,
    "G1 Spring_ss_nre": 93,
    "G1 Spring_dss_nre": 479,
    "G2 Fall_theta_nre": 0.006401258,
    "G2 Fall_pr_1_99_nre": 34,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 501,
    "G2 Winter_theta_nre": 0.316297147,
    "G2 Winter_pr_1_99_nre": 34,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 532,
    "G2 Spring_theta_nre": 0.552339592,
    "G2 Spring_pr_1_99_nre": 34,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 555,
    "G3 Fall_theta_nre": 0.81814084,
    "G3 Fall_pr_1_99_nre": 34,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 582,
    "G3 Winter_theta_nre": 1.152034294,
    "G3 Winter_pr_1_99_nre": 34,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 615,
    "G3 Spring_theta_nre": 1.387221483,
    "G3 Spring_pr_1_99_nre": 34,
    "G3 Spring_ss_nre": 93,
    "G3 Spring_dss_nre": 639
  },
  {
    "ID": 170,
    "KG Winter_theta_nre": -1.267710714,
    "KG Winter_pr_1_99_nre": 34,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 373,
    "KG Spring_theta_nre": -1.012234201,
    "KG Spring_pr_1_99_nre": 34,
    "KG Spring_ss_nre": 93,
    "KG Spring_dss_nre": 399,
    "G1 Fall_theta_nre": -0.750691952,
    "G1 Fall_pr_1_99_nre": 34,
    "G1 Fall_ss_nre": 94,
    "G1 Fall_dss_nre": 425,
    "G1 Winter_theta_nre": -0.478883941,
    "G1 Winter_pr_1_99_nre": 34,
    "G1 Winter_ss_nre": 93,
    "G1 Winter_dss_nre": 452,
    "G1 Spring_theta_nre": -0.173510232,
    "G1 Spring_pr_1_99_nre": 34,
    "G1 Spring_ss_nre": 93,
    "G1 Spring_dss_nre": 483,
    "G2 Fall_theta_nre": 0.007067199,
    "G2 Fall_pr_1_99_nre": 34,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 501,
    "G2 Winter_theta_nre": 0.319473891,
    "G2 Winter_pr_1_99_nre": 34,
    "G2 Winter_ss_nre": 93,
    "G2 Winter_dss_nre": 532,
    "G2 Spring_theta_nre": 0.554174391,
    "G2 Spring_pr_1_99_nre": 34,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 555,
    "G3 Fall_theta_nre": 0.833510864,
    "G3 Fall_pr_1_99_nre": 34,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 583,
    "G3 Winter_theta_nre": 1.15377283,
    "G3 Winter_pr_1_99_nre": 34,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 615,
    "G3 Spring_theta_nre": 1.387589651,
    "G3 Spring_pr_1_99_nre": 34,
    "G3 Spring_ss_nre": 93,
    "G3 Spring_dss_nre": 639
  },
  {
    "ID": 171,
    "KG Winter_theta_nre": -1.267120457,
    "KG Winter_pr_1_99_nre": 34,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 373,
    "KG Spring_theta_nre": -0.999390732,
    "KG Spring_pr_1_99_nre": 34,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 400,
    "G1 Fall_theta_nre": -0.745251809,
    "G1 Fall_pr_1_99_nre": 34,
    "G1 Fall_ss_nre": 94,
    "G1 Fall_dss_nre": 425,
    "G1 Winter_theta_nre": -0.477353158,
    "G1 Winter_pr_1_99_nre": 34,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 452,
    "G1 Spring_theta_nre": -0.169422449,
    "G1 Spring_pr_1_99_nre": 34,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 483,
    "G2 Fall_theta_nre": 0.009926941,
    "G2 Fall_pr_1_99_nre": 34,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 501,
    "G2 Winter_theta_nre": 0.321770201,
    "G2 Winter_pr_1_99_nre": 34,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 532,
    "G2 Spring_theta_nre": 0.554500967,
    "G2 Spring_pr_1_99_nre": 34,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 555,
    "G3 Fall_theta_nre": 0.833656922,
    "G3 Fall_pr_1_99_nre": 34,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 583,
    "G3 Winter_theta_nre": 1.15464395,
    "G3 Winter_pr_1_99_nre": 34,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 615,
    "G3 Spring_theta_nre": 1.387822694,
    "G3 Spring_pr_1_99_nre": 34,
    "G3 Spring_ss_nre": 93,
    "G3 Spring_dss_nre": 639
  },
  {
    "ID": 172,
    "KG Winter_theta_nre": -1.26470381,
    "KG Winter_pr_1_99_nre": 34,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 374,
    "KG Spring_theta_nre": -0.993132965,
    "KG Spring_pr_1_99_nre": 34,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 401,
    "G1 Fall_theta_nre": -0.727591499,
    "G1 Fall_pr_1_99_nre": 34,
    "G1 Fall_ss_nre": 94,
    "G1 Fall_dss_nre": 427,
    "G1 Winter_theta_nre": -0.476208619,
    "G1 Winter_pr_1_99_nre": 34,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 452,
    "G1 Spring_theta_nre": -0.16747729,
    "G1 Spring_pr_1_99_nre": 34,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 483,
    "G2 Fall_theta_nre": 0.012619189,
    "G2 Fall_pr_1_99_nre": 34,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 501,
    "G2 Winter_theta_nre": 0.324117363,
    "G2 Winter_pr_1_99_nre": 34,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 532,
    "G2 Spring_theta_nre": 0.570431877,
    "G2 Spring_pr_1_99_nre": 34,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 557,
    "G3 Fall_theta_nre": 0.835452219,
    "G3 Fall_pr_1_99_nre": 34,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 584,
    "G3 Winter_theta_nre": 1.155315879,
    "G3 Winter_pr_1_99_nre": 34,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 616,
    "G3 Spring_theta_nre": 1.388511332,
    "G3 Spring_pr_1_99_nre": 34,
    "G3 Spring_ss_nre": 93,
    "G3 Spring_dss_nre": 639
  },
  {
    "ID": 173,
    "KG Winter_theta_nre": -1.263972293,
    "KG Winter_pr_1_99_nre": 35,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 374,
    "KG Spring_theta_nre": -0.990567508,
    "KG Spring_pr_1_99_nre": 35,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 401,
    "G1 Fall_theta_nre": -0.717483457,
    "G1 Fall_pr_1_99_nre": 35,
    "G1 Fall_ss_nre": 94,
    "G1 Fall_dss_nre": 428,
    "G1 Winter_theta_nre": -0.475719945,
    "G1 Winter_pr_1_99_nre": 35,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 452,
    "G1 Spring_theta_nre": -0.164819505,
    "G1 Spring_pr_1_99_nre": 35,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 484,
    "G2 Fall_theta_nre": 0.015053742,
    "G2 Fall_pr_1_99_nre": 35,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 502,
    "G2 Winter_theta_nre": 0.328173494,
    "G2 Winter_pr_1_99_nre": 35,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 533,
    "G2 Spring_theta_nre": 0.570641364,
    "G2 Spring_pr_1_99_nre": 35,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 557,
    "G3 Fall_theta_nre": 0.83570795,
    "G3 Fall_pr_1_99_nre": 35,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 584,
    "G3 Winter_theta_nre": 1.163738821,
    "G3 Winter_pr_1_99_nre": 35,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 616,
    "G3 Spring_theta_nre": 1.40237752,
    "G3 Spring_pr_1_99_nre": 35,
    "G3 Spring_ss_nre": 93,
    "G3 Spring_dss_nre": 640
  },
  {
    "ID": 174,
    "KG Winter_theta_nre": -1.251495938,
    "KG Winter_pr_1_99_nre": 35,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 375,
    "KG Spring_theta_nre": -0.99007862,
    "KG Spring_pr_1_99_nre": 35,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 401,
    "G1 Fall_theta_nre": -0.712183972,
    "G1 Fall_pr_1_99_nre": 35,
    "G1 Fall_ss_nre": 94,
    "G1 Fall_dss_nre": 429,
    "G1 Winter_theta_nre": -0.46621757,
    "G1 Winter_pr_1_99_nre": 35,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 453,
    "G1 Spring_theta_nre": -0.157284918,
    "G1 Spring_pr_1_99_nre": 35,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 484,
    "G2 Fall_theta_nre": 0.019894567,
    "G2 Fall_pr_1_99_nre": 35,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 502,
    "G2 Winter_theta_nre": 0.328863116,
    "G2 Winter_pr_1_99_nre": 35,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 533,
    "G2 Spring_theta_nre": 0.576346321,
    "G2 Spring_pr_1_99_nre": 35,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 558,
    "G3 Fall_theta_nre": 0.837461038,
    "G3 Fall_pr_1_99_nre": 35,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 584,
    "G3 Winter_theta_nre": 1.163986305,
    "G3 Winter_pr_1_99_nre": 35,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 616,
    "G3 Spring_theta_nre": 1.407170054,
    "G3 Spring_pr_1_99_nre": 35,
    "G3 Spring_ss_nre": 93,
    "G3 Spring_dss_nre": 641
  },
  {
    "ID": 175,
    "KG Winter_theta_nre": -1.249046631,
    "KG Winter_pr_1_99_nre": 35,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 375,
    "KG Spring_theta_nre": -0.988277453,
    "KG Spring_pr_1_99_nre": 35,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 401,
    "G1 Fall_theta_nre": -0.711686888,
    "G1 Fall_pr_1_99_nre": 35,
    "G1 Fall_ss_nre": 94,
    "G1 Fall_dss_nre": 429,
    "G1 Winter_theta_nre": -0.461127857,
    "G1 Winter_pr_1_99_nre": 35,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 454,
    "G1 Spring_theta_nre": -0.155784391,
    "G1 Spring_pr_1_99_nre": 35,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 484,
    "G2 Fall_theta_nre": 0.02155169,
    "G2 Fall_pr_1_99_nre": 35,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 502,
    "G2 Winter_theta_nre": 0.349046079,
    "G2 Winter_pr_1_99_nre": 35,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 535,
    "G2 Spring_theta_nre": 0.57957006,
    "G2 Spring_pr_1_99_nre": 35,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 558,
    "G3 Fall_theta_nre": 0.83964693,
    "G3 Fall_pr_1_99_nre": 35,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 584,
    "G3 Winter_theta_nre": 1.168213809,
    "G3 Winter_pr_1_99_nre": 35,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 617,
    "G3 Spring_theta_nre": 1.409938649,
    "G3 Spring_pr_1_99_nre": 35,
    "G3 Spring_ss_nre": 93,
    "G3 Spring_dss_nre": 641
  },
  {
    "ID": 176,
    "KG Winter_theta_nre": -1.243985075,
    "KG Winter_pr_1_99_nre": 35,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 376,
    "KG Spring_theta_nre": -0.987408669,
    "KG Spring_pr_1_99_nre": 35,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 401,
    "G1 Fall_theta_nre": -0.692394877,
    "G1 Fall_pr_1_99_nre": 35,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 431,
    "G1 Winter_theta_nre": -0.452073681,
    "G1 Winter_pr_1_99_nre": 35,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 455,
    "G1 Spring_theta_nre": -0.151304937,
    "G1 Spring_pr_1_99_nre": 35,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 485,
    "G2 Fall_theta_nre": 0.027460292,
    "G2 Fall_pr_1_99_nre": 35,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 503,
    "G2 Winter_theta_nre": 0.349154346,
    "G2 Winter_pr_1_99_nre": 35,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 535,
    "G2 Spring_theta_nre": 0.579822949,
    "G2 Spring_pr_1_99_nre": 35,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 558,
    "G3 Fall_theta_nre": 0.844185218,
    "G3 Fall_pr_1_99_nre": 35,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 584,
    "G3 Winter_theta_nre": 1.171507289,
    "G3 Winter_pr_1_99_nre": 35,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 617,
    "G3 Spring_theta_nre": 1.410931879,
    "G3 Spring_pr_1_99_nre": 35,
    "G3 Spring_ss_nre": 93,
    "G3 Spring_dss_nre": 641
  },
  {
    "ID": 177,
    "KG Winter_theta_nre": -1.242866223,
    "KG Winter_pr_1_99_nre": 35,
    "KG Winter_ss_nre": 94,
    "KG Winter_dss_nre": 376,
    "KG Spring_theta_nre": -0.98513521,
    "KG Spring_pr_1_99_nre": 35,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 401,
    "G1 Fall_theta_nre": -0.683323403,
    "G1 Fall_pr_1_99_nre": 35,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 432,
    "G1 Winter_theta_nre": -0.451022897,
    "G1 Winter_pr_1_99_nre": 35,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 455,
    "G1 Spring_theta_nre": -0.150963114,
    "G1 Spring_pr_1_99_nre": 35,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 485,
    "G2 Fall_theta_nre": 0.032887753,
    "G2 Fall_pr_1_99_nre": 35,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 503,
    "G2 Winter_theta_nre": 0.351837442,
    "G2 Winter_pr_1_99_nre": 35,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 535,
    "G2 Spring_theta_nre": 0.590103119,
    "G2 Spring_pr_1_99_nre": 35,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 559,
    "G3 Fall_theta_nre": 0.845922042,
    "G3 Fall_pr_1_99_nre": 35,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 585,
    "G3 Winter_theta_nre": 1.174359951,
    "G3 Winter_pr_1_99_nre": 35,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 617,
    "G3 Spring_theta_nre": 1.420395882,
    "G3 Spring_pr_1_99_nre": 35,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 642
  },
  {
    "ID": 178,
    "KG Winter_theta_nre": -1.239300922,
    "KG Winter_pr_1_99_nre": 36,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 376,
    "KG Spring_theta_nre": -0.984479333,
    "KG Spring_pr_1_99_nre": 36,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 402,
    "G1 Fall_theta_nre": -0.679825667,
    "G1 Fall_pr_1_99_nre": 36,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 432,
    "G1 Winter_theta_nre": -0.450759147,
    "G1 Winter_pr_1_99_nre": 36,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 455,
    "G1 Spring_theta_nre": -0.148954748,
    "G1 Spring_pr_1_99_nre": 36,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 485,
    "G2 Fall_theta_nre": 0.038953134,
    "G2 Fall_pr_1_99_nre": 36,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 504,
    "G2 Winter_theta_nre": 0.356767379,
    "G2 Winter_pr_1_99_nre": 36,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 536,
    "G2 Spring_theta_nre": 0.590723058,
    "G2 Spring_pr_1_99_nre": 36,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 559,
    "G3 Fall_theta_nre": 0.8481467,
    "G3 Fall_pr_1_99_nre": 36,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 585,
    "G3 Winter_theta_nre": 1.175579361,
    "G3 Winter_pr_1_99_nre": 36,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 618,
    "G3 Spring_theta_nre": 1.42307643,
    "G3 Spring_pr_1_99_nre": 36,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 642
  },
  {
    "ID": 179,
    "KG Winter_theta_nre": -1.236556306,
    "KG Winter_pr_1_99_nre": 36,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 376,
    "KG Spring_theta_nre": -0.983982094,
    "KG Spring_pr_1_99_nre": 36,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 402,
    "G1 Fall_theta_nre": -0.678517364,
    "G1 Fall_pr_1_99_nre": 36,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 432,
    "G1 Winter_theta_nre": -0.447917297,
    "G1 Winter_pr_1_99_nre": 36,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 455,
    "G1 Spring_theta_nre": -0.147641985,
    "G1 Spring_pr_1_99_nre": 36,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 485,
    "G2 Fall_theta_nre": 0.041554555,
    "G2 Fall_pr_1_99_nre": 36,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 504,
    "G2 Winter_theta_nre": 0.35795258,
    "G2 Winter_pr_1_99_nre": 36,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 536,
    "G2 Spring_theta_nre": 0.60056912,
    "G2 Spring_pr_1_99_nre": 36,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 560,
    "G3 Fall_theta_nre": 0.855340963,
    "G3 Fall_pr_1_99_nre": 36,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 586,
    "G3 Winter_theta_nre": 1.181802745,
    "G3 Winter_pr_1_99_nre": 36,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 618,
    "G3 Spring_theta_nre": 1.426969739,
    "G3 Spring_pr_1_99_nre": 36,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 643
  },
  {
    "ID": 180,
    "KG Winter_theta_nre": -1.23057692,
    "KG Winter_pr_1_99_nre": 36,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 377,
    "KG Spring_theta_nre": -0.977469334,
    "KG Spring_pr_1_99_nre": 36,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 402,
    "G1 Fall_theta_nre": -0.677432916,
    "G1 Fall_pr_1_99_nre": 36,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 432,
    "G1 Winter_theta_nre": -0.445677923,
    "G1 Winter_pr_1_99_nre": 36,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 455,
    "G1 Spring_theta_nre": -0.145758931,
    "G1 Spring_pr_1_99_nre": 36,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 485,
    "G2 Fall_theta_nre": 0.04321873,
    "G2 Fall_pr_1_99_nre": 36,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 504,
    "G2 Winter_theta_nre": 0.365733734,
    "G2 Winter_pr_1_99_nre": 36,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 537,
    "G2 Spring_theta_nre": 0.600654203,
    "G2 Spring_pr_1_99_nre": 36,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 560,
    "G3 Fall_theta_nre": 0.860559926,
    "G3 Fall_pr_1_99_nre": 36,
    "G3 Fall_ss_nre": 94,
    "G3 Fall_dss_nre": 586,
    "G3 Winter_theta_nre": 1.186231602,
    "G3 Winter_pr_1_99_nre": 36,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 619,
    "G3 Spring_theta_nre": 1.438101734,
    "G3 Spring_pr_1_99_nre": 36,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 644
  },
  {
    "ID": 181,
    "KG Winter_theta_nre": -1.230151378,
    "KG Winter_pr_1_99_nre": 36,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 377,
    "KG Spring_theta_nre": -0.964059548,
    "KG Spring_pr_1_99_nre": 36,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 404,
    "G1 Fall_theta_nre": -0.675809835,
    "G1 Fall_pr_1_99_nre": 36,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 432,
    "G1 Winter_theta_nre": -0.433636176,
    "G1 Winter_pr_1_99_nre": 36,
    "G1 Winter_ss_nre": 94,
    "G1 Winter_dss_nre": 457,
    "G1 Spring_theta_nre": -0.145710604,
    "G1 Spring_pr_1_99_nre": 36,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 485,
    "G2 Fall_theta_nre": 0.059146666,
    "G2 Fall_pr_1_99_nre": 36,
    "G2 Fall_ss_nre": 94,
    "G2 Fall_dss_nre": 506,
    "G2 Winter_theta_nre": 0.370773464,
    "G2 Winter_pr_1_99_nre": 36,
    "G2 Winter_ss_nre": 94,
    "G2 Winter_dss_nre": 537,
    "G2 Spring_theta_nre": 0.600690219,
    "G2 Spring_pr_1_99_nre": 36,
    "G2 Spring_ss_nre": 94,
    "G2 Spring_dss_nre": 560,
    "G3 Fall_theta_nre": 0.871322935,
    "G3 Fall_pr_1_99_nre": 36,
    "G3 Fall_ss_nre": 95,
    "G3 Fall_dss_nre": 587,
    "G3 Winter_theta_nre": 1.189561013,
    "G3 Winter_pr_1_99_nre": 36,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 619,
    "G3 Spring_theta_nre": 1.440787474,
    "G3 Spring_pr_1_99_nre": 36,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 644
  },
  {
    "ID": 182,
    "KG Winter_theta_nre": -1.229256486,
    "KG Winter_pr_1_99_nre": 36,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 377,
    "KG Spring_theta_nre": -0.960202185,
    "KG Spring_pr_1_99_nre": 36,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 404,
    "G1 Fall_theta_nre": -0.672279354,
    "G1 Fall_pr_1_99_nre": 36,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 433,
    "G1 Winter_theta_nre": -0.41801694,
    "G1 Winter_pr_1_99_nre": 36,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 458,
    "G1 Spring_theta_nre": -0.14323934,
    "G1 Spring_pr_1_99_nre": 36,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 486,
    "G2 Fall_theta_nre": 0.067468903,
    "G2 Fall_pr_1_99_nre": 36,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 507,
    "G2 Winter_theta_nre": 0.373862249,
    "G2 Winter_pr_1_99_nre": 36,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 537,
    "G2 Spring_theta_nre": 0.604085442,
    "G2 Spring_pr_1_99_nre": 36,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 560,
    "G3 Fall_theta_nre": 0.871909418,
    "G3 Fall_pr_1_99_nre": 36,
    "G3 Fall_ss_nre": 95,
    "G3 Fall_dss_nre": 587,
    "G3 Winter_theta_nre": 1.189926143,
    "G3 Winter_pr_1_99_nre": 36,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 619,
    "G3 Spring_theta_nre": 1.442405787,
    "G3 Spring_pr_1_99_nre": 36,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 644
  },
  {
    "ID": 183,
    "KG Winter_theta_nre": -1.212247807,
    "KG Winter_pr_1_99_nre": 37,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 379,
    "KG Spring_theta_nre": -0.9547067,
    "KG Spring_pr_1_99_nre": 37,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 405,
    "G1 Fall_theta_nre": -0.66648846,
    "G1 Fall_pr_1_99_nre": 37,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 433,
    "G1 Winter_theta_nre": -0.415152401,
    "G1 Winter_pr_1_99_nre": 37,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 458,
    "G1 Spring_theta_nre": -0.141332257,
    "G1 Spring_pr_1_99_nre": 37,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 486,
    "G2 Fall_theta_nre": 0.072047971,
    "G2 Fall_pr_1_99_nre": 37,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 507,
    "G2 Winter_theta_nre": 0.387135313,
    "G2 Winter_pr_1_99_nre": 37,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 539,
    "G2 Spring_theta_nre": 0.608420638,
    "G2 Spring_pr_1_99_nre": 37,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 561,
    "G3 Fall_theta_nre": 0.877362268,
    "G3 Fall_pr_1_99_nre": 37,
    "G3 Fall_ss_nre": 95,
    "G3 Fall_dss_nre": 588,
    "G3 Winter_theta_nre": 1.195394223,
    "G3 Winter_pr_1_99_nre": 37,
    "G3 Winter_ss_nre": 95,
    "G3 Winter_dss_nre": 620,
    "G3 Spring_theta_nre": 1.442948672,
    "G3 Spring_pr_1_99_nre": 37,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 644
  },
  {
    "ID": 184,
    "KG Winter_theta_nre": -1.211791516,
    "KG Winter_pr_1_99_nre": 37,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 379,
    "KG Spring_theta_nre": -0.950549407,
    "KG Spring_pr_1_99_nre": 37,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 405,
    "G1 Fall_theta_nre": -0.664981878,
    "G1 Fall_pr_1_99_nre": 37,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 434,
    "G1 Winter_theta_nre": -0.412473834,
    "G1 Winter_pr_1_99_nre": 37,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 459,
    "G1 Spring_theta_nre": -0.13526214,
    "G1 Spring_pr_1_99_nre": 37,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 486,
    "G2 Fall_theta_nre": 0.075082693,
    "G2 Fall_pr_1_99_nre": 37,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 508,
    "G2 Winter_theta_nre": 0.388864274,
    "G2 Winter_pr_1_99_nre": 37,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 539,
    "G2 Spring_theta_nre": 0.615156467,
    "G2 Spring_pr_1_99_nre": 37,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 562,
    "G3 Fall_theta_nre": 0.878059804,
    "G3 Fall_pr_1_99_nre": 37,
    "G3 Fall_ss_nre": 95,
    "G3 Fall_dss_nre": 588,
    "G3 Winter_theta_nre": 1.198482569,
    "G3 Winter_pr_1_99_nre": 37,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 620,
    "G3 Spring_theta_nre": 1.446356391,
    "G3 Spring_pr_1_99_nre": 37,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 645
  },
  {
    "ID": 185,
    "KG Winter_theta_nre": -1.211030105,
    "KG Winter_pr_1_99_nre": 37,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 379,
    "KG Spring_theta_nre": -0.946798966,
    "KG Spring_pr_1_99_nre": 37,
    "KG Spring_ss_nre": 94,
    "KG Spring_dss_nre": 405,
    "G1 Fall_theta_nre": -0.644757913,
    "G1 Fall_pr_1_99_nre": 37,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 436,
    "G1 Winter_theta_nre": -0.411319065,
    "G1 Winter_pr_1_99_nre": 37,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 459,
    "G1 Spring_theta_nre": -0.134924538,
    "G1 Spring_pr_1_99_nre": 37,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 487,
    "G2 Fall_theta_nre": 0.086342517,
    "G2 Fall_pr_1_99_nre": 37,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 509,
    "G2 Winter_theta_nre": 0.389208133,
    "G2 Winter_pr_1_99_nre": 37,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 539,
    "G2 Spring_theta_nre": 0.615311857,
    "G2 Spring_pr_1_99_nre": 37,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 562,
    "G3 Fall_theta_nre": 0.888555811,
    "G3 Fall_pr_1_99_nre": 37,
    "G3 Fall_ss_nre": 95,
    "G3 Fall_dss_nre": 589,
    "G3 Winter_theta_nre": 1.202281319,
    "G3 Winter_pr_1_99_nre": 37,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 620,
    "G3 Spring_theta_nre": 1.447898038,
    "G3 Spring_pr_1_99_nre": 37,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 645
  },
  {
    "ID": 186,
    "KG Winter_theta_nre": -1.207119051,
    "KG Winter_pr_1_99_nre": 37,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 379,
    "KG Spring_theta_nre": -0.94057308,
    "KG Spring_pr_1_99_nre": 37,
    "KG Spring_ss_nre": 95,
    "KG Spring_dss_nre": 406,
    "G1 Fall_theta_nre": -0.639876874,
    "G1 Fall_pr_1_99_nre": 37,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 436,
    "G1 Winter_theta_nre": -0.405512677,
    "G1 Winter_pr_1_99_nre": 37,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 459,
    "G1 Spring_theta_nre": -0.133434911,
    "G1 Spring_pr_1_99_nre": 37,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 487,
    "G2 Fall_theta_nre": 0.089293941,
    "G2 Fall_pr_1_99_nre": 37,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 509,
    "G2 Winter_theta_nre": 0.394523511,
    "G2 Winter_pr_1_99_nre": 37,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 539,
    "G2 Spring_theta_nre": 0.619434154,
    "G2 Spring_pr_1_99_nre": 37,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 562,
    "G3 Fall_theta_nre": 0.896536385,
    "G3 Fall_pr_1_99_nre": 37,
    "G3 Fall_ss_nre": 95,
    "G3 Fall_dss_nre": 590,
    "G3 Winter_theta_nre": 1.209481999,
    "G3 Winter_pr_1_99_nre": 37,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 621,
    "G3 Spring_theta_nre": 1.448548475,
    "G3 Spring_pr_1_99_nre": 37,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 645
  },
  {
    "ID": 187,
    "KG Winter_theta_nre": -1.206678484,
    "KG Winter_pr_1_99_nre": 37,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 379,
    "KG Spring_theta_nre": -0.933265704,
    "KG Spring_pr_1_99_nre": 37,
    "KG Spring_ss_nre": 95,
    "KG Spring_dss_nre": 407,
    "G1 Fall_theta_nre": -0.639762646,
    "G1 Fall_pr_1_99_nre": 37,
    "G1 Fall_ss_nre": 95,
    "G1 Fall_dss_nre": 436,
    "G1 Winter_theta_nre": -0.402193089,
    "G1 Winter_pr_1_99_nre": 37,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 460,
    "G1 Spring_theta_nre": -0.126194188,
    "G1 Spring_pr_1_99_nre": 37,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 487,
    "G2 Fall_theta_nre": 0.094007427,
    "G2 Fall_pr_1_99_nre": 37,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 509,
    "G2 Winter_theta_nre": 0.396083217,
    "G2 Winter_pr_1_99_nre": 37,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 540,
    "G2 Spring_theta_nre": 0.621362251,
    "G2 Spring_pr_1_99_nre": 37,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 562,
    "G3 Fall_theta_nre": 0.905678947,
    "G3 Fall_pr_1_99_nre": 37,
    "G3 Fall_ss_nre": 95,
    "G3 Fall_dss_nre": 591,
    "G3 Winter_theta_nre": 1.20962894,
    "G3 Winter_pr_1_99_nre": 37,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 621,
    "G3 Spring_theta_nre": 1.449876752,
    "G3 Spring_pr_1_99_nre": 37,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 645
  },
  {
    "ID": 188,
    "KG Winter_theta_nre": -1.204109585,
    "KG Winter_pr_1_99_nre": 38,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 380,
    "KG Spring_theta_nre": -0.929778753,
    "KG Spring_pr_1_99_nre": 38,
    "KG Spring_ss_nre": 95,
    "KG Spring_dss_nre": 407,
    "G1 Fall_theta_nre": -0.637359377,
    "G1 Fall_pr_1_99_nre": 38,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 436,
    "G1 Winter_theta_nre": -0.40083797,
    "G1 Winter_pr_1_99_nre": 38,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 460,
    "G1 Spring_theta_nre": -0.125058018,
    "G1 Spring_pr_1_99_nre": 38,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 487,
    "G2 Fall_theta_nre": 0.095927104,
    "G2 Fall_pr_1_99_nre": 38,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 510,
    "G2 Winter_theta_nre": 0.397974197,
    "G2 Winter_pr_1_99_nre": 38,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 540,
    "G2 Spring_theta_nre": 0.623976505,
    "G2 Spring_pr_1_99_nre": 38,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 562,
    "G3 Fall_theta_nre": 0.909146202,
    "G3 Fall_pr_1_99_nre": 38,
    "G3 Fall_ss_nre": 95,
    "G3 Fall_dss_nre": 591,
    "G3 Winter_theta_nre": 1.216010769,
    "G3 Winter_pr_1_99_nre": 38,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 622,
    "G3 Spring_theta_nre": 1.454186977,
    "G3 Spring_pr_1_99_nre": 38,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 645
  },
  {
    "ID": 189,
    "KG Winter_theta_nre": -1.200406792,
    "KG Winter_pr_1_99_nre": 38,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 380,
    "KG Spring_theta_nre": -0.923336134,
    "KG Spring_pr_1_99_nre": 38,
    "KG Spring_ss_nre": 95,
    "KG Spring_dss_nre": 408,
    "G1 Fall_theta_nre": -0.629434303,
    "G1 Fall_pr_1_99_nre": 38,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 437,
    "G1 Winter_theta_nre": -0.395952477,
    "G1 Winter_pr_1_99_nre": 38,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 460,
    "G1 Spring_theta_nre": -0.124497483,
    "G1 Spring_pr_1_99_nre": 38,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 488,
    "G2 Fall_theta_nre": 0.09845283,
    "G2 Fall_pr_1_99_nre": 38,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 510,
    "G2 Winter_theta_nre": 0.398813814,
    "G2 Winter_pr_1_99_nre": 38,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 540,
    "G2 Spring_theta_nre": 0.63490295,
    "G2 Spring_pr_1_99_nre": 38,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 563,
    "G3 Fall_theta_nre": 0.914294482,
    "G3 Fall_pr_1_99_nre": 38,
    "G3 Fall_ss_nre": 95,
    "G3 Fall_dss_nre": 591,
    "G3 Winter_theta_nre": 1.218091065,
    "G3 Winter_pr_1_99_nre": 38,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 622,
    "G3 Spring_theta_nre": 1.457606918,
    "G3 Spring_pr_1_99_nre": 38,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 646
  },
  {
    "ID": 190,
    "KG Winter_theta_nre": -1.193444967,
    "KG Winter_pr_1_99_nre": 38,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 381,
    "KG Spring_theta_nre": -0.922498746,
    "KG Spring_pr_1_99_nre": 38,
    "KG Spring_ss_nre": 95,
    "KG Spring_dss_nre": 408,
    "G1 Fall_theta_nre": -0.627987266,
    "G1 Fall_pr_1_99_nre": 38,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 437,
    "G1 Winter_theta_nre": -0.384195995,
    "G1 Winter_pr_1_99_nre": 38,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 462,
    "G1 Spring_theta_nre": -0.119711469,
    "G1 Spring_pr_1_99_nre": 38,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 488,
    "G2 Fall_theta_nre": 0.111451769,
    "G2 Fall_pr_1_99_nre": 38,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 511,
    "G2 Winter_theta_nre": 0.401026269,
    "G2 Winter_pr_1_99_nre": 38,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 540,
    "G2 Spring_theta_nre": 0.635789327,
    "G2 Spring_pr_1_99_nre": 38,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 564,
    "G3 Fall_theta_nre": 0.929959177,
    "G3 Fall_pr_1_99_nre": 38,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 593,
    "G3 Winter_theta_nre": 1.219198828,
    "G3 Winter_pr_1_99_nre": 38,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 622,
    "G3 Spring_theta_nre": 1.459425634,
    "G3 Spring_pr_1_99_nre": 38,
    "G3 Spring_ss_nre": 94,
    "G3 Spring_dss_nre": 646
  },
  {
    "ID": 191,
    "KG Winter_theta_nre": -1.193039269,
    "KG Winter_pr_1_99_nre": 38,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 381,
    "KG Spring_theta_nre": -0.911567869,
    "KG Spring_pr_1_99_nre": 38,
    "KG Spring_ss_nre": 95,
    "KG Spring_dss_nre": 409,
    "G1 Fall_theta_nre": -0.626441384,
    "G1 Fall_pr_1_99_nre": 38,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 437,
    "G1 Winter_theta_nre": -0.381133815,
    "G1 Winter_pr_1_99_nre": 38,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 462,
    "G1 Spring_theta_nre": -0.118430042,
    "G1 Spring_pr_1_99_nre": 38,
    "G1 Spring_ss_nre": 94,
    "G1 Spring_dss_nre": 488,
    "G2 Fall_theta_nre": 0.114679641,
    "G2 Fall_pr_1_99_nre": 38,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 511,
    "G2 Winter_theta_nre": 0.402985678,
    "G2 Winter_pr_1_99_nre": 38,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 540,
    "G2 Spring_theta_nre": 0.636577382,
    "G2 Spring_pr_1_99_nre": 38,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 564,
    "G3 Fall_theta_nre": 0.940670103,
    "G3 Fall_pr_1_99_nre": 38,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 594,
    "G3 Winter_theta_nre": 1.22231317,
    "G3 Winter_pr_1_99_nre": 38,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 622,
    "G3 Spring_theta_nre": 1.466565289,
    "G3 Spring_pr_1_99_nre": 38,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 647
  },
  {
    "ID": 192,
    "KG Winter_theta_nre": -1.192162162,
    "KG Winter_pr_1_99_nre": 38,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 381,
    "KG Spring_theta_nre": -0.891790379,
    "KG Spring_pr_1_99_nre": 38,
    "KG Spring_ss_nre": 95,
    "KG Spring_dss_nre": 411,
    "G1 Fall_theta_nre": -0.621101547,
    "G1 Fall_pr_1_99_nre": 38,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 438,
    "G1 Winter_theta_nre": -0.368439066,
    "G1 Winter_pr_1_99_nre": 38,
    "G1 Winter_ss_nre": 95,
    "G1 Winter_dss_nre": 463,
    "G1 Spring_theta_nre": -0.116579706,
    "G1 Spring_pr_1_99_nre": 38,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 488,
    "G2 Fall_theta_nre": 0.119628876,
    "G2 Fall_pr_1_99_nre": 38,
    "G2 Fall_ss_nre": 95,
    "G2 Fall_dss_nre": 512,
    "G2 Winter_theta_nre": 0.422049862,
    "G2 Winter_pr_1_99_nre": 38,
    "G2 Winter_ss_nre": 95,
    "G2 Winter_dss_nre": 542,
    "G2 Spring_theta_nre": 0.63862733,
    "G2 Spring_pr_1_99_nre": 38,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 564,
    "G3 Fall_theta_nre": 0.942240153,
    "G3 Fall_pr_1_99_nre": 38,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 594,
    "G3 Winter_theta_nre": 1.224731072,
    "G3 Winter_pr_1_99_nre": 38,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 622,
    "G3 Spring_theta_nre": 1.468585314,
    "G3 Spring_pr_1_99_nre": 38,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 647
  },
  {
    "ID": 193,
    "KG Winter_theta_nre": -1.191492863,
    "KG Winter_pr_1_99_nre": 39,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 381,
    "KG Spring_theta_nre": -0.887261042,
    "KG Spring_pr_1_99_nre": 39,
    "KG Spring_ss_nre": 95,
    "KG Spring_dss_nre": 411,
    "G1 Fall_theta_nre": -0.611686375,
    "G1 Fall_pr_1_99_nre": 39,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 439,
    "G1 Winter_theta_nre": -0.354535552,
    "G1 Winter_pr_1_99_nre": 39,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 465,
    "G1 Spring_theta_nre": -0.114688931,
    "G1 Spring_pr_1_99_nre": 39,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 489,
    "G2 Fall_theta_nre": 0.123480235,
    "G2 Fall_pr_1_99_nre": 39,
    "G2 Fall_ss_nre": 96,
    "G2 Fall_dss_nre": 512,
    "G2 Winter_theta_nre": 0.424423097,
    "G2 Winter_pr_1_99_nre": 39,
    "G2 Winter_ss_nre": 96,
    "G2 Winter_dss_nre": 542,
    "G2 Spring_theta_nre": 0.644989182,
    "G2 Spring_pr_1_99_nre": 39,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 564,
    "G3 Fall_theta_nre": 0.949392773,
    "G3 Fall_pr_1_99_nre": 39,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 595,
    "G3 Winter_theta_nre": 1.229688413,
    "G3 Winter_pr_1_99_nre": 39,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 623,
    "G3 Spring_theta_nre": 1.47812452,
    "G3 Spring_pr_1_99_nre": 39,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 648
  },
  {
    "ID": 194,
    "KG Winter_theta_nre": -1.190454035,
    "KG Winter_pr_1_99_nre": 39,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 381,
    "KG Spring_theta_nre": -0.887035951,
    "KG Spring_pr_1_99_nre": 39,
    "KG Spring_ss_nre": 95,
    "KG Spring_dss_nre": 411,
    "G1 Fall_theta_nre": -0.60759978,
    "G1 Fall_pr_1_99_nre": 39,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 439,
    "G1 Winter_theta_nre": -0.350431239,
    "G1 Winter_pr_1_99_nre": 39,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 465,
    "G1 Spring_theta_nre": -0.110006177,
    "G1 Spring_pr_1_99_nre": 39,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 489,
    "G2 Fall_theta_nre": 0.139901255,
    "G2 Fall_pr_1_99_nre": 39,
    "G2 Fall_ss_nre": 96,
    "G2 Fall_dss_nre": 514,
    "G2 Winter_theta_nre": 0.424708285,
    "G2 Winter_pr_1_99_nre": 39,
    "G2 Winter_ss_nre": 96,
    "G2 Winter_dss_nre": 542,
    "G2 Spring_theta_nre": 0.64519498,
    "G2 Spring_pr_1_99_nre": 39,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 565,
    "G3 Fall_theta_nre": 0.963378749,
    "G3 Fall_pr_1_99_nre": 39,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 596,
    "G3 Winter_theta_nre": 1.229979332,
    "G3 Winter_pr_1_99_nre": 39,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 623,
    "G3 Spring_theta_nre": 1.488953409,
    "G3 Spring_pr_1_99_nre": 39,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 649
  },
  {
    "ID": 195,
    "KG Winter_theta_nre": -1.188637437,
    "KG Winter_pr_1_99_nre": 39,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 381,
    "KG Spring_theta_nre": -0.885462934,
    "KG Spring_pr_1_99_nre": 39,
    "KG Spring_ss_nre": 95,
    "KG Spring_dss_nre": 411,
    "G1 Fall_theta_nre": -0.607536426,
    "G1 Fall_pr_1_99_nre": 39,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 439,
    "G1 Winter_theta_nre": -0.345552682,
    "G1 Winter_pr_1_99_nre": 39,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 465,
    "G1 Spring_theta_nre": -0.107656279,
    "G1 Spring_pr_1_99_nre": 39,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 489,
    "G2 Fall_theta_nre": 0.143481523,
    "G2 Fall_pr_1_99_nre": 39,
    "G2 Fall_ss_nre": 96,
    "G2 Fall_dss_nre": 514,
    "G2 Winter_theta_nre": 0.429097589,
    "G2 Winter_pr_1_99_nre": 39,
    "G2 Winter_ss_nre": 96,
    "G2 Winter_dss_nre": 543,
    "G2 Spring_theta_nre": 0.657994158,
    "G2 Spring_pr_1_99_nre": 39,
    "G2 Spring_ss_nre": 95,
    "G2 Spring_dss_nre": 566,
    "G3 Fall_theta_nre": 0.965109273,
    "G3 Fall_pr_1_99_nre": 39,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 597,
    "G3 Winter_theta_nre": 1.234635812,
    "G3 Winter_pr_1_99_nre": 39,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 623,
    "G3 Spring_theta_nre": 1.490607976,
    "G3 Spring_pr_1_99_nre": 39,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 649
  },
  {
    "ID": 196,
    "KG Winter_theta_nre": -1.186201132,
    "KG Winter_pr_1_99_nre": 39,
    "KG Winter_ss_nre": 95,
    "KG Winter_dss_nre": 381,
    "KG Spring_theta_nre": -0.878379324,
    "KG Spring_pr_1_99_nre": 39,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 412,
    "G1 Fall_theta_nre": -0.604363058,
    "G1 Fall_pr_1_99_nre": 39,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 440,
    "G1 Winter_theta_nre": -0.33578038,
    "G1 Winter_pr_1_99_nre": 39,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 466,
    "G1 Spring_theta_nre": -0.1014632,
    "G1 Spring_pr_1_99_nre": 39,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 490,
    "G2 Fall_theta_nre": 0.148491684,
    "G2 Fall_pr_1_99_nre": 39,
    "G2 Fall_ss_nre": 96,
    "G2 Fall_dss_nre": 515,
    "G2 Winter_theta_nre": 0.430161573,
    "G2 Winter_pr_1_99_nre": 39,
    "G2 Winter_ss_nre": 96,
    "G2 Winter_dss_nre": 543,
    "G2 Spring_theta_nre": 0.662591132,
    "G2 Spring_pr_1_99_nre": 39,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 566,
    "G3 Fall_theta_nre": 0.965563292,
    "G3 Fall_pr_1_99_nre": 39,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 597,
    "G3 Winter_theta_nre": 1.235417829,
    "G3 Winter_pr_1_99_nre": 39,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 624,
    "G3 Spring_theta_nre": 1.494193052,
    "G3 Spring_pr_1_99_nre": 39,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 649
  },
  {
    "ID": 197,
    "KG Winter_theta_nre": -1.182826448,
    "KG Winter_pr_1_99_nre": 39,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 382,
    "KG Spring_theta_nre": -0.877781219,
    "KG Spring_pr_1_99_nre": 39,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 412,
    "G1 Fall_theta_nre": -0.603483039,
    "G1 Fall_pr_1_99_nre": 39,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 440,
    "G1 Winter_theta_nre": -0.335164123,
    "G1 Winter_pr_1_99_nre": 39,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 466,
    "G1 Spring_theta_nre": -0.099326158,
    "G1 Spring_pr_1_99_nre": 39,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 490,
    "G2 Fall_theta_nre": 0.148629251,
    "G2 Fall_pr_1_99_nre": 39,
    "G2 Fall_ss_nre": 96,
    "G2 Fall_dss_nre": 515,
    "G2 Winter_theta_nre": 0.444009628,
    "G2 Winter_pr_1_99_nre": 39,
    "G2 Winter_ss_nre": 96,
    "G2 Winter_dss_nre": 544,
    "G2 Spring_theta_nre": 0.664636822,
    "G2 Spring_pr_1_99_nre": 39,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 566,
    "G3 Fall_theta_nre": 0.967990198,
    "G3 Fall_pr_1_99_nre": 39,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 597,
    "G3 Winter_theta_nre": 1.241374934,
    "G3 Winter_pr_1_99_nre": 39,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 624,
    "G3 Spring_theta_nre": 1.500680758,
    "G3 Spring_pr_1_99_nre": 39,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 650
  },
  {
    "ID": 198,
    "KG Winter_theta_nre": -1.177689427,
    "KG Winter_pr_1_99_nre": 40,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 382,
    "KG Spring_theta_nre": -0.872382122,
    "KG Spring_pr_1_99_nre": 40,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 413,
    "G1 Fall_theta_nre": -0.59537159,
    "G1 Fall_pr_1_99_nre": 40,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 440,
    "G1 Winter_theta_nre": -0.329954078,
    "G1 Winter_pr_1_99_nre": 40,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 467,
    "G1 Spring_theta_nre": -0.096696529,
    "G1 Spring_pr_1_99_nre": 40,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 490,
    "G2 Fall_theta_nre": 0.151399642,
    "G2 Fall_pr_1_99_nre": 40,
    "G2 Fall_ss_nre": 96,
    "G2 Fall_dss_nre": 515,
    "G2 Winter_theta_nre": 0.449074186,
    "G2 Winter_pr_1_99_nre": 40,
    "G2 Winter_ss_nre": 96,
    "G2 Winter_dss_nre": 545,
    "G2 Spring_theta_nre": 0.677049196,
    "G2 Spring_pr_1_99_nre": 40,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 568,
    "G3 Fall_theta_nre": 0.97309251,
    "G3 Fall_pr_1_99_nre": 40,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 597,
    "G3 Winter_theta_nre": 1.245715866,
    "G3 Winter_pr_1_99_nre": 40,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 625,
    "G3 Spring_theta_nre": 1.502393316,
    "G3 Spring_pr_1_99_nre": 40,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 650
  },
  {
    "ID": 199,
    "KG Winter_theta_nre": -1.1738195,
    "KG Winter_pr_1_99_nre": 40,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 383,
    "KG Spring_theta_nre": -0.871390911,
    "KG Spring_pr_1_99_nre": 40,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 413,
    "G1 Fall_theta_nre": -0.594163845,
    "G1 Fall_pr_1_99_nre": 40,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 441,
    "G1 Winter_theta_nre": -0.325903297,
    "G1 Winter_pr_1_99_nre": 40,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 467,
    "G1 Spring_theta_nre": -0.096681998,
    "G1 Spring_pr_1_99_nre": 40,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 490,
    "G2 Fall_theta_nre": 0.154791854,
    "G2 Fall_pr_1_99_nre": 40,
    "G2 Fall_ss_nre": 96,
    "G2 Fall_dss_nre": 515,
    "G2 Winter_theta_nre": 0.456875111,
    "G2 Winter_pr_1_99_nre": 40,
    "G2 Winter_ss_nre": 96,
    "G2 Winter_dss_nre": 546,
    "G2 Spring_theta_nre": 0.677805375,
    "G2 Spring_pr_1_99_nre": 40,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 568,
    "G3 Fall_theta_nre": 0.974517989,
    "G3 Fall_pr_1_99_nre": 40,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 597,
    "G3 Winter_theta_nre": 1.245716607,
    "G3 Winter_pr_1_99_nre": 40,
    "G3 Winter_ss_nre": 96,
    "G3 Winter_dss_nre": 625,
    "G3 Spring_theta_nre": 1.504518724,
    "G3 Spring_pr_1_99_nre": 40,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 650
  },
  {
    "ID": 200,
    "KG Winter_theta_nre": -1.16030485,
    "KG Winter_pr_1_99_nre": 40,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 384,
    "KG Spring_theta_nre": -0.858163836,
    "KG Spring_pr_1_99_nre": 40,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 414,
    "G1 Fall_theta_nre": -0.58845479,
    "G1 Fall_pr_1_99_nre": 40,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 441,
    "G1 Winter_theta_nre": -0.323626617,
    "G1 Winter_pr_1_99_nre": 40,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 468,
    "G1 Spring_theta_nre": -0.094335482,
    "G1 Spring_pr_1_99_nre": 40,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 491,
    "G2 Fall_theta_nre": 0.167488232,
    "G2 Fall_pr_1_99_nre": 40,
    "G2 Fall_ss_nre": 96,
    "G2 Fall_dss_nre": 517,
    "G2 Winter_theta_nre": 0.462230817,
    "G2 Winter_pr_1_99_nre": 40,
    "G2 Winter_ss_nre": 96,
    "G2 Winter_dss_nre": 546,
    "G2 Spring_theta_nre": 0.684098453,
    "G2 Spring_pr_1_99_nre": 40,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 568,
    "G3 Fall_theta_nre": 0.982153552,
    "G3 Fall_pr_1_99_nre": 40,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 598,
    "G3 Winter_theta_nre": 1.248527415,
    "G3 Winter_pr_1_99_nre": 40,
    "G3 Winter_ss_nre": 97,
    "G3 Winter_dss_nre": 625,
    "G3 Spring_theta_nre": 1.504855498,
    "G3 Spring_pr_1_99_nre": 40,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 650
  },
  {
    "ID": 201,
    "KG Winter_theta_nre": -1.158724042,
    "KG Winter_pr_1_99_nre": 40,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 384,
    "KG Spring_theta_nre": -0.84516983,
    "KG Spring_pr_1_99_nre": 40,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 415,
    "G1 Fall_theta_nre": -0.583470022,
    "G1 Fall_pr_1_99_nre": 40,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 442,
    "G1 Winter_theta_nre": -0.32055973,
    "G1 Winter_pr_1_99_nre": 40,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 468,
    "G1 Spring_theta_nre": -0.092676887,
    "G1 Spring_pr_1_99_nre": 40,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 491,
    "G2 Fall_theta_nre": 0.16770006,
    "G2 Fall_pr_1_99_nre": 40,
    "G2 Fall_ss_nre": 96,
    "G2 Fall_dss_nre": 517,
    "G2 Winter_theta_nre": 0.47619158,
    "G2 Winter_pr_1_99_nre": 40,
    "G2 Winter_ss_nre": 97,
    "G2 Winter_dss_nre": 548,
    "G2 Spring_theta_nre": 0.686949945,
    "G2 Spring_pr_1_99_nre": 40,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 569,
    "G3 Fall_theta_nre": 0.983943205,
    "G3 Fall_pr_1_99_nre": 40,
    "G3 Fall_ss_nre": 96,
    "G3 Fall_dss_nre": 598,
    "G3 Winter_theta_nre": 1.263094753,
    "G3 Winter_pr_1_99_nre": 40,
    "G3 Winter_ss_nre": 97,
    "G3 Winter_dss_nre": 626,
    "G3 Spring_theta_nre": 1.505191739,
    "G3 Spring_pr_1_99_nre": 40,
    "G3 Spring_ss_nre": 95,
    "G3 Spring_dss_nre": 651
  },
  {
    "ID": 202,
    "KG Winter_theta_nre": -1.155391022,
    "KG Winter_pr_1_99_nre": 40,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 384,
    "KG Spring_theta_nre": -0.838290637,
    "KG Spring_pr_1_99_nre": 40,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 416,
    "G1 Fall_theta_nre": -0.58091447,
    "G1 Fall_pr_1_99_nre": 40,
    "G1 Fall_ss_nre": 96,
    "G1 Fall_dss_nre": 442,
    "G1 Winter_theta_nre": -0.317543623,
    "G1 Winter_pr_1_99_nre": 40,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 468,
    "G1 Spring_theta_nre": -0.087391754,
    "G1 Spring_pr_1_99_nre": 40,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 491,
    "G2 Fall_theta_nre": 0.184863301,
    "G2 Fall_pr_1_99_nre": 40,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 518,
    "G2 Winter_theta_nre": 0.483931171,
    "G2 Winter_pr_1_99_nre": 40,
    "G2 Winter_ss_nre": 97,
    "G2 Winter_dss_nre": 548,
    "G2 Spring_theta_nre": 0.687540916,
    "G2 Spring_pr_1_99_nre": 40,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 569,
    "G3 Fall_theta_nre": 0.98961483,
    "G3 Fall_pr_1_99_nre": 40,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 599,
    "G3 Winter_theta_nre": 1.263110226,
    "G3 Winter_pr_1_99_nre": 40,
    "G3 Winter_ss_nre": 97,
    "G3 Winter_dss_nre": 626,
    "G3 Spring_theta_nre": 1.508560602,
    "G3 Spring_pr_1_99_nre": 40,
    "G3 Spring_ss_nre": 96,
    "G3 Spring_dss_nre": 651
  },
  {
    "ID": 203,
    "KG Winter_theta_nre": -1.14827742,
    "KG Winter_pr_1_99_nre": 41,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 385,
    "KG Spring_theta_nre": -0.837045867,
    "KG Spring_pr_1_99_nre": 41,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 416,
    "G1 Fall_theta_nre": -0.568515565,
    "G1 Fall_pr_1_99_nre": 41,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 443,
    "G1 Winter_theta_nre": -0.31645661,
    "G1 Winter_pr_1_99_nre": 41,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 468,
    "G1 Spring_theta_nre": -0.08631343,
    "G1 Spring_pr_1_99_nre": 41,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 491,
    "G2 Fall_theta_nre": 0.195263111,
    "G2 Fall_pr_1_99_nre": 41,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 520,
    "G2 Winter_theta_nre": 0.484126262,
    "G2 Winter_pr_1_99_nre": 41,
    "G2 Winter_ss_nre": 97,
    "G2 Winter_dss_nre": 548,
    "G2 Spring_theta_nre": 0.688972408,
    "G2 Spring_pr_1_99_nre": 41,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 569,
    "G3 Fall_theta_nre": 0.990406157,
    "G3 Fall_pr_1_99_nre": 41,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 599,
    "G3 Winter_theta_nre": 1.265602081,
    "G3 Winter_pr_1_99_nre": 41,
    "G3 Winter_ss_nre": 97,
    "G3 Winter_dss_nre": 627,
    "G3 Spring_theta_nre": 1.513701805,
    "G3 Spring_pr_1_99_nre": 41,
    "G3 Spring_ss_nre": 96,
    "G3 Spring_dss_nre": 651
  },
  {
    "ID": 204,
    "KG Winter_theta_nre": -1.146749837,
    "KG Winter_pr_1_99_nre": 41,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 385,
    "KG Spring_theta_nre": -0.836963821,
    "KG Spring_pr_1_99_nre": 41,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 416,
    "G1 Fall_theta_nre": -0.556833298,
    "G1 Fall_pr_1_99_nre": 41,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 444,
    "G1 Winter_theta_nre": -0.315217584,
    "G1 Winter_pr_1_99_nre": 41,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 468,
    "G1 Spring_theta_nre": -0.074190108,
    "G1 Spring_pr_1_99_nre": 41,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 493,
    "G2 Fall_theta_nre": 0.195654424,
    "G2 Fall_pr_1_99_nre": 41,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 520,
    "G2 Winter_theta_nre": 0.485014069,
    "G2 Winter_pr_1_99_nre": 41,
    "G2 Winter_ss_nre": 97,
    "G2 Winter_dss_nre": 549,
    "G2 Spring_theta_nre": 0.69239433,
    "G2 Spring_pr_1_99_nre": 41,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 569,
    "G3 Fall_theta_nre": 0.998720475,
    "G3 Fall_pr_1_99_nre": 41,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 600,
    "G3 Winter_theta_nre": 1.265664335,
    "G3 Winter_pr_1_99_nre": 41,
    "G3 Winter_ss_nre": 97,
    "G3 Winter_dss_nre": 627,
    "G3 Spring_theta_nre": 1.526378072,
    "G3 Spring_pr_1_99_nre": 41,
    "G3 Spring_ss_nre": 96,
    "G3 Spring_dss_nre": 653
  },
  {
    "ID": 205,
    "KG Winter_theta_nre": -1.1415057,
    "KG Winter_pr_1_99_nre": 41,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 386,
    "KG Spring_theta_nre": -0.831184071,
    "KG Spring_pr_1_99_nre": 41,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 417,
    "G1 Fall_theta_nre": -0.555058677,
    "G1 Fall_pr_1_99_nre": 41,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 444,
    "G1 Winter_theta_nre": -0.311739406,
    "G1 Winter_pr_1_99_nre": 41,
    "G1 Winter_ss_nre": 96,
    "G1 Winter_dss_nre": 469,
    "G1 Spring_theta_nre": -0.071136069,
    "G1 Spring_pr_1_99_nre": 41,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 493,
    "G2 Fall_theta_nre": 0.201618424,
    "G2 Fall_pr_1_99_nre": 41,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 520,
    "G2 Winter_theta_nre": 0.49500786,
    "G2 Winter_pr_1_99_nre": 41,
    "G2 Winter_ss_nre": 97,
    "G2 Winter_dss_nre": 550,
    "G2 Spring_theta_nre": 0.695119217,
    "G2 Spring_pr_1_99_nre": 41,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 570,
    "G3 Fall_theta_nre": 0.99996038,
    "G3 Fall_pr_1_99_nre": 41,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 600,
    "G3 Winter_theta_nre": 1.277831538,
    "G3 Winter_pr_1_99_nre": 41,
    "G3 Winter_ss_nre": 97,
    "G3 Winter_dss_nre": 628,
    "G3 Spring_theta_nre": 1.527413671,
    "G3 Spring_pr_1_99_nre": 41,
    "G3 Spring_ss_nre": 96,
    "G3 Spring_dss_nre": 653
  },
  {
    "ID": 206,
    "KG Winter_theta_nre": -1.137166426,
    "KG Winter_pr_1_99_nre": 41,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 386,
    "KG Spring_theta_nre": -0.829049123,
    "KG Spring_pr_1_99_nre": 41,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 417,
    "G1 Fall_theta_nre": -0.551190383,
    "G1 Fall_pr_1_99_nre": 41,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 445,
    "G1 Winter_theta_nre": -0.310794542,
    "G1 Winter_pr_1_99_nre": 41,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 469,
    "G1 Spring_theta_nre": -0.07056779,
    "G1 Spring_pr_1_99_nre": 41,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 493,
    "G2 Fall_theta_nre": 0.204698044,
    "G2 Fall_pr_1_99_nre": 41,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 520,
    "G2 Winter_theta_nre": 0.499933668,
    "G2 Winter_pr_1_99_nre": 41,
    "G2 Winter_ss_nre": 97,
    "G2 Winter_dss_nre": 550,
    "G2 Spring_theta_nre": 0.700643671,
    "G2 Spring_pr_1_99_nre": 41,
    "G2 Spring_ss_nre": 96,
    "G2 Spring_dss_nre": 570,
    "G3 Fall_theta_nre": 1.000015202,
    "G3 Fall_pr_1_99_nre": 41,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 600,
    "G3 Winter_theta_nre": 1.278569886,
    "G3 Winter_pr_1_99_nre": 41,
    "G3 Winter_ss_nre": 97,
    "G3 Winter_dss_nre": 628,
    "G3 Spring_theta_nre": 1.544057222,
    "G3 Spring_pr_1_99_nre": 41,
    "G3 Spring_ss_nre": 96,
    "G3 Spring_dss_nre": 654
  },
  {
    "ID": 207,
    "KG Winter_theta_nre": -1.133976647,
    "KG Winter_pr_1_99_nre": 41,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 387,
    "KG Spring_theta_nre": -0.827502792,
    "KG Spring_pr_1_99_nre": 41,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 417,
    "G1 Fall_theta_nre": -0.548625661,
    "G1 Fall_pr_1_99_nre": 41,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 445,
    "G1 Winter_theta_nre": -0.306703074,
    "G1 Winter_pr_1_99_nre": 41,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 469,
    "G1 Spring_theta_nre": -0.070445582,
    "G1 Spring_pr_1_99_nre": 41,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 493,
    "G2 Fall_theta_nre": 0.204816173,
    "G2 Fall_pr_1_99_nre": 41,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 520,
    "G2 Winter_theta_nre": 0.514781237,
    "G2 Winter_pr_1_99_nre": 41,
    "G2 Winter_ss_nre": 97,
    "G2 Winter_dss_nre": 551,
    "G2 Spring_theta_nre": 0.722653659,
    "G2 Spring_pr_1_99_nre": 41,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 572,
    "G3 Fall_theta_nre": 1.000244788,
    "G3 Fall_pr_1_99_nre": 41,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 600,
    "G3 Winter_theta_nre": 1.280170743,
    "G3 Winter_pr_1_99_nre": 41,
    "G3 Winter_ss_nre": 97,
    "G3 Winter_dss_nre": 628,
    "G3 Spring_theta_nre": 1.545221708,
    "G3 Spring_pr_1_99_nre": 41,
    "G3 Spring_ss_nre": 96,
    "G3 Spring_dss_nre": 655
  },
  {
    "ID": 208,
    "KG Winter_theta_nre": -1.131568786,
    "KG Winter_pr_1_99_nre": 42,
    "KG Winter_ss_nre": 96,
    "KG Winter_dss_nre": 387,
    "KG Spring_theta_nre": -0.827096887,
    "KG Spring_pr_1_99_nre": 42,
    "KG Spring_ss_nre": 96,
    "KG Spring_dss_nre": 417,
    "G1 Fall_theta_nre": -0.545851922,
    "G1 Fall_pr_1_99_nre": 42,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 445,
    "G1 Winter_theta_nre": -0.306555308,
    "G1 Winter_pr_1_99_nre": 42,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 469,
    "G1 Spring_theta_nre": -0.066350927,
    "G1 Spring_pr_1_99_nre": 42,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 493,
    "G2 Fall_theta_nre": 0.207536579,
    "G2 Fall_pr_1_99_nre": 42,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 521,
    "G2 Winter_theta_nre": 0.517358551,
    "G2 Winter_pr_1_99_nre": 42,
    "G2 Winter_ss_nre": 97,
    "G2 Winter_dss_nre": 552,
    "G2 Spring_theta_nre": 0.724899514,
    "G2 Spring_pr_1_99_nre": 42,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 572,
    "G3 Fall_theta_nre": 1.003418466,
    "G3 Fall_pr_1_99_nre": 42,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 600,
    "G3 Winter_theta_nre": 1.280980138,
    "G3 Winter_pr_1_99_nre": 42,
    "G3 Winter_ss_nre": 97,
    "G3 Winter_dss_nre": 628,
    "G3 Spring_theta_nre": 1.55043718,
    "G3 Spring_pr_1_99_nre": 42,
    "G3 Spring_ss_nre": 96,
    "G3 Spring_dss_nre": 655
  },
  {
    "ID": 209,
    "KG Winter_theta_nre": -1.121962582,
    "KG Winter_pr_1_99_nre": 42,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 388,
    "KG Spring_theta_nre": -0.808648342,
    "KG Spring_pr_1_99_nre": 42,
    "KG Spring_ss_nre": 97,
    "KG Spring_dss_nre": 419,
    "G1 Fall_theta_nre": -0.545118168,
    "G1 Fall_pr_1_99_nre": 42,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 445,
    "G1 Winter_theta_nre": -0.30236986,
    "G1 Winter_pr_1_99_nre": 42,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 470,
    "G1 Spring_theta_nre": -0.066002871,
    "G1 Spring_pr_1_99_nre": 42,
    "G1 Spring_ss_nre": 95,
    "G1 Spring_dss_nre": 493,
    "G2 Fall_theta_nre": 0.21870285,
    "G2 Fall_pr_1_99_nre": 42,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 522,
    "G2 Winter_theta_nre": 0.517735522,
    "G2 Winter_pr_1_99_nre": 42,
    "G2 Winter_ss_nre": 97,
    "G2 Winter_dss_nre": 552,
    "G2 Spring_theta_nre": 0.733488245,
    "G2 Spring_pr_1_99_nre": 42,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 573,
    "G3 Fall_theta_nre": 1.003854755,
    "G3 Fall_pr_1_99_nre": 42,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 600,
    "G3 Winter_theta_nre": 1.29224663,
    "G3 Winter_pr_1_99_nre": 42,
    "G3 Winter_ss_nre": 97,
    "G3 Winter_dss_nre": 629,
    "G3 Spring_theta_nre": 1.553715107,
    "G3 Spring_pr_1_99_nre": 42,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 655
  },
  {
    "ID": 210,
    "KG Winter_theta_nre": -1.120758808,
    "KG Winter_pr_1_99_nre": 42,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 388,
    "KG Spring_theta_nre": -0.804905816,
    "KG Spring_pr_1_99_nre": 42,
    "KG Spring_ss_nre": 97,
    "KG Spring_dss_nre": 420,
    "G1 Fall_theta_nre": -0.537651513,
    "G1 Fall_pr_1_99_nre": 42,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 446,
    "G1 Winter_theta_nre": -0.301042027,
    "G1 Winter_pr_1_99_nre": 42,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 470,
    "G1 Spring_theta_nre": -0.063044625,
    "G1 Spring_pr_1_99_nre": 42,
    "G1 Spring_ss_nre": 96,
    "G1 Spring_dss_nre": 494,
    "G2 Fall_theta_nre": 0.222903955,
    "G2 Fall_pr_1_99_nre": 42,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 522,
    "G2 Winter_theta_nre": 0.523204495,
    "G2 Winter_pr_1_99_nre": 42,
    "G2 Winter_ss_nre": 97,
    "G2 Winter_dss_nre": 552,
    "G2 Spring_theta_nre": 0.734965998,
    "G2 Spring_pr_1_99_nre": 42,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 573,
    "G3 Fall_theta_nre": 1.011327614,
    "G3 Fall_pr_1_99_nre": 42,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 601,
    "G3 Winter_theta_nre": 1.299050209,
    "G3 Winter_pr_1_99_nre": 42,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 630,
    "G3 Spring_theta_nre": 1.555795513,
    "G3 Spring_pr_1_99_nre": 42,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 656
  },
  {
    "ID": 211,
    "KG Winter_theta_nre": -1.11236512,
    "KG Winter_pr_1_99_nre": 42,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 389,
    "KG Spring_theta_nre": -0.800168762,
    "KG Spring_pr_1_99_nre": 42,
    "KG Spring_ss_nre": 97,
    "KG Spring_dss_nre": 420,
    "G1 Fall_theta_nre": -0.53717163,
    "G1 Fall_pr_1_99_nre": 42,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 446,
    "G1 Winter_theta_nre": -0.300860411,
    "G1 Winter_pr_1_99_nre": 42,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 470,
    "G1 Spring_theta_nre": -0.061238146,
    "G1 Spring_pr_1_99_nre": 42,
    "G1 Spring_ss_nre": 96,
    "G1 Spring_dss_nre": 494,
    "G2 Fall_theta_nre": 0.224059718,
    "G2 Fall_pr_1_99_nre": 42,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 522,
    "G2 Winter_theta_nre": 0.525095121,
    "G2 Winter_pr_1_99_nre": 42,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 553,
    "G2 Spring_theta_nre": 0.735631981,
    "G2 Spring_pr_1_99_nre": 42,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 574,
    "G3 Fall_theta_nre": 1.018390735,
    "G3 Fall_pr_1_99_nre": 42,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 602,
    "G3 Winter_theta_nre": 1.302019193,
    "G3 Winter_pr_1_99_nre": 42,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 630,
    "G3 Spring_theta_nre": 1.559458263,
    "G3 Spring_pr_1_99_nre": 42,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 656
  },
  {
    "ID": 212,
    "KG Winter_theta_nre": -1.112332149,
    "KG Winter_pr_1_99_nre": 42,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 389,
    "KG Spring_theta_nre": -0.798233397,
    "KG Spring_pr_1_99_nre": 42,
    "KG Spring_ss_nre": 97,
    "KG Spring_dss_nre": 420,
    "G1 Fall_theta_nre": -0.53615758,
    "G1 Fall_pr_1_99_nre": 42,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 446,
    "G1 Winter_theta_nre": -0.283048681,
    "G1 Winter_pr_1_99_nre": 42,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 472,
    "G1 Spring_theta_nre": -0.037352122,
    "G1 Spring_pr_1_99_nre": 42,
    "G1 Spring_ss_nre": 96,
    "G1 Spring_dss_nre": 496,
    "G2 Fall_theta_nre": 0.231089663,
    "G2 Fall_pr_1_99_nre": 42,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 523,
    "G2 Winter_theta_nre": 0.52720196,
    "G2 Winter_pr_1_99_nre": 42,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 553,
    "G2 Spring_theta_nre": 0.739772648,
    "G2 Spring_pr_1_99_nre": 42,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 574,
    "G3 Fall_theta_nre": 1.030214286,
    "G3 Fall_pr_1_99_nre": 42,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 603,
    "G3 Winter_theta_nre": 1.30444818,
    "G3 Winter_pr_1_99_nre": 42,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 630,
    "G3 Spring_theta_nre": 1.560068032,
    "G3 Spring_pr_1_99_nre": 42,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 656
  },
  {
    "ID": 213,
    "KG Winter_theta_nre": -1.099310735,
    "KG Winter_pr_1_99_nre": 43,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 390,
    "KG Spring_theta_nre": -0.789207573,
    "KG Spring_pr_1_99_nre": 43,
    "KG Spring_ss_nre": 97,
    "KG Spring_dss_nre": 421,
    "G1 Fall_theta_nre": -0.532605249,
    "G1 Fall_pr_1_99_nre": 43,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 447,
    "G1 Winter_theta_nre": -0.279410189,
    "G1 Winter_pr_1_99_nre": 43,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 472,
    "G1 Spring_theta_nre": -0.030051671,
    "G1 Spring_pr_1_99_nre": 43,
    "G1 Spring_ss_nre": 96,
    "G1 Spring_dss_nre": 497,
    "G2 Fall_theta_nre": 0.231741532,
    "G2 Fall_pr_1_99_nre": 43,
    "G2 Fall_ss_nre": 97,
    "G2 Fall_dss_nre": 523,
    "G2 Winter_theta_nre": 0.527223515,
    "G2 Winter_pr_1_99_nre": 43,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 553,
    "G2 Spring_theta_nre": 0.747383409,
    "G2 Spring_pr_1_99_nre": 43,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 575,
    "G3 Fall_theta_nre": 1.034022525,
    "G3 Fall_pr_1_99_nre": 43,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 603,
    "G3 Winter_theta_nre": 1.306213554,
    "G3 Winter_pr_1_99_nre": 43,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 631,
    "G3 Spring_theta_nre": 1.567162908,
    "G3 Spring_pr_1_99_nre": 43,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 657
  },
  {
    "ID": 214,
    "KG Winter_theta_nre": -1.099236211,
    "KG Winter_pr_1_99_nre": 43,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 390,
    "KG Spring_theta_nre": -0.779456125,
    "KG Spring_pr_1_99_nre": 43,
    "KG Spring_ss_nre": 97,
    "KG Spring_dss_nre": 422,
    "G1 Fall_theta_nre": -0.529595975,
    "G1 Fall_pr_1_99_nre": 43,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 447,
    "G1 Winter_theta_nre": -0.27917565,
    "G1 Winter_pr_1_99_nre": 43,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 472,
    "G1 Spring_theta_nre": -0.024499892,
    "G1 Spring_pr_1_99_nre": 43,
    "G1 Spring_ss_nre": 96,
    "G1 Spring_dss_nre": 498,
    "G2 Fall_theta_nre": 0.243647181,
    "G2 Fall_pr_1_99_nre": 43,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 524,
    "G2 Winter_theta_nre": 0.530118076,
    "G2 Winter_pr_1_99_nre": 43,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 553,
    "G2 Spring_theta_nre": 0.751821068,
    "G2 Spring_pr_1_99_nre": 43,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 575,
    "G3 Fall_theta_nre": 1.038833909,
    "G3 Fall_pr_1_99_nre": 43,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 604,
    "G3 Winter_theta_nre": 1.308715711,
    "G3 Winter_pr_1_99_nre": 43,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 631,
    "G3 Spring_theta_nre": 1.576151737,
    "G3 Spring_pr_1_99_nre": 43,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 658
  },
  {
    "ID": 215,
    "KG Winter_theta_nre": -1.098671758,
    "KG Winter_pr_1_99_nre": 43,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 390,
    "KG Spring_theta_nre": -0.769900902,
    "KG Spring_pr_1_99_nre": 43,
    "KG Spring_ss_nre": 97,
    "KG Spring_dss_nre": 423,
    "G1 Fall_theta_nre": -0.528719843,
    "G1 Fall_pr_1_99_nre": 43,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 447,
    "G1 Winter_theta_nre": -0.27415681,
    "G1 Winter_pr_1_99_nre": 43,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 473,
    "G1 Spring_theta_nre": -0.019708628,
    "G1 Spring_pr_1_99_nre": 43,
    "G1 Spring_ss_nre": 96,
    "G1 Spring_dss_nre": 498,
    "G2 Fall_theta_nre": 0.244205328,
    "G2 Fall_pr_1_99_nre": 43,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 524,
    "G2 Winter_theta_nre": 0.531686604,
    "G2 Winter_pr_1_99_nre": 43,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 553,
    "G2 Spring_theta_nre": 0.753079257,
    "G2 Spring_pr_1_99_nre": 43,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 575,
    "G3 Fall_theta_nre": 1.039605121,
    "G3 Fall_pr_1_99_nre": 43,
    "G3 Fall_ss_nre": 97,
    "G3 Fall_dss_nre": 604,
    "G3 Winter_theta_nre": 1.309223115,
    "G3 Winter_pr_1_99_nre": 43,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 631,
    "G3 Spring_theta_nre": 1.577838808,
    "G3 Spring_pr_1_99_nre": 43,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 658
  },
  {
    "ID": 216,
    "KG Winter_theta_nre": -1.097920564,
    "KG Winter_pr_1_99_nre": 43,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 390,
    "KG Spring_theta_nre": -0.76123659,
    "KG Spring_pr_1_99_nre": 43,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 424,
    "G1 Fall_theta_nre": -0.52277614,
    "G1 Fall_pr_1_99_nre": 43,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 448,
    "G1 Winter_theta_nre": -0.271434341,
    "G1 Winter_pr_1_99_nre": 43,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 473,
    "G1 Spring_theta_nre": -0.01892925,
    "G1 Spring_pr_1_99_nre": 43,
    "G1 Spring_ss_nre": 96,
    "G1 Spring_dss_nre": 498,
    "G2 Fall_theta_nre": 0.244532638,
    "G2 Fall_pr_1_99_nre": 43,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 524,
    "G2 Winter_theta_nre": 0.537848417,
    "G2 Winter_pr_1_99_nre": 43,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 554,
    "G2 Spring_theta_nre": 0.755531671,
    "G2 Spring_pr_1_99_nre": 43,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 576,
    "G3 Fall_theta_nre": 1.059091091,
    "G3 Fall_pr_1_99_nre": 43,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 606,
    "G3 Winter_theta_nre": 1.318506954,
    "G3 Winter_pr_1_99_nre": 43,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 632,
    "G3 Spring_theta_nre": 1.578335054,
    "G3 Spring_pr_1_99_nre": 43,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 658
  },
  {
    "ID": 217,
    "KG Winter_theta_nre": -1.095733842,
    "KG Winter_pr_1_99_nre": 43,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 390,
    "KG Spring_theta_nre": -0.756067384,
    "KG Spring_pr_1_99_nre": 43,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 424,
    "G1 Fall_theta_nre": -0.52190312,
    "G1 Fall_pr_1_99_nre": 43,
    "G1 Fall_ss_nre": 97,
    "G1 Fall_dss_nre": 448,
    "G1 Winter_theta_nre": -0.264203976,
    "G1 Winter_pr_1_99_nre": 43,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 474,
    "G1 Spring_theta_nre": -0.012527662,
    "G1 Spring_pr_1_99_nre": 43,
    "G1 Spring_ss_nre": 96,
    "G1 Spring_dss_nre": 499,
    "G2 Fall_theta_nre": 0.247727644,
    "G2 Fall_pr_1_99_nre": 43,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 525,
    "G2 Winter_theta_nre": 0.539252602,
    "G2 Winter_pr_1_99_nre": 43,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 554,
    "G2 Spring_theta_nre": 0.757312077,
    "G2 Spring_pr_1_99_nre": 43,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 576,
    "G3 Fall_theta_nre": 1.059693798,
    "G3 Fall_pr_1_99_nre": 43,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 606,
    "G3 Winter_theta_nre": 1.320332765,
    "G3 Winter_pr_1_99_nre": 43,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 632,
    "G3 Spring_theta_nre": 1.579680492,
    "G3 Spring_pr_1_99_nre": 43,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 658
  },
  {
    "ID": 218,
    "KG Winter_theta_nre": -1.09481179,
    "KG Winter_pr_1_99_nre": 44,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 391,
    "KG Spring_theta_nre": -0.754333551,
    "KG Spring_pr_1_99_nre": 44,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 425,
    "G1 Fall_theta_nre": -0.520137666,
    "G1 Fall_pr_1_99_nre": 44,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 448,
    "G1 Winter_theta_nre": -0.261804268,
    "G1 Winter_pr_1_99_nre": 44,
    "G1 Winter_ss_nre": 97,
    "G1 Winter_dss_nre": 474,
    "G1 Spring_theta_nre": -0.007975542,
    "G1 Spring_pr_1_99_nre": 44,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 499,
    "G2 Fall_theta_nre": 0.263292968,
    "G2 Fall_pr_1_99_nre": 44,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 526,
    "G2 Winter_theta_nre": 0.539818291,
    "G2 Winter_pr_1_99_nre": 44,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 554,
    "G2 Spring_theta_nre": 0.775346246,
    "G2 Spring_pr_1_99_nre": 44,
    "G2 Spring_ss_nre": 97,
    "G2 Spring_dss_nre": 578,
    "G3 Fall_theta_nre": 1.06236616,
    "G3 Fall_pr_1_99_nre": 44,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 606,
    "G3 Winter_theta_nre": 1.322761763,
    "G3 Winter_pr_1_99_nre": 44,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 632,
    "G3 Spring_theta_nre": 1.582214237,
    "G3 Spring_pr_1_99_nre": 44,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 658
  },
  {
    "ID": 219,
    "KG Winter_theta_nre": -1.093966693,
    "KG Winter_pr_1_99_nre": 44,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 391,
    "KG Spring_theta_nre": -0.749495736,
    "KG Spring_pr_1_99_nre": 44,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 425,
    "G1 Fall_theta_nre": -0.518506908,
    "G1 Fall_pr_1_99_nre": 44,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 448,
    "G1 Winter_theta_nre": -0.25463454,
    "G1 Winter_pr_1_99_nre": 44,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 475,
    "G1 Spring_theta_nre": -0.005937532,
    "G1 Spring_pr_1_99_nre": 44,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 499,
    "G2 Fall_theta_nre": 0.270896707,
    "G2 Fall_pr_1_99_nre": 44,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 527,
    "G2 Winter_theta_nre": 0.540828947,
    "G2 Winter_pr_1_99_nre": 44,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 554,
    "G2 Spring_theta_nre": 0.778298387,
    "G2 Spring_pr_1_99_nre": 44,
    "G2 Spring_ss_nre": 98,
    "G2 Spring_dss_nre": 578,
    "G3 Fall_theta_nre": 1.072074016,
    "G3 Fall_pr_1_99_nre": 44,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 607,
    "G3 Winter_theta_nre": 1.324109404,
    "G3 Winter_pr_1_99_nre": 44,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 632,
    "G3 Spring_theta_nre": 1.59195845,
    "G3 Spring_pr_1_99_nre": 44,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 659
  },
  {
    "ID": 220,
    "KG Winter_theta_nre": -1.093569221,
    "KG Winter_pr_1_99_nre": 44,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 391,
    "KG Spring_theta_nre": -0.749340337,
    "KG Spring_pr_1_99_nre": 44,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 425,
    "G1 Fall_theta_nre": -0.510567445,
    "G1 Fall_pr_1_99_nre": 44,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 449,
    "G1 Winter_theta_nre": -0.253606524,
    "G1 Winter_pr_1_99_nre": 44,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 475,
    "G1 Spring_theta_nre": -0.003953924,
    "G1 Spring_pr_1_99_nre": 44,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 500,
    "G2 Fall_theta_nre": 0.271020222,
    "G2 Fall_pr_1_99_nre": 44,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 527,
    "G2 Winter_theta_nre": 0.542349596,
    "G2 Winter_pr_1_99_nre": 44,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 554,
    "G2 Spring_theta_nre": 0.793610934,
    "G2 Spring_pr_1_99_nre": 44,
    "G2 Spring_ss_nre": 98,
    "G2 Spring_dss_nre": 579,
    "G3 Fall_theta_nre": 1.072277985,
    "G3 Fall_pr_1_99_nre": 44,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 607,
    "G3 Winter_theta_nre": 1.327427023,
    "G3 Winter_pr_1_99_nre": 44,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 633,
    "G3 Spring_theta_nre": 1.597887496,
    "G3 Spring_pr_1_99_nre": 44,
    "G3 Spring_ss_nre": 97,
    "G3 Spring_dss_nre": 660
  },
  {
    "ID": 221,
    "KG Winter_theta_nre": -1.092200503,
    "KG Winter_pr_1_99_nre": 44,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 391,
    "KG Spring_theta_nre": -0.748390511,
    "KG Spring_pr_1_99_nre": 44,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 425,
    "G1 Fall_theta_nre": -0.503358239,
    "G1 Fall_pr_1_99_nre": 44,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 450,
    "G1 Winter_theta_nre": -0.231561167,
    "G1 Winter_pr_1_99_nre": 44,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 477,
    "G1 Spring_theta_nre": -0.003561771,
    "G1 Spring_pr_1_99_nre": 44,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 500,
    "G2 Fall_theta_nre": 0.27150094,
    "G2 Fall_pr_1_99_nre": 44,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 527,
    "G2 Winter_theta_nre": 0.55843927,
    "G2 Winter_pr_1_99_nre": 44,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 556,
    "G2 Spring_theta_nre": 0.799239222,
    "G2 Spring_pr_1_99_nre": 44,
    "G2 Spring_ss_nre": 98,
    "G2 Spring_dss_nre": 580,
    "G3 Fall_theta_nre": 1.073505495,
    "G3 Fall_pr_1_99_nre": 44,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 607,
    "G3 Winter_theta_nre": 1.330255874,
    "G3 Winter_pr_1_99_nre": 44,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 633,
    "G3 Spring_theta_nre": 1.607581475,
    "G3 Spring_pr_1_99_nre": 44,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 661
  },
  {
    "ID": 222,
    "KG Winter_theta_nre": -1.076049853,
    "KG Winter_pr_1_99_nre": 44,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 392,
    "KG Spring_theta_nre": -0.746925889,
    "KG Spring_pr_1_99_nre": 44,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 425,
    "G1 Fall_theta_nre": -0.503066756,
    "G1 Fall_pr_1_99_nre": 44,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 450,
    "G1 Winter_theta_nre": -0.229662452,
    "G1 Winter_pr_1_99_nre": 44,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 477,
    "G1 Spring_theta_nre": 0.008186509,
    "G1 Spring_pr_1_99_nre": 44,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 501,
    "G2 Fall_theta_nre": 0.275329459,
    "G2 Fall_pr_1_99_nre": 44,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 528,
    "G2 Winter_theta_nre": 0.559770553,
    "G2 Winter_pr_1_99_nre": 44,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 556,
    "G2 Spring_theta_nre": 0.805217291,
    "G2 Spring_pr_1_99_nre": 44,
    "G2 Spring_ss_nre": 98,
    "G2 Spring_dss_nre": 581,
    "G3 Fall_theta_nre": 1.074369387,
    "G3 Fall_pr_1_99_nre": 44,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 607,
    "G3 Winter_theta_nre": 1.331253834,
    "G3 Winter_pr_1_99_nre": 44,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 633,
    "G3 Spring_theta_nre": 1.608324324,
    "G3 Spring_pr_1_99_nre": 44,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 661
  },
  {
    "ID": 223,
    "KG Winter_theta_nre": -1.073416587,
    "KG Winter_pr_1_99_nre": 45,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 393,
    "KG Spring_theta_nre": -0.729563855,
    "KG Spring_pr_1_99_nre": 45,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 427,
    "G1 Fall_theta_nre": -0.493250067,
    "G1 Fall_pr_1_99_nre": 45,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 451,
    "G1 Winter_theta_nre": -0.229655824,
    "G1 Winter_pr_1_99_nre": 45,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 477,
    "G1 Spring_theta_nre": 0.008811342,
    "G1 Spring_pr_1_99_nre": 45,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 501,
    "G2 Fall_theta_nre": 0.278750187,
    "G2 Fall_pr_1_99_nre": 45,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 528,
    "G2 Winter_theta_nre": 0.562416657,
    "G2 Winter_pr_1_99_nre": 45,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 556,
    "G2 Spring_theta_nre": 0.816042287,
    "G2 Spring_pr_1_99_nre": 45,
    "G2 Spring_ss_nre": 98,
    "G2 Spring_dss_nre": 582,
    "G3 Fall_theta_nre": 1.07772639,
    "G3 Fall_pr_1_99_nre": 45,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 608,
    "G3 Winter_theta_nre": 1.331516557,
    "G3 Winter_pr_1_99_nre": 45,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 633,
    "G3 Spring_theta_nre": 1.609339313,
    "G3 Spring_pr_1_99_nre": 45,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 661
  },
  {
    "ID": 224,
    "KG Winter_theta_nre": -1.072555781,
    "KG Winter_pr_1_99_nre": 45,
    "KG Winter_ss_nre": 97,
    "KG Winter_dss_nre": 393,
    "KG Spring_theta_nre": -0.724296166,
    "KG Spring_pr_1_99_nre": 45,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 428,
    "G1 Fall_theta_nre": -0.492614282,
    "G1 Fall_pr_1_99_nre": 45,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 451,
    "G1 Winter_theta_nre": -0.224210324,
    "G1 Winter_pr_1_99_nre": 45,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 478,
    "G1 Spring_theta_nre": 0.009773788,
    "G1 Spring_pr_1_99_nre": 45,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 501,
    "G2 Fall_theta_nre": 0.286438504,
    "G2 Fall_pr_1_99_nre": 45,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 529,
    "G2 Winter_theta_nre": 0.563328484,
    "G2 Winter_pr_1_99_nre": 45,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 556,
    "G2 Spring_theta_nre": 0.834864612,
    "G2 Spring_pr_1_99_nre": 45,
    "G2 Spring_ss_nre": 98,
    "G2 Spring_dss_nre": 583,
    "G3 Fall_theta_nre": 1.077735827,
    "G3 Fall_pr_1_99_nre": 45,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 608,
    "G3 Winter_theta_nre": 1.33241528,
    "G3 Winter_pr_1_99_nre": 45,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 633,
    "G3 Spring_theta_nre": 1.609667469,
    "G3 Spring_pr_1_99_nre": 45,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 661
  },
  {
    "ID": 225,
    "KG Winter_theta_nre": -1.067561048,
    "KG Winter_pr_1_99_nre": 45,
    "KG Winter_ss_nre": 98,
    "KG Winter_dss_nre": 393,
    "KG Spring_theta_nre": -0.722965845,
    "KG Spring_pr_1_99_nre": 45,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 428,
    "G1 Fall_theta_nre": -0.488488323,
    "G1 Fall_pr_1_99_nre": 45,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 451,
    "G1 Winter_theta_nre": -0.22392454,
    "G1 Winter_pr_1_99_nre": 45,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 478,
    "G1 Spring_theta_nre": 0.013126999,
    "G1 Spring_pr_1_99_nre": 45,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 501,
    "G2 Fall_theta_nre": 0.293532836,
    "G2 Fall_pr_1_99_nre": 45,
    "G2 Fall_ss_nre": 98,
    "G2 Fall_dss_nre": 529,
    "G2 Winter_theta_nre": 0.571623706,
    "G2 Winter_pr_1_99_nre": 45,
    "G2 Winter_ss_nre": 98,
    "G2 Winter_dss_nre": 557,
    "G2 Spring_theta_nre": 0.836762441,
    "G2 Spring_pr_1_99_nre": 45,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 584,
    "G3 Fall_theta_nre": 1.080561015,
    "G3 Fall_pr_1_99_nre": 45,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 608,
    "G3 Winter_theta_nre": 1.335324245,
    "G3 Winter_pr_1_99_nre": 45,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 634,
    "G3 Spring_theta_nre": 1.61866228,
    "G3 Spring_pr_1_99_nre": 45,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 662
  },
  {
    "ID": 226,
    "KG Winter_theta_nre": -1.061337687,
    "KG Winter_pr_1_99_nre": 45,
    "KG Winter_ss_nre": 98,
    "KG Winter_dss_nre": 394,
    "KG Spring_theta_nre": -0.720292047,
    "KG Spring_pr_1_99_nre": 45,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 428,
    "G1 Fall_theta_nre": -0.484564335,
    "G1 Fall_pr_1_99_nre": 45,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 452,
    "G1 Winter_theta_nre": -0.221744026,
    "G1 Winter_pr_1_99_nre": 45,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 478,
    "G1 Spring_theta_nre": 0.025410846,
    "G1 Spring_pr_1_99_nre": 45,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 503,
    "G2 Fall_theta_nre": 0.311660496,
    "G2 Fall_pr_1_99_nre": 45,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 531,
    "G2 Winter_theta_nre": 0.582376896,
    "G2 Winter_pr_1_99_nre": 45,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 558,
    "G2 Spring_theta_nre": 0.837770757,
    "G2 Spring_pr_1_99_nre": 45,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 584,
    "G3 Fall_theta_nre": 1.092754832,
    "G3 Fall_pr_1_99_nre": 45,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 609,
    "G3 Winter_theta_nre": 1.335415969,
    "G3 Winter_pr_1_99_nre": 45,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 634,
    "G3 Spring_theta_nre": 1.620470467,
    "G3 Spring_pr_1_99_nre": 45,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 662
  },
  {
    "ID": 227,
    "KG Winter_theta_nre": -1.058860408,
    "KG Winter_pr_1_99_nre": 45,
    "KG Winter_ss_nre": 98,
    "KG Winter_dss_nre": 394,
    "KG Spring_theta_nre": -0.719323838,
    "KG Spring_pr_1_99_nre": 45,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 428,
    "G1 Fall_theta_nre": -0.479161948,
    "G1 Fall_pr_1_99_nre": 45,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 452,
    "G1 Winter_theta_nre": -0.216706616,
    "G1 Winter_pr_1_99_nre": 45,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 478,
    "G1 Spring_theta_nre": 0.029529744,
    "G1 Spring_pr_1_99_nre": 45,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 503,
    "G2 Fall_theta_nre": 0.315299401,
    "G2 Fall_pr_1_99_nre": 45,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 532,
    "G2 Winter_theta_nre": 0.582985376,
    "G2 Winter_pr_1_99_nre": 45,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 558,
    "G2 Spring_theta_nre": 0.841502989,
    "G2 Spring_pr_1_99_nre": 45,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 584,
    "G3 Fall_theta_nre": 1.097185845,
    "G3 Fall_pr_1_99_nre": 45,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 610,
    "G3 Winter_theta_nre": 1.336177204,
    "G3 Winter_pr_1_99_nre": 45,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 634,
    "G3 Spring_theta_nre": 1.625583587,
    "G3 Spring_pr_1_99_nre": 45,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 663
  },
  {
    "ID": 228,
    "KG Winter_theta_nre": -1.052531197,
    "KG Winter_pr_1_99_nre": 46,
    "KG Winter_ss_nre": 98,
    "KG Winter_dss_nre": 395,
    "KG Spring_theta_nre": -0.719278593,
    "KG Spring_pr_1_99_nre": 46,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 428,
    "G1 Fall_theta_nre": -0.479140459,
    "G1 Fall_pr_1_99_nre": 46,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 452,
    "G1 Winter_theta_nre": -0.211009997,
    "G1 Winter_pr_1_99_nre": 46,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 479,
    "G1 Spring_theta_nre": 0.037145508,
    "G1 Spring_pr_1_99_nre": 46,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 504,
    "G2 Fall_theta_nre": 0.324472684,
    "G2 Fall_pr_1_99_nre": 46,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 532,
    "G2 Winter_theta_nre": 0.594317623,
    "G2 Winter_pr_1_99_nre": 46,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 559,
    "G2 Spring_theta_nre": 0.849324627,
    "G2 Spring_pr_1_99_nre": 46,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 585,
    "G3 Fall_theta_nre": 1.100421276,
    "G3 Fall_pr_1_99_nre": 46,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 610,
    "G3 Winter_theta_nre": 1.337383847,
    "G3 Winter_pr_1_99_nre": 46,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 634,
    "G3 Spring_theta_nre": 1.628915254,
    "G3 Spring_pr_1_99_nre": 46,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 663
  },
  {
    "ID": 229,
    "KG Winter_theta_nre": -1.049043317,
    "KG Winter_pr_1_99_nre": 46,
    "KG Winter_ss_nre": 98,
    "KG Winter_dss_nre": 395,
    "KG Spring_theta_nre": -0.715648424,
    "KG Spring_pr_1_99_nre": 46,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 428,
    "G1 Fall_theta_nre": -0.478535836,
    "G1 Fall_pr_1_99_nre": 46,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 452,
    "G1 Winter_theta_nre": -0.210701594,
    "G1 Winter_pr_1_99_nre": 46,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 479,
    "G1 Spring_theta_nre": 0.039385628,
    "G1 Spring_pr_1_99_nre": 46,
    "G1 Spring_ss_nre": 97,
    "G1 Spring_dss_nre": 504,
    "G2 Fall_theta_nre": 0.329640821,
    "G2 Fall_pr_1_99_nre": 46,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 533,
    "G2 Winter_theta_nre": 0.595201307,
    "G2 Winter_pr_1_99_nre": 46,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 560,
    "G2 Spring_theta_nre": 0.852395399,
    "G2 Spring_pr_1_99_nre": 46,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 585,
    "G3 Fall_theta_nre": 1.104934978,
    "G3 Fall_pr_1_99_nre": 46,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 610,
    "G3 Winter_theta_nre": 1.337465084,
    "G3 Winter_pr_1_99_nre": 46,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 634,
    "G3 Spring_theta_nre": 1.631175155,
    "G3 Spring_pr_1_99_nre": 46,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 663
  },
  {
    "ID": 230,
    "KG Winter_theta_nre": -1.041082143,
    "KG Winter_pr_1_99_nre": 46,
    "KG Winter_ss_nre": 98,
    "KG Winter_dss_nre": 396,
    "KG Spring_theta_nre": -0.714313117,
    "KG Spring_pr_1_99_nre": 46,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 429,
    "G1 Fall_theta_nre": -0.469458097,
    "G1 Fall_pr_1_99_nre": 46,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 453,
    "G1 Winter_theta_nre": -0.19993967,
    "G1 Winter_pr_1_99_nre": 46,
    "G1 Winter_ss_nre": 98,
    "G1 Winter_dss_nre": 480,
    "G1 Spring_theta_nre": 0.043816704,
    "G1 Spring_pr_1_99_nre": 46,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 504,
    "G2 Fall_theta_nre": 0.336273721,
    "G2 Fall_pr_1_99_nre": 46,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 534,
    "G2 Winter_theta_nre": 0.597128441,
    "G2 Winter_pr_1_99_nre": 46,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 560,
    "G2 Spring_theta_nre": 0.853592103,
    "G2 Spring_pr_1_99_nre": 46,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 585,
    "G3 Fall_theta_nre": 1.108415348,
    "G3 Fall_pr_1_99_nre": 46,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 611,
    "G3 Winter_theta_nre": 1.342562112,
    "G3 Winter_pr_1_99_nre": 46,
    "G3 Winter_ss_nre": 98,
    "G3 Winter_dss_nre": 634,
    "G3 Spring_theta_nre": 1.633247928,
    "G3 Spring_pr_1_99_nre": 46,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 663
  },
  {
    "ID": 231,
    "KG Winter_theta_nre": -1.028396394,
    "KG Winter_pr_1_99_nre": 46,
    "KG Winter_ss_nre": 98,
    "KG Winter_dss_nre": 397,
    "KG Spring_theta_nre": -0.710935504,
    "KG Spring_pr_1_99_nre": 46,
    "KG Spring_ss_nre": 98,
    "KG Spring_dss_nre": 429,
    "G1 Fall_theta_nre": -0.466618615,
    "G1 Fall_pr_1_99_nre": 46,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 453,
    "G1 Winter_theta_nre": -0.197686266,
    "G1 Winter_pr_1_99_nre": 46,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 480,
    "G1 Spring_theta_nre": 0.045186889,
    "G1 Spring_pr_1_99_nre": 46,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 505,
    "G2 Fall_theta_nre": 0.336765782,
    "G2 Fall_pr_1_99_nre": 46,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 534,
    "G2 Winter_theta_nre": 0.598126324,
    "G2 Winter_pr_1_99_nre": 46,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 560,
    "G2 Spring_theta_nre": 0.854882338,
    "G2 Spring_pr_1_99_nre": 46,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 585,
    "G3 Fall_theta_nre": 1.110349544,
    "G3 Fall_pr_1_99_nre": 46,
    "G3 Fall_ss_nre": 98,
    "G3 Fall_dss_nre": 611,
    "G3 Winter_theta_nre": 1.346242133,
    "G3 Winter_pr_1_99_nre": 46,
    "G3 Winter_ss_nre": 99,
    "G3 Winter_dss_nre": 635,
    "G3 Spring_theta_nre": 1.637350804,
    "G3 Spring_pr_1_99_nre": 46,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 664
  },
  {
    "ID": 232,
    "KG Winter_theta_nre": -1.027418464,
    "KG Winter_pr_1_99_nre": 46,
    "KG Winter_ss_nre": 98,
    "KG Winter_dss_nre": 397,
    "KG Spring_theta_nre": -0.704957813,
    "KG Spring_pr_1_99_nre": 46,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 430,
    "G1 Fall_theta_nre": -0.465824417,
    "G1 Fall_pr_1_99_nre": 46,
    "G1 Fall_ss_nre": 98,
    "G1 Fall_dss_nre": 453,
    "G1 Winter_theta_nre": -0.197373836,
    "G1 Winter_pr_1_99_nre": 46,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 480,
    "G1 Spring_theta_nre": 0.04811973,
    "G1 Spring_pr_1_99_nre": 46,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 505,
    "G2 Fall_theta_nre": 0.337460849,
    "G2 Fall_pr_1_99_nre": 46,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 534,
    "G2 Winter_theta_nre": 0.602456507,
    "G2 Winter_pr_1_99_nre": 46,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 560,
    "G2 Spring_theta_nre": 0.855334943,
    "G2 Spring_pr_1_99_nre": 46,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 586,
    "G3 Fall_theta_nre": 1.112066498,
    "G3 Fall_pr_1_99_nre": 46,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 611,
    "G3 Winter_theta_nre": 1.355319913,
    "G3 Winter_pr_1_99_nre": 46,
    "G3 Winter_ss_nre": 99,
    "G3 Winter_dss_nre": 636,
    "G3 Spring_theta_nre": 1.639685696,
    "G3 Spring_pr_1_99_nre": 46,
    "G3 Spring_ss_nre": 98,
    "G3 Spring_dss_nre": 664
  },
  {
    "ID": 233,
    "KG Winter_theta_nre": -1.024278162,
    "KG Winter_pr_1_99_nre": 47,
    "KG Winter_ss_nre": 98,
    "KG Winter_dss_nre": 398,
    "KG Spring_theta_nre": -0.70121983,
    "KG Spring_pr_1_99_nre": 47,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 430,
    "G1 Fall_theta_nre": -0.461607291,
    "G1 Fall_pr_1_99_nre": 47,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 454,
    "G1 Winter_theta_nre": -0.192790648,
    "G1 Winter_pr_1_99_nre": 47,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 481,
    "G1 Spring_theta_nre": 0.059948287,
    "G1 Spring_pr_1_99_nre": 47,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 506,
    "G2 Fall_theta_nre": 0.341717369,
    "G2 Fall_pr_1_99_nre": 47,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 534,
    "G2 Winter_theta_nre": 0.604666847,
    "G2 Winter_pr_1_99_nre": 47,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 560,
    "G2 Spring_theta_nre": 0.855685453,
    "G2 Spring_pr_1_99_nre": 47,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 586,
    "G3 Fall_theta_nre": 1.112539567,
    "G3 Fall_pr_1_99_nre": 47,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 611,
    "G3 Winter_theta_nre": 1.358773792,
    "G3 Winter_pr_1_99_nre": 47,
    "G3 Winter_ss_nre": 99,
    "G3 Winter_dss_nre": 636,
    "G3 Spring_theta_nre": 1.644896223,
    "G3 Spring_pr_1_99_nre": 47,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 664
  },
  {
    "ID": 234,
    "KG Winter_theta_nre": -1.020920046,
    "KG Winter_pr_1_99_nre": 47,
    "KG Winter_ss_nre": 98,
    "KG Winter_dss_nre": 398,
    "KG Spring_theta_nre": -0.689687192,
    "KG Spring_pr_1_99_nre": 47,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 431,
    "G1 Fall_theta_nre": -0.453879348,
    "G1 Fall_pr_1_99_nre": 47,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 455,
    "G1 Winter_theta_nre": -0.191249771,
    "G1 Winter_pr_1_99_nre": 47,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 481,
    "G1 Spring_theta_nre": 0.066707545,
    "G1 Spring_pr_1_99_nre": 47,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 507,
    "G2 Fall_theta_nre": 0.342525606,
    "G2 Fall_pr_1_99_nre": 47,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 534,
    "G2 Winter_theta_nre": 0.605988848,
    "G2 Winter_pr_1_99_nre": 47,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 561,
    "G2 Spring_theta_nre": 0.859487995,
    "G2 Spring_pr_1_99_nre": 47,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 586,
    "G3 Fall_theta_nre": 1.114928954,
    "G3 Fall_pr_1_99_nre": 47,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 611,
    "G3 Winter_theta_nre": 1.35891487,
    "G3 Winter_pr_1_99_nre": 47,
    "G3 Winter_ss_nre": 99,
    "G3 Winter_dss_nre": 636,
    "G3 Spring_theta_nre": 1.649428206,
    "G3 Spring_pr_1_99_nre": 47,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 665
  },
  {
    "ID": 235,
    "KG Winter_theta_nre": -1.013341862,
    "KG Winter_pr_1_99_nre": 47,
    "KG Winter_ss_nre": 99,
    "KG Winter_dss_nre": 399,
    "KG Spring_theta_nre": -0.688957743,
    "KG Spring_pr_1_99_nre": 47,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 431,
    "G1 Fall_theta_nre": -0.45370657,
    "G1 Fall_pr_1_99_nre": 47,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 455,
    "G1 Winter_theta_nre": -0.181332392,
    "G1 Winter_pr_1_99_nre": 47,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 482,
    "G1 Spring_theta_nre": 0.0732804,
    "G1 Spring_pr_1_99_nre": 47,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 507,
    "G2 Fall_theta_nre": 0.343711533,
    "G2 Fall_pr_1_99_nre": 47,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 534,
    "G2 Winter_theta_nre": 0.608658149,
    "G2 Winter_pr_1_99_nre": 47,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 561,
    "G2 Spring_theta_nre": 0.86869805,
    "G2 Spring_pr_1_99_nre": 47,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 587,
    "G3 Fall_theta_nre": 1.11698899,
    "G3 Fall_pr_1_99_nre": 47,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 612,
    "G3 Winter_theta_nre": 1.367145353,
    "G3 Winter_pr_1_99_nre": 47,
    "G3 Winter_ss_nre": 99,
    "G3 Winter_dss_nre": 637,
    "G3 Spring_theta_nre": 1.650225759,
    "G3 Spring_pr_1_99_nre": 47,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 665
  },
  {
    "ID": 236,
    "KG Winter_theta_nre": -1.009087483,
    "KG Winter_pr_1_99_nre": 47,
    "KG Winter_ss_nre": 99,
    "KG Winter_dss_nre": 399,
    "KG Spring_theta_nre": -0.684869486,
    "KG Spring_pr_1_99_nre": 47,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 432,
    "G1 Fall_theta_nre": -0.453102007,
    "G1 Fall_pr_1_99_nre": 47,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 455,
    "G1 Winter_theta_nre": -0.180859648,
    "G1 Winter_pr_1_99_nre": 47,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 482,
    "G1 Spring_theta_nre": 0.074254898,
    "G1 Spring_pr_1_99_nre": 47,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 507,
    "G2 Fall_theta_nre": 0.344543569,
    "G2 Fall_pr_1_99_nre": 47,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 534,
    "G2 Winter_theta_nre": 0.610796238,
    "G2 Winter_pr_1_99_nre": 47,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 561,
    "G2 Spring_theta_nre": 0.869093489,
    "G2 Spring_pr_1_99_nre": 47,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 587,
    "G3 Fall_theta_nre": 1.128619172,
    "G3 Fall_pr_1_99_nre": 47,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 613,
    "G3 Winter_theta_nre": 1.367762185,
    "G3 Winter_pr_1_99_nre": 47,
    "G3 Winter_ss_nre": 99,
    "G3 Winter_dss_nre": 637,
    "G3 Spring_theta_nre": 1.650968044,
    "G3 Spring_pr_1_99_nre": 47,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 665
  },
  {
    "ID": 237,
    "KG Winter_theta_nre": -1.004838099,
    "KG Winter_pr_1_99_nre": 47,
    "KG Winter_ss_nre": 99,
    "KG Winter_dss_nre": 400,
    "KG Spring_theta_nre": -0.67319561,
    "KG Spring_pr_1_99_nre": 47,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 433,
    "G1 Fall_theta_nre": -0.452044868,
    "G1 Fall_pr_1_99_nre": 47,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 455,
    "G1 Winter_theta_nre": -0.170279458,
    "G1 Winter_pr_1_99_nre": 47,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 483,
    "G1 Spring_theta_nre": 0.074444875,
    "G1 Spring_pr_1_99_nre": 47,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 507,
    "G2 Fall_theta_nre": 0.34733867,
    "G2 Fall_pr_1_99_nre": 47,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 535,
    "G2 Winter_theta_nre": 0.61335954,
    "G2 Winter_pr_1_99_nre": 47,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 561,
    "G2 Spring_theta_nre": 0.874040266,
    "G2 Spring_pr_1_99_nre": 47,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 587,
    "G3 Fall_theta_nre": 1.135329889,
    "G3 Fall_pr_1_99_nre": 47,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 614,
    "G3 Winter_theta_nre": 1.3869005,
    "G3 Winter_pr_1_99_nre": 47,
    "G3 Winter_ss_nre": 99,
    "G3 Winter_dss_nre": 639,
    "G3 Spring_theta_nre": 1.651278569,
    "G3 Spring_pr_1_99_nre": 47,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 665
  },
  {
    "ID": 238,
    "KG Winter_theta_nre": -1.001349067,
    "KG Winter_pr_1_99_nre": 48,
    "KG Winter_ss_nre": 99,
    "KG Winter_dss_nre": 400,
    "KG Spring_theta_nre": -0.671631646,
    "KG Spring_pr_1_99_nre": 48,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 433,
    "G1 Fall_theta_nre": -0.450139278,
    "G1 Fall_pr_1_99_nre": 48,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 455,
    "G1 Winter_theta_nre": -0.167754175,
    "G1 Winter_pr_1_99_nre": 48,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 483,
    "G1 Spring_theta_nre": 0.076621714,
    "G1 Spring_pr_1_99_nre": 48,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 508,
    "G2 Fall_theta_nre": 0.3479308,
    "G2 Fall_pr_1_99_nre": 48,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 535,
    "G2 Winter_theta_nre": 0.614452644,
    "G2 Winter_pr_1_99_nre": 48,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 561,
    "G2 Spring_theta_nre": 0.875161173,
    "G2 Spring_pr_1_99_nre": 48,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 588,
    "G3 Fall_theta_nre": 1.138358865,
    "G3 Fall_pr_1_99_nre": 48,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 614,
    "G3 Winter_theta_nre": 1.387099245,
    "G3 Winter_pr_1_99_nre": 48,
    "G3 Winter_ss_nre": 99,
    "G3 Winter_dss_nre": 639,
    "G3 Spring_theta_nre": 1.651653876,
    "G3 Spring_pr_1_99_nre": 48,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 665
  },
  {
    "ID": 239,
    "KG Winter_theta_nre": -0.98202082,
    "KG Winter_pr_1_99_nre": 48,
    "KG Winter_ss_nre": 99,
    "KG Winter_dss_nre": 402,
    "KG Spring_theta_nre": -0.671253077,
    "KG Spring_pr_1_99_nre": 48,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 433,
    "G1 Fall_theta_nre": -0.449796003,
    "G1 Fall_pr_1_99_nre": 48,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 455,
    "G1 Winter_theta_nre": -0.159662891,
    "G1 Winter_pr_1_99_nre": 48,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 484,
    "G1 Spring_theta_nre": 0.083246586,
    "G1 Spring_pr_1_99_nre": 48,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 508,
    "G2 Fall_theta_nre": 0.349833904,
    "G2 Fall_pr_1_99_nre": 48,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 535,
    "G2 Winter_theta_nre": 0.617064095,
    "G2 Winter_pr_1_99_nre": 48,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 562,
    "G2 Spring_theta_nre": 0.878864852,
    "G2 Spring_pr_1_99_nre": 48,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 588,
    "G3 Fall_theta_nre": 1.146109249,
    "G3 Fall_pr_1_99_nre": 48,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 615,
    "G3 Winter_theta_nre": 1.390623265,
    "G3 Winter_pr_1_99_nre": 48,
    "G3 Winter_ss_nre": 99,
    "G3 Winter_dss_nre": 639,
    "G3 Spring_theta_nre": 1.654770752,
    "G3 Spring_pr_1_99_nre": 48,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 665
  },
  {
    "ID": 240,
    "KG Winter_theta_nre": -0.970931101,
    "KG Winter_pr_1_99_nre": 48,
    "KG Winter_ss_nre": 99,
    "KG Winter_dss_nre": 403,
    "KG Spring_theta_nre": -0.671090442,
    "KG Spring_pr_1_99_nre": 48,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 433,
    "G1 Fall_theta_nre": -0.439292018,
    "G1 Fall_pr_1_99_nre": 48,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 456,
    "G1 Winter_theta_nre": -0.157982715,
    "G1 Winter_pr_1_99_nre": 48,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 484,
    "G1 Spring_theta_nre": 0.091379091,
    "G1 Spring_pr_1_99_nre": 48,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 509,
    "G2 Fall_theta_nre": 0.350493551,
    "G2 Fall_pr_1_99_nre": 48,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 535,
    "G2 Winter_theta_nre": 0.623446624,
    "G2 Winter_pr_1_99_nre": 48,
    "G2 Winter_ss_nre": 99,
    "G2 Winter_dss_nre": 562,
    "G2 Spring_theta_nre": 0.879434757,
    "G2 Spring_pr_1_99_nre": 48,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 588,
    "G3 Fall_theta_nre": 1.149167524,
    "G3 Fall_pr_1_99_nre": 48,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 615,
    "G3 Winter_theta_nre": 1.4006722,
    "G3 Winter_pr_1_99_nre": 48,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 640,
    "G3 Spring_theta_nre": 1.655452224,
    "G3 Spring_pr_1_99_nre": 48,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 666
  },
  {
    "ID": 241,
    "KG Winter_theta_nre": -0.957881927,
    "KG Winter_pr_1_99_nre": 48,
    "KG Winter_ss_nre": 100,
    "KG Winter_dss_nre": 404,
    "KG Spring_theta_nre": -0.66244215,
    "KG Spring_pr_1_99_nre": 48,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 434,
    "G1 Fall_theta_nre": -0.433073539,
    "G1 Fall_pr_1_99_nre": 48,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 457,
    "G1 Winter_theta_nre": -0.154295912,
    "G1 Winter_pr_1_99_nre": 48,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 485,
    "G1 Spring_theta_nre": 0.091679203,
    "G1 Spring_pr_1_99_nre": 48,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 509,
    "G2 Fall_theta_nre": 0.351123876,
    "G2 Fall_pr_1_99_nre": 48,
    "G2 Fall_ss_nre": 99,
    "G2 Fall_dss_nre": 535,
    "G2 Winter_theta_nre": 0.629320163,
    "G2 Winter_pr_1_99_nre": 48,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 563,
    "G2 Spring_theta_nre": 0.884179348,
    "G2 Spring_pr_1_99_nre": 48,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 588,
    "G3 Fall_theta_nre": 1.153752,
    "G3 Fall_pr_1_99_nre": 48,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 615,
    "G3 Winter_theta_nre": 1.404164878,
    "G3 Winter_pr_1_99_nre": 48,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 640,
    "G3 Spring_theta_nre": 1.658781386,
    "G3 Spring_pr_1_99_nre": 48,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 666
  },
  {
    "ID": 242,
    "KG Winter_theta_nre": -0.941498344,
    "KG Winter_pr_1_99_nre": 48,
    "KG Winter_ss_nre": 100,
    "KG Winter_dss_nre": 406,
    "KG Spring_theta_nre": -0.661136339,
    "KG Spring_pr_1_99_nre": 48,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 434,
    "G1 Fall_theta_nre": -0.423135895,
    "G1 Fall_pr_1_99_nre": 48,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 458,
    "G1 Winter_theta_nre": -0.146882004,
    "G1 Winter_pr_1_99_nre": 48,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 485,
    "G1 Spring_theta_nre": 0.093062816,
    "G1 Spring_pr_1_99_nre": 48,
    "G1 Spring_ss_nre": 98,
    "G1 Spring_dss_nre": 509,
    "G2 Fall_theta_nre": 0.35292306,
    "G2 Fall_pr_1_99_nre": 48,
    "G2 Fall_ss_nre": 100,
    "G2 Fall_dss_nre": 535,
    "G2 Winter_theta_nre": 0.633150667,
    "G2 Winter_pr_1_99_nre": 48,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 563,
    "G2 Spring_theta_nre": 0.884551837,
    "G2 Spring_pr_1_99_nre": 48,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 588,
    "G3 Fall_theta_nre": 1.15438069,
    "G3 Fall_pr_1_99_nre": 48,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 615,
    "G3 Winter_theta_nre": 1.404297424,
    "G3 Winter_pr_1_99_nre": 48,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 640,
    "G3 Spring_theta_nre": 1.674721807,
    "G3 Spring_pr_1_99_nre": 48,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 667
  },
  {
    "ID": 243,
    "KG Winter_theta_nre": -0.930905544,
    "KG Winter_pr_1_99_nre": 49,
    "KG Winter_ss_nre": 100,
    "KG Winter_dss_nre": 407,
    "KG Spring_theta_nre": -0.658204393,
    "KG Spring_pr_1_99_nre": 49,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 434,
    "G1 Fall_theta_nre": -0.407032133,
    "G1 Fall_pr_1_99_nre": 49,
    "G1 Fall_ss_nre": 99,
    "G1 Fall_dss_nre": 459,
    "G1 Winter_theta_nre": -0.144811887,
    "G1 Winter_pr_1_99_nre": 49,
    "G1 Winter_ss_nre": 99,
    "G1 Winter_dss_nre": 486,
    "G1 Spring_theta_nre": 0.100974007,
    "G1 Spring_pr_1_99_nre": 49,
    "G1 Spring_ss_nre": 99,
    "G1 Spring_dss_nre": 510,
    "G2 Fall_theta_nre": 0.362557573,
    "G2 Fall_pr_1_99_nre": 49,
    "G2 Fall_ss_nre": 100,
    "G2 Fall_dss_nre": 536,
    "G2 Winter_theta_nre": 0.641115328,
    "G2 Winter_pr_1_99_nre": 49,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 564,
    "G2 Spring_theta_nre": 0.88920377,
    "G2 Spring_pr_1_99_nre": 49,
    "G2 Spring_ss_nre": 99,
    "G2 Spring_dss_nre": 589,
    "G3 Fall_theta_nre": 1.155666844,
    "G3 Fall_pr_1_99_nre": 49,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 616,
    "G3 Winter_theta_nre": 1.412496191,
    "G3 Winter_pr_1_99_nre": 49,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 641,
    "G3 Spring_theta_nre": 1.677727781,
    "G3 Spring_pr_1_99_nre": 49,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 668
  },
  {
    "ID": 244,
    "KG Winter_theta_nre": -0.925377594,
    "KG Winter_pr_1_99_nre": 49,
    "KG Winter_ss_nre": 100,
    "KG Winter_dss_nre": 407,
    "KG Spring_theta_nre": -0.655745287,
    "KG Spring_pr_1_99_nre": 49,
    "KG Spring_ss_nre": 99,
    "KG Spring_dss_nre": 434,
    "G1 Fall_theta_nre": -0.399739968,
    "G1 Fall_pr_1_99_nre": 49,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 460,
    "G1 Winter_theta_nre": -0.141088324,
    "G1 Winter_pr_1_99_nre": 49,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 486,
    "G1 Spring_theta_nre": 0.111240793,
    "G1 Spring_pr_1_99_nre": 49,
    "G1 Spring_ss_nre": 99,
    "G1 Spring_dss_nre": 511,
    "G2 Fall_theta_nre": 0.36949494,
    "G2 Fall_pr_1_99_nre": 49,
    "G2 Fall_ss_nre": 100,
    "G2 Fall_dss_nre": 537,
    "G2 Winter_theta_nre": 0.643267111,
    "G2 Winter_pr_1_99_nre": 49,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 564,
    "G2 Spring_theta_nre": 0.895943056,
    "G2 Spring_pr_1_99_nre": 49,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 590,
    "G3 Fall_theta_nre": 1.158140049,
    "G3 Fall_pr_1_99_nre": 49,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 616,
    "G3 Winter_theta_nre": 1.416949026,
    "G3 Winter_pr_1_99_nre": 49,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 642,
    "G3 Spring_theta_nre": 1.679429605,
    "G3 Spring_pr_1_99_nre": 49,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 668
  },
  {
    "ID": 245,
    "KG Winter_theta_nre": -0.918338998,
    "KG Winter_pr_1_99_nre": 49,
    "KG Winter_ss_nre": 100,
    "KG Winter_dss_nre": 408,
    "KG Spring_theta_nre": -0.645081077,
    "KG Spring_pr_1_99_nre": 49,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 435,
    "G1 Fall_theta_nre": -0.390395362,
    "G1 Fall_pr_1_99_nre": 49,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 461,
    "G1 Winter_theta_nre": -0.131918491,
    "G1 Winter_pr_1_99_nre": 49,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 487,
    "G1 Spring_theta_nre": 0.120535642,
    "G1 Spring_pr_1_99_nre": 49,
    "G1 Spring_ss_nre": 99,
    "G1 Spring_dss_nre": 512,
    "G2 Fall_theta_nre": 0.370760109,
    "G2 Fall_pr_1_99_nre": 49,
    "G2 Fall_ss_nre": 100,
    "G2 Fall_dss_nre": 537,
    "G2 Winter_theta_nre": 0.644208641,
    "G2 Winter_pr_1_99_nre": 49,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 564,
    "G2 Spring_theta_nre": 0.899509147,
    "G2 Spring_pr_1_99_nre": 49,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 590,
    "G3 Fall_theta_nre": 1.161205844,
    "G3 Fall_pr_1_99_nre": 49,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 616,
    "G3 Winter_theta_nre": 1.421198254,
    "G3 Winter_pr_1_99_nre": 49,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 642,
    "G3 Spring_theta_nre": 1.68117068,
    "G3 Spring_pr_1_99_nre": 49,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 668
  },
  {
    "ID": 246,
    "KG Winter_theta_nre": -0.902687345,
    "KG Winter_pr_1_99_nre": 49,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 410,
    "KG Spring_theta_nre": -0.643486084,
    "KG Spring_pr_1_99_nre": 49,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 436,
    "G1 Fall_theta_nre": -0.388629974,
    "G1 Fall_pr_1_99_nre": 49,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 461,
    "G1 Winter_theta_nre": -0.122514839,
    "G1 Winter_pr_1_99_nre": 49,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 488,
    "G1 Spring_theta_nre": 0.129693728,
    "G1 Spring_pr_1_99_nre": 49,
    "G1 Spring_ss_nre": 99,
    "G1 Spring_dss_nre": 513,
    "G2 Fall_theta_nre": 0.375256983,
    "G2 Fall_pr_1_99_nre": 49,
    "G2 Fall_ss_nre": 100,
    "G2 Fall_dss_nre": 538,
    "G2 Winter_theta_nre": 0.645140512,
    "G2 Winter_pr_1_99_nre": 49,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 565,
    "G2 Spring_theta_nre": 0.909041224,
    "G2 Spring_pr_1_99_nre": 49,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 591,
    "G3 Fall_theta_nre": 1.16370293,
    "G3 Fall_pr_1_99_nre": 49,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 616,
    "G3 Winter_theta_nre": 1.424163455,
    "G3 Winter_pr_1_99_nre": 49,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 642,
    "G3 Spring_theta_nre": 1.687252403,
    "G3 Spring_pr_1_99_nre": 49,
    "G3 Spring_ss_nre": 99,
    "G3 Spring_dss_nre": 669
  },
  {
    "ID": 247,
    "KG Winter_theta_nre": -0.901113137,
    "KG Winter_pr_1_99_nre": 49,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 410,
    "KG Spring_theta_nre": -0.640982388,
    "KG Spring_pr_1_99_nre": 49,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 436,
    "G1 Fall_theta_nre": -0.386485738,
    "G1 Fall_pr_1_99_nre": 49,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 461,
    "G1 Winter_theta_nre": -0.122155299,
    "G1 Winter_pr_1_99_nre": 49,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 488,
    "G1 Spring_theta_nre": 0.130856784,
    "G1 Spring_pr_1_99_nre": 49,
    "G1 Spring_ss_nre": 99,
    "G1 Spring_dss_nre": 513,
    "G2 Fall_theta_nre": 0.376499695,
    "G2 Fall_pr_1_99_nre": 49,
    "G2 Fall_ss_nre": 100,
    "G2 Fall_dss_nre": 538,
    "G2 Winter_theta_nre": 0.649702992,
    "G2 Winter_pr_1_99_nre": 49,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 565,
    "G2 Spring_theta_nre": 0.912308897,
    "G2 Spring_pr_1_99_nre": 49,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 591,
    "G3 Fall_theta_nre": 1.165624294,
    "G3 Fall_pr_1_99_nre": 49,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 617,
    "G3 Winter_theta_nre": 1.427245624,
    "G3 Winter_pr_1_99_nre": 49,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 643,
    "G3 Spring_theta_nre": 1.692330196,
    "G3 Spring_pr_1_99_nre": 49,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 669
  },
  {
    "ID": 248,
    "KG Winter_theta_nre": -0.90037758,
    "KG Winter_pr_1_99_nre": 50,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 410,
    "KG Spring_theta_nre": -0.640381068,
    "KG Spring_pr_1_99_nre": 50,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 436,
    "G1 Fall_theta_nre": -0.385789671,
    "G1 Fall_pr_1_99_nre": 50,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 461,
    "G1 Winter_theta_nre": -0.118543464,
    "G1 Winter_pr_1_99_nre": 50,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 488,
    "G1 Spring_theta_nre": 0.137671433,
    "G1 Spring_pr_1_99_nre": 50,
    "G1 Spring_ss_nre": 99,
    "G1 Spring_dss_nre": 514,
    "G2 Fall_theta_nre": 0.39030618,
    "G2 Fall_pr_1_99_nre": 50,
    "G2 Fall_ss_nre": 100,
    "G2 Fall_dss_nre": 539,
    "G2 Winter_theta_nre": 0.650156634,
    "G2 Winter_pr_1_99_nre": 50,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 565,
    "G2 Spring_theta_nre": 0.918643693,
    "G2 Spring_pr_1_99_nre": 50,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 592,
    "G3 Fall_theta_nre": 1.172305127,
    "G3 Fall_pr_1_99_nre": 50,
    "G3 Fall_ss_nre": 99,
    "G3 Fall_dss_nre": 617,
    "G3 Winter_theta_nre": 1.427421642,
    "G3 Winter_pr_1_99_nre": 50,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 643,
    "G3 Spring_theta_nre": 1.69419729,
    "G3 Spring_pr_1_99_nre": 50,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 669
  },
  {
    "ID": 249,
    "KG Winter_theta_nre": -0.900248436,
    "KG Winter_pr_1_99_nre": 50,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 410,
    "KG Spring_theta_nre": -0.640237613,
    "KG Spring_pr_1_99_nre": 50,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 436,
    "G1 Fall_theta_nre": -0.384625326,
    "G1 Fall_pr_1_99_nre": 50,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 462,
    "G1 Winter_theta_nre": -0.118206376,
    "G1 Winter_pr_1_99_nre": 50,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 488,
    "G1 Spring_theta_nre": 0.140811285,
    "G1 Spring_pr_1_99_nre": 50,
    "G1 Spring_ss_nre": 99,
    "G1 Spring_dss_nre": 514,
    "G2 Fall_theta_nre": 0.390374147,
    "G2 Fall_pr_1_99_nre": 50,
    "G2 Fall_ss_nre": 100,
    "G2 Fall_dss_nre": 539,
    "G2 Winter_theta_nre": 0.650393993,
    "G2 Winter_pr_1_99_nre": 50,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 565,
    "G2 Spring_theta_nre": 0.920891797,
    "G2 Spring_pr_1_99_nre": 50,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 592,
    "G3 Fall_theta_nre": 1.173268735,
    "G3 Fall_pr_1_99_nre": 50,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 617,
    "G3 Winter_theta_nre": 1.432592078,
    "G3 Winter_pr_1_99_nre": 50,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 643,
    "G3 Spring_theta_nre": 1.699213831,
    "G3 Spring_pr_1_99_nre": 50,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 670
  },
  {
    "ID": 250,
    "KG Winter_theta_nre": -0.898017243,
    "KG Winter_pr_1_99_nre": 50,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 410,
    "KG Spring_theta_nre": -0.639178871,
    "KG Spring_pr_1_99_nre": 50,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 436,
    "G1 Fall_theta_nre": -0.380464196,
    "G1 Fall_pr_1_99_nre": 50,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 462,
    "G1 Winter_theta_nre": -0.117511443,
    "G1 Winter_pr_1_99_nre": 50,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 488,
    "G1 Spring_theta_nre": 0.141872838,
    "G1 Spring_pr_1_99_nre": 50,
    "G1 Spring_ss_nre": 99,
    "G1 Spring_dss_nre": 514,
    "G2 Fall_theta_nre": 0.393729451,
    "G2 Fall_pr_1_99_nre": 50,
    "G2 Fall_ss_nre": 100,
    "G2 Fall_dss_nre": 539,
    "G2 Winter_theta_nre": 0.659159736,
    "G2 Winter_pr_1_99_nre": 50,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 566,
    "G2 Spring_theta_nre": 0.921147605,
    "G2 Spring_pr_1_99_nre": 50,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 592,
    "G3 Fall_theta_nre": 1.181174291,
    "G3 Fall_pr_1_99_nre": 50,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 618,
    "G3 Winter_theta_nre": 1.438866919,
    "G3 Winter_pr_1_99_nre": 50,
    "G3 Winter_ss_nre": 100,
    "G3 Winter_dss_nre": 644,
    "G3 Spring_theta_nre": 1.699586164,
    "G3 Spring_pr_1_99_nre": 50,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 670
  },
  {
    "ID": 251,
    "KG Winter_theta_nre": -0.895803697,
    "KG Winter_pr_1_99_nre": 50,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 410,
    "KG Spring_theta_nre": -0.635023624,
    "KG Spring_pr_1_99_nre": 50,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 436,
    "G1 Fall_theta_nre": -0.374119854,
    "G1 Fall_pr_1_99_nre": 50,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 463,
    "G1 Winter_theta_nre": -0.117454163,
    "G1 Winter_pr_1_99_nre": 50,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 488,
    "G1 Spring_theta_nre": 0.142780001,
    "G1 Spring_pr_1_99_nre": 50,
    "G1 Spring_ss_nre": 99,
    "G1 Spring_dss_nre": 514,
    "G2 Fall_theta_nre": 0.410541834,
    "G2 Fall_pr_1_99_nre": 50,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 541,
    "G2 Winter_theta_nre": 0.664729994,
    "G2 Winter_pr_1_99_nre": 50,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 566,
    "G2 Spring_theta_nre": 0.922360569,
    "G2 Spring_pr_1_99_nre": 50,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 592,
    "G3 Fall_theta_nre": 1.181952328,
    "G3 Fall_pr_1_99_nre": 50,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 618,
    "G3 Winter_theta_nre": 1.443878145,
    "G3 Winter_pr_1_99_nre": 50,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 644,
    "G3 Spring_theta_nre": 1.702777345,
    "G3 Spring_pr_1_99_nre": 50,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 670
  },
  {
    "ID": 252,
    "KG Winter_theta_nre": -0.884272775,
    "KG Winter_pr_1_99_nre": 50,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 412,
    "KG Spring_theta_nre": -0.632447721,
    "KG Spring_pr_1_99_nre": 50,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 437,
    "G1 Fall_theta_nre": -0.371408616,
    "G1 Fall_pr_1_99_nre": 50,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 463,
    "G1 Winter_theta_nre": -0.115604235,
    "G1 Winter_pr_1_99_nre": 50,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 488,
    "G1 Spring_theta_nre": 0.148581633,
    "G1 Spring_pr_1_99_nre": 50,
    "G1 Spring_ss_nre": 99,
    "G1 Spring_dss_nre": 515,
    "G2 Fall_theta_nre": 0.41715391,
    "G2 Fall_pr_1_99_nre": 50,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 542,
    "G2 Winter_theta_nre": 0.666591187,
    "G2 Winter_pr_1_99_nre": 50,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 567,
    "G2 Spring_theta_nre": 0.929895201,
    "G2 Spring_pr_1_99_nre": 50,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 593,
    "G3 Fall_theta_nre": 1.18261089,
    "G3 Fall_pr_1_99_nre": 50,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 618,
    "G3 Winter_theta_nre": 1.451505436,
    "G3 Winter_pr_1_99_nre": 50,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 645,
    "G3 Spring_theta_nre": 1.704039716,
    "G3 Spring_pr_1_99_nre": 50,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 670
  },
  {
    "ID": 253,
    "KG Winter_theta_nre": -0.880812934,
    "KG Winter_pr_1_99_nre": 51,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 412,
    "KG Spring_theta_nre": -0.624172973,
    "KG Spring_pr_1_99_nre": 51,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 438,
    "G1 Fall_theta_nre": -0.370214665,
    "G1 Fall_pr_1_99_nre": 51,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 463,
    "G1 Winter_theta_nre": -0.104683073,
    "G1 Winter_pr_1_99_nre": 51,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 490,
    "G1 Spring_theta_nre": 0.163581278,
    "G1 Spring_pr_1_99_nre": 51,
    "G1 Spring_ss_nre": 100,
    "G1 Spring_dss_nre": 516,
    "G2 Fall_theta_nre": 0.426480955,
    "G2 Fall_pr_1_99_nre": 51,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 543,
    "G2 Winter_theta_nre": 0.669929219,
    "G2 Winter_pr_1_99_nre": 51,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 567,
    "G2 Spring_theta_nre": 0.930232873,
    "G2 Spring_pr_1_99_nre": 51,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 593,
    "G3 Fall_theta_nre": 1.187009056,
    "G3 Fall_pr_1_99_nre": 51,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 619,
    "G3 Winter_theta_nre": 1.452052739,
    "G3 Winter_pr_1_99_nre": 51,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 645,
    "G3 Spring_theta_nre": 1.713002344,
    "G3 Spring_pr_1_99_nre": 51,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 671
  },
  {
    "ID": 254,
    "KG Winter_theta_nre": -0.880681721,
    "KG Winter_pr_1_99_nre": 51,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 412,
    "KG Spring_theta_nre": -0.622195429,
    "KG Spring_pr_1_99_nre": 51,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 438,
    "G1 Fall_theta_nre": -0.363142337,
    "G1 Fall_pr_1_99_nre": 51,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 464,
    "G1 Winter_theta_nre": -0.099461795,
    "G1 Winter_pr_1_99_nre": 51,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 490,
    "G1 Spring_theta_nre": 0.164516959,
    "G1 Spring_pr_1_99_nre": 51,
    "G1 Spring_ss_nre": 100,
    "G1 Spring_dss_nre": 516,
    "G2 Fall_theta_nre": 0.428859195,
    "G2 Fall_pr_1_99_nre": 51,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 543,
    "G2 Winter_theta_nre": 0.670535231,
    "G2 Winter_pr_1_99_nre": 51,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 567,
    "G2 Spring_theta_nre": 0.939213861,
    "G2 Spring_pr_1_99_nre": 51,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 594,
    "G3 Fall_theta_nre": 1.192359534,
    "G3 Fall_pr_1_99_nre": 51,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 619,
    "G3 Winter_theta_nre": 1.455627938,
    "G3 Winter_pr_1_99_nre": 51,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 646,
    "G3 Spring_theta_nre": 1.71901077,
    "G3 Spring_pr_1_99_nre": 51,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 672
  },
  {
    "ID": 255,
    "KG Winter_theta_nre": -0.878661559,
    "KG Winter_pr_1_99_nre": 51,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 412,
    "KG Spring_theta_nre": -0.62161666,
    "KG Spring_pr_1_99_nre": 51,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 438,
    "G1 Fall_theta_nre": -0.36097784,
    "G1 Fall_pr_1_99_nre": 51,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 464,
    "G1 Winter_theta_nre": -0.091873052,
    "G1 Winter_pr_1_99_nre": 51,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 491,
    "G1 Spring_theta_nre": 0.165379701,
    "G1 Spring_pr_1_99_nre": 51,
    "G1 Spring_ss_nre": 100,
    "G1 Spring_dss_nre": 517,
    "G2 Fall_theta_nre": 0.430863172,
    "G2 Fall_pr_1_99_nre": 51,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 543,
    "G2 Winter_theta_nre": 0.67080263,
    "G2 Winter_pr_1_99_nre": 51,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 567,
    "G2 Spring_theta_nre": 0.94105761,
    "G2 Spring_pr_1_99_nre": 51,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 594,
    "G3 Fall_theta_nre": 1.198992027,
    "G3 Fall_pr_1_99_nre": 51,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 620,
    "G3 Winter_theta_nre": 1.457194905,
    "G3 Winter_pr_1_99_nre": 51,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 646,
    "G3 Spring_theta_nre": 1.722939338,
    "G3 Spring_pr_1_99_nre": 51,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 672
  },
  {
    "ID": 256,
    "KG Winter_theta_nre": -0.866604577,
    "KG Winter_pr_1_99_nre": 51,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 413,
    "KG Spring_theta_nre": -0.615989964,
    "KG Spring_pr_1_99_nre": 51,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 438,
    "G1 Fall_theta_nre": -0.352563345,
    "G1 Fall_pr_1_99_nre": 51,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 465,
    "G1 Winter_theta_nre": -0.089627421,
    "G1 Winter_pr_1_99_nre": 51,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 491,
    "G1 Spring_theta_nre": 0.166599748,
    "G1 Spring_pr_1_99_nre": 51,
    "G1 Spring_ss_nre": 100,
    "G1 Spring_dss_nre": 517,
    "G2 Fall_theta_nre": 0.435479341,
    "G2 Fall_pr_1_99_nre": 51,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 544,
    "G2 Winter_theta_nre": 0.675342727,
    "G2 Winter_pr_1_99_nre": 51,
    "G2 Winter_ss_nre": 100,
    "G2 Winter_dss_nre": 568,
    "G2 Spring_theta_nre": 0.942611298,
    "G2 Spring_pr_1_99_nre": 51,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 594,
    "G3 Fall_theta_nre": 1.206909543,
    "G3 Fall_pr_1_99_nre": 51,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 621,
    "G3 Winter_theta_nre": 1.467455981,
    "G3 Winter_pr_1_99_nre": 51,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 647,
    "G3 Spring_theta_nre": 1.723784491,
    "G3 Spring_pr_1_99_nre": 51,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 672
  },
  {
    "ID": 257,
    "KG Winter_theta_nre": -0.861980099,
    "KG Winter_pr_1_99_nre": 51,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 414,
    "KG Spring_theta_nre": -0.611045501,
    "KG Spring_pr_1_99_nre": 51,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 439,
    "G1 Fall_theta_nre": -0.34379286,
    "G1 Fall_pr_1_99_nre": 51,
    "G1 Fall_ss_nre": 100,
    "G1 Fall_dss_nre": 466,
    "G1 Winter_theta_nre": -0.089381988,
    "G1 Winter_pr_1_99_nre": 51,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 491,
    "G1 Spring_theta_nre": 0.177142152,
    "G1 Spring_pr_1_99_nre": 51,
    "G1 Spring_ss_nre": 100,
    "G1 Spring_dss_nre": 518,
    "G2 Fall_theta_nre": 0.437336818,
    "G2 Fall_pr_1_99_nre": 51,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 544,
    "G2 Winter_theta_nre": 0.6803198,
    "G2 Winter_pr_1_99_nre": 51,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 568,
    "G2 Spring_theta_nre": 0.945834053,
    "G2 Spring_pr_1_99_nre": 51,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 595,
    "G3 Fall_theta_nre": 1.207902995,
    "G3 Fall_pr_1_99_nre": 51,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 621,
    "G3 Winter_theta_nre": 1.470789533,
    "G3 Winter_pr_1_99_nre": 51,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 647,
    "G3 Spring_theta_nre": 1.735145822,
    "G3 Spring_pr_1_99_nre": 51,
    "G3 Spring_ss_nre": 100,
    "G3 Spring_dss_nre": 674
  },
  {
    "ID": 258,
    "KG Winter_theta_nre": -0.860474108,
    "KG Winter_pr_1_99_nre": 52,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 414,
    "KG Spring_theta_nre": -0.609773961,
    "KG Spring_pr_1_99_nre": 52,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 439,
    "G1 Fall_theta_nre": -0.336191123,
    "G1 Fall_pr_1_99_nre": 52,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 466,
    "G1 Winter_theta_nre": -0.088036803,
    "G1 Winter_pr_1_99_nre": 52,
    "G1 Winter_ss_nre": 100,
    "G1 Winter_dss_nre": 491,
    "G1 Spring_theta_nre": 0.178501702,
    "G1 Spring_pr_1_99_nre": 52,
    "G1 Spring_ss_nre": 100,
    "G1 Spring_dss_nre": 518,
    "G2 Fall_theta_nre": 0.440189824,
    "G2 Fall_pr_1_99_nre": 52,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 544,
    "G2 Winter_theta_nre": 0.68510467,
    "G2 Winter_pr_1_99_nre": 52,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 569,
    "G2 Spring_theta_nre": 0.949733557,
    "G2 Spring_pr_1_99_nre": 52,
    "G2 Spring_ss_nre": 100,
    "G2 Spring_dss_nre": 595,
    "G3 Fall_theta_nre": 1.210801959,
    "G3 Fall_pr_1_99_nre": 52,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 621,
    "G3 Winter_theta_nre": 1.473911164,
    "G3 Winter_pr_1_99_nre": 52,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 647,
    "G3 Spring_theta_nre": 1.738513801,
    "G3 Spring_pr_1_99_nre": 52,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 674
  },
  {
    "ID": 259,
    "KG Winter_theta_nre": -0.859732436,
    "KG Winter_pr_1_99_nre": 52,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 414,
    "KG Spring_theta_nre": -0.605795774,
    "KG Spring_pr_1_99_nre": 52,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 439,
    "G1 Fall_theta_nre": -0.333359856,
    "G1 Fall_pr_1_99_nre": 52,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 467,
    "G1 Winter_theta_nre": -0.086343028,
    "G1 Winter_pr_1_99_nre": 52,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 491,
    "G1 Spring_theta_nre": 0.179058459,
    "G1 Spring_pr_1_99_nre": 52,
    "G1 Spring_ss_nre": 100,
    "G1 Spring_dss_nre": 518,
    "G2 Fall_theta_nre": 0.443213548,
    "G2 Fall_pr_1_99_nre": 52,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 544,
    "G2 Winter_theta_nre": 0.685599711,
    "G2 Winter_pr_1_99_nre": 52,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 569,
    "G2 Spring_theta_nre": 0.957656992,
    "G2 Spring_pr_1_99_nre": 52,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 596,
    "G3 Fall_theta_nre": 1.222819889,
    "G3 Fall_pr_1_99_nre": 52,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 622,
    "G3 Winter_theta_nre": 1.474187996,
    "G3 Winter_pr_1_99_nre": 52,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 647,
    "G3 Spring_theta_nre": 1.740577078,
    "G3 Spring_pr_1_99_nre": 52,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 674
  },
  {
    "ID": 260,
    "KG Winter_theta_nre": -0.855686507,
    "KG Winter_pr_1_99_nre": 52,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 414,
    "KG Spring_theta_nre": -0.591507314,
    "KG Spring_pr_1_99_nre": 52,
    "KG Spring_ss_nre": 100,
    "KG Spring_dss_nre": 441,
    "G1 Fall_theta_nre": -0.332685751,
    "G1 Fall_pr_1_99_nre": 52,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 467,
    "G1 Winter_theta_nre": -0.071224835,
    "G1 Winter_pr_1_99_nre": 52,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 493,
    "G1 Spring_theta_nre": 0.185841911,
    "G1 Spring_pr_1_99_nre": 52,
    "G1 Spring_ss_nre": 100,
    "G1 Spring_dss_nre": 519,
    "G2 Fall_theta_nre": 0.446959027,
    "G2 Fall_pr_1_99_nre": 52,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 545,
    "G2 Winter_theta_nre": 0.687872346,
    "G2 Winter_pr_1_99_nre": 52,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 569,
    "G2 Spring_theta_nre": 0.964913216,
    "G2 Spring_pr_1_99_nre": 52,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 596,
    "G3 Fall_theta_nre": 1.230430991,
    "G3 Fall_pr_1_99_nre": 52,
    "G3 Fall_ss_nre": 100,
    "G3 Fall_dss_nre": 623,
    "G3 Winter_theta_nre": 1.474217072,
    "G3 Winter_pr_1_99_nre": 52,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 647,
    "G3 Spring_theta_nre": 1.743399134,
    "G3 Spring_pr_1_99_nre": 52,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 674
  },
  {
    "ID": 261,
    "KG Winter_theta_nre": -0.85159685,
    "KG Winter_pr_1_99_nre": 52,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 415,
    "KG Spring_theta_nre": -0.584223391,
    "KG Spring_pr_1_99_nre": 52,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 442,
    "G1 Fall_theta_nre": -0.331911009,
    "G1 Fall_pr_1_99_nre": 52,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 467,
    "G1 Winter_theta_nre": -0.067909424,
    "G1 Winter_pr_1_99_nre": 52,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 493,
    "G1 Spring_theta_nre": 0.186998308,
    "G1 Spring_pr_1_99_nre": 52,
    "G1 Spring_ss_nre": 100,
    "G1 Spring_dss_nre": 519,
    "G2 Fall_theta_nre": 0.452787485,
    "G2 Fall_pr_1_99_nre": 52,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 545,
    "G2 Winter_theta_nre": 0.688424,
    "G2 Winter_pr_1_99_nre": 52,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 569,
    "G2 Spring_theta_nre": 0.967746493,
    "G2 Spring_pr_1_99_nre": 52,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 597,
    "G3 Fall_theta_nre": 1.240403063,
    "G3 Fall_pr_1_99_nre": 52,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 624,
    "G3 Winter_theta_nre": 1.477291846,
    "G3 Winter_pr_1_99_nre": 52,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 648,
    "G3 Spring_theta_nre": 1.758020305,
    "G3 Spring_pr_1_99_nre": 52,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 676
  },
  {
    "ID": 262,
    "KG Winter_theta_nre": -0.850935575,
    "KG Winter_pr_1_99_nre": 52,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 415,
    "KG Spring_theta_nre": -0.567890533,
    "KG Spring_pr_1_99_nre": 52,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 443,
    "G1 Fall_theta_nre": -0.324063613,
    "G1 Fall_pr_1_99_nre": 52,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 468,
    "G1 Winter_theta_nre": -0.063687254,
    "G1 Winter_pr_1_99_nre": 52,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 494,
    "G1 Spring_theta_nre": 0.201302486,
    "G1 Spring_pr_1_99_nre": 52,
    "G1 Spring_ss_nre": 100,
    "G1 Spring_dss_nre": 520,
    "G2 Fall_theta_nre": 0.4573261,
    "G2 Fall_pr_1_99_nre": 52,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 546,
    "G2 Winter_theta_nre": 0.691462538,
    "G2 Winter_pr_1_99_nre": 52,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 569,
    "G2 Spring_theta_nre": 0.970440577,
    "G2 Spring_pr_1_99_nre": 52,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 597,
    "G3 Fall_theta_nre": 1.240657444,
    "G3 Fall_pr_1_99_nre": 52,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 624,
    "G3 Winter_theta_nre": 1.478146869,
    "G3 Winter_pr_1_99_nre": 52,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 648,
    "G3 Spring_theta_nre": 1.75825073,
    "G3 Spring_pr_1_99_nre": 52,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 676
  },
  {
    "ID": 263,
    "KG Winter_theta_nre": -0.848366963,
    "KG Winter_pr_1_99_nre": 53,
    "KG Winter_ss_nre": 101,
    "KG Winter_dss_nre": 415,
    "KG Spring_theta_nre": -0.567600688,
    "KG Spring_pr_1_99_nre": 53,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 443,
    "G1 Fall_theta_nre": -0.319542109,
    "G1 Fall_pr_1_99_nre": 53,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 468,
    "G1 Winter_theta_nre": -0.057530695,
    "G1 Winter_pr_1_99_nre": 53,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 494,
    "G1 Spring_theta_nre": 0.205774718,
    "G1 Spring_pr_1_99_nre": 53,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 521,
    "G2 Fall_theta_nre": 0.458182678,
    "G2 Fall_pr_1_99_nre": 53,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 546,
    "G2 Winter_theta_nre": 0.694785326,
    "G2 Winter_pr_1_99_nre": 53,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 569,
    "G2 Spring_theta_nre": 0.971078351,
    "G2 Spring_pr_1_99_nre": 53,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 597,
    "G3 Fall_theta_nre": 1.24290427,
    "G3 Fall_pr_1_99_nre": 53,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 624,
    "G3 Winter_theta_nre": 1.483915465,
    "G3 Winter_pr_1_99_nre": 53,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 648,
    "G3 Spring_theta_nre": 1.768477808,
    "G3 Spring_pr_1_99_nre": 53,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 677
  },
  {
    "ID": 264,
    "KG Winter_theta_nre": -0.844413798,
    "KG Winter_pr_1_99_nre": 53,
    "KG Winter_ss_nre": 102,
    "KG Winter_dss_nre": 416,
    "KG Spring_theta_nre": -0.56502583,
    "KG Spring_pr_1_99_nre": 53,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 443,
    "G1 Fall_theta_nre": -0.314334523,
    "G1 Fall_pr_1_99_nre": 53,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 469,
    "G1 Winter_theta_nre": -0.056317591,
    "G1 Winter_pr_1_99_nre": 53,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 494,
    "G1 Spring_theta_nre": 0.212535208,
    "G1 Spring_pr_1_99_nre": 53,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 521,
    "G2 Fall_theta_nre": 0.460906558,
    "G2 Fall_pr_1_99_nre": 53,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 546,
    "G2 Winter_theta_nre": 0.696851157,
    "G2 Winter_pr_1_99_nre": 53,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 570,
    "G2 Spring_theta_nre": 0.972643672,
    "G2 Spring_pr_1_99_nre": 53,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 597,
    "G3 Fall_theta_nre": 1.244153424,
    "G3 Fall_pr_1_99_nre": 53,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 624,
    "G3 Winter_theta_nre": 1.488319067,
    "G3 Winter_pr_1_99_nre": 53,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 649,
    "G3 Spring_theta_nre": 1.768718923,
    "G3 Spring_pr_1_99_nre": 53,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 677
  },
  {
    "ID": 265,
    "KG Winter_theta_nre": -0.831091669,
    "KG Winter_pr_1_99_nre": 53,
    "KG Winter_ss_nre": 102,
    "KG Winter_dss_nre": 417,
    "KG Spring_theta_nre": -0.563375942,
    "KG Spring_pr_1_99_nre": 53,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 444,
    "G1 Fall_theta_nre": -0.30968449,
    "G1 Fall_pr_1_99_nre": 53,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 469,
    "G1 Winter_theta_nre": -0.04886124,
    "G1 Winter_pr_1_99_nre": 53,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 495,
    "G1 Spring_theta_nre": 0.216137758,
    "G1 Spring_pr_1_99_nre": 53,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 522,
    "G2 Fall_theta_nre": 0.463405659,
    "G2 Fall_pr_1_99_nre": 53,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 546,
    "G2 Winter_theta_nre": 0.711102287,
    "G2 Winter_pr_1_99_nre": 53,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 571,
    "G2 Spring_theta_nre": 0.981371533,
    "G2 Spring_pr_1_99_nre": 53,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 598,
    "G3 Fall_theta_nre": 1.244427385,
    "G3 Fall_pr_1_99_nre": 53,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 624,
    "G3 Winter_theta_nre": 1.488660434,
    "G3 Winter_pr_1_99_nre": 53,
    "G3 Winter_ss_nre": 101,
    "G3 Winter_dss_nre": 649,
    "G3 Spring_theta_nre": 1.77042545,
    "G3 Spring_pr_1_99_nre": 53,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 677
  },
  {
    "ID": 266,
    "KG Winter_theta_nre": -0.831015975,
    "KG Winter_pr_1_99_nre": 53,
    "KG Winter_ss_nre": 102,
    "KG Winter_dss_nre": 417,
    "KG Spring_theta_nre": -0.556068729,
    "KG Spring_pr_1_99_nre": 53,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 444,
    "G1 Fall_theta_nre": -0.301760815,
    "G1 Fall_pr_1_99_nre": 53,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 470,
    "G1 Winter_theta_nre": -0.048328075,
    "G1 Winter_pr_1_99_nre": 53,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 495,
    "G1 Spring_theta_nre": 0.217694771,
    "G1 Spring_pr_1_99_nre": 53,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 522,
    "G2 Fall_theta_nre": 0.464480536,
    "G2 Fall_pr_1_99_nre": 53,
    "G2 Fall_ss_nre": 101,
    "G2 Fall_dss_nre": 546,
    "G2 Winter_theta_nre": 0.711810708,
    "G2 Winter_pr_1_99_nre": 53,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 571,
    "G2 Spring_theta_nre": 0.982023336,
    "G2 Spring_pr_1_99_nre": 53,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 598,
    "G3 Fall_theta_nre": 1.247321636,
    "G3 Fall_pr_1_99_nre": 53,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 625,
    "G3 Winter_theta_nre": 1.495371321,
    "G3 Winter_pr_1_99_nre": 53,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 650,
    "G3 Spring_theta_nre": 1.771933271,
    "G3 Spring_pr_1_99_nre": 53,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 677
  },
  {
    "ID": 267,
    "KG Winter_theta_nre": -0.825751096,
    "KG Winter_pr_1_99_nre": 53,
    "KG Winter_ss_nre": 102,
    "KG Winter_dss_nre": 417,
    "KG Spring_theta_nre": -0.555992644,
    "KG Spring_pr_1_99_nre": 53,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 444,
    "G1 Fall_theta_nre": -0.296736055,
    "G1 Fall_pr_1_99_nre": 53,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 470,
    "G1 Winter_theta_nre": -0.046348893,
    "G1 Winter_pr_1_99_nre": 53,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 495,
    "G1 Spring_theta_nre": 0.225979002,
    "G1 Spring_pr_1_99_nre": 53,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 523,
    "G2 Fall_theta_nre": 0.467463254,
    "G2 Fall_pr_1_99_nre": 53,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 547,
    "G2 Winter_theta_nre": 0.71455974,
    "G2 Winter_pr_1_99_nre": 53,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 571,
    "G2 Spring_theta_nre": 0.982192248,
    "G2 Spring_pr_1_99_nre": 53,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 598,
    "G3 Fall_theta_nre": 1.258062405,
    "G3 Fall_pr_1_99_nre": 53,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 626,
    "G3 Winter_theta_nre": 1.495992703,
    "G3 Winter_pr_1_99_nre": 53,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 650,
    "G3 Spring_theta_nre": 1.773213784,
    "G3 Spring_pr_1_99_nre": 53,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 677
  },
  {
    "ID": 268,
    "KG Winter_theta_nre": -0.823021214,
    "KG Winter_pr_1_99_nre": 54,
    "KG Winter_ss_nre": 102,
    "KG Winter_dss_nre": 418,
    "KG Spring_theta_nre": -0.53826366,
    "KG Spring_pr_1_99_nre": 54,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 446,
    "G1 Fall_theta_nre": -0.296127608,
    "G1 Fall_pr_1_99_nre": 54,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 470,
    "G1 Winter_theta_nre": -0.045593759,
    "G1 Winter_pr_1_99_nre": 54,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 495,
    "G1 Spring_theta_nre": 0.230364866,
    "G1 Spring_pr_1_99_nre": 54,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 523,
    "G2 Fall_theta_nre": 0.469788499,
    "G2 Fall_pr_1_99_nre": 54,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 547,
    "G2 Winter_theta_nre": 0.718819886,
    "G2 Winter_pr_1_99_nre": 54,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 572,
    "G2 Spring_theta_nre": 0.984458075,
    "G2 Spring_pr_1_99_nre": 54,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 598,
    "G3 Fall_theta_nre": 1.28149091,
    "G3 Fall_pr_1_99_nre": 54,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 628,
    "G3 Winter_theta_nre": 1.502330784,
    "G3 Winter_pr_1_99_nre": 54,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 650,
    "G3 Spring_theta_nre": 1.777384341,
    "G3 Spring_pr_1_99_nre": 54,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 678
  },
  {
    "ID": 269,
    "KG Winter_theta_nre": -0.821802589,
    "KG Winter_pr_1_99_nre": 54,
    "KG Winter_ss_nre": 102,
    "KG Winter_dss_nre": 418,
    "KG Spring_theta_nre": -0.537861496,
    "KG Spring_pr_1_99_nre": 54,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 446,
    "G1 Fall_theta_nre": -0.292812277,
    "G1 Fall_pr_1_99_nre": 54,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 471,
    "G1 Winter_theta_nre": -0.045319736,
    "G1 Winter_pr_1_99_nre": 54,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 495,
    "G1 Spring_theta_nre": 0.238405336,
    "G1 Spring_pr_1_99_nre": 54,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 524,
    "G2 Fall_theta_nre": 0.471149022,
    "G2 Fall_pr_1_99_nre": 54,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 547,
    "G2 Winter_theta_nre": 0.719609349,
    "G2 Winter_pr_1_99_nre": 54,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 572,
    "G2 Spring_theta_nre": 0.995442521,
    "G2 Spring_pr_1_99_nre": 54,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 600,
    "G3 Fall_theta_nre": 1.287381349,
    "G3 Fall_pr_1_99_nre": 54,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 629,
    "G3 Winter_theta_nre": 1.512067796,
    "G3 Winter_pr_1_99_nre": 54,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 651,
    "G3 Spring_theta_nre": 1.778165176,
    "G3 Spring_pr_1_99_nre": 54,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 678
  },
  {
    "ID": 270,
    "KG Winter_theta_nre": -0.813083302,
    "KG Winter_pr_1_99_nre": 54,
    "KG Winter_ss_nre": 102,
    "KG Winter_dss_nre": 419,
    "KG Spring_theta_nre": -0.536620552,
    "KG Spring_pr_1_99_nre": 54,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 446,
    "G1 Fall_theta_nre": -0.290024538,
    "G1 Fall_pr_1_99_nre": 54,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 471,
    "G1 Winter_theta_nre": -0.043134332,
    "G1 Winter_pr_1_99_nre": 54,
    "G1 Winter_ss_nre": 101,
    "G1 Winter_dss_nre": 496,
    "G1 Spring_theta_nre": 0.240098113,
    "G1 Spring_pr_1_99_nre": 54,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 524,
    "G2 Fall_theta_nre": 0.480485171,
    "G2 Fall_pr_1_99_nre": 54,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 548,
    "G2 Winter_theta_nre": 0.722010644,
    "G2 Winter_pr_1_99_nre": 54,
    "G2 Winter_ss_nre": 101,
    "G2 Winter_dss_nre": 572,
    "G2 Spring_theta_nre": 0.996644737,
    "G2 Spring_pr_1_99_nre": 54,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 600,
    "G3 Fall_theta_nre": 1.287519977,
    "G3 Fall_pr_1_99_nre": 54,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 629,
    "G3 Winter_theta_nre": 1.512607328,
    "G3 Winter_pr_1_99_nre": 54,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 651,
    "G3 Spring_theta_nre": 1.781094132,
    "G3 Spring_pr_1_99_nre": 54,
    "G3 Spring_ss_nre": 101,
    "G3 Spring_dss_nre": 678
  },
  {
    "ID": 271,
    "KG Winter_theta_nre": -0.801609574,
    "KG Winter_pr_1_99_nre": 54,
    "KG Winter_ss_nre": 102,
    "KG Winter_dss_nre": 420,
    "KG Spring_theta_nre": -0.535103122,
    "KG Spring_pr_1_99_nre": 54,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 446,
    "G1 Fall_theta_nre": -0.285794568,
    "G1 Fall_pr_1_99_nre": 54,
    "G1 Fall_ss_nre": 101,
    "G1 Fall_dss_nre": 471,
    "G1 Winter_theta_nre": -0.016958792,
    "G1 Winter_pr_1_99_nre": 54,
    "G1 Winter_ss_nre": 102,
    "G1 Winter_dss_nre": 498,
    "G1 Spring_theta_nre": 0.242479983,
    "G1 Spring_pr_1_99_nre": 54,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 524,
    "G2 Fall_theta_nre": 0.481306679,
    "G2 Fall_pr_1_99_nre": 54,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 548,
    "G2 Winter_theta_nre": 0.728361445,
    "G2 Winter_pr_1_99_nre": 54,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 573,
    "G2 Spring_theta_nre": 0.999536757,
    "G2 Spring_pr_1_99_nre": 54,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 600,
    "G3 Fall_theta_nre": 1.289727684,
    "G3 Fall_pr_1_99_nre": 54,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 629,
    "G3 Winter_theta_nre": 1.513692469,
    "G3 Winter_pr_1_99_nre": 54,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 651,
    "G3 Spring_theta_nre": 1.786583361,
    "G3 Spring_pr_1_99_nre": 54,
    "G3 Spring_ss_nre": 102,
    "G3 Spring_dss_nre": 679
  },
  {
    "ID": 272,
    "KG Winter_theta_nre": -0.801331892,
    "KG Winter_pr_1_99_nre": 54,
    "KG Winter_ss_nre": 102,
    "KG Winter_dss_nre": 420,
    "KG Spring_theta_nre": -0.533663308,
    "KG Spring_pr_1_99_nre": 54,
    "KG Spring_ss_nre": 101,
    "KG Spring_dss_nre": 447,
    "G1 Fall_theta_nre": -0.281653626,
    "G1 Fall_pr_1_99_nre": 54,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 472,
    "G1 Winter_theta_nre": -0.013295933,
    "G1 Winter_pr_1_99_nre": 54,
    "G1 Winter_ss_nre": 102,
    "G1 Winter_dss_nre": 499,
    "G1 Spring_theta_nre": 0.243606562,
    "G1 Spring_pr_1_99_nre": 54,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 524,
    "G2 Fall_theta_nre": 0.494508497,
    "G2 Fall_pr_1_99_nre": 54,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 549,
    "G2 Winter_theta_nre": 0.736926769,
    "G2 Winter_pr_1_99_nre": 54,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 574,
    "G2 Spring_theta_nre": 1.000080738,
    "G2 Spring_pr_1_99_nre": 54,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 600,
    "G3 Fall_theta_nre": 1.294074768,
    "G3 Fall_pr_1_99_nre": 54,
    "G3 Fall_ss_nre": 101,
    "G3 Fall_dss_nre": 629,
    "G3 Winter_theta_nre": 1.513805927,
    "G3 Winter_pr_1_99_nre": 54,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 651,
    "G3 Spring_theta_nre": 1.791392468,
    "G3 Spring_pr_1_99_nre": 54,
    "G3 Spring_ss_nre": 102,
    "G3 Spring_dss_nre": 679
  },
  {
    "ID": 273,
    "KG Winter_theta_nre": -0.788915065,
    "KG Winter_pr_1_99_nre": 55,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 421,
    "KG Spring_theta_nre": -0.530115778,
    "KG Spring_pr_1_99_nre": 55,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 447,
    "G1 Fall_theta_nre": -0.27826126,
    "G1 Fall_pr_1_99_nre": 55,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 472,
    "G1 Winter_theta_nre": -0.005350503,
    "G1 Winter_pr_1_99_nre": 55,
    "G1 Winter_ss_nre": 102,
    "G1 Winter_dss_nre": 499,
    "G1 Spring_theta_nre": 0.245621496,
    "G1 Spring_pr_1_99_nre": 55,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 525,
    "G2 Fall_theta_nre": 0.495575349,
    "G2 Fall_pr_1_99_nre": 55,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 550,
    "G2 Winter_theta_nre": 0.745956647,
    "G2 Winter_pr_1_99_nre": 55,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 575,
    "G2 Spring_theta_nre": 1.005320976,
    "G2 Spring_pr_1_99_nre": 55,
    "G2 Spring_ss_nre": 101,
    "G2 Spring_dss_nre": 601,
    "G3 Fall_theta_nre": 1.298507855,
    "G3 Fall_pr_1_99_nre": 55,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 630,
    "G3 Winter_theta_nre": 1.516429536,
    "G3 Winter_pr_1_99_nre": 55,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 652,
    "G3 Spring_theta_nre": 1.793375897,
    "G3 Spring_pr_1_99_nre": 55,
    "G3 Spring_ss_nre": 102,
    "G3 Spring_dss_nre": 679
  },
  {
    "ID": 274,
    "KG Winter_theta_nre": -0.785496259,
    "KG Winter_pr_1_99_nre": 55,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 421,
    "KG Spring_theta_nre": -0.5277071,
    "KG Spring_pr_1_99_nre": 55,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 447,
    "G1 Fall_theta_nre": -0.278154041,
    "G1 Fall_pr_1_99_nre": 55,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 472,
    "G1 Winter_theta_nre": -0.004986544,
    "G1 Winter_pr_1_99_nre": 55,
    "G1 Winter_ss_nre": 102,
    "G1 Winter_dss_nre": 500,
    "G1 Spring_theta_nre": 0.253991367,
    "G1 Spring_pr_1_99_nre": 55,
    "G1 Spring_ss_nre": 101,
    "G1 Spring_dss_nre": 525,
    "G2 Fall_theta_nre": 0.508615642,
    "G2 Fall_pr_1_99_nre": 55,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 551,
    "G2 Winter_theta_nre": 0.74798899,
    "G2 Winter_pr_1_99_nre": 55,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 575,
    "G2 Spring_theta_nre": 1.013544265,
    "G2 Spring_pr_1_99_nre": 55,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 601,
    "G3 Fall_theta_nre": 1.305124739,
    "G3 Fall_pr_1_99_nre": 55,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 631,
    "G3 Winter_theta_nre": 1.519245581,
    "G3 Winter_pr_1_99_nre": 55,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 652,
    "G3 Spring_theta_nre": 1.794030678,
    "G3 Spring_pr_1_99_nre": 55,
    "G3 Spring_ss_nre": 102,
    "G3 Spring_dss_nre": 679
  },
  {
    "ID": 275,
    "KG Winter_theta_nre": -0.775564671,
    "KG Winter_pr_1_99_nre": 55,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 422,
    "KG Spring_theta_nre": -0.527268211,
    "KG Spring_pr_1_99_nre": 55,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 447,
    "G1 Fall_theta_nre": -0.267339607,
    "G1 Fall_pr_1_99_nre": 55,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 473,
    "G1 Winter_theta_nre": 0.003405949,
    "G1 Winter_pr_1_99_nre": 55,
    "G1 Winter_ss_nre": 102,
    "G1 Winter_dss_nre": 500,
    "G1 Spring_theta_nre": 0.255988197,
    "G1 Spring_pr_1_99_nre": 55,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 526,
    "G2 Fall_theta_nre": 0.511119114,
    "G2 Fall_pr_1_99_nre": 55,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 551,
    "G2 Winter_theta_nre": 0.74946193,
    "G2 Winter_pr_1_99_nre": 55,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 575,
    "G2 Spring_theta_nre": 1.017424732,
    "G2 Spring_pr_1_99_nre": 55,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 602,
    "G3 Fall_theta_nre": 1.311293281,
    "G3 Fall_pr_1_99_nre": 55,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 631,
    "G3 Winter_theta_nre": 1.520581444,
    "G3 Winter_pr_1_99_nre": 55,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 652,
    "G3 Spring_theta_nre": 1.7942569,
    "G3 Spring_pr_1_99_nre": 55,
    "G3 Spring_ss_nre": 102,
    "G3 Spring_dss_nre": 679
  },
  {
    "ID": 276,
    "KG Winter_theta_nre": -0.76744798,
    "KG Winter_pr_1_99_nre": 55,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 423,
    "KG Spring_theta_nre": -0.526857171,
    "KG Spring_pr_1_99_nre": 55,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 447,
    "G1 Fall_theta_nre": -0.265817992,
    "G1 Fall_pr_1_99_nre": 55,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 473,
    "G1 Winter_theta_nre": 0.003931696,
    "G1 Winter_pr_1_99_nre": 55,
    "G1 Winter_ss_nre": 102,
    "G1 Winter_dss_nre": 500,
    "G1 Spring_theta_nre": 0.256742514,
    "G1 Spring_pr_1_99_nre": 55,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 526,
    "G2 Fall_theta_nre": 0.513786111,
    "G2 Fall_pr_1_99_nre": 55,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 551,
    "G2 Winter_theta_nre": 0.750301068,
    "G2 Winter_pr_1_99_nre": 55,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 575,
    "G2 Spring_theta_nre": 1.022845947,
    "G2 Spring_pr_1_99_nre": 55,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 602,
    "G3 Fall_theta_nre": 1.312807362,
    "G3 Fall_pr_1_99_nre": 55,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 631,
    "G3 Winter_theta_nre": 1.524438192,
    "G3 Winter_pr_1_99_nre": 55,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 652,
    "G3 Spring_theta_nre": 1.807568151,
    "G3 Spring_pr_1_99_nre": 55,
    "G3 Spring_ss_nre": 102,
    "G3 Spring_dss_nre": 681
  },
  {
    "ID": 277,
    "KG Winter_theta_nre": -0.766623993,
    "KG Winter_pr_1_99_nre": 55,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 423,
    "KG Spring_theta_nre": -0.524035889,
    "KG Spring_pr_1_99_nre": 55,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 448,
    "G1 Fall_theta_nre": -0.264888096,
    "G1 Fall_pr_1_99_nre": 55,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 474,
    "G1 Winter_theta_nre": 0.00452881,
    "G1 Winter_pr_1_99_nre": 55,
    "G1 Winter_ss_nre": 102,
    "G1 Winter_dss_nre": 500,
    "G1 Spring_theta_nre": 0.256802877,
    "G1 Spring_pr_1_99_nre": 55,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 526,
    "G2 Fall_theta_nre": 0.514644475,
    "G2 Fall_pr_1_99_nre": 55,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 551,
    "G2 Winter_theta_nre": 0.750491764,
    "G2 Winter_pr_1_99_nre": 55,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 575,
    "G2 Spring_theta_nre": 1.02285972,
    "G2 Spring_pr_1_99_nre": 55,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 602,
    "G3 Fall_theta_nre": 1.316636825,
    "G3 Fall_pr_1_99_nre": 55,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 632,
    "G3 Winter_theta_nre": 1.524514665,
    "G3 Winter_pr_1_99_nre": 55,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 652,
    "G3 Spring_theta_nre": 1.81234679,
    "G3 Spring_pr_1_99_nre": 55,
    "G3 Spring_ss_nre": 102,
    "G3 Spring_dss_nre": 681
  },
  {
    "ID": 278,
    "KG Winter_theta_nre": -0.763947075,
    "KG Winter_pr_1_99_nre": 56,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 424,
    "KG Spring_theta_nre": -0.52106593,
    "KG Spring_pr_1_99_nre": 56,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 448,
    "G1 Fall_theta_nre": -0.262180611,
    "G1 Fall_pr_1_99_nre": 56,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 474,
    "G1 Winter_theta_nre": 0.006019017,
    "G1 Winter_pr_1_99_nre": 56,
    "G1 Winter_ss_nre": 102,
    "G1 Winter_dss_nre": 501,
    "G1 Spring_theta_nre": 0.257263579,
    "G1 Spring_pr_1_99_nre": 56,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 526,
    "G2 Fall_theta_nre": 0.51612773,
    "G2 Fall_pr_1_99_nre": 56,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 552,
    "G2 Winter_theta_nre": 0.750586992,
    "G2 Winter_pr_1_99_nre": 56,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 575,
    "G2 Spring_theta_nre": 1.026050313,
    "G2 Spring_pr_1_99_nre": 56,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 603,
    "G3 Fall_theta_nre": 1.323674275,
    "G3 Fall_pr_1_99_nre": 56,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 632,
    "G3 Winter_theta_nre": 1.530822358,
    "G3 Winter_pr_1_99_nre": 56,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 653,
    "G3 Spring_theta_nre": 1.819656632,
    "G3 Spring_pr_1_99_nre": 56,
    "G3 Spring_ss_nre": 102,
    "G3 Spring_dss_nre": 682
  },
  {
    "ID": 279,
    "KG Winter_theta_nre": -0.758347702,
    "KG Winter_pr_1_99_nre": 56,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 424,
    "KG Spring_theta_nre": -0.518620853,
    "KG Spring_pr_1_99_nre": 56,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 448,
    "G1 Fall_theta_nre": -0.251161879,
    "G1 Fall_pr_1_99_nre": 56,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 475,
    "G1 Winter_theta_nre": 0.008857669,
    "G1 Winter_pr_1_99_nre": 56,
    "G1 Winter_ss_nre": 102,
    "G1 Winter_dss_nre": 501,
    "G1 Spring_theta_nre": 0.260753533,
    "G1 Spring_pr_1_99_nre": 56,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 526,
    "G2 Fall_theta_nre": 0.522653707,
    "G2 Fall_pr_1_99_nre": 56,
    "G2 Fall_ss_nre": 102,
    "G2 Fall_dss_nre": 552,
    "G2 Winter_theta_nre": 0.754226328,
    "G2 Winter_pr_1_99_nre": 56,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 575,
    "G2 Spring_theta_nre": 1.02651361,
    "G2 Spring_pr_1_99_nre": 56,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 603,
    "G3 Fall_theta_nre": 1.327662978,
    "G3 Fall_pr_1_99_nre": 56,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 633,
    "G3 Winter_theta_nre": 1.538344505,
    "G3 Winter_pr_1_99_nre": 56,
    "G3 Winter_ss_nre": 102,
    "G3 Winter_dss_nre": 654,
    "G3 Spring_theta_nre": 1.821888851,
    "G3 Spring_pr_1_99_nre": 56,
    "G3 Spring_ss_nre": 102,
    "G3 Spring_dss_nre": 682
  },
  {
    "ID": 280,
    "KG Winter_theta_nre": -0.745817613,
    "KG Winter_pr_1_99_nre": 56,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 425,
    "KG Spring_theta_nre": -0.490306633,
    "KG Spring_pr_1_99_nre": 56,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 451,
    "G1 Fall_theta_nre": -0.248219305,
    "G1 Fall_pr_1_99_nre": 56,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 475,
    "G1 Winter_theta_nre": 0.027734532,
    "G1 Winter_pr_1_99_nre": 56,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 503,
    "G1 Spring_theta_nre": 0.261340114,
    "G1 Spring_pr_1_99_nre": 56,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 526,
    "G2 Fall_theta_nre": 0.525800417,
    "G2 Fall_pr_1_99_nre": 56,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 553,
    "G2 Winter_theta_nre": 0.756813496,
    "G2 Winter_pr_1_99_nre": 56,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 576,
    "G2 Spring_theta_nre": 1.028554166,
    "G2 Spring_pr_1_99_nre": 56,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 603,
    "G3 Fall_theta_nre": 1.336563905,
    "G3 Fall_pr_1_99_nre": 56,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 634,
    "G3 Winter_theta_nre": 1.546532531,
    "G3 Winter_pr_1_99_nre": 56,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 655,
    "G3 Spring_theta_nre": 1.824150884,
    "G3 Spring_pr_1_99_nre": 56,
    "G3 Spring_ss_nre": 102,
    "G3 Spring_dss_nre": 682
  },
  {
    "ID": 281,
    "KG Winter_theta_nre": -0.742839661,
    "KG Winter_pr_1_99_nre": 56,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 426,
    "KG Spring_theta_nre": -0.483319171,
    "KG Spring_pr_1_99_nre": 56,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 452,
    "G1 Fall_theta_nre": -0.24428476,
    "G1 Fall_pr_1_99_nre": 56,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 476,
    "G1 Winter_theta_nre": 0.028768121,
    "G1 Winter_pr_1_99_nre": 56,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 503,
    "G1 Spring_theta_nre": 0.262154721,
    "G1 Spring_pr_1_99_nre": 56,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 526,
    "G2 Fall_theta_nre": 0.530806162,
    "G2 Fall_pr_1_99_nre": 56,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 553,
    "G2 Winter_theta_nre": 0.766163118,
    "G2 Winter_pr_1_99_nre": 56,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 577,
    "G2 Spring_theta_nre": 1.029299081,
    "G2 Spring_pr_1_99_nre": 56,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 603,
    "G3 Fall_theta_nre": 1.338969078,
    "G3 Fall_pr_1_99_nre": 56,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 634,
    "G3 Winter_theta_nre": 1.551335502,
    "G3 Winter_pr_1_99_nre": 56,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 655,
    "G3 Spring_theta_nre": 1.828867553,
    "G3 Spring_pr_1_99_nre": 56,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 683
  },
  {
    "ID": 282,
    "KG Winter_theta_nre": -0.741454976,
    "KG Winter_pr_1_99_nre": 56,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 426,
    "KG Spring_theta_nre": -0.477335404,
    "KG Spring_pr_1_99_nre": 56,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 452,
    "G1 Fall_theta_nre": -0.24225078,
    "G1 Fall_pr_1_99_nre": 56,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 476,
    "G1 Winter_theta_nre": 0.035156016,
    "G1 Winter_pr_1_99_nre": 56,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 504,
    "G1 Spring_theta_nre": 0.267495895,
    "G1 Spring_pr_1_99_nre": 56,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 527,
    "G2 Fall_theta_nre": 0.535373787,
    "G2 Fall_pr_1_99_nre": 56,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 554,
    "G2 Winter_theta_nre": 0.776179819,
    "G2 Winter_pr_1_99_nre": 56,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 578,
    "G2 Spring_theta_nre": 1.031987954,
    "G2 Spring_pr_1_99_nre": 56,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 603,
    "G3 Fall_theta_nre": 1.342420341,
    "G3 Fall_pr_1_99_nre": 56,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 634,
    "G3 Winter_theta_nre": 1.557465971,
    "G3 Winter_pr_1_99_nre": 56,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 656,
    "G3 Spring_theta_nre": 1.829029208,
    "G3 Spring_pr_1_99_nre": 56,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 683
  },
  {
    "ID": 283,
    "KG Winter_theta_nre": -0.740892119,
    "KG Winter_pr_1_99_nre": 57,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 426,
    "KG Spring_theta_nre": -0.476643346,
    "KG Spring_pr_1_99_nre": 57,
    "KG Spring_ss_nre": 102,
    "KG Spring_dss_nre": 452,
    "G1 Fall_theta_nre": -0.239541623,
    "G1 Fall_pr_1_99_nre": 57,
    "G1 Fall_ss_nre": 102,
    "G1 Fall_dss_nre": 476,
    "G1 Winter_theta_nre": 0.040389584,
    "G1 Winter_pr_1_99_nre": 57,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 504,
    "G1 Spring_theta_nre": 0.269832528,
    "G1 Spring_pr_1_99_nre": 57,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 527,
    "G2 Fall_theta_nre": 0.542818452,
    "G2 Fall_pr_1_99_nre": 57,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 554,
    "G2 Winter_theta_nre": 0.777699491,
    "G2 Winter_pr_1_99_nre": 57,
    "G2 Winter_ss_nre": 102,
    "G2 Winter_dss_nre": 578,
    "G2 Spring_theta_nre": 1.034828569,
    "G2 Spring_pr_1_99_nre": 57,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 603,
    "G3 Fall_theta_nre": 1.345345422,
    "G3 Fall_pr_1_99_nre": 57,
    "G3 Fall_ss_nre": 102,
    "G3 Fall_dss_nre": 635,
    "G3 Winter_theta_nre": 1.558506176,
    "G3 Winter_pr_1_99_nre": 57,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 656,
    "G3 Spring_theta_nre": 1.831721425,
    "G3 Spring_pr_1_99_nre": 57,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 683
  },
  {
    "ID": 284,
    "KG Winter_theta_nre": -0.739369019,
    "KG Winter_pr_1_99_nre": 57,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 426,
    "KG Spring_theta_nre": -0.468449043,
    "KG Spring_pr_1_99_nre": 57,
    "KG Spring_ss_nre": 103,
    "KG Spring_dss_nre": 453,
    "G1 Fall_theta_nre": -0.221253961,
    "G1 Fall_pr_1_99_nre": 57,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 478,
    "G1 Winter_theta_nre": 0.047186006,
    "G1 Winter_pr_1_99_nre": 57,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 505,
    "G1 Spring_theta_nre": 0.277670892,
    "G1 Spring_pr_1_99_nre": 57,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 528,
    "G2 Fall_theta_nre": 0.543236572,
    "G2 Fall_pr_1_99_nre": 57,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 554,
    "G2 Winter_theta_nre": 0.78260131,
    "G2 Winter_pr_1_99_nre": 57,
    "G2 Winter_ss_nre": 103,
    "G2 Winter_dss_nre": 578,
    "G2 Spring_theta_nre": 1.041536136,
    "G2 Spring_pr_1_99_nre": 57,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 604,
    "G3 Fall_theta_nre": 1.361216142,
    "G3 Fall_pr_1_99_nre": 57,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 636,
    "G3 Winter_theta_nre": 1.561657542,
    "G3 Winter_pr_1_99_nre": 57,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 656,
    "G3 Spring_theta_nre": 1.8326662,
    "G3 Spring_pr_1_99_nre": 57,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 683
  },
  {
    "ID": 285,
    "KG Winter_theta_nre": -0.737502618,
    "KG Winter_pr_1_99_nre": 57,
    "KG Winter_ss_nre": 103,
    "KG Winter_dss_nre": 426,
    "KG Spring_theta_nre": -0.465855215,
    "KG Spring_pr_1_99_nre": 57,
    "KG Spring_ss_nre": 103,
    "KG Spring_dss_nre": 453,
    "G1 Fall_theta_nre": -0.216225099,
    "G1 Fall_pr_1_99_nre": 57,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 478,
    "G1 Winter_theta_nre": 0.049302933,
    "G1 Winter_pr_1_99_nre": 57,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 505,
    "G1 Spring_theta_nre": 0.283275045,
    "G1 Spring_pr_1_99_nre": 57,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 528,
    "G2 Fall_theta_nre": 0.54333361,
    "G2 Fall_pr_1_99_nre": 57,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 554,
    "G2 Winter_theta_nre": 0.782655828,
    "G2 Winter_pr_1_99_nre": 57,
    "G2 Winter_ss_nre": 103,
    "G2 Winter_dss_nre": 578,
    "G2 Spring_theta_nre": 1.041808722,
    "G2 Spring_pr_1_99_nre": 57,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 604,
    "G3 Fall_theta_nre": 1.361486095,
    "G3 Fall_pr_1_99_nre": 57,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 636,
    "G3 Winter_theta_nre": 1.562499538,
    "G3 Winter_pr_1_99_nre": 57,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 656,
    "G3 Spring_theta_nre": 1.83291654,
    "G3 Spring_pr_1_99_nre": 57,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 683
  },
  {
    "ID": 286,
    "KG Winter_theta_nre": -0.730780737,
    "KG Winter_pr_1_99_nre": 57,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 427,
    "KG Spring_theta_nre": -0.456979877,
    "KG Spring_pr_1_99_nre": 57,
    "KG Spring_ss_nre": 103,
    "KG Spring_dss_nre": 454,
    "G1 Fall_theta_nre": -0.216208722,
    "G1 Fall_pr_1_99_nre": 57,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 478,
    "G1 Winter_theta_nre": 0.055357727,
    "G1 Winter_pr_1_99_nre": 57,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 506,
    "G1 Spring_theta_nre": 0.283377413,
    "G1 Spring_pr_1_99_nre": 57,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 528,
    "G2 Fall_theta_nre": 0.544094774,
    "G2 Fall_pr_1_99_nre": 57,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 554,
    "G2 Winter_theta_nre": 0.786756523,
    "G2 Winter_pr_1_99_nre": 57,
    "G2 Winter_ss_nre": 103,
    "G2 Winter_dss_nre": 579,
    "G2 Spring_theta_nre": 1.047325427,
    "G2 Spring_pr_1_99_nre": 57,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 605,
    "G3 Fall_theta_nre": 1.368620583,
    "G3 Fall_pr_1_99_nre": 57,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 637,
    "G3 Winter_theta_nre": 1.57141459,
    "G3 Winter_pr_1_99_nre": 57,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 657,
    "G3 Spring_theta_nre": 1.837449494,
    "G3 Spring_pr_1_99_nre": 57,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 684
  },
  {
    "ID": 287,
    "KG Winter_theta_nre": -0.721292735,
    "KG Winter_pr_1_99_nre": 57,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 428,
    "KG Spring_theta_nre": -0.448901903,
    "KG Spring_pr_1_99_nre": 57,
    "KG Spring_ss_nre": 103,
    "KG Spring_dss_nre": 455,
    "G1 Fall_theta_nre": -0.202393833,
    "G1 Fall_pr_1_99_nre": 57,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 480,
    "G1 Winter_theta_nre": 0.059601258,
    "G1 Winter_pr_1_99_nre": 57,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 506,
    "G1 Spring_theta_nre": 0.292044608,
    "G1 Spring_pr_1_99_nre": 57,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 529,
    "G2 Fall_theta_nre": 0.549887031,
    "G2 Fall_pr_1_99_nre": 57,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 555,
    "G2 Winter_theta_nre": 0.79450037,
    "G2 Winter_pr_1_99_nre": 57,
    "G2 Winter_ss_nre": 103,
    "G2 Winter_dss_nre": 579,
    "G2 Spring_theta_nre": 1.048765165,
    "G2 Spring_pr_1_99_nre": 57,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 605,
    "G3 Fall_theta_nre": 1.375194999,
    "G3 Fall_pr_1_99_nre": 57,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 638,
    "G3 Winter_theta_nre": 1.576019652,
    "G3 Winter_pr_1_99_nre": 57,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 658,
    "G3 Spring_theta_nre": 1.839949699,
    "G3 Spring_pr_1_99_nre": 57,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 684
  },
  {
    "ID": 288,
    "KG Winter_theta_nre": -0.716542417,
    "KG Winter_pr_1_99_nre": 58,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 428,
    "KG Spring_theta_nre": -0.44278404,
    "KG Spring_pr_1_99_nre": 58,
    "KG Spring_ss_nre": 103,
    "KG Spring_dss_nre": 456,
    "G1 Fall_theta_nre": -0.19585816,
    "G1 Fall_pr_1_99_nre": 58,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 480,
    "G1 Winter_theta_nre": 0.069576046,
    "G1 Winter_pr_1_99_nre": 58,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 507,
    "G1 Spring_theta_nre": 0.303850259,
    "G1 Spring_pr_1_99_nre": 58,
    "G1 Spring_ss_nre": 102,
    "G1 Spring_dss_nre": 530,
    "G2 Fall_theta_nre": 0.55459296,
    "G2 Fall_pr_1_99_nre": 58,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 555,
    "G2 Winter_theta_nre": 0.795416136,
    "G2 Winter_pr_1_99_nre": 58,
    "G2 Winter_ss_nre": 103,
    "G2 Winter_dss_nre": 580,
    "G2 Spring_theta_nre": 1.052946889,
    "G2 Spring_pr_1_99_nre": 58,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 605,
    "G3 Fall_theta_nre": 1.377848861,
    "G3 Fall_pr_1_99_nre": 58,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 638,
    "G3 Winter_theta_nre": 1.578372544,
    "G3 Winter_pr_1_99_nre": 58,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 658,
    "G3 Spring_theta_nre": 1.847065605,
    "G3 Spring_pr_1_99_nre": 58,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 685
  },
  {
    "ID": 289,
    "KG Winter_theta_nre": -0.716138106,
    "KG Winter_pr_1_99_nre": 58,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 428,
    "KG Spring_theta_nre": -0.436959487,
    "KG Spring_pr_1_99_nre": 58,
    "KG Spring_ss_nre": 103,
    "KG Spring_dss_nre": 456,
    "G1 Fall_theta_nre": -0.195783483,
    "G1 Fall_pr_1_99_nre": 58,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 480,
    "G1 Winter_theta_nre": 0.074206967,
    "G1 Winter_pr_1_99_nre": 58,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 507,
    "G1 Spring_theta_nre": 0.309899859,
    "G1 Spring_pr_1_99_nre": 58,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 531,
    "G2 Fall_theta_nre": 0.562204678,
    "G2 Fall_pr_1_99_nre": 58,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 556,
    "G2 Winter_theta_nre": 0.798018391,
    "G2 Winter_pr_1_99_nre": 58,
    "G2 Winter_ss_nre": 103,
    "G2 Winter_dss_nre": 580,
    "G2 Spring_theta_nre": 1.057734304,
    "G2 Spring_pr_1_99_nre": 58,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 606,
    "G3 Fall_theta_nre": 1.378360557,
    "G3 Fall_pr_1_99_nre": 58,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 638,
    "G3 Winter_theta_nre": 1.583862659,
    "G3 Winter_pr_1_99_nre": 58,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 658,
    "G3 Spring_theta_nre": 1.848120354,
    "G3 Spring_pr_1_99_nre": 58,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 685
  },
  {
    "ID": 290,
    "KG Winter_theta_nre": -0.706941828,
    "KG Winter_pr_1_99_nre": 58,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 429,
    "KG Spring_theta_nre": -0.432559642,
    "KG Spring_pr_1_99_nre": 58,
    "KG Spring_ss_nre": 103,
    "KG Spring_dss_nre": 457,
    "G1 Fall_theta_nre": -0.191590211,
    "G1 Fall_pr_1_99_nre": 58,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 481,
    "G1 Winter_theta_nre": 0.077139456,
    "G1 Winter_pr_1_99_nre": 58,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 508,
    "G1 Spring_theta_nre": 0.311346785,
    "G1 Spring_pr_1_99_nre": 58,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 531,
    "G2 Fall_theta_nre": 0.567635081,
    "G2 Fall_pr_1_99_nre": 58,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 557,
    "G2 Winter_theta_nre": 0.813565337,
    "G2 Winter_pr_1_99_nre": 58,
    "G2 Winter_ss_nre": 103,
    "G2 Winter_dss_nre": 581,
    "G2 Spring_theta_nre": 1.063336958,
    "G2 Spring_pr_1_99_nre": 58,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 606,
    "G3 Fall_theta_nre": 1.379134684,
    "G3 Fall_pr_1_99_nre": 58,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 638,
    "G3 Winter_theta_nre": 1.584730983,
    "G3 Winter_pr_1_99_nre": 58,
    "G3 Winter_ss_nre": 103,
    "G3 Winter_dss_nre": 658,
    "G3 Spring_theta_nre": 1.848899883,
    "G3 Spring_pr_1_99_nre": 58,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 685
  },
  {
    "ID": 291,
    "KG Winter_theta_nre": -0.706748017,
    "KG Winter_pr_1_99_nre": 58,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 429,
    "KG Spring_theta_nre": -0.431767269,
    "KG Spring_pr_1_99_nre": 58,
    "KG Spring_ss_nre": 103,
    "KG Spring_dss_nre": 457,
    "G1 Fall_theta_nre": -0.183518873,
    "G1 Fall_pr_1_99_nre": 58,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 482,
    "G1 Winter_theta_nre": 0.077994011,
    "G1 Winter_pr_1_99_nre": 58,
    "G1 Winter_ss_nre": 103,
    "G1 Winter_dss_nre": 508,
    "G1 Spring_theta_nre": 0.318978835,
    "G1 Spring_pr_1_99_nre": 58,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 532,
    "G2 Fall_theta_nre": 0.568098978,
    "G2 Fall_pr_1_99_nre": 58,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 557,
    "G2 Winter_theta_nre": 0.813853586,
    "G2 Winter_pr_1_99_nre": 58,
    "G2 Winter_ss_nre": 103,
    "G2 Winter_dss_nre": 581,
    "G2 Spring_theta_nre": 1.068302713,
    "G2 Spring_pr_1_99_nre": 58,
    "G2 Spring_ss_nre": 102,
    "G2 Spring_dss_nre": 607,
    "G3 Fall_theta_nre": 1.381715649,
    "G3 Fall_pr_1_99_nre": 58,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 638,
    "G3 Winter_theta_nre": 1.590648405,
    "G3 Winter_pr_1_99_nre": 58,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 659,
    "G3 Spring_theta_nre": 1.849120552,
    "G3 Spring_pr_1_99_nre": 58,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 685
  },
  {
    "ID": 292,
    "KG Winter_theta_nre": -0.706562308,
    "KG Winter_pr_1_99_nre": 58,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 429,
    "KG Spring_theta_nre": -0.426444955,
    "KG Spring_pr_1_99_nre": 58,
    "KG Spring_ss_nre": 103,
    "KG Spring_dss_nre": 457,
    "G1 Fall_theta_nre": -0.181616612,
    "G1 Fall_pr_1_99_nre": 58,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 482,
    "G1 Winter_theta_nre": 0.080709477,
    "G1 Winter_pr_1_99_nre": 58,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 508,
    "G1 Spring_theta_nre": 0.320770497,
    "G1 Spring_pr_1_99_nre": 58,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 532,
    "G2 Fall_theta_nre": 0.56875731,
    "G2 Fall_pr_1_99_nre": 58,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 557,
    "G2 Winter_theta_nre": 0.816807509,
    "G2 Winter_pr_1_99_nre": 58,
    "G2 Winter_ss_nre": 103,
    "G2 Winter_dss_nre": 582,
    "G2 Spring_theta_nre": 1.078968481,
    "G2 Spring_pr_1_99_nre": 58,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 608,
    "G3 Fall_theta_nre": 1.38717875,
    "G3 Fall_pr_1_99_nre": 58,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 639,
    "G3 Winter_theta_nre": 1.591470184,
    "G3 Winter_pr_1_99_nre": 58,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 659,
    "G3 Spring_theta_nre": 1.86015821,
    "G3 Spring_pr_1_99_nre": 58,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 686
  },
  {
    "ID": 293,
    "KG Winter_theta_nre": -0.7025579,
    "KG Winter_pr_1_99_nre": 59,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 430,
    "KG Spring_theta_nre": -0.424902054,
    "KG Spring_pr_1_99_nre": 59,
    "KG Spring_ss_nre": 103,
    "KG Spring_dss_nre": 458,
    "G1 Fall_theta_nre": -0.181008163,
    "G1 Fall_pr_1_99_nre": 59,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 482,
    "G1 Winter_theta_nre": 0.08838478,
    "G1 Winter_pr_1_99_nre": 59,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 509,
    "G1 Spring_theta_nre": 0.331816533,
    "G1 Spring_pr_1_99_nre": 59,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 533,
    "G2 Fall_theta_nre": 0.571161118,
    "G2 Fall_pr_1_99_nre": 59,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 557,
    "G2 Winter_theta_nre": 0.821581212,
    "G2 Winter_pr_1_99_nre": 59,
    "G2 Winter_ss_nre": 103,
    "G2 Winter_dss_nre": 582,
    "G2 Spring_theta_nre": 1.081466449,
    "G2 Spring_pr_1_99_nre": 59,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 608,
    "G3 Fall_theta_nre": 1.387861417,
    "G3 Fall_pr_1_99_nre": 59,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 639,
    "G3 Winter_theta_nre": 1.604180915,
    "G3 Winter_pr_1_99_nre": 59,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 660,
    "G3 Spring_theta_nre": 1.864082271,
    "G3 Spring_pr_1_99_nre": 59,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 686
  },
  {
    "ID": 294,
    "KG Winter_theta_nre": -0.701527587,
    "KG Winter_pr_1_99_nre": 59,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 430,
    "KG Spring_theta_nre": -0.405007455,
    "KG Spring_pr_1_99_nre": 59,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 459,
    "G1 Fall_theta_nre": -0.172077117,
    "G1 Fall_pr_1_99_nre": 59,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 483,
    "G1 Winter_theta_nre": 0.090235414,
    "G1 Winter_pr_1_99_nre": 59,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 509,
    "G1 Spring_theta_nre": 0.33774728,
    "G1 Spring_pr_1_99_nre": 59,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 534,
    "G2 Fall_theta_nre": 0.576310795,
    "G2 Fall_pr_1_99_nre": 59,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 558,
    "G2 Winter_theta_nre": 0.836526022,
    "G2 Winter_pr_1_99_nre": 59,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 584,
    "G2 Spring_theta_nre": 1.086497329,
    "G2 Spring_pr_1_99_nre": 59,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 609,
    "G3 Fall_theta_nre": 1.396272795,
    "G3 Fall_pr_1_99_nre": 59,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 640,
    "G3 Winter_theta_nre": 1.60665537,
    "G3 Winter_pr_1_99_nre": 59,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 661,
    "G3 Spring_theta_nre": 1.867246534,
    "G3 Spring_pr_1_99_nre": 59,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 687
  },
  {
    "ID": 295,
    "KG Winter_theta_nre": -0.698760691,
    "KG Winter_pr_1_99_nre": 59,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 430,
    "KG Spring_theta_nre": -0.404357602,
    "KG Spring_pr_1_99_nre": 59,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 460,
    "G1 Fall_theta_nre": -0.16783708,
    "G1 Fall_pr_1_99_nre": 59,
    "G1 Fall_ss_nre": 103,
    "G1 Fall_dss_nre": 483,
    "G1 Winter_theta_nre": 0.092424497,
    "G1 Winter_pr_1_99_nre": 59,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 509,
    "G1 Spring_theta_nre": 0.339310395,
    "G1 Spring_pr_1_99_nre": 59,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 534,
    "G2 Fall_theta_nre": 0.581066255,
    "G2 Fall_pr_1_99_nre": 59,
    "G2 Fall_ss_nre": 103,
    "G2 Fall_dss_nre": 558,
    "G2 Winter_theta_nre": 0.840603271,
    "G2 Winter_pr_1_99_nre": 59,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 584,
    "G2 Spring_theta_nre": 1.092983989,
    "G2 Spring_pr_1_99_nre": 59,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 609,
    "G3 Fall_theta_nre": 1.417006876,
    "G3 Fall_pr_1_99_nre": 59,
    "G3 Fall_ss_nre": 103,
    "G3 Fall_dss_nre": 642,
    "G3 Winter_theta_nre": 1.608114411,
    "G3 Winter_pr_1_99_nre": 59,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 661,
    "G3 Spring_theta_nre": 1.867697331,
    "G3 Spring_pr_1_99_nre": 59,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 687
  },
  {
    "ID": 296,
    "KG Winter_theta_nre": -0.695417527,
    "KG Winter_pr_1_99_nre": 59,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 430,
    "KG Spring_theta_nre": -0.402174815,
    "KG Spring_pr_1_99_nre": 59,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 460,
    "G1 Fall_theta_nre": -0.162317748,
    "G1 Fall_pr_1_99_nre": 59,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 484,
    "G1 Winter_theta_nre": 0.097624666,
    "G1 Winter_pr_1_99_nre": 59,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 510,
    "G1 Spring_theta_nre": 0.339886862,
    "G1 Spring_pr_1_99_nre": 59,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 534,
    "G2 Fall_theta_nre": 0.587802115,
    "G2 Fall_pr_1_99_nre": 59,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 559,
    "G2 Winter_theta_nre": 0.840791858,
    "G2 Winter_pr_1_99_nre": 59,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 584,
    "G2 Spring_theta_nre": 1.094209946,
    "G2 Spring_pr_1_99_nre": 59,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 609,
    "G3 Fall_theta_nre": 1.420216926,
    "G3 Fall_pr_1_99_nre": 59,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 642,
    "G3 Winter_theta_nre": 1.615541204,
    "G3 Winter_pr_1_99_nre": 59,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 662,
    "G3 Spring_theta_nre": 1.869349094,
    "G3 Spring_pr_1_99_nre": 59,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 687
  },
  {
    "ID": 297,
    "KG Winter_theta_nre": -0.687220052,
    "KG Winter_pr_1_99_nre": 59,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 431,
    "KG Spring_theta_nre": -0.39625854,
    "KG Spring_pr_1_99_nre": 59,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 460,
    "G1 Fall_theta_nre": -0.155402064,
    "G1 Fall_pr_1_99_nre": 59,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 484,
    "G1 Winter_theta_nre": 0.099548227,
    "G1 Winter_pr_1_99_nre": 59,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 510,
    "G1 Spring_theta_nre": 0.343608912,
    "G1 Spring_pr_1_99_nre": 59,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 534,
    "G2 Fall_theta_nre": 0.594399645,
    "G2 Fall_pr_1_99_nre": 59,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 559,
    "G2 Winter_theta_nre": 0.843671073,
    "G2 Winter_pr_1_99_nre": 59,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 584,
    "G2 Spring_theta_nre": 1.096959472,
    "G2 Spring_pr_1_99_nre": 59,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 610,
    "G3 Fall_theta_nre": 1.427721308,
    "G3 Fall_pr_1_99_nre": 59,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 643,
    "G3 Winter_theta_nre": 1.615728771,
    "G3 Winter_pr_1_99_nre": 59,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 662,
    "G3 Spring_theta_nre": 1.873799589,
    "G3 Spring_pr_1_99_nre": 59,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 687
  },
  {
    "ID": 298,
    "KG Winter_theta_nre": -0.685382553,
    "KG Winter_pr_1_99_nre": 60,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 431,
    "KG Spring_theta_nre": -0.39137987,
    "KG Spring_pr_1_99_nre": 60,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 461,
    "G1 Fall_theta_nre": -0.154976585,
    "G1 Fall_pr_1_99_nre": 60,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 485,
    "G1 Winter_theta_nre": 0.099813912,
    "G1 Winter_pr_1_99_nre": 60,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 510,
    "G1 Spring_theta_nre": 0.347474059,
    "G1 Spring_pr_1_99_nre": 60,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 535,
    "G2 Fall_theta_nre": 0.59461634,
    "G2 Fall_pr_1_99_nre": 60,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 559,
    "G2 Winter_theta_nre": 0.846795874,
    "G2 Winter_pr_1_99_nre": 60,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 585,
    "G2 Spring_theta_nre": 1.100667147,
    "G2 Spring_pr_1_99_nre": 60,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 610,
    "G3 Fall_theta_nre": 1.431203292,
    "G3 Fall_pr_1_99_nre": 60,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 643,
    "G3 Winter_theta_nre": 1.618614602,
    "G3 Winter_pr_1_99_nre": 60,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 662,
    "G3 Spring_theta_nre": 1.874188212,
    "G3 Spring_pr_1_99_nre": 60,
    "G3 Spring_ss_nre": 103,
    "G3 Spring_dss_nre": 687
  },
  {
    "ID": 299,
    "KG Winter_theta_nre": -0.678759115,
    "KG Winter_pr_1_99_nre": 60,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 432,
    "KG Spring_theta_nre": -0.379259876,
    "KG Spring_pr_1_99_nre": 60,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 462,
    "G1 Fall_theta_nre": -0.149872938,
    "G1 Fall_pr_1_99_nre": 60,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 485,
    "G1 Winter_theta_nre": 0.101580603,
    "G1 Winter_pr_1_99_nre": 60,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 510,
    "G1 Spring_theta_nre": 0.348531981,
    "G1 Spring_pr_1_99_nre": 60,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 535,
    "G2 Fall_theta_nre": 0.600667059,
    "G2 Fall_pr_1_99_nre": 60,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 560,
    "G2 Winter_theta_nre": 0.847152336,
    "G2 Winter_pr_1_99_nre": 60,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 585,
    "G2 Spring_theta_nre": 1.109898028,
    "G2 Spring_pr_1_99_nre": 60,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 611,
    "G3 Fall_theta_nre": 1.434277582,
    "G3 Fall_pr_1_99_nre": 60,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 643,
    "G3 Winter_theta_nre": 1.620145845,
    "G3 Winter_pr_1_99_nre": 60,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 662,
    "G3 Spring_theta_nre": 1.881631042,
    "G3 Spring_pr_1_99_nre": 60,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 688
  },
  {
    "ID": 300,
    "KG Winter_theta_nre": -0.678303137,
    "KG Winter_pr_1_99_nre": 60,
    "KG Winter_ss_nre": 104,
    "KG Winter_dss_nre": 432,
    "KG Spring_theta_nre": -0.37224468,
    "KG Spring_pr_1_99_nre": 60,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 463,
    "G1 Fall_theta_nre": -0.149631288,
    "G1 Fall_pr_1_99_nre": 60,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 485,
    "G1 Winter_theta_nre": 0.103028002,
    "G1 Winter_pr_1_99_nre": 60,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 510,
    "G1 Spring_theta_nre": 0.357292801,
    "G1 Spring_pr_1_99_nre": 60,
    "G1 Spring_ss_nre": 103,
    "G1 Spring_dss_nre": 536,
    "G2 Fall_theta_nre": 0.603886433,
    "G2 Fall_pr_1_99_nre": 60,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 560,
    "G2 Winter_theta_nre": 0.85259947,
    "G2 Winter_pr_1_99_nre": 60,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 585,
    "G2 Spring_theta_nre": 1.110611603,
    "G2 Spring_pr_1_99_nre": 60,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 611,
    "G3 Fall_theta_nre": 1.434446635,
    "G3 Fall_pr_1_99_nre": 60,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 643,
    "G3 Winter_theta_nre": 1.620704509,
    "G3 Winter_pr_1_99_nre": 60,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 662,
    "G3 Spring_theta_nre": 1.882652824,
    "G3 Spring_pr_1_99_nre": 60,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 688
  },
  {
    "ID": 301,
    "KG Winter_theta_nre": -0.67225639,
    "KG Winter_pr_1_99_nre": 60,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 433,
    "KG Spring_theta_nre": -0.364597256,
    "KG Spring_pr_1_99_nre": 60,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 464,
    "G1 Fall_theta_nre": -0.148647501,
    "G1 Fall_pr_1_99_nre": 60,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 485,
    "G1 Winter_theta_nre": 0.108443879,
    "G1 Winter_pr_1_99_nre": 60,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 511,
    "G1 Spring_theta_nre": 0.362906916,
    "G1 Spring_pr_1_99_nre": 60,
    "G1 Spring_ss_nre": 104,
    "G1 Spring_dss_nre": 536,
    "G2 Fall_theta_nre": 0.604445374,
    "G2 Fall_pr_1_99_nre": 60,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 560,
    "G2 Winter_theta_nre": 0.857100233,
    "G2 Winter_pr_1_99_nre": 60,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 586,
    "G2 Spring_theta_nre": 1.112860745,
    "G2 Spring_pr_1_99_nre": 60,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 611,
    "G3 Fall_theta_nre": 1.436049796,
    "G3 Fall_pr_1_99_nre": 60,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 644,
    "G3 Winter_theta_nre": 1.623511472,
    "G3 Winter_pr_1_99_nre": 60,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 662,
    "G3 Spring_theta_nre": 1.886279101,
    "G3 Spring_pr_1_99_nre": 60,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 689
  },
  {
    "ID": 302,
    "KG Winter_theta_nre": -0.672052495,
    "KG Winter_pr_1_99_nre": 60,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 433,
    "KG Spring_theta_nre": -0.364303228,
    "KG Spring_pr_1_99_nre": 60,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 464,
    "G1 Fall_theta_nre": -0.145829108,
    "G1 Fall_pr_1_99_nre": 60,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 485,
    "G1 Winter_theta_nre": 0.11032001,
    "G1 Winter_pr_1_99_nre": 60,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 511,
    "G1 Spring_theta_nre": 0.37115532,
    "G1 Spring_pr_1_99_nre": 60,
    "G1 Spring_ss_nre": 104,
    "G1 Spring_dss_nre": 537,
    "G2 Fall_theta_nre": 0.609697862,
    "G2 Fall_pr_1_99_nre": 60,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 561,
    "G2 Winter_theta_nre": 0.862170314,
    "G2 Winter_pr_1_99_nre": 60,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 586,
    "G2 Spring_theta_nre": 1.113600083,
    "G2 Spring_pr_1_99_nre": 60,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 611,
    "G3 Fall_theta_nre": 1.436917797,
    "G3 Fall_pr_1_99_nre": 60,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 644,
    "G3 Winter_theta_nre": 1.625233146,
    "G3 Winter_pr_1_99_nre": 60,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 663,
    "G3 Spring_theta_nre": 1.889686786,
    "G3 Spring_pr_1_99_nre": 60,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 689
  },
  {
    "ID": 303,
    "KG Winter_theta_nre": -0.666527781,
    "KG Winter_pr_1_99_nre": 61,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 433,
    "KG Spring_theta_nre": -0.356954149,
    "KG Spring_pr_1_99_nre": 61,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 464,
    "G1 Fall_theta_nre": -0.139790409,
    "G1 Fall_pr_1_99_nre": 61,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 486,
    "G1 Winter_theta_nre": 0.116767243,
    "G1 Winter_pr_1_99_nre": 61,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 512,
    "G1 Spring_theta_nre": 0.382014404,
    "G1 Spring_pr_1_99_nre": 61,
    "G1 Spring_ss_nre": 104,
    "G1 Spring_dss_nre": 538,
    "G2 Fall_theta_nre": 0.615426443,
    "G2 Fall_pr_1_99_nre": 61,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 562,
    "G2 Winter_theta_nre": 0.864469759,
    "G2 Winter_pr_1_99_nre": 61,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 586,
    "G2 Spring_theta_nre": 1.114826457,
    "G2 Spring_pr_1_99_nre": 61,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 611,
    "G3 Fall_theta_nre": 1.447959658,
    "G3 Fall_pr_1_99_nre": 61,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 645,
    "G3 Winter_theta_nre": 1.62721579,
    "G3 Winter_pr_1_99_nre": 61,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 663,
    "G3 Spring_theta_nre": 1.903779556,
    "G3 Spring_pr_1_99_nre": 61,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 690
  },
  {
    "ID": 304,
    "KG Winter_theta_nre": -0.658777783,
    "KG Winter_pr_1_99_nre": 61,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 434,
    "KG Spring_theta_nre": -0.35689461,
    "KG Spring_pr_1_99_nre": 61,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 464,
    "G1 Fall_theta_nre": -0.136170432,
    "G1 Fall_pr_1_99_nre": 61,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 486,
    "G1 Winter_theta_nre": 0.128024046,
    "G1 Winter_pr_1_99_nre": 61,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 513,
    "G1 Spring_theta_nre": 0.386111493,
    "G1 Spring_pr_1_99_nre": 61,
    "G1 Spring_ss_nre": 104,
    "G1 Spring_dss_nre": 539,
    "G2 Fall_theta_nre": 0.615430376,
    "G2 Fall_pr_1_99_nre": 61,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 562,
    "G2 Winter_theta_nre": 0.864921979,
    "G2 Winter_pr_1_99_nre": 61,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 586,
    "G2 Spring_theta_nre": 1.119750771,
    "G2 Spring_pr_1_99_nre": 61,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 612,
    "G3 Fall_theta_nre": 1.457508106,
    "G3 Fall_pr_1_99_nre": 61,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 646,
    "G3 Winter_theta_nre": 1.628594837,
    "G3 Winter_pr_1_99_nre": 61,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 663,
    "G3 Spring_theta_nre": 1.904959758,
    "G3 Spring_pr_1_99_nre": 61,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 690
  },
  {
    "ID": 305,
    "KG Winter_theta_nre": -0.658768432,
    "KG Winter_pr_1_99_nre": 61,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 434,
    "KG Spring_theta_nre": -0.355322438,
    "KG Spring_pr_1_99_nre": 61,
    "KG Spring_ss_nre": 104,
    "KG Spring_dss_nre": 464,
    "G1 Fall_theta_nre": -0.135997459,
    "G1 Fall_pr_1_99_nre": 61,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 486,
    "G1 Winter_theta_nre": 0.135351857,
    "G1 Winter_pr_1_99_nre": 61,
    "G1 Winter_ss_nre": 104,
    "G1 Winter_dss_nre": 514,
    "G1 Spring_theta_nre": 0.388358968,
    "G1 Spring_pr_1_99_nre": 61,
    "G1 Spring_ss_nre": 104,
    "G1 Spring_dss_nre": 539,
    "G2 Fall_theta_nre": 0.615891881,
    "G2 Fall_pr_1_99_nre": 61,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 562,
    "G2 Winter_theta_nre": 0.867400642,
    "G2 Winter_pr_1_99_nre": 61,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 587,
    "G2 Spring_theta_nre": 1.128183776,
    "G2 Spring_pr_1_99_nre": 61,
    "G2 Spring_ss_nre": 103,
    "G2 Spring_dss_nre": 613,
    "G3 Fall_theta_nre": 1.459542629,
    "G3 Fall_pr_1_99_nre": 61,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 646,
    "G3 Winter_theta_nre": 1.630168897,
    "G3 Winter_pr_1_99_nre": 61,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 663,
    "G3 Spring_theta_nre": 1.908046166,
    "G3 Spring_pr_1_99_nre": 61,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 691
  },
  {
    "ID": 306,
    "KG Winter_theta_nre": -0.656123288,
    "KG Winter_pr_1_99_nre": 61,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 434,
    "KG Spring_theta_nre": -0.354547484,
    "KG Spring_pr_1_99_nre": 61,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 465,
    "G1 Fall_theta_nre": -0.135908019,
    "G1 Fall_pr_1_99_nre": 61,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 486,
    "G1 Winter_theta_nre": 0.140080807,
    "G1 Winter_pr_1_99_nre": 61,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 514,
    "G1 Spring_theta_nre": 0.400771041,
    "G1 Spring_pr_1_99_nre": 61,
    "G1 Spring_ss_nre": 104,
    "G1 Spring_dss_nre": 540,
    "G2 Fall_theta_nre": 0.619378102,
    "G2 Fall_pr_1_99_nre": 61,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 562,
    "G2 Winter_theta_nre": 0.867671676,
    "G2 Winter_pr_1_99_nre": 61,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 587,
    "G2 Spring_theta_nre": 1.131465509,
    "G2 Spring_pr_1_99_nre": 61,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 613,
    "G3 Fall_theta_nre": 1.460848849,
    "G3 Fall_pr_1_99_nre": 61,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 646,
    "G3 Winter_theta_nre": 1.632116939,
    "G3 Winter_pr_1_99_nre": 61,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 663,
    "G3 Spring_theta_nre": 1.909135348,
    "G3 Spring_pr_1_99_nre": 61,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 691
  },
  {
    "ID": 307,
    "KG Winter_theta_nre": -0.655506096,
    "KG Winter_pr_1_99_nre": 61,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 434,
    "KG Spring_theta_nre": -0.354441773,
    "KG Spring_pr_1_99_nre": 61,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 465,
    "G1 Fall_theta_nre": -0.122405308,
    "G1 Fall_pr_1_99_nre": 61,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 488,
    "G1 Winter_theta_nre": 0.142271564,
    "G1 Winter_pr_1_99_nre": 61,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 514,
    "G1 Spring_theta_nre": 0.402993014,
    "G1 Spring_pr_1_99_nre": 61,
    "G1 Spring_ss_nre": 104,
    "G1 Spring_dss_nre": 540,
    "G2 Fall_theta_nre": 0.620037513,
    "G2 Fall_pr_1_99_nre": 61,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 562,
    "G2 Winter_theta_nre": 0.874063924,
    "G2 Winter_pr_1_99_nre": 61,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 587,
    "G2 Spring_theta_nre": 1.135228271,
    "G2 Spring_pr_1_99_nre": 61,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 614,
    "G3 Fall_theta_nre": 1.461192963,
    "G3 Fall_pr_1_99_nre": 61,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 646,
    "G3 Winter_theta_nre": 1.637109749,
    "G3 Winter_pr_1_99_nre": 61,
    "G3 Winter_ss_nre": 104,
    "G3 Winter_dss_nre": 664,
    "G3 Spring_theta_nre": 1.910502976,
    "G3 Spring_pr_1_99_nre": 61,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 691
  },
  {
    "ID": 308,
    "KG Winter_theta_nre": -0.649906489,
    "KG Winter_pr_1_99_nre": 62,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 435,
    "KG Spring_theta_nre": -0.353885577,
    "KG Spring_pr_1_99_nre": 62,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 465,
    "G1 Fall_theta_nre": -0.120571251,
    "G1 Fall_pr_1_99_nre": 62,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 488,
    "G1 Winter_theta_nre": 0.144672146,
    "G1 Winter_pr_1_99_nre": 62,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 514,
    "G1 Spring_theta_nre": 0.418751225,
    "G1 Spring_pr_1_99_nre": 62,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 542,
    "G2 Fall_theta_nre": 0.63322256,
    "G2 Fall_pr_1_99_nre": 62,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 563,
    "G2 Winter_theta_nre": 0.875281838,
    "G2 Winter_pr_1_99_nre": 62,
    "G2 Winter_ss_nre": 104,
    "G2 Winter_dss_nre": 588,
    "G2 Spring_theta_nre": 1.135372587,
    "G2 Spring_pr_1_99_nre": 62,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 614,
    "G3 Fall_theta_nre": 1.470967975,
    "G3 Fall_pr_1_99_nre": 62,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 647,
    "G3 Winter_theta_nre": 1.640501198,
    "G3 Winter_pr_1_99_nre": 62,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 664,
    "G3 Spring_theta_nre": 1.913764956,
    "G3 Spring_pr_1_99_nre": 62,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 691
  },
  {
    "ID": 309,
    "KG Winter_theta_nre": -0.647425857,
    "KG Winter_pr_1_99_nre": 62,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 435,
    "KG Spring_theta_nre": -0.345610526,
    "KG Spring_pr_1_99_nre": 62,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 465,
    "G1 Fall_theta_nre": -0.120220998,
    "G1 Fall_pr_1_99_nre": 62,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 488,
    "G1 Winter_theta_nre": 0.145991664,
    "G1 Winter_pr_1_99_nre": 62,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 515,
    "G1 Spring_theta_nre": 0.418963425,
    "G1 Spring_pr_1_99_nre": 62,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 542,
    "G2 Fall_theta_nre": 0.639941447,
    "G2 Fall_pr_1_99_nre": 62,
    "G2 Fall_ss_nre": 104,
    "G2 Fall_dss_nre": 564,
    "G2 Winter_theta_nre": 0.880592241,
    "G2 Winter_pr_1_99_nre": 62,
    "G2 Winter_ss_nre": 105,
    "G2 Winter_dss_nre": 588,
    "G2 Spring_theta_nre": 1.136251788,
    "G2 Spring_pr_1_99_nre": 62,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 614,
    "G3 Fall_theta_nre": 1.471019848,
    "G3 Fall_pr_1_99_nre": 62,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 647,
    "G3 Winter_theta_nre": 1.646497741,
    "G3 Winter_pr_1_99_nre": 62,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 665,
    "G3 Spring_theta_nre": 1.914363993,
    "G3 Spring_pr_1_99_nre": 62,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 691
  },
  {
    "ID": 310,
    "KG Winter_theta_nre": -0.64458943,
    "KG Winter_pr_1_99_nre": 62,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 436,
    "KG Spring_theta_nre": -0.344130226,
    "KG Spring_pr_1_99_nre": 62,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 466,
    "G1 Fall_theta_nre": -0.116727011,
    "G1 Fall_pr_1_99_nre": 62,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 488,
    "G1 Winter_theta_nre": 0.150725785,
    "G1 Winter_pr_1_99_nre": 62,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 515,
    "G1 Spring_theta_nre": 0.423127055,
    "G1 Spring_pr_1_99_nre": 62,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 542,
    "G2 Fall_theta_nre": 0.640536177,
    "G2 Fall_pr_1_99_nre": 62,
    "G2 Fall_ss_nre": 105,
    "G2 Fall_dss_nre": 564,
    "G2 Winter_theta_nre": 0.885010822,
    "G2 Winter_pr_1_99_nre": 62,
    "G2 Winter_ss_nre": 105,
    "G2 Winter_dss_nre": 589,
    "G2 Spring_theta_nre": 1.138624281,
    "G2 Spring_pr_1_99_nre": 62,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 614,
    "G3 Fall_theta_nre": 1.471890145,
    "G3 Fall_pr_1_99_nre": 62,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 647,
    "G3 Winter_theta_nre": 1.655096644,
    "G3 Winter_pr_1_99_nre": 62,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 666,
    "G3 Spring_theta_nre": 1.916292231,
    "G3 Spring_pr_1_99_nre": 62,
    "G3 Spring_ss_nre": 104,
    "G3 Spring_dss_nre": 692
  },
  {
    "ID": 311,
    "KG Winter_theta_nre": -0.642112988,
    "KG Winter_pr_1_99_nre": 62,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 436,
    "KG Spring_theta_nre": -0.326686138,
    "KG Spring_pr_1_99_nre": 62,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 467,
    "G1 Fall_theta_nre": -0.112199837,
    "G1 Fall_pr_1_99_nre": 62,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 489,
    "G1 Winter_theta_nre": 0.155269323,
    "G1 Winter_pr_1_99_nre": 62,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 516,
    "G1 Spring_theta_nre": 0.426714125,
    "G1 Spring_pr_1_99_nre": 62,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 543,
    "G2 Fall_theta_nre": 0.640545491,
    "G2 Fall_pr_1_99_nre": 62,
    "G2 Fall_ss_nre": 105,
    "G2 Fall_dss_nre": 564,
    "G2 Winter_theta_nre": 0.900487711,
    "G2 Winter_pr_1_99_nre": 62,
    "G2 Winter_ss_nre": 105,
    "G2 Winter_dss_nre": 590,
    "G2 Spring_theta_nre": 1.14563198,
    "G2 Spring_pr_1_99_nre": 62,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 615,
    "G3 Fall_theta_nre": 1.47769432,
    "G3 Fall_pr_1_99_nre": 62,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 648,
    "G3 Winter_theta_nre": 1.657051405,
    "G3 Winter_pr_1_99_nre": 62,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 666,
    "G3 Spring_theta_nre": 1.925053367,
    "G3 Spring_pr_1_99_nre": 62,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 693
  },
  {
    "ID": 312,
    "KG Winter_theta_nre": -0.622714268,
    "KG Winter_pr_1_99_nre": 62,
    "KG Winter_ss_nre": 105,
    "KG Winter_dss_nre": 438,
    "KG Spring_theta_nre": -0.324102064,
    "KG Spring_pr_1_99_nre": 62,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 468,
    "G1 Fall_theta_nre": -0.109644296,
    "G1 Fall_pr_1_99_nre": 62,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 489,
    "G1 Winter_theta_nre": 0.157745793,
    "G1 Winter_pr_1_99_nre": 62,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 516,
    "G1 Spring_theta_nre": 0.427818502,
    "G1 Spring_pr_1_99_nre": 62,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 543,
    "G2 Fall_theta_nre": 0.642240278,
    "G2 Fall_pr_1_99_nre": 62,
    "G2 Fall_ss_nre": 105,
    "G2 Fall_dss_nre": 564,
    "G2 Winter_theta_nre": 0.905909821,
    "G2 Winter_pr_1_99_nre": 62,
    "G2 Winter_ss_nre": 105,
    "G2 Winter_dss_nre": 591,
    "G2 Spring_theta_nre": 1.146483161,
    "G2 Spring_pr_1_99_nre": 62,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 615,
    "G3 Fall_theta_nre": 1.479351637,
    "G3 Fall_pr_1_99_nre": 62,
    "G3 Fall_ss_nre": 104,
    "G3 Fall_dss_nre": 648,
    "G3 Winter_theta_nre": 1.659912736,
    "G3 Winter_pr_1_99_nre": 62,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 666,
    "G3 Spring_theta_nre": 1.926241249,
    "G3 Spring_pr_1_99_nre": 62,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 693
  },
  {
    "ID": 313,
    "KG Winter_theta_nre": -0.618436511,
    "KG Winter_pr_1_99_nre": 63,
    "KG Winter_ss_nre": 106,
    "KG Winter_dss_nre": 438,
    "KG Spring_theta_nre": -0.323726122,
    "KG Spring_pr_1_99_nre": 63,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 468,
    "G1 Fall_theta_nre": -0.108377787,
    "G1 Fall_pr_1_99_nre": 63,
    "G1 Fall_ss_nre": 104,
    "G1 Fall_dss_nre": 489,
    "G1 Winter_theta_nre": 0.158516531,
    "G1 Winter_pr_1_99_nre": 63,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 516,
    "G1 Spring_theta_nre": 0.432438756,
    "G1 Spring_pr_1_99_nre": 63,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 543,
    "G2 Fall_theta_nre": 0.64482361,
    "G2 Fall_pr_1_99_nre": 63,
    "G2 Fall_ss_nre": 105,
    "G2 Fall_dss_nre": 564,
    "G2 Winter_theta_nre": 0.911949419,
    "G2 Winter_pr_1_99_nre": 63,
    "G2 Winter_ss_nre": 105,
    "G2 Winter_dss_nre": 591,
    "G2 Spring_theta_nre": 1.14669712,
    "G2 Spring_pr_1_99_nre": 63,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 615,
    "G3 Fall_theta_nre": 1.496096906,
    "G3 Fall_pr_1_99_nre": 63,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 650,
    "G3 Winter_theta_nre": 1.662713516,
    "G3 Winter_pr_1_99_nre": 63,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 666,
    "G3 Spring_theta_nre": 1.926382216,
    "G3 Spring_pr_1_99_nre": 63,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 693
  },
  {
    "ID": 314,
    "KG Winter_theta_nre": -0.616605841,
    "KG Winter_pr_1_99_nre": 63,
    "KG Winter_ss_nre": 106,
    "KG Winter_dss_nre": 438,
    "KG Spring_theta_nre": -0.318106965,
    "KG Spring_pr_1_99_nre": 63,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 468,
    "G1 Fall_theta_nre": -0.101157194,
    "G1 Fall_pr_1_99_nre": 63,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 490,
    "G1 Winter_theta_nre": 0.158735079,
    "G1 Winter_pr_1_99_nre": 63,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 516,
    "G1 Spring_theta_nre": 0.43775955,
    "G1 Spring_pr_1_99_nre": 63,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 544,
    "G2 Fall_theta_nre": 0.655483784,
    "G2 Fall_pr_1_99_nre": 63,
    "G2 Fall_ss_nre": 105,
    "G2 Fall_dss_nre": 566,
    "G2 Winter_theta_nre": 0.916171112,
    "G2 Winter_pr_1_99_nre": 63,
    "G2 Winter_ss_nre": 105,
    "G2 Winter_dss_nre": 592,
    "G2 Spring_theta_nre": 1.157618014,
    "G2 Spring_pr_1_99_nre": 63,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 616,
    "G3 Fall_theta_nre": 1.500831783,
    "G3 Fall_pr_1_99_nre": 63,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 650,
    "G3 Winter_theta_nre": 1.668431447,
    "G3 Winter_pr_1_99_nre": 63,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 667,
    "G3 Spring_theta_nre": 1.926664437,
    "G3 Spring_pr_1_99_nre": 63,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 693
  },
  {
    "ID": 315,
    "KG Winter_theta_nre": -0.608376321,
    "KG Winter_pr_1_99_nre": 63,
    "KG Winter_ss_nre": 106,
    "KG Winter_dss_nre": 439,
    "KG Spring_theta_nre": -0.316763386,
    "KG Spring_pr_1_99_nre": 63,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 468,
    "G1 Fall_theta_nre": -0.086220879,
    "G1 Fall_pr_1_99_nre": 63,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 491,
    "G1 Winter_theta_nre": 0.15923981,
    "G1 Winter_pr_1_99_nre": 63,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 516,
    "G1 Spring_theta_nre": 0.437923625,
    "G1 Spring_pr_1_99_nre": 63,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 544,
    "G2 Fall_theta_nre": 0.696024501,
    "G2 Fall_pr_1_99_nre": 63,
    "G2 Fall_ss_nre": 105,
    "G2 Fall_dss_nre": 570,
    "G2 Winter_theta_nre": 0.921234718,
    "G2 Winter_pr_1_99_nre": 63,
    "G2 Winter_ss_nre": 105,
    "G2 Winter_dss_nre": 592,
    "G2 Spring_theta_nre": 1.160181264,
    "G2 Spring_pr_1_99_nre": 63,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 616,
    "G3 Fall_theta_nre": 1.519489669,
    "G3 Fall_pr_1_99_nre": 63,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 652,
    "G3 Winter_theta_nre": 1.670969915,
    "G3 Winter_pr_1_99_nre": 63,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 667,
    "G3 Spring_theta_nre": 1.927479707,
    "G3 Spring_pr_1_99_nre": 63,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 693
  },
  {
    "ID": 316,
    "KG Winter_theta_nre": -0.604772857,
    "KG Winter_pr_1_99_nre": 63,
    "KG Winter_ss_nre": 106,
    "KG Winter_dss_nre": 440,
    "KG Spring_theta_nre": -0.315482094,
    "KG Spring_pr_1_99_nre": 63,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 468,
    "G1 Fall_theta_nre": -0.085748545,
    "G1 Fall_pr_1_99_nre": 63,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 491,
    "G1 Winter_theta_nre": 0.169984895,
    "G1 Winter_pr_1_99_nre": 63,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 517,
    "G1 Spring_theta_nre": 0.444663654,
    "G1 Spring_pr_1_99_nre": 63,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 544,
    "G2 Fall_theta_nre": 0.699216503,
    "G2 Fall_pr_1_99_nre": 63,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 570,
    "G2 Winter_theta_nre": 0.925671631,
    "G2 Winter_pr_1_99_nre": 63,
    "G2 Winter_ss_nre": 105,
    "G2 Winter_dss_nre": 593,
    "G2 Spring_theta_nre": 1.161339024,
    "G2 Spring_pr_1_99_nre": 63,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 616,
    "G3 Fall_theta_nre": 1.519633531,
    "G3 Fall_pr_1_99_nre": 63,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 652,
    "G3 Winter_theta_nre": 1.676159953,
    "G3 Winter_pr_1_99_nre": 63,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 668,
    "G3 Spring_theta_nre": 1.930141487,
    "G3 Spring_pr_1_99_nre": 63,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 693
  },
  {
    "ID": 317,
    "KG Winter_theta_nre": -0.597457227,
    "KG Winter_pr_1_99_nre": 63,
    "KG Winter_ss_nre": 106,
    "KG Winter_dss_nre": 440,
    "KG Spring_theta_nre": -0.312039854,
    "KG Spring_pr_1_99_nre": 63,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 469,
    "G1 Fall_theta_nre": -0.082151998,
    "G1 Fall_pr_1_99_nre": 63,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 492,
    "G1 Winter_theta_nre": 0.181872586,
    "G1 Winter_pr_1_99_nre": 63,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 518,
    "G1 Spring_theta_nre": 0.445989831,
    "G1 Spring_pr_1_99_nre": 63,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 545,
    "G2 Fall_theta_nre": 0.70748622,
    "G2 Fall_pr_1_99_nre": 63,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 571,
    "G2 Winter_theta_nre": 0.927342938,
    "G2 Winter_pr_1_99_nre": 63,
    "G2 Winter_ss_nre": 105,
    "G2 Winter_dss_nre": 593,
    "G2 Spring_theta_nre": 1.167752605,
    "G2 Spring_pr_1_99_nre": 63,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 617,
    "G3 Fall_theta_nre": 1.519825797,
    "G3 Fall_pr_1_99_nre": 63,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 652,
    "G3 Winter_theta_nre": 1.676994088,
    "G3 Winter_pr_1_99_nre": 63,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 668,
    "G3 Spring_theta_nre": 1.93640545,
    "G3 Spring_pr_1_99_nre": 63,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 694
  },
  {
    "ID": 318,
    "KG Winter_theta_nre": -0.596096536,
    "KG Winter_pr_1_99_nre": 64,
    "KG Winter_ss_nre": 106,
    "KG Winter_dss_nre": 440,
    "KG Spring_theta_nre": -0.31112267,
    "KG Spring_pr_1_99_nre": 64,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 469,
    "G1 Fall_theta_nre": -0.079866686,
    "G1 Fall_pr_1_99_nre": 64,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 492,
    "G1 Winter_theta_nre": 0.182690713,
    "G1 Winter_pr_1_99_nre": 64,
    "G1 Winter_ss_nre": 105,
    "G1 Winter_dss_nre": 518,
    "G1 Spring_theta_nre": 0.450651033,
    "G1 Spring_pr_1_99_nre": 64,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 545,
    "G2 Fall_theta_nre": 0.708222992,
    "G2 Fall_pr_1_99_nre": 64,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 571,
    "G2 Winter_theta_nre": 0.935280068,
    "G2 Winter_pr_1_99_nre": 64,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 594,
    "G2 Spring_theta_nre": 1.171629954,
    "G2 Spring_pr_1_99_nre": 64,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 617,
    "G3 Fall_theta_nre": 1.523984938,
    "G3 Fall_pr_1_99_nre": 64,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 652,
    "G3 Winter_theta_nre": 1.683531202,
    "G3 Winter_pr_1_99_nre": 64,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 668,
    "G3 Spring_theta_nre": 1.947254091,
    "G3 Spring_pr_1_99_nre": 64,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 695
  },
  {
    "ID": 319,
    "KG Winter_theta_nre": -0.588065276,
    "KG Winter_pr_1_99_nre": 64,
    "KG Winter_ss_nre": 106,
    "KG Winter_dss_nre": 441,
    "KG Spring_theta_nre": -0.311057289,
    "KG Spring_pr_1_99_nre": 64,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 469,
    "G1 Fall_theta_nre": -0.074739647,
    "G1 Fall_pr_1_99_nre": 64,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 493,
    "G1 Winter_theta_nre": 0.198687019,
    "G1 Winter_pr_1_99_nre": 64,
    "G1 Winter_ss_nre": 106,
    "G1 Winter_dss_nre": 520,
    "G1 Spring_theta_nre": 0.451506045,
    "G1 Spring_pr_1_99_nre": 64,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 545,
    "G2 Fall_theta_nre": 0.71105859,
    "G2 Fall_pr_1_99_nre": 64,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 571,
    "G2 Winter_theta_nre": 0.94264847,
    "G2 Winter_pr_1_99_nre": 64,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 594,
    "G2 Spring_theta_nre": 1.175686388,
    "G2 Spring_pr_1_99_nre": 64,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 618,
    "G3 Fall_theta_nre": 1.532855233,
    "G3 Fall_pr_1_99_nre": 64,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 653,
    "G3 Winter_theta_nre": 1.683756417,
    "G3 Winter_pr_1_99_nre": 64,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 668,
    "G3 Spring_theta_nre": 1.948276947,
    "G3 Spring_pr_1_99_nre": 64,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 695
  },
  {
    "ID": 320,
    "KG Winter_theta_nre": -0.577007664,
    "KG Winter_pr_1_99_nre": 64,
    "KG Winter_ss_nre": 106,
    "KG Winter_dss_nre": 442,
    "KG Spring_theta_nre": -0.310240256,
    "KG Spring_pr_1_99_nre": 64,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 469,
    "G1 Fall_theta_nre": -0.07084574,
    "G1 Fall_pr_1_99_nre": 64,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 493,
    "G1 Winter_theta_nre": 0.19911912,
    "G1 Winter_pr_1_99_nre": 64,
    "G1 Winter_ss_nre": 106,
    "G1 Winter_dss_nre": 520,
    "G1 Spring_theta_nre": 0.452665529,
    "G1 Spring_pr_1_99_nre": 64,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 545,
    "G2 Fall_theta_nre": 0.717240489,
    "G2 Fall_pr_1_99_nre": 64,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 572,
    "G2 Winter_theta_nre": 0.94456526,
    "G2 Winter_pr_1_99_nre": 64,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 594,
    "G2 Spring_theta_nre": 1.180132188,
    "G2 Spring_pr_1_99_nre": 64,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 618,
    "G3 Fall_theta_nre": 1.53357574,
    "G3 Fall_pr_1_99_nre": 64,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 653,
    "G3 Winter_theta_nre": 1.687187877,
    "G3 Winter_pr_1_99_nre": 64,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 669,
    "G3 Spring_theta_nre": 1.950507119,
    "G3 Spring_pr_1_99_nre": 64,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 695
  },
  {
    "ID": 321,
    "KG Winter_theta_nre": -0.576659452,
    "KG Winter_pr_1_99_nre": 64,
    "KG Winter_ss_nre": 106,
    "KG Winter_dss_nre": 442,
    "KG Spring_theta_nre": -0.299877778,
    "KG Spring_pr_1_99_nre": 64,
    "KG Spring_ss_nre": 105,
    "KG Spring_dss_nre": 470,
    "G1 Fall_theta_nre": -0.065748948,
    "G1 Fall_pr_1_99_nre": 64,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 493,
    "G1 Winter_theta_nre": 0.209672451,
    "G1 Winter_pr_1_99_nre": 64,
    "G1 Winter_ss_nre": 106,
    "G1 Winter_dss_nre": 521,
    "G1 Spring_theta_nre": 0.453005197,
    "G1 Spring_pr_1_99_nre": 64,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 545,
    "G2 Fall_theta_nre": 0.724870325,
    "G2 Fall_pr_1_99_nre": 64,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 572,
    "G2 Winter_theta_nre": 0.948019235,
    "G2 Winter_pr_1_99_nre": 64,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 595,
    "G2 Spring_theta_nre": 1.180932805,
    "G2 Spring_pr_1_99_nre": 64,
    "G2 Spring_ss_nre": 104,
    "G2 Spring_dss_nre": 618,
    "G3 Fall_theta_nre": 1.535222843,
    "G3 Fall_pr_1_99_nre": 64,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 654,
    "G3 Winter_theta_nre": 1.687285411,
    "G3 Winter_pr_1_99_nre": 64,
    "G3 Winter_ss_nre": 105,
    "G3 Winter_dss_nre": 669,
    "G3 Spring_theta_nre": 1.951439426,
    "G3 Spring_pr_1_99_nre": 64,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 695
  },
  {
    "ID": 322,
    "KG Winter_theta_nre": -0.572644024,
    "KG Winter_pr_1_99_nre": 64,
    "KG Winter_ss_nre": 106,
    "KG Winter_dss_nre": 443,
    "KG Spring_theta_nre": -0.288152423,
    "KG Spring_pr_1_99_nre": 64,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 471,
    "G1 Fall_theta_nre": -0.063438079,
    "G1 Fall_pr_1_99_nre": 64,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 494,
    "G1 Winter_theta_nre": 0.213051201,
    "G1 Winter_pr_1_99_nre": 64,
    "G1 Winter_ss_nre": 106,
    "G1 Winter_dss_nre": 521,
    "G1 Spring_theta_nre": 0.454352394,
    "G1 Spring_pr_1_99_nre": 64,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 545,
    "G2 Fall_theta_nre": 0.72566724,
    "G2 Fall_pr_1_99_nre": 64,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 573,
    "G2 Winter_theta_nre": 0.955780317,
    "G2 Winter_pr_1_99_nre": 64,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 596,
    "G2 Spring_theta_nre": 1.18787744,
    "G2 Spring_pr_1_99_nre": 64,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 619,
    "G3 Fall_theta_nre": 1.53685806,
    "G3 Fall_pr_1_99_nre": 64,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 654,
    "G3 Winter_theta_nre": 1.696528828,
    "G3 Winter_pr_1_99_nre": 64,
    "G3 Winter_ss_nre": 106,
    "G3 Winter_dss_nre": 670,
    "G3 Spring_theta_nre": 1.953064026,
    "G3 Spring_pr_1_99_nre": 64,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 695
  },
  {
    "ID": 323,
    "KG Winter_theta_nre": -0.562022912,
    "KG Winter_pr_1_99_nre": 65,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 444,
    "KG Spring_theta_nre": -0.282436519,
    "KG Spring_pr_1_99_nre": 65,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 472,
    "G1 Fall_theta_nre": -0.063146412,
    "G1 Fall_pr_1_99_nre": 65,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 494,
    "G1 Winter_theta_nre": 0.216387757,
    "G1 Winter_pr_1_99_nre": 65,
    "G1 Winter_ss_nre": 106,
    "G1 Winter_dss_nre": 522,
    "G1 Spring_theta_nre": 0.459193506,
    "G1 Spring_pr_1_99_nre": 65,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 546,
    "G2 Fall_theta_nre": 0.72591916,
    "G2 Fall_pr_1_99_nre": 65,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 573,
    "G2 Winter_theta_nre": 0.964801783,
    "G2 Winter_pr_1_99_nre": 65,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 596,
    "G2 Spring_theta_nre": 1.188323048,
    "G2 Spring_pr_1_99_nre": 65,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 619,
    "G3 Fall_theta_nre": 1.538120582,
    "G3 Fall_pr_1_99_nre": 65,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 654,
    "G3 Winter_theta_nre": 1.69711684,
    "G3 Winter_pr_1_99_nre": 65,
    "G3 Winter_ss_nre": 106,
    "G3 Winter_dss_nre": 670,
    "G3 Spring_theta_nre": 1.956837296,
    "G3 Spring_pr_1_99_nre": 65,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 696
  },
  {
    "ID": 324,
    "KG Winter_theta_nre": -0.554586107,
    "KG Winter_pr_1_99_nre": 65,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 445,
    "KG Spring_theta_nre": -0.277952914,
    "KG Spring_pr_1_99_nre": 65,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 472,
    "G1 Fall_theta_nre": -0.058076883,
    "G1 Fall_pr_1_99_nre": 65,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 494,
    "G1 Winter_theta_nre": 0.21844414,
    "G1 Winter_pr_1_99_nre": 65,
    "G1 Winter_ss_nre": 106,
    "G1 Winter_dss_nre": 522,
    "G1 Spring_theta_nre": 0.459367456,
    "G1 Spring_pr_1_99_nre": 65,
    "G1 Spring_ss_nre": 105,
    "G1 Spring_dss_nre": 546,
    "G2 Fall_theta_nre": 0.732993764,
    "G2 Fall_pr_1_99_nre": 65,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 573,
    "G2 Winter_theta_nre": 0.971076349,
    "G2 Winter_pr_1_99_nre": 65,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 597,
    "G2 Spring_theta_nre": 1.189024334,
    "G2 Spring_pr_1_99_nre": 65,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 619,
    "G3 Fall_theta_nre": 1.539900797,
    "G3 Fall_pr_1_99_nre": 65,
    "G3 Fall_ss_nre": 105,
    "G3 Fall_dss_nre": 654,
    "G3 Winter_theta_nre": 1.706903265,
    "G3 Winter_pr_1_99_nre": 65,
    "G3 Winter_ss_nre": 106,
    "G3 Winter_dss_nre": 671,
    "G3 Spring_theta_nre": 1.961256938,
    "G3 Spring_pr_1_99_nre": 65,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 696
  },
  {
    "ID": 325,
    "KG Winter_theta_nre": -0.553680207,
    "KG Winter_pr_1_99_nre": 65,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 445,
    "KG Spring_theta_nre": -0.275612781,
    "KG Spring_pr_1_99_nre": 65,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 472,
    "G1 Fall_theta_nre": -0.057224742,
    "G1 Fall_pr_1_99_nre": 65,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 494,
    "G1 Winter_theta_nre": 0.221322982,
    "G1 Winter_pr_1_99_nre": 65,
    "G1 Winter_ss_nre": 106,
    "G1 Winter_dss_nre": 522,
    "G1 Spring_theta_nre": 0.475247944,
    "G1 Spring_pr_1_99_nre": 65,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 548,
    "G2 Fall_theta_nre": 0.733785806,
    "G2 Fall_pr_1_99_nre": 65,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 573,
    "G2 Winter_theta_nre": 0.97116426,
    "G2 Winter_pr_1_99_nre": 65,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 597,
    "G2 Spring_theta_nre": 1.19258884,
    "G2 Spring_pr_1_99_nre": 65,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 619,
    "G3 Fall_theta_nre": 1.546742873,
    "G3 Fall_pr_1_99_nre": 65,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 655,
    "G3 Winter_theta_nre": 1.711929262,
    "G3 Winter_pr_1_99_nre": 65,
    "G3 Winter_ss_nre": 106,
    "G3 Winter_dss_nre": 671,
    "G3 Spring_theta_nre": 1.961450437,
    "G3 Spring_pr_1_99_nre": 65,
    "G3 Spring_ss_nre": 105,
    "G3 Spring_dss_nre": 696
  },
  {
    "ID": 326,
    "KG Winter_theta_nre": -0.546608973,
    "KG Winter_pr_1_99_nre": 65,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 445,
    "KG Spring_theta_nre": -0.274410596,
    "KG Spring_pr_1_99_nre": 65,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 473,
    "G1 Fall_theta_nre": -0.048344386,
    "G1 Fall_pr_1_99_nre": 65,
    "G1 Fall_ss_nre": 105,
    "G1 Fall_dss_nre": 495,
    "G1 Winter_theta_nre": 0.223789078,
    "G1 Winter_pr_1_99_nre": 65,
    "G1 Winter_ss_nre": 106,
    "G1 Winter_dss_nre": 522,
    "G1 Spring_theta_nre": 0.479531894,
    "G1 Spring_pr_1_99_nre": 65,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 548,
    "G2 Fall_theta_nre": 0.750928357,
    "G2 Fall_pr_1_99_nre": 65,
    "G2 Fall_ss_nre": 106,
    "G2 Fall_dss_nre": 575,
    "G2 Winter_theta_nre": 0.973280138,
    "G2 Winter_pr_1_99_nre": 65,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 597,
    "G2 Spring_theta_nre": 1.193237142,
    "G2 Spring_pr_1_99_nre": 65,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 619,
    "G3 Fall_theta_nre": 1.547363077,
    "G3 Fall_pr_1_99_nre": 65,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 655,
    "G3 Winter_theta_nre": 1.713602251,
    "G3 Winter_pr_1_99_nre": 65,
    "G3 Winter_ss_nre": 106,
    "G3 Winter_dss_nre": 671,
    "G3 Spring_theta_nre": 1.966710869,
    "G3 Spring_pr_1_99_nre": 65,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 697
  },
  {
    "ID": 327,
    "KG Winter_theta_nre": -0.538668629,
    "KG Winter_pr_1_99_nre": 65,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 446,
    "KG Spring_theta_nre": -0.269822273,
    "KG Spring_pr_1_99_nre": 65,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 473,
    "G1 Fall_theta_nre": -0.041310712,
    "G1 Fall_pr_1_99_nre": 65,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 496,
    "G1 Winter_theta_nre": 0.224979391,
    "G1 Winter_pr_1_99_nre": 65,
    "G1 Winter_ss_nre": 106,
    "G1 Winter_dss_nre": 522,
    "G1 Spring_theta_nre": 0.479681971,
    "G1 Spring_pr_1_99_nre": 65,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 548,
    "G2 Fall_theta_nre": 0.758826781,
    "G2 Fall_pr_1_99_nre": 65,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 576,
    "G2 Winter_theta_nre": 0.976236157,
    "G2 Winter_pr_1_99_nre": 65,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 598,
    "G2 Spring_theta_nre": 1.201714967,
    "G2 Spring_pr_1_99_nre": 65,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 620,
    "G3 Fall_theta_nre": 1.547631069,
    "G3 Fall_pr_1_99_nre": 65,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 655,
    "G3 Winter_theta_nre": 1.714604955,
    "G3 Winter_pr_1_99_nre": 65,
    "G3 Winter_ss_nre": 106,
    "G3 Winter_dss_nre": 671,
    "G3 Spring_theta_nre": 1.969894524,
    "G3 Spring_pr_1_99_nre": 65,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 697
  },
  {
    "ID": 328,
    "KG Winter_theta_nre": -0.537855283,
    "KG Winter_pr_1_99_nre": 66,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 446,
    "KG Spring_theta_nre": -0.266985238,
    "KG Spring_pr_1_99_nre": 66,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 473,
    "G1 Fall_theta_nre": -0.039120421,
    "G1 Fall_pr_1_99_nre": 66,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 496,
    "G1 Winter_theta_nre": 0.23317486,
    "G1 Winter_pr_1_99_nre": 66,
    "G1 Winter_ss_nre": 106,
    "G1 Winter_dss_nre": 523,
    "G1 Spring_theta_nre": 0.492872614,
    "G1 Spring_pr_1_99_nre": 66,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 549,
    "G2 Fall_theta_nre": 0.759978878,
    "G2 Fall_pr_1_99_nre": 66,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 576,
    "G2 Winter_theta_nre": 0.980824661,
    "G2 Winter_pr_1_99_nre": 66,
    "G2 Winter_ss_nre": 106,
    "G2 Winter_dss_nre": 598,
    "G2 Spring_theta_nre": 1.202989174,
    "G2 Spring_pr_1_99_nre": 66,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 620,
    "G3 Fall_theta_nre": 1.550495397,
    "G3 Fall_pr_1_99_nre": 66,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 655,
    "G3 Winter_theta_nre": 1.7155705,
    "G3 Winter_pr_1_99_nre": 66,
    "G3 Winter_ss_nre": 106,
    "G3 Winter_dss_nre": 672,
    "G3 Spring_theta_nre": 1.975503836,
    "G3 Spring_pr_1_99_nre": 66,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 698
  },
  {
    "ID": 329,
    "KG Winter_theta_nre": -0.536112283,
    "KG Winter_pr_1_99_nre": 66,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 446,
    "KG Spring_theta_nre": -0.266224037,
    "KG Spring_pr_1_99_nre": 66,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 473,
    "G1 Fall_theta_nre": -0.038088679,
    "G1 Fall_pr_1_99_nre": 66,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 496,
    "G1 Winter_theta_nre": 0.2498554,
    "G1 Winter_pr_1_99_nre": 66,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 525,
    "G1 Spring_theta_nre": 0.494454289,
    "G1 Spring_pr_1_99_nre": 66,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 549,
    "G2 Fall_theta_nre": 0.764469019,
    "G2 Fall_pr_1_99_nre": 66,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 576,
    "G2 Winter_theta_nre": 0.983838261,
    "G2 Winter_pr_1_99_nre": 66,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 598,
    "G2 Spring_theta_nre": 1.20639774,
    "G2 Spring_pr_1_99_nre": 66,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 621,
    "G3 Fall_theta_nre": 1.561657536,
    "G3 Fall_pr_1_99_nre": 66,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 656,
    "G3 Winter_theta_nre": 1.729756448,
    "G3 Winter_pr_1_99_nre": 66,
    "G3 Winter_ss_nre": 106,
    "G3 Winter_dss_nre": 673,
    "G3 Spring_theta_nre": 1.977824824,
    "G3 Spring_pr_1_99_nre": 66,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 698
  },
  {
    "ID": 330,
    "KG Winter_theta_nre": -0.531632228,
    "KG Winter_pr_1_99_nre": 66,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 447,
    "KG Spring_theta_nre": -0.263339551,
    "KG Spring_pr_1_99_nre": 66,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 474,
    "G1 Fall_theta_nre": -0.032030206,
    "G1 Fall_pr_1_99_nre": 66,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 497,
    "G1 Winter_theta_nre": 0.252522097,
    "G1 Winter_pr_1_99_nre": 66,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 525,
    "G1 Spring_theta_nre": 0.498861147,
    "G1 Spring_pr_1_99_nre": 66,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 550,
    "G2 Fall_theta_nre": 0.766540912,
    "G2 Fall_pr_1_99_nre": 66,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 577,
    "G2 Winter_theta_nre": 0.987870208,
    "G2 Winter_pr_1_99_nre": 66,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 599,
    "G2 Spring_theta_nre": 1.209140056,
    "G2 Spring_pr_1_99_nre": 66,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 621,
    "G3 Fall_theta_nre": 1.562840506,
    "G3 Fall_pr_1_99_nre": 66,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 656,
    "G3 Winter_theta_nre": 1.734123783,
    "G3 Winter_pr_1_99_nre": 66,
    "G3 Winter_ss_nre": 106,
    "G3 Winter_dss_nre": 673,
    "G3 Spring_theta_nre": 1.978821734,
    "G3 Spring_pr_1_99_nre": 66,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 698
  },
  {
    "ID": 331,
    "KG Winter_theta_nre": -0.527966491,
    "KG Winter_pr_1_99_nre": 66,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 447,
    "KG Spring_theta_nre": -0.263273215,
    "KG Spring_pr_1_99_nre": 66,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 474,
    "G1 Fall_theta_nre": -0.031595715,
    "G1 Fall_pr_1_99_nre": 66,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 497,
    "G1 Winter_theta_nre": 0.259361415,
    "G1 Winter_pr_1_99_nre": 66,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 526,
    "G1 Spring_theta_nre": 0.501694503,
    "G1 Spring_pr_1_99_nre": 66,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 550,
    "G2 Fall_theta_nre": 0.786139098,
    "G2 Fall_pr_1_99_nre": 66,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 579,
    "G2 Winter_theta_nre": 0.988212607,
    "G2 Winter_pr_1_99_nre": 66,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 599,
    "G2 Spring_theta_nre": 1.221765771,
    "G2 Spring_pr_1_99_nre": 66,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 622,
    "G3 Fall_theta_nre": 1.564696339,
    "G3 Fall_pr_1_99_nre": 66,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 656,
    "G3 Winter_theta_nre": 1.736184589,
    "G3 Winter_pr_1_99_nre": 66,
    "G3 Winter_ss_nre": 106,
    "G3 Winter_dss_nre": 674,
    "G3 Spring_theta_nre": 1.980633955,
    "G3 Spring_pr_1_99_nre": 66,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 698
  },
  {
    "ID": 332,
    "KG Winter_theta_nre": -0.524993105,
    "KG Winter_pr_1_99_nre": 66,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 448,
    "KG Spring_theta_nre": -0.25894082,
    "KG Spring_pr_1_99_nre": 66,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 474,
    "G1 Fall_theta_nre": -0.031301747,
    "G1 Fall_pr_1_99_nre": 66,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 497,
    "G1 Winter_theta_nre": 0.260430806,
    "G1 Winter_pr_1_99_nre": 66,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 526,
    "G1 Spring_theta_nre": 0.506980158,
    "G1 Spring_pr_1_99_nre": 66,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 551,
    "G2 Fall_theta_nre": 0.792521269,
    "G2 Fall_pr_1_99_nre": 66,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 579,
    "G2 Winter_theta_nre": 0.988321025,
    "G2 Winter_pr_1_99_nre": 66,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 599,
    "G2 Spring_theta_nre": 1.224467077,
    "G2 Spring_pr_1_99_nre": 66,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 622,
    "G3 Fall_theta_nre": 1.566345794,
    "G3 Fall_pr_1_99_nre": 66,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 657,
    "G3 Winter_theta_nre": 1.742197533,
    "G3 Winter_pr_1_99_nre": 66,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 674,
    "G3 Spring_theta_nre": 1.990235938,
    "G3 Spring_pr_1_99_nre": 66,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 699
  },
  {
    "ID": 333,
    "KG Winter_theta_nre": -0.52208082,
    "KG Winter_pr_1_99_nre": 67,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 448,
    "KG Spring_theta_nre": -0.252315782,
    "KG Spring_pr_1_99_nre": 67,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 475,
    "G1 Fall_theta_nre": -0.0205579,
    "G1 Fall_pr_1_99_nre": 67,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 498,
    "G1 Winter_theta_nre": 0.266227669,
    "G1 Winter_pr_1_99_nre": 67,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 527,
    "G1 Spring_theta_nre": 0.512759177,
    "G1 Spring_pr_1_99_nre": 67,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 551,
    "G2 Fall_theta_nre": 0.797821228,
    "G2 Fall_pr_1_99_nre": 67,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 580,
    "G2 Winter_theta_nre": 0.9915411,
    "G2 Winter_pr_1_99_nre": 67,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 599,
    "G2 Spring_theta_nre": 1.228876915,
    "G2 Spring_pr_1_99_nre": 67,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 623,
    "G3 Fall_theta_nre": 1.571127919,
    "G3 Fall_pr_1_99_nre": 67,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 657,
    "G3 Winter_theta_nre": 1.742294339,
    "G3 Winter_pr_1_99_nre": 67,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 674,
    "G3 Spring_theta_nre": 1.996207344,
    "G3 Spring_pr_1_99_nre": 67,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 700
  },
  {
    "ID": 334,
    "KG Winter_theta_nre": -0.509855654,
    "KG Winter_pr_1_99_nre": 67,
    "KG Winter_ss_nre": 107,
    "KG Winter_dss_nre": 449,
    "KG Spring_theta_nre": -0.250223038,
    "KG Spring_pr_1_99_nre": 67,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 475,
    "G1 Fall_theta_nre": -0.008968813,
    "G1 Fall_pr_1_99_nre": 67,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 499,
    "G1 Winter_theta_nre": 0.270512675,
    "G1 Winter_pr_1_99_nre": 67,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 527,
    "G1 Spring_theta_nre": 0.520892068,
    "G1 Spring_pr_1_99_nre": 67,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 552,
    "G2 Fall_theta_nre": 0.7985145,
    "G2 Fall_pr_1_99_nre": 67,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 580,
    "G2 Winter_theta_nre": 1.007398828,
    "G2 Winter_pr_1_99_nre": 67,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 601,
    "G2 Spring_theta_nre": 1.229077784,
    "G2 Spring_pr_1_99_nre": 67,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 623,
    "G3 Fall_theta_nre": 1.575086696,
    "G3 Fall_pr_1_99_nre": 67,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 658,
    "G3 Winter_theta_nre": 1.746236395,
    "G3 Winter_pr_1_99_nre": 67,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 675,
    "G3 Spring_theta_nre": 1.999015583,
    "G3 Spring_pr_1_99_nre": 67,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 700
  },
  {
    "ID": 335,
    "KG Winter_theta_nre": -0.504797502,
    "KG Winter_pr_1_99_nre": 67,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 450,
    "KG Spring_theta_nre": -0.249851814,
    "KG Spring_pr_1_99_nre": 67,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 475,
    "G1 Fall_theta_nre": -0.002097405,
    "G1 Fall_pr_1_99_nre": 67,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 500,
    "G1 Winter_theta_nre": 0.270596607,
    "G1 Winter_pr_1_99_nre": 67,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 527,
    "G1 Spring_theta_nre": 0.522267395,
    "G1 Spring_pr_1_99_nre": 67,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 552,
    "G2 Fall_theta_nre": 0.804795315,
    "G2 Fall_pr_1_99_nre": 67,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 580,
    "G2 Winter_theta_nre": 1.012787642,
    "G2 Winter_pr_1_99_nre": 67,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 601,
    "G2 Spring_theta_nre": 1.237607932,
    "G2 Spring_pr_1_99_nre": 67,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 624,
    "G3 Fall_theta_nre": 1.576263974,
    "G3 Fall_pr_1_99_nre": 67,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 658,
    "G3 Winter_theta_nre": 1.750248698,
    "G3 Winter_pr_1_99_nre": 67,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 675,
    "G3 Spring_theta_nre": 2.001677045,
    "G3 Spring_pr_1_99_nre": 67,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 700
  },
  {
    "ID": 336,
    "KG Winter_theta_nre": -0.502435871,
    "KG Winter_pr_1_99_nre": 67,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 450,
    "KG Spring_theta_nre": -0.243425809,
    "KG Spring_pr_1_99_nre": 67,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 476,
    "G1 Fall_theta_nre": 0.002257907,
    "G1 Fall_pr_1_99_nre": 67,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 500,
    "G1 Winter_theta_nre": 0.290851596,
    "G1 Winter_pr_1_99_nre": 67,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 529,
    "G1 Spring_theta_nre": 0.522534391,
    "G1 Spring_pr_1_99_nre": 67,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 552,
    "G2 Fall_theta_nre": 0.807977882,
    "G2 Fall_pr_1_99_nre": 67,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 581,
    "G2 Winter_theta_nre": 1.01339888,
    "G2 Winter_pr_1_99_nre": 67,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 601,
    "G2 Spring_theta_nre": 1.241324045,
    "G2 Spring_pr_1_99_nre": 67,
    "G2 Spring_ss_nre": 105,
    "G2 Spring_dss_nre": 624,
    "G3 Fall_theta_nre": 1.585508867,
    "G3 Fall_pr_1_99_nre": 67,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 659,
    "G3 Winter_theta_nre": 1.752957097,
    "G3 Winter_pr_1_99_nre": 67,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 675,
    "G3 Spring_theta_nre": 2.005781561,
    "G3 Spring_pr_1_99_nre": 67,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 701
  },
  {
    "ID": 337,
    "KG Winter_theta_nre": -0.487783772,
    "KG Winter_pr_1_99_nre": 67,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 451,
    "KG Spring_theta_nre": -0.24299018,
    "KG Spring_pr_1_99_nre": 67,
    "KG Spring_ss_nre": 106,
    "KG Spring_dss_nre": 476,
    "G1 Fall_theta_nre": 0.003637509,
    "G1 Fall_pr_1_99_nre": 67,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 500,
    "G1 Winter_theta_nre": 0.293702588,
    "G1 Winter_pr_1_99_nre": 67,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 529,
    "G1 Spring_theta_nre": 0.522719458,
    "G1 Spring_pr_1_99_nre": 67,
    "G1 Spring_ss_nre": 106,
    "G1 Spring_dss_nre": 552,
    "G2 Fall_theta_nre": 0.809884464,
    "G2 Fall_pr_1_99_nre": 67,
    "G2 Fall_ss_nre": 107,
    "G2 Fall_dss_nre": 581,
    "G2 Winter_theta_nre": 1.013818749,
    "G2 Winter_pr_1_99_nre": 67,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 601,
    "G2 Spring_theta_nre": 1.248958488,
    "G2 Spring_pr_1_99_nre": 67,
    "G2 Spring_ss_nre": 106,
    "G2 Spring_dss_nre": 625,
    "G3 Fall_theta_nre": 1.599189561,
    "G3 Fall_pr_1_99_nre": 67,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 660,
    "G3 Winter_theta_nre": 1.757051577,
    "G3 Winter_pr_1_99_nre": 67,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 676,
    "G3 Spring_theta_nre": 2.009432795,
    "G3 Spring_pr_1_99_nre": 67,
    "G3 Spring_ss_nre": 106,
    "G3 Spring_dss_nre": 701
  },
  {
    "ID": 338,
    "KG Winter_theta_nre": -0.483067611,
    "KG Winter_pr_1_99_nre": 68,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 452,
    "KG Spring_theta_nre": -0.234513318,
    "KG Spring_pr_1_99_nre": 68,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 477,
    "G1 Fall_theta_nre": 0.00558335,
    "G1 Fall_pr_1_99_nre": 68,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 501,
    "G1 Winter_theta_nre": 0.296311502,
    "G1 Winter_pr_1_99_nre": 68,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 530,
    "G1 Spring_theta_nre": 0.524779531,
    "G1 Spring_pr_1_99_nre": 68,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 552,
    "G2 Fall_theta_nre": 0.819342875,
    "G2 Fall_pr_1_99_nre": 68,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 582,
    "G2 Winter_theta_nre": 1.01483761,
    "G2 Winter_pr_1_99_nre": 68,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 601,
    "G2 Spring_theta_nre": 1.249023693,
    "G2 Spring_pr_1_99_nre": 68,
    "G2 Spring_ss_nre": 106,
    "G2 Spring_dss_nre": 625,
    "G3 Fall_theta_nre": 1.600232199,
    "G3 Fall_pr_1_99_nre": 68,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 660,
    "G3 Winter_theta_nre": 1.758445253,
    "G3 Winter_pr_1_99_nre": 68,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 676,
    "G3 Spring_theta_nre": 2.012997146,
    "G3 Spring_pr_1_99_nre": 68,
    "G3 Spring_ss_nre": 107,
    "G3 Spring_dss_nre": 701
  },
  {
    "ID": 339,
    "KG Winter_theta_nre": -0.482727346,
    "KG Winter_pr_1_99_nre": 68,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 452,
    "KG Spring_theta_nre": -0.233798323,
    "KG Spring_pr_1_99_nre": 68,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 477,
    "G1 Fall_theta_nre": 0.00600399,
    "G1 Fall_pr_1_99_nre": 68,
    "G1 Fall_ss_nre": 106,
    "G1 Fall_dss_nre": 501,
    "G1 Winter_theta_nre": 0.299941474,
    "G1 Winter_pr_1_99_nre": 68,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 530,
    "G1 Spring_theta_nre": 0.526440599,
    "G1 Spring_pr_1_99_nre": 68,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 553,
    "G2 Fall_theta_nre": 0.820258441,
    "G2 Fall_pr_1_99_nre": 68,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 582,
    "G2 Winter_theta_nre": 1.018529345,
    "G2 Winter_pr_1_99_nre": 68,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 602,
    "G2 Spring_theta_nre": 1.25265381,
    "G2 Spring_pr_1_99_nre": 68,
    "G2 Spring_ss_nre": 106,
    "G2 Spring_dss_nre": 625,
    "G3 Fall_theta_nre": 1.601286691,
    "G3 Fall_pr_1_99_nre": 68,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 660,
    "G3 Winter_theta_nre": 1.758548525,
    "G3 Winter_pr_1_99_nre": 68,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 676,
    "G3 Spring_theta_nre": 2.01669727,
    "G3 Spring_pr_1_99_nre": 68,
    "G3 Spring_ss_nre": 107,
    "G3 Spring_dss_nre": 702
  },
  {
    "ID": 340,
    "KG Winter_theta_nre": -0.47561535,
    "KG Winter_pr_1_99_nre": 68,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 452,
    "KG Spring_theta_nre": -0.227561822,
    "KG Spring_pr_1_99_nre": 68,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 477,
    "G1 Fall_theta_nre": 0.016116949,
    "G1 Fall_pr_1_99_nre": 68,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 502,
    "G1 Winter_theta_nre": 0.301693464,
    "G1 Winter_pr_1_99_nre": 68,
    "G1 Winter_ss_nre": 107,
    "G1 Winter_dss_nre": 530,
    "G1 Spring_theta_nre": 0.536379605,
    "G1 Spring_pr_1_99_nre": 68,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 554,
    "G2 Fall_theta_nre": 0.821845455,
    "G2 Fall_pr_1_99_nre": 68,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 582,
    "G2 Winter_theta_nre": 1.027573836,
    "G2 Winter_pr_1_99_nre": 68,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 603,
    "G2 Spring_theta_nre": 1.279496771,
    "G2 Spring_pr_1_99_nre": 68,
    "G2 Spring_ss_nre": 106,
    "G2 Spring_dss_nre": 628,
    "G3 Fall_theta_nre": 1.602534016,
    "G3 Fall_pr_1_99_nre": 68,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 660,
    "G3 Winter_theta_nre": 1.764428843,
    "G3 Winter_pr_1_99_nre": 68,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 676,
    "G3 Spring_theta_nre": 2.025490086,
    "G3 Spring_pr_1_99_nre": 68,
    "G3 Spring_ss_nre": 107,
    "G3 Spring_dss_nre": 703
  },
  {
    "ID": 341,
    "KG Winter_theta_nre": -0.471700789,
    "KG Winter_pr_1_99_nre": 68,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 453,
    "KG Spring_theta_nre": -0.213071774,
    "KG Spring_pr_1_99_nre": 68,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 479,
    "G1 Fall_theta_nre": 0.020860155,
    "G1 Fall_pr_1_99_nre": 68,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 502,
    "G1 Winter_theta_nre": 0.306295909,
    "G1 Winter_pr_1_99_nre": 68,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 531,
    "G1 Spring_theta_nre": 0.542617167,
    "G1 Spring_pr_1_99_nre": 68,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 554,
    "G2 Fall_theta_nre": 0.822642586,
    "G2 Fall_pr_1_99_nre": 68,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 582,
    "G2 Winter_theta_nre": 1.031061505,
    "G2 Winter_pr_1_99_nre": 68,
    "G2 Winter_ss_nre": 107,
    "G2 Winter_dss_nre": 603,
    "G2 Spring_theta_nre": 1.280059554,
    "G2 Spring_pr_1_99_nre": 68,
    "G2 Spring_ss_nre": 106,
    "G2 Spring_dss_nre": 628,
    "G3 Fall_theta_nre": 1.603015813,
    "G3 Fall_pr_1_99_nre": 68,
    "G3 Fall_ss_nre": 106,
    "G3 Fall_dss_nre": 660,
    "G3 Winter_theta_nre": 1.766471727,
    "G3 Winter_pr_1_99_nre": 68,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 677,
    "G3 Spring_theta_nre": 2.026863705,
    "G3 Spring_pr_1_99_nre": 68,
    "G3 Spring_ss_nre": 107,
    "G3 Spring_dss_nre": 703
  },
  {
    "ID": 342,
    "KG Winter_theta_nre": -0.466223402,
    "KG Winter_pr_1_99_nre": 68,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 453,
    "KG Spring_theta_nre": -0.210903566,
    "KG Spring_pr_1_99_nre": 68,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 479,
    "G1 Fall_theta_nre": 0.025597772,
    "G1 Fall_pr_1_99_nre": 68,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 503,
    "G1 Winter_theta_nre": 0.30801186,
    "G1 Winter_pr_1_99_nre": 68,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 531,
    "G1 Spring_theta_nre": 0.546598726,
    "G1 Spring_pr_1_99_nre": 68,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 555,
    "G2 Fall_theta_nre": 0.822739122,
    "G2 Fall_pr_1_99_nre": 68,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 582,
    "G2 Winter_theta_nre": 1.032248741,
    "G2 Winter_pr_1_99_nre": 68,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 603,
    "G2 Spring_theta_nre": 1.280748109,
    "G2 Spring_pr_1_99_nre": 68,
    "G2 Spring_ss_nre": 106,
    "G2 Spring_dss_nre": 628,
    "G3 Fall_theta_nre": 1.607245369,
    "G3 Fall_pr_1_99_nre": 68,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 661,
    "G3 Winter_theta_nre": 1.772034896,
    "G3 Winter_pr_1_99_nre": 68,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 677,
    "G3 Spring_theta_nre": 2.029904363,
    "G3 Spring_pr_1_99_nre": 68,
    "G3 Spring_ss_nre": 107,
    "G3 Spring_dss_nre": 703
  },
  {
    "ID": 343,
    "KG Winter_theta_nre": -0.461865238,
    "KG Winter_pr_1_99_nre": 69,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 454,
    "KG Spring_theta_nre": -0.208632917,
    "KG Spring_pr_1_99_nre": 69,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 479,
    "G1 Fall_theta_nre": 0.032092829,
    "G1 Fall_pr_1_99_nre": 69,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 503,
    "G1 Winter_theta_nre": 0.309384239,
    "G1 Winter_pr_1_99_nre": 69,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 531,
    "G1 Spring_theta_nre": 0.548895519,
    "G1 Spring_pr_1_99_nre": 69,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 555,
    "G2 Fall_theta_nre": 0.823754989,
    "G2 Fall_pr_1_99_nre": 69,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 582,
    "G2 Winter_theta_nre": 1.032347016,
    "G2 Winter_pr_1_99_nre": 69,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 603,
    "G2 Spring_theta_nre": 1.280891745,
    "G2 Spring_pr_1_99_nre": 69,
    "G2 Spring_ss_nre": 106,
    "G2 Spring_dss_nre": 628,
    "G3 Fall_theta_nre": 1.612164692,
    "G3 Fall_pr_1_99_nre": 69,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 661,
    "G3 Winter_theta_nre": 1.773190683,
    "G3 Winter_pr_1_99_nre": 69,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 677,
    "G3 Spring_theta_nre": 2.031123909,
    "G3 Spring_pr_1_99_nre": 69,
    "G3 Spring_ss_nre": 107,
    "G3 Spring_dss_nre": 703
  },
  {
    "ID": 344,
    "KG Winter_theta_nre": -0.459052638,
    "KG Winter_pr_1_99_nre": 69,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 454,
    "KG Spring_theta_nre": -0.20135447,
    "KG Spring_pr_1_99_nre": 69,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 480,
    "G1 Fall_theta_nre": 0.033278132,
    "G1 Fall_pr_1_99_nre": 69,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 503,
    "G1 Winter_theta_nre": 0.312733914,
    "G1 Winter_pr_1_99_nre": 69,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 531,
    "G1 Spring_theta_nre": 0.550531002,
    "G1 Spring_pr_1_99_nre": 69,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 555,
    "G2 Fall_theta_nre": 0.82386453,
    "G2 Fall_pr_1_99_nre": 69,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 582,
    "G2 Winter_theta_nre": 1.043817826,
    "G2 Winter_pr_1_99_nre": 69,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 604,
    "G2 Spring_theta_nre": 1.294977623,
    "G2 Spring_pr_1_99_nre": 69,
    "G2 Spring_ss_nre": 106,
    "G2 Spring_dss_nre": 629,
    "G3 Fall_theta_nre": 1.618462289,
    "G3 Fall_pr_1_99_nre": 69,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 662,
    "G3 Winter_theta_nre": 1.776003379,
    "G3 Winter_pr_1_99_nre": 69,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 678,
    "G3 Spring_theta_nre": 2.038200833,
    "G3 Spring_pr_1_99_nre": 69,
    "G3 Spring_ss_nre": 107,
    "G3 Spring_dss_nre": 704
  },
  {
    "ID": 345,
    "KG Winter_theta_nre": -0.457322131,
    "KG Winter_pr_1_99_nre": 69,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 454,
    "KG Spring_theta_nre": -0.196827441,
    "KG Spring_pr_1_99_nre": 69,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 480,
    "G1 Fall_theta_nre": 0.035656559,
    "G1 Fall_pr_1_99_nre": 69,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 504,
    "G1 Winter_theta_nre": 0.314727793,
    "G1 Winter_pr_1_99_nre": 69,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 531,
    "G1 Spring_theta_nre": 0.550647233,
    "G1 Spring_pr_1_99_nre": 69,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 555,
    "G2 Fall_theta_nre": 0.825600828,
    "G2 Fall_pr_1_99_nre": 69,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 583,
    "G2 Winter_theta_nre": 1.046944159,
    "G2 Winter_pr_1_99_nre": 69,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 605,
    "G2 Spring_theta_nre": 1.298579074,
    "G2 Spring_pr_1_99_nre": 69,
    "G2 Spring_ss_nre": 106,
    "G2 Spring_dss_nre": 630,
    "G3 Fall_theta_nre": 1.623256625,
    "G3 Fall_pr_1_99_nre": 69,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 662,
    "G3 Winter_theta_nre": 1.778776155,
    "G3 Winter_pr_1_99_nre": 69,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 678,
    "G3 Spring_theta_nre": 2.042309722,
    "G3 Spring_pr_1_99_nre": 69,
    "G3 Spring_ss_nre": 107,
    "G3 Spring_dss_nre": 704
  },
  {
    "ID": 346,
    "KG Winter_theta_nre": -0.454267168,
    "KG Winter_pr_1_99_nre": 69,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 455,
    "KG Spring_theta_nre": -0.188937817,
    "KG Spring_pr_1_99_nre": 69,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 481,
    "G1 Fall_theta_nre": 0.041067,
    "G1 Fall_pr_1_99_nre": 69,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 504,
    "G1 Winter_theta_nre": 0.31786558,
    "G1 Winter_pr_1_99_nre": 69,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 532,
    "G1 Spring_theta_nre": 0.550656304,
    "G1 Spring_pr_1_99_nre": 69,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 555,
    "G2 Fall_theta_nre": 0.828579317,
    "G2 Fall_pr_1_99_nre": 69,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 583,
    "G2 Winter_theta_nre": 1.047565226,
    "G2 Winter_pr_1_99_nre": 69,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 605,
    "G2 Spring_theta_nre": 1.305144202,
    "G2 Spring_pr_1_99_nre": 69,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 631,
    "G3 Fall_theta_nre": 1.629318544,
    "G3 Fall_pr_1_99_nre": 69,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 663,
    "G3 Winter_theta_nre": 1.780136488,
    "G3 Winter_pr_1_99_nre": 69,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 678,
    "G3 Spring_theta_nre": 2.0507022,
    "G3 Spring_pr_1_99_nre": 69,
    "G3 Spring_ss_nre": 107,
    "G3 Spring_dss_nre": 705
  },
  {
    "ID": 347,
    "KG Winter_theta_nre": -0.453614908,
    "KG Winter_pr_1_99_nre": 69,
    "KG Winter_ss_nre": 108,
    "KG Winter_dss_nre": 455,
    "KG Spring_theta_nre": -0.182695408,
    "KG Spring_pr_1_99_nre": 69,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 482,
    "G1 Fall_theta_nre": 0.044772853,
    "G1 Fall_pr_1_99_nre": 69,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 504,
    "G1 Winter_theta_nre": 0.318429119,
    "G1 Winter_pr_1_99_nre": 69,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 532,
    "G1 Spring_theta_nre": 0.560850279,
    "G1 Spring_pr_1_99_nre": 69,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 556,
    "G2 Fall_theta_nre": 0.829168107,
    "G2 Fall_pr_1_99_nre": 69,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 583,
    "G2 Winter_theta_nre": 1.052067183,
    "G2 Winter_pr_1_99_nre": 69,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 605,
    "G2 Spring_theta_nre": 1.31468481,
    "G2 Spring_pr_1_99_nre": 69,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 631,
    "G3 Fall_theta_nre": 1.630732,
    "G3 Fall_pr_1_99_nre": 69,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 663,
    "G3 Winter_theta_nre": 1.78320691,
    "G3 Winter_pr_1_99_nre": 69,
    "G3 Winter_ss_nre": 107,
    "G3 Winter_dss_nre": 678,
    "G3 Spring_theta_nre": 2.057137416,
    "G3 Spring_pr_1_99_nre": 69,
    "G3 Spring_ss_nre": 107,
    "G3 Spring_dss_nre": 706
  },
  {
    "ID": 348,
    "KG Winter_theta_nre": -0.449651806,
    "KG Winter_pr_1_99_nre": 70,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 455,
    "KG Spring_theta_nre": -0.181409747,
    "KG Spring_pr_1_99_nre": 70,
    "KG Spring_ss_nre": 107,
    "KG Spring_dss_nre": 482,
    "G1 Fall_theta_nre": 0.054513265,
    "G1 Fall_pr_1_99_nre": 70,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 505,
    "G1 Winter_theta_nre": 0.319558221,
    "G1 Winter_pr_1_99_nre": 70,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 532,
    "G1 Spring_theta_nre": 0.565001895,
    "G1 Spring_pr_1_99_nre": 70,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 557,
    "G2 Fall_theta_nre": 0.832557379,
    "G2 Fall_pr_1_99_nre": 70,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 583,
    "G2 Winter_theta_nre": 1.05511407,
    "G2 Winter_pr_1_99_nre": 70,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 606,
    "G2 Spring_theta_nre": 1.322449872,
    "G2 Spring_pr_1_99_nre": 70,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 632,
    "G3 Fall_theta_nre": 1.634555024,
    "G3 Fall_pr_1_99_nre": 70,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 663,
    "G3 Winter_theta_nre": 1.786037722,
    "G3 Winter_pr_1_99_nre": 70,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 679,
    "G3 Spring_theta_nre": 2.060075779,
    "G3 Spring_pr_1_99_nre": 70,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 706
  },
  {
    "ID": 349,
    "KG Winter_theta_nre": -0.448175266,
    "KG Winter_pr_1_99_nre": 70,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 455,
    "KG Spring_theta_nre": -0.175320281,
    "KG Spring_pr_1_99_nre": 70,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 482,
    "G1 Fall_theta_nre": 0.055497784,
    "G1 Fall_pr_1_99_nre": 70,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 506,
    "G1 Winter_theta_nre": 0.320410982,
    "G1 Winter_pr_1_99_nre": 70,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 532,
    "G1 Spring_theta_nre": 0.565710238,
    "G1 Spring_pr_1_99_nre": 70,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 557,
    "G2 Fall_theta_nre": 0.838195817,
    "G2 Fall_pr_1_99_nre": 70,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 584,
    "G2 Winter_theta_nre": 1.059295106,
    "G2 Winter_pr_1_99_nre": 70,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 606,
    "G2 Spring_theta_nre": 1.323115092,
    "G2 Spring_pr_1_99_nre": 70,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 632,
    "G3 Fall_theta_nre": 1.642943939,
    "G3 Fall_pr_1_99_nre": 70,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 664,
    "G3 Winter_theta_nre": 1.787893312,
    "G3 Winter_pr_1_99_nre": 70,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 679,
    "G3 Spring_theta_nre": 2.062500993,
    "G3 Spring_pr_1_99_nre": 70,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 706
  },
  {
    "ID": 350,
    "KG Winter_theta_nre": -0.446810017,
    "KG Winter_pr_1_99_nre": 70,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 455,
    "KG Spring_theta_nre": -0.171415554,
    "KG Spring_pr_1_99_nre": 70,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 483,
    "G1 Fall_theta_nre": 0.061531164,
    "G1 Fall_pr_1_99_nre": 70,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 506,
    "G1 Winter_theta_nre": 0.331273872,
    "G1 Winter_pr_1_99_nre": 70,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 533,
    "G1 Spring_theta_nre": 0.57397474,
    "G1 Spring_pr_1_99_nre": 70,
    "G1 Spring_ss_nre": 107,
    "G1 Spring_dss_nre": 557,
    "G2 Fall_theta_nre": 0.842144379,
    "G2 Fall_pr_1_99_nre": 70,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 584,
    "G2 Winter_theta_nre": 1.061186028,
    "G2 Winter_pr_1_99_nre": 70,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 606,
    "G2 Spring_theta_nre": 1.323671899,
    "G2 Spring_pr_1_99_nre": 70,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 632,
    "G3 Fall_theta_nre": 1.646972564,
    "G3 Fall_pr_1_99_nre": 70,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 665,
    "G3 Winter_theta_nre": 1.7895091,
    "G3 Winter_pr_1_99_nre": 70,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 679,
    "G3 Spring_theta_nre": 2.06421879,
    "G3 Spring_pr_1_99_nre": 70,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 706
  },
  {
    "ID": 351,
    "KG Winter_theta_nre": -0.444809017,
    "KG Winter_pr_1_99_nre": 70,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 456,
    "KG Spring_theta_nre": -0.169235711,
    "KG Spring_pr_1_99_nre": 70,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 483,
    "G1 Fall_theta_nre": 0.063697007,
    "G1 Fall_pr_1_99_nre": 70,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 506,
    "G1 Winter_theta_nre": 0.33238118,
    "G1 Winter_pr_1_99_nre": 70,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 533,
    "G1 Spring_theta_nre": 0.576404701,
    "G1 Spring_pr_1_99_nre": 70,
    "G1 Spring_ss_nre": 108,
    "G1 Spring_dss_nre": 558,
    "G2 Fall_theta_nre": 0.857587966,
    "G2 Fall_pr_1_99_nre": 70,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 586,
    "G2 Winter_theta_nre": 1.065403208,
    "G2 Winter_pr_1_99_nre": 70,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 607,
    "G2 Spring_theta_nre": 1.330469799,
    "G2 Spring_pr_1_99_nre": 70,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 633,
    "G3 Fall_theta_nre": 1.650069335,
    "G3 Fall_pr_1_99_nre": 70,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 665,
    "G3 Winter_theta_nre": 1.79892318,
    "G3 Winter_pr_1_99_nre": 70,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 680,
    "G3 Spring_theta_nre": 2.065339364,
    "G3 Spring_pr_1_99_nre": 70,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 707
  },
  {
    "ID": 352,
    "KG Winter_theta_nre": -0.44116077,
    "KG Winter_pr_1_99_nre": 70,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 456,
    "KG Spring_theta_nre": -0.168087083,
    "KG Spring_pr_1_99_nre": 70,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 483,
    "G1 Fall_theta_nre": 0.063826831,
    "G1 Fall_pr_1_99_nre": 70,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 506,
    "G1 Winter_theta_nre": 0.334669601,
    "G1 Winter_pr_1_99_nre": 70,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 533,
    "G1 Spring_theta_nre": 0.591545591,
    "G1 Spring_pr_1_99_nre": 70,
    "G1 Spring_ss_nre": 108,
    "G1 Spring_dss_nre": 559,
    "G2 Fall_theta_nre": 0.858900866,
    "G2 Fall_pr_1_99_nre": 70,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 586,
    "G2 Winter_theta_nre": 1.06549564,
    "G2 Winter_pr_1_99_nre": 70,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 607,
    "G2 Spring_theta_nre": 1.335517608,
    "G2 Spring_pr_1_99_nre": 70,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 634,
    "G3 Fall_theta_nre": 1.656321847,
    "G3 Fall_pr_1_99_nre": 70,
    "G3 Fall_ss_nre": 107,
    "G3 Fall_dss_nre": 666,
    "G3 Winter_theta_nre": 1.801047135,
    "G3 Winter_pr_1_99_nre": 70,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 680,
    "G3 Spring_theta_nre": 2.066768742,
    "G3 Spring_pr_1_99_nre": 70,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 707
  },
  {
    "ID": 353,
    "KG Winter_theta_nre": -0.44064654,
    "KG Winter_pr_1_99_nre": 71,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 456,
    "KG Spring_theta_nre": -0.166667607,
    "KG Spring_pr_1_99_nre": 71,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 483,
    "G1 Fall_theta_nre": 0.066427542,
    "G1 Fall_pr_1_99_nre": 71,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 507,
    "G1 Winter_theta_nre": 0.334870204,
    "G1 Winter_pr_1_99_nre": 71,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 533,
    "G1 Spring_theta_nre": 0.59424355,
    "G1 Spring_pr_1_99_nre": 71,
    "G1 Spring_ss_nre": 108,
    "G1 Spring_dss_nre": 559,
    "G2 Fall_theta_nre": 0.86251232,
    "G2 Fall_pr_1_99_nre": 71,
    "G2 Fall_ss_nre": 108,
    "G2 Fall_dss_nre": 586,
    "G2 Winter_theta_nre": 1.066441907,
    "G2 Winter_pr_1_99_nre": 71,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 607,
    "G2 Spring_theta_nre": 1.337718413,
    "G2 Spring_pr_1_99_nre": 71,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 634,
    "G3 Fall_theta_nre": 1.665289969,
    "G3 Fall_pr_1_99_nre": 71,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 667,
    "G3 Winter_theta_nre": 1.802522356,
    "G3 Winter_pr_1_99_nre": 71,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 680,
    "G3 Spring_theta_nre": 2.074359246,
    "G3 Spring_pr_1_99_nre": 71,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 707
  },
  {
    "ID": 354,
    "KG Winter_theta_nre": -0.433471951,
    "KG Winter_pr_1_99_nre": 71,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 457,
    "KG Spring_theta_nre": -0.16105118,
    "KG Spring_pr_1_99_nre": 71,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 484,
    "G1 Fall_theta_nre": 0.06925682,
    "G1 Fall_pr_1_99_nre": 71,
    "G1 Fall_ss_nre": 107,
    "G1 Fall_dss_nre": 507,
    "G1 Winter_theta_nre": 0.347444483,
    "G1 Winter_pr_1_99_nre": 71,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 535,
    "G1 Spring_theta_nre": 0.605166229,
    "G1 Spring_pr_1_99_nre": 71,
    "G1 Spring_ss_nre": 108,
    "G1 Spring_dss_nre": 561,
    "G2 Fall_theta_nre": 0.87436788,
    "G2 Fall_pr_1_99_nre": 71,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 587,
    "G2 Winter_theta_nre": 1.074561638,
    "G2 Winter_pr_1_99_nre": 71,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 607,
    "G2 Spring_theta_nre": 1.339687143,
    "G2 Spring_pr_1_99_nre": 71,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 634,
    "G3 Fall_theta_nre": 1.670069415,
    "G3 Fall_pr_1_99_nre": 71,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 667,
    "G3 Winter_theta_nre": 1.808167472,
    "G3 Winter_pr_1_99_nre": 71,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 681,
    "G3 Spring_theta_nre": 2.07824184,
    "G3 Spring_pr_1_99_nre": 71,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 708
  },
  {
    "ID": 355,
    "KG Winter_theta_nre": -0.4316577,
    "KG Winter_pr_1_99_nre": 71,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 457,
    "KG Spring_theta_nre": -0.148840369,
    "KG Spring_pr_1_99_nre": 71,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 485,
    "G1 Fall_theta_nre": 0.076664415,
    "G1 Fall_pr_1_99_nre": 71,
    "G1 Fall_ss_nre": 108,
    "G1 Fall_dss_nre": 508,
    "G1 Winter_theta_nre": 0.350864204,
    "G1 Winter_pr_1_99_nre": 71,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 535,
    "G1 Spring_theta_nre": 0.616668581,
    "G1 Spring_pr_1_99_nre": 71,
    "G1 Spring_ss_nre": 108,
    "G1 Spring_dss_nre": 562,
    "G2 Fall_theta_nre": 0.880300885,
    "G2 Fall_pr_1_99_nre": 71,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 588,
    "G2 Winter_theta_nre": 1.075029087,
    "G2 Winter_pr_1_99_nre": 71,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 608,
    "G2 Spring_theta_nre": 1.339887036,
    "G2 Spring_pr_1_99_nre": 71,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 634,
    "G3 Fall_theta_nre": 1.676685073,
    "G3 Fall_pr_1_99_nre": 71,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 668,
    "G3 Winter_theta_nre": 1.810279328,
    "G3 Winter_pr_1_99_nre": 71,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 681,
    "G3 Spring_theta_nre": 2.081908479,
    "G3 Spring_pr_1_99_nre": 71,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 708
  },
  {
    "ID": 356,
    "KG Winter_theta_nre": -0.423665075,
    "KG Winter_pr_1_99_nre": 71,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 458,
    "KG Spring_theta_nre": -0.14448679,
    "KG Spring_pr_1_99_nre": 71,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 486,
    "G1 Fall_theta_nre": 0.08599579,
    "G1 Fall_pr_1_99_nre": 71,
    "G1 Fall_ss_nre": 108,
    "G1 Fall_dss_nre": 509,
    "G1 Winter_theta_nre": 0.355427357,
    "G1 Winter_pr_1_99_nre": 71,
    "G1 Winter_ss_nre": 108,
    "G1 Winter_dss_nre": 536,
    "G1 Spring_theta_nre": 0.618517737,
    "G1 Spring_pr_1_99_nre": 71,
    "G1 Spring_ss_nre": 108,
    "G1 Spring_dss_nre": 562,
    "G2 Fall_theta_nre": 0.891081929,
    "G2 Fall_pr_1_99_nre": 71,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 589,
    "G2 Winter_theta_nre": 1.080733523,
    "G2 Winter_pr_1_99_nre": 71,
    "G2 Winter_ss_nre": 108,
    "G2 Winter_dss_nre": 608,
    "G2 Spring_theta_nre": 1.342378018,
    "G2 Spring_pr_1_99_nre": 71,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 634,
    "G3 Fall_theta_nre": 1.678736218,
    "G3 Fall_pr_1_99_nre": 71,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 668,
    "G3 Winter_theta_nre": 1.82449352,
    "G3 Winter_pr_1_99_nre": 71,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 682,
    "G3 Spring_theta_nre": 2.086959835,
    "G3 Spring_pr_1_99_nre": 71,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 709
  },
  {
    "ID": 357,
    "KG Winter_theta_nre": -0.421953124,
    "KG Winter_pr_1_99_nre": 71,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 458,
    "KG Spring_theta_nre": -0.140102206,
    "KG Spring_pr_1_99_nre": 71,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 486,
    "G1 Fall_theta_nre": 0.109937601,
    "G1 Fall_pr_1_99_nre": 71,
    "G1 Fall_ss_nre": 108,
    "G1 Fall_dss_nre": 511,
    "G1 Winter_theta_nre": 0.362385035,
    "G1 Winter_pr_1_99_nre": 71,
    "G1 Winter_ss_nre": 109,
    "G1 Winter_dss_nre": 536,
    "G1 Spring_theta_nre": 0.630454812,
    "G1 Spring_pr_1_99_nre": 71,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 563,
    "G2 Fall_theta_nre": 0.891207643,
    "G2 Fall_pr_1_99_nre": 71,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 589,
    "G2 Winter_theta_nre": 1.082774459,
    "G2 Winter_pr_1_99_nre": 71,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 608,
    "G2 Spring_theta_nre": 1.354287978,
    "G2 Spring_pr_1_99_nre": 71,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 635,
    "G3 Fall_theta_nre": 1.681780301,
    "G3 Fall_pr_1_99_nre": 71,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 668,
    "G3 Winter_theta_nre": 1.825778042,
    "G3 Winter_pr_1_99_nre": 71,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 683,
    "G3 Spring_theta_nre": 2.099652678,
    "G3 Spring_pr_1_99_nre": 71,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 710
  },
  {
    "ID": 358,
    "KG Winter_theta_nre": -0.418146462,
    "KG Winter_pr_1_99_nre": 72,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 458,
    "KG Spring_theta_nre": -0.13495045,
    "KG Spring_pr_1_99_nre": 72,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 487,
    "G1 Fall_theta_nre": 0.110946663,
    "G1 Fall_pr_1_99_nre": 72,
    "G1 Fall_ss_nre": 108,
    "G1 Fall_dss_nre": 511,
    "G1 Winter_theta_nre": 0.366108783,
    "G1 Winter_pr_1_99_nre": 72,
    "G1 Winter_ss_nre": 109,
    "G1 Winter_dss_nre": 537,
    "G1 Spring_theta_nre": 0.637054322,
    "G1 Spring_pr_1_99_nre": 72,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 564,
    "G2 Fall_theta_nre": 0.893845434,
    "G2 Fall_pr_1_99_nre": 72,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 589,
    "G2 Winter_theta_nre": 1.082849604,
    "G2 Winter_pr_1_99_nre": 72,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 608,
    "G2 Spring_theta_nre": 1.355311826,
    "G2 Spring_pr_1_99_nre": 72,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 636,
    "G3 Fall_theta_nre": 1.681921493,
    "G3 Fall_pr_1_99_nre": 72,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 668,
    "G3 Winter_theta_nre": 1.834052784,
    "G3 Winter_pr_1_99_nre": 72,
    "G3 Winter_ss_nre": 108,
    "G3 Winter_dss_nre": 683,
    "G3 Spring_theta_nre": 2.102034813,
    "G3 Spring_pr_1_99_nre": 72,
    "G3 Spring_ss_nre": 108,
    "G3 Spring_dss_nre": 710
  },
  {
    "ID": 359,
    "KG Winter_theta_nre": -0.412328062,
    "KG Winter_pr_1_99_nre": 72,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 459,
    "KG Spring_theta_nre": -0.132666014,
    "KG Spring_pr_1_99_nre": 72,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 487,
    "G1 Fall_theta_nre": 0.115299704,
    "G1 Fall_pr_1_99_nre": 72,
    "G1 Fall_ss_nre": 108,
    "G1 Fall_dss_nre": 512,
    "G1 Winter_theta_nre": 0.378823171,
    "G1 Winter_pr_1_99_nre": 72,
    "G1 Winter_ss_nre": 109,
    "G1 Winter_dss_nre": 538,
    "G1 Spring_theta_nre": 0.63965725,
    "G1 Spring_pr_1_99_nre": 72,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 564,
    "G2 Fall_theta_nre": 0.897501605,
    "G2 Fall_pr_1_99_nre": 72,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 590,
    "G2 Winter_theta_nre": 1.087109872,
    "G2 Winter_pr_1_99_nre": 72,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 609,
    "G2 Spring_theta_nre": 1.357194428,
    "G2 Spring_pr_1_99_nre": 72,
    "G2 Spring_ss_nre": 107,
    "G2 Spring_dss_nre": 636,
    "G3 Fall_theta_nre": 1.6895761,
    "G3 Fall_pr_1_99_nre": 72,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 669,
    "G3 Winter_theta_nre": 1.837737449,
    "G3 Winter_pr_1_99_nre": 72,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 684,
    "G3 Spring_theta_nre": 2.10564927,
    "G3 Spring_pr_1_99_nre": 72,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 711
  },
  {
    "ID": 360,
    "KG Winter_theta_nre": -0.398067438,
    "KG Winter_pr_1_99_nre": 72,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 460,
    "KG Spring_theta_nre": -0.124726197,
    "KG Spring_pr_1_99_nre": 72,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 488,
    "G1 Fall_theta_nre": 0.119812758,
    "G1 Fall_pr_1_99_nre": 72,
    "G1 Fall_ss_nre": 108,
    "G1 Fall_dss_nre": 512,
    "G1 Winter_theta_nre": 0.379600423,
    "G1 Winter_pr_1_99_nre": 72,
    "G1 Winter_ss_nre": 109,
    "G1 Winter_dss_nre": 538,
    "G1 Spring_theta_nre": 0.639724871,
    "G1 Spring_pr_1_99_nre": 72,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 564,
    "G2 Fall_theta_nre": 0.904121052,
    "G2 Fall_pr_1_99_nre": 72,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 590,
    "G2 Winter_theta_nre": 1.08876059,
    "G2 Winter_pr_1_99_nre": 72,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 609,
    "G2 Spring_theta_nre": 1.363576396,
    "G2 Spring_pr_1_99_nre": 72,
    "G2 Spring_ss_nre": 108,
    "G2 Spring_dss_nre": 636,
    "G3 Fall_theta_nre": 1.690391949,
    "G3 Fall_pr_1_99_nre": 72,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 669,
    "G3 Winter_theta_nre": 1.843829813,
    "G3 Winter_pr_1_99_nre": 72,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 684,
    "G3 Spring_theta_nre": 2.107974872,
    "G3 Spring_pr_1_99_nre": 72,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 711
  },
  {
    "ID": 361,
    "KG Winter_theta_nre": -0.396984768,
    "KG Winter_pr_1_99_nre": 72,
    "KG Winter_ss_nre": 109,
    "KG Winter_dss_nre": 460,
    "KG Spring_theta_nre": -0.123460823,
    "KG Spring_pr_1_99_nre": 72,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 488,
    "G1 Fall_theta_nre": 0.12017384,
    "G1 Fall_pr_1_99_nre": 72,
    "G1 Fall_ss_nre": 108,
    "G1 Fall_dss_nre": 512,
    "G1 Winter_theta_nre": 0.390662294,
    "G1 Winter_pr_1_99_nre": 72,
    "G1 Winter_ss_nre": 109,
    "G1 Winter_dss_nre": 539,
    "G1 Spring_theta_nre": 0.645448028,
    "G1 Spring_pr_1_99_nre": 72,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 565,
    "G2 Fall_theta_nre": 0.912575819,
    "G2 Fall_pr_1_99_nre": 72,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 591,
    "G2 Winter_theta_nre": 1.089825542,
    "G2 Winter_pr_1_99_nre": 72,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 609,
    "G2 Spring_theta_nre": 1.363867849,
    "G2 Spring_pr_1_99_nre": 72,
    "G2 Spring_ss_nre": 108,
    "G2 Spring_dss_nre": 636,
    "G3 Fall_theta_nre": 1.69171313,
    "G3 Fall_pr_1_99_nre": 72,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 669,
    "G3 Winter_theta_nre": 1.847989942,
    "G3 Winter_pr_1_99_nre": 72,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 685,
    "G3 Spring_theta_nre": 2.110746412,
    "G3 Spring_pr_1_99_nre": 72,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 711
  },
  {
    "ID": 362,
    "KG Winter_theta_nre": -0.390779173,
    "KG Winter_pr_1_99_nre": 72,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 461,
    "KG Spring_theta_nre": -0.122304164,
    "KG Spring_pr_1_99_nre": 72,
    "KG Spring_ss_nre": 108,
    "KG Spring_dss_nre": 488,
    "G1 Fall_theta_nre": 0.125857575,
    "G1 Fall_pr_1_99_nre": 72,
    "G1 Fall_ss_nre": 108,
    "G1 Fall_dss_nre": 513,
    "G1 Winter_theta_nre": 0.401623298,
    "G1 Winter_pr_1_99_nre": 72,
    "G1 Winter_ss_nre": 109,
    "G1 Winter_dss_nre": 540,
    "G1 Spring_theta_nre": 0.663647214,
    "G1 Spring_pr_1_99_nre": 72,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 566,
    "G2 Fall_theta_nre": 0.921046879,
    "G2 Fall_pr_1_99_nre": 72,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 592,
    "G2 Winter_theta_nre": 1.109042816,
    "G2 Winter_pr_1_99_nre": 72,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 611,
    "G2 Spring_theta_nre": 1.371782588,
    "G2 Spring_pr_1_99_nre": 72,
    "G2 Spring_ss_nre": 108,
    "G2 Spring_dss_nre": 637,
    "G3 Fall_theta_nre": 1.69618571,
    "G3 Fall_pr_1_99_nre": 72,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 670,
    "G3 Winter_theta_nre": 1.851631158,
    "G3 Winter_pr_1_99_nre": 72,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 685,
    "G3 Spring_theta_nre": 2.114480156,
    "G3 Spring_pr_1_99_nre": 72,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 711
  },
  {
    "ID": 363,
    "KG Winter_theta_nre": -0.385412119,
    "KG Winter_pr_1_99_nre": 73,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 461,
    "KG Spring_theta_nre": -0.106067099,
    "KG Spring_pr_1_99_nre": 73,
    "KG Spring_ss_nre": 109,
    "KG Spring_dss_nre": 489,
    "G1 Fall_theta_nre": 0.139369855,
    "G1 Fall_pr_1_99_nre": 73,
    "G1 Fall_ss_nre": 109,
    "G1 Fall_dss_nre": 514,
    "G1 Winter_theta_nre": 0.402140091,
    "G1 Winter_pr_1_99_nre": 73,
    "G1 Winter_ss_nre": 109,
    "G1 Winter_dss_nre": 540,
    "G1 Spring_theta_nre": 0.666341069,
    "G1 Spring_pr_1_99_nre": 73,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 567,
    "G2 Fall_theta_nre": 0.922710432,
    "G2 Fall_pr_1_99_nre": 73,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 592,
    "G2 Winter_theta_nre": 1.110592411,
    "G2 Winter_pr_1_99_nre": 73,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 611,
    "G2 Spring_theta_nre": 1.384916443,
    "G2 Spring_pr_1_99_nre": 73,
    "G2 Spring_ss_nre": 108,
    "G2 Spring_dss_nre": 638,
    "G3 Fall_theta_nre": 1.70670368,
    "G3 Fall_pr_1_99_nre": 73,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 671,
    "G3 Winter_theta_nre": 1.852695463,
    "G3 Winter_pr_1_99_nre": 73,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 685,
    "G3 Spring_theta_nre": 2.123537834,
    "G3 Spring_pr_1_99_nre": 73,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 712
  },
  {
    "ID": 364,
    "KG Winter_theta_nre": -0.384310174,
    "KG Winter_pr_1_99_nre": 73,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 462,
    "KG Spring_theta_nre": -0.094908223,
    "KG Spring_pr_1_99_nre": 73,
    "KG Spring_ss_nre": 109,
    "KG Spring_dss_nre": 491,
    "G1 Fall_theta_nre": 0.143579967,
    "G1 Fall_pr_1_99_nre": 73,
    "G1 Fall_ss_nre": 109,
    "G1 Fall_dss_nre": 514,
    "G1 Winter_theta_nre": 0.409683697,
    "G1 Winter_pr_1_99_nre": 73,
    "G1 Winter_ss_nre": 109,
    "G1 Winter_dss_nre": 541,
    "G1 Spring_theta_nre": 0.667454228,
    "G1 Spring_pr_1_99_nre": 73,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 567,
    "G2 Fall_theta_nre": 0.923295097,
    "G2 Fall_pr_1_99_nre": 73,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 592,
    "G2 Winter_theta_nre": 1.111594126,
    "G2 Winter_pr_1_99_nre": 73,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 611,
    "G2 Spring_theta_nre": 1.416961928,
    "G2 Spring_pr_1_99_nre": 73,
    "G2 Spring_ss_nre": 108,
    "G2 Spring_dss_nre": 642,
    "G3 Fall_theta_nre": 1.708992349,
    "G3 Fall_pr_1_99_nre": 73,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 671,
    "G3 Winter_theta_nre": 1.858901054,
    "G3 Winter_pr_1_99_nre": 73,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 686,
    "G3 Spring_theta_nre": 2.124038422,
    "G3 Spring_pr_1_99_nre": 73,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 712
  },
  {
    "ID": 365,
    "KG Winter_theta_nre": -0.380552887,
    "KG Winter_pr_1_99_nre": 73,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 462,
    "KG Spring_theta_nre": -0.072525361,
    "KG Spring_pr_1_99_nre": 73,
    "KG Spring_ss_nre": 109,
    "KG Spring_dss_nre": 493,
    "G1 Fall_theta_nre": 0.146596214,
    "G1 Fall_pr_1_99_nre": 73,
    "G1 Fall_ss_nre": 109,
    "G1 Fall_dss_nre": 515,
    "G1 Winter_theta_nre": 0.411973801,
    "G1 Winter_pr_1_99_nre": 73,
    "G1 Winter_ss_nre": 109,
    "G1 Winter_dss_nre": 541,
    "G1 Spring_theta_nre": 0.668823553,
    "G1 Spring_pr_1_99_nre": 73,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 567,
    "G2 Fall_theta_nre": 0.928159395,
    "G2 Fall_pr_1_99_nre": 73,
    "G2 Fall_ss_nre": 109,
    "G2 Fall_dss_nre": 593,
    "G2 Winter_theta_nre": 1.121466098,
    "G2 Winter_pr_1_99_nre": 73,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 612,
    "G2 Spring_theta_nre": 1.41807626,
    "G2 Spring_pr_1_99_nre": 73,
    "G2 Spring_ss_nre": 108,
    "G2 Spring_dss_nre": 642,
    "G3 Fall_theta_nre": 1.709713405,
    "G3 Fall_pr_1_99_nre": 73,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 671,
    "G3 Winter_theta_nre": 1.861824212,
    "G3 Winter_pr_1_99_nre": 73,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 686,
    "G3 Spring_theta_nre": 2.124508668,
    "G3 Spring_pr_1_99_nre": 73,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 712
  },
  {
    "ID": 366,
    "KG Winter_theta_nre": -0.378219637,
    "KG Winter_pr_1_99_nre": 73,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 462,
    "KG Spring_theta_nre": -0.070972505,
    "KG Spring_pr_1_99_nre": 73,
    "KG Spring_ss_nre": 109,
    "KG Spring_dss_nre": 493,
    "G1 Fall_theta_nre": 0.153558263,
    "G1 Fall_pr_1_99_nre": 73,
    "G1 Fall_ss_nre": 109,
    "G1 Fall_dss_nre": 515,
    "G1 Winter_theta_nre": 0.417242451,
    "G1 Winter_pr_1_99_nre": 73,
    "G1 Winter_ss_nre": 110,
    "G1 Winter_dss_nre": 542,
    "G1 Spring_theta_nre": 0.669155042,
    "G1 Spring_pr_1_99_nre": 73,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 567,
    "G2 Fall_theta_nre": 0.942152096,
    "G2 Fall_pr_1_99_nre": 73,
    "G2 Fall_ss_nre": 110,
    "G2 Fall_dss_nre": 594,
    "G2 Winter_theta_nre": 1.122444818,
    "G2 Winter_pr_1_99_nre": 73,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 612,
    "G2 Spring_theta_nre": 1.42333282,
    "G2 Spring_pr_1_99_nre": 73,
    "G2 Spring_ss_nre": 109,
    "G2 Spring_dss_nre": 642,
    "G3 Fall_theta_nre": 1.719827924,
    "G3 Fall_pr_1_99_nre": 73,
    "G3 Fall_ss_nre": 108,
    "G3 Fall_dss_nre": 672,
    "G3 Winter_theta_nre": 1.867145091,
    "G3 Winter_pr_1_99_nre": 73,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 687,
    "G3 Spring_theta_nre": 2.129920978,
    "G3 Spring_pr_1_99_nre": 73,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 713
  },
  {
    "ID": 367,
    "KG Winter_theta_nre": -0.375989868,
    "KG Winter_pr_1_99_nre": 73,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 462,
    "KG Spring_theta_nre": -0.063199928,
    "KG Spring_pr_1_99_nre": 73,
    "KG Spring_ss_nre": 109,
    "KG Spring_dss_nre": 494,
    "G1 Fall_theta_nre": 0.154666764,
    "G1 Fall_pr_1_99_nre": 73,
    "G1 Fall_ss_nre": 109,
    "G1 Fall_dss_nre": 515,
    "G1 Winter_theta_nre": 0.426551555,
    "G1 Winter_pr_1_99_nre": 73,
    "G1 Winter_ss_nre": 110,
    "G1 Winter_dss_nre": 543,
    "G1 Spring_theta_nre": 0.675245151,
    "G1 Spring_pr_1_99_nre": 73,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 568,
    "G2 Fall_theta_nre": 0.950131072,
    "G2 Fall_pr_1_99_nre": 73,
    "G2 Fall_ss_nre": 110,
    "G2 Fall_dss_nre": 595,
    "G2 Winter_theta_nre": 1.124838389,
    "G2 Winter_pr_1_99_nre": 73,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 612,
    "G2 Spring_theta_nre": 1.426852529,
    "G2 Spring_pr_1_99_nre": 73,
    "G2 Spring_ss_nre": 109,
    "G2 Spring_dss_nre": 643,
    "G3 Fall_theta_nre": 1.728216718,
    "G3 Fall_pr_1_99_nre": 73,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 673,
    "G3 Winter_theta_nre": 1.868774601,
    "G3 Winter_pr_1_99_nre": 73,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 687,
    "G3 Spring_theta_nre": 2.130578182,
    "G3 Spring_pr_1_99_nre": 73,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 713
  },
  {
    "ID": 368,
    "KG Winter_theta_nre": -0.369873596,
    "KG Winter_pr_1_99_nre": 74,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 463,
    "KG Spring_theta_nre": -0.062530236,
    "KG Spring_pr_1_99_nre": 74,
    "KG Spring_ss_nre": 109,
    "KG Spring_dss_nre": 494,
    "G1 Fall_theta_nre": 0.167227604,
    "G1 Fall_pr_1_99_nre": 74,
    "G1 Fall_ss_nre": 109,
    "G1 Fall_dss_nre": 517,
    "G1 Winter_theta_nre": 0.426635727,
    "G1 Winter_pr_1_99_nre": 74,
    "G1 Winter_ss_nre": 110,
    "G1 Winter_dss_nre": 543,
    "G1 Spring_theta_nre": 0.677673946,
    "G1 Spring_pr_1_99_nre": 74,
    "G1 Spring_ss_nre": 109,
    "G1 Spring_dss_nre": 568,
    "G2 Fall_theta_nre": 0.955040676,
    "G2 Fall_pr_1_99_nre": 74,
    "G2 Fall_ss_nre": 110,
    "G2 Fall_dss_nre": 596,
    "G2 Winter_theta_nre": 1.12970342,
    "G2 Winter_pr_1_99_nre": 74,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 613,
    "G2 Spring_theta_nre": 1.427957275,
    "G2 Spring_pr_1_99_nre": 74,
    "G2 Spring_ss_nre": 109,
    "G2 Spring_dss_nre": 643,
    "G3 Fall_theta_nre": 1.74484001,
    "G3 Fall_pr_1_99_nre": 74,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 674,
    "G3 Winter_theta_nre": 1.869912992,
    "G3 Winter_pr_1_99_nre": 74,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 687,
    "G3 Spring_theta_nre": 2.133101742,
    "G3 Spring_pr_1_99_nre": 74,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 713
  },
  {
    "ID": 369,
    "KG Winter_theta_nre": -0.368822477,
    "KG Winter_pr_1_99_nre": 74,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 463,
    "KG Spring_theta_nre": -0.055876014,
    "KG Spring_pr_1_99_nre": 74,
    "KG Spring_ss_nre": 110,
    "KG Spring_dss_nre": 494,
    "G1 Fall_theta_nre": 0.16943314,
    "G1 Fall_pr_1_99_nre": 74,
    "G1 Fall_ss_nre": 109,
    "G1 Fall_dss_nre": 517,
    "G1 Winter_theta_nre": 0.432693412,
    "G1 Winter_pr_1_99_nre": 74,
    "G1 Winter_ss_nre": 110,
    "G1 Winter_dss_nre": 543,
    "G1 Spring_theta_nre": 0.688799417,
    "G1 Spring_pr_1_99_nre": 74,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 569,
    "G2 Fall_theta_nre": 0.957078383,
    "G2 Fall_pr_1_99_nre": 74,
    "G2 Fall_ss_nre": 110,
    "G2 Fall_dss_nre": 596,
    "G2 Winter_theta_nre": 1.131450471,
    "G2 Winter_pr_1_99_nre": 74,
    "G2 Winter_ss_nre": 109,
    "G2 Winter_dss_nre": 613,
    "G2 Spring_theta_nre": 1.431756056,
    "G2 Spring_pr_1_99_nre": 74,
    "G2 Spring_ss_nre": 109,
    "G2 Spring_dss_nre": 643,
    "G3 Fall_theta_nre": 1.751677832,
    "G3 Fall_pr_1_99_nre": 74,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 675,
    "G3 Winter_theta_nre": 1.881707242,
    "G3 Winter_pr_1_99_nre": 74,
    "G3 Winter_ss_nre": 109,
    "G3 Winter_dss_nre": 688,
    "G3 Spring_theta_nre": 2.144337566,
    "G3 Spring_pr_1_99_nre": 74,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 714
  },
  {
    "ID": 370,
    "KG Winter_theta_nre": -0.363961176,
    "KG Winter_pr_1_99_nre": 74,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 464,
    "KG Spring_theta_nre": -0.04985507,
    "KG Spring_pr_1_99_nre": 74,
    "KG Spring_ss_nre": 110,
    "KG Spring_dss_nre": 495,
    "G1 Fall_theta_nre": 0.171179199,
    "G1 Fall_pr_1_99_nre": 74,
    "G1 Fall_ss_nre": 109,
    "G1 Fall_dss_nre": 517,
    "G1 Winter_theta_nre": 0.443341073,
    "G1 Winter_pr_1_99_nre": 74,
    "G1 Winter_ss_nre": 110,
    "G1 Winter_dss_nre": 544,
    "G1 Spring_theta_nre": 0.691509985,
    "G1 Spring_pr_1_99_nre": 74,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 569,
    "G2 Fall_theta_nre": 0.973392078,
    "G2 Fall_pr_1_99_nre": 74,
    "G2 Fall_ss_nre": 110,
    "G2 Fall_dss_nre": 597,
    "G2 Winter_theta_nre": 1.140899082,
    "G2 Winter_pr_1_99_nre": 74,
    "G2 Winter_ss_nre": 110,
    "G2 Winter_dss_nre": 614,
    "G2 Spring_theta_nre": 1.433885886,
    "G2 Spring_pr_1_99_nre": 74,
    "G2 Spring_ss_nre": 109,
    "G2 Spring_dss_nre": 643,
    "G3 Fall_theta_nre": 1.75522934,
    "G3 Fall_pr_1_99_nre": 74,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 676,
    "G3 Winter_theta_nre": 1.887543985,
    "G3 Winter_pr_1_99_nre": 74,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 689,
    "G3 Spring_theta_nre": 2.145465488,
    "G3 Spring_pr_1_99_nre": 74,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 715
  },
  {
    "ID": 371,
    "KG Winter_theta_nre": -0.362767897,
    "KG Winter_pr_1_99_nre": 74,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 464,
    "KG Spring_theta_nre": -0.015169841,
    "KG Spring_pr_1_99_nre": 74,
    "KG Spring_ss_nre": 110,
    "KG Spring_dss_nre": 498,
    "G1 Fall_theta_nre": 0.174452379,
    "G1 Fall_pr_1_99_nre": 74,
    "G1 Fall_ss_nre": 109,
    "G1 Fall_dss_nre": 517,
    "G1 Winter_theta_nre": 0.445034769,
    "G1 Winter_pr_1_99_nre": 74,
    "G1 Winter_ss_nre": 110,
    "G1 Winter_dss_nre": 545,
    "G1 Spring_theta_nre": 0.697745697,
    "G1 Spring_pr_1_99_nre": 74,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 570,
    "G2 Fall_theta_nre": 0.973463234,
    "G2 Fall_pr_1_99_nre": 74,
    "G2 Fall_ss_nre": 110,
    "G2 Fall_dss_nre": 597,
    "G2 Winter_theta_nre": 1.146133445,
    "G2 Winter_pr_1_99_nre": 74,
    "G2 Winter_ss_nre": 110,
    "G2 Winter_dss_nre": 615,
    "G2 Spring_theta_nre": 1.438518069,
    "G2 Spring_pr_1_99_nre": 74,
    "G2 Spring_ss_nre": 109,
    "G2 Spring_dss_nre": 644,
    "G3 Fall_theta_nre": 1.759161248,
    "G3 Fall_pr_1_99_nre": 74,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 676,
    "G3 Winter_theta_nre": 1.888435361,
    "G3 Winter_pr_1_99_nre": 74,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 689,
    "G3 Spring_theta_nre": 2.147825891,
    "G3 Spring_pr_1_99_nre": 74,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 715
  },
  {
    "ID": 372,
    "KG Winter_theta_nre": -0.362555144,
    "KG Winter_pr_1_99_nre": 74,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 464,
    "KG Spring_theta_nre": -0.012328304,
    "KG Spring_pr_1_99_nre": 74,
    "KG Spring_ss_nre": 110,
    "KG Spring_dss_nre": 499,
    "G1 Fall_theta_nre": 0.175119507,
    "G1 Fall_pr_1_99_nre": 74,
    "G1 Fall_ss_nre": 109,
    "G1 Fall_dss_nre": 518,
    "G1 Winter_theta_nre": 0.451149013,
    "G1 Winter_pr_1_99_nre": 74,
    "G1 Winter_ss_nre": 110,
    "G1 Winter_dss_nre": 545,
    "G1 Spring_theta_nre": 0.698795662,
    "G1 Spring_pr_1_99_nre": 74,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 570,
    "G2 Fall_theta_nre": 0.984060868,
    "G2 Fall_pr_1_99_nre": 74,
    "G2 Fall_ss_nre": 110,
    "G2 Fall_dss_nre": 598,
    "G2 Winter_theta_nre": 1.150519177,
    "G2 Winter_pr_1_99_nre": 74,
    "G2 Winter_ss_nre": 110,
    "G2 Winter_dss_nre": 615,
    "G2 Spring_theta_nre": 1.451218749,
    "G2 Spring_pr_1_99_nre": 74,
    "G2 Spring_ss_nre": 109,
    "G2 Spring_dss_nre": 645,
    "G3 Fall_theta_nre": 1.763404624,
    "G3 Fall_pr_1_99_nre": 74,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 676,
    "G3 Winter_theta_nre": 1.890063121,
    "G3 Winter_pr_1_99_nre": 74,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 689,
    "G3 Spring_theta_nre": 2.148861047,
    "G3 Spring_pr_1_99_nre": 74,
    "G3 Spring_ss_nre": 109,
    "G3 Spring_dss_nre": 715
  },
  {
    "ID": 373,
    "KG Winter_theta_nre": -0.357263775,
    "KG Winter_pr_1_99_nre": 75,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 464,
    "KG Spring_theta_nre": -0.011234784,
    "KG Spring_pr_1_99_nre": 75,
    "KG Spring_ss_nre": 110,
    "KG Spring_dss_nre": 499,
    "G1 Fall_theta_nre": 0.194583227,
    "G1 Fall_pr_1_99_nre": 75,
    "G1 Fall_ss_nre": 110,
    "G1 Fall_dss_nre": 519,
    "G1 Winter_theta_nre": 0.458203893,
    "G1 Winter_pr_1_99_nre": 75,
    "G1 Winter_ss_nre": 110,
    "G1 Winter_dss_nre": 546,
    "G1 Spring_theta_nre": 0.70165083,
    "G1 Spring_pr_1_99_nre": 75,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 570,
    "G2 Fall_theta_nre": 0.984631207,
    "G2 Fall_pr_1_99_nre": 75,
    "G2 Fall_ss_nre": 110,
    "G2 Fall_dss_nre": 598,
    "G2 Winter_theta_nre": 1.155998883,
    "G2 Winter_pr_1_99_nre": 75,
    "G2 Winter_ss_nre": 110,
    "G2 Winter_dss_nre": 616,
    "G2 Spring_theta_nre": 1.468488517,
    "G2 Spring_pr_1_99_nre": 75,
    "G2 Spring_ss_nre": 109,
    "G2 Spring_dss_nre": 647,
    "G3 Fall_theta_nre": 1.775801342,
    "G3 Fall_pr_1_99_nre": 75,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 678,
    "G3 Winter_theta_nre": 1.892585422,
    "G3 Winter_pr_1_99_nre": 75,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 689,
    "G3 Spring_theta_nre": 2.156681458,
    "G3 Spring_pr_1_99_nre": 75,
    "G3 Spring_ss_nre": 110,
    "G3 Spring_dss_nre": 716
  },
  {
    "ID": 374,
    "KG Winter_theta_nre": -0.352749584,
    "KG Winter_pr_1_99_nre": 75,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 465,
    "KG Spring_theta_nre": -0.010939002,
    "KG Spring_pr_1_99_nre": 75,
    "KG Spring_ss_nre": 110,
    "KG Spring_dss_nre": 499,
    "G1 Fall_theta_nre": 0.203424171,
    "G1 Fall_pr_1_99_nre": 75,
    "G1 Fall_ss_nre": 110,
    "G1 Fall_dss_nre": 520,
    "G1 Winter_theta_nre": 0.464229927,
    "G1 Winter_pr_1_99_nre": 75,
    "G1 Winter_ss_nre": 110,
    "G1 Winter_dss_nre": 546,
    "G1 Spring_theta_nre": 0.703120647,
    "G1 Spring_pr_1_99_nre": 75,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 570,
    "G2 Fall_theta_nre": 1.005223223,
    "G2 Fall_pr_1_99_nre": 75,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 601,
    "G2 Winter_theta_nre": 1.157860201,
    "G2 Winter_pr_1_99_nre": 75,
    "G2 Winter_ss_nre": 110,
    "G2 Winter_dss_nre": 616,
    "G2 Spring_theta_nre": 1.495248846,
    "G2 Spring_pr_1_99_nre": 75,
    "G2 Spring_ss_nre": 110,
    "G2 Spring_dss_nre": 650,
    "G3 Fall_theta_nre": 1.777942631,
    "G3 Fall_pr_1_99_nre": 75,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 678,
    "G3 Winter_theta_nre": 1.893132155,
    "G3 Winter_pr_1_99_nre": 75,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 689,
    "G3 Spring_theta_nre": 2.16138892,
    "G3 Spring_pr_1_99_nre": 75,
    "G3 Spring_ss_nre": 110,
    "G3 Spring_dss_nre": 716
  },
  {
    "ID": 375,
    "KG Winter_theta_nre": -0.349181384,
    "KG Winter_pr_1_99_nre": 75,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 465,
    "KG Spring_theta_nre": -0.008337534,
    "KG Spring_pr_1_99_nre": 75,
    "KG Spring_ss_nre": 110,
    "KG Spring_dss_nre": 499,
    "G1 Fall_theta_nre": 0.23151339,
    "G1 Fall_pr_1_99_nre": 75,
    "G1 Fall_ss_nre": 110,
    "G1 Fall_dss_nre": 523,
    "G1 Winter_theta_nre": 0.478537576,
    "G1 Winter_pr_1_99_nre": 75,
    "G1 Winter_ss_nre": 111,
    "G1 Winter_dss_nre": 548,
    "G1 Spring_theta_nre": 0.705383653,
    "G1 Spring_pr_1_99_nre": 75,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 571,
    "G2 Fall_theta_nre": 1.005469302,
    "G2 Fall_pr_1_99_nre": 75,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 601,
    "G2 Winter_theta_nre": 1.171572699,
    "G2 Winter_pr_1_99_nre": 75,
    "G2 Winter_ss_nre": 110,
    "G2 Winter_dss_nre": 617,
    "G2 Spring_theta_nre": 1.497024415,
    "G2 Spring_pr_1_99_nre": 75,
    "G2 Spring_ss_nre": 110,
    "G2 Spring_dss_nre": 650,
    "G3 Fall_theta_nre": 1.777997913,
    "G3 Fall_pr_1_99_nre": 75,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 678,
    "G3 Winter_theta_nre": 1.894216484,
    "G3 Winter_pr_1_99_nre": 75,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 689,
    "G3 Spring_theta_nre": 2.163403057,
    "G3 Spring_pr_1_99_nre": 75,
    "G3 Spring_ss_nre": 110,
    "G3 Spring_dss_nre": 716
  },
  {
    "ID": 376,
    "KG Winter_theta_nre": -0.346297847,
    "KG Winter_pr_1_99_nre": 75,
    "KG Winter_ss_nre": 110,
    "KG Winter_dss_nre": 465,
    "KG Spring_theta_nre": -0.001388743,
    "KG Spring_pr_1_99_nre": 75,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 500,
    "G1 Fall_theta_nre": 0.236921818,
    "G1 Fall_pr_1_99_nre": 75,
    "G1 Fall_ss_nre": 110,
    "G1 Fall_dss_nre": 524,
    "G1 Winter_theta_nre": 0.48884776,
    "G1 Winter_pr_1_99_nre": 75,
    "G1 Winter_ss_nre": 111,
    "G1 Winter_dss_nre": 549,
    "G1 Spring_theta_nre": 0.707316068,
    "G1 Spring_pr_1_99_nre": 75,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 571,
    "G2 Fall_theta_nre": 1.010332367,
    "G2 Fall_pr_1_99_nre": 75,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 601,
    "G2 Winter_theta_nre": 1.17545861,
    "G2 Winter_pr_1_99_nre": 75,
    "G2 Winter_ss_nre": 110,
    "G2 Winter_dss_nre": 618,
    "G2 Spring_theta_nre": 1.500564262,
    "G2 Spring_pr_1_99_nre": 75,
    "G2 Spring_ss_nre": 110,
    "G2 Spring_dss_nre": 650,
    "G3 Fall_theta_nre": 1.785036155,
    "G3 Fall_pr_1_99_nre": 75,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 679,
    "G3 Winter_theta_nre": 1.901187944,
    "G3 Winter_pr_1_99_nre": 75,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 690,
    "G3 Spring_theta_nre": 2.166055975,
    "G3 Spring_pr_1_99_nre": 75,
    "G3 Spring_ss_nre": 110,
    "G3 Spring_dss_nre": 717
  },
  {
    "ID": 377,
    "KG Winter_theta_nre": -0.332078425,
    "KG Winter_pr_1_99_nre": 75,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 467,
    "KG Spring_theta_nre": -0.000508306,
    "KG Spring_pr_1_99_nre": 75,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 500,
    "G1 Fall_theta_nre": 0.238089367,
    "G1 Fall_pr_1_99_nre": 75,
    "G1 Fall_ss_nre": 110,
    "G1 Fall_dss_nre": 524,
    "G1 Winter_theta_nre": 0.489655728,
    "G1 Winter_pr_1_99_nre": 75,
    "G1 Winter_ss_nre": 111,
    "G1 Winter_dss_nre": 549,
    "G1 Spring_theta_nre": 0.708283499,
    "G1 Spring_pr_1_99_nre": 75,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 571,
    "G2 Fall_theta_nre": 1.011878856,
    "G2 Fall_pr_1_99_nre": 75,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 601,
    "G2 Winter_theta_nre": 1.185409586,
    "G2 Winter_pr_1_99_nre": 75,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 619,
    "G2 Spring_theta_nre": 1.506977868,
    "G2 Spring_pr_1_99_nre": 75,
    "G2 Spring_ss_nre": 110,
    "G2 Spring_dss_nre": 651,
    "G3 Fall_theta_nre": 1.78593122,
    "G3 Fall_pr_1_99_nre": 75,
    "G3 Fall_ss_nre": 109,
    "G3 Fall_dss_nre": 679,
    "G3 Winter_theta_nre": 1.909038709,
    "G3 Winter_pr_1_99_nre": 75,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 691,
    "G3 Spring_theta_nre": 2.168886795,
    "G3 Spring_pr_1_99_nre": 75,
    "G3 Spring_ss_nre": 110,
    "G3 Spring_dss_nre": 717
  },
  {
    "ID": 378,
    "KG Winter_theta_nre": -0.331047671,
    "KG Winter_pr_1_99_nre": 76,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 467,
    "KG Spring_theta_nre": 0.009322747,
    "KG Spring_pr_1_99_nre": 76,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 501,
    "G1 Fall_theta_nre": 0.250382865,
    "G1 Fall_pr_1_99_nre": 76,
    "G1 Fall_ss_nre": 111,
    "G1 Fall_dss_nre": 525,
    "G1 Winter_theta_nre": 0.498991016,
    "G1 Winter_pr_1_99_nre": 76,
    "G1 Winter_ss_nre": 111,
    "G1 Winter_dss_nre": 550,
    "G1 Spring_theta_nre": 0.710238856,
    "G1 Spring_pr_1_99_nre": 76,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 571,
    "G2 Fall_theta_nre": 1.011926411,
    "G2 Fall_pr_1_99_nre": 76,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 601,
    "G2 Winter_theta_nre": 1.193234314,
    "G2 Winter_pr_1_99_nre": 76,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 619,
    "G2 Spring_theta_nre": 1.510339617,
    "G2 Spring_pr_1_99_nre": 76,
    "G2 Spring_ss_nre": 110,
    "G2 Spring_dss_nre": 651,
    "G3 Fall_theta_nre": 1.789362023,
    "G3 Fall_pr_1_99_nre": 76,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 679,
    "G3 Winter_theta_nre": 1.912944516,
    "G3 Winter_pr_1_99_nre": 76,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 691,
    "G3 Spring_theta_nre": 2.175798,
    "G3 Spring_pr_1_99_nre": 76,
    "G3 Spring_ss_nre": 110,
    "G3 Spring_dss_nre": 718
  },
  {
    "ID": 379,
    "KG Winter_theta_nre": -0.326776,
    "KG Winter_pr_1_99_nre": 76,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 467,
    "KG Spring_theta_nre": 0.02106391,
    "KG Spring_pr_1_99_nre": 76,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 502,
    "G1 Fall_theta_nre": 0.260308184,
    "G1 Fall_pr_1_99_nre": 76,
    "G1 Fall_ss_nre": 111,
    "G1 Fall_dss_nre": 526,
    "G1 Winter_theta_nre": 0.507447607,
    "G1 Winter_pr_1_99_nre": 76,
    "G1 Winter_ss_nre": 111,
    "G1 Winter_dss_nre": 551,
    "G1 Spring_theta_nre": 0.714400435,
    "G1 Spring_pr_1_99_nre": 76,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 571,
    "G2 Fall_theta_nre": 1.016462242,
    "G2 Fall_pr_1_99_nre": 76,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 602,
    "G2 Winter_theta_nre": 1.195427149,
    "G2 Winter_pr_1_99_nre": 76,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 620,
    "G2 Spring_theta_nre": 1.513944363,
    "G2 Spring_pr_1_99_nre": 76,
    "G2 Spring_ss_nre": 110,
    "G2 Spring_dss_nre": 651,
    "G3 Fall_theta_nre": 1.793075085,
    "G3 Fall_pr_1_99_nre": 76,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 679,
    "G3 Winter_theta_nre": 1.917103948,
    "G3 Winter_pr_1_99_nre": 76,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 692,
    "G3 Spring_theta_nre": 2.187132669,
    "G3 Spring_pr_1_99_nre": 76,
    "G3 Spring_ss_nre": 110,
    "G3 Spring_dss_nre": 719
  },
  {
    "ID": 380,
    "KG Winter_theta_nre": -0.316579864,
    "KG Winter_pr_1_99_nre": 76,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 468,
    "KG Spring_theta_nre": 0.021140237,
    "KG Spring_pr_1_99_nre": 76,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 502,
    "G1 Fall_theta_nre": 0.275662847,
    "G1 Fall_pr_1_99_nre": 76,
    "G1 Fall_ss_nre": 111,
    "G1 Fall_dss_nre": 528,
    "G1 Winter_theta_nre": 0.515829824,
    "G1 Winter_pr_1_99_nre": 76,
    "G1 Winter_ss_nre": 111,
    "G1 Winter_dss_nre": 552,
    "G1 Spring_theta_nre": 0.719968835,
    "G1 Spring_pr_1_99_nre": 76,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 572,
    "G2 Fall_theta_nre": 1.019143638,
    "G2 Fall_pr_1_99_nre": 76,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 602,
    "G2 Winter_theta_nre": 1.195945679,
    "G2 Winter_pr_1_99_nre": 76,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 620,
    "G2 Spring_theta_nre": 1.516635978,
    "G2 Spring_pr_1_99_nre": 76,
    "G2 Spring_ss_nre": 110,
    "G2 Spring_dss_nre": 652,
    "G3 Fall_theta_nre": 1.793170183,
    "G3 Fall_pr_1_99_nre": 76,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 679,
    "G3 Winter_theta_nre": 1.93031723,
    "G3 Winter_pr_1_99_nre": 76,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 693,
    "G3 Spring_theta_nre": 2.190856329,
    "G3 Spring_pr_1_99_nre": 76,
    "G3 Spring_ss_nre": 110,
    "G3 Spring_dss_nre": 719
  },
  {
    "ID": 381,
    "KG Winter_theta_nre": -0.314033282,
    "KG Winter_pr_1_99_nre": 76,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 469,
    "KG Spring_theta_nre": 0.027182354,
    "KG Spring_pr_1_99_nre": 76,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 503,
    "G1 Fall_theta_nre": 0.279113287,
    "G1 Fall_pr_1_99_nre": 76,
    "G1 Fall_ss_nre": 111,
    "G1 Fall_dss_nre": 528,
    "G1 Winter_theta_nre": 0.519535784,
    "G1 Winter_pr_1_99_nre": 76,
    "G1 Winter_ss_nre": 111,
    "G1 Winter_dss_nre": 552,
    "G1 Spring_theta_nre": 0.723152353,
    "G1 Spring_pr_1_99_nre": 76,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 572,
    "G2 Fall_theta_nre": 1.022108286,
    "G2 Fall_pr_1_99_nre": 76,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 602,
    "G2 Winter_theta_nre": 1.20649087,
    "G2 Winter_pr_1_99_nre": 76,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 621,
    "G2 Spring_theta_nre": 1.526860691,
    "G2 Spring_pr_1_99_nre": 76,
    "G2 Spring_ss_nre": 110,
    "G2 Spring_dss_nre": 653,
    "G3 Fall_theta_nre": 1.797550117,
    "G3 Fall_pr_1_99_nre": 76,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 680,
    "G3 Winter_theta_nre": 1.930757065,
    "G3 Winter_pr_1_99_nre": 76,
    "G3 Winter_ss_nre": 110,
    "G3 Winter_dss_nre": 693,
    "G3 Spring_theta_nre": 2.194125084,
    "G3 Spring_pr_1_99_nre": 76,
    "G3 Spring_ss_nre": 110,
    "G3 Spring_dss_nre": 719
  },
  {
    "ID": 382,
    "KG Winter_theta_nre": -0.314030446,
    "KG Winter_pr_1_99_nre": 76,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 469,
    "KG Spring_theta_nre": 0.02751601,
    "KG Spring_pr_1_99_nre": 76,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 503,
    "G1 Fall_theta_nre": 0.283625602,
    "G1 Fall_pr_1_99_nre": 76,
    "G1 Fall_ss_nre": 111,
    "G1 Fall_dss_nre": 528,
    "G1 Winter_theta_nre": 0.52599943,
    "G1 Winter_pr_1_99_nre": 76,
    "G1 Winter_ss_nre": 111,
    "G1 Winter_dss_nre": 553,
    "G1 Spring_theta_nre": 0.724199086,
    "G1 Spring_pr_1_99_nre": 76,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 572,
    "G2 Fall_theta_nre": 1.027188205,
    "G2 Fall_pr_1_99_nre": 76,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 603,
    "G2 Winter_theta_nre": 1.210574256,
    "G2 Winter_pr_1_99_nre": 76,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 621,
    "G2 Spring_theta_nre": 1.54133075,
    "G2 Spring_pr_1_99_nre": 76,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 654,
    "G3 Fall_theta_nre": 1.800862467,
    "G3 Fall_pr_1_99_nre": 76,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 680,
    "G3 Winter_theta_nre": 1.933522902,
    "G3 Winter_pr_1_99_nre": 76,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 693,
    "G3 Spring_theta_nre": 2.199731998,
    "G3 Spring_pr_1_99_nre": 76,
    "G3 Spring_ss_nre": 111,
    "G3 Spring_dss_nre": 720
  },
  {
    "ID": 383,
    "KG Winter_theta_nre": -0.313064879,
    "KG Winter_pr_1_99_nre": 77,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 469,
    "KG Spring_theta_nre": 0.02795921,
    "KG Spring_pr_1_99_nre": 77,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 503,
    "G1 Fall_theta_nre": 0.299071302,
    "G1 Fall_pr_1_99_nre": 77,
    "G1 Fall_ss_nre": 111,
    "G1 Fall_dss_nre": 530,
    "G1 Winter_theta_nre": 0.526590787,
    "G1 Winter_pr_1_99_nre": 77,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 553,
    "G1 Spring_theta_nre": 0.72566416,
    "G1 Spring_pr_1_99_nre": 77,
    "G1 Spring_ss_nre": 110,
    "G1 Spring_dss_nre": 573,
    "G2 Fall_theta_nre": 1.031397496,
    "G2 Fall_pr_1_99_nre": 77,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 603,
    "G2 Winter_theta_nre": 1.210836012,
    "G2 Winter_pr_1_99_nre": 77,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 621,
    "G2 Spring_theta_nre": 1.545734334,
    "G2 Spring_pr_1_99_nre": 77,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 655,
    "G3 Fall_theta_nre": 1.803532485,
    "G3 Fall_pr_1_99_nre": 77,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 680,
    "G3 Winter_theta_nre": 1.937060722,
    "G3 Winter_pr_1_99_nre": 77,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 694,
    "G3 Spring_theta_nre": 2.221634729,
    "G3 Spring_pr_1_99_nre": 77,
    "G3 Spring_ss_nre": 111,
    "G3 Spring_dss_nre": 722
  },
  {
    "ID": 384,
    "KG Winter_theta_nre": -0.304280675,
    "KG Winter_pr_1_99_nre": 77,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 470,
    "KG Spring_theta_nre": 0.044681432,
    "KG Spring_pr_1_99_nre": 77,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 504,
    "G1 Fall_theta_nre": 0.300580874,
    "G1 Fall_pr_1_99_nre": 77,
    "G1 Fall_ss_nre": 111,
    "G1 Fall_dss_nre": 530,
    "G1 Winter_theta_nre": 0.526823771,
    "G1 Winter_pr_1_99_nre": 77,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 553,
    "G1 Spring_theta_nre": 0.738130106,
    "G1 Spring_pr_1_99_nre": 77,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 574,
    "G2 Fall_theta_nre": 1.039138404,
    "G2 Fall_pr_1_99_nre": 77,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 604,
    "G2 Winter_theta_nre": 1.211429942,
    "G2 Winter_pr_1_99_nre": 77,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 621,
    "G2 Spring_theta_nre": 1.548404909,
    "G2 Spring_pr_1_99_nre": 77,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 655,
    "G3 Fall_theta_nre": 1.822239739,
    "G3 Fall_pr_1_99_nre": 77,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 682,
    "G3 Winter_theta_nre": 1.938332209,
    "G3 Winter_pr_1_99_nre": 77,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 694,
    "G3 Spring_theta_nre": 2.225999284,
    "G3 Spring_pr_1_99_nre": 77,
    "G3 Spring_ss_nre": 111,
    "G3 Spring_dss_nre": 723
  },
  {
    "ID": 385,
    "KG Winter_theta_nre": -0.303343354,
    "KG Winter_pr_1_99_nre": 77,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 470,
    "KG Spring_theta_nre": 0.054544537,
    "KG Spring_pr_1_99_nre": 77,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 505,
    "G1 Fall_theta_nre": 0.303109727,
    "G1 Fall_pr_1_99_nre": 77,
    "G1 Fall_ss_nre": 111,
    "G1 Fall_dss_nre": 530,
    "G1 Winter_theta_nre": 0.527545206,
    "G1 Winter_pr_1_99_nre": 77,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 553,
    "G1 Spring_theta_nre": 0.739415007,
    "G1 Spring_pr_1_99_nre": 77,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 574,
    "G2 Fall_theta_nre": 1.039840704,
    "G2 Fall_pr_1_99_nre": 77,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 604,
    "G2 Winter_theta_nre": 1.213259669,
    "G2 Winter_pr_1_99_nre": 77,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 621,
    "G2 Spring_theta_nre": 1.550880707,
    "G2 Spring_pr_1_99_nre": 77,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 655,
    "G3 Fall_theta_nre": 1.829249581,
    "G3 Fall_pr_1_99_nre": 77,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 683,
    "G3 Winter_theta_nre": 1.942954075,
    "G3 Winter_pr_1_99_nre": 77,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 694,
    "G3 Spring_theta_nre": 2.234526856,
    "G3 Spring_pr_1_99_nre": 77,
    "G3 Spring_ss_nre": 111,
    "G3 Spring_dss_nre": 723
  },
  {
    "ID": 386,
    "KG Winter_theta_nre": -0.29068494,
    "KG Winter_pr_1_99_nre": 77,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 471,
    "KG Spring_theta_nre": 0.05534458,
    "KG Spring_pr_1_99_nre": 77,
    "KG Spring_ss_nre": 111,
    "KG Spring_dss_nre": 506,
    "G1 Fall_theta_nre": 0.303612156,
    "G1 Fall_pr_1_99_nre": 77,
    "G1 Fall_ss_nre": 111,
    "G1 Fall_dss_nre": 530,
    "G1 Winter_theta_nre": 0.533988491,
    "G1 Winter_pr_1_99_nre": 77,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 553,
    "G1 Spring_theta_nre": 0.741439139,
    "G1 Spring_pr_1_99_nre": 77,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 574,
    "G2 Fall_theta_nre": 1.041953995,
    "G2 Fall_pr_1_99_nre": 77,
    "G2 Fall_ss_nre": 111,
    "G2 Fall_dss_nre": 604,
    "G2 Winter_theta_nre": 1.215326474,
    "G2 Winter_pr_1_99_nre": 77,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 622,
    "G2 Spring_theta_nre": 1.555838191,
    "G2 Spring_pr_1_99_nre": 77,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 656,
    "G3 Fall_theta_nre": 1.829280009,
    "G3 Fall_pr_1_99_nre": 77,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 683,
    "G3 Winter_theta_nre": 1.960778206,
    "G3 Winter_pr_1_99_nre": 77,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 696,
    "G3 Spring_theta_nre": 2.237499103,
    "G3 Spring_pr_1_99_nre": 77,
    "G3 Spring_ss_nre": 111,
    "G3 Spring_dss_nre": 724
  },
  {
    "ID": 387,
    "KG Winter_theta_nre": -0.285034297,
    "KG Winter_pr_1_99_nre": 77,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 471,
    "KG Spring_theta_nre": 0.060593161,
    "KG Spring_pr_1_99_nre": 77,
    "KG Spring_ss_nre": 112,
    "KG Spring_dss_nre": 506,
    "G1 Fall_theta_nre": 0.30515333,
    "G1 Fall_pr_1_99_nre": 77,
    "G1 Fall_ss_nre": 111,
    "G1 Fall_dss_nre": 531,
    "G1 Winter_theta_nre": 0.539281584,
    "G1 Winter_pr_1_99_nre": 77,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 554,
    "G1 Spring_theta_nre": 0.751432386,
    "G1 Spring_pr_1_99_nre": 77,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 575,
    "G2 Fall_theta_nre": 1.050582222,
    "G2 Fall_pr_1_99_nre": 77,
    "G2 Fall_ss_nre": 112,
    "G2 Fall_dss_nre": 605,
    "G2 Winter_theta_nre": 1.232225864,
    "G2 Winter_pr_1_99_nre": 77,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 623,
    "G2 Spring_theta_nre": 1.561245853,
    "G2 Spring_pr_1_99_nre": 77,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 656,
    "G3 Fall_theta_nre": 1.829884311,
    "G3 Fall_pr_1_99_nre": 77,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 683,
    "G3 Winter_theta_nre": 1.961482722,
    "G3 Winter_pr_1_99_nre": 77,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 696,
    "G3 Spring_theta_nre": 2.240554207,
    "G3 Spring_pr_1_99_nre": 77,
    "G3 Spring_ss_nre": 111,
    "G3 Spring_dss_nre": 724
  },
  {
    "ID": 388,
    "KG Winter_theta_nre": -0.284695652,
    "KG Winter_pr_1_99_nre": 78,
    "KG Winter_ss_nre": 111,
    "KG Winter_dss_nre": 472,
    "KG Spring_theta_nre": 0.063261659,
    "KG Spring_pr_1_99_nre": 78,
    "KG Spring_ss_nre": 112,
    "KG Spring_dss_nre": 506,
    "G1 Fall_theta_nre": 0.311197442,
    "G1 Fall_pr_1_99_nre": 78,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 531,
    "G1 Winter_theta_nre": 0.547659322,
    "G1 Winter_pr_1_99_nre": 78,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 555,
    "G1 Spring_theta_nre": 0.752607851,
    "G1 Spring_pr_1_99_nre": 78,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 575,
    "G2 Fall_theta_nre": 1.063114686,
    "G2 Fall_pr_1_99_nre": 78,
    "G2 Fall_ss_nre": 112,
    "G2 Fall_dss_nre": 606,
    "G2 Winter_theta_nre": 1.232352316,
    "G2 Winter_pr_1_99_nre": 78,
    "G2 Winter_ss_nre": 111,
    "G2 Winter_dss_nre": 623,
    "G2 Spring_theta_nre": 1.573838449,
    "G2 Spring_pr_1_99_nre": 78,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 657,
    "G3 Fall_theta_nre": 1.833441889,
    "G3 Fall_pr_1_99_nre": 78,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 683,
    "G3 Winter_theta_nre": 1.967216563,
    "G3 Winter_pr_1_99_nre": 78,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 697,
    "G3 Spring_theta_nre": 2.242454075,
    "G3 Spring_pr_1_99_nre": 78,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 724
  },
  {
    "ID": 389,
    "KG Winter_theta_nre": -0.271388379,
    "KG Winter_pr_1_99_nre": 78,
    "KG Winter_ss_nre": 112,
    "KG Winter_dss_nre": 473,
    "KG Spring_theta_nre": 0.082056686,
    "KG Spring_pr_1_99_nre": 78,
    "KG Spring_ss_nre": 112,
    "KG Spring_dss_nre": 508,
    "G1 Fall_theta_nre": 0.313396113,
    "G1 Fall_pr_1_99_nre": 78,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 531,
    "G1 Winter_theta_nre": 0.549393122,
    "G1 Winter_pr_1_99_nre": 78,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 555,
    "G1 Spring_theta_nre": 0.761891608,
    "G1 Spring_pr_1_99_nre": 78,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 576,
    "G2 Fall_theta_nre": 1.064824568,
    "G2 Fall_pr_1_99_nre": 78,
    "G2 Fall_ss_nre": 112,
    "G2 Fall_dss_nre": 606,
    "G2 Winter_theta_nre": 1.23649695,
    "G2 Winter_pr_1_99_nre": 78,
    "G2 Winter_ss_nre": 112,
    "G2 Winter_dss_nre": 624,
    "G2 Spring_theta_nre": 1.582303988,
    "G2 Spring_pr_1_99_nre": 78,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 658,
    "G3 Fall_theta_nre": 1.843669592,
    "G3 Fall_pr_1_99_nre": 78,
    "G3 Fall_ss_nre": 110,
    "G3 Fall_dss_nre": 684,
    "G3 Winter_theta_nre": 1.973375135,
    "G3 Winter_pr_1_99_nre": 78,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 697,
    "G3 Spring_theta_nre": 2.247272745,
    "G3 Spring_pr_1_99_nre": 78,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 725
  },
  {
    "ID": 390,
    "KG Winter_theta_nre": -0.267018482,
    "KG Winter_pr_1_99_nre": 78,
    "KG Winter_ss_nre": 112,
    "KG Winter_dss_nre": 473,
    "KG Spring_theta_nre": 0.091344678,
    "KG Spring_pr_1_99_nre": 78,
    "KG Spring_ss_nre": 112,
    "KG Spring_dss_nre": 509,
    "G1 Fall_theta_nre": 0.314111116,
    "G1 Fall_pr_1_99_nre": 78,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 531,
    "G1 Winter_theta_nre": 0.552107785,
    "G1 Winter_pr_1_99_nre": 78,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 555,
    "G1 Spring_theta_nre": 0.766798568,
    "G1 Spring_pr_1_99_nre": 78,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 577,
    "G2 Fall_theta_nre": 1.067120636,
    "G2 Fall_pr_1_99_nre": 78,
    "G2 Fall_ss_nre": 112,
    "G2 Fall_dss_nre": 607,
    "G2 Winter_theta_nre": 1.239538288,
    "G2 Winter_pr_1_99_nre": 78,
    "G2 Winter_ss_nre": 112,
    "G2 Winter_dss_nre": 624,
    "G2 Spring_theta_nre": 1.582986136,
    "G2 Spring_pr_1_99_nre": 78,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 658,
    "G3 Fall_theta_nre": 1.852747737,
    "G3 Fall_pr_1_99_nre": 78,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 685,
    "G3 Winter_theta_nre": 1.974805068,
    "G3 Winter_pr_1_99_nre": 78,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 697,
    "G3 Spring_theta_nre": 2.247892563,
    "G3 Spring_pr_1_99_nre": 78,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 725
  },
  {
    "ID": 391,
    "KG Winter_theta_nre": -0.264331655,
    "KG Winter_pr_1_99_nre": 78,
    "KG Winter_ss_nre": 112,
    "KG Winter_dss_nre": 474,
    "KG Spring_theta_nre": 0.112933871,
    "KG Spring_pr_1_99_nre": 78,
    "KG Spring_ss_nre": 112,
    "KG Spring_dss_nre": 511,
    "G1 Fall_theta_nre": 0.315805032,
    "G1 Fall_pr_1_99_nre": 78,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 532,
    "G1 Winter_theta_nre": 0.559567085,
    "G1 Winter_pr_1_99_nre": 78,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 556,
    "G1 Spring_theta_nre": 0.771988005,
    "G1 Spring_pr_1_99_nre": 78,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 577,
    "G2 Fall_theta_nre": 1.071539515,
    "G2 Fall_pr_1_99_nre": 78,
    "G2 Fall_ss_nre": 112,
    "G2 Fall_dss_nre": 607,
    "G2 Winter_theta_nre": 1.247112842,
    "G2 Winter_pr_1_99_nre": 78,
    "G2 Winter_ss_nre": 112,
    "G2 Winter_dss_nre": 625,
    "G2 Spring_theta_nre": 1.585767047,
    "G2 Spring_pr_1_99_nre": 78,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 659,
    "G3 Fall_theta_nre": 1.859595132,
    "G3 Fall_pr_1_99_nre": 78,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 686,
    "G3 Winter_theta_nre": 1.977538805,
    "G3 Winter_pr_1_99_nre": 78,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 698,
    "G3 Spring_theta_nre": 2.254001153,
    "G3 Spring_pr_1_99_nre": 78,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 725
  },
  {
    "ID": 392,
    "KG Winter_theta_nre": -0.252134822,
    "KG Winter_pr_1_99_nre": 78,
    "KG Winter_ss_nre": 112,
    "KG Winter_dss_nre": 475,
    "KG Spring_theta_nre": 0.113531298,
    "KG Spring_pr_1_99_nre": 78,
    "KG Spring_ss_nre": 112,
    "KG Spring_dss_nre": 511,
    "G1 Fall_theta_nre": 0.323229624,
    "G1 Fall_pr_1_99_nre": 78,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 532,
    "G1 Winter_theta_nre": 0.56358654,
    "G1 Winter_pr_1_99_nre": 78,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 556,
    "G1 Spring_theta_nre": 0.774418384,
    "G1 Spring_pr_1_99_nre": 78,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 577,
    "G2 Fall_theta_nre": 1.077023906,
    "G2 Fall_pr_1_99_nre": 78,
    "G2 Fall_ss_nre": 112,
    "G2 Fall_dss_nre": 608,
    "G2 Winter_theta_nre": 1.25023552,
    "G2 Winter_pr_1_99_nre": 78,
    "G2 Winter_ss_nre": 112,
    "G2 Winter_dss_nre": 625,
    "G2 Spring_theta_nre": 1.595681299,
    "G2 Spring_pr_1_99_nre": 78,
    "G2 Spring_ss_nre": 111,
    "G2 Spring_dss_nre": 660,
    "G3 Fall_theta_nre": 1.861388723,
    "G3 Fall_pr_1_99_nre": 78,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 686,
    "G3 Winter_theta_nre": 1.979239712,
    "G3 Winter_pr_1_99_nre": 78,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 698,
    "G3 Spring_theta_nre": 2.256083948,
    "G3 Spring_pr_1_99_nre": 78,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 726
  },
  {
    "ID": 393,
    "KG Winter_theta_nre": -0.241108381,
    "KG Winter_pr_1_99_nre": 79,
    "KG Winter_ss_nre": 112,
    "KG Winter_dss_nre": 476,
    "KG Spring_theta_nre": 0.12470296,
    "KG Spring_pr_1_99_nre": 79,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 512,
    "G1 Fall_theta_nre": 0.340225444,
    "G1 Fall_pr_1_99_nre": 79,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 534,
    "G1 Winter_theta_nre": 0.565654143,
    "G1 Winter_pr_1_99_nre": 79,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 557,
    "G1 Spring_theta_nre": 0.777956664,
    "G1 Spring_pr_1_99_nre": 79,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 578,
    "G2 Fall_theta_nre": 1.079690327,
    "G2 Fall_pr_1_99_nre": 79,
    "G2 Fall_ss_nre": 112,
    "G2 Fall_dss_nre": 608,
    "G2 Winter_theta_nre": 1.264938033,
    "G2 Winter_pr_1_99_nre": 79,
    "G2 Winter_ss_nre": 112,
    "G2 Winter_dss_nre": 626,
    "G2 Spring_theta_nre": 1.599609914,
    "G2 Spring_pr_1_99_nre": 79,
    "G2 Spring_ss_nre": 112,
    "G2 Spring_dss_nre": 660,
    "G3 Fall_theta_nre": 1.87136624,
    "G3 Fall_pr_1_99_nre": 79,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 687,
    "G3 Winter_theta_nre": 1.979883285,
    "G3 Winter_pr_1_99_nre": 79,
    "G3 Winter_ss_nre": 111,
    "G3 Winter_dss_nre": 698,
    "G3 Spring_theta_nre": 2.261021901,
    "G3 Spring_pr_1_99_nre": 79,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 726
  },
  {
    "ID": 394,
    "KG Winter_theta_nre": -0.234727671,
    "KG Winter_pr_1_99_nre": 79,
    "KG Winter_ss_nre": 112,
    "KG Winter_dss_nre": 477,
    "KG Spring_theta_nre": 0.125538571,
    "KG Spring_pr_1_99_nre": 79,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 513,
    "G1 Fall_theta_nre": 0.340318336,
    "G1 Fall_pr_1_99_nre": 79,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 534,
    "G1 Winter_theta_nre": 0.56658895,
    "G1 Winter_pr_1_99_nre": 79,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 557,
    "G1 Spring_theta_nre": 0.78108414,
    "G1 Spring_pr_1_99_nre": 79,
    "G1 Spring_ss_nre": 111,
    "G1 Spring_dss_nre": 578,
    "G2 Fall_theta_nre": 1.080507426,
    "G2 Fall_pr_1_99_nre": 79,
    "G2 Fall_ss_nre": 112,
    "G2 Fall_dss_nre": 608,
    "G2 Winter_theta_nre": 1.265835879,
    "G2 Winter_pr_1_99_nre": 79,
    "G2 Winter_ss_nre": 112,
    "G2 Winter_dss_nre": 627,
    "G2 Spring_theta_nre": 1.619753495,
    "G2 Spring_pr_1_99_nre": 79,
    "G2 Spring_ss_nre": 112,
    "G2 Spring_dss_nre": 662,
    "G3 Fall_theta_nre": 1.873289837,
    "G3 Fall_pr_1_99_nre": 79,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 687,
    "G3 Winter_theta_nre": 1.984294349,
    "G3 Winter_pr_1_99_nre": 79,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 698,
    "G3 Spring_theta_nre": 2.263345353,
    "G3 Spring_pr_1_99_nre": 79,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 726
  },
  {
    "ID": 395,
    "KG Winter_theta_nre": -0.233728012,
    "KG Winter_pr_1_99_nre": 79,
    "KG Winter_ss_nre": 112,
    "KG Winter_dss_nre": 477,
    "KG Spring_theta_nre": 0.13373282,
    "KG Spring_pr_1_99_nre": 79,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 513,
    "G1 Fall_theta_nre": 0.341150082,
    "G1 Fall_pr_1_99_nre": 79,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 534,
    "G1 Winter_theta_nre": 0.57170625,
    "G1 Winter_pr_1_99_nre": 79,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 557,
    "G1 Spring_theta_nre": 0.79448837,
    "G1 Spring_pr_1_99_nre": 79,
    "G1 Spring_ss_nre": 112,
    "G1 Spring_dss_nre": 579,
    "G2 Fall_theta_nre": 1.087045588,
    "G2 Fall_pr_1_99_nre": 79,
    "G2 Fall_ss_nre": 112,
    "G2 Fall_dss_nre": 609,
    "G2 Winter_theta_nre": 1.27890784,
    "G2 Winter_pr_1_99_nre": 79,
    "G2 Winter_ss_nre": 112,
    "G2 Winter_dss_nre": 628,
    "G2 Spring_theta_nre": 1.622506536,
    "G2 Spring_pr_1_99_nre": 79,
    "G2 Spring_ss_nre": 112,
    "G2 Spring_dss_nre": 662,
    "G3 Fall_theta_nre": 1.875774251,
    "G3 Fall_pr_1_99_nre": 79,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 688,
    "G3 Winter_theta_nre": 1.984613847,
    "G3 Winter_pr_1_99_nre": 79,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 698,
    "G3 Spring_theta_nre": 2.264365325,
    "G3 Spring_pr_1_99_nre": 79,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 726
  },
  {
    "ID": 396,
    "KG Winter_theta_nre": -0.223398454,
    "KG Winter_pr_1_99_nre": 79,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 478,
    "KG Spring_theta_nre": 0.140711524,
    "KG Spring_pr_1_99_nre": 79,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 514,
    "G1 Fall_theta_nre": 0.346208929,
    "G1 Fall_pr_1_99_nre": 79,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 535,
    "G1 Winter_theta_nre": 0.572306675,
    "G1 Winter_pr_1_99_nre": 79,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 557,
    "G1 Spring_theta_nre": 0.81595748,
    "G1 Spring_pr_1_99_nre": 79,
    "G1 Spring_ss_nre": 112,
    "G1 Spring_dss_nre": 582,
    "G2 Fall_theta_nre": 1.088347548,
    "G2 Fall_pr_1_99_nre": 79,
    "G2 Fall_ss_nre": 112,
    "G2 Fall_dss_nre": 609,
    "G2 Winter_theta_nre": 1.282564421,
    "G2 Winter_pr_1_99_nre": 79,
    "G2 Winter_ss_nre": 112,
    "G2 Winter_dss_nre": 628,
    "G2 Spring_theta_nre": 1.631397887,
    "G2 Spring_pr_1_99_nre": 79,
    "G2 Spring_ss_nre": 112,
    "G2 Spring_dss_nre": 663,
    "G3 Fall_theta_nre": 1.876352063,
    "G3 Fall_pr_1_99_nre": 79,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 688,
    "G3 Winter_theta_nre": 1.996215064,
    "G3 Winter_pr_1_99_nre": 79,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 700,
    "G3 Spring_theta_nre": 2.264584974,
    "G3 Spring_pr_1_99_nre": 79,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 726
  },
  {
    "ID": 397,
    "KG Winter_theta_nre": -0.218619318,
    "KG Winter_pr_1_99_nre": 79,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 478,
    "KG Spring_theta_nre": 0.150407082,
    "KG Spring_pr_1_99_nre": 79,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 515,
    "G1 Fall_theta_nre": 0.354423504,
    "G1 Fall_pr_1_99_nre": 79,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 535,
    "G1 Winter_theta_nre": 0.579036703,
    "G1 Winter_pr_1_99_nre": 79,
    "G1 Winter_ss_nre": 112,
    "G1 Winter_dss_nre": 558,
    "G1 Spring_theta_nre": 0.817688285,
    "G1 Spring_pr_1_99_nre": 79,
    "G1 Spring_ss_nre": 112,
    "G1 Spring_dss_nre": 582,
    "G2 Fall_theta_nre": 1.109313381,
    "G2 Fall_pr_1_99_nre": 79,
    "G2 Fall_ss_nre": 113,
    "G2 Fall_dss_nre": 611,
    "G2 Winter_theta_nre": 1.287731818,
    "G2 Winter_pr_1_99_nre": 79,
    "G2 Winter_ss_nre": 113,
    "G2 Winter_dss_nre": 629,
    "G2 Spring_theta_nre": 1.641338913,
    "G2 Spring_pr_1_99_nre": 79,
    "G2 Spring_ss_nre": 112,
    "G2 Spring_dss_nre": 664,
    "G3 Fall_theta_nre": 1.880605022,
    "G3 Fall_pr_1_99_nre": 79,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 688,
    "G3 Winter_theta_nre": 1.996660304,
    "G3 Winter_pr_1_99_nre": 79,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 700,
    "G3 Spring_theta_nre": 2.264760613,
    "G3 Spring_pr_1_99_nre": 79,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 726
  },
  {
    "ID": 398,
    "KG Winter_theta_nre": -0.216756036,
    "KG Winter_pr_1_99_nre": 80,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 478,
    "KG Spring_theta_nre": 0.151923068,
    "KG Spring_pr_1_99_nre": 80,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 515,
    "G1 Fall_theta_nre": 0.359040167,
    "G1 Fall_pr_1_99_nre": 80,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 536,
    "G1 Winter_theta_nre": 0.582665182,
    "G1 Winter_pr_1_99_nre": 80,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 558,
    "G1 Spring_theta_nre": 0.818908446,
    "G1 Spring_pr_1_99_nre": 80,
    "G1 Spring_ss_nre": 112,
    "G1 Spring_dss_nre": 582,
    "G2 Fall_theta_nre": 1.113311258,
    "G2 Fall_pr_1_99_nre": 80,
    "G2 Fall_ss_nre": 113,
    "G2 Fall_dss_nre": 611,
    "G2 Winter_theta_nre": 1.302575732,
    "G2 Winter_pr_1_99_nre": 80,
    "G2 Winter_ss_nre": 113,
    "G2 Winter_dss_nre": 630,
    "G2 Spring_theta_nre": 1.669333955,
    "G2 Spring_pr_1_99_nre": 80,
    "G2 Spring_ss_nre": 113,
    "G2 Spring_dss_nre": 667,
    "G3 Fall_theta_nre": 1.880939146,
    "G3 Fall_pr_1_99_nre": 80,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 688,
    "G3 Winter_theta_nre": 1.996692491,
    "G3 Winter_pr_1_99_nre": 80,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 700,
    "G3 Spring_theta_nre": 2.265337135,
    "G3 Spring_pr_1_99_nre": 80,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 727
  },
  {
    "ID": 399,
    "KG Winter_theta_nre": -0.215167625,
    "KG Winter_pr_1_99_nre": 80,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 478,
    "KG Spring_theta_nre": 0.15381209,
    "KG Spring_pr_1_99_nre": 80,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 515,
    "G1 Fall_theta_nre": 0.363204988,
    "G1 Fall_pr_1_99_nre": 80,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 536,
    "G1 Winter_theta_nre": 0.587228658,
    "G1 Winter_pr_1_99_nre": 80,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 559,
    "G1 Spring_theta_nre": 0.838938905,
    "G1 Spring_pr_1_99_nre": 80,
    "G1 Spring_ss_nre": 112,
    "G1 Spring_dss_nre": 584,
    "G2 Fall_theta_nre": 1.128641156,
    "G2 Fall_pr_1_99_nre": 80,
    "G2 Fall_ss_nre": 113,
    "G2 Fall_dss_nre": 613,
    "G2 Winter_theta_nre": 1.326213023,
    "G2 Winter_pr_1_99_nre": 80,
    "G2 Winter_ss_nre": 113,
    "G2 Winter_dss_nre": 633,
    "G2 Spring_theta_nre": 1.670182849,
    "G2 Spring_pr_1_99_nre": 80,
    "G2 Spring_ss_nre": 113,
    "G2 Spring_dss_nre": 667,
    "G3 Fall_theta_nre": 1.883453507,
    "G3 Fall_pr_1_99_nre": 80,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 688,
    "G3 Winter_theta_nre": 1.997416922,
    "G3 Winter_pr_1_99_nre": 80,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 700,
    "G3 Spring_theta_nre": 2.273408277,
    "G3 Spring_pr_1_99_nre": 80,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 727
  },
  {
    "ID": 400,
    "KG Winter_theta_nre": -0.213292074,
    "KG Winter_pr_1_99_nre": 80,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 479,
    "KG Spring_theta_nre": 0.154405944,
    "KG Spring_pr_1_99_nre": 80,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 515,
    "G1 Fall_theta_nre": 0.365968252,
    "G1 Fall_pr_1_99_nre": 80,
    "G1 Fall_ss_nre": 112,
    "G1 Fall_dss_nre": 537,
    "G1 Winter_theta_nre": 0.590473621,
    "G1 Winter_pr_1_99_nre": 80,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 559,
    "G1 Spring_theta_nre": 0.843063313,
    "G1 Spring_pr_1_99_nre": 80,
    "G1 Spring_ss_nre": 112,
    "G1 Spring_dss_nre": 584,
    "G2 Fall_theta_nre": 1.13473985,
    "G2 Fall_pr_1_99_nre": 80,
    "G2 Fall_ss_nre": 113,
    "G2 Fall_dss_nre": 613,
    "G2 Winter_theta_nre": 1.326741675,
    "G2 Winter_pr_1_99_nre": 80,
    "G2 Winter_ss_nre": 113,
    "G2 Winter_dss_nre": 633,
    "G2 Spring_theta_nre": 1.674476246,
    "G2 Spring_pr_1_99_nre": 80,
    "G2 Spring_ss_nre": 113,
    "G2 Spring_dss_nre": 667,
    "G3 Fall_theta_nre": 1.903615723,
    "G3 Fall_pr_1_99_nre": 80,
    "G3 Fall_ss_nre": 111,
    "G3 Fall_dss_nre": 690,
    "G3 Winter_theta_nre": 2.001243621,
    "G3 Winter_pr_1_99_nre": 80,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 700,
    "G3 Spring_theta_nre": 2.274859838,
    "G3 Spring_pr_1_99_nre": 80,
    "G3 Spring_ss_nre": 112,
    "G3 Spring_dss_nre": 727
  },
  {
    "ID": 401,
    "KG Winter_theta_nre": -0.204258886,
    "KG Winter_pr_1_99_nre": 80,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 480,
    "KG Spring_theta_nre": 0.157448477,
    "KG Spring_pr_1_99_nre": 80,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 516,
    "G1 Fall_theta_nre": 0.369968477,
    "G1 Fall_pr_1_99_nre": 80,
    "G1 Fall_ss_nre": 113,
    "G1 Fall_dss_nre": 537,
    "G1 Winter_theta_nre": 0.593830332,
    "G1 Winter_pr_1_99_nre": 80,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 559,
    "G1 Spring_theta_nre": 0.845207169,
    "G1 Spring_pr_1_99_nre": 80,
    "G1 Spring_ss_nre": 113,
    "G1 Spring_dss_nre": 585,
    "G2 Fall_theta_nre": 1.136570648,
    "G2 Fall_pr_1_99_nre": 80,
    "G2 Fall_ss_nre": 113,
    "G2 Fall_dss_nre": 614,
    "G2 Winter_theta_nre": 1.329446302,
    "G2 Winter_pr_1_99_nre": 80,
    "G2 Winter_ss_nre": 113,
    "G2 Winter_dss_nre": 633,
    "G2 Spring_theta_nre": 1.681743635,
    "G2 Spring_pr_1_99_nre": 80,
    "G2 Spring_ss_nre": 113,
    "G2 Spring_dss_nre": 668,
    "G3 Fall_theta_nre": 1.915632616,
    "G3 Fall_pr_1_99_nre": 80,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 692,
    "G3 Winter_theta_nre": 2.006472679,
    "G3 Winter_pr_1_99_nre": 80,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 701,
    "G3 Spring_theta_nre": 2.289746986,
    "G3 Spring_pr_1_99_nre": 80,
    "G3 Spring_ss_nre": 113,
    "G3 Spring_dss_nre": 729
  },
  {
    "ID": 402,
    "KG Winter_theta_nre": -0.202604944,
    "KG Winter_pr_1_99_nre": 80,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 480,
    "KG Spring_theta_nre": 0.168455897,
    "KG Spring_pr_1_99_nre": 80,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 517,
    "G1 Fall_theta_nre": 0.392148797,
    "G1 Fall_pr_1_99_nre": 80,
    "G1 Fall_ss_nre": 113,
    "G1 Fall_dss_nre": 539,
    "G1 Winter_theta_nre": 0.594783246,
    "G1 Winter_pr_1_99_nre": 80,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 559,
    "G1 Spring_theta_nre": 0.859907444,
    "G1 Spring_pr_1_99_nre": 80,
    "G1 Spring_ss_nre": 113,
    "G1 Spring_dss_nre": 586,
    "G2 Fall_theta_nre": 1.142999783,
    "G2 Fall_pr_1_99_nre": 80,
    "G2 Fall_ss_nre": 113,
    "G2 Fall_dss_nre": 614,
    "G2 Winter_theta_nre": 1.334561216,
    "G2 Winter_pr_1_99_nre": 80,
    "G2 Winter_ss_nre": 113,
    "G2 Winter_dss_nre": 633,
    "G2 Spring_theta_nre": 1.686500032,
    "G2 Spring_pr_1_99_nre": 80,
    "G2 Spring_ss_nre": 113,
    "G2 Spring_dss_nre": 669,
    "G3 Fall_theta_nre": 1.920257925,
    "G3 Fall_pr_1_99_nre": 80,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 692,
    "G3 Winter_theta_nre": 2.006637258,
    "G3 Winter_pr_1_99_nre": 80,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 701,
    "G3 Spring_theta_nre": 2.299257763,
    "G3 Spring_pr_1_99_nre": 80,
    "G3 Spring_ss_nre": 113,
    "G3 Spring_dss_nre": 730
  },
  {
    "ID": 403,
    "KG Winter_theta_nre": -0.195240019,
    "KG Winter_pr_1_99_nre": 81,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 480,
    "KG Spring_theta_nre": 0.171180957,
    "KG Spring_pr_1_99_nre": 81,
    "KG Spring_ss_nre": 113,
    "KG Spring_dss_nre": 517,
    "G1 Fall_theta_nre": 0.392550972,
    "G1 Fall_pr_1_99_nre": 81,
    "G1 Fall_ss_nre": 113,
    "G1 Fall_dss_nre": 539,
    "G1 Winter_theta_nre": 0.596744434,
    "G1 Winter_pr_1_99_nre": 81,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 560,
    "G1 Spring_theta_nre": 0.862919325,
    "G1 Spring_pr_1_99_nre": 81,
    "G1 Spring_ss_nre": 113,
    "G1 Spring_dss_nre": 586,
    "G2 Fall_theta_nre": 1.163095218,
    "G2 Fall_pr_1_99_nre": 81,
    "G2 Fall_ss_nre": 114,
    "G2 Fall_dss_nre": 616,
    "G2 Winter_theta_nre": 1.338772895,
    "G2 Winter_pr_1_99_nre": 81,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 634,
    "G2 Spring_theta_nre": 1.691956486,
    "G2 Spring_pr_1_99_nre": 81,
    "G2 Spring_ss_nre": 113,
    "G2 Spring_dss_nre": 669,
    "G3 Fall_theta_nre": 1.926247462,
    "G3 Fall_pr_1_99_nre": 81,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 693,
    "G3 Winter_theta_nre": 2.015466206,
    "G3 Winter_pr_1_99_nre": 81,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 702,
    "G3 Spring_theta_nre": 2.302266725,
    "G3 Spring_pr_1_99_nre": 81,
    "G3 Spring_ss_nre": 113,
    "G3 Spring_dss_nre": 730
  },
  {
    "ID": 404,
    "KG Winter_theta_nre": -0.190657859,
    "KG Winter_pr_1_99_nre": 81,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 481,
    "KG Spring_theta_nre": 0.182164312,
    "KG Spring_pr_1_99_nre": 81,
    "KG Spring_ss_nre": 114,
    "KG Spring_dss_nre": 518,
    "G1 Fall_theta_nre": 0.394091122,
    "G1 Fall_pr_1_99_nre": 81,
    "G1 Fall_ss_nre": 113,
    "G1 Fall_dss_nre": 539,
    "G1 Winter_theta_nre": 0.600134025,
    "G1 Winter_pr_1_99_nre": 81,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 560,
    "G1 Spring_theta_nre": 0.871292132,
    "G1 Spring_pr_1_99_nre": 81,
    "G1 Spring_ss_nre": 113,
    "G1 Spring_dss_nre": 587,
    "G2 Fall_theta_nre": 1.163422089,
    "G2 Fall_pr_1_99_nre": 81,
    "G2 Fall_ss_nre": 114,
    "G2 Fall_dss_nre": 616,
    "G2 Winter_theta_nre": 1.339144318,
    "G2 Winter_pr_1_99_nre": 81,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 634,
    "G2 Spring_theta_nre": 1.692473173,
    "G2 Spring_pr_1_99_nre": 81,
    "G2 Spring_ss_nre": 113,
    "G2 Spring_dss_nre": 669,
    "G3 Fall_theta_nre": 1.929393341,
    "G3 Fall_pr_1_99_nre": 81,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 693,
    "G3 Winter_theta_nre": 2.016867615,
    "G3 Winter_pr_1_99_nre": 81,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 702,
    "G3 Spring_theta_nre": 2.305273189,
    "G3 Spring_pr_1_99_nre": 81,
    "G3 Spring_ss_nre": 113,
    "G3 Spring_dss_nre": 731
  },
  {
    "ID": 405,
    "KG Winter_theta_nre": -0.181528538,
    "KG Winter_pr_1_99_nre": 81,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 482,
    "KG Spring_theta_nre": 0.184299662,
    "KG Spring_pr_1_99_nre": 81,
    "KG Spring_ss_nre": 114,
    "KG Spring_dss_nre": 518,
    "G1 Fall_theta_nre": 0.39538664,
    "G1 Fall_pr_1_99_nre": 81,
    "G1 Fall_ss_nre": 113,
    "G1 Fall_dss_nre": 540,
    "G1 Winter_theta_nre": 0.603486422,
    "G1 Winter_pr_1_99_nre": 81,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 560,
    "G1 Spring_theta_nre": 0.872718841,
    "G1 Spring_pr_1_99_nre": 81,
    "G1 Spring_ss_nre": 113,
    "G1 Spring_dss_nre": 587,
    "G2 Fall_theta_nre": 1.175642682,
    "G2 Fall_pr_1_99_nre": 81,
    "G2 Fall_ss_nre": 114,
    "G2 Fall_dss_nre": 618,
    "G2 Winter_theta_nre": 1.342004444,
    "G2 Winter_pr_1_99_nre": 81,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 634,
    "G2 Spring_theta_nre": 1.705006227,
    "G2 Spring_pr_1_99_nre": 81,
    "G2 Spring_ss_nre": 113,
    "G2 Spring_dss_nre": 671,
    "G3 Fall_theta_nre": 1.939596716,
    "G3 Fall_pr_1_99_nre": 81,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 694,
    "G3 Winter_theta_nre": 2.020595231,
    "G3 Winter_pr_1_99_nre": 81,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 702,
    "G3 Spring_theta_nre": 2.313559509,
    "G3 Spring_pr_1_99_nre": 81,
    "G3 Spring_ss_nre": 113,
    "G3 Spring_dss_nre": 731
  },
  {
    "ID": 406,
    "KG Winter_theta_nre": -0.179144308,
    "KG Winter_pr_1_99_nre": 81,
    "KG Winter_ss_nre": 113,
    "KG Winter_dss_nre": 482,
    "KG Spring_theta_nre": 0.185806058,
    "KG Spring_pr_1_99_nre": 81,
    "KG Spring_ss_nre": 114,
    "KG Spring_dss_nre": 519,
    "G1 Fall_theta_nre": 0.402187743,
    "G1 Fall_pr_1_99_nre": 81,
    "G1 Fall_ss_nre": 113,
    "G1 Fall_dss_nre": 540,
    "G1 Winter_theta_nre": 0.614359165,
    "G1 Winter_pr_1_99_nre": 81,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 561,
    "G1 Spring_theta_nre": 0.887231762,
    "G1 Spring_pr_1_99_nre": 81,
    "G1 Spring_ss_nre": 113,
    "G1 Spring_dss_nre": 589,
    "G2 Fall_theta_nre": 1.17708264,
    "G2 Fall_pr_1_99_nre": 81,
    "G2 Fall_ss_nre": 114,
    "G2 Fall_dss_nre": 618,
    "G2 Winter_theta_nre": 1.34584299,
    "G2 Winter_pr_1_99_nre": 81,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 635,
    "G2 Spring_theta_nre": 1.726039604,
    "G2 Spring_pr_1_99_nre": 81,
    "G2 Spring_ss_nre": 114,
    "G2 Spring_dss_nre": 673,
    "G3 Fall_theta_nre": 1.941314562,
    "G3 Fall_pr_1_99_nre": 81,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 694,
    "G3 Winter_theta_nre": 2.027092953,
    "G3 Winter_pr_1_99_nre": 81,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 703,
    "G3 Spring_theta_nre": 2.328996976,
    "G3 Spring_pr_1_99_nre": 81,
    "G3 Spring_ss_nre": 113,
    "G3 Spring_dss_nre": 733
  },
  {
    "ID": 407,
    "KG Winter_theta_nre": -0.168268392,
    "KG Winter_pr_1_99_nre": 81,
    "KG Winter_ss_nre": 114,
    "KG Winter_dss_nre": 483,
    "KG Spring_theta_nre": 0.191374136,
    "KG Spring_pr_1_99_nre": 81,
    "KG Spring_ss_nre": 114,
    "KG Spring_dss_nre": 519,
    "G1 Fall_theta_nre": 0.414179244,
    "G1 Fall_pr_1_99_nre": 81,
    "G1 Fall_ss_nre": 113,
    "G1 Fall_dss_nre": 541,
    "G1 Winter_theta_nre": 0.619231956,
    "G1 Winter_pr_1_99_nre": 81,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 562,
    "G1 Spring_theta_nre": 0.902352564,
    "G1 Spring_pr_1_99_nre": 81,
    "G1 Spring_ss_nre": 114,
    "G1 Spring_dss_nre": 590,
    "G2 Fall_theta_nre": 1.182427556,
    "G2 Fall_pr_1_99_nre": 81,
    "G2 Fall_ss_nre": 114,
    "G2 Fall_dss_nre": 618,
    "G2 Winter_theta_nre": 1.347290462,
    "G2 Winter_pr_1_99_nre": 81,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 635,
    "G2 Spring_theta_nre": 1.72662397,
    "G2 Spring_pr_1_99_nre": 81,
    "G2 Spring_ss_nre": 114,
    "G2 Spring_dss_nre": 673,
    "G3 Fall_theta_nre": 1.941482326,
    "G3 Fall_pr_1_99_nre": 81,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 694,
    "G3 Winter_theta_nre": 2.029665631,
    "G3 Winter_pr_1_99_nre": 81,
    "G3 Winter_ss_nre": 112,
    "G3 Winter_dss_nre": 703,
    "G3 Spring_theta_nre": 2.329093257,
    "G3 Spring_pr_1_99_nre": 81,
    "G3 Spring_ss_nre": 113,
    "G3 Spring_dss_nre": 733
  },
  {
    "ID": 408,
    "KG Winter_theta_nre": -0.163280867,
    "KG Winter_pr_1_99_nre": 82,
    "KG Winter_ss_nre": 114,
    "KG Winter_dss_nre": 484,
    "KG Spring_theta_nre": 0.193285282,
    "KG Spring_pr_1_99_nre": 82,
    "KG Spring_ss_nre": 114,
    "KG Spring_dss_nre": 519,
    "G1 Fall_theta_nre": 0.420653346,
    "G1 Fall_pr_1_99_nre": 82,
    "G1 Fall_ss_nre": 113,
    "G1 Fall_dss_nre": 542,
    "G1 Winter_theta_nre": 0.633832116,
    "G1 Winter_pr_1_99_nre": 82,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 563,
    "G1 Spring_theta_nre": 0.909171431,
    "G1 Spring_pr_1_99_nre": 82,
    "G1 Spring_ss_nre": 114,
    "G1 Spring_dss_nre": 591,
    "G2 Fall_theta_nre": 1.185360836,
    "G2 Fall_pr_1_99_nre": 82,
    "G2 Fall_ss_nre": 114,
    "G2 Fall_dss_nre": 619,
    "G2 Winter_theta_nre": 1.352822397,
    "G2 Winter_pr_1_99_nre": 82,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 635,
    "G2 Spring_theta_nre": 1.728764251,
    "G2 Spring_pr_1_99_nre": 82,
    "G2 Spring_ss_nre": 114,
    "G2 Spring_dss_nre": 673,
    "G3 Fall_theta_nre": 1.962301798,
    "G3 Fall_pr_1_99_nre": 82,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 696,
    "G3 Winter_theta_nre": 2.040094762,
    "G3 Winter_pr_1_99_nre": 82,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 704,
    "G3 Spring_theta_nre": 2.338981879,
    "G3 Spring_pr_1_99_nre": 82,
    "G3 Spring_ss_nre": 114,
    "G3 Spring_dss_nre": 734
  },
  {
    "ID": 409,
    "KG Winter_theta_nre": -0.139678037,
    "KG Winter_pr_1_99_nre": 82,
    "KG Winter_ss_nre": 114,
    "KG Winter_dss_nre": 486,
    "KG Spring_theta_nre": 0.203343756,
    "KG Spring_pr_1_99_nre": 82,
    "KG Spring_ss_nre": 114,
    "KG Spring_dss_nre": 520,
    "G1 Fall_theta_nre": 0.432456326,
    "G1 Fall_pr_1_99_nre": 82,
    "G1 Fall_ss_nre": 114,
    "G1 Fall_dss_nre": 543,
    "G1 Winter_theta_nre": 0.636053307,
    "G1 Winter_pr_1_99_nre": 82,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 564,
    "G1 Spring_theta_nre": 0.919733276,
    "G1 Spring_pr_1_99_nre": 82,
    "G1 Spring_ss_nre": 114,
    "G1 Spring_dss_nre": 592,
    "G2 Fall_theta_nre": 1.186554869,
    "G2 Fall_pr_1_99_nre": 82,
    "G2 Fall_ss_nre": 114,
    "G2 Fall_dss_nre": 619,
    "G2 Winter_theta_nre": 1.357168239,
    "G2 Winter_pr_1_99_nre": 82,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 636,
    "G2 Spring_theta_nre": 1.744912912,
    "G2 Spring_pr_1_99_nre": 82,
    "G2 Spring_ss_nre": 114,
    "G2 Spring_dss_nre": 674,
    "G3 Fall_theta_nre": 1.968837296,
    "G3 Fall_pr_1_99_nre": 82,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 697,
    "G3 Winter_theta_nre": 2.045183785,
    "G3 Winter_pr_1_99_nre": 82,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 705,
    "G3 Spring_theta_nre": 2.343149741,
    "G3 Spring_pr_1_99_nre": 82,
    "G3 Spring_ss_nre": 114,
    "G3 Spring_dss_nre": 734
  },
  {
    "ID": 410,
    "KG Winter_theta_nre": -0.136454189,
    "KG Winter_pr_1_99_nre": 82,
    "KG Winter_ss_nre": 114,
    "KG Winter_dss_nre": 486,
    "KG Spring_theta_nre": 0.205037739,
    "KG Spring_pr_1_99_nre": 82,
    "KG Spring_ss_nre": 114,
    "KG Spring_dss_nre": 521,
    "G1 Fall_theta_nre": 0.436453989,
    "G1 Fall_pr_1_99_nre": 82,
    "G1 Fall_ss_nre": 114,
    "G1 Fall_dss_nre": 544,
    "G1 Winter_theta_nre": 0.637102149,
    "G1 Winter_pr_1_99_nre": 82,
    "G1 Winter_ss_nre": 113,
    "G1 Winter_dss_nre": 564,
    "G1 Spring_theta_nre": 0.922011725,
    "G1 Spring_pr_1_99_nre": 82,
    "G1 Spring_ss_nre": 114,
    "G1 Spring_dss_nre": 592,
    "G2 Fall_theta_nre": 1.196338427,
    "G2 Fall_pr_1_99_nre": 82,
    "G2 Fall_ss_nre": 114,
    "G2 Fall_dss_nre": 620,
    "G2 Winter_theta_nre": 1.362264707,
    "G2 Winter_pr_1_99_nre": 82,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 636,
    "G2 Spring_theta_nre": 1.764771019,
    "G2 Spring_pr_1_99_nre": 82,
    "G2 Spring_ss_nre": 114,
    "G2 Spring_dss_nre": 676,
    "G3 Fall_theta_nre": 1.968909504,
    "G3 Fall_pr_1_99_nre": 82,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 697,
    "G3 Winter_theta_nre": 2.046824366,
    "G3 Winter_pr_1_99_nre": 82,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 705,
    "G3 Spring_theta_nre": 2.346478808,
    "G3 Spring_pr_1_99_nre": 82,
    "G3 Spring_ss_nre": 114,
    "G3 Spring_dss_nre": 735
  },
  {
    "ID": 411,
    "KG Winter_theta_nre": -0.134690101,
    "KG Winter_pr_1_99_nre": 82,
    "KG Winter_ss_nre": 114,
    "KG Winter_dss_nre": 487,
    "KG Spring_theta_nre": 0.205425696,
    "KG Spring_pr_1_99_nre": 82,
    "KG Spring_ss_nre": 114,
    "KG Spring_dss_nre": 521,
    "G1 Fall_theta_nre": 0.443716422,
    "G1 Fall_pr_1_99_nre": 82,
    "G1 Fall_ss_nre": 114,
    "G1 Fall_dss_nre": 544,
    "G1 Winter_theta_nre": 0.639092893,
    "G1 Winter_pr_1_99_nre": 82,
    "G1 Winter_ss_nre": 114,
    "G1 Winter_dss_nre": 564,
    "G1 Spring_theta_nre": 0.927217561,
    "G1 Spring_pr_1_99_nre": 82,
    "G1 Spring_ss_nre": 114,
    "G1 Spring_dss_nre": 593,
    "G2 Fall_theta_nre": 1.220331512,
    "G2 Fall_pr_1_99_nre": 82,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 622,
    "G2 Winter_theta_nre": 1.365809633,
    "G2 Winter_pr_1_99_nre": 82,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 637,
    "G2 Spring_theta_nre": 1.766305056,
    "G2 Spring_pr_1_99_nre": 82,
    "G2 Spring_ss_nre": 114,
    "G2 Spring_dss_nre": 677,
    "G3 Fall_theta_nre": 1.972069061,
    "G3 Fall_pr_1_99_nre": 82,
    "G3 Fall_ss_nre": 112,
    "G3 Fall_dss_nre": 697,
    "G3 Winter_theta_nre": 2.054735341,
    "G3 Winter_pr_1_99_nre": 82,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 705,
    "G3 Spring_theta_nre": 2.349024732,
    "G3 Spring_pr_1_99_nre": 82,
    "G3 Spring_ss_nre": 114,
    "G3 Spring_dss_nre": 735
  },
  {
    "ID": 412,
    "KG Winter_theta_nre": -0.130204081,
    "KG Winter_pr_1_99_nre": 82,
    "KG Winter_ss_nre": 114,
    "KG Winter_dss_nre": 487,
    "KG Spring_theta_nre": 0.213019854,
    "KG Spring_pr_1_99_nre": 82,
    "KG Spring_ss_nre": 114,
    "KG Spring_dss_nre": 521,
    "G1 Fall_theta_nre": 0.449874872,
    "G1 Fall_pr_1_99_nre": 82,
    "G1 Fall_ss_nre": 114,
    "G1 Fall_dss_nre": 545,
    "G1 Winter_theta_nre": 0.643195496,
    "G1 Winter_pr_1_99_nre": 82,
    "G1 Winter_ss_nre": 114,
    "G1 Winter_dss_nre": 564,
    "G1 Spring_theta_nre": 0.933218319,
    "G1 Spring_pr_1_99_nre": 82,
    "G1 Spring_ss_nre": 114,
    "G1 Spring_dss_nre": 593,
    "G2 Fall_theta_nre": 1.229918993,
    "G2 Fall_pr_1_99_nre": 82,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 623,
    "G2 Winter_theta_nre": 1.375092121,
    "G2 Winter_pr_1_99_nre": 82,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 638,
    "G2 Spring_theta_nre": 1.766440648,
    "G2 Spring_pr_1_99_nre": 82,
    "G2 Spring_ss_nre": 114,
    "G2 Spring_dss_nre": 677,
    "G3 Fall_theta_nre": 1.981578497,
    "G3 Fall_pr_1_99_nre": 82,
    "G3 Fall_ss_nre": 113,
    "G3 Fall_dss_nre": 698,
    "G3 Winter_theta_nre": 2.058621885,
    "G3 Winter_pr_1_99_nre": 82,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 706,
    "G3 Spring_theta_nre": 2.351164766,
    "G3 Spring_pr_1_99_nre": 82,
    "G3 Spring_ss_nre": 114,
    "G3 Spring_dss_nre": 735
  },
  {
    "ID": 413,
    "KG Winter_theta_nre": -0.121169064,
    "KG Winter_pr_1_99_nre": 83,
    "KG Winter_ss_nre": 114,
    "KG Winter_dss_nre": 488,
    "KG Spring_theta_nre": 0.227451302,
    "KG Spring_pr_1_99_nre": 83,
    "KG Spring_ss_nre": 114,
    "KG Spring_dss_nre": 523,
    "G1 Fall_theta_nre": 0.455624311,
    "G1 Fall_pr_1_99_nre": 83,
    "G1 Fall_ss_nre": 114,
    "G1 Fall_dss_nre": 546,
    "G1 Winter_theta_nre": 0.643685381,
    "G1 Winter_pr_1_99_nre": 83,
    "G1 Winter_ss_nre": 114,
    "G1 Winter_dss_nre": 564,
    "G1 Spring_theta_nre": 0.938319578,
    "G1 Spring_pr_1_99_nre": 83,
    "G1 Spring_ss_nre": 114,
    "G1 Spring_dss_nre": 594,
    "G2 Fall_theta_nre": 1.232469485,
    "G2 Fall_pr_1_99_nre": 83,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 623,
    "G2 Winter_theta_nre": 1.385617814,
    "G2 Winter_pr_1_99_nre": 83,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 639,
    "G2 Spring_theta_nre": 1.778571461,
    "G2 Spring_pr_1_99_nre": 83,
    "G2 Spring_ss_nre": 115,
    "G2 Spring_dss_nre": 678,
    "G3 Fall_theta_nre": 2.004964706,
    "G3 Fall_pr_1_99_nre": 83,
    "G3 Fall_ss_nre": 113,
    "G3 Fall_dss_nre": 700,
    "G3 Winter_theta_nre": 2.060477192,
    "G3 Winter_pr_1_99_nre": 83,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 706,
    "G3 Spring_theta_nre": 2.35952431,
    "G3 Spring_pr_1_99_nre": 83,
    "G3 Spring_ss_nre": 114,
    "G3 Spring_dss_nre": 736
  },
  {
    "ID": 414,
    "KG Winter_theta_nre": -0.11752172,
    "KG Winter_pr_1_99_nre": 83,
    "KG Winter_ss_nre": 114,
    "KG Winter_dss_nre": 488,
    "KG Spring_theta_nre": 0.234500848,
    "KG Spring_pr_1_99_nre": 83,
    "KG Spring_ss_nre": 115,
    "KG Spring_dss_nre": 523,
    "G1 Fall_theta_nre": 0.468592435,
    "G1 Fall_pr_1_99_nre": 83,
    "G1 Fall_ss_nre": 114,
    "G1 Fall_dss_nre": 547,
    "G1 Winter_theta_nre": 0.644180468,
    "G1 Winter_pr_1_99_nre": 83,
    "G1 Winter_ss_nre": 114,
    "G1 Winter_dss_nre": 564,
    "G1 Spring_theta_nre": 0.941765521,
    "G1 Spring_pr_1_99_nre": 83,
    "G1 Spring_ss_nre": 114,
    "G1 Spring_dss_nre": 594,
    "G2 Fall_theta_nre": 1.232695699,
    "G2 Fall_pr_1_99_nre": 83,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 623,
    "G2 Winter_theta_nre": 1.386156758,
    "G2 Winter_pr_1_99_nre": 83,
    "G2 Winter_ss_nre": 114,
    "G2 Winter_dss_nre": 639,
    "G2 Spring_theta_nre": 1.789689161,
    "G2 Spring_pr_1_99_nre": 83,
    "G2 Spring_ss_nre": 115,
    "G2 Spring_dss_nre": 679,
    "G3 Fall_theta_nre": 2.01821802,
    "G3 Fall_pr_1_99_nre": 83,
    "G3 Fall_ss_nre": 113,
    "G3 Fall_dss_nre": 702,
    "G3 Winter_theta_nre": 2.062895994,
    "G3 Winter_pr_1_99_nre": 83,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 706,
    "G3 Spring_theta_nre": 2.367473793,
    "G3 Spring_pr_1_99_nre": 83,
    "G3 Spring_ss_nre": 114,
    "G3 Spring_dss_nre": 737
  },
  {
    "ID": 415,
    "KG Winter_theta_nre": -0.11116578,
    "KG Winter_pr_1_99_nre": 83,
    "KG Winter_ss_nre": 115,
    "KG Winter_dss_nre": 489,
    "KG Spring_theta_nre": 0.248559009,
    "KG Spring_pr_1_99_nre": 83,
    "KG Spring_ss_nre": 115,
    "KG Spring_dss_nre": 525,
    "G1 Fall_theta_nre": 0.473129337,
    "G1 Fall_pr_1_99_nre": 83,
    "G1 Fall_ss_nre": 114,
    "G1 Fall_dss_nre": 547,
    "G1 Winter_theta_nre": 0.648070666,
    "G1 Winter_pr_1_99_nre": 83,
    "G1 Winter_ss_nre": 114,
    "G1 Winter_dss_nre": 565,
    "G1 Spring_theta_nre": 0.943179241,
    "G1 Spring_pr_1_99_nre": 83,
    "G1 Spring_ss_nre": 114,
    "G1 Spring_dss_nre": 594,
    "G2 Fall_theta_nre": 1.233912148,
    "G2 Fall_pr_1_99_nre": 83,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 623,
    "G2 Winter_theta_nre": 1.389909195,
    "G2 Winter_pr_1_99_nre": 83,
    "G2 Winter_ss_nre": 115,
    "G2 Winter_dss_nre": 639,
    "G2 Spring_theta_nre": 1.805105651,
    "G2 Spring_pr_1_99_nre": 83,
    "G2 Spring_ss_nre": 115,
    "G2 Spring_dss_nre": 681,
    "G3 Fall_theta_nre": 2.019890046,
    "G3 Fall_pr_1_99_nre": 83,
    "G3 Fall_ss_nre": 113,
    "G3 Fall_dss_nre": 702,
    "G3 Winter_theta_nre": 2.062898698,
    "G3 Winter_pr_1_99_nre": 83,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 706,
    "G3 Spring_theta_nre": 2.371238938,
    "G3 Spring_pr_1_99_nre": 83,
    "G3 Spring_ss_nre": 114,
    "G3 Spring_dss_nre": 737
  },
  {
    "ID": 416,
    "KG Winter_theta_nre": -0.110113138,
    "KG Winter_pr_1_99_nre": 83,
    "KG Winter_ss_nre": 115,
    "KG Winter_dss_nre": 489,
    "KG Spring_theta_nre": 0.275157728,
    "KG Spring_pr_1_99_nre": 83,
    "KG Spring_ss_nre": 115,
    "KG Spring_dss_nre": 528,
    "G1 Fall_theta_nre": 0.486649247,
    "G1 Fall_pr_1_99_nre": 83,
    "G1 Fall_ss_nre": 115,
    "G1 Fall_dss_nre": 549,
    "G1 Winter_theta_nre": 0.656188759,
    "G1 Winter_pr_1_99_nre": 83,
    "G1 Winter_ss_nre": 114,
    "G1 Winter_dss_nre": 566,
    "G1 Spring_theta_nre": 0.95599637,
    "G1 Spring_pr_1_99_nre": 83,
    "G1 Spring_ss_nre": 115,
    "G1 Spring_dss_nre": 596,
    "G2 Fall_theta_nre": 1.236862743,
    "G2 Fall_pr_1_99_nre": 83,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 624,
    "G2 Winter_theta_nre": 1.390782519,
    "G2 Winter_pr_1_99_nre": 83,
    "G2 Winter_ss_nre": 115,
    "G2 Winter_dss_nre": 639,
    "G2 Spring_theta_nre": 1.810102674,
    "G2 Spring_pr_1_99_nre": 83,
    "G2 Spring_ss_nre": 115,
    "G2 Spring_dss_nre": 681,
    "G3 Fall_theta_nre": 2.029765544,
    "G3 Fall_pr_1_99_nre": 83,
    "G3 Fall_ss_nre": 113,
    "G3 Fall_dss_nre": 703,
    "G3 Winter_theta_nre": 2.065355667,
    "G3 Winter_pr_1_99_nre": 83,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 707,
    "G3 Spring_theta_nre": 2.387773614,
    "G3 Spring_pr_1_99_nre": 83,
    "G3 Spring_ss_nre": 115,
    "G3 Spring_dss_nre": 739
  },
  {
    "ID": 417,
    "KG Winter_theta_nre": -0.094495038,
    "KG Winter_pr_1_99_nre": 83,
    "KG Winter_ss_nre": 115,
    "KG Winter_dss_nre": 491,
    "KG Spring_theta_nre": 0.28743428,
    "KG Spring_pr_1_99_nre": 83,
    "KG Spring_ss_nre": 115,
    "KG Spring_dss_nre": 529,
    "G1 Fall_theta_nre": 0.488002637,
    "G1 Fall_pr_1_99_nre": 83,
    "G1 Fall_ss_nre": 115,
    "G1 Fall_dss_nre": 549,
    "G1 Winter_theta_nre": 0.667031409,
    "G1 Winter_pr_1_99_nre": 83,
    "G1 Winter_ss_nre": 114,
    "G1 Winter_dss_nre": 567,
    "G1 Spring_theta_nre": 0.962129749,
    "G1 Spring_pr_1_99_nre": 83,
    "G1 Spring_ss_nre": 115,
    "G1 Spring_dss_nre": 596,
    "G2 Fall_theta_nre": 1.240970899,
    "G2 Fall_pr_1_99_nre": 83,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 624,
    "G2 Winter_theta_nre": 1.391440319,
    "G2 Winter_pr_1_99_nre": 83,
    "G2 Winter_ss_nre": 115,
    "G2 Winter_dss_nre": 639,
    "G2 Spring_theta_nre": 1.814854453,
    "G2 Spring_pr_1_99_nre": 83,
    "G2 Spring_ss_nre": 115,
    "G2 Spring_dss_nre": 681,
    "G3 Fall_theta_nre": 2.031625902,
    "G3 Fall_pr_1_99_nre": 83,
    "G3 Fall_ss_nre": 113,
    "G3 Fall_dss_nre": 703,
    "G3 Winter_theta_nre": 2.066644853,
    "G3 Winter_pr_1_99_nre": 83,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 707,
    "G3 Spring_theta_nre": 2.394240121,
    "G3 Spring_pr_1_99_nre": 83,
    "G3 Spring_ss_nre": 115,
    "G3 Spring_dss_nre": 739
  },
  {
    "ID": 418,
    "KG Winter_theta_nre": -0.072267883,
    "KG Winter_pr_1_99_nre": 84,
    "KG Winter_ss_nre": 115,
    "KG Winter_dss_nre": 493,
    "KG Spring_theta_nre": 0.310890031,
    "KG Spring_pr_1_99_nre": 84,
    "KG Spring_ss_nre": 116,
    "KG Spring_dss_nre": 531,
    "G1 Fall_theta_nre": 0.509975062,
    "G1 Fall_pr_1_99_nre": 84,
    "G1 Fall_ss_nre": 115,
    "G1 Fall_dss_nre": 551,
    "G1 Winter_theta_nre": 0.670326542,
    "G1 Winter_pr_1_99_nre": 84,
    "G1 Winter_ss_nre": 114,
    "G1 Winter_dss_nre": 567,
    "G1 Spring_theta_nre": 0.979628773,
    "G1 Spring_pr_1_99_nre": 84,
    "G1 Spring_ss_nre": 115,
    "G1 Spring_dss_nre": 598,
    "G2 Fall_theta_nre": 1.243465256,
    "G2 Fall_pr_1_99_nre": 84,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 624,
    "G2 Winter_theta_nre": 1.406604818,
    "G2 Winter_pr_1_99_nre": 84,
    "G2 Winter_ss_nre": 115,
    "G2 Winter_dss_nre": 641,
    "G2 Spring_theta_nre": 1.816483298,
    "G2 Spring_pr_1_99_nre": 84,
    "G2 Spring_ss_nre": 115,
    "G2 Spring_dss_nre": 682,
    "G3 Fall_theta_nre": 2.040540228,
    "G3 Fall_pr_1_99_nre": 84,
    "G3 Fall_ss_nre": 114,
    "G3 Fall_dss_nre": 704,
    "G3 Winter_theta_nre": 2.0669469,
    "G3 Winter_pr_1_99_nre": 84,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 707,
    "G3 Spring_theta_nre": 2.401829772,
    "G3 Spring_pr_1_99_nre": 84,
    "G3 Spring_ss_nre": 115,
    "G3 Spring_dss_nre": 740
  },
  {
    "ID": 419,
    "KG Winter_theta_nre": -0.068695779,
    "KG Winter_pr_1_99_nre": 84,
    "KG Winter_ss_nre": 115,
    "KG Winter_dss_nre": 493,
    "KG Spring_theta_nre": 0.316944492,
    "KG Spring_pr_1_99_nre": 84,
    "KG Spring_ss_nre": 116,
    "KG Spring_dss_nre": 532,
    "G1 Fall_theta_nre": 0.510201965,
    "G1 Fall_pr_1_99_nre": 84,
    "G1 Fall_ss_nre": 115,
    "G1 Fall_dss_nre": 551,
    "G1 Winter_theta_nre": 0.684786653,
    "G1 Winter_pr_1_99_nre": 84,
    "G1 Winter_ss_nre": 114,
    "G1 Winter_dss_nre": 568,
    "G1 Spring_theta_nre": 0.981810931,
    "G1 Spring_pr_1_99_nre": 84,
    "G1 Spring_ss_nre": 115,
    "G1 Spring_dss_nre": 598,
    "G2 Fall_theta_nre": 1.266224965,
    "G2 Fall_pr_1_99_nre": 84,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 627,
    "G2 Winter_theta_nre": 1.407736762,
    "G2 Winter_pr_1_99_nre": 84,
    "G2 Winter_ss_nre": 115,
    "G2 Winter_dss_nre": 641,
    "G2 Spring_theta_nre": 1.817843119,
    "G2 Spring_pr_1_99_nre": 84,
    "G2 Spring_ss_nre": 115,
    "G2 Spring_dss_nre": 682,
    "G3 Fall_theta_nre": 2.041109472,
    "G3 Fall_pr_1_99_nre": 84,
    "G3 Fall_ss_nre": 114,
    "G3 Fall_dss_nre": 704,
    "G3 Winter_theta_nre": 2.078754587,
    "G3 Winter_pr_1_99_nre": 84,
    "G3 Winter_ss_nre": 113,
    "G3 Winter_dss_nre": 708,
    "G3 Spring_theta_nre": 2.403691579,
    "G3 Spring_pr_1_99_nre": 84,
    "G3 Spring_ss_nre": 115,
    "G3 Spring_dss_nre": 740
  },
  {
    "ID": 420,
    "KG Winter_theta_nre": -0.068454497,
    "KG Winter_pr_1_99_nre": 84,
    "KG Winter_ss_nre": 115,
    "KG Winter_dss_nre": 493,
    "KG Spring_theta_nre": 0.318375764,
    "KG Spring_pr_1_99_nre": 84,
    "KG Spring_ss_nre": 116,
    "KG Spring_dss_nre": 532,
    "G1 Fall_theta_nre": 0.525279609,
    "G1 Fall_pr_1_99_nre": 84,
    "G1 Fall_ss_nre": 115,
    "G1 Fall_dss_nre": 553,
    "G1 Winter_theta_nre": 0.688024747,
    "G1 Winter_pr_1_99_nre": 84,
    "G1 Winter_ss_nre": 114,
    "G1 Winter_dss_nre": 569,
    "G1 Spring_theta_nre": 0.996574064,
    "G1 Spring_pr_1_99_nre": 84,
    "G1 Spring_ss_nre": 115,
    "G1 Spring_dss_nre": 600,
    "G2 Fall_theta_nre": 1.266869846,
    "G2 Fall_pr_1_99_nre": 84,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 627,
    "G2 Winter_theta_nre": 1.409675056,
    "G2 Winter_pr_1_99_nre": 84,
    "G2 Winter_ss_nre": 115,
    "G2 Winter_dss_nre": 641,
    "G2 Spring_theta_nre": 1.821105109,
    "G2 Spring_pr_1_99_nre": 84,
    "G2 Spring_ss_nre": 115,
    "G2 Spring_dss_nre": 682,
    "G3 Fall_theta_nre": 2.047571963,
    "G3 Fall_pr_1_99_nre": 84,
    "G3 Fall_ss_nre": 114,
    "G3 Fall_dss_nre": 705,
    "G3 Winter_theta_nre": 2.080487723,
    "G3 Winter_pr_1_99_nre": 84,
    "G3 Winter_ss_nre": 114,
    "G3 Winter_dss_nre": 708,
    "G3 Spring_theta_nre": 2.408796134,
    "G3 Spring_pr_1_99_nre": 84,
    "G3 Spring_ss_nre": 115,
    "G3 Spring_dss_nre": 741
  },
  {
    "ID": 421,
    "KG Winter_theta_nre": -0.064442836,
    "KG Winter_pr_1_99_nre": 84,
    "KG Winter_ss_nre": 115,
    "KG Winter_dss_nre": 494,
    "KG Spring_theta_nre": 0.320599425,
    "KG Spring_pr_1_99_nre": 84,
    "KG Spring_ss_nre": 116,
    "KG Spring_dss_nre": 532,
    "G1 Fall_theta_nre": 0.541370609,
    "G1 Fall_pr_1_99_nre": 84,
    "G1 Fall_ss_nre": 115,
    "G1 Fall_dss_nre": 554,
    "G1 Winter_theta_nre": 0.705314059,
    "G1 Winter_pr_1_99_nre": 84,
    "G1 Winter_ss_nre": 115,
    "G1 Winter_dss_nre": 571,
    "G1 Spring_theta_nre": 1.025784861,
    "G1 Spring_pr_1_99_nre": 84,
    "G1 Spring_ss_nre": 116,
    "G1 Spring_dss_nre": 603,
    "G2 Fall_theta_nre": 1.270180335,
    "G2 Fall_pr_1_99_nre": 84,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 627,
    "G2 Winter_theta_nre": 1.425192014,
    "G2 Winter_pr_1_99_nre": 84,
    "G2 Winter_ss_nre": 115,
    "G2 Winter_dss_nre": 643,
    "G2 Spring_theta_nre": 1.826412963,
    "G2 Spring_pr_1_99_nre": 84,
    "G2 Spring_ss_nre": 115,
    "G2 Spring_dss_nre": 683,
    "G3 Fall_theta_nre": 2.059560748,
    "G3 Fall_pr_1_99_nre": 84,
    "G3 Fall_ss_nre": 114,
    "G3 Fall_dss_nre": 706,
    "G3 Winter_theta_nre": 2.084504802,
    "G3 Winter_pr_1_99_nre": 84,
    "G3 Winter_ss_nre": 114,
    "G3 Winter_dss_nre": 708,
    "G3 Spring_theta_nre": 2.414007585,
    "G3 Spring_pr_1_99_nre": 84,
    "G3 Spring_ss_nre": 115,
    "G3 Spring_dss_nre": 741
  },
  {
    "ID": 422,
    "KG Winter_theta_nre": -0.063117305,
    "KG Winter_pr_1_99_nre": 84,
    "KG Winter_ss_nre": 115,
    "KG Winter_dss_nre": 494,
    "KG Spring_theta_nre": 0.338739317,
    "KG Spring_pr_1_99_nre": 84,
    "KG Spring_ss_nre": 116,
    "KG Spring_dss_nre": 534,
    "G1 Fall_theta_nre": 0.559786968,
    "G1 Fall_pr_1_99_nre": 84,
    "G1 Fall_ss_nre": 116,
    "G1 Fall_dss_nre": 556,
    "G1 Winter_theta_nre": 0.714645976,
    "G1 Winter_pr_1_99_nre": 84,
    "G1 Winter_ss_nre": 115,
    "G1 Winter_dss_nre": 571,
    "G1 Spring_theta_nre": 1.042913586,
    "G1 Spring_pr_1_99_nre": 84,
    "G1 Spring_ss_nre": 116,
    "G1 Spring_dss_nre": 604,
    "G2 Fall_theta_nre": 1.274218656,
    "G2 Fall_pr_1_99_nre": 84,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 627,
    "G2 Winter_theta_nre": 1.43568675,
    "G2 Winter_pr_1_99_nre": 84,
    "G2 Winter_ss_nre": 115,
    "G2 Winter_dss_nre": 644,
    "G2 Spring_theta_nre": 1.833230738,
    "G2 Spring_pr_1_99_nre": 84,
    "G2 Spring_ss_nre": 116,
    "G2 Spring_dss_nre": 683,
    "G3 Fall_theta_nre": 2.060065817,
    "G3 Fall_pr_1_99_nre": 84,
    "G3 Fall_ss_nre": 114,
    "G3 Fall_dss_nre": 706,
    "G3 Winter_theta_nre": 2.101378236,
    "G3 Winter_pr_1_99_nre": 84,
    "G3 Winter_ss_nre": 114,
    "G3 Winter_dss_nre": 710,
    "G3 Spring_theta_nre": 2.417709487,
    "G3 Spring_pr_1_99_nre": 84,
    "G3 Spring_ss_nre": 115,
    "G3 Spring_dss_nre": 742
  },
  {
    "ID": 423,
    "KG Winter_theta_nre": -0.051491191,
    "KG Winter_pr_1_99_nre": 85,
    "KG Winter_ss_nre": 116,
    "KG Winter_dss_nre": 495,
    "KG Spring_theta_nre": 0.342872437,
    "KG Spring_pr_1_99_nre": 85,
    "KG Spring_ss_nre": 116,
    "KG Spring_dss_nre": 534,
    "G1 Fall_theta_nre": 0.581596703,
    "G1 Fall_pr_1_99_nre": 85,
    "G1 Fall_ss_nre": 116,
    "G1 Fall_dss_nre": 558,
    "G1 Winter_theta_nre": 0.716046036,
    "G1 Winter_pr_1_99_nre": 85,
    "G1 Winter_ss_nre": 115,
    "G1 Winter_dss_nre": 572,
    "G1 Spring_theta_nre": 1.059926168,
    "G1 Spring_pr_1_99_nre": 85,
    "G1 Spring_ss_nre": 117,
    "G1 Spring_dss_nre": 606,
    "G2 Fall_theta_nre": 1.275125766,
    "G2 Fall_pr_1_99_nre": 85,
    "G2 Fall_ss_nre": 115,
    "G2 Fall_dss_nre": 628,
    "G2 Winter_theta_nre": 1.44250128,
    "G2 Winter_pr_1_99_nre": 85,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 644,
    "G2 Spring_theta_nre": 1.838462604,
    "G2 Spring_pr_1_99_nre": 85,
    "G2 Spring_ss_nre": 116,
    "G2 Spring_dss_nre": 684,
    "G3 Fall_theta_nre": 2.0676115,
    "G3 Fall_pr_1_99_nre": 85,
    "G3 Fall_ss_nre": 114,
    "G3 Fall_dss_nre": 707,
    "G3 Winter_theta_nre": 2.102540599,
    "G3 Winter_pr_1_99_nre": 85,
    "G3 Winter_ss_nre": 114,
    "G3 Winter_dss_nre": 710,
    "G3 Spring_theta_nre": 2.427898348,
    "G3 Spring_pr_1_99_nre": 85,
    "G3 Spring_ss_nre": 116,
    "G3 Spring_dss_nre": 743
  },
  {
    "ID": 424,
    "KG Winter_theta_nre": -0.050356434,
    "KG Winter_pr_1_99_nre": 85,
    "KG Winter_ss_nre": 116,
    "KG Winter_dss_nre": 495,
    "KG Spring_theta_nre": 0.34471883,
    "KG Spring_pr_1_99_nre": 85,
    "KG Spring_ss_nre": 116,
    "KG Spring_dss_nre": 534,
    "G1 Fall_theta_nre": 0.596495091,
    "G1 Fall_pr_1_99_nre": 85,
    "G1 Fall_ss_nre": 116,
    "G1 Fall_dss_nre": 560,
    "G1 Winter_theta_nre": 0.733303091,
    "G1 Winter_pr_1_99_nre": 85,
    "G1 Winter_ss_nre": 115,
    "G1 Winter_dss_nre": 573,
    "G1 Spring_theta_nre": 1.061893291,
    "G1 Spring_pr_1_99_nre": 85,
    "G1 Spring_ss_nre": 117,
    "G1 Spring_dss_nre": 606,
    "G2 Fall_theta_nre": 1.27968572,
    "G2 Fall_pr_1_99_nre": 85,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 628,
    "G2 Winter_theta_nre": 1.444264206,
    "G2 Winter_pr_1_99_nre": 85,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 644,
    "G2 Spring_theta_nre": 1.853711344,
    "G2 Spring_pr_1_99_nre": 85,
    "G2 Spring_ss_nre": 116,
    "G2 Spring_dss_nre": 685,
    "G3 Fall_theta_nre": 2.06810169,
    "G3 Fall_pr_1_99_nre": 85,
    "G3 Fall_ss_nre": 114,
    "G3 Fall_dss_nre": 707,
    "G3 Winter_theta_nre": 2.102780073,
    "G3 Winter_pr_1_99_nre": 85,
    "G3 Winter_ss_nre": 114,
    "G3 Winter_dss_nre": 710,
    "G3 Spring_theta_nre": 2.436192276,
    "G3 Spring_pr_1_99_nre": 85,
    "G3 Spring_ss_nre": 116,
    "G3 Spring_dss_nre": 744
  },
  {
    "ID": 425,
    "KG Winter_theta_nre": -0.047695251,
    "KG Winter_pr_1_99_nre": 85,
    "KG Winter_ss_nre": 116,
    "KG Winter_dss_nre": 495,
    "KG Spring_theta_nre": 0.35376082,
    "KG Spring_pr_1_99_nre": 85,
    "KG Spring_ss_nre": 117,
    "KG Spring_dss_nre": 535,
    "G1 Fall_theta_nre": 0.599567907,
    "G1 Fall_pr_1_99_nre": 85,
    "G1 Fall_ss_nre": 116,
    "G1 Fall_dss_nre": 560,
    "G1 Winter_theta_nre": 0.735381215,
    "G1 Winter_pr_1_99_nre": 85,
    "G1 Winter_ss_nre": 115,
    "G1 Winter_dss_nre": 574,
    "G1 Spring_theta_nre": 1.062300108,
    "G1 Spring_pr_1_99_nre": 85,
    "G1 Spring_ss_nre": 117,
    "G1 Spring_dss_nre": 606,
    "G2 Fall_theta_nre": 1.284029915,
    "G2 Fall_pr_1_99_nre": 85,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 628,
    "G2 Winter_theta_nre": 1.44861674,
    "G2 Winter_pr_1_99_nre": 85,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 645,
    "G2 Spring_theta_nre": 1.856385536,
    "G2 Spring_pr_1_99_nre": 85,
    "G2 Spring_ss_nre": 116,
    "G2 Spring_dss_nre": 686,
    "G3 Fall_theta_nre": 2.086680674,
    "G3 Fall_pr_1_99_nre": 85,
    "G3 Fall_ss_nre": 114,
    "G3 Fall_dss_nre": 709,
    "G3 Winter_theta_nre": 2.111182982,
    "G3 Winter_pr_1_99_nre": 85,
    "G3 Winter_ss_nre": 114,
    "G3 Winter_dss_nre": 711,
    "G3 Spring_theta_nre": 2.443624385,
    "G3 Spring_pr_1_99_nre": 85,
    "G3 Spring_ss_nre": 116,
    "G3 Spring_dss_nre": 744
  },
  {
    "ID": 426,
    "KG Winter_theta_nre": -0.046482127,
    "KG Winter_pr_1_99_nre": 85,
    "KG Winter_ss_nre": 116,
    "KG Winter_dss_nre": 495,
    "KG Spring_theta_nre": 0.367392435,
    "KG Spring_pr_1_99_nre": 85,
    "KG Spring_ss_nre": 117,
    "KG Spring_dss_nre": 537,
    "G1 Fall_theta_nre": 0.608267186,
    "G1 Fall_pr_1_99_nre": 85,
    "G1 Fall_ss_nre": 117,
    "G1 Fall_dss_nre": 561,
    "G1 Winter_theta_nre": 0.75093433,
    "G1 Winter_pr_1_99_nre": 85,
    "G1 Winter_ss_nre": 116,
    "G1 Winter_dss_nre": 575,
    "G1 Spring_theta_nre": 1.077786402,
    "G1 Spring_pr_1_99_nre": 85,
    "G1 Spring_ss_nre": 117,
    "G1 Spring_dss_nre": 608,
    "G2 Fall_theta_nre": 1.285119034,
    "G2 Fall_pr_1_99_nre": 85,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 629,
    "G2 Winter_theta_nre": 1.451287531,
    "G2 Winter_pr_1_99_nre": 85,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 645,
    "G2 Spring_theta_nre": 1.857709809,
    "G2 Spring_pr_1_99_nre": 85,
    "G2 Spring_ss_nre": 116,
    "G2 Spring_dss_nre": 686,
    "G3 Fall_theta_nre": 2.088594091,
    "G3 Fall_pr_1_99_nre": 85,
    "G3 Fall_ss_nre": 114,
    "G3 Fall_dss_nre": 709,
    "G3 Winter_theta_nre": 2.130019693,
    "G3 Winter_pr_1_99_nre": 85,
    "G3 Winter_ss_nre": 115,
    "G3 Winter_dss_nre": 713,
    "G3 Spring_theta_nre": 2.446870936,
    "G3 Spring_pr_1_99_nre": 85,
    "G3 Spring_ss_nre": 116,
    "G3 Spring_dss_nre": 745
  },
  {
    "ID": 427,
    "KG Winter_theta_nre": -0.040229432,
    "KG Winter_pr_1_99_nre": 85,
    "KG Winter_ss_nre": 116,
    "KG Winter_dss_nre": 496,
    "KG Spring_theta_nre": 0.378340088,
    "KG Spring_pr_1_99_nre": 85,
    "KG Spring_ss_nre": 117,
    "KG Spring_dss_nre": 538,
    "G1 Fall_theta_nre": 0.610236402,
    "G1 Fall_pr_1_99_nre": 85,
    "G1 Fall_ss_nre": 117,
    "G1 Fall_dss_nre": 561,
    "G1 Winter_theta_nre": 0.753073684,
    "G1 Winter_pr_1_99_nre": 85,
    "G1 Winter_ss_nre": 116,
    "G1 Winter_dss_nre": 575,
    "G1 Spring_theta_nre": 1.078912055,
    "G1 Spring_pr_1_99_nre": 85,
    "G1 Spring_ss_nre": 117,
    "G1 Spring_dss_nre": 608,
    "G2 Fall_theta_nre": 1.295140748,
    "G2 Fall_pr_1_99_nre": 85,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 630,
    "G2 Winter_theta_nre": 1.452759388,
    "G2 Winter_pr_1_99_nre": 85,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 645,
    "G2 Spring_theta_nre": 1.880472064,
    "G2 Spring_pr_1_99_nre": 85,
    "G2 Spring_ss_nre": 116,
    "G2 Spring_dss_nre": 688,
    "G3 Fall_theta_nre": 2.09135481,
    "G3 Fall_pr_1_99_nre": 85,
    "G3 Fall_ss_nre": 114,
    "G3 Fall_dss_nre": 709,
    "G3 Winter_theta_nre": 2.130936901,
    "G3 Winter_pr_1_99_nre": 85,
    "G3 Winter_ss_nre": 115,
    "G3 Winter_dss_nre": 713,
    "G3 Spring_theta_nre": 2.447910831,
    "G3 Spring_pr_1_99_nre": 85,
    "G3 Spring_ss_nre": 116,
    "G3 Spring_dss_nre": 745
  },
  {
    "ID": 428,
    "KG Winter_theta_nre": -0.034995451,
    "KG Winter_pr_1_99_nre": 86,
    "KG Winter_ss_nre": 116,
    "KG Winter_dss_nre": 497,
    "KG Spring_theta_nre": 0.385788057,
    "KG Spring_pr_1_99_nre": 86,
    "KG Spring_ss_nre": 117,
    "KG Spring_dss_nre": 539,
    "G1 Fall_theta_nre": 0.611784538,
    "G1 Fall_pr_1_99_nre": 86,
    "G1 Fall_ss_nre": 117,
    "G1 Fall_dss_nre": 561,
    "G1 Winter_theta_nre": 0.753617238,
    "G1 Winter_pr_1_99_nre": 86,
    "G1 Winter_ss_nre": 116,
    "G1 Winter_dss_nre": 575,
    "G1 Spring_theta_nre": 1.079027625,
    "G1 Spring_pr_1_99_nre": 86,
    "G1 Spring_ss_nre": 117,
    "G1 Spring_dss_nre": 608,
    "G2 Fall_theta_nre": 1.300127952,
    "G2 Fall_pr_1_99_nre": 86,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 630,
    "G2 Winter_theta_nre": 1.453370158,
    "G2 Winter_pr_1_99_nre": 86,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 645,
    "G2 Spring_theta_nre": 1.883912587,
    "G2 Spring_pr_1_99_nre": 86,
    "G2 Spring_ss_nre": 116,
    "G2 Spring_dss_nre": 688,
    "G3 Fall_theta_nre": 2.127978418,
    "G3 Fall_pr_1_99_nre": 86,
    "G3 Fall_ss_nre": 115,
    "G3 Fall_dss_nre": 713,
    "G3 Winter_theta_nre": 2.143770395,
    "G3 Winter_pr_1_99_nre": 86,
    "G3 Winter_ss_nre": 115,
    "G3 Winter_dss_nre": 714,
    "G3 Spring_theta_nre": 2.454424101,
    "G3 Spring_pr_1_99_nre": 86,
    "G3 Spring_ss_nre": 116,
    "G3 Spring_dss_nre": 745
  },
  {
    "ID": 429,
    "KG Winter_theta_nre": -0.029167379,
    "KG Winter_pr_1_99_nre": 86,
    "KG Winter_ss_nre": 116,
    "KG Winter_dss_nre": 497,
    "KG Spring_theta_nre": 0.386202912,
    "KG Spring_pr_1_99_nre": 86,
    "KG Spring_ss_nre": 117,
    "KG Spring_dss_nre": 539,
    "G1 Fall_theta_nre": 0.629942283,
    "G1 Fall_pr_1_99_nre": 86,
    "G1 Fall_ss_nre": 117,
    "G1 Fall_dss_nre": 563,
    "G1 Winter_theta_nre": 0.757970236,
    "G1 Winter_pr_1_99_nre": 86,
    "G1 Winter_ss_nre": 116,
    "G1 Winter_dss_nre": 576,
    "G1 Spring_theta_nre": 1.082369405,
    "G1 Spring_pr_1_99_nre": 86,
    "G1 Spring_ss_nre": 117,
    "G1 Spring_dss_nre": 608,
    "G2 Fall_theta_nre": 1.302716248,
    "G2 Fall_pr_1_99_nre": 86,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 630,
    "G2 Winter_theta_nre": 1.456779985,
    "G2 Winter_pr_1_99_nre": 86,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 646,
    "G2 Spring_theta_nre": 1.885437673,
    "G2 Spring_pr_1_99_nre": 86,
    "G2 Spring_ss_nre": 116,
    "G2 Spring_dss_nre": 689,
    "G3 Fall_theta_nre": 2.134966522,
    "G3 Fall_pr_1_99_nre": 86,
    "G3 Fall_ss_nre": 115,
    "G3 Fall_dss_nre": 713,
    "G3 Winter_theta_nre": 2.150783028,
    "G3 Winter_pr_1_99_nre": 86,
    "G3 Winter_ss_nre": 115,
    "G3 Winter_dss_nre": 715,
    "G3 Spring_theta_nre": 2.467871952,
    "G3 Spring_pr_1_99_nre": 86,
    "G3 Spring_ss_nre": 116,
    "G3 Spring_dss_nre": 747
  },
  {
    "ID": 430,
    "KG Winter_theta_nre": -0.027412951,
    "KG Winter_pr_1_99_nre": 86,
    "KG Winter_ss_nre": 116,
    "KG Winter_dss_nre": 497,
    "KG Spring_theta_nre": 0.395484595,
    "KG Spring_pr_1_99_nre": 86,
    "KG Spring_ss_nre": 117,
    "KG Spring_dss_nre": 540,
    "G1 Fall_theta_nre": 0.637330753,
    "G1 Fall_pr_1_99_nre": 86,
    "G1 Fall_ss_nre": 117,
    "G1 Fall_dss_nre": 564,
    "G1 Winter_theta_nre": 0.766413369,
    "G1 Winter_pr_1_99_nre": 86,
    "G1 Winter_ss_nre": 116,
    "G1 Winter_dss_nre": 577,
    "G1 Spring_theta_nre": 1.085307401,
    "G1 Spring_pr_1_99_nre": 86,
    "G1 Spring_ss_nre": 117,
    "G1 Spring_dss_nre": 609,
    "G2 Fall_theta_nre": 1.305353551,
    "G2 Fall_pr_1_99_nre": 86,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 631,
    "G2 Winter_theta_nre": 1.459672361,
    "G2 Winter_pr_1_99_nre": 86,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 646,
    "G2 Spring_theta_nre": 1.914793542,
    "G2 Spring_pr_1_99_nre": 86,
    "G2 Spring_ss_nre": 117,
    "G2 Spring_dss_nre": 691,
    "G3 Fall_theta_nre": 2.160099727,
    "G3 Fall_pr_1_99_nre": 86,
    "G3 Fall_ss_nre": 116,
    "G3 Fall_dss_nre": 716,
    "G3 Winter_theta_nre": 2.159147123,
    "G3 Winter_pr_1_99_nre": 86,
    "G3 Winter_ss_nre": 115,
    "G3 Winter_dss_nre": 716,
    "G3 Spring_theta_nre": 2.470191063,
    "G3 Spring_pr_1_99_nre": 86,
    "G3 Spring_ss_nre": 116,
    "G3 Spring_dss_nre": 747
  },
  {
    "ID": 431,
    "KG Winter_theta_nre": -0.003862072,
    "KG Winter_pr_1_99_nre": 86,
    "KG Winter_ss_nre": 116,
    "KG Winter_dss_nre": 500,
    "KG Spring_theta_nre": 0.405957061,
    "KG Spring_pr_1_99_nre": 86,
    "KG Spring_ss_nre": 117,
    "KG Spring_dss_nre": 541,
    "G1 Fall_theta_nre": 0.640562378,
    "G1 Fall_pr_1_99_nre": 86,
    "G1 Fall_ss_nre": 117,
    "G1 Fall_dss_nre": 564,
    "G1 Winter_theta_nre": 0.768794598,
    "G1 Winter_pr_1_99_nre": 86,
    "G1 Winter_ss_nre": 116,
    "G1 Winter_dss_nre": 577,
    "G1 Spring_theta_nre": 1.087134336,
    "G1 Spring_pr_1_99_nre": 86,
    "G1 Spring_ss_nre": 117,
    "G1 Spring_dss_nre": 609,
    "G2 Fall_theta_nre": 1.317730741,
    "G2 Fall_pr_1_99_nre": 86,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 632,
    "G2 Winter_theta_nre": 1.462866911,
    "G2 Winter_pr_1_99_nre": 86,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 646,
    "G2 Spring_theta_nre": 1.916699458,
    "G2 Spring_pr_1_99_nre": 86,
    "G2 Spring_ss_nre": 117,
    "G2 Spring_dss_nre": 692,
    "G3 Fall_theta_nre": 2.169206705,
    "G3 Fall_pr_1_99_nre": 86,
    "G3 Fall_ss_nre": 116,
    "G3 Fall_dss_nre": 717,
    "G3 Winter_theta_nre": 2.161247625,
    "G3 Winter_pr_1_99_nre": 86,
    "G3 Winter_ss_nre": 115,
    "G3 Winter_dss_nre": 716,
    "G3 Spring_theta_nre": 2.486962326,
    "G3 Spring_pr_1_99_nre": 86,
    "G3 Spring_ss_nre": 117,
    "G3 Spring_dss_nre": 749
  },
  {
    "ID": 432,
    "KG Winter_theta_nre": -0.002015349,
    "KG Winter_pr_1_99_nre": 86,
    "KG Winter_ss_nre": 117,
    "KG Winter_dss_nre": 500,
    "KG Spring_theta_nre": 0.40938687,
    "KG Spring_pr_1_99_nre": 86,
    "KG Spring_ss_nre": 118,
    "KG Spring_dss_nre": 541,
    "G1 Fall_theta_nre": 0.647642719,
    "G1 Fall_pr_1_99_nre": 86,
    "G1 Fall_ss_nre": 117,
    "G1 Fall_dss_nre": 565,
    "G1 Winter_theta_nre": 0.7751802,
    "G1 Winter_pr_1_99_nre": 86,
    "G1 Winter_ss_nre": 116,
    "G1 Winter_dss_nre": 578,
    "G1 Spring_theta_nre": 1.105180582,
    "G1 Spring_pr_1_99_nre": 86,
    "G1 Spring_ss_nre": 117,
    "G1 Spring_dss_nre": 611,
    "G2 Fall_theta_nre": 1.321514062,
    "G2 Fall_pr_1_99_nre": 86,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 632,
    "G2 Winter_theta_nre": 1.46357365,
    "G2 Winter_pr_1_99_nre": 86,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 646,
    "G2 Spring_theta_nre": 1.99496238,
    "G2 Spring_pr_1_99_nre": 86,
    "G2 Spring_ss_nre": 118,
    "G2 Spring_dss_nre": 699,
    "G3 Fall_theta_nre": 2.180309286,
    "G3 Fall_pr_1_99_nre": 86,
    "G3 Fall_ss_nre": 116,
    "G3 Fall_dss_nre": 718,
    "G3 Winter_theta_nre": 2.176001418,
    "G3 Winter_pr_1_99_nre": 86,
    "G3 Winter_ss_nre": 115,
    "G3 Winter_dss_nre": 718,
    "G3 Spring_theta_nre": 2.48798245,
    "G3 Spring_pr_1_99_nre": 86,
    "G3 Spring_ss_nre": 117,
    "G3 Spring_dss_nre": 749
  },
  {
    "ID": 433,
    "KG Winter_theta_nre": 0.010095647,
    "KG Winter_pr_1_99_nre": 87,
    "KG Winter_ss_nre": 117,
    "KG Winter_dss_nre": 501,
    "KG Spring_theta_nre": 0.418828047,
    "KG Spring_pr_1_99_nre": 87,
    "KG Spring_ss_nre": 118,
    "KG Spring_dss_nre": 542,
    "G1 Fall_theta_nre": 0.653040867,
    "G1 Fall_pr_1_99_nre": 87,
    "G1 Fall_ss_nre": 117,
    "G1 Fall_dss_nre": 565,
    "G1 Winter_theta_nre": 0.788394496,
    "G1 Winter_pr_1_99_nre": 87,
    "G1 Winter_ss_nre": 116,
    "G1 Winter_dss_nre": 579,
    "G1 Spring_theta_nre": 1.111139017,
    "G1 Spring_pr_1_99_nre": 87,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 611,
    "G2 Fall_theta_nre": 1.325830879,
    "G2 Fall_pr_1_99_nre": 87,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 633,
    "G2 Winter_theta_nre": 1.463856489,
    "G2 Winter_pr_1_99_nre": 87,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 646,
    "G2 Spring_theta_nre": 1.99826756,
    "G2 Spring_pr_1_99_nre": 87,
    "G2 Spring_ss_nre": 118,
    "G2 Spring_dss_nre": 700,
    "G3 Fall_theta_nre": 2.184990156,
    "G3 Fall_pr_1_99_nre": 87,
    "G3 Fall_ss_nre": 116,
    "G3 Fall_dss_nre": 718,
    "G3 Winter_theta_nre": 2.177295457,
    "G3 Winter_pr_1_99_nre": 87,
    "G3 Winter_ss_nre": 115,
    "G3 Winter_dss_nre": 718,
    "G3 Spring_theta_nre": 2.490014168,
    "G3 Spring_pr_1_99_nre": 87,
    "G3 Spring_ss_nre": 117,
    "G3 Spring_dss_nre": 749
  },
  {
    "ID": 434,
    "KG Winter_theta_nre": 0.016535557,
    "KG Winter_pr_1_99_nre": 87,
    "KG Winter_ss_nre": 117,
    "KG Winter_dss_nre": 502,
    "KG Spring_theta_nre": 0.438566141,
    "KG Spring_pr_1_99_nre": 87,
    "KG Spring_ss_nre": 118,
    "KG Spring_dss_nre": 544,
    "G1 Fall_theta_nre": 0.673351606,
    "G1 Fall_pr_1_99_nre": 87,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 567,
    "G1 Winter_theta_nre": 0.80934435,
    "G1 Winter_pr_1_99_nre": 87,
    "G1 Winter_ss_nre": 117,
    "G1 Winter_dss_nre": 581,
    "G1 Spring_theta_nre": 1.112189536,
    "G1 Spring_pr_1_99_nre": 87,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 611,
    "G2 Fall_theta_nre": 1.328172305,
    "G2 Fall_pr_1_99_nre": 87,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 633,
    "G2 Winter_theta_nre": 1.48267939,
    "G2 Winter_pr_1_99_nre": 87,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 648,
    "G2 Spring_theta_nre": 2.003318443,
    "G2 Spring_pr_1_99_nre": 87,
    "G2 Spring_ss_nre": 118,
    "G2 Spring_dss_nre": 700,
    "G3 Fall_theta_nre": 2.195487358,
    "G3 Fall_pr_1_99_nre": 87,
    "G3 Fall_ss_nre": 116,
    "G3 Fall_dss_nre": 720,
    "G3 Winter_theta_nre": 2.178249193,
    "G3 Winter_pr_1_99_nre": 87,
    "G3 Winter_ss_nre": 116,
    "G3 Winter_dss_nre": 718,
    "G3 Spring_theta_nre": 2.494091479,
    "G3 Spring_pr_1_99_nre": 87,
    "G3 Spring_ss_nre": 117,
    "G3 Spring_dss_nre": 749
  },
  {
    "ID": 435,
    "KG Winter_theta_nre": 0.021259784,
    "KG Winter_pr_1_99_nre": 87,
    "KG Winter_ss_nre": 117,
    "KG Winter_dss_nre": 502,
    "KG Spring_theta_nre": 0.451951957,
    "KG Spring_pr_1_99_nre": 87,
    "KG Spring_ss_nre": 118,
    "KG Spring_dss_nre": 545,
    "G1 Fall_theta_nre": 0.684664894,
    "G1 Fall_pr_1_99_nre": 87,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 568,
    "G1 Winter_theta_nre": 0.809640481,
    "G1 Winter_pr_1_99_nre": 87,
    "G1 Winter_ss_nre": 117,
    "G1 Winter_dss_nre": 581,
    "G1 Spring_theta_nre": 1.114576849,
    "G1 Spring_pr_1_99_nre": 87,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 611,
    "G2 Fall_theta_nre": 1.329688049,
    "G2 Fall_pr_1_99_nre": 87,
    "G2 Fall_ss_nre": 116,
    "G2 Fall_dss_nre": 633,
    "G2 Winter_theta_nre": 1.482984763,
    "G2 Winter_pr_1_99_nre": 87,
    "G2 Winter_ss_nre": 116,
    "G2 Winter_dss_nre": 648,
    "G2 Spring_theta_nre": 2.003737171,
    "G2 Spring_pr_1_99_nre": 87,
    "G2 Spring_ss_nre": 118,
    "G2 Spring_dss_nre": 700,
    "G3 Fall_theta_nre": 2.202306933,
    "G3 Fall_pr_1_99_nre": 87,
    "G3 Fall_ss_nre": 116,
    "G3 Fall_dss_nre": 720,
    "G3 Winter_theta_nre": 2.184889702,
    "G3 Winter_pr_1_99_nre": 87,
    "G3 Winter_ss_nre": 116,
    "G3 Winter_dss_nre": 718,
    "G3 Spring_theta_nre": 2.501613198,
    "G3 Spring_pr_1_99_nre": 87,
    "G3 Spring_ss_nre": 117,
    "G3 Spring_dss_nre": 750
  },
  {
    "ID": 436,
    "KG Winter_theta_nre": 0.026138873,
    "KG Winter_pr_1_99_nre": 87,
    "KG Winter_ss_nre": 117,
    "KG Winter_dss_nre": 503,
    "KG Spring_theta_nre": 0.459756585,
    "KG Spring_pr_1_99_nre": 87,
    "KG Spring_ss_nre": 118,
    "KG Spring_dss_nre": 546,
    "G1 Fall_theta_nre": 0.685121425,
    "G1 Fall_pr_1_99_nre": 87,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 569,
    "G1 Winter_theta_nre": 0.809854365,
    "G1 Winter_pr_1_99_nre": 87,
    "G1 Winter_ss_nre": 117,
    "G1 Winter_dss_nre": 581,
    "G1 Spring_theta_nre": 1.120532395,
    "G1 Spring_pr_1_99_nre": 87,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 612,
    "G2 Fall_theta_nre": 1.339652057,
    "G2 Fall_pr_1_99_nre": 87,
    "G2 Fall_ss_nre": 117,
    "G2 Fall_dss_nre": 634,
    "G2 Winter_theta_nre": 1.496059024,
    "G2 Winter_pr_1_99_nre": 87,
    "G2 Winter_ss_nre": 117,
    "G2 Winter_dss_nre": 650,
    "G2 Spring_theta_nre": 2.004162487,
    "G2 Spring_pr_1_99_nre": 87,
    "G2 Spring_ss_nre": 118,
    "G2 Spring_dss_nre": 700,
    "G3 Fall_theta_nre": 2.206040255,
    "G3 Fall_pr_1_99_nre": 87,
    "G3 Fall_ss_nre": 116,
    "G3 Fall_dss_nre": 721,
    "G3 Winter_theta_nre": 2.192262436,
    "G3 Winter_pr_1_99_nre": 87,
    "G3 Winter_ss_nre": 116,
    "G3 Winter_dss_nre": 719,
    "G3 Spring_theta_nre": 2.501716768,
    "G3 Spring_pr_1_99_nre": 87,
    "G3 Spring_ss_nre": 117,
    "G3 Spring_dss_nre": 750
  },
  {
    "ID": 437,
    "KG Winter_theta_nre": 0.026302058,
    "KG Winter_pr_1_99_nre": 87,
    "KG Winter_ss_nre": 117,
    "KG Winter_dss_nre": 503,
    "KG Spring_theta_nre": 0.472201191,
    "KG Spring_pr_1_99_nre": 87,
    "KG Spring_ss_nre": 119,
    "KG Spring_dss_nre": 547,
    "G1 Fall_theta_nre": 0.69101672,
    "G1 Fall_pr_1_99_nre": 87,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 569,
    "G1 Winter_theta_nre": 0.811732886,
    "G1 Winter_pr_1_99_nre": 87,
    "G1 Winter_ss_nre": 117,
    "G1 Winter_dss_nre": 581,
    "G1 Spring_theta_nre": 1.121387613,
    "G1 Spring_pr_1_99_nre": 87,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 612,
    "G2 Fall_theta_nre": 1.354200074,
    "G2 Fall_pr_1_99_nre": 87,
    "G2 Fall_ss_nre": 117,
    "G2 Fall_dss_nre": 635,
    "G2 Winter_theta_nre": 1.508251674,
    "G2 Winter_pr_1_99_nre": 87,
    "G2 Winter_ss_nre": 117,
    "G2 Winter_dss_nre": 651,
    "G2 Spring_theta_nre": 2.005750551,
    "G2 Spring_pr_1_99_nre": 87,
    "G2 Spring_ss_nre": 118,
    "G2 Spring_dss_nre": 701,
    "G3 Fall_theta_nre": 2.22776823,
    "G3 Fall_pr_1_99_nre": 87,
    "G3 Fall_ss_nre": 117,
    "G3 Fall_dss_nre": 723,
    "G3 Winter_theta_nre": 2.193662211,
    "G3 Winter_pr_1_99_nre": 87,
    "G3 Winter_ss_nre": 116,
    "G3 Winter_dss_nre": 719,
    "G3 Spring_theta_nre": 2.521939947,
    "G3 Spring_pr_1_99_nre": 87,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 752
  },
  {
    "ID": 438,
    "KG Winter_theta_nre": 0.02875534,
    "KG Winter_pr_1_99_nre": 88,
    "KG Winter_ss_nre": 117,
    "KG Winter_dss_nre": 503,
    "KG Spring_theta_nre": 0.475806229,
    "KG Spring_pr_1_99_nre": 88,
    "KG Spring_ss_nre": 119,
    "KG Spring_dss_nre": 548,
    "G1 Fall_theta_nre": 0.69538006,
    "G1 Fall_pr_1_99_nre": 88,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 570,
    "G1 Winter_theta_nre": 0.814957393,
    "G1 Winter_pr_1_99_nre": 88,
    "G1 Winter_ss_nre": 117,
    "G1 Winter_dss_nre": 581,
    "G1 Spring_theta_nre": 1.136173186,
    "G1 Spring_pr_1_99_nre": 88,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 614,
    "G2 Fall_theta_nre": 1.365377605,
    "G2 Fall_pr_1_99_nre": 88,
    "G2 Fall_ss_nre": 117,
    "G2 Fall_dss_nre": 637,
    "G2 Winter_theta_nre": 1.509707397,
    "G2 Winter_pr_1_99_nre": 88,
    "G2 Winter_ss_nre": 117,
    "G2 Winter_dss_nre": 651,
    "G2 Spring_theta_nre": 2.016282717,
    "G2 Spring_pr_1_99_nre": 88,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 702,
    "G3 Fall_theta_nre": 2.232813814,
    "G3 Fall_pr_1_99_nre": 88,
    "G3 Fall_ss_nre": 117,
    "G3 Fall_dss_nre": 723,
    "G3 Winter_theta_nre": 2.22753763,
    "G3 Winter_pr_1_99_nre": 88,
    "G3 Winter_ss_nre": 117,
    "G3 Winter_dss_nre": 723,
    "G3 Spring_theta_nre": 2.532799701,
    "G3 Spring_pr_1_99_nre": 88,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 753
  },
  {
    "ID": 439,
    "KG Winter_theta_nre": 0.043277275,
    "KG Winter_pr_1_99_nre": 88,
    "KG Winter_ss_nre": 117,
    "KG Winter_dss_nre": 504,
    "KG Spring_theta_nre": 0.478826512,
    "KG Spring_pr_1_99_nre": 88,
    "KG Spring_ss_nre": 119,
    "KG Spring_dss_nre": 548,
    "G1 Fall_theta_nre": 0.699169035,
    "G1 Fall_pr_1_99_nre": 88,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 570,
    "G1 Winter_theta_nre": 0.816530832,
    "G1 Winter_pr_1_99_nre": 88,
    "G1 Winter_ss_nre": 117,
    "G1 Winter_dss_nre": 582,
    "G1 Spring_theta_nre": 1.145055012,
    "G1 Spring_pr_1_99_nre": 88,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 615,
    "G2 Fall_theta_nre": 1.368505385,
    "G2 Fall_pr_1_99_nre": 88,
    "G2 Fall_ss_nre": 117,
    "G2 Fall_dss_nre": 637,
    "G2 Winter_theta_nre": 1.520724048,
    "G2 Winter_pr_1_99_nre": 88,
    "G2 Winter_ss_nre": 117,
    "G2 Winter_dss_nre": 652,
    "G2 Spring_theta_nre": 2.037382073,
    "G2 Spring_pr_1_99_nre": 88,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 704,
    "G3 Fall_theta_nre": 2.252180025,
    "G3 Fall_pr_1_99_nre": 88,
    "G3 Fall_ss_nre": 117,
    "G3 Fall_dss_nre": 725,
    "G3 Winter_theta_nre": 2.245451513,
    "G3 Winter_pr_1_99_nre": 88,
    "G3 Winter_ss_nre": 117,
    "G3 Winter_dss_nre": 725,
    "G3 Spring_theta_nre": 2.536162078,
    "G3 Spring_pr_1_99_nre": 88,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 754
  },
  {
    "ID": 440,
    "KG Winter_theta_nre": 0.050604343,
    "KG Winter_pr_1_99_nre": 88,
    "KG Winter_ss_nre": 117,
    "KG Winter_dss_nre": 505,
    "KG Spring_theta_nre": 0.486856344,
    "KG Spring_pr_1_99_nre": 88,
    "KG Spring_ss_nre": 119,
    "KG Spring_dss_nre": 549,
    "G1 Fall_theta_nre": 0.701518783,
    "G1 Fall_pr_1_99_nre": 88,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 570,
    "G1 Winter_theta_nre": 0.816976249,
    "G1 Winter_pr_1_99_nre": 88,
    "G1 Winter_ss_nre": 117,
    "G1 Winter_dss_nre": 582,
    "G1 Spring_theta_nre": 1.149160531,
    "G1 Spring_pr_1_99_nre": 88,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 615,
    "G2 Fall_theta_nre": 1.37221083,
    "G2 Fall_pr_1_99_nre": 88,
    "G2 Fall_ss_nre": 117,
    "G2 Fall_dss_nre": 637,
    "G2 Winter_theta_nre": 1.547287929,
    "G2 Winter_pr_1_99_nre": 88,
    "G2 Winter_ss_nre": 118,
    "G2 Winter_dss_nre": 655,
    "G2 Spring_theta_nre": 2.040608599,
    "G2 Spring_pr_1_99_nre": 88,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 704,
    "G3 Fall_theta_nre": 2.283925497,
    "G3 Fall_pr_1_99_nre": 88,
    "G3 Fall_ss_nre": 118,
    "G3 Fall_dss_nre": 728,
    "G3 Winter_theta_nre": 2.245665996,
    "G3 Winter_pr_1_99_nre": 88,
    "G3 Winter_ss_nre": 117,
    "G3 Winter_dss_nre": 725,
    "G3 Spring_theta_nre": 2.539277337,
    "G3 Spring_pr_1_99_nre": 88,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 754
  },
  {
    "ID": 441,
    "KG Winter_theta_nre": 0.051212691,
    "KG Winter_pr_1_99_nre": 88,
    "KG Winter_ss_nre": 117,
    "KG Winter_dss_nre": 505,
    "KG Spring_theta_nre": 0.488693177,
    "KG Spring_pr_1_99_nre": 88,
    "KG Spring_ss_nre": 119,
    "KG Spring_dss_nre": 549,
    "G1 Fall_theta_nre": 0.701824291,
    "G1 Fall_pr_1_99_nre": 88,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 570,
    "G1 Winter_theta_nre": 0.824072795,
    "G1 Winter_pr_1_99_nre": 88,
    "G1 Winter_ss_nre": 117,
    "G1 Winter_dss_nre": 582,
    "G1 Spring_theta_nre": 1.150621579,
    "G1 Spring_pr_1_99_nre": 88,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 615,
    "G2 Fall_theta_nre": 1.380314114,
    "G2 Fall_pr_1_99_nre": 88,
    "G2 Fall_ss_nre": 117,
    "G2 Fall_dss_nre": 638,
    "G2 Winter_theta_nre": 1.553740478,
    "G2 Winter_pr_1_99_nre": 88,
    "G2 Winter_ss_nre": 118,
    "G2 Winter_dss_nre": 655,
    "G2 Spring_theta_nre": 2.044974566,
    "G2 Spring_pr_1_99_nre": 88,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 704,
    "G3 Fall_theta_nre": 2.307805013,
    "G3 Fall_pr_1_99_nre": 88,
    "G3 Fall_ss_nre": 118,
    "G3 Fall_dss_nre": 731,
    "G3 Winter_theta_nre": 2.24872428,
    "G3 Winter_pr_1_99_nre": 88,
    "G3 Winter_ss_nre": 117,
    "G3 Winter_dss_nre": 725,
    "G3 Spring_theta_nre": 2.540639523,
    "G3 Spring_pr_1_99_nre": 88,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 754
  },
  {
    "ID": 442,
    "KG Winter_theta_nre": 0.05682425,
    "KG Winter_pr_1_99_nre": 88,
    "KG Winter_ss_nre": 118,
    "KG Winter_dss_nre": 506,
    "KG Spring_theta_nre": 0.520271889,
    "KG Spring_pr_1_99_nre": 88,
    "KG Spring_ss_nre": 119,
    "KG Spring_dss_nre": 552,
    "G1 Fall_theta_nre": 0.710176496,
    "G1 Fall_pr_1_99_nre": 88,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 571,
    "G1 Winter_theta_nre": 0.839106642,
    "G1 Winter_pr_1_99_nre": 88,
    "G1 Winter_ss_nre": 117,
    "G1 Winter_dss_nre": 584,
    "G1 Spring_theta_nre": 1.153401676,
    "G1 Spring_pr_1_99_nre": 88,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 615,
    "G2 Fall_theta_nre": 1.390027202,
    "G2 Fall_pr_1_99_nre": 88,
    "G2 Fall_ss_nre": 117,
    "G2 Fall_dss_nre": 639,
    "G2 Winter_theta_nre": 1.567545549,
    "G2 Winter_pr_1_99_nre": 88,
    "G2 Winter_ss_nre": 118,
    "G2 Winter_dss_nre": 657,
    "G2 Spring_theta_nre": 2.049849736,
    "G2 Spring_pr_1_99_nre": 88,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 705,
    "G3 Fall_theta_nre": 2.339685636,
    "G3 Fall_pr_1_99_nre": 88,
    "G3 Fall_ss_nre": 118,
    "G3 Fall_dss_nre": 734,
    "G3 Winter_theta_nre": 2.250121184,
    "G3 Winter_pr_1_99_nre": 88,
    "G3 Winter_ss_nre": 117,
    "G3 Winter_dss_nre": 725,
    "G3 Spring_theta_nre": 2.543751944,
    "G3 Spring_pr_1_99_nre": 88,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 754
  },
  {
    "ID": 443,
    "KG Winter_theta_nre": 0.060521556,
    "KG Winter_pr_1_99_nre": 89,
    "KG Winter_ss_nre": 118,
    "KG Winter_dss_nre": 506,
    "KG Spring_theta_nre": 0.525475549,
    "KG Spring_pr_1_99_nre": 89,
    "KG Spring_ss_nre": 120,
    "KG Spring_dss_nre": 553,
    "G1 Fall_theta_nre": 0.716916471,
    "G1 Fall_pr_1_99_nre": 89,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 572,
    "G1 Winter_theta_nre": 0.844159103,
    "G1 Winter_pr_1_99_nre": 89,
    "G1 Winter_ss_nre": 117,
    "G1 Winter_dss_nre": 584,
    "G1 Spring_theta_nre": 1.15356474,
    "G1 Spring_pr_1_99_nre": 89,
    "G1 Spring_ss_nre": 118,
    "G1 Spring_dss_nre": 615,
    "G2 Fall_theta_nre": 1.395096726,
    "G2 Fall_pr_1_99_nre": 89,
    "G2 Fall_ss_nre": 118,
    "G2 Fall_dss_nre": 640,
    "G2 Winter_theta_nre": 1.568636242,
    "G2 Winter_pr_1_99_nre": 89,
    "G2 Winter_ss_nre": 118,
    "G2 Winter_dss_nre": 657,
    "G2 Spring_theta_nre": 2.050336625,
    "G2 Spring_pr_1_99_nre": 89,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 705,
    "G3 Fall_theta_nre": 2.345692951,
    "G3 Fall_pr_1_99_nre": 89,
    "G3 Fall_ss_nre": 119,
    "G3 Fall_dss_nre": 735,
    "G3 Winter_theta_nre": 2.250417513,
    "G3 Winter_pr_1_99_nre": 89,
    "G3 Winter_ss_nre": 117,
    "G3 Winter_dss_nre": 725,
    "G3 Spring_theta_nre": 2.548348763,
    "G3 Spring_pr_1_99_nre": 89,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 755
  },
  {
    "ID": 444,
    "KG Winter_theta_nre": 0.073282073,
    "KG Winter_pr_1_99_nre": 89,
    "KG Winter_ss_nre": 118,
    "KG Winter_dss_nre": 507,
    "KG Spring_theta_nre": 0.535654211,
    "KG Spring_pr_1_99_nre": 89,
    "KG Spring_ss_nre": 120,
    "KG Spring_dss_nre": 554,
    "G1 Fall_theta_nre": 0.719699192,
    "G1 Fall_pr_1_99_nre": 89,
    "G1 Fall_ss_nre": 118,
    "G1 Fall_dss_nre": 572,
    "G1 Winter_theta_nre": 0.878398949,
    "G1 Winter_pr_1_99_nre": 89,
    "G1 Winter_ss_nre": 118,
    "G1 Winter_dss_nre": 588,
    "G1 Spring_theta_nre": 1.167822108,
    "G1 Spring_pr_1_99_nre": 89,
    "G1 Spring_ss_nre": 119,
    "G1 Spring_dss_nre": 617,
    "G2 Fall_theta_nre": 1.400905428,
    "G2 Fall_pr_1_99_nre": 89,
    "G2 Fall_ss_nre": 118,
    "G2 Fall_dss_nre": 640,
    "G2 Winter_theta_nre": 1.575086459,
    "G2 Winter_pr_1_99_nre": 89,
    "G2 Winter_ss_nre": 118,
    "G2 Winter_dss_nre": 658,
    "G2 Spring_theta_nre": 2.050801911,
    "G2 Spring_pr_1_99_nre": 89,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 705,
    "G3 Fall_theta_nre": 2.360727578,
    "G3 Fall_pr_1_99_nre": 89,
    "G3 Fall_ss_nre": 119,
    "G3 Fall_dss_nre": 736,
    "G3 Winter_theta_nre": 2.265306334,
    "G3 Winter_pr_1_99_nre": 89,
    "G3 Winter_ss_nre": 117,
    "G3 Winter_dss_nre": 727,
    "G3 Spring_theta_nre": 2.548836542,
    "G3 Spring_pr_1_99_nre": 89,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 755
  },
  {
    "ID": 445,
    "KG Winter_theta_nre": 0.075404291,
    "KG Winter_pr_1_99_nre": 89,
    "KG Winter_ss_nre": 118,
    "KG Winter_dss_nre": 508,
    "KG Spring_theta_nre": 0.536114284,
    "KG Spring_pr_1_99_nre": 89,
    "KG Spring_ss_nre": 120,
    "KG Spring_dss_nre": 554,
    "G1 Fall_theta_nre": 0.723800103,
    "G1 Fall_pr_1_99_nre": 89,
    "G1 Fall_ss_nre": 119,
    "G1 Fall_dss_nre": 572,
    "G1 Winter_theta_nre": 0.878994137,
    "G1 Winter_pr_1_99_nre": 89,
    "G1 Winter_ss_nre": 118,
    "G1 Winter_dss_nre": 588,
    "G1 Spring_theta_nre": 1.176351899,
    "G1 Spring_pr_1_99_nre": 89,
    "G1 Spring_ss_nre": 119,
    "G1 Spring_dss_nre": 618,
    "G2 Fall_theta_nre": 1.405271259,
    "G2 Fall_pr_1_99_nre": 89,
    "G2 Fall_ss_nre": 118,
    "G2 Fall_dss_nre": 641,
    "G2 Winter_theta_nre": 1.579084814,
    "G2 Winter_pr_1_99_nre": 89,
    "G2 Winter_ss_nre": 118,
    "G2 Winter_dss_nre": 658,
    "G2 Spring_theta_nre": 2.057449088,
    "G2 Spring_pr_1_99_nre": 89,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 706,
    "G3 Fall_theta_nre": 2.360891277,
    "G3 Fall_pr_1_99_nre": 89,
    "G3 Fall_ss_nre": 119,
    "G3 Fall_dss_nre": 736,
    "G3 Winter_theta_nre": 2.274280484,
    "G3 Winter_pr_1_99_nre": 89,
    "G3 Winter_ss_nre": 117,
    "G3 Winter_dss_nre": 727,
    "G3 Spring_theta_nre": 2.552177545,
    "G3 Spring_pr_1_99_nre": 89,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 755
  },
  {
    "ID": 446,
    "KG Winter_theta_nre": 0.097619624,
    "KG Winter_pr_1_99_nre": 89,
    "KG Winter_ss_nre": 118,
    "KG Winter_dss_nre": 510,
    "KG Spring_theta_nre": 0.539309764,
    "KG Spring_pr_1_99_nre": 89,
    "KG Spring_ss_nre": 120,
    "KG Spring_dss_nre": 554,
    "G1 Fall_theta_nre": 0.733825302,
    "G1 Fall_pr_1_99_nre": 89,
    "G1 Fall_ss_nre": 119,
    "G1 Fall_dss_nre": 573,
    "G1 Winter_theta_nre": 0.90756949,
    "G1 Winter_pr_1_99_nre": 89,
    "G1 Winter_ss_nre": 118,
    "G1 Winter_dss_nre": 591,
    "G1 Spring_theta_nre": 1.18931312,
    "G1 Spring_pr_1_99_nre": 89,
    "G1 Spring_ss_nre": 119,
    "G1 Spring_dss_nre": 619,
    "G2 Fall_theta_nre": 1.412152806,
    "G2 Fall_pr_1_99_nre": 89,
    "G2 Fall_ss_nre": 118,
    "G2 Fall_dss_nre": 641,
    "G2 Winter_theta_nre": 1.580624698,
    "G2 Winter_pr_1_99_nre": 89,
    "G2 Winter_ss_nre": 118,
    "G2 Winter_dss_nre": 658,
    "G2 Spring_theta_nre": 2.057653137,
    "G2 Spring_pr_1_99_nre": 89,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 706,
    "G3 Fall_theta_nre": 2.378710565,
    "G3 Fall_pr_1_99_nre": 89,
    "G3 Fall_ss_nre": 119,
    "G3 Fall_dss_nre": 738,
    "G3 Winter_theta_nre": 2.274971307,
    "G3 Winter_pr_1_99_nre": 89,
    "G3 Winter_ss_nre": 117,
    "G3 Winter_dss_nre": 727,
    "G3 Spring_theta_nre": 2.557442771,
    "G3 Spring_pr_1_99_nre": 89,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 756
  },
  {
    "ID": 447,
    "KG Winter_theta_nre": 0.111147707,
    "KG Winter_pr_1_99_nre": 89,
    "KG Winter_ss_nre": 119,
    "KG Winter_dss_nre": 511,
    "KG Spring_theta_nre": 0.545209596,
    "KG Spring_pr_1_99_nre": 89,
    "KG Spring_ss_nre": 120,
    "KG Spring_dss_nre": 555,
    "G1 Fall_theta_nre": 0.744899281,
    "G1 Fall_pr_1_99_nre": 89,
    "G1 Fall_ss_nre": 119,
    "G1 Fall_dss_nre": 574,
    "G1 Winter_theta_nre": 0.91197741,
    "G1 Winter_pr_1_99_nre": 89,
    "G1 Winter_ss_nre": 118,
    "G1 Winter_dss_nre": 591,
    "G1 Spring_theta_nre": 1.209284721,
    "G1 Spring_pr_1_99_nre": 89,
    "G1 Spring_ss_nre": 119,
    "G1 Spring_dss_nre": 621,
    "G2 Fall_theta_nre": 1.41237283,
    "G2 Fall_pr_1_99_nre": 89,
    "G2 Fall_ss_nre": 118,
    "G2 Fall_dss_nre": 641,
    "G2 Winter_theta_nre": 1.599556374,
    "G2 Winter_pr_1_99_nre": 89,
    "G2 Winter_ss_nre": 119,
    "G2 Winter_dss_nre": 660,
    "G2 Spring_theta_nre": 2.061855285,
    "G2 Spring_pr_1_99_nre": 89,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 706,
    "G3 Fall_theta_nre": 2.381751476,
    "G3 Fall_pr_1_99_nre": 89,
    "G3 Fall_ss_nre": 119,
    "G3 Fall_dss_nre": 738,
    "G3 Winter_theta_nre": 2.302158733,
    "G3 Winter_pr_1_99_nre": 89,
    "G3 Winter_ss_nre": 118,
    "G3 Winter_dss_nre": 730,
    "G3 Spring_theta_nre": 2.557457483,
    "G3 Spring_pr_1_99_nre": 89,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 756
  },
  {
    "ID": 448,
    "KG Winter_theta_nre": 0.122066831,
    "KG Winter_pr_1_99_nre": 90,
    "KG Winter_ss_nre": 119,
    "KG Winter_dss_nre": 512,
    "KG Spring_theta_nre": 0.550734389,
    "KG Spring_pr_1_99_nre": 90,
    "KG Spring_ss_nre": 120,
    "KG Spring_dss_nre": 555,
    "G1 Fall_theta_nre": 0.745236414,
    "G1 Fall_pr_1_99_nre": 90,
    "G1 Fall_ss_nre": 119,
    "G1 Fall_dss_nre": 575,
    "G1 Winter_theta_nre": 0.922151766,
    "G1 Winter_pr_1_99_nre": 90,
    "G1 Winter_ss_nre": 119,
    "G1 Winter_dss_nre": 592,
    "G1 Spring_theta_nre": 1.209434789,
    "G1 Spring_pr_1_99_nre": 90,
    "G1 Spring_ss_nre": 119,
    "G1 Spring_dss_nre": 621,
    "G2 Fall_theta_nre": 1.42667693,
    "G2 Fall_pr_1_99_nre": 90,
    "G2 Fall_ss_nre": 118,
    "G2 Fall_dss_nre": 643,
    "G2 Winter_theta_nre": 1.609930829,
    "G2 Winter_pr_1_99_nre": 90,
    "G2 Winter_ss_nre": 119,
    "G2 Winter_dss_nre": 661,
    "G2 Spring_theta_nre": 2.064955971,
    "G2 Spring_pr_1_99_nre": 90,
    "G2 Spring_ss_nre": 119,
    "G2 Spring_dss_nre": 706,
    "G3 Fall_theta_nre": 2.383178731,
    "G3 Fall_pr_1_99_nre": 90,
    "G3 Fall_ss_nre": 119,
    "G3 Fall_dss_nre": 738,
    "G3 Winter_theta_nre": 2.30346258,
    "G3 Winter_pr_1_99_nre": 90,
    "G3 Winter_ss_nre": 118,
    "G3 Winter_dss_nre": 730,
    "G3 Spring_theta_nre": 2.559915539,
    "G3 Spring_pr_1_99_nre": 90,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 756
  },
  {
    "ID": 449,
    "KG Winter_theta_nre": 0.127632307,
    "KG Winter_pr_1_99_nre": 90,
    "KG Winter_ss_nre": 119,
    "KG Winter_dss_nre": 513,
    "KG Spring_theta_nre": 0.556164169,
    "KG Spring_pr_1_99_nre": 90,
    "KG Spring_ss_nre": 120,
    "KG Spring_dss_nre": 556,
    "G1 Fall_theta_nre": 0.752146919,
    "G1 Fall_pr_1_99_nre": 90,
    "G1 Fall_ss_nre": 119,
    "G1 Fall_dss_nre": 575,
    "G1 Winter_theta_nre": 0.923667849,
    "G1 Winter_pr_1_99_nre": 90,
    "G1 Winter_ss_nre": 119,
    "G1 Winter_dss_nre": 592,
    "G1 Spring_theta_nre": 1.214681205,
    "G1 Spring_pr_1_99_nre": 90,
    "G1 Spring_ss_nre": 119,
    "G1 Spring_dss_nre": 621,
    "G2 Fall_theta_nre": 1.431239619,
    "G2 Fall_pr_1_99_nre": 90,
    "G2 Fall_ss_nre": 118,
    "G2 Fall_dss_nre": 643,
    "G2 Winter_theta_nre": 1.613631619,
    "G2 Winter_pr_1_99_nre": 90,
    "G2 Winter_ss_nre": 119,
    "G2 Winter_dss_nre": 661,
    "G2 Spring_theta_nre": 2.069042401,
    "G2 Spring_pr_1_99_nre": 90,
    "G2 Spring_ss_nre": 120,
    "G2 Spring_dss_nre": 707,
    "G3 Fall_theta_nre": 2.394535443,
    "G3 Fall_pr_1_99_nre": 90,
    "G3 Fall_ss_nre": 119,
    "G3 Fall_dss_nre": 739,
    "G3 Winter_theta_nre": 2.309639438,
    "G3 Winter_pr_1_99_nre": 90,
    "G3 Winter_ss_nre": 118,
    "G3 Winter_dss_nre": 731,
    "G3 Spring_theta_nre": 2.561869646,
    "G3 Spring_pr_1_99_nre": 90,
    "G3 Spring_ss_nre": 118,
    "G3 Spring_dss_nre": 756
  },
  {
    "ID": 450,
    "KG Winter_theta_nre": 0.130677746,
    "KG Winter_pr_1_99_nre": 90,
    "KG Winter_ss_nre": 119,
    "KG Winter_dss_nre": 513,
    "KG Spring_theta_nre": 0.556361714,
    "KG Spring_pr_1_99_nre": 90,
    "KG Spring_ss_nre": 120,
    "KG Spring_dss_nre": 556,
    "G1 Fall_theta_nre": 0.767435499,
    "G1 Fall_pr_1_99_nre": 90,
    "G1 Fall_ss_nre": 119,
    "G1 Fall_dss_nre": 577,
    "G1 Winter_theta_nre": 0.924210755,
    "G1 Winter_pr_1_99_nre": 90,
    "G1 Winter_ss_nre": 119,
    "G1 Winter_dss_nre": 592,
    "G1 Spring_theta_nre": 1.22550614,
    "G1 Spring_pr_1_99_nre": 90,
    "G1 Spring_ss_nre": 120,
    "G1 Spring_dss_nre": 623,
    "G2 Fall_theta_nre": 1.44295765,
    "G2 Fall_pr_1_99_nre": 90,
    "G2 Fall_ss_nre": 118,
    "G2 Fall_dss_nre": 644,
    "G2 Winter_theta_nre": 1.615163262,
    "G2 Winter_pr_1_99_nre": 90,
    "G2 Winter_ss_nre": 119,
    "G2 Winter_dss_nre": 662,
    "G2 Spring_theta_nre": 2.072576078,
    "G2 Spring_pr_1_99_nre": 90,
    "G2 Spring_ss_nre": 120,
    "G2 Spring_dss_nre": 707,
    "G3 Fall_theta_nre": 2.415768296,
    "G3 Fall_pr_1_99_nre": 90,
    "G3 Fall_ss_nre": 120,
    "G3 Fall_dss_nre": 742,
    "G3 Winter_theta_nre": 2.345725549,
    "G3 Winter_pr_1_99_nre": 90,
    "G3 Winter_ss_nre": 119,
    "G3 Winter_dss_nre": 735,
    "G3 Spring_theta_nre": 2.567002621,
    "G3 Spring_pr_1_99_nre": 90,
    "G3 Spring_ss_nre": 119,
    "G3 Spring_dss_nre": 757
  },
  {
    "ID": 451,
    "KG Winter_theta_nre": 0.148211422,
    "KG Winter_pr_1_99_nre": 90,
    "KG Winter_ss_nre": 119,
    "KG Winter_dss_nre": 515,
    "KG Spring_theta_nre": 0.559051617,
    "KG Spring_pr_1_99_nre": 90,
    "KG Spring_ss_nre": 120,
    "KG Spring_dss_nre": 556,
    "G1 Fall_theta_nre": 0.780690698,
    "G1 Fall_pr_1_99_nre": 90,
    "G1 Fall_ss_nre": 119,
    "G1 Fall_dss_nre": 578,
    "G1 Winter_theta_nre": 0.925688606,
    "G1 Winter_pr_1_99_nre": 90,
    "G1 Winter_ss_nre": 119,
    "G1 Winter_dss_nre": 593,
    "G1 Spring_theta_nre": 1.232510389,
    "G1 Spring_pr_1_99_nre": 90,
    "G1 Spring_ss_nre": 120,
    "G1 Spring_dss_nre": 623,
    "G2 Fall_theta_nre": 1.44421438,
    "G2 Fall_pr_1_99_nre": 90,
    "G2 Fall_ss_nre": 118,
    "G2 Fall_dss_nre": 644,
    "G2 Winter_theta_nre": 1.631831059,
    "G2 Winter_pr_1_99_nre": 90,
    "G2 Winter_ss_nre": 119,
    "G2 Winter_dss_nre": 663,
    "G2 Spring_theta_nre": 2.096189532,
    "G2 Spring_pr_1_99_nre": 90,
    "G2 Spring_ss_nre": 120,
    "G2 Spring_dss_nre": 710,
    "G3 Fall_theta_nre": 2.424082872,
    "G3 Fall_pr_1_99_nre": 90,
    "G3 Fall_ss_nre": 120,
    "G3 Fall_dss_nre": 742,
    "G3 Winter_theta_nre": 2.351272627,
    "G3 Winter_pr_1_99_nre": 90,
    "G3 Winter_ss_nre": 119,
    "G3 Winter_dss_nre": 735,
    "G3 Spring_theta_nre": 2.575836951,
    "G3 Spring_pr_1_99_nre": 90,
    "G3 Spring_ss_nre": 119,
    "G3 Spring_dss_nre": 758
  },
  {
    "ID": 452,
    "KG Winter_theta_nre": 0.160279837,
    "KG Winter_pr_1_99_nre": 90,
    "KG Winter_ss_nre": 119,
    "KG Winter_dss_nre": 516,
    "KG Spring_theta_nre": 0.585133418,
    "KG Spring_pr_1_99_nre": 90,
    "KG Spring_ss_nre": 121,
    "KG Spring_dss_nre": 559,
    "G1 Fall_theta_nre": 0.790366424,
    "G1 Fall_pr_1_99_nre": 90,
    "G1 Fall_ss_nre": 120,
    "G1 Fall_dss_nre": 579,
    "G1 Winter_theta_nre": 0.926658776,
    "G1 Winter_pr_1_99_nre": 90,
    "G1 Winter_ss_nre": 119,
    "G1 Winter_dss_nre": 593,
    "G1 Spring_theta_nre": 1.239111325,
    "G1 Spring_pr_1_99_nre": 90,
    "G1 Spring_ss_nre": 120,
    "G1 Spring_dss_nre": 624,
    "G2 Fall_theta_nre": 1.458272209,
    "G2 Fall_pr_1_99_nre": 90,
    "G2 Fall_ss_nre": 119,
    "G2 Fall_dss_nre": 646,
    "G2 Winter_theta_nre": 1.636907556,
    "G2 Winter_pr_1_99_nre": 90,
    "G2 Winter_ss_nre": 119,
    "G2 Winter_dss_nre": 664,
    "G2 Spring_theta_nre": 2.107442426,
    "G2 Spring_pr_1_99_nre": 90,
    "G2 Spring_ss_nre": 120,
    "G2 Spring_dss_nre": 711,
    "G3 Fall_theta_nre": 2.426167663,
    "G3 Fall_pr_1_99_nre": 90,
    "G3 Fall_ss_nre": 120,
    "G3 Fall_dss_nre": 743,
    "G3 Winter_theta_nre": 2.35475307,
    "G3 Winter_pr_1_99_nre": 90,
    "G3 Winter_ss_nre": 119,
    "G3 Winter_dss_nre": 735,
    "G3 Spring_theta_nre": 2.589026417,
    "G3 Spring_pr_1_99_nre": 90,
    "G3 Spring_ss_nre": 119,
    "G3 Spring_dss_nre": 759
  },
  {
    "ID": 453,
    "KG Winter_theta_nre": 0.161295356,
    "KG Winter_pr_1_99_nre": 91,
    "KG Winter_ss_nre": 119,
    "KG Winter_dss_nre": 516,
    "KG Spring_theta_nre": 0.588363979,
    "KG Spring_pr_1_99_nre": 91,
    "KG Spring_ss_nre": 121,
    "KG Spring_dss_nre": 559,
    "G1 Fall_theta_nre": 0.797673441,
    "G1 Fall_pr_1_99_nre": 91,
    "G1 Fall_ss_nre": 120,
    "G1 Fall_dss_nre": 580,
    "G1 Winter_theta_nre": 0.92790745,
    "G1 Winter_pr_1_99_nre": 91,
    "G1 Winter_ss_nre": 119,
    "G1 Winter_dss_nre": 593,
    "G1 Spring_theta_nre": 1.247578694,
    "G1 Spring_pr_1_99_nre": 91,
    "G1 Spring_ss_nre": 120,
    "G1 Spring_dss_nre": 625,
    "G2 Fall_theta_nre": 1.459878201,
    "G2 Fall_pr_1_99_nre": 91,
    "G2 Fall_ss_nre": 119,
    "G2 Fall_dss_nre": 646,
    "G2 Winter_theta_nre": 1.650507467,
    "G2 Winter_pr_1_99_nre": 91,
    "G2 Winter_ss_nre": 120,
    "G2 Winter_dss_nre": 665,
    "G2 Spring_theta_nre": 2.108098792,
    "G2 Spring_pr_1_99_nre": 91,
    "G2 Spring_ss_nre": 120,
    "G2 Spring_dss_nre": 711,
    "G3 Fall_theta_nre": 2.446257315,
    "G3 Fall_pr_1_99_nre": 91,
    "G3 Fall_ss_nre": 120,
    "G3 Fall_dss_nre": 745,
    "G3 Winter_theta_nre": 2.367715207,
    "G3 Winter_pr_1_99_nre": 91,
    "G3 Winter_ss_nre": 119,
    "G3 Winter_dss_nre": 737,
    "G3 Spring_theta_nre": 2.59258678,
    "G3 Spring_pr_1_99_nre": 91,
    "G3 Spring_ss_nre": 119,
    "G3 Spring_dss_nre": 759
  },
  {
    "ID": 454,
    "KG Winter_theta_nre": 0.16770188,
    "KG Winter_pr_1_99_nre": 91,
    "KG Winter_ss_nre": 120,
    "KG Winter_dss_nre": 517,
    "KG Spring_theta_nre": 0.589191364,
    "KG Spring_pr_1_99_nre": 91,
    "KG Spring_ss_nre": 121,
    "KG Spring_dss_nre": 559,
    "G1 Fall_theta_nre": 0.824706373,
    "G1 Fall_pr_1_99_nre": 91,
    "G1 Fall_ss_nre": 120,
    "G1 Fall_dss_nre": 582,
    "G1 Winter_theta_nre": 0.939714731,
    "G1 Winter_pr_1_99_nre": 91,
    "G1 Winter_ss_nre": 119,
    "G1 Winter_dss_nre": 594,
    "G1 Spring_theta_nre": 1.247809459,
    "G1 Spring_pr_1_99_nre": 91,
    "G1 Spring_ss_nre": 120,
    "G1 Spring_dss_nre": 625,
    "G2 Fall_theta_nre": 1.493281764,
    "G2 Fall_pr_1_99_nre": 91,
    "G2 Fall_ss_nre": 119,
    "G2 Fall_dss_nre": 649,
    "G2 Winter_theta_nre": 1.662078675,
    "G2 Winter_pr_1_99_nre": 91,
    "G2 Winter_ss_nre": 120,
    "G2 Winter_dss_nre": 666,
    "G2 Spring_theta_nre": 2.130045837,
    "G2 Spring_pr_1_99_nre": 91,
    "G2 Spring_ss_nre": 121,
    "G2 Spring_dss_nre": 713,
    "G3 Fall_theta_nre": 2.449379268,
    "G3 Fall_pr_1_99_nre": 91,
    "G3 Fall_ss_nre": 120,
    "G3 Fall_dss_nre": 745,
    "G3 Winter_theta_nre": 2.369472813,
    "G3 Winter_pr_1_99_nre": 91,
    "G3 Winter_ss_nre": 119,
    "G3 Winter_dss_nre": 737,
    "G3 Spring_theta_nre": 2.611796123,
    "G3 Spring_pr_1_99_nre": 91,
    "G3 Spring_ss_nre": 120,
    "G3 Spring_dss_nre": 761
  },
  {
    "ID": 455,
    "KG Winter_theta_nre": 0.191215737,
    "KG Winter_pr_1_99_nre": 91,
    "KG Winter_ss_nre": 120,
    "KG Winter_dss_nre": 519,
    "KG Spring_theta_nre": 0.609051893,
    "KG Spring_pr_1_99_nre": 91,
    "KG Spring_ss_nre": 121,
    "KG Spring_dss_nre": 561,
    "G1 Fall_theta_nre": 0.829707562,
    "G1 Fall_pr_1_99_nre": 91,
    "G1 Fall_ss_nre": 120,
    "G1 Fall_dss_nre": 583,
    "G1 Winter_theta_nre": 0.944449004,
    "G1 Winter_pr_1_99_nre": 91,
    "G1 Winter_ss_nre": 119,
    "G1 Winter_dss_nre": 594,
    "G1 Spring_theta_nre": 1.249231967,
    "G1 Spring_pr_1_99_nre": 91,
    "G1 Spring_ss_nre": 120,
    "G1 Spring_dss_nre": 625,
    "G2 Fall_theta_nre": 1.513666652,
    "G2 Fall_pr_1_99_nre": 91,
    "G2 Fall_ss_nre": 120,
    "G2 Fall_dss_nre": 651,
    "G2 Winter_theta_nre": 1.671067018,
    "G2 Winter_pr_1_99_nre": 91,
    "G2 Winter_ss_nre": 120,
    "G2 Winter_dss_nre": 667,
    "G2 Spring_theta_nre": 2.154011317,
    "G2 Spring_pr_1_99_nre": 91,
    "G2 Spring_ss_nre": 121,
    "G2 Spring_dss_nre": 715,
    "G3 Fall_theta_nre": 2.465224247,
    "G3 Fall_pr_1_99_nre": 91,
    "G3 Fall_ss_nre": 121,
    "G3 Fall_dss_nre": 747,
    "G3 Winter_theta_nre": 2.372530433,
    "G3 Winter_pr_1_99_nre": 91,
    "G3 Winter_ss_nre": 119,
    "G3 Winter_dss_nre": 737,
    "G3 Spring_theta_nre": 2.622506086,
    "G3 Spring_pr_1_99_nre": 91,
    "G3 Spring_ss_nre": 120,
    "G3 Spring_dss_nre": 762
  },
  {
    "ID": 456,
    "KG Winter_theta_nre": 0.210139686,
    "KG Winter_pr_1_99_nre": 91,
    "KG Winter_ss_nre": 120,
    "KG Winter_dss_nre": 521,
    "KG Spring_theta_nre": 0.609757345,
    "KG Spring_pr_1_99_nre": 91,
    "KG Spring_ss_nre": 121,
    "KG Spring_dss_nre": 561,
    "G1 Fall_theta_nre": 0.840364135,
    "G1 Fall_pr_1_99_nre": 91,
    "G1 Fall_ss_nre": 120,
    "G1 Fall_dss_nre": 584,
    "G1 Winter_theta_nre": 0.947070825,
    "G1 Winter_pr_1_99_nre": 91,
    "G1 Winter_ss_nre": 119,
    "G1 Winter_dss_nre": 595,
    "G1 Spring_theta_nre": 1.271796983,
    "G1 Spring_pr_1_99_nre": 91,
    "G1 Spring_ss_nre": 121,
    "G1 Spring_dss_nre": 627,
    "G2 Fall_theta_nre": 1.516196321,
    "G2 Fall_pr_1_99_nre": 91,
    "G2 Fall_ss_nre": 120,
    "G2 Fall_dss_nre": 652,
    "G2 Winter_theta_nre": 1.672308679,
    "G2 Winter_pr_1_99_nre": 91,
    "G2 Winter_ss_nre": 120,
    "G2 Winter_dss_nre": 667,
    "G2 Spring_theta_nre": 2.155581955,
    "G2 Spring_pr_1_99_nre": 91,
    "G2 Spring_ss_nre": 121,
    "G2 Spring_dss_nre": 716,
    "G3 Fall_theta_nre": 2.465983775,
    "G3 Fall_pr_1_99_nre": 91,
    "G3 Fall_ss_nre": 121,
    "G3 Fall_dss_nre": 747,
    "G3 Winter_theta_nre": 2.38672066,
    "G3 Winter_pr_1_99_nre": 91,
    "G3 Winter_ss_nre": 120,
    "G3 Winter_dss_nre": 739,
    "G3 Spring_theta_nre": 2.62266631,
    "G3 Spring_pr_1_99_nre": 91,
    "G3 Spring_ss_nre": 120,
    "G3 Spring_dss_nre": 762
  },
  {
    "ID": 457,
    "KG Winter_theta_nre": 0.23916647,
    "KG Winter_pr_1_99_nre": 91,
    "KG Winter_ss_nre": 121,
    "KG Winter_dss_nre": 524,
    "KG Spring_theta_nre": 0.654811519,
    "KG Spring_pr_1_99_nre": 91,
    "KG Spring_ss_nre": 122,
    "KG Spring_dss_nre": 565,
    "G1 Fall_theta_nre": 0.84367359,
    "G1 Fall_pr_1_99_nre": 91,
    "G1 Fall_ss_nre": 121,
    "G1 Fall_dss_nre": 584,
    "G1 Winter_theta_nre": 0.972290962,
    "G1 Winter_pr_1_99_nre": 91,
    "G1 Winter_ss_nre": 119,
    "G1 Winter_dss_nre": 597,
    "G1 Spring_theta_nre": 1.28231314,
    "G1 Spring_pr_1_99_nre": 91,
    "G1 Spring_ss_nre": 121,
    "G1 Spring_dss_nre": 628,
    "G2 Fall_theta_nre": 1.527709166,
    "G2 Fall_pr_1_99_nre": 91,
    "G2 Fall_ss_nre": 120,
    "G2 Fall_dss_nre": 653,
    "G2 Winter_theta_nre": 1.674574676,
    "G2 Winter_pr_1_99_nre": 91,
    "G2 Winter_ss_nre": 120,
    "G2 Winter_dss_nre": 667,
    "G2 Spring_theta_nre": 2.175194867,
    "G2 Spring_pr_1_99_nre": 91,
    "G2 Spring_ss_nre": 121,
    "G2 Spring_dss_nre": 718,
    "G3 Fall_theta_nre": 2.480004784,
    "G3 Fall_pr_1_99_nre": 91,
    "G3 Fall_ss_nre": 121,
    "G3 Fall_dss_nre": 748,
    "G3 Winter_theta_nre": 2.424996747,
    "G3 Winter_pr_1_99_nre": 91,
    "G3 Winter_ss_nre": 121,
    "G3 Winter_dss_nre": 742,
    "G3 Spring_theta_nre": 2.660287682,
    "G3 Spring_pr_1_99_nre": 91,
    "G3 Spring_ss_nre": 121,
    "G3 Spring_dss_nre": 766
  },
  {
    "ID": 458,
    "KG Winter_theta_nre": 0.240206789,
    "KG Winter_pr_1_99_nre": 92,
    "KG Winter_ss_nre": 121,
    "KG Winter_dss_nre": 524,
    "KG Spring_theta_nre": 0.657077924,
    "KG Spring_pr_1_99_nre": 92,
    "KG Spring_ss_nre": 122,
    "KG Spring_dss_nre": 566,
    "G1 Fall_theta_nre": 0.844313262,
    "G1 Fall_pr_1_99_nre": 92,
    "G1 Fall_ss_nre": 121,
    "G1 Fall_dss_nre": 584,
    "G1 Winter_theta_nre": 0.979155585,
    "G1 Winter_pr_1_99_nre": 92,
    "G1 Winter_ss_nre": 120,
    "G1 Winter_dss_nre": 598,
    "G1 Spring_theta_nre": 1.285598977,
    "G1 Spring_pr_1_99_nre": 92,
    "G1 Spring_ss_nre": 121,
    "G1 Spring_dss_nre": 629,
    "G2 Fall_theta_nre": 1.531118058,
    "G2 Fall_pr_1_99_nre": 92,
    "G2 Fall_ss_nre": 120,
    "G2 Fall_dss_nre": 653,
    "G2 Winter_theta_nre": 1.681017797,
    "G2 Winter_pr_1_99_nre": 92,
    "G2 Winter_ss_nre": 120,
    "G2 Winter_dss_nre": 668,
    "G2 Spring_theta_nre": 2.191100107,
    "G2 Spring_pr_1_99_nre": 92,
    "G2 Spring_ss_nre": 122,
    "G2 Spring_dss_nre": 719,
    "G3 Fall_theta_nre": 2.482907354,
    "G3 Fall_pr_1_99_nre": 92,
    "G3 Fall_ss_nre": 121,
    "G3 Fall_dss_nre": 748,
    "G3 Winter_theta_nre": 2.444641452,
    "G3 Winter_pr_1_99_nre": 92,
    "G3 Winter_ss_nre": 121,
    "G3 Winter_dss_nre": 744,
    "G3 Spring_theta_nre": 2.662387644,
    "G3 Spring_pr_1_99_nre": 92,
    "G3 Spring_ss_nre": 121,
    "G3 Spring_dss_nre": 766
  },
  {
    "ID": 459,
    "KG Winter_theta_nre": 0.256465581,
    "KG Winter_pr_1_99_nre": 92,
    "KG Winter_ss_nre": 121,
    "KG Winter_dss_nre": 526,
    "KG Spring_theta_nre": 0.657153211,
    "KG Spring_pr_1_99_nre": 92,
    "KG Spring_ss_nre": 122,
    "KG Spring_dss_nre": 566,
    "G1 Fall_theta_nre": 0.85789337,
    "G1 Fall_pr_1_99_nre": 92,
    "G1 Fall_ss_nre": 121,
    "G1 Fall_dss_nre": 586,
    "G1 Winter_theta_nre": 0.986683805,
    "G1 Winter_pr_1_99_nre": 92,
    "G1 Winter_ss_nre": 120,
    "G1 Winter_dss_nre": 599,
    "G1 Spring_theta_nre": 1.307758129,
    "G1 Spring_pr_1_99_nre": 92,
    "G1 Spring_ss_nre": 121,
    "G1 Spring_dss_nre": 631,
    "G2 Fall_theta_nre": 1.531369807,
    "G2 Fall_pr_1_99_nre": 92,
    "G2 Fall_ss_nre": 120,
    "G2 Fall_dss_nre": 653,
    "G2 Winter_theta_nre": 1.69323299,
    "G2 Winter_pr_1_99_nre": 92,
    "G2 Winter_ss_nre": 121,
    "G2 Winter_dss_nre": 669,
    "G2 Spring_theta_nre": 2.208257772,
    "G2 Spring_pr_1_99_nre": 92,
    "G2 Spring_ss_nre": 122,
    "G2 Spring_dss_nre": 721,
    "G3 Fall_theta_nre": 2.496070697,
    "G3 Fall_pr_1_99_nre": 92,
    "G3 Fall_ss_nre": 121,
    "G3 Fall_dss_nre": 750,
    "G3 Winter_theta_nre": 2.489066248,
    "G3 Winter_pr_1_99_nre": 92,
    "G3 Winter_ss_nre": 122,
    "G3 Winter_dss_nre": 749,
    "G3 Spring_theta_nre": 2.675598909,
    "G3 Spring_pr_1_99_nre": 92,
    "G3 Spring_ss_nre": 121,
    "G3 Spring_dss_nre": 768
  },
  {
    "ID": 460,
    "KG Winter_theta_nre": 0.262961619,
    "KG Winter_pr_1_99_nre": 92,
    "KG Winter_ss_nre": 121,
    "KG Winter_dss_nre": 526,
    "KG Spring_theta_nre": 0.66051276,
    "KG Spring_pr_1_99_nre": 92,
    "KG Spring_ss_nre": 122,
    "KG Spring_dss_nre": 566,
    "G1 Fall_theta_nre": 0.858284785,
    "G1 Fall_pr_1_99_nre": 92,
    "G1 Fall_ss_nre": 121,
    "G1 Fall_dss_nre": 586,
    "G1 Winter_theta_nre": 1.035439585,
    "G1 Winter_pr_1_99_nre": 92,
    "G1 Winter_ss_nre": 121,
    "G1 Winter_dss_nre": 604,
    "G1 Spring_theta_nre": 1.313996562,
    "G1 Spring_pr_1_99_nre": 92,
    "G1 Spring_ss_nre": 121,
    "G1 Spring_dss_nre": 631,
    "G2 Fall_theta_nre": 1.54208429,
    "G2 Fall_pr_1_99_nre": 92,
    "G2 Fall_ss_nre": 120,
    "G2 Fall_dss_nre": 654,
    "G2 Winter_theta_nre": 1.69901576,
    "G2 Winter_pr_1_99_nre": 92,
    "G2 Winter_ss_nre": 121,
    "G2 Winter_dss_nre": 670,
    "G2 Spring_theta_nre": 2.241148983,
    "G2 Spring_pr_1_99_nre": 92,
    "G2 Spring_ss_nre": 122,
    "G2 Spring_dss_nre": 724,
    "G3 Fall_theta_nre": 2.498699314,
    "G3 Fall_pr_1_99_nre": 92,
    "G3 Fall_ss_nre": 121,
    "G3 Fall_dss_nre": 750,
    "G3 Winter_theta_nre": 2.489308684,
    "G3 Winter_pr_1_99_nre": 92,
    "G3 Winter_ss_nre": 122,
    "G3 Winter_dss_nre": 749,
    "G3 Spring_theta_nre": 2.67562872,
    "G3 Spring_pr_1_99_nre": 92,
    "G3 Spring_ss_nre": 121,
    "G3 Spring_dss_nre": 768
  },
  {
    "ID": 461,
    "KG Winter_theta_nre": 0.273851766,
    "KG Winter_pr_1_99_nre": 92,
    "KG Winter_ss_nre": 121,
    "KG Winter_dss_nre": 527,
    "KG Spring_theta_nre": 0.661629152,
    "KG Spring_pr_1_99_nre": 92,
    "KG Spring_ss_nre": 122,
    "KG Spring_dss_nre": 566,
    "G1 Fall_theta_nre": 0.858839273,
    "G1 Fall_pr_1_99_nre": 92,
    "G1 Fall_ss_nre": 121,
    "G1 Fall_dss_nre": 586,
    "G1 Winter_theta_nre": 1.067995403,
    "G1 Winter_pr_1_99_nre": 92,
    "G1 Winter_ss_nre": 121,
    "G1 Winter_dss_nre": 607,
    "G1 Spring_theta_nre": 1.324884826,
    "G1 Spring_pr_1_99_nre": 92,
    "G1 Spring_ss_nre": 122,
    "G1 Spring_dss_nre": 632,
    "G2 Fall_theta_nre": 1.542836938,
    "G2 Fall_pr_1_99_nre": 92,
    "G2 Fall_ss_nre": 120,
    "G2 Fall_dss_nre": 654,
    "G2 Winter_theta_nre": 1.70407411,
    "G2 Winter_pr_1_99_nre": 92,
    "G2 Winter_ss_nre": 121,
    "G2 Winter_dss_nre": 670,
    "G2 Spring_theta_nre": 2.245222011,
    "G2 Spring_pr_1_99_nre": 92,
    "G2 Spring_ss_nre": 123,
    "G2 Spring_dss_nre": 725,
    "G3 Fall_theta_nre": 2.510939399,
    "G3 Fall_pr_1_99_nre": 92,
    "G3 Fall_ss_nre": 121,
    "G3 Fall_dss_nre": 751,
    "G3 Winter_theta_nre": 2.490177885,
    "G3 Winter_pr_1_99_nre": 92,
    "G3 Winter_ss_nre": 122,
    "G3 Winter_dss_nre": 749,
    "G3 Spring_theta_nre": 2.680955787,
    "G3 Spring_pr_1_99_nre": 92,
    "G3 Spring_ss_nre": 121,
    "G3 Spring_dss_nre": 768
  },
  {
    "ID": 462,
    "KG Winter_theta_nre": 0.278996535,
    "KG Winter_pr_1_99_nre": 92,
    "KG Winter_ss_nre": 122,
    "KG Winter_dss_nre": 528,
    "KG Spring_theta_nre": 0.683921039,
    "KG Spring_pr_1_99_nre": 92,
    "KG Spring_ss_nre": 122,
    "KG Spring_dss_nre": 568,
    "G1 Fall_theta_nre": 0.866386991,
    "G1 Fall_pr_1_99_nre": 92,
    "G1 Fall_ss_nre": 121,
    "G1 Fall_dss_nre": 587,
    "G1 Winter_theta_nre": 1.069938031,
    "G1 Winter_pr_1_99_nre": 92,
    "G1 Winter_ss_nre": 121,
    "G1 Winter_dss_nre": 607,
    "G1 Spring_theta_nre": 1.340686215,
    "G1 Spring_pr_1_99_nre": 92,
    "G1 Spring_ss_nre": 122,
    "G1 Spring_dss_nre": 634,
    "G2 Fall_theta_nre": 1.551280701,
    "G2 Fall_pr_1_99_nre": 92,
    "G2 Fall_ss_nre": 120,
    "G2 Fall_dss_nre": 655,
    "G2 Winter_theta_nre": 1.708989776,
    "G2 Winter_pr_1_99_nre": 92,
    "G2 Winter_ss_nre": 121,
    "G2 Winter_dss_nre": 671,
    "G2 Spring_theta_nre": 2.24691346,
    "G2 Spring_pr_1_99_nre": 92,
    "G2 Spring_ss_nre": 123,
    "G2 Spring_dss_nre": 725,
    "G3 Fall_theta_nre": 2.512659189,
    "G3 Fall_pr_1_99_nre": 92,
    "G3 Fall_ss_nre": 121,
    "G3 Fall_dss_nre": 751,
    "G3 Winter_theta_nre": 2.502477058,
    "G3 Winter_pr_1_99_nre": 92,
    "G3 Winter_ss_nre": 122,
    "G3 Winter_dss_nre": 750,
    "G3 Spring_theta_nre": 2.683919895,
    "G3 Spring_pr_1_99_nre": 92,
    "G3 Spring_ss_nre": 121,
    "G3 Spring_dss_nre": 768
  },
  {
    "ID": 463,
    "KG Winter_theta_nre": 0.285257311,
    "KG Winter_pr_1_99_nre": 93,
    "KG Winter_ss_nre": 122,
    "KG Winter_dss_nre": 529,
    "KG Spring_theta_nre": 0.691297365,
    "KG Spring_pr_1_99_nre": 93,
    "KG Spring_ss_nre": 122,
    "KG Spring_dss_nre": 569,
    "G1 Fall_theta_nre": 0.869272435,
    "G1 Fall_pr_1_99_nre": 93,
    "G1 Fall_ss_nre": 121,
    "G1 Fall_dss_nre": 587,
    "G1 Winter_theta_nre": 1.084816801,
    "G1 Winter_pr_1_99_nre": 93,
    "G1 Winter_ss_nre": 122,
    "G1 Winter_dss_nre": 608,
    "G1 Spring_theta_nre": 1.342903741,
    "G1 Spring_pr_1_99_nre": 93,
    "G1 Spring_ss_nre": 122,
    "G1 Spring_dss_nre": 634,
    "G2 Fall_theta_nre": 1.579695526,
    "G2 Fall_pr_1_99_nre": 93,
    "G2 Fall_ss_nre": 121,
    "G2 Fall_dss_nre": 658,
    "G2 Winter_theta_nre": 1.740853326,
    "G2 Winter_pr_1_99_nre": 93,
    "G2 Winter_ss_nre": 121,
    "G2 Winter_dss_nre": 674,
    "G2 Spring_theta_nre": 2.262176042,
    "G2 Spring_pr_1_99_nre": 93,
    "G2 Spring_ss_nre": 123,
    "G2 Spring_dss_nre": 726,
    "G3 Fall_theta_nre": 2.526705901,
    "G3 Fall_pr_1_99_nre": 93,
    "G3 Fall_ss_nre": 122,
    "G3 Fall_dss_nre": 753,
    "G3 Winter_theta_nre": 2.509594685,
    "G3 Winter_pr_1_99_nre": 93,
    "G3 Winter_ss_nre": 122,
    "G3 Winter_dss_nre": 751,
    "G3 Spring_theta_nre": 2.705815771,
    "G3 Spring_pr_1_99_nre": 93,
    "G3 Spring_ss_nre": 122,
    "G3 Spring_dss_nre": 771
  },
  {
    "ID": 464,
    "KG Winter_theta_nre": 0.286158272,
    "KG Winter_pr_1_99_nre": 93,
    "KG Winter_ss_nre": 122,
    "KG Winter_dss_nre": 529,
    "KG Spring_theta_nre": 0.709416702,
    "KG Spring_pr_1_99_nre": 93,
    "KG Spring_ss_nre": 123,
    "KG Spring_dss_nre": 571,
    "G1 Fall_theta_nre": 0.873846409,
    "G1 Fall_pr_1_99_nre": 93,
    "G1 Fall_ss_nre": 121,
    "G1 Fall_dss_nre": 587,
    "G1 Winter_theta_nre": 1.085606586,
    "G1 Winter_pr_1_99_nre": 93,
    "G1 Winter_ss_nre": 122,
    "G1 Winter_dss_nre": 609,
    "G1 Spring_theta_nre": 1.348241836,
    "G1 Spring_pr_1_99_nre": 93,
    "G1 Spring_ss_nre": 122,
    "G1 Spring_dss_nre": 635,
    "G2 Fall_theta_nre": 1.582599562,
    "G2 Fall_pr_1_99_nre": 93,
    "G2 Fall_ss_nre": 121,
    "G2 Fall_dss_nre": 658,
    "G2 Winter_theta_nre": 1.75490712,
    "G2 Winter_pr_1_99_nre": 93,
    "G2 Winter_ss_nre": 122,
    "G2 Winter_dss_nre": 675,
    "G2 Spring_theta_nre": 2.281583416,
    "G2 Spring_pr_1_99_nre": 93,
    "G2 Spring_ss_nre": 123,
    "G2 Spring_dss_nre": 728,
    "G3 Fall_theta_nre": 2.529985253,
    "G3 Fall_pr_1_99_nre": 93,
    "G3 Fall_ss_nre": 122,
    "G3 Fall_dss_nre": 753,
    "G3 Winter_theta_nre": 2.52552739,
    "G3 Winter_pr_1_99_nre": 93,
    "G3 Winter_ss_nre": 123,
    "G3 Winter_dss_nre": 753,
    "G3 Spring_theta_nre": 2.70755076,
    "G3 Spring_pr_1_99_nre": 93,
    "G3 Spring_ss_nre": 122,
    "G3 Spring_dss_nre": 771
  },
  {
    "ID": 465,
    "KG Winter_theta_nre": 0.293626064,
    "KG Winter_pr_1_99_nre": 93,
    "KG Winter_ss_nre": 122,
    "KG Winter_dss_nre": 529,
    "KG Spring_theta_nre": 0.717295173,
    "KG Spring_pr_1_99_nre": 93,
    "KG Spring_ss_nre": 123,
    "KG Spring_dss_nre": 572,
    "G1 Fall_theta_nre": 0.884897109,
    "G1 Fall_pr_1_99_nre": 93,
    "G1 Fall_ss_nre": 121,
    "G1 Fall_dss_nre": 588,
    "G1 Winter_theta_nre": 1.100699279,
    "G1 Winter_pr_1_99_nre": 93,
    "G1 Winter_ss_nre": 122,
    "G1 Winter_dss_nre": 610,
    "G1 Spring_theta_nre": 1.348628433,
    "G1 Spring_pr_1_99_nre": 93,
    "G1 Spring_ss_nre": 122,
    "G1 Spring_dss_nre": 635,
    "G2 Fall_theta_nre": 1.594197939,
    "G2 Fall_pr_1_99_nre": 93,
    "G2 Fall_ss_nre": 121,
    "G2 Fall_dss_nre": 659,
    "G2 Winter_theta_nre": 1.776315745,
    "G2 Winter_pr_1_99_nre": 93,
    "G2 Winter_ss_nre": 122,
    "G2 Winter_dss_nre": 678,
    "G2 Spring_theta_nre": 2.294844556,
    "G2 Spring_pr_1_99_nre": 93,
    "G2 Spring_ss_nre": 123,
    "G2 Spring_dss_nre": 729,
    "G3 Fall_theta_nre": 2.532277681,
    "G3 Fall_pr_1_99_nre": 93,
    "G3 Fall_ss_nre": 122,
    "G3 Fall_dss_nre": 753,
    "G3 Winter_theta_nre": 2.534981504,
    "G3 Winter_pr_1_99_nre": 93,
    "G3 Winter_ss_nre": 123,
    "G3 Winter_dss_nre": 753,
    "G3 Spring_theta_nre": 2.714818417,
    "G3 Spring_pr_1_99_nre": 93,
    "G3 Spring_ss_nre": 122,
    "G3 Spring_dss_nre": 771
  },
  {
    "ID": 466,
    "KG Winter_theta_nre": 0.312691422,
    "KG Winter_pr_1_99_nre": 93,
    "KG Winter_ss_nre": 122,
    "KG Winter_dss_nre": 531,
    "KG Spring_theta_nre": 0.719549158,
    "KG Spring_pr_1_99_nre": 93,
    "KG Spring_ss_nre": 123,
    "KG Spring_dss_nre": 572,
    "G1 Fall_theta_nre": 0.912540778,
    "G1 Fall_pr_1_99_nre": 93,
    "G1 Fall_ss_nre": 122,
    "G1 Fall_dss_nre": 591,
    "G1 Winter_theta_nre": 1.132958725,
    "G1 Winter_pr_1_99_nre": 93,
    "G1 Winter_ss_nre": 122,
    "G1 Winter_dss_nre": 613,
    "G1 Spring_theta_nre": 1.364685102,
    "G1 Spring_pr_1_99_nre": 93,
    "G1 Spring_ss_nre": 122,
    "G1 Spring_dss_nre": 636,
    "G2 Fall_theta_nre": 1.627152665,
    "G2 Fall_pr_1_99_nre": 93,
    "G2 Fall_ss_nre": 122,
    "G2 Fall_dss_nre": 663,
    "G2 Winter_theta_nre": 1.776846747,
    "G2 Winter_pr_1_99_nre": 93,
    "G2 Winter_ss_nre": 122,
    "G2 Winter_dss_nre": 678,
    "G2 Spring_theta_nre": 2.30137196,
    "G2 Spring_pr_1_99_nre": 93,
    "G2 Spring_ss_nre": 124,
    "G2 Spring_dss_nre": 730,
    "G3 Fall_theta_nre": 2.546317194,
    "G3 Fall_pr_1_99_nre": 93,
    "G3 Fall_ss_nre": 122,
    "G3 Fall_dss_nre": 755,
    "G3 Winter_theta_nre": 2.535870394,
    "G3 Winter_pr_1_99_nre": 93,
    "G3 Winter_ss_nre": 123,
    "G3 Winter_dss_nre": 754,
    "G3 Spring_theta_nre": 2.719146886,
    "G3 Spring_pr_1_99_nre": 93,
    "G3 Spring_ss_nre": 122,
    "G3 Spring_dss_nre": 772
  },
  {
    "ID": 467,
    "KG Winter_theta_nre": 0.330328792,
    "KG Winter_pr_1_99_nre": 93,
    "KG Winter_ss_nre": 122,
    "KG Winter_dss_nre": 533,
    "KG Spring_theta_nre": 0.719843182,
    "KG Spring_pr_1_99_nre": 93,
    "KG Spring_ss_nre": 123,
    "KG Spring_dss_nre": 572,
    "G1 Fall_theta_nre": 0.912841484,
    "G1 Fall_pr_1_99_nre": 93,
    "G1 Fall_ss_nre": 122,
    "G1 Fall_dss_nre": 591,
    "G1 Winter_theta_nre": 1.144121715,
    "G1 Winter_pr_1_99_nre": 93,
    "G1 Winter_ss_nre": 123,
    "G1 Winter_dss_nre": 614,
    "G1 Spring_theta_nre": 1.374142933,
    "G1 Spring_pr_1_99_nre": 93,
    "G1 Spring_ss_nre": 122,
    "G1 Spring_dss_nre": 637,
    "G2 Fall_theta_nre": 1.62767589,
    "G2 Fall_pr_1_99_nre": 93,
    "G2 Fall_ss_nre": 122,
    "G2 Fall_dss_nre": 663,
    "G2 Winter_theta_nre": 1.779671247,
    "G2 Winter_pr_1_99_nre": 93,
    "G2 Winter_ss_nre": 122,
    "G2 Winter_dss_nre": 678,
    "G2 Spring_theta_nre": 2.327917583,
    "G2 Spring_pr_1_99_nre": 93,
    "G2 Spring_ss_nre": 124,
    "G2 Spring_dss_nre": 733,
    "G3 Fall_theta_nre": 2.558750683,
    "G3 Fall_pr_1_99_nre": 93,
    "G3 Fall_ss_nre": 122,
    "G3 Fall_dss_nre": 756,
    "G3 Winter_theta_nre": 2.539600049,
    "G3 Winter_pr_1_99_nre": 93,
    "G3 Winter_ss_nre": 123,
    "G3 Winter_dss_nre": 754,
    "G3 Spring_theta_nre": 2.746025665,
    "G3 Spring_pr_1_99_nre": 93,
    "G3 Spring_ss_nre": 122,
    "G3 Spring_dss_nre": 775
  },
  {
    "ID": 468,
    "KG Winter_theta_nre": 0.333306745,
    "KG Winter_pr_1_99_nre": 94,
    "KG Winter_ss_nre": 122,
    "KG Winter_dss_nre": 533,
    "KG Spring_theta_nre": 0.724318588,
    "KG Spring_pr_1_99_nre": 94,
    "KG Spring_ss_nre": 123,
    "KG Spring_dss_nre": 572,
    "G1 Fall_theta_nre": 0.913192841,
    "G1 Fall_pr_1_99_nre": 94,
    "G1 Fall_ss_nre": 122,
    "G1 Fall_dss_nre": 591,
    "G1 Winter_theta_nre": 1.154394922,
    "G1 Winter_pr_1_99_nre": 94,
    "G1 Winter_ss_nre": 123,
    "G1 Winter_dss_nre": 615,
    "G1 Spring_theta_nre": 1.388434567,
    "G1 Spring_pr_1_99_nre": 94,
    "G1 Spring_ss_nre": 123,
    "G1 Spring_dss_nre": 639,
    "G2 Fall_theta_nre": 1.628962713,
    "G2 Fall_pr_1_99_nre": 94,
    "G2 Fall_ss_nre": 122,
    "G2 Fall_dss_nre": 663,
    "G2 Winter_theta_nre": 1.802459569,
    "G2 Winter_pr_1_99_nre": 94,
    "G2 Winter_ss_nre": 123,
    "G2 Winter_dss_nre": 680,
    "G2 Spring_theta_nre": 2.353843003,
    "G2 Spring_pr_1_99_nre": 94,
    "G2 Spring_ss_nre": 124,
    "G2 Spring_dss_nre": 735,
    "G3 Fall_theta_nre": 2.602611154,
    "G3 Fall_pr_1_99_nre": 94,
    "G3 Fall_ss_nre": 123,
    "G3 Fall_dss_nre": 760,
    "G3 Winter_theta_nre": 2.566274992,
    "G3 Winter_pr_1_99_nre": 94,
    "G3 Winter_ss_nre": 123,
    "G3 Winter_dss_nre": 757,
    "G3 Spring_theta_nre": 2.748261263,
    "G3 Spring_pr_1_99_nre": 94,
    "G3 Spring_ss_nre": 123,
    "G3 Spring_dss_nre": 775
  },
  {
    "ID": 469,
    "KG Winter_theta_nre": 0.362787466,
    "KG Winter_pr_1_99_nre": 94,
    "KG Winter_ss_nre": 123,
    "KG Winter_dss_nre": 536,
    "KG Spring_theta_nre": 0.725880201,
    "KG Spring_pr_1_99_nre": 94,
    "KG Spring_ss_nre": 123,
    "KG Spring_dss_nre": 573,
    "G1 Fall_theta_nre": 0.970768707,
    "G1 Fall_pr_1_99_nre": 94,
    "G1 Fall_ss_nre": 123,
    "G1 Fall_dss_nre": 597,
    "G1 Winter_theta_nre": 1.162042337,
    "G1 Winter_pr_1_99_nre": 94,
    "G1 Winter_ss_nre": 123,
    "G1 Winter_dss_nre": 616,
    "G1 Spring_theta_nre": 1.422000571,
    "G1 Spring_pr_1_99_nre": 94,
    "G1 Spring_ss_nre": 123,
    "G1 Spring_dss_nre": 642,
    "G2 Fall_theta_nre": 1.634937099,
    "G2 Fall_pr_1_99_nre": 94,
    "G2 Fall_ss_nre": 122,
    "G2 Fall_dss_nre": 663,
    "G2 Winter_theta_nre": 1.818996218,
    "G2 Winter_pr_1_99_nre": 94,
    "G2 Winter_ss_nre": 123,
    "G2 Winter_dss_nre": 682,
    "G2 Spring_theta_nre": 2.355661136,
    "G2 Spring_pr_1_99_nre": 94,
    "G2 Spring_ss_nre": 124,
    "G2 Spring_dss_nre": 736,
    "G3 Fall_theta_nre": 2.623460499,
    "G3 Fall_pr_1_99_nre": 94,
    "G3 Fall_ss_nre": 123,
    "G3 Fall_dss_nre": 762,
    "G3 Winter_theta_nre": 2.596746272,
    "G3 Winter_pr_1_99_nre": 94,
    "G3 Winter_ss_nre": 124,
    "G3 Winter_dss_nre": 760,
    "G3 Spring_theta_nre": 2.766830785,
    "G3 Spring_pr_1_99_nre": 94,
    "G3 Spring_ss_nre": 123,
    "G3 Spring_dss_nre": 777
  },
  {
    "ID": 470,
    "KG Winter_theta_nre": 0.376660279,
    "KG Winter_pr_1_99_nre": 94,
    "KG Winter_ss_nre": 123,
    "KG Winter_dss_nre": 538,
    "KG Spring_theta_nre": 0.732831252,
    "KG Spring_pr_1_99_nre": 94,
    "KG Spring_ss_nre": 123,
    "KG Spring_dss_nre": 573,
    "G1 Fall_theta_nre": 0.994723005,
    "G1 Fall_pr_1_99_nre": 94,
    "G1 Fall_ss_nre": 123,
    "G1 Fall_dss_nre": 599,
    "G1 Winter_theta_nre": 1.173804192,
    "G1 Winter_pr_1_99_nre": 94,
    "G1 Winter_ss_nre": 123,
    "G1 Winter_dss_nre": 617,
    "G1 Spring_theta_nre": 1.431704042,
    "G1 Spring_pr_1_99_nre": 94,
    "G1 Spring_ss_nre": 124,
    "G1 Spring_dss_nre": 643,
    "G2 Fall_theta_nre": 1.636872588,
    "G2 Fall_pr_1_99_nre": 94,
    "G2 Fall_ss_nre": 122,
    "G2 Fall_dss_nre": 664,
    "G2 Winter_theta_nre": 1.820766677,
    "G2 Winter_pr_1_99_nre": 94,
    "G2 Winter_ss_nre": 123,
    "G2 Winter_dss_nre": 682,
    "G2 Spring_theta_nre": 2.408266719,
    "G2 Spring_pr_1_99_nre": 94,
    "G2 Spring_ss_nre": 125,
    "G2 Spring_dss_nre": 741,
    "G3 Fall_theta_nre": 2.641796978,
    "G3 Fall_pr_1_99_nre": 94,
    "G3 Fall_ss_nre": 123,
    "G3 Fall_dss_nre": 764,
    "G3 Winter_theta_nre": 2.605156894,
    "G3 Winter_pr_1_99_nre": 94,
    "G3 Winter_ss_nre": 124,
    "G3 Winter_dss_nre": 761,
    "G3 Spring_theta_nre": 2.778414934,
    "G3 Spring_pr_1_99_nre": 94,
    "G3 Spring_ss_nre": 123,
    "G3 Spring_dss_nre": 778
  },
  {
    "ID": 471,
    "KG Winter_theta_nre": 0.386582201,
    "KG Winter_pr_1_99_nre": 94,
    "KG Winter_ss_nre": 123,
    "KG Winter_dss_nre": 539,
    "KG Spring_theta_nre": 0.74355396,
    "KG Spring_pr_1_99_nre": 94,
    "KG Spring_ss_nre": 123,
    "KG Spring_dss_nre": 574,
    "G1 Fall_theta_nre": 1.007102186,
    "G1 Fall_pr_1_99_nre": 94,
    "G1 Fall_ss_nre": 123,
    "G1 Fall_dss_nre": 601,
    "G1 Winter_theta_nre": 1.216770284,
    "G1 Winter_pr_1_99_nre": 94,
    "G1 Winter_ss_nre": 124,
    "G1 Winter_dss_nre": 622,
    "G1 Spring_theta_nre": 1.461737415,
    "G1 Spring_pr_1_99_nre": 94,
    "G1 Spring_ss_nre": 124,
    "G1 Spring_dss_nre": 646,
    "G2 Fall_theta_nre": 1.638503042,
    "G2 Fall_pr_1_99_nre": 94,
    "G2 Fall_ss_nre": 122,
    "G2 Fall_dss_nre": 664,
    "G2 Winter_theta_nre": 1.82986183,
    "G2 Winter_pr_1_99_nre": 94,
    "G2 Winter_ss_nre": 123,
    "G2 Winter_dss_nre": 683,
    "G2 Spring_theta_nre": 2.417940037,
    "G2 Spring_pr_1_99_nre": 94,
    "G2 Spring_ss_nre": 126,
    "G2 Spring_dss_nre": 742,
    "G3 Fall_theta_nre": 2.650811279,
    "G3 Fall_pr_1_99_nre": 94,
    "G3 Fall_ss_nre": 124,
    "G3 Fall_dss_nre": 765,
    "G3 Winter_theta_nre": 2.639688437,
    "G3 Winter_pr_1_99_nre": 94,
    "G3 Winter_ss_nre": 125,
    "G3 Winter_dss_nre": 764,
    "G3 Spring_theta_nre": 2.7922554,
    "G3 Spring_pr_1_99_nre": 94,
    "G3 Spring_ss_nre": 123,
    "G3 Spring_dss_nre": 779
  },
  {
    "ID": 472,
    "KG Winter_theta_nre": 0.393776742,
    "KG Winter_pr_1_99_nre": 94,
    "KG Winter_ss_nre": 124,
    "KG Winter_dss_nre": 539,
    "KG Spring_theta_nre": 0.779253188,
    "KG Spring_pr_1_99_nre": 94,
    "KG Spring_ss_nre": 124,
    "KG Spring_dss_nre": 578,
    "G1 Fall_theta_nre": 1.035927961,
    "G1 Fall_pr_1_99_nre": 94,
    "G1 Fall_ss_nre": 124,
    "G1 Fall_dss_nre": 604,
    "G1 Winter_theta_nre": 1.241716087,
    "G1 Winter_pr_1_99_nre": 94,
    "G1 Winter_ss_nre": 124,
    "G1 Winter_dss_nre": 624,
    "G1 Spring_theta_nre": 1.468508865,
    "G1 Spring_pr_1_99_nre": 94,
    "G1 Spring_ss_nre": 124,
    "G1 Spring_dss_nre": 647,
    "G2 Fall_theta_nre": 1.649877842,
    "G2 Fall_pr_1_99_nre": 94,
    "G2 Fall_ss_nre": 122,
    "G2 Fall_dss_nre": 665,
    "G2 Winter_theta_nre": 1.841200241,
    "G2 Winter_pr_1_99_nre": 94,
    "G2 Winter_ss_nre": 123,
    "G2 Winter_dss_nre": 684,
    "G2 Spring_theta_nre": 2.44096037,
    "G2 Spring_pr_1_99_nre": 94,
    "G2 Spring_ss_nre": 126,
    "G2 Spring_dss_nre": 744,
    "G3 Fall_theta_nre": 2.652436035,
    "G3 Fall_pr_1_99_nre": 94,
    "G3 Fall_ss_nre": 124,
    "G3 Fall_dss_nre": 765,
    "G3 Winter_theta_nre": 2.641998572,
    "G3 Winter_pr_1_99_nre": 94,
    "G3 Winter_ss_nre": 125,
    "G3 Winter_dss_nre": 764,
    "G3 Spring_theta_nre": 2.794039972,
    "G3 Spring_pr_1_99_nre": 94,
    "G3 Spring_ss_nre": 124,
    "G3 Spring_dss_nre": 779
  },
  {
    "ID": 473,
    "KG Winter_theta_nre": 0.411151787,
    "KG Winter_pr_1_99_nre": 95,
    "KG Winter_ss_nre": 124,
    "KG Winter_dss_nre": 541,
    "KG Spring_theta_nre": 0.793574066,
    "KG Spring_pr_1_99_nre": 95,
    "KG Spring_ss_nre": 124,
    "KG Spring_dss_nre": 579,
    "G1 Fall_theta_nre": 1.049821485,
    "G1 Fall_pr_1_99_nre": 95,
    "G1 Fall_ss_nre": 124,
    "G1 Fall_dss_nre": 605,
    "G1 Winter_theta_nre": 1.263360462,
    "G1 Winter_pr_1_99_nre": 95,
    "G1 Winter_ss_nre": 125,
    "G1 Winter_dss_nre": 626,
    "G1 Spring_theta_nre": 1.477005236,
    "G1 Spring_pr_1_99_nre": 95,
    "G1 Spring_ss_nre": 124,
    "G1 Spring_dss_nre": 648,
    "G2 Fall_theta_nre": 1.719042383,
    "G2 Fall_pr_1_99_nre": 95,
    "G2 Fall_ss_nre": 123,
    "G2 Fall_dss_nre": 672,
    "G2 Winter_theta_nre": 1.887926116,
    "G2 Winter_pr_1_99_nre": 95,
    "G2 Winter_ss_nre": 124,
    "G2 Winter_dss_nre": 689,
    "G2 Spring_theta_nre": 2.44422374,
    "G2 Spring_pr_1_99_nre": 95,
    "G2 Spring_ss_nre": 126,
    "G2 Spring_dss_nre": 744,
    "G3 Fall_theta_nre": 2.733649793,
    "G3 Fall_pr_1_99_nre": 95,
    "G3 Fall_ss_nre": 125,
    "G3 Fall_dss_nre": 773,
    "G3 Winter_theta_nre": 2.652363209,
    "G3 Winter_pr_1_99_nre": 95,
    "G3 Winter_ss_nre": 125,
    "G3 Winter_dss_nre": 765,
    "G3 Spring_theta_nre": 2.805390365,
    "G3 Spring_pr_1_99_nre": 95,
    "G3 Spring_ss_nre": 124,
    "G3 Spring_dss_nre": 781
  },
  {
    "ID": 474,
    "KG Winter_theta_nre": 0.418608718,
    "KG Winter_pr_1_99_nre": 95,
    "KG Winter_ss_nre": 124,
    "KG Winter_dss_nre": 542,
    "KG Spring_theta_nre": 0.799568139,
    "KG Spring_pr_1_99_nre": 95,
    "KG Spring_ss_nre": 124,
    "KG Spring_dss_nre": 580,
    "G1 Fall_theta_nre": 1.060332682,
    "G1 Fall_pr_1_99_nre": 95,
    "G1 Fall_ss_nre": 124,
    "G1 Fall_dss_nre": 606,
    "G1 Winter_theta_nre": 1.273152839,
    "G1 Winter_pr_1_99_nre": 95,
    "G1 Winter_ss_nre": 125,
    "G1 Winter_dss_nre": 627,
    "G1 Spring_theta_nre": 1.486394029,
    "G1 Spring_pr_1_99_nre": 95,
    "G1 Spring_ss_nre": 125,
    "G1 Spring_dss_nre": 649,
    "G2 Fall_theta_nre": 1.732203464,
    "G2 Fall_pr_1_99_nre": 95,
    "G2 Fall_ss_nre": 123,
    "G2 Fall_dss_nre": 673,
    "G2 Winter_theta_nre": 1.906782423,
    "G2 Winter_pr_1_99_nre": 95,
    "G2 Winter_ss_nre": 125,
    "G2 Winter_dss_nre": 691,
    "G2 Spring_theta_nre": 2.466085055,
    "G2 Spring_pr_1_99_nre": 95,
    "G2 Spring_ss_nre": 126,
    "G2 Spring_dss_nre": 747,
    "G3 Fall_theta_nre": 2.817393525,
    "G3 Fall_pr_1_99_nre": 95,
    "G3 Fall_ss_nre": 126,
    "G3 Fall_dss_nre": 782,
    "G3 Winter_theta_nre": 2.676298183,
    "G3 Winter_pr_1_99_nre": 95,
    "G3 Winter_ss_nre": 126,
    "G3 Winter_dss_nre": 768,
    "G3 Spring_theta_nre": 2.832444818,
    "G3 Spring_pr_1_99_nre": 95,
    "G3 Spring_ss_nre": 124,
    "G3 Spring_dss_nre": 783
  },
  {
    "ID": 475,
    "KG Winter_theta_nre": 0.429394867,
    "KG Winter_pr_1_99_nre": 95,
    "KG Winter_ss_nre": 124,
    "KG Winter_dss_nre": 543,
    "KG Spring_theta_nre": 0.801034719,
    "KG Spring_pr_1_99_nre": 95,
    "KG Spring_ss_nre": 124,
    "KG Spring_dss_nre": 580,
    "G1 Fall_theta_nre": 1.066914248,
    "G1 Fall_pr_1_99_nre": 95,
    "G1 Fall_ss_nre": 124,
    "G1 Fall_dss_nre": 607,
    "G1 Winter_theta_nre": 1.280649795,
    "G1 Winter_pr_1_99_nre": 95,
    "G1 Winter_ss_nre": 125,
    "G1 Winter_dss_nre": 628,
    "G1 Spring_theta_nre": 1.48854745,
    "G1 Spring_pr_1_99_nre": 95,
    "G1 Spring_ss_nre": 125,
    "G1 Spring_dss_nre": 649,
    "G2 Fall_theta_nre": 1.738598675,
    "G2 Fall_pr_1_99_nre": 95,
    "G2 Fall_ss_nre": 124,
    "G2 Fall_dss_nre": 674,
    "G2 Winter_theta_nre": 1.907598949,
    "G2 Winter_pr_1_99_nre": 95,
    "G2 Winter_ss_nre": 125,
    "G2 Winter_dss_nre": 691,
    "G2 Spring_theta_nre": 2.466588304,
    "G2 Spring_pr_1_99_nre": 95,
    "G2 Spring_ss_nre": 126,
    "G2 Spring_dss_nre": 747,
    "G3 Fall_theta_nre": 2.819537062,
    "G3 Fall_pr_1_99_nre": 95,
    "G3 Fall_ss_nre": 126,
    "G3 Fall_dss_nre": 782,
    "G3 Winter_theta_nre": 2.686847781,
    "G3 Winter_pr_1_99_nre": 95,
    "G3 Winter_ss_nre": 126,
    "G3 Winter_dss_nre": 769,
    "G3 Spring_theta_nre": 2.855977776,
    "G3 Spring_pr_1_99_nre": 95,
    "G3 Spring_ss_nre": 125,
    "G3 Spring_dss_nre": 786
  },
  {
    "ID": 476,
    "KG Winter_theta_nre": 0.431621226,
    "KG Winter_pr_1_99_nre": 95,
    "KG Winter_ss_nre": 124,
    "KG Winter_dss_nre": 543,
    "KG Spring_theta_nre": 0.821066735,
    "KG Spring_pr_1_99_nre": 95,
    "KG Spring_ss_nre": 125,
    "KG Spring_dss_nre": 582,
    "G1 Fall_theta_nre": 1.07761747,
    "G1 Fall_pr_1_99_nre": 95,
    "G1 Fall_ss_nre": 125,
    "G1 Fall_dss_nre": 608,
    "G1 Winter_theta_nre": 1.298952145,
    "G1 Winter_pr_1_99_nre": 95,
    "G1 Winter_ss_nre": 125,
    "G1 Winter_dss_nre": 630,
    "G1 Spring_theta_nre": 1.511369588,
    "G1 Spring_pr_1_99_nre": 95,
    "G1 Spring_ss_nre": 125,
    "G1 Spring_dss_nre": 651,
    "G2 Fall_theta_nre": 1.74372858,
    "G2 Fall_pr_1_99_nre": 95,
    "G2 Fall_ss_nre": 124,
    "G2 Fall_dss_nre": 674,
    "G2 Winter_theta_nre": 1.914726642,
    "G2 Winter_pr_1_99_nre": 95,
    "G2 Winter_ss_nre": 125,
    "G2 Winter_dss_nre": 691,
    "G2 Spring_theta_nre": 2.485744264,
    "G2 Spring_pr_1_99_nre": 95,
    "G2 Spring_ss_nre": 127,
    "G2 Spring_dss_nre": 749,
    "G3 Fall_theta_nre": 2.851172693,
    "G3 Fall_pr_1_99_nre": 95,
    "G3 Fall_ss_nre": 127,
    "G3 Fall_dss_nre": 785,
    "G3 Winter_theta_nre": 2.690339244,
    "G3 Winter_pr_1_99_nre": 95,
    "G3 Winter_ss_nre": 126,
    "G3 Winter_dss_nre": 769,
    "G3 Spring_theta_nre": 2.866218077,
    "G3 Spring_pr_1_99_nre": 95,
    "G3 Spring_ss_nre": 125,
    "G3 Spring_dss_nre": 787
  },
  {
    "ID": 477,
    "KG Winter_theta_nre": 0.438435506,
    "KG Winter_pr_1_99_nre": 95,
    "KG Winter_ss_nre": 124,
    "KG Winter_dss_nre": 544,
    "KG Spring_theta_nre": 0.831468239,
    "KG Spring_pr_1_99_nre": 95,
    "KG Spring_ss_nre": 125,
    "KG Spring_dss_nre": 583,
    "G1 Fall_theta_nre": 1.096615217,
    "G1 Fall_pr_1_99_nre": 95,
    "G1 Fall_ss_nre": 125,
    "G1 Fall_dss_nre": 610,
    "G1 Winter_theta_nre": 1.331851129,
    "G1 Winter_pr_1_99_nre": 95,
    "G1 Winter_ss_nre": 126,
    "G1 Winter_dss_nre": 633,
    "G1 Spring_theta_nre": 1.512834359,
    "G1 Spring_pr_1_99_nre": 95,
    "G1 Spring_ss_nre": 125,
    "G1 Spring_dss_nre": 651,
    "G2 Fall_theta_nre": 1.763233376,
    "G2 Fall_pr_1_99_nre": 95,
    "G2 Fall_ss_nre": 124,
    "G2 Fall_dss_nre": 676,
    "G2 Winter_theta_nre": 1.928715118,
    "G2 Winter_pr_1_99_nre": 95,
    "G2 Winter_ss_nre": 125,
    "G2 Winter_dss_nre": 693,
    "G2 Spring_theta_nre": 2.496054101,
    "G2 Spring_pr_1_99_nre": 95,
    "G2 Spring_ss_nre": 127,
    "G2 Spring_dss_nre": 750,
    "G3 Fall_theta_nre": 2.856245081,
    "G3 Fall_pr_1_99_nre": 95,
    "G3 Fall_ss_nre": 127,
    "G3 Fall_dss_nre": 786,
    "G3 Winter_theta_nre": 2.692182158,
    "G3 Winter_pr_1_99_nre": 95,
    "G3 Winter_ss_nre": 126,
    "G3 Winter_dss_nre": 769,
    "G3 Spring_theta_nre": 2.894167537,
    "G3 Spring_pr_1_99_nre": 95,
    "G3 Spring_ss_nre": 126,
    "G3 Spring_dss_nre": 789
  },
  {
    "ID": 478,
    "KG Winter_theta_nre": 0.440813337,
    "KG Winter_pr_1_99_nre": 96,
    "KG Winter_ss_nre": 124,
    "KG Winter_dss_nre": 544,
    "KG Spring_theta_nre": 0.834908568,
    "KG Spring_pr_1_99_nre": 96,
    "KG Spring_ss_nre": 125,
    "KG Spring_dss_nre": 583,
    "G1 Fall_theta_nre": 1.104398707,
    "G1 Fall_pr_1_99_nre": 96,
    "G1 Fall_ss_nre": 125,
    "G1 Fall_dss_nre": 610,
    "G1 Winter_theta_nre": 1.371100586,
    "G1 Winter_pr_1_99_nre": 96,
    "G1 Winter_ss_nre": 127,
    "G1 Winter_dss_nre": 637,
    "G1 Spring_theta_nre": 1.549302927,
    "G1 Spring_pr_1_99_nre": 96,
    "G1 Spring_ss_nre": 126,
    "G1 Spring_dss_nre": 655,
    "G2 Fall_theta_nre": 1.798303517,
    "G2 Fall_pr_1_99_nre": 96,
    "G2 Fall_ss_nre": 125,
    "G2 Fall_dss_nre": 680,
    "G2 Winter_theta_nre": 1.934566982,
    "G2 Winter_pr_1_99_nre": 96,
    "G2 Winter_ss_nre": 125,
    "G2 Winter_dss_nre": 693,
    "G2 Spring_theta_nre": 2.501390022,
    "G2 Spring_pr_1_99_nre": 96,
    "G2 Spring_ss_nre": 127,
    "G2 Spring_dss_nre": 750,
    "G3 Fall_theta_nre": 2.86001513,
    "G3 Fall_pr_1_99_nre": 96,
    "G3 Fall_ss_nre": 127,
    "G3 Fall_dss_nre": 786,
    "G3 Winter_theta_nre": 2.694563861,
    "G3 Winter_pr_1_99_nre": 96,
    "G3 Winter_ss_nre": 126,
    "G3 Winter_dss_nre": 769,
    "G3 Spring_theta_nre": 2.909489773,
    "G3 Spring_pr_1_99_nre": 96,
    "G3 Spring_ss_nre": 126,
    "G3 Spring_dss_nre": 791
  },
  {
    "ID": 479,
    "KG Winter_theta_nre": 0.456875712,
    "KG Winter_pr_1_99_nre": 96,
    "KG Winter_ss_nre": 125,
    "KG Winter_dss_nre": 546,
    "KG Spring_theta_nre": 0.88370062,
    "KG Spring_pr_1_99_nre": 96,
    "KG Spring_ss_nre": 126,
    "KG Spring_dss_nre": 588,
    "G1 Fall_theta_nre": 1.120712449,
    "G1 Fall_pr_1_99_nre": 96,
    "G1 Fall_ss_nre": 125,
    "G1 Fall_dss_nre": 612,
    "G1 Winter_theta_nre": 1.373518736,
    "G1 Winter_pr_1_99_nre": 96,
    "G1 Winter_ss_nre": 127,
    "G1 Winter_dss_nre": 637,
    "G1 Spring_theta_nre": 1.569376904,
    "G1 Spring_pr_1_99_nre": 96,
    "G1 Spring_ss_nre": 126,
    "G1 Spring_dss_nre": 657,
    "G2 Fall_theta_nre": 1.819557627,
    "G2 Fall_pr_1_99_nre": 96,
    "G2 Fall_ss_nre": 125,
    "G2 Fall_dss_nre": 682,
    "G2 Winter_theta_nre": 1.936297543,
    "G2 Winter_pr_1_99_nre": 96,
    "G2 Winter_ss_nre": 125,
    "G2 Winter_dss_nre": 694,
    "G2 Spring_theta_nre": 2.513748938,
    "G2 Spring_pr_1_99_nre": 96,
    "G2 Spring_ss_nre": 127,
    "G2 Spring_dss_nre": 751,
    "G3 Fall_theta_nre": 2.885588896,
    "G3 Fall_pr_1_99_nre": 96,
    "G3 Fall_ss_nre": 127,
    "G3 Fall_dss_nre": 789,
    "G3 Winter_theta_nre": 2.71630965,
    "G3 Winter_pr_1_99_nre": 96,
    "G3 Winter_ss_nre": 127,
    "G3 Winter_dss_nre": 772,
    "G3 Spring_theta_nre": 2.930311957,
    "G3 Spring_pr_1_99_nre": 96,
    "G3 Spring_ss_nre": 126,
    "G3 Spring_dss_nre": 793
  },
  {
    "ID": 480,
    "KG Winter_theta_nre": 0.46596066,
    "KG Winter_pr_1_99_nre": 96,
    "KG Winter_ss_nre": 125,
    "KG Winter_dss_nre": 547,
    "KG Spring_theta_nre": 0.889685409,
    "KG Spring_pr_1_99_nre": 96,
    "KG Spring_ss_nre": 126,
    "KG Spring_dss_nre": 589,
    "G1 Fall_theta_nre": 1.216337649,
    "G1 Fall_pr_1_99_nre": 96,
    "G1 Fall_ss_nre": 127,
    "G1 Fall_dss_nre": 622,
    "G1 Winter_theta_nre": 1.405672288,
    "G1 Winter_pr_1_99_nre": 96,
    "G1 Winter_ss_nre": 127,
    "G1 Winter_dss_nre": 641,
    "G1 Spring_theta_nre": 1.600736986,
    "G1 Spring_pr_1_99_nre": 96,
    "G1 Spring_ss_nre": 127,
    "G1 Spring_dss_nre": 660,
    "G2 Fall_theta_nre": 1.823196759,
    "G2 Fall_pr_1_99_nre": 96,
    "G2 Fall_ss_nre": 125,
    "G2 Fall_dss_nre": 682,
    "G2 Winter_theta_nre": 1.972637373,
    "G2 Winter_pr_1_99_nre": 96,
    "G2 Winter_ss_nre": 126,
    "G2 Winter_dss_nre": 697,
    "G2 Spring_theta_nre": 2.553436121,
    "G2 Spring_pr_1_99_nre": 96,
    "G2 Spring_ss_nre": 128,
    "G2 Spring_dss_nre": 755,
    "G3 Fall_theta_nre": 2.896549931,
    "G3 Fall_pr_1_99_nre": 96,
    "G3 Fall_ss_nre": 128,
    "G3 Fall_dss_nre": 790,
    "G3 Winter_theta_nre": 2.72317249,
    "G3 Winter_pr_1_99_nre": 96,
    "G3 Winter_ss_nre": 127,
    "G3 Winter_dss_nre": 772,
    "G3 Spring_theta_nre": 2.935835039,
    "G3 Spring_pr_1_99_nre": 96,
    "G3 Spring_ss_nre": 127,
    "G3 Spring_dss_nre": 794
  },
  {
    "ID": 481,
    "KG Winter_theta_nre": 0.485824199,
    "KG Winter_pr_1_99_nre": 96,
    "KG Winter_ss_nre": 125,
    "KG Winter_dss_nre": 549,
    "KG Spring_theta_nre": 0.904662575,
    "KG Spring_pr_1_99_nre": 96,
    "KG Spring_ss_nre": 126,
    "KG Spring_dss_nre": 590,
    "G1 Fall_theta_nre": 1.230927334,
    "G1 Fall_pr_1_99_nre": 96,
    "G1 Fall_ss_nre": 127,
    "G1 Fall_dss_nre": 623,
    "G1 Winter_theta_nre": 1.406306793,
    "G1 Winter_pr_1_99_nre": 96,
    "G1 Winter_ss_nre": 127,
    "G1 Winter_dss_nre": 641,
    "G1 Spring_theta_nre": 1.613493639,
    "G1 Spring_pr_1_99_nre": 96,
    "G1 Spring_ss_nre": 127,
    "G1 Spring_dss_nre": 661,
    "G2 Fall_theta_nre": 1.852701343,
    "G2 Fall_pr_1_99_nre": 96,
    "G2 Fall_ss_nre": 126,
    "G2 Fall_dss_nre": 685,
    "G2 Winter_theta_nre": 2.003289423,
    "G2 Winter_pr_1_99_nre": 96,
    "G2 Winter_ss_nre": 127,
    "G2 Winter_dss_nre": 700,
    "G2 Spring_theta_nre": 2.558664177,
    "G2 Spring_pr_1_99_nre": 96,
    "G2 Spring_ss_nre": 128,
    "G2 Spring_dss_nre": 756,
    "G3 Fall_theta_nre": 2.930865469,
    "G3 Fall_pr_1_99_nre": 96,
    "G3 Fall_ss_nre": 128,
    "G3 Fall_dss_nre": 793,
    "G3 Winter_theta_nre": 2.783769566,
    "G3 Winter_pr_1_99_nre": 96,
    "G3 Winter_ss_nre": 128,
    "G3 Winter_dss_nre": 778,
    "G3 Spring_theta_nre": 2.939771031,
    "G3 Spring_pr_1_99_nre": 96,
    "G3 Spring_ss_nre": 127,
    "G3 Spring_dss_nre": 794
  },
  {
    "ID": 482,
    "KG Winter_theta_nre": 0.498486235,
    "KG Winter_pr_1_99_nre": 96,
    "KG Winter_ss_nre": 125,
    "KG Winter_dss_nre": 550,
    "KG Spring_theta_nre": 0.915426223,
    "KG Spring_pr_1_99_nre": 96,
    "KG Spring_ss_nre": 126,
    "KG Spring_dss_nre": 592,
    "G1 Fall_theta_nre": 1.26754317,
    "G1 Fall_pr_1_99_nre": 96,
    "G1 Fall_ss_nre": 128,
    "G1 Fall_dss_nre": 627,
    "G1 Winter_theta_nre": 1.417549436,
    "G1 Winter_pr_1_99_nre": 96,
    "G1 Winter_ss_nre": 127,
    "G1 Winter_dss_nre": 642,
    "G1 Spring_theta_nre": 1.649517713,
    "G1 Spring_pr_1_99_nre": 96,
    "G1 Spring_ss_nre": 128,
    "G1 Spring_dss_nre": 665,
    "G2 Fall_theta_nre": 1.857218306,
    "G2 Fall_pr_1_99_nre": 96,
    "G2 Fall_ss_nre": 126,
    "G2 Fall_dss_nre": 686,
    "G2 Winter_theta_nre": 2.009066356,
    "G2 Winter_pr_1_99_nre": 96,
    "G2 Winter_ss_nre": 127,
    "G2 Winter_dss_nre": 701,
    "G2 Spring_theta_nre": 2.58850269,
    "G2 Spring_pr_1_99_nre": 96,
    "G2 Spring_ss_nre": 128,
    "G2 Spring_dss_nre": 759,
    "G3 Fall_theta_nre": 2.965613316,
    "G3 Fall_pr_1_99_nre": 96,
    "G3 Fall_ss_nre": 129,
    "G3 Fall_dss_nre": 797,
    "G3 Winter_theta_nre": 2.798250523,
    "G3 Winter_pr_1_99_nre": 96,
    "G3 Winter_ss_nre": 128,
    "G3 Winter_dss_nre": 780,
    "G3 Spring_theta_nre": 2.949788959,
    "G3 Spring_pr_1_99_nre": 96,
    "G3 Spring_ss_nre": 127,
    "G3 Spring_dss_nre": 795
  },
  {
    "ID": 483,
    "KG Winter_theta_nre": 0.576378779,
    "KG Winter_pr_1_99_nre": 97,
    "KG Winter_ss_nre": 127,
    "KG Winter_dss_nre": 558,
    "KG Spring_theta_nre": 0.920971341,
    "KG Spring_pr_1_99_nre": 97,
    "KG Spring_ss_nre": 126,
    "KG Spring_dss_nre": 592,
    "G1 Fall_theta_nre": 1.278869259,
    "G1 Fall_pr_1_99_nre": 97,
    "G1 Fall_ss_nre": 128,
    "G1 Fall_dss_nre": 628,
    "G1 Winter_theta_nre": 1.421723677,
    "G1 Winter_pr_1_99_nre": 97,
    "G1 Winter_ss_nre": 128,
    "G1 Winter_dss_nre": 642,
    "G1 Spring_theta_nre": 1.706277345,
    "G1 Spring_pr_1_99_nre": 97,
    "G1 Spring_ss_nre": 129,
    "G1 Spring_dss_nre": 671,
    "G2 Fall_theta_nre": 1.884172113,
    "G2 Fall_pr_1_99_nre": 97,
    "G2 Fall_ss_nre": 126,
    "G2 Fall_dss_nre": 688,
    "G2 Winter_theta_nre": 2.072327641,
    "G2 Winter_pr_1_99_nre": 97,
    "G2 Winter_ss_nre": 128,
    "G2 Winter_dss_nre": 707,
    "G2 Spring_theta_nre": 2.589134102,
    "G2 Spring_pr_1_99_nre": 97,
    "G2 Spring_ss_nre": 128,
    "G2 Spring_dss_nre": 759,
    "G3 Fall_theta_nre": 2.967478509,
    "G3 Fall_pr_1_99_nre": 97,
    "G3 Fall_ss_nre": 129,
    "G3 Fall_dss_nre": 797,
    "G3 Winter_theta_nre": 2.807895715,
    "G3 Winter_pr_1_99_nre": 97,
    "G3 Winter_ss_nre": 128,
    "G3 Winter_dss_nre": 781,
    "G3 Spring_theta_nre": 2.970547044,
    "G3 Spring_pr_1_99_nre": 97,
    "G3 Spring_ss_nre": 127,
    "G3 Spring_dss_nre": 797
  },
  {
    "ID": 484,
    "KG Winter_theta_nre": 0.583295892,
    "KG Winter_pr_1_99_nre": 97,
    "KG Winter_ss_nre": 127,
    "KG Winter_dss_nre": 558,
    "KG Spring_theta_nre": 0.951656576,
    "KG Spring_pr_1_99_nre": 97,
    "KG Spring_ss_nre": 127,
    "KG Spring_dss_nre": 595,
    "G1 Fall_theta_nre": 1.457548995,
    "G1 Fall_pr_1_99_nre": 97,
    "G1 Fall_ss_nre": 131,
    "G1 Fall_dss_nre": 646,
    "G1 Winter_theta_nre": 1.434005875,
    "G1 Winter_pr_1_99_nre": 97,
    "G1 Winter_ss_nre": 128,
    "G1 Winter_dss_nre": 643,
    "G1 Spring_theta_nre": 1.731996367,
    "G1 Spring_pr_1_99_nre": 97,
    "G1 Spring_ss_nre": 129,
    "G1 Spring_dss_nre": 673,
    "G2 Fall_theta_nre": 1.947008833,
    "G2 Fall_pr_1_99_nre": 97,
    "G2 Fall_ss_nre": 127,
    "G2 Fall_dss_nre": 695,
    "G2 Winter_theta_nre": 2.108339125,
    "G2 Winter_pr_1_99_nre": 97,
    "G2 Winter_ss_nre": 129,
    "G2 Winter_dss_nre": 711,
    "G2 Spring_theta_nre": 2.590890265,
    "G2 Spring_pr_1_99_nre": 97,
    "G2 Spring_ss_nre": 128,
    "G2 Spring_dss_nre": 759,
    "G3 Fall_theta_nre": 2.977521965,
    "G3 Fall_pr_1_99_nre": 97,
    "G3 Fall_ss_nre": 129,
    "G3 Fall_dss_nre": 798,
    "G3 Winter_theta_nre": 2.824329741,
    "G3 Winter_pr_1_99_nre": 97,
    "G3 Winter_ss_nre": 129,
    "G3 Winter_dss_nre": 782,
    "G3 Spring_theta_nre": 2.999532366,
    "G3 Spring_pr_1_99_nre": 97,
    "G3 Spring_ss_nre": 128,
    "G3 Spring_dss_nre": 800
  },
  {
    "ID": 485,
    "KG Winter_theta_nre": 0.604295587,
    "KG Winter_pr_1_99_nre": 97,
    "KG Winter_ss_nre": 127,
    "KG Winter_dss_nre": 560,
    "KG Spring_theta_nre": 0.970772353,
    "KG Spring_pr_1_99_nre": 97,
    "KG Spring_ss_nre": 127,
    "KG Spring_dss_nre": 597,
    "G1 Fall_theta_nre": 1.462005675,
    "G1 Fall_pr_1_99_nre": 97,
    "G1 Fall_ss_nre": 131,
    "G1 Fall_dss_nre": 646,
    "G1 Winter_theta_nre": 1.46261092,
    "G1 Winter_pr_1_99_nre": 97,
    "G1 Winter_ss_nre": 128,
    "G1 Winter_dss_nre": 646,
    "G1 Spring_theta_nre": 1.761125835,
    "G1 Spring_pr_1_99_nre": 97,
    "G1 Spring_ss_nre": 130,
    "G1 Spring_dss_nre": 676,
    "G2 Fall_theta_nre": 1.951858956,
    "G2 Fall_pr_1_99_nre": 97,
    "G2 Fall_ss_nre": 127,
    "G2 Fall_dss_nre": 695,
    "G2 Winter_theta_nre": 2.109731454,
    "G2 Winter_pr_1_99_nre": 97,
    "G2 Winter_ss_nre": 129,
    "G2 Winter_dss_nre": 711,
    "G2 Spring_theta_nre": 2.602127322,
    "G2 Spring_pr_1_99_nre": 97,
    "G2 Spring_ss_nre": 129,
    "G2 Spring_dss_nre": 760,
    "G3 Fall_theta_nre": 3.000914498,
    "G3 Fall_pr_1_99_nre": 97,
    "G3 Fall_ss_nre": 129,
    "G3 Fall_dss_nre": 800,
    "G3 Winter_theta_nre": 2.841140562,
    "G3 Winter_pr_1_99_nre": 97,
    "G3 Winter_ss_nre": 129,
    "G3 Winter_dss_nre": 784,
    "G3 Spring_theta_nre": 3.092204233,
    "G3 Spring_pr_1_99_nre": 97,
    "G3 Spring_ss_nre": 130,
    "G3 Spring_dss_nre": 809
  },
  {
    "ID": 486,
    "KG Winter_theta_nre": 0.611696908,
    "KG Winter_pr_1_99_nre": 97,
    "KG Winter_ss_nre": 127,
    "KG Winter_dss_nre": 561,
    "KG Spring_theta_nre": 1.018482829,
    "KG Spring_pr_1_99_nre": 97,
    "KG Spring_ss_nre": 128,
    "KG Spring_dss_nre": 602,
    "G1 Fall_theta_nre": 1.488306006,
    "G1 Fall_pr_1_99_nre": 97,
    "G1 Fall_ss_nre": 131,
    "G1 Fall_dss_nre": 649,
    "G1 Winter_theta_nre": 1.502559819,
    "G1 Winter_pr_1_99_nre": 97,
    "G1 Winter_ss_nre": 129,
    "G1 Winter_dss_nre": 650,
    "G1 Spring_theta_nre": 1.788608691,
    "G1 Spring_pr_1_99_nre": 97,
    "G1 Spring_ss_nre": 130,
    "G1 Spring_dss_nre": 679,
    "G2 Fall_theta_nre": 2.0046937,
    "G2 Fall_pr_1_99_nre": 97,
    "G2 Fall_ss_nre": 128,
    "G2 Fall_dss_nre": 700,
    "G2 Winter_theta_nre": 2.121828931,
    "G2 Winter_pr_1_99_nre": 97,
    "G2 Winter_ss_nre": 129,
    "G2 Winter_dss_nre": 712,
    "G2 Spring_theta_nre": 2.622655324,
    "G2 Spring_pr_1_99_nre": 97,
    "G2 Spring_ss_nre": 129,
    "G2 Spring_dss_nre": 762,
    "G3 Fall_theta_nre": 3.006840262,
    "G3 Fall_pr_1_99_nre": 97,
    "G3 Fall_ss_nre": 129,
    "G3 Fall_dss_nre": 801,
    "G3 Winter_theta_nre": 2.863545634,
    "G3 Winter_pr_1_99_nre": 97,
    "G3 Winter_ss_nre": 130,
    "G3 Winter_dss_nre": 786,
    "G3 Spring_theta_nre": 3.100116065,
    "G3 Spring_pr_1_99_nre": 97,
    "G3 Spring_ss_nre": 130,
    "G3 Spring_dss_nre": 810
  },
  {
    "ID": 487,
    "KG Winter_theta_nre": 0.621921563,
    "KG Winter_pr_1_99_nre": 97,
    "KG Winter_ss_nre": 128,
    "KG Winter_dss_nre": 562,
    "KG Spring_theta_nre": 1.022617771,
    "KG Spring_pr_1_99_nre": 97,
    "KG Spring_ss_nre": 128,
    "KG Spring_dss_nre": 602,
    "G1 Fall_theta_nre": 1.51121794,
    "G1 Fall_pr_1_99_nre": 97,
    "G1 Fall_ss_nre": 132,
    "G1 Fall_dss_nre": 651,
    "G1 Winter_theta_nre": 1.537838936,
    "G1 Winter_pr_1_99_nre": 97,
    "G1 Winter_ss_nre": 130,
    "G1 Winter_dss_nre": 654,
    "G1 Spring_theta_nre": 1.793846229,
    "G1 Spring_pr_1_99_nre": 97,
    "G1 Spring_ss_nre": 130,
    "G1 Spring_dss_nre": 679,
    "G2 Fall_theta_nre": 2.007666725,
    "G2 Fall_pr_1_99_nre": 97,
    "G2 Fall_ss_nre": 128,
    "G2 Fall_dss_nre": 701,
    "G2 Winter_theta_nre": 2.166940454,
    "G2 Winter_pr_1_99_nre": 97,
    "G2 Winter_ss_nre": 130,
    "G2 Winter_dss_nre": 717,
    "G2 Spring_theta_nre": 2.635617375,
    "G2 Spring_pr_1_99_nre": 97,
    "G2 Spring_ss_nre": 129,
    "G2 Spring_dss_nre": 764,
    "G3 Fall_theta_nre": 3.092124138,
    "G3 Fall_pr_1_99_nre": 97,
    "G3 Fall_ss_nre": 131,
    "G3 Fall_dss_nre": 809,
    "G3 Winter_theta_nre": 2.885278776,
    "G3 Winter_pr_1_99_nre": 97,
    "G3 Winter_ss_nre": 130,
    "G3 Winter_dss_nre": 789,
    "G3 Spring_theta_nre": 3.104629935,
    "G3 Spring_pr_1_99_nre": 97,
    "G3 Spring_ss_nre": 130,
    "G3 Spring_dss_nre": 810
  },
  {
    "ID": 488,
    "KG Winter_theta_nre": 0.623840535,
    "KG Winter_pr_1_99_nre": 98,
    "KG Winter_ss_nre": 128,
    "KG Winter_dss_nre": 562,
    "KG Spring_theta_nre": 1.04759824,
    "KG Spring_pr_1_99_nre": 98,
    "KG Spring_ss_nre": 128,
    "KG Spring_dss_nre": 605,
    "G1 Fall_theta_nre": 1.512212588,
    "G1 Fall_pr_1_99_nre": 98,
    "G1 Fall_ss_nre": 132,
    "G1 Fall_dss_nre": 651,
    "G1 Winter_theta_nre": 1.579254004,
    "G1 Winter_pr_1_99_nre": 98,
    "G1 Winter_ss_nre": 130,
    "G1 Winter_dss_nre": 658,
    "G1 Spring_theta_nre": 1.809328874,
    "G1 Spring_pr_1_99_nre": 98,
    "G1 Spring_ss_nre": 131,
    "G1 Spring_dss_nre": 681,
    "G2 Fall_theta_nre": 2.01739596,
    "G2 Fall_pr_1_99_nre": 98,
    "G2 Fall_ss_nre": 128,
    "G2 Fall_dss_nre": 702,
    "G2 Winter_theta_nre": 2.220631176,
    "G2 Winter_pr_1_99_nre": 98,
    "G2 Winter_ss_nre": 131,
    "G2 Winter_dss_nre": 722,
    "G2 Spring_theta_nre": 2.676267029,
    "G2 Spring_pr_1_99_nre": 98,
    "G2 Spring_ss_nre": 130,
    "G2 Spring_dss_nre": 768,
    "G3 Fall_theta_nre": 3.114787269,
    "G3 Fall_pr_1_99_nre": 98,
    "G3 Fall_ss_nre": 131,
    "G3 Fall_dss_nre": 811,
    "G3 Winter_theta_nre": 2.909245744,
    "G3 Winter_pr_1_99_nre": 98,
    "G3 Winter_ss_nre": 130,
    "G3 Winter_dss_nre": 791,
    "G3 Spring_theta_nre": 3.12207295,
    "G3 Spring_pr_1_99_nre": 98,
    "G3 Spring_ss_nre": 131,
    "G3 Spring_dss_nre": 812
  },
  {
    "ID": 489,
    "KG Winter_theta_nre": 0.661068095,
    "KG Winter_pr_1_99_nre": 98,
    "KG Winter_ss_nre": 128,
    "KG Winter_dss_nre": 566,
    "KG Spring_theta_nre": 1.049478621,
    "KG Spring_pr_1_99_nre": 98,
    "KG Spring_ss_nre": 128,
    "KG Spring_dss_nre": 605,
    "G1 Fall_theta_nre": 1.53388376,
    "G1 Fall_pr_1_99_nre": 98,
    "G1 Fall_ss_nre": 132,
    "G1 Fall_dss_nre": 653,
    "G1 Winter_theta_nre": 1.620006746,
    "G1 Winter_pr_1_99_nre": 98,
    "G1 Winter_ss_nre": 131,
    "G1 Winter_dss_nre": 662,
    "G1 Spring_theta_nre": 1.815311605,
    "G1 Spring_pr_1_99_nre": 98,
    "G1 Spring_ss_nre": 131,
    "G1 Spring_dss_nre": 682,
    "G2 Fall_theta_nre": 2.092176692,
    "G2 Fall_pr_1_99_nre": 98,
    "G2 Fall_ss_nre": 130,
    "G2 Fall_dss_nre": 709,
    "G2 Winter_theta_nre": 2.24668141,
    "G2 Winter_pr_1_99_nre": 98,
    "G2 Winter_ss_nre": 131,
    "G2 Winter_dss_nre": 725,
    "G2 Spring_theta_nre": 2.679586816,
    "G2 Spring_pr_1_99_nre": 98,
    "G2 Spring_ss_nre": 130,
    "G2 Spring_dss_nre": 768,
    "G3 Fall_theta_nre": 3.129909964,
    "G3 Fall_pr_1_99_nre": 98,
    "G3 Fall_ss_nre": 131,
    "G3 Fall_dss_nre": 813,
    "G3 Winter_theta_nre": 2.912913663,
    "G3 Winter_pr_1_99_nre": 98,
    "G3 Winter_ss_nre": 131,
    "G3 Winter_dss_nre": 791,
    "G3 Spring_theta_nre": 3.135119596,
    "G3 Spring_pr_1_99_nre": 98,
    "G3 Spring_ss_nre": 131,
    "G3 Spring_dss_nre": 814
  },
  {
    "ID": 490,
    "KG Winter_theta_nre": 0.665037277,
    "KG Winter_pr_1_99_nre": 98,
    "KG Winter_ss_nre": 128,
    "KG Winter_dss_nre": 567,
    "KG Spring_theta_nre": 1.061031208,
    "KG Spring_pr_1_99_nre": 98,
    "KG Spring_ss_nre": 129,
    "KG Spring_dss_nre": 606,
    "G1 Fall_theta_nre": 1.567384258,
    "G1 Fall_pr_1_99_nre": 98,
    "G1 Fall_ss_nre": 133,
    "G1 Fall_dss_nre": 657,
    "G1 Winter_theta_nre": 1.648235732,
    "G1 Winter_pr_1_99_nre": 98,
    "G1 Winter_ss_nre": 132,
    "G1 Winter_dss_nre": 665,
    "G1 Spring_theta_nre": 1.818002472,
    "G1 Spring_pr_1_99_nre": 98,
    "G1 Spring_ss_nre": 131,
    "G1 Spring_dss_nre": 682,
    "G2 Fall_theta_nre": 2.109650136,
    "G2 Fall_pr_1_99_nre": 98,
    "G2 Fall_ss_nre": 130,
    "G2 Fall_dss_nre": 711,
    "G2 Winter_theta_nre": 2.252261945,
    "G2 Winter_pr_1_99_nre": 98,
    "G2 Winter_ss_nre": 132,
    "G2 Winter_dss_nre": 725,
    "G2 Spring_theta_nre": 2.679618966,
    "G2 Spring_pr_1_99_nre": 98,
    "G2 Spring_ss_nre": 130,
    "G2 Spring_dss_nre": 768,
    "G3 Fall_theta_nre": 3.245684358,
    "G3 Fall_pr_1_99_nre": 98,
    "G3 Fall_ss_nre": 133,
    "G3 Fall_dss_nre": 825,
    "G3 Winter_theta_nre": 2.926017783,
    "G3 Winter_pr_1_99_nre": 98,
    "G3 Winter_ss_nre": 131,
    "G3 Winter_dss_nre": 793,
    "G3 Spring_theta_nre": 3.158384725,
    "G3 Spring_pr_1_99_nre": 98,
    "G3 Spring_ss_nre": 131,
    "G3 Spring_dss_nre": 816
  },
  {
    "ID": 491,
    "KG Winter_theta_nre": 0.679621826,
    "KG Winter_pr_1_99_nre": 98,
    "KG Winter_ss_nre": 129,
    "KG Winter_dss_nre": 568,
    "KG Spring_theta_nre": 1.094607308,
    "KG Spring_pr_1_99_nre": 98,
    "KG Spring_ss_nre": 129,
    "KG Spring_dss_nre": 609,
    "G1 Fall_theta_nre": 1.569005908,
    "G1 Fall_pr_1_99_nre": 98,
    "G1 Fall_ss_nre": 133,
    "G1 Fall_dss_nre": 657,
    "G1 Winter_theta_nre": 1.65385319,
    "G1 Winter_pr_1_99_nre": 98,
    "G1 Winter_ss_nre": 132,
    "G1 Winter_dss_nre": 665,
    "G1 Spring_theta_nre": 1.915228218,
    "G1 Spring_pr_1_99_nre": 98,
    "G1 Spring_ss_nre": 133,
    "G1 Spring_dss_nre": 692,
    "G2 Fall_theta_nre": 2.159703171,
    "G2 Fall_pr_1_99_nre": 98,
    "G2 Fall_ss_nre": 131,
    "G2 Fall_dss_nre": 716,
    "G2 Winter_theta_nre": 2.259502833,
    "G2 Winter_pr_1_99_nre": 98,
    "G2 Winter_ss_nre": 132,
    "G2 Winter_dss_nre": 726,
    "G2 Spring_theta_nre": 2.763990335,
    "G2 Spring_pr_1_99_nre": 98,
    "G2 Spring_ss_nre": 131,
    "G2 Spring_dss_nre": 776,
    "G3 Fall_theta_nre": 3.288397404,
    "G3 Fall_pr_1_99_nre": 98,
    "G3 Fall_ss_nre": 134,
    "G3 Fall_dss_nre": 829,
    "G3 Winter_theta_nre": 2.930092849,
    "G3 Winter_pr_1_99_nre": 98,
    "G3 Winter_ss_nre": 131,
    "G3 Winter_dss_nre": 793,
    "G3 Spring_theta_nre": 3.158387197,
    "G3 Spring_pr_1_99_nre": 98,
    "G3 Spring_ss_nre": 131,
    "G3 Spring_dss_nre": 816
  },
  {
    "ID": 492,
    "KG Winter_theta_nre": 0.714311835,
    "KG Winter_pr_1_99_nre": 98,
    "KG Winter_ss_nre": 129,
    "KG Winter_dss_nre": 571,
    "KG Spring_theta_nre": 1.116124675,
    "KG Spring_pr_1_99_nre": 98,
    "KG Spring_ss_nre": 130,
    "KG Spring_dss_nre": 612,
    "G1 Fall_theta_nre": 1.572268545,
    "G1 Fall_pr_1_99_nre": 98,
    "G1 Fall_ss_nre": 133,
    "G1 Fall_dss_nre": 657,
    "G1 Winter_theta_nre": 1.657490103,
    "G1 Winter_pr_1_99_nre": 98,
    "G1 Winter_ss_nre": 132,
    "G1 Winter_dss_nre": 666,
    "G1 Spring_theta_nre": 1.969472562,
    "G1 Spring_pr_1_99_nre": 98,
    "G1 Spring_ss_nre": 134,
    "G1 Spring_dss_nre": 697,
    "G2 Fall_theta_nre": 2.225874957,
    "G2 Fall_pr_1_99_nre": 98,
    "G2 Fall_ss_nre": 132,
    "G2 Fall_dss_nre": 723,
    "G2 Winter_theta_nre": 2.283352714,
    "G2 Winter_pr_1_99_nre": 98,
    "G2 Winter_ss_nre": 132,
    "G2 Winter_dss_nre": 728,
    "G2 Spring_theta_nre": 2.83975543,
    "G2 Spring_pr_1_99_nre": 98,
    "G2 Spring_ss_nre": 133,
    "G2 Spring_dss_nre": 784,
    "G3 Fall_theta_nre": 3.33528924,
    "G3 Fall_pr_1_99_nre": 98,
    "G3 Fall_ss_nre": 135,
    "G3 Fall_dss_nre": 834,
    "G3 Winter_theta_nre": 2.97939787,
    "G3 Winter_pr_1_99_nre": 98,
    "G3 Winter_ss_nre": 132,
    "G3 Winter_dss_nre": 798,
    "G3 Spring_theta_nre": 3.191737952,
    "G3 Spring_pr_1_99_nre": 98,
    "G3 Spring_ss_nre": 132,
    "G3 Spring_dss_nre": 819
  },
  {
    "ID": 493,
    "KG Winter_theta_nre": 0.761001971,
    "KG Winter_pr_1_99_nre": 99,
    "KG Winter_ss_nre": 130,
    "KG Winter_dss_nre": 576,
    "KG Spring_theta_nre": 1.129036698,
    "KG Spring_pr_1_99_nre": 99,
    "KG Spring_ss_nre": 130,
    "KG Spring_dss_nre": 613,
    "G1 Fall_theta_nre": 1.57324484,
    "G1 Fall_pr_1_99_nre": 99,
    "G1 Fall_ss_nre": 133,
    "G1 Fall_dss_nre": 657,
    "G1 Winter_theta_nre": 1.72682737,
    "G1 Winter_pr_1_99_nre": 99,
    "G1 Winter_ss_nre": 133,
    "G1 Winter_dss_nre": 673,
    "G1 Spring_theta_nre": 1.989169992,
    "G1 Spring_pr_1_99_nre": 99,
    "G1 Spring_ss_nre": 134,
    "G1 Spring_dss_nre": 699,
    "G2 Fall_theta_nre": 2.236941021,
    "G2 Fall_pr_1_99_nre": 99,
    "G2 Fall_ss_nre": 132,
    "G2 Fall_dss_nre": 724,
    "G2 Winter_theta_nre": 2.287752087,
    "G2 Winter_pr_1_99_nre": 99,
    "G2 Winter_ss_nre": 132,
    "G2 Winter_dss_nre": 729,
    "G2 Spring_theta_nre": 2.844586515,
    "G2 Spring_pr_1_99_nre": 99,
    "G2 Spring_ss_nre": 133,
    "G2 Spring_dss_nre": 784,
    "G3 Fall_theta_nre": 3.365448124,
    "G3 Fall_pr_1_99_nre": 99,
    "G3 Fall_ss_nre": 135,
    "G3 Fall_dss_nre": 837,
    "G3 Winter_theta_nre": 3.00249288,
    "G3 Winter_pr_1_99_nre": 99,
    "G3 Winter_ss_nre": 132,
    "G3 Winter_dss_nre": 800,
    "G3 Spring_theta_nre": 3.241954663,
    "G3 Spring_pr_1_99_nre": 99,
    "G3 Spring_ss_nre": 133,
    "G3 Spring_dss_nre": 824
  },
  {
    "ID": 494,
    "KG Winter_theta_nre": 0.786430306,
    "KG Winter_pr_1_99_nre": 99,
    "KG Winter_ss_nre": 131,
    "KG Winter_dss_nre": 579,
    "KG Spring_theta_nre": 1.129721501,
    "KG Spring_pr_1_99_nre": 99,
    "KG Spring_ss_nre": 130,
    "KG Spring_dss_nre": 613,
    "G1 Fall_theta_nre": 1.601817917,
    "G1 Fall_pr_1_99_nre": 99,
    "G1 Fall_ss_nre": 133,
    "G1 Fall_dss_nre": 660,
    "G1 Winter_theta_nre": 1.755179017,
    "G1 Winter_pr_1_99_nre": 99,
    "G1 Winter_ss_nre": 134,
    "G1 Winter_dss_nre": 676,
    "G1 Spring_theta_nre": 2.047282145,
    "G1 Spring_pr_1_99_nre": 99,
    "G1 Spring_ss_nre": 135,
    "G1 Spring_dss_nre": 705,
    "G2 Fall_theta_nre": 2.291431165,
    "G2 Fall_pr_1_99_nre": 99,
    "G2 Fall_ss_nre": 133,
    "G2 Fall_dss_nre": 729,
    "G2 Winter_theta_nre": 2.321586466,
    "G2 Winter_pr_1_99_nre": 99,
    "G2 Winter_ss_nre": 133,
    "G2 Winter_dss_nre": 732,
    "G2 Spring_theta_nre": 2.89387901,
    "G2 Spring_pr_1_99_nre": 99,
    "G2 Spring_ss_nre": 134,
    "G2 Spring_dss_nre": 789,
    "G3 Fall_theta_nre": 3.387582869,
    "G3 Fall_pr_1_99_nre": 99,
    "G3 Fall_ss_nre": 136,
    "G3 Fall_dss_nre": 839,
    "G3 Winter_theta_nre": 3.058091814,
    "G3 Winter_pr_1_99_nre": 99,
    "G3 Winter_ss_nre": 133,
    "G3 Winter_dss_nre": 806,
    "G3 Spring_theta_nre": 3.315015491,
    "G3 Spring_pr_1_99_nre": 99,
    "G3 Spring_ss_nre": 135,
    "G3 Spring_dss_nre": 832
  },
  {
    "ID": 495,
    "KG Winter_theta_nre": 0.819582267,
    "KG Winter_pr_1_99_nre": 99,
    "KG Winter_ss_nre": 131,
    "KG Winter_dss_nre": 582,
    "KG Spring_theta_nre": 1.131161143,
    "KG Spring_pr_1_99_nre": 99,
    "KG Spring_ss_nre": 130,
    "KG Spring_dss_nre": 613,
    "G1 Fall_theta_nre": 1.627558541,
    "G1 Fall_pr_1_99_nre": 99,
    "G1 Fall_ss_nre": 134,
    "G1 Fall_dss_nre": 663,
    "G1 Winter_theta_nre": 1.766673102,
    "G1 Winter_pr_1_99_nre": 99,
    "G1 Winter_ss_nre": 134,
    "G1 Winter_dss_nre": 677,
    "G1 Spring_theta_nre": 2.062563391,
    "G1 Spring_pr_1_99_nre": 99,
    "G1 Spring_ss_nre": 135,
    "G1 Spring_dss_nre": 706,
    "G2 Fall_theta_nre": 2.30434153,
    "G2 Fall_pr_1_99_nre": 99,
    "G2 Fall_ss_nre": 133,
    "G2 Fall_dss_nre": 730,
    "G2 Winter_theta_nre": 2.352905511,
    "G2 Winter_pr_1_99_nre": 99,
    "G2 Winter_ss_nre": 134,
    "G2 Winter_dss_nre": 735,
    "G2 Spring_theta_nre": 2.907933099,
    "G2 Spring_pr_1_99_nre": 99,
    "G2 Spring_ss_nre": 134,
    "G2 Spring_dss_nre": 791,
    "G3 Fall_theta_nre": 3.509644664,
    "G3 Fall_pr_1_99_nre": 99,
    "G3 Fall_ss_nre": 138,
    "G3 Fall_dss_nre": 851,
    "G3 Winter_theta_nre": 3.065870446,
    "G3 Winter_pr_1_99_nre": 99,
    "G3 Winter_ss_nre": 134,
    "G3 Winter_dss_nre": 807,
    "G3 Spring_theta_nre": 3.321025679,
    "G3 Spring_pr_1_99_nre": 99,
    "G3 Spring_ss_nre": 135,
    "G3 Spring_dss_nre": 832
  },
  {
    "ID": 496,
    "KG Winter_theta_nre": 0.843335869,
    "KG Winter_pr_1_99_nre": 99,
    "KG Winter_ss_nre": 132,
    "KG Winter_dss_nre": 584,
    "KG Spring_theta_nre": 1.157378071,
    "KG Spring_pr_1_99_nre": 99,
    "KG Spring_ss_nre": 130,
    "KG Spring_dss_nre": 616,
    "G1 Fall_theta_nre": 1.648901506,
    "G1 Fall_pr_1_99_nre": 99,
    "G1 Fall_ss_nre": 134,
    "G1 Fall_dss_nre": 665,
    "G1 Winter_theta_nre": 1.768020533,
    "G1 Winter_pr_1_99_nre": 99,
    "G1 Winter_ss_nre": 134,
    "G1 Winter_dss_nre": 677,
    "G1 Spring_theta_nre": 2.306300531,
    "G1 Spring_pr_1_99_nre": 99,
    "G1 Spring_ss_nre": 140,
    "G1 Spring_dss_nre": 731,
    "G2 Fall_theta_nre": 2.375922042,
    "G2 Fall_pr_1_99_nre": 99,
    "G2 Fall_ss_nre": 135,
    "G2 Fall_dss_nre": 738,
    "G2 Winter_theta_nre": 2.398921112,
    "G2 Winter_pr_1_99_nre": 99,
    "G2 Winter_ss_nre": 134,
    "G2 Winter_dss_nre": 740,
    "G2 Spring_theta_nre": 3.013123926,
    "G2 Spring_pr_1_99_nre": 99,
    "G2 Spring_ss_nre": 136,
    "G2 Spring_dss_nre": 801,
    "G3 Fall_theta_nre": 3.520279409,
    "G3 Fall_pr_1_99_nre": 99,
    "G3 Fall_ss_nre": 138,
    "G3 Fall_dss_nre": 852,
    "G3 Winter_theta_nre": 3.086096807,
    "G3 Winter_pr_1_99_nre": 99,
    "G3 Winter_ss_nre": 134,
    "G3 Winter_dss_nre": 809,
    "G3 Spring_theta_nre": 3.394487044,
    "G3 Spring_pr_1_99_nre": 99,
    "G3 Spring_ss_nre": 137,
    "G3 Spring_dss_nre": 839
  },
  {
    "ID": 497,
    "KG Winter_theta_nre": 0.858572984,
    "KG Winter_pr_1_99_nre": 99,
    "KG Winter_ss_nre": 132,
    "KG Winter_dss_nre": 586,
    "KG Spring_theta_nre": 1.379850488,
    "KG Spring_pr_1_99_nre": 99,
    "KG Spring_ss_nre": 134,
    "KG Spring_dss_nre": 638,
    "G1 Fall_theta_nre": 1.656755717,
    "G1 Fall_pr_1_99_nre": 99,
    "G1 Fall_ss_nre": 134,
    "G1 Fall_dss_nre": 666,
    "G1 Winter_theta_nre": 1.941176359,
    "G1 Winter_pr_1_99_nre": 99,
    "G1 Winter_ss_nre": 137,
    "G1 Winter_dss_nre": 694,
    "G1 Spring_theta_nre": 2.328586402,
    "G1 Spring_pr_1_99_nre": 99,
    "G1 Spring_ss_nre": 140,
    "G1 Spring_dss_nre": 733,
    "G2 Fall_theta_nre": 2.438931727,
    "G2 Fall_pr_1_99_nre": 99,
    "G2 Fall_ss_nre": 136,
    "G2 Fall_dss_nre": 744,
    "G2 Winter_theta_nre": 2.490828183,
    "G2 Winter_pr_1_99_nre": 99,
    "G2 Winter_ss_nre": 136,
    "G2 Winter_dss_nre": 749,
    "G2 Spring_theta_nre": 3.118732719,
    "G2 Spring_pr_1_99_nre": 99,
    "G2 Spring_ss_nre": 137,
    "G2 Spring_dss_nre": 812,
    "G3 Fall_theta_nre": 3.705435099,
    "G3 Fall_pr_1_99_nre": 99,
    "G3 Fall_ss_nre": 141,
    "G3 Fall_dss_nre": 871,
    "G3 Winter_theta_nre": 3.201950263,
    "G3 Winter_pr_1_99_nre": 99,
    "G3 Winter_ss_nre": 136,
    "G3 Winter_dss_nre": 820,
    "G3 Spring_theta_nre": 3.541465181,
    "G3 Spring_pr_1_99_nre": 99,
    "G3 Spring_ss_nre": 140,
    "G3 Spring_dss_nre": 854
  },
  {
    "ID": 498,
    "KG Winter_theta_nre": 0.915205076,
    "KG Winter_pr_1_99_nre": 99,
    "KG Winter_ss_nre": 133,
    "KG Winter_dss_nre": 592,
    "KG Spring_theta_nre": 1.473830896,
    "KG Spring_pr_1_99_nre": 99,
    "KG Spring_ss_nre": 136,
    "KG Spring_dss_nre": 647,
    "G1 Fall_theta_nre": 1.746906745,
    "G1 Fall_pr_1_99_nre": 99,
    "G1 Fall_ss_nre": 136,
    "G1 Fall_dss_nre": 675,
    "G1 Winter_theta_nre": 1.96165478,
    "G1 Winter_pr_1_99_nre": 99,
    "G1 Winter_ss_nre": 137,
    "G1 Winter_dss_nre": 696,
    "G1 Spring_theta_nre": 2.451213504,
    "G1 Spring_pr_1_99_nre": 99,
    "G1 Spring_ss_nre": 143,
    "G1 Spring_dss_nre": 745,
    "G2 Fall_theta_nre": 2.529773805,
    "G2 Fall_pr_1_99_nre": 99,
    "G2 Fall_ss_nre": 137,
    "G2 Fall_dss_nre": 753,
    "G2 Winter_theta_nre": 2.517903191,
    "G2 Winter_pr_1_99_nre": 99,
    "G2 Winter_ss_nre": 137,
    "G2 Winter_dss_nre": 752,
    "G2 Spring_theta_nre": 3.175298406,
    "G2 Spring_pr_1_99_nre": 99,
    "G2 Spring_ss_nre": 138,
    "G2 Spring_dss_nre": 818,
    "G3 Fall_theta_nre": 3.705586832,
    "G3 Fall_pr_1_99_nre": 99,
    "G3 Fall_ss_nre": 141,
    "G3 Fall_dss_nre": 871,
    "G3 Winter_theta_nre": 3.327534744,
    "G3 Winter_pr_1_99_nre": 99,
    "G3 Winter_ss_nre": 139,
    "G3 Winter_dss_nre": 833,
    "G3 Spring_theta_nre": 3.63481269,
    "G3 Spring_pr_1_99_nre": 99,
    "G3 Spring_ss_nre": 142,
    "G3 Spring_dss_nre": 863
  },
  {
    "ID": 499,
    "KG Winter_theta_nre": 1.000336741,
    "KG Winter_pr_1_99_nre": 99,
    "KG Winter_ss_nre": 134,
    "KG Winter_dss_nre": 600,
    "KG Spring_theta_nre": 1.674715235,
    "KG Spring_pr_1_99_nre": 99,
    "KG Spring_ss_nre": 139,
    "KG Spring_dss_nre": 667,
    "G1 Fall_theta_nre": 1.981325186,
    "G1 Fall_pr_1_99_nre": 99,
    "G1 Fall_ss_nre": 140,
    "G1 Fall_dss_nre": 698,
    "G1 Winter_theta_nre": 2.032747789,
    "G1 Winter_pr_1_99_nre": 99,
    "G1 Winter_ss_nre": 138,
    "G1 Winter_dss_nre": 703,
    "G1 Spring_theta_nre": 2.568723877,
    "G1 Spring_pr_1_99_nre": 99,
    "G1 Spring_ss_nre": 145,
    "G1 Spring_dss_nre": 757,
    "G2 Fall_theta_nre": 2.786891847,
    "G2 Fall_pr_1_99_nre": 99,
    "G2 Fall_ss_nre": 142,
    "G2 Fall_dss_nre": 779,
    "G2 Winter_theta_nre": 2.58587779,
    "G2 Winter_pr_1_99_nre": 99,
    "G2 Winter_ss_nre": 138,
    "G2 Winter_dss_nre": 759,
    "G2 Spring_theta_nre": 3.222473616,
    "G2 Spring_pr_1_99_nre": 99,
    "G2 Spring_ss_nre": 139,
    "G2 Spring_dss_nre": 822,
    "G3 Fall_theta_nre": 3.80520901,
    "G3 Fall_pr_1_99_nre": 99,
    "G3 Fall_ss_nre": 142,
    "G3 Fall_dss_nre": 881,
    "G3 Winter_theta_nre": 3.362009772,
    "G3 Winter_pr_1_99_nre": 99,
    "G3 Winter_ss_nre": 140,
    "G3 Winter_dss_nre": 836,
    "G3 Spring_theta_nre": 3.684736158,
    "G3 Spring_pr_1_99_nre": 99,
    "G3 Spring_ss_nre": 143,
    "G3 Spring_dss_nre": 868
  },
  {
    "ID": 500,
    "KG Winter_theta_nre": 1.689455175,
    "KG Winter_pr_1_99_nre": 99,
    "KG Winter_ss_nre": 147,
    "KG Winter_dss_nre": 669,
    "KG Spring_theta_nre": 2.096355761,
    "KG Spring_pr_1_99_nre": 99,
    "KG Spring_ss_nre": 146,
    "KG Spring_dss_nre": 710,
    "G1 Fall_theta_nre": 2.19874462,
    "G1 Fall_pr_1_99_nre": 99,
    "G1 Fall_ss_nre": 143,
    "G1 Fall_dss_nre": 720,
    "G1 Winter_theta_nre": 2.072466598,
    "G1 Winter_pr_1_99_nre": 99,
    "G1 Winter_ss_nre": 139,
    "G1 Winter_dss_nre": 707,
    "G1 Spring_theta_nre": 2.681650184,
    "G1 Spring_pr_1_99_nre": 99,
    "G1 Spring_ss_nre": 147,
    "G1 Spring_dss_nre": 768,
    "G2 Fall_theta_nre": 3.116849981,
    "G2 Fall_pr_1_99_nre": 99,
    "G2 Fall_ss_nre": 147,
    "G2 Fall_dss_nre": 812,
    "G2 Winter_theta_nre": 2.635661875,
    "G2 Winter_pr_1_99_nre": 99,
    "G2 Winter_ss_nre": 139,
    "G2 Winter_dss_nre": 764,
    "G2 Spring_theta_nre": 3.223416555,
    "G2 Spring_pr_1_99_nre": 99,
    "G2 Spring_ss_nre": 139,
    "G2 Spring_dss_nre": 822,
    "G3 Fall_theta_nre": 4.136465485,
    "G3 Fall_pr_1_99_nre": 99,
    "G3 Fall_ss_nre": 148,
    "G3 Fall_dss_nre": 914,
    "G3 Winter_theta_nre": 3.367508788,
    "G3 Winter_pr_1_99_nre": 99,
    "G3 Winter_ss_nre": 140,
    "G3 Winter_dss_nre": 837,
    "G3 Spring_theta_nre": 4.313858833,
    "G3 Spring_pr_1_99_nre": 99,
    "G3 Spring_ss_nre": 157,
    "G3 Spring_dss_nre": 931
  }
];

export const wre: any = [
  {
    "ID": 1,
    "KG Winter_theta_wre": -3.109808421,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 61,
    "KG Winter_dss_wre": 189,
    "KG Spring_theta_wre": -2.779274438,
    "KG Spring_pr_1_99_wre": 1,
    "KG Spring_ss_wre": 62,
    "KG Spring_dss_wre": 222,
    "G1 Fall_theta_wre": -2.897781617,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 53,
    "G1 Fall_dss_wre": 210,
    "G1 Winter_theta_wre": -2.633065593,
    "G1 Winter_pr_1_99_wre": 1,
    "G1 Winter_ss_wre": 51,
    "G1 Winter_dss_wre": 237,
    "G1 Spring_theta_wre": -2.005576279,
    "G1 Spring_pr_1_99_wre": 1,
    "G1 Spring_ss_wre": 58,
    "G1 Spring_dss_wre": 299,
    "G2 Fall_theta_wre": -2.008610285,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 53,
    "G2 Fall_dss_wre": 299,
    "G2 Winter_theta_wre": -1.670694862,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 53,
    "G2 Winter_dss_wre": 333,
    "G2 Spring_theta_wre": -1.141435625,
    "G2 Spring_pr_1_99_wre": 1,
    "G2 Spring_ss_wre": 58,
    "G2 Spring_dss_wre": 386,
    "G3 Fall_theta_wre": -0.863766212,
    "G3 Fall_pr_1_99_wre": 1,
    "G3 Fall_ss_wre": 56,
    "G3 Fall_dss_wre": 414,
    "G3 Winter_theta_wre": -0.986678151,
    "G3 Winter_pr_1_99_wre": 1,
    "G3 Winter_ss_wre": 46,
    "G3 Winter_dss_wre": 401,
    "G3 Spring_theta_wre": -0.131809478,
    "G3 Spring_pr_1_99_wre": 1,
    "G3 Spring_ss_wre": 58,
    "G3 Spring_dss_wre": 487
  },
  {
    "ID": 2,
    "KG Winter_theta_wre": -3.003816788,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 63,
    "KG Winter_dss_wre": 200,
    "KG Spring_theta_wre": -2.747693671,
    "KG Spring_pr_1_99_wre": 1,
    "KG Spring_ss_wre": 63,
    "KG Spring_dss_wre": 225,
    "G1 Fall_theta_wre": -2.813175565,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 55,
    "G1 Fall_dss_wre": 219,
    "G1 Winter_theta_wre": -2.242851114,
    "G1 Winter_pr_1_99_wre": 1,
    "G1 Winter_ss_wre": 58,
    "G1 Winter_dss_wre": 276,
    "G1 Spring_theta_wre": -1.946868218,
    "G1 Spring_pr_1_99_wre": 1,
    "G1 Spring_ss_wre": 59,
    "G1 Spring_dss_wre": 305,
    "G2 Fall_theta_wre": -1.715614765,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 58,
    "G2 Fall_dss_wre": 328,
    "G2 Winter_theta_wre": -1.249196358,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 61,
    "G2 Winter_dss_wre": 375,
    "G2 Spring_theta_wre": -1.03685415,
    "G2 Spring_pr_1_99_wre": 1,
    "G2 Spring_ss_wre": 60,
    "G2 Spring_dss_wre": 396,
    "G3 Fall_theta_wre": -0.755662896,
    "G3 Fall_pr_1_99_wre": 1,
    "G3 Fall_ss_wre": 58,
    "G3 Fall_dss_wre": 424,
    "G3 Winter_theta_wre": -0.346873034,
    "G3 Winter_pr_1_99_wre": 1,
    "G3 Winter_ss_wre": 58,
    "G3 Winter_dss_wre": 465,
    "G3 Spring_theta_wre": -0.112979528,
    "G3 Spring_pr_1_99_wre": 2,
    "G3 Spring_ss_wre": 59,
    "G3 Spring_dss_wre": 489
  },
  {
    "ID": 3,
    "KG Winter_theta_wre": -2.906013685,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 65,
    "KG Winter_dss_wre": 209,
    "KG Spring_theta_wre": -2.599291553,
    "KG Spring_pr_1_99_wre": 1,
    "KG Spring_ss_wre": 65,
    "KG Spring_dss_wre": 240,
    "G1 Fall_theta_wre": -2.78862031,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 55,
    "G1 Fall_dss_wre": 221,
    "G1 Winter_theta_wre": -2.169530104,
    "G1 Winter_pr_1_99_wre": 1,
    "G1 Winter_ss_wre": 60,
    "G1 Winter_dss_wre": 283,
    "G1 Spring_theta_wre": -1.80047991,
    "G1 Spring_pr_1_99_wre": 1,
    "G1 Spring_ss_wre": 62,
    "G1 Spring_dss_wre": 320,
    "G2 Fall_theta_wre": -1.534536102,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 62,
    "G2 Fall_dss_wre": 347,
    "G2 Winter_theta_wre": -1.208347711,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 62,
    "G2 Winter_dss_wre": 379,
    "G2 Spring_theta_wre": -0.940914516,
    "G2 Spring_pr_1_99_wre": 1,
    "G2 Spring_ss_wre": 62,
    "G2 Spring_dss_wre": 406,
    "G3 Fall_theta_wre": -0.59531672,
    "G3 Fall_pr_1_99_wre": 1,
    "G3 Fall_ss_wre": 61,
    "G3 Fall_dss_wre": 440,
    "G3 Winter_theta_wre": -0.337447993,
    "G3 Winter_pr_1_99_wre": 1,
    "G3 Winter_ss_wre": 58,
    "G3 Winter_dss_wre": 466,
    "G3 Spring_theta_wre": -0.093884778,
    "G3 Spring_pr_1_99_wre": 3,
    "G3 Spring_ss_wre": 59,
    "G3 Spring_dss_wre": 491
  },
  {
    "ID": 4,
    "KG Winter_theta_wre": -2.867375061,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 66,
    "KG Winter_dss_wre": 213,
    "KG Spring_theta_wre": -2.594926852,
    "KG Spring_pr_1_99_wre": 1,
    "KG Spring_ss_wre": 65,
    "KG Spring_dss_wre": 241,
    "G1 Fall_theta_wre": -2.281022421,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 64,
    "G1 Fall_dss_wre": 272,
    "G1 Winter_theta_wre": -2.102751766,
    "G1 Winter_pr_1_99_wre": 1,
    "G1 Winter_ss_wre": 61,
    "G1 Winter_dss_wre": 290,
    "G1 Spring_theta_wre": -1.767019625,
    "G1 Spring_pr_1_99_wre": 1,
    "G1 Spring_ss_wre": 62,
    "G1 Spring_dss_wre": 323,
    "G2 Fall_theta_wre": -1.481107299,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 63,
    "G2 Fall_dss_wre": 352,
    "G2 Winter_theta_wre": -1.187903992,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 62,
    "G2 Winter_dss_wre": 381,
    "G2 Spring_theta_wre": -0.872277262,
    "G2 Spring_pr_1_99_wre": 1,
    "G2 Spring_ss_wre": 63,
    "G2 Spring_dss_wre": 413,
    "G3 Fall_theta_wre": -0.575282171,
    "G3 Fall_pr_1_99_wre": 1,
    "G3 Fall_ss_wre": 61,
    "G3 Fall_dss_wre": 442,
    "G3 Winter_theta_wre": -0.063821029,
    "G3 Winter_pr_1_99_wre": 2,
    "G3 Winter_ss_wre": 63,
    "G3 Winter_dss_wre": 494,
    "G3 Spring_theta_wre": 0.100669627,
    "G3 Spring_pr_1_99_wre": 5,
    "G3 Spring_ss_wre": 62,
    "G3 Spring_dss_wre": 510
  },
  {
    "ID": 5,
    "KG Winter_theta_wre": -2.866236068,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 66,
    "KG Winter_dss_wre": 213,
    "KG Spring_theta_wre": -2.554525093,
    "KG Spring_pr_1_99_wre": 1,
    "KG Spring_ss_wre": 66,
    "KG Spring_dss_wre": 245,
    "G1 Fall_theta_wre": -2.251599779,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 65,
    "G1 Fall_dss_wre": 275,
    "G1 Winter_theta_wre": -2.020749614,
    "G1 Winter_pr_1_99_wre": 1,
    "G1 Winter_ss_wre": 63,
    "G1 Winter_dss_wre": 298,
    "G1 Spring_theta_wre": -1.718828819,
    "G1 Spring_pr_1_99_wre": 1,
    "G1 Spring_ss_wre": 63,
    "G1 Spring_dss_wre": 328,
    "G2 Fall_theta_wre": -1.44010165,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 63,
    "G2 Fall_dss_wre": 356,
    "G2 Winter_theta_wre": -1.064123665,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 64,
    "G2 Winter_dss_wre": 394,
    "G2 Spring_theta_wre": -0.798016216,
    "G2 Spring_pr_1_99_wre": 1,
    "G2 Spring_ss_wre": 64,
    "G2 Spring_dss_wre": 420,
    "G3 Fall_theta_wre": -0.514745731,
    "G3 Fall_pr_1_99_wre": 1,
    "G3 Fall_ss_wre": 62,
    "G3 Fall_dss_wre": 449,
    "G3 Winter_theta_wre": -0.053101914,
    "G3 Winter_pr_1_99_wre": 2,
    "G3 Winter_ss_wre": 64,
    "G3 Winter_dss_wre": 495,
    "G3 Spring_theta_wre": 0.163503217,
    "G3 Spring_pr_1_99_wre": 5,
    "G3 Spring_ss_wre": 64,
    "G3 Spring_dss_wre": 516
  },
  {
    "ID": 6,
    "KG Winter_theta_wre": -2.86440666,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 66,
    "KG Winter_dss_wre": 214,
    "KG Spring_theta_wre": -2.545865325,
    "KG Spring_pr_1_99_wre": 1,
    "KG Spring_ss_wre": 66,
    "KG Spring_dss_wre": 245,
    "G1 Fall_theta_wre": -2.24945193,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 65,
    "G1 Fall_dss_wre": 275,
    "G1 Winter_theta_wre": -1.866100672,
    "G1 Winter_pr_1_99_wre": 1,
    "G1 Winter_ss_wre": 66,
    "G1 Winter_dss_wre": 313,
    "G1 Spring_theta_wre": -1.59491176,
    "G1 Spring_pr_1_99_wre": 2,
    "G1 Spring_ss_wre": 65,
    "G1 Spring_dss_wre": 341,
    "G2 Fall_theta_wre": -1.339445089,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 65,
    "G2 Fall_dss_wre": 366,
    "G2 Winter_theta_wre": -1.03721173,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 65,
    "G2 Winter_dss_wre": 396,
    "G2 Spring_theta_wre": -0.713692062,
    "G2 Spring_pr_1_99_wre": 2,
    "G2 Spring_ss_wre": 66,
    "G2 Spring_dss_wre": 429,
    "G3 Fall_theta_wre": -0.400025997,
    "G3 Fall_pr_1_99_wre": 1,
    "G3 Fall_ss_wre": 64,
    "G3 Fall_dss_wre": 460,
    "G3 Winter_theta_wre": -0.039811741,
    "G3 Winter_pr_1_99_wre": 2,
    "G3 Winter_ss_wre": 64,
    "G3 Winter_dss_wre": 496,
    "G3 Spring_theta_wre": 0.270003092,
    "G3 Spring_pr_1_99_wre": 7,
    "G3 Spring_ss_wre": 66,
    "G3 Spring_dss_wre": 527
  },
  {
    "ID": 7,
    "KG Winter_theta_wre": -2.842150998,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 66,
    "KG Winter_dss_wre": 216,
    "KG Spring_theta_wre": -2.533216249,
    "KG Spring_pr_1_99_wre": 1,
    "KG Spring_ss_wre": 66,
    "KG Spring_dss_wre": 247,
    "G1 Fall_theta_wre": -2.225048256,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 65,
    "G1 Fall_dss_wre": 277,
    "G1 Winter_theta_wre": -1.865681941,
    "G1 Winter_pr_1_99_wre": 1,
    "G1 Winter_ss_wre": 66,
    "G1 Winter_dss_wre": 313,
    "G1 Spring_theta_wre": -1.58093101,
    "G1 Spring_pr_1_99_wre": 2,
    "G1 Spring_ss_wre": 66,
    "G1 Spring_dss_wre": 342,
    "G2 Fall_theta_wre": -1.331569698,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 65,
    "G2 Fall_dss_wre": 367,
    "G2 Winter_theta_wre": -1.004283119,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 65,
    "G2 Winter_dss_wre": 400,
    "G2 Spring_theta_wre": -0.700665257,
    "G2 Spring_pr_1_99_wre": 2,
    "G2 Spring_ss_wre": 66,
    "G2 Spring_dss_wre": 430,
    "G3 Fall_theta_wre": -0.292227006,
    "G3 Fall_pr_1_99_wre": 2,
    "G3 Fall_ss_wre": 66,
    "G3 Fall_dss_wre": 471,
    "G3 Winter_theta_wre": -0.021522568,
    "G3 Winter_pr_1_99_wre": 2,
    "G3 Winter_ss_wre": 64,
    "G3 Winter_dss_wre": 498,
    "G3 Spring_theta_wre": 0.299426847,
    "G3 Spring_pr_1_99_wre": 7,
    "G3 Spring_ss_wre": 66,
    "G3 Spring_dss_wre": 530
  },
  {
    "ID": 8,
    "KG Winter_theta_wre": -2.788372974,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 67,
    "KG Winter_dss_wre": 221,
    "KG Spring_theta_wre": -2.495189009,
    "KG Spring_pr_1_99_wre": 1,
    "KG Spring_ss_wre": 67,
    "KG Spring_dss_wre": 250,
    "G1 Fall_theta_wre": -2.164804589,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 66,
    "G1 Fall_dss_wre": 284,
    "G1 Winter_theta_wre": -1.722638393,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 68,
    "G1 Winter_dss_wre": 328,
    "G1 Spring_theta_wre": -1.564864336,
    "G1 Spring_pr_1_99_wre": 2,
    "G1 Spring_ss_wre": 66,
    "G1 Spring_dss_wre": 344,
    "G2 Fall_theta_wre": -1.262618118,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 67,
    "G2 Fall_dss_wre": 374,
    "G2 Winter_theta_wre": -0.99891387,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 65,
    "G2 Winter_dss_wre": 400,
    "G2 Spring_theta_wre": -0.553339077,
    "G2 Spring_pr_1_99_wre": 4,
    "G2 Spring_ss_wre": 69,
    "G2 Spring_dss_wre": 445,
    "G3 Fall_theta_wre": -0.28903923,
    "G3 Fall_pr_1_99_wre": 2,
    "G3 Fall_ss_wre": 66,
    "G3 Fall_dss_wre": 471,
    "G3 Winter_theta_wre": -0.006696185,
    "G3 Winter_pr_1_99_wre": 2,
    "G3 Winter_ss_wre": 64,
    "G3 Winter_dss_wre": 499,
    "G3 Spring_theta_wre": 0.311538276,
    "G3 Spring_pr_1_99_wre": 7,
    "G3 Spring_ss_wre": 66,
    "G3 Spring_dss_wre": 531
  },
  {
    "ID": 9,
    "KG Winter_theta_wre": -2.76091913,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 68,
    "KG Winter_dss_wre": 224,
    "KG Spring_theta_wre": -2.428083205,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 68,
    "KG Spring_dss_wre": 257,
    "G1 Fall_theta_wre": -2.050876472,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 68,
    "G1 Fall_dss_wre": 295,
    "G1 Winter_theta_wre": -1.720958296,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 68,
    "G1 Winter_dss_wre": 328,
    "G1 Spring_theta_wre": -1.499402029,
    "G1 Spring_pr_1_99_wre": 2,
    "G1 Spring_ss_wre": 67,
    "G1 Spring_dss_wre": 350,
    "G2 Fall_theta_wre": -1.202574909,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 68,
    "G2 Fall_dss_wre": 380,
    "G2 Winter_theta_wre": -0.964171815,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 66,
    "G2 Winter_dss_wre": 404,
    "G2 Spring_theta_wre": -0.541416797,
    "G2 Spring_pr_1_99_wre": 4,
    "G2 Spring_ss_wre": 69,
    "G2 Spring_dss_wre": 446,
    "G3 Fall_theta_wre": -0.251081852,
    "G3 Fall_pr_1_99_wre": 3,
    "G3 Fall_ss_wre": 67,
    "G3 Fall_dss_wre": 475,
    "G3 Winter_theta_wre": 0.089366633,
    "G3 Winter_pr_1_99_wre": 2,
    "G3 Winter_ss_wre": 66,
    "G3 Winter_dss_wre": 509,
    "G3 Spring_theta_wre": 0.36397403,
    "G3 Spring_pr_1_99_wre": 9,
    "G3 Spring_ss_wre": 67,
    "G3 Spring_dss_wre": 536
  },
  {
    "ID": 10,
    "KG Winter_theta_wre": -2.715469082,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 69,
    "KG Winter_dss_wre": 228,
    "KG Spring_theta_wre": -2.397351127,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 69,
    "KG Spring_dss_wre": 260,
    "G1 Fall_theta_wre": -2.004708659,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 69,
    "G1 Fall_dss_wre": 300,
    "G1 Winter_theta_wre": -1.719914198,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 68,
    "G1 Winter_dss_wre": 328,
    "G1 Spring_theta_wre": -1.489378639,
    "G1 Spring_pr_1_99_wre": 2,
    "G1 Spring_ss_wre": 67,
    "G1 Spring_dss_wre": 351,
    "G2 Fall_theta_wre": -1.161671916,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 68,
    "G2 Fall_dss_wre": 384,
    "G2 Winter_theta_wre": -0.960621025,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 66,
    "G2 Winter_dss_wre": 404,
    "G2 Spring_theta_wre": -0.529149714,
    "G2 Spring_pr_1_99_wre": 4,
    "G2 Spring_ss_wre": 69,
    "G2 Spring_dss_wre": 447,
    "G3 Fall_theta_wre": -0.222770778,
    "G3 Fall_pr_1_99_wre": 3,
    "G3 Fall_ss_wre": 68,
    "G3 Fall_dss_wre": 478,
    "G3 Winter_theta_wre": 0.198457015,
    "G3 Winter_pr_1_99_wre": 4,
    "G3 Winter_ss_wre": 68,
    "G3 Winter_dss_wre": 520,
    "G3 Spring_theta_wre": 0.382061817,
    "G3 Spring_pr_1_99_wre": 9,
    "G3 Spring_ss_wre": 68,
    "G3 Spring_dss_wre": 538
  },
  {
    "ID": 11,
    "KG Winter_theta_wre": -2.704701033,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 69,
    "KG Winter_dss_wre": 230,
    "KG Spring_theta_wre": -2.330409632,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 70,
    "KG Spring_dss_wre": 267,
    "G1 Fall_theta_wre": -1.983745975,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 70,
    "G1 Fall_dss_wre": 302,
    "G1 Winter_theta_wre": -1.714400275,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 69,
    "G1 Winter_dss_wre": 329,
    "G1 Spring_theta_wre": -1.459103044,
    "G1 Spring_pr_1_99_wre": 2,
    "G1 Spring_ss_wre": 68,
    "G1 Spring_dss_wre": 354,
    "G2 Fall_theta_wre": -1.099041001,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 70,
    "G2 Fall_dss_wre": 390,
    "G2 Winter_theta_wre": -0.882008819,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 67,
    "G2 Winter_dss_wre": 412,
    "G2 Spring_theta_wre": -0.509062254,
    "G2 Spring_pr_1_99_wre": 4,
    "G2 Spring_ss_wre": 69,
    "G2 Spring_dss_wre": 449,
    "G3 Fall_theta_wre": -0.106825438,
    "G3 Fall_pr_1_99_wre": 5,
    "G3 Fall_ss_wre": 70,
    "G3 Fall_dss_wre": 489,
    "G3 Winter_theta_wre": 0.208356864,
    "G3 Winter_pr_1_99_wre": 4,
    "G3 Winter_ss_wre": 69,
    "G3 Winter_dss_wre": 521,
    "G3 Spring_theta_wre": 0.396753364,
    "G3 Spring_pr_1_99_wre": 9,
    "G3 Spring_ss_wre": 68,
    "G3 Spring_dss_wre": 540
  },
  {
    "ID": 12,
    "KG Winter_theta_wre": -2.695281996,
    "KG Winter_pr_1_99_wre": 1,
    "KG Winter_ss_wre": 69,
    "KG Winter_dss_wre": 230,
    "KG Spring_theta_wre": -2.322381753,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 70,
    "KG Spring_dss_wre": 268,
    "G1 Fall_theta_wre": -1.972637423,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 70,
    "G1 Fall_dss_wre": 303,
    "G1 Winter_theta_wre": -1.661435229,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 70,
    "G1 Winter_dss_wre": 334,
    "G1 Spring_theta_wre": -1.41885624,
    "G1 Spring_pr_1_99_wre": 2,
    "G1 Spring_ss_wre": 69,
    "G1 Spring_dss_wre": 358,
    "G2 Fall_theta_wre": -1.069917467,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 70,
    "G2 Fall_dss_wre": 393,
    "G2 Winter_theta_wre": -0.846217075,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 68,
    "G2 Winter_dss_wre": 415,
    "G2 Spring_theta_wre": -0.470556926,
    "G2 Spring_pr_1_99_wre": 4,
    "G2 Spring_ss_wre": 70,
    "G2 Spring_dss_wre": 453,
    "G3 Fall_theta_wre": -0.098607255,
    "G3 Fall_pr_1_99_wre": 5,
    "G3 Fall_ss_wre": 70,
    "G3 Fall_dss_wre": 490,
    "G3 Winter_theta_wre": 0.219150552,
    "G3 Winter_pr_1_99_wre": 4,
    "G3 Winter_ss_wre": 69,
    "G3 Winter_dss_wre": 522,
    "G3 Spring_theta_wre": 0.436261471,
    "G3 Spring_pr_1_99_wre": 9,
    "G3 Spring_ss_wre": 68,
    "G3 Spring_dss_wre": 544
  },
  {
    "ID": 13,
    "KG Winter_theta_wre": -2.624541206,
    "KG Winter_pr_1_99_wre": 3,
    "KG Winter_ss_wre": 70,
    "KG Winter_dss_wre": 238,
    "KG Spring_theta_wre": -2.317959564,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 70,
    "KG Spring_dss_wre": 268,
    "G1 Fall_theta_wre": -1.949604477,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 70,
    "G1 Fall_dss_wre": 305,
    "G1 Winter_theta_wre": -1.652076993,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 70,
    "G1 Winter_dss_wre": 335,
    "G1 Spring_theta_wre": -1.40841831,
    "G1 Spring_pr_1_99_wre": 2,
    "G1 Spring_ss_wre": 69,
    "G1 Spring_dss_wre": 359,
    "G2 Fall_theta_wre": -1.049119728,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 70,
    "G2 Fall_dss_wre": 395,
    "G2 Winter_theta_wre": -0.829218758,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 68,
    "G2 Winter_dss_wre": 417,
    "G2 Spring_theta_wre": -0.465356079,
    "G2 Spring_pr_1_99_wre": 4,
    "G2 Spring_ss_wre": 70,
    "G2 Spring_dss_wre": 453,
    "G3 Fall_theta_wre": -0.091732576,
    "G3 Fall_pr_1_99_wre": 5,
    "G3 Fall_ss_wre": 70,
    "G3 Fall_dss_wre": 491,
    "G3 Winter_theta_wre": 0.293254697,
    "G3 Winter_pr_1_99_wre": 4,
    "G3 Winter_ss_wre": 70,
    "G3 Winter_dss_wre": 529,
    "G3 Spring_theta_wre": 0.441511061,
    "G3 Spring_pr_1_99_wre": 10,
    "G3 Spring_ss_wre": 69,
    "G3 Spring_dss_wre": 544
  },
  {
    "ID": 14,
    "KG Winter_theta_wre": -2.624201947,
    "KG Winter_pr_1_99_wre": 3,
    "KG Winter_ss_wre": 70,
    "KG Winter_dss_wre": 238,
    "KG Spring_theta_wre": -2.287139,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 71,
    "KG Spring_dss_wre": 271,
    "G1 Fall_theta_wre": -1.926315874,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 71,
    "G1 Fall_dss_wre": 307,
    "G1 Winter_theta_wre": -1.590077669,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 71,
    "G1 Winter_dss_wre": 341,
    "G1 Spring_theta_wre": -1.342824683,
    "G1 Spring_pr_1_99_wre": 4,
    "G1 Spring_ss_wre": 70,
    "G1 Spring_dss_wre": 366,
    "G2 Fall_theta_wre": -1.035827161,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 71,
    "G2 Fall_dss_wre": 396,
    "G2 Winter_theta_wre": -0.802152281,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 69,
    "G2 Winter_dss_wre": 420,
    "G2 Spring_theta_wre": -0.397057177,
    "G2 Spring_pr_1_99_wre": 11,
    "G2 Spring_ss_wre": 71,
    "G2 Spring_dss_wre": 460,
    "G3 Fall_theta_wre": -0.059334432,
    "G3 Fall_pr_1_99_wre": 5,
    "G3 Fall_ss_wre": 71,
    "G3 Fall_dss_wre": 494,
    "G3 Winter_theta_wre": 0.406946605,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 72,
    "G3 Winter_dss_wre": 541,
    "G3 Spring_theta_wre": 0.449377587,
    "G3 Spring_pr_1_99_wre": 10,
    "G3 Spring_ss_wre": 69,
    "G3 Spring_dss_wre": 545
  },
  {
    "ID": 15,
    "KG Winter_theta_wre": -2.613609676,
    "KG Winter_pr_1_99_wre": 3,
    "KG Winter_ss_wre": 70,
    "KG Winter_dss_wre": 239,
    "KG Spring_theta_wre": -2.282117296,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 71,
    "KG Spring_dss_wre": 272,
    "G1 Fall_theta_wre": -1.823180473,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 73,
    "G1 Fall_dss_wre": 318,
    "G1 Winter_theta_wre": -1.580355776,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 71,
    "G1 Winter_dss_wre": 342,
    "G1 Spring_theta_wre": -1.205416945,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 73,
    "G1 Spring_dss_wre": 379,
    "G2 Fall_theta_wre": -1.021479434,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 71,
    "G2 Fall_dss_wre": 398,
    "G2 Winter_theta_wre": -0.795515854,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 69,
    "G2 Winter_dss_wre": 420,
    "G2 Spring_theta_wre": -0.376117931,
    "G2 Spring_pr_1_99_wre": 11,
    "G2 Spring_ss_wre": 72,
    "G2 Spring_dss_wre": 462,
    "G3 Fall_theta_wre": -0.027665552,
    "G3 Fall_pr_1_99_wre": 5,
    "G3 Fall_ss_wre": 71,
    "G3 Fall_dss_wre": 497,
    "G3 Winter_theta_wre": 0.422157374,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 73,
    "G3 Winter_dss_wre": 542,
    "G3 Spring_theta_wre": 0.459702963,
    "G3 Spring_pr_1_99_wre": 10,
    "G3 Spring_ss_wre": 69,
    "G3 Spring_dss_wre": 546
  },
  {
    "ID": 16,
    "KG Winter_theta_wre": -2.60396678,
    "KG Winter_pr_1_99_wre": 3,
    "KG Winter_ss_wre": 71,
    "KG Winter_dss_wre": 240,
    "KG Spring_theta_wre": -2.270131664,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 71,
    "KG Spring_dss_wre": 273,
    "G1 Fall_theta_wre": -1.811432601,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 73,
    "G1 Fall_dss_wre": 319,
    "G1 Winter_theta_wre": -1.574846896,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 71,
    "G1 Winter_dss_wre": 343,
    "G1 Spring_theta_wre": -1.196790047,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 73,
    "G1 Spring_dss_wre": 380,
    "G2 Fall_theta_wre": -1.010691095,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 71,
    "G2 Fall_dss_wre": 399,
    "G2 Winter_theta_wre": -0.781468847,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 69,
    "G2 Winter_dss_wre": 422,
    "G2 Spring_theta_wre": -0.343764269,
    "G2 Spring_pr_1_99_wre": 11,
    "G2 Spring_ss_wre": 72,
    "G2 Spring_dss_wre": 466,
    "G3 Fall_theta_wre": 0.006835955,
    "G3 Fall_pr_1_99_wre": 6,
    "G3 Fall_ss_wre": 72,
    "G3 Fall_dss_wre": 501,
    "G3 Winter_theta_wre": 0.439274524,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 73,
    "G3 Winter_dss_wre": 544,
    "G3 Spring_theta_wre": 0.467610204,
    "G3 Spring_pr_1_99_wre": 10,
    "G3 Spring_ss_wre": 69,
    "G3 Spring_dss_wre": 547
  },
  {
    "ID": 17,
    "KG Winter_theta_wre": -2.579544606,
    "KG Winter_pr_1_99_wre": 3,
    "KG Winter_ss_wre": 71,
    "KG Winter_dss_wre": 242,
    "KG Spring_theta_wre": -2.228926816,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 72,
    "KG Spring_dss_wre": 277,
    "G1 Fall_theta_wre": -1.801693143,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 73,
    "G1 Fall_dss_wre": 320,
    "G1 Winter_theta_wre": -1.539300042,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 72,
    "G1 Winter_dss_wre": 346,
    "G1 Spring_theta_wre": -1.178653153,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 73,
    "G1 Spring_dss_wre": 382,
    "G2 Fall_theta_wre": -1.010529026,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 71,
    "G2 Fall_dss_wre": 399,
    "G2 Winter_theta_wre": -0.750709554,
    "G2 Winter_pr_1_99_wre": 1,
    "G2 Winter_ss_wre": 70,
    "G2 Winter_dss_wre": 425,
    "G2 Spring_theta_wre": -0.318860798,
    "G2 Spring_pr_1_99_wre": 11,
    "G2 Spring_ss_wre": 73,
    "G2 Spring_dss_wre": 468,
    "G3 Fall_theta_wre": 0.026648727,
    "G3 Fall_pr_1_99_wre": 6,
    "G3 Fall_ss_wre": 72,
    "G3 Fall_dss_wre": 503,
    "G3 Winter_theta_wre": 0.449213189,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 73,
    "G3 Winter_dss_wre": 545,
    "G3 Spring_theta_wre": 0.537779283,
    "G3 Spring_pr_1_99_wre": 10,
    "G3 Spring_ss_wre": 70,
    "G3 Spring_dss_wre": 554
  },
  {
    "ID": 18,
    "KG Winter_theta_wre": -2.520992523,
    "KG Winter_pr_1_99_wre": 3,
    "KG Winter_ss_wre": 72,
    "KG Winter_dss_wre": 248,
    "KG Spring_theta_wre": -2.203971622,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 72,
    "KG Spring_dss_wre": 280,
    "G1 Fall_theta_wre": -1.789680308,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 73,
    "G1 Fall_dss_wre": 321,
    "G1 Winter_theta_wre": -1.534187707,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 72,
    "G1 Winter_dss_wre": 347,
    "G1 Spring_theta_wre": -1.176431491,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 73,
    "G1 Spring_dss_wre": 382,
    "G2 Fall_theta_wre": -1.004577861,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 71,
    "G2 Fall_dss_wre": 400,
    "G2 Winter_theta_wre": -0.665623006,
    "G2 Winter_pr_1_99_wre": 2,
    "G2 Winter_ss_wre": 71,
    "G2 Winter_dss_wre": 433,
    "G2 Spring_theta_wre": -0.308106372,
    "G2 Spring_pr_1_99_wre": 11,
    "G2 Spring_ss_wre": 73,
    "G2 Spring_dss_wre": 469,
    "G3 Fall_theta_wre": 0.027625667,
    "G3 Fall_pr_1_99_wre": 6,
    "G3 Fall_ss_wre": 72,
    "G3 Fall_dss_wre": 503,
    "G3 Winter_theta_wre": 0.45051025,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 73,
    "G3 Winter_dss_wre": 545,
    "G3 Spring_theta_wre": 0.638053095,
    "G3 Spring_pr_1_99_wre": 12,
    "G3 Spring_ss_wre": 72,
    "G3 Spring_dss_wre": 564
  },
  {
    "ID": 19,
    "KG Winter_theta_wre": -2.508345572,
    "KG Winter_pr_1_99_wre": 3,
    "KG Winter_ss_wre": 72,
    "KG Winter_dss_wre": 249,
    "KG Spring_theta_wre": -2.162319275,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 73,
    "KG Spring_dss_wre": 284,
    "G1 Fall_theta_wre": -1.768850715,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 74,
    "G1 Fall_dss_wre": 323,
    "G1 Winter_theta_wre": -1.516220519,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 72,
    "G1 Winter_dss_wre": 348,
    "G1 Spring_theta_wre": -1.151606874,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 74,
    "G1 Spring_dss_wre": 385,
    "G2 Fall_theta_wre": -0.985541373,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 72,
    "G2 Fall_dss_wre": 401,
    "G2 Winter_theta_wre": -0.624373516,
    "G2 Winter_pr_1_99_wre": 4,
    "G2 Winter_ss_wre": 72,
    "G2 Winter_dss_wre": 438,
    "G2 Spring_theta_wre": -0.290843346,
    "G2 Spring_pr_1_99_wre": 12,
    "G2 Spring_ss_wre": 73,
    "G2 Spring_dss_wre": 471,
    "G3 Fall_theta_wre": 0.048750059,
    "G3 Fall_pr_1_99_wre": 6,
    "G3 Fall_ss_wre": 73,
    "G3 Fall_dss_wre": 505,
    "G3 Winter_theta_wre": 0.464469165,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 73,
    "G3 Winter_dss_wre": 546,
    "G3 Spring_theta_wre": 0.644101867,
    "G3 Spring_pr_1_99_wre": 12,
    "G3 Spring_ss_wre": 72,
    "G3 Spring_dss_wre": 564
  },
  {
    "ID": 20,
    "KG Winter_theta_wre": -2.460857351,
    "KG Winter_pr_1_99_wre": 4,
    "KG Winter_ss_wre": 73,
    "KG Winter_dss_wre": 254,
    "KG Spring_theta_wre": -2.149386521,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 73,
    "KG Spring_dss_wre": 285,
    "G1 Fall_theta_wre": -1.766002135,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 74,
    "G1 Fall_dss_wre": 323,
    "G1 Winter_theta_wre": -1.515730256,
    "G1 Winter_pr_1_99_wre": 3,
    "G1 Winter_ss_wre": 72,
    "G1 Winter_dss_wre": 348,
    "G1 Spring_theta_wre": -1.12731714,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 74,
    "G1 Spring_dss_wre": 387,
    "G2 Fall_theta_wre": -0.925177995,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 73,
    "G2 Fall_dss_wre": 407,
    "G2 Winter_theta_wre": -0.615806752,
    "G2 Winter_pr_1_99_wre": 4,
    "G2 Winter_ss_wre": 72,
    "G2 Winter_dss_wre": 438,
    "G2 Spring_theta_wre": -0.272669876,
    "G2 Spring_pr_1_99_wre": 12,
    "G2 Spring_ss_wre": 74,
    "G2 Spring_dss_wre": 473,
    "G3 Fall_theta_wre": 0.09669886,
    "G3 Fall_pr_1_99_wre": 7,
    "G3 Fall_ss_wre": 74,
    "G3 Fall_dss_wre": 510,
    "G3 Winter_theta_wre": 0.464874188,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 73,
    "G3 Winter_dss_wre": 546,
    "G3 Spring_theta_wre": 0.666629164,
    "G3 Spring_pr_1_99_wre": 12,
    "G3 Spring_ss_wre": 73,
    "G3 Spring_dss_wre": 567
  },
  {
    "ID": 21,
    "KG Winter_theta_wre": -2.440220074,
    "KG Winter_pr_1_99_wre": 4,
    "KG Winter_ss_wre": 74,
    "KG Winter_dss_wre": 256,
    "KG Spring_theta_wre": -2.134939723,
    "KG Spring_pr_1_99_wre": 2,
    "KG Spring_ss_wre": 74,
    "KG Spring_dss_wre": 287,
    "G1 Fall_theta_wre": -1.685511286,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 75,
    "G1 Fall_dss_wre": 331,
    "G1 Winter_theta_wre": -1.404132497,
    "G1 Winter_pr_1_99_wre": 5,
    "G1 Winter_ss_wre": 75,
    "G1 Winter_dss_wre": 360,
    "G1 Spring_theta_wre": -1.097306897,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 74,
    "G1 Spring_dss_wre": 390,
    "G2 Fall_theta_wre": -0.883537638,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 73,
    "G2 Fall_dss_wre": 412,
    "G2 Winter_theta_wre": -0.569185429,
    "G2 Winter_pr_1_99_wre": 4,
    "G2 Winter_ss_wre": 73,
    "G2 Winter_dss_wre": 443,
    "G2 Spring_theta_wre": -0.270072523,
    "G2 Spring_pr_1_99_wre": 12,
    "G2 Spring_ss_wre": 74,
    "G2 Spring_dss_wre": 473,
    "G3 Fall_theta_wre": 0.115564307,
    "G3 Fall_pr_1_99_wre": 7,
    "G3 Fall_ss_wre": 74,
    "G3 Fall_dss_wre": 512,
    "G3 Winter_theta_wre": 0.497204446,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 74,
    "G3 Winter_dss_wre": 550,
    "G3 Spring_theta_wre": 0.67300579,
    "G3 Spring_pr_1_99_wre": 12,
    "G3 Spring_ss_wre": 73,
    "G3 Spring_dss_wre": 567
  },
  {
    "ID": 22,
    "KG Winter_theta_wre": -2.424468846,
    "KG Winter_pr_1_99_wre": 4,
    "KG Winter_ss_wre": 74,
    "KG Winter_dss_wre": 258,
    "KG Spring_theta_wre": -2.131261067,
    "KG Spring_pr_1_99_wre": 4,
    "KG Spring_ss_wre": 74,
    "KG Spring_dss_wre": 287,
    "G1 Fall_theta_wre": -1.679170212,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 75,
    "G1 Fall_dss_wre": 332,
    "G1 Winter_theta_wre": -1.39592599,
    "G1 Winter_pr_1_99_wre": 5,
    "G1 Winter_ss_wre": 75,
    "G1 Winter_dss_wre": 360,
    "G1 Spring_theta_wre": -1.090620431,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 75,
    "G1 Spring_dss_wre": 391,
    "G2 Fall_theta_wre": -0.868383731,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 74,
    "G2 Fall_dss_wre": 413,
    "G2 Winter_theta_wre": -0.56620147,
    "G2 Winter_pr_1_99_wre": 4,
    "G2 Winter_ss_wre": 73,
    "G2 Winter_dss_wre": 443,
    "G2 Spring_theta_wre": -0.250163966,
    "G2 Spring_pr_1_99_wre": 12,
    "G2 Spring_ss_wre": 74,
    "G2 Spring_dss_wre": 475,
    "G3 Fall_theta_wre": 0.124342791,
    "G3 Fall_pr_1_99_wre": 7,
    "G3 Fall_ss_wre": 74,
    "G3 Fall_dss_wre": 512,
    "G3 Winter_theta_wre": 0.516883116,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 74,
    "G3 Winter_dss_wre": 552,
    "G3 Spring_theta_wre": 0.711394989,
    "G3 Spring_pr_1_99_wre": 13,
    "G3 Spring_ss_wre": 73,
    "G3 Spring_dss_wre": 571
  },
  {
    "ID": 23,
    "KG Winter_theta_wre": -2.4073069,
    "KG Winter_pr_1_99_wre": 4,
    "KG Winter_ss_wre": 74,
    "KG Winter_dss_wre": 259,
    "KG Spring_theta_wre": -2.130952032,
    "KG Spring_pr_1_99_wre": 4,
    "KG Spring_ss_wre": 74,
    "KG Spring_dss_wre": 287,
    "G1 Fall_theta_wre": -1.667050272,
    "G1 Fall_pr_1_99_wre": 1,
    "G1 Fall_ss_wre": 75,
    "G1 Fall_dss_wre": 333,
    "G1 Winter_theta_wre": -1.368521683,
    "G1 Winter_pr_1_99_wre": 5,
    "G1 Winter_ss_wre": 75,
    "G1 Winter_dss_wre": 363,
    "G1 Spring_theta_wre": -1.074780989,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 75,
    "G1 Spring_dss_wre": 393,
    "G2 Fall_theta_wre": -0.843418145,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 74,
    "G2 Fall_dss_wre": 416,
    "G2 Winter_theta_wre": -0.55327513,
    "G2 Winter_pr_1_99_wre": 4,
    "G2 Winter_ss_wre": 73,
    "G2 Winter_dss_wre": 445,
    "G2 Spring_theta_wre": -0.23433703,
    "G2 Spring_pr_1_99_wre": 12,
    "G2 Spring_ss_wre": 74,
    "G2 Spring_dss_wre": 477,
    "G3 Fall_theta_wre": 0.16316546,
    "G3 Fall_pr_1_99_wre": 8,
    "G3 Fall_ss_wre": 75,
    "G3 Fall_dss_wre": 516,
    "G3 Winter_theta_wre": 0.548841324,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 75,
    "G3 Winter_dss_wre": 555,
    "G3 Spring_theta_wre": 0.722582842,
    "G3 Spring_pr_1_99_wre": 15,
    "G3 Spring_ss_wre": 74,
    "G3 Spring_dss_wre": 572
  },
  {
    "ID": 24,
    "KG Winter_theta_wre": -2.376477056,
    "KG Winter_pr_1_99_wre": 4,
    "KG Winter_ss_wre": 75,
    "KG Winter_dss_wre": 262,
    "KG Spring_theta_wre": -2.115754424,
    "KG Spring_pr_1_99_wre": 5,
    "KG Spring_ss_wre": 74,
    "KG Spring_dss_wre": 288,
    "G1 Fall_theta_wre": -1.655085983,
    "G1 Fall_pr_1_99_wre": 4,
    "G1 Fall_ss_wre": 76,
    "G1 Fall_dss_wre": 334,
    "G1 Winter_theta_wre": -1.339519216,
    "G1 Winter_pr_1_99_wre": 7,
    "G1 Winter_ss_wre": 76,
    "G1 Winter_dss_wre": 366,
    "G1 Spring_theta_wre": -1.067967157,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 75,
    "G1 Spring_dss_wre": 393,
    "G2 Fall_theta_wre": -0.828420418,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 74,
    "G2 Fall_dss_wre": 417,
    "G2 Winter_theta_wre": -0.547620947,
    "G2 Winter_pr_1_99_wre": 4,
    "G2 Winter_ss_wre": 74,
    "G2 Winter_dss_wre": 445,
    "G2 Spring_theta_wre": -0.213848546,
    "G2 Spring_pr_1_99_wre": 13,
    "G2 Spring_ss_wre": 75,
    "G2 Spring_dss_wre": 479,
    "G3 Fall_theta_wre": 0.166922634,
    "G3 Fall_pr_1_99_wre": 8,
    "G3 Fall_ss_wre": 75,
    "G3 Fall_dss_wre": 517,
    "G3 Winter_theta_wre": 0.557269521,
    "G3 Winter_pr_1_99_wre": 5,
    "G3 Winter_ss_wre": 75,
    "G3 Winter_dss_wre": 556,
    "G3 Spring_theta_wre": 0.726411584,
    "G3 Spring_pr_1_99_wre": 16,
    "G3 Spring_ss_wre": 74,
    "G3 Spring_dss_wre": 573
  },
  {
    "ID": 25,
    "KG Winter_theta_wre": -2.340013151,
    "KG Winter_pr_1_99_wre": 4,
    "KG Winter_ss_wre": 75,
    "KG Winter_dss_wre": 266,
    "KG Spring_theta_wre": -2.090878654,
    "KG Spring_pr_1_99_wre": 5,
    "KG Spring_ss_wre": 74,
    "KG Spring_dss_wre": 291,
    "G1 Fall_theta_wre": -1.646988219,
    "G1 Fall_pr_1_99_wre": 8,
    "G1 Fall_ss_wre": 76,
    "G1 Fall_dss_wre": 335,
    "G1 Winter_theta_wre": -1.338573916,
    "G1 Winter_pr_1_99_wre": 7,
    "G1 Winter_ss_wre": 76,
    "G1 Winter_dss_wre": 366,
    "G1 Spring_theta_wre": -1.060321187,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 75,
    "G1 Spring_dss_wre": 394,
    "G2 Fall_theta_wre": -0.828036429,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 74,
    "G2 Fall_dss_wre": 417,
    "G2 Winter_theta_wre": -0.534905873,
    "G2 Winter_pr_1_99_wre": 4,
    "G2 Winter_ss_wre": 74,
    "G2 Winter_dss_wre": 447,
    "G2 Spring_theta_wre": -0.186833445,
    "G2 Spring_pr_1_99_wre": 13,
    "G2 Spring_ss_wre": 75,
    "G2 Spring_dss_wre": 481,
    "G3 Fall_theta_wre": 0.171311721,
    "G3 Fall_pr_1_99_wre": 8,
    "G3 Fall_ss_wre": 75,
    "G3 Fall_dss_wre": 517,
    "G3 Winter_theta_wre": 0.562299288,
    "G3 Winter_pr_1_99_wre": 6,
    "G3 Winter_ss_wre": 75,
    "G3 Winter_dss_wre": 556,
    "G3 Spring_theta_wre": 0.741941909,
    "G3 Spring_pr_1_99_wre": 16,
    "G3 Spring_ss_wre": 74,
    "G3 Spring_dss_wre": 574
  },
  {
    "ID": 26,
    "KG Winter_theta_wre": -2.306669809,
    "KG Winter_pr_1_99_wre": 5,
    "KG Winter_ss_wre": 76,
    "KG Winter_dss_wre": 269,
    "KG Spring_theta_wre": -2.068653419,
    "KG Spring_pr_1_99_wre": 5,
    "KG Spring_ss_wre": 75,
    "KG Spring_dss_wre": 293,
    "G1 Fall_theta_wre": -1.639177238,
    "G1 Fall_pr_1_99_wre": 8,
    "G1 Fall_ss_wre": 76,
    "G1 Fall_dss_wre": 336,
    "G1 Winter_theta_wre": -1.325676562,
    "G1 Winter_pr_1_99_wre": 7,
    "G1 Winter_ss_wre": 76,
    "G1 Winter_dss_wre": 367,
    "G1 Spring_theta_wre": -1.032322615,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 76,
    "G1 Spring_dss_wre": 397,
    "G2 Fall_theta_wre": -0.819076751,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 75,
    "G2 Fall_dss_wre": 418,
    "G2 Winter_theta_wre": -0.533356432,
    "G2 Winter_pr_1_99_wre": 4,
    "G2 Winter_ss_wre": 74,
    "G2 Winter_dss_wre": 447,
    "G2 Spring_theta_wre": -0.183110593,
    "G2 Spring_pr_1_99_wre": 13,
    "G2 Spring_ss_wre": 75,
    "G2 Spring_dss_wre": 482,
    "G3 Fall_theta_wre": 0.181099296,
    "G3 Fall_pr_1_99_wre": 8,
    "G3 Fall_ss_wre": 75,
    "G3 Fall_dss_wre": 518,
    "G3 Winter_theta_wre": 0.565297415,
    "G3 Winter_pr_1_99_wre": 6,
    "G3 Winter_ss_wre": 75,
    "G3 Winter_dss_wre": 557,
    "G3 Spring_theta_wre": 0.761776215,
    "G3 Spring_pr_1_99_wre": 16,
    "G3 Spring_ss_wre": 74,
    "G3 Spring_dss_wre": 576
  },
  {
    "ID": 27,
    "KG Winter_theta_wre": -2.279227676,
    "KG Winter_pr_1_99_wre": 7,
    "KG Winter_ss_wre": 76,
    "KG Winter_dss_wre": 272,
    "KG Spring_theta_wre": -2.057355544,
    "KG Spring_pr_1_99_wre": 5,
    "KG Spring_ss_wre": 75,
    "KG Spring_dss_wre": 294,
    "G1 Fall_theta_wre": -1.638176021,
    "G1 Fall_pr_1_99_wre": 8,
    "G1 Fall_ss_wre": 76,
    "G1 Fall_dss_wre": 336,
    "G1 Winter_theta_wre": -1.283061726,
    "G1 Winter_pr_1_99_wre": 8,
    "G1 Winter_ss_wre": 77,
    "G1 Winter_dss_wre": 372,
    "G1 Spring_theta_wre": -1.032138537,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 76,
    "G1 Spring_dss_wre": 397,
    "G2 Fall_theta_wre": -0.791184494,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 75,
    "G2 Fall_dss_wre": 421,
    "G2 Winter_theta_wre": -0.506498884,
    "G2 Winter_pr_1_99_wre": 5,
    "G2 Winter_ss_wre": 74,
    "G2 Winter_dss_wre": 449,
    "G2 Spring_theta_wre": -0.182560799,
    "G2 Spring_pr_1_99_wre": 13,
    "G2 Spring_ss_wre": 75,
    "G2 Spring_dss_wre": 482,
    "G3 Fall_theta_wre": 0.192652398,
    "G3 Fall_pr_1_99_wre": 8,
    "G3 Fall_ss_wre": 75,
    "G3 Fall_dss_wre": 519,
    "G3 Winter_theta_wre": 0.576053042,
    "G3 Winter_pr_1_99_wre": 6,
    "G3 Winter_ss_wre": 75,
    "G3 Winter_dss_wre": 558,
    "G3 Spring_theta_wre": 0.771886739,
    "G3 Spring_pr_1_99_wre": 16,
    "G3 Spring_ss_wre": 74,
    "G3 Spring_dss_wre": 577
  },
  {
    "ID": 28,
    "KG Winter_theta_wre": -2.275969596,
    "KG Winter_pr_1_99_wre": 7,
    "KG Winter_ss_wre": 77,
    "KG Winter_dss_wre": 272,
    "KG Spring_theta_wre": -2.004532504,
    "KG Spring_pr_1_99_wre": 10,
    "KG Spring_ss_wre": 76,
    "KG Spring_dss_wre": 300,
    "G1 Fall_theta_wre": -1.627431852,
    "G1 Fall_pr_1_99_wre": 8,
    "G1 Fall_ss_wre": 76,
    "G1 Fall_dss_wre": 337,
    "G1 Winter_theta_wre": -1.278996976,
    "G1 Winter_pr_1_99_wre": 8,
    "G1 Winter_ss_wre": 77,
    "G1 Winter_dss_wre": 372,
    "G1 Spring_theta_wre": -1.022608214,
    "G1 Spring_pr_1_99_wre": 6,
    "G1 Spring_ss_wre": 76,
    "G1 Spring_dss_wre": 398,
    "G2 Fall_theta_wre": -0.785814649,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 75,
    "G2 Fall_dss_wre": 421,
    "G2 Winter_theta_wre": -0.499128674,
    "G2 Winter_pr_1_99_wre": 5,
    "G2 Winter_ss_wre": 74,
    "G2 Winter_dss_wre": 450,
    "G2 Spring_theta_wre": -0.17439165,
    "G2 Spring_pr_1_99_wre": 13,
    "G2 Spring_ss_wre": 75,
    "G2 Spring_dss_wre": 483,
    "G3 Fall_theta_wre": 0.215450953,
    "G3 Fall_pr_1_99_wre": 8,
    "G3 Fall_ss_wre": 76,
    "G3 Fall_dss_wre": 522,
    "G3 Winter_theta_wre": 0.578867963,
    "G3 Winter_pr_1_99_wre": 6,
    "G3 Winter_ss_wre": 76,
    "G3 Winter_dss_wre": 558,
    "G3 Spring_theta_wre": 0.775338388,
    "G3 Spring_pr_1_99_wre": 16,
    "G3 Spring_ss_wre": 75,
    "G3 Spring_dss_wre": 578
  },
  {
    "ID": 29,
    "KG Winter_theta_wre": -2.255869612,
    "KG Winter_pr_1_99_wre": 7,
    "KG Winter_ss_wre": 77,
    "KG Winter_dss_wre": 274,
    "KG Spring_theta_wre": -1.983687355,
    "KG Spring_pr_1_99_wre": 11,
    "KG Spring_ss_wre": 76,
    "KG Spring_dss_wre": 302,
    "G1 Fall_theta_wre": -1.616629815,
    "G1 Fall_pr_1_99_wre": 8,
    "G1 Fall_ss_wre": 76,
    "G1 Fall_dss_wre": 338,
    "G1 Winter_theta_wre": -1.276818729,
    "G1 Winter_pr_1_99_wre": 8,
    "G1 Winter_ss_wre": 77,
    "G1 Winter_dss_wre": 372,
    "G1 Spring_theta_wre": -1.004386401,
    "G1 Spring_pr_1_99_wre": 7,
    "G1 Spring_ss_wre": 76,
    "G1 Spring_dss_wre": 400,
    "G2 Fall_theta_wre": -0.78328596,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 75,
    "G2 Fall_dss_wre": 422,
    "G2 Winter_theta_wre": -0.464454974,
    "G2 Winter_pr_1_99_wre": 5,
    "G2 Winter_ss_wre": 75,
    "G2 Winter_dss_wre": 454,
    "G2 Spring_theta_wre": -0.144333653,
    "G2 Spring_pr_1_99_wre": 13,
    "G2 Spring_ss_wre": 76,
    "G2 Spring_dss_wre": 486,
    "G3 Fall_theta_wre": 0.217998442,
    "G3 Fall_pr_1_99_wre": 8,
    "G3 Fall_ss_wre": 76,
    "G3 Fall_dss_wre": 522,
    "G3 Winter_theta_wre": 0.639079038,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 77,
    "G3 Winter_dss_wre": 564,
    "G3 Spring_theta_wre": 0.780646585,
    "G3 Spring_pr_1_99_wre": 16,
    "G3 Spring_ss_wre": 75,
    "G3 Spring_dss_wre": 578
  },
  {
    "ID": 30,
    "KG Winter_theta_wre": -2.234180582,
    "KG Winter_pr_1_99_wre": 7,
    "KG Winter_ss_wre": 77,
    "KG Winter_dss_wre": 277,
    "KG Spring_theta_wre": -1.98178388,
    "KG Spring_pr_1_99_wre": 11,
    "KG Spring_ss_wre": 76,
    "KG Spring_dss_wre": 302,
    "G1 Fall_theta_wre": -1.583324598,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 77,
    "G1 Fall_dss_wre": 342,
    "G1 Winter_theta_wre": -1.269460676,
    "G1 Winter_pr_1_99_wre": 8,
    "G1 Winter_ss_wre": 77,
    "G1 Winter_dss_wre": 373,
    "G1 Spring_theta_wre": -0.978206227,
    "G1 Spring_pr_1_99_wre": 7,
    "G1 Spring_ss_wre": 77,
    "G1 Spring_dss_wre": 402,
    "G2 Fall_theta_wre": -0.777923033,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 75,
    "G2 Fall_dss_wre": 422,
    "G2 Winter_theta_wre": -0.446168019,
    "G2 Winter_pr_1_99_wre": 6,
    "G2 Winter_ss_wre": 75,
    "G2 Winter_dss_wre": 455,
    "G2 Spring_theta_wre": -0.140202984,
    "G2 Spring_pr_1_99_wre": 13,
    "G2 Spring_ss_wre": 76,
    "G2 Spring_dss_wre": 486,
    "G3 Fall_theta_wre": 0.242332995,
    "G3 Fall_pr_1_99_wre": 8,
    "G3 Fall_ss_wre": 76,
    "G3 Fall_dss_wre": 524,
    "G3 Winter_theta_wre": 0.639902481,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 77,
    "G3 Winter_dss_wre": 564,
    "G3 Spring_theta_wre": 0.790559695,
    "G3 Spring_pr_1_99_wre": 16,
    "G3 Spring_ss_wre": 75,
    "G3 Spring_dss_wre": 579
  },
  {
    "ID": 31,
    "KG Winter_theta_wre": -2.217961705,
    "KG Winter_pr_1_99_wre": 7,
    "KG Winter_ss_wre": 78,
    "KG Winter_dss_wre": 278,
    "KG Spring_theta_wre": -1.9726321,
    "KG Spring_pr_1_99_wre": 11,
    "KG Spring_ss_wre": 76,
    "KG Spring_dss_wre": 303,
    "G1 Fall_theta_wre": -1.582363881,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 77,
    "G1 Fall_dss_wre": 342,
    "G1 Winter_theta_wre": -1.261649257,
    "G1 Winter_pr_1_99_wre": 8,
    "G1 Winter_ss_wre": 77,
    "G1 Winter_dss_wre": 374,
    "G1 Spring_theta_wre": -0.972262977,
    "G1 Spring_pr_1_99_wre": 7,
    "G1 Spring_ss_wre": 77,
    "G1 Spring_dss_wre": 403,
    "G2 Fall_theta_wre": -0.721849245,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 76,
    "G2 Fall_dss_wre": 428,
    "G2 Winter_theta_wre": -0.425454154,
    "G2 Winter_pr_1_99_wre": 6,
    "G2 Winter_ss_wre": 76,
    "G2 Winter_dss_wre": 457,
    "G2 Spring_theta_wre": -0.131246459,
    "G2 Spring_pr_1_99_wre": 13,
    "G2 Spring_ss_wre": 76,
    "G2 Spring_dss_wre": 487,
    "G3 Fall_theta_wre": 0.258595882,
    "G3 Fall_pr_1_99_wre": 9,
    "G3 Fall_ss_wre": 77,
    "G3 Fall_dss_wre": 526,
    "G3 Winter_theta_wre": 0.643791787,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 77,
    "G3 Winter_dss_wre": 564,
    "G3 Spring_theta_wre": 0.820105979,
    "G3 Spring_pr_1_99_wre": 17,
    "G3 Spring_ss_wre": 75,
    "G3 Spring_dss_wre": 582
  },
  {
    "ID": 32,
    "KG Winter_theta_wre": -2.212128063,
    "KG Winter_pr_1_99_wre": 7,
    "KG Winter_ss_wre": 78,
    "KG Winter_dss_wre": 279,
    "KG Spring_theta_wre": -1.972148384,
    "KG Spring_pr_1_99_wre": 11,
    "KG Spring_ss_wre": 76,
    "KG Spring_dss_wre": 303,
    "G1 Fall_theta_wre": -1.576886775,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 77,
    "G1 Fall_dss_wre": 342,
    "G1 Winter_theta_wre": -1.238868281,
    "G1 Winter_pr_1_99_wre": 8,
    "G1 Winter_ss_wre": 78,
    "G1 Winter_dss_wre": 376,
    "G1 Spring_theta_wre": -0.955380146,
    "G1 Spring_pr_1_99_wre": 7,
    "G1 Spring_ss_wre": 77,
    "G1 Spring_dss_wre": 404,
    "G2 Fall_theta_wre": -0.69327366,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 77,
    "G2 Fall_dss_wre": 431,
    "G2 Winter_theta_wre": -0.405208335,
    "G2 Winter_pr_1_99_wre": 6,
    "G2 Winter_ss_wre": 76,
    "G2 Winter_dss_wre": 459,
    "G2 Spring_theta_wre": -0.1246019,
    "G2 Spring_pr_1_99_wre": 13,
    "G2 Spring_ss_wre": 76,
    "G2 Spring_dss_wre": 488,
    "G3 Fall_theta_wre": 0.306177064,
    "G3 Fall_pr_1_99_wre": 9,
    "G3 Fall_ss_wre": 77,
    "G3 Fall_dss_wre": 531,
    "G3 Winter_theta_wre": 0.650590452,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 77,
    "G3 Winter_dss_wre": 565,
    "G3 Spring_theta_wre": 0.824812429,
    "G3 Spring_pr_1_99_wre": 17,
    "G3 Spring_ss_wre": 75,
    "G3 Spring_dss_wre": 582
  },
  {
    "ID": 33,
    "KG Winter_theta_wre": -2.180444603,
    "KG Winter_pr_1_99_wre": 8,
    "KG Winter_ss_wre": 78,
    "KG Winter_dss_wre": 282,
    "KG Spring_theta_wre": -1.927836043,
    "KG Spring_pr_1_99_wre": 11,
    "KG Spring_ss_wre": 77,
    "KG Spring_dss_wre": 307,
    "G1 Fall_theta_wre": -1.576488761,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 77,
    "G1 Fall_dss_wre": 342,
    "G1 Winter_theta_wre": -1.224119275,
    "G1 Winter_pr_1_99_wre": 9,
    "G1 Winter_ss_wre": 78,
    "G1 Winter_dss_wre": 378,
    "G1 Spring_theta_wre": -0.954561384,
    "G1 Spring_pr_1_99_wre": 7,
    "G1 Spring_ss_wre": 77,
    "G1 Spring_dss_wre": 405,
    "G2 Fall_theta_wre": -0.68080056,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 77,
    "G2 Fall_dss_wre": 432,
    "G2 Winter_theta_wre": -0.380084279,
    "G2 Winter_pr_1_99_wre": 6,
    "G2 Winter_ss_wre": 77,
    "G2 Winter_dss_wre": 462,
    "G2 Spring_theta_wre": -0.088408088,
    "G2 Spring_pr_1_99_wre": 14,
    "G2 Spring_ss_wre": 77,
    "G2 Spring_dss_wre": 491,
    "G3 Fall_theta_wre": 0.315608226,
    "G3 Fall_pr_1_99_wre": 10,
    "G3 Fall_ss_wre": 78,
    "G3 Fall_dss_wre": 532,
    "G3 Winter_theta_wre": 0.65557968,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 77,
    "G3 Winter_dss_wre": 566,
    "G3 Spring_theta_wre": 0.835355016,
    "G3 Spring_pr_1_99_wre": 17,
    "G3 Spring_ss_wre": 76,
    "G3 Spring_dss_wre": 584
  },
  {
    "ID": 34,
    "KG Winter_theta_wre": -2.179669327,
    "KG Winter_pr_1_99_wre": 8,
    "KG Winter_ss_wre": 78,
    "KG Winter_dss_wre": 282,
    "KG Spring_theta_wre": -1.92463729,
    "KG Spring_pr_1_99_wre": 11,
    "KG Spring_ss_wre": 77,
    "KG Spring_dss_wre": 308,
    "G1 Fall_theta_wre": -1.560894314,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 77,
    "G1 Fall_dss_wre": 344,
    "G1 Winter_theta_wre": -1.22406628,
    "G1 Winter_pr_1_99_wre": 9,
    "G1 Winter_ss_wre": 78,
    "G1 Winter_dss_wre": 378,
    "G1 Spring_theta_wre": -0.945014544,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 77,
    "G1 Spring_dss_wre": 405,
    "G2 Fall_theta_wre": -0.67896449,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 77,
    "G2 Fall_dss_wre": 432,
    "G2 Winter_theta_wre": -0.377245614,
    "G2 Winter_pr_1_99_wre": 6,
    "G2 Winter_ss_wre": 77,
    "G2 Winter_dss_wre": 462,
    "G2 Spring_theta_wre": -0.07390685,
    "G2 Spring_pr_1_99_wre": 15,
    "G2 Spring_ss_wre": 77,
    "G2 Spring_dss_wre": 493,
    "G3 Fall_theta_wre": 0.322594527,
    "G3 Fall_pr_1_99_wre": 11,
    "G3 Fall_ss_wre": 78,
    "G3 Fall_dss_wre": 532,
    "G3 Winter_theta_wre": 0.719608161,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 78,
    "G3 Winter_dss_wre": 572,
    "G3 Spring_theta_wre": 0.836777047,
    "G3 Spring_pr_1_99_wre": 17,
    "G3 Spring_ss_wre": 76,
    "G3 Spring_dss_wre": 584
  },
  {
    "ID": 35,
    "KG Winter_theta_wre": -2.126541872,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 79,
    "KG Winter_dss_wre": 287,
    "KG Spring_theta_wre": -1.907631408,
    "KG Spring_pr_1_99_wre": 11,
    "KG Spring_ss_wre": 78,
    "KG Spring_dss_wre": 309,
    "G1 Fall_theta_wre": -1.553885055,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 77,
    "G1 Fall_dss_wre": 345,
    "G1 Winter_theta_wre": -1.215542547,
    "G1 Winter_pr_1_99_wre": 9,
    "G1 Winter_ss_wre": 78,
    "G1 Winter_dss_wre": 378,
    "G1 Spring_theta_wre": -0.929539933,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 78,
    "G1 Spring_dss_wre": 407,
    "G2 Fall_theta_wre": -0.673995278,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 77,
    "G2 Fall_dss_wre": 433,
    "G2 Winter_theta_wre": -0.331590518,
    "G2 Winter_pr_1_99_wre": 7,
    "G2 Winter_ss_wre": 77,
    "G2 Winter_dss_wre": 467,
    "G2 Spring_theta_wre": 0.00668522,
    "G2 Spring_pr_1_99_wre": 16,
    "G2 Spring_ss_wre": 79,
    "G2 Spring_dss_wre": 501,
    "G3 Fall_theta_wre": 0.34157739,
    "G3 Fall_pr_1_99_wre": 12,
    "G3 Fall_ss_wre": 78,
    "G3 Fall_dss_wre": 534,
    "G3 Winter_theta_wre": 0.743116064,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 79,
    "G3 Winter_dss_wre": 574,
    "G3 Spring_theta_wre": 0.83678964,
    "G3 Spring_pr_1_99_wre": 17,
    "G3 Spring_ss_wre": 76,
    "G3 Spring_dss_wre": 584
  },
  {
    "ID": 36,
    "KG Winter_theta_wre": -2.125951888,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 79,
    "KG Winter_dss_wre": 287,
    "KG Spring_theta_wre": -1.904788336,
    "KG Spring_pr_1_99_wre": 11,
    "KG Spring_ss_wre": 78,
    "KG Spring_dss_wre": 310,
    "G1 Fall_theta_wre": -1.523307526,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 78,
    "G1 Fall_dss_wre": 348,
    "G1 Winter_theta_wre": -1.210277086,
    "G1 Winter_pr_1_99_wre": 9,
    "G1 Winter_ss_wre": 78,
    "G1 Winter_dss_wre": 379,
    "G1 Spring_theta_wre": -0.923076973,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 78,
    "G1 Spring_dss_wre": 408,
    "G2 Fall_theta_wre": -0.663664452,
    "G2 Fall_pr_1_99_wre": 1,
    "G2 Fall_ss_wre": 77,
    "G2 Fall_dss_wre": 434,
    "G2 Winter_theta_wre": -0.301128065,
    "G2 Winter_pr_1_99_wre": 7,
    "G2 Winter_ss_wre": 78,
    "G2 Winter_dss_wre": 470,
    "G2 Spring_theta_wre": 0.02142743,
    "G2 Spring_pr_1_99_wre": 16,
    "G2 Spring_ss_wre": 79,
    "G2 Spring_dss_wre": 502,
    "G3 Fall_theta_wre": 0.345128814,
    "G3 Fall_pr_1_99_wre": 12,
    "G3 Fall_ss_wre": 78,
    "G3 Fall_dss_wre": 535,
    "G3 Winter_theta_wre": 0.747626242,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 79,
    "G3 Winter_dss_wre": 575,
    "G3 Spring_theta_wre": 0.850247447,
    "G3 Spring_pr_1_99_wre": 17,
    "G3 Spring_ss_wre": 76,
    "G3 Spring_dss_wre": 585
  },
  {
    "ID": 37,
    "KG Winter_theta_wre": -2.115644277,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 79,
    "KG Winter_dss_wre": 288,
    "KG Spring_theta_wre": -1.875002235,
    "KG Spring_pr_1_99_wre": 12,
    "KG Spring_ss_wre": 78,
    "KG Spring_dss_wre": 312,
    "G1 Fall_theta_wre": -1.500417881,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 78,
    "G1 Fall_dss_wre": 350,
    "G1 Winter_theta_wre": -1.200499992,
    "G1 Winter_pr_1_99_wre": 9,
    "G1 Winter_ss_wre": 78,
    "G1 Winter_dss_wre": 380,
    "G1 Spring_theta_wre": -0.907355667,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 78,
    "G1 Spring_dss_wre": 409,
    "G2 Fall_theta_wre": -0.617034384,
    "G2 Fall_pr_1_99_wre": 6,
    "G2 Fall_ss_wre": 78,
    "G2 Fall_dss_wre": 438,
    "G2 Winter_theta_wre": -0.286029403,
    "G2 Winter_pr_1_99_wre": 8,
    "G2 Winter_ss_wre": 78,
    "G2 Winter_dss_wre": 471,
    "G2 Spring_theta_wre": 0.023362226,
    "G2 Spring_pr_1_99_wre": 16,
    "G2 Spring_ss_wre": 79,
    "G2 Spring_dss_wre": 502,
    "G3 Fall_theta_wre": 0.376733788,
    "G3 Fall_pr_1_99_wre": 14,
    "G3 Fall_ss_wre": 79,
    "G3 Fall_dss_wre": 538,
    "G3 Winter_theta_wre": 0.750193485,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 79,
    "G3 Winter_dss_wre": 575,
    "G3 Spring_theta_wre": 0.85474149,
    "G3 Spring_pr_1_99_wre": 18,
    "G3 Spring_ss_wre": 76,
    "G3 Spring_dss_wre": 585
  },
  {
    "ID": 38,
    "KG Winter_theta_wre": -2.107342548,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 80,
    "KG Winter_dss_wre": 289,
    "KG Spring_theta_wre": -1.868067807,
    "KG Spring_pr_1_99_wre": 12,
    "KG Spring_ss_wre": 78,
    "KG Spring_dss_wre": 313,
    "G1 Fall_theta_wre": -1.495364544,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 78,
    "G1 Fall_dss_wre": 350,
    "G1 Winter_theta_wre": -1.194044769,
    "G1 Winter_pr_1_99_wre": 9,
    "G1 Winter_ss_wre": 79,
    "G1 Winter_dss_wre": 381,
    "G1 Spring_theta_wre": -0.906294497,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 78,
    "G1 Spring_dss_wre": 409,
    "G2 Fall_theta_wre": -0.584564953,
    "G2 Fall_pr_1_99_wre": 6,
    "G2 Fall_ss_wre": 79,
    "G2 Fall_dss_wre": 442,
    "G2 Winter_theta_wre": -0.269470849,
    "G2 Winter_pr_1_99_wre": 8,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 473,
    "G2 Spring_theta_wre": 0.041769226,
    "G2 Spring_pr_1_99_wre": 16,
    "G2 Spring_ss_wre": 79,
    "G2 Spring_dss_wre": 504,
    "G3 Fall_theta_wre": 0.37987321,
    "G3 Fall_pr_1_99_wre": 14,
    "G3 Fall_ss_wre": 79,
    "G3 Fall_dss_wre": 538,
    "G3 Winter_theta_wre": 0.762777088,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 79,
    "G3 Winter_dss_wre": 576,
    "G3 Spring_theta_wre": 0.911390016,
    "G3 Spring_pr_1_99_wre": 20,
    "G3 Spring_ss_wre": 77,
    "G3 Spring_dss_wre": 591
  },
  {
    "ID": 39,
    "KG Winter_theta_wre": -2.103298626,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 80,
    "KG Winter_dss_wre": 290,
    "KG Spring_theta_wre": -1.865788804,
    "KG Spring_pr_1_99_wre": 12,
    "KG Spring_ss_wre": 78,
    "KG Spring_dss_wre": 313,
    "G1 Fall_theta_wre": -1.492375783,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 79,
    "G1 Fall_dss_wre": 351,
    "G1 Winter_theta_wre": -1.190326147,
    "G1 Winter_pr_1_99_wre": 9,
    "G1 Winter_ss_wre": 79,
    "G1 Winter_dss_wre": 381,
    "G1 Spring_theta_wre": -0.905874805,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 78,
    "G1 Spring_dss_wre": 409,
    "G2 Fall_theta_wre": -0.580774793,
    "G2 Fall_pr_1_99_wre": 6,
    "G2 Fall_ss_wre": 79,
    "G2 Fall_dss_wre": 442,
    "G2 Winter_theta_wre": -0.269172287,
    "G2 Winter_pr_1_99_wre": 8,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 473,
    "G2 Spring_theta_wre": 0.060761345,
    "G2 Spring_pr_1_99_wre": 17,
    "G2 Spring_ss_wre": 80,
    "G2 Spring_dss_wre": 506,
    "G3 Fall_theta_wre": 0.387676682,
    "G3 Fall_pr_1_99_wre": 14,
    "G3 Fall_ss_wre": 79,
    "G3 Fall_dss_wre": 539,
    "G3 Winter_theta_wre": 0.774418593,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 79,
    "G3 Winter_dss_wre": 577,
    "G3 Spring_theta_wre": 0.918146988,
    "G3 Spring_pr_1_99_wre": 20,
    "G3 Spring_ss_wre": 77,
    "G3 Spring_dss_wre": 592
  },
  {
    "ID": 40,
    "KG Winter_theta_wre": -2.097646529,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 80,
    "KG Winter_dss_wre": 290,
    "KG Spring_theta_wre": -1.864931219,
    "KG Spring_pr_1_99_wre": 12,
    "KG Spring_ss_wre": 78,
    "KG Spring_dss_wre": 314,
    "G1 Fall_theta_wre": -1.485169571,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 79,
    "G1 Fall_dss_wre": 351,
    "G1 Winter_theta_wre": -1.184721322,
    "G1 Winter_pr_1_99_wre": 9,
    "G1 Winter_ss_wre": 79,
    "G1 Winter_dss_wre": 382,
    "G1 Spring_theta_wre": -0.893804659,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 78,
    "G1 Spring_dss_wre": 411,
    "G2 Fall_theta_wre": -0.547175113,
    "G2 Fall_pr_1_99_wre": 8,
    "G2 Fall_ss_wre": 80,
    "G2 Fall_dss_wre": 445,
    "G2 Winter_theta_wre": -0.264122159,
    "G2 Winter_pr_1_99_wre": 8,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 474,
    "G2 Spring_theta_wre": 0.080181038,
    "G2 Spring_pr_1_99_wre": 17,
    "G2 Spring_ss_wre": 80,
    "G2 Spring_dss_wre": 508,
    "G3 Fall_theta_wre": 0.393414458,
    "G3 Fall_pr_1_99_wre": 14,
    "G3 Fall_ss_wre": 79,
    "G3 Fall_dss_wre": 539,
    "G3 Winter_theta_wre": 0.789100021,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 579,
    "G3 Spring_theta_wre": 0.922373429,
    "G3 Spring_pr_1_99_wre": 20,
    "G3 Spring_ss_wre": 77,
    "G3 Spring_dss_wre": 592
  },
  {
    "ID": 41,
    "KG Winter_theta_wre": -2.082683351,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 80,
    "KG Winter_dss_wre": 292,
    "KG Spring_theta_wre": -1.863719486,
    "KG Spring_pr_1_99_wre": 12,
    "KG Spring_ss_wre": 78,
    "KG Spring_dss_wre": 314,
    "G1 Fall_theta_wre": -1.463037391,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 79,
    "G1 Fall_dss_wre": 354,
    "G1 Winter_theta_wre": -1.182994903,
    "G1 Winter_pr_1_99_wre": 9,
    "G1 Winter_ss_wre": 79,
    "G1 Winter_dss_wre": 382,
    "G1 Spring_theta_wre": -0.883656125,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 78,
    "G1 Spring_dss_wre": 412,
    "G2 Fall_theta_wre": -0.540307375,
    "G2 Fall_pr_1_99_wre": 8,
    "G2 Fall_ss_wre": 80,
    "G2 Fall_dss_wre": 446,
    "G2 Winter_theta_wre": -0.263234873,
    "G2 Winter_pr_1_99_wre": 8,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 474,
    "G2 Spring_theta_wre": 0.086442662,
    "G2 Spring_pr_1_99_wre": 17,
    "G2 Spring_ss_wre": 80,
    "G2 Spring_dss_wre": 509,
    "G3 Fall_theta_wre": 0.396379739,
    "G3 Fall_pr_1_99_wre": 14,
    "G3 Fall_ss_wre": 79,
    "G3 Fall_dss_wre": 540,
    "G3 Winter_theta_wre": 0.798993578,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 580,
    "G3 Spring_theta_wre": 0.952659156,
    "G3 Spring_pr_1_99_wre": 20,
    "G3 Spring_ss_wre": 78,
    "G3 Spring_dss_wre": 595
  },
  {
    "ID": 42,
    "KG Winter_theta_wre": -2.080086033,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 80,
    "KG Winter_dss_wre": 292,
    "KG Spring_theta_wre": -1.853317889,
    "KG Spring_pr_1_99_wre": 12,
    "KG Spring_ss_wre": 79,
    "KG Spring_dss_wre": 315,
    "G1 Fall_theta_wre": -1.443377682,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 79,
    "G1 Fall_dss_wre": 356,
    "G1 Winter_theta_wre": -1.182430018,
    "G1 Winter_pr_1_99_wre": 9,
    "G1 Winter_ss_wre": 79,
    "G1 Winter_dss_wre": 382,
    "G1 Spring_theta_wre": -0.882801434,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 78,
    "G1 Spring_dss_wre": 412,
    "G2 Fall_theta_wre": -0.539816695,
    "G2 Fall_pr_1_99_wre": 8,
    "G2 Fall_ss_wre": 80,
    "G2 Fall_dss_wre": 446,
    "G2 Winter_theta_wre": -0.260640261,
    "G2 Winter_pr_1_99_wre": 8,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 474,
    "G2 Spring_theta_wre": 0.089995915,
    "G2 Spring_pr_1_99_wre": 17,
    "G2 Spring_ss_wre": 80,
    "G2 Spring_dss_wre": 509,
    "G3 Fall_theta_wre": 0.408074527,
    "G3 Fall_pr_1_99_wre": 14,
    "G3 Fall_ss_wre": 79,
    "G3 Fall_dss_wre": 541,
    "G3 Winter_theta_wre": 0.805141678,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 581,
    "G3 Spring_theta_wre": 0.955264305,
    "G3 Spring_pr_1_99_wre": 20,
    "G3 Spring_ss_wre": 78,
    "G3 Spring_dss_wre": 596
  },
  {
    "ID": 43,
    "KG Winter_theta_wre": -2.079150384,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 80,
    "KG Winter_dss_wre": 292,
    "KG Spring_theta_wre": -1.850418746,
    "KG Spring_pr_1_99_wre": 12,
    "KG Spring_ss_wre": 79,
    "KG Spring_dss_wre": 315,
    "G1 Fall_theta_wre": -1.443113331,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 79,
    "G1 Fall_dss_wre": 356,
    "G1 Winter_theta_wre": -1.171039844,
    "G1 Winter_pr_1_99_wre": 10,
    "G1 Winter_ss_wre": 79,
    "G1 Winter_dss_wre": 383,
    "G1 Spring_theta_wre": -0.873630874,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 79,
    "G1 Spring_dss_wre": 413,
    "G2 Fall_theta_wre": -0.5344582,
    "G2 Fall_pr_1_99_wre": 8,
    "G2 Fall_ss_wre": 80,
    "G2 Fall_dss_wre": 447,
    "G2 Winter_theta_wre": -0.258381249,
    "G2 Winter_pr_1_99_wre": 8,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 474,
    "G2 Spring_theta_wre": 0.097848592,
    "G2 Spring_pr_1_99_wre": 17,
    "G2 Spring_ss_wre": 80,
    "G2 Spring_dss_wre": 510,
    "G3 Fall_theta_wre": 0.411074609,
    "G3 Fall_pr_1_99_wre": 14,
    "G3 Fall_ss_wre": 79,
    "G3 Fall_dss_wre": 541,
    "G3 Winter_theta_wre": 0.809053907,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 581,
    "G3 Spring_theta_wre": 0.962770713,
    "G3 Spring_pr_1_99_wre": 21,
    "G3 Spring_ss_wre": 78,
    "G3 Spring_dss_wre": 596
  },
  {
    "ID": 44,
    "KG Winter_theta_wre": -2.048077489,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 81,
    "KG Winter_dss_wre": 295,
    "KG Spring_theta_wre": -1.839177108,
    "KG Spring_pr_1_99_wre": 13,
    "KG Spring_ss_wre": 79,
    "KG Spring_dss_wre": 316,
    "G1 Fall_theta_wre": -1.440271384,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 79,
    "G1 Fall_dss_wre": 356,
    "G1 Winter_theta_wre": -1.170634205,
    "G1 Winter_pr_1_99_wre": 10,
    "G1 Winter_ss_wre": 79,
    "G1 Winter_dss_wre": 383,
    "G1 Spring_theta_wre": -0.871979323,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 79,
    "G1 Spring_dss_wre": 413,
    "G2 Fall_theta_wre": -0.53352785,
    "G2 Fall_pr_1_99_wre": 8,
    "G2 Fall_ss_wre": 80,
    "G2 Fall_dss_wre": 447,
    "G2 Winter_theta_wre": -0.243438919,
    "G2 Winter_pr_1_99_wre": 8,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 476,
    "G2 Spring_theta_wre": 0.126153362,
    "G2 Spring_pr_1_99_wre": 17,
    "G2 Spring_ss_wre": 81,
    "G2 Spring_dss_wre": 513,
    "G3 Fall_theta_wre": 0.439965074,
    "G3 Fall_pr_1_99_wre": 15,
    "G3 Fall_ss_wre": 80,
    "G3 Fall_dss_wre": 544,
    "G3 Winter_theta_wre": 0.809326523,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 581,
    "G3 Spring_theta_wre": 0.963549133,
    "G3 Spring_pr_1_99_wre": 21,
    "G3 Spring_ss_wre": 78,
    "G3 Spring_dss_wre": 596
  },
  {
    "ID": 45,
    "KG Winter_theta_wre": -2.046708889,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 81,
    "KG Winter_dss_wre": 295,
    "KG Spring_theta_wre": -1.82664394,
    "KG Spring_pr_1_99_wre": 13,
    "KG Spring_ss_wre": 79,
    "KG Spring_dss_wre": 317,
    "G1 Fall_theta_wre": -1.437543523,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 80,
    "G1 Fall_dss_wre": 356,
    "G1 Winter_theta_wre": -1.158923931,
    "G1 Winter_pr_1_99_wre": 10,
    "G1 Winter_ss_wre": 79,
    "G1 Winter_dss_wre": 384,
    "G1 Spring_theta_wre": -0.870583532,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 79,
    "G1 Spring_dss_wre": 413,
    "G2 Fall_theta_wre": -0.53215423,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 80,
    "G2 Fall_dss_wre": 447,
    "G2 Winter_theta_wre": -0.241370766,
    "G2 Winter_pr_1_99_wre": 8,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 476,
    "G2 Spring_theta_wre": 0.129685562,
    "G2 Spring_pr_1_99_wre": 17,
    "G2 Spring_ss_wre": 81,
    "G2 Spring_dss_wre": 513,
    "G3 Fall_theta_wre": 0.446992951,
    "G3 Fall_pr_1_99_wre": 16,
    "G3 Fall_ss_wre": 80,
    "G3 Fall_dss_wre": 545,
    "G3 Winter_theta_wre": 0.810711257,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 581,
    "G3 Spring_theta_wre": 0.979218571,
    "G3 Spring_pr_1_99_wre": 21,
    "G3 Spring_ss_wre": 78,
    "G3 Spring_dss_wre": 598
  },
  {
    "ID": 46,
    "KG Winter_theta_wre": -2.042148702,
    "KG Winter_pr_1_99_wre": 11,
    "KG Winter_ss_wre": 81,
    "KG Winter_dss_wre": 296,
    "KG Spring_theta_wre": -1.818667994,
    "KG Spring_pr_1_99_wre": 13,
    "KG Spring_ss_wre": 79,
    "KG Spring_dss_wre": 318,
    "G1 Fall_theta_wre": -1.420279252,
    "G1 Fall_pr_1_99_wre": 9,
    "G1 Fall_ss_wre": 80,
    "G1 Fall_dss_wre": 358,
    "G1 Winter_theta_wre": -1.127066883,
    "G1 Winter_pr_1_99_wre": 10,
    "G1 Winter_ss_wre": 80,
    "G1 Winter_dss_wre": 387,
    "G1 Spring_theta_wre": -0.857348998,
    "G1 Spring_pr_1_99_wre": 8,
    "G1 Spring_ss_wre": 79,
    "G1 Spring_dss_wre": 414,
    "G2 Fall_theta_wre": -0.530365792,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 80,
    "G2 Fall_dss_wre": 447,
    "G2 Winter_theta_wre": -0.238933754,
    "G2 Winter_pr_1_99_wre": 8,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 476,
    "G2 Spring_theta_wre": 0.149764032,
    "G2 Spring_pr_1_99_wre": 17,
    "G2 Spring_ss_wre": 81,
    "G2 Spring_dss_wre": 515,
    "G3 Fall_theta_wre": 0.44828876,
    "G3 Fall_pr_1_99_wre": 16,
    "G3 Fall_ss_wre": 80,
    "G3 Fall_dss_wre": 545,
    "G3 Winter_theta_wre": 0.815011978,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 582,
    "G3 Spring_theta_wre": 0.979587611,
    "G3 Spring_pr_1_99_wre": 21,
    "G3 Spring_ss_wre": 78,
    "G3 Spring_dss_wre": 598
  },
  {
    "ID": 47,
    "KG Winter_theta_wre": -2.024387369,
    "KG Winter_pr_1_99_wre": 12,
    "KG Winter_ss_wre": 81,
    "KG Winter_dss_wre": 298,
    "KG Spring_theta_wre": -1.816970248,
    "KG Spring_pr_1_99_wre": 13,
    "KG Spring_ss_wre": 79,
    "KG Spring_dss_wre": 318,
    "G1 Fall_theta_wre": -1.360931464,
    "G1 Fall_pr_1_99_wre": 14,
    "G1 Fall_ss_wre": 81,
    "G1 Fall_dss_wre": 364,
    "G1 Winter_theta_wre": -1.125942288,
    "G1 Winter_pr_1_99_wre": 10,
    "G1 Winter_ss_wre": 80,
    "G1 Winter_dss_wre": 387,
    "G1 Spring_theta_wre": -0.815832369,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 80,
    "G1 Spring_dss_wre": 418,
    "G2 Fall_theta_wre": -0.527833,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 80,
    "G2 Fall_dss_wre": 447,
    "G2 Winter_theta_wre": -0.230535726,
    "G2 Winter_pr_1_99_wre": 9,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 477,
    "G2 Spring_theta_wre": 0.159361583,
    "G2 Spring_pr_1_99_wre": 18,
    "G2 Spring_ss_wre": 81,
    "G2 Spring_dss_wre": 516,
    "G3 Fall_theta_wre": 0.448827666,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 80,
    "G3 Fall_dss_wre": 545,
    "G3 Winter_theta_wre": 0.818042081,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 582,
    "G3 Spring_theta_wre": 0.991885772,
    "G3 Spring_pr_1_99_wre": 21,
    "G3 Spring_ss_wre": 78,
    "G3 Spring_dss_wre": 599
  },
  {
    "ID": 48,
    "KG Winter_theta_wre": -2.014504132,
    "KG Winter_pr_1_99_wre": 12,
    "KG Winter_ss_wre": 81,
    "KG Winter_dss_wre": 299,
    "KG Spring_theta_wre": -1.816354028,
    "KG Spring_pr_1_99_wre": 13,
    "KG Spring_ss_wre": 79,
    "KG Spring_dss_wre": 318,
    "G1 Fall_theta_wre": -1.356848699,
    "G1 Fall_pr_1_99_wre": 14,
    "G1 Fall_ss_wre": 81,
    "G1 Fall_dss_wre": 364,
    "G1 Winter_theta_wre": -1.122305875,
    "G1 Winter_pr_1_99_wre": 10,
    "G1 Winter_ss_wre": 80,
    "G1 Winter_dss_wre": 388,
    "G1 Spring_theta_wre": -0.80249551,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 80,
    "G1 Spring_dss_wre": 420,
    "G2 Fall_theta_wre": -0.522177627,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 80,
    "G2 Fall_dss_wre": 448,
    "G2 Winter_theta_wre": -0.220229408,
    "G2 Winter_pr_1_99_wre": 9,
    "G2 Winter_ss_wre": 79,
    "G2 Winter_dss_wre": 478,
    "G2 Spring_theta_wre": 0.177127043,
    "G2 Spring_pr_1_99_wre": 18,
    "G2 Spring_ss_wre": 82,
    "G2 Spring_dss_wre": 518,
    "G3 Fall_theta_wre": 0.454035138,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 80,
    "G3 Fall_dss_wre": 545,
    "G3 Winter_theta_wre": 0.818530407,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 582,
    "G3 Spring_theta_wre": 0.994246507,
    "G3 Spring_pr_1_99_wre": 21,
    "G3 Spring_ss_wre": 78,
    "G3 Spring_dss_wre": 599
  },
  {
    "ID": 49,
    "KG Winter_theta_wre": -2.008955822,
    "KG Winter_pr_1_99_wre": 12,
    "KG Winter_ss_wre": 81,
    "KG Winter_dss_wre": 299,
    "KG Spring_theta_wre": -1.80283885,
    "KG Spring_pr_1_99_wre": 13,
    "KG Spring_ss_wre": 79,
    "KG Spring_dss_wre": 320,
    "G1 Fall_theta_wre": -1.353758155,
    "G1 Fall_pr_1_99_wre": 14,
    "G1 Fall_ss_wre": 81,
    "G1 Fall_dss_wre": 365,
    "G1 Winter_theta_wre": -1.119258384,
    "G1 Winter_pr_1_99_wre": 10,
    "G1 Winter_ss_wre": 80,
    "G1 Winter_dss_wre": 388,
    "G1 Spring_theta_wre": -0.793075754,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 80,
    "G1 Spring_dss_wre": 421,
    "G2 Fall_theta_wre": -0.521892469,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 80,
    "G2 Fall_dss_wre": 448,
    "G2 Winter_theta_wre": -0.211460049,
    "G2 Winter_pr_1_99_wre": 9,
    "G2 Winter_ss_wre": 80,
    "G2 Winter_dss_wre": 479,
    "G2 Spring_theta_wre": 0.19730543,
    "G2 Spring_pr_1_99_wre": 18,
    "G2 Spring_ss_wre": 82,
    "G2 Spring_dss_wre": 520,
    "G3 Fall_theta_wre": 0.468018166,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 80,
    "G3 Fall_dss_wre": 547,
    "G3 Winter_theta_wre": 0.828316021,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 583,
    "G3 Spring_theta_wre": 0.995780568,
    "G3 Spring_pr_1_99_wre": 21,
    "G3 Spring_ss_wre": 78,
    "G3 Spring_dss_wre": 600
  },
  {
    "ID": 50,
    "KG Winter_theta_wre": -2.005988163,
    "KG Winter_pr_1_99_wre": 12,
    "KG Winter_ss_wre": 81,
    "KG Winter_dss_wre": 299,
    "KG Spring_theta_wre": -1.801624798,
    "KG Spring_pr_1_99_wre": 13,
    "KG Spring_ss_wre": 79,
    "KG Spring_dss_wre": 320,
    "G1 Fall_theta_wre": -1.352064286,
    "G1 Fall_pr_1_99_wre": 14,
    "G1 Fall_ss_wre": 81,
    "G1 Fall_dss_wre": 365,
    "G1 Winter_theta_wre": -1.114181875,
    "G1 Winter_pr_1_99_wre": 10,
    "G1 Winter_ss_wre": 80,
    "G1 Winter_dss_wre": 389,
    "G1 Spring_theta_wre": -0.790594331,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 80,
    "G1 Spring_dss_wre": 421,
    "G2 Fall_theta_wre": -0.476553504,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 81,
    "G2 Fall_dss_wre": 452,
    "G2 Winter_theta_wre": -0.20726051,
    "G2 Winter_pr_1_99_wre": 9,
    "G2 Winter_ss_wre": 80,
    "G2 Winter_dss_wre": 479,
    "G2 Spring_theta_wre": 0.206925062,
    "G2 Spring_pr_1_99_wre": 19,
    "G2 Spring_ss_wre": 82,
    "G2 Spring_dss_wre": 521,
    "G3 Fall_theta_wre": 0.476162339,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 81,
    "G3 Fall_dss_wre": 548,
    "G3 Winter_theta_wre": 0.833959125,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 583,
    "G3 Spring_theta_wre": 1.011083599,
    "G3 Spring_pr_1_99_wre": 22,
    "G3 Spring_ss_wre": 79,
    "G3 Spring_dss_wre": 601
  },
  {
    "ID": 51,
    "KG Winter_theta_wre": -1.994415624,
    "KG Winter_pr_1_99_wre": 19,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 301,
    "KG Spring_theta_wre": -1.799909305,
    "KG Spring_pr_1_99_wre": 13,
    "KG Spring_ss_wre": 79,
    "KG Spring_dss_wre": 320,
    "G1 Fall_theta_wre": -1.349593249,
    "G1 Fall_pr_1_99_wre": 14,
    "G1 Fall_ss_wre": 81,
    "G1 Fall_dss_wre": 365,
    "G1 Winter_theta_wre": -1.10408238,
    "G1 Winter_pr_1_99_wre": 10,
    "G1 Winter_ss_wre": 80,
    "G1 Winter_dss_wre": 390,
    "G1 Spring_theta_wre": -0.790129214,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 80,
    "G1 Spring_dss_wre": 421,
    "G2 Fall_theta_wre": -0.465864174,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 81,
    "G2 Fall_dss_wre": 453,
    "G2 Winter_theta_wre": -0.197499353,
    "G2 Winter_pr_1_99_wre": 9,
    "G2 Winter_ss_wre": 80,
    "G2 Winter_dss_wre": 480,
    "G2 Spring_theta_wre": 0.207155051,
    "G2 Spring_pr_1_99_wre": 19,
    "G2 Spring_ss_wre": 82,
    "G2 Spring_dss_wre": 521,
    "G3 Fall_theta_wre": 0.479804659,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 81,
    "G3 Fall_dss_wre": 548,
    "G3 Winter_theta_wre": 0.834267553,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 583,
    "G3 Spring_theta_wre": 1.015696866,
    "G3 Spring_pr_1_99_wre": 22,
    "G3 Spring_ss_wre": 79,
    "G3 Spring_dss_wre": 602
  },
  {
    "ID": 52,
    "KG Winter_theta_wre": -1.991200596,
    "KG Winter_pr_1_99_wre": 19,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 301,
    "KG Spring_theta_wre": -1.791396913,
    "KG Spring_pr_1_99_wre": 13,
    "KG Spring_ss_wre": 80,
    "KG Spring_dss_wre": 321,
    "G1 Fall_theta_wre": -1.349512828,
    "G1 Fall_pr_1_99_wre": 14,
    "G1 Fall_ss_wre": 81,
    "G1 Fall_dss_wre": 365,
    "G1 Winter_theta_wre": -1.102636709,
    "G1 Winter_pr_1_99_wre": 10,
    "G1 Winter_ss_wre": 80,
    "G1 Winter_dss_wre": 390,
    "G1 Spring_theta_wre": -0.772726353,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 80,
    "G1 Spring_dss_wre": 423,
    "G2 Fall_theta_wre": -0.462749191,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 81,
    "G2 Fall_dss_wre": 454,
    "G2 Winter_theta_wre": -0.194043082,
    "G2 Winter_pr_1_99_wre": 9,
    "G2 Winter_ss_wre": 80,
    "G2 Winter_dss_wre": 481,
    "G2 Spring_theta_wre": 0.218813613,
    "G2 Spring_pr_1_99_wre": 19,
    "G2 Spring_ss_wre": 83,
    "G2 Spring_dss_wre": 522,
    "G3 Fall_theta_wre": 0.483479026,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 81,
    "G3 Fall_dss_wre": 548,
    "G3 Winter_theta_wre": 0.838139161,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 80,
    "G3 Winter_dss_wre": 584,
    "G3 Spring_theta_wre": 1.021679917,
    "G3 Spring_pr_1_99_wre": 23,
    "G3 Spring_ss_wre": 79,
    "G3 Spring_dss_wre": 602
  },
  {
    "ID": 53,
    "KG Winter_theta_wre": -1.986487572,
    "KG Winter_pr_1_99_wre": 20,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 301,
    "KG Spring_theta_wre": -1.785080998,
    "KG Spring_pr_1_99_wre": 14,
    "KG Spring_ss_wre": 80,
    "KG Spring_dss_wre": 321,
    "G1 Fall_theta_wre": -1.345944166,
    "G1 Fall_pr_1_99_wre": 14,
    "G1 Fall_ss_wre": 81,
    "G1 Fall_dss_wre": 365,
    "G1 Winter_theta_wre": -1.071190936,
    "G1 Winter_pr_1_99_wre": 11,
    "G1 Winter_ss_wre": 81,
    "G1 Winter_dss_wre": 393,
    "G1 Spring_theta_wre": -0.727989336,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 81,
    "G1 Spring_dss_wre": 427,
    "G2 Fall_theta_wre": -0.453656902,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 81,
    "G2 Fall_dss_wre": 455,
    "G2 Winter_theta_wre": -0.174074866,
    "G2 Winter_pr_1_99_wre": 9,
    "G2 Winter_ss_wre": 80,
    "G2 Winter_dss_wre": 483,
    "G2 Spring_theta_wre": 0.220081209,
    "G2 Spring_pr_1_99_wre": 19,
    "G2 Spring_ss_wre": 83,
    "G2 Spring_dss_wre": 522,
    "G3 Fall_theta_wre": 0.484129865,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 81,
    "G3 Fall_dss_wre": 548,
    "G3 Winter_theta_wre": 0.884831561,
    "G3 Winter_pr_1_99_wre": 7,
    "G3 Winter_ss_wre": 81,
    "G3 Winter_dss_wre": 588,
    "G3 Spring_theta_wre": 1.037497114,
    "G3 Spring_pr_1_99_wre": 23,
    "G3 Spring_ss_wre": 79,
    "G3 Spring_dss_wre": 604
  },
  {
    "ID": 54,
    "KG Winter_theta_wre": -1.984968557,
    "KG Winter_pr_1_99_wre": 20,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 302,
    "KG Spring_theta_wre": -1.783588095,
    "KG Spring_pr_1_99_wre": 14,
    "KG Spring_ss_wre": 80,
    "KG Spring_dss_wre": 322,
    "G1 Fall_theta_wre": -1.342892256,
    "G1 Fall_pr_1_99_wre": 14,
    "G1 Fall_ss_wre": 81,
    "G1 Fall_dss_wre": 366,
    "G1 Winter_theta_wre": -1.049776376,
    "G1 Winter_pr_1_99_wre": 11,
    "G1 Winter_ss_wre": 81,
    "G1 Winter_dss_wre": 395,
    "G1 Spring_theta_wre": -0.727894766,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 81,
    "G1 Spring_dss_wre": 427,
    "G2 Fall_theta_wre": -0.44836367,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 81,
    "G2 Fall_dss_wre": 455,
    "G2 Winter_theta_wre": -0.127159315,
    "G2 Winter_pr_1_99_wre": 9,
    "G2 Winter_ss_wre": 81,
    "G2 Winter_dss_wre": 487,
    "G2 Spring_theta_wre": 0.235441483,
    "G2 Spring_pr_1_99_wre": 20,
    "G2 Spring_ss_wre": 83,
    "G2 Spring_dss_wre": 524,
    "G3 Fall_theta_wre": 0.48896717,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 81,
    "G3 Fall_dss_wre": 549,
    "G3 Winter_theta_wre": 0.901585618,
    "G3 Winter_pr_1_99_wre": 8,
    "G3 Winter_ss_wre": 82,
    "G3 Winter_dss_wre": 590,
    "G3 Spring_theta_wre": 1.081108675,
    "G3 Spring_pr_1_99_wre": 25,
    "G3 Spring_ss_wre": 80,
    "G3 Spring_dss_wre": 608
  },
  {
    "ID": 55,
    "KG Winter_theta_wre": -1.982118921,
    "KG Winter_pr_1_99_wre": 20,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 302,
    "KG Spring_theta_wre": -1.773952377,
    "KG Spring_pr_1_99_wre": 14,
    "KG Spring_ss_wre": 80,
    "KG Spring_dss_wre": 323,
    "G1 Fall_theta_wre": -1.339486918,
    "G1 Fall_pr_1_99_wre": 14,
    "G1 Fall_ss_wre": 81,
    "G1 Fall_dss_wre": 366,
    "G1 Winter_theta_wre": -1.027932876,
    "G1 Winter_pr_1_99_wre": 11,
    "G1 Winter_ss_wre": 82,
    "G1 Winter_dss_wre": 397,
    "G1 Spring_theta_wre": -0.717936229,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 81,
    "G1 Spring_dss_wre": 428,
    "G2 Fall_theta_wre": -0.442138402,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 81,
    "G2 Fall_dss_wre": 456,
    "G2 Winter_theta_wre": -0.11495858,
    "G2 Winter_pr_1_99_wre": 9,
    "G2 Winter_ss_wre": 81,
    "G2 Winter_dss_wre": 489,
    "G2 Spring_theta_wre": 0.238524937,
    "G2 Spring_pr_1_99_wre": 20,
    "G2 Spring_ss_wre": 83,
    "G2 Spring_dss_wre": 524,
    "G3 Fall_theta_wre": 0.503814801,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 81,
    "G3 Fall_dss_wre": 550,
    "G3 Winter_theta_wre": 0.915663336,
    "G3 Winter_pr_1_99_wre": 9,
    "G3 Winter_ss_wre": 82,
    "G3 Winter_dss_wre": 592,
    "G3 Spring_theta_wre": 1.08523104,
    "G3 Spring_pr_1_99_wre": 25,
    "G3 Spring_ss_wre": 80,
    "G3 Spring_dss_wre": 609
  },
  {
    "ID": 56,
    "KG Winter_theta_wre": -1.981657475,
    "KG Winter_pr_1_99_wre": 20,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 302,
    "KG Spring_theta_wre": -1.77026092,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 80,
    "KG Spring_dss_wre": 323,
    "G1 Fall_theta_wre": -1.325575636,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 82,
    "G1 Fall_dss_wre": 367,
    "G1 Winter_theta_wre": -1.021395177,
    "G1 Winter_pr_1_99_wre": 11,
    "G1 Winter_ss_wre": 82,
    "G1 Winter_dss_wre": 398,
    "G1 Spring_theta_wre": -0.710958666,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 82,
    "G1 Spring_dss_wre": 429,
    "G2 Fall_theta_wre": -0.43803385,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 82,
    "G2 Fall_dss_wre": 456,
    "G2 Winter_theta_wre": -0.102827728,
    "G2 Winter_pr_1_99_wre": 10,
    "G2 Winter_ss_wre": 82,
    "G2 Winter_dss_wre": 490,
    "G2 Spring_theta_wre": 0.239246682,
    "G2 Spring_pr_1_99_wre": 20,
    "G2 Spring_ss_wre": 83,
    "G2 Spring_dss_wre": 524,
    "G3 Fall_theta_wre": 0.505506936,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 81,
    "G3 Fall_dss_wre": 551,
    "G3 Winter_theta_wre": 0.920443007,
    "G3 Winter_pr_1_99_wre": 9,
    "G3 Winter_ss_wre": 82,
    "G3 Winter_dss_wre": 592,
    "G3 Spring_theta_wre": 1.089033511,
    "G3 Spring_pr_1_99_wre": 25,
    "G3 Spring_ss_wre": 80,
    "G3 Spring_dss_wre": 609
  },
  {
    "ID": 57,
    "KG Winter_theta_wre": -1.981484254,
    "KG Winter_pr_1_99_wre": 20,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 302,
    "KG Spring_theta_wre": -1.766670662,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 80,
    "KG Spring_dss_wre": 323,
    "G1 Fall_theta_wre": -1.315782422,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 82,
    "G1 Fall_dss_wre": 368,
    "G1 Winter_theta_wre": -1.012079664,
    "G1 Winter_pr_1_99_wre": 11,
    "G1 Winter_ss_wre": 82,
    "G1 Winter_dss_wre": 399,
    "G1 Spring_theta_wre": -0.704850336,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 82,
    "G1 Spring_dss_wre": 430,
    "G2 Fall_theta_wre": -0.412836844,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 82,
    "G2 Fall_dss_wre": 459,
    "G2 Winter_theta_wre": -0.091810434,
    "G2 Winter_pr_1_99_wre": 11,
    "G2 Winter_ss_wre": 82,
    "G2 Winter_dss_wre": 491,
    "G2 Spring_theta_wre": 0.244664639,
    "G2 Spring_pr_1_99_wre": 20,
    "G2 Spring_ss_wre": 83,
    "G2 Spring_dss_wre": 524,
    "G3 Fall_theta_wre": 0.507699738,
    "G3 Fall_pr_1_99_wre": 17,
    "G3 Fall_ss_wre": 81,
    "G3 Fall_dss_wre": 551,
    "G3 Winter_theta_wre": 0.924155189,
    "G3 Winter_pr_1_99_wre": 9,
    "G3 Winter_ss_wre": 82,
    "G3 Winter_dss_wre": 592,
    "G3 Spring_theta_wre": 1.090547691,
    "G3 Spring_pr_1_99_wre": 26,
    "G3 Spring_ss_wre": 80,
    "G3 Spring_dss_wre": 609
  },
  {
    "ID": 58,
    "KG Winter_theta_wre": -1.975984259,
    "KG Winter_pr_1_99_wre": 20,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 302,
    "KG Spring_theta_wre": -1.754931061,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 80,
    "KG Spring_dss_wre": 325,
    "G1 Fall_theta_wre": -1.307092265,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 82,
    "G1 Fall_dss_wre": 369,
    "G1 Winter_theta_wre": -0.999488666,
    "G1 Winter_pr_1_99_wre": 12,
    "G1 Winter_ss_wre": 82,
    "G1 Winter_dss_wre": 400,
    "G1 Spring_theta_wre": -0.704678319,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 82,
    "G1 Spring_dss_wre": 430,
    "G2 Fall_theta_wre": -0.396898021,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 82,
    "G2 Fall_dss_wre": 460,
    "G2 Winter_theta_wre": -0.08974089,
    "G2 Winter_pr_1_99_wre": 11,
    "G2 Winter_ss_wre": 82,
    "G2 Winter_dss_wre": 491,
    "G2 Spring_theta_wre": 0.24766015,
    "G2 Spring_pr_1_99_wre": 20,
    "G2 Spring_ss_wre": 83,
    "G2 Spring_dss_wre": 525,
    "G3 Fall_theta_wre": 0.531062459,
    "G3 Fall_pr_1_99_wre": 18,
    "G3 Fall_ss_wre": 82,
    "G3 Fall_dss_wre": 553,
    "G3 Winter_theta_wre": 0.925709655,
    "G3 Winter_pr_1_99_wre": 9,
    "G3 Winter_ss_wre": 82,
    "G3 Winter_dss_wre": 593,
    "G3 Spring_theta_wre": 1.096105235,
    "G3 Spring_pr_1_99_wre": 26,
    "G3 Spring_ss_wre": 80,
    "G3 Spring_dss_wre": 610
  },
  {
    "ID": 59,
    "KG Winter_theta_wre": -1.972161644,
    "KG Winter_pr_1_99_wre": 20,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 303,
    "KG Spring_theta_wre": -1.747687519,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 80,
    "KG Spring_dss_wre": 325,
    "G1 Fall_theta_wre": -1.302450359,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 82,
    "G1 Fall_dss_wre": 370,
    "G1 Winter_theta_wre": -0.996753415,
    "G1 Winter_pr_1_99_wre": 12,
    "G1 Winter_ss_wre": 82,
    "G1 Winter_dss_wre": 400,
    "G1 Spring_theta_wre": -0.702976019,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 82,
    "G1 Spring_dss_wre": 430,
    "G2 Fall_theta_wre": -0.392408013,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 82,
    "G2 Fall_dss_wre": 461,
    "G2 Winter_theta_wre": -0.066472313,
    "G2 Winter_pr_1_99_wre": 11,
    "G2 Winter_ss_wre": 82,
    "G2 Winter_dss_wre": 493,
    "G2 Spring_theta_wre": 0.253700628,
    "G2 Spring_pr_1_99_wre": 20,
    "G2 Spring_ss_wre": 83,
    "G2 Spring_dss_wre": 525,
    "G3 Fall_theta_wre": 0.547494283,
    "G3 Fall_pr_1_99_wre": 18,
    "G3 Fall_ss_wre": 82,
    "G3 Fall_dss_wre": 555,
    "G3 Winter_theta_wre": 0.940476234,
    "G3 Winter_pr_1_99_wre": 9,
    "G3 Winter_ss_wre": 82,
    "G3 Winter_dss_wre": 594,
    "G3 Spring_theta_wre": 1.116449331,
    "G3 Spring_pr_1_99_wre": 27,
    "G3 Spring_ss_wre": 81,
    "G3 Spring_dss_wre": 612
  },
  {
    "ID": 60,
    "KG Winter_theta_wre": -1.971367611,
    "KG Winter_pr_1_99_wre": 20,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 303,
    "KG Spring_theta_wre": -1.745205369,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 80,
    "KG Spring_dss_wre": 325,
    "G1 Fall_theta_wre": -1.301556829,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 82,
    "G1 Fall_dss_wre": 370,
    "G1 Winter_theta_wre": -0.973195902,
    "G1 Winter_pr_1_99_wre": 13,
    "G1 Winter_ss_wre": 83,
    "G1 Winter_dss_wre": 403,
    "G1 Spring_theta_wre": -0.68362741,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 82,
    "G1 Spring_dss_wre": 432,
    "G2 Fall_theta_wre": -0.388716113,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 82,
    "G2 Fall_dss_wre": 461,
    "G2 Winter_theta_wre": -0.057588287,
    "G2 Winter_pr_1_99_wre": 11,
    "G2 Winter_ss_wre": 82,
    "G2 Winter_dss_wre": 494,
    "G2 Spring_theta_wre": 0.258241478,
    "G2 Spring_pr_1_99_wre": 20,
    "G2 Spring_ss_wre": 83,
    "G2 Spring_dss_wre": 526,
    "G3 Fall_theta_wre": 0.565457724,
    "G3 Fall_pr_1_99_wre": 19,
    "G3 Fall_ss_wre": 82,
    "G3 Fall_dss_wre": 557,
    "G3 Winter_theta_wre": 0.950503627,
    "G3 Winter_pr_1_99_wre": 9,
    "G3 Winter_ss_wre": 83,
    "G3 Winter_dss_wre": 595,
    "G3 Spring_theta_wre": 1.118183383,
    "G3 Spring_pr_1_99_wre": 27,
    "G3 Spring_ss_wre": 81,
    "G3 Spring_dss_wre": 612
  },
  {
    "ID": 61,
    "KG Winter_theta_wre": -1.966238196,
    "KG Winter_pr_1_99_wre": 20,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 303,
    "KG Spring_theta_wre": -1.741593264,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 81,
    "KG Spring_dss_wre": 326,
    "G1 Fall_theta_wre": -1.295991977,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 82,
    "G1 Fall_dss_wre": 370,
    "G1 Winter_theta_wre": -0.964662988,
    "G1 Winter_pr_1_99_wre": 13,
    "G1 Winter_ss_wre": 83,
    "G1 Winter_dss_wre": 404,
    "G1 Spring_theta_wre": -0.671581134,
    "G1 Spring_pr_1_99_wre": 9,
    "G1 Spring_ss_wre": 82,
    "G1 Spring_dss_wre": 433,
    "G2 Fall_theta_wre": -0.384268571,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 83,
    "G2 Fall_dss_wre": 462,
    "G2 Winter_theta_wre": -0.053796414,
    "G2 Winter_pr_1_99_wre": 11,
    "G2 Winter_ss_wre": 82,
    "G2 Winter_dss_wre": 495,
    "G2 Spring_theta_wre": 0.267426187,
    "G2 Spring_pr_1_99_wre": 20,
    "G2 Spring_ss_wre": 83,
    "G2 Spring_dss_wre": 527,
    "G3 Fall_theta_wre": 0.571843186,
    "G3 Fall_pr_1_99_wre": 19,
    "G3 Fall_ss_wre": 82,
    "G3 Fall_dss_wre": 557,
    "G3 Winter_theta_wre": 0.972048594,
    "G3 Winter_pr_1_99_wre": 9,
    "G3 Winter_ss_wre": 83,
    "G3 Winter_dss_wre": 597,
    "G3 Spring_theta_wre": 1.159010362,
    "G3 Spring_pr_1_99_wre": 29,
    "G3 Spring_ss_wre": 81,
    "G3 Spring_dss_wre": 616
  },
  {
    "ID": 62,
    "KG Winter_theta_wre": -1.955162058,
    "KG Winter_pr_1_99_wre": 20,
    "KG Winter_ss_wre": 82,
    "KG Winter_dss_wre": 304,
    "KG Spring_theta_wre": -1.711802563,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 81,
    "KG Spring_dss_wre": 329,
    "G1 Fall_theta_wre": -1.295896788,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 82,
    "G1 Fall_dss_wre": 370,
    "G1 Winter_theta_wre": -0.963738261,
    "G1 Winter_pr_1_99_wre": 13,
    "G1 Winter_ss_wre": 83,
    "G1 Winter_dss_wre": 404,
    "G1 Spring_theta_wre": -0.661254868,
    "G1 Spring_pr_1_99_wre": 10,
    "G1 Spring_ss_wre": 82,
    "G1 Spring_dss_wre": 434,
    "G2 Fall_theta_wre": -0.383866986,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 83,
    "G2 Fall_dss_wre": 462,
    "G2 Winter_theta_wre": -0.050174625,
    "G2 Winter_pr_1_99_wre": 11,
    "G2 Winter_ss_wre": 83,
    "G2 Winter_dss_wre": 495,
    "G2 Spring_theta_wre": 0.27280415,
    "G2 Spring_pr_1_99_wre": 20,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 527,
    "G3 Fall_theta_wre": 0.582318971,
    "G3 Fall_pr_1_99_wre": 19,
    "G3 Fall_ss_wre": 83,
    "G3 Fall_dss_wre": 558,
    "G3 Winter_theta_wre": 0.972418816,
    "G3 Winter_pr_1_99_wre": 9,
    "G3 Winter_ss_wre": 83,
    "G3 Winter_dss_wre": 597,
    "G3 Spring_theta_wre": 1.172758349,
    "G3 Spring_pr_1_99_wre": 29,
    "G3 Spring_ss_wre": 82,
    "G3 Spring_dss_wre": 617
  },
  {
    "ID": 63,
    "KG Winter_theta_wre": -1.943286912,
    "KG Winter_pr_1_99_wre": 21,
    "KG Winter_ss_wre": 83,
    "KG Winter_dss_wre": 306,
    "KG Spring_theta_wre": -1.693264497,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 81,
    "KG Spring_dss_wre": 331,
    "G1 Fall_theta_wre": -1.294669621,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 82,
    "G1 Fall_dss_wre": 371,
    "G1 Winter_theta_wre": -0.950536105,
    "G1 Winter_pr_1_99_wre": 13,
    "G1 Winter_ss_wre": 83,
    "G1 Winter_dss_wre": 405,
    "G1 Spring_theta_wre": -0.642657826,
    "G1 Spring_pr_1_99_wre": 10,
    "G1 Spring_ss_wre": 83,
    "G1 Spring_dss_wre": 436,
    "G2 Fall_theta_wre": -0.378998349,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 83,
    "G2 Fall_dss_wre": 462,
    "G2 Winter_theta_wre": -0.048660978,
    "G2 Winter_pr_1_99_wre": 11,
    "G2 Winter_ss_wre": 83,
    "G2 Winter_dss_wre": 495,
    "G2 Spring_theta_wre": 0.284217704,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 528,
    "G3 Fall_theta_wre": 0.586242819,
    "G3 Fall_pr_1_99_wre": 19,
    "G3 Fall_ss_wre": 83,
    "G3 Fall_dss_wre": 559,
    "G3 Winter_theta_wre": 0.97619008,
    "G3 Winter_pr_1_99_wre": 9,
    "G3 Winter_ss_wre": 83,
    "G3 Winter_dss_wre": 598,
    "G3 Spring_theta_wre": 1.178220365,
    "G3 Spring_pr_1_99_wre": 29,
    "G3 Spring_ss_wre": 82,
    "G3 Spring_dss_wre": 618
  },
  {
    "ID": 64,
    "KG Winter_theta_wre": -1.939422725,
    "KG Winter_pr_1_99_wre": 21,
    "KG Winter_ss_wre": 83,
    "KG Winter_dss_wre": 306,
    "KG Spring_theta_wre": -1.689821923,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 81,
    "KG Spring_dss_wre": 331,
    "G1 Fall_theta_wre": -1.290953357,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 82,
    "G1 Fall_dss_wre": 371,
    "G1 Winter_theta_wre": -0.949393355,
    "G1 Winter_pr_1_99_wre": 13,
    "G1 Winter_ss_wre": 83,
    "G1 Winter_dss_wre": 405,
    "G1 Spring_theta_wre": -0.628069707,
    "G1 Spring_pr_1_99_wre": 10,
    "G1 Spring_ss_wre": 83,
    "G1 Spring_dss_wre": 437,
    "G2 Fall_theta_wre": -0.377254765,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 83,
    "G2 Fall_dss_wre": 462,
    "G2 Winter_theta_wre": -0.02101908,
    "G2 Winter_pr_1_99_wre": 11,
    "G2 Winter_ss_wre": 83,
    "G2 Winter_dss_wre": 498,
    "G2 Spring_theta_wre": 0.284245292,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 528,
    "G3 Fall_theta_wre": 0.593171149,
    "G3 Fall_pr_1_99_wre": 19,
    "G3 Fall_ss_wre": 83,
    "G3 Fall_dss_wre": 559,
    "G3 Winter_theta_wre": 0.978051106,
    "G3 Winter_pr_1_99_wre": 9,
    "G3 Winter_ss_wre": 83,
    "G3 Winter_dss_wre": 598,
    "G3 Spring_theta_wre": 1.184756003,
    "G3 Spring_pr_1_99_wre": 29,
    "G3 Spring_ss_wre": 82,
    "G3 Spring_dss_wre": 618
  },
  {
    "ID": 65,
    "KG Winter_theta_wre": -1.919686038,
    "KG Winter_pr_1_99_wre": 21,
    "KG Winter_ss_wre": 83,
    "KG Winter_dss_wre": 308,
    "KG Spring_theta_wre": -1.688966787,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 81,
    "KG Spring_dss_wre": 331,
    "G1 Fall_theta_wre": -1.280401547,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 82,
    "G1 Fall_dss_wre": 372,
    "G1 Winter_theta_wre": -0.948709045,
    "G1 Winter_pr_1_99_wre": 13,
    "G1 Winter_ss_wre": 83,
    "G1 Winter_dss_wre": 405,
    "G1 Spring_theta_wre": -0.624310541,
    "G1 Spring_pr_1_99_wre": 10,
    "G1 Spring_ss_wre": 83,
    "G1 Spring_dss_wre": 438,
    "G2 Fall_theta_wre": -0.355510116,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 83,
    "G2 Fall_dss_wre": 464,
    "G2 Winter_theta_wre": -0.005776382,
    "G2 Winter_pr_1_99_wre": 11,
    "G2 Winter_ss_wre": 83,
    "G2 Winter_dss_wre": 499,
    "G2 Spring_theta_wre": 0.286968138,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 529,
    "G3 Fall_theta_wre": 0.59821401,
    "G3 Fall_pr_1_99_wre": 19,
    "G3 Fall_ss_wre": 83,
    "G3 Fall_dss_wre": 560,
    "G3 Winter_theta_wre": 0.984335808,
    "G3 Winter_pr_1_99_wre": 10,
    "G3 Winter_ss_wre": 83,
    "G3 Winter_dss_wre": 598,
    "G3 Spring_theta_wre": 1.192784913,
    "G3 Spring_pr_1_99_wre": 29,
    "G3 Spring_ss_wre": 82,
    "G3 Spring_dss_wre": 619
  },
  {
    "ID": 66,
    "KG Winter_theta_wre": -1.916239438,
    "KG Winter_pr_1_99_wre": 21,
    "KG Winter_ss_wre": 83,
    "KG Winter_dss_wre": 308,
    "KG Spring_theta_wre": -1.688906344,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 81,
    "KG Spring_dss_wre": 331,
    "G1 Fall_theta_wre": -1.271716893,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 83,
    "G1 Fall_dss_wre": 373,
    "G1 Winter_theta_wre": -0.948039698,
    "G1 Winter_pr_1_99_wre": 13,
    "G1 Winter_ss_wre": 83,
    "G1 Winter_dss_wre": 405,
    "G1 Spring_theta_wre": -0.617683717,
    "G1 Spring_pr_1_99_wre": 10,
    "G1 Spring_ss_wre": 83,
    "G1 Spring_dss_wre": 438,
    "G2 Fall_theta_wre": -0.348505387,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 83,
    "G2 Fall_dss_wre": 465,
    "G2 Winter_theta_wre": 0.002378945,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 83,
    "G2 Winter_dss_wre": 500,
    "G2 Spring_theta_wre": 0.288399825,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 529,
    "G3 Fall_theta_wre": 0.626211499,
    "G3 Fall_pr_1_99_wre": 20,
    "G3 Fall_ss_wre": 83,
    "G3 Fall_dss_wre": 563,
    "G3 Winter_theta_wre": 1.007675629,
    "G3 Winter_pr_1_99_wre": 10,
    "G3 Winter_ss_wre": 84,
    "G3 Winter_dss_wre": 601,
    "G3 Spring_theta_wre": 1.193185646,
    "G3 Spring_pr_1_99_wre": 29,
    "G3 Spring_ss_wre": 82,
    "G3 Spring_dss_wre": 619
  },
  {
    "ID": 67,
    "KG Winter_theta_wre": -1.905377976,
    "KG Winter_pr_1_99_wre": 21,
    "KG Winter_ss_wre": 83,
    "KG Winter_dss_wre": 309,
    "KG Spring_theta_wre": -1.656739513,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 82,
    "KG Spring_dss_wre": 334,
    "G1 Fall_theta_wre": -1.269487375,
    "G1 Fall_pr_1_99_wre": 15,
    "G1 Fall_ss_wre": 83,
    "G1 Fall_dss_wre": 373,
    "G1 Winter_theta_wre": -0.947670095,
    "G1 Winter_pr_1_99_wre": 13,
    "G1 Winter_ss_wre": 83,
    "G1 Winter_dss_wre": 405,
    "G1 Spring_theta_wre": -0.611954523,
    "G1 Spring_pr_1_99_wre": 10,
    "G1 Spring_ss_wre": 83,
    "G1 Spring_dss_wre": 439,
    "G2 Fall_theta_wre": -0.338648958,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 83,
    "G2 Fall_dss_wre": 466,
    "G2 Winter_theta_wre": 0.004547803,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 84,
    "G2 Winter_dss_wre": 500,
    "G2 Spring_theta_wre": 0.291928098,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 529,
    "G3 Fall_theta_wre": 0.632799609,
    "G3 Fall_pr_1_99_wre": 20,
    "G3 Fall_ss_wre": 83,
    "G3 Fall_dss_wre": 563,
    "G3 Winter_theta_wre": 1.010716064,
    "G3 Winter_pr_1_99_wre": 10,
    "G3 Winter_ss_wre": 84,
    "G3 Winter_dss_wre": 601,
    "G3 Spring_theta_wre": 1.212666486,
    "G3 Spring_pr_1_99_wre": 30,
    "G3 Spring_ss_wre": 82,
    "G3 Spring_dss_wre": 621
  },
  {
    "ID": 68,
    "KG Winter_theta_wre": -1.89083908,
    "KG Winter_pr_1_99_wre": 21,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 311,
    "KG Spring_theta_wre": -1.649912012,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 82,
    "KG Spring_dss_wre": 335,
    "G1 Fall_theta_wre": -1.237493267,
    "G1 Fall_pr_1_99_wre": 20,
    "G1 Fall_ss_wre": 83,
    "G1 Fall_dss_wre": 376,
    "G1 Winter_theta_wre": -0.938003941,
    "G1 Winter_pr_1_99_wre": 14,
    "G1 Winter_ss_wre": 84,
    "G1 Winter_dss_wre": 406,
    "G1 Spring_theta_wre": -0.599130892,
    "G1 Spring_pr_1_99_wre": 10,
    "G1 Spring_ss_wre": 84,
    "G1 Spring_dss_wre": 440,
    "G2 Fall_theta_wre": -0.333575357,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 83,
    "G2 Fall_dss_wre": 467,
    "G2 Winter_theta_wre": 0.005768103,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 84,
    "G2 Winter_dss_wre": 501,
    "G2 Spring_theta_wre": 0.292440988,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 529,
    "G3 Fall_theta_wre": 0.648355401,
    "G3 Fall_pr_1_99_wre": 20,
    "G3 Fall_ss_wre": 84,
    "G3 Fall_dss_wre": 565,
    "G3 Winter_theta_wre": 1.030160771,
    "G3 Winter_pr_1_99_wre": 10,
    "G3 Winter_ss_wre": 84,
    "G3 Winter_dss_wre": 603,
    "G3 Spring_theta_wre": 1.21832192,
    "G3 Spring_pr_1_99_wre": 30,
    "G3 Spring_ss_wre": 82,
    "G3 Spring_dss_wre": 622
  },
  {
    "ID": 69,
    "KG Winter_theta_wre": -1.882359301,
    "KG Winter_pr_1_99_wre": 21,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 312,
    "KG Spring_theta_wre": -1.648966475,
    "KG Spring_pr_1_99_wre": 15,
    "KG Spring_ss_wre": 82,
    "KG Spring_dss_wre": 335,
    "G1 Fall_theta_wre": -1.236400298,
    "G1 Fall_pr_1_99_wre": 20,
    "G1 Fall_ss_wre": 83,
    "G1 Fall_dss_wre": 376,
    "G1 Winter_theta_wre": -0.937116927,
    "G1 Winter_pr_1_99_wre": 14,
    "G1 Winter_ss_wre": 84,
    "G1 Winter_dss_wre": 406,
    "G1 Spring_theta_wre": -0.593566418,
    "G1 Spring_pr_1_99_wre": 10,
    "G1 Spring_ss_wre": 84,
    "G1 Spring_dss_wre": 441,
    "G2 Fall_theta_wre": -0.333562173,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 83,
    "G2 Fall_dss_wre": 467,
    "G2 Winter_theta_wre": 0.020130517,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 84,
    "G2 Winter_dss_wre": 502,
    "G2 Spring_theta_wre": 0.302693122,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 530,
    "G3 Fall_theta_wre": 0.649759129,
    "G3 Fall_pr_1_99_wre": 21,
    "G3 Fall_ss_wre": 84,
    "G3 Fall_dss_wre": 565,
    "G3 Winter_theta_wre": 1.032972661,
    "G3 Winter_pr_1_99_wre": 11,
    "G3 Winter_ss_wre": 84,
    "G3 Winter_dss_wre": 603,
    "G3 Spring_theta_wre": 1.228794892,
    "G3 Spring_pr_1_99_wre": 31,
    "G3 Spring_ss_wre": 83,
    "G3 Spring_dss_wre": 623
  },
  {
    "ID": 70,
    "KG Winter_theta_wre": -1.878044237,
    "KG Winter_pr_1_99_wre": 21,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 312,
    "KG Spring_theta_wre": -1.624793892,
    "KG Spring_pr_1_99_wre": 16,
    "KG Spring_ss_wre": 83,
    "KG Spring_dss_wre": 338,
    "G1 Fall_theta_wre": -1.191789858,
    "G1 Fall_pr_1_99_wre": 21,
    "G1 Fall_ss_wre": 84,
    "G1 Fall_dss_wre": 381,
    "G1 Winter_theta_wre": -0.911712386,
    "G1 Winter_pr_1_99_wre": 14,
    "G1 Winter_ss_wre": 84,
    "G1 Winter_dss_wre": 409,
    "G1 Spring_theta_wre": -0.585023193,
    "G1 Spring_pr_1_99_wre": 10,
    "G1 Spring_ss_wre": 84,
    "G1 Spring_dss_wre": 441,
    "G2 Fall_theta_wre": -0.323419619,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 84,
    "G2 Fall_dss_wre": 468,
    "G2 Winter_theta_wre": 0.02316559,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 84,
    "G2 Winter_dss_wre": 502,
    "G2 Spring_theta_wre": 0.311924189,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 531,
    "G3 Fall_theta_wre": 0.651247942,
    "G3 Fall_pr_1_99_wre": 21,
    "G3 Fall_ss_wre": 84,
    "G3 Fall_dss_wre": 565,
    "G3 Winter_theta_wre": 1.034368919,
    "G3 Winter_pr_1_99_wre": 11,
    "G3 Winter_ss_wre": 84,
    "G3 Winter_dss_wre": 603,
    "G3 Spring_theta_wre": 1.246264752,
    "G3 Spring_pr_1_99_wre": 33,
    "G3 Spring_ss_wre": 83,
    "G3 Spring_dss_wre": 625
  },
  {
    "ID": 71,
    "KG Winter_theta_wre": -1.876766846,
    "KG Winter_pr_1_99_wre": 21,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 312,
    "KG Spring_theta_wre": -1.591563861,
    "KG Spring_pr_1_99_wre": 17,
    "KG Spring_ss_wre": 83,
    "KG Spring_dss_wre": 341,
    "G1 Fall_theta_wre": -1.174357653,
    "G1 Fall_pr_1_99_wre": 21,
    "G1 Fall_ss_wre": 84,
    "G1 Fall_dss_wre": 383,
    "G1 Winter_theta_wre": -0.909310885,
    "G1 Winter_pr_1_99_wre": 14,
    "G1 Winter_ss_wre": 84,
    "G1 Winter_dss_wre": 409,
    "G1 Spring_theta_wre": -0.581436468,
    "G1 Spring_pr_1_99_wre": 10,
    "G1 Spring_ss_wre": 84,
    "G1 Spring_dss_wre": 442,
    "G2 Fall_theta_wre": -0.308979487,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 84,
    "G2 Fall_dss_wre": 469,
    "G2 Winter_theta_wre": 0.03242447,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 84,
    "G2 Winter_dss_wre": 503,
    "G2 Spring_theta_wre": 0.31365281,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 531,
    "G3 Fall_theta_wre": 0.654260444,
    "G3 Fall_pr_1_99_wre": 22,
    "G3 Fall_ss_wre": 84,
    "G3 Fall_dss_wre": 565,
    "G3 Winter_theta_wre": 1.054401007,
    "G3 Winter_pr_1_99_wre": 11,
    "G3 Winter_ss_wre": 85,
    "G3 Winter_dss_wre": 605,
    "G3 Spring_theta_wre": 1.248630055,
    "G3 Spring_pr_1_99_wre": 33,
    "G3 Spring_ss_wre": 83,
    "G3 Spring_dss_wre": 625
  },
  {
    "ID": 72,
    "KG Winter_theta_wre": -1.87237325,
    "KG Winter_pr_1_99_wre": 21,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 313,
    "KG Spring_theta_wre": -1.58994423,
    "KG Spring_pr_1_99_wre": 17,
    "KG Spring_ss_wre": 83,
    "KG Spring_dss_wre": 341,
    "G1 Fall_theta_wre": -1.173025548,
    "G1 Fall_pr_1_99_wre": 21,
    "G1 Fall_ss_wre": 84,
    "G1 Fall_dss_wre": 383,
    "G1 Winter_theta_wre": -0.907244428,
    "G1 Winter_pr_1_99_wre": 14,
    "G1 Winter_ss_wre": 84,
    "G1 Winter_dss_wre": 409,
    "G1 Spring_theta_wre": -0.576541621,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 84,
    "G1 Spring_dss_wre": 442,
    "G2 Fall_theta_wre": -0.286745069,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 84,
    "G2 Fall_dss_wre": 471,
    "G2 Winter_theta_wre": 0.04256895,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 84,
    "G2 Winter_dss_wre": 504,
    "G2 Spring_theta_wre": 0.319630012,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 84,
    "G2 Spring_dss_wre": 532,
    "G3 Fall_theta_wre": 0.662846104,
    "G3 Fall_pr_1_99_wre": 22,
    "G3 Fall_ss_wre": 84,
    "G3 Fall_dss_wre": 566,
    "G3 Winter_theta_wre": 1.060236114,
    "G3 Winter_pr_1_99_wre": 12,
    "G3 Winter_ss_wre": 85,
    "G3 Winter_dss_wre": 606,
    "G3 Spring_theta_wre": 1.287564871,
    "G3 Spring_pr_1_99_wre": 33,
    "G3 Spring_ss_wre": 84,
    "G3 Spring_dss_wre": 629
  },
  {
    "ID": 73,
    "KG Winter_theta_wre": -1.866669834,
    "KG Winter_pr_1_99_wre": 22,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 313,
    "KG Spring_theta_wre": -1.569586971,
    "KG Spring_pr_1_99_wre": 17,
    "KG Spring_ss_wre": 84,
    "KG Spring_dss_wre": 343,
    "G1 Fall_theta_wre": -1.169970485,
    "G1 Fall_pr_1_99_wre": 21,
    "G1 Fall_ss_wre": 84,
    "G1 Fall_dss_wre": 383,
    "G1 Winter_theta_wre": -0.902720497,
    "G1 Winter_pr_1_99_wre": 14,
    "G1 Winter_ss_wre": 84,
    "G1 Winter_dss_wre": 410,
    "G1 Spring_theta_wre": -0.57467263,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 84,
    "G1 Spring_dss_wre": 443,
    "G2 Fall_theta_wre": -0.284728499,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 84,
    "G2 Fall_dss_wre": 472,
    "G2 Winter_theta_wre": 0.043001873,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 84,
    "G2 Winter_dss_wre": 504,
    "G2 Spring_theta_wre": 0.329885736,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 85,
    "G2 Spring_dss_wre": 533,
    "G3 Fall_theta_wre": 0.663048476,
    "G3 Fall_pr_1_99_wre": 22,
    "G3 Fall_ss_wre": 84,
    "G3 Fall_dss_wre": 566,
    "G3 Winter_theta_wre": 1.062322897,
    "G3 Winter_pr_1_99_wre": 12,
    "G3 Winter_ss_wre": 85,
    "G3 Winter_dss_wre": 606,
    "G3 Spring_theta_wre": 1.291978191,
    "G3 Spring_pr_1_99_wre": 33,
    "G3 Spring_ss_wre": 84,
    "G3 Spring_dss_wre": 629
  },
  {
    "ID": 74,
    "KG Winter_theta_wre": -1.865123771,
    "KG Winter_pr_1_99_wre": 22,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 313,
    "KG Spring_theta_wre": -1.56745953,
    "KG Spring_pr_1_99_wre": 17,
    "KG Spring_ss_wre": 84,
    "KG Spring_dss_wre": 343,
    "G1 Fall_theta_wre": -1.162031712,
    "G1 Fall_pr_1_99_wre": 21,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 384,
    "G1 Winter_theta_wre": -0.870301935,
    "G1 Winter_pr_1_99_wre": 15,
    "G1 Winter_ss_wre": 85,
    "G1 Winter_dss_wre": 413,
    "G1 Spring_theta_wre": -0.567474594,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 84,
    "G1 Spring_dss_wre": 443,
    "G2 Fall_theta_wre": -0.276358206,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 84,
    "G2 Fall_dss_wre": 472,
    "G2 Winter_theta_wre": 0.057916206,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 84,
    "G2 Winter_dss_wre": 506,
    "G2 Spring_theta_wre": 0.333271338,
    "G2 Spring_pr_1_99_wre": 21,
    "G2 Spring_ss_wre": 85,
    "G2 Spring_dss_wre": 533,
    "G3 Fall_theta_wre": 0.666625609,
    "G3 Fall_pr_1_99_wre": 22,
    "G3 Fall_ss_wre": 84,
    "G3 Fall_dss_wre": 567,
    "G3 Winter_theta_wre": 1.065017435,
    "G3 Winter_pr_1_99_wre": 12,
    "G3 Winter_ss_wre": 85,
    "G3 Winter_dss_wre": 607,
    "G3 Spring_theta_wre": 1.293164992,
    "G3 Spring_pr_1_99_wre": 33,
    "G3 Spring_ss_wre": 84,
    "G3 Spring_dss_wre": 629
  },
  {
    "ID": 75,
    "KG Winter_theta_wre": -1.863479931,
    "KG Winter_pr_1_99_wre": 22,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 314,
    "KG Spring_theta_wre": -1.551510131,
    "KG Spring_pr_1_99_wre": 17,
    "KG Spring_ss_wre": 84,
    "KG Spring_dss_wre": 345,
    "G1 Fall_theta_wre": -1.153594071,
    "G1 Fall_pr_1_99_wre": 21,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 385,
    "G1 Winter_theta_wre": -0.866537019,
    "G1 Winter_pr_1_99_wre": 15,
    "G1 Winter_ss_wre": 85,
    "G1 Winter_dss_wre": 413,
    "G1 Spring_theta_wre": -0.560766377,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 84,
    "G1 Spring_dss_wre": 444,
    "G2 Fall_theta_wre": -0.272340073,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 85,
    "G2 Fall_dss_wre": 473,
    "G2 Winter_theta_wre": 0.067581387,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 85,
    "G2 Winter_dss_wre": 507,
    "G2 Spring_theta_wre": 0.34823782,
    "G2 Spring_pr_1_99_wre": 22,
    "G2 Spring_ss_wre": 85,
    "G2 Spring_dss_wre": 535,
    "G3 Fall_theta_wre": 0.668748466,
    "G3 Fall_pr_1_99_wre": 22,
    "G3 Fall_ss_wre": 84,
    "G3 Fall_dss_wre": 567,
    "G3 Winter_theta_wre": 1.065051969,
    "G3 Winter_pr_1_99_wre": 12,
    "G3 Winter_ss_wre": 85,
    "G3 Winter_dss_wre": 607,
    "G3 Spring_theta_wre": 1.293506311,
    "G3 Spring_pr_1_99_wre": 33,
    "G3 Spring_ss_wre": 84,
    "G3 Spring_dss_wre": 629
  },
  {
    "ID": 76,
    "KG Winter_theta_wre": -1.863169838,
    "KG Winter_pr_1_99_wre": 22,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 314,
    "KG Spring_theta_wre": -1.541810391,
    "KG Spring_pr_1_99_wre": 21,
    "KG Spring_ss_wre": 84,
    "KG Spring_dss_wre": 346,
    "G1 Fall_theta_wre": -1.147576933,
    "G1 Fall_pr_1_99_wre": 22,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 385,
    "G1 Winter_theta_wre": -0.862525029,
    "G1 Winter_pr_1_99_wre": 15,
    "G1 Winter_ss_wre": 85,
    "G1 Winter_dss_wre": 414,
    "G1 Spring_theta_wre": -0.55743928,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 84,
    "G1 Spring_dss_wre": 444,
    "G2 Fall_theta_wre": -0.261062031,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 85,
    "G2 Fall_dss_wre": 474,
    "G2 Winter_theta_wre": 0.076485247,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 85,
    "G2 Winter_dss_wre": 508,
    "G2 Spring_theta_wre": 0.354924289,
    "G2 Spring_pr_1_99_wre": 22,
    "G2 Spring_ss_wre": 85,
    "G2 Spring_dss_wre": 535,
    "G3 Fall_theta_wre": 0.683487105,
    "G3 Fall_pr_1_99_wre": 22,
    "G3 Fall_ss_wre": 84,
    "G3 Fall_dss_wre": 568,
    "G3 Winter_theta_wre": 1.072636623,
    "G3 Winter_pr_1_99_wre": 12,
    "G3 Winter_ss_wre": 85,
    "G3 Winter_dss_wre": 607,
    "G3 Spring_theta_wre": 1.296934028,
    "G3 Spring_pr_1_99_wre": 33,
    "G3 Spring_ss_wre": 84,
    "G3 Spring_dss_wre": 630
  },
  {
    "ID": 77,
    "KG Winter_theta_wre": -1.846337917,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 315,
    "KG Spring_theta_wre": -1.536223469,
    "KG Spring_pr_1_99_wre": 21,
    "KG Spring_ss_wre": 84,
    "KG Spring_dss_wre": 346,
    "G1 Fall_theta_wre": -1.146964077,
    "G1 Fall_pr_1_99_wre": 22,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 385,
    "G1 Winter_theta_wre": -0.83751203,
    "G1 Winter_pr_1_99_wre": 15,
    "G1 Winter_ss_wre": 85,
    "G1 Winter_dss_wre": 416,
    "G1 Spring_theta_wre": -0.55350219,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 84,
    "G1 Spring_dss_wre": 445,
    "G2 Fall_theta_wre": -0.254631945,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 85,
    "G2 Fall_dss_wre": 475,
    "G2 Winter_theta_wre": 0.106922399,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 85,
    "G2 Winter_dss_wre": 511,
    "G2 Spring_theta_wre": 0.360126464,
    "G2 Spring_pr_1_99_wre": 22,
    "G2 Spring_ss_wre": 85,
    "G2 Spring_dss_wre": 536,
    "G3 Fall_theta_wre": 0.69219314,
    "G3 Fall_pr_1_99_wre": 23,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 569,
    "G3 Winter_theta_wre": 1.079778401,
    "G3 Winter_pr_1_99_wre": 12,
    "G3 Winter_ss_wre": 85,
    "G3 Winter_dss_wre": 608,
    "G3 Spring_theta_wre": 1.29904256,
    "G3 Spring_pr_1_99_wre": 34,
    "G3 Spring_ss_wre": 84,
    "G3 Spring_dss_wre": 630
  },
  {
    "ID": 78,
    "KG Winter_theta_wre": -1.843874099,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 316,
    "KG Spring_theta_wre": -1.530649323,
    "KG Spring_pr_1_99_wre": 21,
    "KG Spring_ss_wre": 84,
    "KG Spring_dss_wre": 347,
    "G1 Fall_theta_wre": -1.146025248,
    "G1 Fall_pr_1_99_wre": 22,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 385,
    "G1 Winter_theta_wre": -0.833898174,
    "G1 Winter_pr_1_99_wre": 15,
    "G1 Winter_ss_wre": 86,
    "G1 Winter_dss_wre": 417,
    "G1 Spring_theta_wre": -0.549206933,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 85,
    "G1 Spring_dss_wre": 445,
    "G2 Fall_theta_wre": -0.252154892,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 85,
    "G2 Fall_dss_wre": 475,
    "G2 Winter_theta_wre": 0.113080043,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 86,
    "G2 Winter_dss_wre": 511,
    "G2 Spring_theta_wre": 0.360353682,
    "G2 Spring_pr_1_99_wre": 22,
    "G2 Spring_ss_wre": 85,
    "G2 Spring_dss_wre": 536,
    "G3 Fall_theta_wre": 0.709821943,
    "G3 Fall_pr_1_99_wre": 23,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 571,
    "G3 Winter_theta_wre": 1.087238282,
    "G3 Winter_pr_1_99_wre": 13,
    "G3 Winter_ss_wre": 85,
    "G3 Winter_dss_wre": 609,
    "G3 Spring_theta_wre": 1.301007731,
    "G3 Spring_pr_1_99_wre": 34,
    "G3 Spring_ss_wre": 84,
    "G3 Spring_dss_wre": 630
  },
  {
    "ID": 79,
    "KG Winter_theta_wre": -1.83983406,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 316,
    "KG Spring_theta_wre": -1.501836391,
    "KG Spring_pr_1_99_wre": 21,
    "KG Spring_ss_wre": 85,
    "KG Spring_dss_wre": 350,
    "G1 Fall_theta_wre": -1.144836964,
    "G1 Fall_pr_1_99_wre": 22,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 386,
    "G1 Winter_theta_wre": -0.832052341,
    "G1 Winter_pr_1_99_wre": 15,
    "G1 Winter_ss_wre": 86,
    "G1 Winter_dss_wre": 417,
    "G1 Spring_theta_wre": -0.548121422,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 85,
    "G1 Spring_dss_wre": 445,
    "G2 Fall_theta_wre": -0.250989268,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 85,
    "G2 Fall_dss_wre": 475,
    "G2 Winter_theta_wre": 0.138658387,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 86,
    "G2 Winter_dss_wre": 514,
    "G2 Spring_theta_wre": 0.364278802,
    "G2 Spring_pr_1_99_wre": 22,
    "G2 Spring_ss_wre": 85,
    "G2 Spring_dss_wre": 536,
    "G3 Fall_theta_wre": 0.711093877,
    "G3 Fall_pr_1_99_wre": 23,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 571,
    "G3 Winter_theta_wre": 1.115216644,
    "G3 Winter_pr_1_99_wre": 13,
    "G3 Winter_ss_wre": 86,
    "G3 Winter_dss_wre": 612,
    "G3 Spring_theta_wre": 1.309799,
    "G3 Spring_pr_1_99_wre": 34,
    "G3 Spring_ss_wre": 84,
    "G3 Spring_dss_wre": 631
  },
  {
    "ID": 80,
    "KG Winter_theta_wre": -1.837662486,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 84,
    "KG Winter_dss_wre": 316,
    "KG Spring_theta_wre": -1.500645817,
    "KG Spring_pr_1_99_wre": 21,
    "KG Spring_ss_wre": 85,
    "KG Spring_dss_wre": 350,
    "G1 Fall_theta_wre": -1.134197868,
    "G1 Fall_pr_1_99_wre": 22,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 387,
    "G1 Winter_theta_wre": -0.825455074,
    "G1 Winter_pr_1_99_wre": 16,
    "G1 Winter_ss_wre": 86,
    "G1 Winter_dss_wre": 417,
    "G1 Spring_theta_wre": -0.546254696,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 85,
    "G1 Spring_dss_wre": 445,
    "G2 Fall_theta_wre": -0.246337338,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 85,
    "G2 Fall_dss_wre": 475,
    "G2 Winter_theta_wre": 0.141117494,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 86,
    "G2 Winter_dss_wre": 514,
    "G2 Spring_theta_wre": 0.380613747,
    "G2 Spring_pr_1_99_wre": 22,
    "G2 Spring_ss_wre": 85,
    "G2 Spring_dss_wre": 538,
    "G3 Fall_theta_wre": 0.712137081,
    "G3 Fall_pr_1_99_wre": 23,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 571,
    "G3 Winter_theta_wre": 1.124622215,
    "G3 Winter_pr_1_99_wre": 13,
    "G3 Winter_ss_wre": 86,
    "G3 Winter_dss_wre": 612,
    "G3 Spring_theta_wre": 1.316449117,
    "G3 Spring_pr_1_99_wre": 34,
    "G3 Spring_ss_wre": 84,
    "G3 Spring_dss_wre": 632
  },
  {
    "ID": 81,
    "KG Winter_theta_wre": -1.833218737,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 85,
    "KG Winter_dss_wre": 317,
    "KG Spring_theta_wre": -1.495196579,
    "KG Spring_pr_1_99_wre": 22,
    "KG Spring_ss_wre": 85,
    "KG Spring_dss_wre": 350,
    "G1 Fall_theta_wre": -1.127449065,
    "G1 Fall_pr_1_99_wre": 22,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 387,
    "G1 Winter_theta_wre": -0.823583304,
    "G1 Winter_pr_1_99_wre": 16,
    "G1 Winter_ss_wre": 86,
    "G1 Winter_dss_wre": 418,
    "G1 Spring_theta_wre": -0.538325963,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 85,
    "G1 Spring_dss_wre": 446,
    "G2 Fall_theta_wre": -0.244605796,
    "G2 Fall_pr_1_99_wre": 18,
    "G2 Fall_ss_wre": 85,
    "G2 Fall_dss_wre": 476,
    "G2 Winter_theta_wre": 0.146001096,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 86,
    "G2 Winter_dss_wre": 515,
    "G2 Spring_theta_wre": 0.413678007,
    "G2 Spring_pr_1_99_wre": 23,
    "G2 Spring_ss_wre": 86,
    "G2 Spring_dss_wre": 541,
    "G3 Fall_theta_wre": 0.723793314,
    "G3 Fall_pr_1_99_wre": 23,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 572,
    "G3 Winter_theta_wre": 1.127995057,
    "G3 Winter_pr_1_99_wre": 13,
    "G3 Winter_ss_wre": 86,
    "G3 Winter_dss_wre": 613,
    "G3 Spring_theta_wre": 1.325487925,
    "G3 Spring_pr_1_99_wre": 34,
    "G3 Spring_ss_wre": 84,
    "G3 Spring_dss_wre": 633
  },
  {
    "ID": 82,
    "KG Winter_theta_wre": -1.826050301,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 85,
    "KG Winter_dss_wre": 317,
    "KG Spring_theta_wre": -1.490855663,
    "KG Spring_pr_1_99_wre": 22,
    "KG Spring_ss_wre": 85,
    "KG Spring_dss_wre": 351,
    "G1 Fall_theta_wre": -1.119380745,
    "G1 Fall_pr_1_99_wre": 22,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 388,
    "G1 Winter_theta_wre": -0.811037086,
    "G1 Winter_pr_1_99_wre": 16,
    "G1 Winter_ss_wre": 86,
    "G1 Winter_dss_wre": 419,
    "G1 Spring_theta_wre": -0.537913952,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 85,
    "G1 Spring_dss_wre": 446,
    "G2 Fall_theta_wre": -0.220615578,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 86,
    "G2 Fall_dss_wre": 478,
    "G2 Winter_theta_wre": 0.154772718,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 86,
    "G2 Winter_dss_wre": 515,
    "G2 Spring_theta_wre": 0.414251444,
    "G2 Spring_pr_1_99_wre": 23,
    "G2 Spring_ss_wre": 86,
    "G2 Spring_dss_wre": 541,
    "G3 Fall_theta_wre": 0.724384943,
    "G3 Fall_pr_1_99_wre": 23,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 572,
    "G3 Winter_theta_wre": 1.12819232,
    "G3 Winter_pr_1_99_wre": 13,
    "G3 Winter_ss_wre": 86,
    "G3 Winter_dss_wre": 613,
    "G3 Spring_theta_wre": 1.335736872,
    "G3 Spring_pr_1_99_wre": 35,
    "G3 Spring_ss_wre": 85,
    "G3 Spring_dss_wre": 634
  },
  {
    "ID": 83,
    "KG Winter_theta_wre": -1.81975163,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 85,
    "KG Winter_dss_wre": 318,
    "KG Spring_theta_wre": -1.485231052,
    "KG Spring_pr_1_99_wre": 22,
    "KG Spring_ss_wre": 85,
    "KG Spring_dss_wre": 351,
    "G1 Fall_theta_wre": -1.110662058,
    "G1 Fall_pr_1_99_wre": 22,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 389,
    "G1 Winter_theta_wre": -0.806365613,
    "G1 Winter_pr_1_99_wre": 16,
    "G1 Winter_ss_wre": 86,
    "G1 Winter_dss_wre": 419,
    "G1 Spring_theta_wre": -0.534568182,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 85,
    "G1 Spring_dss_wre": 447,
    "G2 Fall_theta_wre": -0.208625379,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 86,
    "G2 Fall_dss_wre": 479,
    "G2 Winter_theta_wre": 0.154939619,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 86,
    "G2 Winter_dss_wre": 515,
    "G2 Spring_theta_wre": 0.418040956,
    "G2 Spring_pr_1_99_wre": 23,
    "G2 Spring_ss_wre": 86,
    "G2 Spring_dss_wre": 542,
    "G3 Fall_theta_wre": 0.724419885,
    "G3 Fall_pr_1_99_wre": 23,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 572,
    "G3 Winter_theta_wre": 1.145803239,
    "G3 Winter_pr_1_99_wre": 15,
    "G3 Winter_ss_wre": 86,
    "G3 Winter_dss_wre": 615,
    "G3 Spring_theta_wre": 1.356503582,
    "G3 Spring_pr_1_99_wre": 35,
    "G3 Spring_ss_wre": 85,
    "G3 Spring_dss_wre": 636
  },
  {
    "ID": 84,
    "KG Winter_theta_wre": -1.815831324,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 85,
    "KG Winter_dss_wre": 318,
    "KG Spring_theta_wre": -1.484363639,
    "KG Spring_pr_1_99_wre": 22,
    "KG Spring_ss_wre": 85,
    "KG Spring_dss_wre": 352,
    "G1 Fall_theta_wre": -1.107589461,
    "G1 Fall_pr_1_99_wre": 22,
    "G1 Fall_ss_wre": 85,
    "G1 Fall_dss_wre": 389,
    "G1 Winter_theta_wre": -0.788387034,
    "G1 Winter_pr_1_99_wre": 16,
    "G1 Winter_ss_wre": 86,
    "G1 Winter_dss_wre": 421,
    "G1 Spring_theta_wre": -0.5330177,
    "G1 Spring_pr_1_99_wre": 11,
    "G1 Spring_ss_wre": 85,
    "G1 Spring_dss_wre": 447,
    "G2 Fall_theta_wre": -0.198185105,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 86,
    "G2 Fall_dss_wre": 480,
    "G2 Winter_theta_wre": 0.160126178,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 86,
    "G2 Winter_dss_wre": 516,
    "G2 Spring_theta_wre": 0.421854208,
    "G2 Spring_pr_1_99_wre": 23,
    "G2 Spring_ss_wre": 86,
    "G2 Spring_dss_wre": 542,
    "G3 Fall_theta_wre": 0.728939918,
    "G3 Fall_pr_1_99_wre": 23,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 573,
    "G3 Winter_theta_wre": 1.155089517,
    "G3 Winter_pr_1_99_wre": 15,
    "G3 Winter_ss_wre": 86,
    "G3 Winter_dss_wre": 616,
    "G3 Spring_theta_wre": 1.358076614,
    "G3 Spring_pr_1_99_wre": 35,
    "G3 Spring_ss_wre": 85,
    "G3 Spring_dss_wre": 636
  },
  {
    "ID": 85,
    "KG Winter_theta_wre": -1.804567795,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 85,
    "KG Winter_dss_wre": 320,
    "KG Spring_theta_wre": -1.478038654,
    "KG Spring_pr_1_99_wre": 22,
    "KG Spring_ss_wre": 85,
    "KG Spring_dss_wre": 352,
    "G1 Fall_theta_wre": -1.095730617,
    "G1 Fall_pr_1_99_wre": 23,
    "G1 Fall_ss_wre": 86,
    "G1 Fall_dss_wre": 390,
    "G1 Winter_theta_wre": -0.781754972,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 422,
    "G1 Spring_theta_wre": -0.509668233,
    "G1 Spring_pr_1_99_wre": 12,
    "G1 Spring_ss_wre": 85,
    "G1 Spring_dss_wre": 449,
    "G2 Fall_theta_wre": -0.184815682,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 86,
    "G2 Fall_dss_wre": 482,
    "G2 Winter_theta_wre": 0.167686453,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 86,
    "G2 Winter_dss_wre": 517,
    "G2 Spring_theta_wre": 0.426123629,
    "G2 Spring_pr_1_99_wre": 23,
    "G2 Spring_ss_wre": 86,
    "G2 Spring_dss_wre": 543,
    "G3 Fall_theta_wre": 0.729264451,
    "G3 Fall_pr_1_99_wre": 23,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 573,
    "G3 Winter_theta_wre": 1.156491176,
    "G3 Winter_pr_1_99_wre": 15,
    "G3 Winter_ss_wre": 87,
    "G3 Winter_dss_wre": 616,
    "G3 Spring_theta_wre": 1.36149306,
    "G3 Spring_pr_1_99_wre": 36,
    "G3 Spring_ss_wre": 85,
    "G3 Spring_dss_wre": 636
  },
  {
    "ID": 86,
    "KG Winter_theta_wre": -1.798126453,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 85,
    "KG Winter_dss_wre": 320,
    "KG Spring_theta_wre": -1.477106952,
    "KG Spring_pr_1_99_wre": 22,
    "KG Spring_ss_wre": 85,
    "KG Spring_dss_wre": 352,
    "G1 Fall_theta_wre": -1.089989604,
    "G1 Fall_pr_1_99_wre": 23,
    "G1 Fall_ss_wre": 86,
    "G1 Fall_dss_wre": 391,
    "G1 Winter_theta_wre": -0.781369307,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 422,
    "G1 Spring_theta_wre": -0.509192768,
    "G1 Spring_pr_1_99_wre": 12,
    "G1 Spring_ss_wre": 85,
    "G1 Spring_dss_wre": 449,
    "G2 Fall_theta_wre": -0.173325054,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 86,
    "G2 Fall_dss_wre": 483,
    "G2 Winter_theta_wre": 0.186139455,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 87,
    "G2 Winter_dss_wre": 519,
    "G2 Spring_theta_wre": 0.427944023,
    "G2 Spring_pr_1_99_wre": 23,
    "G2 Spring_ss_wre": 86,
    "G2 Spring_dss_wre": 543,
    "G3 Fall_theta_wre": 0.731985393,
    "G3 Fall_pr_1_99_wre": 23,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 573,
    "G3 Winter_theta_wre": 1.158099506,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 87,
    "G3 Winter_dss_wre": 616,
    "G3 Spring_theta_wre": 1.361894603,
    "G3 Spring_pr_1_99_wre": 36,
    "G3 Spring_ss_wre": 85,
    "G3 Spring_dss_wre": 636
  },
  {
    "ID": 87,
    "KG Winter_theta_wre": -1.798104043,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 85,
    "KG Winter_dss_wre": 320,
    "KG Spring_theta_wre": -1.4763982,
    "KG Spring_pr_1_99_wre": 22,
    "KG Spring_ss_wre": 85,
    "KG Spring_dss_wre": 352,
    "G1 Fall_theta_wre": -1.078506566,
    "G1 Fall_pr_1_99_wre": 23,
    "G1 Fall_ss_wre": 86,
    "G1 Fall_dss_wre": 392,
    "G1 Winter_theta_wre": -0.779754659,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 422,
    "G1 Spring_theta_wre": -0.505543191,
    "G1 Spring_pr_1_99_wre": 12,
    "G1 Spring_ss_wre": 85,
    "G1 Spring_dss_wre": 449,
    "G2 Fall_theta_wre": -0.171235748,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 86,
    "G2 Fall_dss_wre": 483,
    "G2 Winter_theta_wre": 0.192599593,
    "G2 Winter_pr_1_99_wre": 12,
    "G2 Winter_ss_wre": 87,
    "G2 Winter_dss_wre": 519,
    "G2 Spring_theta_wre": 0.433073171,
    "G2 Spring_pr_1_99_wre": 23,
    "G2 Spring_ss_wre": 86,
    "G2 Spring_dss_wre": 543,
    "G3 Fall_theta_wre": 0.737020638,
    "G3 Fall_pr_1_99_wre": 24,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 574,
    "G3 Winter_theta_wre": 1.159095982,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 87,
    "G3 Winter_dss_wre": 616,
    "G3 Spring_theta_wre": 1.384873851,
    "G3 Spring_pr_1_99_wre": 37,
    "G3 Spring_ss_wre": 85,
    "G3 Spring_dss_wre": 638
  },
  {
    "ID": 88,
    "KG Winter_theta_wre": -1.790273686,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 85,
    "KG Winter_dss_wre": 321,
    "KG Spring_theta_wre": -1.45921052,
    "KG Spring_pr_1_99_wre": 23,
    "KG Spring_ss_wre": 86,
    "KG Spring_dss_wre": 354,
    "G1 Fall_theta_wre": -1.075937704,
    "G1 Fall_pr_1_99_wre": 24,
    "G1 Fall_ss_wre": 86,
    "G1 Fall_dss_wre": 392,
    "G1 Winter_theta_wre": -0.778795172,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 422,
    "G1 Spring_theta_wre": -0.493068798,
    "G1 Spring_pr_1_99_wre": 13,
    "G1 Spring_ss_wre": 86,
    "G1 Spring_dss_wre": 451,
    "G2 Fall_theta_wre": -0.170671718,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 86,
    "G2 Fall_dss_wre": 483,
    "G2 Winter_theta_wre": 0.205441695,
    "G2 Winter_pr_1_99_wre": 13,
    "G2 Winter_ss_wre": 87,
    "G2 Winter_dss_wre": 521,
    "G2 Spring_theta_wre": 0.437433521,
    "G2 Spring_pr_1_99_wre": 24,
    "G2 Spring_ss_wre": 86,
    "G2 Spring_dss_wre": 544,
    "G3 Fall_theta_wre": 0.74079653,
    "G3 Fall_pr_1_99_wre": 24,
    "G3 Fall_ss_wre": 85,
    "G3 Fall_dss_wre": 574,
    "G3 Winter_theta_wre": 1.168773266,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 87,
    "G3 Winter_dss_wre": 617,
    "G3 Spring_theta_wre": 1.387460027,
    "G3 Spring_pr_1_99_wre": 37,
    "G3 Spring_ss_wre": 85,
    "G3 Spring_dss_wre": 639
  },
  {
    "ID": 89,
    "KG Winter_theta_wre": -1.787130764,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 85,
    "KG Winter_dss_wre": 321,
    "KG Spring_theta_wre": -1.448220458,
    "KG Spring_pr_1_99_wre": 23,
    "KG Spring_ss_wre": 86,
    "KG Spring_dss_wre": 355,
    "G1 Fall_theta_wre": -1.071974181,
    "G1 Fall_pr_1_99_wre": 24,
    "G1 Fall_ss_wre": 86,
    "G1 Fall_dss_wre": 393,
    "G1 Winter_theta_wre": -0.767003414,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 423,
    "G1 Spring_theta_wre": -0.491346648,
    "G1 Spring_pr_1_99_wre": 13,
    "G1 Spring_ss_wre": 86,
    "G1 Spring_dss_wre": 451,
    "G2 Fall_theta_wre": -0.15772426,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 87,
    "G2 Fall_dss_wre": 484,
    "G2 Winter_theta_wre": 0.210050633,
    "G2 Winter_pr_1_99_wre": 13,
    "G2 Winter_ss_wre": 87,
    "G2 Winter_dss_wre": 521,
    "G2 Spring_theta_wre": 0.450647682,
    "G2 Spring_pr_1_99_wre": 24,
    "G2 Spring_ss_wre": 87,
    "G2 Spring_dss_wre": 545,
    "G3 Fall_theta_wre": 0.757936876,
    "G3 Fall_pr_1_99_wre": 25,
    "G3 Fall_ss_wre": 86,
    "G3 Fall_dss_wre": 576,
    "G3 Winter_theta_wre": 1.185653323,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 87,
    "G3 Winter_dss_wre": 619,
    "G3 Spring_theta_wre": 1.387951601,
    "G3 Spring_pr_1_99_wre": 37,
    "G3 Spring_ss_wre": 85,
    "G3 Spring_dss_wre": 639
  },
  {
    "ID": 90,
    "KG Winter_theta_wre": -1.784608322,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 85,
    "KG Winter_dss_wre": 322,
    "KG Spring_theta_wre": -1.440994671,
    "KG Spring_pr_1_99_wre": 24,
    "KG Spring_ss_wre": 86,
    "KG Spring_dss_wre": 356,
    "G1 Fall_theta_wre": -1.066589415,
    "G1 Fall_pr_1_99_wre": 24,
    "G1 Fall_ss_wre": 86,
    "G1 Fall_dss_wre": 393,
    "G1 Winter_theta_wre": -0.762612126,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 424,
    "G1 Spring_theta_wre": -0.486454363,
    "G1 Spring_pr_1_99_wre": 13,
    "G1 Spring_ss_wre": 86,
    "G1 Spring_dss_wre": 451,
    "G2 Fall_theta_wre": -0.134555886,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 87,
    "G2 Fall_dss_wre": 487,
    "G2 Winter_theta_wre": 0.214894963,
    "G2 Winter_pr_1_99_wre": 13,
    "G2 Winter_ss_wre": 87,
    "G2 Winter_dss_wre": 521,
    "G2 Spring_theta_wre": 0.458222608,
    "G2 Spring_pr_1_99_wre": 24,
    "G2 Spring_ss_wre": 87,
    "G2 Spring_dss_wre": 546,
    "G3 Fall_theta_wre": 0.780329085,
    "G3 Fall_pr_1_99_wre": 26,
    "G3 Fall_ss_wre": 86,
    "G3 Fall_dss_wre": 578,
    "G3 Winter_theta_wre": 1.19511908,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 87,
    "G3 Winter_dss_wre": 620,
    "G3 Spring_theta_wre": 1.396576604,
    "G3 Spring_pr_1_99_wre": 38,
    "G3 Spring_ss_wre": 86,
    "G3 Spring_dss_wre": 640
  },
  {
    "ID": 91,
    "KG Winter_theta_wre": -1.773204127,
    "KG Winter_pr_1_99_wre": 24,
    "KG Winter_ss_wre": 86,
    "KG Winter_dss_wre": 323,
    "KG Spring_theta_wre": -1.423895095,
    "KG Spring_pr_1_99_wre": 25,
    "KG Spring_ss_wre": 86,
    "KG Spring_dss_wre": 358,
    "G1 Fall_theta_wre": -1.05825369,
    "G1 Fall_pr_1_99_wre": 24,
    "G1 Fall_ss_wre": 86,
    "G1 Fall_dss_wre": 394,
    "G1 Winter_theta_wre": -0.761351256,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 424,
    "G1 Spring_theta_wre": -0.470448045,
    "G1 Spring_pr_1_99_wre": 14,
    "G1 Spring_ss_wre": 86,
    "G1 Spring_dss_wre": 453,
    "G2 Fall_theta_wre": -0.116081739,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 87,
    "G2 Fall_dss_wre": 488,
    "G2 Winter_theta_wre": 0.223033503,
    "G2 Winter_pr_1_99_wre": 13,
    "G2 Winter_ss_wre": 87,
    "G2 Winter_dss_wre": 522,
    "G2 Spring_theta_wre": 0.464119743,
    "G2 Spring_pr_1_99_wre": 24,
    "G2 Spring_ss_wre": 87,
    "G2 Spring_dss_wre": 546,
    "G3 Fall_theta_wre": 0.783624074,
    "G3 Fall_pr_1_99_wre": 27,
    "G3 Fall_ss_wre": 86,
    "G3 Fall_dss_wre": 578,
    "G3 Winter_theta_wre": 1.197913044,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 87,
    "G3 Winter_dss_wre": 620,
    "G3 Spring_theta_wre": 1.399962422,
    "G3 Spring_pr_1_99_wre": 38,
    "G3 Spring_ss_wre": 86,
    "G3 Spring_dss_wre": 640
  },
  {
    "ID": 92,
    "KG Winter_theta_wre": -1.757724172,
    "KG Winter_pr_1_99_wre": 25,
    "KG Winter_ss_wre": 86,
    "KG Winter_dss_wre": 324,
    "KG Spring_theta_wre": -1.423264358,
    "KG Spring_pr_1_99_wre": 25,
    "KG Spring_ss_wre": 86,
    "KG Spring_dss_wre": 358,
    "G1 Fall_theta_wre": -1.040712678,
    "G1 Fall_pr_1_99_wre": 24,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 396,
    "G1 Winter_theta_wre": -0.74848675,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 425,
    "G1 Spring_theta_wre": -0.465680539,
    "G1 Spring_pr_1_99_wre": 14,
    "G1 Spring_ss_wre": 86,
    "G1 Spring_dss_wre": 453,
    "G2 Fall_theta_wre": -0.108914287,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 489,
    "G2 Winter_theta_wre": 0.23257125,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 88,
    "G2 Winter_dss_wre": 523,
    "G2 Spring_theta_wre": 0.468653713,
    "G2 Spring_pr_1_99_wre": 24,
    "G2 Spring_ss_wre": 87,
    "G2 Spring_dss_wre": 547,
    "G3 Fall_theta_wre": 0.799458664,
    "G3 Fall_pr_1_99_wre": 27,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 580,
    "G3 Winter_theta_wre": 1.203110605,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 87,
    "G3 Winter_dss_wre": 620,
    "G3 Spring_theta_wre": 1.402613032,
    "G3 Spring_pr_1_99_wre": 39,
    "G3 Spring_ss_wre": 86,
    "G3 Spring_dss_wre": 640
  },
  {
    "ID": 93,
    "KG Winter_theta_wre": -1.755328278,
    "KG Winter_pr_1_99_wre": 25,
    "KG Winter_ss_wre": 86,
    "KG Winter_dss_wre": 324,
    "KG Spring_theta_wre": -1.423180749,
    "KG Spring_pr_1_99_wre": 25,
    "KG Spring_ss_wre": 86,
    "KG Spring_dss_wre": 358,
    "G1 Fall_theta_wre": -1.040083189,
    "G1 Fall_pr_1_99_wre": 24,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 396,
    "G1 Winter_theta_wre": -0.746206513,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 425,
    "G1 Spring_theta_wre": -0.464180321,
    "G1 Spring_pr_1_99_wre": 14,
    "G1 Spring_ss_wre": 86,
    "G1 Spring_dss_wre": 454,
    "G2 Fall_theta_wre": -0.106496053,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 489,
    "G2 Winter_theta_wre": 0.244295773,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 88,
    "G2 Winter_dss_wre": 524,
    "G2 Spring_theta_wre": 0.470870354,
    "G2 Spring_pr_1_99_wre": 24,
    "G2 Spring_ss_wre": 87,
    "G2 Spring_dss_wre": 547,
    "G3 Fall_theta_wre": 0.799998984,
    "G3 Fall_pr_1_99_wre": 27,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 580,
    "G3 Winter_theta_wre": 1.204499365,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 87,
    "G3 Winter_dss_wre": 620,
    "G3 Spring_theta_wre": 1.414709706,
    "G3 Spring_pr_1_99_wre": 39,
    "G3 Spring_ss_wre": 86,
    "G3 Spring_dss_wre": 641
  },
  {
    "ID": 94,
    "KG Winter_theta_wre": -1.752525405,
    "KG Winter_pr_1_99_wre": 25,
    "KG Winter_ss_wre": 86,
    "KG Winter_dss_wre": 325,
    "KG Spring_theta_wre": -1.422967138,
    "KG Spring_pr_1_99_wre": 25,
    "KG Spring_ss_wre": 86,
    "KG Spring_dss_wre": 358,
    "G1 Fall_theta_wre": -1.039903897,
    "G1 Fall_pr_1_99_wre": 24,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 396,
    "G1 Winter_theta_wre": -0.745451211,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 425,
    "G1 Spring_theta_wre": -0.463055197,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 86,
    "G1 Spring_dss_wre": 454,
    "G2 Fall_theta_wre": -0.104766623,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 490,
    "G2 Winter_theta_wre": 0.244587274,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 88,
    "G2 Winter_dss_wre": 524,
    "G2 Spring_theta_wre": 0.484091833,
    "G2 Spring_pr_1_99_wre": 25,
    "G2 Spring_ss_wre": 87,
    "G2 Spring_dss_wre": 548,
    "G3 Fall_theta_wre": 0.808072738,
    "G3 Fall_pr_1_99_wre": 27,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 581,
    "G3 Winter_theta_wre": 1.206644786,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 87,
    "G3 Winter_dss_wre": 621,
    "G3 Spring_theta_wre": 1.434153519,
    "G3 Spring_pr_1_99_wre": 40,
    "G3 Spring_ss_wre": 86,
    "G3 Spring_dss_wre": 643
  },
  {
    "ID": 95,
    "KG Winter_theta_wre": -1.743036262,
    "KG Winter_pr_1_99_wre": 25,
    "KG Winter_ss_wre": 86,
    "KG Winter_dss_wre": 326,
    "KG Spring_theta_wre": -1.399098956,
    "KG Spring_pr_1_99_wre": 25,
    "KG Spring_ss_wre": 87,
    "KG Spring_dss_wre": 360,
    "G1 Fall_theta_wre": -1.029892592,
    "G1 Fall_pr_1_99_wre": 25,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 397,
    "G1 Winter_theta_wre": -0.743787371,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 426,
    "G1 Spring_theta_wre": -0.443416847,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 86,
    "G1 Spring_dss_wre": 456,
    "G2 Fall_theta_wre": -0.091489434,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 491,
    "G2 Winter_theta_wre": 0.247574479,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 88,
    "G2 Winter_dss_wre": 525,
    "G2 Spring_theta_wre": 0.491994996,
    "G2 Spring_pr_1_99_wre": 25,
    "G2 Spring_ss_wre": 87,
    "G2 Spring_dss_wre": 549,
    "G3 Fall_theta_wre": 0.81241257,
    "G3 Fall_pr_1_99_wre": 28,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 581,
    "G3 Winter_theta_wre": 1.221185171,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 88,
    "G3 Winter_dss_wre": 622,
    "G3 Spring_theta_wre": 1.493589019,
    "G3 Spring_pr_1_99_wre": 42,
    "G3 Spring_ss_wre": 87,
    "G3 Spring_dss_wre": 649
  },
  {
    "ID": 96,
    "KG Winter_theta_wre": -1.734229203,
    "KG Winter_pr_1_99_wre": 25,
    "KG Winter_ss_wre": 86,
    "KG Winter_dss_wre": 327,
    "KG Spring_theta_wre": -1.36897399,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 87,
    "KG Spring_dss_wre": 363,
    "G1 Fall_theta_wre": -1.024940066,
    "G1 Fall_pr_1_99_wre": 25,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 398,
    "G1 Winter_theta_wre": -0.732837864,
    "G1 Winter_pr_1_99_wre": 17,
    "G1 Winter_ss_wre": 87,
    "G1 Winter_dss_wre": 427,
    "G1 Spring_theta_wre": -0.430816025,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 87,
    "G1 Spring_dss_wre": 457,
    "G2 Fall_theta_wre": -0.090339896,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 491,
    "G2 Winter_theta_wre": 0.249822646,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 88,
    "G2 Winter_dss_wre": 525,
    "G2 Spring_theta_wre": 0.497310744,
    "G2 Spring_pr_1_99_wre": 25,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 550,
    "G3 Fall_theta_wre": 0.813681878,
    "G3 Fall_pr_1_99_wre": 28,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 581,
    "G3 Winter_theta_wre": 1.223014621,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 88,
    "G3 Winter_dss_wre": 622,
    "G3 Spring_theta_wre": 1.513737031,
    "G3 Spring_pr_1_99_wre": 42,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 651
  },
  {
    "ID": 97,
    "KG Winter_theta_wre": -1.733883999,
    "KG Winter_pr_1_99_wre": 25,
    "KG Winter_ss_wre": 86,
    "KG Winter_dss_wre": 327,
    "KG Spring_theta_wre": -1.366260897,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 87,
    "KG Spring_dss_wre": 363,
    "G1 Fall_theta_wre": -1.021104072,
    "G1 Fall_pr_1_99_wre": 25,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 398,
    "G1 Winter_theta_wre": -0.72201742,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 428,
    "G1 Spring_theta_wre": -0.425711263,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 87,
    "G1 Spring_dss_wre": 457,
    "G2 Fall_theta_wre": -0.088567329,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 491,
    "G2 Winter_theta_wre": 0.268829159,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 88,
    "G2 Winter_dss_wre": 527,
    "G2 Spring_theta_wre": 0.503430865,
    "G2 Spring_pr_1_99_wre": 25,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 550,
    "G3 Fall_theta_wre": 0.813903825,
    "G3 Fall_pr_1_99_wre": 28,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 581,
    "G3 Winter_theta_wre": 1.223326075,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 88,
    "G3 Winter_dss_wre": 622,
    "G3 Spring_theta_wre": 1.519418035,
    "G3 Spring_pr_1_99_wre": 43,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 652
  },
  {
    "ID": 98,
    "KG Winter_theta_wre": -1.722691219,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 328,
    "KG Spring_theta_wre": -1.364102946,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 87,
    "KG Spring_dss_wre": 364,
    "G1 Fall_theta_wre": -1.007726241,
    "G1 Fall_pr_1_99_wre": 26,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 399,
    "G1 Winter_theta_wre": -0.716815166,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 428,
    "G1 Spring_theta_wre": -0.418718152,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 87,
    "G1 Spring_dss_wre": 458,
    "G2 Fall_theta_wre": -0.084404566,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 492,
    "G2 Winter_theta_wre": 0.270587292,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 88,
    "G2 Winter_dss_wre": 527,
    "G2 Spring_theta_wre": 0.506169176,
    "G2 Spring_pr_1_99_wre": 25,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 551,
    "G3 Fall_theta_wre": 0.814292642,
    "G3 Fall_pr_1_99_wre": 29,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 581,
    "G3 Winter_theta_wre": 1.244842358,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 88,
    "G3 Winter_dss_wre": 624,
    "G3 Spring_theta_wre": 1.530169454,
    "G3 Spring_pr_1_99_wre": 43,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 653
  },
  {
    "ID": 99,
    "KG Winter_theta_wre": -1.722554909,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 328,
    "KG Spring_theta_wre": -1.357590261,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 87,
    "KG Spring_dss_wre": 364,
    "G1 Fall_theta_wre": -1.004778352,
    "G1 Fall_pr_1_99_wre": 26,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 400,
    "G1 Winter_theta_wre": -0.716183575,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 428,
    "G1 Spring_theta_wre": -0.407871218,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 87,
    "G1 Spring_dss_wre": 459,
    "G2 Fall_theta_wre": -0.078689613,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 492,
    "G2 Winter_theta_wre": 0.275251674,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 88,
    "G2 Winter_dss_wre": 528,
    "G2 Spring_theta_wre": 0.51319944,
    "G2 Spring_pr_1_99_wre": 26,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 551,
    "G3 Fall_theta_wre": 0.81470807,
    "G3 Fall_pr_1_99_wre": 29,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 581,
    "G3 Winter_theta_wre": 1.250454762,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 88,
    "G3 Winter_dss_wre": 625,
    "G3 Spring_theta_wre": 1.530255982,
    "G3 Spring_pr_1_99_wre": 43,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 653
  },
  {
    "ID": 100,
    "KG Winter_theta_wre": -1.721332554,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 328,
    "KG Spring_theta_wre": -1.356260273,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 87,
    "KG Spring_dss_wre": 364,
    "G1 Fall_theta_wre": -1.001613491,
    "G1 Fall_pr_1_99_wre": 26,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 400,
    "G1 Winter_theta_wre": -0.713448049,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 429,
    "G1 Spring_theta_wre": -0.397037921,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 87,
    "G1 Spring_dss_wre": 460,
    "G2 Fall_theta_wre": -0.078607112,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 492,
    "G2 Winter_theta_wre": 0.284593055,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 89,
    "G2 Winter_dss_wre": 528,
    "G2 Spring_theta_wre": 0.5135137,
    "G2 Spring_pr_1_99_wre": 26,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 551,
    "G3 Fall_theta_wre": 0.814734797,
    "G3 Fall_pr_1_99_wre": 29,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 581,
    "G3 Winter_theta_wre": 1.251813166,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 88,
    "G3 Winter_dss_wre": 625,
    "G3 Spring_theta_wre": 1.532008014,
    "G3 Spring_pr_1_99_wre": 43,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 653
  },
  {
    "ID": 101,
    "KG Winter_theta_wre": -1.720172539,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 328,
    "KG Spring_theta_wre": -1.35416331,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 87,
    "KG Spring_dss_wre": 365,
    "G1 Fall_theta_wre": -1.001489566,
    "G1 Fall_pr_1_99_wre": 26,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 400,
    "G1 Winter_theta_wre": -0.709925186,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 429,
    "G1 Spring_theta_wre": -0.392417934,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 87,
    "G1 Spring_dss_wre": 461,
    "G2 Fall_theta_wre": -0.078339261,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 492,
    "G2 Winter_theta_wre": 0.284846328,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 89,
    "G2 Winter_dss_wre": 528,
    "G2 Spring_theta_wre": 0.517567701,
    "G2 Spring_pr_1_99_wre": 26,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 552,
    "G3 Fall_theta_wre": 0.819359427,
    "G3 Fall_pr_1_99_wre": 29,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 582,
    "G3 Winter_theta_wre": 1.254378313,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 88,
    "G3 Winter_dss_wre": 625,
    "G3 Spring_theta_wre": 1.532413299,
    "G3 Spring_pr_1_99_wre": 43,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 653
  },
  {
    "ID": 102,
    "KG Winter_theta_wre": -1.711867074,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 329,
    "KG Spring_theta_wre": -1.343733659,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 88,
    "KG Spring_dss_wre": 366,
    "G1 Fall_theta_wre": -1.001477169,
    "G1 Fall_pr_1_99_wre": 26,
    "G1 Fall_ss_wre": 87,
    "G1 Fall_dss_wre": 400,
    "G1 Winter_theta_wre": -0.704496801,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 430,
    "G1 Spring_theta_wre": -0.391736402,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 87,
    "G1 Spring_dss_wre": 461,
    "G2 Fall_theta_wre": -0.07579892,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 492,
    "G2 Winter_theta_wre": 0.291528636,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 89,
    "G2 Winter_dss_wre": 529,
    "G2 Spring_theta_wre": 0.522892863,
    "G2 Spring_pr_1_99_wre": 27,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 552,
    "G3 Fall_theta_wre": 0.823350536,
    "G3 Fall_pr_1_99_wre": 29,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 582,
    "G3 Winter_theta_wre": 1.259946848,
    "G3 Winter_pr_1_99_wre": 16,
    "G3 Winter_ss_wre": 88,
    "G3 Winter_dss_wre": 626,
    "G3 Spring_theta_wre": 1.53738017,
    "G3 Spring_pr_1_99_wre": 43,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 654
  },
  {
    "ID": 103,
    "KG Winter_theta_wre": -1.707876378,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 329,
    "KG Spring_theta_wre": -1.343470784,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 88,
    "KG Spring_dss_wre": 366,
    "G1 Fall_theta_wre": -0.996027448,
    "G1 Fall_pr_1_99_wre": 26,
    "G1 Fall_ss_wre": 88,
    "G1 Fall_dss_wre": 400,
    "G1 Winter_theta_wre": -0.702839513,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 430,
    "G1 Spring_theta_wre": -0.372202657,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 88,
    "G1 Spring_dss_wre": 463,
    "G2 Fall_theta_wre": -0.074202937,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 493,
    "G2 Winter_theta_wre": 0.292054309,
    "G2 Winter_pr_1_99_wre": 14,
    "G2 Winter_ss_wre": 89,
    "G2 Winter_dss_wre": 529,
    "G2 Spring_theta_wre": 0.527858818,
    "G2 Spring_pr_1_99_wre": 27,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 553,
    "G3 Fall_theta_wre": 0.831970488,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 583,
    "G3 Winter_theta_wre": 1.263208518,
    "G3 Winter_pr_1_99_wre": 17,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 626,
    "G3 Spring_theta_wre": 1.539008045,
    "G3 Spring_pr_1_99_wre": 43,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 654
  },
  {
    "ID": 104,
    "KG Winter_theta_wre": -1.701967084,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 330,
    "KG Spring_theta_wre": -1.329819741,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 88,
    "KG Spring_dss_wre": 367,
    "G1 Fall_theta_wre": -0.9908547,
    "G1 Fall_pr_1_99_wre": 26,
    "G1 Fall_ss_wre": 88,
    "G1 Fall_dss_wre": 401,
    "G1 Winter_theta_wre": -0.701656032,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 430,
    "G1 Spring_theta_wre": -0.371604108,
    "G1 Spring_pr_1_99_wre": 15,
    "G1 Spring_ss_wre": 88,
    "G1 Spring_dss_wre": 463,
    "G2 Fall_theta_wre": -0.072883697,
    "G2 Fall_pr_1_99_wre": 22,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 493,
    "G2 Winter_theta_wre": 0.300208559,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 89,
    "G2 Winter_dss_wre": 530,
    "G2 Spring_theta_wre": 0.529695048,
    "G2 Spring_pr_1_99_wre": 27,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 553,
    "G3 Fall_theta_wre": 0.837501995,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 584,
    "G3 Winter_theta_wre": 1.265133643,
    "G3 Winter_pr_1_99_wre": 17,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 627,
    "G3 Spring_theta_wre": 1.540862989,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 654
  },
  {
    "ID": 105,
    "KG Winter_theta_wre": -1.688094428,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 331,
    "KG Spring_theta_wre": -1.32901599,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 88,
    "KG Spring_dss_wre": 367,
    "G1 Fall_theta_wre": -0.985372328,
    "G1 Fall_pr_1_99_wre": 26,
    "G1 Fall_ss_wre": 88,
    "G1 Fall_dss_wre": 401,
    "G1 Winter_theta_wre": -0.701465049,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 430,
    "G1 Spring_theta_wre": -0.36057735,
    "G1 Spring_pr_1_99_wre": 16,
    "G1 Spring_ss_wre": 88,
    "G1 Spring_dss_wre": 464,
    "G2 Fall_theta_wre": -0.062593381,
    "G2 Fall_pr_1_99_wre": 23,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 494,
    "G2 Winter_theta_wre": 0.306301478,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 89,
    "G2 Winter_dss_wre": 531,
    "G2 Spring_theta_wre": 0.529794442,
    "G2 Spring_pr_1_99_wre": 27,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 553,
    "G3 Fall_theta_wre": 0.840621465,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 584,
    "G3 Winter_theta_wre": 1.268865571,
    "G3 Winter_pr_1_99_wre": 18,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 627,
    "G3 Spring_theta_wre": 1.54150855,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 654
  },
  {
    "ID": 106,
    "KG Winter_theta_wre": -1.679765946,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 332,
    "KG Spring_theta_wre": -1.328641175,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 88,
    "KG Spring_dss_wre": 367,
    "G1 Fall_theta_wre": -0.98278852,
    "G1 Fall_pr_1_99_wre": 26,
    "G1 Fall_ss_wre": 88,
    "G1 Fall_dss_wre": 402,
    "G1 Winter_theta_wre": -0.693232237,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 431,
    "G1 Spring_theta_wre": -0.3591558,
    "G1 Spring_pr_1_99_wre": 16,
    "G1 Spring_ss_wre": 88,
    "G1 Spring_dss_wre": 464,
    "G2 Fall_theta_wre": -0.057497663,
    "G2 Fall_pr_1_99_wre": 23,
    "G2 Fall_ss_wre": 88,
    "G2 Fall_dss_wre": 494,
    "G2 Winter_theta_wre": 0.31042409,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 89,
    "G2 Winter_dss_wre": 531,
    "G2 Spring_theta_wre": 0.532105683,
    "G2 Spring_pr_1_99_wre": 27,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 553,
    "G3 Fall_theta_wre": 0.845361161,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 87,
    "G3 Fall_dss_wre": 585,
    "G3 Winter_theta_wre": 1.269963327,
    "G3 Winter_pr_1_99_wre": 18,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 627,
    "G3 Spring_theta_wre": 1.548127823,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 655
  },
  {
    "ID": 107,
    "KG Winter_theta_wre": -1.676565159,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 332,
    "KG Spring_theta_wre": -1.327670356,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 88,
    "KG Spring_dss_wre": 367,
    "G1 Fall_theta_wre": -0.971200405,
    "G1 Fall_pr_1_99_wre": 27,
    "G1 Fall_ss_wre": 88,
    "G1 Fall_dss_wre": 403,
    "G1 Winter_theta_wre": -0.692748677,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 431,
    "G1 Spring_theta_wre": -0.354782351,
    "G1 Spring_pr_1_99_wre": 16,
    "G1 Spring_ss_wre": 88,
    "G1 Spring_dss_wre": 465,
    "G2 Fall_theta_wre": -0.047693757,
    "G2 Fall_pr_1_99_wre": 23,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 495,
    "G2 Winter_theta_wre": 0.312008492,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 89,
    "G2 Winter_dss_wre": 531,
    "G2 Spring_theta_wre": 0.533424257,
    "G2 Spring_pr_1_99_wre": 27,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 553,
    "G3 Fall_theta_wre": 0.866196619,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 88,
    "G3 Fall_dss_wre": 587,
    "G3 Winter_theta_wre": 1.270366792,
    "G3 Winter_pr_1_99_wre": 18,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 627,
    "G3 Spring_theta_wre": 1.548143222,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 88,
    "G3 Spring_dss_wre": 655
  },
  {
    "ID": 108,
    "KG Winter_theta_wre": -1.67471227,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 333,
    "KG Spring_theta_wre": -1.316640772,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 88,
    "KG Spring_dss_wre": 368,
    "G1 Fall_theta_wre": -0.962097067,
    "G1 Fall_pr_1_99_wre": 27,
    "G1 Fall_ss_wre": 88,
    "G1 Fall_dss_wre": 404,
    "G1 Winter_theta_wre": -0.690101675,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 431,
    "G1 Spring_theta_wre": -0.352839257,
    "G1 Spring_pr_1_99_wre": 16,
    "G1 Spring_ss_wre": 88,
    "G1 Spring_dss_wre": 465,
    "G2 Fall_theta_wre": -0.04356709,
    "G2 Fall_pr_1_99_wre": 23,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 496,
    "G2 Winter_theta_wre": 0.323995323,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 89,
    "G2 Winter_dss_wre": 532,
    "G2 Spring_theta_wre": 0.534212531,
    "G2 Spring_pr_1_99_wre": 27,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 553,
    "G3 Fall_theta_wre": 0.867131223,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 88,
    "G3 Fall_dss_wre": 587,
    "G3 Winter_theta_wre": 1.271116795,
    "G3 Winter_pr_1_99_wre": 18,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 627,
    "G3 Spring_theta_wre": 1.560337532,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 89,
    "G3 Spring_dss_wre": 656
  },
  {
    "ID": 109,
    "KG Winter_theta_wre": -1.672754008,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 87,
    "KG Winter_dss_wre": 333,
    "KG Spring_theta_wre": -1.311698669,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 88,
    "KG Spring_dss_wre": 369,
    "G1 Fall_theta_wre": -0.960650967,
    "G1 Fall_pr_1_99_wre": 27,
    "G1 Fall_ss_wre": 88,
    "G1 Fall_dss_wre": 404,
    "G1 Winter_theta_wre": -0.680840341,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 88,
    "G1 Winter_dss_wre": 432,
    "G1 Spring_theta_wre": -0.349470259,
    "G1 Spring_pr_1_99_wre": 16,
    "G1 Spring_ss_wre": 88,
    "G1 Spring_dss_wre": 465,
    "G2 Fall_theta_wre": -0.041591726,
    "G2 Fall_pr_1_99_wre": 23,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 496,
    "G2 Winter_theta_wre": 0.326085885,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 89,
    "G2 Winter_dss_wre": 533,
    "G2 Spring_theta_wre": 0.535018776,
    "G2 Spring_pr_1_99_wre": 27,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 554,
    "G3 Fall_theta_wre": 0.879413778,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 88,
    "G3 Fall_dss_wre": 588,
    "G3 Winter_theta_wre": 1.273398103,
    "G3 Winter_pr_1_99_wre": 18,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 627,
    "G3 Spring_theta_wre": 1.565316011,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 89,
    "G3 Spring_dss_wre": 657
  },
  {
    "ID": 110,
    "KG Winter_theta_wre": -1.665773729,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 88,
    "KG Winter_dss_wre": 333,
    "KG Spring_theta_wre": -1.310523417,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 88,
    "KG Spring_dss_wre": 369,
    "G1 Fall_theta_wre": -0.951990996,
    "G1 Fall_pr_1_99_wre": 27,
    "G1 Fall_ss_wre": 88,
    "G1 Fall_dss_wre": 405,
    "G1 Winter_theta_wre": -0.679267932,
    "G1 Winter_pr_1_99_wre": 18,
    "G1 Winter_ss_wre": 89,
    "G1 Winter_dss_wre": 432,
    "G1 Spring_theta_wre": -0.344252838,
    "G1 Spring_pr_1_99_wre": 16,
    "G1 Spring_ss_wre": 88,
    "G1 Spring_dss_wre": 466,
    "G2 Fall_theta_wre": -0.03159851,
    "G2 Fall_pr_1_99_wre": 23,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 497,
    "G2 Winter_theta_wre": 0.337194725,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 534,
    "G2 Spring_theta_wre": 0.536064128,
    "G2 Spring_pr_1_99_wre": 27,
    "G2 Spring_ss_wre": 88,
    "G2 Spring_dss_wre": 554,
    "G3 Fall_theta_wre": 0.880632648,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 88,
    "G3 Fall_dss_wre": 588,
    "G3 Winter_theta_wre": 1.281632638,
    "G3 Winter_pr_1_99_wre": 19,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 628,
    "G3 Spring_theta_wre": 1.56543206,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 89,
    "G3 Spring_dss_wre": 657
  },
  {
    "ID": 111,
    "KG Winter_theta_wre": -1.646854792,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 88,
    "KG Winter_dss_wre": 335,
    "KG Spring_theta_wre": -1.309808552,
    "KG Spring_pr_1_99_wre": 26,
    "KG Spring_ss_wre": 88,
    "KG Spring_dss_wre": 369,
    "G1 Fall_theta_wre": -0.950147785,
    "G1 Fall_pr_1_99_wre": 27,
    "G1 Fall_ss_wre": 88,
    "G1 Fall_dss_wre": 405,
    "G1 Winter_theta_wre": -0.6713652,
    "G1 Winter_pr_1_99_wre": 19,
    "G1 Winter_ss_wre": 89,
    "G1 Winter_dss_wre": 433,
    "G1 Spring_theta_wre": -0.328413141,
    "G1 Spring_pr_1_99_wre": 16,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 467,
    "G2 Fall_theta_wre": -0.029588663,
    "G2 Fall_pr_1_99_wre": 23,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 497,
    "G2 Winter_theta_wre": 0.340543809,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 534,
    "G2 Spring_theta_wre": 0.55037469,
    "G2 Spring_pr_1_99_wre": 27,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 555,
    "G3 Fall_theta_wre": 0.880635747,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 88,
    "G3 Fall_dss_wre": 588,
    "G3 Winter_theta_wre": 1.283749675,
    "G3 Winter_pr_1_99_wre": 19,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 628,
    "G3 Spring_theta_wre": 1.568880528,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 89,
    "G3 Spring_dss_wre": 657
  },
  {
    "ID": 112,
    "KG Winter_theta_wre": -1.644535048,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 88,
    "KG Winter_dss_wre": 336,
    "KG Spring_theta_wre": -1.283077942,
    "KG Spring_pr_1_99_wre": 27,
    "KG Spring_ss_wre": 89,
    "KG Spring_dss_wre": 372,
    "G1 Fall_theta_wre": -0.929250875,
    "G1 Fall_pr_1_99_wre": 27,
    "G1 Fall_ss_wre": 89,
    "G1 Fall_dss_wre": 407,
    "G1 Winter_theta_wre": -0.669891854,
    "G1 Winter_pr_1_99_wre": 19,
    "G1 Winter_ss_wre": 89,
    "G1 Winter_dss_wre": 433,
    "G1 Spring_theta_wre": -0.32803805,
    "G1 Spring_pr_1_99_wre": 16,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 467,
    "G2 Fall_theta_wre": -0.016224862,
    "G2 Fall_pr_1_99_wre": 23,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 498,
    "G2 Winter_theta_wre": 0.341448519,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 534,
    "G2 Spring_theta_wre": 0.55488323,
    "G2 Spring_pr_1_99_wre": 28,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 555,
    "G3 Fall_theta_wre": 0.881715988,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 88,
    "G3 Fall_dss_wre": 588,
    "G3 Winter_theta_wre": 1.289583395,
    "G3 Winter_pr_1_99_wre": 19,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 629,
    "G3 Spring_theta_wre": 1.57957833,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 89,
    "G3 Spring_dss_wre": 658
  },
  {
    "ID": 113,
    "KG Winter_theta_wre": -1.63868303,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 88,
    "KG Winter_dss_wre": 336,
    "KG Spring_theta_wre": -1.27232247,
    "KG Spring_pr_1_99_wre": 27,
    "KG Spring_ss_wre": 89,
    "KG Spring_dss_wre": 373,
    "G1 Fall_theta_wre": -0.929205754,
    "G1 Fall_pr_1_99_wre": 27,
    "G1 Fall_ss_wre": 89,
    "G1 Fall_dss_wre": 407,
    "G1 Winter_theta_wre": -0.661091364,
    "G1 Winter_pr_1_99_wre": 19,
    "G1 Winter_ss_wre": 89,
    "G1 Winter_dss_wre": 434,
    "G1 Spring_theta_wre": -0.315921138,
    "G1 Spring_pr_1_99_wre": 16,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 468,
    "G2 Fall_theta_wre": -0.015601406,
    "G2 Fall_pr_1_99_wre": 23,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 498,
    "G2 Winter_theta_wre": 0.349288902,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 535,
    "G2 Spring_theta_wre": 0.560577457,
    "G2 Spring_pr_1_99_wre": 28,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 556,
    "G3 Fall_theta_wre": 0.882657951,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 88,
    "G3 Fall_dss_wre": 588,
    "G3 Winter_theta_wre": 1.290819377,
    "G3 Winter_pr_1_99_wre": 19,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 629,
    "G3 Spring_theta_wre": 1.580192776,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 89,
    "G3 Spring_dss_wre": 658
  },
  {
    "ID": 114,
    "KG Winter_theta_wre": -1.637096725,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 88,
    "KG Winter_dss_wre": 336,
    "KG Spring_theta_wre": -1.271530815,
    "KG Spring_pr_1_99_wre": 27,
    "KG Spring_ss_wre": 89,
    "KG Spring_dss_wre": 373,
    "G1 Fall_theta_wre": -0.911870063,
    "G1 Fall_pr_1_99_wre": 30,
    "G1 Fall_ss_wre": 89,
    "G1 Fall_dss_wre": 409,
    "G1 Winter_theta_wre": -0.657870177,
    "G1 Winter_pr_1_99_wre": 20,
    "G1 Winter_ss_wre": 89,
    "G1 Winter_dss_wre": 434,
    "G1 Spring_theta_wre": -0.310954376,
    "G1 Spring_pr_1_99_wre": 17,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 469,
    "G2 Fall_theta_wre": -0.014354229,
    "G2 Fall_pr_1_99_wre": 23,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 499,
    "G2 Winter_theta_wre": 0.353420599,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 535,
    "G2 Spring_theta_wre": 0.563333398,
    "G2 Spring_pr_1_99_wre": 28,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 556,
    "G3 Fall_theta_wre": 0.883884765,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 88,
    "G3 Fall_dss_wre": 588,
    "G3 Winter_theta_wre": 1.294398141,
    "G3 Winter_pr_1_99_wre": 19,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 629,
    "G3 Spring_theta_wre": 1.580459286,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 89,
    "G3 Spring_dss_wre": 658
  },
  {
    "ID": 115,
    "KG Winter_theta_wre": -1.635055365,
    "KG Winter_pr_1_99_wre": 26,
    "KG Winter_ss_wre": 88,
    "KG Winter_dss_wre": 336,
    "KG Spring_theta_wre": -1.270073492,
    "KG Spring_pr_1_99_wre": 28,
    "KG Spring_ss_wre": 89,
    "KG Spring_dss_wre": 373,
    "G1 Fall_theta_wre": -0.906889654,
    "G1 Fall_pr_1_99_wre": 30,
    "G1 Fall_ss_wre": 89,
    "G1 Fall_dss_wre": 409,
    "G1 Winter_theta_wre": -0.634063041,
    "G1 Winter_pr_1_99_wre": 20,
    "G1 Winter_ss_wre": 89,
    "G1 Winter_dss_wre": 437,
    "G1 Spring_theta_wre": -0.309083316,
    "G1 Spring_pr_1_99_wre": 17,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 469,
    "G2 Fall_theta_wre": -0.010019363,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 499,
    "G2 Winter_theta_wre": 0.355982856,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 536,
    "G2 Spring_theta_wre": 0.568248494,
    "G2 Spring_pr_1_99_wre": 28,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 557,
    "G3 Fall_theta_wre": 0.888542257,
    "G3 Fall_pr_1_99_wre": 30,
    "G3 Fall_ss_wre": 88,
    "G3 Fall_dss_wre": 589,
    "G3 Winter_theta_wre": 1.302085827,
    "G3 Winter_pr_1_99_wre": 20,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 630,
    "G3 Spring_theta_wre": 1.581864934,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 89,
    "G3 Spring_dss_wre": 658
  },
  {
    "ID": 116,
    "KG Winter_theta_wre": -1.628516136,
    "KG Winter_pr_1_99_wre": 27,
    "KG Winter_ss_wre": 88,
    "KG Winter_dss_wre": 337,
    "KG Spring_theta_wre": -1.264638688,
    "KG Spring_pr_1_99_wre": 28,
    "KG Spring_ss_wre": 89,
    "KG Spring_dss_wre": 374,
    "G1 Fall_theta_wre": -0.90139184,
    "G1 Fall_pr_1_99_wre": 30,
    "G1 Fall_ss_wre": 89,
    "G1 Fall_dss_wre": 410,
    "G1 Winter_theta_wre": -0.622837116,
    "G1 Winter_pr_1_99_wre": 21,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 438,
    "G1 Spring_theta_wre": -0.308397749,
    "G1 Spring_pr_1_99_wre": 17,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 469,
    "G2 Fall_theta_wre": -0.005954747,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 499,
    "G2 Winter_theta_wre": 0.35785523,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 536,
    "G2 Spring_theta_wre": 0.575935211,
    "G2 Spring_pr_1_99_wre": 29,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 558,
    "G3 Fall_theta_wre": 0.890698898,
    "G3 Fall_pr_1_99_wre": 31,
    "G3 Fall_ss_wre": 88,
    "G3 Fall_dss_wre": 589,
    "G3 Winter_theta_wre": 1.304905389,
    "G3 Winter_pr_1_99_wre": 20,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 630,
    "G3 Spring_theta_wre": 1.587847231,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 89,
    "G3 Spring_dss_wre": 659
  },
  {
    "ID": 117,
    "KG Winter_theta_wre": -1.625900721,
    "KG Winter_pr_1_99_wre": 27,
    "KG Winter_ss_wre": 88,
    "KG Winter_dss_wre": 337,
    "KG Spring_theta_wre": -1.260304508,
    "KG Spring_pr_1_99_wre": 28,
    "KG Spring_ss_wre": 89,
    "KG Spring_dss_wre": 374,
    "G1 Fall_theta_wre": -0.901171896,
    "G1 Fall_pr_1_99_wre": 30,
    "G1 Fall_ss_wre": 89,
    "G1 Fall_dss_wre": 410,
    "G1 Winter_theta_wre": -0.619586561,
    "G1 Winter_pr_1_99_wre": 21,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 438,
    "G1 Spring_theta_wre": -0.30636988,
    "G1 Spring_pr_1_99_wre": 17,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 469,
    "G2 Fall_theta_wre": -0.002392446,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 89,
    "G2 Fall_dss_wre": 500,
    "G2 Winter_theta_wre": 0.359347272,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 536,
    "G2 Spring_theta_wre": 0.587265731,
    "G2 Spring_pr_1_99_wre": 29,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 559,
    "G3 Fall_theta_wre": 0.904020915,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 89,
    "G3 Fall_dss_wre": 590,
    "G3 Winter_theta_wre": 1.310437662,
    "G3 Winter_pr_1_99_wre": 20,
    "G3 Winter_ss_wre": 89,
    "G3 Winter_dss_wre": 631,
    "G3 Spring_theta_wre": 1.597043846,
    "G3 Spring_pr_1_99_wre": 44,
    "G3 Spring_ss_wre": 89,
    "G3 Spring_dss_wre": 660
  },
  {
    "ID": 118,
    "KG Winter_theta_wre": -1.611748967,
    "KG Winter_pr_1_99_wre": 29,
    "KG Winter_ss_wre": 89,
    "KG Winter_dss_wre": 339,
    "KG Spring_theta_wre": -1.239554043,
    "KG Spring_pr_1_99_wre": 28,
    "KG Spring_ss_wre": 89,
    "KG Spring_dss_wre": 376,
    "G1 Fall_theta_wre": -0.883192387,
    "G1 Fall_pr_1_99_wre": 30,
    "G1 Fall_ss_wre": 90,
    "G1 Fall_dss_wre": 412,
    "G1 Winter_theta_wre": -0.619266367,
    "G1 Winter_pr_1_99_wre": 21,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 438,
    "G1 Spring_theta_wre": -0.302120205,
    "G1 Spring_pr_1_99_wre": 17,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 470,
    "G2 Fall_theta_wre": -0.000681742,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 90,
    "G2 Fall_dss_wre": 500,
    "G2 Winter_theta_wre": 0.361574666,
    "G2 Winter_pr_1_99_wre": 15,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 536,
    "G2 Spring_theta_wre": 0.589520429,
    "G2 Spring_pr_1_99_wre": 29,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 559,
    "G3 Fall_theta_wre": 0.920812297,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 89,
    "G3 Fall_dss_wre": 592,
    "G3 Winter_theta_wre": 1.325305745,
    "G3 Winter_pr_1_99_wre": 20,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 633,
    "G3 Spring_theta_wre": 1.618070736,
    "G3 Spring_pr_1_99_wre": 45,
    "G3 Spring_ss_wre": 90,
    "G3 Spring_dss_wre": 662
  },
  {
    "ID": 119,
    "KG Winter_theta_wre": -1.610116304,
    "KG Winter_pr_1_99_wre": 29,
    "KG Winter_ss_wre": 89,
    "KG Winter_dss_wre": 339,
    "KG Spring_theta_wre": -1.235866284,
    "KG Spring_pr_1_99_wre": 29,
    "KG Spring_ss_wre": 89,
    "KG Spring_dss_wre": 376,
    "G1 Fall_theta_wre": -0.874356441,
    "G1 Fall_pr_1_99_wre": 30,
    "G1 Fall_ss_wre": 90,
    "G1 Fall_dss_wre": 413,
    "G1 Winter_theta_wre": -0.616797879,
    "G1 Winter_pr_1_99_wre": 21,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 438,
    "G1 Spring_theta_wre": -0.301044426,
    "G1 Spring_pr_1_99_wre": 17,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 470,
    "G2 Fall_theta_wre": 0.009827983,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 90,
    "G2 Fall_dss_wre": 501,
    "G2 Winter_theta_wre": 0.372294993,
    "G2 Winter_pr_1_99_wre": 16,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 537,
    "G2 Spring_theta_wre": 0.590475515,
    "G2 Spring_pr_1_99_wre": 29,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 559,
    "G3 Fall_theta_wre": 0.922533868,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 89,
    "G3 Fall_dss_wre": 592,
    "G3 Winter_theta_wre": 1.326830592,
    "G3 Winter_pr_1_99_wre": 20,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 633,
    "G3 Spring_theta_wre": 1.618498442,
    "G3 Spring_pr_1_99_wre": 45,
    "G3 Spring_ss_wre": 90,
    "G3 Spring_dss_wre": 662
  },
  {
    "ID": 120,
    "KG Winter_theta_wre": -1.603868119,
    "KG Winter_pr_1_99_wre": 29,
    "KG Winter_ss_wre": 89,
    "KG Winter_dss_wre": 340,
    "KG Spring_theta_wre": -1.232952213,
    "KG Spring_pr_1_99_wre": 29,
    "KG Spring_ss_wre": 90,
    "KG Spring_dss_wre": 377,
    "G1 Fall_theta_wre": -0.865170452,
    "G1 Fall_pr_1_99_wre": 31,
    "G1 Fall_ss_wre": 90,
    "G1 Fall_dss_wre": 413,
    "G1 Winter_theta_wre": -0.616167643,
    "G1 Winter_pr_1_99_wre": 21,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 438,
    "G1 Spring_theta_wre": -0.299433229,
    "G1 Spring_pr_1_99_wre": 17,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 470,
    "G2 Fall_theta_wre": 0.032131125,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 90,
    "G2 Fall_dss_wre": 503,
    "G2 Winter_theta_wre": 0.381555832,
    "G2 Winter_pr_1_99_wre": 16,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 538,
    "G2 Spring_theta_wre": 0.590829611,
    "G2 Spring_pr_1_99_wre": 29,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 559,
    "G3 Fall_theta_wre": 0.92770202,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 89,
    "G3 Fall_dss_wre": 593,
    "G3 Winter_theta_wre": 1.329145333,
    "G3 Winter_pr_1_99_wre": 20,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 633,
    "G3 Spring_theta_wre": 1.634304549,
    "G3 Spring_pr_1_99_wre": 45,
    "G3 Spring_ss_wre": 90,
    "G3 Spring_dss_wre": 663
  },
  {
    "ID": 121,
    "KG Winter_theta_wre": -1.578722215,
    "KG Winter_pr_1_99_wre": 29,
    "KG Winter_ss_wre": 89,
    "KG Winter_dss_wre": 342,
    "KG Spring_theta_wre": -1.232918247,
    "KG Spring_pr_1_99_wre": 29,
    "KG Spring_ss_wre": 90,
    "KG Spring_dss_wre": 377,
    "G1 Fall_theta_wre": -0.842607281,
    "G1 Fall_pr_1_99_wre": 31,
    "G1 Fall_ss_wre": 90,
    "G1 Fall_dss_wre": 416,
    "G1 Winter_theta_wre": -0.608331518,
    "G1 Winter_pr_1_99_wre": 21,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 439,
    "G1 Spring_theta_wre": -0.29710313,
    "G1 Spring_pr_1_99_wre": 17,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 470,
    "G2 Fall_theta_wre": 0.034853659,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 90,
    "G2 Fall_dss_wre": 503,
    "G2 Winter_theta_wre": 0.383893673,
    "G2 Winter_pr_1_99_wre": 16,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 538,
    "G2 Spring_theta_wre": 0.595127468,
    "G2 Spring_pr_1_99_wre": 29,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 560,
    "G3 Fall_theta_wre": 0.941702803,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 89,
    "G3 Fall_dss_wre": 594,
    "G3 Winter_theta_wre": 1.33493864,
    "G3 Winter_pr_1_99_wre": 21,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 633,
    "G3 Spring_theta_wre": 1.638037609,
    "G3 Spring_pr_1_99_wre": 47,
    "G3 Spring_ss_wre": 90,
    "G3 Spring_dss_wre": 664
  },
  {
    "ID": 122,
    "KG Winter_theta_wre": -1.566066013,
    "KG Winter_pr_1_99_wre": 29,
    "KG Winter_ss_wre": 89,
    "KG Winter_dss_wre": 343,
    "KG Spring_theta_wre": -1.232623452,
    "KG Spring_pr_1_99_wre": 29,
    "KG Spring_ss_wre": 90,
    "KG Spring_dss_wre": 377,
    "G1 Fall_theta_wre": -0.839581141,
    "G1 Fall_pr_1_99_wre": 31,
    "G1 Fall_ss_wre": 90,
    "G1 Fall_dss_wre": 416,
    "G1 Winter_theta_wre": -0.60269302,
    "G1 Winter_pr_1_99_wre": 21,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 440,
    "G1 Spring_theta_wre": -0.280576933,
    "G1 Spring_pr_1_99_wre": 18,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 472,
    "G2 Fall_theta_wre": 0.036317341,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 90,
    "G2 Fall_dss_wre": 504,
    "G2 Winter_theta_wre": 0.386839482,
    "G2 Winter_pr_1_99_wre": 16,
    "G2 Winter_ss_wre": 90,
    "G2 Winter_dss_wre": 539,
    "G2 Spring_theta_wre": 0.597188372,
    "G2 Spring_pr_1_99_wre": 29,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 560,
    "G3 Fall_theta_wre": 0.942286311,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 89,
    "G3 Fall_dss_wre": 594,
    "G3 Winter_theta_wre": 1.336127666,
    "G3 Winter_pr_1_99_wre": 22,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 634,
    "G3 Spring_theta_wre": 1.644350269,
    "G3 Spring_pr_1_99_wre": 47,
    "G3 Spring_ss_wre": 90,
    "G3 Spring_dss_wre": 664
  },
  {
    "ID": 123,
    "KG Winter_theta_wre": -1.566057729,
    "KG Winter_pr_1_99_wre": 29,
    "KG Winter_ss_wre": 89,
    "KG Winter_dss_wre": 343,
    "KG Spring_theta_wre": -1.232555876,
    "KG Spring_pr_1_99_wre": 29,
    "KG Spring_ss_wre": 90,
    "KG Spring_dss_wre": 377,
    "G1 Fall_theta_wre": -0.838288624,
    "G1 Fall_pr_1_99_wre": 31,
    "G1 Fall_ss_wre": 90,
    "G1 Fall_dss_wre": 416,
    "G1 Winter_theta_wre": -0.600964173,
    "G1 Winter_pr_1_99_wre": 21,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 440,
    "G1 Spring_theta_wre": -0.279085705,
    "G1 Spring_pr_1_99_wre": 18,
    "G1 Spring_ss_wre": 89,
    "G1 Spring_dss_wre": 472,
    "G2 Fall_theta_wre": 0.037750654,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 90,
    "G2 Fall_dss_wre": 504,
    "G2 Winter_theta_wre": 0.390834023,
    "G2 Winter_pr_1_99_wre": 16,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 539,
    "G2 Spring_theta_wre": 0.601840096,
    "G2 Spring_pr_1_99_wre": 29,
    "G2 Spring_ss_wre": 89,
    "G2 Spring_dss_wre": 560,
    "G3 Fall_theta_wre": 0.944720277,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 89,
    "G3 Fall_dss_wre": 594,
    "G3 Winter_theta_wre": 1.338769829,
    "G3 Winter_pr_1_99_wre": 22,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 634,
    "G3 Spring_theta_wre": 1.655564412,
    "G3 Spring_pr_1_99_wre": 48,
    "G3 Spring_ss_wre": 90,
    "G3 Spring_dss_wre": 666
  },
  {
    "ID": 124,
    "KG Winter_theta_wre": -1.559536481,
    "KG Winter_pr_1_99_wre": 29,
    "KG Winter_ss_wre": 90,
    "KG Winter_dss_wre": 344,
    "KG Spring_theta_wre": -1.22632091,
    "KG Spring_pr_1_99_wre": 29,
    "KG Spring_ss_wre": 90,
    "KG Spring_dss_wre": 377,
    "G1 Fall_theta_wre": -0.838086731,
    "G1 Fall_pr_1_99_wre": 31,
    "G1 Fall_ss_wre": 90,
    "G1 Fall_dss_wre": 416,
    "G1 Winter_theta_wre": -0.583443284,
    "G1 Winter_pr_1_99_wre": 22,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 442,
    "G1 Spring_theta_wre": -0.264534688,
    "G1 Spring_pr_1_99_wre": 18,
    "G1 Spring_ss_wre": 90,
    "G1 Spring_dss_wre": 474,
    "G2 Fall_theta_wre": 0.048956253,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 90,
    "G2 Fall_dss_wre": 505,
    "G2 Winter_theta_wre": 0.391709254,
    "G2 Winter_pr_1_99_wre": 16,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 539,
    "G2 Spring_theta_wre": 0.612462278,
    "G2 Spring_pr_1_99_wre": 30,
    "G2 Spring_ss_wre": 90,
    "G2 Spring_dss_wre": 561,
    "G3 Fall_theta_wre": 0.945411365,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 89,
    "G3 Fall_dss_wre": 595,
    "G3 Winter_theta_wre": 1.339019311,
    "G3 Winter_pr_1_99_wre": 22,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 634,
    "G3 Spring_theta_wre": 1.656238467,
    "G3 Spring_pr_1_99_wre": 48,
    "G3 Spring_ss_wre": 90,
    "G3 Spring_dss_wre": 666
  },
  {
    "ID": 125,
    "KG Winter_theta_wre": -1.556856308,
    "KG Winter_pr_1_99_wre": 29,
    "KG Winter_ss_wre": 90,
    "KG Winter_dss_wre": 344,
    "KG Spring_theta_wre": -1.225199792,
    "KG Spring_pr_1_99_wre": 29,
    "KG Spring_ss_wre": 90,
    "KG Spring_dss_wre": 377,
    "G1 Fall_theta_wre": -0.830446118,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 90,
    "G1 Fall_dss_wre": 417,
    "G1 Winter_theta_wre": -0.581856445,
    "G1 Winter_pr_1_99_wre": 22,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 442,
    "G1 Spring_theta_wre": -0.264039768,
    "G1 Spring_pr_1_99_wre": 18,
    "G1 Spring_ss_wre": 90,
    "G1 Spring_dss_wre": 474,
    "G2 Fall_theta_wre": 0.054944142,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 505,
    "G2 Winter_theta_wre": 0.396111272,
    "G2 Winter_pr_1_99_wre": 16,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 540,
    "G2 Spring_theta_wre": 0.617376461,
    "G2 Spring_pr_1_99_wre": 30,
    "G2 Spring_ss_wre": 90,
    "G2 Spring_dss_wre": 562,
    "G3 Fall_theta_wre": 0.950919515,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 89,
    "G3 Fall_dss_wre": 595,
    "G3 Winter_theta_wre": 1.339956798,
    "G3 Winter_pr_1_99_wre": 22,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 634,
    "G3 Spring_theta_wre": 1.656923922,
    "G3 Spring_pr_1_99_wre": 48,
    "G3 Spring_ss_wre": 90,
    "G3 Spring_dss_wre": 666
  },
  {
    "ID": 126,
    "KG Winter_theta_wre": -1.553551885,
    "KG Winter_pr_1_99_wre": 30,
    "KG Winter_ss_wre": 90,
    "KG Winter_dss_wre": 345,
    "KG Spring_theta_wre": -1.207492836,
    "KG Spring_pr_1_99_wre": 30,
    "KG Spring_ss_wre": 90,
    "KG Spring_dss_wre": 379,
    "G1 Fall_theta_wre": -0.829671545,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 417,
    "G1 Winter_theta_wre": -0.580970842,
    "G1 Winter_pr_1_99_wre": 22,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 442,
    "G1 Spring_theta_wre": -0.249971335,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 90,
    "G1 Spring_dss_wre": 475,
    "G2 Fall_theta_wre": 0.05633397,
    "G2 Fall_pr_1_99_wre": 25,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 506,
    "G2 Winter_theta_wre": 0.400427785,
    "G2 Winter_pr_1_99_wre": 16,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 540,
    "G2 Spring_theta_wre": 0.619660478,
    "G2 Spring_pr_1_99_wre": 30,
    "G2 Spring_ss_wre": 90,
    "G2 Spring_dss_wre": 562,
    "G3 Fall_theta_wre": 0.95206813,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 89,
    "G3 Fall_dss_wre": 595,
    "G3 Winter_theta_wre": 1.341507924,
    "G3 Winter_pr_1_99_wre": 23,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 634,
    "G3 Spring_theta_wre": 1.665298743,
    "G3 Spring_pr_1_99_wre": 49,
    "G3 Spring_ss_wre": 90,
    "G3 Spring_dss_wre": 667
  },
  {
    "ID": 127,
    "KG Winter_theta_wre": -1.552967261,
    "KG Winter_pr_1_99_wre": 30,
    "KG Winter_ss_wre": 90,
    "KG Winter_dss_wre": 345,
    "KG Spring_theta_wre": -1.19684414,
    "KG Spring_pr_1_99_wre": 30,
    "KG Spring_ss_wre": 90,
    "KG Spring_dss_wre": 380,
    "G1 Fall_theta_wre": -0.829173208,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 417,
    "G1 Winter_theta_wre": -0.580385261,
    "G1 Winter_pr_1_99_wre": 22,
    "G1 Winter_ss_wre": 90,
    "G1 Winter_dss_wre": 442,
    "G1 Spring_theta_wre": -0.247959364,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 90,
    "G1 Spring_dss_wre": 475,
    "G2 Fall_theta_wre": 0.067880088,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 507,
    "G2 Winter_theta_wre": 0.404476034,
    "G2 Winter_pr_1_99_wre": 16,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 540,
    "G2 Spring_theta_wre": 0.632726856,
    "G2 Spring_pr_1_99_wre": 31,
    "G2 Spring_ss_wre": 90,
    "G2 Spring_dss_wre": 563,
    "G3 Fall_theta_wre": 0.968469538,
    "G3 Fall_pr_1_99_wre": 32,
    "G3 Fall_ss_wre": 90,
    "G3 Fall_dss_wre": 597,
    "G3 Winter_theta_wre": 1.349309364,
    "G3 Winter_pr_1_99_wre": 23,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 635,
    "G3 Spring_theta_wre": 1.680463118,
    "G3 Spring_pr_1_99_wre": 49,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 668
  },
  {
    "ID": 128,
    "KG Winter_theta_wre": -1.532884978,
    "KG Winter_pr_1_99_wre": 33,
    "KG Winter_ss_wre": 90,
    "KG Winter_dss_wre": 347,
    "KG Spring_theta_wre": -1.175743098,
    "KG Spring_pr_1_99_wre": 33,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 382,
    "G1 Fall_theta_wre": -0.828485833,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 417,
    "G1 Winter_theta_wre": -0.572253181,
    "G1 Winter_pr_1_99_wre": 22,
    "G1 Winter_ss_wre": 91,
    "G1 Winter_dss_wre": 443,
    "G1 Spring_theta_wre": -0.244239381,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 90,
    "G1 Spring_dss_wre": 476,
    "G2 Fall_theta_wre": 0.073570114,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 507,
    "G2 Winter_theta_wre": 0.407281081,
    "G2 Winter_pr_1_99_wre": 17,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 541,
    "G2 Spring_theta_wre": 0.635970043,
    "G2 Spring_pr_1_99_wre": 31,
    "G2 Spring_ss_wre": 90,
    "G2 Spring_dss_wre": 564,
    "G3 Fall_theta_wre": 0.974474943,
    "G3 Fall_pr_1_99_wre": 33,
    "G3 Fall_ss_wre": 90,
    "G3 Fall_dss_wre": 597,
    "G3 Winter_theta_wre": 1.349609779,
    "G3 Winter_pr_1_99_wre": 23,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 635,
    "G3 Spring_theta_wre": 1.682166405,
    "G3 Spring_pr_1_99_wre": 49,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 668
  },
  {
    "ID": 129,
    "KG Winter_theta_wre": -1.531840598,
    "KG Winter_pr_1_99_wre": 33,
    "KG Winter_ss_wre": 90,
    "KG Winter_dss_wre": 347,
    "KG Spring_theta_wre": -1.167072903,
    "KG Spring_pr_1_99_wre": 33,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 383,
    "G1 Fall_theta_wre": -0.825995839,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 417,
    "G1 Winter_theta_wre": -0.5571878,
    "G1 Winter_pr_1_99_wre": 22,
    "G1 Winter_ss_wre": 91,
    "G1 Winter_dss_wre": 444,
    "G1 Spring_theta_wre": -0.241447895,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 90,
    "G1 Spring_dss_wre": 476,
    "G2 Fall_theta_wre": 0.075094707,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 508,
    "G2 Winter_theta_wre": 0.413931022,
    "G2 Winter_pr_1_99_wre": 17,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 541,
    "G2 Spring_theta_wre": 0.636647852,
    "G2 Spring_pr_1_99_wre": 31,
    "G2 Spring_ss_wre": 90,
    "G2 Spring_dss_wre": 564,
    "G3 Fall_theta_wre": 0.974904602,
    "G3 Fall_pr_1_99_wre": 33,
    "G3 Fall_ss_wre": 90,
    "G3 Fall_dss_wre": 597,
    "G3 Winter_theta_wre": 1.354015236,
    "G3 Winter_pr_1_99_wre": 23,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 635,
    "G3 Spring_theta_wre": 1.685097982,
    "G3 Spring_pr_1_99_wre": 50,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 669
  },
  {
    "ID": 130,
    "KG Winter_theta_wre": -1.51119836,
    "KG Winter_pr_1_99_wre": 33,
    "KG Winter_ss_wre": 90,
    "KG Winter_dss_wre": 349,
    "KG Spring_theta_wre": -1.164002788,
    "KG Spring_pr_1_99_wre": 33,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 384,
    "G1 Fall_theta_wre": -0.825638212,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 417,
    "G1 Winter_theta_wre": -0.553892066,
    "G1 Winter_pr_1_99_wre": 22,
    "G1 Winter_ss_wre": 91,
    "G1 Winter_dss_wre": 445,
    "G1 Spring_theta_wre": -0.240124091,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 90,
    "G1 Spring_dss_wre": 476,
    "G2 Fall_theta_wre": 0.076697944,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 508,
    "G2 Winter_theta_wre": 0.415831039,
    "G2 Winter_pr_1_99_wre": 17,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 542,
    "G2 Spring_theta_wre": 0.637014902,
    "G2 Spring_pr_1_99_wre": 31,
    "G2 Spring_ss_wre": 90,
    "G2 Spring_dss_wre": 564,
    "G3 Fall_theta_wre": 0.979064019,
    "G3 Fall_pr_1_99_wre": 33,
    "G3 Fall_ss_wre": 90,
    "G3 Fall_dss_wre": 598,
    "G3 Winter_theta_wre": 1.358380646,
    "G3 Winter_pr_1_99_wre": 23,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 636,
    "G3 Spring_theta_wre": 1.685297395,
    "G3 Spring_pr_1_99_wre": 50,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 669
  },
  {
    "ID": 131,
    "KG Winter_theta_wre": -1.508218943,
    "KG Winter_pr_1_99_wre": 33,
    "KG Winter_ss_wre": 90,
    "KG Winter_dss_wre": 349,
    "KG Spring_theta_wre": -1.163391216,
    "KG Spring_pr_1_99_wre": 33,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 384,
    "G1 Fall_theta_wre": -0.821270694,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 418,
    "G1 Winter_theta_wre": -0.552683616,
    "G1 Winter_pr_1_99_wre": 22,
    "G1 Winter_ss_wre": 91,
    "G1 Winter_dss_wre": 445,
    "G1 Spring_theta_wre": -0.238886103,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 90,
    "G1 Spring_dss_wre": 476,
    "G2 Fall_theta_wre": 0.080555761,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 508,
    "G2 Winter_theta_wre": 0.425752643,
    "G2 Winter_pr_1_99_wre": 17,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 543,
    "G2 Spring_theta_wre": 0.639835155,
    "G2 Spring_pr_1_99_wre": 31,
    "G2 Spring_ss_wre": 90,
    "G2 Spring_dss_wre": 564,
    "G3 Fall_theta_wre": 0.991168819,
    "G3 Fall_pr_1_99_wre": 33,
    "G3 Fall_ss_wre": 90,
    "G3 Fall_dss_wre": 599,
    "G3 Winter_theta_wre": 1.365572851,
    "G3 Winter_pr_1_99_wre": 23,
    "G3 Winter_ss_wre": 90,
    "G3 Winter_dss_wre": 637,
    "G3 Spring_theta_wre": 1.690902315,
    "G3 Spring_pr_1_99_wre": 51,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 669
  },
  {
    "ID": 132,
    "KG Winter_theta_wre": -1.508030678,
    "KG Winter_pr_1_99_wre": 33,
    "KG Winter_ss_wre": 90,
    "KG Winter_dss_wre": 349,
    "KG Spring_theta_wre": -1.157933721,
    "KG Spring_pr_1_99_wre": 34,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 384,
    "G1 Fall_theta_wre": -0.815893972,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 418,
    "G1 Winter_theta_wre": -0.550172417,
    "G1 Winter_pr_1_99_wre": 22,
    "G1 Winter_ss_wre": 91,
    "G1 Winter_dss_wre": 445,
    "G1 Spring_theta_wre": -0.221964589,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 90,
    "G1 Spring_dss_wre": 478,
    "G2 Fall_theta_wre": 0.084051092,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 508,
    "G2 Winter_theta_wre": 0.429404946,
    "G2 Winter_pr_1_99_wre": 17,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 543,
    "G2 Spring_theta_wre": 0.650361603,
    "G2 Spring_pr_1_99_wre": 31,
    "G2 Spring_ss_wre": 90,
    "G2 Spring_dss_wre": 565,
    "G3 Fall_theta_wre": 0.995057182,
    "G3 Fall_pr_1_99_wre": 33,
    "G3 Fall_ss_wre": 90,
    "G3 Fall_dss_wre": 600,
    "G3 Winter_theta_wre": 1.38251078,
    "G3 Winter_pr_1_99_wre": 24,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 638,
    "G3 Spring_theta_wre": 1.700373432,
    "G3 Spring_pr_1_99_wre": 51,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 670
  },
  {
    "ID": 133,
    "KG Winter_theta_wre": -1.499825352,
    "KG Winter_pr_1_99_wre": 33,
    "KG Winter_ss_wre": 91,
    "KG Winter_dss_wre": 350,
    "KG Spring_theta_wre": -1.154185685,
    "KG Spring_pr_1_99_wre": 34,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 385,
    "G1 Fall_theta_wre": -0.812887294,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 419,
    "G1 Winter_theta_wre": -0.534937146,
    "G1 Winter_pr_1_99_wre": 23,
    "G1 Winter_ss_wre": 91,
    "G1 Winter_dss_wre": 447,
    "G1 Spring_theta_wre": -0.216483257,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 91,
    "G1 Spring_dss_wre": 478,
    "G2 Fall_theta_wre": 0.089277641,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 509,
    "G2 Winter_theta_wre": 0.429870586,
    "G2 Winter_pr_1_99_wre": 17,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 543,
    "G2 Spring_theta_wre": 0.653256826,
    "G2 Spring_pr_1_99_wre": 31,
    "G2 Spring_ss_wre": 90,
    "G2 Spring_dss_wre": 565,
    "G3 Fall_theta_wre": 1.006903562,
    "G3 Fall_pr_1_99_wre": 35,
    "G3 Fall_ss_wre": 90,
    "G3 Fall_dss_wre": 601,
    "G3 Winter_theta_wre": 1.383529814,
    "G3 Winter_pr_1_99_wre": 24,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 638,
    "G3 Spring_theta_wre": 1.702111646,
    "G3 Spring_pr_1_99_wre": 51,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 670
  },
  {
    "ID": 134,
    "KG Winter_theta_wre": -1.488086191,
    "KG Winter_pr_1_99_wre": 34,
    "KG Winter_ss_wre": 91,
    "KG Winter_dss_wre": 351,
    "KG Spring_theta_wre": -1.15164509,
    "KG Spring_pr_1_99_wre": 34,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 385,
    "G1 Fall_theta_wre": -0.812149235,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 419,
    "G1 Winter_theta_wre": -0.534622875,
    "G1 Winter_pr_1_99_wre": 23,
    "G1 Winter_ss_wre": 91,
    "G1 Winter_dss_wre": 447,
    "G1 Spring_theta_wre": -0.215062825,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 91,
    "G1 Spring_dss_wre": 478,
    "G2 Fall_theta_wre": 0.089903648,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 509,
    "G2 Winter_theta_wre": 0.43256059,
    "G2 Winter_pr_1_99_wre": 17,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 543,
    "G2 Spring_theta_wre": 0.66130899,
    "G2 Spring_pr_1_99_wre": 32,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 566,
    "G3 Fall_theta_wre": 1.008432431,
    "G3 Fall_pr_1_99_wre": 35,
    "G3 Fall_ss_wre": 90,
    "G3 Fall_dss_wre": 601,
    "G3 Winter_theta_wre": 1.386045053,
    "G3 Winter_pr_1_99_wre": 24,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 639,
    "G3 Spring_theta_wre": 1.703797822,
    "G3 Spring_pr_1_99_wre": 51,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 670
  },
  {
    "ID": 135,
    "KG Winter_theta_wre": -1.487012751,
    "KG Winter_pr_1_99_wre": 34,
    "KG Winter_ss_wre": 91,
    "KG Winter_dss_wre": 351,
    "KG Spring_theta_wre": -1.148011669,
    "KG Spring_pr_1_99_wre": 34,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 385,
    "G1 Fall_theta_wre": -0.810624874,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 419,
    "G1 Winter_theta_wre": -0.530956878,
    "G1 Winter_pr_1_99_wre": 23,
    "G1 Winter_ss_wre": 91,
    "G1 Winter_dss_wre": 447,
    "G1 Spring_theta_wre": -0.201187339,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 91,
    "G1 Spring_dss_wre": 480,
    "G2 Fall_theta_wre": 0.08990491,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 509,
    "G2 Winter_theta_wre": 0.440772698,
    "G2 Winter_pr_1_99_wre": 17,
    "G2 Winter_ss_wre": 91,
    "G2 Winter_dss_wre": 544,
    "G2 Spring_theta_wre": 0.668731965,
    "G2 Spring_pr_1_99_wre": 32,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 567,
    "G3 Fall_theta_wre": 1.014362875,
    "G3 Fall_pr_1_99_wre": 35,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 601,
    "G3 Winter_theta_wre": 1.389186054,
    "G3 Winter_pr_1_99_wre": 25,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 639,
    "G3 Spring_theta_wre": 1.703944199,
    "G3 Spring_pr_1_99_wre": 51,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 670
  },
  {
    "ID": 136,
    "KG Winter_theta_wre": -1.483252631,
    "KG Winter_pr_1_99_wre": 34,
    "KG Winter_ss_wre": 91,
    "KG Winter_dss_wre": 352,
    "KG Spring_theta_wre": -1.147961972,
    "KG Spring_pr_1_99_wre": 34,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 385,
    "G1 Fall_theta_wre": -0.803205945,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 420,
    "G1 Winter_theta_wre": -0.5294445,
    "G1 Winter_pr_1_99_wre": 23,
    "G1 Winter_ss_wre": 91,
    "G1 Winter_dss_wre": 447,
    "G1 Spring_theta_wre": -0.18488755,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 91,
    "G1 Spring_dss_wre": 482,
    "G2 Fall_theta_wre": 0.098860469,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 510,
    "G2 Winter_theta_wre": 0.445822535,
    "G2 Winter_pr_1_99_wre": 17,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 545,
    "G2 Spring_theta_wre": 0.670219978,
    "G2 Spring_pr_1_99_wre": 32,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 567,
    "G3 Fall_theta_wre": 1.014857575,
    "G3 Fall_pr_1_99_wre": 35,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 601,
    "G3 Winter_theta_wre": 1.38929415,
    "G3 Winter_pr_1_99_wre": 25,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 639,
    "G3 Spring_theta_wre": 1.709496479,
    "G3 Spring_pr_1_99_wre": 51,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 671
  },
  {
    "ID": 137,
    "KG Winter_theta_wre": -1.476818562,
    "KG Winter_pr_1_99_wre": 34,
    "KG Winter_ss_wre": 91,
    "KG Winter_dss_wre": 352,
    "KG Spring_theta_wre": -1.145562395,
    "KG Spring_pr_1_99_wre": 35,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 385,
    "G1 Fall_theta_wre": -0.797099483,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 420,
    "G1 Winter_theta_wre": -0.521074929,
    "G1 Winter_pr_1_99_wre": 23,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 448,
    "G1 Spring_theta_wre": -0.184251281,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 91,
    "G1 Spring_dss_wre": 482,
    "G2 Fall_theta_wre": 0.099601279,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 510,
    "G2 Winter_theta_wre": 0.446733875,
    "G2 Winter_pr_1_99_wre": 17,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 545,
    "G2 Spring_theta_wre": 0.673543453,
    "G2 Spring_pr_1_99_wre": 32,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 567,
    "G3 Fall_theta_wre": 1.017628279,
    "G3 Fall_pr_1_99_wre": 35,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 602,
    "G3 Winter_theta_wre": 1.395735414,
    "G3 Winter_pr_1_99_wre": 26,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 640,
    "G3 Spring_theta_wre": 1.720747952,
    "G3 Spring_pr_1_99_wre": 51,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 672
  },
  {
    "ID": 138,
    "KG Winter_theta_wre": -1.470901202,
    "KG Winter_pr_1_99_wre": 34,
    "KG Winter_ss_wre": 91,
    "KG Winter_dss_wre": 353,
    "KG Spring_theta_wre": -1.138690413,
    "KG Spring_pr_1_99_wre": 35,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 386,
    "G1 Fall_theta_wre": -0.795339945,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 420,
    "G1 Winter_theta_wre": -0.506893667,
    "G1 Winter_pr_1_99_wre": 23,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 449,
    "G1 Spring_theta_wre": -0.174560009,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 91,
    "G1 Spring_dss_wre": 483,
    "G2 Fall_theta_wre": 0.104795489,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 510,
    "G2 Winter_theta_wre": 0.453599888,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 545,
    "G2 Spring_theta_wre": 0.673728132,
    "G2 Spring_pr_1_99_wre": 32,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 567,
    "G3 Fall_theta_wre": 1.019177648,
    "G3 Fall_pr_1_99_wre": 35,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 602,
    "G3 Winter_theta_wre": 1.398670772,
    "G3 Winter_pr_1_99_wre": 26,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 640,
    "G3 Spring_theta_wre": 1.725709334,
    "G3 Spring_pr_1_99_wre": 51,
    "G3 Spring_ss_wre": 91,
    "G3 Spring_dss_wre": 673
  },
  {
    "ID": 139,
    "KG Winter_theta_wre": -1.460591943,
    "KG Winter_pr_1_99_wre": 35,
    "KG Winter_ss_wre": 91,
    "KG Winter_dss_wre": 354,
    "KG Spring_theta_wre": -1.134759284,
    "KG Spring_pr_1_99_wre": 35,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 387,
    "G1 Fall_theta_wre": -0.794853483,
    "G1 Fall_pr_1_99_wre": 32,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 421,
    "G1 Winter_theta_wre": -0.505365694,
    "G1 Winter_pr_1_99_wre": 23,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 449,
    "G1 Spring_theta_wre": -0.172692762,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 91,
    "G1 Spring_dss_wre": 483,
    "G2 Fall_theta_wre": 0.108415796,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 91,
    "G2 Fall_dss_wre": 511,
    "G2 Winter_theta_wre": 0.463122228,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 546,
    "G2 Spring_theta_wre": 0.68069547,
    "G2 Spring_pr_1_99_wre": 32,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 568,
    "G3 Fall_theta_wre": 1.025803513,
    "G3 Fall_pr_1_99_wre": 36,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 603,
    "G3 Winter_theta_wre": 1.401122583,
    "G3 Winter_pr_1_99_wre": 27,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 640,
    "G3 Spring_theta_wre": 1.730254324,
    "G3 Spring_pr_1_99_wre": 51,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 673
  },
  {
    "ID": 140,
    "KG Winter_theta_wre": -1.459940025,
    "KG Winter_pr_1_99_wre": 35,
    "KG Winter_ss_wre": 91,
    "KG Winter_dss_wre": 354,
    "KG Spring_theta_wre": -1.133135561,
    "KG Spring_pr_1_99_wre": 35,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 387,
    "G1 Fall_theta_wre": -0.791737724,
    "G1 Fall_pr_1_99_wre": 33,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 421,
    "G1 Winter_theta_wre": -0.502360604,
    "G1 Winter_pr_1_99_wre": 23,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 450,
    "G1 Spring_theta_wre": -0.171589574,
    "G1 Spring_pr_1_99_wre": 20,
    "G1 Spring_ss_wre": 91,
    "G1 Spring_dss_wre": 483,
    "G2 Fall_theta_wre": 0.116920825,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 512,
    "G2 Winter_theta_wre": 0.468359206,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 547,
    "G2 Spring_theta_wre": 0.684502562,
    "G2 Spring_pr_1_99_wre": 33,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 568,
    "G3 Fall_theta_wre": 1.031435468,
    "G3 Fall_pr_1_99_wre": 36,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 603,
    "G3 Winter_theta_wre": 1.406669245,
    "G3 Winter_pr_1_99_wre": 27,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 641,
    "G3 Spring_theta_wre": 1.732913076,
    "G3 Spring_pr_1_99_wre": 52,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 673
  },
  {
    "ID": 141,
    "KG Winter_theta_wre": -1.454925909,
    "KG Winter_pr_1_99_wre": 35,
    "KG Winter_ss_wre": 91,
    "KG Winter_dss_wre": 355,
    "KG Spring_theta_wre": -1.132327105,
    "KG Spring_pr_1_99_wre": 35,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 387,
    "G1 Fall_theta_wre": -0.788970067,
    "G1 Fall_pr_1_99_wre": 33,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 421,
    "G1 Winter_theta_wre": -0.494118884,
    "G1 Winter_pr_1_99_wre": 24,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 451,
    "G1 Spring_theta_wre": -0.15739307,
    "G1 Spring_pr_1_99_wre": 22,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 484,
    "G2 Fall_theta_wre": 0.120106479,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 512,
    "G2 Winter_theta_wre": 0.471106195,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 547,
    "G2 Spring_theta_wre": 0.686661839,
    "G2 Spring_pr_1_99_wre": 33,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 569,
    "G3 Fall_theta_wre": 1.033758996,
    "G3 Fall_pr_1_99_wre": 36,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 603,
    "G3 Winter_theta_wre": 1.408178454,
    "G3 Winter_pr_1_99_wre": 27,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 641,
    "G3 Spring_theta_wre": 1.735543723,
    "G3 Spring_pr_1_99_wre": 52,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 674
  },
  {
    "ID": 142,
    "KG Winter_theta_wre": -1.454164269,
    "KG Winter_pr_1_99_wre": 35,
    "KG Winter_ss_wre": 91,
    "KG Winter_dss_wre": 355,
    "KG Spring_theta_wre": -1.131846539,
    "KG Spring_pr_1_99_wre": 35,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 387,
    "G1 Fall_theta_wre": -0.778432082,
    "G1 Fall_pr_1_99_wre": 33,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 422,
    "G1 Winter_theta_wre": -0.492488233,
    "G1 Winter_pr_1_99_wre": 24,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 451,
    "G1 Spring_theta_wre": -0.153485552,
    "G1 Spring_pr_1_99_wre": 22,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 485,
    "G2 Fall_theta_wre": 0.123807494,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 512,
    "G2 Winter_theta_wre": 0.471884477,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 547,
    "G2 Spring_theta_wre": 0.68800466,
    "G2 Spring_pr_1_99_wre": 33,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 569,
    "G3 Fall_theta_wre": 1.038191699,
    "G3 Fall_pr_1_99_wre": 36,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 604,
    "G3 Winter_theta_wre": 1.410465035,
    "G3 Winter_pr_1_99_wre": 27,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 641,
    "G3 Spring_theta_wre": 1.738552987,
    "G3 Spring_pr_1_99_wre": 52,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 674
  },
  {
    "ID": 143,
    "KG Winter_theta_wre": -1.447421732,
    "KG Winter_pr_1_99_wre": 36,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 355,
    "KG Spring_theta_wre": -1.129517767,
    "KG Spring_pr_1_99_wre": 35,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 387,
    "G1 Fall_theta_wre": -0.777129641,
    "G1 Fall_pr_1_99_wre": 33,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 422,
    "G1 Winter_theta_wre": -0.491652948,
    "G1 Winter_pr_1_99_wre": 24,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 451,
    "G1 Spring_theta_wre": -0.150022508,
    "G1 Spring_pr_1_99_wre": 22,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 485,
    "G2 Fall_theta_wre": 0.132296663,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 513,
    "G2 Winter_theta_wre": 0.483872556,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 548,
    "G2 Spring_theta_wre": 0.693976542,
    "G2 Spring_pr_1_99_wre": 33,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 569,
    "G3 Fall_theta_wre": 1.044578092,
    "G3 Fall_pr_1_99_wre": 36,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 604,
    "G3 Winter_theta_wre": 1.411092849,
    "G3 Winter_pr_1_99_wre": 27,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 641,
    "G3 Spring_theta_wre": 1.740317809,
    "G3 Spring_pr_1_99_wre": 52,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 674
  },
  {
    "ID": 144,
    "KG Winter_theta_wre": -1.446303497,
    "KG Winter_pr_1_99_wre": 36,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 355,
    "KG Spring_theta_wre": -1.128671468,
    "KG Spring_pr_1_99_wre": 35,
    "KG Spring_ss_wre": 91,
    "KG Spring_dss_wre": 387,
    "G1 Fall_theta_wre": -0.776485591,
    "G1 Fall_pr_1_99_wre": 33,
    "G1 Fall_ss_wre": 91,
    "G1 Fall_dss_wre": 422,
    "G1 Winter_theta_wre": -0.490886133,
    "G1 Winter_pr_1_99_wre": 24,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 451,
    "G1 Spring_theta_wre": -0.148864243,
    "G1 Spring_pr_1_99_wre": 22,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 485,
    "G2 Fall_theta_wre": 0.1379618,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 514,
    "G2 Winter_theta_wre": 0.484688295,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 548,
    "G2 Spring_theta_wre": 0.709893648,
    "G2 Spring_pr_1_99_wre": 34,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 571,
    "G3 Fall_theta_wre": 1.044824081,
    "G3 Fall_pr_1_99_wre": 36,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 604,
    "G3 Winter_theta_wre": 1.412750178,
    "G3 Winter_pr_1_99_wre": 27,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 641,
    "G3 Spring_theta_wre": 1.742535612,
    "G3 Spring_pr_1_99_wre": 52,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 674
  },
  {
    "ID": 145,
    "KG Winter_theta_wre": -1.440494778,
    "KG Winter_pr_1_99_wre": 36,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 356,
    "KG Spring_theta_wre": -1.119787685,
    "KG Spring_pr_1_99_wre": 35,
    "KG Spring_ss_wre": 92,
    "KG Spring_dss_wre": 388,
    "G1 Fall_theta_wre": -0.767326847,
    "G1 Fall_pr_1_99_wre": 34,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 423,
    "G1 Winter_theta_wre": -0.490287465,
    "G1 Winter_pr_1_99_wre": 24,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 451,
    "G1 Spring_theta_wre": -0.144140597,
    "G1 Spring_pr_1_99_wre": 23,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 486,
    "G2 Fall_theta_wre": 0.138994856,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 514,
    "G2 Winter_theta_wre": 0.485394559,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 549,
    "G2 Spring_theta_wre": 0.710812715,
    "G2 Spring_pr_1_99_wre": 34,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 571,
    "G3 Fall_theta_wre": 1.045819316,
    "G3 Fall_pr_1_99_wre": 36,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 605,
    "G3 Winter_theta_wre": 1.413630176,
    "G3 Winter_pr_1_99_wre": 27,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 641,
    "G3 Spring_theta_wre": 1.742979447,
    "G3 Spring_pr_1_99_wre": 52,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 674
  },
  {
    "ID": 146,
    "KG Winter_theta_wre": -1.439370262,
    "KG Winter_pr_1_99_wre": 36,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 356,
    "KG Spring_theta_wre": -1.115412535,
    "KG Spring_pr_1_99_wre": 35,
    "KG Spring_ss_wre": 92,
    "KG Spring_dss_wre": 388,
    "G1 Fall_theta_wre": -0.763735257,
    "G1 Fall_pr_1_99_wre": 34,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 424,
    "G1 Winter_theta_wre": -0.488640371,
    "G1 Winter_pr_1_99_wre": 24,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 451,
    "G1 Spring_theta_wre": -0.143539805,
    "G1 Spring_pr_1_99_wre": 23,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 486,
    "G2 Fall_theta_wre": 0.139177447,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 514,
    "G2 Winter_theta_wre": 0.487841467,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 549,
    "G2 Spring_theta_wre": 0.712318306,
    "G2 Spring_pr_1_99_wre": 34,
    "G2 Spring_ss_wre": 91,
    "G2 Spring_dss_wre": 571,
    "G3 Fall_theta_wre": 1.054443436,
    "G3 Fall_pr_1_99_wre": 37,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 605,
    "G3 Winter_theta_wre": 1.41547076,
    "G3 Winter_pr_1_99_wre": 27,
    "G3 Winter_ss_wre": 91,
    "G3 Winter_dss_wre": 642,
    "G3 Spring_theta_wre": 1.763122387,
    "G3 Spring_pr_1_99_wre": 52,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 676
  },
  {
    "ID": 147,
    "KG Winter_theta_wre": -1.435865498,
    "KG Winter_pr_1_99_wre": 36,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 356,
    "KG Spring_theta_wre": -1.106328127,
    "KG Spring_pr_1_99_wre": 36,
    "KG Spring_ss_wre": 92,
    "KG Spring_dss_wre": 389,
    "G1 Fall_theta_wre": -0.75193683,
    "G1 Fall_pr_1_99_wre": 36,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 425,
    "G1 Winter_theta_wre": -0.48268581,
    "G1 Winter_pr_1_99_wre": 24,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 452,
    "G1 Spring_theta_wre": -0.139226597,
    "G1 Spring_pr_1_99_wre": 23,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 486,
    "G2 Fall_theta_wre": 0.141162075,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 514,
    "G2 Winter_theta_wre": 0.489749435,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 549,
    "G2 Spring_theta_wre": 0.716385894,
    "G2 Spring_pr_1_99_wre": 34,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 572,
    "G3 Fall_theta_wre": 1.062001433,
    "G3 Fall_pr_1_99_wre": 37,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 606,
    "G3 Winter_theta_wre": 1.418715224,
    "G3 Winter_pr_1_99_wre": 27,
    "G3 Winter_ss_wre": 92,
    "G3 Winter_dss_wre": 642,
    "G3 Spring_theta_wre": 1.768901501,
    "G3 Spring_pr_1_99_wre": 52,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 677
  },
  {
    "ID": 148,
    "KG Winter_theta_wre": -1.42727548,
    "KG Winter_pr_1_99_wre": 37,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 357,
    "KG Spring_theta_wre": -1.101914372,
    "KG Spring_pr_1_99_wre": 36,
    "KG Spring_ss_wre": 92,
    "KG Spring_dss_wre": 390,
    "G1 Fall_theta_wre": -0.748575484,
    "G1 Fall_pr_1_99_wre": 36,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 425,
    "G1 Winter_theta_wre": -0.473509018,
    "G1 Winter_pr_1_99_wre": 25,
    "G1 Winter_ss_wre": 92,
    "G1 Winter_dss_wre": 453,
    "G1 Spring_theta_wre": -0.129417275,
    "G1 Spring_pr_1_99_wre": 24,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 487,
    "G2 Fall_theta_wre": 0.143664841,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 514,
    "G2 Winter_theta_wre": 0.49458332,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 549,
    "G2 Spring_theta_wre": 0.720701149,
    "G2 Spring_pr_1_99_wre": 35,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 572,
    "G3 Fall_theta_wre": 1.063998002,
    "G3 Fall_pr_1_99_wre": 37,
    "G3 Fall_ss_wre": 91,
    "G3 Fall_dss_wre": 606,
    "G3 Winter_theta_wre": 1.432430454,
    "G3 Winter_pr_1_99_wre": 28,
    "G3 Winter_ss_wre": 92,
    "G3 Winter_dss_wre": 643,
    "G3 Spring_theta_wre": 1.770710404,
    "G3 Spring_pr_1_99_wre": 53,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 677
  },
  {
    "ID": 149,
    "KG Winter_theta_wre": -1.426170623,
    "KG Winter_pr_1_99_wre": 37,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 357,
    "KG Spring_theta_wre": -1.093890932,
    "KG Spring_pr_1_99_wre": 37,
    "KG Spring_ss_wre": 92,
    "KG Spring_dss_wre": 391,
    "G1 Fall_theta_wre": -0.746153506,
    "G1 Fall_pr_1_99_wre": 36,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 425,
    "G1 Winter_theta_wre": -0.471408386,
    "G1 Winter_pr_1_99_wre": 25,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 453,
    "G1 Spring_theta_wre": -0.125145261,
    "G1 Spring_pr_1_99_wre": 24,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 487,
    "G2 Fall_theta_wre": 0.14390215,
    "G2 Fall_pr_1_99_wre": 26,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 514,
    "G2 Winter_theta_wre": 0.498463455,
    "G2 Winter_pr_1_99_wre": 18,
    "G2 Winter_ss_wre": 92,
    "G2 Winter_dss_wre": 550,
    "G2 Spring_theta_wre": 0.726026306,
    "G2 Spring_pr_1_99_wre": 35,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 573,
    "G3 Fall_theta_wre": 1.067074591,
    "G3 Fall_pr_1_99_wre": 37,
    "G3 Fall_ss_wre": 92,
    "G3 Fall_dss_wre": 607,
    "G3 Winter_theta_wre": 1.436329703,
    "G3 Winter_pr_1_99_wre": 28,
    "G3 Winter_ss_wre": 92,
    "G3 Winter_dss_wre": 644,
    "G3 Spring_theta_wre": 1.77965021,
    "G3 Spring_pr_1_99_wre": 53,
    "G3 Spring_ss_wre": 92,
    "G3 Spring_dss_wre": 678
  },
  {
    "ID": 150,
    "KG Winter_theta_wre": -1.419342084,
    "KG Winter_pr_1_99_wre": 37,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 358,
    "KG Spring_theta_wre": -1.091817527,
    "KG Spring_pr_1_99_wre": 37,
    "KG Spring_ss_wre": 92,
    "KG Spring_dss_wre": 391,
    "G1 Fall_theta_wre": -0.745366473,
    "G1 Fall_pr_1_99_wre": 36,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 425,
    "G1 Winter_theta_wre": -0.464007475,
    "G1 Winter_pr_1_99_wre": 25,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 454,
    "G1 Spring_theta_wre": -0.123824264,
    "G1 Spring_pr_1_99_wre": 24,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 488,
    "G2 Fall_theta_wre": 0.148628162,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 515,
    "G2 Winter_theta_wre": 0.499650109,
    "G2 Winter_pr_1_99_wre": 19,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 550,
    "G2 Spring_theta_wre": 0.733855478,
    "G2 Spring_pr_1_99_wre": 35,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 573,
    "G3 Fall_theta_wre": 1.071611909,
    "G3 Fall_pr_1_99_wre": 37,
    "G3 Fall_ss_wre": 92,
    "G3 Fall_dss_wre": 607,
    "G3 Winter_theta_wre": 1.436514573,
    "G3 Winter_pr_1_99_wre": 28,
    "G3 Winter_ss_wre": 92,
    "G3 Winter_dss_wre": 644,
    "G3 Spring_theta_wre": 1.788649249,
    "G3 Spring_pr_1_99_wre": 53,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 679
  },
  {
    "ID": 151,
    "KG Winter_theta_wre": -1.414216247,
    "KG Winter_pr_1_99_wre": 37,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 359,
    "KG Spring_theta_wre": -1.082936132,
    "KG Spring_pr_1_99_wre": 37,
    "KG Spring_ss_wre": 92,
    "KG Spring_dss_wre": 392,
    "G1 Fall_theta_wre": -0.742516578,
    "G1 Fall_pr_1_99_wre": 36,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 426,
    "G1 Winter_theta_wre": -0.458118983,
    "G1 Winter_pr_1_99_wre": 25,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 454,
    "G1 Spring_theta_wre": -0.115284076,
    "G1 Spring_pr_1_99_wre": 24,
    "G1 Spring_ss_wre": 92,
    "G1 Spring_dss_wre": 488,
    "G2 Fall_theta_wre": 0.152131041,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 515,
    "G2 Winter_theta_wre": 0.505381864,
    "G2 Winter_pr_1_99_wre": 19,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 551,
    "G2 Spring_theta_wre": 0.735471471,
    "G2 Spring_pr_1_99_wre": 35,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 574,
    "G3 Fall_theta_wre": 1.073014504,
    "G3 Fall_pr_1_99_wre": 37,
    "G3 Fall_ss_wre": 92,
    "G3 Fall_dss_wre": 607,
    "G3 Winter_theta_wre": 1.439541226,
    "G3 Winter_pr_1_99_wre": 28,
    "G3 Winter_ss_wre": 92,
    "G3 Winter_dss_wre": 644,
    "G3 Spring_theta_wre": 1.791513478,
    "G3 Spring_pr_1_99_wre": 54,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 679
  },
  {
    "ID": 152,
    "KG Winter_theta_wre": -1.414111453,
    "KG Winter_pr_1_99_wre": 37,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 359,
    "KG Spring_theta_wre": -1.074826802,
    "KG Spring_pr_1_99_wre": 37,
    "KG Spring_ss_wre": 92,
    "KG Spring_dss_wre": 393,
    "G1 Fall_theta_wre": -0.742341756,
    "G1 Fall_pr_1_99_wre": 36,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 426,
    "G1 Winter_theta_wre": -0.453657334,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 455,
    "G1 Spring_theta_wre": -0.103072323,
    "G1 Spring_pr_1_99_wre": 25,
    "G1 Spring_ss_wre": 93,
    "G1 Spring_dss_wre": 490,
    "G2 Fall_theta_wre": 0.15720619,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 516,
    "G2 Winter_theta_wre": 0.505620746,
    "G2 Winter_pr_1_99_wre": 19,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 551,
    "G2 Spring_theta_wre": 0.735808021,
    "G2 Spring_pr_1_99_wre": 35,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 574,
    "G3 Fall_theta_wre": 1.073494938,
    "G3 Fall_pr_1_99_wre": 37,
    "G3 Fall_ss_wre": 92,
    "G3 Fall_dss_wre": 607,
    "G3 Winter_theta_wre": 1.439551563,
    "G3 Winter_pr_1_99_wre": 28,
    "G3 Winter_ss_wre": 92,
    "G3 Winter_dss_wre": 644,
    "G3 Spring_theta_wre": 1.792320116,
    "G3 Spring_pr_1_99_wre": 54,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 679
  },
  {
    "ID": 153,
    "KG Winter_theta_wre": -1.406761361,
    "KG Winter_pr_1_99_wre": 37,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 359,
    "KG Spring_theta_wre": -1.073106464,
    "KG Spring_pr_1_99_wre": 37,
    "KG Spring_ss_wre": 92,
    "KG Spring_dss_wre": 393,
    "G1 Fall_theta_wre": -0.739673125,
    "G1 Fall_pr_1_99_wre": 36,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 426,
    "G1 Winter_theta_wre": -0.452666726,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 455,
    "G1 Spring_theta_wre": -0.097426101,
    "G1 Spring_pr_1_99_wre": 25,
    "G1 Spring_ss_wre": 93,
    "G1 Spring_dss_wre": 490,
    "G2 Fall_theta_wre": 0.158299084,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 516,
    "G2 Winter_theta_wre": 0.506293905,
    "G2 Winter_pr_1_99_wre": 19,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 551,
    "G2 Spring_theta_wre": 0.7378786,
    "G2 Spring_pr_1_99_wre": 35,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 574,
    "G3 Fall_theta_wre": 1.080187138,
    "G3 Fall_pr_1_99_wre": 38,
    "G3 Fall_ss_wre": 92,
    "G3 Fall_dss_wre": 608,
    "G3 Winter_theta_wre": 1.439851812,
    "G3 Winter_pr_1_99_wre": 28,
    "G3 Winter_ss_wre": 92,
    "G3 Winter_dss_wre": 644,
    "G3 Spring_theta_wre": 1.794234299,
    "G3 Spring_pr_1_99_wre": 54,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 679
  },
  {
    "ID": 154,
    "KG Winter_theta_wre": -1.406188874,
    "KG Winter_pr_1_99_wre": 37,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 359,
    "KG Spring_theta_wre": -1.067545967,
    "KG Spring_pr_1_99_wre": 37,
    "KG Spring_ss_wre": 92,
    "KG Spring_dss_wre": 393,
    "G1 Fall_theta_wre": -0.736726689,
    "G1 Fall_pr_1_99_wre": 36,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 426,
    "G1 Winter_theta_wre": -0.448099038,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 455,
    "G1 Spring_theta_wre": -0.093484552,
    "G1 Spring_pr_1_99_wre": 25,
    "G1 Spring_ss_wre": 93,
    "G1 Spring_dss_wre": 491,
    "G2 Fall_theta_wre": 0.163192548,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 516,
    "G2 Winter_theta_wre": 0.508247039,
    "G2 Winter_pr_1_99_wre": 19,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 551,
    "G2 Spring_theta_wre": 0.740716193,
    "G2 Spring_pr_1_99_wre": 35,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 574,
    "G3 Fall_theta_wre": 1.089305755,
    "G3 Fall_pr_1_99_wre": 38,
    "G3 Fall_ss_wre": 92,
    "G3 Fall_dss_wre": 609,
    "G3 Winter_theta_wre": 1.441639958,
    "G3 Winter_pr_1_99_wre": 28,
    "G3 Winter_ss_wre": 92,
    "G3 Winter_dss_wre": 644,
    "G3 Spring_theta_wre": 1.795415026,
    "G3 Spring_pr_1_99_wre": 54,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 680
  },
  {
    "ID": 155,
    "KG Winter_theta_wre": -1.404474702,
    "KG Winter_pr_1_99_wre": 37,
    "KG Winter_ss_wre": 92,
    "KG Winter_dss_wre": 360,
    "KG Spring_theta_wre": -1.060785901,
    "KG Spring_pr_1_99_wre": 37,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 394,
    "G1 Fall_theta_wre": -0.733817077,
    "G1 Fall_pr_1_99_wre": 36,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 427,
    "G1 Winter_theta_wre": -0.441844408,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 456,
    "G1 Spring_theta_wre": -0.092163563,
    "G1 Spring_pr_1_99_wre": 25,
    "G1 Spring_ss_wre": 93,
    "G1 Spring_dss_wre": 491,
    "G2 Fall_theta_wre": 0.163768855,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 92,
    "G2 Fall_dss_wre": 516,
    "G2 Winter_theta_wre": 0.511236156,
    "G2 Winter_pr_1_99_wre": 19,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 551,
    "G2 Spring_theta_wre": 0.741311571,
    "G2 Spring_pr_1_99_wre": 35,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 574,
    "G3 Fall_theta_wre": 1.100134649,
    "G3 Fall_pr_1_99_wre": 39,
    "G3 Fall_ss_wre": 92,
    "G3 Fall_dss_wre": 610,
    "G3 Winter_theta_wre": 1.448152174,
    "G3 Winter_pr_1_99_wre": 28,
    "G3 Winter_ss_wre": 92,
    "G3 Winter_dss_wre": 645,
    "G3 Spring_theta_wre": 1.797539444,
    "G3 Spring_pr_1_99_wre": 54,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 680
  },
  {
    "ID": 156,
    "KG Winter_theta_wre": -1.387796393,
    "KG Winter_pr_1_99_wre": 39,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 361,
    "KG Spring_theta_wre": -1.053142894,
    "KG Spring_pr_1_99_wre": 37,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 395,
    "G1 Fall_theta_wre": -0.727542713,
    "G1 Fall_pr_1_99_wre": 36,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 427,
    "G1 Winter_theta_wre": -0.439210809,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 456,
    "G1 Spring_theta_wre": -0.091587111,
    "G1 Spring_pr_1_99_wre": 25,
    "G1 Spring_ss_wre": 93,
    "G1 Spring_dss_wre": 491,
    "G2 Fall_theta_wre": 0.164672427,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 516,
    "G2 Winter_theta_wre": 0.516055881,
    "G2 Winter_pr_1_99_wre": 19,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 552,
    "G2 Spring_theta_wre": 0.743095419,
    "G2 Spring_pr_1_99_wre": 35,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 574,
    "G3 Fall_theta_wre": 1.106431962,
    "G3 Fall_pr_1_99_wre": 39,
    "G3 Fall_ss_wre": 92,
    "G3 Fall_dss_wre": 611,
    "G3 Winter_theta_wre": 1.456158855,
    "G3 Winter_pr_1_99_wre": 28,
    "G3 Winter_ss_wre": 92,
    "G3 Winter_dss_wre": 646,
    "G3 Spring_theta_wre": 1.804369294,
    "G3 Spring_pr_1_99_wre": 55,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 680
  },
  {
    "ID": 157,
    "KG Winter_theta_wre": -1.377830973,
    "KG Winter_pr_1_99_wre": 39,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 362,
    "KG Spring_theta_wre": -1.047443062,
    "KG Spring_pr_1_99_wre": 37,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 395,
    "G1 Fall_theta_wre": -0.721214376,
    "G1 Fall_pr_1_99_wre": 37,
    "G1 Fall_ss_wre": 92,
    "G1 Fall_dss_wre": 428,
    "G1 Winter_theta_wre": -0.439051987,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 456,
    "G1 Spring_theta_wre": -0.08990485,
    "G1 Spring_pr_1_99_wre": 25,
    "G1 Spring_ss_wre": 93,
    "G1 Spring_dss_wre": 491,
    "G2 Fall_theta_wre": 0.165888259,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 517,
    "G2 Winter_theta_wre": 0.520108849,
    "G2 Winter_pr_1_99_wre": 19,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 552,
    "G2 Spring_theta_wre": 0.747682553,
    "G2 Spring_pr_1_99_wre": 36,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 575,
    "G3 Fall_theta_wre": 1.111384014,
    "G3 Fall_pr_1_99_wre": 39,
    "G3 Fall_ss_wre": 92,
    "G3 Fall_dss_wre": 611,
    "G3 Winter_theta_wre": 1.471763473,
    "G3 Winter_pr_1_99_wre": 30,
    "G3 Winter_ss_wre": 93,
    "G3 Winter_dss_wre": 647,
    "G3 Spring_theta_wre": 1.80774526,
    "G3 Spring_pr_1_99_wre": 55,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 681
  },
  {
    "ID": 158,
    "KG Winter_theta_wre": -1.374052775,
    "KG Winter_pr_1_99_wre": 39,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 363,
    "KG Spring_theta_wre": -1.046148749,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 395,
    "G1 Fall_theta_wre": -0.71774402,
    "G1 Fall_pr_1_99_wre": 37,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 428,
    "G1 Winter_theta_wre": -0.43888832,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 456,
    "G1 Spring_theta_wre": -0.078243448,
    "G1 Spring_pr_1_99_wre": 26,
    "G1 Spring_ss_wre": 93,
    "G1 Spring_dss_wre": 492,
    "G2 Fall_theta_wre": 0.16861045,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 517,
    "G2 Winter_theta_wre": 0.520653585,
    "G2 Winter_pr_1_99_wre": 19,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 552,
    "G2 Spring_theta_wre": 0.752093091,
    "G2 Spring_pr_1_99_wre": 36,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 575,
    "G3 Fall_theta_wre": 1.145866492,
    "G3 Fall_pr_1_99_wre": 39,
    "G3 Fall_ss_wre": 93,
    "G3 Fall_dss_wre": 615,
    "G3 Winter_theta_wre": 1.490276693,
    "G3 Winter_pr_1_99_wre": 32,
    "G3 Winter_ss_wre": 93,
    "G3 Winter_dss_wre": 649,
    "G3 Spring_theta_wre": 1.80824641,
    "G3 Spring_pr_1_99_wre": 55,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 681
  },
  {
    "ID": 159,
    "KG Winter_theta_wre": -1.373938799,
    "KG Winter_pr_1_99_wre": 39,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 363,
    "KG Spring_theta_wre": -1.045789292,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 395,
    "G1 Fall_theta_wre": -0.711663826,
    "G1 Fall_pr_1_99_wre": 37,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 429,
    "G1 Winter_theta_wre": -0.435966369,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 456,
    "G1 Spring_theta_wre": -0.073579871,
    "G1 Spring_pr_1_99_wre": 26,
    "G1 Spring_ss_wre": 93,
    "G1 Spring_dss_wre": 493,
    "G2 Fall_theta_wre": 0.17070669,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 517,
    "G2 Winter_theta_wre": 0.538169811,
    "G2 Winter_pr_1_99_wre": 20,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 554,
    "G2 Spring_theta_wre": 0.755361525,
    "G2 Spring_pr_1_99_wre": 36,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 576,
    "G3 Fall_theta_wre": 1.151576591,
    "G3 Fall_pr_1_99_wre": 39,
    "G3 Fall_ss_wre": 93,
    "G3 Fall_dss_wre": 615,
    "G3 Winter_theta_wre": 1.497733278,
    "G3 Winter_pr_1_99_wre": 32,
    "G3 Winter_ss_wre": 93,
    "G3 Winter_dss_wre": 650,
    "G3 Spring_theta_wre": 1.810991248,
    "G3 Spring_pr_1_99_wre": 55,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 681
  },
  {
    "ID": 160,
    "KG Winter_theta_wre": -1.366041359,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 363,
    "KG Spring_theta_wre": -1.037874802,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 396,
    "G1 Fall_theta_wre": -0.708950295,
    "G1 Fall_pr_1_99_wre": 37,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 429,
    "G1 Winter_theta_wre": -0.435075771,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 456,
    "G1 Spring_theta_wre": -0.068728028,
    "G1 Spring_pr_1_99_wre": 27,
    "G1 Spring_ss_wre": 93,
    "G1 Spring_dss_wre": 493,
    "G2 Fall_theta_wre": 0.174491094,
    "G2 Fall_pr_1_99_wre": 27,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 517,
    "G2 Winter_theta_wre": 0.541602565,
    "G2 Winter_pr_1_99_wre": 20,
    "G2 Winter_ss_wre": 93,
    "G2 Winter_dss_wre": 554,
    "G2 Spring_theta_wre": 0.756090666,
    "G2 Spring_pr_1_99_wre": 36,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 576,
    "G3 Fall_theta_wre": 1.152895254,
    "G3 Fall_pr_1_99_wre": 39,
    "G3 Fall_ss_wre": 93,
    "G3 Fall_dss_wre": 615,
    "G3 Winter_theta_wre": 1.499550704,
    "G3 Winter_pr_1_99_wre": 32,
    "G3 Winter_ss_wre": 93,
    "G3 Winter_dss_wre": 650,
    "G3 Spring_theta_wre": 1.811683484,
    "G3 Spring_pr_1_99_wre": 55,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 681
  },
  {
    "ID": 161,
    "KG Winter_theta_wre": -1.364417911,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 364,
    "KG Spring_theta_wre": -1.018268125,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 398,
    "G1 Fall_theta_wre": -0.708605512,
    "G1 Fall_pr_1_99_wre": 37,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 429,
    "G1 Winter_theta_wre": -0.434528299,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 457,
    "G1 Spring_theta_wre": -0.064277382,
    "G1 Spring_pr_1_99_wre": 27,
    "G1 Spring_ss_wre": 93,
    "G1 Spring_dss_wre": 494,
    "G2 Fall_theta_wre": 0.177213013,
    "G2 Fall_pr_1_99_wre": 28,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 518,
    "G2 Winter_theta_wre": 0.554975656,
    "G2 Winter_pr_1_99_wre": 20,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 555,
    "G2 Spring_theta_wre": 0.757034707,
    "G2 Spring_pr_1_99_wre": 36,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 576,
    "G3 Fall_theta_wre": 1.154504992,
    "G3 Fall_pr_1_99_wre": 39,
    "G3 Fall_ss_wre": 93,
    "G3 Fall_dss_wre": 615,
    "G3 Winter_theta_wre": 1.500623534,
    "G3 Winter_pr_1_99_wre": 32,
    "G3 Winter_ss_wre": 93,
    "G3 Winter_dss_wre": 650,
    "G3 Spring_theta_wre": 1.818943001,
    "G3 Spring_pr_1_99_wre": 55,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 682
  },
  {
    "ID": 162,
    "KG Winter_theta_wre": -1.364415715,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 364,
    "KG Spring_theta_wre": -1.017942104,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 398,
    "G1 Fall_theta_wre": -0.704618126,
    "G1 Fall_pr_1_99_wre": 37,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 430,
    "G1 Winter_theta_wre": -0.4344344,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 457,
    "G1 Spring_theta_wre": -0.055936608,
    "G1 Spring_pr_1_99_wre": 27,
    "G1 Spring_ss_wre": 94,
    "G1 Spring_dss_wre": 494,
    "G2 Fall_theta_wre": 0.190415361,
    "G2 Fall_pr_1_99_wre": 28,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 519,
    "G2 Winter_theta_wre": 0.564023407,
    "G2 Winter_pr_1_99_wre": 20,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 556,
    "G2 Spring_theta_wre": 0.759633645,
    "G2 Spring_pr_1_99_wre": 36,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 576,
    "G3 Fall_theta_wre": 1.157859499,
    "G3 Fall_pr_1_99_wre": 40,
    "G3 Fall_ss_wre": 93,
    "G3 Fall_dss_wre": 616,
    "G3 Winter_theta_wre": 1.505731491,
    "G3 Winter_pr_1_99_wre": 33,
    "G3 Winter_ss_wre": 93,
    "G3 Winter_dss_wre": 651,
    "G3 Spring_theta_wre": 1.827821134,
    "G3 Spring_pr_1_99_wre": 55,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 683
  },
  {
    "ID": 163,
    "KG Winter_theta_wre": -1.359331538,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 364,
    "KG Spring_theta_wre": -1.016723448,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 398,
    "G1 Fall_theta_wre": -0.696655764,
    "G1 Fall_pr_1_99_wre": 38,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 430,
    "G1 Winter_theta_wre": -0.433469917,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 457,
    "G1 Spring_theta_wre": -0.046192439,
    "G1 Spring_pr_1_99_wre": 28,
    "G1 Spring_ss_wre": 94,
    "G1 Spring_dss_wre": 495,
    "G2 Fall_theta_wre": 0.191825205,
    "G2 Fall_pr_1_99_wre": 28,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 519,
    "G2 Winter_theta_wre": 0.567274493,
    "G2 Winter_pr_1_99_wre": 21,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 557,
    "G2 Spring_theta_wre": 0.761716235,
    "G2 Spring_pr_1_99_wre": 37,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 576,
    "G3 Fall_theta_wre": 1.159246506,
    "G3 Fall_pr_1_99_wre": 40,
    "G3 Fall_ss_wre": 93,
    "G3 Fall_dss_wre": 616,
    "G3 Winter_theta_wre": 1.51454101,
    "G3 Winter_pr_1_99_wre": 33,
    "G3 Winter_ss_wre": 93,
    "G3 Winter_dss_wre": 651,
    "G3 Spring_theta_wre": 1.828175855,
    "G3 Spring_pr_1_99_wre": 56,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 683
  },
  {
    "ID": 164,
    "KG Winter_theta_wre": -1.353707577,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 365,
    "KG Spring_theta_wre": -1.013387686,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 399,
    "G1 Fall_theta_wre": -0.67619555,
    "G1 Fall_pr_1_99_wre": 38,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 432,
    "G1 Winter_theta_wre": -0.431938502,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 457,
    "G1 Spring_theta_wre": -0.043133825,
    "G1 Spring_pr_1_99_wre": 28,
    "G1 Spring_ss_wre": 94,
    "G1 Spring_dss_wre": 496,
    "G2 Fall_theta_wre": 0.20616513,
    "G2 Fall_pr_1_99_wre": 28,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 521,
    "G2 Winter_theta_wre": 0.569220976,
    "G2 Winter_pr_1_99_wre": 21,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 557,
    "G2 Spring_theta_wre": 0.765815066,
    "G2 Spring_pr_1_99_wre": 37,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 577,
    "G3 Fall_theta_wre": 1.163494431,
    "G3 Fall_pr_1_99_wre": 40,
    "G3 Fall_ss_wre": 93,
    "G3 Fall_dss_wre": 616,
    "G3 Winter_theta_wre": 1.517777962,
    "G3 Winter_pr_1_99_wre": 33,
    "G3 Winter_ss_wre": 93,
    "G3 Winter_dss_wre": 652,
    "G3 Spring_theta_wre": 1.828705865,
    "G3 Spring_pr_1_99_wre": 56,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 683
  },
  {
    "ID": 165,
    "KG Winter_theta_wre": -1.349558434,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 365,
    "KG Spring_theta_wre": -1.010061124,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 93,
    "KG Spring_dss_wre": 399,
    "G1 Fall_theta_wre": -0.672483194,
    "G1 Fall_pr_1_99_wre": 38,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 433,
    "G1 Winter_theta_wre": -0.429808338,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 457,
    "G1 Spring_theta_wre": -0.028376931,
    "G1 Spring_pr_1_99_wre": 29,
    "G1 Spring_ss_wre": 94,
    "G1 Spring_dss_wre": 497,
    "G2 Fall_theta_wre": 0.214298095,
    "G2 Fall_pr_1_99_wre": 28,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 521,
    "G2 Winter_theta_wre": 0.57247706,
    "G2 Winter_pr_1_99_wre": 21,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 557,
    "G2 Spring_theta_wre": 0.765896389,
    "G2 Spring_pr_1_99_wre": 37,
    "G2 Spring_ss_wre": 92,
    "G2 Spring_dss_wre": 577,
    "G3 Fall_theta_wre": 1.183601378,
    "G3 Fall_pr_1_99_wre": 40,
    "G3 Fall_ss_wre": 94,
    "G3 Fall_dss_wre": 618,
    "G3 Winter_theta_wre": 1.519658536,
    "G3 Winter_pr_1_99_wre": 33,
    "G3 Winter_ss_wre": 93,
    "G3 Winter_dss_wre": 652,
    "G3 Spring_theta_wre": 1.82956392,
    "G3 Spring_pr_1_99_wre": 56,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 683
  },
  {
    "ID": 166,
    "KG Winter_theta_wre": -1.347682651,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 365,
    "KG Spring_theta_wre": -1.005672969,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 94,
    "KG Spring_dss_wre": 399,
    "G1 Fall_theta_wre": -0.671676487,
    "G1 Fall_pr_1_99_wre": 38,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 433,
    "G1 Winter_theta_wre": -0.429538329,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 457,
    "G1 Spring_theta_wre": -0.019486618,
    "G1 Spring_pr_1_99_wre": 30,
    "G1 Spring_ss_wre": 94,
    "G1 Spring_dss_wre": 498,
    "G2 Fall_theta_wre": 0.219525766,
    "G2 Fall_pr_1_99_wre": 28,
    "G2 Fall_ss_wre": 93,
    "G2 Fall_dss_wre": 522,
    "G2 Winter_theta_wre": 0.573866639,
    "G2 Winter_pr_1_99_wre": 21,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 557,
    "G2 Spring_theta_wre": 0.7778644,
    "G2 Spring_pr_1_99_wre": 37,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 578,
    "G3 Fall_theta_wre": 1.194445839,
    "G3 Fall_pr_1_99_wre": 41,
    "G3 Fall_ss_wre": 94,
    "G3 Fall_dss_wre": 619,
    "G3 Winter_theta_wre": 1.525436052,
    "G3 Winter_pr_1_99_wre": 33,
    "G3 Winter_ss_wre": 94,
    "G3 Winter_dss_wre": 653,
    "G3 Spring_theta_wre": 1.83124894,
    "G3 Spring_pr_1_99_wre": 56,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 683
  },
  {
    "ID": 167,
    "KG Winter_theta_wre": -1.347673636,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 365,
    "KG Spring_theta_wre": -1.000759158,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 94,
    "KG Spring_dss_wre": 400,
    "G1 Fall_theta_wre": -0.670817287,
    "G1 Fall_pr_1_99_wre": 38,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 433,
    "G1 Winter_theta_wre": -0.420968777,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 93,
    "G1 Winter_dss_wre": 458,
    "G1 Spring_theta_wre": -0.011619148,
    "G1 Spring_pr_1_99_wre": 30,
    "G1 Spring_ss_wre": 94,
    "G1 Spring_dss_wre": 499,
    "G2 Fall_theta_wre": 0.221878406,
    "G2 Fall_pr_1_99_wre": 29,
    "G2 Fall_ss_wre": 94,
    "G2 Fall_dss_wre": 522,
    "G2 Winter_theta_wre": 0.586452415,
    "G2 Winter_pr_1_99_wre": 21,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 559,
    "G2 Spring_theta_wre": 0.778237502,
    "G2 Spring_pr_1_99_wre": 37,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 578,
    "G3 Fall_theta_wre": 1.19563537,
    "G3 Fall_pr_1_99_wre": 41,
    "G3 Fall_ss_wre": 94,
    "G3 Fall_dss_wre": 620,
    "G3 Winter_theta_wre": 1.52557958,
    "G3 Winter_pr_1_99_wre": 33,
    "G3 Winter_ss_wre": 94,
    "G3 Winter_dss_wre": 653,
    "G3 Spring_theta_wre": 1.840209173,
    "G3 Spring_pr_1_99_wre": 56,
    "G3 Spring_ss_wre": 93,
    "G3 Spring_dss_wre": 684
  },
  {
    "ID": 168,
    "KG Winter_theta_wre": -1.344494675,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 93,
    "KG Winter_dss_wre": 366,
    "KG Spring_theta_wre": -0.992406657,
    "KG Spring_pr_1_99_wre": 38,
    "KG Spring_ss_wre": 94,
    "KG Spring_dss_wre": 401,
    "G1 Fall_theta_wre": -0.663834595,
    "G1 Fall_pr_1_99_wre": 38,
    "G1 Fall_ss_wre": 93,
    "G1 Fall_dss_wre": 434,
    "G1 Winter_theta_wre": -0.420218794,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 458,
    "G1 Spring_theta_wre": -0.010162838,
    "G1 Spring_pr_1_99_wre": 30,
    "G1 Spring_ss_wre": 94,
    "G1 Spring_dss_wre": 499,
    "G2 Fall_theta_wre": 0.222223159,
    "G2 Fall_pr_1_99_wre": 29,
    "G2 Fall_ss_wre": 94,
    "G2 Fall_dss_wre": 522,
    "G2 Winter_theta_wre": 0.587692698,
    "G2 Winter_pr_1_99_wre": 21,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 559,
    "G2 Spring_theta_wre": 0.785601642,
    "G2 Spring_pr_1_99_wre": 38,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 579,
    "G3 Fall_theta_wre": 1.205304536,
    "G3 Fall_pr_1_99_wre": 41,
    "G3 Fall_ss_wre": 94,
    "G3 Fall_dss_wre": 621,
    "G3 Winter_theta_wre": 1.527869152,
    "G3 Winter_pr_1_99_wre": 33,
    "G3 Winter_ss_wre": 94,
    "G3 Winter_dss_wre": 653,
    "G3 Spring_theta_wre": 1.840363518,
    "G3 Spring_pr_1_99_wre": 56,
    "G3 Spring_ss_wre": 94,
    "G3 Spring_dss_wre": 684
  },
  {
    "ID": 169,
    "KG Winter_theta_wre": -1.331593702,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 94,
    "KG Winter_dss_wre": 367,
    "KG Spring_theta_wre": -0.991337425,
    "KG Spring_pr_1_99_wre": 39,
    "KG Spring_ss_wre": 94,
    "KG Spring_dss_wre": 401,
    "G1 Fall_theta_wre": -0.662291048,
    "G1 Fall_pr_1_99_wre": 38,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 434,
    "G1 Winter_theta_wre": -0.408154488,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 459,
    "G1 Spring_theta_wre": -0.004696047,
    "G1 Spring_pr_1_99_wre": 30,
    "G1 Spring_ss_wre": 94,
    "G1 Spring_dss_wre": 500,
    "G2 Fall_theta_wre": 0.233039414,
    "G2 Fall_pr_1_99_wre": 29,
    "G2 Fall_ss_wre": 94,
    "G2 Fall_dss_wre": 523,
    "G2 Winter_theta_wre": 0.589614131,
    "G2 Winter_pr_1_99_wre": 21,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 559,
    "G2 Spring_theta_wre": 0.790389569,
    "G2 Spring_pr_1_99_wre": 38,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 579,
    "G3 Fall_theta_wre": 1.205559365,
    "G3 Fall_pr_1_99_wre": 41,
    "G3 Fall_ss_wre": 94,
    "G3 Fall_dss_wre": 621,
    "G3 Winter_theta_wre": 1.535702791,
    "G3 Winter_pr_1_99_wre": 33,
    "G3 Winter_ss_wre": 94,
    "G3 Winter_dss_wre": 654,
    "G3 Spring_theta_wre": 1.840778047,
    "G3 Spring_pr_1_99_wre": 56,
    "G3 Spring_ss_wre": 94,
    "G3 Spring_dss_wre": 684
  },
  {
    "ID": 170,
    "KG Winter_theta_wre": -1.329591769,
    "KG Winter_pr_1_99_wre": 42,
    "KG Winter_ss_wre": 94,
    "KG Winter_dss_wre": 367,
    "KG Spring_theta_wre": -0.982713018,
    "KG Spring_pr_1_99_wre": 39,
    "KG Spring_ss_wre": 94,
    "KG Spring_dss_wre": 402,
    "G1 Fall_theta_wre": -0.656467763,
    "G1 Fall_pr_1_99_wre": 38,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 434,
    "G1 Winter_theta_wre": -0.407617091,
    "G1 Winter_pr_1_99_wre": 26,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 459,
    "G1 Spring_theta_wre": -0.003220283,
    "G1 Spring_pr_1_99_wre": 30,
    "G1 Spring_ss_wre": 94,
    "G1 Spring_dss_wre": 500,
    "G2 Fall_theta_wre": 0.237500285,
    "G2 Fall_pr_1_99_wre": 29,
    "G2 Fall_ss_wre": 94,
    "G2 Fall_dss_wre": 524,
    "G2 Winter_theta_wre": 0.59150869,
    "G2 Winter_pr_1_99_wre": 21,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 559,
    "G2 Spring_theta_wre": 0.792160839,
    "G2 Spring_pr_1_99_wre": 38,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 579,
    "G3 Fall_theta_wre": 1.207528727,
    "G3 Fall_pr_1_99_wre": 41,
    "G3 Fall_ss_wre": 94,
    "G3 Fall_dss_wre": 621,
    "G3 Winter_theta_wre": 1.553500256,
    "G3 Winter_pr_1_99_wre": 34,
    "G3 Winter_ss_wre": 94,
    "G3 Winter_dss_wre": 655,
    "G3 Spring_theta_wre": 1.852403196,
    "G3 Spring_pr_1_99_wre": 57,
    "G3 Spring_ss_wre": 94,
    "G3 Spring_dss_wre": 685
  },
  {
    "ID": 171,
    "KG Winter_theta_wre": -1.326484848,
    "KG Winter_pr_1_99_wre": 43,
    "KG Winter_ss_wre": 94,
    "KG Winter_dss_wre": 367,
    "KG Spring_theta_wre": -0.973326152,
    "KG Spring_pr_1_99_wre": 40,
    "KG Spring_ss_wre": 94,
    "KG Spring_dss_wre": 403,
    "G1 Fall_theta_wre": -0.650462136,
    "G1 Fall_pr_1_99_wre": 38,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 435,
    "G1 Winter_theta_wre": -0.394737361,
    "G1 Winter_pr_1_99_wre": 27,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 461,
    "G1 Spring_theta_wre": 0.006090809,
    "G1 Spring_pr_1_99_wre": 31,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 501,
    "G2 Fall_theta_wre": 0.258540073,
    "G2 Fall_pr_1_99_wre": 29,
    "G2 Fall_ss_wre": 94,
    "G2 Fall_dss_wre": 526,
    "G2 Winter_theta_wre": 0.59953122,
    "G2 Winter_pr_1_99_wre": 21,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 560,
    "G2 Spring_theta_wre": 0.797534938,
    "G2 Spring_pr_1_99_wre": 38,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 580,
    "G3 Fall_theta_wre": 1.210144349,
    "G3 Fall_pr_1_99_wre": 41,
    "G3 Fall_ss_wre": 94,
    "G3 Fall_dss_wre": 621,
    "G3 Winter_theta_wre": 1.553505543,
    "G3 Winter_pr_1_99_wre": 34,
    "G3 Winter_ss_wre": 94,
    "G3 Winter_dss_wre": 655,
    "G3 Spring_theta_wre": 1.860512841,
    "G3 Spring_pr_1_99_wre": 58,
    "G3 Spring_ss_wre": 94,
    "G3 Spring_dss_wre": 686
  },
  {
    "ID": 172,
    "KG Winter_theta_wre": -1.305410367,
    "KG Winter_pr_1_99_wre": 43,
    "KG Winter_ss_wre": 94,
    "KG Winter_dss_wre": 369,
    "KG Spring_theta_wre": -0.971036041,
    "KG Spring_pr_1_99_wre": 40,
    "KG Spring_ss_wre": 94,
    "KG Spring_dss_wre": 403,
    "G1 Fall_theta_wre": -0.64851852,
    "G1 Fall_pr_1_99_wre": 39,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 435,
    "G1 Winter_theta_wre": -0.385562939,
    "G1 Winter_pr_1_99_wre": 27,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 461,
    "G1 Spring_theta_wre": 0.006286582,
    "G1 Spring_pr_1_99_wre": 31,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 501,
    "G2 Fall_theta_wre": 0.25968384,
    "G2 Fall_pr_1_99_wre": 29,
    "G2 Fall_ss_wre": 94,
    "G2 Fall_dss_wre": 526,
    "G2 Winter_theta_wre": 0.602066139,
    "G2 Winter_pr_1_99_wre": 22,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 560,
    "G2 Spring_theta_wre": 0.797977863,
    "G2 Spring_pr_1_99_wre": 38,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 580,
    "G3 Fall_theta_wre": 1.222229165,
    "G3 Fall_pr_1_99_wre": 42,
    "G3 Fall_ss_wre": 94,
    "G3 Fall_dss_wre": 622,
    "G3 Winter_theta_wre": 1.553600882,
    "G3 Winter_pr_1_99_wre": 34,
    "G3 Winter_ss_wre": 94,
    "G3 Winter_dss_wre": 655,
    "G3 Spring_theta_wre": 1.871706124,
    "G3 Spring_pr_1_99_wre": 58,
    "G3 Spring_ss_wre": 94,
    "G3 Spring_dss_wre": 687
  },
  {
    "ID": 173,
    "KG Winter_theta_wre": -1.293808315,
    "KG Winter_pr_1_99_wre": 43,
    "KG Winter_ss_wre": 94,
    "KG Winter_dss_wre": 371,
    "KG Spring_theta_wre": -0.963760869,
    "KG Spring_pr_1_99_wre": 40,
    "KG Spring_ss_wre": 94,
    "KG Spring_dss_wre": 404,
    "G1 Fall_theta_wre": -0.629517817,
    "G1 Fall_pr_1_99_wre": 39,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 437,
    "G1 Winter_theta_wre": -0.381445631,
    "G1 Winter_pr_1_99_wre": 27,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 462,
    "G1 Spring_theta_wre": 0.016134781,
    "G1 Spring_pr_1_99_wre": 32,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 502,
    "G2 Fall_theta_wre": 0.264275881,
    "G2 Fall_pr_1_99_wre": 29,
    "G2 Fall_ss_wre": 94,
    "G2 Fall_dss_wre": 526,
    "G2 Winter_theta_wre": 0.603255926,
    "G2 Winter_pr_1_99_wre": 22,
    "G2 Winter_ss_wre": 94,
    "G2 Winter_dss_wre": 560,
    "G2 Spring_theta_wre": 0.802566649,
    "G2 Spring_pr_1_99_wre": 38,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 580,
    "G3 Fall_theta_wre": 1.231149304,
    "G3 Fall_pr_1_99_wre": 42,
    "G3 Fall_ss_wre": 95,
    "G3 Fall_dss_wre": 623,
    "G3 Winter_theta_wre": 1.582325052,
    "G3 Winter_pr_1_99_wre": 35,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 658,
    "G3 Spring_theta_wre": 1.875115655,
    "G3 Spring_pr_1_99_wre": 58,
    "G3 Spring_ss_wre": 94,
    "G3 Spring_dss_wre": 688
  },
  {
    "ID": 174,
    "KG Winter_theta_wre": -1.289063964,
    "KG Winter_pr_1_99_wre": 43,
    "KG Winter_ss_wre": 94,
    "KG Winter_dss_wre": 371,
    "KG Spring_theta_wre": -0.956603224,
    "KG Spring_pr_1_99_wre": 41,
    "KG Spring_ss_wre": 94,
    "KG Spring_dss_wre": 404,
    "G1 Fall_theta_wre": -0.627181516,
    "G1 Fall_pr_1_99_wre": 39,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 437,
    "G1 Winter_theta_wre": -0.381372809,
    "G1 Winter_pr_1_99_wre": 27,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 462,
    "G1 Spring_theta_wre": 0.020025032,
    "G1 Spring_pr_1_99_wre": 32,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 502,
    "G2 Fall_theta_wre": 0.264444808,
    "G2 Fall_pr_1_99_wre": 29,
    "G2 Fall_ss_wre": 94,
    "G2 Fall_dss_wre": 526,
    "G2 Winter_theta_wre": 0.612683324,
    "G2 Winter_pr_1_99_wre": 22,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 561,
    "G2 Spring_theta_wre": 0.804128427,
    "G2 Spring_pr_1_99_wre": 38,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 580,
    "G3 Fall_theta_wre": 1.23894133,
    "G3 Fall_pr_1_99_wre": 42,
    "G3 Fall_ss_wre": 95,
    "G3 Fall_dss_wre": 624,
    "G3 Winter_theta_wre": 1.582708076,
    "G3 Winter_pr_1_99_wre": 35,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 658,
    "G3 Spring_theta_wre": 1.886501018,
    "G3 Spring_pr_1_99_wre": 59,
    "G3 Spring_ss_wre": 94,
    "G3 Spring_dss_wre": 689
  },
  {
    "ID": 175,
    "KG Winter_theta_wre": -1.2851548,
    "KG Winter_pr_1_99_wre": 44,
    "KG Winter_ss_wre": 94,
    "KG Winter_dss_wre": 371,
    "KG Spring_theta_wre": -0.954456432,
    "KG Spring_pr_1_99_wre": 41,
    "KG Spring_ss_wre": 94,
    "KG Spring_dss_wre": 405,
    "G1 Fall_theta_wre": -0.624732259,
    "G1 Fall_pr_1_99_wre": 40,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 438,
    "G1 Winter_theta_wre": -0.376537216,
    "G1 Winter_pr_1_99_wre": 27,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 462,
    "G1 Spring_theta_wre": 0.024906588,
    "G1 Spring_pr_1_99_wre": 32,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 502,
    "G2 Fall_theta_wre": 0.269989482,
    "G2 Fall_pr_1_99_wre": 29,
    "G2 Fall_ss_wre": 94,
    "G2 Fall_dss_wre": 527,
    "G2 Winter_theta_wre": 0.61435129,
    "G2 Winter_pr_1_99_wre": 22,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 561,
    "G2 Spring_theta_wre": 0.807522558,
    "G2 Spring_pr_1_99_wre": 38,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 581,
    "G3 Fall_theta_wre": 1.244436853,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 95,
    "G3 Fall_dss_wre": 624,
    "G3 Winter_theta_wre": 1.591438987,
    "G3 Winter_pr_1_99_wre": 35,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 659,
    "G3 Spring_theta_wre": 1.890314655,
    "G3 Spring_pr_1_99_wre": 59,
    "G3 Spring_ss_wre": 94,
    "G3 Spring_dss_wre": 689
  },
  {
    "ID": 176,
    "KG Winter_theta_wre": -1.279897556,
    "KG Winter_pr_1_99_wre": 44,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 372,
    "KG Spring_theta_wre": -0.950204116,
    "KG Spring_pr_1_99_wre": 41,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 405,
    "G1 Fall_theta_wre": -0.621119252,
    "G1 Fall_pr_1_99_wre": 40,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 438,
    "G1 Winter_theta_wre": -0.375685065,
    "G1 Winter_pr_1_99_wre": 27,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 462,
    "G1 Spring_theta_wre": 0.025325701,
    "G1 Spring_pr_1_99_wre": 32,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 503,
    "G2 Fall_theta_wre": 0.271773971,
    "G2 Fall_pr_1_99_wre": 29,
    "G2 Fall_ss_wre": 94,
    "G2 Fall_dss_wre": 527,
    "G2 Winter_theta_wre": 0.615571974,
    "G2 Winter_pr_1_99_wre": 22,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 562,
    "G2 Spring_theta_wre": 0.811504778,
    "G2 Spring_pr_1_99_wre": 38,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 581,
    "G3 Fall_theta_wre": 1.25024117,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 95,
    "G3 Fall_dss_wre": 625,
    "G3 Winter_theta_wre": 1.594973421,
    "G3 Winter_pr_1_99_wre": 35,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 659,
    "G3 Spring_theta_wre": 1.897853934,
    "G3 Spring_pr_1_99_wre": 59,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 690
  },
  {
    "ID": 177,
    "KG Winter_theta_wre": -1.275181226,
    "KG Winter_pr_1_99_wre": 44,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 372,
    "KG Spring_theta_wre": -0.950008654,
    "KG Spring_pr_1_99_wre": 41,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 405,
    "G1 Fall_theta_wre": -0.620644706,
    "G1 Fall_pr_1_99_wre": 40,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 438,
    "G1 Winter_theta_wre": -0.375683275,
    "G1 Winter_pr_1_99_wre": 27,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 462,
    "G1 Spring_theta_wre": 0.025893532,
    "G1 Spring_pr_1_99_wre": 32,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 503,
    "G2 Fall_theta_wre": 0.279498397,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 528,
    "G2 Winter_theta_wre": 0.624337023,
    "G2 Winter_pr_1_99_wre": 22,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 562,
    "G2 Spring_theta_wre": 0.811594817,
    "G2 Spring_pr_1_99_wre": 38,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 581,
    "G3 Fall_theta_wre": 1.250687105,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 95,
    "G3 Fall_dss_wre": 625,
    "G3 Winter_theta_wre": 1.596257721,
    "G3 Winter_pr_1_99_wre": 36,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 660,
    "G3 Spring_theta_wre": 1.90497767,
    "G3 Spring_pr_1_99_wre": 59,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 690
  },
  {
    "ID": 178,
    "KG Winter_theta_wre": -1.27127313,
    "KG Winter_pr_1_99_wre": 44,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 373,
    "KG Spring_theta_wre": -0.949134035,
    "KG Spring_pr_1_99_wre": 41,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 405,
    "G1 Fall_theta_wre": -0.619590049,
    "G1 Fall_pr_1_99_wre": 40,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 438,
    "G1 Winter_theta_wre": -0.374512691,
    "G1 Winter_pr_1_99_wre": 27,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 463,
    "G1 Spring_theta_wre": 0.031221811,
    "G1 Spring_pr_1_99_wre": 32,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 503,
    "G2 Fall_theta_wre": 0.280922366,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 528,
    "G2 Winter_theta_wre": 0.625606823,
    "G2 Winter_pr_1_99_wre": 22,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 563,
    "G2 Spring_theta_wre": 0.813691757,
    "G2 Spring_pr_1_99_wre": 39,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 581,
    "G3 Fall_theta_wre": 1.250691682,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 95,
    "G3 Fall_dss_wre": 625,
    "G3 Winter_theta_wre": 1.598370137,
    "G3 Winter_pr_1_99_wre": 36,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 660,
    "G3 Spring_theta_wre": 1.908662823,
    "G3 Spring_pr_1_99_wre": 59,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 691
  },
  {
    "ID": 179,
    "KG Winter_theta_wre": -1.26909765,
    "KG Winter_pr_1_99_wre": 44,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 373,
    "KG Spring_theta_wre": -0.948166612,
    "KG Spring_pr_1_99_wre": 41,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 405,
    "G1 Fall_theta_wre": -0.61671471,
    "G1 Fall_pr_1_99_wre": 40,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 438,
    "G1 Winter_theta_wre": -0.371845052,
    "G1 Winter_pr_1_99_wre": 28,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 463,
    "G1 Spring_theta_wre": 0.035103111,
    "G1 Spring_pr_1_99_wre": 32,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 504,
    "G2 Fall_theta_wre": 0.287131296,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 529,
    "G2 Winter_theta_wre": 0.626018195,
    "G2 Winter_pr_1_99_wre": 22,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 563,
    "G2 Spring_theta_wre": 0.819046687,
    "G2 Spring_pr_1_99_wre": 39,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 582,
    "G3 Fall_theta_wre": 1.272077011,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 95,
    "G3 Fall_dss_wre": 627,
    "G3 Winter_theta_wre": 1.606260552,
    "G3 Winter_pr_1_99_wre": 36,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 661,
    "G3 Spring_theta_wre": 1.918174177,
    "G3 Spring_pr_1_99_wre": 59,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 692
  },
  {
    "ID": 180,
    "KG Winter_theta_wre": -1.269038047,
    "KG Winter_pr_1_99_wre": 44,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 373,
    "KG Spring_theta_wre": -0.93624251,
    "KG Spring_pr_1_99_wre": 41,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 406,
    "G1 Fall_theta_wre": -0.608957221,
    "G1 Fall_pr_1_99_wre": 41,
    "G1 Fall_ss_wre": 94,
    "G1 Fall_dss_wre": 439,
    "G1 Winter_theta_wre": -0.370900221,
    "G1 Winter_pr_1_99_wre": 28,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 463,
    "G1 Spring_theta_wre": 0.03832494,
    "G1 Spring_pr_1_99_wre": 32,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 504,
    "G2 Fall_theta_wre": 0.28798217,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 529,
    "G2 Winter_theta_wre": 0.628632436,
    "G2 Winter_pr_1_99_wre": 22,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 563,
    "G2 Spring_theta_wre": 0.821082282,
    "G2 Spring_pr_1_99_wre": 39,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 582,
    "G3 Fall_theta_wre": 1.281817947,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 95,
    "G3 Fall_dss_wre": 628,
    "G3 Winter_theta_wre": 1.60841681,
    "G3 Winter_pr_1_99_wre": 36,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 661,
    "G3 Spring_theta_wre": 1.918652742,
    "G3 Spring_pr_1_99_wre": 59,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 692
  },
  {
    "ID": 181,
    "KG Winter_theta_wre": -1.268836647,
    "KG Winter_pr_1_99_wre": 44,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 373,
    "KG Spring_theta_wre": -0.924700823,
    "KG Spring_pr_1_99_wre": 42,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 408,
    "G1 Fall_theta_wre": -0.607549227,
    "G1 Fall_pr_1_99_wre": 41,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 439,
    "G1 Winter_theta_wre": -0.369983607,
    "G1 Winter_pr_1_99_wre": 28,
    "G1 Winter_ss_wre": 94,
    "G1 Winter_dss_wre": 463,
    "G1 Spring_theta_wre": 0.044923826,
    "G1 Spring_pr_1_99_wre": 33,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 504,
    "G2 Fall_theta_wre": 0.288139892,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 529,
    "G2 Winter_theta_wre": 0.634534422,
    "G2 Winter_pr_1_99_wre": 23,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 563,
    "G2 Spring_theta_wre": 0.825359526,
    "G2 Spring_pr_1_99_wre": 39,
    "G2 Spring_ss_wre": 93,
    "G2 Spring_dss_wre": 583,
    "G3 Fall_theta_wre": 1.288590988,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 96,
    "G3 Fall_dss_wre": 629,
    "G3 Winter_theta_wre": 1.608589858,
    "G3 Winter_pr_1_99_wre": 36,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 661,
    "G3 Spring_theta_wre": 1.92634245,
    "G3 Spring_pr_1_99_wre": 59,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 693
  },
  {
    "ID": 182,
    "KG Winter_theta_wre": -1.267345385,
    "KG Winter_pr_1_99_wre": 44,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 373,
    "KG Spring_theta_wre": -0.923417591,
    "KG Spring_pr_1_99_wre": 42,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 408,
    "G1 Fall_theta_wre": -0.607429756,
    "G1 Fall_pr_1_99_wre": 41,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 439,
    "G1 Winter_theta_wre": -0.357943394,
    "G1 Winter_pr_1_99_wre": 28,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 464,
    "G1 Spring_theta_wre": 0.04948669,
    "G1 Spring_pr_1_99_wre": 33,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 505,
    "G2 Fall_theta_wre": 0.288970187,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 529,
    "G2 Winter_theta_wre": 0.635648275,
    "G2 Winter_pr_1_99_wre": 23,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 564,
    "G2 Spring_theta_wre": 0.835113113,
    "G2 Spring_pr_1_99_wre": 40,
    "G2 Spring_ss_wre": 94,
    "G2 Spring_dss_wre": 584,
    "G3 Fall_theta_wre": 1.298109126,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 96,
    "G3 Fall_dss_wre": 630,
    "G3 Winter_theta_wre": 1.620440382,
    "G3 Winter_pr_1_99_wre": 37,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 662,
    "G3 Spring_theta_wre": 1.937075507,
    "G3 Spring_pr_1_99_wre": 60,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 694
  },
  {
    "ID": 183,
    "KG Winter_theta_wre": -1.266292626,
    "KG Winter_pr_1_99_wre": 44,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 373,
    "KG Spring_theta_wre": -0.915988171,
    "KG Spring_pr_1_99_wre": 42,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 408,
    "G1 Fall_theta_wre": -0.602974808,
    "G1 Fall_pr_1_99_wre": 41,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 440,
    "G1 Winter_theta_wre": -0.356580813,
    "G1 Winter_pr_1_99_wre": 28,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 464,
    "G1 Spring_theta_wre": 0.051255523,
    "G1 Spring_pr_1_99_wre": 33,
    "G1 Spring_ss_wre": 95,
    "G1 Spring_dss_wre": 505,
    "G2 Fall_theta_wre": 0.289587803,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 529,
    "G2 Winter_theta_wre": 0.638235285,
    "G2 Winter_pr_1_99_wre": 23,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 564,
    "G2 Spring_theta_wre": 0.846633921,
    "G2 Spring_pr_1_99_wre": 40,
    "G2 Spring_ss_wre": 94,
    "G2 Spring_dss_wre": 585,
    "G3 Fall_theta_wre": 1.298157667,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 96,
    "G3 Fall_dss_wre": 630,
    "G3 Winter_theta_wre": 1.621618554,
    "G3 Winter_pr_1_99_wre": 37,
    "G3 Winter_ss_wre": 95,
    "G3 Winter_dss_wre": 662,
    "G3 Spring_theta_wre": 1.937824739,
    "G3 Spring_pr_1_99_wre": 60,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 694
  },
  {
    "ID": 184,
    "KG Winter_theta_wre": -1.25632847,
    "KG Winter_pr_1_99_wre": 45,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 374,
    "KG Spring_theta_wre": -0.911889404,
    "KG Spring_pr_1_99_wre": 42,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 409,
    "G1 Fall_theta_wre": -0.600917676,
    "G1 Fall_pr_1_99_wre": 41,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 440,
    "G1 Winter_theta_wre": -0.356554877,
    "G1 Winter_pr_1_99_wre": 28,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 464,
    "G1 Spring_theta_wre": 0.069404184,
    "G1 Spring_pr_1_99_wre": 34,
    "G1 Spring_ss_wre": 96,
    "G1 Spring_dss_wre": 507,
    "G2 Fall_theta_wre": 0.290322733,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 529,
    "G2 Winter_theta_wre": 0.642682478,
    "G2 Winter_pr_1_99_wre": 23,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 564,
    "G2 Spring_theta_wre": 0.853300411,
    "G2 Spring_pr_1_99_wre": 40,
    "G2 Spring_ss_wre": 94,
    "G2 Spring_dss_wre": 585,
    "G3 Fall_theta_wre": 1.306666502,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 96,
    "G3 Fall_dss_wre": 631,
    "G3 Winter_theta_wre": 1.630537673,
    "G3 Winter_pr_1_99_wre": 37,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 663,
    "G3 Spring_theta_wre": 1.941169576,
    "G3 Spring_pr_1_99_wre": 60,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 694
  },
  {
    "ID": 185,
    "KG Winter_theta_wre": -1.254677636,
    "KG Winter_pr_1_99_wre": 45,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 375,
    "KG Spring_theta_wre": -0.904666333,
    "KG Spring_pr_1_99_wre": 42,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 410,
    "G1 Fall_theta_wre": -0.600642808,
    "G1 Fall_pr_1_99_wre": 41,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 440,
    "G1 Winter_theta_wre": -0.351522569,
    "G1 Winter_pr_1_99_wre": 28,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 465,
    "G1 Spring_theta_wre": 0.073887964,
    "G1 Spring_pr_1_99_wre": 35,
    "G1 Spring_ss_wre": 96,
    "G1 Spring_dss_wre": 507,
    "G2 Fall_theta_wre": 0.293611806,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 529,
    "G2 Winter_theta_wre": 0.645713849,
    "G2 Winter_pr_1_99_wre": 23,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 565,
    "G2 Spring_theta_wre": 0.857659509,
    "G2 Spring_pr_1_99_wre": 41,
    "G2 Spring_ss_wre": 94,
    "G2 Spring_dss_wre": 586,
    "G3 Fall_theta_wre": 1.307311237,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 96,
    "G3 Fall_dss_wre": 631,
    "G3 Winter_theta_wre": 1.630930838,
    "G3 Winter_pr_1_99_wre": 37,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 663,
    "G3 Spring_theta_wre": 1.941416268,
    "G3 Spring_pr_1_99_wre": 60,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 694
  },
  {
    "ID": 186,
    "KG Winter_theta_wre": -1.231938449,
    "KG Winter_pr_1_99_wre": 46,
    "KG Winter_ss_wre": 95,
    "KG Winter_dss_wre": 377,
    "KG Spring_theta_wre": -0.900306917,
    "KG Spring_pr_1_99_wre": 42,
    "KG Spring_ss_wre": 95,
    "KG Spring_dss_wre": 410,
    "G1 Fall_theta_wre": -0.597371858,
    "G1 Fall_pr_1_99_wre": 41,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 440,
    "G1 Winter_theta_wre": -0.350356468,
    "G1 Winter_pr_1_99_wre": 28,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 465,
    "G1 Spring_theta_wre": 0.074286342,
    "G1 Spring_pr_1_99_wre": 35,
    "G1 Spring_ss_wre": 96,
    "G1 Spring_dss_wre": 507,
    "G2 Fall_theta_wre": 0.296941822,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 530,
    "G2 Winter_theta_wre": 0.656397748,
    "G2 Winter_pr_1_99_wre": 24,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 566,
    "G2 Spring_theta_wre": 0.870355439,
    "G2 Spring_pr_1_99_wre": 41,
    "G2 Spring_ss_wre": 94,
    "G2 Spring_dss_wre": 587,
    "G3 Fall_theta_wre": 1.30907746,
    "G3 Fall_pr_1_99_wre": 43,
    "G3 Fall_ss_wre": 96,
    "G3 Fall_dss_wre": 631,
    "G3 Winter_theta_wre": 1.632532496,
    "G3 Winter_pr_1_99_wre": 37,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 663,
    "G3 Spring_theta_wre": 1.947706899,
    "G3 Spring_pr_1_99_wre": 60,
    "G3 Spring_ss_wre": 95,
    "G3 Spring_dss_wre": 695
  },
  {
    "ID": 187,
    "KG Winter_theta_wre": -1.224952149,
    "KG Winter_pr_1_99_wre": 46,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 378,
    "KG Spring_theta_wre": -0.893087525,
    "KG Spring_pr_1_99_wre": 42,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 411,
    "G1 Fall_theta_wre": -0.591253812,
    "G1 Fall_pr_1_99_wre": 41,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 441,
    "G1 Winter_theta_wre": -0.347406531,
    "G1 Winter_pr_1_99_wre": 28,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 465,
    "G1 Spring_theta_wre": 0.085146869,
    "G1 Spring_pr_1_99_wre": 35,
    "G1 Spring_ss_wre": 96,
    "G1 Spring_dss_wre": 509,
    "G2 Fall_theta_wre": 0.301142103,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 530,
    "G2 Winter_theta_wre": 0.662960906,
    "G2 Winter_pr_1_99_wre": 24,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 566,
    "G2 Spring_theta_wre": 0.871260655,
    "G2 Spring_pr_1_99_wre": 41,
    "G2 Spring_ss_wre": 94,
    "G2 Spring_dss_wre": 587,
    "G3 Fall_theta_wre": 1.326514728,
    "G3 Fall_pr_1_99_wre": 44,
    "G3 Fall_ss_wre": 96,
    "G3 Fall_dss_wre": 633,
    "G3 Winter_theta_wre": 1.637053655,
    "G3 Winter_pr_1_99_wre": 38,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 664,
    "G3 Spring_theta_wre": 1.961292723,
    "G3 Spring_pr_1_99_wre": 61,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 696
  },
  {
    "ID": 188,
    "KG Winter_theta_wre": -1.223626557,
    "KG Winter_pr_1_99_wre": 46,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 378,
    "KG Spring_theta_wre": -0.883649938,
    "KG Spring_pr_1_99_wre": 42,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 412,
    "G1 Fall_theta_wre": -0.588065724,
    "G1 Fall_pr_1_99_wre": 41,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 441,
    "G1 Winter_theta_wre": -0.345944734,
    "G1 Winter_pr_1_99_wre": 28,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 465,
    "G1 Spring_theta_wre": 0.087086031,
    "G1 Spring_pr_1_99_wre": 35,
    "G1 Spring_ss_wre": 96,
    "G1 Spring_dss_wre": 509,
    "G2 Fall_theta_wre": 0.30181572,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 530,
    "G2 Winter_theta_wre": 0.663958332,
    "G2 Winter_pr_1_99_wre": 24,
    "G2 Winter_ss_wre": 95,
    "G2 Winter_dss_wre": 566,
    "G2 Spring_theta_wre": 0.879952767,
    "G2 Spring_pr_1_99_wre": 42,
    "G2 Spring_ss_wre": 94,
    "G2 Spring_dss_wre": 588,
    "G3 Fall_theta_wre": 1.336040437,
    "G3 Fall_pr_1_99_wre": 44,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 634,
    "G3 Winter_theta_wre": 1.637364577,
    "G3 Winter_pr_1_99_wre": 38,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 664,
    "G3 Spring_theta_wre": 1.962615584,
    "G3 Spring_pr_1_99_wre": 61,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 696
  },
  {
    "ID": 189,
    "KG Winter_theta_wre": -1.21883686,
    "KG Winter_pr_1_99_wre": 46,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 378,
    "KG Spring_theta_wre": -0.874502284,
    "KG Spring_pr_1_99_wre": 43,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 413,
    "G1 Fall_theta_wre": -0.579046697,
    "G1 Fall_pr_1_99_wre": 42,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 442,
    "G1 Winter_theta_wre": -0.332799239,
    "G1 Winter_pr_1_99_wre": 29,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 467,
    "G1 Spring_theta_wre": 0.091665008,
    "G1 Spring_pr_1_99_wre": 35,
    "G1 Spring_ss_wre": 96,
    "G1 Spring_dss_wre": 509,
    "G2 Fall_theta_wre": 0.305345949,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 531,
    "G2 Winter_theta_wre": 0.665331781,
    "G2 Winter_pr_1_99_wre": 24,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 567,
    "G2 Spring_theta_wre": 0.882168073,
    "G2 Spring_pr_1_99_wre": 42,
    "G2 Spring_ss_wre": 94,
    "G2 Spring_dss_wre": 588,
    "G3 Fall_theta_wre": 1.337314927,
    "G3 Fall_pr_1_99_wre": 44,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 634,
    "G3 Winter_theta_wre": 1.639831279,
    "G3 Winter_pr_1_99_wre": 38,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 664,
    "G3 Spring_theta_wre": 1.964580392,
    "G3 Spring_pr_1_99_wre": 61,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 696
  },
  {
    "ID": 190,
    "KG Winter_theta_wre": -1.211199296,
    "KG Winter_pr_1_99_wre": 46,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 379,
    "KG Spring_theta_wre": -0.870845406,
    "KG Spring_pr_1_99_wre": 43,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 413,
    "G1 Fall_theta_wre": -0.57713671,
    "G1 Fall_pr_1_99_wre": 42,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 442,
    "G1 Winter_theta_wre": -0.331420406,
    "G1 Winter_pr_1_99_wre": 29,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 467,
    "G1 Spring_theta_wre": 0.092992716,
    "G1 Spring_pr_1_99_wre": 35,
    "G1 Spring_ss_wre": 96,
    "G1 Spring_dss_wre": 509,
    "G2 Fall_theta_wre": 0.307162781,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 531,
    "G2 Winter_theta_wre": 0.669073271,
    "G2 Winter_pr_1_99_wre": 24,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 567,
    "G2 Spring_theta_wre": 0.885993483,
    "G2 Spring_pr_1_99_wre": 42,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 589,
    "G3 Fall_theta_wre": 1.340188774,
    "G3 Fall_pr_1_99_wre": 44,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 634,
    "G3 Winter_theta_wre": 1.644207471,
    "G3 Winter_pr_1_99_wre": 38,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 664,
    "G3 Spring_theta_wre": 1.984480085,
    "G3 Spring_pr_1_99_wre": 62,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 698
  },
  {
    "ID": 191,
    "KG Winter_theta_wre": -1.206851489,
    "KG Winter_pr_1_99_wre": 46,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 379,
    "KG Spring_theta_wre": -0.868467148,
    "KG Spring_pr_1_99_wre": 43,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 413,
    "G1 Fall_theta_wre": -0.574488313,
    "G1 Fall_pr_1_99_wre": 42,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 443,
    "G1 Winter_theta_wre": -0.329439795,
    "G1 Winter_pr_1_99_wre": 29,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 467,
    "G1 Spring_theta_wre": 0.102877706,
    "G1 Spring_pr_1_99_wre": 36,
    "G1 Spring_ss_wre": 96,
    "G1 Spring_dss_wre": 510,
    "G2 Fall_theta_wre": 0.313749669,
    "G2 Fall_pr_1_99_wre": 30,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 531,
    "G2 Winter_theta_wre": 0.670812942,
    "G2 Winter_pr_1_99_wre": 24,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 567,
    "G2 Spring_theta_wre": 0.886133523,
    "G2 Spring_pr_1_99_wre": 42,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 589,
    "G3 Fall_theta_wre": 1.342352292,
    "G3 Fall_pr_1_99_wre": 44,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 634,
    "G3 Winter_theta_wre": 1.645981698,
    "G3 Winter_pr_1_99_wre": 38,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 665,
    "G3 Spring_theta_wre": 1.985050208,
    "G3 Spring_pr_1_99_wre": 62,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 699
  },
  {
    "ID": 192,
    "KG Winter_theta_wre": -1.196867207,
    "KG Winter_pr_1_99_wre": 47,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 380,
    "KG Spring_theta_wre": -0.863891933,
    "KG Spring_pr_1_99_wre": 43,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 414,
    "G1 Fall_theta_wre": -0.574135078,
    "G1 Fall_pr_1_99_wre": 42,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 443,
    "G1 Winter_theta_wre": -0.327048599,
    "G1 Winter_pr_1_99_wre": 29,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 467,
    "G1 Spring_theta_wre": 0.104155879,
    "G1 Spring_pr_1_99_wre": 36,
    "G1 Spring_ss_wre": 96,
    "G1 Spring_dss_wre": 510,
    "G2 Fall_theta_wre": 0.317837429,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 532,
    "G2 Winter_theta_wre": 0.677719454,
    "G2 Winter_pr_1_99_wre": 25,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 568,
    "G2 Spring_theta_wre": 0.888256849,
    "G2 Spring_pr_1_99_wre": 42,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 589,
    "G3 Fall_theta_wre": 1.348235267,
    "G3 Fall_pr_1_99_wre": 44,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 635,
    "G3 Winter_theta_wre": 1.650600638,
    "G3 Winter_pr_1_99_wre": 38,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 665,
    "G3 Spring_theta_wre": 1.987579175,
    "G3 Spring_pr_1_99_wre": 62,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 699
  },
  {
    "ID": 193,
    "KG Winter_theta_wre": -1.195301767,
    "KG Winter_pr_1_99_wre": 47,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 380,
    "KG Spring_theta_wre": -0.863661269,
    "KG Spring_pr_1_99_wre": 43,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 414,
    "G1 Fall_theta_wre": -0.573846454,
    "G1 Fall_pr_1_99_wre": 42,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 443,
    "G1 Winter_theta_wre": -0.326856752,
    "G1 Winter_pr_1_99_wre": 29,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 467,
    "G1 Spring_theta_wre": 0.113221758,
    "G1 Spring_pr_1_99_wre": 36,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 511,
    "G2 Fall_theta_wre": 0.321267266,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 532,
    "G2 Winter_theta_wre": 0.681478272,
    "G2 Winter_pr_1_99_wre": 25,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 568,
    "G2 Spring_theta_wre": 0.895855994,
    "G2 Spring_pr_1_99_wre": 42,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 590,
    "G3 Fall_theta_wre": 1.351251599,
    "G3 Fall_pr_1_99_wre": 44,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 635,
    "G3 Winter_theta_wre": 1.659168305,
    "G3 Winter_pr_1_99_wre": 38,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 666,
    "G3 Spring_theta_wre": 1.988033983,
    "G3 Spring_pr_1_99_wre": 62,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 699
  },
  {
    "ID": 194,
    "KG Winter_theta_wre": -1.194933393,
    "KG Winter_pr_1_99_wre": 47,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 381,
    "KG Spring_theta_wre": -0.861633153,
    "KG Spring_pr_1_99_wre": 43,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 414,
    "G1 Fall_theta_wre": -0.572646992,
    "G1 Fall_pr_1_99_wre": 42,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 443,
    "G1 Winter_theta_wre": -0.326377018,
    "G1 Winter_pr_1_99_wre": 29,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 467,
    "G1 Spring_theta_wre": 0.118078285,
    "G1 Spring_pr_1_99_wre": 36,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 512,
    "G2 Fall_theta_wre": 0.322394105,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 532,
    "G2 Winter_theta_wre": 0.683398937,
    "G2 Winter_pr_1_99_wre": 25,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 568,
    "G2 Spring_theta_wre": 0.900711975,
    "G2 Spring_pr_1_99_wre": 42,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 590,
    "G3 Fall_theta_wre": 1.357447458,
    "G3 Fall_pr_1_99_wre": 45,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 636,
    "G3 Winter_theta_wre": 1.660907765,
    "G3 Winter_pr_1_99_wre": 38,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 666,
    "G3 Spring_theta_wre": 1.989079633,
    "G3 Spring_pr_1_99_wre": 62,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 699
  },
  {
    "ID": 195,
    "KG Winter_theta_wre": -1.194435099,
    "KG Winter_pr_1_99_wre": 47,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 381,
    "KG Spring_theta_wre": -0.860388565,
    "KG Spring_pr_1_99_wre": 44,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 414,
    "G1 Fall_theta_wre": -0.572336269,
    "G1 Fall_pr_1_99_wre": 42,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 443,
    "G1 Winter_theta_wre": -0.326125294,
    "G1 Winter_pr_1_99_wre": 29,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 467,
    "G1 Spring_theta_wre": 0.121793244,
    "G1 Spring_pr_1_99_wre": 36,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 512,
    "G2 Fall_theta_wre": 0.324071044,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 95,
    "G2 Fall_dss_wre": 532,
    "G2 Winter_theta_wre": 0.69335603,
    "G2 Winter_pr_1_99_wre": 25,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 569,
    "G2 Spring_theta_wre": 0.900772837,
    "G2 Spring_pr_1_99_wre": 42,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 590,
    "G3 Fall_theta_wre": 1.358036026,
    "G3 Fall_pr_1_99_wre": 45,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 636,
    "G3 Winter_theta_wre": 1.663431673,
    "G3 Winter_pr_1_99_wre": 38,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 666,
    "G3 Spring_theta_wre": 1.992788893,
    "G3 Spring_pr_1_99_wre": 62,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 699
  },
  {
    "ID": 196,
    "KG Winter_theta_wre": -1.18017759,
    "KG Winter_pr_1_99_wre": 49,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 382,
    "KG Spring_theta_wre": -0.860327732,
    "KG Spring_pr_1_99_wre": 44,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 414,
    "G1 Fall_theta_wre": -0.569606875,
    "G1 Fall_pr_1_99_wre": 42,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 443,
    "G1 Winter_theta_wre": -0.320374841,
    "G1 Winter_pr_1_99_wre": 29,
    "G1 Winter_ss_wre": 95,
    "G1 Winter_dss_wre": 468,
    "G1 Spring_theta_wre": 0.122291166,
    "G1 Spring_pr_1_99_wre": 36,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 512,
    "G2 Fall_theta_wre": 0.332151346,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 533,
    "G2 Winter_theta_wre": 0.694692302,
    "G2 Winter_pr_1_99_wre": 25,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 569,
    "G2 Spring_theta_wre": 0.905755367,
    "G2 Spring_pr_1_99_wre": 42,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 591,
    "G3 Fall_theta_wre": 1.361805863,
    "G3 Fall_pr_1_99_wre": 45,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 636,
    "G3 Winter_theta_wre": 1.6724786,
    "G3 Winter_pr_1_99_wre": 38,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 667,
    "G3 Spring_theta_wre": 1.995491252,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 700
  },
  {
    "ID": 197,
    "KG Winter_theta_wre": -1.173457328,
    "KG Winter_pr_1_99_wre": 50,
    "KG Winter_ss_wre": 96,
    "KG Winter_dss_wre": 383,
    "KG Spring_theta_wre": -0.859916904,
    "KG Spring_pr_1_99_wre": 44,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 414,
    "G1 Fall_theta_wre": -0.569262551,
    "G1 Fall_pr_1_99_wre": 42,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 443,
    "G1 Winter_theta_wre": -0.313263828,
    "G1 Winter_pr_1_99_wre": 29,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 469,
    "G1 Spring_theta_wre": 0.125202992,
    "G1 Spring_pr_1_99_wre": 36,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 513,
    "G2 Fall_theta_wre": 0.332280324,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 533,
    "G2 Winter_theta_wre": 0.697612006,
    "G2 Winter_pr_1_99_wre": 26,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 570,
    "G2 Spring_theta_wre": 0.916716064,
    "G2 Spring_pr_1_99_wre": 43,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 592,
    "G3 Fall_theta_wre": 1.364121682,
    "G3 Fall_pr_1_99_wre": 45,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 636,
    "G3 Winter_theta_wre": 1.674295392,
    "G3 Winter_pr_1_99_wre": 39,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 667,
    "G3 Spring_theta_wre": 1.998856412,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 700
  },
  {
    "ID": 198,
    "KG Winter_theta_wre": -1.166590392,
    "KG Winter_pr_1_99_wre": 50,
    "KG Winter_ss_wre": 97,
    "KG Winter_dss_wre": 383,
    "KG Spring_theta_wre": -0.858775698,
    "KG Spring_pr_1_99_wre": 44,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 414,
    "G1 Fall_theta_wre": -0.554131847,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 95,
    "G1 Fall_dss_wre": 445,
    "G1 Winter_theta_wre": -0.310983496,
    "G1 Winter_pr_1_99_wre": 29,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 469,
    "G1 Spring_theta_wre": 0.1286138,
    "G1 Spring_pr_1_99_wre": 36,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 513,
    "G2 Fall_theta_wre": 0.333687861,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 533,
    "G2 Winter_theta_wre": 0.700247897,
    "G2 Winter_pr_1_99_wre": 26,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 570,
    "G2 Spring_theta_wre": 0.91696853,
    "G2 Spring_pr_1_99_wre": 43,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 592,
    "G3 Fall_theta_wre": 1.365242587,
    "G3 Fall_pr_1_99_wre": 46,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 637,
    "G3 Winter_theta_wre": 1.674629879,
    "G3 Winter_pr_1_99_wre": 39,
    "G3 Winter_ss_wre": 96,
    "G3 Winter_dss_wre": 667,
    "G3 Spring_theta_wre": 2.004170972,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 700
  },
  {
    "ID": 199,
    "KG Winter_theta_wre": -1.158987984,
    "KG Winter_pr_1_99_wre": 50,
    "KG Winter_ss_wre": 97,
    "KG Winter_dss_wre": 384,
    "KG Spring_theta_wre": -0.851369257,
    "KG Spring_pr_1_99_wre": 44,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 415,
    "G1 Fall_theta_wre": -0.547387583,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 445,
    "G1 Winter_theta_wre": -0.305667794,
    "G1 Winter_pr_1_99_wre": 30,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 469,
    "G1 Spring_theta_wre": 0.131501704,
    "G1 Spring_pr_1_99_wre": 36,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 513,
    "G2 Fall_theta_wre": 0.335903894,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 534,
    "G2 Winter_theta_wre": 0.700616047,
    "G2 Winter_pr_1_99_wre": 26,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 570,
    "G2 Spring_theta_wre": 0.918980916,
    "G2 Spring_pr_1_99_wre": 43,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 592,
    "G3 Fall_theta_wre": 1.366129977,
    "G3 Fall_pr_1_99_wre": 46,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 637,
    "G3 Winter_theta_wre": 1.687211844,
    "G3 Winter_pr_1_99_wre": 40,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 669,
    "G3 Spring_theta_wre": 2.008155681,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 96,
    "G3 Spring_dss_wre": 701
  },
  {
    "ID": 200,
    "KG Winter_theta_wre": -1.155019082,
    "KG Winter_pr_1_99_wre": 50,
    "KG Winter_ss_wre": 97,
    "KG Winter_dss_wre": 384,
    "KG Spring_theta_wre": -0.84447389,
    "KG Spring_pr_1_99_wre": 45,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 416,
    "G1 Fall_theta_wre": -0.546587988,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 445,
    "G1 Winter_theta_wre": -0.301365409,
    "G1 Winter_pr_1_99_wre": 30,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 470,
    "G1 Spring_theta_wre": 0.13262396,
    "G1 Spring_pr_1_99_wre": 36,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 513,
    "G2 Fall_theta_wre": 0.337429596,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 534,
    "G2 Winter_theta_wre": 0.704793698,
    "G2 Winter_pr_1_99_wre": 26,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 570,
    "G2 Spring_theta_wre": 0.928252015,
    "G2 Spring_pr_1_99_wre": 43,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 593,
    "G3 Fall_theta_wre": 1.378062633,
    "G3 Fall_pr_1_99_wre": 46,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 638,
    "G3 Winter_theta_wre": 1.688576843,
    "G3 Winter_pr_1_99_wre": 40,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 669,
    "G3 Spring_theta_wre": 2.009263489,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 701
  },
  {
    "ID": 201,
    "KG Winter_theta_wre": -1.143445366,
    "KG Winter_pr_1_99_wre": 50,
    "KG Winter_ss_wre": 97,
    "KG Winter_dss_wre": 386,
    "KG Spring_theta_wre": -0.841604738,
    "KG Spring_pr_1_99_wre": 45,
    "KG Spring_ss_wre": 96,
    "KG Spring_dss_wre": 416,
    "G1 Fall_theta_wre": -0.538817504,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 446,
    "G1 Winter_theta_wre": -0.292932816,
    "G1 Winter_pr_1_99_wre": 30,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 471,
    "G1 Spring_theta_wre": 0.134878846,
    "G1 Spring_pr_1_99_wre": 37,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 513,
    "G2 Fall_theta_wre": 0.341867183,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 534,
    "G2 Winter_theta_wre": 0.704895817,
    "G2 Winter_pr_1_99_wre": 26,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 570,
    "G2 Spring_theta_wre": 0.931121644,
    "G2 Spring_pr_1_99_wre": 44,
    "G2 Spring_ss_wre": 95,
    "G2 Spring_dss_wre": 593,
    "G3 Fall_theta_wre": 1.378349567,
    "G3 Fall_pr_1_99_wre": 46,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 638,
    "G3 Winter_theta_wre": 1.691444919,
    "G3 Winter_pr_1_99_wre": 40,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 669,
    "G3 Spring_theta_wre": 2.017992386,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 702
  },
  {
    "ID": 202,
    "KG Winter_theta_wre": -1.122364318,
    "KG Winter_pr_1_99_wre": 51,
    "KG Winter_ss_wre": 97,
    "KG Winter_dss_wre": 388,
    "KG Spring_theta_wre": -0.835373051,
    "KG Spring_pr_1_99_wre": 45,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 416,
    "G1 Fall_theta_wre": -0.538385824,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 446,
    "G1 Winter_theta_wre": -0.292680136,
    "G1 Winter_pr_1_99_wre": 30,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 471,
    "G1 Spring_theta_wre": 0.142020368,
    "G1 Spring_pr_1_99_wre": 37,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 514,
    "G2 Fall_theta_wre": 0.34243976,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 534,
    "G2 Winter_theta_wre": 0.710963036,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 571,
    "G2 Spring_theta_wre": 0.939359967,
    "G2 Spring_pr_1_99_wre": 44,
    "G2 Spring_ss_wre": 96,
    "G2 Spring_dss_wre": 594,
    "G3 Fall_theta_wre": 1.378478507,
    "G3 Fall_pr_1_99_wre": 46,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 638,
    "G3 Winter_theta_wre": 1.693445653,
    "G3 Winter_pr_1_99_wre": 40,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 669,
    "G3 Spring_theta_wre": 2.019408418,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 702
  },
  {
    "ID": 203,
    "KG Winter_theta_wre": -1.119741347,
    "KG Winter_pr_1_99_wre": 51,
    "KG Winter_ss_wre": 97,
    "KG Winter_dss_wre": 388,
    "KG Spring_theta_wre": -0.832206136,
    "KG Spring_pr_1_99_wre": 46,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 417,
    "G1 Fall_theta_wre": -0.53704432,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 446,
    "G1 Winter_theta_wre": -0.290332214,
    "G1 Winter_pr_1_99_wre": 30,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 471,
    "G1 Spring_theta_wre": 0.149573396,
    "G1 Spring_pr_1_99_wre": 37,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 515,
    "G2 Fall_theta_wre": 0.344611508,
    "G2 Fall_pr_1_99_wre": 31,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 534,
    "G2 Winter_theta_wre": 0.712686597,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 96,
    "G2 Winter_dss_wre": 571,
    "G2 Spring_theta_wre": 0.939902717,
    "G2 Spring_pr_1_99_wre": 44,
    "G2 Spring_ss_wre": 96,
    "G2 Spring_dss_wre": 594,
    "G3 Fall_theta_wre": 1.37910085,
    "G3 Fall_pr_1_99_wre": 46,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 638,
    "G3 Winter_theta_wre": 1.695923578,
    "G3 Winter_pr_1_99_wre": 40,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 670,
    "G3 Spring_theta_wre": 2.023815129,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 702
  },
  {
    "ID": 204,
    "KG Winter_theta_wre": -1.11858457,
    "KG Winter_pr_1_99_wre": 52,
    "KG Winter_ss_wre": 97,
    "KG Winter_dss_wre": 388,
    "KG Spring_theta_wre": -0.830048124,
    "KG Spring_pr_1_99_wre": 46,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 417,
    "G1 Fall_theta_wre": -0.535463465,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 446,
    "G1 Winter_theta_wre": -0.287612221,
    "G1 Winter_pr_1_99_wre": 31,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 471,
    "G1 Spring_theta_wre": 0.149927039,
    "G1 Spring_pr_1_99_wre": 37,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 515,
    "G2 Fall_theta_wre": 0.345977929,
    "G2 Fall_pr_1_99_wre": 32,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 535,
    "G2 Winter_theta_wre": 0.721273662,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 572,
    "G2 Spring_theta_wre": 0.941078465,
    "G2 Spring_pr_1_99_wre": 44,
    "G2 Spring_ss_wre": 96,
    "G2 Spring_dss_wre": 594,
    "G3 Fall_theta_wre": 1.386976656,
    "G3 Fall_pr_1_99_wre": 46,
    "G3 Fall_ss_wre": 97,
    "G3 Fall_dss_wre": 639,
    "G3 Winter_theta_wre": 1.696748524,
    "G3 Winter_pr_1_99_wre": 40,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 670,
    "G3 Spring_theta_wre": 2.023970906,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 702
  },
  {
    "ID": 205,
    "KG Winter_theta_wre": -1.117480789,
    "KG Winter_pr_1_99_wre": 52,
    "KG Winter_ss_wre": 97,
    "KG Winter_dss_wre": 388,
    "KG Spring_theta_wre": -0.829951139,
    "KG Spring_pr_1_99_wre": 46,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 417,
    "G1 Fall_theta_wre": -0.5343135,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 447,
    "G1 Winter_theta_wre": -0.285188652,
    "G1 Winter_pr_1_99_wre": 31,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 471,
    "G1 Spring_theta_wre": 0.150453281,
    "G1 Spring_pr_1_99_wre": 37,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 515,
    "G2 Fall_theta_wre": 0.350639978,
    "G2 Fall_pr_1_99_wre": 32,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 535,
    "G2 Winter_theta_wre": 0.724316386,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 572,
    "G2 Spring_theta_wre": 0.953228681,
    "G2 Spring_pr_1_99_wre": 45,
    "G2 Spring_ss_wre": 96,
    "G2 Spring_dss_wre": 595,
    "G3 Fall_theta_wre": 1.390010769,
    "G3 Fall_pr_1_99_wre": 46,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 639,
    "G3 Winter_theta_wre": 1.70332558,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 670,
    "G3 Spring_theta_wre": 2.025904092,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 703
  },
  {
    "ID": 206,
    "KG Winter_theta_wre": -1.111003254,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 389,
    "KG Spring_theta_wre": -0.821548947,
    "KG Spring_pr_1_99_wre": 46,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 418,
    "G1 Fall_theta_wre": -0.532295347,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 447,
    "G1 Winter_theta_wre": -0.283232523,
    "G1 Winter_pr_1_99_wre": 31,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 472,
    "G1 Spring_theta_wre": 0.155590003,
    "G1 Spring_pr_1_99_wre": 38,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 516,
    "G2 Fall_theta_wre": 0.35097271,
    "G2 Fall_pr_1_99_wre": 32,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 535,
    "G2 Winter_theta_wre": 0.725854982,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 573,
    "G2 Spring_theta_wre": 0.955953983,
    "G2 Spring_pr_1_99_wre": 45,
    "G2 Spring_ss_wre": 96,
    "G2 Spring_dss_wre": 596,
    "G3 Fall_theta_wre": 1.394218236,
    "G3 Fall_pr_1_99_wre": 46,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 639,
    "G3 Winter_theta_wre": 1.712028293,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 671,
    "G3 Spring_theta_wre": 2.028223167,
    "G3 Spring_pr_1_99_wre": 63,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 703
  },
  {
    "ID": 207,
    "KG Winter_theta_wre": -1.110900549,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 389,
    "KG Spring_theta_wre": -0.816977545,
    "KG Spring_pr_1_99_wre": 46,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 418,
    "G1 Fall_theta_wre": -0.52550614,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 447,
    "G1 Winter_theta_wre": -0.282042211,
    "G1 Winter_pr_1_99_wre": 31,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 472,
    "G1 Spring_theta_wre": 0.157801508,
    "G1 Spring_pr_1_99_wre": 38,
    "G1 Spring_ss_wre": 97,
    "G1 Spring_dss_wre": 516,
    "G2 Fall_theta_wre": 0.352993275,
    "G2 Fall_pr_1_99_wre": 32,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 535,
    "G2 Winter_theta_wre": 0.72628938,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 573,
    "G2 Spring_theta_wre": 0.964248824,
    "G2 Spring_pr_1_99_wre": 45,
    "G2 Spring_ss_wre": 96,
    "G2 Spring_dss_wre": 596,
    "G3 Fall_theta_wre": 1.396491884,
    "G3 Fall_pr_1_99_wre": 47,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 640,
    "G3 Winter_theta_wre": 1.717879802,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 672,
    "G3 Spring_theta_wre": 2.031804786,
    "G3 Spring_pr_1_99_wre": 64,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 703
  },
  {
    "ID": 208,
    "KG Winter_theta_wre": -1.110767198,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 389,
    "KG Spring_theta_wre": -0.816830441,
    "KG Spring_pr_1_99_wre": 46,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 418,
    "G1 Fall_theta_wre": -0.520303749,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 448,
    "G1 Winter_theta_wre": -0.272782198,
    "G1 Winter_pr_1_99_wre": 31,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 473,
    "G1 Spring_theta_wre": 0.162072967,
    "G1 Spring_pr_1_99_wre": 38,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 516,
    "G2 Fall_theta_wre": 0.362918577,
    "G2 Fall_pr_1_99_wre": 33,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 536,
    "G2 Winter_theta_wre": 0.729207323,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 573,
    "G2 Spring_theta_wre": 0.964253954,
    "G2 Spring_pr_1_99_wre": 45,
    "G2 Spring_ss_wre": 96,
    "G2 Spring_dss_wre": 596,
    "G3 Fall_theta_wre": 1.402192858,
    "G3 Fall_pr_1_99_wre": 47,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 640,
    "G3 Winter_theta_wre": 1.717984254,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 672,
    "G3 Spring_theta_wre": 2.036321569,
    "G3 Spring_pr_1_99_wre": 64,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 704
  },
  {
    "ID": 209,
    "KG Winter_theta_wre": -1.105622011,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 389,
    "KG Spring_theta_wre": -0.808861202,
    "KG Spring_pr_1_99_wre": 46,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 419,
    "G1 Fall_theta_wre": -0.50774271,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 449,
    "G1 Winter_theta_wre": -0.268657104,
    "G1 Winter_pr_1_99_wre": 31,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 473,
    "G1 Spring_theta_wre": 0.162720051,
    "G1 Spring_pr_1_99_wre": 39,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 516,
    "G2 Fall_theta_wre": 0.36491467,
    "G2 Fall_pr_1_99_wre": 33,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 536,
    "G2 Winter_theta_wre": 0.731488319,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 573,
    "G2 Spring_theta_wre": 0.99240759,
    "G2 Spring_pr_1_99_wre": 46,
    "G2 Spring_ss_wre": 96,
    "G2 Spring_dss_wre": 599,
    "G3 Fall_theta_wre": 1.402425261,
    "G3 Fall_pr_1_99_wre": 48,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 640,
    "G3 Winter_theta_wre": 1.71853489,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 672,
    "G3 Spring_theta_wre": 2.037225367,
    "G3 Spring_pr_1_99_wre": 64,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 704
  },
  {
    "ID": 210,
    "KG Winter_theta_wre": -1.101448046,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 390,
    "KG Spring_theta_wre": -0.808710004,
    "KG Spring_pr_1_99_wre": 46,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 419,
    "G1 Fall_theta_wre": -0.500282045,
    "G1 Fall_pr_1_99_wre": 43,
    "G1 Fall_ss_wre": 96,
    "G1 Fall_dss_wre": 450,
    "G1 Winter_theta_wre": -0.267915285,
    "G1 Winter_pr_1_99_wre": 31,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 473,
    "G1 Spring_theta_wre": 0.167069997,
    "G1 Spring_pr_1_99_wre": 39,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 517,
    "G2 Fall_theta_wre": 0.373615546,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 537,
    "G2 Winter_theta_wre": 0.733624005,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 573,
    "G2 Spring_theta_wre": 1.006635983,
    "G2 Spring_pr_1_99_wre": 47,
    "G2 Spring_ss_wre": 97,
    "G2 Spring_dss_wre": 601,
    "G3 Fall_theta_wre": 1.415771431,
    "G3 Fall_pr_1_99_wre": 48,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 642,
    "G3 Winter_theta_wre": 1.728398054,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 673,
    "G3 Spring_theta_wre": 2.043241964,
    "G3 Spring_pr_1_99_wre": 64,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 704
  },
  {
    "ID": 211,
    "KG Winter_theta_wre": -1.101267396,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 390,
    "KG Spring_theta_wre": -0.807139657,
    "KG Spring_pr_1_99_wre": 46,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 419,
    "G1 Fall_theta_wre": -0.491431432,
    "G1 Fall_pr_1_99_wre": 44,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 451,
    "G1 Winter_theta_wre": -0.26594329,
    "G1 Winter_pr_1_99_wre": 31,
    "G1 Winter_ss_wre": 96,
    "G1 Winter_dss_wre": 473,
    "G1 Spring_theta_wre": 0.168648912,
    "G1 Spring_pr_1_99_wre": 39,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 517,
    "G2 Fall_theta_wre": 0.3804925,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 96,
    "G2 Fall_dss_wre": 538,
    "G2 Winter_theta_wre": 0.734864144,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 573,
    "G2 Spring_theta_wre": 1.008222726,
    "G2 Spring_pr_1_99_wre": 47,
    "G2 Spring_ss_wre": 97,
    "G2 Spring_dss_wre": 601,
    "G3 Fall_theta_wre": 1.415841575,
    "G3 Fall_pr_1_99_wre": 48,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 642,
    "G3 Winter_theta_wre": 1.734343491,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 97,
    "G3 Winter_dss_wre": 673,
    "G3 Spring_theta_wre": 2.052076415,
    "G3 Spring_pr_1_99_wre": 64,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 705
  },
  {
    "ID": 212,
    "KG Winter_theta_wre": -1.095694947,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 390,
    "KG Spring_theta_wre": -0.806541929,
    "KG Spring_pr_1_99_wre": 46,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 419,
    "G1 Fall_theta_wre": -0.480271878,
    "G1 Fall_pr_1_99_wre": 44,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 452,
    "G1 Winter_theta_wre": -0.255316962,
    "G1 Winter_pr_1_99_wre": 32,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 474,
    "G1 Spring_theta_wre": 0.174048179,
    "G1 Spring_pr_1_99_wre": 39,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 517,
    "G2 Fall_theta_wre": 0.390207975,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 539,
    "G2 Winter_theta_wre": 0.742949015,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 574,
    "G2 Spring_theta_wre": 1.00853697,
    "G2 Spring_pr_1_99_wre": 47,
    "G2 Spring_ss_wre": 97,
    "G2 Spring_dss_wre": 601,
    "G3 Fall_theta_wre": 1.420054049,
    "G3 Fall_pr_1_99_wre": 48,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 642,
    "G3 Winter_theta_wre": 1.744502224,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 674,
    "G3 Spring_theta_wre": 2.052215079,
    "G3 Spring_pr_1_99_wre": 64,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 705
  },
  {
    "ID": 213,
    "KG Winter_theta_wre": -1.094726124,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 391,
    "KG Spring_theta_wre": -0.803573404,
    "KG Spring_pr_1_99_wre": 47,
    "KG Spring_ss_wre": 97,
    "KG Spring_dss_wre": 420,
    "G1 Fall_theta_wre": -0.478213848,
    "G1 Fall_pr_1_99_wre": 45,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 452,
    "G1 Winter_theta_wre": -0.245490436,
    "G1 Winter_pr_1_99_wre": 32,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 475,
    "G1 Spring_theta_wre": 0.178643396,
    "G1 Spring_pr_1_99_wre": 39,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 518,
    "G2 Fall_theta_wre": 0.39026477,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 539,
    "G2 Winter_theta_wre": 0.743033529,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 574,
    "G2 Spring_theta_wre": 1.018039443,
    "G2 Spring_pr_1_99_wre": 47,
    "G2 Spring_ss_wre": 97,
    "G2 Spring_dss_wre": 602,
    "G3 Fall_theta_wre": 1.421177644,
    "G3 Fall_pr_1_99_wre": 49,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 642,
    "G3 Winter_theta_wre": 1.748952864,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 675,
    "G3 Spring_theta_wre": 2.054515586,
    "G3 Spring_pr_1_99_wre": 64,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 705
  },
  {
    "ID": 214,
    "KG Winter_theta_wre": -1.093918336,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 391,
    "KG Spring_theta_wre": -0.777274942,
    "KG Spring_pr_1_99_wre": 48,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 422,
    "G1 Fall_theta_wre": -0.474608268,
    "G1 Fall_pr_1_99_wre": 45,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 453,
    "G1 Winter_theta_wre": -0.244001311,
    "G1 Winter_pr_1_99_wre": 32,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 476,
    "G1 Spring_theta_wre": 0.181284928,
    "G1 Spring_pr_1_99_wre": 39,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 518,
    "G2 Fall_theta_wre": 0.398576321,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 540,
    "G2 Winter_theta_wre": 0.747144049,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 575,
    "G2 Spring_theta_wre": 1.019117962,
    "G2 Spring_pr_1_99_wre": 47,
    "G2 Spring_ss_wre": 97,
    "G2 Spring_dss_wre": 602,
    "G3 Fall_theta_wre": 1.425057357,
    "G3 Fall_pr_1_99_wre": 49,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 643,
    "G3 Winter_theta_wre": 1.753318652,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 675,
    "G3 Spring_theta_wre": 2.056915082,
    "G3 Spring_pr_1_99_wre": 64,
    "G3 Spring_ss_wre": 97,
    "G3 Spring_dss_wre": 706
  },
  {
    "ID": 215,
    "KG Winter_theta_wre": -1.085602893,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 391,
    "KG Spring_theta_wre": -0.771862434,
    "KG Spring_pr_1_99_wre": 48,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 423,
    "G1 Fall_theta_wre": -0.473327269,
    "G1 Fall_pr_1_99_wre": 45,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 453,
    "G1 Winter_theta_wre": -0.238010454,
    "G1 Winter_pr_1_99_wre": 33,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 476,
    "G1 Spring_theta_wre": 0.181445703,
    "G1 Spring_pr_1_99_wre": 39,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 518,
    "G2 Fall_theta_wre": 0.412476294,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 541,
    "G2 Winter_theta_wre": 0.747586682,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 575,
    "G2 Spring_theta_wre": 1.028861543,
    "G2 Spring_pr_1_99_wre": 48,
    "G2 Spring_ss_wre": 97,
    "G2 Spring_dss_wre": 603,
    "G3 Fall_theta_wre": 1.425950055,
    "G3 Fall_pr_1_99_wre": 49,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 643,
    "G3 Winter_theta_wre": 1.758339914,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 676,
    "G3 Spring_theta_wre": 2.113519275,
    "G3 Spring_pr_1_99_wre": 67,
    "G3 Spring_ss_wre": 98,
    "G3 Spring_dss_wre": 711
  },
  {
    "ID": 216,
    "KG Winter_theta_wre": -1.079512907,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 392,
    "KG Spring_theta_wre": -0.762531766,
    "KG Spring_pr_1_99_wre": 48,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 424,
    "G1 Fall_theta_wre": -0.472090979,
    "G1 Fall_pr_1_99_wre": 45,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 453,
    "G1 Winter_theta_wre": -0.237500318,
    "G1 Winter_pr_1_99_wre": 33,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 476,
    "G1 Spring_theta_wre": 0.185516671,
    "G1 Spring_pr_1_99_wre": 40,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 519,
    "G2 Fall_theta_wre": 0.414794329,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 541,
    "G2 Winter_theta_wre": 0.749136239,
    "G2 Winter_pr_1_99_wre": 27,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 575,
    "G2 Spring_theta_wre": 1.030323943,
    "G2 Spring_pr_1_99_wre": 48,
    "G2 Spring_ss_wre": 97,
    "G2 Spring_dss_wre": 603,
    "G3 Fall_theta_wre": 1.428872547,
    "G3 Fall_pr_1_99_wre": 49,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 643,
    "G3 Winter_theta_wre": 1.758467822,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 676,
    "G3 Spring_theta_wre": 2.114822446,
    "G3 Spring_pr_1_99_wre": 67,
    "G3 Spring_ss_wre": 98,
    "G3 Spring_dss_wre": 711
  },
  {
    "ID": 217,
    "KG Winter_theta_wre": -1.07538961,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 392,
    "KG Spring_theta_wre": -0.755541233,
    "KG Spring_pr_1_99_wre": 49,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 424,
    "G1 Fall_theta_wre": -0.4669547,
    "G1 Fall_pr_1_99_wre": 45,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 453,
    "G1 Winter_theta_wre": -0.235984298,
    "G1 Winter_pr_1_99_wre": 33,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 476,
    "G1 Spring_theta_wre": 0.186193491,
    "G1 Spring_pr_1_99_wre": 40,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 519,
    "G2 Fall_theta_wre": 0.415068807,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 542,
    "G2 Winter_theta_wre": 0.763242433,
    "G2 Winter_pr_1_99_wre": 28,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 576,
    "G2 Spring_theta_wre": 1.03549011,
    "G2 Spring_pr_1_99_wre": 48,
    "G2 Spring_ss_wre": 97,
    "G2 Spring_dss_wre": 604,
    "G3 Fall_theta_wre": 1.432420829,
    "G3 Fall_pr_1_99_wre": 49,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 643,
    "G3 Winter_theta_wre": 1.762084682,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 676,
    "G3 Spring_theta_wre": 2.120281513,
    "G3 Spring_pr_1_99_wre": 67,
    "G3 Spring_ss_wre": 98,
    "G3 Spring_dss_wre": 712
  },
  {
    "ID": 218,
    "KG Winter_theta_wre": -1.075119493,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 392,
    "KG Spring_theta_wre": -0.753354835,
    "KG Spring_pr_1_99_wre": 49,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 425,
    "G1 Fall_theta_wre": -0.46155568,
    "G1 Fall_pr_1_99_wre": 46,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 454,
    "G1 Winter_theta_wre": -0.228996507,
    "G1 Winter_pr_1_99_wre": 33,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 477,
    "G1 Spring_theta_wre": 0.188751803,
    "G1 Spring_pr_1_99_wre": 40,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 519,
    "G2 Fall_theta_wre": 0.415950722,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 542,
    "G2 Winter_theta_wre": 0.770111684,
    "G2 Winter_pr_1_99_wre": 28,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 577,
    "G2 Spring_theta_wre": 1.052315353,
    "G2 Spring_pr_1_99_wre": 48,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 605,
    "G3 Fall_theta_wre": 1.437727201,
    "G3 Fall_pr_1_99_wre": 49,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 644,
    "G3 Winter_theta_wre": 1.763818524,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 676,
    "G3 Spring_theta_wre": 2.124656951,
    "G3 Spring_pr_1_99_wre": 67,
    "G3 Spring_ss_wre": 99,
    "G3 Spring_dss_wre": 712
  },
  {
    "ID": 219,
    "KG Winter_theta_wre": -1.074389296,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 393,
    "KG Spring_theta_wre": -0.747118594,
    "KG Spring_pr_1_99_wre": 49,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 425,
    "G1 Fall_theta_wre": -0.45505089,
    "G1 Fall_pr_1_99_wre": 46,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 454,
    "G1 Winter_theta_wre": -0.223627709,
    "G1 Winter_pr_1_99_wre": 33,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 478,
    "G1 Spring_theta_wre": 0.192796416,
    "G1 Spring_pr_1_99_wre": 40,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 519,
    "G2 Fall_theta_wre": 0.417076681,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 542,
    "G2 Winter_theta_wre": 0.772593089,
    "G2 Winter_pr_1_99_wre": 28,
    "G2 Winter_ss_wre": 97,
    "G2 Winter_dss_wre": 577,
    "G2 Spring_theta_wre": 1.060641449,
    "G2 Spring_pr_1_99_wre": 48,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 606,
    "G3 Fall_theta_wre": 1.442535523,
    "G3 Fall_pr_1_99_wre": 50,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 644,
    "G3 Winter_theta_wre": 1.764888346,
    "G3 Winter_pr_1_99_wre": 41,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 676,
    "G3 Spring_theta_wre": 2.126318413,
    "G3 Spring_pr_1_99_wre": 67,
    "G3 Spring_ss_wre": 99,
    "G3 Spring_dss_wre": 713
  },
  {
    "ID": 220,
    "KG Winter_theta_wre": -1.073149769,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 393,
    "KG Spring_theta_wre": -0.74296547,
    "KG Spring_pr_1_99_wre": 50,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 426,
    "G1 Fall_theta_wre": -0.454358129,
    "G1 Fall_pr_1_99_wre": 46,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 455,
    "G1 Winter_theta_wre": -0.222778214,
    "G1 Winter_pr_1_99_wre": 33,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 478,
    "G1 Spring_theta_wre": 0.202720038,
    "G1 Spring_pr_1_99_wre": 41,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 520,
    "G2 Fall_theta_wre": 0.418728083,
    "G2 Fall_pr_1_99_wre": 34,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 542,
    "G2 Winter_theta_wre": 0.783146653,
    "G2 Winter_pr_1_99_wre": 28,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 578,
    "G2 Spring_theta_wre": 1.06979646,
    "G2 Spring_pr_1_99_wre": 49,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 607,
    "G3 Fall_theta_wre": 1.442641393,
    "G3 Fall_pr_1_99_wre": 50,
    "G3 Fall_ss_wre": 98,
    "G3 Fall_dss_wre": 644,
    "G3 Winter_theta_wre": 1.769083062,
    "G3 Winter_pr_1_99_wre": 42,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 677,
    "G3 Spring_theta_wre": 2.13095409,
    "G3 Spring_pr_1_99_wre": 67,
    "G3 Spring_ss_wre": 99,
    "G3 Spring_dss_wre": 713
  },
  {
    "ID": 221,
    "KG Winter_theta_wre": -1.070202786,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 393,
    "KG Spring_theta_wre": -0.742273749,
    "KG Spring_pr_1_99_wre": 50,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 426,
    "G1 Fall_theta_wre": -0.444500091,
    "G1 Fall_pr_1_99_wre": 46,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 456,
    "G1 Winter_theta_wre": -0.222224638,
    "G1 Winter_pr_1_99_wre": 33,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 478,
    "G1 Spring_theta_wre": 0.213441436,
    "G1 Spring_pr_1_99_wre": 41,
    "G1 Spring_ss_wre": 98,
    "G1 Spring_dss_wre": 521,
    "G2 Fall_theta_wre": 0.428742499,
    "G2 Fall_pr_1_99_wre": 35,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 543,
    "G2 Winter_theta_wre": 0.787629474,
    "G2 Winter_pr_1_99_wre": 28,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 579,
    "G2 Spring_theta_wre": 1.077270693,
    "G2 Spring_pr_1_99_wre": 50,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 608,
    "G3 Fall_theta_wre": 1.448109199,
    "G3 Fall_pr_1_99_wre": 51,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 645,
    "G3 Winter_theta_wre": 1.772901959,
    "G3 Winter_pr_1_99_wre": 42,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 677,
    "G3 Spring_theta_wre": 2.13542565,
    "G3 Spring_pr_1_99_wre": 68,
    "G3 Spring_ss_wre": 99,
    "G3 Spring_dss_wre": 714
  },
  {
    "ID": 222,
    "KG Winter_theta_wre": -1.06737068,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 98,
    "KG Winter_dss_wre": 393,
    "KG Spring_theta_wre": -0.740239369,
    "KG Spring_pr_1_99_wre": 50,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 426,
    "G1 Fall_theta_wre": -0.442304678,
    "G1 Fall_pr_1_99_wre": 46,
    "G1 Fall_ss_wre": 97,
    "G1 Fall_dss_wre": 456,
    "G1 Winter_theta_wre": -0.216203254,
    "G1 Winter_pr_1_99_wre": 34,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 478,
    "G1 Spring_theta_wre": 0.217195619,
    "G1 Spring_pr_1_99_wre": 41,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 522,
    "G2 Fall_theta_wre": 0.436098543,
    "G2 Fall_pr_1_99_wre": 35,
    "G2 Fall_ss_wre": 97,
    "G2 Fall_dss_wre": 544,
    "G2 Winter_theta_wre": 0.788497144,
    "G2 Winter_pr_1_99_wre": 28,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 579,
    "G2 Spring_theta_wre": 1.079026276,
    "G2 Spring_pr_1_99_wre": 50,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 608,
    "G3 Fall_theta_wre": 1.448511714,
    "G3 Fall_pr_1_99_wre": 51,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 645,
    "G3 Winter_theta_wre": 1.774473407,
    "G3 Winter_pr_1_99_wre": 42,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 677,
    "G3 Spring_theta_wre": 2.139072262,
    "G3 Spring_pr_1_99_wre": 70,
    "G3 Spring_ss_wre": 99,
    "G3 Spring_dss_wre": 714
  },
  {
    "ID": 223,
    "KG Winter_theta_wre": -1.059958417,
    "KG Winter_pr_1_99_wre": 53,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 394,
    "KG Spring_theta_wre": -0.731363058,
    "KG Spring_pr_1_99_wre": 50,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 427,
    "G1 Fall_theta_wre": -0.438535811,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 456,
    "G1 Winter_theta_wre": -0.21316047,
    "G1 Winter_pr_1_99_wre": 34,
    "G1 Winter_ss_wre": 97,
    "G1 Winter_dss_wre": 479,
    "G1 Spring_theta_wre": 0.224857898,
    "G1 Spring_pr_1_99_wre": 42,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 522,
    "G2 Fall_theta_wre": 0.440275774,
    "G2 Fall_pr_1_99_wre": 35,
    "G2 Fall_ss_wre": 98,
    "G2 Fall_dss_wre": 544,
    "G2 Winter_theta_wre": 0.790798199,
    "G2 Winter_pr_1_99_wre": 28,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 579,
    "G2 Spring_theta_wre": 1.080753131,
    "G2 Spring_pr_1_99_wre": 50,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 608,
    "G3 Fall_theta_wre": 1.452005839,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 645,
    "G3 Winter_theta_wre": 1.775649472,
    "G3 Winter_pr_1_99_wre": 42,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 678,
    "G3 Spring_theta_wre": 2.148416653,
    "G3 Spring_pr_1_99_wre": 70,
    "G3 Spring_ss_wre": 99,
    "G3 Spring_dss_wre": 715
  },
  {
    "ID": 224,
    "KG Winter_theta_wre": -1.05611539,
    "KG Winter_pr_1_99_wre": 54,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 394,
    "KG Spring_theta_wre": -0.731306311,
    "KG Spring_pr_1_99_wre": 50,
    "KG Spring_ss_wre": 98,
    "KG Spring_dss_wre": 427,
    "G1 Fall_theta_wre": -0.438447812,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 456,
    "G1 Winter_theta_wre": -0.205328962,
    "G1 Winter_pr_1_99_wre": 34,
    "G1 Winter_ss_wre": 98,
    "G1 Winter_dss_wre": 479,
    "G1 Spring_theta_wre": 0.226210663,
    "G1 Spring_pr_1_99_wre": 42,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 523,
    "G2 Fall_theta_wre": 0.451464327,
    "G2 Fall_pr_1_99_wre": 35,
    "G2 Fall_ss_wre": 98,
    "G2 Fall_dss_wre": 545,
    "G2 Winter_theta_wre": 0.791849346,
    "G2 Winter_pr_1_99_wre": 28,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 579,
    "G2 Spring_theta_wre": 1.080915753,
    "G2 Spring_pr_1_99_wre": 50,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 608,
    "G3 Fall_theta_wre": 1.456859861,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 646,
    "G3 Winter_theta_wre": 1.776966541,
    "G3 Winter_pr_1_99_wre": 42,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 678,
    "G3 Spring_theta_wre": 2.166704169,
    "G3 Spring_pr_1_99_wre": 70,
    "G3 Spring_ss_wre": 99,
    "G3 Spring_dss_wre": 717
  },
  {
    "ID": 225,
    "KG Winter_theta_wre": -1.053639501,
    "KG Winter_pr_1_99_wre": 54,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 395,
    "KG Spring_theta_wre": -0.724792834,
    "KG Spring_pr_1_99_wre": 50,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 428,
    "G1 Fall_theta_wre": -0.434265717,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 457,
    "G1 Winter_theta_wre": -0.196033057,
    "G1 Winter_pr_1_99_wre": 35,
    "G1 Winter_ss_wre": 98,
    "G1 Winter_dss_wre": 480,
    "G1 Spring_theta_wre": 0.227345504,
    "G1 Spring_pr_1_99_wre": 42,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 523,
    "G2 Fall_theta_wre": 0.455352222,
    "G2 Fall_pr_1_99_wre": 35,
    "G2 Fall_ss_wre": 98,
    "G2 Fall_dss_wre": 546,
    "G2 Winter_theta_wre": 0.796198022,
    "G2 Winter_pr_1_99_wre": 29,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 580,
    "G2 Spring_theta_wre": 1.084309866,
    "G2 Spring_pr_1_99_wre": 50,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 608,
    "G3 Fall_theta_wre": 1.4590722,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 646,
    "G3 Winter_theta_wre": 1.780886679,
    "G3 Winter_pr_1_99_wre": 42,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 678,
    "G3 Spring_theta_wre": 2.173895712,
    "G3 Spring_pr_1_99_wre": 70,
    "G3 Spring_ss_wre": 99,
    "G3 Spring_dss_wre": 717
  },
  {
    "ID": 226,
    "KG Winter_theta_wre": -1.051463379,
    "KG Winter_pr_1_99_wre": 54,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 395,
    "KG Spring_theta_wre": -0.723141552,
    "KG Spring_pr_1_99_wre": 50,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 428,
    "G1 Fall_theta_wre": -0.429568607,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 457,
    "G1 Winter_theta_wre": -0.188114856,
    "G1 Winter_pr_1_99_wre": 35,
    "G1 Winter_ss_wre": 98,
    "G1 Winter_dss_wre": 481,
    "G1 Spring_theta_wre": 0.235347429,
    "G1 Spring_pr_1_99_wre": 42,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 524,
    "G2 Fall_theta_wre": 0.45792923,
    "G2 Fall_pr_1_99_wre": 35,
    "G2 Fall_ss_wre": 98,
    "G2 Fall_dss_wre": 546,
    "G2 Winter_theta_wre": 0.796477601,
    "G2 Winter_pr_1_99_wre": 29,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 580,
    "G2 Spring_theta_wre": 1.084399983,
    "G2 Spring_pr_1_99_wre": 50,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 608,
    "G3 Fall_theta_wre": 1.459723548,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 646,
    "G3 Winter_theta_wre": 1.784804123,
    "G3 Winter_pr_1_99_wre": 42,
    "G3 Winter_ss_wre": 98,
    "G3 Winter_dss_wre": 678,
    "G3 Spring_theta_wre": 2.176424464,
    "G3 Spring_pr_1_99_wre": 71,
    "G3 Spring_ss_wre": 99,
    "G3 Spring_dss_wre": 718
  },
  {
    "ID": 227,
    "KG Winter_theta_wre": -1.050067741,
    "KG Winter_pr_1_99_wre": 54,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 395,
    "KG Spring_theta_wre": -0.720569329,
    "KG Spring_pr_1_99_wre": 51,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 428,
    "G1 Fall_theta_wre": -0.424058629,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 458,
    "G1 Winter_theta_wre": -0.178697144,
    "G1 Winter_pr_1_99_wre": 36,
    "G1 Winter_ss_wre": 98,
    "G1 Winter_dss_wre": 482,
    "G1 Spring_theta_wre": 0.238358788,
    "G1 Spring_pr_1_99_wre": 42,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 524,
    "G2 Fall_theta_wre": 0.461466416,
    "G2 Fall_pr_1_99_wre": 35,
    "G2 Fall_ss_wre": 98,
    "G2 Fall_dss_wre": 546,
    "G2 Winter_theta_wre": 0.797545012,
    "G2 Winter_pr_1_99_wre": 29,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 580,
    "G2 Spring_theta_wre": 1.085405855,
    "G2 Spring_pr_1_99_wre": 50,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 609,
    "G3 Fall_theta_wre": 1.469010371,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 647,
    "G3 Winter_theta_wre": 1.787607362,
    "G3 Winter_pr_1_99_wre": 43,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 679,
    "G3 Spring_theta_wre": 2.176445054,
    "G3 Spring_pr_1_99_wre": 71,
    "G3 Spring_ss_wre": 99,
    "G3 Spring_dss_wre": 718
  },
  {
    "ID": 228,
    "KG Winter_theta_wre": -1.044759388,
    "KG Winter_pr_1_99_wre": 54,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 396,
    "KG Spring_theta_wre": -0.720179712,
    "KG Spring_pr_1_99_wre": 51,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 428,
    "G1 Fall_theta_wre": -0.421198385,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 458,
    "G1 Winter_theta_wre": -0.17855301,
    "G1 Winter_pr_1_99_wre": 36,
    "G1 Winter_ss_wre": 98,
    "G1 Winter_dss_wre": 482,
    "G1 Spring_theta_wre": 0.239190991,
    "G1 Spring_pr_1_99_wre": 42,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 524,
    "G2 Fall_theta_wre": 0.466920851,
    "G2 Fall_pr_1_99_wre": 35,
    "G2 Fall_ss_wre": 98,
    "G2 Fall_dss_wre": 547,
    "G2 Winter_theta_wre": 0.798286119,
    "G2 Winter_pr_1_99_wre": 29,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 580,
    "G2 Spring_theta_wre": 1.087782684,
    "G2 Spring_pr_1_99_wre": 50,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 609,
    "G3 Fall_theta_wre": 1.469485645,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 647,
    "G3 Winter_theta_wre": 1.794805461,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 679,
    "G3 Spring_theta_wre": 2.184509863,
    "G3 Spring_pr_1_99_wre": 71,
    "G3 Spring_ss_wre": 100,
    "G3 Spring_dss_wre": 718
  },
  {
    "ID": 229,
    "KG Winter_theta_wre": -1.042897351,
    "KG Winter_pr_1_99_wre": 54,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 396,
    "KG Spring_theta_wre": -0.718889615,
    "KG Spring_pr_1_99_wre": 51,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 428,
    "G1 Fall_theta_wre": -0.420052671,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 458,
    "G1 Winter_theta_wre": -0.174619573,
    "G1 Winter_pr_1_99_wre": 36,
    "G1 Winter_ss_wre": 98,
    "G1 Winter_dss_wre": 483,
    "G1 Spring_theta_wre": 0.248404208,
    "G1 Spring_pr_1_99_wre": 43,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 525,
    "G2 Fall_theta_wre": 0.469966931,
    "G2 Fall_pr_1_99_wre": 35,
    "G2 Fall_ss_wre": 98,
    "G2 Fall_dss_wre": 547,
    "G2 Winter_theta_wre": 0.799993851,
    "G2 Winter_pr_1_99_wre": 29,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 580,
    "G2 Spring_theta_wre": 1.091486594,
    "G2 Spring_pr_1_99_wre": 50,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 609,
    "G3 Fall_theta_wre": 1.475045387,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 648,
    "G3 Winter_theta_wre": 1.795921506,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 680,
    "G3 Spring_theta_wre": 2.192347893,
    "G3 Spring_pr_1_99_wre": 71,
    "G3 Spring_ss_wre": 100,
    "G3 Spring_dss_wre": 719
  },
  {
    "ID": 230,
    "KG Winter_theta_wre": -1.040867003,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 396,
    "KG Spring_theta_wre": -0.707372402,
    "KG Spring_pr_1_99_wre": 51,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 429,
    "G1 Fall_theta_wre": -0.417861317,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 458,
    "G1 Winter_theta_wre": -0.173543572,
    "G1 Winter_pr_1_99_wre": 36,
    "G1 Winter_ss_wre": 98,
    "G1 Winter_dss_wre": 483,
    "G1 Spring_theta_wre": 0.249200301,
    "G1 Spring_pr_1_99_wre": 43,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 525,
    "G2 Fall_theta_wre": 0.473370657,
    "G2 Fall_pr_1_99_wre": 35,
    "G2 Fall_ss_wre": 98,
    "G2 Fall_dss_wre": 547,
    "G2 Winter_theta_wre": 0.803131148,
    "G2 Winter_pr_1_99_wre": 29,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 580,
    "G2 Spring_theta_wre": 1.092600005,
    "G2 Spring_pr_1_99_wre": 50,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 609,
    "G3 Fall_theta_wre": 1.477501519,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 648,
    "G3 Winter_theta_wre": 1.796679667,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 680,
    "G3 Spring_theta_wre": 2.193938387,
    "G3 Spring_pr_1_99_wre": 71,
    "G3 Spring_ss_wre": 100,
    "G3 Spring_dss_wre": 719
  },
  {
    "ID": 231,
    "KG Winter_theta_wre": -1.038216225,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 396,
    "KG Spring_theta_wre": -0.702590292,
    "KG Spring_pr_1_99_wre": 52,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 430,
    "G1 Fall_theta_wre": -0.417369258,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 458,
    "G1 Winter_theta_wre": -0.170307211,
    "G1 Winter_pr_1_99_wre": 36,
    "G1 Winter_ss_wre": 98,
    "G1 Winter_dss_wre": 483,
    "G1 Spring_theta_wre": 0.251423176,
    "G1 Spring_pr_1_99_wre": 43,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 525,
    "G2 Fall_theta_wre": 0.478436994,
    "G2 Fall_pr_1_99_wre": 36,
    "G2 Fall_ss_wre": 98,
    "G2 Fall_dss_wre": 548,
    "G2 Winter_theta_wre": 0.812339446,
    "G2 Winter_pr_1_99_wre": 29,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 581,
    "G2 Spring_theta_wre": 1.100663734,
    "G2 Spring_pr_1_99_wre": 51,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 610,
    "G3 Fall_theta_wre": 1.478363866,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 648,
    "G3 Winter_theta_wre": 1.796788264,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 680,
    "G3 Spring_theta_wre": 2.194962632,
    "G3 Spring_pr_1_99_wre": 71,
    "G3 Spring_ss_wre": 100,
    "G3 Spring_dss_wre": 719
  },
  {
    "ID": 232,
    "KG Winter_theta_wre": -1.032866626,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 397,
    "KG Spring_theta_wre": -0.697472514,
    "KG Spring_pr_1_99_wre": 52,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 430,
    "G1 Fall_theta_wre": -0.415445099,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 458,
    "G1 Winter_theta_wre": -0.15803276,
    "G1 Winter_pr_1_99_wre": 37,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 484,
    "G1 Spring_theta_wre": 0.251639187,
    "G1 Spring_pr_1_99_wre": 43,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 525,
    "G2 Fall_theta_wre": 0.495398956,
    "G2 Fall_pr_1_99_wre": 37,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 550,
    "G2 Winter_theta_wre": 0.813205196,
    "G2 Winter_pr_1_99_wre": 29,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 581,
    "G2 Spring_theta_wre": 1.10245681,
    "G2 Spring_pr_1_99_wre": 51,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 610,
    "G3 Fall_theta_wre": 1.485280569,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 649,
    "G3 Winter_theta_wre": 1.797304735,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 680,
    "G3 Spring_theta_wre": 2.198898451,
    "G3 Spring_pr_1_99_wre": 71,
    "G3 Spring_ss_wre": 100,
    "G3 Spring_dss_wre": 720
  },
  {
    "ID": 233,
    "KG Winter_theta_wre": -1.029882847,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 397,
    "KG Spring_theta_wre": -0.696808259,
    "KG Spring_pr_1_99_wre": 52,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 430,
    "G1 Fall_theta_wre": -0.410084061,
    "G1 Fall_pr_1_99_wre": 47,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 459,
    "G1 Winter_theta_wre": -0.156232728,
    "G1 Winter_pr_1_99_wre": 37,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 484,
    "G1 Spring_theta_wre": 0.257890219,
    "G1 Spring_pr_1_99_wre": 43,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 526,
    "G2 Fall_theta_wre": 0.49774062,
    "G2 Fall_pr_1_99_wre": 37,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 550,
    "G2 Winter_theta_wre": 0.814562678,
    "G2 Winter_pr_1_99_wre": 30,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 581,
    "G2 Spring_theta_wre": 1.104878957,
    "G2 Spring_pr_1_99_wre": 51,
    "G2 Spring_ss_wre": 98,
    "G2 Spring_dss_wre": 610,
    "G3 Fall_theta_wre": 1.485316037,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 649,
    "G3 Winter_theta_wre": 1.797574349,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 680,
    "G3 Spring_theta_wre": 2.208291106,
    "G3 Spring_pr_1_99_wre": 72,
    "G3 Spring_ss_wre": 100,
    "G3 Spring_dss_wre": 721
  },
  {
    "ID": 234,
    "KG Winter_theta_wre": -1.02761169,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 397,
    "KG Spring_theta_wre": -0.696568284,
    "KG Spring_pr_1_99_wre": 52,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 430,
    "G1 Fall_theta_wre": -0.398541899,
    "G1 Fall_pr_1_99_wre": 48,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 460,
    "G1 Winter_theta_wre": -0.152738202,
    "G1 Winter_pr_1_99_wre": 37,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 485,
    "G1 Spring_theta_wre": 0.264300626,
    "G1 Spring_pr_1_99_wre": 44,
    "G1 Spring_ss_wre": 99,
    "G1 Spring_dss_wre": 526,
    "G2 Fall_theta_wre": 0.502785121,
    "G2 Fall_pr_1_99_wre": 37,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 550,
    "G2 Winter_theta_wre": 0.814789522,
    "G2 Winter_pr_1_99_wre": 30,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 581,
    "G2 Spring_theta_wre": 1.105408666,
    "G2 Spring_pr_1_99_wre": 51,
    "G2 Spring_ss_wre": 99,
    "G2 Spring_dss_wre": 611,
    "G3 Fall_theta_wre": 1.488561536,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 649,
    "G3 Winter_theta_wre": 1.799074992,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 680,
    "G3 Spring_theta_wre": 2.208364138,
    "G3 Spring_pr_1_99_wre": 72,
    "G3 Spring_ss_wre": 100,
    "G3 Spring_dss_wre": 721
  },
  {
    "ID": 235,
    "KG Winter_theta_wre": -1.02444572,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 398,
    "KG Spring_theta_wre": -0.694101241,
    "KG Spring_pr_1_99_wre": 52,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 431,
    "G1 Fall_theta_wre": -0.394758019,
    "G1 Fall_pr_1_99_wre": 48,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 461,
    "G1 Winter_theta_wre": -0.151429024,
    "G1 Winter_pr_1_99_wre": 37,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 485,
    "G1 Spring_theta_wre": 0.278315466,
    "G1 Spring_pr_1_99_wre": 44,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 528,
    "G2 Fall_theta_wre": 0.505163314,
    "G2 Fall_pr_1_99_wre": 37,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 551,
    "G2 Winter_theta_wre": 0.816667189,
    "G2 Winter_pr_1_99_wre": 30,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 582,
    "G2 Spring_theta_wre": 1.106810803,
    "G2 Spring_pr_1_99_wre": 51,
    "G2 Spring_ss_wre": 99,
    "G2 Spring_dss_wre": 611,
    "G3 Fall_theta_wre": 1.489720844,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 649,
    "G3 Winter_theta_wre": 1.799912372,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 680,
    "G3 Spring_theta_wre": 2.21366688,
    "G3 Spring_pr_1_99_wre": 72,
    "G3 Spring_ss_wre": 100,
    "G3 Spring_dss_wre": 721
  },
  {
    "ID": 236,
    "KG Winter_theta_wre": -1.021167891,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 398,
    "KG Spring_theta_wre": -0.692840479,
    "KG Spring_pr_1_99_wre": 52,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 431,
    "G1 Fall_theta_wre": -0.390680877,
    "G1 Fall_pr_1_99_wre": 48,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 461,
    "G1 Winter_theta_wre": -0.144033108,
    "G1 Winter_pr_1_99_wre": 37,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 486,
    "G1 Spring_theta_wre": 0.282241955,
    "G1 Spring_pr_1_99_wre": 44,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 528,
    "G2 Fall_theta_wre": 0.508650273,
    "G2 Fall_pr_1_99_wre": 37,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 551,
    "G2 Winter_theta_wre": 0.817752346,
    "G2 Winter_pr_1_99_wre": 30,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 582,
    "G2 Spring_theta_wre": 1.110267279,
    "G2 Spring_pr_1_99_wre": 51,
    "G2 Spring_ss_wre": 99,
    "G2 Spring_dss_wre": 611,
    "G3 Fall_theta_wre": 1.490217864,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 649,
    "G3 Winter_theta_wre": 1.802805281,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 680,
    "G3 Spring_theta_wre": 2.222096934,
    "G3 Spring_pr_1_99_wre": 72,
    "G3 Spring_ss_wre": 100,
    "G3 Spring_dss_wre": 722
  },
  {
    "ID": 237,
    "KG Winter_theta_wre": -1.020928992,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 398,
    "KG Spring_theta_wre": -0.688887857,
    "KG Spring_pr_1_99_wre": 52,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 431,
    "G1 Fall_theta_wre": -0.390580567,
    "G1 Fall_pr_1_99_wre": 48,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 461,
    "G1 Winter_theta_wre": -0.139019598,
    "G1 Winter_pr_1_99_wre": 38,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 486,
    "G1 Spring_theta_wre": 0.288498388,
    "G1 Spring_pr_1_99_wre": 45,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 529,
    "G2 Fall_theta_wre": 0.515611185,
    "G2 Fall_pr_1_99_wre": 38,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 552,
    "G2 Winter_theta_wre": 0.819234058,
    "G2 Winter_pr_1_99_wre": 30,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 582,
    "G2 Spring_theta_wre": 1.118316725,
    "G2 Spring_pr_1_99_wre": 51,
    "G2 Spring_ss_wre": 99,
    "G2 Spring_dss_wre": 612,
    "G3 Fall_theta_wre": 1.491587384,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 99,
    "G3 Fall_dss_wre": 649,
    "G3 Winter_theta_wre": 1.804680739,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 680,
    "G3 Spring_theta_wre": 2.227140545,
    "G3 Spring_pr_1_99_wre": 73,
    "G3 Spring_ss_wre": 100,
    "G3 Spring_dss_wre": 723
  },
  {
    "ID": 238,
    "KG Winter_theta_wre": -1.01918344,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 398,
    "KG Spring_theta_wre": -0.686651833,
    "KG Spring_pr_1_99_wre": 52,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 431,
    "G1 Fall_theta_wre": -0.390571444,
    "G1 Fall_pr_1_99_wre": 48,
    "G1 Fall_ss_wre": 98,
    "G1 Fall_dss_wre": 461,
    "G1 Winter_theta_wre": -0.136095193,
    "G1 Winter_pr_1_99_wre": 38,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 486,
    "G1 Spring_theta_wre": 0.289917677,
    "G1 Spring_pr_1_99_wre": 45,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 529,
    "G2 Fall_theta_wre": 0.515633682,
    "G2 Fall_pr_1_99_wre": 38,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 552,
    "G2 Winter_theta_wre": 0.819399379,
    "G2 Winter_pr_1_99_wre": 30,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 582,
    "G2 Spring_theta_wre": 1.139589905,
    "G2 Spring_pr_1_99_wre": 51,
    "G2 Spring_ss_wre": 99,
    "G2 Spring_dss_wre": 614,
    "G3 Fall_theta_wre": 1.508323943,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 651,
    "G3 Winter_theta_wre": 1.810131153,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 681,
    "G3 Spring_theta_wre": 2.245457756,
    "G3 Spring_pr_1_99_wre": 75,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 725
  },
  {
    "ID": 239,
    "KG Winter_theta_wre": -1.016972489,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 398,
    "KG Spring_theta_wre": -0.684157396,
    "KG Spring_pr_1_99_wre": 53,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 432,
    "G1 Fall_theta_wre": -0.38530565,
    "G1 Fall_pr_1_99_wre": 48,
    "G1 Fall_ss_wre": 99,
    "G1 Fall_dss_wre": 461,
    "G1 Winter_theta_wre": -0.131427601,
    "G1 Winter_pr_1_99_wre": 38,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 487,
    "G1 Spring_theta_wre": 0.291652107,
    "G1 Spring_pr_1_99_wre": 45,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 529,
    "G2 Fall_theta_wre": 0.51931305,
    "G2 Fall_pr_1_99_wre": 38,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 552,
    "G2 Winter_theta_wre": 0.829056376,
    "G2 Winter_pr_1_99_wre": 30,
    "G2 Winter_ss_wre": 98,
    "G2 Winter_dss_wre": 583,
    "G2 Spring_theta_wre": 1.142355694,
    "G2 Spring_pr_1_99_wre": 51,
    "G2 Spring_ss_wre": 99,
    "G2 Spring_dss_wre": 614,
    "G3 Fall_theta_wre": 1.512328315,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 651,
    "G3 Winter_theta_wre": 1.810429668,
    "G3 Winter_pr_1_99_wre": 45,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 681,
    "G3 Spring_theta_wre": 2.248300919,
    "G3 Spring_pr_1_99_wre": 75,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 725
  },
  {
    "ID": 240,
    "KG Winter_theta_wre": -1.014611159,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 399,
    "KG Spring_theta_wre": -0.682532514,
    "KG Spring_pr_1_99_wre": 53,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 432,
    "G1 Fall_theta_wre": -0.377840576,
    "G1 Fall_pr_1_99_wre": 49,
    "G1 Fall_ss_wre": 99,
    "G1 Fall_dss_wre": 462,
    "G1 Winter_theta_wre": -0.127912096,
    "G1 Winter_pr_1_99_wre": 38,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 487,
    "G1 Spring_theta_wre": 0.293127381,
    "G1 Spring_pr_1_99_wre": 45,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 529,
    "G2 Fall_theta_wre": 0.529735105,
    "G2 Fall_pr_1_99_wre": 38,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 553,
    "G2 Winter_theta_wre": 0.831995102,
    "G2 Winter_pr_1_99_wre": 30,
    "G2 Winter_ss_wre": 99,
    "G2 Winter_dss_wre": 583,
    "G2 Spring_theta_wre": 1.144312042,
    "G2 Spring_pr_1_99_wre": 51,
    "G2 Spring_ss_wre": 99,
    "G2 Spring_dss_wre": 614,
    "G3 Fall_theta_wre": 1.513967219,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 651,
    "G3 Winter_theta_wre": 1.811529212,
    "G3 Winter_pr_1_99_wre": 46,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 681,
    "G3 Spring_theta_wre": 2.253867946,
    "G3 Spring_pr_1_99_wre": 76,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 725
  },
  {
    "ID": 241,
    "KG Winter_theta_wre": -1.014237419,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 399,
    "KG Spring_theta_wre": -0.674792563,
    "KG Spring_pr_1_99_wre": 53,
    "KG Spring_ss_wre": 99,
    "KG Spring_dss_wre": 433,
    "G1 Fall_theta_wre": -0.377341796,
    "G1 Fall_pr_1_99_wre": 49,
    "G1 Fall_ss_wre": 99,
    "G1 Fall_dss_wre": 462,
    "G1 Winter_theta_wre": -0.123071112,
    "G1 Winter_pr_1_99_wre": 38,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 488,
    "G1 Spring_theta_wre": 0.298004591,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 530,
    "G2 Fall_theta_wre": 0.533702814,
    "G2 Fall_pr_1_99_wre": 39,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 553,
    "G2 Winter_theta_wre": 0.836499433,
    "G2 Winter_pr_1_99_wre": 31,
    "G2 Winter_ss_wre": 99,
    "G2 Winter_dss_wre": 584,
    "G2 Spring_theta_wre": 1.155182582,
    "G2 Spring_pr_1_99_wre": 52,
    "G2 Spring_ss_wre": 99,
    "G2 Spring_dss_wre": 616,
    "G3 Fall_theta_wre": 1.516081267,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 652,
    "G3 Winter_theta_wre": 1.823083236,
    "G3 Winter_pr_1_99_wre": 46,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 682,
    "G3 Spring_theta_wre": 2.25513005,
    "G3 Spring_pr_1_99_wre": 76,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 726
  },
  {
    "ID": 242,
    "KG Winter_theta_wre": -1.013769963,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 399,
    "KG Spring_theta_wre": -0.658357381,
    "KG Spring_pr_1_99_wre": 54,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 434,
    "G1 Fall_theta_wre": -0.36141834,
    "G1 Fall_pr_1_99_wre": 49,
    "G1 Fall_ss_wre": 99,
    "G1 Fall_dss_wre": 464,
    "G1 Winter_theta_wre": -0.121275299,
    "G1 Winter_pr_1_99_wre": 38,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 488,
    "G1 Spring_theta_wre": 0.300206727,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 530,
    "G2 Fall_theta_wre": 0.538702274,
    "G2 Fall_pr_1_99_wre": 39,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 554,
    "G2 Winter_theta_wre": 0.840772915,
    "G2 Winter_pr_1_99_wre": 31,
    "G2 Winter_ss_wre": 99,
    "G2 Winter_dss_wre": 584,
    "G2 Spring_theta_wre": 1.161122235,
    "G2 Spring_pr_1_99_wre": 52,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 616,
    "G3 Fall_theta_wre": 1.520443365,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 652,
    "G3 Winter_theta_wre": 1.831714173,
    "G3 Winter_pr_1_99_wre": 46,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 683,
    "G3 Spring_theta_wre": 2.256011881,
    "G3 Spring_pr_1_99_wre": 76,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 726
  },
  {
    "ID": 243,
    "KG Winter_theta_wre": -1.013656282,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 399,
    "KG Spring_theta_wre": -0.642431069,
    "KG Spring_pr_1_99_wre": 54,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 436,
    "G1 Fall_theta_wre": -0.353832489,
    "G1 Fall_pr_1_99_wre": 50,
    "G1 Fall_ss_wre": 99,
    "G1 Fall_dss_wre": 465,
    "G1 Winter_theta_wre": -0.118764561,
    "G1 Winter_pr_1_99_wre": 38,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 488,
    "G1 Spring_theta_wre": 0.301806605,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 530,
    "G2 Fall_theta_wre": 0.539412849,
    "G2 Fall_pr_1_99_wre": 39,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 554,
    "G2 Winter_theta_wre": 0.843256664,
    "G2 Winter_pr_1_99_wre": 31,
    "G2 Winter_ss_wre": 99,
    "G2 Winter_dss_wre": 584,
    "G2 Spring_theta_wre": 1.163677393,
    "G2 Spring_pr_1_99_wre": 52,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 616,
    "G3 Fall_theta_wre": 1.522126074,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 652,
    "G3 Winter_theta_wre": 1.834770773,
    "G3 Winter_pr_1_99_wre": 46,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 683,
    "G3 Spring_theta_wre": 2.259245554,
    "G3 Spring_pr_1_99_wre": 76,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 726
  },
  {
    "ID": 244,
    "KG Winter_theta_wre": -1.010211954,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 99,
    "KG Winter_dss_wre": 399,
    "KG Spring_theta_wre": -0.640825061,
    "KG Spring_pr_1_99_wre": 54,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 436,
    "G1 Fall_theta_wre": -0.347163459,
    "G1 Fall_pr_1_99_wre": 51,
    "G1 Fall_ss_wre": 99,
    "G1 Fall_dss_wre": 465,
    "G1 Winter_theta_wre": -0.117026185,
    "G1 Winter_pr_1_99_wre": 38,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 488,
    "G1 Spring_theta_wre": 0.311147112,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 531,
    "G2 Fall_theta_wre": 0.54074781,
    "G2 Fall_pr_1_99_wre": 39,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 554,
    "G2 Winter_theta_wre": 0.843868871,
    "G2 Winter_pr_1_99_wre": 31,
    "G2 Winter_ss_wre": 99,
    "G2 Winter_dss_wre": 584,
    "G2 Spring_theta_wre": 1.1655533,
    "G2 Spring_pr_1_99_wre": 52,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 617,
    "G3 Fall_theta_wre": 1.522656374,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 652,
    "G3 Winter_theta_wre": 1.834777497,
    "G3 Winter_pr_1_99_wre": 46,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 683,
    "G3 Spring_theta_wre": 2.260478979,
    "G3 Spring_pr_1_99_wre": 76,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 726
  },
  {
    "ID": 245,
    "KG Winter_theta_wre": -1.002279138,
    "KG Winter_pr_1_99_wre": 55,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 400,
    "KG Spring_theta_wre": -0.640076357,
    "KG Spring_pr_1_99_wre": 54,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 436,
    "G1 Fall_theta_wre": -0.339094305,
    "G1 Fall_pr_1_99_wre": 51,
    "G1 Fall_ss_wre": 99,
    "G1 Fall_dss_wre": 466,
    "G1 Winter_theta_wre": -0.116092911,
    "G1 Winter_pr_1_99_wre": 38,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 488,
    "G1 Spring_theta_wre": 0.314847677,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 531,
    "G2 Fall_theta_wre": 0.542281612,
    "G2 Fall_pr_1_99_wre": 39,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 554,
    "G2 Winter_theta_wre": 0.846852432,
    "G2 Winter_pr_1_99_wre": 31,
    "G2 Winter_ss_wre": 99,
    "G2 Winter_dss_wre": 585,
    "G2 Spring_theta_wre": 1.17714639,
    "G2 Spring_pr_1_99_wre": 53,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 618,
    "G3 Fall_theta_wre": 1.525150008,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 653,
    "G3 Winter_theta_wre": 1.835861321,
    "G3 Winter_pr_1_99_wre": 46,
    "G3 Winter_ss_wre": 99,
    "G3 Winter_dss_wre": 684,
    "G3 Spring_theta_wre": 2.261155801,
    "G3 Spring_pr_1_99_wre": 76,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 726
  },
  {
    "ID": 246,
    "KG Winter_theta_wre": -0.998423564,
    "KG Winter_pr_1_99_wre": 56,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 400,
    "KG Spring_theta_wre": -0.638477408,
    "KG Spring_pr_1_99_wre": 54,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 436,
    "G1 Fall_theta_wre": -0.334235112,
    "G1 Fall_pr_1_99_wre": 51,
    "G1 Fall_ss_wre": 99,
    "G1 Fall_dss_wre": 467,
    "G1 Winter_theta_wre": -0.115774484,
    "G1 Winter_pr_1_99_wre": 38,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 488,
    "G1 Spring_theta_wre": 0.316621028,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 532,
    "G2 Fall_theta_wre": 0.545558294,
    "G2 Fall_pr_1_99_wre": 39,
    "G2 Fall_ss_wre": 99,
    "G2 Fall_dss_wre": 555,
    "G2 Winter_theta_wre": 0.864534787,
    "G2 Winter_pr_1_99_wre": 32,
    "G2 Winter_ss_wre": 99,
    "G2 Winter_dss_wre": 586,
    "G2 Spring_theta_wre": 1.178335259,
    "G2 Spring_pr_1_99_wre": 53,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 618,
    "G3 Fall_theta_wre": 1.526160765,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 653,
    "G3 Winter_theta_wre": 1.854706546,
    "G3 Winter_pr_1_99_wre": 47,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 685,
    "G3 Spring_theta_wre": 2.263545875,
    "G3 Spring_pr_1_99_wre": 76,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 726
  },
  {
    "ID": 247,
    "KG Winter_theta_wre": -0.993281283,
    "KG Winter_pr_1_99_wre": 56,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 401,
    "KG Spring_theta_wre": -0.636389871,
    "KG Spring_pr_1_99_wre": 54,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 436,
    "G1 Fall_theta_wre": -0.331488858,
    "G1 Fall_pr_1_99_wre": 51,
    "G1 Fall_ss_wre": 99,
    "G1 Fall_dss_wre": 467,
    "G1 Winter_theta_wre": -0.109173478,
    "G1 Winter_pr_1_99_wre": 39,
    "G1 Winter_ss_wre": 99,
    "G1 Winter_dss_wre": 489,
    "G1 Spring_theta_wre": 0.321877604,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 532,
    "G2 Fall_theta_wre": 0.552488273,
    "G2 Fall_pr_1_99_wre": 39,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 555,
    "G2 Winter_theta_wre": 0.864662826,
    "G2 Winter_pr_1_99_wre": 32,
    "G2 Winter_ss_wre": 99,
    "G2 Winter_dss_wre": 586,
    "G2 Spring_theta_wre": 1.179432499,
    "G2 Spring_pr_1_99_wre": 53,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 618,
    "G3 Fall_theta_wre": 1.527440102,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 653,
    "G3 Winter_theta_wre": 1.856122267,
    "G3 Winter_pr_1_99_wre": 47,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 686,
    "G3 Spring_theta_wre": 2.266597229,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 727
  },
  {
    "ID": 248,
    "KG Winter_theta_wre": -0.992530644,
    "KG Winter_pr_1_99_wre": 56,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 401,
    "KG Spring_theta_wre": -0.633338933,
    "KG Spring_pr_1_99_wre": 54,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 437,
    "G1 Fall_theta_wre": -0.329970177,
    "G1 Fall_pr_1_99_wre": 52,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 467,
    "G1 Winter_theta_wre": -0.102119588,
    "G1 Winter_pr_1_99_wre": 39,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 490,
    "G1 Spring_theta_wre": 0.324765436,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 100,
    "G1 Spring_dss_wre": 532,
    "G2 Fall_theta_wre": 0.552720455,
    "G2 Fall_pr_1_99_wre": 39,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 555,
    "G2 Winter_theta_wre": 0.872982949,
    "G2 Winter_pr_1_99_wre": 33,
    "G2 Winter_ss_wre": 99,
    "G2 Winter_dss_wre": 587,
    "G2 Spring_theta_wre": 1.182203851,
    "G2 Spring_pr_1_99_wre": 53,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 618,
    "G3 Fall_theta_wre": 1.528004746,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 653,
    "G3 Winter_theta_wre": 1.861829594,
    "G3 Winter_pr_1_99_wre": 47,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 686,
    "G3 Spring_theta_wre": 2.268426078,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 727
  },
  {
    "ID": 249,
    "KG Winter_theta_wre": -0.991025695,
    "KG Winter_pr_1_99_wre": 56,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 401,
    "KG Spring_theta_wre": -0.624215995,
    "KG Spring_pr_1_99_wre": 55,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 438,
    "G1 Fall_theta_wre": -0.32112478,
    "G1 Fall_pr_1_99_wre": 52,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 468,
    "G1 Winter_theta_wre": -0.098962494,
    "G1 Winter_pr_1_99_wre": 39,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 490,
    "G1 Spring_theta_wre": 0.32975005,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 533,
    "G2 Fall_theta_wre": 0.5551467,
    "G2 Fall_pr_1_99_wre": 40,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 556,
    "G2 Winter_theta_wre": 0.883740074,
    "G2 Winter_pr_1_99_wre": 33,
    "G2 Winter_ss_wre": 99,
    "G2 Winter_dss_wre": 588,
    "G2 Spring_theta_wre": 1.184379839,
    "G2 Spring_pr_1_99_wre": 53,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 618,
    "G3 Fall_theta_wre": 1.530771525,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 653,
    "G3 Winter_theta_wre": 1.862396266,
    "G3 Winter_pr_1_99_wre": 47,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 686,
    "G3 Spring_theta_wre": 2.272379015,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 727
  },
  {
    "ID": 250,
    "KG Winter_theta_wre": -0.989851518,
    "KG Winter_pr_1_99_wre": 56,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 401,
    "KG Spring_theta_wre": -0.620732494,
    "KG Spring_pr_1_99_wre": 55,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 438,
    "G1 Fall_theta_wre": -0.313238593,
    "G1 Fall_pr_1_99_wre": 52,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 469,
    "G1 Winter_theta_wre": -0.093945176,
    "G1 Winter_pr_1_99_wre": 39,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 491,
    "G1 Spring_theta_wre": 0.330375822,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 533,
    "G2 Fall_theta_wre": 0.563737621,
    "G2 Fall_pr_1_99_wre": 40,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 556,
    "G2 Winter_theta_wre": 0.897057338,
    "G2 Winter_pr_1_99_wre": 34,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 590,
    "G2 Spring_theta_wre": 1.191902581,
    "G2 Spring_pr_1_99_wre": 54,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 619,
    "G3 Fall_theta_wre": 1.535057645,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 654,
    "G3 Winter_theta_wre": 1.863232482,
    "G3 Winter_pr_1_99_wre": 47,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 686,
    "G3 Spring_theta_wre": 2.275686573,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 728
  },
  {
    "ID": 251,
    "KG Winter_theta_wre": -0.98684411,
    "KG Winter_pr_1_99_wre": 56,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 401,
    "KG Spring_theta_wre": -0.620691944,
    "KG Spring_pr_1_99_wre": 55,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 438,
    "G1 Fall_theta_wre": -0.311987862,
    "G1 Fall_pr_1_99_wre": 52,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 469,
    "G1 Winter_theta_wre": -0.090092593,
    "G1 Winter_pr_1_99_wre": 39,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 491,
    "G1 Spring_theta_wre": 0.333516229,
    "G1 Spring_pr_1_99_wre": 46,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 533,
    "G2 Fall_theta_wre": 0.566756398,
    "G2 Fall_pr_1_99_wre": 40,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 557,
    "G2 Winter_theta_wre": 0.898687267,
    "G2 Winter_pr_1_99_wre": 34,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 590,
    "G2 Spring_theta_wre": 1.19272432,
    "G2 Spring_pr_1_99_wre": 54,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 619,
    "G3 Fall_theta_wre": 1.543196899,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 654,
    "G3 Winter_theta_wre": 1.86332285,
    "G3 Winter_pr_1_99_wre": 47,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 686,
    "G3 Spring_theta_wre": 2.278666758,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 728
  },
  {
    "ID": 252,
    "KG Winter_theta_wre": -0.977088897,
    "KG Winter_pr_1_99_wre": 57,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 402,
    "KG Spring_theta_wre": -0.61947142,
    "KG Spring_pr_1_99_wre": 55,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 438,
    "G1 Fall_theta_wre": -0.310822904,
    "G1 Fall_pr_1_99_wre": 52,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 469,
    "G1 Winter_theta_wre": -0.089270198,
    "G1 Winter_pr_1_99_wre": 39,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 491,
    "G1 Spring_theta_wre": 0.342985607,
    "G1 Spring_pr_1_99_wre": 47,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 534,
    "G2 Fall_theta_wre": 0.573037183,
    "G2 Fall_pr_1_99_wre": 40,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 557,
    "G2 Winter_theta_wre": 0.902629745,
    "G2 Winter_pr_1_99_wre": 34,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 590,
    "G2 Spring_theta_wre": 1.195477115,
    "G2 Spring_pr_1_99_wre": 54,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 620,
    "G3 Fall_theta_wre": 1.546551889,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 655,
    "G3 Winter_theta_wre": 1.866013635,
    "G3 Winter_pr_1_99_wre": 47,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 687,
    "G3 Spring_theta_wre": 2.280279865,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 728
  },
  {
    "ID": 253,
    "KG Winter_theta_wre": -0.975974563,
    "KG Winter_pr_1_99_wre": 57,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 402,
    "KG Spring_theta_wre": -0.616807535,
    "KG Spring_pr_1_99_wre": 55,
    "KG Spring_ss_wre": 100,
    "KG Spring_dss_wre": 438,
    "G1 Fall_theta_wre": -0.306863093,
    "G1 Fall_pr_1_99_wre": 52,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 469,
    "G1 Winter_theta_wre": -0.088525969,
    "G1 Winter_pr_1_99_wre": 39,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 491,
    "G1 Spring_theta_wre": 0.344403452,
    "G1 Spring_pr_1_99_wre": 47,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 534,
    "G2 Fall_theta_wre": 0.578260104,
    "G2 Fall_pr_1_99_wre": 40,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 558,
    "G2 Winter_theta_wre": 0.90686994,
    "G2 Winter_pr_1_99_wre": 34,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 591,
    "G2 Spring_theta_wre": 1.207254589,
    "G2 Spring_pr_1_99_wre": 54,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 621,
    "G3 Fall_theta_wre": 1.547616921,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 655,
    "G3 Winter_theta_wre": 1.871542965,
    "G3 Winter_pr_1_99_wre": 48,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 687,
    "G3 Spring_theta_wre": 2.281362627,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 728
  },
  {
    "ID": 254,
    "KG Winter_theta_wre": -0.971065204,
    "KG Winter_pr_1_99_wre": 57,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 403,
    "KG Spring_theta_wre": -0.601879121,
    "KG Spring_pr_1_99_wre": 55,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 440,
    "G1 Fall_theta_wre": -0.304546981,
    "G1 Fall_pr_1_99_wre": 52,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 470,
    "G1 Winter_theta_wre": -0.087837385,
    "G1 Winter_pr_1_99_wre": 40,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 491,
    "G1 Spring_theta_wre": 0.345630647,
    "G1 Spring_pr_1_99_wre": 47,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 535,
    "G2 Fall_theta_wre": 0.580604068,
    "G2 Fall_pr_1_99_wre": 40,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 558,
    "G2 Winter_theta_wre": 0.907662689,
    "G2 Winter_pr_1_99_wre": 34,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 591,
    "G2 Spring_theta_wre": 1.214928053,
    "G2 Spring_pr_1_99_wre": 54,
    "G2 Spring_ss_wre": 100,
    "G2 Spring_dss_wre": 621,
    "G3 Fall_theta_wre": 1.5481215,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 655,
    "G3 Winter_theta_wre": 1.872536693,
    "G3 Winter_pr_1_99_wre": 48,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 687,
    "G3 Spring_theta_wre": 2.281425343,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 101,
    "G3 Spring_dss_wre": 728
  },
  {
    "ID": 255,
    "KG Winter_theta_wre": -0.962984028,
    "KG Winter_pr_1_99_wre": 57,
    "KG Winter_ss_wre": 100,
    "KG Winter_dss_wre": 404,
    "KG Spring_theta_wre": -0.601736984,
    "KG Spring_pr_1_99_wre": 55,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 440,
    "G1 Fall_theta_wre": -0.29959519,
    "G1 Fall_pr_1_99_wre": 53,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 470,
    "G1 Winter_theta_wre": -0.08276869,
    "G1 Winter_pr_1_99_wre": 40,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 492,
    "G1 Spring_theta_wre": 0.352088774,
    "G1 Spring_pr_1_99_wre": 47,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 535,
    "G2 Fall_theta_wre": 0.582612046,
    "G2 Fall_pr_1_99_wre": 40,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 558,
    "G2 Winter_theta_wre": 0.908920484,
    "G2 Winter_pr_1_99_wre": 34,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 591,
    "G2 Spring_theta_wre": 1.217640244,
    "G2 Spring_pr_1_99_wre": 54,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 622,
    "G3 Fall_theta_wre": 1.549047727,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 100,
    "G3 Fall_dss_wre": 655,
    "G3 Winter_theta_wre": 1.872969687,
    "G3 Winter_pr_1_99_wre": 48,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 687,
    "G3 Spring_theta_wre": 2.291999757,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 102,
    "G3 Spring_dss_wre": 729
  },
  {
    "ID": 256,
    "KG Winter_theta_wre": -0.949323443,
    "KG Winter_pr_1_99_wre": 58,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 405,
    "KG Spring_theta_wre": -0.587230385,
    "KG Spring_pr_1_99_wre": 56,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 441,
    "G1 Fall_theta_wre": -0.294370724,
    "G1 Fall_pr_1_99_wre": 53,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 471,
    "G1 Winter_theta_wre": -0.079540407,
    "G1 Winter_pr_1_99_wre": 40,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 492,
    "G1 Spring_theta_wre": 0.353284544,
    "G1 Spring_pr_1_99_wre": 47,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 535,
    "G2 Fall_theta_wre": 0.585871279,
    "G2 Fall_pr_1_99_wre": 41,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 559,
    "G2 Winter_theta_wre": 0.912954482,
    "G2 Winter_pr_1_99_wre": 34,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 591,
    "G2 Spring_theta_wre": 1.218759145,
    "G2 Spring_pr_1_99_wre": 54,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 622,
    "G3 Fall_theta_wre": 1.55510648,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 656,
    "G3 Winter_theta_wre": 1.88861603,
    "G3 Winter_pr_1_99_wre": 48,
    "G3 Winter_ss_wre": 100,
    "G3 Winter_dss_wre": 689,
    "G3 Spring_theta_wre": 2.296694384,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 102,
    "G3 Spring_dss_wre": 730
  },
  {
    "ID": 257,
    "KG Winter_theta_wre": -0.945236886,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 405,
    "KG Spring_theta_wre": -0.581166017,
    "KG Spring_pr_1_99_wre": 56,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 442,
    "G1 Fall_theta_wre": -0.294263118,
    "G1 Fall_pr_1_99_wre": 53,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 471,
    "G1 Winter_theta_wre": -0.0725765,
    "G1 Winter_pr_1_99_wre": 40,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 493,
    "G1 Spring_theta_wre": 0.364122719,
    "G1 Spring_pr_1_99_wre": 47,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 536,
    "G2 Fall_theta_wre": 0.588691587,
    "G2 Fall_pr_1_99_wre": 41,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 559,
    "G2 Winter_theta_wre": 0.913569374,
    "G2 Winter_pr_1_99_wre": 34,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 591,
    "G2 Spring_theta_wre": 1.225530387,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 623,
    "G3 Fall_theta_wre": 1.556566001,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 656,
    "G3 Winter_theta_wre": 1.896920085,
    "G3 Winter_pr_1_99_wre": 49,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 690,
    "G3 Spring_theta_wre": 2.297590946,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 102,
    "G3 Spring_dss_wre": 730
  },
  {
    "ID": 258,
    "KG Winter_theta_wre": -0.942778621,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 406,
    "KG Spring_theta_wre": -0.580611113,
    "KG Spring_pr_1_99_wre": 56,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 442,
    "G1 Fall_theta_wre": -0.292552019,
    "G1 Fall_pr_1_99_wre": 53,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 471,
    "G1 Winter_theta_wre": -0.072551936,
    "G1 Winter_pr_1_99_wre": 40,
    "G1 Winter_ss_wre": 100,
    "G1 Winter_dss_wre": 493,
    "G1 Spring_theta_wre": 0.364307163,
    "G1 Spring_pr_1_99_wre": 47,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 536,
    "G2 Fall_theta_wre": 0.590483223,
    "G2 Fall_pr_1_99_wre": 41,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 559,
    "G2 Winter_theta_wre": 0.915278714,
    "G2 Winter_pr_1_99_wre": 34,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 592,
    "G2 Spring_theta_wre": 1.230335896,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 623,
    "G3 Fall_theta_wre": 1.557290983,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 656,
    "G3 Winter_theta_wre": 1.899476518,
    "G3 Winter_pr_1_99_wre": 49,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 690,
    "G3 Spring_theta_wre": 2.298183459,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 102,
    "G3 Spring_dss_wre": 730
  },
  {
    "ID": 259,
    "KG Winter_theta_wre": -0.941619563,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 406,
    "KG Spring_theta_wre": -0.580314405,
    "KG Spring_pr_1_99_wre": 56,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 442,
    "G1 Fall_theta_wre": -0.292268997,
    "G1 Fall_pr_1_99_wre": 53,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 471,
    "G1 Winter_theta_wre": -0.048842959,
    "G1 Winter_pr_1_99_wre": 42,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 495,
    "G1 Spring_theta_wre": 0.366527805,
    "G1 Spring_pr_1_99_wre": 47,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 537,
    "G2 Fall_theta_wre": 0.597933715,
    "G2 Fall_pr_1_99_wre": 41,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 560,
    "G2 Winter_theta_wre": 0.918361415,
    "G2 Winter_pr_1_99_wre": 35,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 592,
    "G2 Spring_theta_wre": 1.233622805,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 623,
    "G3 Fall_theta_wre": 1.561712021,
    "G3 Fall_pr_1_99_wre": 52,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 656,
    "G3 Winter_theta_wre": 1.90762795,
    "G3 Winter_pr_1_99_wre": 50,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 691,
    "G3 Spring_theta_wre": 2.302462708,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 102,
    "G3 Spring_dss_wre": 730
  },
  {
    "ID": 260,
    "KG Winter_theta_wre": -0.939762055,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 406,
    "KG Spring_theta_wre": -0.580265762,
    "KG Spring_pr_1_99_wre": 56,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 442,
    "G1 Fall_theta_wre": -0.292179867,
    "G1 Fall_pr_1_99_wre": 53,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 471,
    "G1 Winter_theta_wre": -0.048688721,
    "G1 Winter_pr_1_99_wre": 42,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 495,
    "G1 Spring_theta_wre": 0.370707997,
    "G1 Spring_pr_1_99_wre": 48,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 537,
    "G2 Fall_theta_wre": 0.599159281,
    "G2 Fall_pr_1_99_wre": 41,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 560,
    "G2 Winter_theta_wre": 0.932037191,
    "G2 Winter_pr_1_99_wre": 36,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 593,
    "G2 Spring_theta_wre": 1.23418793,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 623,
    "G3 Fall_theta_wre": 1.569722784,
    "G3 Fall_pr_1_99_wre": 53,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 657,
    "G3 Winter_theta_wre": 1.912357511,
    "G3 Winter_pr_1_99_wre": 50,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 691,
    "G3 Spring_theta_wre": 2.304657907,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 102,
    "G3 Spring_dss_wre": 730
  },
  {
    "ID": 261,
    "KG Winter_theta_wre": -0.935060344,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 406,
    "KG Spring_theta_wre": -0.579751352,
    "KG Spring_pr_1_99_wre": 56,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 442,
    "G1 Fall_theta_wre": -0.287478941,
    "G1 Fall_pr_1_99_wre": 53,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 471,
    "G1 Winter_theta_wre": -0.046800593,
    "G1 Winter_pr_1_99_wre": 42,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 495,
    "G1 Spring_theta_wre": 0.375996901,
    "G1 Spring_pr_1_99_wre": 48,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 538,
    "G2 Fall_theta_wre": 0.60073932,
    "G2 Fall_pr_1_99_wre": 41,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 560,
    "G2 Winter_theta_wre": 0.933245595,
    "G2 Winter_pr_1_99_wre": 36,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 593,
    "G2 Spring_theta_wre": 1.234391194,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 623,
    "G3 Fall_theta_wre": 1.569940134,
    "G3 Fall_pr_1_99_wre": 53,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 657,
    "G3 Winter_theta_wre": 1.914046826,
    "G3 Winter_pr_1_99_wre": 51,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 691,
    "G3 Spring_theta_wre": 2.3198295,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 102,
    "G3 Spring_dss_wre": 732
  },
  {
    "ID": 262,
    "KG Winter_theta_wre": -0.933946677,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 407,
    "KG Spring_theta_wre": -0.577183547,
    "KG Spring_pr_1_99_wre": 57,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 442,
    "G1 Fall_theta_wre": -0.283027107,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 472,
    "G1 Winter_theta_wre": -0.04360673,
    "G1 Winter_pr_1_99_wre": 42,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 496,
    "G1 Spring_theta_wre": 0.378249354,
    "G1 Spring_pr_1_99_wre": 48,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 538,
    "G2 Fall_theta_wre": 0.601458696,
    "G2 Fall_pr_1_99_wre": 41,
    "G2 Fall_ss_wre": 100,
    "G2 Fall_dss_wre": 560,
    "G2 Winter_theta_wre": 0.936916878,
    "G2 Winter_pr_1_99_wre": 36,
    "G2 Winter_ss_wre": 100,
    "G2 Winter_dss_wre": 594,
    "G2 Spring_theta_wre": 1.238684015,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 624,
    "G3 Fall_theta_wre": 1.573407917,
    "G3 Fall_pr_1_99_wre": 53,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 657,
    "G3 Winter_theta_wre": 1.917436948,
    "G3 Winter_pr_1_99_wre": 51,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 692,
    "G3 Spring_theta_wre": 2.322216151,
    "G3 Spring_pr_1_99_wre": 78,
    "G3 Spring_ss_wre": 102,
    "G3 Spring_dss_wre": 732
  },
  {
    "ID": 263,
    "KG Winter_theta_wre": -0.930469559,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 407,
    "KG Spring_theta_wre": -0.571564982,
    "KG Spring_pr_1_99_wre": 57,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 443,
    "G1 Fall_theta_wre": -0.279509966,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 472,
    "G1 Winter_theta_wre": -0.03779739,
    "G1 Winter_pr_1_99_wre": 43,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 496,
    "G1 Spring_theta_wre": 0.380129173,
    "G1 Spring_pr_1_99_wre": 48,
    "G1 Spring_ss_wre": 101,
    "G1 Spring_dss_wre": 538,
    "G2 Fall_theta_wre": 0.623681127,
    "G2 Fall_pr_1_99_wre": 42,
    "G2 Fall_ss_wre": 101,
    "G2 Fall_dss_wre": 562,
    "G2 Winter_theta_wre": 0.940023232,
    "G2 Winter_pr_1_99_wre": 36,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 594,
    "G2 Spring_theta_wre": 1.239663266,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 624,
    "G3 Fall_theta_wre": 1.574593499,
    "G3 Fall_pr_1_99_wre": 53,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 657,
    "G3 Winter_theta_wre": 1.919690308,
    "G3 Winter_pr_1_99_wre": 51,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 692,
    "G3 Spring_theta_wre": 2.325296759,
    "G3 Spring_pr_1_99_wre": 79,
    "G3 Spring_ss_wre": 102,
    "G3 Spring_dss_wre": 733
  },
  {
    "ID": 264,
    "KG Winter_theta_wre": -0.929006517,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 407,
    "KG Spring_theta_wre": -0.56587032,
    "KG Spring_pr_1_99_wre": 58,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 443,
    "G1 Fall_theta_wre": -0.278818383,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 472,
    "G1 Winter_theta_wre": -0.036505122,
    "G1 Winter_pr_1_99_wre": 43,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 496,
    "G1 Spring_theta_wre": 0.381061276,
    "G1 Spring_pr_1_99_wre": 48,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 538,
    "G2 Fall_theta_wre": 0.632734441,
    "G2 Fall_pr_1_99_wre": 43,
    "G2 Fall_ss_wre": 101,
    "G2 Fall_dss_wre": 563,
    "G2 Winter_theta_wre": 0.945961001,
    "G2 Winter_pr_1_99_wre": 36,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 595,
    "G2 Spring_theta_wre": 1.242412128,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 624,
    "G3 Fall_theta_wre": 1.57679415,
    "G3 Fall_pr_1_99_wre": 53,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 658,
    "G3 Winter_theta_wre": 1.927574902,
    "G3 Winter_pr_1_99_wre": 52,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 693,
    "G3 Spring_theta_wre": 2.350042794,
    "G3 Spring_pr_1_99_wre": 80,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 735
  },
  {
    "ID": 265,
    "KG Winter_theta_wre": -0.927440416,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 407,
    "KG Spring_theta_wre": -0.563466098,
    "KG Spring_pr_1_99_wre": 58,
    "KG Spring_ss_wre": 101,
    "KG Spring_dss_wre": 444,
    "G1 Fall_theta_wre": -0.27787767,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 100,
    "G1 Fall_dss_wre": 472,
    "G1 Winter_theta_wre": -0.036371071,
    "G1 Winter_pr_1_99_wre": 43,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 496,
    "G1 Spring_theta_wre": 0.387563548,
    "G1 Spring_pr_1_99_wre": 49,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 539,
    "G2 Fall_theta_wre": 0.642934779,
    "G2 Fall_pr_1_99_wre": 43,
    "G2 Fall_ss_wre": 101,
    "G2 Fall_dss_wre": 564,
    "G2 Winter_theta_wre": 0.946842778,
    "G2 Winter_pr_1_99_wre": 36,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 595,
    "G2 Spring_theta_wre": 1.247219228,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 625,
    "G3 Fall_theta_wre": 1.577236049,
    "G3 Fall_pr_1_99_wre": 53,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 658,
    "G3 Winter_theta_wre": 1.927803132,
    "G3 Winter_pr_1_99_wre": 52,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 693,
    "G3 Spring_theta_wre": 2.363757193,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 736
  },
  {
    "ID": 266,
    "KG Winter_theta_wre": -0.926418846,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 407,
    "KG Spring_theta_wre": -0.55381976,
    "KG Spring_pr_1_99_wre": 58,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 445,
    "G1 Fall_theta_wre": -0.271763369,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 473,
    "G1 Winter_theta_wre": -0.027867845,
    "G1 Winter_pr_1_99_wre": 43,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 497,
    "G1 Spring_theta_wre": 0.396951464,
    "G1 Spring_pr_1_99_wre": 49,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 540,
    "G2 Fall_theta_wre": 0.648380469,
    "G2 Fall_pr_1_99_wre": 43,
    "G2 Fall_ss_wre": 101,
    "G2 Fall_dss_wre": 565,
    "G2 Winter_theta_wre": 0.952989054,
    "G2 Winter_pr_1_99_wre": 36,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 595,
    "G2 Spring_theta_wre": 1.249205151,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 625,
    "G3 Fall_theta_wre": 1.578745108,
    "G3 Fall_pr_1_99_wre": 53,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 658,
    "G3 Winter_theta_wre": 1.928864549,
    "G3 Winter_pr_1_99_wre": 52,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 693,
    "G3 Spring_theta_wre": 2.367387427,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 737
  },
  {
    "ID": 267,
    "KG Winter_theta_wre": -0.922693189,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 408,
    "KG Spring_theta_wre": -0.545387541,
    "KG Spring_pr_1_99_wre": 58,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 445,
    "G1 Fall_theta_wre": -0.268960838,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 473,
    "G1 Winter_theta_wre": -0.02636632,
    "G1 Winter_pr_1_99_wre": 43,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 497,
    "G1 Spring_theta_wre": 0.398157694,
    "G1 Spring_pr_1_99_wre": 49,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 540,
    "G2 Fall_theta_wre": 0.658259068,
    "G2 Fall_pr_1_99_wre": 43,
    "G2 Fall_ss_wre": 101,
    "G2 Fall_dss_wre": 566,
    "G2 Winter_theta_wre": 0.954438699,
    "G2 Winter_pr_1_99_wre": 36,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 595,
    "G2 Spring_theta_wre": 1.249720052,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 625,
    "G3 Fall_theta_wre": 1.580253584,
    "G3 Fall_pr_1_99_wre": 53,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 658,
    "G3 Winter_theta_wre": 1.930218333,
    "G3 Winter_pr_1_99_wre": 52,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 693,
    "G3 Spring_theta_wre": 2.36825708,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 737
  },
  {
    "ID": 268,
    "KG Winter_theta_wre": -0.920762801,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 408,
    "KG Spring_theta_wre": -0.543326707,
    "KG Spring_pr_1_99_wre": 58,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 446,
    "G1 Fall_theta_wre": -0.263106912,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 474,
    "G1 Winter_theta_wre": -0.026051916,
    "G1 Winter_pr_1_99_wre": 43,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 497,
    "G1 Spring_theta_wre": 0.399829347,
    "G1 Spring_pr_1_99_wre": 50,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 540,
    "G2 Fall_theta_wre": 0.661451546,
    "G2 Fall_pr_1_99_wre": 43,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 566,
    "G2 Winter_theta_wre": 0.970928422,
    "G2 Winter_pr_1_99_wre": 37,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 597,
    "G2 Spring_theta_wre": 1.250768945,
    "G2 Spring_pr_1_99_wre": 55,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 625,
    "G3 Fall_theta_wre": 1.585509281,
    "G3 Fall_pr_1_99_wre": 54,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 659,
    "G3 Winter_theta_wre": 1.935121508,
    "G3 Winter_pr_1_99_wre": 53,
    "G3 Winter_ss_wre": 101,
    "G3 Winter_dss_wre": 694,
    "G3 Spring_theta_wre": 2.37179126,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 737
  },
  {
    "ID": 269,
    "KG Winter_theta_wre": -0.919291643,
    "KG Winter_pr_1_99_wre": 60,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 408,
    "KG Spring_theta_wre": -0.541648527,
    "KG Spring_pr_1_99_wre": 58,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 446,
    "G1 Fall_theta_wre": -0.257086764,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 474,
    "G1 Winter_theta_wre": -0.02519455,
    "G1 Winter_pr_1_99_wre": 43,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 497,
    "G1 Spring_theta_wre": 0.400216619,
    "G1 Spring_pr_1_99_wre": 50,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 540,
    "G2 Fall_theta_wre": 0.663065667,
    "G2 Fall_pr_1_99_wre": 43,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 566,
    "G2 Winter_theta_wre": 0.973475627,
    "G2 Winter_pr_1_99_wre": 37,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 597,
    "G2 Spring_theta_wre": 1.256307846,
    "G2 Spring_pr_1_99_wre": 57,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 626,
    "G3 Fall_theta_wre": 1.587251128,
    "G3 Fall_pr_1_99_wre": 54,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 659,
    "G3 Winter_theta_wre": 1.94846349,
    "G3 Winter_pr_1_99_wre": 53,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 695,
    "G3 Spring_theta_wre": 2.379222245,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 738
  },
  {
    "ID": 270,
    "KG Winter_theta_wre": -0.918205055,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 408,
    "KG Spring_theta_wre": -0.538616346,
    "KG Spring_pr_1_99_wre": 59,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 446,
    "G1 Fall_theta_wre": -0.2541007,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 475,
    "G1 Winter_theta_wre": -0.021645098,
    "G1 Winter_pr_1_99_wre": 43,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 498,
    "G1 Spring_theta_wre": 0.404425555,
    "G1 Spring_pr_1_99_wre": 50,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 540,
    "G2 Fall_theta_wre": 0.663814196,
    "G2 Fall_pr_1_99_wre": 43,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 566,
    "G2 Winter_theta_wre": 0.976273763,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 598,
    "G2 Spring_theta_wre": 1.257825963,
    "G2 Spring_pr_1_99_wre": 57,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 626,
    "G3 Fall_theta_wre": 1.59734506,
    "G3 Fall_pr_1_99_wre": 55,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 660,
    "G3 Winter_theta_wre": 1.953187193,
    "G3 Winter_pr_1_99_wre": 53,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 695,
    "G3 Spring_theta_wre": 2.380436686,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 738
  },
  {
    "ID": 271,
    "KG Winter_theta_wre": -0.916825473,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 408,
    "KG Spring_theta_wre": -0.537098928,
    "KG Spring_pr_1_99_wre": 59,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 446,
    "G1 Fall_theta_wre": -0.248383291,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 475,
    "G1 Winter_theta_wre": -0.019179905,
    "G1 Winter_pr_1_99_wre": 43,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 498,
    "G1 Spring_theta_wre": 0.406500334,
    "G1 Spring_pr_1_99_wre": 50,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 541,
    "G2 Fall_theta_wre": 0.66573182,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 567,
    "G2 Winter_theta_wre": 0.978706201,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 598,
    "G2 Spring_theta_wre": 1.262362016,
    "G2 Spring_pr_1_99_wre": 57,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 626,
    "G3 Fall_theta_wre": 1.598669586,
    "G3 Fall_pr_1_99_wre": 55,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 660,
    "G3 Winter_theta_wre": 1.95453803,
    "G3 Winter_pr_1_99_wre": 53,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 695,
    "G3 Spring_theta_wre": 2.383215248,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 738
  },
  {
    "ID": 272,
    "KG Winter_theta_wre": -0.915333569,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 408,
    "KG Spring_theta_wre": -0.526581321,
    "KG Spring_pr_1_99_wre": 59,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 447,
    "G1 Fall_theta_wre": -0.243837412,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 476,
    "G1 Winter_theta_wre": -0.014746193,
    "G1 Winter_pr_1_99_wre": 44,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 499,
    "G1 Spring_theta_wre": 0.409648079,
    "G1 Spring_pr_1_99_wre": 50,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 541,
    "G2 Fall_theta_wre": 0.666347449,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 567,
    "G2 Winter_theta_wre": 0.98128074,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 598,
    "G2 Spring_theta_wre": 1.263808434,
    "G2 Spring_pr_1_99_wre": 58,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 626,
    "G3 Fall_theta_wre": 1.599153607,
    "G3 Fall_pr_1_99_wre": 55,
    "G3 Fall_ss_wre": 101,
    "G3 Fall_dss_wre": 660,
    "G3 Winter_theta_wre": 1.957213053,
    "G3 Winter_pr_1_99_wre": 53,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 696,
    "G3 Spring_theta_wre": 2.38471888,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 738
  },
  {
    "ID": 273,
    "KG Winter_theta_wre": -0.905899716,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 409,
    "KG Spring_theta_wre": -0.522649004,
    "KG Spring_pr_1_99_wre": 59,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 448,
    "G1 Fall_theta_wre": -0.240731556,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 476,
    "G1 Winter_theta_wre": -0.010890573,
    "G1 Winter_pr_1_99_wre": 44,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 499,
    "G1 Spring_theta_wre": 0.411193344,
    "G1 Spring_pr_1_99_wre": 50,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 541,
    "G2 Fall_theta_wre": 0.672246774,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 567,
    "G2 Winter_theta_wre": 0.984429089,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 598,
    "G2 Spring_theta_wre": 1.264163892,
    "G2 Spring_pr_1_99_wre": 58,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 626,
    "G3 Fall_theta_wre": 1.611729699,
    "G3 Fall_pr_1_99_wre": 55,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 661,
    "G3 Winter_theta_wre": 1.960686708,
    "G3 Winter_pr_1_99_wre": 53,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 696,
    "G3 Spring_theta_wre": 2.385518929,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 739
  },
  {
    "ID": 274,
    "KG Winter_theta_wre": -0.904451689,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 410,
    "KG Spring_theta_wre": -0.520683638,
    "KG Spring_pr_1_99_wre": 59,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 448,
    "G1 Fall_theta_wre": -0.234412388,
    "G1 Fall_pr_1_99_wre": 54,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 477,
    "G1 Winter_theta_wre": -0.005724507,
    "G1 Winter_pr_1_99_wre": 44,
    "G1 Winter_ss_wre": 101,
    "G1 Winter_dss_wre": 499,
    "G1 Spring_theta_wre": 0.416133059,
    "G1 Spring_pr_1_99_wre": 50,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 542,
    "G2 Fall_theta_wre": 0.67285521,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 567,
    "G2 Winter_theta_wre": 0.993142009,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 101,
    "G2 Winter_dss_wre": 599,
    "G2 Spring_theta_wre": 1.26994453,
    "G2 Spring_pr_1_99_wre": 58,
    "G2 Spring_ss_wre": 101,
    "G2 Spring_dss_wre": 627,
    "G3 Fall_theta_wre": 1.617226861,
    "G3 Fall_pr_1_99_wre": 55,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 662,
    "G3 Winter_theta_wre": 1.961762676,
    "G3 Winter_pr_1_99_wre": 53,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 696,
    "G3 Spring_theta_wre": 2.387229364,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 739
  },
  {
    "ID": 275,
    "KG Winter_theta_wre": -0.900669614,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 101,
    "KG Winter_dss_wre": 410,
    "KG Spring_theta_wre": -0.515421949,
    "KG Spring_pr_1_99_wre": 59,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 448,
    "G1 Fall_theta_wre": -0.227742522,
    "G1 Fall_pr_1_99_wre": 55,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 477,
    "G1 Winter_theta_wre": -0.004492276,
    "G1 Winter_pr_1_99_wre": 44,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 500,
    "G1 Spring_theta_wre": 0.430390398,
    "G1 Spring_pr_1_99_wre": 51,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 543,
    "G2 Fall_theta_wre": 0.674130532,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 567,
    "G2 Winter_theta_wre": 1.000750955,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 102,
    "G2 Winter_dss_wre": 600,
    "G2 Spring_theta_wre": 1.274189567,
    "G2 Spring_pr_1_99_wre": 58,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 627,
    "G3 Fall_theta_wre": 1.618776959,
    "G3 Fall_pr_1_99_wre": 55,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 662,
    "G3 Winter_theta_wre": 1.963003065,
    "G3 Winter_pr_1_99_wre": 53,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 696,
    "G3 Spring_theta_wre": 2.388203711,
    "G3 Spring_pr_1_99_wre": 82,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 739
  },
  {
    "ID": 276,
    "KG Winter_theta_wre": -0.888998642,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 411,
    "KG Spring_theta_wre": -0.51119242,
    "KG Spring_pr_1_99_wre": 59,
    "KG Spring_ss_wre": 102,
    "KG Spring_dss_wre": 449,
    "G1 Fall_theta_wre": -0.22582249,
    "G1 Fall_pr_1_99_wre": 55,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 477,
    "G1 Winter_theta_wre": -0.003147318,
    "G1 Winter_pr_1_99_wre": 44,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 500,
    "G1 Spring_theta_wre": 0.431250625,
    "G1 Spring_pr_1_99_wre": 51,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 543,
    "G2 Fall_theta_wre": 0.675697697,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 568,
    "G2 Winter_theta_wre": 1.009759392,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 102,
    "G2 Winter_dss_wre": 601,
    "G2 Spring_theta_wre": 1.275623039,
    "G2 Spring_pr_1_99_wre": 58,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 628,
    "G3 Fall_theta_wre": 1.619083004,
    "G3 Fall_pr_1_99_wre": 55,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 662,
    "G3 Winter_theta_wre": 1.968043147,
    "G3 Winter_pr_1_99_wre": 53,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 697,
    "G3 Spring_theta_wre": 2.392794452,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 739
  },
  {
    "ID": 277,
    "KG Winter_theta_wre": -0.888698797,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 411,
    "KG Spring_theta_wre": -0.498167938,
    "KG Spring_pr_1_99_wre": 60,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 450,
    "G1 Fall_theta_wre": -0.22204011,
    "G1 Fall_pr_1_99_wre": 55,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 478,
    "G1 Winter_theta_wre": -0.00046974,
    "G1 Winter_pr_1_99_wre": 44,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 500,
    "G1 Spring_theta_wre": 0.433242365,
    "G1 Spring_pr_1_99_wre": 51,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 543,
    "G2 Fall_theta_wre": 0.678478493,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 568,
    "G2 Winter_theta_wre": 1.010593926,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 102,
    "G2 Winter_dss_wre": 601,
    "G2 Spring_theta_wre": 1.281025309,
    "G2 Spring_pr_1_99_wre": 58,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 628,
    "G3 Fall_theta_wre": 1.626791514,
    "G3 Fall_pr_1_99_wre": 56,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 663,
    "G3 Winter_theta_wre": 1.969048807,
    "G3 Winter_pr_1_99_wre": 53,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 697,
    "G3 Spring_theta_wre": 2.394349664,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 103,
    "G3 Spring_dss_wre": 739
  },
  {
    "ID": 278,
    "KG Winter_theta_wre": -0.886296764,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 411,
    "KG Spring_theta_wre": -0.492848391,
    "KG Spring_pr_1_99_wre": 60,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 451,
    "G1 Fall_theta_wre": -0.221467598,
    "G1 Fall_pr_1_99_wre": 55,
    "G1 Fall_ss_wre": 101,
    "G1 Fall_dss_wre": 478,
    "G1 Winter_theta_wre": 0.00862884,
    "G1 Winter_pr_1_99_wre": 45,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 501,
    "G1 Spring_theta_wre": 0.433913603,
    "G1 Spring_pr_1_99_wre": 51,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 543,
    "G2 Fall_theta_wre": 0.681177874,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 568,
    "G2 Winter_theta_wre": 1.012061298,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 102,
    "G2 Winter_dss_wre": 601,
    "G2 Spring_theta_wre": 1.281076243,
    "G2 Spring_pr_1_99_wre": 58,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 628,
    "G3 Fall_theta_wre": 1.626812983,
    "G3 Fall_pr_1_99_wre": 56,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 663,
    "G3 Winter_theta_wre": 1.97380168,
    "G3 Winter_pr_1_99_wre": 54,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 697,
    "G3 Spring_theta_wre": 2.407621227,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 741
  },
  {
    "ID": 279,
    "KG Winter_theta_wre": -0.885759151,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 411,
    "KG Spring_theta_wre": -0.492787685,
    "KG Spring_pr_1_99_wre": 60,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 451,
    "G1 Fall_theta_wre": -0.212859224,
    "G1 Fall_pr_1_99_wre": 55,
    "G1 Fall_ss_wre": 102,
    "G1 Fall_dss_wre": 479,
    "G1 Winter_theta_wre": 0.012460113,
    "G1 Winter_pr_1_99_wre": 45,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 501,
    "G1 Spring_theta_wre": 0.43471703,
    "G1 Spring_pr_1_99_wre": 51,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 543,
    "G2 Fall_theta_wre": 0.689608712,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 569,
    "G2 Winter_theta_wre": 1.023472377,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 102,
    "G2 Winter_dss_wre": 602,
    "G2 Spring_theta_wre": 1.28274991,
    "G2 Spring_pr_1_99_wre": 58,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 628,
    "G3 Fall_theta_wre": 1.631614922,
    "G3 Fall_pr_1_99_wre": 56,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 663,
    "G3 Winter_theta_wre": 1.979431528,
    "G3 Winter_pr_1_99_wre": 54,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 698,
    "G3 Spring_theta_wre": 2.414674302,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 741
  },
  {
    "ID": 280,
    "KG Winter_theta_wre": -0.867380719,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 413,
    "KG Spring_theta_wre": -0.488606335,
    "KG Spring_pr_1_99_wre": 60,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 451,
    "G1 Fall_theta_wre": -0.193017284,
    "G1 Fall_pr_1_99_wre": 56,
    "G1 Fall_ss_wre": 102,
    "G1 Fall_dss_wre": 481,
    "G1 Winter_theta_wre": 0.024389929,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 502,
    "G1 Spring_theta_wre": 0.435142183,
    "G1 Spring_pr_1_99_wre": 51,
    "G1 Spring_ss_wre": 102,
    "G1 Spring_dss_wre": 544,
    "G2 Fall_theta_wre": 0.699920518,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 570,
    "G2 Winter_theta_wre": 1.025911009,
    "G2 Winter_pr_1_99_wre": 38,
    "G2 Winter_ss_wre": 102,
    "G2 Winter_dss_wre": 603,
    "G2 Spring_theta_wre": 1.286325328,
    "G2 Spring_pr_1_99_wre": 59,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 629,
    "G3 Fall_theta_wre": 1.634500662,
    "G3 Fall_pr_1_99_wre": 56,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 663,
    "G3 Winter_theta_wre": 1.983449897,
    "G3 Winter_pr_1_99_wre": 54,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 698,
    "G3 Spring_theta_wre": 2.416337063,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 742
  },
  {
    "ID": 281,
    "KG Winter_theta_wre": -0.863992814,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 414,
    "KG Spring_theta_wre": -0.482266891,
    "KG Spring_pr_1_99_wre": 60,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 452,
    "G1 Fall_theta_wre": -0.191531061,
    "G1 Fall_pr_1_99_wre": 56,
    "G1 Fall_ss_wre": 102,
    "G1 Fall_dss_wre": 481,
    "G1 Winter_theta_wre": 0.025509844,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 503,
    "G1 Spring_theta_wre": 0.448225858,
    "G1 Spring_pr_1_99_wre": 52,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 545,
    "G2 Fall_theta_wre": 0.703214766,
    "G2 Fall_pr_1_99_wre": 44,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 570,
    "G2 Winter_theta_wre": 1.036552463,
    "G2 Winter_pr_1_99_wre": 39,
    "G2 Winter_ss_wre": 102,
    "G2 Winter_dss_wre": 604,
    "G2 Spring_theta_wre": 1.290170213,
    "G2 Spring_pr_1_99_wre": 59,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 629,
    "G3 Fall_theta_wre": 1.634660704,
    "G3 Fall_pr_1_99_wre": 56,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 663,
    "G3 Winter_theta_wre": 1.98507227,
    "G3 Winter_pr_1_99_wre": 54,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 699,
    "G3 Spring_theta_wre": 2.416337776,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 742
  },
  {
    "ID": 282,
    "KG Winter_theta_wre": -0.861612941,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 414,
    "KG Spring_theta_wre": -0.479819682,
    "KG Spring_pr_1_99_wre": 61,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 452,
    "G1 Fall_theta_wre": -0.181906655,
    "G1 Fall_pr_1_99_wre": 56,
    "G1 Fall_ss_wre": 102,
    "G1 Fall_dss_wre": 482,
    "G1 Winter_theta_wre": 0.025589201,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 503,
    "G1 Spring_theta_wre": 0.450263021,
    "G1 Spring_pr_1_99_wre": 52,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 545,
    "G2 Fall_theta_wre": 0.708967035,
    "G2 Fall_pr_1_99_wre": 45,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 571,
    "G2 Winter_theta_wre": 1.048211789,
    "G2 Winter_pr_1_99_wre": 39,
    "G2 Winter_ss_wre": 102,
    "G2 Winter_dss_wre": 605,
    "G2 Spring_theta_wre": 1.291279138,
    "G2 Spring_pr_1_99_wre": 59,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 629,
    "G3 Fall_theta_wre": 1.635092809,
    "G3 Fall_pr_1_99_wre": 56,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 664,
    "G3 Winter_theta_wre": 1.985573195,
    "G3 Winter_pr_1_99_wre": 54,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 699,
    "G3 Spring_theta_wre": 2.420155209,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 742
  },
  {
    "ID": 283,
    "KG Winter_theta_wre": -0.861178026,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 414,
    "KG Spring_theta_wre": -0.479796453,
    "KG Spring_pr_1_99_wre": 61,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 452,
    "G1 Fall_theta_wre": -0.180974929,
    "G1 Fall_pr_1_99_wre": 56,
    "G1 Fall_ss_wre": 102,
    "G1 Fall_dss_wre": 482,
    "G1 Winter_theta_wre": 0.026151651,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 503,
    "G1 Spring_theta_wre": 0.451626942,
    "G1 Spring_pr_1_99_wre": 52,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 545,
    "G2 Fall_theta_wre": 0.711793247,
    "G2 Fall_pr_1_99_wre": 46,
    "G2 Fall_ss_wre": 102,
    "G2 Fall_dss_wre": 571,
    "G2 Winter_theta_wre": 1.055521747,
    "G2 Winter_pr_1_99_wre": 40,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 606,
    "G2 Spring_theta_wre": 1.29173699,
    "G2 Spring_pr_1_99_wre": 59,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 629,
    "G3 Fall_theta_wre": 1.637447395,
    "G3 Fall_pr_1_99_wre": 56,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 664,
    "G3 Winter_theta_wre": 1.99596934,
    "G3 Winter_pr_1_99_wre": 54,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 700,
    "G3 Spring_theta_wre": 2.426367543,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 743
  },
  {
    "ID": 284,
    "KG Winter_theta_wre": -0.854723322,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 415,
    "KG Spring_theta_wre": -0.471329427,
    "KG Spring_pr_1_99_wre": 61,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 453,
    "G1 Fall_theta_wre": -0.167612584,
    "G1 Fall_pr_1_99_wre": 56,
    "G1 Fall_ss_wre": 102,
    "G1 Fall_dss_wre": 483,
    "G1 Winter_theta_wre": 0.027928055,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 503,
    "G1 Spring_theta_wre": 0.453020727,
    "G1 Spring_pr_1_99_wre": 52,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 545,
    "G2 Fall_theta_wre": 0.723668272,
    "G2 Fall_pr_1_99_wre": 47,
    "G2 Fall_ss_wre": 103,
    "G2 Fall_dss_wre": 572,
    "G2 Winter_theta_wre": 1.065677387,
    "G2 Winter_pr_1_99_wre": 40,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 607,
    "G2 Spring_theta_wre": 1.309205108,
    "G2 Spring_pr_1_99_wre": 60,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 631,
    "G3 Fall_theta_wre": 1.643659527,
    "G3 Fall_pr_1_99_wre": 56,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 664,
    "G3 Winter_theta_wre": 1.997389108,
    "G3 Winter_pr_1_99_wre": 54,
    "G3 Winter_ss_wre": 102,
    "G3 Winter_dss_wre": 700,
    "G3 Spring_theta_wre": 2.434327808,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 743
  },
  {
    "ID": 285,
    "KG Winter_theta_wre": -0.854592718,
    "KG Winter_pr_1_99_wre": 61,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 415,
    "KG Spring_theta_wre": -0.464730446,
    "KG Spring_pr_1_99_wre": 61,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 454,
    "G1 Fall_theta_wre": -0.161847648,
    "G1 Fall_pr_1_99_wre": 57,
    "G1 Fall_ss_wre": 103,
    "G1 Fall_dss_wre": 484,
    "G1 Winter_theta_wre": 0.029821966,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 503,
    "G1 Spring_theta_wre": 0.460283984,
    "G1 Spring_pr_1_99_wre": 52,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 546,
    "G2 Fall_theta_wre": 0.727302795,
    "G2 Fall_pr_1_99_wre": 47,
    "G2 Fall_ss_wre": 103,
    "G2 Fall_dss_wre": 573,
    "G2 Winter_theta_wre": 1.070211379,
    "G2 Winter_pr_1_99_wre": 40,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 607,
    "G2 Spring_theta_wre": 1.318909805,
    "G2 Spring_pr_1_99_wre": 60,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 632,
    "G3 Fall_theta_wre": 1.644426205,
    "G3 Fall_pr_1_99_wre": 56,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 664,
    "G3 Winter_theta_wre": 1.999602705,
    "G3 Winter_pr_1_99_wre": 54,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 700,
    "G3 Spring_theta_wre": 2.437456864,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 744
  },
  {
    "ID": 286,
    "KG Winter_theta_wre": -0.849446496,
    "KG Winter_pr_1_99_wre": 62,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 415,
    "KG Spring_theta_wre": -0.464097658,
    "KG Spring_pr_1_99_wre": 61,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 454,
    "G1 Fall_theta_wre": -0.15963557,
    "G1 Fall_pr_1_99_wre": 57,
    "G1 Fall_ss_wre": 103,
    "G1 Fall_dss_wre": 484,
    "G1 Winter_theta_wre": 0.035184895,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 504,
    "G1 Spring_theta_wre": 0.46141495,
    "G1 Spring_pr_1_99_wre": 52,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 546,
    "G2 Fall_theta_wre": 0.733537118,
    "G2 Fall_pr_1_99_wre": 48,
    "G2 Fall_ss_wre": 103,
    "G2 Fall_dss_wre": 573,
    "G2 Winter_theta_wre": 1.076303056,
    "G2 Winter_pr_1_99_wre": 40,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 608,
    "G2 Spring_theta_wre": 1.322128073,
    "G2 Spring_pr_1_99_wre": 61,
    "G2 Spring_ss_wre": 102,
    "G2 Spring_dss_wre": 632,
    "G3 Fall_theta_wre": 1.644593894,
    "G3 Fall_pr_1_99_wre": 56,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 664,
    "G3 Winter_theta_wre": 2.010155469,
    "G3 Winter_pr_1_99_wre": 54,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 701,
    "G3 Spring_theta_wre": 2.444575577,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 744
  },
  {
    "ID": 287,
    "KG Winter_theta_wre": -0.843749596,
    "KG Winter_pr_1_99_wre": 62,
    "KG Winter_ss_wre": 102,
    "KG Winter_dss_wre": 416,
    "KG Spring_theta_wre": -0.462213174,
    "KG Spring_pr_1_99_wre": 61,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 454,
    "G1 Fall_theta_wre": -0.157000897,
    "G1 Fall_pr_1_99_wre": 57,
    "G1 Fall_ss_wre": 103,
    "G1 Fall_dss_wre": 484,
    "G1 Winter_theta_wre": 0.040964774,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 504,
    "G1 Spring_theta_wre": 0.462806433,
    "G1 Spring_pr_1_99_wre": 52,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 546,
    "G2 Fall_theta_wre": 0.733724067,
    "G2 Fall_pr_1_99_wre": 48,
    "G2 Fall_ss_wre": 103,
    "G2 Fall_dss_wre": 573,
    "G2 Winter_theta_wre": 1.085141405,
    "G2 Winter_pr_1_99_wre": 40,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 609,
    "G2 Spring_theta_wre": 1.333916907,
    "G2 Spring_pr_1_99_wre": 61,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 633,
    "G3 Fall_theta_wre": 1.648816138,
    "G3 Fall_pr_1_99_wre": 57,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 665,
    "G3 Winter_theta_wre": 2.025138846,
    "G3 Winter_pr_1_99_wre": 55,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 703,
    "G3 Spring_theta_wre": 2.446942136,
    "G3 Spring_pr_1_99_wre": 83,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 745
  },
  {
    "ID": 288,
    "KG Winter_theta_wre": -0.838116819,
    "KG Winter_pr_1_99_wre": 62,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 416,
    "KG Spring_theta_wre": -0.45739023,
    "KG Spring_pr_1_99_wre": 62,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 454,
    "G1 Fall_theta_wre": -0.15448595,
    "G1 Fall_pr_1_99_wre": 57,
    "G1 Fall_ss_wre": 103,
    "G1 Fall_dss_wre": 485,
    "G1 Winter_theta_wre": 0.04177408,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 504,
    "G1 Spring_theta_wre": 0.463741783,
    "G1 Spring_pr_1_99_wre": 52,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 546,
    "G2 Fall_theta_wre": 0.733784186,
    "G2 Fall_pr_1_99_wre": 48,
    "G2 Fall_ss_wre": 103,
    "G2 Fall_dss_wre": 573,
    "G2 Winter_theta_wre": 1.088651836,
    "G2 Winter_pr_1_99_wre": 40,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 609,
    "G2 Spring_theta_wre": 1.343565484,
    "G2 Spring_pr_1_99_wre": 61,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 634,
    "G3 Fall_theta_wre": 1.656713205,
    "G3 Fall_pr_1_99_wre": 57,
    "G3 Fall_ss_wre": 102,
    "G3 Fall_dss_wre": 666,
    "G3 Winter_theta_wre": 2.028653374,
    "G3 Winter_pr_1_99_wre": 55,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 703,
    "G3 Spring_theta_wre": 2.450829326,
    "G3 Spring_pr_1_99_wre": 84,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 745
  },
  {
    "ID": 289,
    "KG Winter_theta_wre": -0.833083947,
    "KG Winter_pr_1_99_wre": 62,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 417,
    "KG Spring_theta_wre": -0.456002597,
    "KG Spring_pr_1_99_wre": 62,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 454,
    "G1 Fall_theta_wre": -0.14511178,
    "G1 Fall_pr_1_99_wre": 57,
    "G1 Fall_ss_wre": 103,
    "G1 Fall_dss_wre": 485,
    "G1 Winter_theta_wre": 0.046556053,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 102,
    "G1 Winter_dss_wre": 505,
    "G1 Spring_theta_wre": 0.466990105,
    "G1 Spring_pr_1_99_wre": 52,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 547,
    "G2 Fall_theta_wre": 0.735693103,
    "G2 Fall_pr_1_99_wre": 48,
    "G2 Fall_ss_wre": 103,
    "G2 Fall_dss_wre": 574,
    "G2 Winter_theta_wre": 1.093379188,
    "G2 Winter_pr_1_99_wre": 41,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 609,
    "G2 Spring_theta_wre": 1.353476274,
    "G2 Spring_pr_1_99_wre": 62,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 635,
    "G3 Fall_theta_wre": 1.665355314,
    "G3 Fall_pr_1_99_wre": 57,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 667,
    "G3 Winter_theta_wre": 2.031227247,
    "G3 Winter_pr_1_99_wre": 55,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 703,
    "G3 Spring_theta_wre": 2.452392729,
    "G3 Spring_pr_1_99_wre": 84,
    "G3 Spring_ss_wre": 104,
    "G3 Spring_dss_wre": 745
  },
  {
    "ID": 290,
    "KG Winter_theta_wre": -0.831140961,
    "KG Winter_pr_1_99_wre": 63,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 417,
    "KG Spring_theta_wre": -0.451494783,
    "KG Spring_pr_1_99_wre": 62,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 455,
    "G1 Fall_theta_wre": -0.141273725,
    "G1 Fall_pr_1_99_wre": 57,
    "G1 Fall_ss_wre": 103,
    "G1 Fall_dss_wre": 486,
    "G1 Winter_theta_wre": 0.047608683,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 505,
    "G1 Spring_theta_wre": 0.470595892,
    "G1 Spring_pr_1_99_wre": 52,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 547,
    "G2 Fall_theta_wre": 0.741534031,
    "G2 Fall_pr_1_99_wre": 48,
    "G2 Fall_ss_wre": 103,
    "G2 Fall_dss_wre": 574,
    "G2 Winter_theta_wre": 1.099312824,
    "G2 Winter_pr_1_99_wre": 42,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 610,
    "G2 Spring_theta_wre": 1.353769878,
    "G2 Spring_pr_1_99_wre": 62,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 635,
    "G3 Fall_theta_wre": 1.676702747,
    "G3 Fall_pr_1_99_wre": 57,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 668,
    "G3 Winter_theta_wre": 2.032105039,
    "G3 Winter_pr_1_99_wre": 55,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 703,
    "G3 Spring_theta_wre": 2.471019455,
    "G3 Spring_pr_1_99_wre": 84,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 747
  },
  {
    "ID": 291,
    "KG Winter_theta_wre": -0.818394724,
    "KG Winter_pr_1_99_wre": 64,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 418,
    "KG Spring_theta_wre": -0.447112727,
    "KG Spring_pr_1_99_wre": 62,
    "KG Spring_ss_wre": 103,
    "KG Spring_dss_wre": 455,
    "G1 Fall_theta_wre": -0.128314037,
    "G1 Fall_pr_1_99_wre": 58,
    "G1 Fall_ss_wre": 103,
    "G1 Fall_dss_wre": 487,
    "G1 Winter_theta_wre": 0.049825854,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 505,
    "G1 Spring_theta_wre": 0.481738605,
    "G1 Spring_pr_1_99_wre": 53,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 548,
    "G2 Fall_theta_wre": 0.749964699,
    "G2 Fall_pr_1_99_wre": 48,
    "G2 Fall_ss_wre": 103,
    "G2 Fall_dss_wre": 575,
    "G2 Winter_theta_wre": 1.100068005,
    "G2 Winter_pr_1_99_wre": 42,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 610,
    "G2 Spring_theta_wre": 1.355835867,
    "G2 Spring_pr_1_99_wre": 62,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 636,
    "G3 Fall_theta_wre": 1.683683368,
    "G3 Fall_pr_1_99_wre": 57,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 668,
    "G3 Winter_theta_wre": 2.034644408,
    "G3 Winter_pr_1_99_wre": 55,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 703,
    "G3 Spring_theta_wre": 2.472784295,
    "G3 Spring_pr_1_99_wre": 84,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 747
  },
  {
    "ID": 292,
    "KG Winter_theta_wre": -0.813123652,
    "KG Winter_pr_1_99_wre": 64,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 419,
    "KG Spring_theta_wre": -0.445271027,
    "KG Spring_pr_1_99_wre": 62,
    "KG Spring_ss_wre": 104,
    "KG Spring_dss_wre": 455,
    "G1 Fall_theta_wre": -0.125501622,
    "G1 Fall_pr_1_99_wre": 58,
    "G1 Fall_ss_wre": 103,
    "G1 Fall_dss_wre": 487,
    "G1 Winter_theta_wre": 0.049841144,
    "G1 Winter_pr_1_99_wre": 46,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 505,
    "G1 Spring_theta_wre": 0.482948643,
    "G1 Spring_pr_1_99_wre": 53,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 548,
    "G2 Fall_theta_wre": 0.754122106,
    "G2 Fall_pr_1_99_wre": 48,
    "G2 Fall_ss_wre": 103,
    "G2 Fall_dss_wre": 575,
    "G2 Winter_theta_wre": 1.100136599,
    "G2 Winter_pr_1_99_wre": 42,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 610,
    "G2 Spring_theta_wre": 1.360434746,
    "G2 Spring_pr_1_99_wre": 62,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 636,
    "G3 Fall_theta_wre": 1.68614844,
    "G3 Fall_pr_1_99_wre": 57,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 669,
    "G3 Winter_theta_wre": 2.041246177,
    "G3 Winter_pr_1_99_wre": 55,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 704,
    "G3 Spring_theta_wre": 2.475842969,
    "G3 Spring_pr_1_99_wre": 84,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 748
  },
  {
    "ID": 293,
    "KG Winter_theta_wre": -0.811784874,
    "KG Winter_pr_1_99_wre": 64,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 419,
    "KG Spring_theta_wre": -0.442183506,
    "KG Spring_pr_1_99_wre": 63,
    "KG Spring_ss_wre": 104,
    "KG Spring_dss_wre": 456,
    "G1 Fall_theta_wre": -0.104620908,
    "G1 Fall_pr_1_99_wre": 59,
    "G1 Fall_ss_wre": 104,
    "G1 Fall_dss_wre": 490,
    "G1 Winter_theta_wre": 0.058522886,
    "G1 Winter_pr_1_99_wre": 47,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 506,
    "G1 Spring_theta_wre": 0.483258199,
    "G1 Spring_pr_1_99_wre": 53,
    "G1 Spring_ss_wre": 103,
    "G1 Spring_dss_wre": 548,
    "G2 Fall_theta_wre": 0.757091778,
    "G2 Fall_pr_1_99_wre": 48,
    "G2 Fall_ss_wre": 103,
    "G2 Fall_dss_wre": 576,
    "G2 Winter_theta_wre": 1.102537219,
    "G2 Winter_pr_1_99_wre": 42,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 610,
    "G2 Spring_theta_wre": 1.362340829,
    "G2 Spring_pr_1_99_wre": 62,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 636,
    "G3 Fall_theta_wre": 1.690576121,
    "G3 Fall_pr_1_99_wre": 57,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 669,
    "G3 Winter_theta_wre": 2.043943822,
    "G3 Winter_pr_1_99_wre": 55,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 704,
    "G3 Spring_theta_wre": 2.476138757,
    "G3 Spring_pr_1_99_wre": 84,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 748
  },
  {
    "ID": 294,
    "KG Winter_theta_wre": -0.802897894,
    "KG Winter_pr_1_99_wre": 64,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 420,
    "KG Spring_theta_wre": -0.43754395,
    "KG Spring_pr_1_99_wre": 63,
    "KG Spring_ss_wre": 104,
    "KG Spring_dss_wre": 456,
    "G1 Fall_theta_wre": -0.09756541,
    "G1 Fall_pr_1_99_wre": 59,
    "G1 Fall_ss_wre": 104,
    "G1 Fall_dss_wre": 490,
    "G1 Winter_theta_wre": 0.061189126,
    "G1 Winter_pr_1_99_wre": 47,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 506,
    "G1 Spring_theta_wre": 0.490057206,
    "G1 Spring_pr_1_99_wre": 54,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 549,
    "G2 Fall_theta_wre": 0.770554547,
    "G2 Fall_pr_1_99_wre": 49,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 577,
    "G2 Winter_theta_wre": 1.103056555,
    "G2 Winter_pr_1_99_wre": 43,
    "G2 Winter_ss_wre": 103,
    "G2 Winter_dss_wre": 610,
    "G2 Spring_theta_wre": 1.362674644,
    "G2 Spring_pr_1_99_wre": 62,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 636,
    "G3 Fall_theta_wre": 1.695491106,
    "G3 Fall_pr_1_99_wre": 57,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 670,
    "G3 Winter_theta_wre": 2.044380166,
    "G3 Winter_pr_1_99_wre": 55,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 704,
    "G3 Spring_theta_wre": 2.477776391,
    "G3 Spring_pr_1_99_wre": 84,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 748
  },
  {
    "ID": 295,
    "KG Winter_theta_wre": -0.800650651,
    "KG Winter_pr_1_99_wre": 64,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 420,
    "KG Spring_theta_wre": -0.43674293,
    "KG Spring_pr_1_99_wre": 63,
    "KG Spring_ss_wre": 104,
    "KG Spring_dss_wre": 456,
    "G1 Fall_theta_wre": -0.093022259,
    "G1 Fall_pr_1_99_wre": 59,
    "G1 Fall_ss_wre": 104,
    "G1 Fall_dss_wre": 491,
    "G1 Winter_theta_wre": 0.061963069,
    "G1 Winter_pr_1_99_wre": 47,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 506,
    "G1 Spring_theta_wre": 0.492172842,
    "G1 Spring_pr_1_99_wre": 54,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 549,
    "G2 Fall_theta_wre": 0.776714327,
    "G2 Fall_pr_1_99_wre": 49,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 578,
    "G2 Winter_theta_wre": 1.107062043,
    "G2 Winter_pr_1_99_wre": 43,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 611,
    "G2 Spring_theta_wre": 1.367062673,
    "G2 Spring_pr_1_99_wre": 62,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 637,
    "G3 Fall_theta_wre": 1.699218593,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 670,
    "G3 Winter_theta_wre": 2.045273356,
    "G3 Winter_pr_1_99_wre": 55,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 705,
    "G3 Spring_theta_wre": 2.481029233,
    "G3 Spring_pr_1_99_wre": 84,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 748
  },
  {
    "ID": 296,
    "KG Winter_theta_wre": -0.793635335,
    "KG Winter_pr_1_99_wre": 65,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 421,
    "KG Spring_theta_wre": -0.42941116,
    "KG Spring_pr_1_99_wre": 63,
    "KG Spring_ss_wre": 104,
    "KG Spring_dss_wre": 457,
    "G1 Fall_theta_wre": -0.084443928,
    "G1 Fall_pr_1_99_wre": 59,
    "G1 Fall_ss_wre": 104,
    "G1 Fall_dss_wre": 492,
    "G1 Winter_theta_wre": 0.069466794,
    "G1 Winter_pr_1_99_wre": 48,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 507,
    "G1 Spring_theta_wre": 0.49543259,
    "G1 Spring_pr_1_99_wre": 54,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 550,
    "G2 Fall_theta_wre": 0.781926374,
    "G2 Fall_pr_1_99_wre": 49,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 578,
    "G2 Winter_theta_wre": 1.117895883,
    "G2 Winter_pr_1_99_wre": 43,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 612,
    "G2 Spring_theta_wre": 1.368124534,
    "G2 Spring_pr_1_99_wre": 62,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 637,
    "G3 Fall_theta_wre": 1.701282535,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 670,
    "G3 Winter_theta_wre": 2.047198985,
    "G3 Winter_pr_1_99_wre": 55,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 705,
    "G3 Spring_theta_wre": 2.483162426,
    "G3 Spring_pr_1_99_wre": 84,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 748
  },
  {
    "ID": 297,
    "KG Winter_theta_wre": -0.793593709,
    "KG Winter_pr_1_99_wre": 65,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 421,
    "KG Spring_theta_wre": -0.420980895,
    "KG Spring_pr_1_99_wre": 64,
    "KG Spring_ss_wre": 104,
    "KG Spring_dss_wre": 458,
    "G1 Fall_theta_wre": -0.079481665,
    "G1 Fall_pr_1_99_wre": 60,
    "G1 Fall_ss_wre": 104,
    "G1 Fall_dss_wre": 492,
    "G1 Winter_theta_wre": 0.071809493,
    "G1 Winter_pr_1_99_wre": 48,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 507,
    "G1 Spring_theta_wre": 0.500146575,
    "G1 Spring_pr_1_99_wre": 55,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 550,
    "G2 Fall_theta_wre": 0.783094149,
    "G2 Fall_pr_1_99_wre": 49,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 578,
    "G2 Winter_theta_wre": 1.11869292,
    "G2 Winter_pr_1_99_wre": 43,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 612,
    "G2 Spring_theta_wre": 1.37604182,
    "G2 Spring_pr_1_99_wre": 62,
    "G2 Spring_ss_wre": 103,
    "G2 Spring_dss_wre": 638,
    "G3 Fall_theta_wre": 1.702750894,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 670,
    "G3 Winter_theta_wre": 2.047564345,
    "G3 Winter_pr_1_99_wre": 56,
    "G3 Winter_ss_wre": 103,
    "G3 Winter_dss_wre": 705,
    "G3 Spring_theta_wre": 2.485342678,
    "G3 Spring_pr_1_99_wre": 84,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 749
  },
  {
    "ID": 298,
    "KG Winter_theta_wre": -0.793508472,
    "KG Winter_pr_1_99_wre": 65,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 421,
    "KG Spring_theta_wre": -0.415018086,
    "KG Spring_pr_1_99_wre": 64,
    "KG Spring_ss_wre": 104,
    "KG Spring_dss_wre": 458,
    "G1 Fall_theta_wre": -0.078473484,
    "G1 Fall_pr_1_99_wre": 60,
    "G1 Fall_ss_wre": 104,
    "G1 Fall_dss_wre": 492,
    "G1 Winter_theta_wre": 0.072330588,
    "G1 Winter_pr_1_99_wre": 48,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 507,
    "G1 Spring_theta_wre": 0.501098453,
    "G1 Spring_pr_1_99_wre": 55,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 550,
    "G2 Fall_theta_wre": 0.783321346,
    "G2 Fall_pr_1_99_wre": 49,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 578,
    "G2 Winter_theta_wre": 1.118888222,
    "G2 Winter_pr_1_99_wre": 43,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 612,
    "G2 Spring_theta_wre": 1.383676821,
    "G2 Spring_pr_1_99_wre": 63,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 638,
    "G3 Fall_theta_wre": 1.705775914,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 671,
    "G3 Winter_theta_wre": 2.055222172,
    "G3 Winter_pr_1_99_wre": 57,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 706,
    "G3 Spring_theta_wre": 2.492905204,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 749
  },
  {
    "ID": 299,
    "KG Winter_theta_wre": -0.790184061,
    "KG Winter_pr_1_99_wre": 65,
    "KG Winter_ss_wre": 103,
    "KG Winter_dss_wre": 421,
    "KG Spring_theta_wre": -0.407499563,
    "KG Spring_pr_1_99_wre": 64,
    "KG Spring_ss_wre": 104,
    "KG Spring_dss_wre": 459,
    "G1 Fall_theta_wre": -0.065171092,
    "G1 Fall_pr_1_99_wre": 60,
    "G1 Fall_ss_wre": 104,
    "G1 Fall_dss_wre": 493,
    "G1 Winter_theta_wre": 0.074890869,
    "G1 Winter_pr_1_99_wre": 48,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 507,
    "G1 Spring_theta_wre": 0.510584471,
    "G1 Spring_pr_1_99_wre": 55,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 551,
    "G2 Fall_theta_wre": 0.787623166,
    "G2 Fall_pr_1_99_wre": 50,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 579,
    "G2 Winter_theta_wre": 1.120989688,
    "G2 Winter_pr_1_99_wre": 43,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 612,
    "G2 Spring_theta_wre": 1.387014837,
    "G2 Spring_pr_1_99_wre": 63,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 639,
    "G3 Fall_theta_wre": 1.709005208,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 671,
    "G3 Winter_theta_wre": 2.056159861,
    "G3 Winter_pr_1_99_wre": 57,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 706,
    "G3 Spring_theta_wre": 2.493608587,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 749
  },
  {
    "ID": 300,
    "KG Winter_theta_wre": -0.780991082,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 104,
    "KG Winter_dss_wre": 422,
    "KG Spring_theta_wre": -0.388855191,
    "KG Spring_pr_1_99_wre": 65,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 461,
    "G1 Fall_theta_wre": -0.05093519,
    "G1 Fall_pr_1_99_wre": 61,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 495,
    "G1 Winter_theta_wre": 0.086061267,
    "G1 Winter_pr_1_99_wre": 48,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 509,
    "G1 Spring_theta_wre": 0.511007019,
    "G1 Spring_pr_1_99_wre": 55,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 551,
    "G2 Fall_theta_wre": 0.790860538,
    "G2 Fall_pr_1_99_wre": 50,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 579,
    "G2 Winter_theta_wre": 1.121277749,
    "G2 Winter_pr_1_99_wre": 43,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 612,
    "G2 Spring_theta_wre": 1.389756078,
    "G2 Spring_pr_1_99_wre": 63,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 639,
    "G3 Fall_theta_wre": 1.711060719,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 103,
    "G3 Fall_dss_wre": 671,
    "G3 Winter_theta_wre": 2.056874501,
    "G3 Winter_pr_1_99_wre": 57,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 706,
    "G3 Spring_theta_wre": 2.495753404,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 750
  },
  {
    "ID": 301,
    "KG Winter_theta_wre": -0.780500478,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 104,
    "KG Winter_dss_wre": 422,
    "KG Spring_theta_wre": -0.380584024,
    "KG Spring_pr_1_99_wre": 65,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 462,
    "G1 Fall_theta_wre": -0.04919594,
    "G1 Fall_pr_1_99_wre": 61,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 495,
    "G1 Winter_theta_wre": 0.093683,
    "G1 Winter_pr_1_99_wre": 49,
    "G1 Winter_ss_wre": 103,
    "G1 Winter_dss_wre": 509,
    "G1 Spring_theta_wre": 0.511022371,
    "G1 Spring_pr_1_99_wre": 55,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 551,
    "G2 Fall_theta_wre": 0.791500551,
    "G2 Fall_pr_1_99_wre": 50,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 579,
    "G2 Winter_theta_wre": 1.127761672,
    "G2 Winter_pr_1_99_wre": 44,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 613,
    "G2 Spring_theta_wre": 1.39234366,
    "G2 Spring_pr_1_99_wre": 63,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 639,
    "G3 Fall_theta_wre": 1.718467678,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 672,
    "G3 Winter_theta_wre": 2.057336724,
    "G3 Winter_pr_1_99_wre": 57,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 706,
    "G3 Spring_theta_wre": 2.498390526,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 750
  },
  {
    "ID": 302,
    "KG Winter_theta_wre": -0.780093858,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 104,
    "KG Winter_dss_wre": 422,
    "KG Spring_theta_wre": -0.377755022,
    "KG Spring_pr_1_99_wre": 65,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 462,
    "G1 Fall_theta_wre": -0.047236925,
    "G1 Fall_pr_1_99_wre": 61,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 495,
    "G1 Winter_theta_wre": 0.099733477,
    "G1 Winter_pr_1_99_wre": 49,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 510,
    "G1 Spring_theta_wre": 0.513704287,
    "G1 Spring_pr_1_99_wre": 55,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 551,
    "G2 Fall_theta_wre": 0.799365541,
    "G2 Fall_pr_1_99_wre": 50,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 580,
    "G2 Winter_theta_wre": 1.128136053,
    "G2 Winter_pr_1_99_wre": 44,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 613,
    "G2 Spring_theta_wre": 1.392737451,
    "G2 Spring_pr_1_99_wre": 63,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 639,
    "G3 Fall_theta_wre": 1.722124214,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 672,
    "G3 Winter_theta_wre": 2.057937862,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 706,
    "G3 Spring_theta_wre": 2.501993608,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 750
  },
  {
    "ID": 303,
    "KG Winter_theta_wre": -0.775478487,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 104,
    "KG Winter_dss_wre": 422,
    "KG Spring_theta_wre": -0.377373341,
    "KG Spring_pr_1_99_wre": 65,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 462,
    "G1 Fall_theta_wre": -0.03965523,
    "G1 Fall_pr_1_99_wre": 61,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 496,
    "G1 Winter_theta_wre": 0.101791509,
    "G1 Winter_pr_1_99_wre": 49,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 510,
    "G1 Spring_theta_wre": 0.51461126,
    "G1 Spring_pr_1_99_wre": 55,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 551,
    "G2 Fall_theta_wre": 0.801321549,
    "G2 Fall_pr_1_99_wre": 50,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 580,
    "G2 Winter_theta_wre": 1.132151275,
    "G2 Winter_pr_1_99_wre": 44,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 613,
    "G2 Spring_theta_wre": 1.392822023,
    "G2 Spring_pr_1_99_wre": 63,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 639,
    "G3 Fall_theta_wre": 1.726552412,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 673,
    "G3 Winter_theta_wre": 2.05886454,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 706,
    "G3 Spring_theta_wre": 2.504700066,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 750
  },
  {
    "ID": 304,
    "KG Winter_theta_wre": -0.769472142,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 104,
    "KG Winter_dss_wre": 423,
    "KG Spring_theta_wre": -0.376287336,
    "KG Spring_pr_1_99_wre": 65,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 462,
    "G1 Fall_theta_wre": -0.035024816,
    "G1 Fall_pr_1_99_wre": 61,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 496,
    "G1 Winter_theta_wre": 0.102750181,
    "G1 Winter_pr_1_99_wre": 49,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 510,
    "G1 Spring_theta_wre": 0.52000284,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 552,
    "G2 Fall_theta_wre": 0.809823356,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 581,
    "G2 Winter_theta_wre": 1.135445086,
    "G2 Winter_pr_1_99_wre": 44,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 614,
    "G2 Spring_theta_wre": 1.40203826,
    "G2 Spring_pr_1_99_wre": 63,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 640,
    "G3 Fall_theta_wre": 1.730265292,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 673,
    "G3 Winter_theta_wre": 2.059122177,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 706,
    "G3 Spring_theta_wre": 2.508104056,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 105,
    "G3 Spring_dss_wre": 751
  },
  {
    "ID": 305,
    "KG Winter_theta_wre": -0.754989914,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 104,
    "KG Winter_dss_wre": 425,
    "KG Spring_theta_wre": -0.37007469,
    "KG Spring_pr_1_99_wre": 65,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 463,
    "G1 Fall_theta_wre": -0.027983308,
    "G1 Fall_pr_1_99_wre": 61,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 497,
    "G1 Winter_theta_wre": 0.109311215,
    "G1 Winter_pr_1_99_wre": 50,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 511,
    "G1 Spring_theta_wre": 0.520750714,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 552,
    "G2 Fall_theta_wre": 0.811689002,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 581,
    "G2 Winter_theta_wre": 1.136084408,
    "G2 Winter_pr_1_99_wre": 44,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 614,
    "G2 Spring_theta_wre": 1.406790462,
    "G2 Spring_pr_1_99_wre": 63,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 641,
    "G3 Fall_theta_wre": 1.733812876,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 673,
    "G3 Winter_theta_wre": 2.065542952,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 707,
    "G3 Spring_theta_wre": 2.513355864,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 751
  },
  {
    "ID": 306,
    "KG Winter_theta_wre": -0.740005291,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 104,
    "KG Winter_dss_wre": 426,
    "KG Spring_theta_wre": -0.368843423,
    "KG Spring_pr_1_99_wre": 65,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 463,
    "G1 Fall_theta_wre": -0.023543711,
    "G1 Fall_pr_1_99_wre": 61,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 498,
    "G1 Winter_theta_wre": 0.1150069,
    "G1 Winter_pr_1_99_wre": 50,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 512,
    "G1 Spring_theta_wre": 0.522339702,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 552,
    "G2 Fall_theta_wre": 0.813690714,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 581,
    "G2 Winter_theta_wre": 1.141581079,
    "G2 Winter_pr_1_99_wre": 44,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 614,
    "G2 Spring_theta_wre": 1.410739858,
    "G2 Spring_pr_1_99_wre": 63,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 641,
    "G3 Fall_theta_wre": 1.7356577,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 674,
    "G3 Winter_theta_wre": 2.06750119,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 707,
    "G3 Spring_theta_wre": 2.513988334,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 751
  },
  {
    "ID": 307,
    "KG Winter_theta_wre": -0.731032295,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 104,
    "KG Winter_dss_wre": 427,
    "KG Spring_theta_wre": -0.363917363,
    "KG Spring_pr_1_99_wre": 66,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 464,
    "G1 Fall_theta_wre": -0.021466964,
    "G1 Fall_pr_1_99_wre": 61,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 498,
    "G1 Winter_theta_wre": 0.122983706,
    "G1 Winter_pr_1_99_wre": 50,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 512,
    "G1 Spring_theta_wre": 0.525008323,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 553,
    "G2 Fall_theta_wre": 0.82349771,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 104,
    "G2 Fall_dss_wre": 582,
    "G2 Winter_theta_wre": 1.150911213,
    "G2 Winter_pr_1_99_wre": 45,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 615,
    "G2 Spring_theta_wre": 1.416014244,
    "G2 Spring_pr_1_99_wre": 63,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 642,
    "G3 Fall_theta_wre": 1.735694734,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 674,
    "G3 Winter_theta_wre": 2.070371702,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 707,
    "G3 Spring_theta_wre": 2.517006155,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 752
  },
  {
    "ID": 308,
    "KG Winter_theta_wre": -0.730898086,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 104,
    "KG Winter_dss_wre": 427,
    "KG Spring_theta_wre": -0.354603077,
    "KG Spring_pr_1_99_wre": 66,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 465,
    "G1 Fall_theta_wre": -0.020093738,
    "G1 Fall_pr_1_99_wre": 61,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 498,
    "G1 Winter_theta_wre": 0.129940262,
    "G1 Winter_pr_1_99_wre": 50,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 513,
    "G1 Spring_theta_wre": 0.52921058,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 553,
    "G2 Fall_theta_wre": 0.826697193,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 583,
    "G2 Winter_theta_wre": 1.160251859,
    "G2 Winter_pr_1_99_wre": 46,
    "G2 Winter_ss_wre": 104,
    "G2 Winter_dss_wre": 616,
    "G2 Spring_theta_wre": 1.420683601,
    "G2 Spring_pr_1_99_wre": 64,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 642,
    "G3 Fall_theta_wre": 1.738296813,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 674,
    "G3 Winter_theta_wre": 2.07436417,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 707,
    "G3 Spring_theta_wre": 2.522346561,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 752
  },
  {
    "ID": 309,
    "KG Winter_theta_wre": -0.72630002,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 427,
    "KG Spring_theta_wre": -0.346978852,
    "KG Spring_pr_1_99_wre": 66,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 465,
    "G1 Fall_theta_wre": -0.012956969,
    "G1 Fall_pr_1_99_wre": 62,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 499,
    "G1 Winter_theta_wre": 0.136113395,
    "G1 Winter_pr_1_99_wre": 50,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 514,
    "G1 Spring_theta_wre": 0.531506762,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 553,
    "G2 Fall_theta_wre": 0.827233599,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 583,
    "G2 Winter_theta_wre": 1.160646741,
    "G2 Winter_pr_1_99_wre": 46,
    "G2 Winter_ss_wre": 105,
    "G2 Winter_dss_wre": 616,
    "G2 Spring_theta_wre": 1.421176432,
    "G2 Spring_pr_1_99_wre": 64,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 642,
    "G3 Fall_theta_wre": 1.743972382,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 674,
    "G3 Winter_theta_wre": 2.085925646,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 709,
    "G3 Spring_theta_wre": 2.527523232,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 753
  },
  {
    "ID": 310,
    "KG Winter_theta_wre": -0.723117221,
    "KG Winter_pr_1_99_wre": 66,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 428,
    "KG Spring_theta_wre": -0.345791145,
    "KG Spring_pr_1_99_wre": 66,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 465,
    "G1 Fall_theta_wre": -0.007264957,
    "G1 Fall_pr_1_99_wre": 62,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 499,
    "G1 Winter_theta_wre": 0.141370934,
    "G1 Winter_pr_1_99_wre": 51,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 514,
    "G1 Spring_theta_wre": 0.535538246,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 554,
    "G2 Fall_theta_wre": 0.832863574,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 583,
    "G2 Winter_theta_wre": 1.166972485,
    "G2 Winter_pr_1_99_wre": 46,
    "G2 Winter_ss_wre": 105,
    "G2 Winter_dss_wre": 617,
    "G2 Spring_theta_wre": 1.422284252,
    "G2 Spring_pr_1_99_wre": 64,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 642,
    "G3 Fall_theta_wre": 1.746183251,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 675,
    "G3 Winter_theta_wre": 2.089738479,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 709,
    "G3 Spring_theta_wre": 2.528974192,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 753
  },
  {
    "ID": 311,
    "KG Winter_theta_wre": -0.7201244,
    "KG Winter_pr_1_99_wre": 67,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 428,
    "KG Spring_theta_wre": -0.341732899,
    "KG Spring_pr_1_99_wre": 67,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 466,
    "G1 Fall_theta_wre": -0.007202534,
    "G1 Fall_pr_1_99_wre": 62,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 499,
    "G1 Winter_theta_wre": 0.143267807,
    "G1 Winter_pr_1_99_wre": 51,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 514,
    "G1 Spring_theta_wre": 0.535824947,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 554,
    "G2 Fall_theta_wre": 0.838255585,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 584,
    "G2 Winter_theta_wre": 1.175249396,
    "G2 Winter_pr_1_99_wre": 47,
    "G2 Winter_ss_wre": 105,
    "G2 Winter_dss_wre": 618,
    "G2 Spring_theta_wre": 1.424344314,
    "G2 Spring_pr_1_99_wre": 64,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 642,
    "G3 Fall_theta_wre": 1.752122551,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 675,
    "G3 Winter_theta_wre": 2.089832741,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 709,
    "G3 Spring_theta_wre": 2.529166484,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 753
  },
  {
    "ID": 312,
    "KG Winter_theta_wre": -0.713190152,
    "KG Winter_pr_1_99_wre": 67,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 429,
    "KG Spring_theta_wre": -0.336907977,
    "KG Spring_pr_1_99_wre": 67,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 466,
    "G1 Fall_theta_wre": -0.006650781,
    "G1 Fall_pr_1_99_wre": 62,
    "G1 Fall_ss_wre": 105,
    "G1 Fall_dss_wre": 499,
    "G1 Winter_theta_wre": 0.144548186,
    "G1 Winter_pr_1_99_wre": 51,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 514,
    "G1 Spring_theta_wre": 0.538628534,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 104,
    "G1 Spring_dss_wre": 554,
    "G2 Fall_theta_wre": 0.83977764,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 584,
    "G2 Winter_theta_wre": 1.176050415,
    "G2 Winter_pr_1_99_wre": 47,
    "G2 Winter_ss_wre": 105,
    "G2 Winter_dss_wre": 618,
    "G2 Spring_theta_wre": 1.426922903,
    "G2 Spring_pr_1_99_wre": 64,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 643,
    "G3 Fall_theta_wre": 1.754912465,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 675,
    "G3 Winter_theta_wre": 2.090878904,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 709,
    "G3 Spring_theta_wre": 2.530245176,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 753
  },
  {
    "ID": 313,
    "KG Winter_theta_wre": -0.707042225,
    "KG Winter_pr_1_99_wre": 67,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 429,
    "KG Spring_theta_wre": -0.336225218,
    "KG Spring_pr_1_99_wre": 67,
    "KG Spring_ss_wre": 105,
    "KG Spring_dss_wre": 466,
    "G1 Fall_theta_wre": 0.002137447,
    "G1 Fall_pr_1_99_wre": 62,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 500,
    "G1 Winter_theta_wre": 0.145411832,
    "G1 Winter_pr_1_99_wre": 51,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 515,
    "G1 Spring_theta_wre": 0.551376429,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 555,
    "G2 Fall_theta_wre": 0.846283292,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 585,
    "G2 Winter_theta_wre": 1.17861145,
    "G2 Winter_pr_1_99_wre": 47,
    "G2 Winter_ss_wre": 105,
    "G2 Winter_dss_wre": 618,
    "G2 Spring_theta_wre": 1.427900209,
    "G2 Spring_pr_1_99_wre": 64,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 643,
    "G3 Fall_theta_wre": 1.756829822,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 676,
    "G3 Winter_theta_wre": 2.09100115,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 709,
    "G3 Spring_theta_wre": 2.53067057,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 753
  },
  {
    "ID": 314,
    "KG Winter_theta_wre": -0.704360065,
    "KG Winter_pr_1_99_wre": 67,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 430,
    "KG Spring_theta_wre": -0.331796411,
    "KG Spring_pr_1_99_wre": 68,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 467,
    "G1 Fall_theta_wre": 0.016494903,
    "G1 Fall_pr_1_99_wre": 63,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 502,
    "G1 Winter_theta_wre": 0.14586145,
    "G1 Winter_pr_1_99_wre": 51,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 515,
    "G1 Spring_theta_wre": 0.553337853,
    "G1 Spring_pr_1_99_wre": 56,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 555,
    "G2 Fall_theta_wre": 0.847476587,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 585,
    "G2 Winter_theta_wre": 1.179510748,
    "G2 Winter_pr_1_99_wre": 47,
    "G2 Winter_ss_wre": 105,
    "G2 Winter_dss_wre": 618,
    "G2 Spring_theta_wre": 1.430902035,
    "G2 Spring_pr_1_99_wre": 64,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 643,
    "G3 Fall_theta_wre": 1.761348921,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 104,
    "G3 Fall_dss_wre": 676,
    "G3 Winter_theta_wre": 2.097284847,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 710,
    "G3 Spring_theta_wre": 2.530756243,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 753
  },
  {
    "ID": 315,
    "KG Winter_theta_wre": -0.704346827,
    "KG Winter_pr_1_99_wre": 67,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 430,
    "KG Spring_theta_wre": -0.331725305,
    "KG Spring_pr_1_99_wre": 68,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 467,
    "G1 Fall_theta_wre": 0.019865959,
    "G1 Fall_pr_1_99_wre": 63,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 502,
    "G1 Winter_theta_wre": 0.149779641,
    "G1 Winter_pr_1_99_wre": 51,
    "G1 Winter_ss_wre": 104,
    "G1 Winter_dss_wre": 515,
    "G1 Spring_theta_wre": 0.559976303,
    "G1 Spring_pr_1_99_wre": 57,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 556,
    "G2 Fall_theta_wre": 0.850830855,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 585,
    "G2 Winter_theta_wre": 1.180029177,
    "G2 Winter_pr_1_99_wre": 47,
    "G2 Winter_ss_wre": 105,
    "G2 Winter_dss_wre": 618,
    "G2 Spring_theta_wre": 1.433319533,
    "G2 Spring_pr_1_99_wre": 64,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 643,
    "G3 Fall_theta_wre": 1.777991079,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 105,
    "G3 Fall_dss_wre": 678,
    "G3 Winter_theta_wre": 2.09902965,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 710,
    "G3 Spring_theta_wre": 2.533241818,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 753
  },
  {
    "ID": 316,
    "KG Winter_theta_wre": -0.703742002,
    "KG Winter_pr_1_99_wre": 67,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 430,
    "KG Spring_theta_wre": -0.329030845,
    "KG Spring_pr_1_99_wre": 68,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 467,
    "G1 Fall_theta_wre": 0.022893395,
    "G1 Fall_pr_1_99_wre": 64,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 502,
    "G1 Winter_theta_wre": 0.15898295,
    "G1 Winter_pr_1_99_wre": 51,
    "G1 Winter_ss_wre": 105,
    "G1 Winter_dss_wre": 516,
    "G1 Spring_theta_wre": 0.562132599,
    "G1 Spring_pr_1_99_wre": 57,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 556,
    "G2 Fall_theta_wre": 0.852195417,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 585,
    "G2 Winter_theta_wre": 1.196603715,
    "G2 Winter_pr_1_99_wre": 47,
    "G2 Winter_ss_wre": 105,
    "G2 Winter_dss_wre": 620,
    "G2 Spring_theta_wre": 1.437883942,
    "G2 Spring_pr_1_99_wre": 65,
    "G2 Spring_ss_wre": 104,
    "G2 Spring_dss_wre": 644,
    "G3 Fall_theta_wre": 1.77970496,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 105,
    "G3 Fall_dss_wre": 678,
    "G3 Winter_theta_wre": 2.099059094,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 104,
    "G3 Winter_dss_wre": 710,
    "G3 Spring_theta_wre": 2.535801688,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 754
  },
  {
    "ID": 317,
    "KG Winter_theta_wre": -0.698829529,
    "KG Winter_pr_1_99_wre": 67,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 430,
    "KG Spring_theta_wre": -0.325198149,
    "KG Spring_pr_1_99_wre": 68,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 467,
    "G1 Fall_theta_wre": 0.0264229,
    "G1 Fall_pr_1_99_wre": 64,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 503,
    "G1 Winter_theta_wre": 0.160377875,
    "G1 Winter_pr_1_99_wre": 51,
    "G1 Winter_ss_wre": 105,
    "G1 Winter_dss_wre": 516,
    "G1 Spring_theta_wre": 0.564763751,
    "G1 Spring_pr_1_99_wre": 57,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 556,
    "G2 Fall_theta_wre": 0.856089067,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 586,
    "G2 Winter_theta_wre": 1.204450911,
    "G2 Winter_pr_1_99_wre": 48,
    "G2 Winter_ss_wre": 105,
    "G2 Winter_dss_wre": 620,
    "G2 Spring_theta_wre": 1.439200088,
    "G2 Spring_pr_1_99_wre": 65,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 644,
    "G3 Fall_theta_wre": 1.784671119,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 105,
    "G3 Fall_dss_wre": 678,
    "G3 Winter_theta_wre": 2.102998996,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 710,
    "G3 Spring_theta_wre": 2.53591179,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 754
  },
  {
    "ID": 318,
    "KG Winter_theta_wre": -0.698238855,
    "KG Winter_pr_1_99_wre": 67,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 430,
    "KG Spring_theta_wre": -0.322675792,
    "KG Spring_pr_1_99_wre": 68,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 468,
    "G1 Fall_theta_wre": 0.029969314,
    "G1 Fall_pr_1_99_wre": 64,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 503,
    "G1 Winter_theta_wre": 0.162137033,
    "G1 Winter_pr_1_99_wre": 51,
    "G1 Winter_ss_wre": 105,
    "G1 Winter_dss_wre": 516,
    "G1 Spring_theta_wre": 0.564807255,
    "G1 Spring_pr_1_99_wre": 57,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 556,
    "G2 Fall_theta_wre": 0.856283579,
    "G2 Fall_pr_1_99_wre": 51,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 586,
    "G2 Winter_theta_wre": 1.21190921,
    "G2 Winter_pr_1_99_wre": 48,
    "G2 Winter_ss_wre": 105,
    "G2 Winter_dss_wre": 621,
    "G2 Spring_theta_wre": 1.449325617,
    "G2 Spring_pr_1_99_wre": 66,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 645,
    "G3 Fall_theta_wre": 1.793245141,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 105,
    "G3 Fall_dss_wre": 679,
    "G3 Winter_theta_wre": 2.10639871,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 711,
    "G3 Spring_theta_wre": 2.540628822,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 754
  },
  {
    "ID": 319,
    "KG Winter_theta_wre": -0.694917422,
    "KG Winter_pr_1_99_wre": 67,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 431,
    "KG Spring_theta_wre": -0.318691117,
    "KG Spring_pr_1_99_wre": 68,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 468,
    "G1 Fall_theta_wre": 0.036157941,
    "G1 Fall_pr_1_99_wre": 64,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 504,
    "G1 Winter_theta_wre": 0.164080211,
    "G1 Winter_pr_1_99_wre": 51,
    "G1 Winter_ss_wre": 105,
    "G1 Winter_dss_wre": 516,
    "G1 Spring_theta_wre": 0.570585764,
    "G1 Spring_pr_1_99_wre": 58,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 557,
    "G2 Fall_theta_wre": 0.86191513,
    "G2 Fall_pr_1_99_wre": 52,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 586,
    "G2 Winter_theta_wre": 1.215800233,
    "G2 Winter_pr_1_99_wre": 48,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 622,
    "G2 Spring_theta_wre": 1.449533037,
    "G2 Spring_pr_1_99_wre": 66,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 645,
    "G3 Fall_theta_wre": 1.793580301,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 105,
    "G3 Fall_dss_wre": 679,
    "G3 Winter_theta_wre": 2.106825103,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 711,
    "G3 Spring_theta_wre": 2.54273926,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 754
  },
  {
    "ID": 320,
    "KG Winter_theta_wre": -0.693872319,
    "KG Winter_pr_1_99_wre": 67,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 431,
    "KG Spring_theta_wre": -0.312158188,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 469,
    "G1 Fall_theta_wre": 0.037520711,
    "G1 Fall_pr_1_99_wre": 64,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 504,
    "G1 Winter_theta_wre": 0.17870844,
    "G1 Winter_pr_1_99_wre": 52,
    "G1 Winter_ss_wre": 105,
    "G1 Winter_dss_wre": 518,
    "G1 Spring_theta_wre": 0.57478468,
    "G1 Spring_pr_1_99_wre": 58,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 557,
    "G2 Fall_theta_wre": 0.867802896,
    "G2 Fall_pr_1_99_wre": 52,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 587,
    "G2 Winter_theta_wre": 1.21851331,
    "G2 Winter_pr_1_99_wre": 48,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 622,
    "G2 Spring_theta_wre": 1.462216222,
    "G2 Spring_pr_1_99_wre": 67,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 646,
    "G3 Fall_theta_wre": 1.79932064,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 105,
    "G3 Fall_dss_wre": 680,
    "G3 Winter_theta_wre": 2.108426832,
    "G3 Winter_pr_1_99_wre": 58,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 711,
    "G3 Spring_theta_wre": 2.54851654,
    "G3 Spring_pr_1_99_wre": 85,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 755
  },
  {
    "ID": 321,
    "KG Winter_theta_wre": -0.692695151,
    "KG Winter_pr_1_99_wre": 68,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 431,
    "KG Spring_theta_wre": -0.311893054,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 469,
    "G1 Fall_theta_wre": 0.043208965,
    "G1 Fall_pr_1_99_wre": 65,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 504,
    "G1 Winter_theta_wre": 0.187878244,
    "G1 Winter_pr_1_99_wre": 53,
    "G1 Winter_ss_wre": 105,
    "G1 Winter_dss_wre": 519,
    "G1 Spring_theta_wre": 0.578133993,
    "G1 Spring_pr_1_99_wre": 58,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 558,
    "G2 Fall_theta_wre": 0.8718834,
    "G2 Fall_pr_1_99_wre": 53,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 587,
    "G2 Winter_theta_wre": 1.228004428,
    "G2 Winter_pr_1_99_wre": 48,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 623,
    "G2 Spring_theta_wre": 1.46455835,
    "G2 Spring_pr_1_99_wre": 67,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 646,
    "G3 Fall_theta_wre": 1.803642434,
    "G3 Fall_pr_1_99_wre": 58,
    "G3 Fall_ss_wre": 105,
    "G3 Fall_dss_wre": 680,
    "G3 Winter_theta_wre": 2.112975194,
    "G3 Winter_pr_1_99_wre": 59,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 711,
    "G3 Spring_theta_wre": 2.558308682,
    "G3 Spring_pr_1_99_wre": 86,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 756
  },
  {
    "ID": 322,
    "KG Winter_theta_wre": -0.691650781,
    "KG Winter_pr_1_99_wre": 68,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 431,
    "KG Spring_theta_wre": -0.308634127,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 469,
    "G1 Fall_theta_wre": 0.046931831,
    "G1 Fall_pr_1_99_wre": 65,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 505,
    "G1 Winter_theta_wre": 0.188020522,
    "G1 Winter_pr_1_99_wre": 53,
    "G1 Winter_ss_wre": 105,
    "G1 Winter_dss_wre": 519,
    "G1 Spring_theta_wre": 0.58167686,
    "G1 Spring_pr_1_99_wre": 58,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 558,
    "G2 Fall_theta_wre": 0.878000253,
    "G2 Fall_pr_1_99_wre": 53,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 588,
    "G2 Winter_theta_wre": 1.232049017,
    "G2 Winter_pr_1_99_wre": 48,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 623,
    "G2 Spring_theta_wre": 1.467170516,
    "G2 Spring_pr_1_99_wre": 67,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 647,
    "G3 Fall_theta_wre": 1.823199385,
    "G3 Fall_pr_1_99_wre": 61,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 682,
    "G3 Winter_theta_wre": 2.114515162,
    "G3 Winter_pr_1_99_wre": 59,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 711,
    "G3 Spring_theta_wre": 2.561912829,
    "G3 Spring_pr_1_99_wre": 86,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 756
  },
  {
    "ID": 323,
    "KG Winter_theta_wre": -0.686544814,
    "KG Winter_pr_1_99_wre": 68,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 431,
    "KG Spring_theta_wre": -0.305586412,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 469,
    "G1 Fall_theta_wre": 0.051885374,
    "G1 Fall_pr_1_99_wre": 66,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 505,
    "G1 Winter_theta_wre": 0.194003294,
    "G1 Winter_pr_1_99_wre": 53,
    "G1 Winter_ss_wre": 105,
    "G1 Winter_dss_wre": 519,
    "G1 Spring_theta_wre": 0.589220097,
    "G1 Spring_pr_1_99_wre": 58,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 559,
    "G2 Fall_theta_wre": 0.879113331,
    "G2 Fall_pr_1_99_wre": 53,
    "G2 Fall_ss_wre": 105,
    "G2 Fall_dss_wre": 588,
    "G2 Winter_theta_wre": 1.238695846,
    "G2 Winter_pr_1_99_wre": 49,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 624,
    "G2 Spring_theta_wre": 1.467751427,
    "G2 Spring_pr_1_99_wre": 67,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 647,
    "G3 Fall_theta_wre": 1.830802572,
    "G3 Fall_pr_1_99_wre": 61,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 683,
    "G3 Winter_theta_wre": 2.125728206,
    "G3 Winter_pr_1_99_wre": 59,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 713,
    "G3 Spring_theta_wre": 2.562152694,
    "G3 Spring_pr_1_99_wre": 86,
    "G3 Spring_ss_wre": 106,
    "G3 Spring_dss_wre": 756
  },
  {
    "ID": 324,
    "KG Winter_theta_wre": -0.685827424,
    "KG Winter_pr_1_99_wre": 68,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 431,
    "KG Spring_theta_wre": -0.303840368,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 470,
    "G1 Fall_theta_wre": 0.055333882,
    "G1 Fall_pr_1_99_wre": 66,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 506,
    "G1 Winter_theta_wre": 0.197710663,
    "G1 Winter_pr_1_99_wre": 53,
    "G1 Winter_ss_wre": 105,
    "G1 Winter_dss_wre": 520,
    "G1 Spring_theta_wre": 0.596712828,
    "G1 Spring_pr_1_99_wre": 59,
    "G1 Spring_ss_wre": 105,
    "G1 Spring_dss_wre": 560,
    "G2 Fall_theta_wre": 0.880757799,
    "G2 Fall_pr_1_99_wre": 53,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 588,
    "G2 Winter_theta_wre": 1.240395109,
    "G2 Winter_pr_1_99_wre": 49,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 624,
    "G2 Spring_theta_wre": 1.468476101,
    "G2 Spring_pr_1_99_wre": 67,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 647,
    "G3 Fall_theta_wre": 1.834530984,
    "G3 Fall_pr_1_99_wre": 62,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 683,
    "G3 Winter_theta_wre": 2.139817839,
    "G3 Winter_pr_1_99_wre": 59,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 714,
    "G3 Spring_theta_wre": 2.569741122,
    "G3 Spring_pr_1_99_wre": 86,
    "G3 Spring_ss_wre": 107,
    "G3 Spring_dss_wre": 757
  },
  {
    "ID": 325,
    "KG Winter_theta_wre": -0.684428467,
    "KG Winter_pr_1_99_wre": 68,
    "KG Winter_ss_wre": 105,
    "KG Winter_dss_wre": 432,
    "KG Spring_theta_wre": -0.299380927,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 470,
    "G1 Fall_theta_wre": 0.056715374,
    "G1 Fall_pr_1_99_wre": 66,
    "G1 Fall_ss_wre": 106,
    "G1 Fall_dss_wre": 506,
    "G1 Winter_theta_wre": 0.199557729,
    "G1 Winter_pr_1_99_wre": 53,
    "G1 Winter_ss_wre": 105,
    "G1 Winter_dss_wre": 520,
    "G1 Spring_theta_wre": 0.600338073,
    "G1 Spring_pr_1_99_wre": 59,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 560,
    "G2 Fall_theta_wre": 0.894775012,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 589,
    "G2 Winter_theta_wre": 1.241180168,
    "G2 Winter_pr_1_99_wre": 49,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 624,
    "G2 Spring_theta_wre": 1.472629282,
    "G2 Spring_pr_1_99_wre": 67,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 647,
    "G3 Fall_theta_wre": 1.841017348,
    "G3 Fall_pr_1_99_wre": 62,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 684,
    "G3 Winter_theta_wre": 2.140139302,
    "G3 Winter_pr_1_99_wre": 59,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 714,
    "G3 Spring_theta_wre": 2.572625133,
    "G3 Spring_pr_1_99_wre": 86,
    "G3 Spring_ss_wre": 107,
    "G3 Spring_dss_wre": 757
  },
  {
    "ID": 326,
    "KG Winter_theta_wre": -0.67046788,
    "KG Winter_pr_1_99_wre": 69,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 433,
    "KG Spring_theta_wre": -0.292418665,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 471,
    "G1 Fall_theta_wre": 0.061461007,
    "G1 Fall_pr_1_99_wre": 66,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 506,
    "G1 Winter_theta_wre": 0.205925207,
    "G1 Winter_pr_1_99_wre": 54,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 521,
    "G1 Spring_theta_wre": 0.601522619,
    "G1 Spring_pr_1_99_wre": 59,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 560,
    "G2 Fall_theta_wre": 0.903704809,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 590,
    "G2 Winter_theta_wre": 1.259296055,
    "G2 Winter_pr_1_99_wre": 49,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 626,
    "G2 Spring_theta_wre": 1.473487708,
    "G2 Spring_pr_1_99_wre": 67,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 647,
    "G3 Fall_theta_wre": 1.845384903,
    "G3 Fall_pr_1_99_wre": 62,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 685,
    "G3 Winter_theta_wre": 2.144381883,
    "G3 Winter_pr_1_99_wre": 59,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 714,
    "G3 Spring_theta_wre": 2.588561028,
    "G3 Spring_pr_1_99_wre": 86,
    "G3 Spring_ss_wre": 107,
    "G3 Spring_dss_wre": 759
  },
  {
    "ID": 327,
    "KG Winter_theta_wre": -0.666735828,
    "KG Winter_pr_1_99_wre": 69,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 433,
    "KG Spring_theta_wre": -0.288303534,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 471,
    "G1 Fall_theta_wre": 0.070524465,
    "G1 Fall_pr_1_99_wre": 66,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 507,
    "G1 Winter_theta_wre": 0.20709481,
    "G1 Winter_pr_1_99_wre": 54,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 521,
    "G1 Spring_theta_wre": 0.619912517,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 562,
    "G2 Fall_theta_wre": 0.908294697,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 591,
    "G2 Winter_theta_wre": 1.262933155,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 626,
    "G2 Spring_theta_wre": 1.476796926,
    "G2 Spring_pr_1_99_wre": 67,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 648,
    "G3 Fall_theta_wre": 1.84563899,
    "G3 Fall_pr_1_99_wre": 62,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 685,
    "G3 Winter_theta_wre": 2.147792947,
    "G3 Winter_pr_1_99_wre": 59,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 715,
    "G3 Spring_theta_wre": 2.592624394,
    "G3 Spring_pr_1_99_wre": 86,
    "G3 Spring_ss_wre": 107,
    "G3 Spring_dss_wre": 759
  },
  {
    "ID": 328,
    "KG Winter_theta_wre": -0.666321776,
    "KG Winter_pr_1_99_wre": 69,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 433,
    "KG Spring_theta_wre": -0.286940476,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 471,
    "G1 Fall_theta_wre": 0.071358753,
    "G1 Fall_pr_1_99_wre": 66,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 507,
    "G1 Winter_theta_wre": 0.208560583,
    "G1 Winter_pr_1_99_wre": 54,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 521,
    "G1 Spring_theta_wre": 0.620675117,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 562,
    "G2 Fall_theta_wre": 0.910259338,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 591,
    "G2 Winter_theta_wre": 1.2639997,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 626,
    "G2 Spring_theta_wre": 1.491722808,
    "G2 Spring_pr_1_99_wre": 68,
    "G2 Spring_ss_wre": 105,
    "G2 Spring_dss_wre": 649,
    "G3 Fall_theta_wre": 1.847994412,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 685,
    "G3 Winter_theta_wre": 2.14836607,
    "G3 Winter_pr_1_99_wre": 59,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 715,
    "G3 Spring_theta_wre": 2.602722851,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 107,
    "G3 Spring_dss_wre": 760
  },
  {
    "ID": 329,
    "KG Winter_theta_wre": -0.666295108,
    "KG Winter_pr_1_99_wre": 69,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 433,
    "KG Spring_theta_wre": -0.282787692,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 472,
    "G1 Fall_theta_wre": 0.072363404,
    "G1 Fall_pr_1_99_wre": 66,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 507,
    "G1 Winter_theta_wre": 0.212582064,
    "G1 Winter_pr_1_99_wre": 54,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 521,
    "G1 Spring_theta_wre": 0.622015855,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 562,
    "G2 Fall_theta_wre": 0.91088166,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 591,
    "G2 Winter_theta_wre": 1.265637769,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 627,
    "G2 Spring_theta_wre": 1.495569851,
    "G2 Spring_pr_1_99_wre": 68,
    "G2 Spring_ss_wre": 106,
    "G2 Spring_dss_wre": 650,
    "G3 Fall_theta_wre": 1.850795577,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 685,
    "G3 Winter_theta_wre": 2.15286787,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 715,
    "G3 Spring_theta_wre": 2.603591328,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 107,
    "G3 Spring_dss_wre": 760
  },
  {
    "ID": 330,
    "KG Winter_theta_wre": -0.663791347,
    "KG Winter_pr_1_99_wre": 69,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 434,
    "KG Spring_theta_wre": -0.280673788,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 472,
    "G1 Fall_theta_wre": 0.077018538,
    "G1 Fall_pr_1_99_wre": 67,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 508,
    "G1 Winter_theta_wre": 0.213009075,
    "G1 Winter_pr_1_99_wre": 54,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 521,
    "G1 Spring_theta_wre": 0.622487922,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 562,
    "G2 Fall_theta_wre": 0.916145536,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 592,
    "G2 Winter_theta_wre": 1.267336416,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 627,
    "G2 Spring_theta_wre": 1.497268126,
    "G2 Spring_pr_1_99_wre": 68,
    "G2 Spring_ss_wre": 106,
    "G2 Spring_dss_wre": 650,
    "G3 Fall_theta_wre": 1.853667427,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 685,
    "G3 Winter_theta_wre": 2.153156975,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 715,
    "G3 Spring_theta_wre": 2.614110451,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 107,
    "G3 Spring_dss_wre": 761
  },
  {
    "ID": 331,
    "KG Winter_theta_wre": -0.660675838,
    "KG Winter_pr_1_99_wre": 70,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 434,
    "KG Spring_theta_wre": -0.276417198,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 106,
    "KG Spring_dss_wre": 472,
    "G1 Fall_theta_wre": 0.079033872,
    "G1 Fall_pr_1_99_wre": 67,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 508,
    "G1 Winter_theta_wre": 0.213846277,
    "G1 Winter_pr_1_99_wre": 54,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 521,
    "G1 Spring_theta_wre": 0.629116096,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 563,
    "G2 Fall_theta_wre": 0.919104942,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 592,
    "G2 Winter_theta_wre": 1.267881608,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 106,
    "G2 Winter_dss_wre": 627,
    "G2 Spring_theta_wre": 1.506316148,
    "G2 Spring_pr_1_99_wre": 68,
    "G2 Spring_ss_wre": 106,
    "G2 Spring_dss_wre": 651,
    "G3 Fall_theta_wre": 1.858827001,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 686,
    "G3 Winter_theta_wre": 2.153276254,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 105,
    "G3 Winter_dss_wre": 715,
    "G3 Spring_theta_wre": 2.618532166,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 107,
    "G3 Spring_dss_wre": 762
  },
  {
    "ID": 332,
    "KG Winter_theta_wre": -0.655749504,
    "KG Winter_pr_1_99_wre": 70,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 434,
    "KG Spring_theta_wre": -0.276100377,
    "KG Spring_pr_1_99_wre": 69,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 472,
    "G1 Fall_theta_wre": 0.087053643,
    "G1 Fall_pr_1_99_wre": 68,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 509,
    "G1 Winter_theta_wre": 0.220499428,
    "G1 Winter_pr_1_99_wre": 54,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 522,
    "G1 Spring_theta_wre": 0.632221368,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 563,
    "G2 Fall_theta_wre": 0.922159653,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 592,
    "G2 Winter_theta_wre": 1.271128834,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 627,
    "G2 Spring_theta_wre": 1.506850585,
    "G2 Spring_pr_1_99_wre": 68,
    "G2 Spring_ss_wre": 106,
    "G2 Spring_dss_wre": 651,
    "G3 Fall_theta_wre": 1.865572942,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 687,
    "G3 Winter_theta_wre": 2.15581283,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 716,
    "G3 Spring_theta_wre": 2.619122925,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 107,
    "G3 Spring_dss_wre": 762
  },
  {
    "ID": 333,
    "KG Winter_theta_wre": -0.655282371,
    "KG Winter_pr_1_99_wre": 70,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 434,
    "KG Spring_theta_wre": -0.274186511,
    "KG Spring_pr_1_99_wre": 70,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 473,
    "G1 Fall_theta_wre": 0.089151402,
    "G1 Fall_pr_1_99_wre": 68,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 509,
    "G1 Winter_theta_wre": 0.222432169,
    "G1 Winter_pr_1_99_wre": 54,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 522,
    "G1 Spring_theta_wre": 0.635005865,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 564,
    "G2 Fall_theta_wre": 0.931952693,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 593,
    "G2 Winter_theta_wre": 1.276074464,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 628,
    "G2 Spring_theta_wre": 1.507479444,
    "G2 Spring_pr_1_99_wre": 68,
    "G2 Spring_ss_wre": 106,
    "G2 Spring_dss_wre": 651,
    "G3 Fall_theta_wre": 1.866853027,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 687,
    "G3 Winter_theta_wre": 2.163387172,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 716,
    "G3 Spring_theta_wre": 2.631324871,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 763
  },
  {
    "ID": 334,
    "KG Winter_theta_wre": -0.64507956,
    "KG Winter_pr_1_99_wre": 70,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 435,
    "KG Spring_theta_wre": -0.271706225,
    "KG Spring_pr_1_99_wre": 70,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 473,
    "G1 Fall_theta_wre": 0.091510388,
    "G1 Fall_pr_1_99_wre": 68,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 509,
    "G1 Winter_theta_wre": 0.228735539,
    "G1 Winter_pr_1_99_wre": 55,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 523,
    "G1 Spring_theta_wre": 0.635526291,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 564,
    "G2 Fall_theta_wre": 0.932497412,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 106,
    "G2 Fall_dss_wre": 593,
    "G2 Winter_theta_wre": 1.288115703,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 629,
    "G2 Spring_theta_wre": 1.51527197,
    "G2 Spring_pr_1_99_wre": 68,
    "G2 Spring_ss_wre": 106,
    "G2 Spring_dss_wre": 652,
    "G3 Fall_theta_wre": 1.873716512,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 687,
    "G3 Winter_theta_wre": 2.164487666,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 716,
    "G3 Spring_theta_wre": 2.632751769,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 763
  },
  {
    "ID": 335,
    "KG Winter_theta_wre": -0.643104273,
    "KG Winter_pr_1_99_wre": 70,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 436,
    "KG Spring_theta_wre": -0.267943925,
    "KG Spring_pr_1_99_wre": 70,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 473,
    "G1 Fall_theta_wre": 0.098592884,
    "G1 Fall_pr_1_99_wre": 68,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 510,
    "G1 Winter_theta_wre": 0.237278746,
    "G1 Winter_pr_1_99_wre": 55,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 524,
    "G1 Spring_theta_wre": 0.636772171,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 564,
    "G2 Fall_theta_wre": 0.935595777,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 594,
    "G2 Winter_theta_wre": 1.288941227,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 629,
    "G2 Spring_theta_wre": 1.523723782,
    "G2 Spring_pr_1_99_wre": 69,
    "G2 Spring_ss_wre": 106,
    "G2 Spring_dss_wre": 652,
    "G3 Fall_theta_wre": 1.874789712,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 106,
    "G3 Fall_dss_wre": 687,
    "G3 Winter_theta_wre": 2.167571285,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 717,
    "G3 Spring_theta_wre": 2.634463675,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 763
  },
  {
    "ID": 336,
    "KG Winter_theta_wre": -0.640252473,
    "KG Winter_pr_1_99_wre": 70,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 436,
    "KG Spring_theta_wre": -0.267802644,
    "KG Spring_pr_1_99_wre": 70,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 473,
    "G1 Fall_theta_wre": 0.103368013,
    "G1 Fall_pr_1_99_wre": 68,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 510,
    "G1 Winter_theta_wre": 0.243650091,
    "G1 Winter_pr_1_99_wre": 55,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 524,
    "G1 Spring_theta_wre": 0.642638685,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 564,
    "G2 Fall_theta_wre": 0.939104992,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 594,
    "G2 Winter_theta_wre": 1.289841705,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 629,
    "G2 Spring_theta_wre": 1.53656803,
    "G2 Spring_pr_1_99_wre": 69,
    "G2 Spring_ss_wre": 106,
    "G2 Spring_dss_wre": 654,
    "G3 Fall_theta_wre": 1.881823497,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 688,
    "G3 Winter_theta_wre": 2.168532352,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 717,
    "G3 Spring_theta_wre": 2.635463395,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 764
  },
  {
    "ID": 337,
    "KG Winter_theta_wre": -0.633233773,
    "KG Winter_pr_1_99_wre": 71,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 437,
    "KG Spring_theta_wre": -0.256735206,
    "KG Spring_pr_1_99_wre": 70,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 474,
    "G1 Fall_theta_wre": 0.108917487,
    "G1 Fall_pr_1_99_wre": 68,
    "G1 Fall_ss_wre": 107,
    "G1 Fall_dss_wre": 511,
    "G1 Winter_theta_wre": 0.247347924,
    "G1 Winter_pr_1_99_wre": 55,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 525,
    "G1 Spring_theta_wre": 0.643909622,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 564,
    "G2 Fall_theta_wre": 0.941551228,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 594,
    "G2 Winter_theta_wre": 1.30085118,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 630,
    "G2 Spring_theta_wre": 1.543561955,
    "G2 Spring_pr_1_99_wre": 69,
    "G2 Spring_ss_wre": 106,
    "G2 Spring_dss_wre": 654,
    "G3 Fall_theta_wre": 1.883010044,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 688,
    "G3 Winter_theta_wre": 2.169156285,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 717,
    "G3 Spring_theta_wre": 2.641878096,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 764
  },
  {
    "ID": 338,
    "KG Winter_theta_wre": -0.631371823,
    "KG Winter_pr_1_99_wre": 71,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 437,
    "KG Spring_theta_wre": -0.256115708,
    "KG Spring_pr_1_99_wre": 70,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 474,
    "G1 Fall_theta_wre": 0.112401216,
    "G1 Fall_pr_1_99_wre": 68,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 511,
    "G1 Winter_theta_wre": 0.247881754,
    "G1 Winter_pr_1_99_wre": 55,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 525,
    "G1 Spring_theta_wre": 0.644077708,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 564,
    "G2 Fall_theta_wre": 0.941948082,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 594,
    "G2 Winter_theta_wre": 1.300868039,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 630,
    "G2 Spring_theta_wre": 1.549353843,
    "G2 Spring_pr_1_99_wre": 69,
    "G2 Spring_ss_wre": 107,
    "G2 Spring_dss_wre": 655,
    "G3 Fall_theta_wre": 1.883397864,
    "G3 Fall_pr_1_99_wre": 63,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 688,
    "G3 Winter_theta_wre": 2.169397873,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 717,
    "G3 Spring_theta_wre": 2.642770112,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 764
  },
  {
    "ID": 339,
    "KG Winter_theta_wre": -0.622612103,
    "KG Winter_pr_1_99_wre": 72,
    "KG Winter_ss_wre": 106,
    "KG Winter_dss_wre": 438,
    "KG Spring_theta_wre": -0.252950436,
    "KG Spring_pr_1_99_wre": 70,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 475,
    "G1 Fall_theta_wre": 0.114173834,
    "G1 Fall_pr_1_99_wre": 68,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 511,
    "G1 Winter_theta_wre": 0.248648546,
    "G1 Winter_pr_1_99_wre": 55,
    "G1 Winter_ss_wre": 106,
    "G1 Winter_dss_wre": 525,
    "G1 Spring_theta_wre": 0.646596509,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 565,
    "G2 Fall_theta_wre": 0.942585708,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 594,
    "G2 Winter_theta_wre": 1.302163019,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 630,
    "G2 Spring_theta_wre": 1.553251872,
    "G2 Spring_pr_1_99_wre": 70,
    "G2 Spring_ss_wre": 107,
    "G2 Spring_dss_wre": 655,
    "G3 Fall_theta_wre": 1.89060516,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 689,
    "G3 Winter_theta_wre": 2.174703687,
    "G3 Winter_pr_1_99_wre": 60,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 717,
    "G3 Spring_theta_wre": 2.647829569,
    "G3 Spring_pr_1_99_wre": 87,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 765
  },
  {
    "ID": 340,
    "KG Winter_theta_wre": -0.613341413,
    "KG Winter_pr_1_99_wre": 72,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 439,
    "KG Spring_theta_wre": -0.252277718,
    "KG Spring_pr_1_99_wre": 71,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 475,
    "G1 Fall_theta_wre": 0.122171543,
    "G1 Fall_pr_1_99_wre": 68,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 512,
    "G1 Winter_theta_wre": 0.257925978,
    "G1 Winter_pr_1_99_wre": 56,
    "G1 Winter_ss_wre": 107,
    "G1 Winter_dss_wre": 526,
    "G1 Spring_theta_wre": 0.647948105,
    "G1 Spring_pr_1_99_wre": 60,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 565,
    "G2 Fall_theta_wre": 0.945957993,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 595,
    "G2 Winter_theta_wre": 1.306594624,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 631,
    "G2 Spring_theta_wre": 1.562891344,
    "G2 Spring_pr_1_99_wre": 70,
    "G2 Spring_ss_wre": 107,
    "G2 Spring_dss_wre": 656,
    "G3 Fall_theta_wre": 1.891650744,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 689,
    "G3 Winter_theta_wre": 2.186463366,
    "G3 Winter_pr_1_99_wre": 62,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 719,
    "G3 Spring_theta_wre": 2.656358639,
    "G3 Spring_pr_1_99_wre": 88,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 766
  },
  {
    "ID": 341,
    "KG Winter_theta_wre": -0.611784771,
    "KG Winter_pr_1_99_wre": 72,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 439,
    "KG Spring_theta_wre": -0.251194973,
    "KG Spring_pr_1_99_wre": 71,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 475,
    "G1 Fall_theta_wre": 0.123159201,
    "G1 Fall_pr_1_99_wre": 68,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 512,
    "G1 Winter_theta_wre": 0.262721984,
    "G1 Winter_pr_1_99_wre": 56,
    "G1 Winter_ss_wre": 107,
    "G1 Winter_dss_wre": 526,
    "G1 Spring_theta_wre": 0.650621104,
    "G1 Spring_pr_1_99_wre": 61,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 565,
    "G2 Fall_theta_wre": 0.946236127,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 595,
    "G2 Winter_theta_wre": 1.307166505,
    "G2 Winter_pr_1_99_wre": 50,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 631,
    "G2 Spring_theta_wre": 1.566195204,
    "G2 Spring_pr_1_99_wre": 70,
    "G2 Spring_ss_wre": 107,
    "G2 Spring_dss_wre": 657,
    "G3 Fall_theta_wre": 1.894568423,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 689,
    "G3 Winter_theta_wre": 2.18796836,
    "G3 Winter_pr_1_99_wre": 62,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 719,
    "G3 Spring_theta_wre": 2.658827147,
    "G3 Spring_pr_1_99_wre": 88,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 766
  },
  {
    "ID": 342,
    "KG Winter_theta_wre": -0.608007282,
    "KG Winter_pr_1_99_wre": 72,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 439,
    "KG Spring_theta_wre": -0.243859965,
    "KG Spring_pr_1_99_wre": 71,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 476,
    "G1 Fall_theta_wre": 0.135621173,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 514,
    "G1 Winter_theta_wre": 0.267271751,
    "G1 Winter_pr_1_99_wre": 57,
    "G1 Winter_ss_wre": 107,
    "G1 Winter_dss_wre": 527,
    "G1 Spring_theta_wre": 0.651172294,
    "G1 Spring_pr_1_99_wre": 61,
    "G1 Spring_ss_wre": 106,
    "G1 Spring_dss_wre": 565,
    "G2 Fall_theta_wre": 0.946764503,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 595,
    "G2 Winter_theta_wre": 1.31782049,
    "G2 Winter_pr_1_99_wre": 51,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 632,
    "G2 Spring_theta_wre": 1.571285477,
    "G2 Spring_pr_1_99_wre": 70,
    "G2 Spring_ss_wre": 107,
    "G2 Spring_dss_wre": 657,
    "G3 Fall_theta_wre": 1.898746388,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 690,
    "G3 Winter_theta_wre": 2.18902843,
    "G3 Winter_pr_1_99_wre": 62,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 719,
    "G3 Spring_theta_wre": 2.661004173,
    "G3 Spring_pr_1_99_wre": 88,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 766
  },
  {
    "ID": 343,
    "KG Winter_theta_wre": -0.60417975,
    "KG Winter_pr_1_99_wre": 72,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 440,
    "KG Spring_theta_wre": -0.236867482,
    "KG Spring_pr_1_99_wre": 71,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 476,
    "G1 Fall_theta_wre": 0.136749553,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 514,
    "G1 Winter_theta_wre": 0.27264041,
    "G1 Winter_pr_1_99_wre": 57,
    "G1 Winter_ss_wre": 107,
    "G1 Winter_dss_wre": 527,
    "G1 Spring_theta_wre": 0.675246878,
    "G1 Spring_pr_1_99_wre": 62,
    "G1 Spring_ss_wre": 107,
    "G1 Spring_dss_wre": 568,
    "G2 Fall_theta_wre": 0.948347686,
    "G2 Fall_pr_1_99_wre": 54,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 595,
    "G2 Winter_theta_wre": 1.318868731,
    "G2 Winter_pr_1_99_wre": 51,
    "G2 Winter_ss_wre": 107,
    "G2 Winter_dss_wre": 632,
    "G2 Spring_theta_wre": 1.585442486,
    "G2 Spring_pr_1_99_wre": 71,
    "G2 Spring_ss_wre": 107,
    "G2 Spring_dss_wre": 659,
    "G3 Fall_theta_wre": 1.909756474,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 691,
    "G3 Winter_theta_wre": 2.205542849,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 106,
    "G3 Winter_dss_wre": 721,
    "G3 Spring_theta_wre": 2.662978393,
    "G3 Spring_pr_1_99_wre": 88,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 766
  },
  {
    "ID": 344,
    "KG Winter_theta_wre": -0.586598873,
    "KG Winter_pr_1_99_wre": 73,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 441,
    "KG Spring_theta_wre": -0.225976509,
    "KG Spring_pr_1_99_wre": 71,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 477,
    "G1 Fall_theta_wre": 0.142557604,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 514,
    "G1 Winter_theta_wre": 0.272777741,
    "G1 Winter_pr_1_99_wre": 57,
    "G1 Winter_ss_wre": 107,
    "G1 Winter_dss_wre": 527,
    "G1 Spring_theta_wre": 0.692390315,
    "G1 Spring_pr_1_99_wre": 64,
    "G1 Spring_ss_wre": 107,
    "G1 Spring_dss_wre": 569,
    "G2 Fall_theta_wre": 0.953263509,
    "G2 Fall_pr_1_99_wre": 55,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 595,
    "G2 Winter_theta_wre": 1.338353243,
    "G2 Winter_pr_1_99_wre": 52,
    "G2 Winter_ss_wre": 108,
    "G2 Winter_dss_wre": 634,
    "G2 Spring_theta_wre": 1.597770651,
    "G2 Spring_pr_1_99_wre": 71,
    "G2 Spring_ss_wre": 107,
    "G2 Spring_dss_wre": 660,
    "G3 Fall_theta_wre": 1.914817995,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 691,
    "G3 Winter_theta_wre": 2.220635619,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 722,
    "G3 Spring_theta_wre": 2.664687599,
    "G3 Spring_pr_1_99_wre": 88,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 766
  },
  {
    "ID": 345,
    "KG Winter_theta_wre": -0.586591412,
    "KG Winter_pr_1_99_wre": 73,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 441,
    "KG Spring_theta_wre": -0.224633589,
    "KG Spring_pr_1_99_wre": 71,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 478,
    "G1 Fall_theta_wre": 0.145038342,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 515,
    "G1 Winter_theta_wre": 0.275682958,
    "G1 Winter_pr_1_99_wre": 57,
    "G1 Winter_ss_wre": 107,
    "G1 Winter_dss_wre": 528,
    "G1 Spring_theta_wre": 0.693815342,
    "G1 Spring_pr_1_99_wre": 64,
    "G1 Spring_ss_wre": 107,
    "G1 Spring_dss_wre": 569,
    "G2 Fall_theta_wre": 0.955802003,
    "G2 Fall_pr_1_99_wre": 55,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 596,
    "G2 Winter_theta_wre": 1.339930374,
    "G2 Winter_pr_1_99_wre": 53,
    "G2 Winter_ss_wre": 108,
    "G2 Winter_dss_wre": 634,
    "G2 Spring_theta_wre": 1.605374262,
    "G2 Spring_pr_1_99_wre": 72,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 661,
    "G3 Fall_theta_wre": 1.919418008,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 692,
    "G3 Winter_theta_wre": 2.220670673,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 722,
    "G3 Spring_theta_wre": 2.667681701,
    "G3 Spring_pr_1_99_wre": 88,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 767
  },
  {
    "ID": 346,
    "KG Winter_theta_wre": -0.579518086,
    "KG Winter_pr_1_99_wre": 73,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 442,
    "KG Spring_theta_wre": -0.220591153,
    "KG Spring_pr_1_99_wre": 71,
    "KG Spring_ss_wre": 107,
    "KG Spring_dss_wre": 478,
    "G1 Fall_theta_wre": 0.15454811,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 515,
    "G1 Winter_theta_wre": 0.285654759,
    "G1 Winter_pr_1_99_wre": 58,
    "G1 Winter_ss_wre": 107,
    "G1 Winter_dss_wre": 529,
    "G1 Spring_theta_wre": 0.694235223,
    "G1 Spring_pr_1_99_wre": 64,
    "G1 Spring_ss_wre": 107,
    "G1 Spring_dss_wre": 569,
    "G2 Fall_theta_wre": 0.962458984,
    "G2 Fall_pr_1_99_wre": 55,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 596,
    "G2 Winter_theta_wre": 1.346945142,
    "G2 Winter_pr_1_99_wre": 53,
    "G2 Winter_ss_wre": 108,
    "G2 Winter_dss_wre": 635,
    "G2 Spring_theta_wre": 1.607300635,
    "G2 Spring_pr_1_99_wre": 72,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 661,
    "G3 Fall_theta_wre": 1.920482011,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 692,
    "G3 Winter_theta_wre": 2.224637378,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 722,
    "G3 Spring_theta_wre": 2.677221447,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 768
  },
  {
    "ID": 347,
    "KG Winter_theta_wre": -0.575318903,
    "KG Winter_pr_1_99_wre": 73,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 442,
    "KG Spring_theta_wre": -0.218007208,
    "KG Spring_pr_1_99_wre": 72,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 478,
    "G1 Fall_theta_wre": 0.157121663,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 516,
    "G1 Winter_theta_wre": 0.300412431,
    "G1 Winter_pr_1_99_wre": 59,
    "G1 Winter_ss_wre": 107,
    "G1 Winter_dss_wre": 530,
    "G1 Spring_theta_wre": 0.703184601,
    "G1 Spring_pr_1_99_wre": 64,
    "G1 Spring_ss_wre": 107,
    "G1 Spring_dss_wre": 570,
    "G2 Fall_theta_wre": 0.965485768,
    "G2 Fall_pr_1_99_wre": 56,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 597,
    "G2 Winter_theta_wre": 1.347698025,
    "G2 Winter_pr_1_99_wre": 53,
    "G2 Winter_ss_wre": 108,
    "G2 Winter_dss_wre": 635,
    "G2 Spring_theta_wre": 1.607832975,
    "G2 Spring_pr_1_99_wre": 72,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 661,
    "G3 Fall_theta_wre": 1.922170951,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 692,
    "G3 Winter_theta_wre": 2.228244367,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 723,
    "G3 Spring_theta_wre": 2.677932398,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 108,
    "G3 Spring_dss_wre": 768
  },
  {
    "ID": 348,
    "KG Winter_theta_wre": -0.57005942,
    "KG Winter_pr_1_99_wre": 74,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 443,
    "KG Spring_theta_wre": -0.217836778,
    "KG Spring_pr_1_99_wre": 72,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 478,
    "G1 Fall_theta_wre": 0.159229259,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 108,
    "G1 Fall_dss_wre": 516,
    "G1 Winter_theta_wre": 0.322134327,
    "G1 Winter_pr_1_99_wre": 59,
    "G1 Winter_ss_wre": 108,
    "G1 Winter_dss_wre": 532,
    "G1 Spring_theta_wre": 0.708407721,
    "G1 Spring_pr_1_99_wre": 65,
    "G1 Spring_ss_wre": 107,
    "G1 Spring_dss_wre": 571,
    "G2 Fall_theta_wre": 0.970562129,
    "G2 Fall_pr_1_99_wre": 57,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 597,
    "G2 Winter_theta_wre": 1.348878979,
    "G2 Winter_pr_1_99_wre": 53,
    "G2 Winter_ss_wre": 108,
    "G2 Winter_dss_wre": 635,
    "G2 Spring_theta_wre": 1.611078606,
    "G2 Spring_pr_1_99_wre": 72,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 661,
    "G3 Fall_theta_wre": 1.926968448,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 107,
    "G3 Fall_dss_wre": 693,
    "G3 Winter_theta_wre": 2.229418153,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 723,
    "G3 Spring_theta_wre": 2.688797113,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 769
  },
  {
    "ID": 349,
    "KG Winter_theta_wre": -0.566384077,
    "KG Winter_pr_1_99_wre": 74,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 443,
    "KG Spring_theta_wre": -0.215534513,
    "KG Spring_pr_1_99_wre": 72,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 478,
    "G1 Fall_theta_wre": 0.168182051,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 517,
    "G1 Winter_theta_wre": 0.3318811,
    "G1 Winter_pr_1_99_wre": 60,
    "G1 Winter_ss_wre": 108,
    "G1 Winter_dss_wre": 533,
    "G1 Spring_theta_wre": 0.711226439,
    "G1 Spring_pr_1_99_wre": 65,
    "G1 Spring_ss_wre": 108,
    "G1 Spring_dss_wre": 571,
    "G2 Fall_theta_wre": 0.98180072,
    "G2 Fall_pr_1_99_wre": 57,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 598,
    "G2 Winter_theta_wre": 1.352864282,
    "G2 Winter_pr_1_99_wre": 53,
    "G2 Winter_ss_wre": 108,
    "G2 Winter_dss_wre": 635,
    "G2 Spring_theta_wre": 1.61350715,
    "G2 Spring_pr_1_99_wre": 73,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 661,
    "G3 Fall_theta_wre": 1.93000308,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 693,
    "G3 Winter_theta_wre": 2.229458788,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 723,
    "G3 Spring_theta_wre": 2.691268644,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 769
  },
  {
    "ID": 350,
    "KG Winter_theta_wre": -0.565740221,
    "KG Winter_pr_1_99_wre": 74,
    "KG Winter_ss_wre": 107,
    "KG Winter_dss_wre": 443,
    "KG Spring_theta_wre": -0.211810034,
    "KG Spring_pr_1_99_wre": 72,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 479,
    "G1 Fall_theta_wre": 0.171128831,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 517,
    "G1 Winter_theta_wre": 0.335013595,
    "G1 Winter_pr_1_99_wre": 60,
    "G1 Winter_ss_wre": 108,
    "G1 Winter_dss_wre": 534,
    "G1 Spring_theta_wre": 0.720748027,
    "G1 Spring_pr_1_99_wre": 65,
    "G1 Spring_ss_wre": 108,
    "G1 Spring_dss_wre": 572,
    "G2 Fall_theta_wre": 0.98343875,
    "G2 Fall_pr_1_99_wre": 57,
    "G2 Fall_ss_wre": 107,
    "G2 Fall_dss_wre": 598,
    "G2 Winter_theta_wre": 1.357905488,
    "G2 Winter_pr_1_99_wre": 53,
    "G2 Winter_ss_wre": 108,
    "G2 Winter_dss_wre": 636,
    "G2 Spring_theta_wre": 1.615987907,
    "G2 Spring_pr_1_99_wre": 73,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 662,
    "G3 Fall_theta_wre": 1.934213639,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 693,
    "G3 Winter_theta_wre": 2.231755758,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 723,
    "G3 Spring_theta_wre": 2.692472968,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 769
  },
  {
    "ID": 351,
    "KG Winter_theta_wre": -0.562288285,
    "KG Winter_pr_1_99_wre": 74,
    "KG Winter_ss_wre": 108,
    "KG Winter_dss_wre": 444,
    "KG Spring_theta_wre": -0.211594645,
    "KG Spring_pr_1_99_wre": 72,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 479,
    "G1 Fall_theta_wre": 0.183714762,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 518,
    "G1 Winter_theta_wre": 0.335252013,
    "G1 Winter_pr_1_99_wre": 60,
    "G1 Winter_ss_wre": 108,
    "G1 Winter_dss_wre": 534,
    "G1 Spring_theta_wre": 0.721080508,
    "G1 Spring_pr_1_99_wre": 65,
    "G1 Spring_ss_wre": 108,
    "G1 Spring_dss_wre": 572,
    "G2 Fall_theta_wre": 0.997570556,
    "G2 Fall_pr_1_99_wre": 58,
    "G2 Fall_ss_wre": 108,
    "G2 Fall_dss_wre": 600,
    "G2 Winter_theta_wre": 1.362033955,
    "G2 Winter_pr_1_99_wre": 53,
    "G2 Winter_ss_wre": 108,
    "G2 Winter_dss_wre": 636,
    "G2 Spring_theta_wre": 1.619379144,
    "G2 Spring_pr_1_99_wre": 73,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 662,
    "G3 Fall_theta_wre": 1.934660952,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 693,
    "G3 Winter_theta_wre": 2.232689202,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 723,
    "G3 Spring_theta_wre": 2.695988079,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 770
  },
  {
    "ID": 352,
    "KG Winter_theta_wre": -0.55981342,
    "KG Winter_pr_1_99_wre": 74,
    "KG Winter_ss_wre": 108,
    "KG Winter_dss_wre": 444,
    "KG Spring_theta_wre": -0.207670426,
    "KG Spring_pr_1_99_wre": 72,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 479,
    "G1 Fall_theta_wre": 0.188005798,
    "G1 Fall_pr_1_99_wre": 69,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 519,
    "G1 Winter_theta_wre": 0.342094599,
    "G1 Winter_pr_1_99_wre": 60,
    "G1 Winter_ss_wre": 108,
    "G1 Winter_dss_wre": 534,
    "G1 Spring_theta_wre": 0.721465477,
    "G1 Spring_pr_1_99_wre": 65,
    "G1 Spring_ss_wre": 108,
    "G1 Spring_dss_wre": 572,
    "G2 Fall_theta_wre": 1.010227077,
    "G2 Fall_pr_1_99_wre": 59,
    "G2 Fall_ss_wre": 108,
    "G2 Fall_dss_wre": 601,
    "G2 Winter_theta_wre": 1.364131552,
    "G2 Winter_pr_1_99_wre": 53,
    "G2 Winter_ss_wre": 108,
    "G2 Winter_dss_wre": 636,
    "G2 Spring_theta_wre": 1.622120417,
    "G2 Spring_pr_1_99_wre": 73,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 662,
    "G3 Fall_theta_wre": 1.936779665,
    "G3 Fall_pr_1_99_wre": 70,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 694,
    "G3 Winter_theta_wre": 2.234656539,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 723,
    "G3 Spring_theta_wre": 2.697176546,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 770
  },
  {
    "ID": 353,
    "KG Winter_theta_wre": -0.554822223,
    "KG Winter_pr_1_99_wre": 74,
    "KG Winter_ss_wre": 108,
    "KG Winter_dss_wre": 445,
    "KG Spring_theta_wre": -0.205565423,
    "KG Spring_pr_1_99_wre": 72,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 479,
    "G1 Fall_theta_wre": 0.190705608,
    "G1 Fall_pr_1_99_wre": 70,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 519,
    "G1 Winter_theta_wre": 0.349250464,
    "G1 Winter_pr_1_99_wre": 61,
    "G1 Winter_ss_wre": 108,
    "G1 Winter_dss_wre": 535,
    "G1 Spring_theta_wre": 0.736348416,
    "G1 Spring_pr_1_99_wre": 66,
    "G1 Spring_ss_wre": 108,
    "G1 Spring_dss_wre": 574,
    "G2 Fall_theta_wre": 1.013873164,
    "G2 Fall_pr_1_99_wre": 59,
    "G2 Fall_ss_wre": 108,
    "G2 Fall_dss_wre": 601,
    "G2 Winter_theta_wre": 1.370334469,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 108,
    "G2 Winter_dss_wre": 637,
    "G2 Spring_theta_wre": 1.627534766,
    "G2 Spring_pr_1_99_wre": 73,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 663,
    "G3 Fall_theta_wre": 1.940012228,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 694,
    "G3 Winter_theta_wre": 2.236299176,
    "G3 Winter_pr_1_99_wre": 63,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 724,
    "G3 Spring_theta_wre": 2.701214536,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 770
  },
  {
    "ID": 354,
    "KG Winter_theta_wre": -0.541412573,
    "KG Winter_pr_1_99_wre": 74,
    "KG Winter_ss_wre": 108,
    "KG Winter_dss_wre": 446,
    "KG Spring_theta_wre": -0.198422695,
    "KG Spring_pr_1_99_wre": 72,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 480,
    "G1 Fall_theta_wre": 0.202022694,
    "G1 Fall_pr_1_99_wre": 70,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 520,
    "G1 Winter_theta_wre": 0.351340435,
    "G1 Winter_pr_1_99_wre": 61,
    "G1 Winter_ss_wre": 108,
    "G1 Winter_dss_wre": 535,
    "G1 Spring_theta_wre": 0.739312394,
    "G1 Spring_pr_1_99_wre": 66,
    "G1 Spring_ss_wre": 108,
    "G1 Spring_dss_wre": 574,
    "G2 Fall_theta_wre": 1.025823065,
    "G2 Fall_pr_1_99_wre": 59,
    "G2 Fall_ss_wre": 108,
    "G2 Fall_dss_wre": 603,
    "G2 Winter_theta_wre": 1.38131377,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 638,
    "G2 Spring_theta_wre": 1.64770623,
    "G2 Spring_pr_1_99_wre": 74,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 665,
    "G3 Fall_theta_wre": 1.944160391,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 694,
    "G3 Winter_theta_wre": 2.245570735,
    "G3 Winter_pr_1_99_wre": 65,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 725,
    "G3 Spring_theta_wre": 2.705335743,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 771
  },
  {
    "ID": 355,
    "KG Winter_theta_wre": -0.540762019,
    "KG Winter_pr_1_99_wre": 74,
    "KG Winter_ss_wre": 108,
    "KG Winter_dss_wre": 446,
    "KG Spring_theta_wre": -0.198234458,
    "KG Spring_pr_1_99_wre": 72,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 480,
    "G1 Fall_theta_wre": 0.204327549,
    "G1 Fall_pr_1_99_wre": 70,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 520,
    "G1 Winter_theta_wre": 0.351501462,
    "G1 Winter_pr_1_99_wre": 61,
    "G1 Winter_ss_wre": 108,
    "G1 Winter_dss_wre": 535,
    "G1 Spring_theta_wre": 0.768990667,
    "G1 Spring_pr_1_99_wre": 69,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 577,
    "G2 Fall_theta_wre": 1.048098438,
    "G2 Fall_pr_1_99_wre": 60,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 605,
    "G2 Winter_theta_wre": 1.384116267,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 638,
    "G2 Spring_theta_wre": 1.650682609,
    "G2 Spring_pr_1_99_wre": 74,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 665,
    "G3 Fall_theta_wre": 1.944806046,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 694,
    "G3 Winter_theta_wre": 2.246986573,
    "G3 Winter_pr_1_99_wre": 65,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 725,
    "G3 Spring_theta_wre": 2.705671337,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 771
  },
  {
    "ID": 356,
    "KG Winter_theta_wre": -0.517042158,
    "KG Winter_pr_1_99_wre": 74,
    "KG Winter_ss_wre": 108,
    "KG Winter_dss_wre": 448,
    "KG Spring_theta_wre": -0.181547467,
    "KG Spring_pr_1_99_wre": 73,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 482,
    "G1 Fall_theta_wre": 0.206040477,
    "G1 Fall_pr_1_99_wre": 70,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 521,
    "G1 Winter_theta_wre": 0.369123456,
    "G1 Winter_pr_1_99_wre": 61,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 537,
    "G1 Spring_theta_wre": 0.773841932,
    "G1 Spring_pr_1_99_wre": 69,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 577,
    "G2 Fall_theta_wre": 1.048636647,
    "G2 Fall_pr_1_99_wre": 60,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 605,
    "G2 Winter_theta_wre": 1.384358711,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 638,
    "G2 Spring_theta_wre": 1.655649892,
    "G2 Spring_pr_1_99_wre": 74,
    "G2 Spring_ss_wre": 108,
    "G2 Spring_dss_wre": 666,
    "G3 Fall_theta_wre": 1.947955987,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 695,
    "G3 Winter_theta_wre": 2.254570327,
    "G3 Winter_pr_1_99_wre": 66,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 725,
    "G3 Spring_theta_wre": 2.707816031,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 771
  },
  {
    "ID": 357,
    "KG Winter_theta_wre": -0.504994171,
    "KG Winter_pr_1_99_wre": 75,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 450,
    "KG Spring_theta_wre": -0.173767319,
    "KG Spring_pr_1_99_wre": 74,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 483,
    "G1 Fall_theta_wre": 0.206812131,
    "G1 Fall_pr_1_99_wre": 70,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 521,
    "G1 Winter_theta_wre": 0.37371499,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 537,
    "G1 Spring_theta_wre": 0.780039734,
    "G1 Spring_pr_1_99_wre": 69,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 578,
    "G2 Fall_theta_wre": 1.051078784,
    "G2 Fall_pr_1_99_wre": 61,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 605,
    "G2 Winter_theta_wre": 1.387368095,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 639,
    "G2 Spring_theta_wre": 1.663211818,
    "G2 Spring_pr_1_99_wre": 75,
    "G2 Spring_ss_wre": 109,
    "G2 Spring_dss_wre": 666,
    "G3 Fall_theta_wre": 1.95344645,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 695,
    "G3 Winter_theta_wre": 2.259428658,
    "G3 Winter_pr_1_99_wre": 66,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 726,
    "G3 Spring_theta_wre": 2.70812648,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 771
  },
  {
    "ID": 358,
    "KG Winter_theta_wre": -0.502324003,
    "KG Winter_pr_1_99_wre": 75,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 450,
    "KG Spring_theta_wre": -0.170584014,
    "KG Spring_pr_1_99_wre": 74,
    "KG Spring_ss_wre": 108,
    "KG Spring_dss_wre": 483,
    "G1 Fall_theta_wre": 0.213729966,
    "G1 Fall_pr_1_99_wre": 70,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 521,
    "G1 Winter_theta_wre": 0.377723063,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 538,
    "G1 Spring_theta_wre": 0.783232228,
    "G1 Spring_pr_1_99_wre": 69,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 578,
    "G2 Fall_theta_wre": 1.053242074,
    "G2 Fall_pr_1_99_wre": 61,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 605,
    "G2 Winter_theta_wre": 1.399289708,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 640,
    "G2 Spring_theta_wre": 1.663674793,
    "G2 Spring_pr_1_99_wre": 75,
    "G2 Spring_ss_wre": 109,
    "G2 Spring_dss_wre": 666,
    "G3 Fall_theta_wre": 1.956292339,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 696,
    "G3 Winter_theta_wre": 2.260069595,
    "G3 Winter_pr_1_99_wre": 66,
    "G3 Winter_ss_wre": 107,
    "G3 Winter_dss_wre": 726,
    "G3 Spring_theta_wre": 2.708248833,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 771
  },
  {
    "ID": 359,
    "KG Winter_theta_wre": -0.499695211,
    "KG Winter_pr_1_99_wre": 75,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 450,
    "KG Spring_theta_wre": -0.15472416,
    "KG Spring_pr_1_99_wre": 75,
    "KG Spring_ss_wre": 109,
    "KG Spring_dss_wre": 485,
    "G1 Fall_theta_wre": 0.217710193,
    "G1 Fall_pr_1_99_wre": 70,
    "G1 Fall_ss_wre": 109,
    "G1 Fall_dss_wre": 522,
    "G1 Winter_theta_wre": 0.378181061,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 538,
    "G1 Spring_theta_wre": 0.78363714,
    "G1 Spring_pr_1_99_wre": 69,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 578,
    "G2 Fall_theta_wre": 1.06428915,
    "G2 Fall_pr_1_99_wre": 63,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 606,
    "G2 Winter_theta_wre": 1.402528289,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 640,
    "G2 Spring_theta_wre": 1.666791009,
    "G2 Spring_pr_1_99_wre": 75,
    "G2 Spring_ss_wre": 109,
    "G2 Spring_dss_wre": 667,
    "G3 Fall_theta_wre": 1.958791969,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 696,
    "G3 Winter_theta_wre": 2.260843355,
    "G3 Winter_pr_1_99_wre": 66,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 726,
    "G3 Spring_theta_wre": 2.722702694,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 772
  },
  {
    "ID": 360,
    "KG Winter_theta_wre": -0.49598985,
    "KG Winter_pr_1_99_wre": 75,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 450,
    "KG Spring_theta_wre": -0.149395405,
    "KG Spring_pr_1_99_wre": 75,
    "KG Spring_ss_wre": 109,
    "KG Spring_dss_wre": 485,
    "G1 Fall_theta_wre": 0.225624447,
    "G1 Fall_pr_1_99_wre": 71,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 523,
    "G1 Winter_theta_wre": 0.379132493,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 538,
    "G1 Spring_theta_wre": 0.797087153,
    "G1 Spring_pr_1_99_wre": 70,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 580,
    "G2 Fall_theta_wre": 1.068017669,
    "G2 Fall_pr_1_99_wre": 63,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 607,
    "G2 Winter_theta_wre": 1.404834319,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 640,
    "G2 Spring_theta_wre": 1.673767825,
    "G2 Spring_pr_1_99_wre": 75,
    "G2 Spring_ss_wre": 109,
    "G2 Spring_dss_wre": 667,
    "G3 Fall_theta_wre": 1.967796295,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 697,
    "G3 Winter_theta_wre": 2.262980787,
    "G3 Winter_pr_1_99_wre": 66,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 726,
    "G3 Spring_theta_wre": 2.723914564,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 109,
    "G3 Spring_dss_wre": 772
  },
  {
    "ID": 361,
    "KG Winter_theta_wre": -0.495836505,
    "KG Winter_pr_1_99_wre": 75,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 450,
    "KG Spring_theta_wre": -0.140806961,
    "KG Spring_pr_1_99_wre": 75,
    "KG Spring_ss_wre": 109,
    "KG Spring_dss_wre": 486,
    "G1 Fall_theta_wre": 0.228245574,
    "G1 Fall_pr_1_99_wre": 71,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 523,
    "G1 Winter_theta_wre": 0.380110628,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 538,
    "G1 Spring_theta_wre": 0.798310003,
    "G1 Spring_pr_1_99_wre": 70,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 580,
    "G2 Fall_theta_wre": 1.069276705,
    "G2 Fall_pr_1_99_wre": 63,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 607,
    "G2 Winter_theta_wre": 1.413308133,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 641,
    "G2 Spring_theta_wre": 1.694359437,
    "G2 Spring_pr_1_99_wre": 75,
    "G2 Spring_ss_wre": 109,
    "G2 Spring_dss_wre": 669,
    "G3 Fall_theta_wre": 1.971002718,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 697,
    "G3 Winter_theta_wre": 2.263524217,
    "G3 Winter_pr_1_99_wre": 66,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 726,
    "G3 Spring_theta_wre": 2.741033492,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 774
  },
  {
    "ID": 362,
    "KG Winter_theta_wre": -0.49500011,
    "KG Winter_pr_1_99_wre": 75,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 450,
    "KG Spring_theta_wre": -0.139514851,
    "KG Spring_pr_1_99_wre": 75,
    "KG Spring_ss_wre": 109,
    "KG Spring_dss_wre": 486,
    "G1 Fall_theta_wre": 0.232690121,
    "G1 Fall_pr_1_99_wre": 72,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 523,
    "G1 Winter_theta_wre": 0.380563104,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 538,
    "G1 Spring_theta_wre": 0.799101645,
    "G1 Spring_pr_1_99_wre": 70,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 580,
    "G2 Fall_theta_wre": 1.075417196,
    "G2 Fall_pr_1_99_wre": 63,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 608,
    "G2 Winter_theta_wre": 1.416093351,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 642,
    "G2 Spring_theta_wre": 1.700325472,
    "G2 Spring_pr_1_99_wre": 76,
    "G2 Spring_ss_wre": 109,
    "G2 Spring_dss_wre": 670,
    "G3 Fall_theta_wre": 1.978801227,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 698,
    "G3 Winter_theta_wre": 2.270712005,
    "G3 Winter_pr_1_99_wre": 66,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 727,
    "G3 Spring_theta_wre": 2.746325465,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 775
  },
  {
    "ID": 363,
    "KG Winter_theta_wre": -0.492558061,
    "KG Winter_pr_1_99_wre": 75,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 451,
    "KG Spring_theta_wre": -0.131897251,
    "KG Spring_pr_1_99_wre": 76,
    "KG Spring_ss_wre": 109,
    "KG Spring_dss_wre": 487,
    "G1 Fall_theta_wre": 0.237028091,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 524,
    "G1 Winter_theta_wre": 0.381910906,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 538,
    "G1 Spring_theta_wre": 0.799915422,
    "G1 Spring_pr_1_99_wre": 70,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 580,
    "G2 Fall_theta_wre": 1.077443298,
    "G2 Fall_pr_1_99_wre": 63,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 608,
    "G2 Winter_theta_wre": 1.421086943,
    "G2 Winter_pr_1_99_wre": 54,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 642,
    "G2 Spring_theta_wre": 1.703402272,
    "G2 Spring_pr_1_99_wre": 76,
    "G2 Spring_ss_wre": 109,
    "G2 Spring_dss_wre": 670,
    "G3 Fall_theta_wre": 1.979483877,
    "G3 Fall_pr_1_99_wre": 73,
    "G3 Fall_ss_wre": 108,
    "G3 Fall_dss_wre": 698,
    "G3 Winter_theta_wre": 2.273688637,
    "G3 Winter_pr_1_99_wre": 66,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 727,
    "G3 Spring_theta_wre": 2.7485144,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 775
  },
  {
    "ID": 364,
    "KG Winter_theta_wre": -0.485825461,
    "KG Winter_pr_1_99_wre": 76,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 451,
    "KG Spring_theta_wre": -0.130951886,
    "KG Spring_pr_1_99_wre": 76,
    "KG Spring_ss_wre": 109,
    "KG Spring_dss_wre": 487,
    "G1 Fall_theta_wre": 0.240480764,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 524,
    "G1 Winter_theta_wre": 0.383858189,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 538,
    "G1 Spring_theta_wre": 0.805732599,
    "G1 Spring_pr_1_99_wre": 71,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 581,
    "G2 Fall_theta_wre": 1.077489463,
    "G2 Fall_pr_1_99_wre": 63,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 608,
    "G2 Winter_theta_wre": 1.425395645,
    "G2 Winter_pr_1_99_wre": 56,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 643,
    "G2 Spring_theta_wre": 1.709840862,
    "G2 Spring_pr_1_99_wre": 76,
    "G2 Spring_ss_wre": 109,
    "G2 Spring_dss_wre": 671,
    "G3 Fall_theta_wre": 1.984373638,
    "G3 Fall_pr_1_99_wre": 74,
    "G3 Fall_ss_wre": 109,
    "G3 Fall_dss_wre": 698,
    "G3 Winter_theta_wre": 2.276673382,
    "G3 Winter_pr_1_99_wre": 66,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 728,
    "G3 Spring_theta_wre": 2.750738024,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 775
  },
  {
    "ID": 365,
    "KG Winter_theta_wre": -0.481720122,
    "KG Winter_pr_1_99_wre": 76,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 452,
    "KG Spring_theta_wre": -0.119767151,
    "KG Spring_pr_1_99_wre": 76,
    "KG Spring_ss_wre": 109,
    "KG Spring_dss_wre": 488,
    "G1 Fall_theta_wre": 0.243637589,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 524,
    "G1 Winter_theta_wre": 0.386759013,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 539,
    "G1 Spring_theta_wre": 0.816854124,
    "G1 Spring_pr_1_99_wre": 72,
    "G1 Spring_ss_wre": 109,
    "G1 Spring_dss_wre": 582,
    "G2 Fall_theta_wre": 1.078788982,
    "G2 Fall_pr_1_99_wre": 63,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 608,
    "G2 Winter_theta_wre": 1.426736899,
    "G2 Winter_pr_1_99_wre": 56,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 643,
    "G2 Spring_theta_wre": 1.710025054,
    "G2 Spring_pr_1_99_wre": 76,
    "G2 Spring_ss_wre": 109,
    "G2 Spring_dss_wre": 671,
    "G3 Fall_theta_wre": 1.984477995,
    "G3 Fall_pr_1_99_wre": 74,
    "G3 Fall_ss_wre": 109,
    "G3 Fall_dss_wre": 698,
    "G3 Winter_theta_wre": 2.280087754,
    "G3 Winter_pr_1_99_wre": 66,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 728,
    "G3 Spring_theta_wre": 2.751280852,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 775
  },
  {
    "ID": 366,
    "KG Winter_theta_wre": -0.471640621,
    "KG Winter_pr_1_99_wre": 77,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 453,
    "KG Spring_theta_wre": -0.116089462,
    "KG Spring_pr_1_99_wre": 76,
    "KG Spring_ss_wre": 109,
    "KG Spring_dss_wre": 488,
    "G1 Fall_theta_wre": 0.250449964,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 525,
    "G1 Winter_theta_wre": 0.390041845,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 539,
    "G1 Spring_theta_wre": 0.822583995,
    "G1 Spring_pr_1_99_wre": 72,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 582,
    "G2 Fall_theta_wre": 1.08261771,
    "G2 Fall_pr_1_99_wre": 63,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 608,
    "G2 Winter_theta_wre": 1.429996934,
    "G2 Winter_pr_1_99_wre": 56,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 643,
    "G2 Spring_theta_wre": 1.712518454,
    "G2 Spring_pr_1_99_wre": 76,
    "G2 Spring_ss_wre": 109,
    "G2 Spring_dss_wre": 671,
    "G3 Fall_theta_wre": 1.987567714,
    "G3 Fall_pr_1_99_wre": 74,
    "G3 Fall_ss_wre": 109,
    "G3 Fall_dss_wre": 699,
    "G3 Winter_theta_wre": 2.28426141,
    "G3 Winter_pr_1_99_wre": 67,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 728,
    "G3 Spring_theta_wre": 2.755873657,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 776
  },
  {
    "ID": 367,
    "KG Winter_theta_wre": -0.470050273,
    "KG Winter_pr_1_99_wre": 77,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 453,
    "KG Spring_theta_wre": -0.110262491,
    "KG Spring_pr_1_99_wre": 76,
    "KG Spring_ss_wre": 109,
    "KG Spring_dss_wre": 489,
    "G1 Fall_theta_wre": 0.253463619,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 525,
    "G1 Winter_theta_wre": 0.392368221,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 539,
    "G1 Spring_theta_wre": 0.828890875,
    "G1 Spring_pr_1_99_wre": 72,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 583,
    "G2 Fall_theta_wre": 1.087442941,
    "G2 Fall_pr_1_99_wre": 64,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 609,
    "G2 Winter_theta_wre": 1.430253786,
    "G2 Winter_pr_1_99_wre": 56,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 643,
    "G2 Spring_theta_wre": 1.729322144,
    "G2 Spring_pr_1_99_wre": 78,
    "G2 Spring_ss_wre": 110,
    "G2 Spring_dss_wre": 673,
    "G3 Fall_theta_wre": 1.997468967,
    "G3 Fall_pr_1_99_wre": 74,
    "G3 Fall_ss_wre": 109,
    "G3 Fall_dss_wre": 700,
    "G3 Winter_theta_wre": 2.287132754,
    "G3 Winter_pr_1_99_wre": 67,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 729,
    "G3 Spring_theta_wre": 2.758154384,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 776
  },
  {
    "ID": 368,
    "KG Winter_theta_wre": -0.464684534,
    "KG Winter_pr_1_99_wre": 77,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 454,
    "KG Spring_theta_wre": -0.10711795,
    "KG Spring_pr_1_99_wre": 76,
    "KG Spring_ss_wre": 110,
    "KG Spring_dss_wre": 489,
    "G1 Fall_theta_wre": 0.262298804,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 526,
    "G1 Winter_theta_wre": 0.394459746,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 539,
    "G1 Spring_theta_wre": 0.82924786,
    "G1 Spring_pr_1_99_wre": 72,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 583,
    "G2 Fall_theta_wre": 1.090561255,
    "G2 Fall_pr_1_99_wre": 64,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 609,
    "G2 Winter_theta_wre": 1.431116931,
    "G2 Winter_pr_1_99_wre": 56,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 643,
    "G2 Spring_theta_wre": 1.730998782,
    "G2 Spring_pr_1_99_wre": 78,
    "G2 Spring_ss_wre": 110,
    "G2 Spring_dss_wre": 673,
    "G3 Fall_theta_wre": 2.008784012,
    "G3 Fall_pr_1_99_wre": 74,
    "G3 Fall_ss_wre": 109,
    "G3 Fall_dss_wre": 701,
    "G3 Winter_theta_wre": 2.291046511,
    "G3 Winter_pr_1_99_wre": 67,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 729,
    "G3 Spring_theta_wre": 2.758716271,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 776
  },
  {
    "ID": 369,
    "KG Winter_theta_wre": -0.462682804,
    "KG Winter_pr_1_99_wre": 77,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 454,
    "KG Spring_theta_wre": -0.103445024,
    "KG Spring_pr_1_99_wre": 76,
    "KG Spring_ss_wre": 110,
    "KG Spring_dss_wre": 490,
    "G1 Fall_theta_wre": 0.262428568,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 526,
    "G1 Winter_theta_wre": 0.399593928,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 540,
    "G1 Spring_theta_wre": 0.832011863,
    "G1 Spring_pr_1_99_wre": 72,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 583,
    "G2 Fall_theta_wre": 1.093740874,
    "G2 Fall_pr_1_99_wre": 64,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 609,
    "G2 Winter_theta_wre": 1.43278956,
    "G2 Winter_pr_1_99_wre": 56,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 643,
    "G2 Spring_theta_wre": 1.732874146,
    "G2 Spring_pr_1_99_wre": 78,
    "G2 Spring_ss_wre": 110,
    "G2 Spring_dss_wre": 673,
    "G3 Fall_theta_wre": 2.012325626,
    "G3 Fall_pr_1_99_wre": 74,
    "G3 Fall_ss_wre": 109,
    "G3 Fall_dss_wre": 701,
    "G3 Winter_theta_wre": 2.29354691,
    "G3 Winter_pr_1_99_wre": 67,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 729,
    "G3 Spring_theta_wre": 2.764392071,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 776
  },
  {
    "ID": 370,
    "KG Winter_theta_wre": -0.457048909,
    "KG Winter_pr_1_99_wre": 77,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 454,
    "KG Spring_theta_wre": -0.100021202,
    "KG Spring_pr_1_99_wre": 76,
    "KG Spring_ss_wre": 110,
    "KG Spring_dss_wre": 490,
    "G1 Fall_theta_wre": 0.262993531,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 526,
    "G1 Winter_theta_wre": 0.409195298,
    "G1 Winter_pr_1_99_wre": 62,
    "G1 Winter_ss_wre": 109,
    "G1 Winter_dss_wre": 541,
    "G1 Spring_theta_wre": 0.853714597,
    "G1 Spring_pr_1_99_wre": 73,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 585,
    "G2 Fall_theta_wre": 1.098448924,
    "G2 Fall_pr_1_99_wre": 64,
    "G2 Fall_ss_wre": 109,
    "G2 Fall_dss_wre": 610,
    "G2 Winter_theta_wre": 1.434036218,
    "G2 Winter_pr_1_99_wre": 56,
    "G2 Winter_ss_wre": 109,
    "G2 Winter_dss_wre": 643,
    "G2 Spring_theta_wre": 1.733772558,
    "G2 Spring_pr_1_99_wre": 78,
    "G2 Spring_ss_wre": 110,
    "G2 Spring_dss_wre": 673,
    "G3 Fall_theta_wre": 2.026745244,
    "G3 Fall_pr_1_99_wre": 75,
    "G3 Fall_ss_wre": 109,
    "G3 Fall_dss_wre": 703,
    "G3 Winter_theta_wre": 2.295986628,
    "G3 Winter_pr_1_99_wre": 67,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 730,
    "G3 Spring_theta_wre": 2.76892226,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 777
  },
  {
    "ID": 371,
    "KG Winter_theta_wre": -0.456912477,
    "KG Winter_pr_1_99_wre": 77,
    "KG Winter_ss_wre": 109,
    "KG Winter_dss_wre": 454,
    "KG Spring_theta_wre": -0.093480125,
    "KG Spring_pr_1_99_wre": 76,
    "KG Spring_ss_wre": 110,
    "KG Spring_dss_wre": 491,
    "G1 Fall_theta_wre": 0.264005938,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 526,
    "G1 Winter_theta_wre": 0.413065052,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 541,
    "G1 Spring_theta_wre": 0.85461537,
    "G1 Spring_pr_1_99_wre": 73,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 585,
    "G2 Fall_theta_wre": 1.101133693,
    "G2 Fall_pr_1_99_wre": 64,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 610,
    "G2 Winter_theta_wre": 1.440006801,
    "G2 Winter_pr_1_99_wre": 56,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 644,
    "G2 Spring_theta_wre": 1.755523976,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 110,
    "G2 Spring_dss_wre": 676,
    "G3 Fall_theta_wre": 2.028086402,
    "G3 Fall_pr_1_99_wre": 75,
    "G3 Fall_ss_wre": 109,
    "G3 Fall_dss_wre": 703,
    "G3 Winter_theta_wre": 2.299113041,
    "G3 Winter_pr_1_99_wre": 67,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 730,
    "G3 Spring_theta_wre": 2.769949859,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 777
  },
  {
    "ID": 372,
    "KG Winter_theta_wre": -0.453350332,
    "KG Winter_pr_1_99_wre": 78,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 455,
    "KG Spring_theta_wre": -0.092133516,
    "KG Spring_pr_1_99_wre": 76,
    "KG Spring_ss_wre": 110,
    "KG Spring_dss_wre": 491,
    "G1 Fall_theta_wre": 0.266864378,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 527,
    "G1 Winter_theta_wre": 0.413824931,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 541,
    "G1 Spring_theta_wre": 0.855040387,
    "G1 Spring_pr_1_99_wre": 73,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 586,
    "G2 Fall_theta_wre": 1.102157246,
    "G2 Fall_pr_1_99_wre": 64,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 610,
    "G2 Winter_theta_wre": 1.445666457,
    "G2 Winter_pr_1_99_wre": 57,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 645,
    "G2 Spring_theta_wre": 1.755598541,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 110,
    "G2 Spring_dss_wre": 676,
    "G3 Fall_theta_wre": 2.033201508,
    "G3 Fall_pr_1_99_wre": 75,
    "G3 Fall_ss_wre": 109,
    "G3 Fall_dss_wre": 703,
    "G3 Winter_theta_wre": 2.306069741,
    "G3 Winter_pr_1_99_wre": 67,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 731,
    "G3 Spring_theta_wre": 2.771587548,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 777
  },
  {
    "ID": 373,
    "KG Winter_theta_wre": -0.450344725,
    "KG Winter_pr_1_99_wre": 78,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 455,
    "KG Spring_theta_wre": -0.081324384,
    "KG Spring_pr_1_99_wre": 77,
    "KG Spring_ss_wre": 110,
    "KG Spring_dss_wre": 492,
    "G1 Fall_theta_wre": 0.268570704,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 527,
    "G1 Winter_theta_wre": 0.414296235,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 541,
    "G1 Spring_theta_wre": 0.856127993,
    "G1 Spring_pr_1_99_wre": 73,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 586,
    "G2 Fall_theta_wre": 1.111532355,
    "G2 Fall_pr_1_99_wre": 64,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 611,
    "G2 Winter_theta_wre": 1.44719641,
    "G2 Winter_pr_1_99_wre": 57,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 645,
    "G2 Spring_theta_wre": 1.757231217,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 110,
    "G2 Spring_dss_wre": 676,
    "G3 Fall_theta_wre": 2.036691519,
    "G3 Fall_pr_1_99_wre": 75,
    "G3 Fall_ss_wre": 109,
    "G3 Fall_dss_wre": 704,
    "G3 Winter_theta_wre": 2.306080777,
    "G3 Winter_pr_1_99_wre": 67,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 731,
    "G3 Spring_theta_wre": 2.775899742,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 778
  },
  {
    "ID": 374,
    "KG Winter_theta_wre": -0.449082491,
    "KG Winter_pr_1_99_wre": 78,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 455,
    "KG Spring_theta_wre": -0.079388216,
    "KG Spring_pr_1_99_wre": 77,
    "KG Spring_ss_wre": 110,
    "KG Spring_dss_wre": 492,
    "G1 Fall_theta_wre": 0.268790402,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 527,
    "G1 Winter_theta_wre": 0.415940221,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 542,
    "G1 Spring_theta_wre": 0.858897675,
    "G1 Spring_pr_1_99_wre": 74,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 586,
    "G2 Fall_theta_wre": 1.111719997,
    "G2 Fall_pr_1_99_wre": 64,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 611,
    "G2 Winter_theta_wre": 1.454702216,
    "G2 Winter_pr_1_99_wre": 58,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 645,
    "G2 Spring_theta_wre": 1.75932025,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 110,
    "G2 Spring_dss_wre": 676,
    "G3 Fall_theta_wre": 2.038636619,
    "G3 Fall_pr_1_99_wre": 76,
    "G3 Fall_ss_wre": 110,
    "G3 Fall_dss_wre": 704,
    "G3 Winter_theta_wre": 2.312910181,
    "G3 Winter_pr_1_99_wre": 68,
    "G3 Winter_ss_wre": 108,
    "G3 Winter_dss_wre": 731,
    "G3 Spring_theta_wre": 2.779874204,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 778
  },
  {
    "ID": 375,
    "KG Winter_theta_wre": -0.437803963,
    "KG Winter_pr_1_99_wre": 78,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 456,
    "KG Spring_theta_wre": -0.066843602,
    "KG Spring_pr_1_99_wre": 77,
    "KG Spring_ss_wre": 110,
    "KG Spring_dss_wre": 493,
    "G1 Fall_theta_wre": 0.269399621,
    "G1 Fall_pr_1_99_wre": 73,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 527,
    "G1 Winter_theta_wre": 0.418575593,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 542,
    "G1 Spring_theta_wre": 0.862846663,
    "G1 Spring_pr_1_99_wre": 74,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 586,
    "G2 Fall_theta_wre": 1.112490445,
    "G2 Fall_pr_1_99_wre": 64,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 611,
    "G2 Winter_theta_wre": 1.456545867,
    "G2 Winter_pr_1_99_wre": 58,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 646,
    "G2 Spring_theta_wre": 1.764804491,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 110,
    "G2 Spring_dss_wre": 676,
    "G3 Fall_theta_wre": 2.039416987,
    "G3 Fall_pr_1_99_wre": 76,
    "G3 Fall_ss_wre": 110,
    "G3 Fall_dss_wre": 704,
    "G3 Winter_theta_wre": 2.316010312,
    "G3 Winter_pr_1_99_wre": 68,
    "G3 Winter_ss_wre": 109,
    "G3 Winter_dss_wre": 732,
    "G3 Spring_theta_wre": 2.783098316,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 110,
    "G3 Spring_dss_wre": 778
  },
  {
    "ID": 376,
    "KG Winter_theta_wre": -0.430221312,
    "KG Winter_pr_1_99_wre": 78,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 457,
    "KG Spring_theta_wre": -0.066394746,
    "KG Spring_pr_1_99_wre": 77,
    "KG Spring_ss_wre": 110,
    "KG Spring_dss_wre": 493,
    "G1 Fall_theta_wre": 0.27306974,
    "G1 Fall_pr_1_99_wre": 74,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 527,
    "G1 Winter_theta_wre": 0.419001581,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 542,
    "G1 Spring_theta_wre": 0.864049594,
    "G1 Spring_pr_1_99_wre": 74,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 586,
    "G2 Fall_theta_wre": 1.115262461,
    "G2 Fall_pr_1_99_wre": 65,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 612,
    "G2 Winter_theta_wre": 1.456807574,
    "G2 Winter_pr_1_99_wre": 58,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 646,
    "G2 Spring_theta_wre": 1.769788286,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 110,
    "G2 Spring_dss_wre": 677,
    "G3 Fall_theta_wre": 2.082445713,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 110,
    "G3 Fall_dss_wre": 708,
    "G3 Winter_theta_wre": 2.318097042,
    "G3 Winter_pr_1_99_wre": 68,
    "G3 Winter_ss_wre": 109,
    "G3 Winter_dss_wre": 732,
    "G3 Spring_theta_wre": 2.794871275,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 779
  },
  {
    "ID": 377,
    "KG Winter_theta_wre": -0.426858387,
    "KG Winter_pr_1_99_wre": 78,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 457,
    "KG Spring_theta_wre": -0.061229263,
    "KG Spring_pr_1_99_wre": 77,
    "KG Spring_ss_wre": 110,
    "KG Spring_dss_wre": 494,
    "G1 Fall_theta_wre": 0.273173562,
    "G1 Fall_pr_1_99_wre": 74,
    "G1 Fall_ss_wre": 110,
    "G1 Fall_dss_wre": 527,
    "G1 Winter_theta_wre": 0.42012549,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 542,
    "G1 Spring_theta_wre": 0.866230482,
    "G1 Spring_pr_1_99_wre": 74,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 587,
    "G2 Fall_theta_wre": 1.121553841,
    "G2 Fall_pr_1_99_wre": 65,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 612,
    "G2 Winter_theta_wre": 1.456875142,
    "G2 Winter_pr_1_99_wre": 58,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 646,
    "G2 Spring_theta_wre": 1.77768546,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 678,
    "G3 Fall_theta_wre": 2.10248036,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 710,
    "G3 Winter_theta_wre": 2.323375301,
    "G3 Winter_pr_1_99_wre": 68,
    "G3 Winter_ss_wre": 109,
    "G3 Winter_dss_wre": 732,
    "G3 Spring_theta_wre": 2.804467,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 780
  },
  {
    "ID": 378,
    "KG Winter_theta_wre": -0.419941262,
    "KG Winter_pr_1_99_wre": 78,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 458,
    "KG Spring_theta_wre": -0.050109366,
    "KG Spring_pr_1_99_wre": 78,
    "KG Spring_ss_wre": 111,
    "KG Spring_dss_wre": 495,
    "G1 Fall_theta_wre": 0.285908388,
    "G1 Fall_pr_1_99_wre": 74,
    "G1 Fall_ss_wre": 111,
    "G1 Fall_dss_wre": 529,
    "G1 Winter_theta_wre": 0.422002371,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 542,
    "G1 Spring_theta_wre": 0.869937367,
    "G1 Spring_pr_1_99_wre": 74,
    "G1 Spring_ss_wre": 110,
    "G1 Spring_dss_wre": 587,
    "G2 Fall_theta_wre": 1.125922846,
    "G2 Fall_pr_1_99_wre": 65,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 613,
    "G2 Winter_theta_wre": 1.458496759,
    "G2 Winter_pr_1_99_wre": 58,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 646,
    "G2 Spring_theta_wre": 1.778462226,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 678,
    "G3 Fall_theta_wre": 2.103365021,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 710,
    "G3 Winter_theta_wre": 2.326523553,
    "G3 Winter_pr_1_99_wre": 68,
    "G3 Winter_ss_wre": 109,
    "G3 Winter_dss_wre": 733,
    "G3 Spring_theta_wre": 2.807174256,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 781
  },
  {
    "ID": 379,
    "KG Winter_theta_wre": -0.41520538,
    "KG Winter_pr_1_99_wre": 78,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 458,
    "KG Spring_theta_wre": -0.042794867,
    "KG Spring_pr_1_99_wre": 78,
    "KG Spring_ss_wre": 111,
    "KG Spring_dss_wre": 496,
    "G1 Fall_theta_wre": 0.290668066,
    "G1 Fall_pr_1_99_wre": 74,
    "G1 Fall_ss_wre": 111,
    "G1 Fall_dss_wre": 529,
    "G1 Winter_theta_wre": 0.42272031,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 542,
    "G1 Spring_theta_wre": 0.873652391,
    "G1 Spring_pr_1_99_wre": 74,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 587,
    "G2 Fall_theta_wre": 1.126783964,
    "G2 Fall_pr_1_99_wre": 65,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 613,
    "G2 Winter_theta_wre": 1.458738658,
    "G2 Winter_pr_1_99_wre": 58,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 646,
    "G2 Spring_theta_wre": 1.782881211,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 678,
    "G3 Fall_theta_wre": 2.107145477,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 711,
    "G3 Winter_theta_wre": 2.328326178,
    "G3 Winter_pr_1_99_wre": 68,
    "G3 Winter_ss_wre": 109,
    "G3 Winter_dss_wre": 733,
    "G3 Spring_theta_wre": 2.814067583,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 781
  },
  {
    "ID": 380,
    "KG Winter_theta_wre": -0.414720738,
    "KG Winter_pr_1_99_wre": 78,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 459,
    "KG Spring_theta_wre": -0.037913036,
    "KG Spring_pr_1_99_wre": 79,
    "KG Spring_ss_wre": 111,
    "KG Spring_dss_wre": 496,
    "G1 Fall_theta_wre": 0.318976257,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 111,
    "G1 Fall_dss_wre": 532,
    "G1 Winter_theta_wre": 0.425720152,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 543,
    "G1 Spring_theta_wre": 0.876638419,
    "G1 Spring_pr_1_99_wre": 74,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 588,
    "G2 Fall_theta_wre": 1.136931629,
    "G2 Fall_pr_1_99_wre": 66,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 614,
    "G2 Winter_theta_wre": 1.459701288,
    "G2 Winter_pr_1_99_wre": 58,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 646,
    "G2 Spring_theta_wre": 1.784258078,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 678,
    "G3 Fall_theta_wre": 2.107354205,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 711,
    "G3 Winter_theta_wre": 2.331729439,
    "G3 Winter_pr_1_99_wre": 69,
    "G3 Winter_ss_wre": 109,
    "G3 Winter_dss_wre": 733,
    "G3 Spring_theta_wre": 2.814949566,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 781
  },
  {
    "ID": 381,
    "KG Winter_theta_wre": -0.41198403,
    "KG Winter_pr_1_99_wre": 79,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 459,
    "KG Spring_theta_wre": -0.026218748,
    "KG Spring_pr_1_99_wre": 79,
    "KG Spring_ss_wre": 111,
    "KG Spring_dss_wre": 497,
    "G1 Fall_theta_wre": 0.321282004,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 111,
    "G1 Fall_dss_wre": 532,
    "G1 Winter_theta_wre": 0.42964923,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 543,
    "G1 Spring_theta_wre": 0.881298266,
    "G1 Spring_pr_1_99_wre": 75,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 588,
    "G2 Fall_theta_wre": 1.14408505,
    "G2 Fall_pr_1_99_wre": 66,
    "G2 Fall_ss_wre": 110,
    "G2 Fall_dss_wre": 614,
    "G2 Winter_theta_wre": 1.46075338,
    "G2 Winter_pr_1_99_wre": 58,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 646,
    "G2 Spring_theta_wre": 1.784360232,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 678,
    "G3 Fall_theta_wre": 2.108624563,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 711,
    "G3 Winter_theta_wre": 2.363755756,
    "G3 Winter_pr_1_99_wre": 72,
    "G3 Winter_ss_wre": 109,
    "G3 Winter_dss_wre": 736,
    "G3 Spring_theta_wre": 2.817898808,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 782
  },
  {
    "ID": 382,
    "KG Winter_theta_wre": -0.40922265,
    "KG Winter_pr_1_99_wre": 79,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 459,
    "KG Spring_theta_wre": -0.016490895,
    "KG Spring_pr_1_99_wre": 79,
    "KG Spring_ss_wre": 111,
    "KG Spring_dss_wre": 498,
    "G1 Fall_theta_wre": 0.325371114,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 111,
    "G1 Fall_dss_wre": 533,
    "G1 Winter_theta_wre": 0.431726238,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 543,
    "G1 Spring_theta_wre": 0.881689206,
    "G1 Spring_pr_1_99_wre": 76,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 588,
    "G2 Fall_theta_wre": 1.179536585,
    "G2 Fall_pr_1_99_wre": 67,
    "G2 Fall_ss_wre": 111,
    "G2 Fall_dss_wre": 618,
    "G2 Winter_theta_wre": 1.462514399,
    "G2 Winter_pr_1_99_wre": 58,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 646,
    "G2 Spring_theta_wre": 1.785682149,
    "G2 Spring_pr_1_99_wre": 79,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 679,
    "G3 Fall_theta_wre": 2.116502994,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 712,
    "G3 Winter_theta_wre": 2.367537986,
    "G3 Winter_pr_1_99_wre": 73,
    "G3 Winter_ss_wre": 110,
    "G3 Winter_dss_wre": 737,
    "G3 Spring_theta_wre": 2.818450686,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 782
  },
  {
    "ID": 383,
    "KG Winter_theta_wre": -0.40377128,
    "KG Winter_pr_1_99_wre": 79,
    "KG Winter_ss_wre": 110,
    "KG Winter_dss_wre": 460,
    "KG Spring_theta_wre": -0.012642799,
    "KG Spring_pr_1_99_wre": 79,
    "KG Spring_ss_wre": 111,
    "KG Spring_dss_wre": 499,
    "G1 Fall_theta_wre": 0.327462783,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 111,
    "G1 Fall_dss_wre": 533,
    "G1 Winter_theta_wre": 0.433293767,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 543,
    "G1 Spring_theta_wre": 0.886906562,
    "G1 Spring_pr_1_99_wre": 76,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 589,
    "G2 Fall_theta_wre": 1.183256434,
    "G2 Fall_pr_1_99_wre": 68,
    "G2 Fall_ss_wre": 111,
    "G2 Fall_dss_wre": 618,
    "G2 Winter_theta_wre": 1.470999903,
    "G2 Winter_pr_1_99_wre": 58,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 647,
    "G2 Spring_theta_wre": 1.792659516,
    "G2 Spring_pr_1_99_wre": 80,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 679,
    "G3 Fall_theta_wre": 2.117043589,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 712,
    "G3 Winter_theta_wre": 2.384994028,
    "G3 Winter_pr_1_99_wre": 74,
    "G3 Winter_ss_wre": 110,
    "G3 Winter_dss_wre": 738,
    "G3 Spring_theta_wre": 2.820478623,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 782
  },
  {
    "ID": 384,
    "KG Winter_theta_wre": -0.393054438,
    "KG Winter_pr_1_99_wre": 79,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 461,
    "KG Spring_theta_wre": -0.012544721,
    "KG Spring_pr_1_99_wre": 79,
    "KG Spring_ss_wre": 111,
    "KG Spring_dss_wre": 499,
    "G1 Fall_theta_wre": 0.328080156,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 111,
    "G1 Fall_dss_wre": 533,
    "G1 Winter_theta_wre": 0.434483226,
    "G1 Winter_pr_1_99_wre": 63,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 543,
    "G1 Spring_theta_wre": 0.889156373,
    "G1 Spring_pr_1_99_wre": 76,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 589,
    "G2 Fall_theta_wre": 1.191688672,
    "G2 Fall_pr_1_99_wre": 69,
    "G2 Fall_ss_wre": 111,
    "G2 Fall_dss_wre": 619,
    "G2 Winter_theta_wre": 1.477850375,
    "G2 Winter_pr_1_99_wre": 60,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 648,
    "G2 Spring_theta_wre": 1.796237838,
    "G2 Spring_pr_1_99_wre": 80,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 680,
    "G3 Fall_theta_wre": 2.130340264,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 713,
    "G3 Winter_theta_wre": 2.41975385,
    "G3 Winter_pr_1_99_wre": 75,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 742,
    "G3 Spring_theta_wre": 2.830262892,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 783
  },
  {
    "ID": 385,
    "KG Winter_theta_wre": -0.387743106,
    "KG Winter_pr_1_99_wre": 79,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 461,
    "KG Spring_theta_wre": -0.012329466,
    "KG Spring_pr_1_99_wre": 79,
    "KG Spring_ss_wre": 111,
    "KG Spring_dss_wre": 499,
    "G1 Fall_theta_wre": 0.329990919,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 111,
    "G1 Fall_dss_wre": 533,
    "G1 Winter_theta_wre": 0.445465302,
    "G1 Winter_pr_1_99_wre": 64,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 545,
    "G1 Spring_theta_wre": 0.891534268,
    "G1 Spring_pr_1_99_wre": 76,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 589,
    "G2 Fall_theta_wre": 1.191768848,
    "G2 Fall_pr_1_99_wre": 69,
    "G2 Fall_ss_wre": 111,
    "G2 Fall_dss_wre": 619,
    "G2 Winter_theta_wre": 1.48054984,
    "G2 Winter_pr_1_99_wre": 60,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 648,
    "G2 Spring_theta_wre": 1.802828595,
    "G2 Spring_pr_1_99_wre": 80,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 680,
    "G3 Fall_theta_wre": 2.1309815,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 713,
    "G3 Winter_theta_wre": 2.420746473,
    "G3 Winter_pr_1_99_wre": 75,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 742,
    "G3 Spring_theta_wre": 2.830639651,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 783
  },
  {
    "ID": 386,
    "KG Winter_theta_wre": -0.385926563,
    "KG Winter_pr_1_99_wre": 79,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 461,
    "KG Spring_theta_wre": -0.011369888,
    "KG Spring_pr_1_99_wre": 79,
    "KG Spring_ss_wre": 111,
    "KG Spring_dss_wre": 499,
    "G1 Fall_theta_wre": 0.33263871,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 111,
    "G1 Fall_dss_wre": 533,
    "G1 Winter_theta_wre": 0.453422844,
    "G1 Winter_pr_1_99_wre": 64,
    "G1 Winter_ss_wre": 110,
    "G1 Winter_dss_wre": 545,
    "G1 Spring_theta_wre": 0.892734137,
    "G1 Spring_pr_1_99_wre": 76,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 589,
    "G2 Fall_theta_wre": 1.19199975,
    "G2 Fall_pr_1_99_wre": 69,
    "G2 Fall_ss_wre": 111,
    "G2 Fall_dss_wre": 619,
    "G2 Winter_theta_wre": 1.488852469,
    "G2 Winter_pr_1_99_wre": 60,
    "G2 Winter_ss_wre": 110,
    "G2 Winter_dss_wre": 649,
    "G2 Spring_theta_wre": 1.806106578,
    "G2 Spring_pr_1_99_wre": 80,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 681,
    "G3 Fall_theta_wre": 2.133063523,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 713,
    "G3 Winter_theta_wre": 2.422059151,
    "G3 Winter_pr_1_99_wre": 75,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 742,
    "G3 Spring_theta_wre": 2.83757383,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 784
  },
  {
    "ID": 387,
    "KG Winter_theta_wre": -0.38494029,
    "KG Winter_pr_1_99_wre": 80,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 462,
    "KG Spring_theta_wre": -0.010884987,
    "KG Spring_pr_1_99_wre": 79,
    "KG Spring_ss_wre": 111,
    "KG Spring_dss_wre": 499,
    "G1 Fall_theta_wre": 0.340437236,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 534,
    "G1 Winter_theta_wre": 0.482259323,
    "G1 Winter_pr_1_99_wre": 66,
    "G1 Winter_ss_wre": 111,
    "G1 Winter_dss_wre": 548,
    "G1 Spring_theta_wre": 0.893899461,
    "G1 Spring_pr_1_99_wre": 76,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 589,
    "G2 Fall_theta_wre": 1.192135278,
    "G2 Fall_pr_1_99_wre": 69,
    "G2 Fall_ss_wre": 111,
    "G2 Fall_dss_wre": 619,
    "G2 Winter_theta_wre": 1.508213452,
    "G2 Winter_pr_1_99_wre": 60,
    "G2 Winter_ss_wre": 111,
    "G2 Winter_dss_wre": 651,
    "G2 Spring_theta_wre": 1.81498893,
    "G2 Spring_pr_1_99_wre": 80,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 681,
    "G3 Fall_theta_wre": 2.144207118,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 111,
    "G3 Fall_dss_wre": 714,
    "G3 Winter_theta_wre": 2.433772836,
    "G3 Winter_pr_1_99_wre": 76,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 743,
    "G3 Spring_theta_wre": 2.841815481,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 784
  },
  {
    "ID": 388,
    "KG Winter_theta_wre": -0.384073073,
    "KG Winter_pr_1_99_wre": 80,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 462,
    "KG Spring_theta_wre": 0.00621191,
    "KG Spring_pr_1_99_wre": 80,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 501,
    "G1 Fall_theta_wre": 0.343727133,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 534,
    "G1 Winter_theta_wre": 0.498705938,
    "G1 Winter_pr_1_99_wre": 67,
    "G1 Winter_ss_wre": 111,
    "G1 Winter_dss_wre": 550,
    "G1 Spring_theta_wre": 0.899211601,
    "G1 Spring_pr_1_99_wre": 77,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 590,
    "G2 Fall_theta_wre": 1.194222884,
    "G2 Fall_pr_1_99_wre": 69,
    "G2 Fall_ss_wre": 111,
    "G2 Fall_dss_wre": 619,
    "G2 Winter_theta_wre": 1.508867223,
    "G2 Winter_pr_1_99_wre": 60,
    "G2 Winter_ss_wre": 111,
    "G2 Winter_dss_wre": 651,
    "G2 Spring_theta_wre": 1.816737188,
    "G2 Spring_pr_1_99_wre": 80,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 682,
    "G3 Fall_theta_wre": 2.155122438,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 112,
    "G3 Fall_dss_wre": 716,
    "G3 Winter_theta_wre": 2.443646204,
    "G3 Winter_pr_1_99_wre": 77,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 744,
    "G3 Spring_theta_wre": 2.847391146,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 785
  },
  {
    "ID": 389,
    "KG Winter_theta_wre": -0.381225562,
    "KG Winter_pr_1_99_wre": 80,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 462,
    "KG Spring_theta_wre": 0.015106334,
    "KG Spring_pr_1_99_wre": 80,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 502,
    "G1 Fall_theta_wre": 0.345450825,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 535,
    "G1 Winter_theta_wre": 0.504117735,
    "G1 Winter_pr_1_99_wre": 67,
    "G1 Winter_ss_wre": 111,
    "G1 Winter_dss_wre": 550,
    "G1 Spring_theta_wre": 0.901638644,
    "G1 Spring_pr_1_99_wre": 77,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 590,
    "G2 Fall_theta_wre": 1.201060399,
    "G2 Fall_pr_1_99_wre": 69,
    "G2 Fall_ss_wre": 111,
    "G2 Fall_dss_wre": 620,
    "G2 Winter_theta_wre": 1.515885983,
    "G2 Winter_pr_1_99_wre": 60,
    "G2 Winter_ss_wre": 111,
    "G2 Winter_dss_wre": 652,
    "G2 Spring_theta_wre": 1.823414169,
    "G2 Spring_pr_1_99_wre": 81,
    "G2 Spring_ss_wre": 111,
    "G2 Spring_dss_wre": 682,
    "G3 Fall_theta_wre": 2.164222298,
    "G3 Fall_pr_1_99_wre": 77,
    "G3 Fall_ss_wre": 112,
    "G3 Fall_dss_wre": 716,
    "G3 Winter_theta_wre": 2.450015452,
    "G3 Winter_pr_1_99_wre": 77,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 745,
    "G3 Spring_theta_wre": 2.848328593,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 111,
    "G3 Spring_dss_wre": 785
  },
  {
    "ID": 390,
    "KG Winter_theta_wre": -0.380566508,
    "KG Winter_pr_1_99_wre": 80,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 462,
    "KG Spring_theta_wre": 0.017085453,
    "KG Spring_pr_1_99_wre": 80,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 502,
    "G1 Fall_theta_wre": 0.348429952,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 535,
    "G1 Winter_theta_wre": 0.507472626,
    "G1 Winter_pr_1_99_wre": 67,
    "G1 Winter_ss_wre": 111,
    "G1 Winter_dss_wre": 551,
    "G1 Spring_theta_wre": 0.90472785,
    "G1 Spring_pr_1_99_wre": 77,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 590,
    "G2 Fall_theta_wre": 1.203326795,
    "G2 Fall_pr_1_99_wre": 69,
    "G2 Fall_ss_wre": 111,
    "G2 Fall_dss_wre": 620,
    "G2 Winter_theta_wre": 1.524431505,
    "G2 Winter_pr_1_99_wre": 61,
    "G2 Winter_ss_wre": 111,
    "G2 Winter_dss_wre": 652,
    "G2 Spring_theta_wre": 1.829293209,
    "G2 Spring_pr_1_99_wre": 81,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 683,
    "G3 Fall_theta_wre": 2.164663223,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 112,
    "G3 Fall_dss_wre": 716,
    "G3 Winter_theta_wre": 2.451986765,
    "G3 Winter_pr_1_99_wre": 77,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 745,
    "G3 Spring_theta_wre": 2.854001148,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 112,
    "G3 Spring_dss_wre": 785
  },
  {
    "ID": 391,
    "KG Winter_theta_wre": -0.376283718,
    "KG Winter_pr_1_99_wre": 80,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 462,
    "KG Spring_theta_wre": 0.026950183,
    "KG Spring_pr_1_99_wre": 81,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 503,
    "G1 Fall_theta_wre": 0.355559115,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 536,
    "G1 Winter_theta_wre": 0.511505058,
    "G1 Winter_pr_1_99_wre": 67,
    "G1 Winter_ss_wre": 111,
    "G1 Winter_dss_wre": 551,
    "G1 Spring_theta_wre": 0.907846982,
    "G1 Spring_pr_1_99_wre": 77,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 591,
    "G2 Fall_theta_wre": 1.20720055,
    "G2 Fall_pr_1_99_wre": 69,
    "G2 Fall_ss_wre": 111,
    "G2 Fall_dss_wre": 621,
    "G2 Winter_theta_wre": 1.53468769,
    "G2 Winter_pr_1_99_wre": 61,
    "G2 Winter_ss_wre": 111,
    "G2 Winter_dss_wre": 653,
    "G2 Spring_theta_wre": 1.830735766,
    "G2 Spring_pr_1_99_wre": 81,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 683,
    "G3 Fall_theta_wre": 2.178195896,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 112,
    "G3 Fall_dss_wre": 718,
    "G3 Winter_theta_wre": 2.454831367,
    "G3 Winter_pr_1_99_wre": 77,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 745,
    "G3 Spring_theta_wre": 2.861668069,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 112,
    "G3 Spring_dss_wre": 786
  },
  {
    "ID": 392,
    "KG Winter_theta_wre": -0.372761781,
    "KG Winter_pr_1_99_wre": 80,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 463,
    "KG Spring_theta_wre": 0.039988989,
    "KG Spring_pr_1_99_wre": 81,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 504,
    "G1 Fall_theta_wre": 0.35856842,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 536,
    "G1 Winter_theta_wre": 0.519314665,
    "G1 Winter_pr_1_99_wre": 68,
    "G1 Winter_ss_wre": 112,
    "G1 Winter_dss_wre": 552,
    "G1 Spring_theta_wre": 0.908332931,
    "G1 Spring_pr_1_99_wre": 77,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 591,
    "G2 Fall_theta_wre": 1.227726196,
    "G2 Fall_pr_1_99_wre": 69,
    "G2 Fall_ss_wre": 112,
    "G2 Fall_dss_wre": 623,
    "G2 Winter_theta_wre": 1.538344401,
    "G2 Winter_pr_1_99_wre": 62,
    "G2 Winter_ss_wre": 111,
    "G2 Winter_dss_wre": 654,
    "G2 Spring_theta_wre": 1.843821938,
    "G2 Spring_pr_1_99_wre": 81,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 684,
    "G3 Fall_theta_wre": 2.182194565,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 112,
    "G3 Fall_dss_wre": 718,
    "G3 Winter_theta_wre": 2.458825832,
    "G3 Winter_pr_1_99_wre": 77,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 746,
    "G3 Spring_theta_wre": 2.869772288,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 112,
    "G3 Spring_dss_wre": 787
  },
  {
    "ID": 393,
    "KG Winter_theta_wre": -0.364944186,
    "KG Winter_pr_1_99_wre": 80,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 464,
    "KG Spring_theta_wre": 0.040620245,
    "KG Spring_pr_1_99_wre": 81,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 504,
    "G1 Fall_theta_wre": 0.359690108,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 536,
    "G1 Winter_theta_wre": 0.526862318,
    "G1 Winter_pr_1_99_wre": 68,
    "G1 Winter_ss_wre": 112,
    "G1 Winter_dss_wre": 553,
    "G1 Spring_theta_wre": 0.909004064,
    "G1 Spring_pr_1_99_wre": 77,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 591,
    "G2 Fall_theta_wre": 1.228555335,
    "G2 Fall_pr_1_99_wre": 70,
    "G2 Fall_ss_wre": 112,
    "G2 Fall_dss_wre": 623,
    "G2 Winter_theta_wre": 1.544256536,
    "G2 Winter_pr_1_99_wre": 62,
    "G2 Winter_ss_wre": 111,
    "G2 Winter_dss_wre": 654,
    "G2 Spring_theta_wre": 1.855747704,
    "G2 Spring_pr_1_99_wre": 81,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 686,
    "G3 Fall_theta_wre": 2.194244994,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 112,
    "G3 Fall_dss_wre": 719,
    "G3 Winter_theta_wre": 2.462495725,
    "G3 Winter_pr_1_99_wre": 77,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 746,
    "G3 Spring_theta_wre": 2.87244649,
    "G3 Spring_pr_1_99_wre": 95,
    "G3 Spring_ss_wre": 112,
    "G3 Spring_dss_wre": 787
  },
  {
    "ID": 394,
    "KG Winter_theta_wre": -0.350401263,
    "KG Winter_pr_1_99_wre": 80,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 465,
    "KG Spring_theta_wre": 0.043022804,
    "KG Spring_pr_1_99_wre": 81,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 504,
    "G1 Fall_theta_wre": 0.371681304,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 537,
    "G1 Winter_theta_wre": 0.529073217,
    "G1 Winter_pr_1_99_wre": 68,
    "G1 Winter_ss_wre": 112,
    "G1 Winter_dss_wre": 553,
    "G1 Spring_theta_wre": 0.910668627,
    "G1 Spring_pr_1_99_wre": 77,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 591,
    "G2 Fall_theta_wre": 1.233210134,
    "G2 Fall_pr_1_99_wre": 70,
    "G2 Fall_ss_wre": 112,
    "G2 Fall_dss_wre": 623,
    "G2 Winter_theta_wre": 1.547500774,
    "G2 Winter_pr_1_99_wre": 62,
    "G2 Winter_ss_wre": 112,
    "G2 Winter_dss_wre": 655,
    "G2 Spring_theta_wre": 1.871124866,
    "G2 Spring_pr_1_99_wre": 81,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 687,
    "G3 Fall_theta_wre": 2.196169624,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 112,
    "G3 Fall_dss_wre": 720,
    "G3 Winter_theta_wre": 2.464511821,
    "G3 Winter_pr_1_99_wre": 77,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 746,
    "G3 Spring_theta_wre": 2.888820846,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 112,
    "G3 Spring_dss_wre": 789
  },
  {
    "ID": 395,
    "KG Winter_theta_wre": -0.349672367,
    "KG Winter_pr_1_99_wre": 80,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 465,
    "KG Spring_theta_wre": 0.046558079,
    "KG Spring_pr_1_99_wre": 81,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 505,
    "G1 Fall_theta_wre": 0.372134472,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 537,
    "G1 Winter_theta_wre": 0.541324426,
    "G1 Winter_pr_1_99_wre": 69,
    "G1 Winter_ss_wre": 112,
    "G1 Winter_dss_wre": 554,
    "G1 Spring_theta_wre": 0.911699869,
    "G1 Spring_pr_1_99_wre": 77,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 591,
    "G2 Fall_theta_wre": 1.242025475,
    "G2 Fall_pr_1_99_wre": 71,
    "G2 Fall_ss_wre": 112,
    "G2 Fall_dss_wre": 624,
    "G2 Winter_theta_wre": 1.552137167,
    "G2 Winter_pr_1_99_wre": 62,
    "G2 Winter_ss_wre": 112,
    "G2 Winter_dss_wre": 655,
    "G2 Spring_theta_wre": 1.873902965,
    "G2 Spring_pr_1_99_wre": 81,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 687,
    "G3 Fall_theta_wre": 2.199449017,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 112,
    "G3 Fall_dss_wre": 720,
    "G3 Winter_theta_wre": 2.471243917,
    "G3 Winter_pr_1_99_wre": 77,
    "G3 Winter_ss_wre": 111,
    "G3 Winter_dss_wre": 747,
    "G3 Spring_theta_wre": 2.889225202,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 112,
    "G3 Spring_dss_wre": 789
  },
  {
    "ID": 396,
    "KG Winter_theta_wre": -0.344676992,
    "KG Winter_pr_1_99_wre": 81,
    "KG Winter_ss_wre": 111,
    "KG Winter_dss_wre": 466,
    "KG Spring_theta_wre": 0.050822346,
    "KG Spring_pr_1_99_wre": 81,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 505,
    "G1 Fall_theta_wre": 0.380581475,
    "G1 Fall_pr_1_99_wre": 75,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 538,
    "G1 Winter_theta_wre": 0.543778674,
    "G1 Winter_pr_1_99_wre": 69,
    "G1 Winter_ss_wre": 112,
    "G1 Winter_dss_wre": 554,
    "G1 Spring_theta_wre": 0.916448068,
    "G1 Spring_pr_1_99_wre": 78,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 592,
    "G2 Fall_theta_wre": 1.243730146,
    "G2 Fall_pr_1_99_wre": 71,
    "G2 Fall_ss_wre": 112,
    "G2 Fall_dss_wre": 624,
    "G2 Winter_theta_wre": 1.570511271,
    "G2 Winter_pr_1_99_wre": 63,
    "G2 Winter_ss_wre": 112,
    "G2 Winter_dss_wre": 657,
    "G2 Spring_theta_wre": 1.875481957,
    "G2 Spring_pr_1_99_wre": 81,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 688,
    "G3 Fall_theta_wre": 2.199878768,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 112,
    "G3 Fall_dss_wre": 720,
    "G3 Winter_theta_wre": 2.474218068,
    "G3 Winter_pr_1_99_wre": 77,
    "G3 Winter_ss_wre": 112,
    "G3 Winter_dss_wre": 747,
    "G3 Spring_theta_wre": 2.89147208,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 112,
    "G3 Spring_dss_wre": 789
  },
  {
    "ID": 397,
    "KG Winter_theta_wre": -0.340301922,
    "KG Winter_pr_1_99_wre": 81,
    "KG Winter_ss_wre": 112,
    "KG Winter_dss_wre": 466,
    "KG Spring_theta_wre": 0.060263625,
    "KG Spring_pr_1_99_wre": 82,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 506,
    "G1 Fall_theta_wre": 0.386077272,
    "G1 Fall_pr_1_99_wre": 76,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 539,
    "G1 Winter_theta_wre": 0.55343274,
    "G1 Winter_pr_1_99_wre": 69,
    "G1 Winter_ss_wre": 112,
    "G1 Winter_dss_wre": 555,
    "G1 Spring_theta_wre": 0.919152899,
    "G1 Spring_pr_1_99_wre": 78,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 592,
    "G2 Fall_theta_wre": 1.243787951,
    "G2 Fall_pr_1_99_wre": 71,
    "G2 Fall_ss_wre": 112,
    "G2 Fall_dss_wre": 624,
    "G2 Winter_theta_wre": 1.584610674,
    "G2 Winter_pr_1_99_wre": 67,
    "G2 Winter_ss_wre": 112,
    "G2 Winter_dss_wre": 658,
    "G2 Spring_theta_wre": 1.878354822,
    "G2 Spring_pr_1_99_wre": 82,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 688,
    "G3 Fall_theta_wre": 2.203241699,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 720,
    "G3 Winter_theta_wre": 2.474662606,
    "G3 Winter_pr_1_99_wre": 77,
    "G3 Winter_ss_wre": 112,
    "G3 Winter_dss_wre": 747,
    "G3 Spring_theta_wre": 2.892673702,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 112,
    "G3 Spring_dss_wre": 789
  },
  {
    "ID": 398,
    "KG Winter_theta_wre": -0.336797538,
    "KG Winter_pr_1_99_wre": 81,
    "KG Winter_ss_wre": 112,
    "KG Winter_dss_wre": 466,
    "KG Spring_theta_wre": 0.061583256,
    "KG Spring_pr_1_99_wre": 82,
    "KG Spring_ss_wre": 112,
    "KG Spring_dss_wre": 506,
    "G1 Fall_theta_wre": 0.38847122,
    "G1 Fall_pr_1_99_wre": 76,
    "G1 Fall_ss_wre": 112,
    "G1 Fall_dss_wre": 539,
    "G1 Winter_theta_wre": 0.555307991,
    "G1 Winter_pr_1_99_wre": 69,
    "G1 Winter_ss_wre": 112,
    "G1 Winter_dss_wre": 556,
    "G1 Spring_theta_wre": 0.922456102,
    "G1 Spring_pr_1_99_wre": 78,
    "G1 Spring_ss_wre": 111,
    "G1 Spring_dss_wre": 592,
    "G2 Fall_theta_wre": 1.255653174,
    "G2 Fall_pr_1_99_wre": 72,
    "G2 Fall_ss_wre": 112,
    "G2 Fall_dss_wre": 626,
    "G2 Winter_theta_wre": 1.605434868,
    "G2 Winter_pr_1_99_wre": 68,
    "G2 Winter_ss_wre": 113,
    "G2 Winter_dss_wre": 661,
    "G2 Spring_theta_wre": 1.87857348,
    "G2 Spring_pr_1_99_wre": 82,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 688,
    "G3 Fall_theta_wre": 2.217014207,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 722,
    "G3 Winter_theta_wre": 2.502436062,
    "G3 Winter_pr_1_99_wre": 79,
    "G3 Winter_ss_wre": 112,
    "G3 Winter_dss_wre": 750,
    "G3 Spring_theta_wre": 2.898288215,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 112,
    "G3 Spring_dss_wre": 790
  },
  {
    "ID": 399,
    "KG Winter_theta_wre": -0.335562148,
    "KG Winter_pr_1_99_wre": 81,
    "KG Winter_ss_wre": 112,
    "KG Winter_dss_wre": 466,
    "KG Spring_theta_wre": 0.071350904,
    "KG Spring_pr_1_99_wre": 82,
    "KG Spring_ss_wre": 113,
    "KG Spring_dss_wre": 507,
    "G1 Fall_theta_wre": 0.405791309,
    "G1 Fall_pr_1_99_wre": 76,
    "G1 Fall_ss_wre": 113,
    "G1 Fall_dss_wre": 541,
    "G1 Winter_theta_wre": 0.566035973,
    "G1 Winter_pr_1_99_wre": 69,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 557,
    "G1 Spring_theta_wre": 0.930737241,
    "G1 Spring_pr_1_99_wre": 78,
    "G1 Spring_ss_wre": 112,
    "G1 Spring_dss_wre": 593,
    "G2 Fall_theta_wre": 1.259498073,
    "G2 Fall_pr_1_99_wre": 72,
    "G2 Fall_ss_wre": 112,
    "G2 Fall_dss_wre": 626,
    "G2 Winter_theta_wre": 1.605472771,
    "G2 Winter_pr_1_99_wre": 68,
    "G2 Winter_ss_wre": 113,
    "G2 Winter_dss_wre": 661,
    "G2 Spring_theta_wre": 1.878663076,
    "G2 Spring_pr_1_99_wre": 82,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 688,
    "G3 Fall_theta_wre": 2.219290886,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 722,
    "G3 Winter_theta_wre": 2.50635526,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 112,
    "G3 Winter_dss_wre": 751,
    "G3 Spring_theta_wre": 2.906899503,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 113,
    "G3 Spring_dss_wre": 791
  },
  {
    "ID": 400,
    "KG Winter_theta_wre": -0.332821309,
    "KG Winter_pr_1_99_wre": 81,
    "KG Winter_ss_wre": 112,
    "KG Winter_dss_wre": 467,
    "KG Spring_theta_wre": 0.073507611,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 113,
    "KG Spring_dss_wre": 507,
    "G1 Fall_theta_wre": 0.417453607,
    "G1 Fall_pr_1_99_wre": 76,
    "G1 Fall_ss_wre": 113,
    "G1 Fall_dss_wre": 542,
    "G1 Winter_theta_wre": 0.571455917,
    "G1 Winter_pr_1_99_wre": 70,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 557,
    "G1 Spring_theta_wre": 0.936701687,
    "G1 Spring_pr_1_99_wre": 78,
    "G1 Spring_ss_wre": 112,
    "G1 Spring_dss_wre": 594,
    "G2 Fall_theta_wre": 1.26196715,
    "G2 Fall_pr_1_99_wre": 72,
    "G2 Fall_ss_wre": 112,
    "G2 Fall_dss_wre": 626,
    "G2 Winter_theta_wre": 1.607650777,
    "G2 Winter_pr_1_99_wre": 68,
    "G2 Winter_ss_wre": 113,
    "G2 Winter_dss_wre": 661,
    "G2 Spring_theta_wre": 1.878910349,
    "G2 Spring_pr_1_99_wre": 82,
    "G2 Spring_ss_wre": 112,
    "G2 Spring_dss_wre": 688,
    "G3 Fall_theta_wre": 2.225831393,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 723,
    "G3 Winter_theta_wre": 2.511009451,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 112,
    "G3 Winter_dss_wre": 751,
    "G3 Spring_theta_wre": 2.917965579,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 113,
    "G3 Spring_dss_wre": 792
  },
  {
    "ID": 401,
    "KG Winter_theta_wre": -0.328521786,
    "KG Winter_pr_1_99_wre": 81,
    "KG Winter_ss_wre": 112,
    "KG Winter_dss_wre": 467,
    "KG Spring_theta_wre": 0.076409813,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 113,
    "KG Spring_dss_wre": 508,
    "G1 Fall_theta_wre": 0.41793158,
    "G1 Fall_pr_1_99_wre": 76,
    "G1 Fall_ss_wre": 113,
    "G1 Fall_dss_wre": 542,
    "G1 Winter_theta_wre": 0.579265885,
    "G1 Winter_pr_1_99_wre": 70,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 558,
    "G1 Spring_theta_wre": 0.967152201,
    "G1 Spring_pr_1_99_wre": 79,
    "G1 Spring_ss_wre": 112,
    "G1 Spring_dss_wre": 597,
    "G2 Fall_theta_wre": 1.269136048,
    "G2 Fall_pr_1_99_wre": 72,
    "G2 Fall_ss_wre": 113,
    "G2 Fall_dss_wre": 627,
    "G2 Winter_theta_wre": 1.611593259,
    "G2 Winter_pr_1_99_wre": 71,
    "G2 Winter_ss_wre": 113,
    "G2 Winter_dss_wre": 661,
    "G2 Spring_theta_wre": 1.889597726,
    "G2 Spring_pr_1_99_wre": 83,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 689,
    "G3 Fall_theta_wre": 2.231042688,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 723,
    "G3 Winter_theta_wre": 2.518414336,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 112,
    "G3 Winter_dss_wre": 752,
    "G3 Spring_theta_wre": 2.925953857,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 113,
    "G3 Spring_dss_wre": 793
  },
  {
    "ID": 402,
    "KG Winter_theta_wre": -0.304279831,
    "KG Winter_pr_1_99_wre": 82,
    "KG Winter_ss_wre": 112,
    "KG Winter_dss_wre": 470,
    "KG Spring_theta_wre": 0.077717337,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 113,
    "KG Spring_dss_wre": 508,
    "G1 Fall_theta_wre": 0.420118565,
    "G1 Fall_pr_1_99_wre": 76,
    "G1 Fall_ss_wre": 113,
    "G1 Fall_dss_wre": 542,
    "G1 Winter_theta_wre": 0.591857823,
    "G1 Winter_pr_1_99_wre": 70,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 559,
    "G1 Spring_theta_wre": 0.968096656,
    "G1 Spring_pr_1_99_wre": 79,
    "G1 Spring_ss_wre": 112,
    "G1 Spring_dss_wre": 597,
    "G2 Fall_theta_wre": 1.269844837,
    "G2 Fall_pr_1_99_wre": 72,
    "G2 Fall_ss_wre": 113,
    "G2 Fall_dss_wre": 627,
    "G2 Winter_theta_wre": 1.618780247,
    "G2 Winter_pr_1_99_wre": 74,
    "G2 Winter_ss_wre": 113,
    "G2 Winter_dss_wre": 662,
    "G2 Spring_theta_wre": 1.892872688,
    "G2 Spring_pr_1_99_wre": 83,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 689,
    "G3 Fall_theta_wre": 2.231376621,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 723,
    "G3 Winter_theta_wre": 2.530269364,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 113,
    "G3 Winter_dss_wre": 753,
    "G3 Spring_theta_wre": 2.932923964,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 113,
    "G3 Spring_dss_wre": 793
  },
  {
    "ID": 403,
    "KG Winter_theta_wre": -0.303498686,
    "KG Winter_pr_1_99_wre": 82,
    "KG Winter_ss_wre": 112,
    "KG Winter_dss_wre": 470,
    "KG Spring_theta_wre": 0.078602877,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 113,
    "KG Spring_dss_wre": 508,
    "G1 Fall_theta_wre": 0.439615675,
    "G1 Fall_pr_1_99_wre": 77,
    "G1 Fall_ss_wre": 113,
    "G1 Fall_dss_wre": 544,
    "G1 Winter_theta_wre": 0.598283339,
    "G1 Winter_pr_1_99_wre": 70,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 560,
    "G1 Spring_theta_wre": 0.973204,
    "G1 Spring_pr_1_99_wre": 79,
    "G1 Spring_ss_wre": 112,
    "G1 Spring_dss_wre": 597,
    "G2 Fall_theta_wre": 1.277333245,
    "G2 Fall_pr_1_99_wre": 72,
    "G2 Fall_ss_wre": 113,
    "G2 Fall_dss_wre": 628,
    "G2 Winter_theta_wre": 1.62361663,
    "G2 Winter_pr_1_99_wre": 74,
    "G2 Winter_ss_wre": 113,
    "G2 Winter_dss_wre": 662,
    "G2 Spring_theta_wre": 1.892938568,
    "G2 Spring_pr_1_99_wre": 83,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 689,
    "G3 Fall_theta_wre": 2.233161312,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 723,
    "G3 Winter_theta_wre": 2.533188767,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 113,
    "G3 Winter_dss_wre": 753,
    "G3 Spring_theta_wre": 2.939839008,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 113,
    "G3 Spring_dss_wre": 794
  },
  {
    "ID": 404,
    "KG Winter_theta_wre": -0.299276893,
    "KG Winter_pr_1_99_wre": 82,
    "KG Winter_ss_wre": 112,
    "KG Winter_dss_wre": 470,
    "KG Spring_theta_wre": 0.097973411,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 113,
    "KG Spring_dss_wre": 510,
    "G1 Fall_theta_wre": 0.44539108,
    "G1 Fall_pr_1_99_wre": 77,
    "G1 Fall_ss_wre": 114,
    "G1 Fall_dss_wre": 545,
    "G1 Winter_theta_wre": 0.5999104,
    "G1 Winter_pr_1_99_wre": 70,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 560,
    "G1 Spring_theta_wre": 0.978433379,
    "G1 Spring_pr_1_99_wre": 79,
    "G1 Spring_ss_wre": 112,
    "G1 Spring_dss_wre": 598,
    "G2 Fall_theta_wre": 1.279564171,
    "G2 Fall_pr_1_99_wre": 72,
    "G2 Fall_ss_wre": 113,
    "G2 Fall_dss_wre": 628,
    "G2 Winter_theta_wre": 1.632325419,
    "G2 Winter_pr_1_99_wre": 74,
    "G2 Winter_ss_wre": 113,
    "G2 Winter_dss_wre": 663,
    "G2 Spring_theta_wre": 1.893819434,
    "G2 Spring_pr_1_99_wre": 83,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 689,
    "G3 Fall_theta_wre": 2.235350035,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 724,
    "G3 Winter_theta_wre": 2.544994734,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 113,
    "G3 Winter_dss_wre": 754,
    "G3 Spring_theta_wre": 2.944453292,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 113,
    "G3 Spring_dss_wre": 794
  },
  {
    "ID": 405,
    "KG Winter_theta_wre": -0.294539219,
    "KG Winter_pr_1_99_wre": 82,
    "KG Winter_ss_wre": 112,
    "KG Winter_dss_wre": 471,
    "KG Spring_theta_wre": 0.101857706,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 113,
    "KG Spring_dss_wre": 510,
    "G1 Fall_theta_wre": 0.485521328,
    "G1 Fall_pr_1_99_wre": 77,
    "G1 Fall_ss_wre": 114,
    "G1 Fall_dss_wre": 549,
    "G1 Winter_theta_wre": 0.601805083,
    "G1 Winter_pr_1_99_wre": 70,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 560,
    "G1 Spring_theta_wre": 0.992083786,
    "G1 Spring_pr_1_99_wre": 81,
    "G1 Spring_ss_wre": 113,
    "G1 Spring_dss_wre": 599,
    "G2 Fall_theta_wre": 1.280877343,
    "G2 Fall_pr_1_99_wre": 72,
    "G2 Fall_ss_wre": 113,
    "G2 Fall_dss_wre": 628,
    "G2 Winter_theta_wre": 1.646492193,
    "G2 Winter_pr_1_99_wre": 74,
    "G2 Winter_ss_wre": 113,
    "G2 Winter_dss_wre": 665,
    "G2 Spring_theta_wre": 1.898117501,
    "G2 Spring_pr_1_99_wre": 83,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 690,
    "G3 Fall_theta_wre": 2.239436859,
    "G3 Fall_pr_1_99_wre": 79,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 724,
    "G3 Winter_theta_wre": 2.545891051,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 113,
    "G3 Winter_dss_wre": 755,
    "G3 Spring_theta_wre": 2.949165222,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 113,
    "G3 Spring_dss_wre": 795
  },
  {
    "ID": 406,
    "KG Winter_theta_wre": -0.292487991,
    "KG Winter_pr_1_99_wre": 82,
    "KG Winter_ss_wre": 112,
    "KG Winter_dss_wre": 471,
    "KG Spring_theta_wre": 0.102513099,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 113,
    "KG Spring_dss_wre": 510,
    "G1 Fall_theta_wre": 0.487642512,
    "G1 Fall_pr_1_99_wre": 77,
    "G1 Fall_ss_wre": 114,
    "G1 Fall_dss_wre": 549,
    "G1 Winter_theta_wre": 0.60502393,
    "G1 Winter_pr_1_99_wre": 70,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 561,
    "G1 Spring_theta_wre": 0.992721822,
    "G1 Spring_pr_1_99_wre": 81,
    "G1 Spring_ss_wre": 113,
    "G1 Spring_dss_wre": 599,
    "G2 Fall_theta_wre": 1.284693772,
    "G2 Fall_pr_1_99_wre": 72,
    "G2 Fall_ss_wre": 113,
    "G2 Fall_dss_wre": 628,
    "G2 Winter_theta_wre": 1.65635142,
    "G2 Winter_pr_1_99_wre": 74,
    "G2 Winter_ss_wre": 113,
    "G2 Winter_dss_wre": 666,
    "G2 Spring_theta_wre": 1.904195941,
    "G2 Spring_pr_1_99_wre": 83,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 690,
    "G3 Fall_theta_wre": 2.247043163,
    "G3 Fall_pr_1_99_wre": 80,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 725,
    "G3 Winter_theta_wre": 2.546466994,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 113,
    "G3 Winter_dss_wre": 755,
    "G3 Spring_theta_wre": 2.955581103,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 113,
    "G3 Spring_dss_wre": 796
  },
  {
    "ID": 407,
    "KG Winter_theta_wre": -0.283467133,
    "KG Winter_pr_1_99_wre": 83,
    "KG Winter_ss_wre": 113,
    "KG Winter_dss_wre": 472,
    "KG Spring_theta_wre": 0.10852596,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 113,
    "KG Spring_dss_wre": 511,
    "G1 Fall_theta_wre": 0.487862767,
    "G1 Fall_pr_1_99_wre": 77,
    "G1 Fall_ss_wre": 114,
    "G1 Fall_dss_wre": 549,
    "G1 Winter_theta_wre": 0.61006486,
    "G1 Winter_pr_1_99_wre": 70,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 561,
    "G1 Spring_theta_wre": 0.994284226,
    "G1 Spring_pr_1_99_wre": 81,
    "G1 Spring_ss_wre": 113,
    "G1 Spring_dss_wre": 599,
    "G2 Fall_theta_wre": 1.299083171,
    "G2 Fall_pr_1_99_wre": 73,
    "G2 Fall_ss_wre": 113,
    "G2 Fall_dss_wre": 630,
    "G2 Winter_theta_wre": 1.657477467,
    "G2 Winter_pr_1_99_wre": 74,
    "G2 Winter_ss_wre": 114,
    "G2 Winter_dss_wre": 666,
    "G2 Spring_theta_wre": 1.905191336,
    "G2 Spring_pr_1_99_wre": 83,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 691,
    "G3 Fall_theta_wre": 2.252480869,
    "G3 Fall_pr_1_99_wre": 80,
    "G3 Fall_ss_wre": 113,
    "G3 Fall_dss_wre": 725,
    "G3 Winter_theta_wre": 2.559686716,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 113,
    "G3 Winter_dss_wre": 756,
    "G3 Spring_theta_wre": 2.962382036,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 114,
    "G3 Spring_dss_wre": 796
  },
  {
    "ID": 408,
    "KG Winter_theta_wre": -0.276847977,
    "KG Winter_pr_1_99_wre": 83,
    "KG Winter_ss_wre": 113,
    "KG Winter_dss_wre": 472,
    "KG Spring_theta_wre": 0.110826545,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 113,
    "KG Spring_dss_wre": 511,
    "G1 Fall_theta_wre": 0.488913922,
    "G1 Fall_pr_1_99_wre": 77,
    "G1 Fall_ss_wre": 114,
    "G1 Fall_dss_wre": 549,
    "G1 Winter_theta_wre": 0.611481062,
    "G1 Winter_pr_1_99_wre": 70,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 561,
    "G1 Spring_theta_wre": 0.996075349,
    "G1 Spring_pr_1_99_wre": 81,
    "G1 Spring_ss_wre": 113,
    "G1 Spring_dss_wre": 600,
    "G2 Fall_theta_wre": 1.302046913,
    "G2 Fall_pr_1_99_wre": 73,
    "G2 Fall_ss_wre": 113,
    "G2 Fall_dss_wre": 630,
    "G2 Winter_theta_wre": 1.659897,
    "G2 Winter_pr_1_99_wre": 74,
    "G2 Winter_ss_wre": 114,
    "G2 Winter_dss_wre": 666,
    "G2 Spring_theta_wre": 1.90543243,
    "G2 Spring_pr_1_99_wre": 83,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 691,
    "G3 Fall_theta_wre": 2.258147207,
    "G3 Fall_pr_1_99_wre": 80,
    "G3 Fall_ss_wre": 114,
    "G3 Fall_dss_wre": 726,
    "G3 Winter_theta_wre": 2.560969175,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 113,
    "G3 Winter_dss_wre": 756,
    "G3 Spring_theta_wre": 2.964953859,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 114,
    "G3 Spring_dss_wre": 796
  },
  {
    "ID": 409,
    "KG Winter_theta_wre": -0.260829634,
    "KG Winter_pr_1_99_wre": 84,
    "KG Winter_ss_wre": 113,
    "KG Winter_dss_wre": 474,
    "KG Spring_theta_wre": 0.122799913,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 114,
    "KG Spring_dss_wre": 512,
    "G1 Fall_theta_wre": 0.494324811,
    "G1 Fall_pr_1_99_wre": 77,
    "G1 Fall_ss_wre": 114,
    "G1 Fall_dss_wre": 549,
    "G1 Winter_theta_wre": 0.615538146,
    "G1 Winter_pr_1_99_wre": 71,
    "G1 Winter_ss_wre": 113,
    "G1 Winter_dss_wre": 562,
    "G1 Spring_theta_wre": 0.999054874,
    "G1 Spring_pr_1_99_wre": 81,
    "G1 Spring_ss_wre": 113,
    "G1 Spring_dss_wre": 600,
    "G2 Fall_theta_wre": 1.330054001,
    "G2 Fall_pr_1_99_wre": 74,
    "G2 Fall_ss_wre": 114,
    "G2 Fall_dss_wre": 633,
    "G2 Winter_theta_wre": 1.663904733,
    "G2 Winter_pr_1_99_wre": 74,
    "G2 Winter_ss_wre": 114,
    "G2 Winter_dss_wre": 666,
    "G2 Spring_theta_wre": 1.907169689,
    "G2 Spring_pr_1_99_wre": 83,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 691,
    "G3 Fall_theta_wre": 2.261169152,
    "G3 Fall_pr_1_99_wre": 80,
    "G3 Fall_ss_wre": 114,
    "G3 Fall_dss_wre": 726,
    "G3 Winter_theta_wre": 2.568314887,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 113,
    "G3 Winter_dss_wre": 757,
    "G3 Spring_theta_wre": 2.9765913,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 114,
    "G3 Spring_dss_wre": 798
  },
  {
    "ID": 410,
    "KG Winter_theta_wre": -0.26044719,
    "KG Winter_pr_1_99_wre": 84,
    "KG Winter_ss_wre": 113,
    "KG Winter_dss_wre": 474,
    "KG Spring_theta_wre": 0.127799191,
    "KG Spring_pr_1_99_wre": 83,
    "KG Spring_ss_wre": 114,
    "KG Spring_dss_wre": 513,
    "G1 Fall_theta_wre": 0.499656631,
    "G1 Fall_pr_1_99_wre": 77,
    "G1 Fall_ss_wre": 114,
    "G1 Fall_dss_wre": 550,
    "G1 Winter_theta_wre": 0.620728882,
    "G1 Winter_pr_1_99_wre": 71,
    "G1 Winter_ss_wre": 114,
    "G1 Winter_dss_wre": 562,
    "G1 Spring_theta_wre": 1.010857217,
    "G1 Spring_pr_1_99_wre": 81,
    "G1 Spring_ss_wre": 113,
    "G1 Spring_dss_wre": 601,
    "G2 Fall_theta_wre": 1.335465252,
    "G2 Fall_pr_1_99_wre": 74,
    "G2 Fall_ss_wre": 114,
    "G2 Fall_dss_wre": 634,
    "G2 Winter_theta_wre": 1.666846435,
    "G2 Winter_pr_1_99_wre": 74,
    "G2 Winter_ss_wre": 114,
    "G2 Winter_dss_wre": 667,
    "G2 Spring_theta_wre": 1.912280601,
    "G2 Spring_pr_1_99_wre": 83,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 691,
    "G3 Fall_theta_wre": 2.27623166,
    "G3 Fall_pr_1_99_wre": 80,
    "G3 Fall_ss_wre": 114,
    "G3 Fall_dss_wre": 728,
    "G3 Winter_theta_wre": 2.568787629,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 113,
    "G3 Winter_dss_wre": 757,
    "G3 Spring_theta_wre": 2.984779068,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 114,
    "G3 Spring_dss_wre": 798
  },
  {
    "ID": 411,
    "KG Winter_theta_wre": -0.260090661,
    "KG Winter_pr_1_99_wre": 84,
    "KG Winter_ss_wre": 113,
    "KG Winter_dss_wre": 474,
    "KG Spring_theta_wre": 0.141872924,
    "KG Spring_pr_1_99_wre": 84,
    "KG Spring_ss_wre": 114,
    "KG Spring_dss_wre": 514,
    "G1 Fall_theta_wre": 0.503932562,
    "G1 Fall_pr_1_99_wre": 77,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 550,
    "G1 Winter_theta_wre": 0.624342817,
    "G1 Winter_pr_1_99_wre": 71,
    "G1 Winter_ss_wre": 114,
    "G1 Winter_dss_wre": 562,
    "G1 Spring_theta_wre": 1.015845172,
    "G1 Spring_pr_1_99_wre": 81,
    "G1 Spring_ss_wre": 113,
    "G1 Spring_dss_wre": 602,
    "G2 Fall_theta_wre": 1.340254683,
    "G2 Fall_pr_1_99_wre": 74,
    "G2 Fall_ss_wre": 114,
    "G2 Fall_dss_wre": 634,
    "G2 Winter_theta_wre": 1.677647253,
    "G2 Winter_pr_1_99_wre": 75,
    "G2 Winter_ss_wre": 114,
    "G2 Winter_dss_wre": 668,
    "G2 Spring_theta_wre": 1.921104703,
    "G2 Spring_pr_1_99_wre": 84,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 692,
    "G3 Fall_theta_wre": 2.283597894,
    "G3 Fall_pr_1_99_wre": 80,
    "G3 Fall_ss_wre": 114,
    "G3 Fall_dss_wre": 728,
    "G3 Winter_theta_wre": 2.579270825,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 114,
    "G3 Winter_dss_wre": 758,
    "G3 Spring_theta_wre": 2.986867483,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 114,
    "G3 Spring_dss_wre": 799
  },
  {
    "ID": 412,
    "KG Winter_theta_wre": -0.251969158,
    "KG Winter_pr_1_99_wre": 84,
    "KG Winter_ss_wre": 113,
    "KG Winter_dss_wre": 475,
    "KG Spring_theta_wre": 0.14575392,
    "KG Spring_pr_1_99_wre": 84,
    "KG Spring_ss_wre": 114,
    "KG Spring_dss_wre": 515,
    "G1 Fall_theta_wre": 0.504259646,
    "G1 Fall_pr_1_99_wre": 77,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 550,
    "G1 Winter_theta_wre": 0.625246676,
    "G1 Winter_pr_1_99_wre": 71,
    "G1 Winter_ss_wre": 114,
    "G1 Winter_dss_wre": 563,
    "G1 Spring_theta_wre": 1.020397695,
    "G1 Spring_pr_1_99_wre": 81,
    "G1 Spring_ss_wre": 113,
    "G1 Spring_dss_wre": 602,
    "G2 Fall_theta_wre": 1.344916039,
    "G2 Fall_pr_1_99_wre": 75,
    "G2 Fall_ss_wre": 114,
    "G2 Fall_dss_wre": 634,
    "G2 Winter_theta_wre": 1.697073521,
    "G2 Winter_pr_1_99_wre": 75,
    "G2 Winter_ss_wre": 114,
    "G2 Winter_dss_wre": 670,
    "G2 Spring_theta_wre": 1.929991635,
    "G2 Spring_pr_1_99_wre": 84,
    "G2 Spring_ss_wre": 113,
    "G2 Spring_dss_wre": 693,
    "G3 Fall_theta_wre": 2.294914037,
    "G3 Fall_pr_1_99_wre": 80,
    "G3 Fall_ss_wre": 114,
    "G3 Fall_dss_wre": 729,
    "G3 Winter_theta_wre": 2.580766977,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 114,
    "G3 Winter_dss_wre": 758,
    "G3 Spring_theta_wre": 2.98963191,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 114,
    "G3 Spring_dss_wre": 799
  },
  {
    "ID": 413,
    "KG Winter_theta_wre": -0.223343336,
    "KG Winter_pr_1_99_wre": 84,
    "KG Winter_ss_wre": 114,
    "KG Winter_dss_wre": 478,
    "KG Spring_theta_wre": 0.146189888,
    "KG Spring_pr_1_99_wre": 84,
    "KG Spring_ss_wre": 114,
    "KG Spring_dss_wre": 515,
    "G1 Fall_theta_wre": 0.514177957,
    "G1 Fall_pr_1_99_wre": 81,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 551,
    "G1 Winter_theta_wre": 0.656705672,
    "G1 Winter_pr_1_99_wre": 73,
    "G1 Winter_ss_wre": 114,
    "G1 Winter_dss_wre": 566,
    "G1 Spring_theta_wre": 1.030045005,
    "G1 Spring_pr_1_99_wre": 81,
    "G1 Spring_ss_wre": 113,
    "G1 Spring_dss_wre": 603,
    "G2 Fall_theta_wre": 1.361328883,
    "G2 Fall_pr_1_99_wre": 76,
    "G2 Fall_ss_wre": 114,
    "G2 Fall_dss_wre": 636,
    "G2 Winter_theta_wre": 1.698586128,
    "G2 Winter_pr_1_99_wre": 75,
    "G2 Winter_ss_wre": 114,
    "G2 Winter_dss_wre": 670,
    "G2 Spring_theta_wre": 1.938105341,
    "G2 Spring_pr_1_99_wre": 84,
    "G2 Spring_ss_wre": 114,
    "G2 Spring_dss_wre": 694,
    "G3 Fall_theta_wre": 2.310176105,
    "G3 Fall_pr_1_99_wre": 82,
    "G3 Fall_ss_wre": 115,
    "G3 Fall_dss_wre": 731,
    "G3 Winter_theta_wre": 2.584972423,
    "G3 Winter_pr_1_99_wre": 80,
    "G3 Winter_ss_wre": 114,
    "G3 Winter_dss_wre": 758,
    "G3 Spring_theta_wre": 2.989963181,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 114,
    "G3 Spring_dss_wre": 799
  },
  {
    "ID": 414,
    "KG Winter_theta_wre": -0.216028347,
    "KG Winter_pr_1_99_wre": 84,
    "KG Winter_ss_wre": 114,
    "KG Winter_dss_wre": 478,
    "KG Spring_theta_wre": 0.163209599,
    "KG Spring_pr_1_99_wre": 84,
    "KG Spring_ss_wre": 114,
    "KG Spring_dss_wre": 516,
    "G1 Fall_theta_wre": 0.527185732,
    "G1 Fall_pr_1_99_wre": 82,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 553,
    "G1 Winter_theta_wre": 0.672325039,
    "G1 Winter_pr_1_99_wre": 73,
    "G1 Winter_ss_wre": 115,
    "G1 Winter_dss_wre": 567,
    "G1 Spring_theta_wre": 1.046058941,
    "G1 Spring_pr_1_99_wre": 83,
    "G1 Spring_ss_wre": 114,
    "G1 Spring_dss_wre": 605,
    "G2 Fall_theta_wre": 1.36646394,
    "G2 Fall_pr_1_99_wre": 76,
    "G2 Fall_ss_wre": 114,
    "G2 Fall_dss_wre": 637,
    "G2 Winter_theta_wre": 1.714991693,
    "G2 Winter_pr_1_99_wre": 76,
    "G2 Winter_ss_wre": 115,
    "G2 Winter_dss_wre": 671,
    "G2 Spring_theta_wre": 1.939084452,
    "G2 Spring_pr_1_99_wre": 84,
    "G2 Spring_ss_wre": 114,
    "G2 Spring_dss_wre": 694,
    "G3 Fall_theta_wre": 2.31262649,
    "G3 Fall_pr_1_99_wre": 82,
    "G3 Fall_ss_wre": 115,
    "G3 Fall_dss_wre": 731,
    "G3 Winter_theta_wre": 2.590358151,
    "G3 Winter_pr_1_99_wre": 81,
    "G3 Winter_ss_wre": 114,
    "G3 Winter_dss_wre": 759,
    "G3 Spring_theta_wre": 3.008237862,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 114,
    "G3 Spring_dss_wre": 801
  },
  {
    "ID": 415,
    "KG Winter_theta_wre": -0.207441561,
    "KG Winter_pr_1_99_wre": 85,
    "KG Winter_ss_wre": 114,
    "KG Winter_dss_wre": 479,
    "KG Spring_theta_wre": 0.16534365,
    "KG Spring_pr_1_99_wre": 84,
    "KG Spring_ss_wre": 114,
    "KG Spring_dss_wre": 517,
    "G1 Fall_theta_wre": 0.531404928,
    "G1 Fall_pr_1_99_wre": 82,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 553,
    "G1 Winter_theta_wre": 0.689063865,
    "G1 Winter_pr_1_99_wre": 74,
    "G1 Winter_ss_wre": 115,
    "G1 Winter_dss_wre": 569,
    "G1 Spring_theta_wre": 1.049874383,
    "G1 Spring_pr_1_99_wre": 83,
    "G1 Spring_ss_wre": 114,
    "G1 Spring_dss_wre": 605,
    "G2 Fall_theta_wre": 1.368551521,
    "G2 Fall_pr_1_99_wre": 76,
    "G2 Fall_ss_wre": 114,
    "G2 Fall_dss_wre": 637,
    "G2 Winter_theta_wre": 1.726474249,
    "G2 Winter_pr_1_99_wre": 76,
    "G2 Winter_ss_wre": 115,
    "G2 Winter_dss_wre": 673,
    "G2 Spring_theta_wre": 1.940069797,
    "G2 Spring_pr_1_99_wre": 84,
    "G2 Spring_ss_wre": 114,
    "G2 Spring_dss_wre": 694,
    "G3 Fall_theta_wre": 2.318150421,
    "G3 Fall_pr_1_99_wre": 82,
    "G3 Fall_ss_wre": 115,
    "G3 Fall_dss_wre": 732,
    "G3 Winter_theta_wre": 2.598826875,
    "G3 Winter_pr_1_99_wre": 81,
    "G3 Winter_ss_wre": 114,
    "G3 Winter_dss_wre": 760,
    "G3 Spring_theta_wre": 3.020262311,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 802
  },
  {
    "ID": 416,
    "KG Winter_theta_wre": -0.194821164,
    "KG Winter_pr_1_99_wre": 85,
    "KG Winter_ss_wre": 114,
    "KG Winter_dss_wre": 481,
    "KG Spring_theta_wre": 0.173530472,
    "KG Spring_pr_1_99_wre": 84,
    "KG Spring_ss_wre": 114,
    "KG Spring_dss_wre": 517,
    "G1 Fall_theta_wre": 0.531709754,
    "G1 Fall_pr_1_99_wre": 82,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 553,
    "G1 Winter_theta_wre": 0.694558786,
    "G1 Winter_pr_1_99_wre": 74,
    "G1 Winter_ss_wre": 115,
    "G1 Winter_dss_wre": 569,
    "G1 Spring_theta_wre": 1.053493479,
    "G1 Spring_pr_1_99_wre": 84,
    "G1 Spring_ss_wre": 114,
    "G1 Spring_dss_wre": 605,
    "G2 Fall_theta_wre": 1.376935943,
    "G2 Fall_pr_1_99_wre": 77,
    "G2 Fall_ss_wre": 115,
    "G2 Fall_dss_wre": 638,
    "G2 Winter_theta_wre": 1.748989932,
    "G2 Winter_pr_1_99_wre": 76,
    "G2 Winter_ss_wre": 115,
    "G2 Winter_dss_wre": 675,
    "G2 Spring_theta_wre": 1.948241,
    "G2 Spring_pr_1_99_wre": 84,
    "G2 Spring_ss_wre": 114,
    "G2 Spring_dss_wre": 695,
    "G3 Fall_theta_wre": 2.326553746,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 115,
    "G3 Fall_dss_wre": 733,
    "G3 Winter_theta_wre": 2.609548537,
    "G3 Winter_pr_1_99_wre": 82,
    "G3 Winter_ss_wre": 114,
    "G3 Winter_dss_wre": 761,
    "G3 Spring_theta_wre": 3.021063135,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 802
  },
  {
    "ID": 417,
    "KG Winter_theta_wre": -0.18641562,
    "KG Winter_pr_1_99_wre": 85,
    "KG Winter_ss_wre": 114,
    "KG Winter_dss_wre": 481,
    "KG Spring_theta_wre": 0.174432801,
    "KG Spring_pr_1_99_wre": 84,
    "KG Spring_ss_wre": 114,
    "KG Spring_dss_wre": 517,
    "G1 Fall_theta_wre": 0.533061869,
    "G1 Fall_pr_1_99_wre": 82,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 553,
    "G1 Winter_theta_wre": 0.695381021,
    "G1 Winter_pr_1_99_wre": 74,
    "G1 Winter_ss_wre": 115,
    "G1 Winter_dss_wre": 570,
    "G1 Spring_theta_wre": 1.059109942,
    "G1 Spring_pr_1_99_wre": 84,
    "G1 Spring_ss_wre": 114,
    "G1 Spring_dss_wre": 606,
    "G2 Fall_theta_wre": 1.37765629,
    "G2 Fall_pr_1_99_wre": 77,
    "G2 Fall_ss_wre": 115,
    "G2 Fall_dss_wre": 638,
    "G2 Winter_theta_wre": 1.754940003,
    "G2 Winter_pr_1_99_wre": 76,
    "G2 Winter_ss_wre": 115,
    "G2 Winter_dss_wre": 675,
    "G2 Spring_theta_wre": 1.958857565,
    "G2 Spring_pr_1_99_wre": 86,
    "G2 Spring_ss_wre": 114,
    "G2 Spring_dss_wre": 696,
    "G3 Fall_theta_wre": 2.327166893,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 115,
    "G3 Fall_dss_wre": 733,
    "G3 Winter_theta_wre": 2.611006479,
    "G3 Winter_pr_1_99_wre": 83,
    "G3 Winter_ss_wre": 114,
    "G3 Winter_dss_wre": 761,
    "G3 Spring_theta_wre": 3.024412021,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 802
  },
  {
    "ID": 418,
    "KG Winter_theta_wre": -0.174950724,
    "KG Winter_pr_1_99_wre": 85,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 483,
    "KG Spring_theta_wre": 0.175730398,
    "KG Spring_pr_1_99_wre": 84,
    "KG Spring_ss_wre": 115,
    "KG Spring_dss_wre": 518,
    "G1 Fall_theta_wre": 0.534438959,
    "G1 Fall_pr_1_99_wre": 82,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 553,
    "G1 Winter_theta_wre": 0.705060162,
    "G1 Winter_pr_1_99_wre": 74,
    "G1 Winter_ss_wre": 115,
    "G1 Winter_dss_wre": 571,
    "G1 Spring_theta_wre": 1.064998339,
    "G1 Spring_pr_1_99_wre": 84,
    "G1 Spring_ss_wre": 114,
    "G1 Spring_dss_wre": 606,
    "G2 Fall_theta_wre": 1.384554143,
    "G2 Fall_pr_1_99_wre": 77,
    "G2 Fall_ss_wre": 115,
    "G2 Fall_dss_wre": 638,
    "G2 Winter_theta_wre": 1.756984222,
    "G2 Winter_pr_1_99_wre": 76,
    "G2 Winter_ss_wre": 115,
    "G2 Winter_dss_wre": 676,
    "G2 Spring_theta_wre": 1.974619239,
    "G2 Spring_pr_1_99_wre": 87,
    "G2 Spring_ss_wre": 114,
    "G2 Spring_dss_wre": 697,
    "G3 Fall_theta_wre": 2.344510943,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 115,
    "G3 Fall_dss_wre": 734,
    "G3 Winter_theta_wre": 2.611089068,
    "G3 Winter_pr_1_99_wre": 83,
    "G3 Winter_ss_wre": 114,
    "G3 Winter_dss_wre": 761,
    "G3 Spring_theta_wre": 3.028678374,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 803
  },
  {
    "ID": 419,
    "KG Winter_theta_wre": -0.174715501,
    "KG Winter_pr_1_99_wre": 85,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 483,
    "KG Spring_theta_wre": 0.183108097,
    "KG Spring_pr_1_99_wre": 84,
    "KG Spring_ss_wre": 115,
    "KG Spring_dss_wre": 518,
    "G1 Fall_theta_wre": 0.534582415,
    "G1 Fall_pr_1_99_wre": 82,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 553,
    "G1 Winter_theta_wre": 0.713507562,
    "G1 Winter_pr_1_99_wre": 74,
    "G1 Winter_ss_wre": 115,
    "G1 Winter_dss_wre": 571,
    "G1 Spring_theta_wre": 1.077808547,
    "G1 Spring_pr_1_99_wre": 84,
    "G1 Spring_ss_wre": 114,
    "G1 Spring_dss_wre": 608,
    "G2 Fall_theta_wre": 1.389711063,
    "G2 Fall_pr_1_99_wre": 77,
    "G2 Fall_ss_wre": 115,
    "G2 Fall_dss_wre": 639,
    "G2 Winter_theta_wre": 1.75995056,
    "G2 Winter_pr_1_99_wre": 76,
    "G2 Winter_ss_wre": 115,
    "G2 Winter_dss_wre": 676,
    "G2 Spring_theta_wre": 1.975848634,
    "G2 Spring_pr_1_99_wre": 87,
    "G2 Spring_ss_wre": 114,
    "G2 Spring_dss_wre": 698,
    "G3 Fall_theta_wre": 2.347144887,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 115,
    "G3 Fall_dss_wre": 735,
    "G3 Winter_theta_wre": 2.612604956,
    "G3 Winter_pr_1_99_wre": 83,
    "G3 Winter_ss_wre": 114,
    "G3 Winter_dss_wre": 761,
    "G3 Spring_theta_wre": 3.041222596,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 804
  },
  {
    "ID": 420,
    "KG Winter_theta_wre": -0.170321634,
    "KG Winter_pr_1_99_wre": 85,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 483,
    "KG Spring_theta_wre": 0.187380033,
    "KG Spring_pr_1_99_wre": 84,
    "KG Spring_ss_wre": 115,
    "KG Spring_dss_wre": 519,
    "G1 Fall_theta_wre": 0.541614818,
    "G1 Fall_pr_1_99_wre": 82,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 554,
    "G1 Winter_theta_wre": 0.715303447,
    "G1 Winter_pr_1_99_wre": 75,
    "G1 Winter_ss_wre": 115,
    "G1 Winter_dss_wre": 572,
    "G1 Spring_theta_wre": 1.079898871,
    "G1 Spring_pr_1_99_wre": 84,
    "G1 Spring_ss_wre": 114,
    "G1 Spring_dss_wre": 608,
    "G2 Fall_theta_wre": 1.413200985,
    "G2 Fall_pr_1_99_wre": 78,
    "G2 Fall_ss_wre": 115,
    "G2 Fall_dss_wre": 641,
    "G2 Winter_theta_wre": 1.767100383,
    "G2 Winter_pr_1_99_wre": 76,
    "G2 Winter_ss_wre": 116,
    "G2 Winter_dss_wre": 677,
    "G2 Spring_theta_wre": 1.976160696,
    "G2 Spring_pr_1_99_wre": 87,
    "G2 Spring_ss_wre": 114,
    "G2 Spring_dss_wre": 698,
    "G3 Fall_theta_wre": 2.352339254,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 115,
    "G3 Fall_dss_wre": 735,
    "G3 Winter_theta_wre": 2.615262032,
    "G3 Winter_pr_1_99_wre": 83,
    "G3 Winter_ss_wre": 114,
    "G3 Winter_dss_wre": 762,
    "G3 Spring_theta_wre": 3.051048855,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 805
  },
  {
    "ID": 421,
    "KG Winter_theta_wre": -0.162189889,
    "KG Winter_pr_1_99_wre": 86,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 484,
    "KG Spring_theta_wre": 0.193476118,
    "KG Spring_pr_1_99_wre": 85,
    "KG Spring_ss_wre": 115,
    "KG Spring_dss_wre": 519,
    "G1 Fall_theta_wre": 0.543284494,
    "G1 Fall_pr_1_99_wre": 82,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 554,
    "G1 Winter_theta_wre": 0.715556509,
    "G1 Winter_pr_1_99_wre": 75,
    "G1 Winter_ss_wre": 115,
    "G1 Winter_dss_wre": 572,
    "G1 Spring_theta_wre": 1.083539446,
    "G1 Spring_pr_1_99_wre": 84,
    "G1 Spring_ss_wre": 114,
    "G1 Spring_dss_wre": 608,
    "G2 Fall_theta_wre": 1.413341331,
    "G2 Fall_pr_1_99_wre": 78,
    "G2 Fall_ss_wre": 115,
    "G2 Fall_dss_wre": 641,
    "G2 Winter_theta_wre": 1.779652496,
    "G2 Winter_pr_1_99_wre": 76,
    "G2 Winter_ss_wre": 116,
    "G2 Winter_dss_wre": 678,
    "G2 Spring_theta_wre": 2.003311524,
    "G2 Spring_pr_1_99_wre": 89,
    "G2 Spring_ss_wre": 115,
    "G2 Spring_dss_wre": 700,
    "G3 Fall_theta_wre": 2.364175875,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 116,
    "G3 Fall_dss_wre": 736,
    "G3 Winter_theta_wre": 2.642243784,
    "G3 Winter_pr_1_99_wre": 84,
    "G3 Winter_ss_wre": 115,
    "G3 Winter_dss_wre": 764,
    "G3 Spring_theta_wre": 3.053522597,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 805
  },
  {
    "ID": 422,
    "KG Winter_theta_wre": -0.156014045,
    "KG Winter_pr_1_99_wre": 86,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 484,
    "KG Spring_theta_wre": 0.203639058,
    "KG Spring_pr_1_99_wre": 85,
    "KG Spring_ss_wre": 115,
    "KG Spring_dss_wre": 520,
    "G1 Fall_theta_wre": 0.543993005,
    "G1 Fall_pr_1_99_wre": 83,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 554,
    "G1 Winter_theta_wre": 0.726352598,
    "G1 Winter_pr_1_99_wre": 75,
    "G1 Winter_ss_wre": 116,
    "G1 Winter_dss_wre": 573,
    "G1 Spring_theta_wre": 1.084769944,
    "G1 Spring_pr_1_99_wre": 84,
    "G1 Spring_ss_wre": 114,
    "G1 Spring_dss_wre": 608,
    "G2 Fall_theta_wre": 1.423839222,
    "G2 Fall_pr_1_99_wre": 78,
    "G2 Fall_ss_wre": 115,
    "G2 Fall_dss_wre": 642,
    "G2 Winter_theta_wre": 1.788027605,
    "G2 Winter_pr_1_99_wre": 76,
    "G2 Winter_ss_wre": 116,
    "G2 Winter_dss_wre": 679,
    "G2 Spring_theta_wre": 2.004852628,
    "G2 Spring_pr_1_99_wre": 89,
    "G2 Spring_ss_wre": 115,
    "G2 Spring_dss_wre": 700,
    "G3 Fall_theta_wre": 2.370704315,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 116,
    "G3 Fall_dss_wre": 737,
    "G3 Winter_theta_wre": 2.64619457,
    "G3 Winter_pr_1_99_wre": 84,
    "G3 Winter_ss_wre": 115,
    "G3 Winter_dss_wre": 765,
    "G3 Spring_theta_wre": 3.061224609,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 806
  },
  {
    "ID": 423,
    "KG Winter_theta_wre": -0.150445226,
    "KG Winter_pr_1_99_wre": 86,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 485,
    "KG Spring_theta_wre": 0.209687065,
    "KG Spring_pr_1_99_wre": 85,
    "KG Spring_ss_wre": 115,
    "KG Spring_dss_wre": 521,
    "G1 Fall_theta_wre": 0.550062413,
    "G1 Fall_pr_1_99_wre": 83,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 555,
    "G1 Winter_theta_wre": 0.726899684,
    "G1 Winter_pr_1_99_wre": 75,
    "G1 Winter_ss_wre": 116,
    "G1 Winter_dss_wre": 573,
    "G1 Spring_theta_wre": 1.091700839,
    "G1 Spring_pr_1_99_wre": 84,
    "G1 Spring_ss_wre": 114,
    "G1 Spring_dss_wre": 609,
    "G2 Fall_theta_wre": 1.427231268,
    "G2 Fall_pr_1_99_wre": 78,
    "G2 Fall_ss_wre": 115,
    "G2 Fall_dss_wre": 643,
    "G2 Winter_theta_wre": 1.800384283,
    "G2 Winter_pr_1_99_wre": 76,
    "G2 Winter_ss_wre": 116,
    "G2 Winter_dss_wre": 680,
    "G2 Spring_theta_wre": 2.009331907,
    "G2 Spring_pr_1_99_wre": 89,
    "G2 Spring_ss_wre": 115,
    "G2 Spring_dss_wre": 701,
    "G3 Fall_theta_wre": 2.384300345,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 116,
    "G3 Fall_dss_wre": 738,
    "G3 Winter_theta_wre": 2.649261979,
    "G3 Winter_pr_1_99_wre": 84,
    "G3 Winter_ss_wre": 115,
    "G3 Winter_dss_wre": 765,
    "G3 Spring_theta_wre": 3.061227038,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 806
  },
  {
    "ID": 424,
    "KG Winter_theta_wre": -0.144715195,
    "KG Winter_pr_1_99_wre": 86,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 486,
    "KG Spring_theta_wre": 0.213862362,
    "KG Spring_pr_1_99_wre": 85,
    "KG Spring_ss_wre": 115,
    "KG Spring_dss_wre": 521,
    "G1 Fall_theta_wre": 0.554590853,
    "G1 Fall_pr_1_99_wre": 83,
    "G1 Fall_ss_wre": 115,
    "G1 Fall_dss_wre": 555,
    "G1 Winter_theta_wre": 0.730631629,
    "G1 Winter_pr_1_99_wre": 75,
    "G1 Winter_ss_wre": 116,
    "G1 Winter_dss_wre": 573,
    "G1 Spring_theta_wre": 1.098264633,
    "G1 Spring_pr_1_99_wre": 85,
    "G1 Spring_ss_wre": 115,
    "G1 Spring_dss_wre": 610,
    "G2 Fall_theta_wre": 1.433021135,
    "G2 Fall_pr_1_99_wre": 78,
    "G2 Fall_ss_wre": 116,
    "G2 Fall_dss_wre": 643,
    "G2 Winter_theta_wre": 1.821603652,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 116,
    "G2 Winter_dss_wre": 682,
    "G2 Spring_theta_wre": 2.034278362,
    "G2 Spring_pr_1_99_wre": 89,
    "G2 Spring_ss_wre": 115,
    "G2 Spring_dss_wre": 703,
    "G3 Fall_theta_wre": 2.386167655,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 116,
    "G3 Fall_dss_wre": 739,
    "G3 Winter_theta_wre": 2.651707087,
    "G3 Winter_pr_1_99_wre": 84,
    "G3 Winter_ss_wre": 115,
    "G3 Winter_dss_wre": 765,
    "G3 Spring_theta_wre": 3.06551353,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 807
  },
  {
    "ID": 425,
    "KG Winter_theta_wre": -0.144552999,
    "KG Winter_pr_1_99_wre": 86,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 486,
    "KG Spring_theta_wre": 0.22154175,
    "KG Spring_pr_1_99_wre": 85,
    "KG Spring_ss_wre": 115,
    "KG Spring_dss_wre": 522,
    "G1 Fall_theta_wre": 0.556260403,
    "G1 Fall_pr_1_99_wre": 84,
    "G1 Fall_ss_wre": 116,
    "G1 Fall_dss_wre": 556,
    "G1 Winter_theta_wre": 0.733369188,
    "G1 Winter_pr_1_99_wre": 75,
    "G1 Winter_ss_wre": 116,
    "G1 Winter_dss_wre": 573,
    "G1 Spring_theta_wre": 1.109825727,
    "G1 Spring_pr_1_99_wre": 86,
    "G1 Spring_ss_wre": 115,
    "G1 Spring_dss_wre": 611,
    "G2 Fall_theta_wre": 1.434809378,
    "G2 Fall_pr_1_99_wre": 78,
    "G2 Fall_ss_wre": 116,
    "G2 Fall_dss_wre": 643,
    "G2 Winter_theta_wre": 1.834374931,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 117,
    "G2 Winter_dss_wre": 683,
    "G2 Spring_theta_wre": 2.04116036,
    "G2 Spring_pr_1_99_wre": 89,
    "G2 Spring_ss_wre": 115,
    "G2 Spring_dss_wre": 704,
    "G3 Fall_theta_wre": 2.395051831,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 116,
    "G3 Fall_dss_wre": 740,
    "G3 Winter_theta_wre": 2.652268337,
    "G3 Winter_pr_1_99_wre": 84,
    "G3 Winter_ss_wre": 115,
    "G3 Winter_dss_wre": 765,
    "G3 Spring_theta_wre": 3.07074262,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 807
  },
  {
    "ID": 426,
    "KG Winter_theta_wre": -0.142354031,
    "KG Winter_pr_1_99_wre": 87,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 486,
    "KG Spring_theta_wre": 0.223250571,
    "KG Spring_pr_1_99_wre": 85,
    "KG Spring_ss_wre": 115,
    "KG Spring_dss_wre": 522,
    "G1 Fall_theta_wre": 0.564527795,
    "G1 Fall_pr_1_99_wre": 84,
    "G1 Fall_ss_wre": 116,
    "G1 Fall_dss_wre": 556,
    "G1 Winter_theta_wre": 0.752714572,
    "G1 Winter_pr_1_99_wre": 76,
    "G1 Winter_ss_wre": 116,
    "G1 Winter_dss_wre": 575,
    "G1 Spring_theta_wre": 1.110890881,
    "G1 Spring_pr_1_99_wre": 86,
    "G1 Spring_ss_wre": 115,
    "G1 Spring_dss_wre": 611,
    "G2 Fall_theta_wre": 1.441503543,
    "G2 Fall_pr_1_99_wre": 78,
    "G2 Fall_ss_wre": 116,
    "G2 Fall_dss_wre": 644,
    "G2 Winter_theta_wre": 1.850284275,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 117,
    "G2 Winter_dss_wre": 685,
    "G2 Spring_theta_wre": 2.055747382,
    "G2 Spring_pr_1_99_wre": 89,
    "G2 Spring_ss_wre": 116,
    "G2 Spring_dss_wre": 706,
    "G3 Fall_theta_wre": 2.406526218,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 116,
    "G3 Fall_dss_wre": 741,
    "G3 Winter_theta_wre": 2.661192388,
    "G3 Winter_pr_1_99_wre": 84,
    "G3 Winter_ss_wre": 115,
    "G3 Winter_dss_wre": 766,
    "G3 Spring_theta_wre": 3.071281845,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 807
  },
  {
    "ID": 427,
    "KG Winter_theta_wre": -0.130871348,
    "KG Winter_pr_1_99_wre": 87,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 487,
    "KG Spring_theta_wre": 0.224647075,
    "KG Spring_pr_1_99_wre": 85,
    "KG Spring_ss_wre": 115,
    "KG Spring_dss_wre": 522,
    "G1 Fall_theta_wre": 0.575057136,
    "G1 Fall_pr_1_99_wre": 84,
    "G1 Fall_ss_wre": 116,
    "G1 Fall_dss_wre": 558,
    "G1 Winter_theta_wre": 0.753574619,
    "G1 Winter_pr_1_99_wre": 76,
    "G1 Winter_ss_wre": 116,
    "G1 Winter_dss_wre": 575,
    "G1 Spring_theta_wre": 1.112562542,
    "G1 Spring_pr_1_99_wre": 86,
    "G1 Spring_ss_wre": 115,
    "G1 Spring_dss_wre": 611,
    "G2 Fall_theta_wre": 1.44165673,
    "G2 Fall_pr_1_99_wre": 78,
    "G2 Fall_ss_wre": 116,
    "G2 Fall_dss_wre": 644,
    "G2 Winter_theta_wre": 1.855452841,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 117,
    "G2 Winter_dss_wre": 686,
    "G2 Spring_theta_wre": 2.06006293,
    "G2 Spring_pr_1_99_wre": 91,
    "G2 Spring_ss_wre": 116,
    "G2 Spring_dss_wre": 706,
    "G3 Fall_theta_wre": 2.413861402,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 116,
    "G3 Fall_dss_wre": 741,
    "G3 Winter_theta_wre": 2.663814191,
    "G3 Winter_pr_1_99_wre": 84,
    "G3 Winter_ss_wre": 115,
    "G3 Winter_dss_wre": 766,
    "G3 Spring_theta_wre": 3.07360272,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 115,
    "G3 Spring_dss_wre": 807
  },
  {
    "ID": 428,
    "KG Winter_theta_wre": -0.128093529,
    "KG Winter_pr_1_99_wre": 87,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 487,
    "KG Spring_theta_wre": 0.242868356,
    "KG Spring_pr_1_99_wre": 86,
    "KG Spring_ss_wre": 116,
    "KG Spring_dss_wre": 524,
    "G1 Fall_theta_wre": 0.589089498,
    "G1 Fall_pr_1_99_wre": 84,
    "G1 Fall_ss_wre": 116,
    "G1 Fall_dss_wre": 559,
    "G1 Winter_theta_wre": 0.778682024,
    "G1 Winter_pr_1_99_wre": 76,
    "G1 Winter_ss_wre": 117,
    "G1 Winter_dss_wre": 578,
    "G1 Spring_theta_wre": 1.132424231,
    "G1 Spring_pr_1_99_wre": 87,
    "G1 Spring_ss_wre": 115,
    "G1 Spring_dss_wre": 613,
    "G2 Fall_theta_wre": 1.457334703,
    "G2 Fall_pr_1_99_wre": 79,
    "G2 Fall_ss_wre": 116,
    "G2 Fall_dss_wre": 646,
    "G2 Winter_theta_wre": 1.869543265,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 117,
    "G2 Winter_dss_wre": 687,
    "G2 Spring_theta_wre": 2.074863317,
    "G2 Spring_pr_1_99_wre": 91,
    "G2 Spring_ss_wre": 116,
    "G2 Spring_dss_wre": 707,
    "G3 Fall_theta_wre": 2.422142491,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 117,
    "G3 Fall_dss_wre": 742,
    "G3 Winter_theta_wre": 2.67541028,
    "G3 Winter_pr_1_99_wre": 85,
    "G3 Winter_ss_wre": 115,
    "G3 Winter_dss_wre": 768,
    "G3 Spring_theta_wre": 3.075598888,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 808
  },
  {
    "ID": 429,
    "KG Winter_theta_wre": -0.123765472,
    "KG Winter_pr_1_99_wre": 87,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 488,
    "KG Spring_theta_wre": 0.250791646,
    "KG Spring_pr_1_99_wre": 86,
    "KG Spring_ss_wre": 116,
    "KG Spring_dss_wre": 525,
    "G1 Fall_theta_wre": 0.603456031,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 116,
    "G1 Fall_dss_wre": 560,
    "G1 Winter_theta_wre": 0.787346354,
    "G1 Winter_pr_1_99_wre": 76,
    "G1 Winter_ss_wre": 117,
    "G1 Winter_dss_wre": 579,
    "G1 Spring_theta_wre": 1.132588686,
    "G1 Spring_pr_1_99_wre": 87,
    "G1 Spring_ss_wre": 115,
    "G1 Spring_dss_wre": 613,
    "G2 Fall_theta_wre": 1.464145227,
    "G2 Fall_pr_1_99_wre": 79,
    "G2 Fall_ss_wre": 116,
    "G2 Fall_dss_wre": 646,
    "G2 Winter_theta_wre": 1.869918099,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 117,
    "G2 Winter_dss_wre": 687,
    "G2 Spring_theta_wre": 2.080884552,
    "G2 Spring_pr_1_99_wre": 91,
    "G2 Spring_ss_wre": 116,
    "G2 Spring_dss_wre": 708,
    "G3 Fall_theta_wre": 2.429168975,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 117,
    "G3 Fall_dss_wre": 743,
    "G3 Winter_theta_wre": 2.682811079,
    "G3 Winter_pr_1_99_wre": 85,
    "G3 Winter_ss_wre": 116,
    "G3 Winter_dss_wre": 768,
    "G3 Spring_theta_wre": 3.077524005,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 808
  },
  {
    "ID": 430,
    "KG Winter_theta_wre": -0.123656197,
    "KG Winter_pr_1_99_wre": 87,
    "KG Winter_ss_wre": 115,
    "KG Winter_dss_wre": 488,
    "KG Spring_theta_wre": 0.251390096,
    "KG Spring_pr_1_99_wre": 86,
    "KG Spring_ss_wre": 116,
    "KG Spring_dss_wre": 525,
    "G1 Fall_theta_wre": 0.603794619,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 116,
    "G1 Fall_dss_wre": 560,
    "G1 Winter_theta_wre": 0.792209437,
    "G1 Winter_pr_1_99_wre": 76,
    "G1 Winter_ss_wre": 117,
    "G1 Winter_dss_wre": 579,
    "G1 Spring_theta_wre": 1.134398965,
    "G1 Spring_pr_1_99_wre": 87,
    "G1 Spring_ss_wre": 115,
    "G1 Spring_dss_wre": 613,
    "G2 Fall_theta_wre": 1.47132781,
    "G2 Fall_pr_1_99_wre": 79,
    "G2 Fall_ss_wre": 116,
    "G2 Fall_dss_wre": 647,
    "G2 Winter_theta_wre": 1.870485663,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 117,
    "G2 Winter_dss_wre": 687,
    "G2 Spring_theta_wre": 2.088632999,
    "G2 Spring_pr_1_99_wre": 91,
    "G2 Spring_ss_wre": 116,
    "G2 Spring_dss_wre": 709,
    "G3 Fall_theta_wre": 2.433153271,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 117,
    "G3 Fall_dss_wre": 743,
    "G3 Winter_theta_wre": 2.705975632,
    "G3 Winter_pr_1_99_wre": 87,
    "G3 Winter_ss_wre": 116,
    "G3 Winter_dss_wre": 771,
    "G3 Spring_theta_wre": 3.081441998,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 808
  },
  {
    "ID": 431,
    "KG Winter_theta_wre": -0.114371752,
    "KG Winter_pr_1_99_wre": 87,
    "KG Winter_ss_wre": 116,
    "KG Winter_dss_wre": 489,
    "KG Spring_theta_wre": 0.255222187,
    "KG Spring_pr_1_99_wre": 86,
    "KG Spring_ss_wre": 116,
    "KG Spring_dss_wre": 526,
    "G1 Fall_theta_wre": 0.624170569,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 117,
    "G1 Fall_dss_wre": 562,
    "G1 Winter_theta_wre": 0.796092206,
    "G1 Winter_pr_1_99_wre": 76,
    "G1 Winter_ss_wre": 117,
    "G1 Winter_dss_wre": 580,
    "G1 Spring_theta_wre": 1.136787193,
    "G1 Spring_pr_1_99_wre": 87,
    "G1 Spring_ss_wre": 115,
    "G1 Spring_dss_wre": 614,
    "G2 Fall_theta_wre": 1.473122572,
    "G2 Fall_pr_1_99_wre": 79,
    "G2 Fall_ss_wre": 116,
    "G2 Fall_dss_wre": 647,
    "G2 Winter_theta_wre": 1.87256298,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 117,
    "G2 Winter_dss_wre": 687,
    "G2 Spring_theta_wre": 2.100700677,
    "G2 Spring_pr_1_99_wre": 91,
    "G2 Spring_ss_wre": 116,
    "G2 Spring_dss_wre": 710,
    "G3 Fall_theta_wre": 2.43501744,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 117,
    "G3 Fall_dss_wre": 744,
    "G3 Winter_theta_wre": 2.713829279,
    "G3 Winter_pr_1_99_wre": 87,
    "G3 Winter_ss_wre": 116,
    "G3 Winter_dss_wre": 771,
    "G3 Spring_theta_wre": 3.095019498,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 810
  },
  {
    "ID": 432,
    "KG Winter_theta_wre": -0.100449622,
    "KG Winter_pr_1_99_wre": 87,
    "KG Winter_ss_wre": 116,
    "KG Winter_dss_wre": 490,
    "KG Spring_theta_wre": 0.261843684,
    "KG Spring_pr_1_99_wre": 87,
    "KG Spring_ss_wre": 116,
    "KG Spring_dss_wre": 526,
    "G1 Fall_theta_wre": 0.632065188,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 117,
    "G1 Fall_dss_wre": 563,
    "G1 Winter_theta_wre": 0.79812315,
    "G1 Winter_pr_1_99_wre": 76,
    "G1 Winter_ss_wre": 117,
    "G1 Winter_dss_wre": 580,
    "G1 Spring_theta_wre": 1.142404382,
    "G1 Spring_pr_1_99_wre": 87,
    "G1 Spring_ss_wre": 115,
    "G1 Spring_dss_wre": 614,
    "G2 Fall_theta_wre": 1.483150831,
    "G2 Fall_pr_1_99_wre": 79,
    "G2 Fall_ss_wre": 116,
    "G2 Fall_dss_wre": 648,
    "G2 Winter_theta_wre": 1.878029169,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 688,
    "G2 Spring_theta_wre": 2.110053741,
    "G2 Spring_pr_1_99_wre": 92,
    "G2 Spring_ss_wre": 117,
    "G2 Spring_dss_wre": 711,
    "G3 Fall_theta_wre": 2.435640468,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 117,
    "G3 Fall_dss_wre": 744,
    "G3 Winter_theta_wre": 2.716263436,
    "G3 Winter_pr_1_99_wre": 87,
    "G3 Winter_ss_wre": 116,
    "G3 Winter_dss_wre": 772,
    "G3 Spring_theta_wre": 3.103367329,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 810
  },
  {
    "ID": 433,
    "KG Winter_theta_wre": -0.097937328,
    "KG Winter_pr_1_99_wre": 87,
    "KG Winter_ss_wre": 116,
    "KG Winter_dss_wre": 490,
    "KG Spring_theta_wre": 0.265018154,
    "KG Spring_pr_1_99_wre": 87,
    "KG Spring_ss_wre": 116,
    "KG Spring_dss_wre": 527,
    "G1 Fall_theta_wre": 0.633097376,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 117,
    "G1 Fall_dss_wre": 563,
    "G1 Winter_theta_wre": 0.800910661,
    "G1 Winter_pr_1_99_wre": 77,
    "G1 Winter_ss_wre": 117,
    "G1 Winter_dss_wre": 580,
    "G1 Spring_theta_wre": 1.144688832,
    "G1 Spring_pr_1_99_wre": 87,
    "G1 Spring_ss_wre": 115,
    "G1 Spring_dss_wre": 614,
    "G2 Fall_theta_wre": 1.501458744,
    "G2 Fall_pr_1_99_wre": 80,
    "G2 Fall_ss_wre": 117,
    "G2 Fall_dss_wre": 650,
    "G2 Winter_theta_wre": 1.883639743,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 688,
    "G2 Spring_theta_wre": 2.120133668,
    "G2 Spring_pr_1_99_wre": 94,
    "G2 Spring_ss_wre": 117,
    "G2 Spring_dss_wre": 712,
    "G3 Fall_theta_wre": 2.445111618,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 117,
    "G3 Fall_dss_wre": 745,
    "G3 Winter_theta_wre": 2.729757593,
    "G3 Winter_pr_1_99_wre": 87,
    "G3 Winter_ss_wre": 116,
    "G3 Winter_dss_wre": 773,
    "G3 Spring_theta_wre": 3.104977592,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 810
  },
  {
    "ID": 434,
    "KG Winter_theta_wre": -0.085980506,
    "KG Winter_pr_1_99_wre": 88,
    "KG Winter_ss_wre": 116,
    "KG Winter_dss_wre": 491,
    "KG Spring_theta_wre": 0.269745215,
    "KG Spring_pr_1_99_wre": 87,
    "KG Spring_ss_wre": 116,
    "KG Spring_dss_wre": 527,
    "G1 Fall_theta_wre": 0.640268155,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 117,
    "G1 Fall_dss_wre": 564,
    "G1 Winter_theta_wre": 0.801526835,
    "G1 Winter_pr_1_99_wre": 77,
    "G1 Winter_ss_wre": 117,
    "G1 Winter_dss_wre": 580,
    "G1 Spring_theta_wre": 1.14743407,
    "G1 Spring_pr_1_99_wre": 87,
    "G1 Spring_ss_wre": 116,
    "G1 Spring_dss_wre": 615,
    "G2 Fall_theta_wre": 1.501765069,
    "G2 Fall_pr_1_99_wre": 80,
    "G2 Fall_ss_wre": 117,
    "G2 Fall_dss_wre": 650,
    "G2 Winter_theta_wre": 1.886867289,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 689,
    "G2 Spring_theta_wre": 2.129239193,
    "G2 Spring_pr_1_99_wre": 94,
    "G2 Spring_ss_wre": 117,
    "G2 Spring_dss_wre": 713,
    "G3 Fall_theta_wre": 2.449217922,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 117,
    "G3 Fall_dss_wre": 745,
    "G3 Winter_theta_wre": 2.749183859,
    "G3 Winter_pr_1_99_wre": 87,
    "G3 Winter_ss_wre": 117,
    "G3 Winter_dss_wre": 775,
    "G3 Spring_theta_wre": 3.107027896,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 811
  },
  {
    "ID": 435,
    "KG Winter_theta_wre": -0.081152027,
    "KG Winter_pr_1_99_wre": 88,
    "KG Winter_ss_wre": 116,
    "KG Winter_dss_wre": 492,
    "KG Spring_theta_wre": 0.278499515,
    "KG Spring_pr_1_99_wre": 87,
    "KG Spring_ss_wre": 116,
    "KG Spring_dss_wre": 528,
    "G1 Fall_theta_wre": 0.642093459,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 117,
    "G1 Fall_dss_wre": 564,
    "G1 Winter_theta_wre": 0.803399283,
    "G1 Winter_pr_1_99_wre": 77,
    "G1 Winter_ss_wre": 117,
    "G1 Winter_dss_wre": 580,
    "G1 Spring_theta_wre": 1.160770643,
    "G1 Spring_pr_1_99_wre": 88,
    "G1 Spring_ss_wre": 116,
    "G1 Spring_dss_wre": 616,
    "G2 Fall_theta_wre": 1.504118938,
    "G2 Fall_pr_1_99_wre": 80,
    "G2 Fall_ss_wre": 117,
    "G2 Fall_dss_wre": 650,
    "G2 Winter_theta_wre": 1.887818244,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 689,
    "G2 Spring_theta_wre": 2.139861397,
    "G2 Spring_pr_1_99_wre": 94,
    "G2 Spring_ss_wre": 117,
    "G2 Spring_dss_wre": 714,
    "G3 Fall_theta_wre": 2.465921747,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 117,
    "G3 Fall_dss_wre": 747,
    "G3 Winter_theta_wre": 2.757299061,
    "G3 Winter_pr_1_99_wre": 87,
    "G3 Winter_ss_wre": 117,
    "G3 Winter_dss_wre": 776,
    "G3 Spring_theta_wre": 3.113389896,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 811
  },
  {
    "ID": 436,
    "KG Winter_theta_wre": -0.080944705,
    "KG Winter_pr_1_99_wre": 88,
    "KG Winter_ss_wre": 116,
    "KG Winter_dss_wre": 492,
    "KG Spring_theta_wre": 0.281667583,
    "KG Spring_pr_1_99_wre": 87,
    "KG Spring_ss_wre": 116,
    "KG Spring_dss_wre": 528,
    "G1 Fall_theta_wre": 0.644926665,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 117,
    "G1 Fall_dss_wre": 564,
    "G1 Winter_theta_wre": 0.806804148,
    "G1 Winter_pr_1_99_wre": 77,
    "G1 Winter_ss_wre": 117,
    "G1 Winter_dss_wre": 581,
    "G1 Spring_theta_wre": 1.163152811,
    "G1 Spring_pr_1_99_wre": 88,
    "G1 Spring_ss_wre": 116,
    "G1 Spring_dss_wre": 616,
    "G2 Fall_theta_wre": 1.516477624,
    "G2 Fall_pr_1_99_wre": 80,
    "G2 Fall_ss_wre": 117,
    "G2 Fall_dss_wre": 652,
    "G2 Winter_theta_wre": 1.891561836,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 689,
    "G2 Spring_theta_wre": 2.141300881,
    "G2 Spring_pr_1_99_wre": 94,
    "G2 Spring_ss_wre": 117,
    "G2 Spring_dss_wre": 714,
    "G3 Fall_theta_wre": 2.469930651,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 117,
    "G3 Fall_dss_wre": 747,
    "G3 Winter_theta_wre": 2.763246572,
    "G3 Winter_pr_1_99_wre": 87,
    "G3 Winter_ss_wre": 117,
    "G3 Winter_dss_wre": 776,
    "G3 Spring_theta_wre": 3.115992739,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 812
  },
  {
    "ID": 437,
    "KG Winter_theta_wre": -0.075517555,
    "KG Winter_pr_1_99_wre": 88,
    "KG Winter_ss_wre": 116,
    "KG Winter_dss_wre": 492,
    "KG Spring_theta_wre": 0.284395751,
    "KG Spring_pr_1_99_wre": 87,
    "KG Spring_ss_wre": 116,
    "KG Spring_dss_wre": 528,
    "G1 Fall_theta_wre": 0.648778178,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 117,
    "G1 Fall_dss_wre": 565,
    "G1 Winter_theta_wre": 0.833103192,
    "G1 Winter_pr_1_99_wre": 78,
    "G1 Winter_ss_wre": 118,
    "G1 Winter_dss_wre": 583,
    "G1 Spring_theta_wre": 1.189143126,
    "G1 Spring_pr_1_99_wre": 88,
    "G1 Spring_ss_wre": 116,
    "G1 Spring_dss_wre": 619,
    "G2 Fall_theta_wre": 1.517873715,
    "G2 Fall_pr_1_99_wre": 80,
    "G2 Fall_ss_wre": 117,
    "G2 Fall_dss_wre": 652,
    "G2 Winter_theta_wre": 1.902216567,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 690,
    "G2 Spring_theta_wre": 2.153236735,
    "G2 Spring_pr_1_99_wre": 94,
    "G2 Spring_ss_wre": 117,
    "G2 Spring_dss_wre": 715,
    "G3 Fall_theta_wre": 2.471622137,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 118,
    "G3 Fall_dss_wre": 747,
    "G3 Winter_theta_wre": 2.774267963,
    "G3 Winter_pr_1_99_wre": 87,
    "G3 Winter_ss_wre": 117,
    "G3 Winter_dss_wre": 777,
    "G3 Spring_theta_wre": 3.118940219,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 812
  },
  {
    "ID": 438,
    "KG Winter_theta_wre": -0.053971379,
    "KG Winter_pr_1_99_wre": 89,
    "KG Winter_ss_wre": 117,
    "KG Winter_dss_wre": 495,
    "KG Spring_theta_wre": 0.289709352,
    "KG Spring_pr_1_99_wre": 87,
    "KG Spring_ss_wre": 117,
    "KG Spring_dss_wre": 529,
    "G1 Fall_theta_wre": 0.649391689,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 117,
    "G1 Fall_dss_wre": 565,
    "G1 Winter_theta_wre": 0.849552692,
    "G1 Winter_pr_1_99_wre": 79,
    "G1 Winter_ss_wre": 118,
    "G1 Winter_dss_wre": 585,
    "G1 Spring_theta_wre": 1.190809146,
    "G1 Spring_pr_1_99_wre": 88,
    "G1 Spring_ss_wre": 116,
    "G1 Spring_dss_wre": 619,
    "G2 Fall_theta_wre": 1.521603665,
    "G2 Fall_pr_1_99_wre": 80,
    "G2 Fall_ss_wre": 117,
    "G2 Fall_dss_wre": 652,
    "G2 Winter_theta_wre": 1.903130041,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 690,
    "G2 Spring_theta_wre": 2.162441928,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 716,
    "G3 Fall_theta_wre": 2.479028029,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 118,
    "G3 Fall_dss_wre": 748,
    "G3 Winter_theta_wre": 2.79980165,
    "G3 Winter_pr_1_99_wre": 88,
    "G3 Winter_ss_wre": 118,
    "G3 Winter_dss_wre": 780,
    "G3 Spring_theta_wre": 3.121200504,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 812
  },
  {
    "ID": 439,
    "KG Winter_theta_wre": -0.04881584,
    "KG Winter_pr_1_99_wre": 89,
    "KG Winter_ss_wre": 117,
    "KG Winter_dss_wre": 495,
    "KG Spring_theta_wre": 0.29013631,
    "KG Spring_pr_1_99_wre": 87,
    "KG Spring_ss_wre": 117,
    "KG Spring_dss_wre": 529,
    "G1 Fall_theta_wre": 0.668245776,
    "G1 Fall_pr_1_99_wre": 85,
    "G1 Fall_ss_wre": 118,
    "G1 Fall_dss_wre": 567,
    "G1 Winter_theta_wre": 0.852548368,
    "G1 Winter_pr_1_99_wre": 79,
    "G1 Winter_ss_wre": 118,
    "G1 Winter_dss_wre": 585,
    "G1 Spring_theta_wre": 1.192266376,
    "G1 Spring_pr_1_99_wre": 88,
    "G1 Spring_ss_wre": 116,
    "G1 Spring_dss_wre": 619,
    "G2 Fall_theta_wre": 1.529851275,
    "G2 Fall_pr_1_99_wre": 80,
    "G2 Fall_ss_wre": 117,
    "G2 Fall_dss_wre": 653,
    "G2 Winter_theta_wre": 1.907091881,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 691,
    "G2 Spring_theta_wre": 2.173624581,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 717,
    "G3 Fall_theta_wre": 2.483557766,
    "G3 Fall_pr_1_99_wre": 83,
    "G3 Fall_ss_wre": 118,
    "G3 Fall_dss_wre": 748,
    "G3 Winter_theta_wre": 2.80675187,
    "G3 Winter_pr_1_99_wre": 88,
    "G3 Winter_ss_wre": 118,
    "G3 Winter_dss_wre": 781,
    "G3 Spring_theta_wre": 3.121755944,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 812
  },
  {
    "ID": 440,
    "KG Winter_theta_wre": -0.043223439,
    "KG Winter_pr_1_99_wre": 89,
    "KG Winter_ss_wre": 117,
    "KG Winter_dss_wre": 496,
    "KG Spring_theta_wre": 0.290216511,
    "KG Spring_pr_1_99_wre": 87,
    "KG Spring_ss_wre": 117,
    "KG Spring_dss_wre": 529,
    "G1 Fall_theta_wre": 0.677206826,
    "G1 Fall_pr_1_99_wre": 86,
    "G1 Fall_ss_wre": 118,
    "G1 Fall_dss_wre": 568,
    "G1 Winter_theta_wre": 0.859986268,
    "G1 Winter_pr_1_99_wre": 80,
    "G1 Winter_ss_wre": 118,
    "G1 Winter_dss_wre": 586,
    "G1 Spring_theta_wre": 1.193358767,
    "G1 Spring_pr_1_99_wre": 88,
    "G1 Spring_ss_wre": 116,
    "G1 Spring_dss_wre": 619,
    "G2 Fall_theta_wre": 1.541726757,
    "G2 Fall_pr_1_99_wre": 81,
    "G2 Fall_ss_wre": 118,
    "G2 Fall_dss_wre": 654,
    "G2 Winter_theta_wre": 1.908317256,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 691,
    "G2 Spring_theta_wre": 2.190370951,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 719,
    "G3 Fall_theta_wre": 2.492348659,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 118,
    "G3 Fall_dss_wre": 749,
    "G3 Winter_theta_wre": 2.810148125,
    "G3 Winter_pr_1_99_wre": 88,
    "G3 Winter_ss_wre": 118,
    "G3 Winter_dss_wre": 781,
    "G3 Spring_theta_wre": 3.126364641,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 116,
    "G3 Spring_dss_wre": 813
  },
  {
    "ID": 441,
    "KG Winter_theta_wre": -0.035024737,
    "KG Winter_pr_1_99_wre": 89,
    "KG Winter_ss_wre": 117,
    "KG Winter_dss_wre": 496,
    "KG Spring_theta_wre": 0.294593345,
    "KG Spring_pr_1_99_wre": 87,
    "KG Spring_ss_wre": 117,
    "KG Spring_dss_wre": 529,
    "G1 Fall_theta_wre": 0.677974225,
    "G1 Fall_pr_1_99_wre": 86,
    "G1 Fall_ss_wre": 118,
    "G1 Fall_dss_wre": 568,
    "G1 Winter_theta_wre": 0.868671563,
    "G1 Winter_pr_1_99_wre": 80,
    "G1 Winter_ss_wre": 118,
    "G1 Winter_dss_wre": 587,
    "G1 Spring_theta_wre": 1.204508497,
    "G1 Spring_pr_1_99_wre": 89,
    "G1 Spring_ss_wre": 117,
    "G1 Spring_dss_wre": 620,
    "G2 Fall_theta_wre": 1.546174951,
    "G2 Fall_pr_1_99_wre": 81,
    "G2 Fall_ss_wre": 118,
    "G2 Fall_dss_wre": 655,
    "G2 Winter_theta_wre": 1.908611727,
    "G2 Winter_pr_1_99_wre": 78,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 691,
    "G2 Spring_theta_wre": 2.191943412,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 719,
    "G3 Fall_theta_wre": 2.510130246,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 118,
    "G3 Fall_dss_wre": 751,
    "G3 Winter_theta_wre": 2.824892352,
    "G3 Winter_pr_1_99_wre": 88,
    "G3 Winter_ss_wre": 118,
    "G3 Winter_dss_wre": 782,
    "G3 Spring_theta_wre": 3.150807705,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 117,
    "G3 Spring_dss_wre": 815
  },
  {
    "ID": 442,
    "KG Winter_theta_wre": -0.027953346,
    "KG Winter_pr_1_99_wre": 89,
    "KG Winter_ss_wre": 117,
    "KG Winter_dss_wre": 497,
    "KG Spring_theta_wre": 0.310604568,
    "KG Spring_pr_1_99_wre": 88,
    "KG Spring_ss_wre": 117,
    "KG Spring_dss_wre": 531,
    "G1 Fall_theta_wre": 0.688329998,
    "G1 Fall_pr_1_99_wre": 86,
    "G1 Fall_ss_wre": 118,
    "G1 Fall_dss_wre": 569,
    "G1 Winter_theta_wre": 0.870187131,
    "G1 Winter_pr_1_99_wre": 80,
    "G1 Winter_ss_wre": 118,
    "G1 Winter_dss_wre": 587,
    "G1 Spring_theta_wre": 1.211583196,
    "G1 Spring_pr_1_99_wre": 89,
    "G1 Spring_ss_wre": 117,
    "G1 Spring_dss_wre": 621,
    "G2 Fall_theta_wre": 1.546826542,
    "G2 Fall_pr_1_99_wre": 81,
    "G2 Fall_ss_wre": 118,
    "G2 Fall_dss_wre": 655,
    "G2 Winter_theta_wre": 1.913830348,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 691,
    "G2 Spring_theta_wre": 2.195549435,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 720,
    "G3 Fall_theta_wre": 2.51149975,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 118,
    "G3 Fall_dss_wre": 751,
    "G3 Winter_theta_wre": 2.84535532,
    "G3 Winter_pr_1_99_wre": 89,
    "G3 Winter_ss_wre": 119,
    "G3 Winter_dss_wre": 785,
    "G3 Spring_theta_wre": 3.150836468,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 117,
    "G3 Spring_dss_wre": 815
  },
  {
    "ID": 443,
    "KG Winter_theta_wre": -0.020638524,
    "KG Winter_pr_1_99_wre": 89,
    "KG Winter_ss_wre": 117,
    "KG Winter_dss_wre": 498,
    "KG Spring_theta_wre": 0.314637941,
    "KG Spring_pr_1_99_wre": 88,
    "KG Spring_ss_wre": 117,
    "KG Spring_dss_wre": 531,
    "G1 Fall_theta_wre": 0.705378518,
    "G1 Fall_pr_1_99_wre": 86,
    "G1 Fall_ss_wre": 118,
    "G1 Fall_dss_wre": 571,
    "G1 Winter_theta_wre": 0.874522756,
    "G1 Winter_pr_1_99_wre": 80,
    "G1 Winter_ss_wre": 118,
    "G1 Winter_dss_wre": 587,
    "G1 Spring_theta_wre": 1.224503095,
    "G1 Spring_pr_1_99_wre": 89,
    "G1 Spring_ss_wre": 117,
    "G1 Spring_dss_wre": 622,
    "G2 Fall_theta_wre": 1.562980849,
    "G2 Fall_pr_1_99_wre": 81,
    "G2 Fall_ss_wre": 118,
    "G2 Fall_dss_wre": 656,
    "G2 Winter_theta_wre": 1.915826091,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 692,
    "G2 Spring_theta_wre": 2.196535926,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 720,
    "G3 Fall_theta_wre": 2.520276511,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 118,
    "G3 Fall_dss_wre": 752,
    "G3 Winter_theta_wre": 2.846016575,
    "G3 Winter_pr_1_99_wre": 89,
    "G3 Winter_ss_wre": 119,
    "G3 Winter_dss_wre": 785,
    "G3 Spring_theta_wre": 3.160314798,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 117,
    "G3 Spring_dss_wre": 816
  },
  {
    "ID": 444,
    "KG Winter_theta_wre": -0.01786695,
    "KG Winter_pr_1_99_wre": 89,
    "KG Winter_ss_wre": 117,
    "KG Winter_dss_wre": 498,
    "KG Spring_theta_wre": 0.351671497,
    "KG Spring_pr_1_99_wre": 88,
    "KG Spring_ss_wre": 118,
    "KG Spring_dss_wre": 535,
    "G1 Fall_theta_wre": 0.707329044,
    "G1 Fall_pr_1_99_wre": 86,
    "G1 Fall_ss_wre": 118,
    "G1 Fall_dss_wre": 571,
    "G1 Winter_theta_wre": 0.894771396,
    "G1 Winter_pr_1_99_wre": 80,
    "G1 Winter_ss_wre": 119,
    "G1 Winter_dss_wre": 589,
    "G1 Spring_theta_wre": 1.234692379,
    "G1 Spring_pr_1_99_wre": 89,
    "G1 Spring_ss_wre": 117,
    "G1 Spring_dss_wre": 623,
    "G2 Fall_theta_wre": 1.582415991,
    "G2 Fall_pr_1_99_wre": 83,
    "G2 Fall_ss_wre": 118,
    "G2 Fall_dss_wre": 658,
    "G2 Winter_theta_wre": 1.916110582,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 692,
    "G2 Spring_theta_wre": 2.199844592,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 720,
    "G3 Fall_theta_wre": 2.528662326,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 119,
    "G3 Fall_dss_wre": 753,
    "G3 Winter_theta_wre": 2.864046985,
    "G3 Winter_pr_1_99_wre": 90,
    "G3 Winter_ss_wre": 119,
    "G3 Winter_dss_wre": 786,
    "G3 Spring_theta_wre": 3.161893129,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 117,
    "G3 Spring_dss_wre": 816
  },
  {
    "ID": 445,
    "KG Winter_theta_wre": -0.015501817,
    "KG Winter_pr_1_99_wre": 89,
    "KG Winter_ss_wre": 117,
    "KG Winter_dss_wre": 498,
    "KG Spring_theta_wre": 0.356920643,
    "KG Spring_pr_1_99_wre": 88,
    "KG Spring_ss_wre": 118,
    "KG Spring_dss_wre": 536,
    "G1 Fall_theta_wre": 0.707688605,
    "G1 Fall_pr_1_99_wre": 86,
    "G1 Fall_ss_wre": 118,
    "G1 Fall_dss_wre": 571,
    "G1 Winter_theta_wre": 0.910983244,
    "G1 Winter_pr_1_99_wre": 81,
    "G1 Winter_ss_wre": 119,
    "G1 Winter_dss_wre": 591,
    "G1 Spring_theta_wre": 1.239749829,
    "G1 Spring_pr_1_99_wre": 89,
    "G1 Spring_ss_wre": 117,
    "G1 Spring_dss_wre": 624,
    "G2 Fall_theta_wre": 1.629125489,
    "G2 Fall_pr_1_99_wre": 85,
    "G2 Fall_ss_wre": 119,
    "G2 Fall_dss_wre": 663,
    "G2 Winter_theta_wre": 1.922722607,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 692,
    "G2 Spring_theta_wre": 2.200112269,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 720,
    "G3 Fall_theta_wre": 2.540328462,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 119,
    "G3 Fall_dss_wre": 754,
    "G3 Winter_theta_wre": 2.874238024,
    "G3 Winter_pr_1_99_wre": 90,
    "G3 Winter_ss_wre": 119,
    "G3 Winter_dss_wre": 787,
    "G3 Spring_theta_wre": 3.168868242,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 117,
    "G3 Spring_dss_wre": 817
  },
  {
    "ID": 446,
    "KG Winter_theta_wre": 0.012695129,
    "KG Winter_pr_1_99_wre": 90,
    "KG Winter_ss_wre": 118,
    "KG Winter_dss_wre": 501,
    "KG Spring_theta_wre": 0.358291495,
    "KG Spring_pr_1_99_wre": 88,
    "KG Spring_ss_wre": 118,
    "KG Spring_dss_wre": 536,
    "G1 Fall_theta_wre": 0.717663807,
    "G1 Fall_pr_1_99_wre": 87,
    "G1 Fall_ss_wre": 118,
    "G1 Fall_dss_wre": 572,
    "G1 Winter_theta_wre": 0.913838481,
    "G1 Winter_pr_1_99_wre": 81,
    "G1 Winter_ss_wre": 119,
    "G1 Winter_dss_wre": 591,
    "G1 Spring_theta_wre": 1.245559969,
    "G1 Spring_pr_1_99_wre": 89,
    "G1 Spring_ss_wre": 117,
    "G1 Spring_dss_wre": 625,
    "G2 Fall_theta_wre": 1.642290071,
    "G2 Fall_pr_1_99_wre": 85,
    "G2 Fall_ss_wre": 119,
    "G2 Fall_dss_wre": 664,
    "G2 Winter_theta_wre": 1.924081831,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 692,
    "G2 Spring_theta_wre": 2.205148836,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 721,
    "G3 Fall_theta_wre": 2.546176797,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 119,
    "G3 Fall_dss_wre": 755,
    "G3 Winter_theta_wre": 2.875762771,
    "G3 Winter_pr_1_99_wre": 90,
    "G3 Winter_ss_wre": 119,
    "G3 Winter_dss_wre": 788,
    "G3 Spring_theta_wre": 3.173149619,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 117,
    "G3 Spring_dss_wre": 817
  },
  {
    "ID": 447,
    "KG Winter_theta_wre": 0.022369057,
    "KG Winter_pr_1_99_wre": 90,
    "KG Winter_ss_wre": 118,
    "KG Winter_dss_wre": 502,
    "KG Spring_theta_wre": 0.366934251,
    "KG Spring_pr_1_99_wre": 88,
    "KG Spring_ss_wre": 118,
    "KG Spring_dss_wre": 537,
    "G1 Fall_theta_wre": 0.7181911,
    "G1 Fall_pr_1_99_wre": 87,
    "G1 Fall_ss_wre": 118,
    "G1 Fall_dss_wre": 572,
    "G1 Winter_theta_wre": 0.929030476,
    "G1 Winter_pr_1_99_wre": 81,
    "G1 Winter_ss_wre": 119,
    "G1 Winter_dss_wre": 593,
    "G1 Spring_theta_wre": 1.256716461,
    "G1 Spring_pr_1_99_wre": 89,
    "G1 Spring_ss_wre": 118,
    "G1 Spring_dss_wre": 626,
    "G2 Fall_theta_wre": 1.64790764,
    "G2 Fall_pr_1_99_wre": 85,
    "G2 Fall_ss_wre": 119,
    "G2 Fall_dss_wre": 665,
    "G2 Winter_theta_wre": 1.924551188,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 692,
    "G2 Spring_theta_wre": 2.208724338,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 721,
    "G3 Fall_theta_wre": 2.547064253,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 119,
    "G3 Fall_dss_wre": 755,
    "G3 Winter_theta_wre": 2.883478937,
    "G3 Winter_pr_1_99_wre": 90,
    "G3 Winter_ss_wre": 119,
    "G3 Winter_dss_wre": 788,
    "G3 Spring_theta_wre": 3.174144634,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 117,
    "G3 Spring_dss_wre": 817
  },
  {
    "ID": 448,
    "KG Winter_theta_wre": 0.042181667,
    "KG Winter_pr_1_99_wre": 90,
    "KG Winter_ss_wre": 118,
    "KG Winter_dss_wre": 504,
    "KG Spring_theta_wre": 0.375466963,
    "KG Spring_pr_1_99_wre": 88,
    "KG Spring_ss_wre": 118,
    "KG Spring_dss_wre": 538,
    "G1 Fall_theta_wre": 0.73862438,
    "G1 Fall_pr_1_99_wre": 87,
    "G1 Fall_ss_wre": 119,
    "G1 Fall_dss_wre": 574,
    "G1 Winter_theta_wre": 0.93006201,
    "G1 Winter_pr_1_99_wre": 81,
    "G1 Winter_ss_wre": 120,
    "G1 Winter_dss_wre": 593,
    "G1 Spring_theta_wre": 1.262390583,
    "G1 Spring_pr_1_99_wre": 90,
    "G1 Spring_ss_wre": 118,
    "G1 Spring_dss_wre": 626,
    "G2 Fall_theta_wre": 1.663416084,
    "G2 Fall_pr_1_99_wre": 85,
    "G2 Fall_ss_wre": 120,
    "G2 Fall_dss_wre": 666,
    "G2 Winter_theta_wre": 1.925634968,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 693,
    "G2 Spring_theta_wre": 2.211389169,
    "G2 Spring_pr_1_99_wre": 96,
    "G2 Spring_ss_wre": 118,
    "G2 Spring_dss_wre": 721,
    "G3 Fall_theta_wre": 2.55263801,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 119,
    "G3 Fall_dss_wre": 755,
    "G3 Winter_theta_wre": 2.883968322,
    "G3 Winter_pr_1_99_wre": 90,
    "G3 Winter_ss_wre": 119,
    "G3 Winter_dss_wre": 788,
    "G3 Spring_theta_wre": 3.201329883,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 118,
    "G3 Spring_dss_wre": 820
  },
  {
    "ID": 449,
    "KG Winter_theta_wre": 0.042683797,
    "KG Winter_pr_1_99_wre": 90,
    "KG Winter_ss_wre": 118,
    "KG Winter_dss_wre": 504,
    "KG Spring_theta_wre": 0.382118429,
    "KG Spring_pr_1_99_wre": 88,
    "KG Spring_ss_wre": 118,
    "KG Spring_dss_wre": 538,
    "G1 Fall_theta_wre": 0.768280556,
    "G1 Fall_pr_1_99_wre": 87,
    "G1 Fall_ss_wre": 119,
    "G1 Fall_dss_wre": 577,
    "G1 Winter_theta_wre": 0.931972863,
    "G1 Winter_pr_1_99_wre": 81,
    "G1 Winter_ss_wre": 120,
    "G1 Winter_dss_wre": 593,
    "G1 Spring_theta_wre": 1.276413428,
    "G1 Spring_pr_1_99_wre": 90,
    "G1 Spring_ss_wre": 118,
    "G1 Spring_dss_wre": 628,
    "G2 Fall_theta_wre": 1.66438255,
    "G2 Fall_pr_1_99_wre": 85,
    "G2 Fall_ss_wre": 120,
    "G2 Fall_dss_wre": 666,
    "G2 Winter_theta_wre": 1.931069834,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 118,
    "G2 Winter_dss_wre": 693,
    "G2 Spring_theta_wre": 2.22470613,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 119,
    "G2 Spring_dss_wre": 722,
    "G3 Fall_theta_wre": 2.555047994,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 119,
    "G3 Fall_dss_wre": 756,
    "G3 Winter_theta_wre": 2.88436319,
    "G3 Winter_pr_1_99_wre": 90,
    "G3 Winter_ss_wre": 119,
    "G3 Winter_dss_wre": 788,
    "G3 Spring_theta_wre": 3.201749236,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 118,
    "G3 Spring_dss_wre": 820
  },
  {
    "ID": 450,
    "KG Winter_theta_wre": 0.064949112,
    "KG Winter_pr_1_99_wre": 90,
    "KG Winter_ss_wre": 119,
    "KG Winter_dss_wre": 506,
    "KG Spring_theta_wre": 0.398774218,
    "KG Spring_pr_1_99_wre": 88,
    "KG Spring_ss_wre": 118,
    "KG Spring_dss_wre": 540,
    "G1 Fall_theta_wre": 0.768744555,
    "G1 Fall_pr_1_99_wre": 87,
    "G1 Fall_ss_wre": 119,
    "G1 Fall_dss_wre": 577,
    "G1 Winter_theta_wre": 0.934801415,
    "G1 Winter_pr_1_99_wre": 81,
    "G1 Winter_ss_wre": 120,
    "G1 Winter_dss_wre": 593,
    "G1 Spring_theta_wre": 1.279333093,
    "G1 Spring_pr_1_99_wre": 90,
    "G1 Spring_ss_wre": 118,
    "G1 Spring_dss_wre": 628,
    "G2 Fall_theta_wre": 1.685582922,
    "G2 Fall_pr_1_99_wre": 85,
    "G2 Fall_ss_wre": 120,
    "G2 Fall_dss_wre": 669,
    "G2 Winter_theta_wre": 1.932554058,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 119,
    "G2 Winter_dss_wre": 693,
    "G2 Spring_theta_wre": 2.234244165,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 119,
    "G2 Spring_dss_wre": 723,
    "G3 Fall_theta_wre": 2.563617251,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 119,
    "G3 Fall_dss_wre": 756,
    "G3 Winter_theta_wre": 2.893054215,
    "G3 Winter_pr_1_99_wre": 91,
    "G3 Winter_ss_wre": 120,
    "G3 Winter_dss_wre": 789,
    "G3 Spring_theta_wre": 3.204684814,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 118,
    "G3 Spring_dss_wre": 820
  },
  {
    "ID": 451,
    "KG Winter_theta_wre": 0.06935867,
    "KG Winter_pr_1_99_wre": 90,
    "KG Winter_ss_wre": 119,
    "KG Winter_dss_wre": 507,
    "KG Spring_theta_wre": 0.416491577,
    "KG Spring_pr_1_99_wre": 88,
    "KG Spring_ss_wre": 119,
    "KG Spring_dss_wre": 542,
    "G1 Fall_theta_wre": 0.770102855,
    "G1 Fall_pr_1_99_wre": 87,
    "G1 Fall_ss_wre": 119,
    "G1 Fall_dss_wre": 577,
    "G1 Winter_theta_wre": 0.939716438,
    "G1 Winter_pr_1_99_wre": 81,
    "G1 Winter_ss_wre": 120,
    "G1 Winter_dss_wre": 594,
    "G1 Spring_theta_wre": 1.287769852,
    "G1 Spring_pr_1_99_wre": 90,
    "G1 Spring_ss_wre": 118,
    "G1 Spring_dss_wre": 629,
    "G2 Fall_theta_wre": 1.693468344,
    "G2 Fall_pr_1_99_wre": 85,
    "G2 Fall_ss_wre": 120,
    "G2 Fall_dss_wre": 669,
    "G2 Winter_theta_wre": 1.941954824,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 119,
    "G2 Winter_dss_wre": 694,
    "G2 Spring_theta_wre": 2.244025946,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 119,
    "G2 Spring_dss_wre": 724,
    "G3 Fall_theta_wre": 2.579319992,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 120,
    "G3 Fall_dss_wre": 758,
    "G3 Winter_theta_wre": 2.914153656,
    "G3 Winter_pr_1_99_wre": 91,
    "G3 Winter_ss_wre": 120,
    "G3 Winter_dss_wre": 791,
    "G3 Spring_theta_wre": 3.205808943,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 118,
    "G3 Spring_dss_wre": 821
  },
  {
    "ID": 452,
    "KG Winter_theta_wre": 0.070343055,
    "KG Winter_pr_1_99_wre": 90,
    "KG Winter_ss_wre": 119,
    "KG Winter_dss_wre": 507,
    "KG Spring_theta_wre": 0.42885941,
    "KG Spring_pr_1_99_wre": 89,
    "KG Spring_ss_wre": 119,
    "KG Spring_dss_wre": 543,
    "G1 Fall_theta_wre": 0.77472555,
    "G1 Fall_pr_1_99_wre": 87,
    "G1 Fall_ss_wre": 119,
    "G1 Fall_dss_wre": 577,
    "G1 Winter_theta_wre": 0.952085465,
    "G1 Winter_pr_1_99_wre": 82,
    "G1 Winter_ss_wre": 120,
    "G1 Winter_dss_wre": 595,
    "G1 Spring_theta_wre": 1.293706988,
    "G1 Spring_pr_1_99_wre": 91,
    "G1 Spring_ss_wre": 118,
    "G1 Spring_dss_wre": 629,
    "G2 Fall_theta_wre": 1.718605561,
    "G2 Fall_pr_1_99_wre": 87,
    "G2 Fall_ss_wre": 121,
    "G2 Fall_dss_wre": 672,
    "G2 Winter_theta_wre": 1.943450758,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 119,
    "G2 Winter_dss_wre": 694,
    "G2 Spring_theta_wre": 2.257002139,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 119,
    "G2 Spring_dss_wre": 726,
    "G3 Fall_theta_wre": 2.581780137,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 120,
    "G3 Fall_dss_wre": 758,
    "G3 Winter_theta_wre": 2.937349855,
    "G3 Winter_pr_1_99_wre": 92,
    "G3 Winter_ss_wre": 120,
    "G3 Winter_dss_wre": 794,
    "G3 Spring_theta_wre": 3.205947467,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 118,
    "G3 Spring_dss_wre": 821
  },
  {
    "ID": 453,
    "KG Winter_theta_wre": 0.074933806,
    "KG Winter_pr_1_99_wre": 90,
    "KG Winter_ss_wre": 119,
    "KG Winter_dss_wre": 507,
    "KG Spring_theta_wre": 0.432902416,
    "KG Spring_pr_1_99_wre": 89,
    "KG Spring_ss_wre": 119,
    "KG Spring_dss_wre": 543,
    "G1 Fall_theta_wre": 0.775294858,
    "G1 Fall_pr_1_99_wre": 87,
    "G1 Fall_ss_wre": 119,
    "G1 Fall_dss_wre": 578,
    "G1 Winter_theta_wre": 0.952122464,
    "G1 Winter_pr_1_99_wre": 82,
    "G1 Winter_ss_wre": 120,
    "G1 Winter_dss_wre": 595,
    "G1 Spring_theta_wre": 1.294174934,
    "G1 Spring_pr_1_99_wre": 91,
    "G1 Spring_ss_wre": 118,
    "G1 Spring_dss_wre": 629,
    "G2 Fall_theta_wre": 1.728205518,
    "G2 Fall_pr_1_99_wre": 87,
    "G2 Fall_ss_wre": 121,
    "G2 Fall_dss_wre": 673,
    "G2 Winter_theta_wre": 1.950343726,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 119,
    "G2 Winter_dss_wre": 695,
    "G2 Spring_theta_wre": 2.28042928,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 120,
    "G2 Spring_dss_wre": 728,
    "G3 Fall_theta_wre": 2.588019946,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 120,
    "G3 Fall_dss_wre": 759,
    "G3 Winter_theta_wre": 2.966353788,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 121,
    "G3 Winter_dss_wre": 797,
    "G3 Spring_theta_wre": 3.216980267,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 118,
    "G3 Spring_dss_wre": 822
  },
  {
    "ID": 454,
    "KG Winter_theta_wre": 0.080793495,
    "KG Winter_pr_1_99_wre": 91,
    "KG Winter_ss_wre": 119,
    "KG Winter_dss_wre": 508,
    "KG Spring_theta_wre": 0.446148863,
    "KG Spring_pr_1_99_wre": 89,
    "KG Spring_ss_wre": 119,
    "KG Spring_dss_wre": 545,
    "G1 Fall_theta_wre": 0.78283041,
    "G1 Fall_pr_1_99_wre": 88,
    "G1 Fall_ss_wre": 120,
    "G1 Fall_dss_wre": 578,
    "G1 Winter_theta_wre": 0.954816532,
    "G1 Winter_pr_1_99_wre": 82,
    "G1 Winter_ss_wre": 120,
    "G1 Winter_dss_wre": 595,
    "G1 Spring_theta_wre": 1.295337225,
    "G1 Spring_pr_1_99_wre": 91,
    "G1 Spring_ss_wre": 118,
    "G1 Spring_dss_wre": 630,
    "G2 Fall_theta_wre": 1.728428613,
    "G2 Fall_pr_1_99_wre": 87,
    "G2 Fall_ss_wre": 121,
    "G2 Fall_dss_wre": 673,
    "G2 Winter_theta_wre": 1.956461885,
    "G2 Winter_pr_1_99_wre": 82,
    "G2 Winter_ss_wre": 119,
    "G2 Winter_dss_wre": 696,
    "G2 Spring_theta_wre": 2.282380654,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 120,
    "G2 Spring_dss_wre": 728,
    "G3 Fall_theta_wre": 2.589830517,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 120,
    "G3 Fall_dss_wre": 759,
    "G3 Winter_theta_wre": 2.971537266,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 121,
    "G3 Winter_dss_wre": 797,
    "G3 Spring_theta_wre": 3.21980856,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 118,
    "G3 Spring_dss_wre": 822
  },
  {
    "ID": 455,
    "KG Winter_theta_wre": 0.093860716,
    "KG Winter_pr_1_99_wre": 91,
    "KG Winter_ss_wre": 119,
    "KG Winter_dss_wre": 509,
    "KG Spring_theta_wre": 0.455740227,
    "KG Spring_pr_1_99_wre": 89,
    "KG Spring_ss_wre": 119,
    "KG Spring_dss_wre": 546,
    "G1 Fall_theta_wre": 0.785766533,
    "G1 Fall_pr_1_99_wre": 88,
    "G1 Fall_ss_wre": 120,
    "G1 Fall_dss_wre": 579,
    "G1 Winter_theta_wre": 0.955939205,
    "G1 Winter_pr_1_99_wre": 82,
    "G1 Winter_ss_wre": 120,
    "G1 Winter_dss_wre": 596,
    "G1 Spring_theta_wre": 1.299737072,
    "G1 Spring_pr_1_99_wre": 91,
    "G1 Spring_ss_wre": 118,
    "G1 Spring_dss_wre": 630,
    "G2 Fall_theta_wre": 1.734388103,
    "G2 Fall_pr_1_99_wre": 87,
    "G2 Fall_ss_wre": 121,
    "G2 Fall_dss_wre": 673,
    "G2 Winter_theta_wre": 1.964867959,
    "G2 Winter_pr_1_99_wre": 83,
    "G2 Winter_ss_wre": 119,
    "G2 Winter_dss_wre": 696,
    "G2 Spring_theta_wre": 2.318350325,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 120,
    "G2 Spring_dss_wre": 732,
    "G3 Fall_theta_wre": 2.594107257,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 120,
    "G3 Fall_dss_wre": 759,
    "G3 Winter_theta_wre": 2.973013299,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 121,
    "G3 Winter_dss_wre": 797,
    "G3 Spring_theta_wre": 3.221593554,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 118,
    "G3 Spring_dss_wre": 822
  },
  {
    "ID": 456,
    "KG Winter_theta_wre": 0.100810539,
    "KG Winter_pr_1_99_wre": 91,
    "KG Winter_ss_wre": 120,
    "KG Winter_dss_wre": 510,
    "KG Spring_theta_wre": 0.467562536,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 120,
    "KG Spring_dss_wre": 547,
    "G1 Fall_theta_wre": 0.797842513,
    "G1 Fall_pr_1_99_wre": 88,
    "G1 Fall_ss_wre": 120,
    "G1 Fall_dss_wre": 580,
    "G1 Winter_theta_wre": 0.963599626,
    "G1 Winter_pr_1_99_wre": 82,
    "G1 Winter_ss_wre": 120,
    "G1 Winter_dss_wre": 596,
    "G1 Spring_theta_wre": 1.326269957,
    "G1 Spring_pr_1_99_wre": 92,
    "G1 Spring_ss_wre": 119,
    "G1 Spring_dss_wre": 633,
    "G2 Fall_theta_wre": 1.738202084,
    "G2 Fall_pr_1_99_wre": 87,
    "G2 Fall_ss_wre": 121,
    "G2 Fall_dss_wre": 674,
    "G2 Winter_theta_wre": 1.980378303,
    "G2 Winter_pr_1_99_wre": 92,
    "G2 Winter_ss_wre": 119,
    "G2 Winter_dss_wre": 698,
    "G2 Spring_theta_wre": 2.327861572,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 121,
    "G2 Spring_dss_wre": 733,
    "G3 Fall_theta_wre": 2.602746101,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 120,
    "G3 Fall_dss_wre": 760,
    "G3 Winter_theta_wre": 2.976890593,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 121,
    "G3 Winter_dss_wre": 798,
    "G3 Spring_theta_wre": 3.226464307,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 118,
    "G3 Spring_dss_wre": 823
  },
  {
    "ID": 457,
    "KG Winter_theta_wre": 0.118268911,
    "KG Winter_pr_1_99_wre": 91,
    "KG Winter_ss_wre": 120,
    "KG Winter_dss_wre": 512,
    "KG Spring_theta_wre": 0.472942612,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 120,
    "KG Spring_dss_wre": 547,
    "G1 Fall_theta_wre": 0.804423368,
    "G1 Fall_pr_1_99_wre": 88,
    "G1 Fall_ss_wre": 120,
    "G1 Fall_dss_wre": 580,
    "G1 Winter_theta_wre": 0.967233654,
    "G1 Winter_pr_1_99_wre": 82,
    "G1 Winter_ss_wre": 120,
    "G1 Winter_dss_wre": 597,
    "G1 Spring_theta_wre": 1.336448405,
    "G1 Spring_pr_1_99_wre": 92,
    "G1 Spring_ss_wre": 119,
    "G1 Spring_dss_wre": 634,
    "G2 Fall_theta_wre": 1.740621059,
    "G2 Fall_pr_1_99_wre": 87,
    "G2 Fall_ss_wre": 121,
    "G2 Fall_dss_wre": 674,
    "G2 Winter_theta_wre": 1.992722025,
    "G2 Winter_pr_1_99_wre": 92,
    "G2 Winter_ss_wre": 120,
    "G2 Winter_dss_wre": 699,
    "G2 Spring_theta_wre": 2.3301273,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 121,
    "G2 Spring_dss_wre": 733,
    "G3 Fall_theta_wre": 2.607403774,
    "G3 Fall_pr_1_99_wre": 95,
    "G3 Fall_ss_wre": 120,
    "G3 Fall_dss_wre": 761,
    "G3 Winter_theta_wre": 2.9798945,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 121,
    "G3 Winter_dss_wre": 798,
    "G3 Spring_theta_wre": 3.231491395,
    "G3 Spring_pr_1_99_wre": 98,
    "G3 Spring_ss_wre": 118,
    "G3 Spring_dss_wre": 823
  },
  {
    "ID": 458,
    "KG Winter_theta_wre": 0.124484103,
    "KG Winter_pr_1_99_wre": 91,
    "KG Winter_ss_wre": 120,
    "KG Winter_dss_wre": 512,
    "KG Spring_theta_wre": 0.47667313,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 120,
    "KG Spring_dss_wre": 548,
    "G1 Fall_theta_wre": 0.806073953,
    "G1 Fall_pr_1_99_wre": 88,
    "G1 Fall_ss_wre": 120,
    "G1 Fall_dss_wre": 581,
    "G1 Winter_theta_wre": 0.984890226,
    "G1 Winter_pr_1_99_wre": 83,
    "G1 Winter_ss_wre": 121,
    "G1 Winter_dss_wre": 598,
    "G1 Spring_theta_wre": 1.337492387,
    "G1 Spring_pr_1_99_wre": 92,
    "G1 Spring_ss_wre": 119,
    "G1 Spring_dss_wre": 634,
    "G2 Fall_theta_wre": 1.755362803,
    "G2 Fall_pr_1_99_wre": 87,
    "G2 Fall_ss_wre": 121,
    "G2 Fall_dss_wre": 676,
    "G2 Winter_theta_wre": 1.995741343,
    "G2 Winter_pr_1_99_wre": 92,
    "G2 Winter_ss_wre": 120,
    "G2 Winter_dss_wre": 700,
    "G2 Spring_theta_wre": 2.331928601,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 121,
    "G2 Spring_dss_wre": 733,
    "G3 Fall_theta_wre": 2.634728411,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 121,
    "G3 Fall_dss_wre": 763,
    "G3 Winter_theta_wre": 2.979971499,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 121,
    "G3 Winter_dss_wre": 798,
    "G3 Spring_theta_wre": 3.280228421,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 119,
    "G3 Spring_dss_wre": 828
  },
  {
    "ID": 459,
    "KG Winter_theta_wre": 0.12466447,
    "KG Winter_pr_1_99_wre": 91,
    "KG Winter_ss_wre": 120,
    "KG Winter_dss_wre": 512,
    "KG Spring_theta_wre": 0.48825812,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 120,
    "KG Spring_dss_wre": 549,
    "G1 Fall_theta_wre": 0.807851878,
    "G1 Fall_pr_1_99_wre": 88,
    "G1 Fall_ss_wre": 120,
    "G1 Fall_dss_wre": 581,
    "G1 Winter_theta_wre": 0.987825061,
    "G1 Winter_pr_1_99_wre": 83,
    "G1 Winter_ss_wre": 121,
    "G1 Winter_dss_wre": 599,
    "G1 Spring_theta_wre": 1.340828408,
    "G1 Spring_pr_1_99_wre": 92,
    "G1 Spring_ss_wre": 119,
    "G1 Spring_dss_wre": 634,
    "G2 Fall_theta_wre": 1.76268372,
    "G2 Fall_pr_1_99_wre": 88,
    "G2 Fall_ss_wre": 122,
    "G2 Fall_dss_wre": 676,
    "G2 Winter_theta_wre": 2.011799753,
    "G2 Winter_pr_1_99_wre": 92,
    "G2 Winter_ss_wre": 120,
    "G2 Winter_dss_wre": 701,
    "G2 Spring_theta_wre": 2.337247721,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 121,
    "G2 Spring_dss_wre": 734,
    "G3 Fall_theta_wre": 2.649105129,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 121,
    "G3 Fall_dss_wre": 765,
    "G3 Winter_theta_wre": 3.030460762,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 122,
    "G3 Winter_dss_wre": 803,
    "G3 Spring_theta_wre": 3.281895066,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 119,
    "G3 Spring_dss_wre": 828
  },
  {
    "ID": 460,
    "KG Winter_theta_wre": 0.134296587,
    "KG Winter_pr_1_99_wre": 91,
    "KG Winter_ss_wre": 120,
    "KG Winter_dss_wre": 513,
    "KG Spring_theta_wre": 0.507514279,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 120,
    "KG Spring_dss_wre": 551,
    "G1 Fall_theta_wre": 0.808574239,
    "G1 Fall_pr_1_99_wre": 88,
    "G1 Fall_ss_wre": 120,
    "G1 Fall_dss_wre": 581,
    "G1 Winter_theta_wre": 1.007558111,
    "G1 Winter_pr_1_99_wre": 84,
    "G1 Winter_ss_wre": 121,
    "G1 Winter_dss_wre": 601,
    "G1 Spring_theta_wre": 1.362458514,
    "G1 Spring_pr_1_99_wre": 92,
    "G1 Spring_ss_wre": 119,
    "G1 Spring_dss_wre": 636,
    "G2 Fall_theta_wre": 1.770588522,
    "G2 Fall_pr_1_99_wre": 88,
    "G2 Fall_ss_wre": 122,
    "G2 Fall_dss_wre": 677,
    "G2 Winter_theta_wre": 2.012385735,
    "G2 Winter_pr_1_99_wre": 92,
    "G2 Winter_ss_wre": 120,
    "G2 Winter_dss_wre": 701,
    "G2 Spring_theta_wre": 2.338508112,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 121,
    "G2 Spring_dss_wre": 734,
    "G3 Fall_theta_wre": 2.657216364,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 121,
    "G3 Fall_dss_wre": 766,
    "G3 Winter_theta_wre": 3.033267034,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 122,
    "G3 Winter_dss_wre": 803,
    "G3 Spring_theta_wre": 3.282966768,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 119,
    "G3 Spring_dss_wre": 828
  },
  {
    "ID": 461,
    "KG Winter_theta_wre": 0.147051281,
    "KG Winter_pr_1_99_wre": 92,
    "KG Winter_ss_wre": 120,
    "KG Winter_dss_wre": 515,
    "KG Spring_theta_wre": 0.518637272,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 121,
    "KG Spring_dss_wre": 552,
    "G1 Fall_theta_wre": 0.810554442,
    "G1 Fall_pr_1_99_wre": 88,
    "G1 Fall_ss_wre": 120,
    "G1 Fall_dss_wre": 581,
    "G1 Winter_theta_wre": 1.015951244,
    "G1 Winter_pr_1_99_wre": 84,
    "G1 Winter_ss_wre": 121,
    "G1 Winter_dss_wre": 602,
    "G1 Spring_theta_wre": 1.365107386,
    "G1 Spring_pr_1_99_wre": 92,
    "G1 Spring_ss_wre": 119,
    "G1 Spring_dss_wre": 637,
    "G2 Fall_theta_wre": 1.800086592,
    "G2 Fall_pr_1_99_wre": 88,
    "G2 Fall_ss_wre": 122,
    "G2 Fall_dss_wre": 680,
    "G2 Winter_theta_wre": 2.018179066,
    "G2 Winter_pr_1_99_wre": 92,
    "G2 Winter_ss_wre": 120,
    "G2 Winter_dss_wre": 702,
    "G2 Spring_theta_wre": 2.339332865,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 121,
    "G2 Spring_dss_wre": 734,
    "G3 Fall_theta_wre": 2.659402139,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 121,
    "G3 Fall_dss_wre": 766,
    "G3 Winter_theta_wre": 3.036766916,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 122,
    "G3 Winter_dss_wre": 804,
    "G3 Spring_theta_wre": 3.285552169,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 119,
    "G3 Spring_dss_wre": 829
  },
  {
    "ID": 462,
    "KG Winter_theta_wre": 0.151828205,
    "KG Winter_pr_1_99_wre": 92,
    "KG Winter_ss_wre": 120,
    "KG Winter_dss_wre": 515,
    "KG Spring_theta_wre": 0.52173148,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 121,
    "KG Spring_dss_wre": 552,
    "G1 Fall_theta_wre": 0.813737912,
    "G1 Fall_pr_1_99_wre": 88,
    "G1 Fall_ss_wre": 120,
    "G1 Fall_dss_wre": 581,
    "G1 Winter_theta_wre": 1.032599957,
    "G1 Winter_pr_1_99_wre": 86,
    "G1 Winter_ss_wre": 121,
    "G1 Winter_dss_wre": 603,
    "G1 Spring_theta_wre": 1.369877743,
    "G1 Spring_pr_1_99_wre": 93,
    "G1 Spring_ss_wre": 120,
    "G1 Spring_dss_wre": 637,
    "G2 Fall_theta_wre": 1.808662516,
    "G2 Fall_pr_1_99_wre": 88,
    "G2 Fall_ss_wre": 122,
    "G2 Fall_dss_wre": 681,
    "G2 Winter_theta_wre": 2.079160394,
    "G2 Winter_pr_1_99_wre": 93,
    "G2 Winter_ss_wre": 121,
    "G2 Winter_dss_wre": 708,
    "G2 Spring_theta_wre": 2.339838343,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 121,
    "G2 Spring_dss_wre": 734,
    "G3 Fall_theta_wre": 2.66856019,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 121,
    "G3 Fall_dss_wre": 767,
    "G3 Winter_theta_wre": 3.063614003,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 123,
    "G3 Winter_dss_wre": 806,
    "G3 Spring_theta_wre": 3.326040144,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 120,
    "G3 Spring_dss_wre": 833
  },
  {
    "ID": 463,
    "KG Winter_theta_wre": 0.157973765,
    "KG Winter_pr_1_99_wre": 92,
    "KG Winter_ss_wre": 121,
    "KG Winter_dss_wre": 516,
    "KG Spring_theta_wre": 0.532943234,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 121,
    "KG Spring_dss_wre": 553,
    "G1 Fall_theta_wre": 0.83204218,
    "G1 Fall_pr_1_99_wre": 89,
    "G1 Fall_ss_wre": 120,
    "G1 Fall_dss_wre": 583,
    "G1 Winter_theta_wre": 1.033892053,
    "G1 Winter_pr_1_99_wre": 86,
    "G1 Winter_ss_wre": 122,
    "G1 Winter_dss_wre": 603,
    "G1 Spring_theta_wre": 1.381721152,
    "G1 Spring_pr_1_99_wre": 93,
    "G1 Spring_ss_wre": 120,
    "G1 Spring_dss_wre": 638,
    "G2 Fall_theta_wre": 1.818939479,
    "G2 Fall_pr_1_99_wre": 88,
    "G2 Fall_ss_wre": 123,
    "G2 Fall_dss_wre": 682,
    "G2 Winter_theta_wre": 2.087321788,
    "G2 Winter_pr_1_99_wre": 93,
    "G2 Winter_ss_wre": 121,
    "G2 Winter_dss_wre": 709,
    "G2 Spring_theta_wre": 2.345761918,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 121,
    "G2 Spring_dss_wre": 735,
    "G3 Fall_theta_wre": 2.671071465,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 121,
    "G3 Fall_dss_wre": 767,
    "G3 Winter_theta_wre": 3.064405131,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 123,
    "G3 Winter_dss_wre": 806,
    "G3 Spring_theta_wre": 3.330932803,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 120,
    "G3 Spring_dss_wre": 833
  },
  {
    "ID": 464,
    "KG Winter_theta_wre": 0.158631432,
    "KG Winter_pr_1_99_wre": 92,
    "KG Winter_ss_wre": 121,
    "KG Winter_dss_wre": 516,
    "KG Spring_theta_wre": 0.568533631,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 121,
    "KG Spring_dss_wre": 557,
    "G1 Fall_theta_wre": 0.837565419,
    "G1 Fall_pr_1_99_wre": 89,
    "G1 Fall_ss_wre": 121,
    "G1 Fall_dss_wre": 584,
    "G1 Winter_theta_wre": 1.033994632,
    "G1 Winter_pr_1_99_wre": 86,
    "G1 Winter_ss_wre": 122,
    "G1 Winter_dss_wre": 603,
    "G1 Spring_theta_wre": 1.410155531,
    "G1 Spring_pr_1_99_wre": 93,
    "G1 Spring_ss_wre": 120,
    "G1 Spring_dss_wre": 641,
    "G2 Fall_theta_wre": 1.839648071,
    "G2 Fall_pr_1_99_wre": 89,
    "G2 Fall_ss_wre": 123,
    "G2 Fall_dss_wre": 684,
    "G2 Winter_theta_wre": 2.088634125,
    "G2 Winter_pr_1_99_wre": 93,
    "G2 Winter_ss_wre": 121,
    "G2 Winter_dss_wre": 709,
    "G2 Spring_theta_wre": 2.356678143,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 121,
    "G2 Spring_dss_wre": 736,
    "G3 Fall_theta_wre": 2.673224693,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 121,
    "G3 Fall_dss_wre": 767,
    "G3 Winter_theta_wre": 3.068404025,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 123,
    "G3 Winter_dss_wre": 807,
    "G3 Spring_theta_wre": 3.33255651,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 120,
    "G3 Spring_dss_wre": 833
  },
  {
    "ID": 465,
    "KG Winter_theta_wre": 0.169479532,
    "KG Winter_pr_1_99_wre": 92,
    "KG Winter_ss_wre": 121,
    "KG Winter_dss_wre": 517,
    "KG Spring_theta_wre": 0.587745901,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 122,
    "KG Spring_dss_wre": 559,
    "G1 Fall_theta_wre": 0.881611476,
    "G1 Fall_pr_1_99_wre": 89,
    "G1 Fall_ss_wre": 121,
    "G1 Fall_dss_wre": 588,
    "G1 Winter_theta_wre": 1.038231553,
    "G1 Winter_pr_1_99_wre": 86,
    "G1 Winter_ss_wre": 122,
    "G1 Winter_dss_wre": 604,
    "G1 Spring_theta_wre": 1.411432491,
    "G1 Spring_pr_1_99_wre": 93,
    "G1 Spring_ss_wre": 120,
    "G1 Spring_dss_wre": 641,
    "G2 Fall_theta_wre": 1.846584467,
    "G2 Fall_pr_1_99_wre": 89,
    "G2 Fall_ss_wre": 123,
    "G2 Fall_dss_wre": 685,
    "G2 Winter_theta_wre": 2.099887488,
    "G2 Winter_pr_1_99_wre": 93,
    "G2 Winter_ss_wre": 122,
    "G2 Winter_dss_wre": 710,
    "G2 Spring_theta_wre": 2.383413487,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 122,
    "G2 Spring_dss_wre": 738,
    "G3 Fall_theta_wre": 2.675244998,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 121,
    "G3 Fall_dss_wre": 768,
    "G3 Winter_theta_wre": 3.080897931,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 123,
    "G3 Winter_dss_wre": 808,
    "G3 Spring_theta_wre": 3.336698751,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 120,
    "G3 Spring_dss_wre": 834
  },
  {
    "ID": 466,
    "KG Winter_theta_wre": 0.182535089,
    "KG Winter_pr_1_99_wre": 93,
    "KG Winter_ss_wre": 121,
    "KG Winter_dss_wre": 518,
    "KG Spring_theta_wre": 0.589427793,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 122,
    "KG Spring_dss_wre": 559,
    "G1 Fall_theta_wre": 0.88879778,
    "G1 Fall_pr_1_99_wre": 89,
    "G1 Fall_ss_wre": 122,
    "G1 Fall_dss_wre": 589,
    "G1 Winter_theta_wre": 1.072513063,
    "G1 Winter_pr_1_99_wre": 87,
    "G1 Winter_ss_wre": 122,
    "G1 Winter_dss_wre": 607,
    "G1 Spring_theta_wre": 1.430931807,
    "G1 Spring_pr_1_99_wre": 93,
    "G1 Spring_ss_wre": 121,
    "G1 Spring_dss_wre": 643,
    "G2 Fall_theta_wre": 1.85970025,
    "G2 Fall_pr_1_99_wre": 89,
    "G2 Fall_ss_wre": 123,
    "G2 Fall_dss_wre": 686,
    "G2 Winter_theta_wre": 2.100323627,
    "G2 Winter_pr_1_99_wre": 93,
    "G2 Winter_ss_wre": 122,
    "G2 Winter_dss_wre": 710,
    "G2 Spring_theta_wre": 2.386788525,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 122,
    "G2 Spring_dss_wre": 739,
    "G3 Fall_theta_wre": 2.684089276,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 121,
    "G3 Fall_dss_wre": 768,
    "G3 Winter_theta_wre": 3.094989681,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 123,
    "G3 Winter_dss_wre": 809,
    "G3 Spring_theta_wre": 3.347508011,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 120,
    "G3 Spring_dss_wre": 835
  },
  {
    "ID": 467,
    "KG Winter_theta_wre": 0.192680012,
    "KG Winter_pr_1_99_wre": 93,
    "KG Winter_ss_wre": 121,
    "KG Winter_dss_wre": 519,
    "KG Spring_theta_wre": 0.592535739,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 122,
    "KG Spring_dss_wre": 559,
    "G1 Fall_theta_wre": 0.894160602,
    "G1 Fall_pr_1_99_wre": 90,
    "G1 Fall_ss_wre": 122,
    "G1 Fall_dss_wre": 589,
    "G1 Winter_theta_wre": 1.074085398,
    "G1 Winter_pr_1_99_wre": 87,
    "G1 Winter_ss_wre": 122,
    "G1 Winter_dss_wre": 607,
    "G1 Spring_theta_wre": 1.432497462,
    "G1 Spring_pr_1_99_wre": 93,
    "G1 Spring_ss_wre": 121,
    "G1 Spring_dss_wre": 643,
    "G2 Fall_theta_wre": 1.877862124,
    "G2 Fall_pr_1_99_wre": 89,
    "G2 Fall_ss_wre": 124,
    "G2 Fall_dss_wre": 688,
    "G2 Winter_theta_wre": 2.131054424,
    "G2 Winter_pr_1_99_wre": 94,
    "G2 Winter_ss_wre": 122,
    "G2 Winter_dss_wre": 713,
    "G2 Spring_theta_wre": 2.405464827,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 122,
    "G2 Spring_dss_wre": 741,
    "G3 Fall_theta_wre": 2.706788184,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 122,
    "G3 Fall_dss_wre": 771,
    "G3 Winter_theta_wre": 3.102427202,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 123,
    "G3 Winter_dss_wre": 810,
    "G3 Spring_theta_wre": 3.348986206,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 120,
    "G3 Spring_dss_wre": 835
  },
  {
    "ID": 468,
    "KG Winter_theta_wre": 0.217392828,
    "KG Winter_pr_1_99_wre": 93,
    "KG Winter_ss_wre": 122,
    "KG Winter_dss_wre": 522,
    "KG Spring_theta_wre": 0.608017845,
    "KG Spring_pr_1_99_wre": 90,
    "KG Spring_ss_wre": 122,
    "KG Spring_dss_wre": 561,
    "G1 Fall_theta_wre": 0.907040708,
    "G1 Fall_pr_1_99_wre": 90,
    "G1 Fall_ss_wre": 122,
    "G1 Fall_dss_wre": 591,
    "G1 Winter_theta_wre": 1.081060046,
    "G1 Winter_pr_1_99_wre": 87,
    "G1 Winter_ss_wre": 122,
    "G1 Winter_dss_wre": 608,
    "G1 Spring_theta_wre": 1.459735936,
    "G1 Spring_pr_1_99_wre": 93,
    "G1 Spring_ss_wre": 121,
    "G1 Spring_dss_wre": 646,
    "G2 Fall_theta_wre": 1.899774579,
    "G2 Fall_pr_1_99_wre": 89,
    "G2 Fall_ss_wre": 124,
    "G2 Fall_dss_wre": 690,
    "G2 Winter_theta_wre": 2.177069573,
    "G2 Winter_pr_1_99_wre": 96,
    "G2 Winter_ss_wre": 123,
    "G2 Winter_dss_wre": 718,
    "G2 Spring_theta_wre": 2.413382914,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 122,
    "G2 Spring_dss_wre": 741,
    "G3 Fall_theta_wre": 2.707385536,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 122,
    "G3 Fall_dss_wre": 771,
    "G3 Winter_theta_wre": 3.117318,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 124,
    "G3 Winter_dss_wre": 812,
    "G3 Spring_theta_wre": 3.379757968,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 121,
    "G3 Spring_dss_wre": 838
  },
  {
    "ID": 469,
    "KG Winter_theta_wre": 0.241445067,
    "KG Winter_pr_1_99_wre": 93,
    "KG Winter_ss_wre": 122,
    "KG Winter_dss_wre": 524,
    "KG Spring_theta_wre": 0.624516467,
    "KG Spring_pr_1_99_wre": 95,
    "KG Spring_ss_wre": 122,
    "KG Spring_dss_wre": 562,
    "G1 Fall_theta_wre": 0.923447129,
    "G1 Fall_pr_1_99_wre": 90,
    "G1 Fall_ss_wre": 122,
    "G1 Fall_dss_wre": 592,
    "G1 Winter_theta_wre": 1.10663426,
    "G1 Winter_pr_1_99_wre": 88,
    "G1 Winter_ss_wre": 123,
    "G1 Winter_dss_wre": 611,
    "G1 Spring_theta_wre": 1.466046418,
    "G1 Spring_pr_1_99_wre": 94,
    "G1 Spring_ss_wre": 121,
    "G1 Spring_dss_wre": 647,
    "G2 Fall_theta_wre": 1.900116194,
    "G2 Fall_pr_1_99_wre": 89,
    "G2 Fall_ss_wre": 124,
    "G2 Fall_dss_wre": 690,
    "G2 Winter_theta_wre": 2.185233985,
    "G2 Winter_pr_1_99_wre": 96,
    "G2 Winter_ss_wre": 123,
    "G2 Winter_dss_wre": 719,
    "G2 Spring_theta_wre": 2.416612447,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 122,
    "G2 Spring_dss_wre": 742,
    "G3 Fall_theta_wre": 2.723043599,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 122,
    "G3 Fall_dss_wre": 772,
    "G3 Winter_theta_wre": 3.138890594,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 124,
    "G3 Winter_dss_wre": 814,
    "G3 Spring_theta_wre": 3.385769284,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 121,
    "G3 Spring_dss_wre": 839
  },
  {
    "ID": 470,
    "KG Winter_theta_wre": 0.263929441,
    "KG Winter_pr_1_99_wre": 94,
    "KG Winter_ss_wre": 122,
    "KG Winter_dss_wre": 526,
    "KG Spring_theta_wre": 0.626768769,
    "KG Spring_pr_1_99_wre": 95,
    "KG Spring_ss_wre": 123,
    "KG Spring_dss_wre": 563,
    "G1 Fall_theta_wre": 0.944023772,
    "G1 Fall_pr_1_99_wre": 90,
    "G1 Fall_ss_wre": 123,
    "G1 Fall_dss_wre": 594,
    "G1 Winter_theta_wre": 1.109649258,
    "G1 Winter_pr_1_99_wre": 88,
    "G1 Winter_ss_wre": 123,
    "G1 Winter_dss_wre": 611,
    "G1 Spring_theta_wre": 1.47817678,
    "G1 Spring_pr_1_99_wre": 95,
    "G1 Spring_ss_wre": 122,
    "G1 Spring_dss_wre": 648,
    "G2 Fall_theta_wre": 1.933103975,
    "G2 Fall_pr_1_99_wre": 90,
    "G2 Fall_ss_wre": 125,
    "G2 Fall_dss_wre": 693,
    "G2 Winter_theta_wre": 2.198665117,
    "G2 Winter_pr_1_99_wre": 96,
    "G2 Winter_ss_wre": 123,
    "G2 Winter_dss_wre": 720,
    "G2 Spring_theta_wre": 2.417670956,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 122,
    "G2 Spring_dss_wre": 742,
    "G3 Fall_theta_wre": 2.743805229,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 123,
    "G3 Fall_dss_wre": 774,
    "G3 Winter_theta_wre": 3.144631714,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 124,
    "G3 Winter_dss_wre": 814,
    "G3 Spring_theta_wre": 3.409801015,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 121,
    "G3 Spring_dss_wre": 841
  },
  {
    "ID": 471,
    "KG Winter_theta_wre": 0.267653696,
    "KG Winter_pr_1_99_wre": 94,
    "KG Winter_ss_wre": 123,
    "KG Winter_dss_wre": 527,
    "KG Spring_theta_wre": 0.648913761,
    "KG Spring_pr_1_99_wre": 95,
    "KG Spring_ss_wre": 123,
    "KG Spring_dss_wre": 565,
    "G1 Fall_theta_wre": 0.954074333,
    "G1 Fall_pr_1_99_wre": 90,
    "G1 Fall_ss_wre": 123,
    "G1 Fall_dss_wre": 595,
    "G1 Winter_theta_wre": 1.110275852,
    "G1 Winter_pr_1_99_wre": 88,
    "G1 Winter_ss_wre": 123,
    "G1 Winter_dss_wre": 611,
    "G1 Spring_theta_wre": 1.486065566,
    "G1 Spring_pr_1_99_wre": 95,
    "G1 Spring_ss_wre": 122,
    "G1 Spring_dss_wre": 649,
    "G2 Fall_theta_wre": 1.933423396,
    "G2 Fall_pr_1_99_wre": 90,
    "G2 Fall_ss_wre": 125,
    "G2 Fall_dss_wre": 693,
    "G2 Winter_theta_wre": 2.201327519,
    "G2 Winter_pr_1_99_wre": 96,
    "G2 Winter_ss_wre": 123,
    "G2 Winter_dss_wre": 720,
    "G2 Spring_theta_wre": 2.427628379,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 122,
    "G2 Spring_dss_wre": 743,
    "G3 Fall_theta_wre": 2.751749156,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 123,
    "G3 Fall_dss_wre": 775,
    "G3 Winter_theta_wre": 3.161579087,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 125,
    "G3 Winter_dss_wre": 816,
    "G3 Spring_theta_wre": 3.415954242,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 122,
    "G3 Spring_dss_wre": 842
  },
  {
    "ID": 472,
    "KG Winter_theta_wre": 0.271435593,
    "KG Winter_pr_1_99_wre": 94,
    "KG Winter_ss_wre": 123,
    "KG Winter_dss_wre": 527,
    "KG Spring_theta_wre": 0.670556278,
    "KG Spring_pr_1_99_wre": 95,
    "KG Spring_ss_wre": 123,
    "KG Spring_dss_wre": 567,
    "G1 Fall_theta_wre": 0.968008052,
    "G1 Fall_pr_1_99_wre": 90,
    "G1 Fall_ss_wre": 123,
    "G1 Fall_dss_wre": 597,
    "G1 Winter_theta_wre": 1.116865705,
    "G1 Winter_pr_1_99_wre": 88,
    "G1 Winter_ss_wre": 123,
    "G1 Winter_dss_wre": 612,
    "G1 Spring_theta_wre": 1.494431798,
    "G1 Spring_pr_1_99_wre": 95,
    "G1 Spring_ss_wre": 122,
    "G1 Spring_dss_wre": 649,
    "G2 Fall_theta_wre": 1.985580697,
    "G2 Fall_pr_1_99_wre": 95,
    "G2 Fall_ss_wre": 126,
    "G2 Fall_dss_wre": 699,
    "G2 Winter_theta_wre": 2.228179842,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 124,
    "G2 Winter_dss_wre": 723,
    "G2 Spring_theta_wre": 2.451015933,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 123,
    "G2 Spring_dss_wre": 745,
    "G3 Fall_theta_wre": 2.778782501,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 123,
    "G3 Fall_dss_wre": 778,
    "G3 Winter_theta_wre": 3.172234543,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 125,
    "G3 Winter_dss_wre": 817,
    "G3 Spring_theta_wre": 3.419193206,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 122,
    "G3 Spring_dss_wre": 842
  },
  {
    "ID": 473,
    "KG Winter_theta_wre": 0.313187475,
    "KG Winter_pr_1_99_wre": 94,
    "KG Winter_ss_wre": 123,
    "KG Winter_dss_wre": 531,
    "KG Spring_theta_wre": 0.681688667,
    "KG Spring_pr_1_99_wre": 95,
    "KG Spring_ss_wre": 123,
    "KG Spring_dss_wre": 568,
    "G1 Fall_theta_wre": 0.993078912,
    "G1 Fall_pr_1_99_wre": 90,
    "G1 Fall_ss_wre": 123,
    "G1 Fall_dss_wre": 599,
    "G1 Winter_theta_wre": 1.127123334,
    "G1 Winter_pr_1_99_wre": 88,
    "G1 Winter_ss_wre": 123,
    "G1 Winter_dss_wre": 613,
    "G1 Spring_theta_wre": 1.511862319,
    "G1 Spring_pr_1_99_wre": 95,
    "G1 Spring_ss_wre": 122,
    "G1 Spring_dss_wre": 651,
    "G2 Fall_theta_wre": 1.98571404,
    "G2 Fall_pr_1_99_wre": 95,
    "G2 Fall_ss_wre": 126,
    "G2 Fall_dss_wre": 699,
    "G2 Winter_theta_wre": 2.248793015,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 124,
    "G2 Winter_dss_wre": 725,
    "G2 Spring_theta_wre": 2.475565735,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 123,
    "G2 Spring_dss_wre": 748,
    "G3 Fall_theta_wre": 2.80539981,
    "G3 Fall_pr_1_99_wre": 98,
    "G3 Fall_ss_wre": 124,
    "G3 Fall_dss_wre": 781,
    "G3 Winter_theta_wre": 3.186148087,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 125,
    "G3 Winter_dss_wre": 819,
    "G3 Spring_theta_wre": 3.433233319,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 122,
    "G3 Spring_dss_wre": 843
  },
  {
    "ID": 474,
    "KG Winter_theta_wre": 0.32064273,
    "KG Winter_pr_1_99_wre": 94,
    "KG Winter_ss_wre": 124,
    "KG Winter_dss_wre": 532,
    "KG Spring_theta_wre": 0.686031984,
    "KG Spring_pr_1_99_wre": 95,
    "KG Spring_ss_wre": 124,
    "KG Spring_dss_wre": 569,
    "G1 Fall_theta_wre": 0.996820198,
    "G1 Fall_pr_1_99_wre": 90,
    "G1 Fall_ss_wre": 123,
    "G1 Fall_dss_wre": 600,
    "G1 Winter_theta_wre": 1.135109761,
    "G1 Winter_pr_1_99_wre": 88,
    "G1 Winter_ss_wre": 123,
    "G1 Winter_dss_wre": 614,
    "G1 Spring_theta_wre": 1.513772521,
    "G1 Spring_pr_1_99_wre": 95,
    "G1 Spring_ss_wre": 122,
    "G1 Spring_dss_wre": 651,
    "G2 Fall_theta_wre": 1.998069474,
    "G2 Fall_pr_1_99_wre": 95,
    "G2 Fall_ss_wre": 126,
    "G2 Fall_dss_wre": 700,
    "G2 Winter_theta_wre": 2.252874084,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 124,
    "G2 Winter_dss_wre": 725,
    "G2 Spring_theta_wre": 2.479417385,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 123,
    "G2 Spring_dss_wre": 748,
    "G3 Fall_theta_wre": 2.819210332,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 124,
    "G3 Fall_dss_wre": 782,
    "G3 Winter_theta_wre": 3.196939153,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 125,
    "G3 Winter_dss_wre": 820,
    "G3 Spring_theta_wre": 3.441316177,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 122,
    "G3 Spring_dss_wre": 844
  },
  {
    "ID": 475,
    "KG Winter_theta_wre": 0.326346305,
    "KG Winter_pr_1_99_wre": 94,
    "KG Winter_ss_wre": 124,
    "KG Winter_dss_wre": 533,
    "KG Spring_theta_wre": 0.72509524,
    "KG Spring_pr_1_99_wre": 96,
    "KG Spring_ss_wre": 124,
    "KG Spring_dss_wre": 573,
    "G1 Fall_theta_wre": 0.997971532,
    "G1 Fall_pr_1_99_wre": 90,
    "G1 Fall_ss_wre": 123,
    "G1 Fall_dss_wre": 600,
    "G1 Winter_theta_wre": 1.154478626,
    "G1 Winter_pr_1_99_wre": 89,
    "G1 Winter_ss_wre": 124,
    "G1 Winter_dss_wre": 615,
    "G1 Spring_theta_wre": 1.521352059,
    "G1 Spring_pr_1_99_wre": 95,
    "G1 Spring_ss_wre": 122,
    "G1 Spring_dss_wre": 652,
    "G2 Fall_theta_wre": 2.010369413,
    "G2 Fall_pr_1_99_wre": 95,
    "G2 Fall_ss_wre": 126,
    "G2 Fall_dss_wre": 701,
    "G2 Winter_theta_wre": 2.265569232,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 125,
    "G2 Winter_dss_wre": 727,
    "G2 Spring_theta_wre": 2.480849887,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 123,
    "G2 Spring_dss_wre": 748,
    "G3 Fall_theta_wre": 2.854602155,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 125,
    "G3 Fall_dss_wre": 785,
    "G3 Winter_theta_wre": 3.202006729,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 125,
    "G3 Winter_dss_wre": 820,
    "G3 Spring_theta_wre": 3.44947583,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 122,
    "G3 Spring_dss_wre": 845
  },
  {
    "ID": 476,
    "KG Winter_theta_wre": 0.330621341,
    "KG Winter_pr_1_99_wre": 94,
    "KG Winter_ss_wre": 124,
    "KG Winter_dss_wre": 533,
    "KG Spring_theta_wre": 0.73296659,
    "KG Spring_pr_1_99_wre": 96,
    "KG Spring_ss_wre": 124,
    "KG Spring_dss_wre": 573,
    "G1 Fall_theta_wre": 1.049965487,
    "G1 Fall_pr_1_99_wre": 95,
    "G1 Fall_ss_wre": 124,
    "G1 Fall_dss_wre": 605,
    "G1 Winter_theta_wre": 1.16114311,
    "G1 Winter_pr_1_99_wre": 89,
    "G1 Winter_ss_wre": 124,
    "G1 Winter_dss_wre": 616,
    "G1 Spring_theta_wre": 1.562173837,
    "G1 Spring_pr_1_99_wre": 95,
    "G1 Spring_ss_wre": 123,
    "G1 Spring_dss_wre": 656,
    "G2 Fall_theta_wre": 2.017450072,
    "G2 Fall_pr_1_99_wre": 95,
    "G2 Fall_ss_wre": 126,
    "G2 Fall_dss_wre": 702,
    "G2 Winter_theta_wre": 2.275559269,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 125,
    "G2 Winter_dss_wre": 728,
    "G2 Spring_theta_wre": 2.48810068,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 123,
    "G2 Spring_dss_wre": 749,
    "G3 Fall_theta_wre": 2.896579731,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 125,
    "G3 Fall_dss_wre": 790,
    "G3 Winter_theta_wre": 3.204960481,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 125,
    "G3 Winter_dss_wre": 820,
    "G3 Spring_theta_wre": 3.458246674,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 122,
    "G3 Spring_dss_wre": 846
  },
  {
    "ID": 477,
    "KG Winter_theta_wre": 0.405749292,
    "KG Winter_pr_1_99_wre": 94,
    "KG Winter_ss_wre": 125,
    "KG Winter_dss_wre": 541,
    "KG Spring_theta_wre": 0.733823148,
    "KG Spring_pr_1_99_wre": 96,
    "KG Spring_ss_wre": 124,
    "KG Spring_dss_wre": 573,
    "G1 Fall_theta_wre": 1.054872905,
    "G1 Fall_pr_1_99_wre": 95,
    "G1 Fall_ss_wre": 125,
    "G1 Fall_dss_wre": 605,
    "G1 Winter_theta_wre": 1.162241871,
    "G1 Winter_pr_1_99_wre": 89,
    "G1 Winter_ss_wre": 124,
    "G1 Winter_dss_wre": 616,
    "G1 Spring_theta_wre": 1.62047657,
    "G1 Spring_pr_1_99_wre": 98,
    "G1 Spring_ss_wre": 124,
    "G1 Spring_dss_wre": 662,
    "G2 Fall_theta_wre": 2.032489628,
    "G2 Fall_pr_1_99_wre": 95,
    "G2 Fall_ss_wre": 126,
    "G2 Fall_dss_wre": 703,
    "G2 Winter_theta_wre": 2.313715537,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 125,
    "G2 Winter_dss_wre": 731,
    "G2 Spring_theta_wre": 2.498681964,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 124,
    "G2 Spring_dss_wre": 750,
    "G3 Fall_theta_wre": 2.901453666,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 125,
    "G3 Fall_dss_wre": 790,
    "G3 Winter_theta_wre": 3.228952463,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 126,
    "G3 Winter_dss_wre": 823,
    "G3 Spring_theta_wre": 3.47552954,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 123,
    "G3 Spring_dss_wre": 848
  },
  {
    "ID": 478,
    "KG Winter_theta_wre": 0.414395987,
    "KG Winter_pr_1_99_wre": 95,
    "KG Winter_ss_wre": 125,
    "KG Winter_dss_wre": 541,
    "KG Spring_theta_wre": 0.763891865,
    "KG Spring_pr_1_99_wre": 98,
    "KG Spring_ss_wre": 125,
    "KG Spring_dss_wre": 576,
    "G1 Fall_theta_wre": 1.062446908,
    "G1 Fall_pr_1_99_wre": 95,
    "G1 Fall_ss_wre": 125,
    "G1 Fall_dss_wre": 606,
    "G1 Winter_theta_wre": 1.184077169,
    "G1 Winter_pr_1_99_wre": 89,
    "G1 Winter_ss_wre": 124,
    "G1 Winter_dss_wre": 618,
    "G1 Spring_theta_wre": 1.630898518,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 124,
    "G1 Spring_dss_wre": 663,
    "G2 Fall_theta_wre": 2.035539201,
    "G2 Fall_pr_1_99_wre": 95,
    "G2 Fall_ss_wre": 126,
    "G2 Fall_dss_wre": 704,
    "G2 Winter_theta_wre": 2.320301568,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 126,
    "G2 Winter_dss_wre": 732,
    "G2 Spring_theta_wre": 2.5086205,
    "G2 Spring_pr_1_99_wre": 97,
    "G2 Spring_ss_wre": 124,
    "G2 Spring_dss_wre": 751,
    "G3 Fall_theta_wre": 2.93583694,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 126,
    "G3 Fall_dss_wre": 794,
    "G3 Winter_theta_wre": 3.260282923,
    "G3 Winter_pr_1_99_wre": 93,
    "G3 Winter_ss_wre": 126,
    "G3 Winter_dss_wre": 826,
    "G3 Spring_theta_wre": 3.477626147,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 123,
    "G3 Spring_dss_wre": 848
  },
  {
    "ID": 479,
    "KG Winter_theta_wre": 0.427340049,
    "KG Winter_pr_1_99_wre": 95,
    "KG Winter_ss_wre": 125,
    "KG Winter_dss_wre": 543,
    "KG Spring_theta_wre": 0.778840728,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 125,
    "KG Spring_dss_wre": 578,
    "G1 Fall_theta_wre": 1.07278035,
    "G1 Fall_pr_1_99_wre": 95,
    "G1 Fall_ss_wre": 125,
    "G1 Fall_dss_wre": 607,
    "G1 Winter_theta_wre": 1.196655226,
    "G1 Winter_pr_1_99_wre": 89,
    "G1 Winter_ss_wre": 125,
    "G1 Winter_dss_wre": 620,
    "G1 Spring_theta_wre": 1.677536747,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 125,
    "G1 Spring_dss_wre": 668,
    "G2 Fall_theta_wre": 2.039651283,
    "G2 Fall_pr_1_99_wre": 95,
    "G2 Fall_ss_wre": 127,
    "G2 Fall_dss_wre": 704,
    "G2 Winter_theta_wre": 2.351304546,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 126,
    "G2 Winter_dss_wre": 735,
    "G2 Spring_theta_wre": 2.533512141,
    "G2 Spring_pr_1_99_wre": 98,
    "G2 Spring_ss_wre": 124,
    "G2 Spring_dss_wre": 753,
    "G3 Fall_theta_wre": 2.942248982,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 126,
    "G3 Fall_dss_wre": 794,
    "G3 Winter_theta_wre": 3.281267983,
    "G3 Winter_pr_1_99_wre": 96,
    "G3 Winter_ss_wre": 127,
    "G3 Winter_dss_wre": 828,
    "G3 Spring_theta_wre": 3.495596374,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 123,
    "G3 Spring_dss_wre": 850
  },
  {
    "ID": 480,
    "KG Winter_theta_wre": 0.472593991,
    "KG Winter_pr_1_99_wre": 95,
    "KG Winter_ss_wre": 126,
    "KG Winter_dss_wre": 547,
    "KG Spring_theta_wre": 0.784565708,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 125,
    "KG Spring_dss_wre": 578,
    "G1 Fall_theta_wre": 1.090279288,
    "G1 Fall_pr_1_99_wre": 95,
    "G1 Fall_ss_wre": 125,
    "G1 Fall_dss_wre": 609,
    "G1 Winter_theta_wre": 1.197161041,
    "G1 Winter_pr_1_99_wre": 89,
    "G1 Winter_ss_wre": 125,
    "G1 Winter_dss_wre": 620,
    "G1 Spring_theta_wre": 1.709106263,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 126,
    "G1 Spring_dss_wre": 671,
    "G2 Fall_theta_wre": 2.044980595,
    "G2 Fall_pr_1_99_wre": 95,
    "G2 Fall_ss_wre": 127,
    "G2 Fall_dss_wre": 704,
    "G2 Winter_theta_wre": 2.358736899,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 126,
    "G2 Winter_dss_wre": 736,
    "G2 Spring_theta_wre": 2.607600517,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 126,
    "G2 Spring_dss_wre": 761,
    "G3 Fall_theta_wre": 2.946962482,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 126,
    "G3 Fall_dss_wre": 795,
    "G3 Winter_theta_wre": 3.282586406,
    "G3 Winter_pr_1_99_wre": 96,
    "G3 Winter_ss_wre": 127,
    "G3 Winter_dss_wre": 828,
    "G3 Spring_theta_wre": 3.500808737,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 123,
    "G3 Spring_dss_wre": 850
  },
  {
    "ID": 481,
    "KG Winter_theta_wre": 0.485943091,
    "KG Winter_pr_1_99_wre": 95,
    "KG Winter_ss_wre": 127,
    "KG Winter_dss_wre": 549,
    "KG Spring_theta_wre": 0.797481705,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 126,
    "KG Spring_dss_wre": 580,
    "G1 Fall_theta_wre": 1.13305989,
    "G1 Fall_pr_1_99_wre": 96,
    "G1 Fall_ss_wre": 126,
    "G1 Fall_dss_wre": 613,
    "G1 Winter_theta_wre": 1.235393577,
    "G1 Winter_pr_1_99_wre": 93,
    "G1 Winter_ss_wre": 125,
    "G1 Winter_dss_wre": 624,
    "G1 Spring_theta_wre": 1.709910213,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 126,
    "G1 Spring_dss_wre": 671,
    "G2 Fall_theta_wre": 2.048213527,
    "G2 Fall_pr_1_99_wre": 95,
    "G2 Fall_ss_wre": 127,
    "G2 Fall_dss_wre": 705,
    "G2 Winter_theta_wre": 2.361016958,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 126,
    "G2 Winter_dss_wre": 736,
    "G2 Spring_theta_wre": 2.611595008,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 126,
    "G2 Spring_dss_wre": 761,
    "G3 Fall_theta_wre": 2.955574048,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 126,
    "G3 Fall_dss_wre": 796,
    "G3 Winter_theta_wre": 3.294535623,
    "G3 Winter_pr_1_99_wre": 96,
    "G3 Winter_ss_wre": 127,
    "G3 Winter_dss_wre": 829,
    "G3 Spring_theta_wre": 3.502170582,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 123,
    "G3 Spring_dss_wre": 850
  },
  {
    "ID": 482,
    "KG Winter_theta_wre": 0.518597733,
    "KG Winter_pr_1_99_wre": 96,
    "KG Winter_ss_wre": 127,
    "KG Winter_dss_wre": 552,
    "KG Spring_theta_wre": 0.803770021,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 126,
    "KG Spring_dss_wre": 580,
    "G1 Fall_theta_wre": 1.144902348,
    "G1 Fall_pr_1_99_wre": 96,
    "G1 Fall_ss_wre": 126,
    "G1 Fall_dss_wre": 614,
    "G1 Winter_theta_wre": 1.260512308,
    "G1 Winter_pr_1_99_wre": 93,
    "G1 Winter_ss_wre": 126,
    "G1 Winter_dss_wre": 626,
    "G1 Spring_theta_wre": 1.719906983,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 126,
    "G1 Spring_dss_wre": 672,
    "G2 Fall_theta_wre": 2.054536167,
    "G2 Fall_pr_1_99_wre": 96,
    "G2 Fall_ss_wre": 127,
    "G2 Fall_dss_wre": 705,
    "G2 Winter_theta_wre": 2.397897219,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 127,
    "G2 Winter_dss_wre": 740,
    "G2 Spring_theta_wre": 2.668686246,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 127,
    "G2 Spring_dss_wre": 767,
    "G3 Fall_theta_wre": 2.956676032,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 127,
    "G3 Fall_dss_wre": 796,
    "G3 Winter_theta_wre": 3.311972016,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 127,
    "G3 Winter_dss_wre": 831,
    "G3 Spring_theta_wre": 3.50387722,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 123,
    "G3 Spring_dss_wre": 850
  },
  {
    "ID": 483,
    "KG Winter_theta_wre": 0.604375619,
    "KG Winter_pr_1_99_wre": 96,
    "KG Winter_ss_wre": 129,
    "KG Winter_dss_wre": 560,
    "KG Spring_theta_wre": 0.813269576,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 126,
    "KG Spring_dss_wre": 581,
    "G1 Fall_theta_wre": 1.161363684,
    "G1 Fall_pr_1_99_wre": 97,
    "G1 Fall_ss_wre": 126,
    "G1 Fall_dss_wre": 616,
    "G1 Winter_theta_wre": 1.297836013,
    "G1 Winter_pr_1_99_wre": 94,
    "G1 Winter_ss_wre": 127,
    "G1 Winter_dss_wre": 630,
    "G1 Spring_theta_wre": 1.723838584,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 126,
    "G1 Spring_dss_wre": 672,
    "G2 Fall_theta_wre": 2.068585675,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 127,
    "G2 Fall_dss_wre": 707,
    "G2 Winter_theta_wre": 2.405883569,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 127,
    "G2 Winter_dss_wre": 741,
    "G2 Spring_theta_wre": 2.728417813,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 128,
    "G2 Spring_dss_wre": 773,
    "G3 Fall_theta_wre": 2.978266497,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 127,
    "G3 Fall_dss_wre": 798,
    "G3 Winter_theta_wre": 3.329203193,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 128,
    "G3 Winter_dss_wre": 833,
    "G3 Spring_theta_wre": 3.520739952,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 123,
    "G3 Spring_dss_wre": 852
  },
  {
    "ID": 484,
    "KG Winter_theta_wre": 0.634573876,
    "KG Winter_pr_1_99_wre": 96,
    "KG Winter_ss_wre": 129,
    "KG Winter_dss_wre": 563,
    "KG Spring_theta_wre": 0.841622612,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 126,
    "KG Spring_dss_wre": 584,
    "G1 Fall_theta_wre": 1.171306515,
    "G1 Fall_pr_1_99_wre": 97,
    "G1 Fall_ss_wre": 127,
    "G1 Fall_dss_wre": 617,
    "G1 Winter_theta_wre": 1.322420706,
    "G1 Winter_pr_1_99_wre": 95,
    "G1 Winter_ss_wre": 127,
    "G1 Winter_dss_wre": 632,
    "G1 Spring_theta_wre": 1.734413844,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 126,
    "G1 Spring_dss_wre": 673,
    "G2 Fall_theta_wre": 2.071029687,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 127,
    "G2 Fall_dss_wre": 707,
    "G2 Winter_theta_wre": 2.421664517,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 127,
    "G2 Winter_dss_wre": 742,
    "G2 Spring_theta_wre": 2.814914886,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 129,
    "G2 Spring_dss_wre": 781,
    "G3 Fall_theta_wre": 2.982462481,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 127,
    "G3 Fall_dss_wre": 798,
    "G3 Winter_theta_wre": 3.354415812,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 128,
    "G3 Winter_dss_wre": 835,
    "G3 Spring_theta_wre": 3.526585861,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 124,
    "G3 Spring_dss_wre": 853
  },
  {
    "ID": 485,
    "KG Winter_theta_wre": 0.679196007,
    "KG Winter_pr_1_99_wre": 97,
    "KG Winter_ss_wre": 130,
    "KG Winter_dss_wre": 568,
    "KG Spring_theta_wre": 0.849370093,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 126,
    "KG Spring_dss_wre": 585,
    "G1 Fall_theta_wre": 1.186528155,
    "G1 Fall_pr_1_99_wre": 97,
    "G1 Fall_ss_wre": 127,
    "G1 Fall_dss_wre": 619,
    "G1 Winter_theta_wre": 1.337996811,
    "G1 Winter_pr_1_99_wre": 95,
    "G1 Winter_ss_wre": 127,
    "G1 Winter_dss_wre": 634,
    "G1 Spring_theta_wre": 1.7958995,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 127,
    "G1 Spring_dss_wre": 680,
    "G2 Fall_theta_wre": 2.093152921,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 128,
    "G2 Fall_dss_wre": 709,
    "G2 Winter_theta_wre": 2.453279425,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 128,
    "G2 Winter_dss_wre": 745,
    "G2 Spring_theta_wre": 2.860043765,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 130,
    "G2 Spring_dss_wre": 786,
    "G3 Fall_theta_wre": 3.01626691,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 128,
    "G3 Fall_dss_wre": 802,
    "G3 Winter_theta_wre": 3.362014772,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 128,
    "G3 Winter_dss_wre": 836,
    "G3 Spring_theta_wre": 3.557085739,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 124,
    "G3 Spring_dss_wre": 856
  },
  {
    "ID": 486,
    "KG Winter_theta_wre": 0.679657142,
    "KG Winter_pr_1_99_wre": 97,
    "KG Winter_ss_wre": 130,
    "KG Winter_dss_wre": 568,
    "KG Spring_theta_wre": 0.873857016,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 127,
    "KG Spring_dss_wre": 587,
    "G1 Fall_theta_wre": 1.215879794,
    "G1 Fall_pr_1_99_wre": 97,
    "G1 Fall_ss_wre": 127,
    "G1 Fall_dss_wre": 622,
    "G1 Winter_theta_wre": 1.394912457,
    "G1 Winter_pr_1_99_wre": 95,
    "G1 Winter_ss_wre": 128,
    "G1 Winter_dss_wre": 639,
    "G1 Spring_theta_wre": 1.852379196,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 128,
    "G1 Spring_dss_wre": 685,
    "G2 Fall_theta_wre": 2.098928951,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 128,
    "G2 Fall_dss_wre": 710,
    "G2 Winter_theta_wre": 2.460252614,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 128,
    "G2 Winter_dss_wre": 746,
    "G2 Spring_theta_wre": 2.894802506,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 131,
    "G2 Spring_dss_wre": 789,
    "G3 Fall_theta_wre": 3.019697368,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 128,
    "G3 Fall_dss_wre": 802,
    "G3 Winter_theta_wre": 3.38227453,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 129,
    "G3 Winter_dss_wre": 838,
    "G3 Spring_theta_wre": 3.594389048,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 125,
    "G3 Spring_dss_wre": 859
  },
  {
    "ID": 487,
    "KG Winter_theta_wre": 0.754308625,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 131,
    "KG Winter_dss_wre": 575,
    "KG Spring_theta_wre": 0.91094495,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 128,
    "KG Spring_dss_wre": 591,
    "G1 Fall_theta_wre": 1.248678885,
    "G1 Fall_pr_1_99_wre": 97,
    "G1 Fall_ss_wre": 128,
    "G1 Fall_dss_wre": 625,
    "G1 Winter_theta_wre": 1.398829612,
    "G1 Winter_pr_1_99_wre": 95,
    "G1 Winter_ss_wre": 129,
    "G1 Winter_dss_wre": 640,
    "G1 Spring_theta_wre": 1.896502689,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 129,
    "G1 Spring_dss_wre": 690,
    "G2 Fall_theta_wre": 2.106483391,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 128,
    "G2 Fall_dss_wre": 711,
    "G2 Winter_theta_wre": 2.474381262,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 128,
    "G2 Winter_dss_wre": 747,
    "G2 Spring_theta_wre": 2.90397341,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 131,
    "G2 Spring_dss_wre": 790,
    "G3 Fall_theta_wre": 3.028467254,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 128,
    "G3 Fall_dss_wre": 803,
    "G3 Winter_theta_wre": 3.407395904,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 129,
    "G3 Winter_dss_wre": 841,
    "G3 Spring_theta_wre": 3.775247907,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 128,
    "G3 Spring_dss_wre": 878
  },
  {
    "ID": 488,
    "KG Winter_theta_wre": 0.785474708,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 132,
    "KG Winter_dss_wre": 579,
    "KG Spring_theta_wre": 0.930840586,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 128,
    "KG Spring_dss_wre": 593,
    "G1 Fall_theta_wre": 1.252959312,
    "G1 Fall_pr_1_99_wre": 97,
    "G1 Fall_ss_wre": 128,
    "G1 Fall_dss_wre": 625,
    "G1 Winter_theta_wre": 1.455573471,
    "G1 Winter_pr_1_99_wre": 95,
    "G1 Winter_ss_wre": 130,
    "G1 Winter_dss_wre": 646,
    "G1 Spring_theta_wre": 1.991253802,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 131,
    "G1 Spring_dss_wre": 699,
    "G2 Fall_theta_wre": 2.138194669,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 128,
    "G2 Fall_dss_wre": 714,
    "G2 Winter_theta_wre": 2.497512407,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 129,
    "G2 Winter_dss_wre": 750,
    "G2 Spring_theta_wre": 2.936631968,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 131,
    "G2 Spring_dss_wre": 794,
    "G3 Fall_theta_wre": 3.049468306,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 128,
    "G3 Fall_dss_wre": 805,
    "G3 Winter_theta_wre": 3.438322128,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 130,
    "G3 Winter_dss_wre": 844,
    "G3 Spring_theta_wre": 3.793498215,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 128,
    "G3 Spring_dss_wre": 879
  },
  {
    "ID": 489,
    "KG Winter_theta_wre": 0.803319527,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 132,
    "KG Winter_dss_wre": 580,
    "KG Spring_theta_wre": 0.981700627,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 129,
    "KG Spring_dss_wre": 598,
    "G1 Fall_theta_wre": 1.272748635,
    "G1 Fall_pr_1_99_wre": 97,
    "G1 Fall_ss_wre": 128,
    "G1 Fall_dss_wre": 627,
    "G1 Winter_theta_wre": 1.479852298,
    "G1 Winter_pr_1_99_wre": 98,
    "G1 Winter_ss_wre": 130,
    "G1 Winter_dss_wre": 648,
    "G1 Spring_theta_wre": 2.111342742,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 133,
    "G1 Spring_dss_wre": 711,
    "G2 Fall_theta_wre": 2.159803679,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 129,
    "G2 Fall_dss_wre": 716,
    "G2 Winter_theta_wre": 2.513901359,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 129,
    "G2 Winter_dss_wre": 751,
    "G2 Spring_theta_wre": 2.986235572,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 132,
    "G2 Spring_dss_wre": 799,
    "G3 Fall_theta_wre": 3.100092912,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 129,
    "G3 Fall_dss_wre": 810,
    "G3 Winter_theta_wre": 3.457942459,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 130,
    "G3 Winter_dss_wre": 846,
    "G3 Spring_theta_wre": 3.824065337,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 129,
    "G3 Spring_dss_wre": 882
  },
  {
    "ID": 490,
    "KG Winter_theta_wre": 0.852651497,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 133,
    "KG Winter_dss_wre": 585,
    "KG Spring_theta_wre": 1.014247087,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 129,
    "KG Spring_dss_wre": 601,
    "G1 Fall_theta_wre": 1.290216549,
    "G1 Fall_pr_1_99_wre": 97,
    "G1 Fall_ss_wre": 129,
    "G1 Fall_dss_wre": 629,
    "G1 Winter_theta_wre": 1.547970934,
    "G1 Winter_pr_1_99_wre": 98,
    "G1 Winter_ss_wre": 131,
    "G1 Winter_dss_wre": 655,
    "G1 Spring_theta_wre": 2.195854097,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 135,
    "G1 Spring_dss_wre": 720,
    "G2 Fall_theta_wre": 2.16130658,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 129,
    "G2 Fall_dss_wre": 716,
    "G2 Winter_theta_wre": 2.518025944,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 129,
    "G2 Winter_dss_wre": 752,
    "G2 Spring_theta_wre": 2.991789084,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 132,
    "G2 Spring_dss_wre": 799,
    "G3 Fall_theta_wre": 3.103254749,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 129,
    "G3 Fall_dss_wre": 810,
    "G3 Winter_theta_wre": 3.506316218,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 131,
    "G3 Winter_dss_wre": 851,
    "G3 Spring_theta_wre": 3.859397407,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 129,
    "G3 Spring_dss_wre": 886
  },
  {
    "ID": 491,
    "KG Winter_theta_wre": 0.920547756,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 134,
    "KG Winter_dss_wre": 592,
    "KG Spring_theta_wre": 1.036541316,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 130,
    "KG Spring_dss_wre": 604,
    "G1 Fall_theta_wre": 1.319708106,
    "G1 Fall_pr_1_99_wre": 97,
    "G1 Fall_ss_wre": 129,
    "G1 Fall_dss_wre": 632,
    "G1 Winter_theta_wre": 1.549026624,
    "G1 Winter_pr_1_99_wre": 98,
    "G1 Winter_ss_wre": 131,
    "G1 Winter_dss_wre": 655,
    "G1 Spring_theta_wre": 2.200607242,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 135,
    "G1 Spring_dss_wre": 720,
    "G2 Fall_theta_wre": 2.164581801,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 129,
    "G2 Fall_dss_wre": 716,
    "G2 Winter_theta_wre": 2.527464986,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 129,
    "G2 Winter_dss_wre": 753,
    "G2 Spring_theta_wre": 2.996609819,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 133,
    "G2 Spring_dss_wre": 800,
    "G3 Fall_theta_wre": 3.193897414,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 131,
    "G3 Fall_dss_wre": 819,
    "G3 Winter_theta_wre": 3.507178318,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 131,
    "G3 Winter_dss_wre": 851,
    "G3 Spring_theta_wre": 3.86639091,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 130,
    "G3 Spring_dss_wre": 887
  },
  {
    "ID": 492,
    "KG Winter_theta_wre": 0.993453818,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 136,
    "KG Winter_dss_wre": 599,
    "KG Spring_theta_wre": 1.048986946,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 130,
    "KG Spring_dss_wre": 605,
    "G1 Fall_theta_wre": 1.353472912,
    "G1 Fall_pr_1_99_wre": 98,
    "G1 Fall_ss_wre": 130,
    "G1 Fall_dss_wre": 635,
    "G1 Winter_theta_wre": 1.570736102,
    "G1 Winter_pr_1_99_wre": 98,
    "G1 Winter_ss_wre": 132,
    "G1 Winter_dss_wre": 657,
    "G1 Spring_theta_wre": 2.202675549,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 135,
    "G1 Spring_dss_wre": 720,
    "G2 Fall_theta_wre": 2.201109601,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 129,
    "G2 Fall_dss_wre": 720,
    "G2 Winter_theta_wre": 2.532352684,
    "G2 Winter_pr_1_99_wre": 98,
    "G2 Winter_ss_wre": 129,
    "G2 Winter_dss_wre": 753,
    "G2 Spring_theta_wre": 3.017902809,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 133,
    "G2 Spring_dss_wre": 802,
    "G3 Fall_theta_wre": 3.211083817,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 131,
    "G3 Fall_dss_wre": 821,
    "G3 Winter_theta_wre": 3.512887771,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 131,
    "G3 Winter_dss_wre": 851,
    "G3 Spring_theta_wre": 3.901584421,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 130,
    "G3 Spring_dss_wre": 890
  },
  {
    "ID": 493,
    "KG Winter_theta_wre": 1.063337724,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 137,
    "KG Winter_dss_wre": 606,
    "KG Spring_theta_wre": 1.164650809,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 132,
    "KG Spring_dss_wre": 616,
    "G1 Fall_theta_wre": 1.417054384,
    "G1 Fall_pr_1_99_wre": 99,
    "G1 Fall_ss_wre": 131,
    "G1 Fall_dss_wre": 642,
    "G1 Winter_theta_wre": 1.574850256,
    "G1 Winter_pr_1_99_wre": 98,
    "G1 Winter_ss_wre": 132,
    "G1 Winter_dss_wre": 657,
    "G1 Spring_theta_wre": 2.224174089,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 135,
    "G1 Spring_dss_wre": 722,
    "G2 Fall_theta_wre": 2.204772047,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 130,
    "G2 Fall_dss_wre": 720,
    "G2 Winter_theta_wre": 2.596538909,
    "G2 Winter_pr_1_99_wre": 99,
    "G2 Winter_ss_wre": 131,
    "G2 Winter_dss_wre": 760,
    "G2 Spring_theta_wre": 3.070474485,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 134,
    "G2 Spring_dss_wre": 807,
    "G3 Fall_theta_wre": 3.212190358,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 131,
    "G3 Fall_dss_wre": 821,
    "G3 Winter_theta_wre": 3.543391757,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 132,
    "G3 Winter_dss_wre": 854,
    "G3 Spring_theta_wre": 3.924477304,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 131,
    "G3 Spring_dss_wre": 892
  },
  {
    "ID": 494,
    "KG Winter_theta_wre": 1.104636518,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 138,
    "KG Winter_dss_wre": 610,
    "KG Spring_theta_wre": 1.21849002,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 133,
    "KG Spring_dss_wre": 622,
    "G1 Fall_theta_wre": 1.504269853,
    "G1 Fall_pr_1_99_wre": 99,
    "G1 Fall_ss_wre": 133,
    "G1 Fall_dss_wre": 650,
    "G1 Winter_theta_wre": 1.587729423,
    "G1 Winter_pr_1_99_wre": 98,
    "G1 Winter_ss_wre": 132,
    "G1 Winter_dss_wre": 659,
    "G1 Spring_theta_wre": 2.271118158,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 136,
    "G1 Spring_dss_wre": 727,
    "G2 Fall_theta_wre": 2.275957516,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 131,
    "G2 Fall_dss_wre": 728,
    "G2 Winter_theta_wre": 2.603617687,
    "G2 Winter_pr_1_99_wre": 99,
    "G2 Winter_ss_wre": 131,
    "G2 Winter_dss_wre": 760,
    "G2 Spring_theta_wre": 3.074739588,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 134,
    "G2 Spring_dss_wre": 807,
    "G3 Fall_theta_wre": 3.224333501,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 131,
    "G3 Fall_dss_wre": 822,
    "G3 Winter_theta_wre": 3.593624305,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 133,
    "G3 Winter_dss_wre": 859,
    "G3 Spring_theta_wre": 4.050922975,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 133,
    "G3 Spring_dss_wre": 905
  },
  {
    "ID": 495,
    "KG Winter_theta_wre": 1.147125112,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 138,
    "KG Winter_dss_wre": 615,
    "KG Spring_theta_wre": 1.364755835,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 136,
    "KG Spring_dss_wre": 636,
    "G1 Fall_theta_wre": 1.654512526,
    "G1 Fall_pr_1_99_wre": 99,
    "G1 Fall_ss_wre": 135,
    "G1 Fall_dss_wre": 665,
    "G1 Winter_theta_wre": 1.60720489,
    "G1 Winter_pr_1_99_wre": 99,
    "G1 Winter_ss_wre": 133,
    "G1 Winter_dss_wre": 661,
    "G1 Spring_theta_wre": 2.33737671,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 137,
    "G1 Spring_dss_wre": 734,
    "G2 Fall_theta_wre": 2.373760665,
    "G2 Fall_pr_1_99_wre": 97,
    "G2 Fall_ss_wre": 133,
    "G2 Fall_dss_wre": 737,
    "G2 Winter_theta_wre": 2.6903393,
    "G2 Winter_pr_1_99_wre": 99,
    "G2 Winter_ss_wre": 132,
    "G2 Winter_dss_wre": 769,
    "G2 Spring_theta_wre": 3.289292611,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 138,
    "G2 Spring_dss_wre": 829,
    "G3 Fall_theta_wre": 3.238059045,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 132,
    "G3 Fall_dss_wre": 824,
    "G3 Winter_theta_wre": 3.679921789,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 134,
    "G3 Winter_dss_wre": 868,
    "G3 Spring_theta_wre": 4.05214352,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 133,
    "G3 Spring_dss_wre": 905
  },
  {
    "ID": 496,
    "KG Winter_theta_wre": 1.166088201,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 139,
    "KG Winter_dss_wre": 617,
    "KG Spring_theta_wre": 1.563123342,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 139,
    "KG Spring_dss_wre": 656,
    "G1 Fall_theta_wre": 1.681679173,
    "G1 Fall_pr_1_99_wre": 99,
    "G1 Fall_ss_wre": 136,
    "G1 Fall_dss_wre": 668,
    "G1 Winter_theta_wre": 1.745299876,
    "G1 Winter_pr_1_99_wre": 99,
    "G1 Winter_ss_wre": 135,
    "G1 Winter_dss_wre": 675,
    "G1 Spring_theta_wre": 2.375886253,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 138,
    "G1 Spring_dss_wre": 738,
    "G2 Fall_theta_wre": 2.450442582,
    "G2 Fall_pr_1_99_wre": 99,
    "G2 Fall_ss_wre": 134,
    "G2 Fall_dss_wre": 745,
    "G2 Winter_theta_wre": 2.816807352,
    "G2 Winter_pr_1_99_wre": 99,
    "G2 Winter_ss_wre": 135,
    "G2 Winter_dss_wre": 782,
    "G2 Spring_theta_wre": 3.431926244,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 140,
    "G2 Spring_dss_wre": 843,
    "G3 Fall_theta_wre": 3.327184801,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 133,
    "G3 Fall_dss_wre": 833,
    "G3 Winter_theta_wre": 3.685481659,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 135,
    "G3 Winter_dss_wre": 869,
    "G3 Spring_theta_wre": 4.155861104,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 135,
    "G3 Spring_dss_wre": 916
  },
  {
    "ID": 497,
    "KG Winter_theta_wre": 1.343234924,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 142,
    "KG Winter_dss_wre": 634,
    "KG Spring_theta_wre": 1.570563712,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 139,
    "KG Spring_dss_wre": 657,
    "G1 Fall_theta_wre": 1.700255454,
    "G1 Fall_pr_1_99_wre": 99,
    "G1 Fall_ss_wre": 136,
    "G1 Fall_dss_wre": 670,
    "G1 Winter_theta_wre": 1.750742849,
    "G1 Winter_pr_1_99_wre": 99,
    "G1 Winter_ss_wre": 135,
    "G1 Winter_dss_wre": 675,
    "G1 Spring_theta_wre": 2.430598507,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 139,
    "G1 Spring_dss_wre": 743,
    "G2 Fall_theta_wre": 2.554500196,
    "G2 Fall_pr_1_99_wre": 99,
    "G2 Fall_ss_wre": 136,
    "G2 Fall_dss_wre": 755,
    "G2 Winter_theta_wre": 2.820033144,
    "G2 Winter_pr_1_99_wre": 99,
    "G2 Winter_ss_wre": 135,
    "G2 Winter_dss_wre": 782,
    "G2 Spring_theta_wre": 3.462974858,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 141,
    "G2 Spring_dss_wre": 846,
    "G3 Fall_theta_wre": 3.445709887,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 136,
    "G3 Fall_dss_wre": 845,
    "G3 Winter_theta_wre": 3.710062525,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 135,
    "G3 Winter_dss_wre": 871,
    "G3 Spring_theta_wre": 4.182957259,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 135,
    "G3 Spring_dss_wre": 918
  },
  {
    "ID": 498,
    "KG Winter_theta_wre": 1.402544334,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 143,
    "KG Winter_dss_wre": 640,
    "KG Spring_theta_wre": 1.627951989,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 140,
    "KG Spring_dss_wre": 663,
    "G1 Fall_theta_wre": 1.832571016,
    "G1 Fall_pr_1_99_wre": 99,
    "G1 Fall_ss_wre": 139,
    "G1 Fall_dss_wre": 683,
    "G1 Winter_theta_wre": 2.171734471,
    "G1 Winter_pr_1_99_wre": 99,
    "G1 Winter_ss_wre": 143,
    "G1 Winter_dss_wre": 717,
    "G1 Spring_theta_wre": 2.666786376,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 143,
    "G1 Spring_dss_wre": 767,
    "G2 Fall_theta_wre": 2.599470776,
    "G2 Fall_pr_1_99_wre": 99,
    "G2 Fall_ss_wre": 137,
    "G2 Fall_dss_wre": 760,
    "G2 Winter_theta_wre": 2.886069778,
    "G2 Winter_pr_1_99_wre": 99,
    "G2 Winter_ss_wre": 136,
    "G2 Winter_dss_wre": 789,
    "G2 Spring_theta_wre": 3.486675826,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 141,
    "G2 Spring_dss_wre": 849,
    "G3 Fall_theta_wre": 3.445757585,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 136,
    "G3 Fall_dss_wre": 845,
    "G3 Winter_theta_wre": 3.765141851,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 136,
    "G3 Winter_dss_wre": 877,
    "G3 Spring_theta_wre": 4.326079765,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 138,
    "G3 Spring_dss_wre": 933
  },
  {
    "ID": 499,
    "KG Winter_theta_wre": 1.469311518,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 144,
    "KG Winter_dss_wre": 647,
    "KG Spring_theta_wre": 1.881979582,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 145,
    "KG Spring_dss_wre": 688,
    "G1 Fall_theta_wre": 1.861308384,
    "G1 Fall_pr_1_99_wre": 99,
    "G1 Fall_ss_wre": 139,
    "G1 Fall_dss_wre": 686,
    "G1 Winter_theta_wre": 2.391304966,
    "G1 Winter_pr_1_99_wre": 99,
    "G1 Winter_ss_wre": 148,
    "G1 Winter_dss_wre": 739,
    "G1 Spring_theta_wre": 2.929873677,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 148,
    "G1 Spring_dss_wre": 793,
    "G2 Fall_theta_wre": 2.630030183,
    "G2 Fall_pr_1_99_wre": 99,
    "G2 Fall_ss_wre": 137,
    "G2 Fall_dss_wre": 763,
    "G2 Winter_theta_wre": 3.137686379,
    "G2 Winter_pr_1_99_wre": 99,
    "G2 Winter_ss_wre": 140,
    "G2 Winter_dss_wre": 814,
    "G2 Spring_theta_wre": 3.593457424,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 143,
    "G2 Spring_dss_wre": 859,
    "G3 Fall_theta_wre": 3.551952658,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 138,
    "G3 Fall_dss_wre": 855,
    "G3 Winter_theta_wre": 4.015683324,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 141,
    "G3 Winter_dss_wre": 902,
    "G3 Spring_theta_wre": 4.385478557,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 139,
    "G3 Spring_dss_wre": 939
  },
  {
    "ID": 500,
    "KG Winter_theta_wre": 1.564315673,
    "KG Winter_pr_1_99_wre": 99,
    "KG Winter_ss_wre": 146,
    "KG Winter_dss_wre": 656,
    "KG Spring_theta_wre": 2.156439581,
    "KG Spring_pr_1_99_wre": 99,
    "KG Spring_ss_wre": 150,
    "KG Spring_dss_wre": 716,
    "G1 Fall_theta_wre": 2.203244095,
    "G1 Fall_pr_1_99_wre": 99,
    "G1 Fall_ss_wre": 145,
    "G1 Fall_dss_wre": 720,
    "G1 Winter_theta_wre": 2.416385697,
    "G1 Winter_pr_1_99_wre": 99,
    "G1 Winter_ss_wre": 148,
    "G1 Winter_dss_wre": 742,
    "G1 Spring_theta_wre": 2.980187621,
    "G1 Spring_pr_1_99_wre": 99,
    "G1 Spring_ss_wre": 149,
    "G1 Spring_dss_wre": 798,
    "G2 Fall_theta_wre": 3.064869598,
    "G2 Fall_pr_1_99_wre": 99,
    "G2 Fall_ss_wre": 145,
    "G2 Fall_dss_wre": 806,
    "G2 Winter_theta_wre": 3.342527449,
    "G2 Winter_pr_1_99_wre": 99,
    "G2 Winter_ss_wre": 144,
    "G2 Winter_dss_wre": 834,
    "G2 Spring_theta_wre": 4.027559746,
    "G2 Spring_pr_1_99_wre": 99,
    "G2 Spring_ss_wre": 151,
    "G2 Spring_dss_wre": 903,
    "G3 Fall_theta_wre": 4.196227494,
    "G3 Fall_pr_1_99_wre": 99,
    "G3 Fall_ss_wre": 149,
    "G3 Fall_dss_wre": 920,
    "G3 Winter_theta_wre": 4.309529129,
    "G3 Winter_pr_1_99_wre": 99,
    "G3 Winter_ss_wre": 146,
    "G3 Winter_dss_wre": 931,
    "G3 Spring_theta_wre": 4.639085904,
    "G3 Spring_pr_1_99_wre": 99,
    "G3 Spring_ss_wre": 143,
    "G3 Spring_dss_wre": 964
  }
];

export const ble: any = [
  {
    "ID": 1,
    "KG Fall_theta_ble": -3.528379784,
    "KG Fall_pr_1_99_ble": 1,
    "KG Fall_ss_ble": 44,
    "KG Fall_dss_ble": 147,
    "KG Winter_theta_ble": -2.060721371,
    "KG Winter_pr_1_99_ble": 1,
    "KG Winter_ss_ble": 61,
    "KG Winter_dss_ble": 294,
    "KG Spring_theta_ble": -1.767772674,
    "KG Spring_pr_1_99_ble": 1,
    "KG Spring_ss_ble": 56,
    "KG Spring_dss_ble": 323
  },
  {
    "ID": 2,
    "KG Fall_theta_ble": -2.989387811,
    "KG Fall_pr_1_99_ble": 1,
    "KG Fall_ss_ble": 53,
    "KG Fall_dss_ble": 201,
    "KG Winter_theta_ble": -1.918601342,
    "KG Winter_pr_1_99_ble": 1,
    "KG Winter_ss_ble": 63,
    "KG Winter_dss_ble": 308,
    "KG Spring_theta_ble": -1.604929378,
    "KG Spring_pr_1_99_ble": 1,
    "KG Spring_ss_ble": 59,
    "KG Spring_dss_ble": 340
  },
  {
    "ID": 3,
    "KG Fall_theta_ble": -2.759841505,
    "KG Fall_pr_1_99_ble": 1,
    "KG Fall_ss_ble": 57,
    "KG Fall_dss_ble": 224,
    "KG Winter_theta_ble": -1.769076647,
    "KG Winter_pr_1_99_ble": 1,
    "KG Winter_ss_ble": 66,
    "KG Winter_dss_ble": 323,
    "KG Spring_theta_ble": -1.501687115,
    "KG Spring_pr_1_99_ble": 1,
    "KG Spring_ss_ble": 61,
    "KG Spring_dss_ble": 350
  },
  {
    "ID": 4,
    "KG Fall_theta_ble": -2.666720517,
    "KG Fall_pr_1_99_ble": 1,
    "KG Fall_ss_ble": 59,
    "KG Fall_dss_ble": 233,
    "KG Winter_theta_ble": -1.677503342,
    "KG Winter_pr_1_99_ble": 1,
    "KG Winter_ss_ble": 68,
    "KG Winter_dss_ble": 332,
    "KG Spring_theta_ble": -1.31710553,
    "KG Spring_pr_1_99_ble": 1,
    "KG Spring_ss_ble": 65,
    "KG Spring_dss_ble": 368
  },
  {
    "ID": 5,
    "KG Fall_theta_ble": -2.555842038,
    "KG Fall_pr_1_99_ble": 1,
    "KG Fall_ss_ble": 61,
    "KG Fall_dss_ble": 244,
    "KG Winter_theta_ble": -1.653002134,
    "KG Winter_pr_1_99_ble": 1,
    "KG Winter_ss_ble": 68,
    "KG Winter_dss_ble": 335,
    "KG Spring_theta_ble": -1.284176031,
    "KG Spring_pr_1_99_ble": 1,
    "KG Spring_ss_ble": 66,
    "KG Spring_dss_ble": 372
  },
  {
    "ID": 6,
    "KG Fall_theta_ble": -2.362028184,
    "KG Fall_pr_1_99_ble": 1,
    "KG Fall_ss_ble": 64,
    "KG Fall_dss_ble": 264,
    "KG Winter_theta_ble": -1.651943597,
    "KG Winter_pr_1_99_ble": 1,
    "KG Winter_ss_ble": 68,
    "KG Winter_dss_ble": 335,
    "KG Spring_theta_ble": -1.278567258,
    "KG Spring_pr_1_99_ble": 1,
    "KG Spring_ss_ble": 66,
    "KG Spring_dss_ble": 372
  },
  {
    "ID": 7,
    "KG Fall_theta_ble": -2.330838429,
    "KG Fall_pr_1_99_ble": 1,
    "KG Fall_ss_ble": 65,
    "KG Fall_dss_ble": 267,
    "KG Winter_theta_ble": -1.624795704,
    "KG Winter_pr_1_99_ble": 1,
    "KG Winter_ss_ble": 69,
    "KG Winter_dss_ble": 338,
    "KG Spring_theta_ble": -1.259142962,
    "KG Spring_pr_1_99_ble": 1,
    "KG Spring_ss_ble": 66,
    "KG Spring_dss_ble": 374
  },
  {
    "ID": 8,
    "KG Fall_theta_ble": -2.192689565,
    "KG Fall_pr_1_99_ble": 2,
    "KG Fall_ss_ble": 67,
    "KG Fall_dss_ble": 281,
    "KG Winter_theta_ble": -1.579420623,
    "KG Winter_pr_1_99_ble": 2,
    "KG Winter_ss_ble": 69,
    "KG Winter_dss_ble": 342,
    "KG Spring_theta_ble": -1.24001766,
    "KG Spring_pr_1_99_ble": 2,
    "KG Spring_ss_ble": 67,
    "KG Spring_dss_ble": 376
  },
  {
    "ID": 9,
    "KG Fall_theta_ble": -2.1874361,
    "KG Fall_pr_1_99_ble": 2,
    "KG Fall_ss_ble": 67,
    "KG Fall_dss_ble": 281,
    "KG Winter_theta_ble": -1.527303896,
    "KG Winter_pr_1_99_ble": 2,
    "KG Winter_ss_ble": 70,
    "KG Winter_dss_ble": 347,
    "KG Spring_theta_ble": -1.184708681,
    "KG Spring_pr_1_99_ble": 2,
    "KG Spring_ss_ble": 68,
    "KG Spring_dss_ble": 382
  },
  {
    "ID": 10,
    "KG Fall_theta_ble": -2.11334064,
    "KG Fall_pr_1_99_ble": 2,
    "KG Fall_ss_ble": 68,
    "KG Fall_dss_ble": 289,
    "KG Winter_theta_ble": -1.514661473,
    "KG Winter_pr_1_99_ble": 2,
    "KG Winter_ss_ble": 71,
    "KG Winter_dss_ble": 349,
    "KG Spring_theta_ble": -1.100269348,
    "KG Spring_pr_1_99_ble": 2,
    "KG Spring_ss_ble": 69,
    "KG Spring_dss_ble": 390
  },
  {
    "ID": 11,
    "KG Fall_theta_ble": -2.103409467,
    "KG Fall_pr_1_99_ble": 2,
    "KG Fall_ss_ble": 69,
    "KG Fall_dss_ble": 290,
    "KG Winter_theta_ble": -1.467751676,
    "KG Winter_pr_1_99_ble": 2,
    "KG Winter_ss_ble": 71,
    "KG Winter_dss_ble": 353,
    "KG Spring_theta_ble": -1.084789715,
    "KG Spring_pr_1_99_ble": 2,
    "KG Spring_ss_ble": 70,
    "KG Spring_dss_ble": 392
  },
  {
    "ID": 12,
    "KG Fall_theta_ble": -2.005029527,
    "KG Fall_pr_1_99_ble": 2,
    "KG Fall_ss_ble": 70,
    "KG Fall_dss_ble": 299,
    "KG Winter_theta_ble": -1.431256079,
    "KG Winter_pr_1_99_ble": 2,
    "KG Winter_ss_ble": 72,
    "KG Winter_dss_ble": 357,
    "KG Spring_theta_ble": -1.052856775,
    "KG Spring_pr_1_99_ble": 2,
    "KG Spring_ss_ble": 70,
    "KG Spring_dss_ble": 395
  },
  {
    "ID": 13,
    "KG Fall_theta_ble": -1.947904668,
    "KG Fall_pr_1_99_ble": 3,
    "KG Fall_ss_ble": 71,
    "KG Fall_dss_ble": 305,
    "KG Winter_theta_ble": -1.410640811,
    "KG Winter_pr_1_99_ble": 3,
    "KG Winter_ss_ble": 72,
    "KG Winter_dss_ble": 359,
    "KG Spring_theta_ble": -1.037355112,
    "KG Spring_pr_1_99_ble": 3,
    "KG Spring_ss_ble": 71,
    "KG Spring_dss_ble": 396
  },
  {
    "ID": 14,
    "KG Fall_theta_ble": -1.929384424,
    "KG Fall_pr_1_99_ble": 3,
    "KG Fall_ss_ble": 72,
    "KG Fall_dss_ble": 307,
    "KG Winter_theta_ble": -1.399234825,
    "KG Winter_pr_1_99_ble": 3,
    "KG Winter_ss_ble": 73,
    "KG Winter_dss_ble": 360,
    "KG Spring_theta_ble": -1.028990935,
    "KG Spring_pr_1_99_ble": 3,
    "KG Spring_ss_ble": 71,
    "KG Spring_dss_ble": 397
  },
  {
    "ID": 15,
    "KG Fall_theta_ble": -1.857388994,
    "KG Fall_pr_1_99_ble": 3,
    "KG Fall_ss_ble": 73,
    "KG Fall_dss_ble": 314,
    "KG Winter_theta_ble": -1.393630035,
    "KG Winter_pr_1_99_ble": 3,
    "KG Winter_ss_ble": 73,
    "KG Winter_dss_ble": 361,
    "KG Spring_theta_ble": -1.026196078,
    "KG Spring_pr_1_99_ble": 3,
    "KG Spring_ss_ble": 71,
    "KG Spring_dss_ble": 397
  },
  {
    "ID": 16,
    "KG Fall_theta_ble": -1.822289561,
    "KG Fall_pr_1_99_ble": 3,
    "KG Fall_ss_ble": 73,
    "KG Fall_dss_ble": 318,
    "KG Winter_theta_ble": -1.391993838,
    "KG Winter_pr_1_99_ble": 3,
    "KG Winter_ss_ble": 73,
    "KG Winter_dss_ble": 361,
    "KG Spring_theta_ble": -0.965017813,
    "KG Spring_pr_1_99_ble": 3,
    "KG Spring_ss_ble": 72,
    "KG Spring_dss_ble": 403
  },
  {
    "ID": 17,
    "KG Fall_theta_ble": -1.811750244,
    "KG Fall_pr_1_99_ble": 3,
    "KG Fall_ss_ble": 74,
    "KG Fall_dss_ble": 319,
    "KG Winter_theta_ble": -1.379744536,
    "KG Winter_pr_1_99_ble": 3,
    "KG Winter_ss_ble": 73,
    "KG Winter_dss_ble": 362,
    "KG Spring_theta_ble": -0.913905967,
    "KG Spring_pr_1_99_ble": 3,
    "KG Spring_ss_ble": 73,
    "KG Spring_dss_ble": 409
  },
  {
    "ID": 18,
    "KG Fall_theta_ble": -1.801567357,
    "KG Fall_pr_1_99_ble": 4,
    "KG Fall_ss_ble": 74,
    "KG Fall_dss_ble": 320,
    "KG Winter_theta_ble": -1.379486666,
    "KG Winter_pr_1_99_ble": 4,
    "KG Winter_ss_ble": 73,
    "KG Winter_dss_ble": 362,
    "KG Spring_theta_ble": -0.908417689,
    "KG Spring_pr_1_99_ble": 4,
    "KG Spring_ss_ble": 73,
    "KG Spring_dss_ble": 409
  },
  {
    "ID": 19,
    "KG Fall_theta_ble": -1.787073548,
    "KG Fall_pr_1_99_ble": 4,
    "KG Fall_ss_ble": 74,
    "KG Fall_dss_ble": 321,
    "KG Winter_theta_ble": -1.370085743,
    "KG Winter_pr_1_99_ble": 4,
    "KG Winter_ss_ble": 73,
    "KG Winter_dss_ble": 363,
    "KG Spring_theta_ble": -0.871910633,
    "KG Spring_pr_1_99_ble": 4,
    "KG Spring_ss_ble": 74,
    "KG Spring_dss_ble": 413
  },
  {
    "ID": 20,
    "KG Fall_theta_ble": -1.743193776,
    "KG Fall_pr_1_99_ble": 4,
    "KG Fall_ss_ble": 75,
    "KG Fall_dss_ble": 326,
    "KG Winter_theta_ble": -1.328383262,
    "KG Winter_pr_1_99_ble": 4,
    "KG Winter_ss_ble": 74,
    "KG Winter_dss_ble": 367,
    "KG Spring_theta_ble": -0.84733679,
    "KG Spring_pr_1_99_ble": 4,
    "KG Spring_ss_ble": 74,
    "KG Spring_dss_ble": 415
  },
  {
    "ID": 21,
    "KG Fall_theta_ble": -1.687400196,
    "KG Fall_pr_1_99_ble": 4,
    "KG Fall_ss_ble": 76,
    "KG Fall_dss_ble": 331,
    "KG Winter_theta_ble": -1.325290696,
    "KG Winter_pr_1_99_ble": 4,
    "KG Winter_ss_ble": 74,
    "KG Winter_dss_ble": 367,
    "KG Spring_theta_ble": -0.828674252,
    "KG Spring_pr_1_99_ble": 4,
    "KG Spring_ss_ble": 75,
    "KG Spring_dss_ble": 417
  },
  {
    "ID": 22,
    "KG Fall_theta_ble": -1.68094101,
    "KG Fall_pr_1_99_ble": 4,
    "KG Fall_ss_ble": 76,
    "KG Fall_dss_ble": 332,
    "KG Winter_theta_ble": -1.255415089,
    "KG Winter_pr_1_99_ble": 4,
    "KG Winter_ss_ble": 75,
    "KG Winter_dss_ble": 374,
    "KG Spring_theta_ble": -0.797787758,
    "KG Spring_pr_1_99_ble": 4,
    "KG Spring_ss_ble": 75,
    "KG Spring_dss_ble": 420
  },
  {
    "ID": 23,
    "KG Fall_theta_ble": -1.678281871,
    "KG Fall_pr_1_99_ble": 5,
    "KG Fall_ss_ble": 76,
    "KG Fall_dss_ble": 332,
    "KG Winter_theta_ble": -1.239305014,
    "KG Winter_pr_1_99_ble": 5,
    "KG Winter_ss_ble": 75,
    "KG Winter_dss_ble": 376,
    "KG Spring_theta_ble": -0.784937314,
    "KG Spring_pr_1_99_ble": 5,
    "KG Spring_ss_ble": 76,
    "KG Spring_dss_ble": 422
  },
  {
    "ID": 24,
    "KG Fall_theta_ble": -1.670822406,
    "KG Fall_pr_1_99_ble": 5,
    "KG Fall_ss_ble": 76,
    "KG Fall_dss_ble": 333,
    "KG Winter_theta_ble": -1.207486942,
    "KG Winter_pr_1_99_ble": 5,
    "KG Winter_ss_ble": 76,
    "KG Winter_dss_ble": 379,
    "KG Spring_theta_ble": -0.778751841,
    "KG Spring_pr_1_99_ble": 5,
    "KG Spring_ss_ble": 76,
    "KG Spring_dss_ble": 422
  },
  {
    "ID": 25,
    "KG Fall_theta_ble": -1.658376249,
    "KG Fall_pr_1_99_ble": 5,
    "KG Fall_ss_ble": 76,
    "KG Fall_dss_ble": 334,
    "KG Winter_theta_ble": -1.195790768,
    "KG Winter_pr_1_99_ble": 5,
    "KG Winter_ss_ble": 76,
    "KG Winter_dss_ble": 380,
    "KG Spring_theta_ble": -0.769370964,
    "KG Spring_pr_1_99_ble": 5,
    "KG Spring_ss_ble": 76,
    "KG Spring_dss_ble": 423
  },
  {
    "ID": 26,
    "KG Fall_theta_ble": -1.624391238,
    "KG Fall_pr_1_99_ble": 5,
    "KG Fall_ss_ble": 77,
    "KG Fall_dss_ble": 338,
    "KG Winter_theta_ble": -1.18999871,
    "KG Winter_pr_1_99_ble": 5,
    "KG Winter_ss_ble": 76,
    "KG Winter_dss_ble": 381,
    "KG Spring_theta_ble": -0.767130434,
    "KG Spring_pr_1_99_ble": 5,
    "KG Spring_ss_ble": 76,
    "KG Spring_dss_ble": 423
  },
  {
    "ID": 27,
    "KG Fall_theta_ble": -1.600906816,
    "KG Fall_pr_1_99_ble": 5,
    "KG Fall_ss_ble": 77,
    "KG Fall_dss_ble": 340,
    "KG Winter_theta_ble": -1.153457368,
    "KG Winter_pr_1_99_ble": 5,
    "KG Winter_ss_ble": 77,
    "KG Winter_dss_ble": 385,
    "KG Spring_theta_ble": -0.746758985,
    "KG Spring_pr_1_99_ble": 5,
    "KG Spring_ss_ble": 76,
    "KG Spring_dss_ble": 425
  },
  {
    "ID": 28,
    "KG Fall_theta_ble": -1.587709408,
    "KG Fall_pr_1_99_ble": 6,
    "KG Fall_ss_ble": 77,
    "KG Fall_dss_ble": 341,
    "KG Winter_theta_ble": -1.153079843,
    "KG Winter_pr_1_99_ble": 6,
    "KG Winter_ss_ble": 77,
    "KG Winter_dss_ble": 385,
    "KG Spring_theta_ble": -0.707876172,
    "KG Spring_pr_1_99_ble": 6,
    "KG Spring_ss_ble": 77,
    "KG Spring_dss_ble": 429
  },
  {
    "ID": 29,
    "KG Fall_theta_ble": -1.541685841,
    "KG Fall_pr_1_99_ble": 6,
    "KG Fall_ss_ble": 78,
    "KG Fall_dss_ble": 346,
    "KG Winter_theta_ble": -1.143372955,
    "KG Winter_pr_1_99_ble": 6,
    "KG Winter_ss_ble": 77,
    "KG Winter_dss_ble": 386,
    "KG Spring_theta_ble": -0.699276051,
    "KG Spring_pr_1_99_ble": 6,
    "KG Spring_ss_ble": 77,
    "KG Spring_dss_ble": 430
  },
  {
    "ID": 30,
    "KG Fall_theta_ble": -1.531499037,
    "KG Fall_pr_1_99_ble": 6,
    "KG Fall_ss_ble": 78,
    "KG Fall_dss_ble": 347,
    "KG Winter_theta_ble": -1.134014638,
    "KG Winter_pr_1_99_ble": 6,
    "KG Winter_ss_ble": 77,
    "KG Winter_dss_ble": 387,
    "KG Spring_theta_ble": -0.696555376,
    "KG Spring_pr_1_99_ble": 6,
    "KG Spring_ss_ble": 77,
    "KG Spring_dss_ble": 430
  },
  {
    "ID": 31,
    "KG Fall_theta_ble": -1.514728584,
    "KG Fall_pr_1_99_ble": 6,
    "KG Fall_ss_ble": 79,
    "KG Fall_dss_ble": 349,
    "KG Winter_theta_ble": -1.132909761,
    "KG Winter_pr_1_99_ble": 6,
    "KG Winter_ss_ble": 77,
    "KG Winter_dss_ble": 387,
    "KG Spring_theta_ble": -0.694081078,
    "KG Spring_pr_1_99_ble": 6,
    "KG Spring_ss_ble": 77,
    "KG Spring_dss_ble": 431
  },
  {
    "ID": 32,
    "KG Fall_theta_ble": -1.50024232,
    "KG Fall_pr_1_99_ble": 6,
    "KG Fall_ss_ble": 79,
    "KG Fall_dss_ble": 350,
    "KG Winter_theta_ble": -1.126248882,
    "KG Winter_pr_1_99_ble": 6,
    "KG Winter_ss_ble": 77,
    "KG Winter_dss_ble": 387,
    "KG Spring_theta_ble": -0.69298174,
    "KG Spring_pr_1_99_ble": 6,
    "KG Spring_ss_ble": 77,
    "KG Spring_dss_ble": 431
  },
  {
    "ID": 33,
    "KG Fall_theta_ble": -1.486818659,
    "KG Fall_pr_1_99_ble": 7,
    "KG Fall_ss_ble": 79,
    "KG Fall_dss_ble": 351,
    "KG Winter_theta_ble": -1.124688513,
    "KG Winter_pr_1_99_ble": 7,
    "KG Winter_ss_ble": 77,
    "KG Winter_dss_ble": 388,
    "KG Spring_theta_ble": -0.686919141,
    "KG Spring_pr_1_99_ble": 7,
    "KG Spring_ss_ble": 77,
    "KG Spring_dss_ble": 431
  },
  {
    "ID": 34,
    "KG Fall_theta_ble": -1.486720693,
    "KG Fall_pr_1_99_ble": 7,
    "KG Fall_ss_ble": 79,
    "KG Fall_dss_ble": 351,
    "KG Winter_theta_ble": -1.085177292,
    "KG Winter_pr_1_99_ble": 7,
    "KG Winter_ss_ble": 78,
    "KG Winter_dss_ble": 391,
    "KG Spring_theta_ble": -0.673506779,
    "KG Spring_pr_1_99_ble": 7,
    "KG Spring_ss_ble": 78,
    "KG Spring_dss_ble": 433
  },
  {
    "ID": 35,
    "KG Fall_theta_ble": -1.474399386,
    "KG Fall_pr_1_99_ble": 7,
    "KG Fall_ss_ble": 79,
    "KG Fall_dss_ble": 353,
    "KG Winter_theta_ble": -1.079291959,
    "KG Winter_pr_1_99_ble": 7,
    "KG Winter_ss_ble": 78,
    "KG Winter_dss_ble": 392,
    "KG Spring_theta_ble": -0.663470766,
    "KG Spring_pr_1_99_ble": 7,
    "KG Spring_ss_ble": 78,
    "KG Spring_dss_ble": 434
  },
  {
    "ID": 36,
    "KG Fall_theta_ble": -1.470915372,
    "KG Fall_pr_1_99_ble": 7,
    "KG Fall_ss_ble": 79,
    "KG Fall_dss_ble": 353,
    "KG Winter_theta_ble": -1.070926141,
    "KG Winter_pr_1_99_ble": 7,
    "KG Winter_ss_ble": 78,
    "KG Winter_dss_ble": 393,
    "KG Spring_theta_ble": -0.657433082,
    "KG Spring_pr_1_99_ble": 7,
    "KG Spring_ss_ble": 78,
    "KG Spring_dss_ble": 434
  },
  {
    "ID": 37,
    "KG Fall_theta_ble": -1.46173377,
    "KG Fall_pr_1_99_ble": 7,
    "KG Fall_ss_ble": 80,
    "KG Fall_dss_ble": 354,
    "KG Winter_theta_ble": -1.063776506,
    "KG Winter_pr_1_99_ble": 7,
    "KG Winter_ss_ble": 79,
    "KG Winter_dss_ble": 394,
    "KG Spring_theta_ble": -0.655392189,
    "KG Spring_pr_1_99_ble": 7,
    "KG Spring_ss_ble": 78,
    "KG Spring_dss_ble": 434
  },
  {
    "ID": 38,
    "KG Fall_theta_ble": -1.454339695,
    "KG Fall_pr_1_99_ble": 8,
    "KG Fall_ss_ble": 80,
    "KG Fall_dss_ble": 355,
    "KG Winter_theta_ble": -1.059925185,
    "KG Winter_pr_1_99_ble": 8,
    "KG Winter_ss_ble": 79,
    "KG Winter_dss_ble": 394,
    "KG Spring_theta_ble": -0.629879157,
    "KG Spring_pr_1_99_ble": 8,
    "KG Spring_ss_ble": 79,
    "KG Spring_dss_ble": 437
  },
  {
    "ID": 39,
    "KG Fall_theta_ble": -1.449260687,
    "KG Fall_pr_1_99_ble": 8,
    "KG Fall_ss_ble": 80,
    "KG Fall_dss_ble": 355,
    "KG Winter_theta_ble": -1.049953065,
    "KG Winter_pr_1_99_ble": 8,
    "KG Winter_ss_ble": 79,
    "KG Winter_dss_ble": 395,
    "KG Spring_theta_ble": -0.62950005,
    "KG Spring_pr_1_99_ble": 8,
    "KG Spring_ss_ble": 79,
    "KG Spring_dss_ble": 437
  },
  {
    "ID": 40,
    "KG Fall_theta_ble": -1.437627545,
    "KG Fall_pr_1_99_ble": 8,
    "KG Fall_ss_ble": 80,
    "KG Fall_dss_ble": 356,
    "KG Winter_theta_ble": -1.036172264,
    "KG Winter_pr_1_99_ble": 8,
    "KG Winter_ss_ble": 79,
    "KG Winter_dss_ble": 396,
    "KG Spring_theta_ble": -0.629151946,
    "KG Spring_pr_1_99_ble": 8,
    "KG Spring_ss_ble": 79,
    "KG Spring_dss_ble": 437
  },
  {
    "ID": 41,
    "KG Fall_theta_ble": -1.435436103,
    "KG Fall_pr_1_99_ble": 8,
    "KG Fall_ss_ble": 80,
    "KG Fall_dss_ble": 356,
    "KG Winter_theta_ble": -1.031113907,
    "KG Winter_pr_1_99_ble": 8,
    "KG Winter_ss_ble": 79,
    "KG Winter_dss_ble": 397,
    "KG Spring_theta_ble": -0.604153295,
    "KG Spring_pr_1_99_ble": 8,
    "KG Spring_ss_ble": 79,
    "KG Spring_dss_ble": 440
  },
  {
    "ID": 42,
    "KG Fall_theta_ble": -1.431065407,
    "KG Fall_pr_1_99_ble": 8,
    "KG Fall_ss_ble": 80,
    "KG Fall_dss_ble": 357,
    "KG Winter_theta_ble": -1.009207463,
    "KG Winter_pr_1_99_ble": 8,
    "KG Winter_ss_ble": 80,
    "KG Winter_dss_ble": 399,
    "KG Spring_theta_ble": -0.603436397,
    "KG Spring_pr_1_99_ble": 8,
    "KG Spring_ss_ble": 79,
    "KG Spring_dss_ble": 440
  },
  {
    "ID": 43,
    "KG Fall_theta_ble": -1.416203186,
    "KG Fall_pr_1_99_ble": 9,
    "KG Fall_ss_ble": 80,
    "KG Fall_dss_ble": 358,
    "KG Winter_theta_ble": -0.999024177,
    "KG Winter_pr_1_99_ble": 9,
    "KG Winter_ss_ble": 80,
    "KG Winter_dss_ble": 400,
    "KG Spring_theta_ble": -0.590728044,
    "KG Spring_pr_1_99_ble": 9,
    "KG Spring_ss_ble": 79,
    "KG Spring_dss_ble": 441
  },
  {
    "ID": 44,
    "KG Fall_theta_ble": -1.407749367,
    "KG Fall_pr_1_99_ble": 9,
    "KG Fall_ss_ble": 80,
    "KG Fall_dss_ble": 359,
    "KG Winter_theta_ble": -0.983953203,
    "KG Winter_pr_1_99_ble": 9,
    "KG Winter_ss_ble": 80,
    "KG Winter_dss_ble": 402,
    "KG Spring_theta_ble": -0.579251001,
    "KG Spring_pr_1_99_ble": 9,
    "KG Spring_ss_ble": 80,
    "KG Spring_dss_ble": 442
  },
  {
    "ID": 45,
    "KG Fall_theta_ble": -1.390460215,
    "KG Fall_pr_1_99_ble": 9,
    "KG Fall_ss_ble": 81,
    "KG Fall_dss_ble": 361,
    "KG Winter_theta_ble": -0.983940258,
    "KG Winter_pr_1_99_ble": 9,
    "KG Winter_ss_ble": 80,
    "KG Winter_dss_ble": 402,
    "KG Spring_theta_ble": -0.578610402,
    "KG Spring_pr_1_99_ble": 9,
    "KG Spring_ss_ble": 80,
    "KG Spring_dss_ble": 442
  },
  {
    "ID": 46,
    "KG Fall_theta_ble": -1.384591609,
    "KG Fall_pr_1_99_ble": 9,
    "KG Fall_ss_ble": 81,
    "KG Fall_dss_ble": 362,
    "KG Winter_theta_ble": -0.975228406,
    "KG Winter_pr_1_99_ble": 9,
    "KG Winter_ss_ble": 80,
    "KG Winter_dss_ble": 402,
    "KG Spring_theta_ble": -0.560531927,
    "KG Spring_pr_1_99_ble": 9,
    "KG Spring_ss_ble": 80,
    "KG Spring_dss_ble": 444
  },
  {
    "ID": 47,
    "KG Fall_theta_ble": -1.377235351,
    "KG Fall_pr_1_99_ble": 9,
    "KG Fall_ss_ble": 81,
    "KG Fall_dss_ble": 362,
    "KG Winter_theta_ble": -0.972252793,
    "KG Winter_pr_1_99_ble": 9,
    "KG Winter_ss_ble": 80,
    "KG Winter_dss_ble": 403,
    "KG Spring_theta_ble": -0.553748222,
    "KG Spring_pr_1_99_ble": 9,
    "KG Spring_ss_ble": 80,
    "KG Spring_dss_ble": 445
  },
  {
    "ID": 48,
    "KG Fall_theta_ble": -1.376512885,
    "KG Fall_pr_1_99_ble": 10,
    "KG Fall_ss_ble": 81,
    "KG Fall_dss_ble": 362,
    "KG Winter_theta_ble": -0.970625482,
    "KG Winter_pr_1_99_ble": 10,
    "KG Winter_ss_ble": 80,
    "KG Winter_dss_ble": 403,
    "KG Spring_theta_ble": -0.553341666,
    "KG Spring_pr_1_99_ble": 10,
    "KG Spring_ss_ble": 80,
    "KG Spring_dss_ble": 445
  },
  {
    "ID": 49,
    "KG Fall_theta_ble": -1.369296012,
    "KG Fall_pr_1_99_ble": 10,
    "KG Fall_ss_ble": 81,
    "KG Fall_dss_ble": 363,
    "KG Winter_theta_ble": -0.963524648,
    "KG Winter_pr_1_99_ble": 10,
    "KG Winter_ss_ble": 80,
    "KG Winter_dss_ble": 404,
    "KG Spring_theta_ble": -0.515993159,
    "KG Spring_pr_1_99_ble": 10,
    "KG Spring_ss_ble": 81,
    "KG Spring_dss_ble": 448
  },
  {
    "ID": 50,
    "KG Fall_theta_ble": -1.367255149,
    "KG Fall_pr_1_99_ble": 10,
    "KG Fall_ss_ble": 81,
    "KG Fall_dss_ble": 363,
    "KG Winter_theta_ble": -0.960547404,
    "KG Winter_pr_1_99_ble": 10,
    "KG Winter_ss_ble": 80,
    "KG Winter_dss_ble": 404,
    "KG Spring_theta_ble": -0.507825518,
    "KG Spring_pr_1_99_ble": 10,
    "KG Spring_ss_ble": 81,
    "KG Spring_dss_ble": 449
  },
  {
    "ID": 51,
    "KG Fall_theta_ble": -1.360393162,
    "KG Fall_pr_1_99_ble": 10,
    "KG Fall_ss_ble": 81,
    "KG Fall_dss_ble": 364,
    "KG Winter_theta_ble": -0.958772154,
    "KG Winter_pr_1_99_ble": 10,
    "KG Winter_ss_ble": 80,
    "KG Winter_dss_ble": 404,
    "KG Spring_theta_ble": -0.503373323,
    "KG Spring_pr_1_99_ble": 10,
    "KG Spring_ss_ble": 81,
    "KG Spring_dss_ble": 450
  },
  {
    "ID": 52,
    "KG Fall_theta_ble": -1.354654933,
    "KG Fall_pr_1_99_ble": 10,
    "KG Fall_ss_ble": 81,
    "KG Fall_dss_ble": 365,
    "KG Winter_theta_ble": -0.923109708,
    "KG Winter_pr_1_99_ble": 10,
    "KG Winter_ss_ble": 81,
    "KG Winter_dss_ble": 408,
    "KG Spring_theta_ble": -0.501997272,
    "KG Spring_pr_1_99_ble": 10,
    "KG Spring_ss_ble": 81,
    "KG Spring_dss_ble": 450
  },
  {
    "ID": 53,
    "KG Fall_theta_ble": -1.343720955,
    "KG Fall_pr_1_99_ble": 11,
    "KG Fall_ss_ble": 82,
    "KG Fall_dss_ble": 366,
    "KG Winter_theta_ble": -0.896930267,
    "KG Winter_pr_1_99_ble": 11,
    "KG Winter_ss_ble": 82,
    "KG Winter_dss_ble": 410,
    "KG Spring_theta_ble": -0.498428256,
    "KG Spring_pr_1_99_ble": 11,
    "KG Spring_ss_ble": 81,
    "KG Spring_dss_ble": 450
  },
  {
    "ID": 54,
    "KG Fall_theta_ble": -1.341676646,
    "KG Fall_pr_1_99_ble": 11,
    "KG Fall_ss_ble": 82,
    "KG Fall_dss_ble": 366,
    "KG Winter_theta_ble": -0.893711107,
    "KG Winter_pr_1_99_ble": 11,
    "KG Winter_ss_ble": 82,
    "KG Winter_dss_ble": 411,
    "KG Spring_theta_ble": -0.487098031,
    "KG Spring_pr_1_99_ble": 11,
    "KG Spring_ss_ble": 81,
    "KG Spring_dss_ble": 451
  },
  {
    "ID": 55,
    "KG Fall_theta_ble": -1.333456893,
    "KG Fall_pr_1_99_ble": 11,
    "KG Fall_ss_ble": 82,
    "KG Fall_dss_ble": 367,
    "KG Winter_theta_ble": -0.8751766,
    "KG Winter_pr_1_99_ble": 11,
    "KG Winter_ss_ble": 82,
    "KG Winter_dss_ble": 412,
    "KG Spring_theta_ble": -0.486661485,
    "KG Spring_pr_1_99_ble": 11,
    "KG Spring_ss_ble": 81,
    "KG Spring_dss_ble": 451
  },
  {
    "ID": 56,
    "KG Fall_theta_ble": -1.31538931,
    "KG Fall_pr_1_99_ble": 11,
    "KG Fall_ss_ble": 82,
    "KG Fall_dss_ble": 368,
    "KG Winter_theta_ble": -0.859786148,
    "KG Winter_pr_1_99_ble": 11,
    "KG Winter_ss_ble": 82,
    "KG Winter_dss_ble": 414,
    "KG Spring_theta_ble": -0.468355521,
    "KG Spring_pr_1_99_ble": 11,
    "KG Spring_ss_ble": 82,
    "KG Spring_dss_ble": 453
  },
  {
    "ID": 57,
    "KG Fall_theta_ble": -1.311238043,
    "KG Fall_pr_1_99_ble": 11,
    "KG Fall_ss_ble": 82,
    "KG Fall_dss_ble": 369,
    "KG Winter_theta_ble": -0.859708415,
    "KG Winter_pr_1_99_ble": 11,
    "KG Winter_ss_ble": 82,
    "KG Winter_dss_ble": 414,
    "KG Spring_theta_ble": -0.463872395,
    "KG Spring_pr_1_99_ble": 11,
    "KG Spring_ss_ble": 82,
    "KG Spring_dss_ble": 454
  },
  {
    "ID": 58,
    "KG Fall_theta_ble": -1.310261402,
    "KG Fall_pr_1_99_ble": 12,
    "KG Fall_ss_ble": 82,
    "KG Fall_dss_ble": 369,
    "KG Winter_theta_ble": -0.84909523,
    "KG Winter_pr_1_99_ble": 12,
    "KG Winter_ss_ble": 82,
    "KG Winter_dss_ble": 415,
    "KG Spring_theta_ble": -0.462626816,
    "KG Spring_pr_1_99_ble": 12,
    "KG Spring_ss_ble": 82,
    "KG Spring_dss_ble": 454
  },
  {
    "ID": 59,
    "KG Fall_theta_ble": -1.309594984,
    "KG Fall_pr_1_99_ble": 12,
    "KG Fall_ss_ble": 82,
    "KG Fall_dss_ble": 369,
    "KG Winter_theta_ble": -0.844646583,
    "KG Winter_pr_1_99_ble": 12,
    "KG Winter_ss_ble": 82,
    "KG Winter_dss_ble": 416,
    "KG Spring_theta_ble": -0.4301809,
    "KG Spring_pr_1_99_ble": 12,
    "KG Spring_ss_ble": 83,
    "KG Spring_dss_ble": 457
  },
  {
    "ID": 60,
    "KG Fall_theta_ble": -1.3087046,
    "KG Fall_pr_1_99_ble": 12,
    "KG Fall_ss_ble": 82,
    "KG Fall_dss_ble": 369,
    "KG Winter_theta_ble": -0.842482888,
    "KG Winter_pr_1_99_ble": 12,
    "KG Winter_ss_ble": 83,
    "KG Winter_dss_ble": 416,
    "KG Spring_theta_ble": -0.429005082,
    "KG Spring_pr_1_99_ble": 12,
    "KG Spring_ss_ble": 83,
    "KG Spring_dss_ble": 457
  },
  {
    "ID": 61,
    "KG Fall_theta_ble": -1.295000919,
    "KG Fall_pr_1_99_ble": 12,
    "KG Fall_ss_ble": 82,
    "KG Fall_dss_ble": 370,
    "KG Winter_theta_ble": -0.841040273,
    "KG Winter_pr_1_99_ble": 12,
    "KG Winter_ss_ble": 83,
    "KG Winter_dss_ble": 416,
    "KG Spring_theta_ble": -0.423542371,
    "KG Spring_pr_1_99_ble": 12,
    "KG Spring_ss_ble": 83,
    "KG Spring_dss_ble": 458
  },
  {
    "ID": 62,
    "KG Fall_theta_ble": -1.272717208,
    "KG Fall_pr_1_99_ble": 12,
    "KG Fall_ss_ble": 83,
    "KG Fall_dss_ble": 373,
    "KG Winter_theta_ble": -0.834160543,
    "KG Winter_pr_1_99_ble": 12,
    "KG Winter_ss_ble": 83,
    "KG Winter_dss_ble": 417,
    "KG Spring_theta_ble": -0.422507816,
    "KG Spring_pr_1_99_ble": 12,
    "KG Spring_ss_ble": 83,
    "KG Spring_dss_ble": 458
  },
  {
    "ID": 63,
    "KG Fall_theta_ble": -1.265904665,
    "KG Fall_pr_1_99_ble": 13,
    "KG Fall_ss_ble": 83,
    "KG Fall_dss_ble": 373,
    "KG Winter_theta_ble": -0.80882476,
    "KG Winter_pr_1_99_ble": 13,
    "KG Winter_ss_ble": 83,
    "KG Winter_dss_ble": 419,
    "KG Spring_theta_ble": -0.414354144,
    "KG Spring_pr_1_99_ble": 13,
    "KG Spring_ss_ble": 83,
    "KG Spring_dss_ble": 459
  },
  {
    "ID": 64,
    "KG Fall_theta_ble": -1.258078828,
    "KG Fall_pr_1_99_ble": 13,
    "KG Fall_ss_ble": 83,
    "KG Fall_dss_ble": 374,
    "KG Winter_theta_ble": -0.80025417,
    "KG Winter_pr_1_99_ble": 13,
    "KG Winter_ss_ble": 83,
    "KG Winter_dss_ble": 420,
    "KG Spring_theta_ble": -0.409706844,
    "KG Spring_pr_1_99_ble": 13,
    "KG Spring_ss_ble": 83,
    "KG Spring_dss_ble": 459
  },
  {
    "ID": 65,
    "KG Fall_theta_ble": -1.256850051,
    "KG Fall_pr_1_99_ble": 13,
    "KG Fall_ss_ble": 83,
    "KG Fall_dss_ble": 374,
    "KG Winter_theta_ble": -0.784995733,
    "KG Winter_pr_1_99_ble": 13,
    "KG Winter_ss_ble": 84,
    "KG Winter_dss_ble": 422,
    "KG Spring_theta_ble": -0.404840171,
    "KG Spring_pr_1_99_ble": 13,
    "KG Spring_ss_ble": 83,
    "KG Spring_dss_ble": 460
  },
  {
    "ID": 66,
    "KG Fall_theta_ble": -1.251436038,
    "KG Fall_pr_1_99_ble": 13,
    "KG Fall_ss_ble": 83,
    "KG Fall_dss_ble": 375,
    "KG Winter_theta_ble": -0.772441474,
    "KG Winter_pr_1_99_ble": 13,
    "KG Winter_ss_ble": 84,
    "KG Winter_dss_ble": 423,
    "KG Spring_theta_ble": -0.383740068,
    "KG Spring_pr_1_99_ble": 13,
    "KG Spring_ss_ble": 83,
    "KG Spring_dss_ble": 462
  },
  {
    "ID": 67,
    "KG Fall_theta_ble": -1.229804365,
    "KG Fall_pr_1_99_ble": 13,
    "KG Fall_ss_ble": 83,
    "KG Fall_dss_ble": 377,
    "KG Winter_theta_ble": -0.770140459,
    "KG Winter_pr_1_99_ble": 13,
    "KG Winter_ss_ble": 84,
    "KG Winter_dss_ble": 423,
    "KG Spring_theta_ble": -0.379372635,
    "KG Spring_pr_1_99_ble": 13,
    "KG Spring_ss_ble": 84,
    "KG Spring_dss_ble": 462
  },
  {
    "ID": 68,
    "KG Fall_theta_ble": -1.220226139,
    "KG Fall_pr_1_99_ble": 14,
    "KG Fall_ss_ble": 84,
    "KG Fall_dss_ble": 378,
    "KG Winter_theta_ble": -0.765108053,
    "KG Winter_pr_1_99_ble": 14,
    "KG Winter_ss_ble": 84,
    "KG Winter_dss_ble": 423,
    "KG Spring_theta_ble": -0.37314461,
    "KG Spring_pr_1_99_ble": 14,
    "KG Spring_ss_ble": 84,
    "KG Spring_dss_ble": 463
  },
  {
    "ID": 69,
    "KG Fall_theta_ble": -1.213333235,
    "KG Fall_pr_1_99_ble": 14,
    "KG Fall_ss_ble": 84,
    "KG Fall_dss_ble": 379,
    "KG Winter_theta_ble": -0.75723818,
    "KG Winter_pr_1_99_ble": 14,
    "KG Winter_ss_ble": 84,
    "KG Winter_dss_ble": 424,
    "KG Spring_theta_ble": -0.368658495,
    "KG Spring_pr_1_99_ble": 14,
    "KG Spring_ss_ble": 84,
    "KG Spring_dss_ble": 463
  },
  {
    "ID": 70,
    "KG Fall_theta_ble": -1.211583931,
    "KG Fall_pr_1_99_ble": 14,
    "KG Fall_ss_ble": 84,
    "KG Fall_dss_ble": 379,
    "KG Winter_theta_ble": -0.736496503,
    "KG Winter_pr_1_99_ble": 14,
    "KG Winter_ss_ble": 84,
    "KG Winter_dss_ble": 426,
    "KG Spring_theta_ble": -0.367431982,
    "KG Spring_pr_1_99_ble": 14,
    "KG Spring_ss_ble": 84,
    "KG Spring_dss_ble": 463
  },
  {
    "ID": 71,
    "KG Fall_theta_ble": -1.192096233,
    "KG Fall_pr_1_99_ble": 14,
    "KG Fall_ss_ble": 84,
    "KG Fall_dss_ble": 381,
    "KG Winter_theta_ble": -0.731007963,
    "KG Winter_pr_1_99_ble": 14,
    "KG Winter_ss_ble": 85,
    "KG Winter_dss_ble": 427,
    "KG Spring_theta_ble": -0.357592662,
    "KG Spring_pr_1_99_ble": 14,
    "KG Spring_ss_ble": 84,
    "KG Spring_dss_ble": 464
  },
  {
    "ID": 72,
    "KG Fall_theta_ble": -1.190234174,
    "KG Fall_pr_1_99_ble": 14,
    "KG Fall_ss_ble": 84,
    "KG Fall_dss_ble": 381,
    "KG Winter_theta_ble": -0.720764794,
    "KG Winter_pr_1_99_ble": 14,
    "KG Winter_ss_ble": 85,
    "KG Winter_dss_ble": 428,
    "KG Spring_theta_ble": -0.350619353,
    "KG Spring_pr_1_99_ble": 14,
    "KG Spring_ss_ble": 84,
    "KG Spring_dss_ble": 465
  },
  {
    "ID": 73,
    "KG Fall_theta_ble": -1.177082234,
    "KG Fall_pr_1_99_ble": 15,
    "KG Fall_ss_ble": 84,
    "KG Fall_dss_ble": 382,
    "KG Winter_theta_ble": -0.719785443,
    "KG Winter_pr_1_99_ble": 15,
    "KG Winter_ss_ble": 85,
    "KG Winter_dss_ble": 428,
    "KG Spring_theta_ble": -0.350369777,
    "KG Spring_pr_1_99_ble": 15,
    "KG Spring_ss_ble": 84,
    "KG Spring_dss_ble": 465
  },
  {
    "ID": 74,
    "KG Fall_theta_ble": -1.173743442,
    "KG Fall_pr_1_99_ble": 15,
    "KG Fall_ss_ble": 84,
    "KG Fall_dss_ble": 383,
    "KG Winter_theta_ble": -0.71856283,
    "KG Winter_pr_1_99_ble": 15,
    "KG Winter_ss_ble": 85,
    "KG Winter_dss_ble": 428,
    "KG Spring_theta_ble": -0.345093436,
    "KG Spring_pr_1_99_ble": 15,
    "KG Spring_ss_ble": 84,
    "KG Spring_dss_ble": 465
  },
  {
    "ID": 75,
    "KG Fall_theta_ble": -1.159384076,
    "KG Fall_pr_1_99_ble": 15,
    "KG Fall_ss_ble": 85,
    "KG Fall_dss_ble": 384,
    "KG Winter_theta_ble": -0.717446071,
    "KG Winter_pr_1_99_ble": 15,
    "KG Winter_ss_ble": 85,
    "KG Winter_dss_ble": 428,
    "KG Spring_theta_ble": -0.341660907,
    "KG Spring_pr_1_99_ble": 15,
    "KG Spring_ss_ble": 84,
    "KG Spring_dss_ble": 466
  },
  {
    "ID": 76,
    "KG Fall_theta_ble": -1.125027281,
    "KG Fall_pr_1_99_ble": 15,
    "KG Fall_ss_ble": 85,
    "KG Fall_dss_ble": 387,
    "KG Winter_theta_ble": -0.716031804,
    "KG Winter_pr_1_99_ble": 15,
    "KG Winter_ss_ble": 85,
    "KG Winter_dss_ble": 428,
    "KG Spring_theta_ble": -0.339572182,
    "KG Spring_pr_1_99_ble": 15,
    "KG Spring_ss_ble": 84,
    "KG Spring_dss_ble": 466
  },
  {
    "ID": 77,
    "KG Fall_theta_ble": -1.12479647,
    "KG Fall_pr_1_99_ble": 15,
    "KG Fall_ss_ble": 85,
    "KG Fall_dss_ble": 388,
    "KG Winter_theta_ble": -0.709988935,
    "KG Winter_pr_1_99_ble": 15,
    "KG Winter_ss_ble": 85,
    "KG Winter_dss_ble": 429,
    "KG Spring_theta_ble": -0.329982617,
    "KG Spring_pr_1_99_ble": 15,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 467
  },
  {
    "ID": 78,
    "KG Fall_theta_ble": -1.120182254,
    "KG Fall_pr_1_99_ble": 16,
    "KG Fall_ss_ble": 85,
    "KG Fall_dss_ble": 388,
    "KG Winter_theta_ble": -0.709181678,
    "KG Winter_pr_1_99_ble": 16,
    "KG Winter_ss_ble": 85,
    "KG Winter_dss_ble": 429,
    "KG Spring_theta_ble": -0.326923598,
    "KG Spring_pr_1_99_ble": 16,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 467
  },
  {
    "ID": 79,
    "KG Fall_theta_ble": -1.11824212,
    "KG Fall_pr_1_99_ble": 16,
    "KG Fall_ss_ble": 85,
    "KG Fall_dss_ble": 388,
    "KG Winter_theta_ble": -0.696533351,
    "KG Winter_pr_1_99_ble": 16,
    "KG Winter_ss_ble": 85,
    "KG Winter_dss_ble": 430,
    "KG Spring_theta_ble": -0.318875666,
    "KG Spring_pr_1_99_ble": 16,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 468
  },
  {
    "ID": 80,
    "KG Fall_theta_ble": -1.117986698,
    "KG Fall_pr_1_99_ble": 16,
    "KG Fall_ss_ble": 85,
    "KG Fall_dss_ble": 388,
    "KG Winter_theta_ble": -0.696434216,
    "KG Winter_pr_1_99_ble": 16,
    "KG Winter_ss_ble": 85,
    "KG Winter_dss_ble": 430,
    "KG Spring_theta_ble": -0.317049018,
    "KG Spring_pr_1_99_ble": 16,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 468
  },
  {
    "ID": 81,
    "KG Fall_theta_ble": -1.08959823,
    "KG Fall_pr_1_99_ble": 16,
    "KG Fall_ss_ble": 86,
    "KG Fall_dss_ble": 391,
    "KG Winter_theta_ble": -0.675737345,
    "KG Winter_pr_1_99_ble": 16,
    "KG Winter_ss_ble": 86,
    "KG Winter_dss_ble": 432,
    "KG Spring_theta_ble": -0.311288971,
    "KG Spring_pr_1_99_ble": 16,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 469
  },
  {
    "ID": 82,
    "KG Fall_theta_ble": -1.084947104,
    "KG Fall_pr_1_99_ble": 16,
    "KG Fall_ss_ble": 86,
    "KG Fall_dss_ble": 392,
    "KG Winter_theta_ble": -0.671318251,
    "KG Winter_pr_1_99_ble": 16,
    "KG Winter_ss_ble": 86,
    "KG Winter_dss_ble": 433,
    "KG Spring_theta_ble": -0.308746428,
    "KG Spring_pr_1_99_ble": 16,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 469
  },
  {
    "ID": 83,
    "KG Fall_theta_ble": -1.08312653,
    "KG Fall_pr_1_99_ble": 17,
    "KG Fall_ss_ble": 86,
    "KG Fall_dss_ble": 392,
    "KG Winter_theta_ble": -0.670125487,
    "KG Winter_pr_1_99_ble": 17,
    "KG Winter_ss_ble": 86,
    "KG Winter_dss_ble": 433,
    "KG Spring_theta_ble": -0.308140221,
    "KG Spring_pr_1_99_ble": 17,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 469
  },
  {
    "ID": 84,
    "KG Fall_theta_ble": -1.079659066,
    "KG Fall_pr_1_99_ble": 17,
    "KG Fall_ss_ble": 86,
    "KG Fall_dss_ble": 392,
    "KG Winter_theta_ble": -0.657358763,
    "KG Winter_pr_1_99_ble": 17,
    "KG Winter_ss_ble": 86,
    "KG Winter_dss_ble": 434,
    "KG Spring_theta_ble": -0.303182045,
    "KG Spring_pr_1_99_ble": 17,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 470
  },
  {
    "ID": 85,
    "KG Fall_theta_ble": -1.078728327,
    "KG Fall_pr_1_99_ble": 17,
    "KG Fall_ss_ble": 86,
    "KG Fall_dss_ble": 392,
    "KG Winter_theta_ble": -0.654142933,
    "KG Winter_pr_1_99_ble": 17,
    "KG Winter_ss_ble": 86,
    "KG Winter_dss_ble": 435,
    "KG Spring_theta_ble": -0.300960181,
    "KG Spring_pr_1_99_ble": 17,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 470
  },
  {
    "ID": 86,
    "KG Fall_theta_ble": -1.05993881,
    "KG Fall_pr_1_99_ble": 17,
    "KG Fall_ss_ble": 86,
    "KG Fall_dss_ble": 394,
    "KG Winter_theta_ble": -0.64355776,
    "KG Winter_pr_1_99_ble": 17,
    "KG Winter_ss_ble": 86,
    "KG Winter_dss_ble": 436,
    "KG Spring_theta_ble": -0.29803129,
    "KG Spring_pr_1_99_ble": 17,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 470
  },
  {
    "ID": 87,
    "KG Fall_theta_ble": -1.059609893,
    "KG Fall_pr_1_99_ble": 17,
    "KG Fall_ss_ble": 86,
    "KG Fall_dss_ble": 394,
    "KG Winter_theta_ble": -0.63522933,
    "KG Winter_pr_1_99_ble": 17,
    "KG Winter_ss_ble": 86,
    "KG Winter_dss_ble": 436,
    "KG Spring_theta_ble": -0.291797835,
    "KG Spring_pr_1_99_ble": 17,
    "KG Spring_ss_ble": 85,
    "KG Spring_dss_ble": 471
  },
  {
    "ID": 88,
    "KG Fall_theta_ble": -1.055753312,
    "KG Fall_pr_1_99_ble": 18,
    "KG Fall_ss_ble": 86,
    "KG Fall_dss_ble": 394,
    "KG Winter_theta_ble": -0.629837779,
    "KG Winter_pr_1_99_ble": 18,
    "KG Winter_ss_ble": 86,
    "KG Winter_dss_ble": 437,
    "KG Spring_theta_ble": -0.279515,
    "KG Spring_pr_1_99_ble": 18,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 472
  },
  {
    "ID": 89,
    "KG Fall_theta_ble": -1.035962928,
    "KG Fall_pr_1_99_ble": 18,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 396,
    "KG Winter_theta_ble": -0.623206514,
    "KG Winter_pr_1_99_ble": 18,
    "KG Winter_ss_ble": 86,
    "KG Winter_dss_ble": 438,
    "KG Spring_theta_ble": -0.276010851,
    "KG Spring_pr_1_99_ble": 18,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 472
  },
  {
    "ID": 90,
    "KG Fall_theta_ble": -1.033950639,
    "KG Fall_pr_1_99_ble": 18,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 397,
    "KG Winter_theta_ble": -0.61871922,
    "KG Winter_pr_1_99_ble": 18,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 438,
    "KG Spring_theta_ble": -0.27524572,
    "KG Spring_pr_1_99_ble": 18,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 472
  },
  {
    "ID": 91,
    "KG Fall_theta_ble": -1.033555734,
    "KG Fall_pr_1_99_ble": 18,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 397,
    "KG Winter_theta_ble": -0.61125709,
    "KG Winter_pr_1_99_ble": 18,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 439,
    "KG Spring_theta_ble": -0.270915936,
    "KG Spring_pr_1_99_ble": 18,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 473
  },
  {
    "ID": 92,
    "KG Fall_theta_ble": -1.031771158,
    "KG Fall_pr_1_99_ble": 18,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 397,
    "KG Winter_theta_ble": -0.609111001,
    "KG Winter_pr_1_99_ble": 18,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 439,
    "KG Spring_theta_ble": -0.266268705,
    "KG Spring_pr_1_99_ble": 18,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 473
  },
  {
    "ID": 93,
    "KG Fall_theta_ble": -1.029323388,
    "KG Fall_pr_1_99_ble": 19,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 397,
    "KG Winter_theta_ble": -0.607648238,
    "KG Winter_pr_1_99_ble": 19,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 439,
    "KG Spring_theta_ble": -0.260935333,
    "KG Spring_pr_1_99_ble": 19,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 474
  },
  {
    "ID": 94,
    "KG Fall_theta_ble": -1.016318417,
    "KG Fall_pr_1_99_ble": 19,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 398,
    "KG Winter_theta_ble": -0.603040978,
    "KG Winter_pr_1_99_ble": 19,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 440,
    "KG Spring_theta_ble": -0.259592799,
    "KG Spring_pr_1_99_ble": 19,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 474
  },
  {
    "ID": 95,
    "KG Fall_theta_ble": -1.013267174,
    "KG Fall_pr_1_99_ble": 19,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 399,
    "KG Winter_theta_ble": -0.599685312,
    "KG Winter_pr_1_99_ble": 19,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 440,
    "KG Spring_theta_ble": -0.257388056,
    "KG Spring_pr_1_99_ble": 19,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 474
  },
  {
    "ID": 96,
    "KG Fall_theta_ble": -1.010592077,
    "KG Fall_pr_1_99_ble": 19,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 399,
    "KG Winter_theta_ble": -0.59628964,
    "KG Winter_pr_1_99_ble": 19,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 440,
    "KG Spring_theta_ble": -0.251932443,
    "KG Spring_pr_1_99_ble": 19,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 475
  },
  {
    "ID": 97,
    "KG Fall_theta_ble": -1.008637426,
    "KG Fall_pr_1_99_ble": 19,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 399,
    "KG Winter_theta_ble": -0.595064117,
    "KG Winter_pr_1_99_ble": 19,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 440,
    "KG Spring_theta_ble": -0.235951118,
    "KG Spring_pr_1_99_ble": 19,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 476
  },
  {
    "ID": 98,
    "KG Fall_theta_ble": -1.008188589,
    "KG Fall_pr_1_99_ble": 20,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 399,
    "KG Winter_theta_ble": -0.594785912,
    "KG Winter_pr_1_99_ble": 20,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 441,
    "KG Spring_theta_ble": -0.235301092,
    "KG Spring_pr_1_99_ble": 20,
    "KG Spring_ss_ble": 86,
    "KG Spring_dss_ble": 476
  },
  {
    "ID": 99,
    "KG Fall_theta_ble": -1.007305609,
    "KG Fall_pr_1_99_ble": 20,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 399,
    "KG Winter_theta_ble": -0.594592709,
    "KG Winter_pr_1_99_ble": 20,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 441,
    "KG Spring_theta_ble": -0.228198772,
    "KG Spring_pr_1_99_ble": 20,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 477
  },
  {
    "ID": 100,
    "KG Fall_theta_ble": -1.005232165,
    "KG Fall_pr_1_99_ble": 20,
    "KG Fall_ss_ble": 87,
    "KG Fall_dss_ble": 399,
    "KG Winter_theta_ble": -0.593716484,
    "KG Winter_pr_1_99_ble": 20,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 441,
    "KG Spring_theta_ble": -0.21864747,
    "KG Spring_pr_1_99_ble": 20,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 478
  },
  {
    "ID": 101,
    "KG Fall_theta_ble": -0.980231052,
    "KG Fall_pr_1_99_ble": 20,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 402,
    "KG Winter_theta_ble": -0.564459213,
    "KG Winter_pr_1_99_ble": 20,
    "KG Winter_ss_ble": 87,
    "KG Winter_dss_ble": 444,
    "KG Spring_theta_ble": -0.214322386,
    "KG Spring_pr_1_99_ble": 20,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 479
  },
  {
    "ID": 102,
    "KG Fall_theta_ble": -0.973447875,
    "KG Fall_pr_1_99_ble": 20,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 403,
    "KG Winter_theta_ble": -0.557918201,
    "KG Winter_pr_1_99_ble": 20,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 444,
    "KG Spring_theta_ble": -0.204596707,
    "KG Spring_pr_1_99_ble": 20,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 480
  },
  {
    "ID": 103,
    "KG Fall_theta_ble": -0.969953699,
    "KG Fall_pr_1_99_ble": 21,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 403,
    "KG Winter_theta_ble": -0.553216442,
    "KG Winter_pr_1_99_ble": 21,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 445,
    "KG Spring_theta_ble": -0.202023042,
    "KG Spring_pr_1_99_ble": 21,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 480
  },
  {
    "ID": 104,
    "KG Fall_theta_ble": -0.968040676,
    "KG Fall_pr_1_99_ble": 21,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 403,
    "KG Winter_theta_ble": -0.549406945,
    "KG Winter_pr_1_99_ble": 21,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 445,
    "KG Spring_theta_ble": -0.195764486,
    "KG Spring_pr_1_99_ble": 21,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 480
  },
  {
    "ID": 105,
    "KG Fall_theta_ble": -0.964002835,
    "KG Fall_pr_1_99_ble": 21,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 404,
    "KG Winter_theta_ble": -0.537763197,
    "KG Winter_pr_1_99_ble": 21,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 446,
    "KG Spring_theta_ble": -0.194546505,
    "KG Spring_pr_1_99_ble": 21,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 481
  },
  {
    "ID": 106,
    "KG Fall_theta_ble": -0.95635704,
    "KG Fall_pr_1_99_ble": 21,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 404,
    "KG Winter_theta_ble": -0.5339974,
    "KG Winter_pr_1_99_ble": 21,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 447,
    "KG Spring_theta_ble": -0.186741816,
    "KG Spring_pr_1_99_ble": 21,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 481
  },
  {
    "ID": 107,
    "KG Fall_theta_ble": -0.952907903,
    "KG Fall_pr_1_99_ble": 21,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 405,
    "KG Winter_theta_ble": -0.527413719,
    "KG Winter_pr_1_99_ble": 21,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 447,
    "KG Spring_theta_ble": -0.186537826,
    "KG Spring_pr_1_99_ble": 21,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 481
  },
  {
    "ID": 108,
    "KG Fall_theta_ble": -0.948318674,
    "KG Fall_pr_1_99_ble": 22,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 405,
    "KG Winter_theta_ble": -0.5235466,
    "KG Winter_pr_1_99_ble": 22,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 448,
    "KG Spring_theta_ble": -0.183932111,
    "KG Spring_pr_1_99_ble": 22,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 482
  },
  {
    "ID": 109,
    "KG Fall_theta_ble": -0.9467747,
    "KG Fall_pr_1_99_ble": 22,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 405,
    "KG Winter_theta_ble": -0.523415334,
    "KG Winter_pr_1_99_ble": 22,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 448,
    "KG Spring_theta_ble": -0.182688607,
    "KG Spring_pr_1_99_ble": 22,
    "KG Spring_ss_ble": 87,
    "KG Spring_dss_ble": 482
  },
  {
    "ID": 110,
    "KG Fall_theta_ble": -0.943973466,
    "KG Fall_pr_1_99_ble": 22,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 406,
    "KG Winter_theta_ble": -0.519370286,
    "KG Winter_pr_1_99_ble": 22,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 448,
    "KG Spring_theta_ble": -0.167987781,
    "KG Spring_pr_1_99_ble": 22,
    "KG Spring_ss_ble": 88,
    "KG Spring_dss_ble": 483
  },
  {
    "ID": 111,
    "KG Fall_theta_ble": -0.942731341,
    "KG Fall_pr_1_99_ble": 22,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 406,
    "KG Winter_theta_ble": -0.517299535,
    "KG Winter_pr_1_99_ble": 22,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 448,
    "KG Spring_theta_ble": -0.158914787,
    "KG Spring_pr_1_99_ble": 22,
    "KG Spring_ss_ble": 88,
    "KG Spring_dss_ble": 484
  },
  {
    "ID": 112,
    "KG Fall_theta_ble": -0.941924298,
    "KG Fall_pr_1_99_ble": 22,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 406,
    "KG Winter_theta_ble": -0.511318739,
    "KG Winter_pr_1_99_ble": 22,
    "KG Winter_ss_ble": 88,
    "KG Winter_dss_ble": 449,
    "KG Spring_theta_ble": -0.157608602,
    "KG Spring_pr_1_99_ble": 22,
    "KG Spring_ss_ble": 88,
    "KG Spring_dss_ble": 484
  },
  {
    "ID": 113,
    "KG Fall_theta_ble": -0.941274357,
    "KG Fall_pr_1_99_ble": 23,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 406,
    "KG Winter_theta_ble": -0.503455963,
    "KG Winter_pr_1_99_ble": 23,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 450,
    "KG Spring_theta_ble": -0.149977762,
    "KG Spring_pr_1_99_ble": 23,
    "KG Spring_ss_ble": 88,
    "KG Spring_dss_ble": 485
  },
  {
    "ID": 114,
    "KG Fall_theta_ble": -0.937205417,
    "KG Fall_pr_1_99_ble": 23,
    "KG Fall_ss_ble": 88,
    "KG Fall_dss_ble": 406,
    "KG Winter_theta_ble": -0.502561545,
    "KG Winter_pr_1_99_ble": 23,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 450,
    "KG Spring_theta_ble": -0.146984741,
    "KG Spring_pr_1_99_ble": 23,
    "KG Spring_ss_ble": 88,
    "KG Spring_dss_ble": 485
  },
  {
    "ID": 115,
    "KG Fall_theta_ble": -0.930998577,
    "KG Fall_pr_1_99_ble": 23,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 407,
    "KG Winter_theta_ble": -0.496587629,
    "KG Winter_pr_1_99_ble": 23,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 450,
    "KG Spring_theta_ble": -0.146199895,
    "KG Spring_pr_1_99_ble": 23,
    "KG Spring_ss_ble": 88,
    "KG Spring_dss_ble": 485
  },
  {
    "ID": 116,
    "KG Fall_theta_ble": -0.919750774,
    "KG Fall_pr_1_99_ble": 23,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 408,
    "KG Winter_theta_ble": -0.493808455,
    "KG Winter_pr_1_99_ble": 23,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 451,
    "KG Spring_theta_ble": -0.140899632,
    "KG Spring_pr_1_99_ble": 23,
    "KG Spring_ss_ble": 88,
    "KG Spring_dss_ble": 486
  },
  {
    "ID": 117,
    "KG Fall_theta_ble": -0.914777166,
    "KG Fall_pr_1_99_ble": 23,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 409,
    "KG Winter_theta_ble": -0.493053108,
    "KG Winter_pr_1_99_ble": 23,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 451,
    "KG Spring_theta_ble": -0.135928867,
    "KG Spring_pr_1_99_ble": 23,
    "KG Spring_ss_ble": 88,
    "KG Spring_dss_ble": 486
  },
  {
    "ID": 118,
    "KG Fall_theta_ble": -0.913873104,
    "KG Fall_pr_1_99_ble": 24,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 409,
    "KG Winter_theta_ble": -0.491515577,
    "KG Winter_pr_1_99_ble": 24,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 451,
    "KG Spring_theta_ble": -0.123216881,
    "KG Spring_pr_1_99_ble": 24,
    "KG Spring_ss_ble": 89,
    "KG Spring_dss_ble": 488
  },
  {
    "ID": 119,
    "KG Fall_theta_ble": -0.910579894,
    "KG Fall_pr_1_99_ble": 24,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 409,
    "KG Winter_theta_ble": -0.488778033,
    "KG Winter_pr_1_99_ble": 24,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 451,
    "KG Spring_theta_ble": -0.104790594,
    "KG Spring_pr_1_99_ble": 24,
    "KG Spring_ss_ble": 89,
    "KG Spring_dss_ble": 490
  },
  {
    "ID": 120,
    "KG Fall_theta_ble": -0.908736093,
    "KG Fall_pr_1_99_ble": 24,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 409,
    "KG Winter_theta_ble": -0.487094403,
    "KG Winter_pr_1_99_ble": 24,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 451,
    "KG Spring_theta_ble": -0.096521871,
    "KG Spring_pr_1_99_ble": 24,
    "KG Spring_ss_ble": 89,
    "KG Spring_dss_ble": 490
  },
  {
    "ID": 121,
    "KG Fall_theta_ble": -0.903688808,
    "KG Fall_pr_1_99_ble": 24,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 410,
    "KG Winter_theta_ble": -0.484545106,
    "KG Winter_pr_1_99_ble": 24,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 452,
    "KG Spring_theta_ble": -0.092625041,
    "KG Spring_pr_1_99_ble": 24,
    "KG Spring_ss_ble": 89,
    "KG Spring_dss_ble": 491
  },
  {
    "ID": 122,
    "KG Fall_theta_ble": -0.898523269,
    "KG Fall_pr_1_99_ble": 24,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 410,
    "KG Winter_theta_ble": -0.475741288,
    "KG Winter_pr_1_99_ble": 24,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 452,
    "KG Spring_theta_ble": -0.08921215,
    "KG Spring_pr_1_99_ble": 24,
    "KG Spring_ss_ble": 89,
    "KG Spring_dss_ble": 491
  },
  {
    "ID": 123,
    "KG Fall_theta_ble": -0.897347204,
    "KG Fall_pr_1_99_ble": 25,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 410,
    "KG Winter_theta_ble": -0.473798246,
    "KG Winter_pr_1_99_ble": 25,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 453,
    "KG Spring_theta_ble": -0.07865768,
    "KG Spring_pr_1_99_ble": 25,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 492
  },
  {
    "ID": 124,
    "KG Fall_theta_ble": -0.894677223,
    "KG Fall_pr_1_99_ble": 25,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 411,
    "KG Winter_theta_ble": -0.471720332,
    "KG Winter_pr_1_99_ble": 25,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 453,
    "KG Spring_theta_ble": -0.075703175,
    "KG Spring_pr_1_99_ble": 25,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 492
  },
  {
    "ID": 125,
    "KG Fall_theta_ble": -0.886591562,
    "KG Fall_pr_1_99_ble": 25,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 411,
    "KG Winter_theta_ble": -0.468431225,
    "KG Winter_pr_1_99_ble": 25,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 453,
    "KG Spring_theta_ble": -0.065618322,
    "KG Spring_pr_1_99_ble": 25,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 493
  },
  {
    "ID": 126,
    "KG Fall_theta_ble": -0.875920409,
    "KG Fall_pr_1_99_ble": 25,
    "KG Fall_ss_ble": 89,
    "KG Fall_dss_ble": 412,
    "KG Winter_theta_ble": -0.461660938,
    "KG Winter_pr_1_99_ble": 25,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 454,
    "KG Spring_theta_ble": -0.06544643,
    "KG Spring_pr_1_99_ble": 25,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 493
  },
  {
    "ID": 127,
    "KG Fall_theta_ble": -0.869277904,
    "KG Fall_pr_1_99_ble": 25,
    "KG Fall_ss_ble": 90,
    "KG Fall_dss_ble": 413,
    "KG Winter_theta_ble": -0.456682362,
    "KG Winter_pr_1_99_ble": 25,
    "KG Winter_ss_ble": 89,
    "KG Winter_dss_ble": 454,
    "KG Spring_theta_ble": -0.064243203,
    "KG Spring_pr_1_99_ble": 25,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 494
  },
  {
    "ID": 128,
    "KG Fall_theta_ble": -0.862016917,
    "KG Fall_pr_1_99_ble": 26,
    "KG Fall_ss_ble": 90,
    "KG Fall_dss_ble": 414,
    "KG Winter_theta_ble": -0.451312152,
    "KG Winter_pr_1_99_ble": 26,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 455,
    "KG Spring_theta_ble": -0.057526808,
    "KG Spring_pr_1_99_ble": 26,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 494
  },
  {
    "ID": 129,
    "KG Fall_theta_ble": -0.859301056,
    "KG Fall_pr_1_99_ble": 26,
    "KG Fall_ss_ble": 90,
    "KG Fall_dss_ble": 414,
    "KG Winter_theta_ble": -0.446931604,
    "KG Winter_pr_1_99_ble": 26,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 455,
    "KG Spring_theta_ble": -0.04999845,
    "KG Spring_pr_1_99_ble": 26,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 495
  },
  {
    "ID": 130,
    "KG Fall_theta_ble": -0.857321876,
    "KG Fall_pr_1_99_ble": 26,
    "KG Fall_ss_ble": 90,
    "KG Fall_dss_ble": 414,
    "KG Winter_theta_ble": -0.446716272,
    "KG Winter_pr_1_99_ble": 26,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 455,
    "KG Spring_theta_ble": -0.04334695,
    "KG Spring_pr_1_99_ble": 26,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 496
  },
  {
    "ID": 131,
    "KG Fall_theta_ble": -0.844908405,
    "KG Fall_pr_1_99_ble": 26,
    "KG Fall_ss_ble": 90,
    "KG Fall_dss_ble": 416,
    "KG Winter_theta_ble": -0.442800302,
    "KG Winter_pr_1_99_ble": 26,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 456,
    "KG Spring_theta_ble": -0.041651846,
    "KG Spring_pr_1_99_ble": 26,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 496
  },
  {
    "ID": 132,
    "KG Fall_theta_ble": -0.835200787,
    "KG Fall_pr_1_99_ble": 26,
    "KG Fall_ss_ble": 90,
    "KG Fall_dss_ble": 416,
    "KG Winter_theta_ble": -0.43968477,
    "KG Winter_pr_1_99_ble": 26,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 456,
    "KG Spring_theta_ble": -0.040941172,
    "KG Spring_pr_1_99_ble": 26,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 496
  },
  {
    "ID": 133,
    "KG Fall_theta_ble": -0.834637308,
    "KG Fall_pr_1_99_ble": 27,
    "KG Fall_ss_ble": 90,
    "KG Fall_dss_ble": 417,
    "KG Winter_theta_ble": -0.438279215,
    "KG Winter_pr_1_99_ble": 27,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 456,
    "KG Spring_theta_ble": -0.035084982,
    "KG Spring_pr_1_99_ble": 27,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 496
  },
  {
    "ID": 134,
    "KG Fall_theta_ble": -0.811882423,
    "KG Fall_pr_1_99_ble": 27,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 419,
    "KG Winter_theta_ble": -0.434805292,
    "KG Winter_pr_1_99_ble": 27,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 457,
    "KG Spring_theta_ble": -0.032242934,
    "KG Spring_pr_1_99_ble": 27,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 497
  },
  {
    "ID": 135,
    "KG Fall_theta_ble": -0.799977708,
    "KG Fall_pr_1_99_ble": 27,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 420,
    "KG Winter_theta_ble": -0.431573913,
    "KG Winter_pr_1_99_ble": 27,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 457,
    "KG Spring_theta_ble": -0.030821013,
    "KG Spring_pr_1_99_ble": 27,
    "KG Spring_ss_ble": 90,
    "KG Spring_dss_ble": 497
  },
  {
    "ID": 136,
    "KG Fall_theta_ble": -0.798262602,
    "KG Fall_pr_1_99_ble": 27,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 420,
    "KG Winter_theta_ble": -0.430346914,
    "KG Winter_pr_1_99_ble": 27,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 457,
    "KG Spring_theta_ble": -0.028980694,
    "KG Spring_pr_1_99_ble": 27,
    "KG Spring_ss_ble": 91,
    "KG Spring_dss_ble": 497
  },
  {
    "ID": 137,
    "KG Fall_theta_ble": -0.79823591,
    "KG Fall_pr_1_99_ble": 27,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 420,
    "KG Winter_theta_ble": -0.412035958,
    "KG Winter_pr_1_99_ble": 27,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 459,
    "KG Spring_theta_ble": -0.022531637,
    "KG Spring_pr_1_99_ble": 27,
    "KG Spring_ss_ble": 91,
    "KG Spring_dss_ble": 498
  },
  {
    "ID": 138,
    "KG Fall_theta_ble": -0.794283442,
    "KG Fall_pr_1_99_ble": 28,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 421,
    "KG Winter_theta_ble": -0.410920681,
    "KG Winter_pr_1_99_ble": 28,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 459,
    "KG Spring_theta_ble": -0.022175847,
    "KG Spring_pr_1_99_ble": 28,
    "KG Spring_ss_ble": 91,
    "KG Spring_dss_ble": 498
  },
  {
    "ID": 139,
    "KG Fall_theta_ble": -0.787376999,
    "KG Fall_pr_1_99_ble": 28,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 421,
    "KG Winter_theta_ble": -0.410140277,
    "KG Winter_pr_1_99_ble": 28,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 459,
    "KG Spring_theta_ble": -0.018045462,
    "KG Spring_pr_1_99_ble": 28,
    "KG Spring_ss_ble": 91,
    "KG Spring_dss_ble": 498
  },
  {
    "ID": 140,
    "KG Fall_theta_ble": -0.786956574,
    "KG Fall_pr_1_99_ble": 28,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 421,
    "KG Winter_theta_ble": -0.405457744,
    "KG Winter_pr_1_99_ble": 28,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 459,
    "KG Spring_theta_ble": -0.000791712,
    "KG Spring_pr_1_99_ble": 28,
    "KG Spring_ss_ble": 91,
    "KG Spring_dss_ble": 500
  },
  {
    "ID": 141,
    "KG Fall_theta_ble": -0.785083005,
    "KG Fall_pr_1_99_ble": 28,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 421,
    "KG Winter_theta_ble": -0.404133282,
    "KG Winter_pr_1_99_ble": 28,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 460,
    "KG Spring_theta_ble": 0.005214133,
    "KG Spring_pr_1_99_ble": 28,
    "KG Spring_ss_ble": 91,
    "KG Spring_dss_ble": 501
  },
  {
    "ID": 142,
    "KG Fall_theta_ble": -0.78415171,
    "KG Fall_pr_1_99_ble": 28,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 422,
    "KG Winter_theta_ble": -0.397470874,
    "KG Winter_pr_1_99_ble": 28,
    "KG Winter_ss_ble": 90,
    "KG Winter_dss_ble": 460,
    "KG Spring_theta_ble": 0.006815439,
    "KG Spring_pr_1_99_ble": 28,
    "KG Spring_ss_ble": 91,
    "KG Spring_dss_ble": 501
  },
  {
    "ID": 143,
    "KG Fall_theta_ble": -0.780870573,
    "KG Fall_pr_1_99_ble": 29,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 422,
    "KG Winter_theta_ble": -0.390484951,
    "KG Winter_pr_1_99_ble": 29,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 461,
    "KG Spring_theta_ble": 0.007960909,
    "KG Spring_pr_1_99_ble": 29,
    "KG Spring_ss_ble": 91,
    "KG Spring_dss_ble": 501
  },
  {
    "ID": 144,
    "KG Fall_theta_ble": -0.780147846,
    "KG Fall_pr_1_99_ble": 29,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 422,
    "KG Winter_theta_ble": -0.383630631,
    "KG Winter_pr_1_99_ble": 29,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 462,
    "KG Spring_theta_ble": 0.007974873,
    "KG Spring_pr_1_99_ble": 29,
    "KG Spring_ss_ble": 91,
    "KG Spring_dss_ble": 501
  },
  {
    "ID": 145,
    "KG Fall_theta_ble": -0.766932164,
    "KG Fall_pr_1_99_ble": 29,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 423,
    "KG Winter_theta_ble": -0.378762464,
    "KG Winter_pr_1_99_ble": 29,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 462,
    "KG Spring_theta_ble": 0.014439408,
    "KG Spring_pr_1_99_ble": 29,
    "KG Spring_ss_ble": 91,
    "KG Spring_dss_ble": 501
  },
  {
    "ID": 146,
    "KG Fall_theta_ble": -0.761735927,
    "KG Fall_pr_1_99_ble": 29,
    "KG Fall_ss_ble": 91,
    "KG Fall_dss_ble": 424,
    "KG Winter_theta_ble": -0.375937828,
    "KG Winter_pr_1_99_ble": 29,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 462,
    "KG Spring_theta_ble": 0.020199187,
    "KG Spring_pr_1_99_ble": 29,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 502
  },
  {
    "ID": 147,
    "KG Fall_theta_ble": -0.748695112,
    "KG Fall_pr_1_99_ble": 29,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 425,
    "KG Winter_theta_ble": -0.375591407,
    "KG Winter_pr_1_99_ble": 29,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 462,
    "KG Spring_theta_ble": 0.022528985,
    "KG Spring_pr_1_99_ble": 29,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 502
  },
  {
    "ID": 148,
    "KG Fall_theta_ble": -0.748357333,
    "KG Fall_pr_1_99_ble": 30,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 425,
    "KG Winter_theta_ble": -0.372420303,
    "KG Winter_pr_1_99_ble": 30,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 463,
    "KG Spring_theta_ble": 0.0238127,
    "KG Spring_pr_1_99_ble": 30,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 502
  },
  {
    "ID": 149,
    "KG Fall_theta_ble": -0.747599903,
    "KG Fall_pr_1_99_ble": 30,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 425,
    "KG Winter_theta_ble": -0.364120681,
    "KG Winter_pr_1_99_ble": 30,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 464,
    "KG Spring_theta_ble": 0.029717129,
    "KG Spring_pr_1_99_ble": 30,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 503
  },
  {
    "ID": 150,
    "KG Fall_theta_ble": -0.744606416,
    "KG Fall_pr_1_99_ble": 30,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 426,
    "KG Winter_theta_ble": -0.357457627,
    "KG Winter_pr_1_99_ble": 30,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 464,
    "KG Spring_theta_ble": 0.032942421,
    "KG Spring_pr_1_99_ble": 30,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 503
  },
  {
    "ID": 151,
    "KG Fall_theta_ble": -0.734939104,
    "KG Fall_pr_1_99_ble": 30,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 427,
    "KG Winter_theta_ble": -0.351961597,
    "KG Winter_pr_1_99_ble": 30,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 465,
    "KG Spring_theta_ble": 0.045636176,
    "KG Spring_pr_1_99_ble": 30,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 505
  },
  {
    "ID": 152,
    "KG Fall_theta_ble": -0.733527397,
    "KG Fall_pr_1_99_ble": 30,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 427,
    "KG Winter_theta_ble": -0.351502674,
    "KG Winter_pr_1_99_ble": 30,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 465,
    "KG Spring_theta_ble": 0.046908249,
    "KG Spring_pr_1_99_ble": 30,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 505
  },
  {
    "ID": 153,
    "KG Fall_theta_ble": -0.730101409,
    "KG Fall_pr_1_99_ble": 31,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 427,
    "KG Winter_theta_ble": -0.34340911,
    "KG Winter_pr_1_99_ble": 31,
    "KG Winter_ss_ble": 91,
    "KG Winter_dss_ble": 466,
    "KG Spring_theta_ble": 0.04866017,
    "KG Spring_pr_1_99_ble": 31,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 505
  },
  {
    "ID": 154,
    "KG Fall_theta_ble": -0.729571114,
    "KG Fall_pr_1_99_ble": 31,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 427,
    "KG Winter_theta_ble": -0.33779873,
    "KG Winter_pr_1_99_ble": 31,
    "KG Winter_ss_ble": 92,
    "KG Winter_dss_ble": 466,
    "KG Spring_theta_ble": 0.050349619,
    "KG Spring_pr_1_99_ble": 31,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 505
  },
  {
    "ID": 155,
    "KG Fall_theta_ble": -0.729160419,
    "KG Fall_pr_1_99_ble": 31,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 427,
    "KG Winter_theta_ble": -0.329245421,
    "KG Winter_pr_1_99_ble": 31,
    "KG Winter_ss_ble": 92,
    "KG Winter_dss_ble": 467,
    "KG Spring_theta_ble": 0.058867823,
    "KG Spring_pr_1_99_ble": 31,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 506
  },
  {
    "ID": 156,
    "KG Fall_theta_ble": -0.719864808,
    "KG Fall_pr_1_99_ble": 31,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 428,
    "KG Winter_theta_ble": -0.326029504,
    "KG Winter_pr_1_99_ble": 31,
    "KG Winter_ss_ble": 92,
    "KG Winter_dss_ble": 467,
    "KG Spring_theta_ble": 0.067216648,
    "KG Spring_pr_1_99_ble": 31,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 507
  },
  {
    "ID": 157,
    "KG Fall_theta_ble": -0.716403745,
    "KG Fall_pr_1_99_ble": 31,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 428,
    "KG Winter_theta_ble": -0.322509638,
    "KG Winter_pr_1_99_ble": 31,
    "KG Winter_ss_ble": 92,
    "KG Winter_dss_ble": 468,
    "KG Spring_theta_ble": 0.068674013,
    "KG Spring_pr_1_99_ble": 31,
    "KG Spring_ss_ble": 92,
    "KG Spring_dss_ble": 507
  },
  {
    "ID": 158,
    "KG Fall_theta_ble": -0.71150689,
    "KG Fall_pr_1_99_ble": 32,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 429,
    "KG Winter_theta_ble": -0.32062113,
    "KG Winter_pr_1_99_ble": 32,
    "KG Winter_ss_ble": 92,
    "KG Winter_dss_ble": 468,
    "KG Spring_theta_ble": 0.075139461,
    "KG Spring_pr_1_99_ble": 32,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 508
  },
  {
    "ID": 159,
    "KG Fall_theta_ble": -0.711245251,
    "KG Fall_pr_1_99_ble": 32,
    "KG Fall_ss_ble": 92,
    "KG Fall_dss_ble": 429,
    "KG Winter_theta_ble": -0.312764114,
    "KG Winter_pr_1_99_ble": 32,
    "KG Winter_ss_ble": 92,
    "KG Winter_dss_ble": 469,
    "KG Spring_theta_ble": 0.075704459,
    "KG Spring_pr_1_99_ble": 32,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 508
  },
  {
    "ID": 160,
    "KG Fall_theta_ble": -0.69110149,
    "KG Fall_pr_1_99_ble": 32,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 431,
    "KG Winter_theta_ble": -0.301551774,
    "KG Winter_pr_1_99_ble": 32,
    "KG Winter_ss_ble": 92,
    "KG Winter_dss_ble": 470,
    "KG Spring_theta_ble": 0.078171599,
    "KG Spring_pr_1_99_ble": 32,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 508
  },
  {
    "ID": 161,
    "KG Fall_theta_ble": -0.686396385,
    "KG Fall_pr_1_99_ble": 32,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 431,
    "KG Winter_theta_ble": -0.299103153,
    "KG Winter_pr_1_99_ble": 32,
    "KG Winter_ss_ble": 92,
    "KG Winter_dss_ble": 470,
    "KG Spring_theta_ble": 0.078517245,
    "KG Spring_pr_1_99_ble": 32,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 508
  },
  {
    "ID": 162,
    "KG Fall_theta_ble": -0.68423346,
    "KG Fall_pr_1_99_ble": 32,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 432,
    "KG Winter_theta_ble": -0.296263859,
    "KG Winter_pr_1_99_ble": 32,
    "KG Winter_ss_ble": 92,
    "KG Winter_dss_ble": 470,
    "KG Spring_theta_ble": 0.089982159,
    "KG Spring_pr_1_99_ble": 32,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 509
  },
  {
    "ID": 163,
    "KG Fall_theta_ble": -0.680145549,
    "KG Fall_pr_1_99_ble": 33,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 432,
    "KG Winter_theta_ble": -0.290123199,
    "KG Winter_pr_1_99_ble": 33,
    "KG Winter_ss_ble": 92,
    "KG Winter_dss_ble": 471,
    "KG Spring_theta_ble": 0.093828653,
    "KG Spring_pr_1_99_ble": 33,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 509
  },
  {
    "ID": 164,
    "KG Fall_theta_ble": -0.678694721,
    "KG Fall_pr_1_99_ble": 33,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 432,
    "KG Winter_theta_ble": -0.280105317,
    "KG Winter_pr_1_99_ble": 33,
    "KG Winter_ss_ble": 93,
    "KG Winter_dss_ble": 472,
    "KG Spring_theta_ble": 0.09463777,
    "KG Spring_pr_1_99_ble": 33,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 509
  },
  {
    "ID": 165,
    "KG Fall_theta_ble": -0.673157472,
    "KG Fall_pr_1_99_ble": 33,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 433,
    "KG Winter_theta_ble": -0.27088911,
    "KG Winter_pr_1_99_ble": 33,
    "KG Winter_ss_ble": 93,
    "KG Winter_dss_ble": 473,
    "KG Spring_theta_ble": 0.095933429,
    "KG Spring_pr_1_99_ble": 33,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 510
  },
  {
    "ID": 166,
    "KG Fall_theta_ble": -0.673149834,
    "KG Fall_pr_1_99_ble": 33,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 433,
    "KG Winter_theta_ble": -0.270098344,
    "KG Winter_pr_1_99_ble": 33,
    "KG Winter_ss_ble": 93,
    "KG Winter_dss_ble": 473,
    "KG Spring_theta_ble": 0.097175613,
    "KG Spring_pr_1_99_ble": 33,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 510
  },
  {
    "ID": 167,
    "KG Fall_theta_ble": -0.670902178,
    "KG Fall_pr_1_99_ble": 33,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 433,
    "KG Winter_theta_ble": -0.262205369,
    "KG Winter_pr_1_99_ble": 33,
    "KG Winter_ss_ble": 93,
    "KG Winter_dss_ble": 474,
    "KG Spring_theta_ble": 0.098869709,
    "KG Spring_pr_1_99_ble": 33,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 510
  },
  {
    "ID": 168,
    "KG Fall_theta_ble": -0.662129776,
    "KG Fall_pr_1_99_ble": 34,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 434,
    "KG Winter_theta_ble": -0.25992811,
    "KG Winter_pr_1_99_ble": 34,
    "KG Winter_ss_ble": 93,
    "KG Winter_dss_ble": 474,
    "KG Spring_theta_ble": 0.103691655,
    "KG Spring_pr_1_99_ble": 34,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 510
  },
  {
    "ID": 169,
    "KG Fall_theta_ble": -0.659107728,
    "KG Fall_pr_1_99_ble": 34,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 434,
    "KG Winter_theta_ble": -0.257034241,
    "KG Winter_pr_1_99_ble": 34,
    "KG Winter_ss_ble": 93,
    "KG Winter_dss_ble": 474,
    "KG Spring_theta_ble": 0.105606087,
    "KG Spring_pr_1_99_ble": 34,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 511
  },
  {
    "ID": 170,
    "KG Fall_theta_ble": -0.657841696,
    "KG Fall_pr_1_99_ble": 34,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 434,
    "KG Winter_theta_ble": -0.250736352,
    "KG Winter_pr_1_99_ble": 34,
    "KG Winter_ss_ble": 93,
    "KG Winter_dss_ble": 475,
    "KG Spring_theta_ble": 0.110947815,
    "KG Spring_pr_1_99_ble": 34,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 511
  },
  {
    "ID": 171,
    "KG Fall_theta_ble": -0.651712086,
    "KG Fall_pr_1_99_ble": 34,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 435,
    "KG Winter_theta_ble": -0.242878791,
    "KG Winter_pr_1_99_ble": 34,
    "KG Winter_ss_ble": 93,
    "KG Winter_dss_ble": 476,
    "KG Spring_theta_ble": 0.112858568,
    "KG Spring_pr_1_99_ble": 34,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 511
  },
  {
    "ID": 172,
    "KG Fall_theta_ble": -0.649444234,
    "KG Fall_pr_1_99_ble": 34,
    "KG Fall_ss_ble": 93,
    "KG Fall_dss_ble": 435,
    "KG Winter_theta_ble": -0.241264358,
    "KG Winter_pr_1_99_ble": 34,
    "KG Winter_ss_ble": 93,
    "KG Winter_dss_ble": 476,
    "KG Spring_theta_ble": 0.113493997,
    "KG Spring_pr_1_99_ble": 34,
    "KG Spring_ss_ble": 93,
    "KG Spring_dss_ble": 511
  },
  {
    "ID": 173,
    "KG Fall_theta_ble": -0.637772615,
    "KG Fall_pr_1_99_ble": 35,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 436,
    "KG Winter_theta_ble": -0.232697823,
    "KG Winter_pr_1_99_ble": 35,
    "KG Winter_ss_ble": 93,
    "KG Winter_dss_ble": 477,
    "KG Spring_theta_ble": 0.121165141,
    "KG Spring_pr_1_99_ble": 35,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 512
  },
  {
    "ID": 174,
    "KG Fall_theta_ble": -0.633152978,
    "KG Fall_pr_1_99_ble": 35,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 437,
    "KG Winter_theta_ble": -0.22334264,
    "KG Winter_pr_1_99_ble": 35,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 478,
    "KG Spring_theta_ble": 0.122366089,
    "KG Spring_pr_1_99_ble": 35,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 512
  },
  {
    "ID": 175,
    "KG Fall_theta_ble": -0.62919205,
    "KG Fall_pr_1_99_ble": 35,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 437,
    "KG Winter_theta_ble": -0.221000295,
    "KG Winter_pr_1_99_ble": 35,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 478,
    "KG Spring_theta_ble": 0.124399083,
    "KG Spring_pr_1_99_ble": 35,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 512
  },
  {
    "ID": 176,
    "KG Fall_theta_ble": -0.627797407,
    "KG Fall_pr_1_99_ble": 35,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 437,
    "KG Winter_theta_ble": -0.216592139,
    "KG Winter_pr_1_99_ble": 35,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 478,
    "KG Spring_theta_ble": 0.12866957,
    "KG Spring_pr_1_99_ble": 35,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 513
  },
  {
    "ID": 177,
    "KG Fall_theta_ble": -0.624563585,
    "KG Fall_pr_1_99_ble": 35,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 438,
    "KG Winter_theta_ble": -0.215330644,
    "KG Winter_pr_1_99_ble": 35,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 478,
    "KG Spring_theta_ble": 0.12909583,
    "KG Spring_pr_1_99_ble": 35,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 513
  },
  {
    "ID": 178,
    "KG Fall_theta_ble": -0.623190869,
    "KG Fall_pr_1_99_ble": 36,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 438,
    "KG Winter_theta_ble": -0.211608846,
    "KG Winter_pr_1_99_ble": 36,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 479,
    "KG Spring_theta_ble": 0.130146869,
    "KG Spring_pr_1_99_ble": 36,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 513
  },
  {
    "ID": 179,
    "KG Fall_theta_ble": -0.61775655,
    "KG Fall_pr_1_99_ble": 36,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 438,
    "KG Winter_theta_ble": -0.204090293,
    "KG Winter_pr_1_99_ble": 36,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 480,
    "KG Spring_theta_ble": 0.13090888,
    "KG Spring_pr_1_99_ble": 36,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 513
  },
  {
    "ID": 180,
    "KG Fall_theta_ble": -0.613077138,
    "KG Fall_pr_1_99_ble": 36,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 439,
    "KG Winter_theta_ble": -0.201918725,
    "KG Winter_pr_1_99_ble": 36,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 480,
    "KG Spring_theta_ble": 0.138328064,
    "KG Spring_pr_1_99_ble": 36,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 514
  },
  {
    "ID": 181,
    "KG Fall_theta_ble": -0.609985012,
    "KG Fall_pr_1_99_ble": 36,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 439,
    "KG Winter_theta_ble": -0.198511889,
    "KG Winter_pr_1_99_ble": 36,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 480,
    "KG Spring_theta_ble": 0.144219061,
    "KG Spring_pr_1_99_ble": 36,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 514
  },
  {
    "ID": 182,
    "KG Fall_theta_ble": -0.598892008,
    "KG Fall_pr_1_99_ble": 36,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 440,
    "KG Winter_theta_ble": -0.197471985,
    "KG Winter_pr_1_99_ble": 36,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 480,
    "KG Spring_theta_ble": 0.148493071,
    "KG Spring_pr_1_99_ble": 36,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 515
  },
  {
    "ID": 183,
    "KG Fall_theta_ble": -0.59743974,
    "KG Fall_pr_1_99_ble": 37,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 440,
    "KG Winter_theta_ble": -0.196787732,
    "KG Winter_pr_1_99_ble": 37,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 480,
    "KG Spring_theta_ble": 0.153132964,
    "KG Spring_pr_1_99_ble": 37,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 515
  },
  {
    "ID": 184,
    "KG Fall_theta_ble": -0.595956198,
    "KG Fall_pr_1_99_ble": 37,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 440,
    "KG Winter_theta_ble": -0.194092882,
    "KG Winter_pr_1_99_ble": 37,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 481,
    "KG Spring_theta_ble": 0.159168901,
    "KG Spring_pr_1_99_ble": 37,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 516
  },
  {
    "ID": 185,
    "KG Fall_theta_ble": -0.593655175,
    "KG Fall_pr_1_99_ble": 37,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 441,
    "KG Winter_theta_ble": -0.194017174,
    "KG Winter_pr_1_99_ble": 37,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 481,
    "KG Spring_theta_ble": 0.164479186,
    "KG Spring_pr_1_99_ble": 37,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 516
  },
  {
    "ID": 186,
    "KG Fall_theta_ble": -0.592594356,
    "KG Fall_pr_1_99_ble": 37,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 441,
    "KG Winter_theta_ble": -0.187473091,
    "KG Winter_pr_1_99_ble": 37,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 481,
    "KG Spring_theta_ble": 0.166319922,
    "KG Spring_pr_1_99_ble": 37,
    "KG Spring_ss_ble": 94,
    "KG Spring_dss_ble": 517
  },
  {
    "ID": 187,
    "KG Fall_theta_ble": -0.592587501,
    "KG Fall_pr_1_99_ble": 37,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 441,
    "KG Winter_theta_ble": -0.17633443,
    "KG Winter_pr_1_99_ble": 37,
    "KG Winter_ss_ble": 94,
    "KG Winter_dss_ble": 482,
    "KG Spring_theta_ble": 0.177785819,
    "KG Spring_pr_1_99_ble": 37,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 518
  },
  {
    "ID": 188,
    "KG Fall_theta_ble": -0.59112598,
    "KG Fall_pr_1_99_ble": 38,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 441,
    "KG Winter_theta_ble": -0.171178185,
    "KG Winter_pr_1_99_ble": 38,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 483,
    "KG Spring_theta_ble": 0.178296102,
    "KG Spring_pr_1_99_ble": 38,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 518
  },
  {
    "ID": 189,
    "KG Fall_theta_ble": -0.589498836,
    "KG Fall_pr_1_99_ble": 38,
    "KG Fall_ss_ble": 94,
    "KG Fall_dss_ble": 441,
    "KG Winter_theta_ble": -0.168975136,
    "KG Winter_pr_1_99_ble": 38,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 483,
    "KG Spring_theta_ble": 0.182620072,
    "KG Spring_pr_1_99_ble": 38,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 518
  },
  {
    "ID": 190,
    "KG Fall_theta_ble": -0.580266263,
    "KG Fall_pr_1_99_ble": 38,
    "KG Fall_ss_ble": 95,
    "KG Fall_dss_ble": 442,
    "KG Winter_theta_ble": -0.166146763,
    "KG Winter_pr_1_99_ble": 38,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 483,
    "KG Spring_theta_ble": 0.191149282,
    "KG Spring_pr_1_99_ble": 38,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 519
  },
  {
    "ID": 191,
    "KG Fall_theta_ble": -0.569160297,
    "KG Fall_pr_1_99_ble": 38,
    "KG Fall_ss_ble": 95,
    "KG Fall_dss_ble": 443,
    "KG Winter_theta_ble": -0.159448435,
    "KG Winter_pr_1_99_ble": 38,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 484,
    "KG Spring_theta_ble": 0.196283616,
    "KG Spring_pr_1_99_ble": 38,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 520
  },
  {
    "ID": 192,
    "KG Fall_theta_ble": -0.562826485,
    "KG Fall_pr_1_99_ble": 38,
    "KG Fall_ss_ble": 95,
    "KG Fall_dss_ble": 444,
    "KG Winter_theta_ble": -0.15502855,
    "KG Winter_pr_1_99_ble": 38,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 484,
    "KG Spring_theta_ble": 0.19661025,
    "KG Spring_pr_1_99_ble": 38,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 520
  },
  {
    "ID": 193,
    "KG Fall_theta_ble": -0.556043172,
    "KG Fall_pr_1_99_ble": 39,
    "KG Fall_ss_ble": 95,
    "KG Fall_dss_ble": 444,
    "KG Winter_theta_ble": -0.154353845,
    "KG Winter_pr_1_99_ble": 39,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 485,
    "KG Spring_theta_ble": 0.197583621,
    "KG Spring_pr_1_99_ble": 39,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 520
  },
  {
    "ID": 194,
    "KG Fall_theta_ble": -0.554868531,
    "KG Fall_pr_1_99_ble": 39,
    "KG Fall_ss_ble": 95,
    "KG Fall_dss_ble": 445,
    "KG Winter_theta_ble": -0.154320354,
    "KG Winter_pr_1_99_ble": 39,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 485,
    "KG Spring_theta_ble": 0.199041169,
    "KG Spring_pr_1_99_ble": 39,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 520
  },
  {
    "ID": 195,
    "KG Fall_theta_ble": -0.551012785,
    "KG Fall_pr_1_99_ble": 39,
    "KG Fall_ss_ble": 95,
    "KG Fall_dss_ble": 445,
    "KG Winter_theta_ble": -0.128816368,
    "KG Winter_pr_1_99_ble": 39,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 487,
    "KG Spring_theta_ble": 0.199489223,
    "KG Spring_pr_1_99_ble": 39,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 520
  },
  {
    "ID": 196,
    "KG Fall_theta_ble": -0.546929239,
    "KG Fall_pr_1_99_ble": 39,
    "KG Fall_ss_ble": 95,
    "KG Fall_dss_ble": 445,
    "KG Winter_theta_ble": -0.122346226,
    "KG Winter_pr_1_99_ble": 39,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 488,
    "KG Spring_theta_ble": 0.200441182,
    "KG Spring_pr_1_99_ble": 39,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 520
  },
  {
    "ID": 197,
    "KG Fall_theta_ble": -0.546266557,
    "KG Fall_pr_1_99_ble": 39,
    "KG Fall_ss_ble": 95,
    "KG Fall_dss_ble": 445,
    "KG Winter_theta_ble": -0.120555013,
    "KG Winter_pr_1_99_ble": 39,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 488,
    "KG Spring_theta_ble": 0.202539618,
    "KG Spring_pr_1_99_ble": 39,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 520
  },
  {
    "ID": 198,
    "KG Fall_theta_ble": -0.541499393,
    "KG Fall_pr_1_99_ble": 40,
    "KG Fall_ss_ble": 95,
    "KG Fall_dss_ble": 446,
    "KG Winter_theta_ble": -0.118755457,
    "KG Winter_pr_1_99_ble": 40,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 488,
    "KG Spring_theta_ble": 0.207080414,
    "KG Spring_pr_1_99_ble": 40,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 521
  },
  {
    "ID": 199,
    "KG Fall_theta_ble": -0.532686792,
    "KG Fall_pr_1_99_ble": 40,
    "KG Fall_ss_ble": 95,
    "KG Fall_dss_ble": 447,
    "KG Winter_theta_ble": -0.117887947,
    "KG Winter_pr_1_99_ble": 40,
    "KG Winter_ss_ble": 95,
    "KG Winter_dss_ble": 488,
    "KG Spring_theta_ble": 0.216080415,
    "KG Spring_pr_1_99_ble": 40,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 522
  },
  {
    "ID": 200,
    "KG Fall_theta_ble": -0.522362785,
    "KG Fall_pr_1_99_ble": 40,
    "KG Fall_ss_ble": 96,
    "KG Fall_dss_ble": 448,
    "KG Winter_theta_ble": -0.111866033,
    "KG Winter_pr_1_99_ble": 40,
    "KG Winter_ss_ble": 96,
    "KG Winter_dss_ble": 489,
    "KG Spring_theta_ble": 0.217208678,
    "KG Spring_pr_1_99_ble": 40,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 522
  },
  {
    "ID": 201,
    "KG Fall_theta_ble": -0.501283776,
    "KG Fall_pr_1_99_ble": 40,
    "KG Fall_ss_ble": 96,
    "KG Fall_dss_ble": 450,
    "KG Winter_theta_ble": -0.101844835,
    "KG Winter_pr_1_99_ble": 40,
    "KG Winter_ss_ble": 96,
    "KG Winter_dss_ble": 490,
    "KG Spring_theta_ble": 0.220378425,
    "KG Spring_pr_1_99_ble": 40,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 522
  },
  {
    "ID": 202,
    "KG Fall_theta_ble": -0.4952402,
    "KG Fall_pr_1_99_ble": 40,
    "KG Fall_ss_ble": 96,
    "KG Fall_dss_ble": 450,
    "KG Winter_theta_ble": -0.096068434,
    "KG Winter_pr_1_99_ble": 40,
    "KG Winter_ss_ble": 96,
    "KG Winter_dss_ble": 490,
    "KG Spring_theta_ble": 0.220801104,
    "KG Spring_pr_1_99_ble": 40,
    "KG Spring_ss_ble": 95,
    "KG Spring_dss_ble": 522
  },
  {
    "ID": 203,
    "KG Fall_theta_ble": -0.494053508,
    "KG Fall_pr_1_99_ble": 41,
    "KG Fall_ss_ble": 96,
    "KG Fall_dss_ble": 451,
    "KG Winter_theta_ble": -0.093082043,
    "KG Winter_pr_1_99_ble": 41,
    "KG Winter_ss_ble": 96,
    "KG Winter_dss_ble": 491,
    "KG Spring_theta_ble": 0.24094203,
    "KG Spring_pr_1_99_ble": 41,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 524
  },
  {
    "ID": 204,
    "KG Fall_theta_ble": -0.49382962,
    "KG Fall_pr_1_99_ble": 41,
    "KG Fall_ss_ble": 96,
    "KG Fall_dss_ble": 451,
    "KG Winter_theta_ble": -0.087844173,
    "KG Winter_pr_1_99_ble": 41,
    "KG Winter_ss_ble": 96,
    "KG Winter_dss_ble": 491,
    "KG Spring_theta_ble": 0.241839733,
    "KG Spring_pr_1_99_ble": 41,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 524
  },
  {
    "ID": 205,
    "KG Fall_theta_ble": -0.49210734,
    "KG Fall_pr_1_99_ble": 41,
    "KG Fall_ss_ble": 96,
    "KG Fall_dss_ble": 451,
    "KG Winter_theta_ble": -0.084722904,
    "KG Winter_pr_1_99_ble": 41,
    "KG Winter_ss_ble": 96,
    "KG Winter_dss_ble": 492,
    "KG Spring_theta_ble": 0.245311733,
    "KG Spring_pr_1_99_ble": 41,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 525
  },
  {
    "ID": 206,
    "KG Fall_theta_ble": -0.483274443,
    "KG Fall_pr_1_99_ble": 41,
    "KG Fall_ss_ble": 96,
    "KG Fall_dss_ble": 452,
    "KG Winter_theta_ble": -0.077211002,
    "KG Winter_pr_1_99_ble": 41,
    "KG Winter_ss_ble": 96,
    "KG Winter_dss_ble": 492,
    "KG Spring_theta_ble": 0.246357042,
    "KG Spring_pr_1_99_ble": 41,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 525
  },
  {
    "ID": 207,
    "KG Fall_theta_ble": -0.475911681,
    "KG Fall_pr_1_99_ble": 41,
    "KG Fall_ss_ble": 96,
    "KG Fall_dss_ble": 452,
    "KG Winter_theta_ble": -0.070349086,
    "KG Winter_pr_1_99_ble": 41,
    "KG Winter_ss_ble": 96,
    "KG Winter_dss_ble": 493,
    "KG Spring_theta_ble": 0.247169859,
    "KG Spring_pr_1_99_ble": 41,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 525
  },
  {
    "ID": 208,
    "KG Fall_theta_ble": -0.474770662,
    "KG Fall_pr_1_99_ble": 42,
    "KG Fall_ss_ble": 96,
    "KG Fall_dss_ble": 453,
    "KG Winter_theta_ble": -0.068749226,
    "KG Winter_pr_1_99_ble": 42,
    "KG Winter_ss_ble": 96,
    "KG Winter_dss_ble": 493,
    "KG Spring_theta_ble": 0.249144401,
    "KG Spring_pr_1_99_ble": 42,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 525
  },
  {
    "ID": 209,
    "KG Fall_theta_ble": -0.444572638,
    "KG Fall_pr_1_99_ble": 42,
    "KG Fall_ss_ble": 97,
    "KG Fall_dss_ble": 456,
    "KG Winter_theta_ble": -0.059218091,
    "KG Winter_pr_1_99_ble": 42,
    "KG Winter_ss_ble": 97,
    "KG Winter_dss_ble": 494,
    "KG Spring_theta_ble": 0.255143179,
    "KG Spring_pr_1_99_ble": 42,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 526
  },
  {
    "ID": 210,
    "KG Fall_theta_ble": -0.440581256,
    "KG Fall_pr_1_99_ble": 42,
    "KG Fall_ss_ble": 97,
    "KG Fall_dss_ble": 456,
    "KG Winter_theta_ble": -0.048509285,
    "KG Winter_pr_1_99_ble": 42,
    "KG Winter_ss_ble": 97,
    "KG Winter_dss_ble": 495,
    "KG Spring_theta_ble": 0.2568471,
    "KG Spring_pr_1_99_ble": 42,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 526
  },
  {
    "ID": 211,
    "KG Fall_theta_ble": -0.421531379,
    "KG Fall_pr_1_99_ble": 42,
    "KG Fall_ss_ble": 97,
    "KG Fall_dss_ble": 458,
    "KG Winter_theta_ble": -0.044566955,
    "KG Winter_pr_1_99_ble": 42,
    "KG Winter_ss_ble": 97,
    "KG Winter_dss_ble": 496,
    "KG Spring_theta_ble": 0.258617285,
    "KG Spring_pr_1_99_ble": 42,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 526
  },
  {
    "ID": 212,
    "KG Fall_theta_ble": -0.419846901,
    "KG Fall_pr_1_99_ble": 42,
    "KG Fall_ss_ble": 97,
    "KG Fall_dss_ble": 458,
    "KG Winter_theta_ble": -0.043182777,
    "KG Winter_pr_1_99_ble": 42,
    "KG Winter_ss_ble": 97,
    "KG Winter_dss_ble": 496,
    "KG Spring_theta_ble": 0.26552362,
    "KG Spring_pr_1_99_ble": 42,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 527
  },
  {
    "ID": 213,
    "KG Fall_theta_ble": -0.414218809,
    "KG Fall_pr_1_99_ble": 43,
    "KG Fall_ss_ble": 97,
    "KG Fall_dss_ble": 459,
    "KG Winter_theta_ble": -0.038728852,
    "KG Winter_pr_1_99_ble": 43,
    "KG Winter_ss_ble": 97,
    "KG Winter_dss_ble": 496,
    "KG Spring_theta_ble": 0.268566046,
    "KG Spring_pr_1_99_ble": 43,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 527
  },
  {
    "ID": 214,
    "KG Fall_theta_ble": -0.400484205,
    "KG Fall_pr_1_99_ble": 43,
    "KG Fall_ss_ble": 98,
    "KG Fall_dss_ble": 460,
    "KG Winter_theta_ble": -0.0224751,
    "KG Winter_pr_1_99_ble": 43,
    "KG Winter_ss_ble": 97,
    "KG Winter_dss_ble": 498,
    "KG Spring_theta_ble": 0.270275581,
    "KG Spring_pr_1_99_ble": 43,
    "KG Spring_ss_ble": 96,
    "KG Spring_dss_ble": 527
  },
  {
    "ID": 215,
    "KG Fall_theta_ble": -0.397575036,
    "KG Fall_pr_1_99_ble": 43,
    "KG Fall_ss_ble": 98,
    "KG Fall_dss_ble": 460,
    "KG Winter_theta_ble": -0.022297958,
    "KG Winter_pr_1_99_ble": 43,
    "KG Winter_ss_ble": 97,
    "KG Winter_dss_ble": 498,
    "KG Spring_theta_ble": 0.276780362,
    "KG Spring_pr_1_99_ble": 43,
    "KG Spring_ss_ble": 97,
    "KG Spring_dss_ble": 528
  },
  {
    "ID": 216,
    "KG Fall_theta_ble": -0.395918968,
    "KG Fall_pr_1_99_ble": 43,
    "KG Fall_ss_ble": 98,
    "KG Fall_dss_ble": 460,
    "KG Winter_theta_ble": -0.015556697,
    "KG Winter_pr_1_99_ble": 43,
    "KG Winter_ss_ble": 97,
    "KG Winter_dss_ble": 498,
    "KG Spring_theta_ble": 0.279782641,
    "KG Spring_pr_1_99_ble": 43,
    "KG Spring_ss_ble": 97,
    "KG Spring_dss_ble": 528
  },
  {
    "ID": 217,
    "KG Fall_theta_ble": -0.394608308,
    "KG Fall_pr_1_99_ble": 43,
    "KG Fall_ss_ble": 98,
    "KG Fall_dss_ble": 461,
    "KG Winter_theta_ble": -0.014368806,
    "KG Winter_pr_1_99_ble": 43,
    "KG Winter_ss_ble": 97,
    "KG Winter_dss_ble": 499,
    "KG Spring_theta_ble": 0.280999104,
    "KG Spring_pr_1_99_ble": 43,
    "KG Spring_ss_ble": 97,
    "KG Spring_dss_ble": 528
  },
  {
    "ID": 218,
    "KG Fall_theta_ble": -0.390685045,
    "KG Fall_pr_1_99_ble": 44,
    "KG Fall_ss_ble": 98,
    "KG Fall_dss_ble": 461,
    "KG Winter_theta_ble": -0.00232655,
    "KG Winter_pr_1_99_ble": 44,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 500,
    "KG Spring_theta_ble": 0.283518524,
    "KG Spring_pr_1_99_ble": 44,
    "KG Spring_ss_ble": 97,
    "KG Spring_dss_ble": 528
  },
  {
    "ID": 219,
    "KG Fall_theta_ble": -0.3770496,
    "KG Fall_pr_1_99_ble": 44,
    "KG Fall_ss_ble": 98,
    "KG Fall_dss_ble": 462,
    "KG Winter_theta_ble": -0.00024602,
    "KG Winter_pr_1_99_ble": 44,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 500,
    "KG Spring_theta_ble": 0.307027691,
    "KG Spring_pr_1_99_ble": 44,
    "KG Spring_ss_ble": 97,
    "KG Spring_dss_ble": 531
  },
  {
    "ID": 220,
    "KG Fall_theta_ble": -0.371151671,
    "KG Fall_pr_1_99_ble": 44,
    "KG Fall_ss_ble": 98,
    "KG Fall_dss_ble": 463,
    "KG Winter_theta_ble": 0.002229874,
    "KG Winter_pr_1_99_ble": 44,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 500,
    "KG Spring_theta_ble": 0.307206905,
    "KG Spring_pr_1_99_ble": 44,
    "KG Spring_ss_ble": 97,
    "KG Spring_dss_ble": 531
  },
  {
    "ID": 221,
    "KG Fall_theta_ble": -0.37010815,
    "KG Fall_pr_1_99_ble": 44,
    "KG Fall_ss_ble": 98,
    "KG Fall_dss_ble": 463,
    "KG Winter_theta_ble": 0.004705408,
    "KG Winter_pr_1_99_ble": 44,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 500,
    "KG Spring_theta_ble": 0.309220121,
    "KG Spring_pr_1_99_ble": 44,
    "KG Spring_ss_ble": 97,
    "KG Spring_dss_ble": 531
  },
  {
    "ID": 222,
    "KG Fall_theta_ble": -0.345447883,
    "KG Fall_pr_1_99_ble": 44,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 465,
    "KG Winter_theta_ble": 0.006938609,
    "KG Winter_pr_1_99_ble": 44,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 501,
    "KG Spring_theta_ble": 0.314165584,
    "KG Spring_pr_1_99_ble": 44,
    "KG Spring_ss_ble": 97,
    "KG Spring_dss_ble": 531
  },
  {
    "ID": 223,
    "KG Fall_theta_ble": -0.340619598,
    "KG Fall_pr_1_99_ble": 45,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 466,
    "KG Winter_theta_ble": 0.009217346,
    "KG Winter_pr_1_99_ble": 45,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 501,
    "KG Spring_theta_ble": 0.314271961,
    "KG Spring_pr_1_99_ble": 45,
    "KG Spring_ss_ble": 97,
    "KG Spring_dss_ble": 531
  },
  {
    "ID": 224,
    "KG Fall_theta_ble": -0.336825189,
    "KG Fall_pr_1_99_ble": 45,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 466,
    "KG Winter_theta_ble": 0.014267828,
    "KG Winter_pr_1_99_ble": 45,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 501,
    "KG Spring_theta_ble": 0.328449014,
    "KG Spring_pr_1_99_ble": 45,
    "KG Spring_ss_ble": 98,
    "KG Spring_dss_ble": 533
  },
  {
    "ID": 225,
    "KG Fall_theta_ble": -0.330750373,
    "KG Fall_pr_1_99_ble": 45,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 467,
    "KG Winter_theta_ble": 0.021413859,
    "KG Winter_pr_1_99_ble": 45,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 502,
    "KG Spring_theta_ble": 0.335903209,
    "KG Spring_pr_1_99_ble": 45,
    "KG Spring_ss_ble": 98,
    "KG Spring_dss_ble": 534
  },
  {
    "ID": 226,
    "KG Fall_theta_ble": -0.325914789,
    "KG Fall_pr_1_99_ble": 45,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 467,
    "KG Winter_theta_ble": 0.027426944,
    "KG Winter_pr_1_99_ble": 45,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 503,
    "KG Spring_theta_ble": 0.344218717,
    "KG Spring_pr_1_99_ble": 45,
    "KG Spring_ss_ble": 98,
    "KG Spring_dss_ble": 534
  },
  {
    "ID": 227,
    "KG Fall_theta_ble": -0.318535512,
    "KG Fall_pr_1_99_ble": 45,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 468,
    "KG Winter_theta_ble": 0.032330407,
    "KG Winter_pr_1_99_ble": 45,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 503,
    "KG Spring_theta_ble": 0.345530546,
    "KG Spring_pr_1_99_ble": 45,
    "KG Spring_ss_ble": 98,
    "KG Spring_dss_ble": 535
  },
  {
    "ID": 228,
    "KG Fall_theta_ble": -0.311240138,
    "KG Fall_pr_1_99_ble": 46,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 469,
    "KG Winter_theta_ble": 0.036951702,
    "KG Winter_pr_1_99_ble": 46,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 504,
    "KG Spring_theta_ble": 0.346150994,
    "KG Spring_pr_1_99_ble": 46,
    "KG Spring_ss_ble": 98,
    "KG Spring_dss_ble": 535
  },
  {
    "ID": 229,
    "KG Fall_theta_ble": -0.295613922,
    "KG Fall_pr_1_99_ble": 46,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 470,
    "KG Winter_theta_ble": 0.042472311,
    "KG Winter_pr_1_99_ble": 46,
    "KG Winter_ss_ble": 98,
    "KG Winter_dss_ble": 504,
    "KG Spring_theta_ble": 0.361404745,
    "KG Spring_pr_1_99_ble": 46,
    "KG Spring_ss_ble": 98,
    "KG Spring_dss_ble": 536
  },
  {
    "ID": 230,
    "KG Fall_theta_ble": -0.295012706,
    "KG Fall_pr_1_99_ble": 46,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 470,
    "KG Winter_theta_ble": 0.052306125,
    "KG Winter_pr_1_99_ble": 46,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 505,
    "KG Spring_theta_ble": 0.374663961,
    "KG Spring_pr_1_99_ble": 46,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 537
  },
  {
    "ID": 231,
    "KG Fall_theta_ble": -0.294185377,
    "KG Fall_pr_1_99_ble": 46,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 471,
    "KG Winter_theta_ble": 0.053988117,
    "KG Winter_pr_1_99_ble": 46,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 505,
    "KG Spring_theta_ble": 0.376517797,
    "KG Spring_pr_1_99_ble": 46,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 538
  },
  {
    "ID": 232,
    "KG Fall_theta_ble": -0.290417357,
    "KG Fall_pr_1_99_ble": 46,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 471,
    "KG Winter_theta_ble": 0.056994018,
    "KG Winter_pr_1_99_ble": 46,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 506,
    "KG Spring_theta_ble": 0.385069928,
    "KG Spring_pr_1_99_ble": 46,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 539
  },
  {
    "ID": 233,
    "KG Fall_theta_ble": -0.289424301,
    "KG Fall_pr_1_99_ble": 47,
    "KG Fall_ss_ble": 99,
    "KG Fall_dss_ble": 471,
    "KG Winter_theta_ble": 0.065573579,
    "KG Winter_pr_1_99_ble": 47,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 507,
    "KG Spring_theta_ble": 0.38567846,
    "KG Spring_pr_1_99_ble": 47,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 539
  },
  {
    "ID": 234,
    "KG Fall_theta_ble": -0.288048398,
    "KG Fall_pr_1_99_ble": 47,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 471,
    "KG Winter_theta_ble": 0.067855132,
    "KG Winter_pr_1_99_ble": 47,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 507,
    "KG Spring_theta_ble": 0.392686822,
    "KG Spring_pr_1_99_ble": 47,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 539
  },
  {
    "ID": 235,
    "KG Fall_theta_ble": -0.28097551,
    "KG Fall_pr_1_99_ble": 47,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 472,
    "KG Winter_theta_ble": 0.069404544,
    "KG Winter_pr_1_99_ble": 47,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 507,
    "KG Spring_theta_ble": 0.393602769,
    "KG Spring_pr_1_99_ble": 47,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 539
  },
  {
    "ID": 236,
    "KG Fall_theta_ble": -0.271127219,
    "KG Fall_pr_1_99_ble": 47,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 473,
    "KG Winter_theta_ble": 0.070163804,
    "KG Winter_pr_1_99_ble": 47,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 507,
    "KG Spring_theta_ble": 0.394139296,
    "KG Spring_pr_1_99_ble": 47,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 539
  },
  {
    "ID": 237,
    "KG Fall_theta_ble": -0.268807251,
    "KG Fall_pr_1_99_ble": 47,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 473,
    "KG Winter_theta_ble": 0.076090517,
    "KG Winter_pr_1_99_ble": 47,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 508,
    "KG Spring_theta_ble": 0.402842844,
    "KG Spring_pr_1_99_ble": 47,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 540
  },
  {
    "ID": 238,
    "KG Fall_theta_ble": -0.261041637,
    "KG Fall_pr_1_99_ble": 48,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 474,
    "KG Winter_theta_ble": 0.077017965,
    "KG Winter_pr_1_99_ble": 48,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 508,
    "KG Spring_theta_ble": 0.403132739,
    "KG Spring_pr_1_99_ble": 48,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 540
  },
  {
    "ID": 239,
    "KG Fall_theta_ble": -0.254798141,
    "KG Fall_pr_1_99_ble": 48,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 475,
    "KG Winter_theta_ble": 0.077647463,
    "KG Winter_pr_1_99_ble": 48,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 508,
    "KG Spring_theta_ble": 0.403797768,
    "KG Spring_pr_1_99_ble": 48,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 540
  },
  {
    "ID": 240,
    "KG Fall_theta_ble": -0.254109804,
    "KG Fall_pr_1_99_ble": 48,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 475,
    "KG Winter_theta_ble": 0.080997489,
    "KG Winter_pr_1_99_ble": 48,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 508,
    "KG Spring_theta_ble": 0.406807776,
    "KG Spring_pr_1_99_ble": 48,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 541
  },
  {
    "ID": 241,
    "KG Fall_theta_ble": -0.25368336,
    "KG Fall_pr_1_99_ble": 48,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 475,
    "KG Winter_theta_ble": 0.086848702,
    "KG Winter_pr_1_99_ble": 48,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 509,
    "KG Spring_theta_ble": 0.407164785,
    "KG Spring_pr_1_99_ble": 48,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 541
  },
  {
    "ID": 242,
    "KG Fall_theta_ble": -0.253335463,
    "KG Fall_pr_1_99_ble": 48,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 475,
    "KG Winter_theta_ble": 0.091051331,
    "KG Winter_pr_1_99_ble": 48,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 509,
    "KG Spring_theta_ble": 0.413847946,
    "KG Spring_pr_1_99_ble": 48,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 541
  },
  {
    "ID": 243,
    "KG Fall_theta_ble": -0.250633117,
    "KG Fall_pr_1_99_ble": 49,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 475,
    "KG Winter_theta_ble": 0.091977398,
    "KG Winter_pr_1_99_ble": 49,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 509,
    "KG Spring_theta_ble": 0.416115453,
    "KG Spring_pr_1_99_ble": 49,
    "KG Spring_ss_ble": 99,
    "KG Spring_dss_ble": 542
  },
  {
    "ID": 244,
    "KG Fall_theta_ble": -0.247763013,
    "KG Fall_pr_1_99_ble": 49,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 475,
    "KG Winter_theta_ble": 0.098145648,
    "KG Winter_pr_1_99_ble": 49,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 510,
    "KG Spring_theta_ble": 0.43535181,
    "KG Spring_pr_1_99_ble": 49,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 544
  },
  {
    "ID": 245,
    "KG Fall_theta_ble": -0.241826234,
    "KG Fall_pr_1_99_ble": 49,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 476,
    "KG Winter_theta_ble": 0.101470631,
    "KG Winter_pr_1_99_ble": 49,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 510,
    "KG Spring_theta_ble": 0.438990381,
    "KG Spring_pr_1_99_ble": 49,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 544
  },
  {
    "ID": 246,
    "KG Fall_theta_ble": -0.241482954,
    "KG Fall_pr_1_99_ble": 49,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 476,
    "KG Winter_theta_ble": 0.103230542,
    "KG Winter_pr_1_99_ble": 49,
    "KG Winter_ss_ble": 99,
    "KG Winter_dss_ble": 510,
    "KG Spring_theta_ble": 0.445697466,
    "KG Spring_pr_1_99_ble": 49,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 545
  },
  {
    "ID": 247,
    "KG Fall_theta_ble": -0.24040066,
    "KG Fall_pr_1_99_ble": 49,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 476,
    "KG Winter_theta_ble": 0.109492702,
    "KG Winter_pr_1_99_ble": 49,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 511,
    "KG Spring_theta_ble": 0.447472702,
    "KG Spring_pr_1_99_ble": 49,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 545
  },
  {
    "ID": 248,
    "KG Fall_theta_ble": -0.237502428,
    "KG Fall_pr_1_99_ble": 50,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 476,
    "KG Winter_theta_ble": 0.110295517,
    "KG Winter_pr_1_99_ble": 50,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 511,
    "KG Spring_theta_ble": 0.447607286,
    "KG Spring_pr_1_99_ble": 50,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 545
  },
  {
    "ID": 249,
    "KG Fall_theta_ble": -0.235461882,
    "KG Fall_pr_1_99_ble": 50,
    "KG Fall_ss_ble": 100,
    "KG Fall_dss_ble": 476,
    "KG Winter_theta_ble": 0.111194443,
    "KG Winter_pr_1_99_ble": 50,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 511,
    "KG Spring_theta_ble": 0.447637717,
    "KG Spring_pr_1_99_ble": 50,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 545
  },
  {
    "ID": 250,
    "KG Fall_theta_ble": -0.218149359,
    "KG Fall_pr_1_99_ble": 50,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 478,
    "KG Winter_theta_ble": 0.115078257,
    "KG Winter_pr_1_99_ble": 50,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 512,
    "KG Spring_theta_ble": 0.451040634,
    "KG Spring_pr_1_99_ble": 50,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 545
  },
  {
    "ID": 251,
    "KG Fall_theta_ble": -0.209346092,
    "KG Fall_pr_1_99_ble": 50,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 479,
    "KG Winter_theta_ble": 0.123497365,
    "KG Winter_pr_1_99_ble": 50,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 512,
    "KG Spring_theta_ble": 0.453606062,
    "KG Spring_pr_1_99_ble": 50,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 545
  },
  {
    "ID": 252,
    "KG Fall_theta_ble": -0.208420121,
    "KG Fall_pr_1_99_ble": 50,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 479,
    "KG Winter_theta_ble": 0.127625341,
    "KG Winter_pr_1_99_ble": 50,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 513,
    "KG Spring_theta_ble": 0.454125183,
    "KG Spring_pr_1_99_ble": 50,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 545
  },
  {
    "ID": 253,
    "KG Fall_theta_ble": -0.205425906,
    "KG Fall_pr_1_99_ble": 51,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 479,
    "KG Winter_theta_ble": 0.134949569,
    "KG Winter_pr_1_99_ble": 51,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 513,
    "KG Spring_theta_ble": 0.454589446,
    "KG Spring_pr_1_99_ble": 51,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 545
  },
  {
    "ID": 254,
    "KG Fall_theta_ble": -0.201671776,
    "KG Fall_pr_1_99_ble": 51,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 480,
    "KG Winter_theta_ble": 0.138181476,
    "KG Winter_pr_1_99_ble": 51,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 514,
    "KG Spring_theta_ble": 0.459532677,
    "KG Spring_pr_1_99_ble": 51,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 546
  },
  {
    "ID": 255,
    "KG Fall_theta_ble": -0.196872517,
    "KG Fall_pr_1_99_ble": 51,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 480,
    "KG Winter_theta_ble": 0.138854305,
    "KG Winter_pr_1_99_ble": 51,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 514,
    "KG Spring_theta_ble": 0.467749614,
    "KG Spring_pr_1_99_ble": 51,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 547
  },
  {
    "ID": 256,
    "KG Fall_theta_ble": -0.195260348,
    "KG Fall_pr_1_99_ble": 51,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 480,
    "KG Winter_theta_ble": 0.142626728,
    "KG Winter_pr_1_99_ble": 51,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 514,
    "KG Spring_theta_ble": 0.469888648,
    "KG Spring_pr_1_99_ble": 51,
    "KG Spring_ss_ble": 100,
    "KG Spring_dss_ble": 547
  },
  {
    "ID": 257,
    "KG Fall_theta_ble": -0.190670583,
    "KG Fall_pr_1_99_ble": 51,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 481,
    "KG Winter_theta_ble": 0.143444154,
    "KG Winter_pr_1_99_ble": 51,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 514,
    "KG Spring_theta_ble": 0.475239238,
    "KG Spring_pr_1_99_ble": 51,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 548
  },
  {
    "ID": 258,
    "KG Fall_theta_ble": -0.189503918,
    "KG Fall_pr_1_99_ble": 52,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 481,
    "KG Winter_theta_ble": 0.145117859,
    "KG Winter_pr_1_99_ble": 52,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 515,
    "KG Spring_theta_ble": 0.493034966,
    "KG Spring_pr_1_99_ble": 52,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 549
  },
  {
    "ID": 259,
    "KG Fall_theta_ble": -0.187183074,
    "KG Fall_pr_1_99_ble": 52,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 481,
    "KG Winter_theta_ble": 0.147538762,
    "KG Winter_pr_1_99_ble": 52,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 515,
    "KG Spring_theta_ble": 0.494551185,
    "KG Spring_pr_1_99_ble": 52,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 549
  },
  {
    "ID": 260,
    "KG Fall_theta_ble": -0.182400781,
    "KG Fall_pr_1_99_ble": 52,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 482,
    "KG Winter_theta_ble": 0.14755407,
    "KG Winter_pr_1_99_ble": 52,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 515,
    "KG Spring_theta_ble": 0.501126644,
    "KG Spring_pr_1_99_ble": 52,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 550
  },
  {
    "ID": 261,
    "KG Fall_theta_ble": -0.175761387,
    "KG Fall_pr_1_99_ble": 52,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 482,
    "KG Winter_theta_ble": 0.157282434,
    "KG Winter_pr_1_99_ble": 52,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 516,
    "KG Spring_theta_ble": 0.502616697,
    "KG Spring_pr_1_99_ble": 52,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 550
  },
  {
    "ID": 262,
    "KG Fall_theta_ble": -0.172616396,
    "KG Fall_pr_1_99_ble": 52,
    "KG Fall_ss_ble": 101,
    "KG Fall_dss_ble": 483,
    "KG Winter_theta_ble": 0.160665318,
    "KG Winter_pr_1_99_ble": 52,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 516,
    "KG Spring_theta_ble": 0.504288291,
    "KG Spring_pr_1_99_ble": 52,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 550
  },
  {
    "ID": 263,
    "KG Fall_theta_ble": -0.168303192,
    "KG Fall_pr_1_99_ble": 53,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 483,
    "KG Winter_theta_ble": 0.162340764,
    "KG Winter_pr_1_99_ble": 53,
    "KG Winter_ss_ble": 100,
    "KG Winter_dss_ble": 516,
    "KG Spring_theta_ble": 0.504595963,
    "KG Spring_pr_1_99_ble": 53,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 550
  },
  {
    "ID": 264,
    "KG Fall_theta_ble": -0.166792785,
    "KG Fall_pr_1_99_ble": 53,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 483,
    "KG Winter_theta_ble": 0.173111104,
    "KG Winter_pr_1_99_ble": 53,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 517,
    "KG Spring_theta_ble": 0.505719646,
    "KG Spring_pr_1_99_ble": 53,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 551
  },
  {
    "ID": 265,
    "KG Fall_theta_ble": -0.164523216,
    "KG Fall_pr_1_99_ble": 53,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 484,
    "KG Winter_theta_ble": 0.175548585,
    "KG Winter_pr_1_99_ble": 53,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 518,
    "KG Spring_theta_ble": 0.508743413,
    "KG Spring_pr_1_99_ble": 53,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 551
  },
  {
    "ID": 266,
    "KG Fall_theta_ble": -0.163370146,
    "KG Fall_pr_1_99_ble": 53,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 484,
    "KG Winter_theta_ble": 0.176534552,
    "KG Winter_pr_1_99_ble": 53,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 518,
    "KG Spring_theta_ble": 0.512623586,
    "KG Spring_pr_1_99_ble": 53,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 551
  },
  {
    "ID": 267,
    "KG Fall_theta_ble": -0.152477117,
    "KG Fall_pr_1_99_ble": 53,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 485,
    "KG Winter_theta_ble": 0.178024901,
    "KG Winter_pr_1_99_ble": 53,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 518,
    "KG Spring_theta_ble": 0.51323503,
    "KG Spring_pr_1_99_ble": 53,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 551
  },
  {
    "ID": 268,
    "KG Fall_theta_ble": -0.149982739,
    "KG Fall_pr_1_99_ble": 54,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 485,
    "KG Winter_theta_ble": 0.184739368,
    "KG Winter_pr_1_99_ble": 54,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 518,
    "KG Spring_theta_ble": 0.513755628,
    "KG Spring_pr_1_99_ble": 54,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 551
  },
  {
    "ID": 269,
    "KG Fall_theta_ble": -0.144852586,
    "KG Fall_pr_1_99_ble": 54,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 486,
    "KG Winter_theta_ble": 0.184951324,
    "KG Winter_pr_1_99_ble": 54,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 518,
    "KG Spring_theta_ble": 0.51876516,
    "KG Spring_pr_1_99_ble": 54,
    "KG Spring_ss_ble": 101,
    "KG Spring_dss_ble": 552
  },
  {
    "ID": 270,
    "KG Fall_theta_ble": -0.143499562,
    "KG Fall_pr_1_99_ble": 54,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 486,
    "KG Winter_theta_ble": 0.188751663,
    "KG Winter_pr_1_99_ble": 54,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 519,
    "KG Spring_theta_ble": 0.530229327,
    "KG Spring_pr_1_99_ble": 54,
    "KG Spring_ss_ble": 102,
    "KG Spring_dss_ble": 553
  },
  {
    "ID": 271,
    "KG Fall_theta_ble": -0.1416363,
    "KG Fall_pr_1_99_ble": 54,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 486,
    "KG Winter_theta_ble": 0.190006972,
    "KG Winter_pr_1_99_ble": 54,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 519,
    "KG Spring_theta_ble": 0.531187361,
    "KG Spring_pr_1_99_ble": 54,
    "KG Spring_ss_ble": 102,
    "KG Spring_dss_ble": 553
  },
  {
    "ID": 272,
    "KG Fall_theta_ble": -0.134079406,
    "KG Fall_pr_1_99_ble": 54,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 487,
    "KG Winter_theta_ble": 0.194068169,
    "KG Winter_pr_1_99_ble": 54,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 519,
    "KG Spring_theta_ble": 0.536196071,
    "KG Spring_pr_1_99_ble": 54,
    "KG Spring_ss_ble": 102,
    "KG Spring_dss_ble": 554
  },
  {
    "ID": 273,
    "KG Fall_theta_ble": -0.133358082,
    "KG Fall_pr_1_99_ble": 55,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 487,
    "KG Winter_theta_ble": 0.1964693,
    "KG Winter_pr_1_99_ble": 55,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 520,
    "KG Spring_theta_ble": 0.542193679,
    "KG Spring_pr_1_99_ble": 55,
    "KG Spring_ss_ble": 102,
    "KG Spring_dss_ble": 554
  },
  {
    "ID": 274,
    "KG Fall_theta_ble": -0.129054484,
    "KG Fall_pr_1_99_ble": 55,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 487,
    "KG Winter_theta_ble": 0.197937002,
    "KG Winter_pr_1_99_ble": 55,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 520,
    "KG Spring_theta_ble": 0.545494489,
    "KG Spring_pr_1_99_ble": 55,
    "KG Spring_ss_ble": 102,
    "KG Spring_dss_ble": 555
  },
  {
    "ID": 275,
    "KG Fall_theta_ble": -0.128574736,
    "KG Fall_pr_1_99_ble": 55,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 487,
    "KG Winter_theta_ble": 0.199436305,
    "KG Winter_pr_1_99_ble": 55,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 520,
    "KG Spring_theta_ble": 0.545958603,
    "KG Spring_pr_1_99_ble": 55,
    "KG Spring_ss_ble": 102,
    "KG Spring_dss_ble": 555
  },
  {
    "ID": 276,
    "KG Fall_theta_ble": -0.127862921,
    "KG Fall_pr_1_99_ble": 55,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 487,
    "KG Winter_theta_ble": 0.200854717,
    "KG Winter_pr_1_99_ble": 55,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 520,
    "KG Spring_theta_ble": 0.54686624,
    "KG Spring_pr_1_99_ble": 55,
    "KG Spring_ss_ble": 102,
    "KG Spring_dss_ble": 555
  },
  {
    "ID": 277,
    "KG Fall_theta_ble": -0.125100755,
    "KG Fall_pr_1_99_ble": 55,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 487,
    "KG Winter_theta_ble": 0.218667018,
    "KG Winter_pr_1_99_ble": 55,
    "KG Winter_ss_ble": 101,
    "KG Winter_dss_ble": 522,
    "KG Spring_theta_ble": 0.568204913,
    "KG Spring_pr_1_99_ble": 55,
    "KG Spring_ss_ble": 102,
    "KG Spring_dss_ble": 557
  },
  {
    "ID": 278,
    "KG Fall_theta_ble": -0.124253607,
    "KG Fall_pr_1_99_ble": 56,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 488,
    "KG Winter_theta_ble": 0.22178155,
    "KG Winter_pr_1_99_ble": 56,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 522,
    "KG Spring_theta_ble": 0.578138086,
    "KG Spring_pr_1_99_ble": 56,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 558
  },
  {
    "ID": 279,
    "KG Fall_theta_ble": -0.119045801,
    "KG Fall_pr_1_99_ble": 56,
    "KG Fall_ss_ble": 102,
    "KG Fall_dss_ble": 488,
    "KG Winter_theta_ble": 0.233481891,
    "KG Winter_pr_1_99_ble": 56,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 523,
    "KG Spring_theta_ble": 0.581524669,
    "KG Spring_pr_1_99_ble": 56,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 558
  },
  {
    "ID": 280,
    "KG Fall_theta_ble": -0.112224929,
    "KG Fall_pr_1_99_ble": 56,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 489,
    "KG Winter_theta_ble": 0.233715763,
    "KG Winter_pr_1_99_ble": 56,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 523,
    "KG Spring_theta_ble": 0.581824033,
    "KG Spring_pr_1_99_ble": 56,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 558
  },
  {
    "ID": 281,
    "KG Fall_theta_ble": -0.10728666,
    "KG Fall_pr_1_99_ble": 56,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 489,
    "KG Winter_theta_ble": 0.234040888,
    "KG Winter_pr_1_99_ble": 56,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 523,
    "KG Spring_theta_ble": 0.587975402,
    "KG Spring_pr_1_99_ble": 56,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 559
  },
  {
    "ID": 282,
    "KG Fall_theta_ble": -0.106472368,
    "KG Fall_pr_1_99_ble": 56,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 489,
    "KG Winter_theta_ble": 0.234184213,
    "KG Winter_pr_1_99_ble": 56,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 523,
    "KG Spring_theta_ble": 0.588541523,
    "KG Spring_pr_1_99_ble": 56,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 559
  },
  {
    "ID": 283,
    "KG Fall_theta_ble": -0.104003315,
    "KG Fall_pr_1_99_ble": 57,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 490,
    "KG Winter_theta_ble": 0.235439089,
    "KG Winter_pr_1_99_ble": 57,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 524,
    "KG Spring_theta_ble": 0.592294709,
    "KG Spring_pr_1_99_ble": 57,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 559
  },
  {
    "ID": 284,
    "KG Fall_theta_ble": -0.101726492,
    "KG Fall_pr_1_99_ble": 57,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 490,
    "KG Winter_theta_ble": 0.2365264,
    "KG Winter_pr_1_99_ble": 57,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 524,
    "KG Spring_theta_ble": 0.595170113,
    "KG Spring_pr_1_99_ble": 57,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 560
  },
  {
    "ID": 285,
    "KG Fall_theta_ble": -0.095529736,
    "KG Fall_pr_1_99_ble": 57,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 490,
    "KG Winter_theta_ble": 0.238038391,
    "KG Winter_pr_1_99_ble": 57,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 524,
    "KG Spring_theta_ble": 0.596881413,
    "KG Spring_pr_1_99_ble": 57,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 560
  },
  {
    "ID": 286,
    "KG Fall_theta_ble": -0.094475448,
    "KG Fall_pr_1_99_ble": 57,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 491,
    "KG Winter_theta_ble": 0.239940115,
    "KG Winter_pr_1_99_ble": 57,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 524,
    "KG Spring_theta_ble": 0.601446866,
    "KG Spring_pr_1_99_ble": 57,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 560
  },
  {
    "ID": 287,
    "KG Fall_theta_ble": -0.088420614,
    "KG Fall_pr_1_99_ble": 57,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 491,
    "KG Winter_theta_ble": 0.244866437,
    "KG Winter_pr_1_99_ble": 57,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 524,
    "KG Spring_theta_ble": 0.601610186,
    "KG Spring_pr_1_99_ble": 57,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 560
  },
  {
    "ID": 288,
    "KG Fall_theta_ble": -0.087003342,
    "KG Fall_pr_1_99_ble": 58,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 491,
    "KG Winter_theta_ble": 0.249795398,
    "KG Winter_pr_1_99_ble": 58,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 525,
    "KG Spring_theta_ble": 0.601811773,
    "KG Spring_pr_1_99_ble": 58,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 560
  },
  {
    "ID": 289,
    "KG Fall_theta_ble": -0.086953074,
    "KG Fall_pr_1_99_ble": 58,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 491,
    "KG Winter_theta_ble": 0.260167054,
    "KG Winter_pr_1_99_ble": 58,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 526,
    "KG Spring_theta_ble": 0.603525227,
    "KG Spring_pr_1_99_ble": 58,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 560
  },
  {
    "ID": 290,
    "KG Fall_theta_ble": -0.075255403,
    "KG Fall_pr_1_99_ble": 58,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 492,
    "KG Winter_theta_ble": 0.262511138,
    "KG Winter_pr_1_99_ble": 58,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 526,
    "KG Spring_theta_ble": 0.608299887,
    "KG Spring_pr_1_99_ble": 58,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 561
  },
  {
    "ID": 291,
    "KG Fall_theta_ble": -0.071487874,
    "KG Fall_pr_1_99_ble": 58,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 493,
    "KG Winter_theta_ble": 0.263146932,
    "KG Winter_pr_1_99_ble": 58,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 526,
    "KG Spring_theta_ble": 0.608870703,
    "KG Spring_pr_1_99_ble": 58,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 561
  },
  {
    "ID": 292,
    "KG Fall_theta_ble": -0.0707904,
    "KG Fall_pr_1_99_ble": 58,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 493,
    "KG Winter_theta_ble": 0.273149913,
    "KG Winter_pr_1_99_ble": 58,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 527,
    "KG Spring_theta_ble": 0.611670115,
    "KG Spring_pr_1_99_ble": 58,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 561
  },
  {
    "ID": 293,
    "KG Fall_theta_ble": -0.068575763,
    "KG Fall_pr_1_99_ble": 59,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 493,
    "KG Winter_theta_ble": 0.274195189,
    "KG Winter_pr_1_99_ble": 59,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 527,
    "KG Spring_theta_ble": 0.612413802,
    "KG Spring_pr_1_99_ble": 59,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 561
  },
  {
    "ID": 294,
    "KG Fall_theta_ble": -0.065005951,
    "KG Fall_pr_1_99_ble": 59,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 493,
    "KG Winter_theta_ble": 0.274278245,
    "KG Winter_pr_1_99_ble": 59,
    "KG Winter_ss_ble": 102,
    "KG Winter_dss_ble": 527,
    "KG Spring_theta_ble": 0.615200085,
    "KG Spring_pr_1_99_ble": 59,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 562
  },
  {
    "ID": 295,
    "KG Fall_theta_ble": -0.064486301,
    "KG Fall_pr_1_99_ble": 59,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 494,
    "KG Winter_theta_ble": 0.276180248,
    "KG Winter_pr_1_99_ble": 59,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 528,
    "KG Spring_theta_ble": 0.61759271,
    "KG Spring_pr_1_99_ble": 59,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 562
  },
  {
    "ID": 296,
    "KG Fall_theta_ble": -0.063628843,
    "KG Fall_pr_1_99_ble": 59,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 494,
    "KG Winter_theta_ble": 0.278881474,
    "KG Winter_pr_1_99_ble": 59,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 528,
    "KG Spring_theta_ble": 0.620986796,
    "KG Spring_pr_1_99_ble": 59,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 562
  },
  {
    "ID": 297,
    "KG Fall_theta_ble": -0.061270414,
    "KG Fall_pr_1_99_ble": 59,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 494,
    "KG Winter_theta_ble": 0.283044201,
    "KG Winter_pr_1_99_ble": 59,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 528,
    "KG Spring_theta_ble": 0.623730958,
    "KG Spring_pr_1_99_ble": 59,
    "KG Spring_ss_ble": 103,
    "KG Spring_dss_ble": 562
  },
  {
    "ID": 298,
    "KG Fall_theta_ble": -0.05698558,
    "KG Fall_pr_1_99_ble": 60,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 494,
    "KG Winter_theta_ble": 0.284659309,
    "KG Winter_pr_1_99_ble": 60,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 528,
    "KG Spring_theta_ble": 0.629867171,
    "KG Spring_pr_1_99_ble": 60,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 563
  },
  {
    "ID": 299,
    "KG Fall_theta_ble": -0.056529364,
    "KG Fall_pr_1_99_ble": 60,
    "KG Fall_ss_ble": 103,
    "KG Fall_dss_ble": 494,
    "KG Winter_theta_ble": 0.286188058,
    "KG Winter_pr_1_99_ble": 60,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 529,
    "KG Spring_theta_ble": 0.639595672,
    "KG Spring_pr_1_99_ble": 60,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 564
  },
  {
    "ID": 300,
    "KG Fall_theta_ble": -0.041335674,
    "KG Fall_pr_1_99_ble": 60,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 496,
    "KG Winter_theta_ble": 0.289693751,
    "KG Winter_pr_1_99_ble": 60,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 529,
    "KG Spring_theta_ble": 0.643418417,
    "KG Spring_pr_1_99_ble": 60,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 564
  },
  {
    "ID": 301,
    "KG Fall_theta_ble": -0.036526784,
    "KG Fall_pr_1_99_ble": 60,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 496,
    "KG Winter_theta_ble": 0.297275884,
    "KG Winter_pr_1_99_ble": 60,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 530,
    "KG Spring_theta_ble": 0.651991339,
    "KG Spring_pr_1_99_ble": 60,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 565
  },
  {
    "ID": 302,
    "KG Fall_theta_ble": -0.036296791,
    "KG Fall_pr_1_99_ble": 60,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 496,
    "KG Winter_theta_ble": 0.297292357,
    "KG Winter_pr_1_99_ble": 60,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 530,
    "KG Spring_theta_ble": 0.654060025,
    "KG Spring_pr_1_99_ble": 60,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 565
  },
  {
    "ID": 303,
    "KG Fall_theta_ble": -0.035723356,
    "KG Fall_pr_1_99_ble": 61,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 496,
    "KG Winter_theta_ble": 0.300139629,
    "KG Winter_pr_1_99_ble": 61,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 530,
    "KG Spring_theta_ble": 0.660136568,
    "KG Spring_pr_1_99_ble": 61,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 566
  },
  {
    "ID": 304,
    "KG Fall_theta_ble": -0.022005216,
    "KG Fall_pr_1_99_ble": 61,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 498,
    "KG Winter_theta_ble": 0.302607259,
    "KG Winter_pr_1_99_ble": 61,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 530,
    "KG Spring_theta_ble": 0.661934377,
    "KG Spring_pr_1_99_ble": 61,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 566
  },
  {
    "ID": 305,
    "KG Fall_theta_ble": -0.018331827,
    "KG Fall_pr_1_99_ble": 61,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 498,
    "KG Winter_theta_ble": 0.303425013,
    "KG Winter_pr_1_99_ble": 61,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 530,
    "KG Spring_theta_ble": 0.662486921,
    "KG Spring_pr_1_99_ble": 61,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 566
  },
  {
    "ID": 306,
    "KG Fall_theta_ble": -0.017699863,
    "KG Fall_pr_1_99_ble": 61,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 498,
    "KG Winter_theta_ble": 0.310760112,
    "KG Winter_pr_1_99_ble": 61,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 531,
    "KG Spring_theta_ble": 0.662556345,
    "KG Spring_pr_1_99_ble": 61,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 566
  },
  {
    "ID": 307,
    "KG Fall_theta_ble": -0.014101572,
    "KG Fall_pr_1_99_ble": 61,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 499,
    "KG Winter_theta_ble": 0.313124234,
    "KG Winter_pr_1_99_ble": 61,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 531,
    "KG Spring_theta_ble": 0.663220567,
    "KG Spring_pr_1_99_ble": 61,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 566
  },
  {
    "ID": 308,
    "KG Fall_theta_ble": -0.008891491,
    "KG Fall_pr_1_99_ble": 62,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 499,
    "KG Winter_theta_ble": 0.315771879,
    "KG Winter_pr_1_99_ble": 62,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 532,
    "KG Spring_theta_ble": 0.664324338,
    "KG Spring_pr_1_99_ble": 62,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 566
  },
  {
    "ID": 309,
    "KG Fall_theta_ble": -0.008319036,
    "KG Fall_pr_1_99_ble": 62,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 499,
    "KG Winter_theta_ble": 0.32588316,
    "KG Winter_pr_1_99_ble": 62,
    "KG Winter_ss_ble": 103,
    "KG Winter_dss_ble": 533,
    "KG Spring_theta_ble": 0.666241107,
    "KG Spring_pr_1_99_ble": 62,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 567
  },
  {
    "ID": 310,
    "KG Fall_theta_ble": -0.003360793,
    "KG Fall_pr_1_99_ble": 62,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 500,
    "KG Winter_theta_ble": 0.350943572,
    "KG Winter_pr_1_99_ble": 62,
    "KG Winter_ss_ble": 104,
    "KG Winter_dss_ble": 535,
    "KG Spring_theta_ble": 0.674336053,
    "KG Spring_pr_1_99_ble": 62,
    "KG Spring_ss_ble": 104,
    "KG Spring_dss_ble": 567
  },
  {
    "ID": 311,
    "KG Fall_theta_ble": -0.001252701,
    "KG Fall_pr_1_99_ble": 62,
    "KG Fall_ss_ble": 104,
    "KG Fall_dss_ble": 500,
    "KG Winter_theta_ble": 0.357872243,
    "KG Winter_pr_1_99_ble": 62,
    "KG Winter_ss_ble": 104,
    "KG Winter_dss_ble": 536,
    "KG Spring_theta_ble": 0.678364726,
    "KG Spring_pr_1_99_ble": 62,
    "KG Spring_ss_ble": 105,
    "KG Spring_dss_ble": 568
  },
  {
    "ID": 312,
    "KG Fall_theta_ble": 0.005573465,
    "KG Fall_pr_1_99_ble": 62,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 501,
    "KG Winter_theta_ble": 0.362234863,
    "KG Winter_pr_1_99_ble": 62,
    "KG Winter_ss_ble": 104,
    "KG Winter_dss_ble": 536,
    "KG Spring_theta_ble": 0.688846599,
    "KG Spring_pr_1_99_ble": 62,
    "KG Spring_ss_ble": 105,
    "KG Spring_dss_ble": 569
  },
  {
    "ID": 313,
    "KG Fall_theta_ble": 0.010157658,
    "KG Fall_pr_1_99_ble": 63,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 501,
    "KG Winter_theta_ble": 0.381309576,
    "KG Winter_pr_1_99_ble": 63,
    "KG Winter_ss_ble": 104,
    "KG Winter_dss_ble": 538,
    "KG Spring_theta_ble": 0.705542674,
    "KG Spring_pr_1_99_ble": 63,
    "KG Spring_ss_ble": 105,
    "KG Spring_dss_ble": 571
  },
  {
    "ID": 314,
    "KG Fall_theta_ble": 0.013167845,
    "KG Fall_pr_1_99_ble": 63,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 501,
    "KG Winter_theta_ble": 0.386042597,
    "KG Winter_pr_1_99_ble": 63,
    "KG Winter_ss_ble": 104,
    "KG Winter_dss_ble": 539,
    "KG Spring_theta_ble": 0.705901117,
    "KG Spring_pr_1_99_ble": 63,
    "KG Spring_ss_ble": 105,
    "KG Spring_dss_ble": 571
  },
  {
    "ID": 315,
    "KG Fall_theta_ble": 0.014891161,
    "KG Fall_pr_1_99_ble": 63,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 501,
    "KG Winter_theta_ble": 0.389941864,
    "KG Winter_pr_1_99_ble": 63,
    "KG Winter_ss_ble": 105,
    "KG Winter_dss_ble": 539,
    "KG Spring_theta_ble": 0.712828828,
    "KG Spring_pr_1_99_ble": 63,
    "KG Spring_ss_ble": 105,
    "KG Spring_dss_ble": 571
  },
  {
    "ID": 316,
    "KG Fall_theta_ble": 0.016082273,
    "KG Fall_pr_1_99_ble": 63,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 502,
    "KG Winter_theta_ble": 0.392643803,
    "KG Winter_pr_1_99_ble": 63,
    "KG Winter_ss_ble": 105,
    "KG Winter_dss_ble": 539,
    "KG Spring_theta_ble": 0.714191104,
    "KG Spring_pr_1_99_ble": 63,
    "KG Spring_ss_ble": 105,
    "KG Spring_dss_ble": 571
  },
  {
    "ID": 317,
    "KG Fall_theta_ble": 0.019264004,
    "KG Fall_pr_1_99_ble": 63,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 502,
    "KG Winter_theta_ble": 0.402614809,
    "KG Winter_pr_1_99_ble": 63,
    "KG Winter_ss_ble": 105,
    "KG Winter_dss_ble": 540,
    "KG Spring_theta_ble": 0.71782674,
    "KG Spring_pr_1_99_ble": 63,
    "KG Spring_ss_ble": 105,
    "KG Spring_dss_ble": 572
  },
  {
    "ID": 318,
    "KG Fall_theta_ble": 0.021061656,
    "KG Fall_pr_1_99_ble": 64,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 502,
    "KG Winter_theta_ble": 0.409890222,
    "KG Winter_pr_1_99_ble": 64,
    "KG Winter_ss_ble": 105,
    "KG Winter_dss_ble": 541,
    "KG Spring_theta_ble": 0.724951094,
    "KG Spring_pr_1_99_ble": 64,
    "KG Spring_ss_ble": 105,
    "KG Spring_dss_ble": 572
  },
  {
    "ID": 319,
    "KG Fall_theta_ble": 0.021189496,
    "KG Fall_pr_1_99_ble": 64,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 502,
    "KG Winter_theta_ble": 0.412441674,
    "KG Winter_pr_1_99_ble": 64,
    "KG Winter_ss_ble": 105,
    "KG Winter_dss_ble": 541,
    "KG Spring_theta_ble": 0.725439427,
    "KG Spring_pr_1_99_ble": 64,
    "KG Spring_ss_ble": 105,
    "KG Spring_dss_ble": 573
  },
  {
    "ID": 320,
    "KG Fall_theta_ble": 0.021736294,
    "KG Fall_pr_1_99_ble": 64,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 502,
    "KG Winter_theta_ble": 0.418277755,
    "KG Winter_pr_1_99_ble": 64,
    "KG Winter_ss_ble": 105,
    "KG Winter_dss_ble": 542,
    "KG Spring_theta_ble": 0.726957713,
    "KG Spring_pr_1_99_ble": 64,
    "KG Spring_ss_ble": 106,
    "KG Spring_dss_ble": 573
  },
  {
    "ID": 321,
    "KG Fall_theta_ble": 0.02701243,
    "KG Fall_pr_1_99_ble": 64,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 503,
    "KG Winter_theta_ble": 0.4232935,
    "KG Winter_pr_1_99_ble": 64,
    "KG Winter_ss_ble": 105,
    "KG Winter_dss_ble": 542,
    "KG Spring_theta_ble": 0.727128299,
    "KG Spring_pr_1_99_ble": 64,
    "KG Spring_ss_ble": 106,
    "KG Spring_dss_ble": 573
  },
  {
    "ID": 322,
    "KG Fall_theta_ble": 0.032426688,
    "KG Fall_pr_1_99_ble": 64,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 503,
    "KG Winter_theta_ble": 0.427265286,
    "KG Winter_pr_1_99_ble": 64,
    "KG Winter_ss_ble": 105,
    "KG Winter_dss_ble": 543,
    "KG Spring_theta_ble": 0.74283831,
    "KG Spring_pr_1_99_ble": 64,
    "KG Spring_ss_ble": 106,
    "KG Spring_dss_ble": 574
  },
  {
    "ID": 323,
    "KG Fall_theta_ble": 0.033035065,
    "KG Fall_pr_1_99_ble": 65,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 503,
    "KG Winter_theta_ble": 0.450586819,
    "KG Winter_pr_1_99_ble": 65,
    "KG Winter_ss_ble": 106,
    "KG Winter_dss_ble": 545,
    "KG Spring_theta_ble": 0.748099674,
    "KG Spring_pr_1_99_ble": 65,
    "KG Spring_ss_ble": 106,
    "KG Spring_dss_ble": 575
  },
  {
    "ID": 324,
    "KG Fall_theta_ble": 0.033254939,
    "KG Fall_pr_1_99_ble": 65,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 503,
    "KG Winter_theta_ble": 0.452529996,
    "KG Winter_pr_1_99_ble": 65,
    "KG Winter_ss_ble": 106,
    "KG Winter_dss_ble": 545,
    "KG Spring_theta_ble": 0.748781972,
    "KG Spring_pr_1_99_ble": 65,
    "KG Spring_ss_ble": 106,
    "KG Spring_dss_ble": 575
  },
  {
    "ID": 325,
    "KG Fall_theta_ble": 0.037746685,
    "KG Fall_pr_1_99_ble": 65,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 504,
    "KG Winter_theta_ble": 0.470863313,
    "KG Winter_pr_1_99_ble": 65,
    "KG Winter_ss_ble": 106,
    "KG Winter_dss_ble": 547,
    "KG Spring_theta_ble": 0.749579083,
    "KG Spring_pr_1_99_ble": 65,
    "KG Spring_ss_ble": 106,
    "KG Spring_dss_ble": 575
  },
  {
    "ID": 326,
    "KG Fall_theta_ble": 0.04758181,
    "KG Fall_pr_1_99_ble": 65,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 505,
    "KG Winter_theta_ble": 0.476082796,
    "KG Winter_pr_1_99_ble": 65,
    "KG Winter_ss_ble": 106,
    "KG Winter_dss_ble": 548,
    "KG Spring_theta_ble": 0.750382108,
    "KG Spring_pr_1_99_ble": 65,
    "KG Spring_ss_ble": 106,
    "KG Spring_dss_ble": 575
  },
  {
    "ID": 327,
    "KG Fall_theta_ble": 0.049557296,
    "KG Fall_pr_1_99_ble": 65,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 505,
    "KG Winter_theta_ble": 0.478274874,
    "KG Winter_pr_1_99_ble": 65,
    "KG Winter_ss_ble": 106,
    "KG Winter_dss_ble": 548,
    "KG Spring_theta_ble": 0.77274823,
    "KG Spring_pr_1_99_ble": 65,
    "KG Spring_ss_ble": 106,
    "KG Spring_dss_ble": 577
  },
  {
    "ID": 328,
    "KG Fall_theta_ble": 0.057563449,
    "KG Fall_pr_1_99_ble": 66,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 506,
    "KG Winter_theta_ble": 0.485882911,
    "KG Winter_pr_1_99_ble": 66,
    "KG Winter_ss_ble": 106,
    "KG Winter_dss_ble": 549,
    "KG Spring_theta_ble": 0.777395796,
    "KG Spring_pr_1_99_ble": 66,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 578
  },
  {
    "ID": 329,
    "KG Fall_theta_ble": 0.061891836,
    "KG Fall_pr_1_99_ble": 66,
    "KG Fall_ss_ble": 105,
    "KG Fall_dss_ble": 506,
    "KG Winter_theta_ble": 0.487258678,
    "KG Winter_pr_1_99_ble": 66,
    "KG Winter_ss_ble": 106,
    "KG Winter_dss_ble": 549,
    "KG Spring_theta_ble": 0.79214227,
    "KG Spring_pr_1_99_ble": 66,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 579
  },
  {
    "ID": 330,
    "KG Fall_theta_ble": 0.063884592,
    "KG Fall_pr_1_99_ble": 66,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 506,
    "KG Winter_theta_ble": 0.489455546,
    "KG Winter_pr_1_99_ble": 66,
    "KG Winter_ss_ble": 106,
    "KG Winter_dss_ble": 549,
    "KG Spring_theta_ble": 0.793635043,
    "KG Spring_pr_1_99_ble": 66,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 579
  },
  {
    "ID": 331,
    "KG Fall_theta_ble": 0.070689633,
    "KG Fall_pr_1_99_ble": 66,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 507,
    "KG Winter_theta_ble": 0.497039684,
    "KG Winter_pr_1_99_ble": 66,
    "KG Winter_ss_ble": 106,
    "KG Winter_dss_ble": 550,
    "KG Spring_theta_ble": 0.794079721,
    "KG Spring_pr_1_99_ble": 66,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 579
  },
  {
    "ID": 332,
    "KG Fall_theta_ble": 0.074061952,
    "KG Fall_pr_1_99_ble": 66,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 507,
    "KG Winter_theta_ble": 0.498909775,
    "KG Winter_pr_1_99_ble": 66,
    "KG Winter_ss_ble": 106,
    "KG Winter_dss_ble": 550,
    "KG Spring_theta_ble": 0.794631387,
    "KG Spring_pr_1_99_ble": 66,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 579
  },
  {
    "ID": 333,
    "KG Fall_theta_ble": 0.082154124,
    "KG Fall_pr_1_99_ble": 67,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 508,
    "KG Winter_theta_ble": 0.506261063,
    "KG Winter_pr_1_99_ble": 67,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 551,
    "KG Spring_theta_ble": 0.79830195,
    "KG Spring_pr_1_99_ble": 67,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 580
  },
  {
    "ID": 334,
    "KG Fall_theta_ble": 0.085844387,
    "KG Fall_pr_1_99_ble": 67,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 509,
    "KG Winter_theta_ble": 0.510271013,
    "KG Winter_pr_1_99_ble": 67,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 551,
    "KG Spring_theta_ble": 0.798478407,
    "KG Spring_pr_1_99_ble": 67,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 580
  },
  {
    "ID": 335,
    "KG Fall_theta_ble": 0.098713056,
    "KG Fall_pr_1_99_ble": 67,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 510,
    "KG Winter_theta_ble": 0.514898961,
    "KG Winter_pr_1_99_ble": 67,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 551,
    "KG Spring_theta_ble": 0.80072003,
    "KG Spring_pr_1_99_ble": 67,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 580
  },
  {
    "ID": 336,
    "KG Fall_theta_ble": 0.106571228,
    "KG Fall_pr_1_99_ble": 67,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 511,
    "KG Winter_theta_ble": 0.519995775,
    "KG Winter_pr_1_99_ble": 67,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 552,
    "KG Spring_theta_ble": 0.803457672,
    "KG Spring_pr_1_99_ble": 67,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 580
  },
  {
    "ID": 337,
    "KG Fall_theta_ble": 0.112245285,
    "KG Fall_pr_1_99_ble": 67,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 511,
    "KG Winter_theta_ble": 0.52222833,
    "KG Winter_pr_1_99_ble": 67,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 552,
    "KG Spring_theta_ble": 0.804253168,
    "KG Spring_pr_1_99_ble": 67,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 580
  },
  {
    "ID": 338,
    "KG Fall_theta_ble": 0.114012083,
    "KG Fall_pr_1_99_ble": 68,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 511,
    "KG Winter_theta_ble": 0.52494686,
    "KG Winter_pr_1_99_ble": 68,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 552,
    "KG Spring_theta_ble": 0.80446463,
    "KG Spring_pr_1_99_ble": 68,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 580
  },
  {
    "ID": 339,
    "KG Fall_theta_ble": 0.115931141,
    "KG Fall_pr_1_99_ble": 68,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 512,
    "KG Winter_theta_ble": 0.527174625,
    "KG Winter_pr_1_99_ble": 68,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 553,
    "KG Spring_theta_ble": 0.808215696,
    "KG Spring_pr_1_99_ble": 68,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 581
  },
  {
    "ID": 340,
    "KG Fall_theta_ble": 0.11641471,
    "KG Fall_pr_1_99_ble": 68,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 512,
    "KG Winter_theta_ble": 0.531938518,
    "KG Winter_pr_1_99_ble": 68,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 553,
    "KG Spring_theta_ble": 0.808266362,
    "KG Spring_pr_1_99_ble": 68,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 581
  },
  {
    "ID": 341,
    "KG Fall_theta_ble": 0.117242741,
    "KG Fall_pr_1_99_ble": 68,
    "KG Fall_ss_ble": 106,
    "KG Fall_dss_ble": 512,
    "KG Winter_theta_ble": 0.534627409,
    "KG Winter_pr_1_99_ble": 68,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 553,
    "KG Spring_theta_ble": 0.808698977,
    "KG Spring_pr_1_99_ble": 68,
    "KG Spring_ss_ble": 107,
    "KG Spring_dss_ble": 581
  },
  {
    "ID": 342,
    "KG Fall_theta_ble": 0.131026231,
    "KG Fall_pr_1_99_ble": 68,
    "KG Fall_ss_ble": 107,
    "KG Fall_dss_ble": 513,
    "KG Winter_theta_ble": 0.535140113,
    "KG Winter_pr_1_99_ble": 68,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 554,
    "KG Spring_theta_ble": 0.82720826,
    "KG Spring_pr_1_99_ble": 68,
    "KG Spring_ss_ble": 108,
    "KG Spring_dss_ble": 583
  },
  {
    "ID": 343,
    "KG Fall_theta_ble": 0.134035995,
    "KG Fall_pr_1_99_ble": 69,
    "KG Fall_ss_ble": 107,
    "KG Fall_dss_ble": 513,
    "KG Winter_theta_ble": 0.53596357,
    "KG Winter_pr_1_99_ble": 69,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 554,
    "KG Spring_theta_ble": 0.834454434,
    "KG Spring_pr_1_99_ble": 69,
    "KG Spring_ss_ble": 108,
    "KG Spring_dss_ble": 583
  },
  {
    "ID": 344,
    "KG Fall_theta_ble": 0.145358516,
    "KG Fall_pr_1_99_ble": 69,
    "KG Fall_ss_ble": 107,
    "KG Fall_dss_ble": 515,
    "KG Winter_theta_ble": 0.55053142,
    "KG Winter_pr_1_99_ble": 69,
    "KG Winter_ss_ble": 107,
    "KG Winter_dss_ble": 555,
    "KG Spring_theta_ble": 0.834793149,
    "KG Spring_pr_1_99_ble": 69,
    "KG Spring_ss_ble": 108,
    "KG Spring_dss_ble": 583
  },
  {
    "ID": 345,
    "KG Fall_theta_ble": 0.153118304,
    "KG Fall_pr_1_99_ble": 69,
    "KG Fall_ss_ble": 107,
    "KG Fall_dss_ble": 515,
    "KG Winter_theta_ble": 0.558260766,
    "KG Winter_pr_1_99_ble": 69,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 556,
    "KG Spring_theta_ble": 0.841871743,
    "KG Spring_pr_1_99_ble": 69,
    "KG Spring_ss_ble": 108,
    "KG Spring_dss_ble": 584
  },
  {
    "ID": 346,
    "KG Fall_theta_ble": 0.158920019,
    "KG Fall_pr_1_99_ble": 69,
    "KG Fall_ss_ble": 107,
    "KG Fall_dss_ble": 516,
    "KG Winter_theta_ble": 0.558699635,
    "KG Winter_pr_1_99_ble": 69,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 556,
    "KG Spring_theta_ble": 0.855506776,
    "KG Spring_pr_1_99_ble": 69,
    "KG Spring_ss_ble": 108,
    "KG Spring_dss_ble": 586
  },
  {
    "ID": 347,
    "KG Fall_theta_ble": 0.165920307,
    "KG Fall_pr_1_99_ble": 69,
    "KG Fall_ss_ble": 107,
    "KG Fall_dss_ble": 517,
    "KG Winter_theta_ble": 0.563813351,
    "KG Winter_pr_1_99_ble": 69,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 556,
    "KG Spring_theta_ble": 0.860189076,
    "KG Spring_pr_1_99_ble": 69,
    "KG Spring_ss_ble": 108,
    "KG Spring_dss_ble": 586
  },
  {
    "ID": 348,
    "KG Fall_theta_ble": 0.168778768,
    "KG Fall_pr_1_99_ble": 70,
    "KG Fall_ss_ble": 107,
    "KG Fall_dss_ble": 517,
    "KG Winter_theta_ble": 0.564144937,
    "KG Winter_pr_1_99_ble": 70,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 556,
    "KG Spring_theta_ble": 0.86400892,
    "KG Spring_pr_1_99_ble": 70,
    "KG Spring_ss_ble": 108,
    "KG Spring_dss_ble": 586
  },
  {
    "ID": 349,
    "KG Fall_theta_ble": 0.17791341,
    "KG Fall_pr_1_99_ble": 70,
    "KG Fall_ss_ble": 107,
    "KG Fall_dss_ble": 518,
    "KG Winter_theta_ble": 0.570927481,
    "KG Winter_pr_1_99_ble": 70,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 557,
    "KG Spring_theta_ble": 0.864303963,
    "KG Spring_pr_1_99_ble": 70,
    "KG Spring_ss_ble": 108,
    "KG Spring_dss_ble": 586
  },
  {
    "ID": 350,
    "KG Fall_theta_ble": 0.179845445,
    "KG Fall_pr_1_99_ble": 70,
    "KG Fall_ss_ble": 107,
    "KG Fall_dss_ble": 518,
    "KG Winter_theta_ble": 0.588424284,
    "KG Winter_pr_1_99_ble": 70,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 559,
    "KG Spring_theta_ble": 0.872532088,
    "KG Spring_pr_1_99_ble": 70,
    "KG Spring_ss_ble": 108,
    "KG Spring_dss_ble": 587
  },
  {
    "ID": 351,
    "KG Fall_theta_ble": 0.191532843,
    "KG Fall_pr_1_99_ble": 70,
    "KG Fall_ss_ble": 108,
    "KG Fall_dss_ble": 519,
    "KG Winter_theta_ble": 0.59154715,
    "KG Winter_pr_1_99_ble": 70,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 559,
    "KG Spring_theta_ble": 0.875181674,
    "KG Spring_pr_1_99_ble": 70,
    "KG Spring_ss_ble": 108,
    "KG Spring_dss_ble": 588
  },
  {
    "ID": 352,
    "KG Fall_theta_ble": 0.193281723,
    "KG Fall_pr_1_99_ble": 70,
    "KG Fall_ss_ble": 108,
    "KG Fall_dss_ble": 519,
    "KG Winter_theta_ble": 0.593773195,
    "KG Winter_pr_1_99_ble": 70,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 559,
    "KG Spring_theta_ble": 0.880646697,
    "KG Spring_pr_1_99_ble": 70,
    "KG Spring_ss_ble": 109,
    "KG Spring_dss_ble": 588
  },
  {
    "ID": 353,
    "KG Fall_theta_ble": 0.210119753,
    "KG Fall_pr_1_99_ble": 71,
    "KG Fall_ss_ble": 108,
    "KG Fall_dss_ble": 521,
    "KG Winter_theta_ble": 0.602647368,
    "KG Winter_pr_1_99_ble": 71,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 560,
    "KG Spring_theta_ble": 0.881090156,
    "KG Spring_pr_1_99_ble": 71,
    "KG Spring_ss_ble": 109,
    "KG Spring_dss_ble": 588
  },
  {
    "ID": 354,
    "KG Fall_theta_ble": 0.216059549,
    "KG Fall_pr_1_99_ble": 71,
    "KG Fall_ss_ble": 108,
    "KG Fall_dss_ble": 522,
    "KG Winter_theta_ble": 0.606568675,
    "KG Winter_pr_1_99_ble": 71,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 561,
    "KG Spring_theta_ble": 0.883974962,
    "KG Spring_pr_1_99_ble": 71,
    "KG Spring_ss_ble": 109,
    "KG Spring_dss_ble": 588
  },
  {
    "ID": 355,
    "KG Fall_theta_ble": 0.217356056,
    "KG Fall_pr_1_99_ble": 71,
    "KG Fall_ss_ble": 108,
    "KG Fall_dss_ble": 522,
    "KG Winter_theta_ble": 0.610935385,
    "KG Winter_pr_1_99_ble": 71,
    "KG Winter_ss_ble": 108,
    "KG Winter_dss_ble": 561,
    "KG Spring_theta_ble": 0.887398257,
    "KG Spring_pr_1_99_ble": 71,
    "KG Spring_ss_ble": 109,
    "KG Spring_dss_ble": 589
  },
  {
    "ID": 356,
    "KG Fall_theta_ble": 0.222545216,
    "KG Fall_pr_1_99_ble": 71,
    "KG Fall_ss_ble": 108,
    "KG Fall_dss_ble": 522,
    "KG Winter_theta_ble": 0.613404612,
    "KG Winter_pr_1_99_ble": 71,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 561,
    "KG Spring_theta_ble": 0.897945184,
    "KG Spring_pr_1_99_ble": 71,
    "KG Spring_ss_ble": 109,
    "KG Spring_dss_ble": 590
  },
  {
    "ID": 357,
    "KG Fall_theta_ble": 0.249112294,
    "KG Fall_pr_1_99_ble": 71,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 525,
    "KG Winter_theta_ble": 0.614355489,
    "KG Winter_pr_1_99_ble": 71,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 561,
    "KG Spring_theta_ble": 0.899774114,
    "KG Spring_pr_1_99_ble": 71,
    "KG Spring_ss_ble": 109,
    "KG Spring_dss_ble": 590
  },
  {
    "ID": 358,
    "KG Fall_theta_ble": 0.258296453,
    "KG Fall_pr_1_99_ble": 72,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 526,
    "KG Winter_theta_ble": 0.619186054,
    "KG Winter_pr_1_99_ble": 72,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 562,
    "KG Spring_theta_ble": 0.911371792,
    "KG Spring_pr_1_99_ble": 72,
    "KG Spring_ss_ble": 109,
    "KG Spring_dss_ble": 591
  },
  {
    "ID": 359,
    "KG Fall_theta_ble": 0.259954046,
    "KG Fall_pr_1_99_ble": 72,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 526,
    "KG Winter_theta_ble": 0.621124753,
    "KG Winter_pr_1_99_ble": 72,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 562,
    "KG Spring_theta_ble": 0.923693578,
    "KG Spring_pr_1_99_ble": 72,
    "KG Spring_ss_ble": 109,
    "KG Spring_dss_ble": 592
  },
  {
    "ID": 360,
    "KG Fall_theta_ble": 0.260349379,
    "KG Fall_pr_1_99_ble": 72,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 526,
    "KG Winter_theta_ble": 0.623889649,
    "KG Winter_pr_1_99_ble": 72,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 562,
    "KG Spring_theta_ble": 0.923760578,
    "KG Spring_pr_1_99_ble": 72,
    "KG Spring_ss_ble": 109,
    "KG Spring_dss_ble": 592
  },
  {
    "ID": 361,
    "KG Fall_theta_ble": 0.267090171,
    "KG Fall_pr_1_99_ble": 72,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 527,
    "KG Winter_theta_ble": 0.632064056,
    "KG Winter_pr_1_99_ble": 72,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 563,
    "KG Spring_theta_ble": 0.928948548,
    "KG Spring_pr_1_99_ble": 72,
    "KG Spring_ss_ble": 110,
    "KG Spring_dss_ble": 593
  },
  {
    "ID": 362,
    "KG Fall_theta_ble": 0.267407032,
    "KG Fall_pr_1_99_ble": 72,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 527,
    "KG Winter_theta_ble": 0.641307357,
    "KG Winter_pr_1_99_ble": 72,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 564,
    "KG Spring_theta_ble": 0.933332359,
    "KG Spring_pr_1_99_ble": 72,
    "KG Spring_ss_ble": 110,
    "KG Spring_dss_ble": 593
  },
  {
    "ID": 363,
    "KG Fall_theta_ble": 0.269729291,
    "KG Fall_pr_1_99_ble": 73,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 527,
    "KG Winter_theta_ble": 0.645242068,
    "KG Winter_pr_1_99_ble": 73,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 565,
    "KG Spring_theta_ble": 0.938222929,
    "KG Spring_pr_1_99_ble": 73,
    "KG Spring_ss_ble": 110,
    "KG Spring_dss_ble": 594
  },
  {
    "ID": 364,
    "KG Fall_theta_ble": 0.272632203,
    "KG Fall_pr_1_99_ble": 73,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 527,
    "KG Winter_theta_ble": 0.65441379,
    "KG Winter_pr_1_99_ble": 73,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 565,
    "KG Spring_theta_ble": 0.942017455,
    "KG Spring_pr_1_99_ble": 73,
    "KG Spring_ss_ble": 110,
    "KG Spring_dss_ble": 594
  },
  {
    "ID": 365,
    "KG Fall_theta_ble": 0.276118142,
    "KG Fall_pr_1_99_ble": 73,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 528,
    "KG Winter_theta_ble": 0.662554841,
    "KG Winter_pr_1_99_ble": 73,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 566,
    "KG Spring_theta_ble": 0.942437337,
    "KG Spring_pr_1_99_ble": 73,
    "KG Spring_ss_ble": 110,
    "KG Spring_dss_ble": 594
  },
  {
    "ID": 366,
    "KG Fall_theta_ble": 0.277044224,
    "KG Fall_pr_1_99_ble": 73,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 528,
    "KG Winter_theta_ble": 0.665244886,
    "KG Winter_pr_1_99_ble": 73,
    "KG Winter_ss_ble": 109,
    "KG Winter_dss_ble": 567,
    "KG Spring_theta_ble": 0.946315329,
    "KG Spring_pr_1_99_ble": 73,
    "KG Spring_ss_ble": 110,
    "KG Spring_dss_ble": 595
  },
  {
    "ID": 367,
    "KG Fall_theta_ble": 0.285593305,
    "KG Fall_pr_1_99_ble": 73,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 529,
    "KG Winter_theta_ble": 0.66844955,
    "KG Winter_pr_1_99_ble": 73,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 567,
    "KG Spring_theta_ble": 0.950988015,
    "KG Spring_pr_1_99_ble": 73,
    "KG Spring_ss_ble": 110,
    "KG Spring_dss_ble": 595
  },
  {
    "ID": 368,
    "KG Fall_theta_ble": 0.288131299,
    "KG Fall_pr_1_99_ble": 74,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 529,
    "KG Winter_theta_ble": 0.670290667,
    "KG Winter_pr_1_99_ble": 74,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 567,
    "KG Spring_theta_ble": 0.958081767,
    "KG Spring_pr_1_99_ble": 74,
    "KG Spring_ss_ble": 110,
    "KG Spring_dss_ble": 596
  },
  {
    "ID": 369,
    "KG Fall_theta_ble": 0.288837809,
    "KG Fall_pr_1_99_ble": 74,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 529,
    "KG Winter_theta_ble": 0.671835999,
    "KG Winter_pr_1_99_ble": 74,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 567,
    "KG Spring_theta_ble": 0.961739347,
    "KG Spring_pr_1_99_ble": 74,
    "KG Spring_ss_ble": 110,
    "KG Spring_dss_ble": 596
  },
  {
    "ID": 370,
    "KG Fall_theta_ble": 0.290410296,
    "KG Fall_pr_1_99_ble": 74,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 529,
    "KG Winter_theta_ble": 0.675555991,
    "KG Winter_pr_1_99_ble": 74,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 568,
    "KG Spring_theta_ble": 0.97127491,
    "KG Spring_pr_1_99_ble": 74,
    "KG Spring_ss_ble": 110,
    "KG Spring_dss_ble": 597
  },
  {
    "ID": 371,
    "KG Fall_theta_ble": 0.293125063,
    "KG Fall_pr_1_99_ble": 74,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 529,
    "KG Winter_theta_ble": 0.684901091,
    "KG Winter_pr_1_99_ble": 74,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 568,
    "KG Spring_theta_ble": 0.980574922,
    "KG Spring_pr_1_99_ble": 74,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 598
  },
  {
    "ID": 372,
    "KG Fall_theta_ble": 0.293182802,
    "KG Fall_pr_1_99_ble": 74,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 529,
    "KG Winter_theta_ble": 0.688719949,
    "KG Winter_pr_1_99_ble": 74,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 569,
    "KG Spring_theta_ble": 0.981696534,
    "KG Spring_pr_1_99_ble": 74,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 598
  },
  {
    "ID": 373,
    "KG Fall_theta_ble": 0.294494841,
    "KG Fall_pr_1_99_ble": 75,
    "KG Fall_ss_ble": 109,
    "KG Fall_dss_ble": 529,
    "KG Winter_theta_ble": 0.693707241,
    "KG Winter_pr_1_99_ble": 75,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 569,
    "KG Spring_theta_ble": 0.982222381,
    "KG Spring_pr_1_99_ble": 75,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 598
  },
  {
    "ID": 374,
    "KG Fall_theta_ble": 0.314896761,
    "KG Fall_pr_1_99_ble": 75,
    "KG Fall_ss_ble": 110,
    "KG Fall_dss_ble": 531,
    "KG Winter_theta_ble": 0.697266954,
    "KG Winter_pr_1_99_ble": 75,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 570,
    "KG Spring_theta_ble": 0.98586862,
    "KG Spring_pr_1_99_ble": 75,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 599
  },
  {
    "ID": 375,
    "KG Fall_theta_ble": 0.315746995,
    "KG Fall_pr_1_99_ble": 75,
    "KG Fall_ss_ble": 110,
    "KG Fall_dss_ble": 532,
    "KG Winter_theta_ble": 0.699567272,
    "KG Winter_pr_1_99_ble": 75,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 570,
    "KG Spring_theta_ble": 0.987277627,
    "KG Spring_pr_1_99_ble": 75,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 599
  },
  {
    "ID": 376,
    "KG Fall_theta_ble": 0.322733847,
    "KG Fall_pr_1_99_ble": 75,
    "KG Fall_ss_ble": 110,
    "KG Fall_dss_ble": 532,
    "KG Winter_theta_ble": 0.707747108,
    "KG Winter_pr_1_99_ble": 75,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 571,
    "KG Spring_theta_ble": 0.989962219,
    "KG Spring_pr_1_99_ble": 75,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 599
  },
  {
    "ID": 377,
    "KG Fall_theta_ble": 0.329659527,
    "KG Fall_pr_1_99_ble": 75,
    "KG Fall_ss_ble": 110,
    "KG Fall_dss_ble": 533,
    "KG Winter_theta_ble": 0.720308128,
    "KG Winter_pr_1_99_ble": 75,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 572,
    "KG Spring_theta_ble": 0.990983809,
    "KG Spring_pr_1_99_ble": 75,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 599
  },
  {
    "ID": 378,
    "KG Fall_theta_ble": 0.357492739,
    "KG Fall_pr_1_99_ble": 76,
    "KG Fall_ss_ble": 111,
    "KG Fall_dss_ble": 536,
    "KG Winter_theta_ble": 0.721738431,
    "KG Winter_pr_1_99_ble": 76,
    "KG Winter_ss_ble": 110,
    "KG Winter_dss_ble": 572,
    "KG Spring_theta_ble": 0.994675801,
    "KG Spring_pr_1_99_ble": 76,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 599
  },
  {
    "ID": 379,
    "KG Fall_theta_ble": 0.365069612,
    "KG Fall_pr_1_99_ble": 76,
    "KG Fall_ss_ble": 111,
    "KG Fall_dss_ble": 537,
    "KG Winter_theta_ble": 0.724885752,
    "KG Winter_pr_1_99_ble": 76,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 572,
    "KG Spring_theta_ble": 0.994771607,
    "KG Spring_pr_1_99_ble": 76,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 599
  },
  {
    "ID": 380,
    "KG Fall_theta_ble": 0.374882056,
    "KG Fall_pr_1_99_ble": 76,
    "KG Fall_ss_ble": 111,
    "KG Fall_dss_ble": 537,
    "KG Winter_theta_ble": 0.728528665,
    "KG Winter_pr_1_99_ble": 76,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 573,
    "KG Spring_theta_ble": 1.00794476,
    "KG Spring_pr_1_99_ble": 76,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 601
  },
  {
    "ID": 381,
    "KG Fall_theta_ble": 0.380056354,
    "KG Fall_pr_1_99_ble": 76,
    "KG Fall_ss_ble": 111,
    "KG Fall_dss_ble": 538,
    "KG Winter_theta_ble": 0.735665477,
    "KG Winter_pr_1_99_ble": 76,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 574,
    "KG Spring_theta_ble": 1.008234509,
    "KG Spring_pr_1_99_ble": 76,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 601
  },
  {
    "ID": 382,
    "KG Fall_theta_ble": 0.390070033,
    "KG Fall_pr_1_99_ble": 76,
    "KG Fall_ss_ble": 111,
    "KG Fall_dss_ble": 539,
    "KG Winter_theta_ble": 0.741308553,
    "KG Winter_pr_1_99_ble": 76,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 574,
    "KG Spring_theta_ble": 1.008255591,
    "KG Spring_pr_1_99_ble": 76,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 601
  },
  {
    "ID": 383,
    "KG Fall_theta_ble": 0.393800709,
    "KG Fall_pr_1_99_ble": 77,
    "KG Fall_ss_ble": 111,
    "KG Fall_dss_ble": 539,
    "KG Winter_theta_ble": 0.75105832,
    "KG Winter_pr_1_99_ble": 77,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 575,
    "KG Spring_theta_ble": 1.019181097,
    "KG Spring_pr_1_99_ble": 77,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 602
  },
  {
    "ID": 384,
    "KG Fall_theta_ble": 0.400506861,
    "KG Fall_pr_1_99_ble": 77,
    "KG Fall_ss_ble": 111,
    "KG Fall_dss_ble": 540,
    "KG Winter_theta_ble": 0.753461304,
    "KG Winter_pr_1_99_ble": 77,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 575,
    "KG Spring_theta_ble": 1.020036937,
    "KG Spring_pr_1_99_ble": 77,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 602
  },
  {
    "ID": 385,
    "KG Fall_theta_ble": 0.401663717,
    "KG Fall_pr_1_99_ble": 77,
    "KG Fall_ss_ble": 111,
    "KG Fall_dss_ble": 540,
    "KG Winter_theta_ble": 0.756470078,
    "KG Winter_pr_1_99_ble": 77,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 576,
    "KG Spring_theta_ble": 1.020435934,
    "KG Spring_pr_1_99_ble": 77,
    "KG Spring_ss_ble": 111,
    "KG Spring_dss_ble": 602
  },
  {
    "ID": 386,
    "KG Fall_theta_ble": 0.408727,
    "KG Fall_pr_1_99_ble": 77,
    "KG Fall_ss_ble": 111,
    "KG Fall_dss_ble": 541,
    "KG Winter_theta_ble": 0.758419549,
    "KG Winter_pr_1_99_ble": 77,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 576,
    "KG Spring_theta_ble": 1.028829561,
    "KG Spring_pr_1_99_ble": 77,
    "KG Spring_ss_ble": 112,
    "KG Spring_dss_ble": 603
  },
  {
    "ID": 387,
    "KG Fall_theta_ble": 0.41751336,
    "KG Fall_pr_1_99_ble": 77,
    "KG Fall_ss_ble": 112,
    "KG Fall_dss_ble": 542,
    "KG Winter_theta_ble": 0.758424951,
    "KG Winter_pr_1_99_ble": 77,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 576,
    "KG Spring_theta_ble": 1.03117046,
    "KG Spring_pr_1_99_ble": 77,
    "KG Spring_ss_ble": 112,
    "KG Spring_dss_ble": 603
  },
  {
    "ID": 388,
    "KG Fall_theta_ble": 0.418871778,
    "KG Fall_pr_1_99_ble": 78,
    "KG Fall_ss_ble": 112,
    "KG Fall_dss_ble": 542,
    "KG Winter_theta_ble": 0.758459159,
    "KG Winter_pr_1_99_ble": 78,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 576,
    "KG Spring_theta_ble": 1.047585152,
    "KG Spring_pr_1_99_ble": 78,
    "KG Spring_ss_ble": 112,
    "KG Spring_dss_ble": 605
  },
  {
    "ID": 389,
    "KG Fall_theta_ble": 0.432934349,
    "KG Fall_pr_1_99_ble": 78,
    "KG Fall_ss_ble": 112,
    "KG Fall_dss_ble": 543,
    "KG Winter_theta_ble": 0.773949603,
    "KG Winter_pr_1_99_ble": 78,
    "KG Winter_ss_ble": 111,
    "KG Winter_dss_ble": 577,
    "KG Spring_theta_ble": 1.05024541,
    "KG Spring_pr_1_99_ble": 78,
    "KG Spring_ss_ble": 112,
    "KG Spring_dss_ble": 605
  },
  {
    "ID": 390,
    "KG Fall_theta_ble": 0.436605474,
    "KG Fall_pr_1_99_ble": 78,
    "KG Fall_ss_ble": 112,
    "KG Fall_dss_ble": 544,
    "KG Winter_theta_ble": 0.786125949,
    "KG Winter_pr_1_99_ble": 78,
    "KG Winter_ss_ble": 112,
    "KG Winter_dss_ble": 579,
    "KG Spring_theta_ble": 1.05687711,
    "KG Spring_pr_1_99_ble": 78,
    "KG Spring_ss_ble": 112,
    "KG Spring_dss_ble": 606
  },
  {
    "ID": 391,
    "KG Fall_theta_ble": 0.438919871,
    "KG Fall_pr_1_99_ble": 78,
    "KG Fall_ss_ble": 112,
    "KG Fall_dss_ble": 544,
    "KG Winter_theta_ble": 0.793383606,
    "KG Winter_pr_1_99_ble": 78,
    "KG Winter_ss_ble": 112,
    "KG Winter_dss_ble": 579,
    "KG Spring_theta_ble": 1.068874222,
    "KG Spring_pr_1_99_ble": 78,
    "KG Spring_ss_ble": 112,
    "KG Spring_dss_ble": 607
  },
  {
    "ID": 392,
    "KG Fall_theta_ble": 0.447697761,
    "KG Fall_pr_1_99_ble": 78,
    "KG Fall_ss_ble": 112,
    "KG Fall_dss_ble": 545,
    "KG Winter_theta_ble": 0.802023386,
    "KG Winter_pr_1_99_ble": 78,
    "KG Winter_ss_ble": 112,
    "KG Winter_dss_ble": 580,
    "KG Spring_theta_ble": 1.069167892,
    "KG Spring_pr_1_99_ble": 78,
    "KG Spring_ss_ble": 112,
    "KG Spring_dss_ble": 607
  },
  {
    "ID": 393,
    "KG Fall_theta_ble": 0.448716405,
    "KG Fall_pr_1_99_ble": 79,
    "KG Fall_ss_ble": 112,
    "KG Fall_dss_ble": 545,
    "KG Winter_theta_ble": 0.811624238,
    "KG Winter_pr_1_99_ble": 79,
    "KG Winter_ss_ble": 112,
    "KG Winter_dss_ble": 581,
    "KG Spring_theta_ble": 1.0948711,
    "KG Spring_pr_1_99_ble": 79,
    "KG Spring_ss_ble": 113,
    "KG Spring_dss_ble": 609
  },
  {
    "ID": 394,
    "KG Fall_theta_ble": 0.450955958,
    "KG Fall_pr_1_99_ble": 79,
    "KG Fall_ss_ble": 112,
    "KG Fall_dss_ble": 545,
    "KG Winter_theta_ble": 0.813845439,
    "KG Winter_pr_1_99_ble": 79,
    "KG Winter_ss_ble": 112,
    "KG Winter_dss_ble": 581,
    "KG Spring_theta_ble": 1.096456385,
    "KG Spring_pr_1_99_ble": 79,
    "KG Spring_ss_ble": 113,
    "KG Spring_dss_ble": 610
  },
  {
    "ID": 395,
    "KG Fall_theta_ble": 0.473178628,
    "KG Fall_pr_1_99_ble": 79,
    "KG Fall_ss_ble": 112,
    "KG Fall_dss_ble": 547,
    "KG Winter_theta_ble": 0.819011346,
    "KG Winter_pr_1_99_ble": 79,
    "KG Winter_ss_ble": 112,
    "KG Winter_dss_ble": 582,
    "KG Spring_theta_ble": 1.103728186,
    "KG Spring_pr_1_99_ble": 79,
    "KG Spring_ss_ble": 113,
    "KG Spring_dss_ble": 610
  },
  {
    "ID": 396,
    "KG Fall_theta_ble": 0.480986121,
    "KG Fall_pr_1_99_ble": 79,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 548,
    "KG Winter_theta_ble": 0.832421301,
    "KG Winter_pr_1_99_ble": 79,
    "KG Winter_ss_ble": 112,
    "KG Winter_dss_ble": 583,
    "KG Spring_theta_ble": 1.116687703,
    "KG Spring_pr_1_99_ble": 79,
    "KG Spring_ss_ble": 113,
    "KG Spring_dss_ble": 612
  },
  {
    "ID": 397,
    "KG Fall_theta_ble": 0.485960658,
    "KG Fall_pr_1_99_ble": 79,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 549,
    "KG Winter_theta_ble": 0.839185632,
    "KG Winter_pr_1_99_ble": 79,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 584,
    "KG Spring_theta_ble": 1.11739892,
    "KG Spring_pr_1_99_ble": 79,
    "KG Spring_ss_ble": 113,
    "KG Spring_dss_ble": 612
  },
  {
    "ID": 398,
    "KG Fall_theta_ble": 0.499558346,
    "KG Fall_pr_1_99_ble": 80,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 550,
    "KG Winter_theta_ble": 0.839412099,
    "KG Winter_pr_1_99_ble": 80,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 584,
    "KG Spring_theta_ble": 1.11883914,
    "KG Spring_pr_1_99_ble": 80,
    "KG Spring_ss_ble": 113,
    "KG Spring_dss_ble": 612
  },
  {
    "ID": 399,
    "KG Fall_theta_ble": 0.500552924,
    "KG Fall_pr_1_99_ble": 80,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 550,
    "KG Winter_theta_ble": 0.848523279,
    "KG Winter_pr_1_99_ble": 80,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 585,
    "KG Spring_theta_ble": 1.130549334,
    "KG Spring_pr_1_99_ble": 80,
    "KG Spring_ss_ble": 114,
    "KG Spring_dss_ble": 613
  },
  {
    "ID": 400,
    "KG Fall_theta_ble": 0.50744461,
    "KG Fall_pr_1_99_ble": 80,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 551,
    "KG Winter_theta_ble": 0.856760557,
    "KG Winter_pr_1_99_ble": 80,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 586,
    "KG Spring_theta_ble": 1.15498141,
    "KG Spring_pr_1_99_ble": 80,
    "KG Spring_ss_ble": 114,
    "KG Spring_dss_ble": 615
  },
  {
    "ID": 401,
    "KG Fall_theta_ble": 0.512138397,
    "KG Fall_pr_1_99_ble": 80,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 551,
    "KG Winter_theta_ble": 0.862918835,
    "KG Winter_pr_1_99_ble": 80,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 586,
    "KG Spring_theta_ble": 1.163607398,
    "KG Spring_pr_1_99_ble": 80,
    "KG Spring_ss_ble": 114,
    "KG Spring_dss_ble": 616
  },
  {
    "ID": 402,
    "KG Fall_theta_ble": 0.512514223,
    "KG Fall_pr_1_99_ble": 80,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 551,
    "KG Winter_theta_ble": 0.86866857,
    "KG Winter_pr_1_99_ble": 80,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 587,
    "KG Spring_theta_ble": 1.16398074,
    "KG Spring_pr_1_99_ble": 80,
    "KG Spring_ss_ble": 114,
    "KG Spring_dss_ble": 616
  },
  {
    "ID": 403,
    "KG Fall_theta_ble": 0.51369232,
    "KG Fall_pr_1_99_ble": 81,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 551,
    "KG Winter_theta_ble": 0.873736481,
    "KG Winter_pr_1_99_ble": 81,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 587,
    "KG Spring_theta_ble": 1.16504101,
    "KG Spring_pr_1_99_ble": 81,
    "KG Spring_ss_ble": 114,
    "KG Spring_dss_ble": 617
  },
  {
    "ID": 404,
    "KG Fall_theta_ble": 0.514568429,
    "KG Fall_pr_1_99_ble": 81,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 551,
    "KG Winter_theta_ble": 0.874215055,
    "KG Winter_pr_1_99_ble": 81,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 587,
    "KG Spring_theta_ble": 1.168348825,
    "KG Spring_pr_1_99_ble": 81,
    "KG Spring_ss_ble": 114,
    "KG Spring_dss_ble": 617
  },
  {
    "ID": 405,
    "KG Fall_theta_ble": 0.514997846,
    "KG Fall_pr_1_99_ble": 81,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 551,
    "KG Winter_theta_ble": 0.875227587,
    "KG Winter_pr_1_99_ble": 81,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 588,
    "KG Spring_theta_ble": 1.169595362,
    "KG Spring_pr_1_99_ble": 81,
    "KG Spring_ss_ble": 114,
    "KG Spring_dss_ble": 617
  },
  {
    "ID": 406,
    "KG Fall_theta_ble": 0.517260731,
    "KG Fall_pr_1_99_ble": 81,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 552,
    "KG Winter_theta_ble": 0.879795052,
    "KG Winter_pr_1_99_ble": 81,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 588,
    "KG Spring_theta_ble": 1.175077579,
    "KG Spring_pr_1_99_ble": 81,
    "KG Spring_ss_ble": 114,
    "KG Spring_dss_ble": 618
  },
  {
    "ID": 407,
    "KG Fall_theta_ble": 0.522683263,
    "KG Fall_pr_1_99_ble": 81,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 552,
    "KG Winter_theta_ble": 0.887590075,
    "KG Winter_pr_1_99_ble": 81,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 589,
    "KG Spring_theta_ble": 1.175083779,
    "KG Spring_pr_1_99_ble": 81,
    "KG Spring_ss_ble": 114,
    "KG Spring_dss_ble": 618
  },
  {
    "ID": 408,
    "KG Fall_theta_ble": 0.529971536,
    "KG Fall_pr_1_99_ble": 82,
    "KG Fall_ss_ble": 113,
    "KG Fall_dss_ble": 553,
    "KG Winter_theta_ble": 0.888150422,
    "KG Winter_pr_1_99_ble": 82,
    "KG Winter_ss_ble": 113,
    "KG Winter_dss_ble": 589,
    "KG Spring_theta_ble": 1.17886695,
    "KG Spring_pr_1_99_ble": 82,
    "KG Spring_ss_ble": 114,
    "KG Spring_dss_ble": 618
  },
  {
    "ID": 409,
    "KG Fall_theta_ble": 0.533139075,
    "KG Fall_pr_1_99_ble": 82,
    "KG Fall_ss_ble": 114,
    "KG Fall_dss_ble": 553,
    "KG Winter_theta_ble": 0.900548082,
    "KG Winter_pr_1_99_ble": 82,
    "KG Winter_ss_ble": 114,
    "KG Winter_dss_ble": 590,
    "KG Spring_theta_ble": 1.183393727,
    "KG Spring_pr_1_99_ble": 82,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 618
  },
  {
    "ID": 410,
    "KG Fall_theta_ble": 0.533282407,
    "KG Fall_pr_1_99_ble": 82,
    "KG Fall_ss_ble": 114,
    "KG Fall_dss_ble": 553,
    "KG Winter_theta_ble": 0.901487668,
    "KG Winter_pr_1_99_ble": 82,
    "KG Winter_ss_ble": 114,
    "KG Winter_dss_ble": 590,
    "KG Spring_theta_ble": 1.183512891,
    "KG Spring_pr_1_99_ble": 82,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 618
  },
  {
    "ID": 411,
    "KG Fall_theta_ble": 0.537995296,
    "KG Fall_pr_1_99_ble": 82,
    "KG Fall_ss_ble": 114,
    "KG Fall_dss_ble": 554,
    "KG Winter_theta_ble": 0.907926222,
    "KG Winter_pr_1_99_ble": 82,
    "KG Winter_ss_ble": 114,
    "KG Winter_dss_ble": 591,
    "KG Spring_theta_ble": 1.184780584,
    "KG Spring_pr_1_99_ble": 82,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 618
  },
  {
    "ID": 412,
    "KG Fall_theta_ble": 0.541064939,
    "KG Fall_pr_1_99_ble": 82,
    "KG Fall_ss_ble": 114,
    "KG Fall_dss_ble": 554,
    "KG Winter_theta_ble": 0.922165597,
    "KG Winter_pr_1_99_ble": 82,
    "KG Winter_ss_ble": 114,
    "KG Winter_dss_ble": 592,
    "KG Spring_theta_ble": 1.185615844,
    "KG Spring_pr_1_99_ble": 82,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 619
  },
  {
    "ID": 413,
    "KG Fall_theta_ble": 0.541568957,
    "KG Fall_pr_1_99_ble": 83,
    "KG Fall_ss_ble": 114,
    "KG Fall_dss_ble": 554,
    "KG Winter_theta_ble": 0.923327409,
    "KG Winter_pr_1_99_ble": 83,
    "KG Winter_ss_ble": 114,
    "KG Winter_dss_ble": 592,
    "KG Spring_theta_ble": 1.205445637,
    "KG Spring_pr_1_99_ble": 83,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 621
  },
  {
    "ID": 414,
    "KG Fall_theta_ble": 0.542416908,
    "KG Fall_pr_1_99_ble": 83,
    "KG Fall_ss_ble": 114,
    "KG Fall_dss_ble": 554,
    "KG Winter_theta_ble": 0.930549528,
    "KG Winter_pr_1_99_ble": 83,
    "KG Winter_ss_ble": 114,
    "KG Winter_dss_ble": 593,
    "KG Spring_theta_ble": 1.212115647,
    "KG Spring_pr_1_99_ble": 83,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 621
  },
  {
    "ID": 415,
    "KG Fall_theta_ble": 0.553672211,
    "KG Fall_pr_1_99_ble": 83,
    "KG Fall_ss_ble": 114,
    "KG Fall_dss_ble": 555,
    "KG Winter_theta_ble": 0.935646935,
    "KG Winter_pr_1_99_ble": 83,
    "KG Winter_ss_ble": 114,
    "KG Winter_dss_ble": 594,
    "KG Spring_theta_ble": 1.213475789,
    "KG Spring_pr_1_99_ble": 83,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 621
  },
  {
    "ID": 416,
    "KG Fall_theta_ble": 0.578789529,
    "KG Fall_pr_1_99_ble": 83,
    "KG Fall_ss_ble": 114,
    "KG Fall_dss_ble": 558,
    "KG Winter_theta_ble": 0.948338962,
    "KG Winter_pr_1_99_ble": 83,
    "KG Winter_ss_ble": 115,
    "KG Winter_dss_ble": 595,
    "KG Spring_theta_ble": 1.214607345,
    "KG Spring_pr_1_99_ble": 83,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 621
  },
  {
    "ID": 417,
    "KG Fall_theta_ble": 0.589142409,
    "KG Fall_pr_1_99_ble": 83,
    "KG Fall_ss_ble": 114,
    "KG Fall_dss_ble": 559,
    "KG Winter_theta_ble": 0.95408342,
    "KG Winter_pr_1_99_ble": 83,
    "KG Winter_ss_ble": 115,
    "KG Winter_dss_ble": 595,
    "KG Spring_theta_ble": 1.217199522,
    "KG Spring_pr_1_99_ble": 83,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 622
  },
  {
    "ID": 418,
    "KG Fall_theta_ble": 0.597169364,
    "KG Fall_pr_1_99_ble": 84,
    "KG Fall_ss_ble": 115,
    "KG Fall_dss_ble": 560,
    "KG Winter_theta_ble": 0.9597519,
    "KG Winter_pr_1_99_ble": 84,
    "KG Winter_ss_ble": 115,
    "KG Winter_dss_ble": 596,
    "KG Spring_theta_ble": 1.223608358,
    "KG Spring_pr_1_99_ble": 84,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 622
  },
  {
    "ID": 419,
    "KG Fall_theta_ble": 0.597467379,
    "KG Fall_pr_1_99_ble": 84,
    "KG Fall_ss_ble": 115,
    "KG Fall_dss_ble": 560,
    "KG Winter_theta_ble": 0.967377084,
    "KG Winter_pr_1_99_ble": 84,
    "KG Winter_ss_ble": 115,
    "KG Winter_dss_ble": 597,
    "KG Spring_theta_ble": 1.227205221,
    "KG Spring_pr_1_99_ble": 84,
    "KG Spring_ss_ble": 115,
    "KG Spring_dss_ble": 623
  },
  {
    "ID": 420,
    "KG Fall_theta_ble": 0.602484348,
    "KG Fall_pr_1_99_ble": 84,
    "KG Fall_ss_ble": 115,
    "KG Fall_dss_ble": 560,
    "KG Winter_theta_ble": 0.968255395,
    "KG Winter_pr_1_99_ble": 84,
    "KG Winter_ss_ble": 115,
    "KG Winter_dss_ble": 597,
    "KG Spring_theta_ble": 1.230242113,
    "KG Spring_pr_1_99_ble": 84,
    "KG Spring_ss_ble": 116,
    "KG Spring_dss_ble": 623
  },
  {
    "ID": 421,
    "KG Fall_theta_ble": 0.614933396,
    "KG Fall_pr_1_99_ble": 84,
    "KG Fall_ss_ble": 115,
    "KG Fall_dss_ble": 561,
    "KG Winter_theta_ble": 0.973287017,
    "KG Winter_pr_1_99_ble": 84,
    "KG Winter_ss_ble": 115,
    "KG Winter_dss_ble": 597,
    "KG Spring_theta_ble": 1.233287148,
    "KG Spring_pr_1_99_ble": 84,
    "KG Spring_ss_ble": 116,
    "KG Spring_dss_ble": 623
  },
  {
    "ID": 422,
    "KG Fall_theta_ble": 0.616017434,
    "KG Fall_pr_1_99_ble": 84,
    "KG Fall_ss_ble": 115,
    "KG Fall_dss_ble": 562,
    "KG Winter_theta_ble": 0.979724564,
    "KG Winter_pr_1_99_ble": 84,
    "KG Winter_ss_ble": 115,
    "KG Winter_dss_ble": 598,
    "KG Spring_theta_ble": 1.250010835,
    "KG Spring_pr_1_99_ble": 84,
    "KG Spring_ss_ble": 116,
    "KG Spring_dss_ble": 625
  },
  {
    "ID": 423,
    "KG Fall_theta_ble": 0.617484804,
    "KG Fall_pr_1_99_ble": 85,
    "KG Fall_ss_ble": 115,
    "KG Fall_dss_ble": 562,
    "KG Winter_theta_ble": 0.98032687,
    "KG Winter_pr_1_99_ble": 85,
    "KG Winter_ss_ble": 115,
    "KG Winter_dss_ble": 598,
    "KG Spring_theta_ble": 1.273574289,
    "KG Spring_pr_1_99_ble": 85,
    "KG Spring_ss_ble": 116,
    "KG Spring_dss_ble": 627
  },
  {
    "ID": 424,
    "KG Fall_theta_ble": 0.625908834,
    "KG Fall_pr_1_99_ble": 85,
    "KG Fall_ss_ble": 115,
    "KG Fall_dss_ble": 563,
    "KG Winter_theta_ble": 0.98122921,
    "KG Winter_pr_1_99_ble": 85,
    "KG Winter_ss_ble": 115,
    "KG Winter_dss_ble": 598,
    "KG Spring_theta_ble": 1.276638876,
    "KG Spring_pr_1_99_ble": 85,
    "KG Spring_ss_ble": 116,
    "KG Spring_dss_ble": 628
  },
  {
    "ID": 425,
    "KG Fall_theta_ble": 0.632792982,
    "KG Fall_pr_1_99_ble": 85,
    "KG Fall_ss_ble": 115,
    "KG Fall_dss_ble": 563,
    "KG Winter_theta_ble": 0.98499367,
    "KG Winter_pr_1_99_ble": 85,
    "KG Winter_ss_ble": 115,
    "KG Winter_dss_ble": 598,
    "KG Spring_theta_ble": 1.282215432,
    "KG Spring_pr_1_99_ble": 85,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 628
  },
  {
    "ID": 426,
    "KG Fall_theta_ble": 0.636476453,
    "KG Fall_pr_1_99_ble": 85,
    "KG Fall_ss_ble": 115,
    "KG Fall_dss_ble": 564,
    "KG Winter_theta_ble": 1.003650962,
    "KG Winter_pr_1_99_ble": 85,
    "KG Winter_ss_ble": 116,
    "KG Winter_dss_ble": 600,
    "KG Spring_theta_ble": 1.282515984,
    "KG Spring_pr_1_99_ble": 85,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 628
  },
  {
    "ID": 427,
    "KG Fall_theta_ble": 0.670765002,
    "KG Fall_pr_1_99_ble": 85,
    "KG Fall_ss_ble": 116,
    "KG Fall_dss_ble": 567,
    "KG Winter_theta_ble": 1.008578636,
    "KG Winter_pr_1_99_ble": 85,
    "KG Winter_ss_ble": 116,
    "KG Winter_dss_ble": 601,
    "KG Spring_theta_ble": 1.286474213,
    "KG Spring_pr_1_99_ble": 85,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 629
  },
  {
    "ID": 428,
    "KG Fall_theta_ble": 0.681141653,
    "KG Fall_pr_1_99_ble": 86,
    "KG Fall_ss_ble": 116,
    "KG Fall_dss_ble": 568,
    "KG Winter_theta_ble": 1.027675755,
    "KG Winter_pr_1_99_ble": 86,
    "KG Winter_ss_ble": 116,
    "KG Winter_dss_ble": 603,
    "KG Spring_theta_ble": 1.288446647,
    "KG Spring_pr_1_99_ble": 86,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 629
  },
  {
    "ID": 429,
    "KG Fall_theta_ble": 0.692242213,
    "KG Fall_pr_1_99_ble": 86,
    "KG Fall_ss_ble": 116,
    "KG Fall_dss_ble": 569,
    "KG Winter_theta_ble": 1.030875518,
    "KG Winter_pr_1_99_ble": 86,
    "KG Winter_ss_ble": 116,
    "KG Winter_dss_ble": 603,
    "KG Spring_theta_ble": 1.288549191,
    "KG Spring_pr_1_99_ble": 86,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 629
  },
  {
    "ID": 430,
    "KG Fall_theta_ble": 0.695356575,
    "KG Fall_pr_1_99_ble": 86,
    "KG Fall_ss_ble": 116,
    "KG Fall_dss_ble": 570,
    "KG Winter_theta_ble": 1.041341653,
    "KG Winter_pr_1_99_ble": 86,
    "KG Winter_ss_ble": 116,
    "KG Winter_dss_ble": 604,
    "KG Spring_theta_ble": 1.292086127,
    "KG Spring_pr_1_99_ble": 86,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 629
  },
  {
    "ID": 431,
    "KG Fall_theta_ble": 0.698820931,
    "KG Fall_pr_1_99_ble": 86,
    "KG Fall_ss_ble": 116,
    "KG Fall_dss_ble": 570,
    "KG Winter_theta_ble": 1.052687785,
    "KG Winter_pr_1_99_ble": 86,
    "KG Winter_ss_ble": 116,
    "KG Winter_dss_ble": 605,
    "KG Spring_theta_ble": 1.292493022,
    "KG Spring_pr_1_99_ble": 86,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 629
  },
  {
    "ID": 432,
    "KG Fall_theta_ble": 0.699295249,
    "KG Fall_pr_1_99_ble": 86,
    "KG Fall_ss_ble": 116,
    "KG Fall_dss_ble": 570,
    "KG Winter_theta_ble": 1.071140741,
    "KG Winter_pr_1_99_ble": 86,
    "KG Winter_ss_ble": 117,
    "KG Winter_dss_ble": 607,
    "KG Spring_theta_ble": 1.293668452,
    "KG Spring_pr_1_99_ble": 86,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 629
  },
  {
    "ID": 433,
    "KG Fall_theta_ble": 0.699851538,
    "KG Fall_pr_1_99_ble": 87,
    "KG Fall_ss_ble": 116,
    "KG Fall_dss_ble": 570,
    "KG Winter_theta_ble": 1.072069647,
    "KG Winter_pr_1_99_ble": 87,
    "KG Winter_ss_ble": 117,
    "KG Winter_dss_ble": 607,
    "KG Spring_theta_ble": 1.295034927,
    "KG Spring_pr_1_99_ble": 87,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 630
  },
  {
    "ID": 434,
    "KG Fall_theta_ble": 0.703263448,
    "KG Fall_pr_1_99_ble": 87,
    "KG Fall_ss_ble": 116,
    "KG Fall_dss_ble": 570,
    "KG Winter_theta_ble": 1.080431191,
    "KG Winter_pr_1_99_ble": 87,
    "KG Winter_ss_ble": 117,
    "KG Winter_dss_ble": 608,
    "KG Spring_theta_ble": 1.29619252,
    "KG Spring_pr_1_99_ble": 87,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 630
  },
  {
    "ID": 435,
    "KG Fall_theta_ble": 0.72580412,
    "KG Fall_pr_1_99_ble": 87,
    "KG Fall_ss_ble": 117,
    "KG Fall_dss_ble": 573,
    "KG Winter_theta_ble": 1.082459833,
    "KG Winter_pr_1_99_ble": 87,
    "KG Winter_ss_ble": 117,
    "KG Winter_dss_ble": 608,
    "KG Spring_theta_ble": 1.296898413,
    "KG Spring_pr_1_99_ble": 87,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 630
  },
  {
    "ID": 436,
    "KG Fall_theta_ble": 0.730281374,
    "KG Fall_pr_1_99_ble": 87,
    "KG Fall_ss_ble": 117,
    "KG Fall_dss_ble": 573,
    "KG Winter_theta_ble": 1.10717939,
    "KG Winter_pr_1_99_ble": 87,
    "KG Winter_ss_ble": 117,
    "KG Winter_dss_ble": 611,
    "KG Spring_theta_ble": 1.298534667,
    "KG Spring_pr_1_99_ble": 87,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 630
  },
  {
    "ID": 437,
    "KG Fall_theta_ble": 0.730303996,
    "KG Fall_pr_1_99_ble": 87,
    "KG Fall_ss_ble": 117,
    "KG Fall_dss_ble": 573,
    "KG Winter_theta_ble": 1.107249748,
    "KG Winter_pr_1_99_ble": 87,
    "KG Winter_ss_ble": 117,
    "KG Winter_dss_ble": 611,
    "KG Spring_theta_ble": 1.304237653,
    "KG Spring_pr_1_99_ble": 87,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 630
  },
  {
    "ID": 438,
    "KG Fall_theta_ble": 0.730717559,
    "KG Fall_pr_1_99_ble": 88,
    "KG Fall_ss_ble": 117,
    "KG Fall_dss_ble": 573,
    "KG Winter_theta_ble": 1.108677569,
    "KG Winter_pr_1_99_ble": 88,
    "KG Winter_ss_ble": 117,
    "KG Winter_dss_ble": 611,
    "KG Spring_theta_ble": 1.319955576,
    "KG Spring_pr_1_99_ble": 88,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 632
  },
  {
    "ID": 439,
    "KG Fall_theta_ble": 0.731482693,
    "KG Fall_pr_1_99_ble": 88,
    "KG Fall_ss_ble": 117,
    "KG Fall_dss_ble": 573,
    "KG Winter_theta_ble": 1.115905002,
    "KG Winter_pr_1_99_ble": 88,
    "KG Winter_ss_ble": 118,
    "KG Winter_dss_ble": 612,
    "KG Spring_theta_ble": 1.326957146,
    "KG Spring_pr_1_99_ble": 88,
    "KG Spring_ss_ble": 117,
    "KG Spring_dss_ble": 633
  },
  {
    "ID": 440,
    "KG Fall_theta_ble": 0.732898585,
    "KG Fall_pr_1_99_ble": 88,
    "KG Fall_ss_ble": 117,
    "KG Fall_dss_ble": 573,
    "KG Winter_theta_ble": 1.117247218,
    "KG Winter_pr_1_99_ble": 88,
    "KG Winter_ss_ble": 118,
    "KG Winter_dss_ble": 612,
    "KG Spring_theta_ble": 1.331808258,
    "KG Spring_pr_1_99_ble": 88,
    "KG Spring_ss_ble": 118,
    "KG Spring_dss_ble": 633
  },
  {
    "ID": 441,
    "KG Fall_theta_ble": 0.736657101,
    "KG Fall_pr_1_99_ble": 88,
    "KG Fall_ss_ble": 117,
    "KG Fall_dss_ble": 574,
    "KG Winter_theta_ble": 1.11850371,
    "KG Winter_pr_1_99_ble": 88,
    "KG Winter_ss_ble": 118,
    "KG Winter_dss_ble": 612,
    "KG Spring_theta_ble": 1.335963808,
    "KG Spring_pr_1_99_ble": 88,
    "KG Spring_ss_ble": 118,
    "KG Spring_dss_ble": 634
  },
  {
    "ID": 442,
    "KG Fall_theta_ble": 0.740570675,
    "KG Fall_pr_1_99_ble": 88,
    "KG Fall_ss_ble": 117,
    "KG Fall_dss_ble": 574,
    "KG Winter_theta_ble": 1.12082249,
    "KG Winter_pr_1_99_ble": 88,
    "KG Winter_ss_ble": 118,
    "KG Winter_dss_ble": 612,
    "KG Spring_theta_ble": 1.341599962,
    "KG Spring_pr_1_99_ble": 88,
    "KG Spring_ss_ble": 118,
    "KG Spring_dss_ble": 634
  },
  {
    "ID": 443,
    "KG Fall_theta_ble": 0.749588148,
    "KG Fall_pr_1_99_ble": 89,
    "KG Fall_ss_ble": 117,
    "KG Fall_dss_ble": 575,
    "KG Winter_theta_ble": 1.132016638,
    "KG Winter_pr_1_99_ble": 89,
    "KG Winter_ss_ble": 118,
    "KG Winter_dss_ble": 613,
    "KG Spring_theta_ble": 1.342300676,
    "KG Spring_pr_1_99_ble": 89,
    "KG Spring_ss_ble": 118,
    "KG Spring_dss_ble": 634
  },
  {
    "ID": 444,
    "KG Fall_theta_ble": 0.751375505,
    "KG Fall_pr_1_99_ble": 89,
    "KG Fall_ss_ble": 117,
    "KG Fall_dss_ble": 575,
    "KG Winter_theta_ble": 1.13339053,
    "KG Winter_pr_1_99_ble": 89,
    "KG Winter_ss_ble": 118,
    "KG Winter_dss_ble": 613,
    "KG Spring_theta_ble": 1.344695851,
    "KG Spring_pr_1_99_ble": 89,
    "KG Spring_ss_ble": 118,
    "KG Spring_dss_ble": 634
  },
  {
    "ID": 445,
    "KG Fall_theta_ble": 0.778964978,
    "KG Fall_pr_1_99_ble": 89,
    "KG Fall_ss_ble": 118,
    "KG Fall_dss_ble": 578,
    "KG Winter_theta_ble": 1.143266988,
    "KG Winter_pr_1_99_ble": 89,
    "KG Winter_ss_ble": 118,
    "KG Winter_dss_ble": 614,
    "KG Spring_theta_ble": 1.354657328,
    "KG Spring_pr_1_99_ble": 89,
    "KG Spring_ss_ble": 118,
    "KG Spring_dss_ble": 635
  },
  {
    "ID": 446,
    "KG Fall_theta_ble": 0.790463491,
    "KG Fall_pr_1_99_ble": 89,
    "KG Fall_ss_ble": 118,
    "KG Fall_dss_ble": 579,
    "KG Winter_theta_ble": 1.176764014,
    "KG Winter_pr_1_99_ble": 89,
    "KG Winter_ss_ble": 119,
    "KG Winter_dss_ble": 618,
    "KG Spring_theta_ble": 1.361599653,
    "KG Spring_pr_1_99_ble": 89,
    "KG Spring_ss_ble": 118,
    "KG Spring_dss_ble": 636
  },
  {
    "ID": 447,
    "KG Fall_theta_ble": 0.80728668,
    "KG Fall_pr_1_99_ble": 89,
    "KG Fall_ss_ble": 118,
    "KG Fall_dss_ble": 581,
    "KG Winter_theta_ble": 1.181138882,
    "KG Winter_pr_1_99_ble": 89,
    "KG Winter_ss_ble": 119,
    "KG Winter_dss_ble": 618,
    "KG Spring_theta_ble": 1.362739772,
    "KG Spring_pr_1_99_ble": 89,
    "KG Spring_ss_ble": 118,
    "KG Spring_dss_ble": 636
  },
  {
    "ID": 448,
    "KG Fall_theta_ble": 0.819212759,
    "KG Fall_pr_1_99_ble": 90,
    "KG Fall_ss_ble": 118,
    "KG Fall_dss_ble": 582,
    "KG Winter_theta_ble": 1.184784065,
    "KG Winter_pr_1_99_ble": 90,
    "KG Winter_ss_ble": 119,
    "KG Winter_dss_ble": 618,
    "KG Spring_theta_ble": 1.364065709,
    "KG Spring_pr_1_99_ble": 90,
    "KG Spring_ss_ble": 118,
    "KG Spring_dss_ble": 636
  },
  {
    "ID": 449,
    "KG Fall_theta_ble": 0.824021341,
    "KG Fall_pr_1_99_ble": 90,
    "KG Fall_ss_ble": 118,
    "KG Fall_dss_ble": 582,
    "KG Winter_theta_ble": 1.184990734,
    "KG Winter_pr_1_99_ble": 90,
    "KG Winter_ss_ble": 119,
    "KG Winter_dss_ble": 618,
    "KG Spring_theta_ble": 1.364225762,
    "KG Spring_pr_1_99_ble": 90,
    "KG Spring_ss_ble": 118,
    "KG Spring_dss_ble": 636
  },
  {
    "ID": 450,
    "KG Fall_theta_ble": 0.846838606,
    "KG Fall_pr_1_99_ble": 90,
    "KG Fall_ss_ble": 119,
    "KG Fall_dss_ble": 585,
    "KG Winter_theta_ble": 1.201938465,
    "KG Winter_pr_1_99_ble": 90,
    "KG Winter_ss_ble": 119,
    "KG Winter_dss_ble": 620,
    "KG Spring_theta_ble": 1.380870109,
    "KG Spring_pr_1_99_ble": 90,
    "KG Spring_ss_ble": 119,
    "KG Spring_dss_ble": 638
  },
  {
    "ID": 451,
    "KG Fall_theta_ble": 0.856394406,
    "KG Fall_pr_1_99_ble": 90,
    "KG Fall_ss_ble": 119,
    "KG Fall_dss_ble": 586,
    "KG Winter_theta_ble": 1.204934807,
    "KG Winter_pr_1_99_ble": 90,
    "KG Winter_ss_ble": 119,
    "KG Winter_dss_ble": 620,
    "KG Spring_theta_ble": 1.390679416,
    "KG Spring_pr_1_99_ble": 90,
    "KG Spring_ss_ble": 119,
    "KG Spring_dss_ble": 639
  },
  {
    "ID": 452,
    "KG Fall_theta_ble": 0.867109352,
    "KG Fall_pr_1_99_ble": 90,
    "KG Fall_ss_ble": 119,
    "KG Fall_dss_ble": 587,
    "KG Winter_theta_ble": 1.209815178,
    "KG Winter_pr_1_99_ble": 90,
    "KG Winter_ss_ble": 119,
    "KG Winter_dss_ble": 621,
    "KG Spring_theta_ble": 1.404812993,
    "KG Spring_pr_1_99_ble": 90,
    "KG Spring_ss_ble": 119,
    "KG Spring_dss_ble": 640
  },
  {
    "ID": 453,
    "KG Fall_theta_ble": 0.868908437,
    "KG Fall_pr_1_99_ble": 91,
    "KG Fall_ss_ble": 119,
    "KG Fall_dss_ble": 587,
    "KG Winter_theta_ble": 1.21536596,
    "KG Winter_pr_1_99_ble": 91,
    "KG Winter_ss_ble": 119,
    "KG Winter_dss_ble": 622,
    "KG Spring_theta_ble": 1.413739388,
    "KG Spring_pr_1_99_ble": 91,
    "KG Spring_ss_ble": 119,
    "KG Spring_dss_ble": 641
  },
  {
    "ID": 454,
    "KG Fall_theta_ble": 0.873528115,
    "KG Fall_pr_1_99_ble": 91,
    "KG Fall_ss_ble": 119,
    "KG Fall_dss_ble": 587,
    "KG Winter_theta_ble": 1.234340299,
    "KG Winter_pr_1_99_ble": 91,
    "KG Winter_ss_ble": 120,
    "KG Winter_dss_ble": 623,
    "KG Spring_theta_ble": 1.42262654,
    "KG Spring_pr_1_99_ble": 91,
    "KG Spring_ss_ble": 119,
    "KG Spring_dss_ble": 642
  },
  {
    "ID": 455,
    "KG Fall_theta_ble": 0.913652119,
    "KG Fall_pr_1_99_ble": 91,
    "KG Fall_ss_ble": 120,
    "KG Fall_dss_ble": 591,
    "KG Winter_theta_ble": 1.241916137,
    "KG Winter_pr_1_99_ble": 91,
    "KG Winter_ss_ble": 120,
    "KG Winter_dss_ble": 624,
    "KG Spring_theta_ble": 1.428833721,
    "KG Spring_pr_1_99_ble": 91,
    "KG Spring_ss_ble": 119,
    "KG Spring_dss_ble": 643
  },
  {
    "ID": 456,
    "KG Fall_theta_ble": 0.920267127,
    "KG Fall_pr_1_99_ble": 91,
    "KG Fall_ss_ble": 120,
    "KG Fall_dss_ble": 592,
    "KG Winter_theta_ble": 1.245979211,
    "KG Winter_pr_1_99_ble": 91,
    "KG Winter_ss_ble": 120,
    "KG Winter_dss_ble": 625,
    "KG Spring_theta_ble": 1.442377368,
    "KG Spring_pr_1_99_ble": 91,
    "KG Spring_ss_ble": 120,
    "KG Spring_dss_ble": 644
  },
  {
    "ID": 457,
    "KG Fall_theta_ble": 0.921491846,
    "KG Fall_pr_1_99_ble": 91,
    "KG Fall_ss_ble": 120,
    "KG Fall_dss_ble": 592,
    "KG Winter_theta_ble": 1.253967915,
    "KG Winter_pr_1_99_ble": 91,
    "KG Winter_ss_ble": 120,
    "KG Winter_dss_ble": 625,
    "KG Spring_theta_ble": 1.449529765,
    "KG Spring_pr_1_99_ble": 91,
    "KG Spring_ss_ble": 120,
    "KG Spring_dss_ble": 645
  },
  {
    "ID": 458,
    "KG Fall_theta_ble": 0.922165291,
    "KG Fall_pr_1_99_ble": 92,
    "KG Fall_ss_ble": 120,
    "KG Fall_dss_ble": 592,
    "KG Winter_theta_ble": 1.254857362,
    "KG Winter_pr_1_99_ble": 92,
    "KG Winter_ss_ble": 120,
    "KG Winter_dss_ble": 625,
    "KG Spring_theta_ble": 1.478837565,
    "KG Spring_pr_1_99_ble": 92,
    "KG Spring_ss_ble": 120,
    "KG Spring_dss_ble": 648
  },
  {
    "ID": 459,
    "KG Fall_theta_ble": 0.923370811,
    "KG Fall_pr_1_99_ble": 92,
    "KG Fall_ss_ble": 120,
    "KG Fall_dss_ble": 592,
    "KG Winter_theta_ble": 1.258772361,
    "KG Winter_pr_1_99_ble": 92,
    "KG Winter_ss_ble": 120,
    "KG Winter_dss_ble": 626,
    "KG Spring_theta_ble": 1.481795176,
    "KG Spring_pr_1_99_ble": 92,
    "KG Spring_ss_ble": 121,
    "KG Spring_dss_ble": 648
  },
  {
    "ID": 460,
    "KG Fall_theta_ble": 0.971663159,
    "KG Fall_pr_1_99_ble": 92,
    "KG Fall_ss_ble": 121,
    "KG Fall_dss_ble": 597,
    "KG Winter_theta_ble": 1.273668347,
    "KG Winter_pr_1_99_ble": 92,
    "KG Winter_ss_ble": 120,
    "KG Winter_dss_ble": 627,
    "KG Spring_theta_ble": 1.485534544,
    "KG Spring_pr_1_99_ble": 92,
    "KG Spring_ss_ble": 121,
    "KG Spring_dss_ble": 649
  },
  {
    "ID": 461,
    "KG Fall_theta_ble": 0.987423905,
    "KG Fall_pr_1_99_ble": 92,
    "KG Fall_ss_ble": 121,
    "KG Fall_dss_ble": 599,
    "KG Winter_theta_ble": 1.291871272,
    "KG Winter_pr_1_99_ble": 92,
    "KG Winter_ss_ble": 121,
    "KG Winter_dss_ble": 629,
    "KG Spring_theta_ble": 1.497735852,
    "KG Spring_pr_1_99_ble": 92,
    "KG Spring_ss_ble": 121,
    "KG Spring_dss_ble": 650
  },
  {
    "ID": 462,
    "KG Fall_theta_ble": 0.993755281,
    "KG Fall_pr_1_99_ble": 92,
    "KG Fall_ss_ble": 121,
    "KG Fall_dss_ble": 599,
    "KG Winter_theta_ble": 1.29399853,
    "KG Winter_pr_1_99_ble": 92,
    "KG Winter_ss_ble": 121,
    "KG Winter_dss_ble": 629,
    "KG Spring_theta_ble": 1.512500606,
    "KG Spring_pr_1_99_ble": 92,
    "KG Spring_ss_ble": 121,
    "KG Spring_dss_ble": 651
  },
  {
    "ID": 463,
    "KG Fall_theta_ble": 1.009406319,
    "KG Fall_pr_1_99_ble": 93,
    "KG Fall_ss_ble": 122,
    "KG Fall_dss_ble": 601,
    "KG Winter_theta_ble": 1.350442044,
    "KG Winter_pr_1_99_ble": 93,
    "KG Winter_ss_ble": 122,
    "KG Winter_dss_ble": 635,
    "KG Spring_theta_ble": 1.515122309,
    "KG Spring_pr_1_99_ble": 93,
    "KG Spring_ss_ble": 121,
    "KG Spring_dss_ble": 652
  },
  {
    "ID": 464,
    "KG Fall_theta_ble": 1.024911353,
    "KG Fall_pr_1_99_ble": 93,
    "KG Fall_ss_ble": 122,
    "KG Fall_dss_ble": 602,
    "KG Winter_theta_ble": 1.359222907,
    "KG Winter_pr_1_99_ble": 93,
    "KG Winter_ss_ble": 122,
    "KG Winter_dss_ble": 636,
    "KG Spring_theta_ble": 1.528017266,
    "KG Spring_pr_1_99_ble": 93,
    "KG Spring_ss_ble": 121,
    "KG Spring_dss_ble": 653
  },
  {
    "ID": 465,
    "KG Fall_theta_ble": 1.061507094,
    "KG Fall_pr_1_99_ble": 93,
    "KG Fall_ss_ble": 123,
    "KG Fall_dss_ble": 606,
    "KG Winter_theta_ble": 1.365713242,
    "KG Winter_pr_1_99_ble": 93,
    "KG Winter_ss_ble": 122,
    "KG Winter_dss_ble": 637,
    "KG Spring_theta_ble": 1.54063988,
    "KG Spring_pr_1_99_ble": 93,
    "KG Spring_ss_ble": 122,
    "KG Spring_dss_ble": 654
  },
  {
    "ID": 466,
    "KG Fall_theta_ble": 1.077787456,
    "KG Fall_pr_1_99_ble": 93,
    "KG Fall_ss_ble": 123,
    "KG Fall_dss_ble": 608,
    "KG Winter_theta_ble": 1.374191254,
    "KG Winter_pr_1_99_ble": 93,
    "KG Winter_ss_ble": 122,
    "KG Winter_dss_ble": 637,
    "KG Spring_theta_ble": 1.542918596,
    "KG Spring_pr_1_99_ble": 93,
    "KG Spring_ss_ble": 122,
    "KG Spring_dss_ble": 654
  },
  {
    "ID": 467,
    "KG Fall_theta_ble": 1.080788358,
    "KG Fall_pr_1_99_ble": 93,
    "KG Fall_ss_ble": 123,
    "KG Fall_dss_ble": 608,
    "KG Winter_theta_ble": 1.405057302,
    "KG Winter_pr_1_99_ble": 93,
    "KG Winter_ss_ble": 123,
    "KG Winter_dss_ble": 641,
    "KG Spring_theta_ble": 1.553694892,
    "KG Spring_pr_1_99_ble": 93,
    "KG Spring_ss_ble": 122,
    "KG Spring_dss_ble": 655
  },
  {
    "ID": 468,
    "KG Fall_theta_ble": 1.081735681,
    "KG Fall_pr_1_99_ble": 94,
    "KG Fall_ss_ble": 123,
    "KG Fall_dss_ble": 608,
    "KG Winter_theta_ble": 1.407820254,
    "KG Winter_pr_1_99_ble": 94,
    "KG Winter_ss_ble": 123,
    "KG Winter_dss_ble": 641,
    "KG Spring_theta_ble": 1.55454331,
    "KG Spring_pr_1_99_ble": 94,
    "KG Spring_ss_ble": 122,
    "KG Spring_dss_ble": 655
  },
  {
    "ID": 469,
    "KG Fall_theta_ble": 1.115605337,
    "KG Fall_pr_1_99_ble": 94,
    "KG Fall_ss_ble": 123,
    "KG Fall_dss_ble": 612,
    "KG Winter_theta_ble": 1.414654608,
    "KG Winter_pr_1_99_ble": 94,
    "KG Winter_ss_ble": 123,
    "KG Winter_dss_ble": 641,
    "KG Spring_theta_ble": 1.577284577,
    "KG Spring_pr_1_99_ble": 94,
    "KG Spring_ss_ble": 122,
    "KG Spring_dss_ble": 658
  },
  {
    "ID": 470,
    "KG Fall_theta_ble": 1.136409269,
    "KG Fall_pr_1_99_ble": 94,
    "KG Fall_ss_ble": 124,
    "KG Fall_dss_ble": 614,
    "KG Winter_theta_ble": 1.422933688,
    "KG Winter_pr_1_99_ble": 94,
    "KG Winter_ss_ble": 123,
    "KG Winter_dss_ble": 642,
    "KG Spring_theta_ble": 1.579323029,
    "KG Spring_pr_1_99_ble": 94,
    "KG Spring_ss_ble": 122,
    "KG Spring_dss_ble": 658
  },
  {
    "ID": 471,
    "KG Fall_theta_ble": 1.136443153,
    "KG Fall_pr_1_99_ble": 94,
    "KG Fall_ss_ble": 124,
    "KG Fall_dss_ble": 614,
    "KG Winter_theta_ble": 1.442547248,
    "KG Winter_pr_1_99_ble": 94,
    "KG Winter_ss_ble": 123,
    "KG Winter_dss_ble": 644,
    "KG Spring_theta_ble": 1.585166971,
    "KG Spring_pr_1_99_ble": 94,
    "KG Spring_ss_ble": 123,
    "KG Spring_dss_ble": 659
  },
  {
    "ID": 472,
    "KG Fall_theta_ble": 1.147080317,
    "KG Fall_pr_1_99_ble": 94,
    "KG Fall_ss_ble": 124,
    "KG Fall_dss_ble": 615,
    "KG Winter_theta_ble": 1.445273372,
    "KG Winter_pr_1_99_ble": 94,
    "KG Winter_ss_ble": 123,
    "KG Winter_dss_ble": 645,
    "KG Spring_theta_ble": 1.598332402,
    "KG Spring_pr_1_99_ble": 94,
    "KG Spring_ss_ble": 123,
    "KG Spring_dss_ble": 660
  },
  {
    "ID": 473,
    "KG Fall_theta_ble": 1.153149076,
    "KG Fall_pr_1_99_ble": 95,
    "KG Fall_ss_ble": 124,
    "KG Fall_dss_ble": 615,
    "KG Winter_theta_ble": 1.445415988,
    "KG Winter_pr_1_99_ble": 95,
    "KG Winter_ss_ble": 123,
    "KG Winter_dss_ble": 645,
    "KG Spring_theta_ble": 1.601963311,
    "KG Spring_pr_1_99_ble": 95,
    "KG Spring_ss_ble": 123,
    "KG Spring_dss_ble": 660
  },
  {
    "ID": 474,
    "KG Fall_theta_ble": 1.204235514,
    "KG Fall_pr_1_99_ble": 95,
    "KG Fall_ss_ble": 125,
    "KG Fall_dss_ble": 620,
    "KG Winter_theta_ble": 1.453967097,
    "KG Winter_pr_1_99_ble": 95,
    "KG Winter_ss_ble": 124,
    "KG Winter_dss_ble": 645,
    "KG Spring_theta_ble": 1.642363405,
    "KG Spring_pr_1_99_ble": 95,
    "KG Spring_ss_ble": 124,
    "KG Spring_dss_ble": 664
  },
  {
    "ID": 475,
    "KG Fall_theta_ble": 1.216463316,
    "KG Fall_pr_1_99_ble": 95,
    "KG Fall_ss_ble": 125,
    "KG Fall_dss_ble": 622,
    "KG Winter_theta_ble": 1.465320297,
    "KG Winter_pr_1_99_ble": 95,
    "KG Winter_ss_ble": 124,
    "KG Winter_dss_ble": 647,
    "KG Spring_theta_ble": 1.643656778,
    "KG Spring_pr_1_99_ble": 95,
    "KG Spring_ss_ble": 124,
    "KG Spring_dss_ble": 664
  },
  {
    "ID": 476,
    "KG Fall_theta_ble": 1.251262882,
    "KG Fall_pr_1_99_ble": 95,
    "KG Fall_ss_ble": 126,
    "KG Fall_dss_ble": 625,
    "KG Winter_theta_ble": 1.468691786,
    "KG Winter_pr_1_99_ble": 95,
    "KG Winter_ss_ble": 124,
    "KG Winter_dss_ble": 647,
    "KG Spring_theta_ble": 1.668547913,
    "KG Spring_pr_1_99_ble": 95,
    "KG Spring_ss_ble": 124,
    "KG Spring_dss_ble": 667
  },
  {
    "ID": 477,
    "KG Fall_theta_ble": 1.26350218,
    "KG Fall_pr_1_99_ble": 95,
    "KG Fall_ss_ble": 126,
    "KG Fall_dss_ble": 626,
    "KG Winter_theta_ble": 1.473222883,
    "KG Winter_pr_1_99_ble": 95,
    "KG Winter_ss_ble": 124,
    "KG Winter_dss_ble": 647,
    "KG Spring_theta_ble": 1.698092606,
    "KG Spring_pr_1_99_ble": 95,
    "KG Spring_ss_ble": 125,
    "KG Spring_dss_ble": 670
  },
  {
    "ID": 478,
    "KG Fall_theta_ble": 1.270347375,
    "KG Fall_pr_1_99_ble": 96,
    "KG Fall_ss_ble": 126,
    "KG Fall_dss_ble": 627,
    "KG Winter_theta_ble": 1.479388969,
    "KG Winter_pr_1_99_ble": 96,
    "KG Winter_ss_ble": 124,
    "KG Winter_dss_ble": 648,
    "KG Spring_theta_ble": 1.704224946,
    "KG Spring_pr_1_99_ble": 96,
    "KG Spring_ss_ble": 125,
    "KG Spring_dss_ble": 670
  },
  {
    "ID": 479,
    "KG Fall_theta_ble": 1.270721047,
    "KG Fall_pr_1_99_ble": 96,
    "KG Fall_ss_ble": 126,
    "KG Fall_dss_ble": 627,
    "KG Winter_theta_ble": 1.505151066,
    "KG Winter_pr_1_99_ble": 96,
    "KG Winter_ss_ble": 124,
    "KG Winter_dss_ble": 651,
    "KG Spring_theta_ble": 1.746914826,
    "KG Spring_pr_1_99_ble": 96,
    "KG Spring_ss_ble": 126,
    "KG Spring_dss_ble": 675
  },
  {
    "ID": 480,
    "KG Fall_theta_ble": 1.28023357,
    "KG Fall_pr_1_99_ble": 96,
    "KG Fall_ss_ble": 126,
    "KG Fall_dss_ble": 628,
    "KG Winter_theta_ble": 1.528077191,
    "KG Winter_pr_1_99_ble": 96,
    "KG Winter_ss_ble": 125,
    "KG Winter_dss_ble": 653,
    "KG Spring_theta_ble": 1.762544125,
    "KG Spring_pr_1_99_ble": 96,
    "KG Spring_ss_ble": 126,
    "KG Spring_dss_ble": 676
  },
  {
    "ID": 481,
    "KG Fall_theta_ble": 1.295118318,
    "KG Fall_pr_1_99_ble": 96,
    "KG Fall_ss_ble": 127,
    "KG Fall_dss_ble": 630,
    "KG Winter_theta_ble": 1.548521158,
    "KG Winter_pr_1_99_ble": 96,
    "KG Winter_ss_ble": 125,
    "KG Winter_dss_ble": 655,
    "KG Spring_theta_ble": 1.76645503,
    "KG Spring_pr_1_99_ble": 96,
    "KG Spring_ss_ble": 126,
    "KG Spring_dss_ble": 677
  },
  {
    "ID": 482,
    "KG Fall_theta_ble": 1.313650966,
    "KG Fall_pr_1_99_ble": 96,
    "KG Fall_ss_ble": 127,
    "KG Fall_dss_ble": 631,
    "KG Winter_theta_ble": 1.562940244,
    "KG Winter_pr_1_99_ble": 96,
    "KG Winter_ss_ble": 126,
    "KG Winter_dss_ble": 656,
    "KG Spring_theta_ble": 1.788849138,
    "KG Spring_pr_1_99_ble": 96,
    "KG Spring_ss_ble": 127,
    "KG Spring_dss_ble": 679
  },
  {
    "ID": 483,
    "KG Fall_theta_ble": 1.330840183,
    "KG Fall_pr_1_99_ble": 97,
    "KG Fall_ss_ble": 127,
    "KG Fall_dss_ble": 633,
    "KG Winter_theta_ble": 1.585129981,
    "KG Winter_pr_1_99_ble": 97,
    "KG Winter_ss_ble": 126,
    "KG Winter_dss_ble": 659,
    "KG Spring_theta_ble": 1.812717157,
    "KG Spring_pr_1_99_ble": 97,
    "KG Spring_ss_ble": 127,
    "KG Spring_dss_ble": 681
  },
  {
    "ID": 484,
    "KG Fall_theta_ble": 1.352083071,
    "KG Fall_pr_1_99_ble": 97,
    "KG Fall_ss_ble": 127,
    "KG Fall_dss_ble": 635,
    "KG Winter_theta_ble": 1.597567451,
    "KG Winter_pr_1_99_ble": 97,
    "KG Winter_ss_ble": 126,
    "KG Winter_dss_ble": 660,
    "KG Spring_theta_ble": 1.814326421,
    "KG Spring_pr_1_99_ble": 97,
    "KG Spring_ss_ble": 127,
    "KG Spring_dss_ble": 681
  },
  {
    "ID": 485,
    "KG Fall_theta_ble": 1.387595996,
    "KG Fall_pr_1_99_ble": 97,
    "KG Fall_ss_ble": 128,
    "KG Fall_dss_ble": 639,
    "KG Winter_theta_ble": 1.601927473,
    "KG Winter_pr_1_99_ble": 97,
    "KG Winter_ss_ble": 126,
    "KG Winter_dss_ble": 660,
    "KG Spring_theta_ble": 1.820897742,
    "KG Spring_pr_1_99_ble": 97,
    "KG Spring_ss_ble": 127,
    "KG Spring_dss_ble": 682
  },
  {
    "ID": 486,
    "KG Fall_theta_ble": 1.395498862,
    "KG Fall_pr_1_99_ble": 97,
    "KG Fall_ss_ble": 128,
    "KG Fall_dss_ble": 640,
    "KG Winter_theta_ble": 1.613965335,
    "KG Winter_pr_1_99_ble": 97,
    "KG Winter_ss_ble": 126,
    "KG Winter_dss_ble": 661,
    "KG Spring_theta_ble": 1.828603042,
    "KG Spring_pr_1_99_ble": 97,
    "KG Spring_ss_ble": 127,
    "KG Spring_dss_ble": 683
  },
  {
    "ID": 487,
    "KG Fall_theta_ble": 1.43660501,
    "KG Fall_pr_1_99_ble": 97,
    "KG Fall_ss_ble": 129,
    "KG Fall_dss_ble": 644,
    "KG Winter_theta_ble": 1.684640216,
    "KG Winter_pr_1_99_ble": 97,
    "KG Winter_ss_ble": 128,
    "KG Winter_dss_ble": 668,
    "KG Spring_theta_ble": 1.835436213,
    "KG Spring_pr_1_99_ble": 97,
    "KG Spring_ss_ble": 128,
    "KG Spring_dss_ble": 684
  },
  {
    "ID": 488,
    "KG Fall_theta_ble": 1.494559376,
    "KG Fall_pr_1_99_ble": 98,
    "KG Fall_ss_ble": 130,
    "KG Fall_dss_ble": 649,
    "KG Winter_theta_ble": 1.797053318,
    "KG Winter_pr_1_99_ble": 98,
    "KG Winter_ss_ble": 130,
    "KG Winter_dss_ble": 680,
    "KG Spring_theta_ble": 1.848989607,
    "KG Spring_pr_1_99_ble": 98,
    "KG Spring_ss_ble": 128,
    "KG Spring_dss_ble": 685
  },
  {
    "ID": 489,
    "KG Fall_theta_ble": 1.56109996,
    "KG Fall_pr_1_99_ble": 98,
    "KG Fall_ss_ble": 131,
    "KG Fall_dss_ble": 656,
    "KG Winter_theta_ble": 1.802115275,
    "KG Winter_pr_1_99_ble": 98,
    "KG Winter_ss_ble": 130,
    "KG Winter_dss_ble": 680,
    "KG Spring_theta_ble": 1.897526943,
    "KG Spring_pr_1_99_ble": 98,
    "KG Spring_ss_ble": 129,
    "KG Spring_dss_ble": 690
  },
  {
    "ID": 490,
    "KG Fall_theta_ble": 1.576440849,
    "KG Fall_pr_1_99_ble": 98,
    "KG Fall_ss_ble": 131,
    "KG Fall_dss_ble": 658,
    "KG Winter_theta_ble": 1.850692892,
    "KG Winter_pr_1_99_ble": 98,
    "KG Winter_ss_ble": 131,
    "KG Winter_dss_ble": 685,
    "KG Spring_theta_ble": 1.921713558,
    "KG Spring_pr_1_99_ble": 98,
    "KG Spring_ss_ble": 129,
    "KG Spring_dss_ble": 692
  },
  {
    "ID": 491,
    "KG Fall_theta_ble": 1.588317729,
    "KG Fall_pr_1_99_ble": 98,
    "KG Fall_ss_ble": 132,
    "KG Fall_dss_ble": 659,
    "KG Winter_theta_ble": 1.935013563,
    "KG Winter_pr_1_99_ble": 98,
    "KG Winter_ss_ble": 132,
    "KG Winter_dss_ble": 694,
    "KG Spring_theta_ble": 1.957101076,
    "KG Spring_pr_1_99_ble": 98,
    "KG Spring_ss_ble": 130,
    "KG Spring_dss_ble": 696
  },
  {
    "ID": 492,
    "KG Fall_theta_ble": 1.603811819,
    "KG Fall_pr_1_99_ble": 98,
    "KG Fall_ss_ble": 132,
    "KG Fall_dss_ble": 660,
    "KG Winter_theta_ble": 2.03230363,
    "KG Winter_pr_1_99_ble": 98,
    "KG Winter_ss_ble": 134,
    "KG Winter_dss_ble": 703,
    "KG Spring_theta_ble": 2.01286512,
    "KG Spring_pr_1_99_ble": 98,
    "KG Spring_ss_ble": 131,
    "KG Spring_dss_ble": 701
  },
  {
    "ID": 493,
    "KG Fall_theta_ble": 1.608486239,
    "KG Fall_pr_1_99_ble": 99,
    "KG Fall_ss_ble": 132,
    "KG Fall_dss_ble": 661,
    "KG Winter_theta_ble": 2.131097703,
    "KG Winter_pr_1_99_ble": 99,
    "KG Winter_ss_ble": 136,
    "KG Winter_dss_ble": 713,
    "KG Spring_theta_ble": 2.033302424,
    "KG Spring_pr_1_99_ble": 99,
    "KG Spring_ss_ble": 131,
    "KG Spring_dss_ble": 703
  },
  {
    "ID": 494,
    "KG Fall_theta_ble": 1.648325345,
    "KG Fall_pr_1_99_ble": 99,
    "KG Fall_ss_ble": 133,
    "KG Fall_dss_ble": 665,
    "KG Winter_theta_ble": 2.235699463,
    "KG Winter_pr_1_99_ble": 99,
    "KG Winter_ss_ble": 138,
    "KG Winter_dss_ble": 724,
    "KG Spring_theta_ble": 2.078860613,
    "KG Spring_pr_1_99_ble": 99,
    "KG Spring_ss_ble": 132,
    "KG Spring_dss_ble": 708
  },
  {
    "ID": 495,
    "KG Fall_theta_ble": 1.778945411,
    "KG Fall_pr_1_99_ble": 99,
    "KG Fall_ss_ble": 135,
    "KG Fall_dss_ble": 678,
    "KG Winter_theta_ble": 2.367351931,
    "KG Winter_pr_1_99_ble": 99,
    "KG Winter_ss_ble": 140,
    "KG Winter_dss_ble": 737,
    "KG Spring_theta_ble": 2.116534856,
    "KG Spring_pr_1_99_ble": 99,
    "KG Spring_ss_ble": 133,
    "KG Spring_dss_ble": 712
  },
  {
    "ID": 496,
    "KG Fall_theta_ble": 1.820762357,
    "KG Fall_pr_1_99_ble": 99,
    "KG Fall_ss_ble": 135,
    "KG Fall_dss_ble": 682,
    "KG Winter_theta_ble": 2.483129871,
    "KG Winter_pr_1_99_ble": 99,
    "KG Winter_ss_ble": 142,
    "KG Winter_dss_ble": 748,
    "KG Spring_theta_ble": 2.119942854,
    "KG Spring_pr_1_99_ble": 99,
    "KG Spring_ss_ble": 133,
    "KG Spring_dss_ble": 712
  },
  {
    "ID": 497,
    "KG Fall_theta_ble": 1.837750935,
    "KG Fall_pr_1_99_ble": 99,
    "KG Fall_ss_ble": 136,
    "KG Fall_dss_ble": 684,
    "KG Winter_theta_ble": 2.59394827,
    "KG Winter_pr_1_99_ble": 99,
    "KG Winter_ss_ble": 144,
    "KG Winter_dss_ble": 759,
    "KG Spring_theta_ble": 2.365475087,
    "KG Spring_pr_1_99_ble": 99,
    "KG Spring_ss_ble": 138,
    "KG Spring_dss_ble": 737
  },
  {
    "ID": 498,
    "KG Fall_theta_ble": 1.839668386,
    "KG Fall_pr_1_99_ble": 99,
    "KG Fall_ss_ble": 136,
    "KG Fall_dss_ble": 684,
    "KG Winter_theta_ble": 2.655910556,
    "KG Winter_pr_1_99_ble": 99,
    "KG Winter_ss_ble": 145,
    "KG Winter_dss_ble": 766,
    "KG Spring_theta_ble": 2.497975349,
    "KG Spring_pr_1_99_ble": 99,
    "KG Spring_ss_ble": 141,
    "KG Spring_dss_ble": 750
  },
  {
    "ID": 499,
    "KG Fall_theta_ble": 2.023266557,
    "KG Fall_pr_1_99_ble": 99,
    "KG Fall_ss_ble": 139,
    "KG Fall_dss_ble": 702,
    "KG Winter_theta_ble": 2.923654366,
    "KG Winter_pr_1_99_ble": 99,
    "KG Winter_ss_ble": 150,
    "KG Winter_dss_ble": 792,
    "KG Spring_theta_ble": 2.530209472,
    "KG Spring_pr_1_99_ble": 99,
    "KG Spring_ss_ble": 141,
    "KG Spring_dss_ble": 753
  },
  {
    "ID": 500,
    "KG Fall_theta_ble": 2.241584588,
    "KG Fall_pr_1_99_ble": 99,
    "KG Fall_ss_ble": 143,
    "KG Fall_dss_ble": 724,
    "KG Winter_theta_ble": 3.210060296,
    "KG Winter_pr_1_99_ble": 99,
    "KG Winter_ss_ble": 155,
    "KG Winter_dss_ble": 821,
    "KG Spring_theta_ble": 2.590019009,
    "KG Spring_pr_1_99_ble": 99,
    "KG Spring_ss_ble": 142,
    "KG Spring_dss_ble": 759
  }
];

export const del: any = [
  {
    "ID": 28,
    "KG Fall_theta_del": -3.913168613,
    "KG Fall_pr_1_99_del": 1,
    "KG Fall_ss_del": 49,
    "KG Fall_dss_del": 109,
    "KG Winter_theta_del": -3.503153809,
    "KG Winter_pr_1_99_del": 1,
    "KG Winter_ss_del": 53,
    "KG Winter_dss_del": 150,
    "KG Spring_theta_del": -3.395020856,
    "KG Spring_pr_1_99_del": 1,
    "KG Spring_ss_del": 47,
    "KG Spring_dss_del": 160,
    "G1 Fall_theta_del": -2.872375787,
    "G1 Fall_pr_1_99_del": 1,
    "G1 Fall_ss_del": 52,
    "G1 Fall_dss_del": 213,
    "G1 Winter_theta_del": -2.186882187,
    "G1 Winter_pr_1_99_del": 1,
    "G1 Winter_ss_del": 59,
    "G1 Winter_dss_del": 281,
    "G1 Spring_theta_del": -3.140536777,
    "G1 Spring_pr_1_99_del": 1,
    "G1 Spring_ss_del": 36,
    "G1 Spring_dss_del": 186,
    "G2 Fall_theta_del": -2.099394293,
    "G2 Fall_pr_1_99_del": 1,
    "G2 Fall_ss_del": 53,
    "G2 Fall_dss_del": 290,
    "G2 Winter_theta_del": -1.972352892,
    "G2 Winter_pr_1_99_del": 1,
    "G2 Winter_ss_del": 53,
    "G2 Winter_dss_del": 303,
    "G2 Spring_theta_del": -1.544692559,
    "G2 Spring_pr_1_99_del": 1,
    "G2 Spring_ss_del": 55,
    "G2 Spring_dss_del": 346
  },
  {
    "ID": 216,
    "KG Fall_theta_del": -3.520524914,
    "KG Fall_pr_1_99_del": 1,
    "KG Fall_ss_del": 56,
    "KG Fall_dss_del": 148,
    "KG Winter_theta_del": -3.021040649,
    "KG Winter_pr_1_99_del": 1,
    "KG Winter_ss_del": 62,
    "KG Winter_dss_del": 198,
    "KG Spring_theta_del": -2.757162835,
    "KG Spring_pr_1_99_del": 1,
    "KG Spring_ss_del": 59,
    "KG Spring_dss_del": 224,
    "G1 Fall_theta_del": -2.549001245,
    "G1 Fall_pr_1_99_del": 1,
    "G1 Fall_ss_del": 58,
    "G1 Fall_dss_del": 245,
    "G1 Winter_theta_del": -2.0927336,
    "G1 Winter_pr_1_99_del": 1,
    "G1 Winter_ss_del": 61,
    "G1 Winter_dss_del": 291,
    "G1 Spring_theta_del": -1.99625681,
    "G1 Spring_pr_1_99_del": 1,
    "G1 Spring_ss_del": 58,
    "G1 Spring_dss_del": 300,
    "G2 Fall_theta_del": -1.882358319,
    "G2 Fall_pr_1_99_del": 1,
    "G2 Fall_ss_del": 57,
    "G2 Fall_dss_del": 312,
    "G2 Winter_theta_del": -1.868158001,
    "G2 Winter_pr_1_99_del": 1,
    "G2 Winter_ss_del": 55,
    "G2 Winter_dss_del": 313,
    "G2 Spring_theta_del": -1.446972962,
    "G2 Spring_pr_1_99_del": 1,
    "G2 Spring_ss_del": 57,
    "G2 Spring_dss_del": 355
  },
  {
    "ID": 279,
    "KG Fall_theta_del": -3.471642277,
    "KG Fall_pr_1_99_del": 1,
    "KG Fall_ss_del": 57,
    "KG Fall_dss_del": 153,
    "KG Winter_theta_del": -2.785316675,
    "KG Winter_pr_1_99_del": 1,
    "KG Winter_ss_del": 66,
    "KG Winter_dss_del": 221,
    "KG Spring_theta_del": -2.637804772,
    "KG Spring_pr_1_99_del": 1,
    "KG Spring_ss_del": 61,
    "KG Spring_dss_del": 236,
    "G1 Fall_theta_del": -2.372330372,
    "G1 Fall_pr_1_99_del": 1,
    "G1 Fall_ss_del": 61,
    "G1 Fall_dss_del": 263,
    "G1 Winter_theta_del": -2.086298472,
    "G1 Winter_pr_1_99_del": 1,
    "G1 Winter_ss_del": 61,
    "G1 Winter_dss_del": 291,
    "G1 Spring_theta_del": -1.70270786,
    "G1 Spring_pr_1_99_del": 1,
    "G1 Spring_ss_del": 64,
    "G1 Spring_dss_del": 330,
    "G2 Fall_theta_del": -1.851071302,
    "G2 Fall_pr_1_99_del": 1,
    "G2 Fall_ss_del": 58,
    "G2 Fall_dss_del": 315,
    "G2 Winter_theta_del": -1.864087516,
    "G2 Winter_pr_1_99_del": 1,
    "G2 Winter_ss_del": 55,
    "G2 Winter_dss_del": 314,
    "G2 Spring_theta_del": -1.168500487,
    "G2 Spring_pr_1_99_del": 1,
    "G2 Spring_ss_del": 62,
    "G2 Spring_dss_del": 383
  },
  {
    "ID": 404,
    "KG Fall_theta_del": -3.131132768,
    "KG Fall_pr_1_99_del": 1,
    "KG Fall_ss_del": 63,
    "KG Fall_dss_del": 187,
    "KG Winter_theta_del": -2.726125075,
    "KG Winter_pr_1_99_del": 1,
    "KG Winter_ss_del": 67,
    "KG Winter_dss_del": 227,
    "KG Spring_theta_del": -2.561219238,
    "KG Spring_pr_1_99_del": 1,
    "KG Spring_ss_del": 63,
    "KG Spring_dss_del": 244,
    "G1 Fall_theta_del": -2.266193248,
    "G1 Fall_pr_1_99_del": 1,
    "G1 Fall_ss_del": 63,
    "G1 Fall_dss_del": 273,
    "G1 Winter_theta_del": -2.033036938,
    "G1 Winter_pr_1_99_del": 1,
    "G1 Winter_ss_del": 62,
    "G1 Winter_dss_del": 297,
    "G1 Spring_theta_del": -1.697771975,
    "G1 Spring_pr_1_99_del": 1,
    "G1 Spring_ss_del": 64,
    "G1 Spring_dss_del": 330,
    "G2 Fall_theta_del": -1.753751011,
    "G2 Fall_pr_1_99_del": 1,
    "G2 Fall_ss_del": 60,
    "G2 Fall_dss_del": 325,
    "G2 Winter_theta_del": -1.392728585,
    "G2 Winter_pr_1_99_del": 1,
    "G2 Winter_ss_del": 64,
    "G2 Winter_dss_del": 361,
    "G2 Spring_theta_del": -1.057296186,
    "G2 Spring_pr_1_99_del": 1,
    "G2 Spring_ss_del": 64,
    "G2 Spring_dss_del": 394
  },
  {
    "ID": 420,
    "KG Fall_theta_del": -3.017856239,
    "KG Fall_pr_1_99_del": 1,
    "KG Fall_ss_del": 66,
    "KG Fall_dss_del": 198,
    "KG Winter_theta_del": -2.690497622,
    "KG Winter_pr_1_99_del": 1,
    "KG Winter_ss_del": 67,
    "KG Winter_dss_del": 231,
    "KG Spring_theta_del": -2.420511827,
    "KG Spring_pr_1_99_del": 1,
    "KG Spring_ss_del": 65,
    "KG Spring_dss_del": 258,
    "G1 Fall_theta_del": -2.258286139,
    "G1 Fall_pr_1_99_del": 1,
    "G1 Fall_ss_del": 64,
    "G1 Fall_dss_del": 274,
    "G1 Winter_theta_del": -1.843400055,
    "G1 Winter_pr_1_99_del": 1,
    "G1 Winter_ss_del": 66,
    "G1 Winter_dss_del": 316,
    "G1 Spring_theta_del": -1.690943242,
    "G1 Spring_pr_1_99_del": 1,
    "G1 Spring_ss_del": 64,
    "G1 Spring_dss_del": 331,
    "G2 Fall_theta_del": -1.595669101,
    "G2 Fall_pr_1_99_del": 1,
    "G2 Fall_ss_del": 63,
    "G2 Fall_dss_del": 340,
    "G2 Winter_theta_del": -1.36768335,
    "G2 Winter_pr_1_99_del": 1,
    "G2 Winter_ss_del": 64,
    "G2 Winter_dss_del": 363,
    "G2 Spring_theta_del": -1.033977938,
    "G2 Spring_pr_1_99_del": 1,
    "G2 Spring_ss_del": 65,
    "G2 Spring_dss_del": 397
  },
  {
    "ID": 433,
    "KG Fall_theta_del": -2.864742806,
    "KG Fall_pr_1_99_del": 1,
    "KG Fall_ss_del": 68,
    "KG Fall_dss_del": 214,
    "KG Winter_theta_del": -2.660911002,
    "KG Winter_pr_1_99_del": 1,
    "KG Winter_ss_del": 68,
    "KG Winter_dss_del": 234,
    "KG Spring_theta_del": -2.356609873,
    "KG Spring_pr_1_99_del": 1,
    "KG Spring_ss_del": 67,
    "KG Spring_dss_del": 264,
    "G1 Fall_theta_del": -2.23911286,
    "G1 Fall_pr_1_99_del": 1,
    "G1 Fall_ss_del": 64,
    "G1 Fall_dss_del": 276,
    "G1 Winter_theta_del": -1.825750439,
    "G1 Winter_pr_1_99_del": 1,
    "G1 Winter_ss_del": 66,
    "G1 Winter_dss_del": 317,
    "G1 Spring_theta_del": -1.66807405,
    "G1 Spring_pr_1_99_del": 1,
    "G1 Spring_ss_del": 65,
    "G1 Spring_dss_del": 333,
    "G2 Fall_theta_del": -1.419252039,
    "G2 Fall_pr_1_99_del": 1,
    "G2 Fall_ss_del": 66,
    "G2 Fall_dss_del": 358,
    "G2 Winter_theta_del": -1.294724705,
    "G2 Winter_pr_1_99_del": 1,
    "G2 Winter_ss_del": 65,
    "G2 Winter_dss_del": 371,
    "G2 Spring_theta_del": -0.993117906,
    "G2 Spring_pr_1_99_del": 1,
    "G2 Spring_ss_del": 66,
    "G2 Spring_dss_del": 401
  },
  {
    "ID": 462,
    "KG Fall_theta_del": -2.800694816,
    "KG Fall_pr_1_99_del": 1,
    "KG Fall_ss_del": 70,
    "KG Fall_dss_del": 220,
    "KG Winter_theta_del": -2.645545161,
    "KG Winter_pr_1_99_del": 1,
    "KG Winter_ss_del": 68,
    "KG Winter_dss_del": 235,
    "KG Spring_theta_del": -2.328361939,
    "KG Spring_pr_1_99_del": 1,
    "KG Spring_ss_del": 67,
    "KG Spring_dss_del": 267,
    "G1 Fall_theta_del": -2.231656175,
    "G1 Fall_pr_1_99_del": 1,
    "G1 Fall_ss_del": 64,
    "G1 Fall_dss_del": 277,
    "G1 Winter_theta_del": -1.821978401,
    "G1 Winter_pr_1_99_del": 1,
    "G1 Winter_ss_del": 66,
    "G1 Winter_dss_del": 318,
    "G1 Spring_theta_del": -1.575484771,
    "G1 Spring_pr_1_99_del": 1,
    "G1 Spring_ss_del": 67,
    "G1 Spring_dss_del": 342,
    "G2 Fall_theta_del": -1.363333472,
    "G2 Fall_pr_1_99_del": 1,
    "G2 Fall_ss_del": 67,
    "G2 Fall_dss_del": 364,
    "G2 Winter_theta_del": -1.286605528,
    "G2 Winter_pr_1_99_del": 1,
    "G2 Winter_ss_del": 66,
    "G2 Winter_dss_del": 371,
    "G2 Spring_theta_del": -0.969278055,
    "G2 Spring_pr_1_99_del": 1,
    "G2 Spring_ss_del": 66,
    "G2 Spring_dss_del": 403
  },
  {
    "ID": 32,
    "KG Fall_theta_del": -2.786701863,
    "KG Fall_pr_1_99_del": 2,
    "KG Fall_ss_del": 70,
    "KG Fall_dss_del": 221,
    "KG Winter_theta_del": -2.639271116,
    "KG Winter_pr_1_99_del": 2,
    "KG Winter_ss_del": 68,
    "KG Winter_dss_del": 236,
    "KG Spring_theta_del": -2.272096781,
    "KG Spring_pr_1_99_del": 2,
    "KG Spring_ss_del": 68,
    "KG Spring_dss_del": 273,
    "G1 Fall_theta_del": -2.193195937,
    "G1 Fall_pr_1_99_del": 2,
    "G1 Fall_ss_del": 65,
    "G1 Fall_dss_del": 281,
    "G1 Winter_theta_del": -1.718613384,
    "G1 Winter_pr_1_99_del": 2,
    "G1 Winter_ss_del": 68,
    "G1 Winter_dss_del": 328,
    "G1 Spring_theta_del": -1.499075917,
    "G1 Spring_pr_1_99_del": 2,
    "G1 Spring_ss_del": 68,
    "G1 Spring_dss_del": 350,
    "G2 Fall_theta_del": -1.350053429,
    "G2 Fall_pr_1_99_del": 2,
    "G2 Fall_ss_del": 68,
    "G2 Fall_dss_del": 365,
    "G2 Winter_theta_del": -1.252971972,
    "G2 Winter_pr_1_99_del": 2,
    "G2 Winter_ss_del": 66,
    "G2 Winter_dss_del": 375,
    "G2 Spring_theta_del": -0.959345972,
    "G2 Spring_pr_1_99_del": 2,
    "G2 Spring_ss_del": 66,
    "G2 Spring_dss_del": 404
  },
  {
    "ID": 41,
    "KG Fall_theta_del": -2.771178671,
    "KG Fall_pr_1_99_del": 2,
    "KG Fall_ss_del": 70,
    "KG Fall_dss_del": 223,
    "KG Winter_theta_del": -2.619804256,
    "KG Winter_pr_1_99_del": 2,
    "KG Winter_ss_del": 69,
    "KG Winter_dss_del": 238,
    "KG Spring_theta_del": -2.218332781,
    "KG Spring_pr_1_99_del": 2,
    "KG Spring_ss_del": 69,
    "KG Spring_dss_del": 278,
    "G1 Fall_theta_del": -2.101177525,
    "G1 Fall_pr_1_99_del": 2,
    "G1 Fall_ss_del": 67,
    "G1 Fall_dss_del": 290,
    "G1 Winter_theta_del": -1.700966156,
    "G1 Winter_pr_1_99_del": 2,
    "G1 Winter_ss_del": 69,
    "G1 Winter_dss_del": 330,
    "G1 Spring_theta_del": -1.487263129,
    "G1 Spring_pr_1_99_del": 2,
    "G1 Spring_ss_del": 68,
    "G1 Spring_dss_del": 351,
    "G2 Fall_theta_del": -1.279003443,
    "G2 Fall_pr_1_99_del": 2,
    "G2 Fall_ss_del": 69,
    "G2 Fall_dss_del": 372,
    "G2 Winter_theta_del": -1.202908939,
    "G2 Winter_pr_1_99_del": 2,
    "G2 Winter_ss_del": 67,
    "G2 Winter_dss_del": 380,
    "G2 Spring_theta_del": -0.943417609,
    "G2 Spring_pr_1_99_del": 2,
    "G2 Spring_ss_del": 67,
    "G2 Spring_dss_del": 406
  },
  {
    "ID": 79,
    "KG Fall_theta_del": -2.768774376,
    "KG Fall_pr_1_99_del": 2,
    "KG Fall_ss_del": 70,
    "KG Fall_dss_del": 223,
    "KG Winter_theta_del": -2.553003116,
    "KG Winter_pr_1_99_del": 2,
    "KG Winter_ss_del": 70,
    "KG Winter_dss_del": 245,
    "KG Spring_theta_del": -2.209102611,
    "KG Spring_pr_1_99_del": 2,
    "KG Spring_ss_del": 69,
    "KG Spring_dss_del": 279,
    "G1 Fall_theta_del": -1.987117318,
    "G1 Fall_pr_1_99_del": 2,
    "G1 Fall_ss_del": 69,
    "G1 Fall_dss_del": 301,
    "G1 Winter_theta_del": -1.667350112,
    "G1 Winter_pr_1_99_del": 2,
    "G1 Winter_ss_del": 69,
    "G1 Winter_dss_del": 333,
    "G1 Spring_theta_del": -1.379816849,
    "G1 Spring_pr_1_99_del": 2,
    "G1 Spring_ss_del": 71,
    "G1 Spring_dss_del": 362,
    "G2 Fall_theta_del": -1.260822592,
    "G2 Fall_pr_1_99_del": 2,
    "G2 Fall_ss_del": 69,
    "G2 Fall_dss_del": 374,
    "G2 Winter_theta_del": -1.192420119,
    "G2 Winter_pr_1_99_del": 2,
    "G2 Winter_ss_del": 67,
    "G2 Winter_dss_del": 381,
    "G2 Spring_theta_del": -0.930683352,
    "G2 Spring_pr_1_99_del": 2,
    "G2 Spring_ss_del": 67,
    "G2 Spring_dss_del": 407
  },
  {
    "ID": 123,
    "KG Fall_theta_del": -2.763950448,
    "KG Fall_pr_1_99_del": 2,
    "KG Fall_ss_del": 70,
    "KG Fall_dss_del": 224,
    "KG Winter_theta_del": -2.517373373,
    "KG Winter_pr_1_99_del": 2,
    "KG Winter_ss_del": 71,
    "KG Winter_dss_del": 248,
    "KG Spring_theta_del": -2.187517365,
    "KG Spring_pr_1_99_del": 2,
    "KG Spring_ss_del": 70,
    "KG Spring_dss_del": 281,
    "G1 Fall_theta_del": -1.958946718,
    "G1 Fall_pr_1_99_del": 2,
    "G1 Fall_ss_del": 69,
    "G1 Fall_dss_del": 304,
    "G1 Winter_theta_del": -1.63850802,
    "G1 Winter_pr_1_99_del": 2,
    "G1 Winter_ss_del": 70,
    "G1 Winter_dss_del": 336,
    "G1 Spring_theta_del": -1.36122787,
    "G1 Spring_pr_1_99_del": 2,
    "G1 Spring_ss_del": 71,
    "G1 Spring_dss_del": 364,
    "G2 Fall_theta_del": -1.255277336,
    "G2 Fall_pr_1_99_del": 2,
    "G2 Fall_ss_del": 69,
    "G2 Fall_dss_del": 374,
    "G2 Winter_theta_del": -1.145029323,
    "G2 Winter_pr_1_99_del": 2,
    "G2 Winter_ss_del": 68,
    "G2 Winter_dss_del": 385,
    "G2 Spring_theta_del": -0.917853621,
    "G2 Spring_pr_1_99_del": 2,
    "G2 Spring_ss_del": 67,
    "G2 Spring_dss_del": 408
  },
  {
    "ID": 480,
    "KG Fall_theta_del": -2.738087466,
    "KG Fall_pr_1_99_del": 2,
    "KG Fall_ss_del": 71,
    "KG Fall_dss_del": 226,
    "KG Winter_theta_del": -2.501818898,
    "KG Winter_pr_1_99_del": 2,
    "KG Winter_ss_del": 71,
    "KG Winter_dss_del": 250,
    "KG Spring_theta_del": -2.154104474,
    "KG Spring_pr_1_99_del": 2,
    "KG Spring_ss_del": 70,
    "KG Spring_dss_del": 285,
    "G1 Fall_theta_del": -1.939895253,
    "G1 Fall_pr_1_99_del": 2,
    "G1 Fall_ss_del": 70,
    "G1 Fall_dss_del": 306,
    "G1 Winter_theta_del": -1.636356376,
    "G1 Winter_pr_1_99_del": 2,
    "G1 Winter_ss_del": 70,
    "G1 Winter_dss_del": 336,
    "G1 Spring_theta_del": -1.350273427,
    "G1 Spring_pr_1_99_del": 2,
    "G1 Spring_ss_del": 71,
    "G1 Spring_dss_del": 365,
    "G2 Fall_theta_del": -1.236050549,
    "G2 Fall_pr_1_99_del": 2,
    "G2 Fall_ss_del": 70,
    "G2 Fall_dss_del": 376,
    "G2 Winter_theta_del": -1.120655267,
    "G2 Winter_pr_1_99_del": 2,
    "G2 Winter_ss_del": 69,
    "G2 Winter_dss_del": 388,
    "G2 Spring_theta_del": -0.88915822,
    "G2 Spring_pr_1_99_del": 2,
    "G2 Spring_ss_del": 68,
    "G2 Spring_dss_del": 411
  },
  {
    "ID": 203,
    "KG Fall_theta_del": -2.722673673,
    "KG Fall_pr_1_99_del": 3,
    "KG Fall_ss_del": 71,
    "KG Fall_dss_del": 228,
    "KG Winter_theta_del": -2.461257647,
    "KG Winter_pr_1_99_del": 3,
    "KG Winter_ss_del": 72,
    "KG Winter_dss_del": 254,
    "KG Spring_theta_del": -2.1078684,
    "KG Spring_pr_1_99_del": 3,
    "KG Spring_ss_del": 71,
    "KG Spring_dss_del": 289,
    "G1 Fall_theta_del": -1.904305894,
    "G1 Fall_pr_1_99_del": 3,
    "G1 Fall_ss_del": 70,
    "G1 Fall_dss_del": 310,
    "G1 Winter_theta_del": -1.619829749,
    "G1 Winter_pr_1_99_del": 3,
    "G1 Winter_ss_del": 70,
    "G1 Winter_dss_del": 338,
    "G1 Spring_theta_del": -1.323494749,
    "G1 Spring_pr_1_99_del": 3,
    "G1 Spring_ss_del": 72,
    "G1 Spring_dss_del": 368,
    "G2 Fall_theta_del": -1.203920579,
    "G2 Fall_pr_1_99_del": 3,
    "G2 Fall_ss_del": 70,
    "G2 Fall_dss_del": 380,
    "G2 Winter_theta_del": -1.074696831,
    "G2 Winter_pr_1_99_del": 3,
    "G2 Winter_ss_del": 70,
    "G2 Winter_dss_del": 393,
    "G2 Spring_theta_del": -0.862838805,
    "G2 Spring_pr_1_99_del": 3,
    "G2 Spring_ss_del": 68,
    "G2 Spring_dss_del": 414
  },
  {
    "ID": 238,
    "KG Fall_theta_del": -2.671965146,
    "KG Fall_pr_1_99_del": 3,
    "KG Fall_ss_del": 72,
    "KG Fall_dss_del": 233,
    "KG Winter_theta_del": -2.460291452,
    "KG Winter_pr_1_99_del": 3,
    "KG Winter_ss_del": 72,
    "KG Winter_dss_del": 254,
    "KG Spring_theta_del": -2.087459626,
    "KG Spring_pr_1_99_del": 3,
    "KG Spring_ss_del": 72,
    "KG Spring_dss_del": 291,
    "G1 Fall_theta_del": -1.886547595,
    "G1 Fall_pr_1_99_del": 3,
    "G1 Fall_ss_del": 71,
    "G1 Fall_dss_del": 311,
    "G1 Winter_theta_del": -1.549691556,
    "G1 Winter_pr_1_99_del": 3,
    "G1 Winter_ss_del": 72,
    "G1 Winter_dss_del": 345,
    "G1 Spring_theta_del": -1.310961666,
    "G1 Spring_pr_1_99_del": 3,
    "G1 Spring_ss_del": 72,
    "G1 Spring_dss_del": 369,
    "G2 Fall_theta_del": -1.139423159,
    "G2 Fall_pr_1_99_del": 3,
    "G2 Fall_ss_del": 72,
    "G2 Fall_dss_del": 386,
    "G2 Winter_theta_del": -1.051855937,
    "G2 Winter_pr_1_99_del": 3,
    "G2 Winter_ss_del": 70,
    "G2 Winter_dss_del": 395,
    "G2 Spring_theta_del": -0.839176613,
    "G2 Spring_pr_1_99_del": 3,
    "G2 Spring_ss_del": 68,
    "G2 Spring_dss_del": 416
  },
  {
    "ID": 299,
    "KG Fall_theta_del": -2.667052457,
    "KG Fall_pr_1_99_del": 3,
    "KG Fall_ss_del": 72,
    "KG Fall_dss_del": 233,
    "KG Winter_theta_del": -2.449296615,
    "KG Winter_pr_1_99_del": 3,
    "KG Winter_ss_del": 72,
    "KG Winter_dss_del": 255,
    "KG Spring_theta_del": -2.077885394,
    "KG Spring_pr_1_99_del": 3,
    "KG Spring_ss_del": 72,
    "KG Spring_dss_del": 292,
    "G1 Fall_theta_del": -1.877718445,
    "G1 Fall_pr_1_99_del": 3,
    "G1 Fall_ss_del": 71,
    "G1 Fall_dss_del": 312,
    "G1 Winter_theta_del": -1.526454123,
    "G1 Winter_pr_1_99_del": 3,
    "G1 Winter_ss_del": 72,
    "G1 Winter_dss_del": 347,
    "G1 Spring_theta_del": -1.294428291,
    "G1 Spring_pr_1_99_del": 3,
    "G1 Spring_ss_del": 72,
    "G1 Spring_dss_del": 371,
    "G2 Fall_theta_del": -1.131086673,
    "G2 Fall_pr_1_99_del": 3,
    "G2 Fall_ss_del": 72,
    "G2 Fall_dss_del": 387,
    "G2 Winter_theta_del": -1.051842278,
    "G2 Winter_pr_1_99_del": 3,
    "G2 Winter_ss_del": 70,
    "G2 Winter_dss_del": 395,
    "G2 Spring_theta_del": -0.76929894,
    "G2 Spring_pr_1_99_del": 3,
    "G2 Spring_ss_del": 70,
    "G2 Spring_dss_del": 423
  },
  {
    "ID": 368,
    "KG Fall_theta_del": -2.653480032,
    "KG Fall_pr_1_99_del": 3,
    "KG Fall_ss_del": 72,
    "KG Fall_dss_del": 235,
    "KG Winter_theta_del": -2.40880389,
    "KG Winter_pr_1_99_del": 3,
    "KG Winter_ss_del": 73,
    "KG Winter_dss_del": 259,
    "KG Spring_theta_del": -2.06884486,
    "KG Spring_pr_1_99_del": 3,
    "KG Spring_ss_del": 72,
    "KG Spring_dss_del": 293,
    "G1 Fall_theta_del": -1.859041475,
    "G1 Fall_pr_1_99_del": 3,
    "G1 Fall_ss_del": 71,
    "G1 Fall_dss_del": 314,
    "G1 Winter_theta_del": -1.525143927,
    "G1 Winter_pr_1_99_del": 3,
    "G1 Winter_ss_del": 72,
    "G1 Winter_dss_del": 347,
    "G1 Spring_theta_del": -1.261575149,
    "G1 Spring_pr_1_99_del": 3,
    "G1 Spring_ss_del": 73,
    "G1 Spring_dss_del": 374,
    "G2 Fall_theta_del": -1.114994193,
    "G2 Fall_pr_1_99_del": 3,
    "G2 Fall_ss_del": 72,
    "G2 Fall_dss_del": 389,
    "G2 Winter_theta_del": -1.044742895,
    "G2 Winter_pr_1_99_del": 3,
    "G2 Winter_ss_del": 70,
    "G2 Winter_dss_del": 396,
    "G2 Spring_theta_del": -0.76339197,
    "G2 Spring_pr_1_99_del": 3,
    "G2 Spring_ss_del": 70,
    "G2 Spring_dss_del": 424
  },
  {
    "ID": 442,
    "KG Fall_theta_del": -2.640680812,
    "KG Fall_pr_1_99_del": 3,
    "KG Fall_ss_del": 73,
    "KG Fall_dss_del": 236,
    "KG Winter_theta_del": -2.40499347,
    "KG Winter_pr_1_99_del": 3,
    "KG Winter_ss_del": 73,
    "KG Winter_dss_del": 260,
    "KG Spring_theta_del": -2.044787828,
    "KG Spring_pr_1_99_del": 3,
    "KG Spring_ss_del": 72,
    "KG Spring_dss_del": 296,
    "G1 Fall_theta_del": -1.858846054,
    "G1 Fall_pr_1_99_del": 3,
    "G1 Fall_ss_del": 71,
    "G1 Fall_dss_del": 314,
    "G1 Winter_theta_del": -1.4779004,
    "G1 Winter_pr_1_99_del": 3,
    "G1 Winter_ss_del": 73,
    "G1 Winter_dss_del": 352,
    "G1 Spring_theta_del": -1.218380959,
    "G1 Spring_pr_1_99_del": 3,
    "G1 Spring_ss_del": 74,
    "G1 Spring_dss_del": 378,
    "G2 Fall_theta_del": -1.071680799,
    "G2 Fall_pr_1_99_del": 3,
    "G2 Fall_ss_del": 73,
    "G2 Fall_dss_del": 393,
    "G2 Winter_theta_del": -1.020990652,
    "G2 Winter_pr_1_99_del": 3,
    "G2 Winter_ss_del": 71,
    "G2 Winter_dss_del": 398,
    "G2 Spring_theta_del": -0.652838625,
    "G2 Spring_pr_1_99_del": 3,
    "G2 Spring_ss_del": 72,
    "G2 Spring_dss_del": 435
  },
  {
    "ID": 126,
    "KG Fall_theta_del": -2.636904759,
    "KG Fall_pr_1_99_del": 4,
    "KG Fall_ss_del": 73,
    "KG Fall_dss_del": 236,
    "KG Winter_theta_del": -2.394552414,
    "KG Winter_pr_1_99_del": 4,
    "KG Winter_ss_del": 73,
    "KG Winter_dss_del": 261,
    "KG Spring_theta_del": -2.041216584,
    "KG Spring_pr_1_99_del": 4,
    "KG Spring_ss_del": 72,
    "KG Spring_dss_del": 296,
    "G1 Fall_theta_del": -1.814752615,
    "G1 Fall_pr_1_99_del": 4,
    "G1 Fall_ss_del": 72,
    "G1 Fall_dss_del": 319,
    "G1 Winter_theta_del": -1.44555461,
    "G1 Winter_pr_1_99_del": 4,
    "G1 Winter_ss_del": 74,
    "G1 Winter_dss_del": 355,
    "G1 Spring_theta_del": -1.216285612,
    "G1 Spring_pr_1_99_del": 4,
    "G1 Spring_ss_del": 74,
    "G1 Spring_dss_del": 378,
    "G2 Fall_theta_del": -1.057128751,
    "G2 Fall_pr_1_99_del": 4,
    "G2 Fall_ss_del": 73,
    "G2 Fall_dss_del": 394,
    "G2 Winter_theta_del": -0.946892983,
    "G2 Winter_pr_1_99_del": 4,
    "G2 Winter_ss_del": 72,
    "G2 Winter_dss_del": 405,
    "G2 Spring_theta_del": -0.633938961,
    "G2 Spring_pr_1_99_del": 4,
    "G2 Spring_ss_del": 72,
    "G2 Spring_dss_del": 437
  },
  {
    "ID": 160,
    "KG Fall_theta_del": -2.610350899,
    "KG Fall_pr_1_99_del": 4,
    "KG Fall_ss_del": 73,
    "KG Fall_dss_del": 239,
    "KG Winter_theta_del": -2.383138676,
    "KG Winter_pr_1_99_del": 4,
    "KG Winter_ss_del": 73,
    "KG Winter_dss_del": 262,
    "KG Spring_theta_del": -2.036156399,
    "KG Spring_pr_1_99_del": 4,
    "KG Spring_ss_del": 72,
    "KG Spring_dss_del": 296,
    "G1 Fall_theta_del": -1.799762909,
    "G1 Fall_pr_1_99_del": 4,
    "G1 Fall_ss_del": 72,
    "G1 Fall_dss_del": 320,
    "G1 Winter_theta_del": -1.43706604,
    "G1 Winter_pr_1_99_del": 4,
    "G1 Winter_ss_del": 74,
    "G1 Winter_dss_del": 356,
    "G1 Spring_theta_del": -1.191981454,
    "G1 Spring_pr_1_99_del": 4,
    "G1 Spring_ss_del": 74,
    "G1 Spring_dss_del": 381,
    "G2 Fall_theta_del": -1.044591643,
    "G2 Fall_pr_1_99_del": 4,
    "G2 Fall_ss_del": 73,
    "G2 Fall_dss_del": 396,
    "G2 Winter_theta_del": -0.917256026,
    "G2 Winter_pr_1_99_del": 4,
    "G2 Winter_ss_del": 72,
    "G2 Winter_dss_del": 408,
    "G2 Spring_theta_del": -0.606962273,
    "G2 Spring_pr_1_99_del": 4,
    "G2 Spring_ss_del": 73,
    "G2 Spring_dss_del": 439
  },
  {
    "ID": 209,
    "KG Fall_theta_del": -2.606407172,
    "KG Fall_pr_1_99_del": 4,
    "KG Fall_ss_del": 73,
    "KG Fall_dss_del": 239,
    "KG Winter_theta_del": -2.324767328,
    "KG Winter_pr_1_99_del": 4,
    "KG Winter_ss_del": 74,
    "KG Winter_dss_del": 268,
    "KG Spring_theta_del": -1.976358611,
    "KG Spring_pr_1_99_del": 4,
    "KG Spring_ss_del": 74,
    "KG Spring_dss_del": 302,
    "G1 Fall_theta_del": -1.757805315,
    "G1 Fall_pr_1_99_del": 4,
    "G1 Fall_ss_del": 73,
    "G1 Fall_dss_del": 324,
    "G1 Winter_theta_del": -1.427580532,
    "G1 Winter_pr_1_99_del": 4,
    "G1 Winter_ss_del": 74,
    "G1 Winter_dss_del": 357,
    "G1 Spring_theta_del": -1.185583245,
    "G1 Spring_pr_1_99_del": 4,
    "G1 Spring_ss_del": 74,
    "G1 Spring_dss_del": 381,
    "G2 Fall_theta_del": -1.037370251,
    "G2 Fall_pr_1_99_del": 4,
    "G2 Fall_ss_del": 74,
    "G2 Fall_dss_del": 396,
    "G2 Winter_theta_del": -0.905566907,
    "G2 Winter_pr_1_99_del": 4,
    "G2 Winter_ss_del": 73,
    "G2 Winter_dss_del": 409,
    "G2 Spring_theta_del": -0.558909722,
    "G2 Spring_pr_1_99_del": 4,
    "G2 Spring_ss_del": 74,
    "G2 Spring_dss_del": 444
  },
  {
    "ID": 309,
    "KG Fall_theta_del": -2.601101566,
    "KG Fall_pr_1_99_del": 4,
    "KG Fall_ss_del": 73,
    "KG Fall_dss_del": 240,
    "KG Winter_theta_del": -2.320411868,
    "KG Winter_pr_1_99_del": 4,
    "KG Winter_ss_del": 74,
    "KG Winter_dss_del": 268,
    "KG Spring_theta_del": -1.974468174,
    "KG Spring_pr_1_99_del": 4,
    "KG Spring_ss_del": 74,
    "KG Spring_dss_del": 303,
    "G1 Fall_theta_del": -1.734988683,
    "G1 Fall_pr_1_99_del": 4,
    "G1 Fall_ss_del": 74,
    "G1 Fall_dss_del": 327,
    "G1 Winter_theta_del": -1.421746132,
    "G1 Winter_pr_1_99_del": 4,
    "G1 Winter_ss_del": 74,
    "G1 Winter_dss_del": 358,
    "G1 Spring_theta_del": -1.176581147,
    "G1 Spring_pr_1_99_del": 4,
    "G1 Spring_ss_del": 75,
    "G1 Spring_dss_del": 382,
    "G2 Fall_theta_del": -1.030618611,
    "G2 Fall_pr_1_99_del": 4,
    "G2 Fall_ss_del": 74,
    "G2 Fall_dss_del": 397,
    "G2 Winter_theta_del": -0.904039246,
    "G2 Winter_pr_1_99_del": 4,
    "G2 Winter_ss_del": 73,
    "G2 Winter_dss_del": 410,
    "G2 Spring_theta_del": -0.556871354,
    "G2 Spring_pr_1_99_del": 4,
    "G2 Spring_ss_del": 74,
    "G2 Spring_dss_del": 444
  },
  {
    "ID": 359,
    "KG Fall_theta_del": -2.597585848,
    "KG Fall_pr_1_99_del": 4,
    "KG Fall_ss_del": 73,
    "KG Fall_dss_del": 240,
    "KG Winter_theta_del": -2.312981778,
    "KG Winter_pr_1_99_del": 4,
    "KG Winter_ss_del": 74,
    "KG Winter_dss_del": 269,
    "KG Spring_theta_del": -1.962869658,
    "KG Spring_pr_1_99_del": 4,
    "KG Spring_ss_del": 74,
    "KG Spring_dss_del": 304,
    "G1 Fall_theta_del": -1.717427836,
    "G1 Fall_pr_1_99_del": 4,
    "G1 Fall_ss_del": 74,
    "G1 Fall_dss_del": 328,
    "G1 Winter_theta_del": -1.413858133,
    "G1 Winter_pr_1_99_del": 4,
    "G1 Winter_ss_del": 74,
    "G1 Winter_dss_del": 359,
    "G1 Spring_theta_del": -1.135286797,
    "G1 Spring_pr_1_99_del": 4,
    "G1 Spring_ss_del": 75,
    "G1 Spring_dss_del": 386,
    "G2 Fall_theta_del": -0.998573882,
    "G2 Fall_pr_1_99_del": 4,
    "G2 Fall_ss_del": 74,
    "G2 Fall_dss_del": 400,
    "G2 Winter_theta_del": -0.89892909,
    "G2 Winter_pr_1_99_del": 4,
    "G2 Winter_ss_del": 73,
    "G2 Winter_dss_del": 410,
    "G2 Spring_theta_del": -0.549949117,
    "G2 Spring_pr_1_99_del": 4,
    "G2 Spring_ss_del": 74,
    "G2 Spring_dss_del": 445
  },
  {
    "ID": 281,
    "KG Fall_theta_del": -2.588806542,
    "KG Fall_pr_1_99_del": 5,
    "KG Fall_ss_del": 74,
    "KG Fall_dss_del": 241,
    "KG Winter_theta_del": -2.293006855,
    "KG Winter_pr_1_99_del": 5,
    "KG Winter_ss_del": 75,
    "KG Winter_dss_del": 271,
    "KG Spring_theta_del": -1.954744779,
    "KG Spring_pr_1_99_del": 5,
    "KG Spring_ss_del": 74,
    "KG Spring_dss_del": 305,
    "G1 Fall_theta_del": -1.677277563,
    "G1 Fall_pr_1_99_del": 5,
    "G1 Fall_ss_del": 75,
    "G1 Fall_dss_del": 332,
    "G1 Winter_theta_del": -1.41346269,
    "G1 Winter_pr_1_99_del": 5,
    "G1 Winter_ss_del": 74,
    "G1 Winter_dss_del": 359,
    "G1 Spring_theta_del": -1.115976197,
    "G1 Spring_pr_1_99_del": 5,
    "G1 Spring_ss_del": 76,
    "G1 Spring_dss_del": 388,
    "G2 Fall_theta_del": -0.950043595,
    "G2 Fall_pr_1_99_del": 5,
    "G2 Fall_ss_del": 75,
    "G2 Fall_dss_del": 405,
    "G2 Winter_theta_del": -0.884326612,
    "G2 Winter_pr_1_99_del": 5,
    "G2 Winter_ss_del": 73,
    "G2 Winter_dss_del": 412,
    "G2 Spring_theta_del": -0.53805283,
    "G2 Spring_pr_1_99_del": 5,
    "G2 Spring_ss_del": 74,
    "G2 Spring_dss_del": 446
  },
  {
    "ID": 284,
    "KG Fall_theta_del": -2.582491521,
    "KG Fall_pr_1_99_del": 5,
    "KG Fall_ss_del": 74,
    "KG Fall_dss_del": 242,
    "KG Winter_theta_del": -2.288938475,
    "KG Winter_pr_1_99_del": 5,
    "KG Winter_ss_del": 75,
    "KG Winter_dss_del": 271,
    "KG Spring_theta_del": -1.913569841,
    "KG Spring_pr_1_99_del": 5,
    "KG Spring_ss_del": 75,
    "KG Spring_dss_del": 309,
    "G1 Fall_theta_del": -1.656199808,
    "G1 Fall_pr_1_99_del": 5,
    "G1 Fall_ss_del": 75,
    "G1 Fall_dss_del": 334,
    "G1 Winter_theta_del": -1.392110322,
    "G1 Winter_pr_1_99_del": 5,
    "G1 Winter_ss_del": 75,
    "G1 Winter_dss_del": 361,
    "G1 Spring_theta_del": -1.113569343,
    "G1 Spring_pr_1_99_del": 5,
    "G1 Spring_ss_del": 76,
    "G1 Spring_dss_del": 389,
    "G2 Fall_theta_del": -0.949163757,
    "G2 Fall_pr_1_99_del": 5,
    "G2 Fall_ss_del": 75,
    "G2 Fall_dss_del": 405,
    "G2 Winter_theta_del": -0.873842705,
    "G2 Winter_pr_1_99_del": 5,
    "G2 Winter_ss_del": 73,
    "G2 Winter_dss_del": 413,
    "G2 Spring_theta_del": -0.526659844,
    "G2 Spring_pr_1_99_del": 5,
    "G2 Spring_ss_del": 74,
    "G2 Spring_dss_del": 447
  },
  {
    "ID": 348,
    "KG Fall_theta_del": -2.558639056,
    "KG Fall_pr_1_99_del": 5,
    "KG Fall_ss_del": 74,
    "KG Fall_dss_del": 244,
    "KG Winter_theta_del": -2.282887363,
    "KG Winter_pr_1_99_del": 5,
    "KG Winter_ss_del": 75,
    "KG Winter_dss_del": 272,
    "KG Spring_theta_del": -1.904327149,
    "KG Spring_pr_1_99_del": 5,
    "KG Spring_ss_del": 75,
    "KG Spring_dss_del": 310,
    "G1 Fall_theta_del": -1.622012477,
    "G1 Fall_pr_1_99_del": 5,
    "G1 Fall_ss_del": 76,
    "G1 Fall_dss_del": 338,
    "G1 Winter_theta_del": -1.352194349,
    "G1 Winter_pr_1_99_del": 5,
    "G1 Winter_ss_del": 76,
    "G1 Winter_dss_del": 365,
    "G1 Spring_theta_del": -1.103388673,
    "G1 Spring_pr_1_99_del": 5,
    "G1 Spring_ss_del": 76,
    "G1 Spring_dss_del": 390,
    "G2 Fall_theta_del": -0.946040186,
    "G2 Fall_pr_1_99_del": 5,
    "G2 Fall_ss_del": 75,
    "G2 Fall_dss_del": 405,
    "G2 Winter_theta_del": -0.838724617,
    "G2 Winter_pr_1_99_del": 5,
    "G2 Winter_ss_del": 74,
    "G2 Winter_dss_del": 416,
    "G2 Spring_theta_del": -0.503330599,
    "G2 Spring_pr_1_99_del": 5,
    "G2 Spring_ss_del": 75,
    "G2 Spring_dss_del": 450
  },
  {
    "ID": 380,
    "KG Fall_theta_del": -2.504915183,
    "KG Fall_pr_1_99_del": 5,
    "KG Fall_ss_del": 75,
    "KG Fall_dss_del": 250,
    "KG Winter_theta_del": -2.267218235,
    "KG Winter_pr_1_99_del": 5,
    "KG Winter_ss_del": 75,
    "KG Winter_dss_del": 273,
    "KG Spring_theta_del": -1.898652103,
    "KG Spring_pr_1_99_del": 5,
    "KG Spring_ss_del": 75,
    "KG Spring_dss_del": 310,
    "G1 Fall_theta_del": -1.620824141,
    "G1 Fall_pr_1_99_del": 5,
    "G1 Fall_ss_del": 76,
    "G1 Fall_dss_del": 338,
    "G1 Winter_theta_del": -1.351500676,
    "G1 Winter_pr_1_99_del": 5,
    "G1 Winter_ss_del": 76,
    "G1 Winter_dss_del": 365,
    "G1 Spring_theta_del": -1.083809313,
    "G1 Spring_pr_1_99_del": 5,
    "G1 Spring_ss_del": 76,
    "G1 Spring_dss_del": 392,
    "G2 Fall_theta_del": -0.934699142,
    "G2 Fall_pr_1_99_del": 5,
    "G2 Fall_ss_del": 76,
    "G2 Fall_dss_del": 407,
    "G2 Winter_theta_del": -0.835868627,
    "G2 Winter_pr_1_99_del": 5,
    "G2 Winter_ss_del": 74,
    "G2 Winter_dss_del": 416,
    "G2 Spring_theta_del": -0.498993696,
    "G2 Spring_pr_1_99_del": 5,
    "G2 Spring_ss_del": 75,
    "G2 Spring_dss_del": 450
  },
  {
    "ID": 469,
    "KG Fall_theta_del": -2.473309941,
    "KG Fall_pr_1_99_del": 5,
    "KG Fall_ss_del": 76,
    "KG Fall_dss_del": 253,
    "KG Winter_theta_del": -2.222074329,
    "KG Winter_pr_1_99_del": 5,
    "KG Winter_ss_del": 76,
    "KG Winter_dss_del": 278,
    "KG Spring_theta_del": -1.887156169,
    "KG Spring_pr_1_99_del": 5,
    "KG Spring_ss_del": 75,
    "KG Spring_dss_del": 311,
    "G1 Fall_theta_del": -1.618858736,
    "G1 Fall_pr_1_99_del": 5,
    "G1 Fall_ss_del": 76,
    "G1 Fall_dss_del": 338,
    "G1 Winter_theta_del": -1.31370737,
    "G1 Winter_pr_1_99_del": 5,
    "G1 Winter_ss_del": 76,
    "G1 Winter_dss_del": 369,
    "G1 Spring_theta_del": -1.068666454,
    "G1 Spring_pr_1_99_del": 5,
    "G1 Spring_ss_del": 77,
    "G1 Spring_dss_del": 393,
    "G2 Fall_theta_del": -0.90885756,
    "G2 Fall_pr_1_99_del": 5,
    "G2 Fall_ss_del": 76,
    "G2 Fall_dss_del": 409,
    "G2 Winter_theta_del": -0.833522399,
    "G2 Winter_pr_1_99_del": 5,
    "G2 Winter_ss_del": 74,
    "G2 Winter_dss_del": 417,
    "G2 Spring_theta_del": -0.485992311,
    "G2 Spring_pr_1_99_del": 5,
    "G2 Spring_ss_del": 75,
    "G2 Spring_dss_del": 451
  },
  {
    "ID": 234,
    "KG Fall_theta_del": -2.426245688,
    "KG Fall_pr_1_99_del": 6,
    "KG Fall_ss_del": 77,
    "KG Fall_dss_del": 257,
    "KG Winter_theta_del": -2.212967733,
    "KG Winter_pr_1_99_del": 6,
    "KG Winter_ss_del": 76,
    "KG Winter_dss_del": 279,
    "KG Spring_theta_del": -1.868653656,
    "KG Spring_pr_1_99_del": 6,
    "KG Spring_ss_del": 76,
    "KG Spring_dss_del": 313,
    "G1 Fall_theta_del": -1.615077812,
    "G1 Fall_pr_1_99_del": 6,
    "G1 Fall_ss_del": 76,
    "G1 Fall_dss_del": 338,
    "G1 Winter_theta_del": -1.30275057,
    "G1 Winter_pr_1_99_del": 6,
    "G1 Winter_ss_del": 76,
    "G1 Winter_dss_del": 370,
    "G1 Spring_theta_del": -1.06078946,
    "G1 Spring_pr_1_99_del": 6,
    "G1 Spring_ss_del": 77,
    "G1 Spring_dss_del": 394,
    "G2 Fall_theta_del": -0.892104857,
    "G2 Fall_pr_1_99_del": 6,
    "G2 Fall_ss_del": 76,
    "G2 Fall_dss_del": 411,
    "G2 Winter_theta_del": -0.82008341,
    "G2 Winter_pr_1_99_del": 6,
    "G2 Winter_ss_del": 74,
    "G2 Winter_dss_del": 418,
    "G2 Spring_theta_del": -0.480706719,
    "G2 Spring_pr_1_99_del": 6,
    "G2 Spring_ss_del": 75,
    "G2 Spring_dss_del": 452
  },
  {
    "ID": 291,
    "KG Fall_theta_del": -2.422287213,
    "KG Fall_pr_1_99_del": 6,
    "KG Fall_ss_del": 77,
    "KG Fall_dss_del": 258,
    "KG Winter_theta_del": -2.202925916,
    "KG Winter_pr_1_99_del": 6,
    "KG Winter_ss_del": 76,
    "KG Winter_dss_del": 280,
    "KG Spring_theta_del": -1.850224033,
    "KG Spring_pr_1_99_del": 6,
    "KG Spring_ss_del": 76,
    "KG Spring_dss_del": 315,
    "G1 Fall_theta_del": -1.611164669,
    "G1 Fall_pr_1_99_del": 6,
    "G1 Fall_ss_del": 76,
    "G1 Fall_dss_del": 339,
    "G1 Winter_theta_del": -1.279959196,
    "G1 Winter_pr_1_99_del": 6,
    "G1 Winter_ss_del": 77,
    "G1 Winter_dss_del": 372,
    "G1 Spring_theta_del": -1.060370515,
    "G1 Spring_pr_1_99_del": 6,
    "G1 Spring_ss_del": 77,
    "G1 Spring_dss_del": 394,
    "G2 Fall_theta_del": -0.890289371,
    "G2 Fall_pr_1_99_del": 6,
    "G2 Fall_ss_del": 76,
    "G2 Fall_dss_del": 411,
    "G2 Winter_theta_del": -0.79477447,
    "G2 Winter_pr_1_99_del": 6,
    "G2 Winter_ss_del": 75,
    "G2 Winter_dss_del": 421,
    "G2 Spring_theta_del": -0.450720532,
    "G2 Spring_pr_1_99_del": 6,
    "G2 Spring_ss_del": 76,
    "G2 Spring_dss_del": 455
  },
  {
    "ID": 319,
    "KG Fall_theta_del": -2.41710728,
    "KG Fall_pr_1_99_del": 6,
    "KG Fall_ss_del": 77,
    "KG Fall_dss_del": 258,
    "KG Winter_theta_del": -2.184929818,
    "KG Winter_pr_1_99_del": 6,
    "KG Winter_ss_del": 77,
    "KG Winter_dss_del": 282,
    "KG Spring_theta_del": -1.848211524,
    "KG Spring_pr_1_99_del": 6,
    "KG Spring_ss_del": 76,
    "KG Spring_dss_del": 315,
    "G1 Fall_theta_del": -1.601453933,
    "G1 Fall_pr_1_99_del": 6,
    "G1 Fall_ss_del": 76,
    "G1 Fall_dss_del": 340,
    "G1 Winter_theta_del": -1.279203455,
    "G1 Winter_pr_1_99_del": 6,
    "G1 Winter_ss_del": 77,
    "G1 Winter_dss_del": 372,
    "G1 Spring_theta_del": -1.050304444,
    "G1 Spring_pr_1_99_del": 6,
    "G1 Spring_ss_del": 77,
    "G1 Spring_dss_del": 395,
    "G2 Fall_theta_del": -0.888759168,
    "G2 Fall_pr_1_99_del": 6,
    "G2 Fall_ss_del": 76,
    "G2 Fall_dss_del": 411,
    "G2 Winter_theta_del": -0.792242802,
    "G2 Winter_pr_1_99_del": 6,
    "G2 Winter_ss_del": 75,
    "G2 Winter_dss_del": 421,
    "G2 Spring_theta_del": -0.434067301,
    "G2 Spring_pr_1_99_del": 6,
    "G2 Spring_ss_del": 76,
    "G2 Spring_dss_del": 457
  },
  {
    "ID": 364,
    "KG Fall_theta_del": -2.412514022,
    "KG Fall_pr_1_99_del": 6,
    "KG Fall_ss_del": 77,
    "KG Fall_dss_del": 259,
    "KG Winter_theta_del": -2.178792444,
    "KG Winter_pr_1_99_del": 6,
    "KG Winter_ss_del": 77,
    "KG Winter_dss_del": 282,
    "KG Spring_theta_del": -1.835200631,
    "KG Spring_pr_1_99_del": 6,
    "KG Spring_ss_del": 76,
    "KG Spring_dss_del": 316,
    "G1 Fall_theta_del": -1.568912822,
    "G1 Fall_pr_1_99_del": 6,
    "G1 Fall_ss_del": 77,
    "G1 Fall_dss_del": 343,
    "G1 Winter_theta_del": -1.256149321,
    "G1 Winter_pr_1_99_del": 6,
    "G1 Winter_ss_del": 77,
    "G1 Winter_dss_del": 374,
    "G1 Spring_theta_del": -1.046291266,
    "G1 Spring_pr_1_99_del": 6,
    "G1 Spring_ss_del": 77,
    "G1 Spring_dss_del": 395,
    "G2 Fall_theta_del": -0.886510281,
    "G2 Fall_pr_1_99_del": 6,
    "G2 Fall_ss_del": 76,
    "G2 Fall_dss_del": 411,
    "G2 Winter_theta_del": -0.728402802,
    "G2 Winter_pr_1_99_del": 6,
    "G2 Winter_ss_del": 76,
    "G2 Winter_dss_del": 427,
    "G2 Spring_theta_del": -0.423173569,
    "G2 Spring_pr_1_99_del": 6,
    "G2 Spring_ss_del": 76,
    "G2 Spring_dss_del": 458
  },
  {
    "ID": 397,
    "KG Fall_theta_del": -2.410068031,
    "KG Fall_pr_1_99_del": 6,
    "KG Fall_ss_del": 77,
    "KG Fall_dss_del": 259,
    "KG Winter_theta_del": -2.172329974,
    "KG Winter_pr_1_99_del": 6,
    "KG Winter_ss_del": 77,
    "KG Winter_dss_del": 283,
    "KG Spring_theta_del": -1.804798386,
    "KG Spring_pr_1_99_del": 6,
    "KG Spring_ss_del": 77,
    "KG Spring_dss_del": 320,
    "G1 Fall_theta_del": -1.542097211,
    "G1 Fall_pr_1_99_del": 6,
    "G1 Fall_ss_del": 77,
    "G1 Fall_dss_del": 346,
    "G1 Winter_theta_del": -1.246430307,
    "G1 Winter_pr_1_99_del": 6,
    "G1 Winter_ss_del": 78,
    "G1 Winter_dss_del": 375,
    "G1 Spring_theta_del": -1.032853349,
    "G1 Spring_pr_1_99_del": 6,
    "G1 Spring_ss_del": 77,
    "G1 Spring_dss_del": 397,
    "G2 Fall_theta_del": -0.885480217,
    "G2 Fall_pr_1_99_del": 6,
    "G2 Fall_ss_del": 77,
    "G2 Fall_dss_del": 411,
    "G2 Winter_theta_del": -0.705847998,
    "G2 Winter_pr_1_99_del": 6,
    "G2 Winter_ss_del": 76,
    "G2 Winter_dss_del": 429,
    "G2 Spring_theta_del": -0.413254126,
    "G2 Spring_pr_1_99_del": 6,
    "G2 Spring_ss_del": 76,
    "G2 Spring_dss_del": 459
  },
  {
    "ID": 74,
    "KG Fall_theta_del": -2.400349022,
    "KG Fall_pr_1_99_del": 7,
    "KG Fall_ss_del": 77,
    "KG Fall_dss_del": 260,
    "KG Winter_theta_del": -2.170377664,
    "KG Winter_pr_1_99_del": 7,
    "KG Winter_ss_del": 77,
    "KG Winter_dss_del": 283,
    "KG Spring_theta_del": -1.797479916,
    "KG Spring_pr_1_99_del": 7,
    "KG Spring_ss_del": 77,
    "KG Spring_dss_del": 320,
    "G1 Fall_theta_del": -1.54009469,
    "G1 Fall_pr_1_99_del": 7,
    "G1 Fall_ss_del": 77,
    "G1 Fall_dss_del": 346,
    "G1 Winter_theta_del": -1.246398058,
    "G1 Winter_pr_1_99_del": 7,
    "G1 Winter_ss_del": 78,
    "G1 Winter_dss_del": 375,
    "G1 Spring_theta_del": -1.026992586,
    "G1 Spring_pr_1_99_del": 7,
    "G1 Spring_ss_del": 78,
    "G1 Spring_dss_del": 397,
    "G2 Fall_theta_del": -0.850276661,
    "G2 Fall_pr_1_99_del": 7,
    "G2 Fall_ss_del": 77,
    "G2 Fall_dss_del": 415,
    "G2 Winter_theta_del": -0.703974706,
    "G2 Winter_pr_1_99_del": 7,
    "G2 Winter_ss_del": 76,
    "G2 Winter_dss_del": 430,
    "G2 Spring_theta_del": -0.406339097,
    "G2 Spring_pr_1_99_del": 7,
    "G2 Spring_ss_del": 77,
    "G2 Spring_dss_del": 459
  },
  {
    "ID": 83,
    "KG Fall_theta_del": -2.388469808,
    "KG Fall_pr_1_99_del": 7,
    "KG Fall_ss_del": 77,
    "KG Fall_dss_del": 261,
    "KG Winter_theta_del": -2.162803064,
    "KG Winter_pr_1_99_del": 7,
    "KG Winter_ss_del": 77,
    "KG Winter_dss_del": 284,
    "KG Spring_theta_del": -1.773431814,
    "KG Spring_pr_1_99_del": 7,
    "KG Spring_ss_del": 77,
    "KG Spring_dss_del": 323,
    "G1 Fall_theta_del": -1.536394538,
    "G1 Fall_pr_1_99_del": 7,
    "G1 Fall_ss_del": 77,
    "G1 Fall_dss_del": 346,
    "G1 Winter_theta_del": -1.24163532,
    "G1 Winter_pr_1_99_del": 7,
    "G1 Winter_ss_del": 78,
    "G1 Winter_dss_del": 376,
    "G1 Spring_theta_del": -1.010900435,
    "G1 Spring_pr_1_99_del": 7,
    "G1 Spring_ss_del": 78,
    "G1 Spring_dss_del": 399,
    "G2 Fall_theta_del": -0.843656595,
    "G2 Fall_pr_1_99_del": 7,
    "G2 Fall_ss_del": 77,
    "G2 Fall_dss_del": 416,
    "G2 Winter_theta_del": -0.693858551,
    "G2 Winter_pr_1_99_del": 7,
    "G2 Winter_ss_del": 77,
    "G2 Winter_dss_del": 431,
    "G2 Spring_theta_del": -0.400904633,
    "G2 Spring_pr_1_99_del": 7,
    "G2 Spring_ss_del": 77,
    "G2 Spring_dss_del": 460
  },
  {
    "ID": 173,
    "KG Fall_theta_del": -2.385358855,
    "KG Fall_pr_1_99_del": 7,
    "KG Fall_ss_del": 77,
    "KG Fall_dss_del": 261,
    "KG Winter_theta_del": -2.14759302,
    "KG Winter_pr_1_99_del": 7,
    "KG Winter_ss_del": 77,
    "KG Winter_dss_del": 285,
    "KG Spring_theta_del": -1.762345059,
    "KG Spring_pr_1_99_del": 7,
    "KG Spring_ss_del": 78,
    "KG Spring_dss_del": 324,
    "G1 Fall_theta_del": -1.527558288,
    "G1 Fall_pr_1_99_del": 7,
    "G1 Fall_ss_del": 78,
    "G1 Fall_dss_del": 347,
    "G1 Winter_theta_del": -1.221596994,
    "G1 Winter_pr_1_99_del": 7,
    "G1 Winter_ss_del": 78,
    "G1 Winter_dss_del": 378,
    "G1 Spring_theta_del": -1.007532908,
    "G1 Spring_pr_1_99_del": 7,
    "G1 Spring_ss_del": 78,
    "G1 Spring_dss_del": 399,
    "G2 Fall_theta_del": -0.838110054,
    "G2 Fall_pr_1_99_del": 7,
    "G2 Fall_ss_del": 77,
    "G2 Fall_dss_del": 416,
    "G2 Winter_theta_del": -0.692311645,
    "G2 Winter_pr_1_99_del": 7,
    "G2 Winter_ss_del": 77,
    "G2 Winter_dss_del": 431,
    "G2 Spring_theta_del": -0.398260339,
    "G2 Spring_pr_1_99_del": 7,
    "G2 Spring_ss_del": 77,
    "G2 Spring_dss_del": 460
  },
  {
    "ID": 293,
    "KG Fall_theta_del": -2.366466674,
    "KG Fall_pr_1_99_del": 7,
    "KG Fall_ss_del": 78,
    "KG Fall_dss_del": 263,
    "KG Winter_theta_del": -2.139013329,
    "KG Winter_pr_1_99_del": 7,
    "KG Winter_ss_del": 77,
    "KG Winter_dss_del": 286,
    "KG Spring_theta_del": -1.759054462,
    "KG Spring_pr_1_99_del": 7,
    "KG Spring_ss_del": 78,
    "KG Spring_dss_del": 324,
    "G1 Fall_theta_del": -1.523943433,
    "G1 Fall_pr_1_99_del": 7,
    "G1 Fall_ss_del": 78,
    "G1 Fall_dss_del": 348,
    "G1 Winter_theta_del": -1.219239443,
    "G1 Winter_pr_1_99_del": 7,
    "G1 Winter_ss_del": 78,
    "G1 Winter_dss_del": 378,
    "G1 Spring_theta_del": -0.984808093,
    "G1 Spring_pr_1_99_del": 7,
    "G1 Spring_ss_del": 78,
    "G1 Spring_dss_del": 402,
    "G2 Fall_theta_del": -0.834901717,
    "G2 Fall_pr_1_99_del": 7,
    "G2 Fall_ss_del": 77,
    "G2 Fall_dss_del": 417,
    "G2 Winter_theta_del": -0.681439226,
    "G2 Winter_pr_1_99_del": 7,
    "G2 Winter_ss_del": 77,
    "G2 Winter_dss_del": 432,
    "G2 Spring_theta_del": -0.364858322,
    "G2 Spring_pr_1_99_del": 7,
    "G2 Spring_ss_del": 77,
    "G2 Spring_dss_del": 464
  },
  {
    "ID": 315,
    "KG Fall_theta_del": -2.358845257,
    "KG Fall_pr_1_99_del": 7,
    "KG Fall_ss_del": 78,
    "KG Fall_dss_del": 264,
    "KG Winter_theta_del": -2.119458093,
    "KG Winter_pr_1_99_del": 7,
    "KG Winter_ss_del": 78,
    "KG Winter_dss_del": 288,
    "KG Spring_theta_del": -1.745052834,
    "KG Spring_pr_1_99_del": 7,
    "KG Spring_ss_del": 78,
    "KG Spring_dss_del": 325,
    "G1 Fall_theta_del": -1.495928619,
    "G1 Fall_pr_1_99_del": 7,
    "G1 Fall_ss_del": 78,
    "G1 Fall_dss_del": 350,
    "G1 Winter_theta_del": -1.215343888,
    "G1 Winter_pr_1_99_del": 7,
    "G1 Winter_ss_del": 78,
    "G1 Winter_dss_del": 378,
    "G1 Spring_theta_del": -0.983599413,
    "G1 Spring_pr_1_99_del": 7,
    "G1 Spring_ss_del": 78,
    "G1 Spring_dss_del": 402,
    "G2 Fall_theta_del": -0.82674438,
    "G2 Fall_pr_1_99_del": 7,
    "G2 Fall_ss_del": 78,
    "G2 Fall_dss_del": 417,
    "G2 Winter_theta_del": -0.658725394,
    "G2 Winter_pr_1_99_del": 7,
    "G2 Winter_ss_del": 77,
    "G2 Winter_dss_del": 434,
    "G2 Spring_theta_del": -0.343730055,
    "G2 Spring_pr_1_99_del": 7,
    "G2 Spring_ss_del": 78,
    "G2 Spring_dss_del": 466
  },
  {
    "ID": 116,
    "KG Fall_theta_del": -2.35384021,
    "KG Fall_pr_1_99_del": 8,
    "KG Fall_ss_del": 78,
    "KG Fall_dss_del": 265,
    "KG Winter_theta_del": -2.111404961,
    "KG Winter_pr_1_99_del": 8,
    "KG Winter_ss_del": 78,
    "KG Winter_dss_del": 289,
    "KG Spring_theta_del": -1.726204146,
    "KG Spring_pr_1_99_del": 8,
    "KG Spring_ss_del": 78,
    "KG Spring_dss_del": 327,
    "G1 Fall_theta_del": -1.489853784,
    "G1 Fall_pr_1_99_del": 8,
    "G1 Fall_ss_del": 78,
    "G1 Fall_dss_del": 351,
    "G1 Winter_theta_del": -1.214597637,
    "G1 Winter_pr_1_99_del": 8,
    "G1 Winter_ss_del": 78,
    "G1 Winter_dss_del": 379,
    "G1 Spring_theta_del": -0.97467655,
    "G1 Spring_pr_1_99_del": 8,
    "G1 Spring_ss_del": 79,
    "G1 Spring_dss_del": 403,
    "G2 Fall_theta_del": -0.817214971,
    "G2 Fall_pr_1_99_del": 8,
    "G2 Fall_ss_del": 78,
    "G2 Fall_dss_del": 418,
    "G2 Winter_theta_del": -0.657283841,
    "G2 Winter_pr_1_99_del": 8,
    "G2 Winter_ss_del": 77,
    "G2 Winter_dss_del": 434,
    "G2 Spring_theta_del": -0.341504284,
    "G2 Spring_pr_1_99_del": 8,
    "G2 Spring_ss_del": 78,
    "G2 Spring_dss_del": 466
  },
  {
    "ID": 163,
    "KG Fall_theta_del": -2.342067561,
    "KG Fall_pr_1_99_del": 8,
    "KG Fall_ss_del": 78,
    "KG Fall_dss_del": 266,
    "KG Winter_theta_del": -2.064299991,
    "KG Winter_pr_1_99_del": 8,
    "KG Winter_ss_del": 79,
    "KG Winter_dss_del": 294,
    "KG Spring_theta_del": -1.715199891,
    "KG Spring_pr_1_99_del": 8,
    "KG Spring_ss_del": 78,
    "KG Spring_dss_del": 328,
    "G1 Fall_theta_del": -1.486027455,
    "G1 Fall_pr_1_99_del": 8,
    "G1 Fall_ss_del": 78,
    "G1 Fall_dss_del": 351,
    "G1 Winter_theta_del": -1.208200553,
    "G1 Winter_pr_1_99_del": 8,
    "G1 Winter_ss_del": 78,
    "G1 Winter_dss_del": 379,
    "G1 Spring_theta_del": -0.971001458,
    "G1 Spring_pr_1_99_del": 8,
    "G1 Spring_ss_del": 79,
    "G1 Spring_dss_del": 403,
    "G2 Fall_theta_del": -0.802546552,
    "G2 Fall_pr_1_99_del": 8,
    "G2 Fall_ss_del": 78,
    "G2 Fall_dss_del": 420,
    "G2 Winter_theta_del": -0.648471013,
    "G2 Winter_pr_1_99_del": 8,
    "G2 Winter_ss_del": 78,
    "G2 Winter_dss_del": 435,
    "G2 Spring_theta_del": -0.313806068,
    "G2 Spring_pr_1_99_del": 8,
    "G2 Spring_ss_del": 78,
    "G2 Spring_dss_del": 469
  },
  {
    "ID": 185,
    "KG Fall_theta_del": -2.341228258,
    "KG Fall_pr_1_99_del": 8,
    "KG Fall_ss_del": 78,
    "KG Fall_dss_del": 266,
    "KG Winter_theta_del": -2.043657771,
    "KG Winter_pr_1_99_del": 8,
    "KG Winter_ss_del": 79,
    "KG Winter_dss_del": 296,
    "KG Spring_theta_del": -1.713122521,
    "KG Spring_pr_1_99_del": 8,
    "KG Spring_ss_del": 78,
    "KG Spring_dss_del": 329,
    "G1 Fall_theta_del": -1.463136154,
    "G1 Fall_pr_1_99_del": 8,
    "G1 Fall_ss_del": 79,
    "G1 Fall_dss_del": 354,
    "G1 Winter_theta_del": -1.206291424,
    "G1 Winter_pr_1_99_del": 8,
    "G1 Winter_ss_del": 78,
    "G1 Winter_dss_del": 379,
    "G1 Spring_theta_del": -0.961004224,
    "G1 Spring_pr_1_99_del": 8,
    "G1 Spring_ss_del": 79,
    "G1 Spring_dss_del": 404,
    "G2 Fall_theta_del": -0.794515233,
    "G2 Fall_pr_1_99_del": 8,
    "G2 Fall_ss_del": 78,
    "G2 Fall_dss_del": 421,
    "G2 Winter_theta_del": -0.623772837,
    "G2 Winter_pr_1_99_del": 8,
    "G2 Winter_ss_del": 78,
    "G2 Winter_dss_del": 438,
    "G2 Spring_theta_del": -0.311011158,
    "G2 Spring_pr_1_99_del": 8,
    "G2 Spring_ss_del": 78,
    "G2 Spring_dss_del": 469
  },
  {
    "ID": 262,
    "KG Fall_theta_del": -2.275062352,
    "KG Fall_pr_1_99_del": 8,
    "KG Fall_ss_del": 79,
    "KG Fall_dss_del": 272,
    "KG Winter_theta_del": -2.033402939,
    "KG Winter_pr_1_99_del": 8,
    "KG Winter_ss_del": 79,
    "KG Winter_dss_del": 297,
    "KG Spring_theta_del": -1.685318957,
    "KG Spring_pr_1_99_del": 8,
    "KG Spring_ss_del": 79,
    "KG Spring_dss_del": 331,
    "G1 Fall_theta_del": -1.456166006,
    "G1 Fall_pr_1_99_del": 8,
    "G1 Fall_ss_del": 79,
    "G1 Fall_dss_del": 354,
    "G1 Winter_theta_del": -1.205804513,
    "G1 Winter_pr_1_99_del": 8,
    "G1 Winter_ss_del": 78,
    "G1 Winter_dss_del": 379,
    "G1 Spring_theta_del": -0.956657169,
    "G1 Spring_pr_1_99_del": 8,
    "G1 Spring_ss_del": 79,
    "G1 Spring_dss_del": 404,
    "G2 Fall_theta_del": -0.782793472,
    "G2 Fall_pr_1_99_del": 8,
    "G2 Fall_ss_del": 78,
    "G2 Fall_dss_del": 422,
    "G2 Winter_theta_del": -0.613921112,
    "G2 Winter_pr_1_99_del": 8,
    "G2 Winter_ss_del": 78,
    "G2 Winter_dss_del": 439,
    "G2 Spring_theta_del": -0.271238179,
    "G2 Spring_pr_1_99_del": 8,
    "G2 Spring_ss_del": 79,
    "G2 Spring_dss_del": 473
  },
  {
    "ID": 451,
    "KG Fall_theta_del": -2.271884489,
    "KG Fall_pr_1_99_del": 8,
    "KG Fall_ss_del": 79,
    "KG Fall_dss_del": 273,
    "KG Winter_theta_del": -2.030236318,
    "KG Winter_pr_1_99_del": 8,
    "KG Winter_ss_del": 79,
    "KG Winter_dss_del": 297,
    "KG Spring_theta_del": -1.680805578,
    "KG Spring_pr_1_99_del": 8,
    "KG Spring_ss_del": 79,
    "KG Spring_dss_del": 332,
    "G1 Fall_theta_del": -1.438510289,
    "G1 Fall_pr_1_99_del": 8,
    "G1 Fall_ss_del": 79,
    "G1 Fall_dss_del": 356,
    "G1 Winter_theta_del": -1.169298693,
    "G1 Winter_pr_1_99_del": 8,
    "G1 Winter_ss_del": 79,
    "G1 Winter_dss_del": 383,
    "G1 Spring_theta_del": -0.952914572,
    "G1 Spring_pr_1_99_del": 8,
    "G1 Spring_ss_del": 79,
    "G1 Spring_dss_del": 405,
    "G2 Fall_theta_del": -0.782460433,
    "G2 Fall_pr_1_99_del": 8,
    "G2 Fall_ss_del": 78,
    "G2 Fall_dss_del": 422,
    "G2 Winter_theta_del": -0.604205272,
    "G2 Winter_pr_1_99_del": 8,
    "G2 Winter_ss_del": 78,
    "G2 Winter_dss_del": 440,
    "G2 Spring_theta_del": -0.262013389,
    "G2 Spring_pr_1_99_del": 8,
    "G2 Spring_ss_del": 79,
    "G2 Spring_dss_del": 474
  },
  {
    "ID": 57,
    "KG Fall_theta_del": -2.248399587,
    "KG Fall_pr_1_99_del": 9,
    "KG Fall_ss_del": 80,
    "KG Fall_dss_del": 275,
    "KG Winter_theta_del": -2.020339483,
    "KG Winter_pr_1_99_del": 9,
    "KG Winter_ss_del": 80,
    "KG Winter_dss_del": 298,
    "KG Spring_theta_del": -1.67611433,
    "KG Spring_pr_1_99_del": 9,
    "KG Spring_ss_del": 79,
    "KG Spring_dss_del": 332,
    "G1 Fall_theta_del": -1.414746008,
    "G1 Fall_pr_1_99_del": 9,
    "G1 Fall_ss_del": 80,
    "G1 Fall_dss_del": 359,
    "G1 Winter_theta_del": -1.16312822,
    "G1 Winter_pr_1_99_del": 9,
    "G1 Winter_ss_del": 79,
    "G1 Winter_dss_del": 384,
    "G1 Spring_theta_del": -0.938366037,
    "G1 Spring_pr_1_99_del": 9,
    "G1 Spring_ss_del": 79,
    "G1 Spring_dss_del": 406,
    "G2 Fall_theta_del": -0.769582509,
    "G2 Fall_pr_1_99_del": 9,
    "G2 Fall_ss_del": 79,
    "G2 Fall_dss_del": 423,
    "G2 Winter_theta_del": -0.596002731,
    "G2 Winter_pr_1_99_del": 9,
    "G2 Winter_ss_del": 79,
    "G2 Winter_dss_del": 440,
    "G2 Spring_theta_del": -0.24462369,
    "G2 Spring_pr_1_99_del": 9,
    "G2 Spring_ss_del": 80,
    "G2 Spring_dss_del": 476
  },
  {
    "ID": 288,
    "KG Fall_theta_del": -2.2012816,
    "KG Fall_pr_1_99_del": 9,
    "KG Fall_ss_del": 81,
    "KG Fall_dss_del": 280,
    "KG Winter_theta_del": -2.016533059,
    "KG Winter_pr_1_99_del": 9,
    "KG Winter_ss_del": 80,
    "KG Winter_dss_del": 298,
    "KG Spring_theta_del": -1.669934963,
    "KG Spring_pr_1_99_del": 9,
    "KG Spring_ss_del": 79,
    "KG Spring_dss_del": 333,
    "G1 Fall_theta_del": -1.394526354,
    "G1 Fall_pr_1_99_del": 9,
    "G1 Fall_ss_del": 80,
    "G1 Fall_dss_del": 361,
    "G1 Winter_theta_del": -1.147952329,
    "G1 Winter_pr_1_99_del": 9,
    "G1 Winter_ss_del": 80,
    "G1 Winter_dss_del": 385,
    "G1 Spring_theta_del": -0.9371741,
    "G1 Spring_pr_1_99_del": 9,
    "G1 Spring_ss_del": 79,
    "G1 Spring_dss_del": 406,
    "G2 Fall_theta_del": -0.769331377,
    "G2 Fall_pr_1_99_del": 9,
    "G2 Fall_ss_del": 79,
    "G2 Fall_dss_del": 423,
    "G2 Winter_theta_del": -0.594542345,
    "G2 Winter_pr_1_99_del": 9,
    "G2 Winter_ss_del": 79,
    "G2 Winter_dss_del": 441,
    "G2 Spring_theta_del": -0.23214965,
    "G2 Spring_pr_1_99_del": 9,
    "G2 Spring_ss_del": 80,
    "G2 Spring_dss_del": 477
  },
  {
    "ID": 335,
    "KG Fall_theta_del": -2.180918435,
    "KG Fall_pr_1_99_del": 9,
    "KG Fall_ss_del": 81,
    "KG Fall_dss_del": 282,
    "KG Winter_theta_del": -2.012236768,
    "KG Winter_pr_1_99_del": 9,
    "KG Winter_ss_del": 80,
    "KG Winter_dss_del": 299,
    "KG Spring_theta_del": -1.660790438,
    "KG Spring_pr_1_99_del": 9,
    "KG Spring_ss_del": 79,
    "KG Spring_dss_del": 334,
    "G1 Fall_theta_del": -1.375456537,
    "G1 Fall_pr_1_99_del": 9,
    "G1 Fall_ss_del": 80,
    "G1 Fall_dss_del": 362,
    "G1 Winter_theta_del": -1.146173352,
    "G1 Winter_pr_1_99_del": 9,
    "G1 Winter_ss_del": 80,
    "G1 Winter_dss_del": 385,
    "G1 Spring_theta_del": -0.936040975,
    "G1 Spring_pr_1_99_del": 9,
    "G1 Spring_ss_del": 79,
    "G1 Spring_dss_del": 406,
    "G2 Fall_theta_del": -0.741994217,
    "G2 Fall_pr_1_99_del": 9,
    "G2 Fall_ss_del": 79,
    "G2 Fall_dss_del": 426,
    "G2 Winter_theta_del": -0.585175295,
    "G2 Winter_pr_1_99_del": 9,
    "G2 Winter_ss_del": 79,
    "G2 Winter_dss_del": 441,
    "G2 Spring_theta_del": -0.191489458,
    "G2 Spring_pr_1_99_del": 9,
    "G2 Spring_ss_del": 81,
    "G2 Spring_dss_del": 481
  },
  {
    "ID": 402,
    "KG Fall_theta_del": -2.174760633,
    "KG Fall_pr_1_99_del": 9,
    "KG Fall_ss_del": 81,
    "KG Fall_dss_del": 283,
    "KG Winter_theta_del": -2.007728726,
    "KG Winter_pr_1_99_del": 9,
    "KG Winter_ss_del": 80,
    "KG Winter_dss_del": 299,
    "KG Spring_theta_del": -1.645792024,
    "KG Spring_pr_1_99_del": 9,
    "KG Spring_ss_del": 80,
    "KG Spring_dss_del": 335,
    "G1 Fall_theta_del": -1.373897235,
    "G1 Fall_pr_1_99_del": 9,
    "G1 Fall_ss_del": 81,
    "G1 Fall_dss_del": 363,
    "G1 Winter_theta_del": -1.142769809,
    "G1 Winter_pr_1_99_del": 9,
    "G1 Winter_ss_del": 80,
    "G1 Winter_dss_del": 386,
    "G1 Spring_theta_del": -0.92334861,
    "G1 Spring_pr_1_99_del": 9,
    "G1 Spring_ss_del": 80,
    "G1 Spring_dss_del": 408,
    "G2 Fall_theta_del": -0.733043601,
    "G2 Fall_pr_1_99_del": 9,
    "G2 Fall_ss_del": 79,
    "G2 Fall_dss_del": 427,
    "G2 Winter_theta_del": -0.581646353,
    "G2 Winter_pr_1_99_del": 9,
    "G2 Winter_ss_del": 79,
    "G2 Winter_dss_del": 442,
    "G2 Spring_theta_del": -0.178512651,
    "G2 Spring_pr_1_99_del": 9,
    "G2 Spring_ss_del": 81,
    "G2 Spring_dss_del": 482
  },
  {
    "ID": 454,
    "KG Fall_theta_del": -2.15983875,
    "KG Fall_pr_1_99_del": 9,
    "KG Fall_ss_del": 81,
    "KG Fall_dss_del": 284,
    "KG Winter_theta_del": -1.998756633,
    "KG Winter_pr_1_99_del": 9,
    "KG Winter_ss_del": 80,
    "KG Winter_dss_del": 300,
    "KG Spring_theta_del": -1.63062699,
    "KG Spring_pr_1_99_del": 9,
    "KG Spring_ss_del": 80,
    "KG Spring_dss_del": 337,
    "G1 Fall_theta_del": -1.367799733,
    "G1 Fall_pr_1_99_del": 9,
    "G1 Fall_ss_del": 81,
    "G1 Fall_dss_del": 363,
    "G1 Winter_theta_del": -1.127676067,
    "G1 Winter_pr_1_99_del": 9,
    "G1 Winter_ss_del": 80,
    "G1 Winter_dss_del": 387,
    "G1 Spring_theta_del": -0.906525477,
    "G1 Spring_pr_1_99_del": 9,
    "G1 Spring_ss_del": 80,
    "G1 Spring_dss_del": 409,
    "G2 Fall_theta_del": -0.730847423,
    "G2 Fall_pr_1_99_del": 9,
    "G2 Fall_ss_del": 79,
    "G2 Fall_dss_del": 427,
    "G2 Winter_theta_del": -0.568300887,
    "G2 Winter_pr_1_99_del": 9,
    "G2 Winter_ss_del": 79,
    "G2 Winter_dss_del": 443,
    "G2 Spring_theta_del": -0.174061452,
    "G2 Spring_pr_1_99_del": 9,
    "G2 Spring_ss_del": 81,
    "G2 Spring_dss_del": 483
  },
  {
    "ID": 86,
    "KG Fall_theta_del": -2.155641129,
    "KG Fall_pr_1_99_del": 10,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 284,
    "KG Winter_theta_del": -1.997906916,
    "KG Winter_pr_1_99_del": 10,
    "KG Winter_ss_del": 80,
    "KG Winter_dss_del": 300,
    "KG Spring_theta_del": -1.609337774,
    "KG Spring_pr_1_99_del": 10,
    "KG Spring_ss_del": 80,
    "KG Spring_dss_del": 339,
    "G1 Fall_theta_del": -1.354000621,
    "G1 Fall_pr_1_99_del": 10,
    "G1 Fall_ss_del": 81,
    "G1 Fall_dss_del": 365,
    "G1 Winter_theta_del": -1.125660982,
    "G1 Winter_pr_1_99_del": 10,
    "G1 Winter_ss_del": 80,
    "G1 Winter_dss_del": 387,
    "G1 Spring_theta_del": -0.895230767,
    "G1 Spring_pr_1_99_del": 10,
    "G1 Spring_ss_del": 80,
    "G1 Spring_dss_del": 410,
    "G2 Fall_theta_del": -0.695726294,
    "G2 Fall_pr_1_99_del": 10,
    "G2 Fall_ss_del": 80,
    "G2 Fall_dss_del": 430,
    "G2 Winter_theta_del": -0.557317038,
    "G2 Winter_pr_1_99_del": 10,
    "G2 Winter_ss_del": 79,
    "G2 Winter_dss_del": 444,
    "G2 Spring_theta_del": -0.165337424,
    "G2 Spring_pr_1_99_del": 10,
    "G2 Spring_ss_del": 81,
    "G2 Spring_dss_del": 483
  },
  {
    "ID": 231,
    "KG Fall_theta_del": -2.15163513,
    "KG Fall_pr_1_99_del": 10,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 285,
    "KG Winter_theta_del": -1.992616338,
    "KG Winter_pr_1_99_del": 10,
    "KG Winter_ss_del": 80,
    "KG Winter_dss_del": 301,
    "KG Spring_theta_del": -1.60162601,
    "KG Spring_pr_1_99_del": 10,
    "KG Spring_ss_del": 81,
    "KG Spring_dss_del": 340,
    "G1 Fall_theta_del": -1.351852942,
    "G1 Fall_pr_1_99_del": 10,
    "G1 Fall_ss_del": 81,
    "G1 Fall_dss_del": 365,
    "G1 Winter_theta_del": -1.124658068,
    "G1 Winter_pr_1_99_del": 10,
    "G1 Winter_ss_del": 80,
    "G1 Winter_dss_del": 388,
    "G1 Spring_theta_del": -0.893214612,
    "G1 Spring_pr_1_99_del": 10,
    "G1 Spring_ss_del": 80,
    "G1 Spring_dss_del": 411,
    "G2 Fall_theta_del": -0.688443427,
    "G2 Fall_pr_1_99_del": 10,
    "G2 Fall_ss_del": 80,
    "G2 Fall_dss_del": 431,
    "G2 Winter_theta_del": -0.555908712,
    "G2 Winter_pr_1_99_del": 10,
    "G2 Winter_ss_del": 79,
    "G2 Winter_dss_del": 444,
    "G2 Spring_theta_del": -0.16345219,
    "G2 Spring_pr_1_99_del": 10,
    "G2 Spring_ss_del": 81,
    "G2 Spring_dss_del": 484
  },
  {
    "ID": 292,
    "KG Fall_theta_del": -2.149774009,
    "KG Fall_pr_1_99_del": 10,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 285,
    "KG Winter_theta_del": -1.990558753,
    "KG Winter_pr_1_99_del": 10,
    "KG Winter_ss_del": 80,
    "KG Winter_dss_del": 301,
    "KG Spring_theta_del": -1.601047,
    "KG Spring_pr_1_99_del": 10,
    "KG Spring_ss_del": 81,
    "KG Spring_dss_del": 340,
    "G1 Fall_theta_del": -1.323502338,
    "G1 Fall_pr_1_99_del": 10,
    "G1 Fall_ss_del": 81,
    "G1 Fall_dss_del": 368,
    "G1 Winter_theta_del": -1.088513627,
    "G1 Winter_pr_1_99_del": 10,
    "G1 Winter_ss_del": 81,
    "G1 Winter_dss_del": 391,
    "G1 Spring_theta_del": -0.890546983,
    "G1 Spring_pr_1_99_del": 10,
    "G1 Spring_ss_del": 80,
    "G1 Spring_dss_del": 411,
    "G2 Fall_theta_del": -0.687377041,
    "G2 Fall_pr_1_99_del": 10,
    "G2 Fall_ss_del": 80,
    "G2 Fall_dss_del": 431,
    "G2 Winter_theta_del": -0.554880664,
    "G2 Winter_pr_1_99_del": 10,
    "G2 Winter_ss_del": 79,
    "G2 Winter_dss_del": 445,
    "G2 Spring_theta_del": -0.148121652,
    "G2 Spring_pr_1_99_del": 10,
    "G2 Spring_ss_del": 81,
    "G2 Spring_dss_del": 485
  },
  {
    "ID": 437,
    "KG Fall_theta_del": -2.144708802,
    "KG Fall_pr_1_99_del": 10,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 286,
    "KG Winter_theta_del": -1.963498825,
    "KG Winter_pr_1_99_del": 10,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 304,
    "KG Spring_theta_del": -1.588192384,
    "KG Spring_pr_1_99_del": 10,
    "KG Spring_ss_del": 81,
    "KG Spring_dss_del": 341,
    "G1 Fall_theta_del": -1.321538405,
    "G1 Fall_pr_1_99_del": 10,
    "G1 Fall_ss_del": 82,
    "G1 Fall_dss_del": 368,
    "G1 Winter_theta_del": -1.085721366,
    "G1 Winter_pr_1_99_del": 10,
    "G1 Winter_ss_del": 81,
    "G1 Winter_dss_del": 391,
    "G1 Spring_theta_del": -0.886166329,
    "G1 Spring_pr_1_99_del": 10,
    "G1 Spring_ss_del": 80,
    "G1 Spring_dss_del": 411,
    "G2 Fall_theta_del": -0.686989972,
    "G2 Fall_pr_1_99_del": 10,
    "G2 Fall_ss_del": 80,
    "G2 Fall_dss_del": 431,
    "G2 Winter_theta_del": -0.553106663,
    "G2 Winter_pr_1_99_del": 10,
    "G2 Winter_ss_del": 79,
    "G2 Winter_dss_del": 445,
    "G2 Spring_theta_del": -0.145255555,
    "G2 Spring_pr_1_99_del": 10,
    "G2 Spring_ss_del": 81,
    "G2 Spring_dss_del": 485
  },
  {
    "ID": 461,
    "KG Fall_theta_del": -2.137200418,
    "KG Fall_pr_1_99_del": 10,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 286,
    "KG Winter_theta_del": -1.957775736,
    "KG Winter_pr_1_99_del": 10,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 304,
    "KG Spring_theta_del": -1.560731185,
    "KG Spring_pr_1_99_del": 10,
    "KG Spring_ss_del": 81,
    "KG Spring_dss_del": 344,
    "G1 Fall_theta_del": -1.297175606,
    "G1 Fall_pr_1_99_del": 10,
    "G1 Fall_ss_del": 82,
    "G1 Fall_dss_del": 370,
    "G1 Winter_theta_del": -1.044225574,
    "G1 Winter_pr_1_99_del": 10,
    "G1 Winter_ss_del": 82,
    "G1 Winter_dss_del": 396,
    "G1 Spring_theta_del": -0.873517942,
    "G1 Spring_pr_1_99_del": 10,
    "G1 Spring_ss_del": 81,
    "G1 Spring_dss_del": 413,
    "G2 Fall_theta_del": -0.680473828,
    "G2 Fall_pr_1_99_del": 10,
    "G2 Fall_ss_del": 80,
    "G2 Fall_dss_del": 432,
    "G2 Winter_theta_del": -0.518187699,
    "G2 Winter_pr_1_99_del": 10,
    "G2 Winter_ss_del": 80,
    "G2 Winter_dss_del": 448,
    "G2 Spring_theta_del": -0.138151032,
    "G2 Spring_pr_1_99_del": 10,
    "G2 Spring_ss_del": 82,
    "G2 Spring_dss_del": 486
  },
  {
    "ID": 26,
    "KG Fall_theta_del": -2.134507795,
    "KG Fall_pr_1_99_del": 11,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 287,
    "KG Winter_theta_del": -1.956022618,
    "KG Winter_pr_1_99_del": 11,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 304,
    "KG Spring_theta_del": -1.558035299,
    "KG Spring_pr_1_99_del": 11,
    "KG Spring_ss_del": 81,
    "KG Spring_dss_del": 344,
    "G1 Fall_theta_del": -1.29603241,
    "G1 Fall_pr_1_99_del": 11,
    "G1 Fall_ss_del": 82,
    "G1 Fall_dss_del": 370,
    "G1 Winter_theta_del": -1.039508783,
    "G1 Winter_pr_1_99_del": 11,
    "G1 Winter_ss_del": 82,
    "G1 Winter_dss_del": 396,
    "G1 Spring_theta_del": -0.871730358,
    "G1 Spring_pr_1_99_del": 11,
    "G1 Spring_ss_del": 81,
    "G1 Spring_dss_del": 413,
    "G2 Fall_theta_del": -0.658627946,
    "G2 Fall_pr_1_99_del": 11,
    "G2 Fall_ss_del": 81,
    "G2 Fall_dss_del": 434,
    "G2 Winter_theta_del": -0.502946888,
    "G2 Winter_pr_1_99_del": 11,
    "G2 Winter_ss_del": 80,
    "G2 Winter_dss_del": 450,
    "G2 Spring_theta_del": -0.134195744,
    "G2 Spring_pr_1_99_del": 11,
    "G2 Spring_ss_del": 82,
    "G2 Spring_dss_del": 487
  },
  {
    "ID": 80,
    "KG Fall_theta_del": -2.122324838,
    "KG Fall_pr_1_99_del": 11,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 288,
    "KG Winter_theta_del": -1.951485315,
    "KG Winter_pr_1_99_del": 11,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 305,
    "KG Spring_theta_del": -1.547353528,
    "KG Spring_pr_1_99_del": 11,
    "KG Spring_ss_del": 82,
    "KG Spring_dss_del": 345,
    "G1 Fall_theta_del": -1.2909766,
    "G1 Fall_pr_1_99_del": 11,
    "G1 Fall_ss_del": 82,
    "G1 Fall_dss_del": 371,
    "G1 Winter_theta_del": -1.027905359,
    "G1 Winter_pr_1_99_del": 11,
    "G1 Winter_ss_del": 82,
    "G1 Winter_dss_del": 397,
    "G1 Spring_theta_del": -0.863554103,
    "G1 Spring_pr_1_99_del": 11,
    "G1 Spring_ss_del": 81,
    "G1 Spring_dss_del": 414,
    "G2 Fall_theta_del": -0.650624295,
    "G2 Fall_pr_1_99_del": 11,
    "G2 Fall_ss_del": 81,
    "G2 Fall_dss_del": 435,
    "G2 Winter_theta_del": -0.49138624,
    "G2 Winter_pr_1_99_del": 11,
    "G2 Winter_ss_del": 80,
    "G2 Winter_dss_del": 451,
    "G2 Spring_theta_del": -0.129326119,
    "G2 Spring_pr_1_99_del": 11,
    "G2 Spring_ss_del": 82,
    "G2 Spring_dss_del": 487
  },
  {
    "ID": 98,
    "KG Fall_theta_del": -2.116355471,
    "KG Fall_pr_1_99_del": 11,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 288,
    "KG Winter_theta_del": -1.950145589,
    "KG Winter_pr_1_99_del": 11,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 305,
    "KG Spring_theta_del": -1.539177597,
    "KG Spring_pr_1_99_del": 11,
    "KG Spring_ss_del": 82,
    "KG Spring_dss_del": 346,
    "G1 Fall_theta_del": -1.265819985,
    "G1 Fall_pr_1_99_del": 11,
    "G1 Fall_ss_del": 83,
    "G1 Fall_dss_del": 373,
    "G1 Winter_theta_del": -1.025804682,
    "G1 Winter_pr_1_99_del": 11,
    "G1 Winter_ss_del": 82,
    "G1 Winter_dss_del": 397,
    "G1 Spring_theta_del": -0.861068687,
    "G1 Spring_pr_1_99_del": 11,
    "G1 Spring_ss_del": 81,
    "G1 Spring_dss_del": 414,
    "G2 Fall_theta_del": -0.642232471,
    "G2 Fall_pr_1_99_del": 11,
    "G2 Fall_ss_del": 81,
    "G2 Fall_dss_del": 436,
    "G2 Winter_theta_del": -0.482760215,
    "G2 Winter_pr_1_99_del": 11,
    "G2 Winter_ss_del": 81,
    "G2 Winter_dss_del": 452,
    "G2 Spring_theta_del": -0.127096563,
    "G2 Spring_pr_1_99_del": 11,
    "G2 Spring_ss_del": 82,
    "G2 Spring_dss_del": 487
  },
  {
    "ID": 139,
    "KG Fall_theta_del": -2.115840108,
    "KG Fall_pr_1_99_del": 11,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 288,
    "KG Winter_theta_del": -1.938137743,
    "KG Winter_pr_1_99_del": 11,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 306,
    "KG Spring_theta_del": -1.527062283,
    "KG Spring_pr_1_99_del": 11,
    "KG Spring_ss_del": 82,
    "KG Spring_dss_del": 347,
    "G1 Fall_theta_del": -1.264106009,
    "G1 Fall_pr_1_99_del": 11,
    "G1 Fall_ss_del": 83,
    "G1 Fall_dss_del": 374,
    "G1 Winter_theta_del": -1.02243323,
    "G1 Winter_pr_1_99_del": 11,
    "G1 Winter_ss_del": 82,
    "G1 Winter_dss_del": 398,
    "G1 Spring_theta_del": -0.859550932,
    "G1 Spring_pr_1_99_del": 11,
    "G1 Spring_ss_del": 81,
    "G1 Spring_dss_del": 414,
    "G2 Fall_theta_del": -0.641516921,
    "G2 Fall_pr_1_99_del": 11,
    "G2 Fall_ss_del": 81,
    "G2 Fall_dss_del": 436,
    "G2 Winter_theta_del": -0.476614112,
    "G2 Winter_pr_1_99_del": 11,
    "G2 Winter_ss_del": 81,
    "G2 Winter_dss_del": 452,
    "G2 Spring_theta_del": -0.122791034,
    "G2 Spring_pr_1_99_del": 11,
    "G2 Spring_ss_del": 82,
    "G2 Spring_dss_del": 488
  },
  {
    "ID": 393,
    "KG Fall_theta_del": -2.115468753,
    "KG Fall_pr_1_99_del": 11,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 288,
    "KG Winter_theta_del": -1.937115799,
    "KG Winter_pr_1_99_del": 11,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 306,
    "KG Spring_theta_del": -1.524872749,
    "KG Spring_pr_1_99_del": 11,
    "KG Spring_ss_del": 82,
    "KG Spring_dss_del": 348,
    "G1 Fall_theta_del": -1.261631341,
    "G1 Fall_pr_1_99_del": 11,
    "G1 Fall_ss_del": 83,
    "G1 Fall_dss_del": 374,
    "G1 Winter_theta_del": -1.018976608,
    "G1 Winter_pr_1_99_del": 11,
    "G1 Winter_ss_del": 82,
    "G1 Winter_dss_del": 398,
    "G1 Spring_theta_del": -0.849995599,
    "G1 Spring_pr_1_99_del": 11,
    "G1 Spring_ss_del": 81,
    "G1 Spring_dss_del": 415,
    "G2 Fall_theta_del": -0.639239057,
    "G2 Fall_pr_1_99_del": 11,
    "G2 Fall_ss_del": 81,
    "G2 Fall_dss_del": 436,
    "G2 Winter_theta_del": -0.442195586,
    "G2 Winter_pr_1_99_del": 11,
    "G2 Winter_ss_del": 81,
    "G2 Winter_dss_del": 456,
    "G2 Spring_theta_del": -0.119140641,
    "G2 Spring_pr_1_99_del": 11,
    "G2 Spring_ss_del": 82,
    "G2 Spring_dss_del": 488
  },
  {
    "ID": 148,
    "KG Fall_theta_del": -2.111826919,
    "KG Fall_pr_1_99_del": 12,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 289,
    "KG Winter_theta_del": -1.930259561,
    "KG Winter_pr_1_99_del": 12,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 307,
    "KG Spring_theta_del": -1.51687497,
    "KG Spring_pr_1_99_del": 12,
    "KG Spring_ss_del": 82,
    "KG Spring_dss_del": 348,
    "G1 Fall_theta_del": -1.260179845,
    "G1 Fall_pr_1_99_del": 12,
    "G1 Fall_ss_del": 83,
    "G1 Fall_dss_del": 374,
    "G1 Winter_theta_del": -1.014174165,
    "G1 Winter_pr_1_99_del": 12,
    "G1 Winter_ss_del": 82,
    "G1 Winter_dss_del": 399,
    "G1 Spring_theta_del": -0.847566427,
    "G1 Spring_pr_1_99_del": 12,
    "G1 Spring_ss_del": 81,
    "G1 Spring_dss_del": 415,
    "G2 Fall_theta_del": -0.636276137,
    "G2 Fall_pr_1_99_del": 12,
    "G2 Fall_ss_del": 81,
    "G2 Fall_dss_del": 436,
    "G2 Winter_theta_del": -0.406110683,
    "G2 Winter_pr_1_99_del": 12,
    "G2 Winter_ss_del": 82,
    "G2 Winter_dss_del": 459,
    "G2 Spring_theta_del": -0.107342018,
    "G2 Spring_pr_1_99_del": 12,
    "G2 Spring_ss_del": 82,
    "G2 Spring_dss_del": 489
  },
  {
    "ID": 186,
    "KG Fall_theta_del": -2.111075041,
    "KG Fall_pr_1_99_del": 12,
    "KG Fall_ss_del": 82,
    "KG Fall_dss_del": 289,
    "KG Winter_theta_del": -1.92754041,
    "KG Winter_pr_1_99_del": 12,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 307,
    "KG Spring_theta_del": -1.507908228,
    "KG Spring_pr_1_99_del": 12,
    "KG Spring_ss_del": 82,
    "KG Spring_dss_del": 349,
    "G1 Fall_theta_del": -1.256420069,
    "G1 Fall_pr_1_99_del": 12,
    "G1 Fall_ss_del": 83,
    "G1 Fall_dss_del": 374,
    "G1 Winter_theta_del": -1.011371663,
    "G1 Winter_pr_1_99_del": 12,
    "G1 Winter_ss_del": 82,
    "G1 Winter_dss_del": 399,
    "G1 Spring_theta_del": -0.837709927,
    "G1 Spring_pr_1_99_del": 12,
    "G1 Spring_ss_del": 81,
    "G1 Spring_dss_del": 416,
    "G2 Fall_theta_del": -0.633517381,
    "G2 Fall_pr_1_99_del": 12,
    "G2 Fall_ss_del": 81,
    "G2 Fall_dss_del": 437,
    "G2 Winter_theta_del": -0.402189326,
    "G2 Winter_pr_1_99_del": 12,
    "G2 Winter_ss_del": 82,
    "G2 Winter_dss_del": 460,
    "G2 Spring_theta_del": -0.10239861,
    "G2 Spring_pr_1_99_del": 12,
    "G2 Spring_ss_del": 82,
    "G2 Spring_dss_del": 490
  },
  {
    "ID": 256,
    "KG Fall_theta_del": -2.100865182,
    "KG Fall_pr_1_99_del": 12,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 290,
    "KG Winter_theta_del": -1.926508843,
    "KG Winter_pr_1_99_del": 12,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 307,
    "KG Spring_theta_del": -1.502049789,
    "KG Spring_pr_1_99_del": 12,
    "KG Spring_ss_del": 82,
    "KG Spring_dss_del": 350,
    "G1 Fall_theta_del": -1.247278566,
    "G1 Fall_pr_1_99_del": 12,
    "G1 Fall_ss_del": 83,
    "G1 Fall_dss_del": 375,
    "G1 Winter_theta_del": -1.000304412,
    "G1 Winter_pr_1_99_del": 12,
    "G1 Winter_ss_del": 82,
    "G1 Winter_dss_del": 400,
    "G1 Spring_theta_del": -0.832106422,
    "G1 Spring_pr_1_99_del": 12,
    "G1 Spring_ss_del": 81,
    "G1 Spring_dss_del": 417,
    "G2 Fall_theta_del": -0.63308065,
    "G2 Fall_pr_1_99_del": 12,
    "G2 Fall_ss_del": 81,
    "G2 Fall_dss_del": 437,
    "G2 Winter_theta_del": -0.40101302,
    "G2 Winter_pr_1_99_del": 12,
    "G2 Winter_ss_del": 82,
    "G2 Winter_dss_del": 460,
    "G2 Spring_theta_del": -0.101235785,
    "G2 Spring_pr_1_99_del": 12,
    "G2 Spring_ss_del": 82,
    "G2 Spring_dss_del": 490
  },
  {
    "ID": 416,
    "KG Fall_theta_del": -2.081914946,
    "KG Fall_pr_1_99_del": 12,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 292,
    "KG Winter_theta_del": -1.925413708,
    "KG Winter_pr_1_99_del": 12,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 307,
    "KG Spring_theta_del": -1.502010492,
    "KG Spring_pr_1_99_del": 12,
    "KG Spring_ss_del": 82,
    "KG Spring_dss_del": 350,
    "G1 Fall_theta_del": -1.231796074,
    "G1 Fall_pr_1_99_del": 12,
    "G1 Fall_ss_del": 83,
    "G1 Fall_dss_del": 377,
    "G1 Winter_theta_del": -0.994966173,
    "G1 Winter_pr_1_99_del": 12,
    "G1 Winter_ss_del": 83,
    "G1 Winter_dss_del": 401,
    "G1 Spring_theta_del": -0.825691077,
    "G1 Spring_pr_1_99_del": 12,
    "G1 Spring_ss_del": 82,
    "G1 Spring_dss_del": 417,
    "G2 Fall_theta_del": -0.631858623,
    "G2 Fall_pr_1_99_del": 12,
    "G2 Fall_ss_del": 81,
    "G2 Fall_dss_del": 437,
    "G2 Winter_theta_del": -0.391495046,
    "G2 Winter_pr_1_99_del": 12,
    "G2 Winter_ss_del": 82,
    "G2 Winter_dss_del": 461,
    "G2 Spring_theta_del": -0.092597048,
    "G2 Spring_pr_1_99_del": 12,
    "G2 Spring_ss_del": 82,
    "G2 Spring_dss_del": 491
  },
  {
    "ID": 483,
    "KG Fall_theta_del": -2.080870449,
    "KG Fall_pr_1_99_del": 12,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 292,
    "KG Winter_theta_del": -1.91971993,
    "KG Winter_pr_1_99_del": 12,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 308,
    "KG Spring_theta_del": -1.50110674,
    "KG Spring_pr_1_99_del": 12,
    "KG Spring_ss_del": 82,
    "KG Spring_dss_del": 350,
    "G1 Fall_theta_del": -1.228802964,
    "G1 Fall_pr_1_99_del": 12,
    "G1 Fall_ss_del": 83,
    "G1 Fall_dss_del": 377,
    "G1 Winter_theta_del": -0.986557603,
    "G1 Winter_pr_1_99_del": 12,
    "G1 Winter_ss_del": 83,
    "G1 Winter_dss_del": 401,
    "G1 Spring_theta_del": -0.807306428,
    "G1 Spring_pr_1_99_del": 12,
    "G1 Spring_ss_del": 82,
    "G1 Spring_dss_del": 419,
    "G2 Fall_theta_del": -0.627796818,
    "G2 Fall_pr_1_99_del": 12,
    "G2 Fall_ss_del": 81,
    "G2 Fall_dss_del": 437,
    "G2 Winter_theta_del": -0.361697651,
    "G2 Winter_pr_1_99_del": 12,
    "G2 Winter_ss_del": 83,
    "G2 Winter_dss_del": 464,
    "G2 Spring_theta_del": -0.047331281,
    "G2 Spring_pr_1_99_del": 12,
    "G2 Spring_ss_del": 83,
    "G2 Spring_dss_del": 495
  },
  {
    "ID": 311,
    "KG Fall_theta_del": -2.080308361,
    "KG Fall_pr_1_99_del": 13,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 292,
    "KG Winter_theta_del": -1.919333933,
    "KG Winter_pr_1_99_del": 13,
    "KG Winter_ss_del": 81,
    "KG Winter_dss_del": 308,
    "KG Spring_theta_del": -1.48994471,
    "KG Spring_pr_1_99_del": 13,
    "KG Spring_ss_del": 83,
    "KG Spring_dss_del": 351,
    "G1 Fall_theta_del": -1.218581447,
    "G1 Fall_pr_1_99_del": 13,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 378,
    "G1 Winter_theta_del": -0.979240385,
    "G1 Winter_pr_1_99_del": 13,
    "G1 Winter_ss_del": 83,
    "G1 Winter_dss_del": 402,
    "G1 Spring_theta_del": -0.795810915,
    "G1 Spring_pr_1_99_del": 13,
    "G1 Spring_ss_del": 82,
    "G1 Spring_dss_del": 420,
    "G2 Fall_theta_del": -0.597149356,
    "G2 Fall_pr_1_99_del": 13,
    "G2 Fall_ss_del": 82,
    "G2 Fall_dss_del": 440,
    "G2 Winter_theta_del": -0.344909047,
    "G2 Winter_pr_1_99_del": 13,
    "G2 Winter_ss_del": 83,
    "G2 Winter_dss_del": 466,
    "G2 Spring_theta_del": -0.020716508,
    "G2 Spring_pr_1_99_del": 13,
    "G2 Spring_ss_del": 84,
    "G2 Spring_dss_del": 498
  },
  {
    "ID": 366,
    "KG Fall_theta_del": -2.07736157,
    "KG Fall_pr_1_99_del": 13,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 292,
    "KG Winter_theta_del": -1.909936449,
    "KG Winter_pr_1_99_del": 13,
    "KG Winter_ss_del": 82,
    "KG Winter_dss_del": 309,
    "KG Spring_theta_del": -1.489567396,
    "KG Spring_pr_1_99_del": 13,
    "KG Spring_ss_del": 83,
    "KG Spring_dss_del": 351,
    "G1 Fall_theta_del": -1.21617641,
    "G1 Fall_pr_1_99_del": 13,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 378,
    "G1 Winter_theta_del": -0.959304024,
    "G1 Winter_pr_1_99_del": 13,
    "G1 Winter_ss_del": 83,
    "G1 Winter_dss_del": 404,
    "G1 Spring_theta_del": -0.788771159,
    "G1 Spring_pr_1_99_del": 13,
    "G1 Spring_ss_del": 82,
    "G1 Spring_dss_del": 421,
    "G2 Fall_theta_del": -0.587299563,
    "G2 Fall_pr_1_99_del": 13,
    "G2 Fall_ss_del": 82,
    "G2 Fall_dss_del": 441,
    "G2 Winter_theta_del": -0.343738885,
    "G2 Winter_pr_1_99_del": 13,
    "G2 Winter_ss_del": 83,
    "G2 Winter_dss_del": 466,
    "G2 Spring_theta_del": -0.007579238,
    "G2 Spring_pr_1_99_del": 13,
    "G2 Spring_ss_del": 84,
    "G2 Spring_dss_del": 499
  },
  {
    "ID": 367,
    "KG Fall_theta_del": -2.075535343,
    "KG Fall_pr_1_99_del": 13,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 292,
    "KG Winter_theta_del": -1.904801286,
    "KG Winter_pr_1_99_del": 13,
    "KG Winter_ss_del": 82,
    "KG Winter_dss_del": 310,
    "KG Spring_theta_del": -1.481357761,
    "KG Spring_pr_1_99_del": 13,
    "KG Spring_ss_del": 83,
    "KG Spring_dss_del": 352,
    "G1 Fall_theta_del": -1.212428664,
    "G1 Fall_pr_1_99_del": 13,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 379,
    "G1 Winter_theta_del": -0.943618116,
    "G1 Winter_pr_1_99_del": 13,
    "G1 Winter_ss_del": 84,
    "G1 Winter_dss_del": 406,
    "G1 Spring_theta_del": -0.779700869,
    "G1 Spring_pr_1_99_del": 13,
    "G1 Spring_ss_del": 82,
    "G1 Spring_dss_del": 422,
    "G2 Fall_theta_del": -0.582924556,
    "G2 Fall_pr_1_99_del": 13,
    "G2 Fall_ss_del": 82,
    "G2 Fall_dss_del": 442,
    "G2 Winter_theta_del": -0.338173784,
    "G2 Winter_pr_1_99_del": 13,
    "G2 Winter_ss_del": 83,
    "G2 Winter_dss_del": 466,
    "G2 Spring_theta_del": -0.005468265,
    "G2 Spring_pr_1_99_del": 13,
    "G2 Spring_ss_del": 84,
    "G2 Spring_dss_del": 499
  },
  {
    "ID": 471,
    "KG Fall_theta_del": -2.064969214,
    "KG Fall_pr_1_99_del": 13,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 294,
    "KG Winter_theta_del": -1.896243559,
    "KG Winter_pr_1_99_del": 13,
    "KG Winter_ss_del": 82,
    "KG Winter_dss_del": 310,
    "KG Spring_theta_del": -1.477837175,
    "KG Spring_pr_1_99_del": 13,
    "KG Spring_ss_del": 83,
    "KG Spring_dss_del": 352,
    "G1 Fall_theta_del": -1.212088987,
    "G1 Fall_pr_1_99_del": 13,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 379,
    "G1 Winter_theta_del": -0.939843722,
    "G1 Winter_pr_1_99_del": 13,
    "G1 Winter_ss_del": 84,
    "G1 Winter_dss_del": 406,
    "G1 Spring_theta_del": -0.776400539,
    "G1 Spring_pr_1_99_del": 13,
    "G1 Spring_ss_del": 83,
    "G1 Spring_dss_del": 422,
    "G2 Fall_theta_del": -0.582862932,
    "G2 Fall_pr_1_99_del": 13,
    "G2 Fall_ss_del": 82,
    "G2 Fall_dss_del": 442,
    "G2 Winter_theta_del": -0.337399037,
    "G2 Winter_pr_1_99_del": 13,
    "G2 Winter_ss_del": 83,
    "G2 Winter_dss_del": 466,
    "G2 Spring_theta_del": -0.003512693,
    "G2 Spring_pr_1_99_del": 13,
    "G2 Spring_ss_del": 84,
    "G2 Spring_dss_del": 500
  },
  {
    "ID": 475,
    "KG Fall_theta_del": -2.064634658,
    "KG Fall_pr_1_99_del": 13,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 294,
    "KG Winter_theta_del": -1.884418934,
    "KG Winter_pr_1_99_del": 13,
    "KG Winter_ss_del": 82,
    "KG Winter_dss_del": 312,
    "KG Spring_theta_del": -1.464509253,
    "KG Spring_pr_1_99_del": 13,
    "KG Spring_ss_del": 83,
    "KG Spring_dss_del": 354,
    "G1 Fall_theta_del": -1.201318094,
    "G1 Fall_pr_1_99_del": 13,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 380,
    "G1 Winter_theta_del": -0.939342128,
    "G1 Winter_pr_1_99_del": 13,
    "G1 Winter_ss_del": 84,
    "G1 Winter_dss_del": 406,
    "G1 Spring_theta_del": -0.77016375,
    "G1 Spring_pr_1_99_del": 13,
    "G1 Spring_ss_del": 83,
    "G1 Spring_dss_del": 423,
    "G2 Fall_theta_del": -0.582188913,
    "G2 Fall_pr_1_99_del": 13,
    "G2 Fall_ss_del": 82,
    "G2 Fall_dss_del": 442,
    "G2 Winter_theta_del": -0.311234289,
    "G2 Winter_pr_1_99_del": 13,
    "G2 Winter_ss_del": 84,
    "G2 Winter_dss_del": 469,
    "G2 Spring_theta_del": 0.013150452,
    "G2 Spring_pr_1_99_del": 13,
    "G2 Spring_ss_del": 84,
    "G2 Spring_dss_del": 501
  },
  {
    "ID": 268,
    "KG Fall_theta_del": -2.063555212,
    "KG Fall_pr_1_99_del": 14,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 294,
    "KG Winter_theta_del": -1.872940675,
    "KG Winter_pr_1_99_del": 14,
    "KG Winter_ss_del": 82,
    "KG Winter_dss_del": 313,
    "KG Spring_theta_del": -1.46132646,
    "KG Spring_pr_1_99_del": 14,
    "KG Spring_ss_del": 83,
    "KG Spring_dss_del": 354,
    "G1 Fall_theta_del": -1.19876279,
    "G1 Fall_pr_1_99_del": 14,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 380,
    "G1 Winter_theta_del": -0.937782111,
    "G1 Winter_pr_1_99_del": 14,
    "G1 Winter_ss_del": 84,
    "G1 Winter_dss_del": 406,
    "G1 Spring_theta_del": -0.762121515,
    "G1 Spring_pr_1_99_del": 14,
    "G1 Spring_ss_del": 83,
    "G1 Spring_dss_del": 424,
    "G2 Fall_theta_del": -0.554552138,
    "G2 Fall_pr_1_99_del": 14,
    "G2 Fall_ss_del": 83,
    "G2 Fall_dss_del": 445,
    "G2 Winter_theta_del": -0.310027129,
    "G2 Winter_pr_1_99_del": 14,
    "G2 Winter_ss_del": 84,
    "G2 Winter_dss_del": 469,
    "G2 Spring_theta_del": 0.015739747,
    "G2 Spring_pr_1_99_del": 14,
    "G2 Spring_ss_del": 84,
    "G2 Spring_dss_del": 502
  },
  {
    "ID": 308,
    "KG Fall_theta_del": -2.06168602,
    "KG Fall_pr_1_99_del": 14,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 294,
    "KG Winter_theta_del": -1.872065908,
    "KG Winter_pr_1_99_del": 14,
    "KG Winter_ss_del": 82,
    "KG Winter_dss_del": 313,
    "KG Spring_theta_del": -1.453688829,
    "KG Spring_pr_1_99_del": 14,
    "KG Spring_ss_del": 83,
    "KG Spring_dss_del": 355,
    "G1 Fall_theta_del": -1.194982625,
    "G1 Fall_pr_1_99_del": 14,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 381,
    "G1 Winter_theta_del": -0.936957958,
    "G1 Winter_pr_1_99_del": 14,
    "G1 Winter_ss_del": 84,
    "G1 Winter_dss_del": 406,
    "G1 Spring_theta_del": -0.749625627,
    "G1 Spring_pr_1_99_del": 14,
    "G1 Spring_ss_del": 83,
    "G1 Spring_dss_del": 425,
    "G2 Fall_theta_del": -0.54822165,
    "G2 Fall_pr_1_99_del": 14,
    "G2 Fall_ss_del": 83,
    "G2 Fall_dss_del": 445,
    "G2 Winter_theta_del": -0.305902325,
    "G2 Winter_pr_1_99_del": 14,
    "G2 Winter_ss_del": 84,
    "G2 Winter_dss_del": 469,
    "G2 Spring_theta_del": 0.033577283,
    "G2 Spring_pr_1_99_del": 14,
    "G2 Spring_ss_del": 85,
    "G2 Spring_dss_del": 503
  },
  {
    "ID": 396,
    "KG Fall_theta_del": -2.054358105,
    "KG Fall_pr_1_99_del": 14,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 295,
    "KG Winter_theta_del": -1.862905454,
    "KG Winter_pr_1_99_del": 14,
    "KG Winter_ss_del": 82,
    "KG Winter_dss_del": 314,
    "KG Spring_theta_del": -1.451040005,
    "KG Spring_pr_1_99_del": 14,
    "KG Spring_ss_del": 83,
    "KG Spring_dss_del": 355,
    "G1 Fall_theta_del": -1.193966749,
    "G1 Fall_pr_1_99_del": 14,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 381,
    "G1 Winter_theta_del": -0.933615201,
    "G1 Winter_pr_1_99_del": 14,
    "G1 Winter_ss_del": 84,
    "G1 Winter_dss_del": 407,
    "G1 Spring_theta_del": -0.734198348,
    "G1 Spring_pr_1_99_del": 14,
    "G1 Spring_ss_del": 83,
    "G1 Spring_dss_del": 427,
    "G2 Fall_theta_del": -0.543587378,
    "G2 Fall_pr_1_99_del": 14,
    "G2 Fall_ss_del": 83,
    "G2 Fall_dss_del": 446,
    "G2 Winter_theta_del": -0.294899055,
    "G2 Winter_pr_1_99_del": 14,
    "G2 Winter_ss_del": 84,
    "G2 Winter_dss_del": 471,
    "G2 Spring_theta_del": 0.035305885,
    "G2 Spring_pr_1_99_del": 14,
    "G2 Spring_ss_del": 85,
    "G2 Spring_dss_del": 504
  },
  {
    "ID": 405,
    "KG Fall_theta_del": -2.050285631,
    "KG Fall_pr_1_99_del": 14,
    "KG Fall_ss_del": 83,
    "KG Fall_dss_del": 295,
    "KG Winter_theta_del": -1.852711,
    "KG Winter_pr_1_99_del": 14,
    "KG Winter_ss_del": 83,
    "KG Winter_dss_del": 315,
    "KG Spring_theta_del": -1.415597704,
    "KG Spring_pr_1_99_del": 14,
    "KG Spring_ss_del": 84,
    "KG Spring_dss_del": 358,
    "G1 Fall_theta_del": -1.188097301,
    "G1 Fall_pr_1_99_del": 14,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 381,
    "G1 Winter_theta_del": -0.914129386,
    "G1 Winter_pr_1_99_del": 14,
    "G1 Winter_ss_del": 84,
    "G1 Winter_dss_del": 409,
    "G1 Spring_theta_del": -0.732319463,
    "G1 Spring_pr_1_99_del": 14,
    "G1 Spring_ss_del": 83,
    "G1 Spring_dss_del": 427,
    "G2 Fall_theta_del": -0.528944465,
    "G2 Fall_pr_1_99_del": 14,
    "G2 Fall_ss_del": 83,
    "G2 Fall_dss_del": 447,
    "G2 Winter_theta_del": -0.287127956,
    "G2 Winter_pr_1_99_del": 14,
    "G2 Winter_ss_del": 84,
    "G2 Winter_dss_del": 471,
    "G2 Spring_theta_del": 0.05139963,
    "G2 Spring_pr_1_99_del": 14,
    "G2 Spring_ss_del": 85,
    "G2 Spring_dss_del": 505
  },
  {
    "ID": 431,
    "KG Fall_theta_del": -2.048636991,
    "KG Fall_pr_1_99_del": 14,
    "KG Fall_ss_del": 84,
    "KG Fall_dss_del": 295,
    "KG Winter_theta_del": -1.838690504,
    "KG Winter_pr_1_99_del": 14,
    "KG Winter_ss_del": 83,
    "KG Winter_dss_del": 316,
    "KG Spring_theta_del": -1.40342271,
    "KG Spring_pr_1_99_del": 14,
    "KG Spring_ss_del": 84,
    "KG Spring_dss_del": 360,
    "G1 Fall_theta_del": -1.179713252,
    "G1 Fall_pr_1_99_del": 14,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 382,
    "G1 Winter_theta_del": -0.906248831,
    "G1 Winter_pr_1_99_del": 14,
    "G1 Winter_ss_del": 84,
    "G1 Winter_dss_del": 409,
    "G1 Spring_theta_del": -0.722622537,
    "G1 Spring_pr_1_99_del": 14,
    "G1 Spring_ss_del": 84,
    "G1 Spring_dss_del": 428,
    "G2 Fall_theta_del": -0.52678895,
    "G2 Fall_pr_1_99_del": 14,
    "G2 Fall_ss_del": 83,
    "G2 Fall_dss_del": 447,
    "G2 Winter_theta_del": -0.28486016,
    "G2 Winter_pr_1_99_del": 14,
    "G2 Winter_ss_del": 84,
    "G2 Winter_dss_del": 472,
    "G2 Spring_theta_del": 0.052292028,
    "G2 Spring_pr_1_99_del": 14,
    "G2 Spring_ss_del": 85,
    "G2 Spring_dss_del": 505
  },
  {
    "ID": 162,
    "KG Fall_theta_del": -2.039027366,
    "KG Fall_pr_1_99_del": 15,
    "KG Fall_ss_del": 84,
    "KG Fall_dss_del": 296,
    "KG Winter_theta_del": -1.837954411,
    "KG Winter_pr_1_99_del": 15,
    "KG Winter_ss_del": 83,
    "KG Winter_dss_del": 316,
    "KG Spring_theta_del": -1.401412225,
    "KG Spring_pr_1_99_del": 15,
    "KG Spring_ss_del": 84,
    "KG Spring_dss_del": 360,
    "G1 Fall_theta_del": -1.176592642,
    "G1 Fall_pr_1_99_del": 15,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 382,
    "G1 Winter_theta_del": -0.901280337,
    "G1 Winter_pr_1_99_del": 15,
    "G1 Winter_ss_del": 84,
    "G1 Winter_dss_del": 410,
    "G1 Spring_theta_del": -0.717631981,
    "G1 Spring_pr_1_99_del": 15,
    "G1 Spring_ss_del": 84,
    "G1 Spring_dss_del": 428,
    "G2 Fall_theta_del": -0.492606089,
    "G2 Fall_pr_1_99_del": 15,
    "G2 Fall_ss_del": 84,
    "G2 Fall_dss_del": 451,
    "G2 Winter_theta_del": -0.284114843,
    "G2 Winter_pr_1_99_del": 15,
    "G2 Winter_ss_del": 84,
    "G2 Winter_dss_del": 472,
    "G2 Spring_theta_del": 0.057243958,
    "G2 Spring_pr_1_99_del": 15,
    "G2 Spring_ss_del": 85,
    "G2 Spring_dss_del": 506
  },
  {
    "ID": 325,
    "KG Fall_theta_del": -2.034835708,
    "KG Fall_pr_1_99_del": 15,
    "KG Fall_ss_del": 84,
    "KG Fall_dss_del": 297,
    "KG Winter_theta_del": -1.823359778,
    "KG Winter_pr_1_99_del": 15,
    "KG Winter_ss_del": 83,
    "KG Winter_dss_del": 318,
    "KG Spring_theta_del": -1.396508101,
    "KG Spring_pr_1_99_del": 15,
    "KG Spring_ss_del": 84,
    "KG Spring_dss_del": 360,
    "G1 Fall_theta_del": -1.176337534,
    "G1 Fall_pr_1_99_del": 15,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 382,
    "G1 Winter_theta_del": -0.888487885,
    "G1 Winter_pr_1_99_del": 15,
    "G1 Winter_ss_del": 85,
    "G1 Winter_dss_del": 411,
    "G1 Spring_theta_del": -0.717193587,
    "G1 Spring_pr_1_99_del": 15,
    "G1 Spring_ss_del": 84,
    "G1 Spring_dss_del": 428,
    "G2 Fall_theta_del": -0.489635973,
    "G2 Fall_pr_1_99_del": 15,
    "G2 Fall_ss_del": 84,
    "G2 Fall_dss_del": 451,
    "G2 Winter_theta_del": -0.269394272,
    "G2 Winter_pr_1_99_del": 15,
    "G2 Winter_ss_del": 85,
    "G2 Winter_dss_del": 473,
    "G2 Spring_theta_del": 0.057315929,
    "G2 Spring_pr_1_99_del": 15,
    "G2 Spring_ss_del": 85,
    "G2 Spring_dss_del": 506
  },
  {
    "ID": 342,
    "KG Fall_theta_del": -2.034779776,
    "KG Fall_pr_1_99_del": 15,
    "KG Fall_ss_del": 84,
    "KG Fall_dss_del": 297,
    "KG Winter_theta_del": -1.820483325,
    "KG Winter_pr_1_99_del": 15,
    "KG Winter_ss_del": 83,
    "KG Winter_dss_del": 318,
    "KG Spring_theta_del": -1.391220619,
    "KG Spring_pr_1_99_del": 15,
    "KG Spring_ss_del": 84,
    "KG Spring_dss_del": 361,
    "G1 Fall_theta_del": -1.169644994,
    "G1 Fall_pr_1_99_del": 15,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 383,
    "G1 Winter_theta_del": -0.881461308,
    "G1 Winter_pr_1_99_del": 15,
    "G1 Winter_ss_del": 85,
    "G1 Winter_dss_del": 412,
    "G1 Spring_theta_del": -0.70849571,
    "G1 Spring_pr_1_99_del": 15,
    "G1 Spring_ss_del": 84,
    "G1 Spring_dss_del": 429,
    "G2 Fall_theta_del": -0.47635747,
    "G2 Fall_pr_1_99_del": 15,
    "G2 Fall_ss_del": 84,
    "G2 Fall_dss_del": 452,
    "G2 Winter_theta_del": -0.268669676,
    "G2 Winter_pr_1_99_del": 15,
    "G2 Winter_ss_del": 85,
    "G2 Winter_dss_del": 473,
    "G2 Spring_theta_del": 0.062856355,
    "G2 Spring_pr_1_99_del": 15,
    "G2 Spring_ss_del": 85,
    "G2 Spring_dss_del": 506
  },
  {
    "ID": 357,
    "KG Fall_theta_del": -2.033365786,
    "KG Fall_pr_1_99_del": 15,
    "KG Fall_ss_del": 84,
    "KG Fall_dss_del": 297,
    "KG Winter_theta_del": -1.803217304,
    "KG Winter_pr_1_99_del": 15,
    "KG Winter_ss_del": 83,
    "KG Winter_dss_del": 320,
    "KG Spring_theta_del": -1.383189354,
    "KG Spring_pr_1_99_del": 15,
    "KG Spring_ss_del": 85,
    "KG Spring_dss_del": 362,
    "G1 Fall_theta_del": -1.169246641,
    "G1 Fall_pr_1_99_del": 15,
    "G1 Fall_ss_del": 84,
    "G1 Fall_dss_del": 383,
    "G1 Winter_theta_del": -0.880432547,
    "G1 Winter_pr_1_99_del": 15,
    "G1 Winter_ss_del": 85,
    "G1 Winter_dss_del": 412,
    "G1 Spring_theta_del": -0.706821926,
    "G1 Spring_pr_1_99_del": 15,
    "G1 Spring_ss_del": 84,
    "G1 Spring_dss_del": 429,
    "G2 Fall_theta_del": -0.45959799,
    "G2 Fall_pr_1_99_del": 15,
    "G2 Fall_ss_del": 85,
    "G2 Fall_dss_del": 454,
    "G2 Winter_theta_del": -0.259220519,
    "G2 Winter_pr_1_99_del": 15,
    "G2 Winter_ss_del": 85,
    "G2 Winter_dss_del": 474,
    "G2 Spring_theta_del": 0.070556098,
    "G2 Spring_pr_1_99_del": 15,
    "G2 Spring_ss_del": 86,
    "G2 Spring_dss_del": 507
  },
  {
    "ID": 476,
    "KG Fall_theta_del": -2.027858124,
    "KG Fall_pr_1_99_del": 15,
    "KG Fall_ss_del": 84,
    "KG Fall_dss_del": 297,
    "KG Winter_theta_del": -1.800943633,
    "KG Winter_pr_1_99_del": 15,
    "KG Winter_ss_del": 84,
    "KG Winter_dss_del": 320,
    "KG Spring_theta_del": -1.380948027,
    "KG Spring_pr_1_99_del": 15,
    "KG Spring_ss_del": 85,
    "KG Spring_dss_del": 362,
    "G1 Fall_theta_del": -1.162850113,
    "G1 Fall_pr_1_99_del": 15,
    "G1 Fall_ss_del": 85,
    "G1 Fall_dss_del": 384,
    "G1 Winter_theta_del": -0.874495212,
    "G1 Winter_pr_1_99_del": 15,
    "G1 Winter_ss_del": 85,
    "G1 Winter_dss_del": 413,
    "G1 Spring_theta_del": -0.704689564,
    "G1 Spring_pr_1_99_del": 15,
    "G1 Spring_ss_del": 84,
    "G1 Spring_dss_del": 430,
    "G2 Fall_theta_del": -0.441976485,
    "G2 Fall_pr_1_99_del": 15,
    "G2 Fall_ss_del": 85,
    "G2 Fall_dss_del": 456,
    "G2 Winter_theta_del": -0.248175883,
    "G2 Winter_pr_1_99_del": 15,
    "G2 Winter_ss_del": 85,
    "G2 Winter_dss_del": 475,
    "G2 Spring_theta_del": 0.071280315,
    "G2 Spring_pr_1_99_del": 15,
    "G2 Spring_ss_del": 86,
    "G2 Spring_dss_del": 507
  },
  {
    "ID": 20,
    "KG Fall_theta_del": -2.026282915,
    "KG Fall_pr_1_99_del": 16,
    "KG Fall_ss_del": 84,
    "KG Fall_dss_del": 297,
    "KG Winter_theta_del": -1.791990053,
    "KG Winter_pr_1_99_del": 16,
    "KG Winter_ss_del": 84,
    "KG Winter_dss_del": 321,
    "KG Spring_theta_del": -1.377599423,
    "KG Spring_pr_1_99_del": 16,
    "KG Spring_ss_del": 85,
    "KG Spring_dss_del": 362,
    "G1 Fall_theta_del": -1.159567751,
    "G1 Fall_pr_1_99_del": 16,
    "G1 Fall_ss_del": 85,
    "G1 Fall_dss_del": 384,
    "G1 Winter_theta_del": -0.866457442,
    "G1 Winter_pr_1_99_del": 16,
    "G1 Winter_ss_del": 85,
    "G1 Winter_dss_del": 413,
    "G1 Spring_theta_del": -0.686650815,
    "G1 Spring_pr_1_99_del": 16,
    "G1 Spring_ss_del": 84,
    "G1 Spring_dss_del": 431,
    "G2 Fall_theta_del": -0.440120646,
    "G2 Fall_pr_1_99_del": 16,
    "G2 Fall_ss_del": 85,
    "G2 Fall_dss_del": 456,
    "G2 Winter_theta_del": -0.238359142,
    "G2 Winter_pr_1_99_del": 16,
    "G2 Winter_ss_del": 85,
    "G2 Winter_dss_del": 476,
    "G2 Spring_theta_del": 0.071335592,
    "G2 Spring_pr_1_99_del": 16,
    "G2 Spring_ss_del": 86,
    "G2 Spring_dss_del": 507
  },
  {
    "ID": 64,
    "KG Fall_theta_del": -2.010633598,
    "KG Fall_pr_1_99_del": 16,
    "KG Fall_ss_del": 84,
    "KG Fall_dss_del": 299,
    "KG Winter_theta_del": -1.777264313,
    "KG Winter_pr_1_99_del": 16,
    "KG Winter_ss_del": 84,
    "KG Winter_dss_del": 322,
    "KG Spring_theta_del": -1.356864801,
    "KG Spring_pr_1_99_del": 16,
    "KG Spring_ss_del": 85,
    "KG Spring_dss_del": 364,
    "G1 Fall_theta_del": -1.153457312,
    "G1 Fall_pr_1_99_del": 16,
    "G1 Fall_ss_del": 85,
    "G1 Fall_dss_del": 385,
    "G1 Winter_theta_del": -0.864980542,
    "G1 Winter_pr_1_99_del": 16,
    "G1 Winter_ss_del": 85,
    "G1 Winter_dss_del": 414,
    "G1 Spring_theta_del": -0.682365281,
    "G1 Spring_pr_1_99_del": 16,
    "G1 Spring_ss_del": 84,
    "G1 Spring_dss_del": 432,
    "G2 Fall_theta_del": -0.436898082,
    "G2 Fall_pr_1_99_del": 16,
    "G2 Fall_ss_del": 85,
    "G2 Fall_dss_del": 456,
    "G2 Winter_theta_del": -0.233142292,
    "G2 Winter_pr_1_99_del": 16,
    "G2 Winter_ss_del": 85,
    "G2 Winter_dss_del": 477,
    "G2 Spring_theta_del": 0.084249753,
    "G2 Spring_pr_1_99_del": 16,
    "G2 Spring_ss_del": 86,
    "G2 Spring_dss_del": 508
  },
  {
    "ID": 109,
    "KG Fall_theta_del": -1.997860774,
    "KG Fall_pr_1_99_del": 16,
    "KG Fall_ss_del": 84,
    "KG Fall_dss_del": 300,
    "KG Winter_theta_del": -1.771655431,
    "KG Winter_pr_1_99_del": 16,
    "KG Winter_ss_del": 84,
    "KG Winter_dss_del": 323,
    "KG Spring_theta_del": -1.355948893,
    "KG Spring_pr_1_99_del": 16,
    "KG Spring_ss_del": 85,
    "KG Spring_dss_del": 364,
    "G1 Fall_theta_del": -1.141428044,
    "G1 Fall_pr_1_99_del": 16,
    "G1 Fall_ss_del": 85,
    "G1 Fall_dss_del": 386,
    "G1 Winter_theta_del": -0.854471085,
    "G1 Winter_pr_1_99_del": 16,
    "G1 Winter_ss_del": 85,
    "G1 Winter_dss_del": 415,
    "G1 Spring_theta_del": -0.659049064,
    "G1 Spring_pr_1_99_del": 16,
    "G1 Spring_ss_del": 85,
    "G1 Spring_dss_del": 434,
    "G2 Fall_theta_del": -0.434168666,
    "G2 Fall_pr_1_99_del": 16,
    "G2 Fall_ss_del": 85,
    "G2 Fall_dss_del": 457,
    "G2 Winter_theta_del": -0.228943075,
    "G2 Winter_pr_1_99_del": 16,
    "G2 Winter_ss_del": 85,
    "G2 Winter_dss_del": 477,
    "G2 Spring_theta_del": 0.105099246,
    "G2 Spring_pr_1_99_del": 16,
    "G2 Spring_ss_del": 86,
    "G2 Spring_dss_del": 511
  },
  {
    "ID": 217,
    "KG Fall_theta_del": -1.994880934,
    "KG Fall_pr_1_99_del": 16,
    "KG Fall_ss_del": 85,
    "KG Fall_dss_del": 301,
    "KG Winter_theta_del": -1.759090116,
    "KG Winter_pr_1_99_del": 16,
    "KG Winter_ss_del": 84,
    "KG Winter_dss_del": 324,
    "KG Spring_theta_del": -1.34521117,
    "KG Spring_pr_1_99_del": 16,
    "KG Spring_ss_del": 85,
    "KG Spring_dss_del": 365,
    "G1 Fall_theta_del": -1.139327288,
    "G1 Fall_pr_1_99_del": 16,
    "G1 Fall_ss_del": 85,
    "G1 Fall_dss_del": 386,
    "G1 Winter_theta_del": -0.847985458,
    "G1 Winter_pr_1_99_del": 16,
    "G1 Winter_ss_del": 86,
    "G1 Winter_dss_del": 415,
    "G1 Spring_theta_del": -0.65186745,
    "G1 Spring_pr_1_99_del": 16,
    "G1 Spring_ss_del": 85,
    "G1 Spring_dss_del": 435,
    "G2 Fall_theta_del": -0.433719251,
    "G2 Fall_pr_1_99_del": 16,
    "G2 Fall_ss_del": 85,
    "G2 Fall_dss_del": 457,
    "G2 Winter_theta_del": -0.223897227,
    "G2 Winter_pr_1_99_del": 16,
    "G2 Winter_ss_del": 85,
    "G2 Winter_dss_del": 478,
    "G2 Spring_theta_del": 0.108706278,
    "G2 Spring_pr_1_99_del": 16,
    "G2 Spring_ss_del": 86,
    "G2 Spring_dss_del": 511
  },
  {
    "ID": 472,
    "KG Fall_theta_del": -1.994069988,
    "KG Fall_pr_1_99_del": 16,
    "KG Fall_ss_del": 85,
    "KG Fall_dss_del": 301,
    "KG Winter_theta_del": -1.753968296,
    "KG Winter_pr_1_99_del": 16,
    "KG Winter_ss_del": 84,
    "KG Winter_dss_del": 325,
    "KG Spring_theta_del": -1.343121979,
    "KG Spring_pr_1_99_del": 16,
    "KG Spring_ss_del": 85,
    "KG Spring_dss_del": 366,
    "G1 Fall_theta_del": -1.126081348,
    "G1 Fall_pr_1_99_del": 16,
    "G1 Fall_ss_del": 85,
    "G1 Fall_dss_del": 387,
    "G1 Winter_theta_del": -0.842016755,
    "G1 Winter_pr_1_99_del": 16,
    "G1 Winter_ss_del": 86,
    "G1 Winter_dss_del": 416,
    "G1 Spring_theta_del": -0.647071332,
    "G1 Spring_pr_1_99_del": 16,
    "G1 Spring_ss_del": 85,
    "G1 Spring_dss_del": 435,
    "G2 Fall_theta_del": -0.4244091,
    "G2 Fall_pr_1_99_del": 16,
    "G2 Fall_ss_del": 85,
    "G2 Fall_dss_del": 458,
    "G2 Winter_theta_del": -0.196782887,
    "G2 Winter_pr_1_99_del": 16,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 480,
    "G2 Spring_theta_del": 0.119807734,
    "G2 Spring_pr_1_99_del": 16,
    "G2 Spring_ss_del": 86,
    "G2 Spring_dss_del": 512
  },
  {
    "ID": 106,
    "KG Fall_theta_del": -1.978107452,
    "KG Fall_pr_1_99_del": 17,
    "KG Fall_ss_del": 85,
    "KG Fall_dss_del": 302,
    "KG Winter_theta_del": -1.743357688,
    "KG Winter_pr_1_99_del": 17,
    "KG Winter_ss_del": 85,
    "KG Winter_dss_del": 326,
    "KG Spring_theta_del": -1.343027926,
    "KG Spring_pr_1_99_del": 17,
    "KG Spring_ss_del": 85,
    "KG Spring_dss_del": 366,
    "G1 Fall_theta_del": -1.122156145,
    "G1 Fall_pr_1_99_del": 17,
    "G1 Fall_ss_del": 85,
    "G1 Fall_dss_del": 388,
    "G1 Winter_theta_del": -0.841819851,
    "G1 Winter_pr_1_99_del": 17,
    "G1 Winter_ss_del": 86,
    "G1 Winter_dss_del": 416,
    "G1 Spring_theta_del": -0.637981724,
    "G1 Spring_pr_1_99_del": 17,
    "G1 Spring_ss_del": 85,
    "G1 Spring_dss_del": 436,
    "G2 Fall_theta_del": -0.415813077,
    "G2 Fall_pr_1_99_del": 17,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 458,
    "G2 Winter_theta_del": -0.194423619,
    "G2 Winter_pr_1_99_del": 17,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 481,
    "G2 Spring_theta_del": 0.123886894,
    "G2 Spring_pr_1_99_del": 17,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 512
  },
  {
    "ID": 165,
    "KG Fall_theta_del": -1.975242818,
    "KG Fall_pr_1_99_del": 17,
    "KG Fall_ss_del": 85,
    "KG Fall_dss_del": 302,
    "KG Winter_theta_del": -1.732081758,
    "KG Winter_pr_1_99_del": 17,
    "KG Winter_ss_del": 85,
    "KG Winter_dss_del": 327,
    "KG Spring_theta_del": -1.342275062,
    "KG Spring_pr_1_99_del": 17,
    "KG Spring_ss_del": 85,
    "KG Spring_dss_del": 366,
    "G1 Fall_theta_del": -1.119662809,
    "G1 Fall_pr_1_99_del": 17,
    "G1 Fall_ss_del": 85,
    "G1 Fall_dss_del": 388,
    "G1 Winter_theta_del": -0.838926696,
    "G1 Winter_pr_1_99_del": 17,
    "G1 Winter_ss_del": 86,
    "G1 Winter_dss_del": 416,
    "G1 Spring_theta_del": -0.631112717,
    "G1 Spring_pr_1_99_del": 17,
    "G1 Spring_ss_del": 85,
    "G1 Spring_dss_del": 437,
    "G2 Fall_theta_del": -0.414013237,
    "G2 Fall_pr_1_99_del": 17,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 459,
    "G2 Winter_theta_del": -0.190230044,
    "G2 Winter_pr_1_99_del": 17,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 481,
    "G2 Spring_theta_del": 0.125075603,
    "G2 Spring_pr_1_99_del": 17,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 513
  },
  {
    "ID": 403,
    "KG Fall_theta_del": -1.966101134,
    "KG Fall_pr_1_99_del": 17,
    "KG Fall_ss_del": 85,
    "KG Fall_dss_del": 303,
    "KG Winter_theta_del": -1.715724756,
    "KG Winter_pr_1_99_del": 17,
    "KG Winter_ss_del": 85,
    "KG Winter_dss_del": 328,
    "KG Spring_theta_del": -1.32925505,
    "KG Spring_pr_1_99_del": 17,
    "KG Spring_ss_del": 86,
    "KG Spring_dss_del": 367,
    "G1 Fall_theta_del": -1.110753951,
    "G1 Fall_pr_1_99_del": 17,
    "G1 Fall_ss_del": 86,
    "G1 Fall_dss_del": 389,
    "G1 Winter_theta_del": -0.838816839,
    "G1 Winter_pr_1_99_del": 17,
    "G1 Winter_ss_del": 86,
    "G1 Winter_dss_del": 416,
    "G1 Spring_theta_del": -0.626570998,
    "G1 Spring_pr_1_99_del": 17,
    "G1 Spring_ss_del": 86,
    "G1 Spring_dss_del": 437,
    "G2 Fall_theta_del": -0.407373989,
    "G2 Fall_pr_1_99_del": 17,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 459,
    "G2 Winter_theta_del": -0.189994569,
    "G2 Winter_pr_1_99_del": 17,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 481,
    "G2 Spring_theta_del": 0.127551655,
    "G2 Spring_pr_1_99_del": 17,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 513
  },
  {
    "ID": 412,
    "KG Fall_theta_del": -1.963851239,
    "KG Fall_pr_1_99_del": 17,
    "KG Fall_ss_del": 85,
    "KG Fall_dss_del": 304,
    "KG Winter_theta_del": -1.715219296,
    "KG Winter_pr_1_99_del": 17,
    "KG Winter_ss_del": 85,
    "KG Winter_dss_del": 328,
    "KG Spring_theta_del": -1.326007932,
    "KG Spring_pr_1_99_del": 17,
    "KG Spring_ss_del": 86,
    "KG Spring_dss_del": 367,
    "G1 Fall_theta_del": -1.107219131,
    "G1 Fall_pr_1_99_del": 17,
    "G1 Fall_ss_del": 86,
    "G1 Fall_dss_del": 389,
    "G1 Winter_theta_del": -0.833877145,
    "G1 Winter_pr_1_99_del": 17,
    "G1 Winter_ss_del": 86,
    "G1 Winter_dss_del": 417,
    "G1 Spring_theta_del": -0.621495888,
    "G1 Spring_pr_1_99_del": 17,
    "G1 Spring_ss_del": 86,
    "G1 Spring_dss_del": 438,
    "G2 Fall_theta_del": -0.402323573,
    "G2 Fall_pr_1_99_del": 17,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 460,
    "G2 Winter_theta_del": -0.188290887,
    "G2 Winter_pr_1_99_del": 17,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 481,
    "G2 Spring_theta_del": 0.1276089,
    "G2 Spring_pr_1_99_del": 17,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 513
  },
  {
    "ID": 490,
    "KG Fall_theta_del": -1.958440963,
    "KG Fall_pr_1_99_del": 17,
    "KG Fall_ss_del": 85,
    "KG Fall_dss_del": 304,
    "KG Winter_theta_del": -1.710455307,
    "KG Winter_pr_1_99_del": 17,
    "KG Winter_ss_del": 85,
    "KG Winter_dss_del": 329,
    "KG Spring_theta_del": -1.313169697,
    "KG Spring_pr_1_99_del": 17,
    "KG Spring_ss_del": 86,
    "KG Spring_dss_del": 369,
    "G1 Fall_theta_del": -1.103133672,
    "G1 Fall_pr_1_99_del": 17,
    "G1 Fall_ss_del": 86,
    "G1 Fall_dss_del": 390,
    "G1 Winter_theta_del": -0.829528113,
    "G1 Winter_pr_1_99_del": 17,
    "G1 Winter_ss_del": 86,
    "G1 Winter_dss_del": 417,
    "G1 Spring_theta_del": -0.615633099,
    "G1 Spring_pr_1_99_del": 17,
    "G1 Spring_ss_del": 86,
    "G1 Spring_dss_del": 438,
    "G2 Fall_theta_del": -0.383834668,
    "G2 Fall_pr_1_99_del": 17,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 462,
    "G2 Winter_theta_del": -0.181822894,
    "G2 Winter_pr_1_99_del": 17,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 482,
    "G2 Spring_theta_del": 0.130028182,
    "G2 Spring_pr_1_99_del": 17,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 513
  },
  {
    "ID": 4,
    "KG Fall_theta_del": -1.958071475,
    "KG Fall_pr_1_99_del": 18,
    "KG Fall_ss_del": 85,
    "KG Fall_dss_del": 304,
    "KG Winter_theta_del": -1.710385708,
    "KG Winter_pr_1_99_del": 18,
    "KG Winter_ss_del": 85,
    "KG Winter_dss_del": 329,
    "KG Spring_theta_del": -1.307483968,
    "KG Spring_pr_1_99_del": 18,
    "KG Spring_ss_del": 86,
    "KG Spring_dss_del": 369,
    "G1 Fall_theta_del": -1.100854413,
    "G1 Fall_pr_1_99_del": 18,
    "G1 Fall_ss_del": 86,
    "G1 Fall_dss_del": 390,
    "G1 Winter_theta_del": -0.810948019,
    "G1 Winter_pr_1_99_del": 18,
    "G1 Winter_ss_del": 86,
    "G1 Winter_dss_del": 419,
    "G1 Spring_theta_del": -0.612535471,
    "G1 Spring_pr_1_99_del": 18,
    "G1 Spring_ss_del": 86,
    "G1 Spring_dss_del": 439,
    "G2 Fall_theta_del": -0.383245685,
    "G2 Fall_pr_1_99_del": 18,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 462,
    "G2 Winter_theta_del": -0.176499766,
    "G2 Winter_pr_1_99_del": 18,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 482,
    "G2 Spring_theta_del": 0.130826296,
    "G2 Spring_pr_1_99_del": 18,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 513
  },
  {
    "ID": 46,
    "KG Fall_theta_del": -1.956472328,
    "KG Fall_pr_1_99_del": 18,
    "KG Fall_ss_del": 85,
    "KG Fall_dss_del": 304,
    "KG Winter_theta_del": -1.704799725,
    "KG Winter_pr_1_99_del": 18,
    "KG Winter_ss_del": 85,
    "KG Winter_dss_del": 330,
    "KG Spring_theta_del": -1.292734839,
    "KG Spring_pr_1_99_del": 18,
    "KG Spring_ss_del": 86,
    "KG Spring_dss_del": 371,
    "G1 Fall_theta_del": -1.097251096,
    "G1 Fall_pr_1_99_del": 18,
    "G1 Fall_ss_del": 86,
    "G1 Fall_dss_del": 390,
    "G1 Winter_theta_del": -0.810931856,
    "G1 Winter_pr_1_99_del": 18,
    "G1 Winter_ss_del": 86,
    "G1 Winter_dss_del": 419,
    "G1 Spring_theta_del": -0.608645765,
    "G1 Spring_pr_1_99_del": 18,
    "G1 Spring_ss_del": 86,
    "G1 Spring_dss_del": 439,
    "G2 Fall_theta_del": -0.381692186,
    "G2 Fall_pr_1_99_del": 18,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 462,
    "G2 Winter_theta_del": -0.175898945,
    "G2 Winter_pr_1_99_del": 18,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 482,
    "G2 Spring_theta_del": 0.131973356,
    "G2 Spring_pr_1_99_del": 18,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 513
  },
  {
    "ID": 73,
    "KG Fall_theta_del": -1.932486212,
    "KG Fall_pr_1_99_del": 18,
    "KG Fall_ss_del": 86,
    "KG Fall_dss_del": 307,
    "KG Winter_theta_del": -1.703805282,
    "KG Winter_pr_1_99_del": 18,
    "KG Winter_ss_del": 85,
    "KG Winter_dss_del": 330,
    "KG Spring_theta_del": -1.281590244,
    "KG Spring_pr_1_99_del": 18,
    "KG Spring_ss_del": 87,
    "KG Spring_dss_del": 372,
    "G1 Fall_theta_del": -1.091217092,
    "G1 Fall_pr_1_99_del": 18,
    "G1 Fall_ss_del": 86,
    "G1 Fall_dss_del": 391,
    "G1 Winter_theta_del": -0.797840635,
    "G1 Winter_pr_1_99_del": 18,
    "G1 Winter_ss_del": 86,
    "G1 Winter_dss_del": 420,
    "G1 Spring_theta_del": -0.607164075,
    "G1 Spring_pr_1_99_del": 18,
    "G1 Spring_ss_del": 86,
    "G1 Spring_dss_del": 439,
    "G2 Fall_theta_del": -0.378091678,
    "G2 Fall_pr_1_99_del": 18,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 462,
    "G2 Winter_theta_del": -0.174344648,
    "G2 Winter_pr_1_99_del": 18,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 483,
    "G2 Spring_theta_del": 0.136831994,
    "G2 Spring_pr_1_99_del": 18,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 514
  },
  {
    "ID": 153,
    "KG Fall_theta_del": -1.927627775,
    "KG Fall_pr_1_99_del": 18,
    "KG Fall_ss_del": 86,
    "KG Fall_dss_del": 307,
    "KG Winter_theta_del": -1.701046693,
    "KG Winter_pr_1_99_del": 18,
    "KG Winter_ss_del": 85,
    "KG Winter_dss_del": 330,
    "KG Spring_theta_del": -1.281165577,
    "KG Spring_pr_1_99_del": 18,
    "KG Spring_ss_del": 87,
    "KG Spring_dss_del": 372,
    "G1 Fall_theta_del": -1.085782951,
    "G1 Fall_pr_1_99_del": 18,
    "G1 Fall_ss_del": 86,
    "G1 Fall_dss_del": 391,
    "G1 Winter_theta_del": -0.797707953,
    "G1 Winter_pr_1_99_del": 18,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 420,
    "G1 Spring_theta_del": -0.595401316,
    "G1 Spring_pr_1_99_del": 18,
    "G1 Spring_ss_del": 86,
    "G1 Spring_dss_del": 440,
    "G2 Fall_theta_del": -0.374809644,
    "G2 Fall_pr_1_99_del": 18,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 463,
    "G2 Winter_theta_del": -0.169299385,
    "G2 Winter_pr_1_99_del": 18,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 483,
    "G2 Spring_theta_del": 0.137574893,
    "G2 Spring_pr_1_99_del": 18,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 514
  },
  {
    "ID": 446,
    "KG Fall_theta_del": -1.915239722,
    "KG Fall_pr_1_99_del": 18,
    "KG Fall_ss_del": 86,
    "KG Fall_dss_del": 308,
    "KG Winter_theta_del": -1.686646187,
    "KG Winter_pr_1_99_del": 18,
    "KG Winter_ss_del": 86,
    "KG Winter_dss_del": 331,
    "KG Spring_theta_del": -1.28063641,
    "KG Spring_pr_1_99_del": 18,
    "KG Spring_ss_del": 87,
    "KG Spring_dss_del": 372,
    "G1 Fall_theta_del": -1.08016236,
    "G1 Fall_pr_1_99_del": 18,
    "G1 Fall_ss_del": 86,
    "G1 Fall_dss_del": 392,
    "G1 Winter_theta_del": -0.790109209,
    "G1 Winter_pr_1_99_del": 18,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 421,
    "G1 Spring_theta_del": -0.582898185,
    "G1 Spring_pr_1_99_del": 18,
    "G1 Spring_ss_del": 86,
    "G1 Spring_dss_del": 442,
    "G2 Fall_theta_del": -0.370374262,
    "G2 Fall_pr_1_99_del": 18,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 463,
    "G2 Winter_theta_del": -0.168891824,
    "G2 Winter_pr_1_99_del": 18,
    "G2 Winter_ss_del": 86,
    "G2 Winter_dss_del": 483,
    "G2 Spring_theta_del": 0.137580137,
    "G2 Spring_pr_1_99_del": 18,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 514
  },
  {
    "ID": 9,
    "KG Fall_theta_del": -1.910933478,
    "KG Fall_pr_1_99_del": 19,
    "KG Fall_ss_del": 86,
    "KG Fall_dss_del": 309,
    "KG Winter_theta_del": -1.686344511,
    "KG Winter_pr_1_99_del": 19,
    "KG Winter_ss_del": 86,
    "KG Winter_dss_del": 331,
    "KG Spring_theta_del": -1.280034427,
    "KG Spring_pr_1_99_del": 19,
    "KG Spring_ss_del": 87,
    "KG Spring_dss_del": 372,
    "G1 Fall_theta_del": -1.073184078,
    "G1 Fall_pr_1_99_del": 19,
    "G1 Fall_ss_del": 86,
    "G1 Fall_dss_del": 393,
    "G1 Winter_theta_del": -0.786307806,
    "G1 Winter_pr_1_99_del": 19,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 421,
    "G1 Spring_theta_del": -0.570984052,
    "G1 Spring_pr_1_99_del": 19,
    "G1 Spring_ss_del": 87,
    "G1 Spring_dss_del": 443,
    "G2 Fall_theta_del": -0.369240738,
    "G2 Fall_pr_1_99_del": 19,
    "G2 Fall_ss_del": 86,
    "G2 Fall_dss_del": 463,
    "G2 Winter_theta_del": -0.152035736,
    "G2 Winter_pr_1_99_del": 19,
    "G2 Winter_ss_del": 87,
    "G2 Winter_dss_del": 485,
    "G2 Spring_theta_del": 0.137671273,
    "G2 Spring_pr_1_99_del": 19,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 514
  },
  {
    "ID": 99,
    "KG Fall_theta_del": -1.90897174,
    "KG Fall_pr_1_99_del": 19,
    "KG Fall_ss_del": 86,
    "KG Fall_dss_del": 309,
    "KG Winter_theta_del": -1.685732624,
    "KG Winter_pr_1_99_del": 19,
    "KG Winter_ss_del": 86,
    "KG Winter_dss_del": 331,
    "KG Spring_theta_del": -1.279939156,
    "KG Spring_pr_1_99_del": 19,
    "KG Spring_ss_del": 87,
    "KG Spring_dss_del": 372,
    "G1 Fall_theta_del": -1.06723974,
    "G1 Fall_pr_1_99_del": 19,
    "G1 Fall_ss_del": 86,
    "G1 Fall_dss_del": 393,
    "G1 Winter_theta_del": -0.785398387,
    "G1 Winter_pr_1_99_del": 19,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 421,
    "G1 Spring_theta_del": -0.569691465,
    "G1 Spring_pr_1_99_del": 19,
    "G1 Spring_ss_del": 87,
    "G1 Spring_dss_del": 443,
    "G2 Fall_theta_del": -0.356133981,
    "G2 Fall_pr_1_99_del": 19,
    "G2 Fall_ss_del": 87,
    "G2 Fall_dss_del": 464,
    "G2 Winter_theta_del": -0.144818814,
    "G2 Winter_pr_1_99_del": 19,
    "G2 Winter_ss_del": 87,
    "G2 Winter_dss_del": 486,
    "G2 Spring_theta_del": 0.139595689,
    "G2 Spring_pr_1_99_del": 19,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 514
  },
  {
    "ID": 338,
    "KG Fall_theta_del": -1.907631764,
    "KG Fall_pr_1_99_del": 19,
    "KG Fall_ss_del": 86,
    "KG Fall_dss_del": 309,
    "KG Winter_theta_del": -1.674456449,
    "KG Winter_pr_1_99_del": 19,
    "KG Winter_ss_del": 86,
    "KG Winter_dss_del": 333,
    "KG Spring_theta_del": -1.248043987,
    "KG Spring_pr_1_99_del": 19,
    "KG Spring_ss_del": 87,
    "KG Spring_dss_del": 375,
    "G1 Fall_theta_del": -1.061159885,
    "G1 Fall_pr_1_99_del": 19,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 394,
    "G1 Winter_theta_del": -0.780328474,
    "G1 Winter_pr_1_99_del": 19,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 422,
    "G1 Spring_theta_del": -0.562099362,
    "G1 Spring_pr_1_99_del": 19,
    "G1 Spring_ss_del": 87,
    "G1 Spring_dss_del": 444,
    "G2 Fall_theta_del": -0.344146321,
    "G2 Fall_pr_1_99_del": 19,
    "G2 Fall_ss_del": 87,
    "G2 Fall_dss_del": 466,
    "G2 Winter_theta_del": -0.143821717,
    "G2 Winter_pr_1_99_del": 19,
    "G2 Winter_ss_del": 87,
    "G2 Winter_dss_del": 486,
    "G2 Spring_theta_del": 0.173808275,
    "G2 Spring_pr_1_99_del": 19,
    "G2 Spring_ss_del": 87,
    "G2 Spring_dss_del": 517
  },
  {
    "ID": 369,
    "KG Fall_theta_del": -1.900106316,
    "KG Fall_pr_1_99_del": 19,
    "KG Fall_ss_del": 86,
    "KG Fall_dss_del": 310,
    "KG Winter_theta_del": -1.671623146,
    "KG Winter_pr_1_99_del": 19,
    "KG Winter_ss_del": 86,
    "KG Winter_dss_del": 333,
    "KG Spring_theta_del": -1.239838506,
    "KG Spring_pr_1_99_del": 19,
    "KG Spring_ss_del": 87,
    "KG Spring_dss_del": 376,
    "G1 Fall_theta_del": -1.061090067,
    "G1 Fall_pr_1_99_del": 19,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 394,
    "G1 Winter_theta_del": -0.776620672,
    "G1 Winter_pr_1_99_del": 19,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 422,
    "G1 Spring_theta_del": -0.555936158,
    "G1 Spring_pr_1_99_del": 19,
    "G1 Spring_ss_del": 87,
    "G1 Spring_dss_del": 444,
    "G2 Fall_theta_del": -0.343297461,
    "G2 Fall_pr_1_99_del": 19,
    "G2 Fall_ss_del": 87,
    "G2 Fall_dss_del": 466,
    "G2 Winter_theta_del": -0.13461977,
    "G2 Winter_pr_1_99_del": 19,
    "G2 Winter_ss_del": 87,
    "G2 Winter_dss_del": 487,
    "G2 Spring_theta_del": 0.184746432,
    "G2 Spring_pr_1_99_del": 19,
    "G2 Spring_ss_del": 88,
    "G2 Spring_dss_del": 518
  },
  {
    "ID": 391,
    "KG Fall_theta_del": -1.895922168,
    "KG Fall_pr_1_99_del": 19,
    "KG Fall_ss_del": 86,
    "KG Fall_dss_del": 310,
    "KG Winter_theta_del": -1.658895671,
    "KG Winter_pr_1_99_del": 19,
    "KG Winter_ss_del": 86,
    "KG Winter_dss_del": 334,
    "KG Spring_theta_del": -1.237625056,
    "KG Spring_pr_1_99_del": 19,
    "KG Spring_ss_del": 87,
    "KG Spring_dss_del": 376,
    "G1 Fall_theta_del": -1.05665655,
    "G1 Fall_pr_1_99_del": 19,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 394,
    "G1 Winter_theta_del": -0.768017319,
    "G1 Winter_pr_1_99_del": 19,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 423,
    "G1 Spring_theta_del": -0.544538324,
    "G1 Spring_pr_1_99_del": 19,
    "G1 Spring_ss_del": 87,
    "G1 Spring_dss_del": 446,
    "G2 Fall_theta_del": -0.331500225,
    "G2 Fall_pr_1_99_del": 19,
    "G2 Fall_ss_del": 87,
    "G2 Fall_dss_del": 467,
    "G2 Winter_theta_del": -0.133944191,
    "G2 Winter_pr_1_99_del": 19,
    "G2 Winter_ss_del": 87,
    "G2 Winter_dss_del": 487,
    "G2 Spring_theta_del": 0.185450592,
    "G2 Spring_pr_1_99_del": 19,
    "G2 Spring_ss_del": 88,
    "G2 Spring_dss_del": 519
  },
  {
    "ID": 68,
    "KG Fall_theta_del": -1.89435101,
    "KG Fall_pr_1_99_del": 20,
    "KG Fall_ss_del": 86,
    "KG Fall_dss_del": 311,
    "KG Winter_theta_del": -1.656654574,
    "KG Winter_pr_1_99_del": 20,
    "KG Winter_ss_del": 86,
    "KG Winter_dss_del": 334,
    "KG Spring_theta_del": -1.229279012,
    "KG Spring_pr_1_99_del": 20,
    "KG Spring_ss_del": 87,
    "KG Spring_dss_del": 377,
    "G1 Fall_theta_del": -1.053387415,
    "G1 Fall_pr_1_99_del": 20,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 395,
    "G1 Winter_theta_del": -0.762021445,
    "G1 Winter_pr_1_99_del": 20,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 424,
    "G1 Spring_theta_del": -0.539698053,
    "G1 Spring_pr_1_99_del": 20,
    "G1 Spring_ss_del": 87,
    "G1 Spring_dss_del": 446,
    "G2 Fall_theta_del": -0.329053677,
    "G2 Fall_pr_1_99_del": 20,
    "G2 Fall_ss_del": 87,
    "G2 Fall_dss_del": 467,
    "G2 Winter_theta_del": -0.100741353,
    "G2 Winter_pr_1_99_del": 20,
    "G2 Winter_ss_del": 88,
    "G2 Winter_dss_del": 490,
    "G2 Spring_theta_del": 0.200534284,
    "G2 Spring_pr_1_99_del": 20,
    "G2 Spring_ss_del": 88,
    "G2 Spring_dss_del": 520
  },
  {
    "ID": 84,
    "KG Fall_theta_del": -1.890833924,
    "KG Fall_pr_1_99_del": 20,
    "KG Fall_ss_del": 86,
    "KG Fall_dss_del": 311,
    "KG Winter_theta_del": -1.650018692,
    "KG Winter_pr_1_99_del": 20,
    "KG Winter_ss_del": 86,
    "KG Winter_dss_del": 335,
    "KG Spring_theta_del": -1.229210118,
    "KG Spring_pr_1_99_del": 20,
    "KG Spring_ss_del": 87,
    "KG Spring_dss_del": 377,
    "G1 Fall_theta_del": -1.047718637,
    "G1 Fall_pr_1_99_del": 20,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 395,
    "G1 Winter_theta_del": -0.759940894,
    "G1 Winter_pr_1_99_del": 20,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 424,
    "G1 Spring_theta_del": -0.534888858,
    "G1 Spring_pr_1_99_del": 20,
    "G1 Spring_ss_del": 87,
    "G1 Spring_dss_del": 447,
    "G2 Fall_theta_del": -0.327537381,
    "G2 Fall_pr_1_99_del": 20,
    "G2 Fall_ss_del": 87,
    "G2 Fall_dss_del": 467,
    "G2 Winter_theta_del": -0.092461132,
    "G2 Winter_pr_1_99_del": 20,
    "G2 Winter_ss_del": 88,
    "G2 Winter_dss_del": 491,
    "G2 Spring_theta_del": 0.203541994,
    "G2 Spring_pr_1_99_del": 20,
    "G2 Spring_ss_del": 88,
    "G2 Spring_dss_del": 520
  },
  {
    "ID": 156,
    "KG Fall_theta_del": -1.887254623,
    "KG Fall_pr_1_99_del": 20,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 311,
    "KG Winter_theta_del": -1.64001937,
    "KG Winter_pr_1_99_del": 20,
    "KG Winter_ss_del": 86,
    "KG Winter_dss_del": 336,
    "KG Spring_theta_del": -1.220814601,
    "KG Spring_pr_1_99_del": 20,
    "KG Spring_ss_del": 88,
    "KG Spring_dss_del": 378,
    "G1 Fall_theta_del": -1.047252559,
    "G1 Fall_pr_1_99_del": 20,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 395,
    "G1 Winter_theta_del": -0.75663896,
    "G1 Winter_pr_1_99_del": 20,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 424,
    "G1 Spring_theta_del": -0.515618865,
    "G1 Spring_pr_1_99_del": 20,
    "G1 Spring_ss_del": 88,
    "G1 Spring_dss_del": 448,
    "G2 Fall_theta_del": -0.324737119,
    "G2 Fall_pr_1_99_del": 20,
    "G2 Fall_ss_del": 87,
    "G2 Fall_dss_del": 468,
    "G2 Winter_theta_del": -0.090330728,
    "G2 Winter_pr_1_99_del": 20,
    "G2 Winter_ss_del": 88,
    "G2 Winter_dss_del": 491,
    "G2 Spring_theta_del": 0.208616934,
    "G2 Spring_pr_1_99_del": 20,
    "G2 Spring_ss_del": 88,
    "G2 Spring_dss_del": 521
  },
  {
    "ID": 199,
    "KG Fall_theta_del": -1.88276576,
    "KG Fall_pr_1_99_del": 20,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 312,
    "KG Winter_theta_del": -1.635706956,
    "KG Winter_pr_1_99_del": 20,
    "KG Winter_ss_del": 86,
    "KG Winter_dss_del": 336,
    "KG Spring_theta_del": -1.211457713,
    "KG Spring_pr_1_99_del": 20,
    "KG Spring_ss_del": 88,
    "KG Spring_dss_del": 379,
    "G1 Fall_theta_del": -1.045333576,
    "G1 Fall_pr_1_99_del": 20,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 395,
    "G1 Winter_theta_del": -0.748408244,
    "G1 Winter_pr_1_99_del": 20,
    "G1 Winter_ss_del": 87,
    "G1 Winter_dss_del": 425,
    "G1 Spring_theta_del": -0.511439163,
    "G1 Spring_pr_1_99_del": 20,
    "G1 Spring_ss_del": 88,
    "G1 Spring_dss_del": 449,
    "G2 Fall_theta_del": -0.309743074,
    "G2 Fall_pr_1_99_del": 20,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 469,
    "G2 Winter_theta_del": -0.090304004,
    "G2 Winter_pr_1_99_del": 20,
    "G2 Winter_ss_del": 88,
    "G2 Winter_dss_del": 491,
    "G2 Spring_theta_del": 0.215856668,
    "G2 Spring_pr_1_99_del": 20,
    "G2 Spring_ss_del": 88,
    "G2 Spring_dss_del": 522
  },
  {
    "ID": 449,
    "KG Fall_theta_del": -1.881785839,
    "KG Fall_pr_1_99_del": 20,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 312,
    "KG Winter_theta_del": -1.632326029,
    "KG Winter_pr_1_99_del": 20,
    "KG Winter_ss_del": 87,
    "KG Winter_dss_del": 337,
    "KG Spring_theta_del": -1.205495022,
    "KG Spring_pr_1_99_del": 20,
    "KG Spring_ss_del": 88,
    "KG Spring_dss_del": 379,
    "G1 Fall_theta_del": -1.04476089,
    "G1 Fall_pr_1_99_del": 20,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 396,
    "G1 Winter_theta_del": -0.744227425,
    "G1 Winter_pr_1_99_del": 20,
    "G1 Winter_ss_del": 88,
    "G1 Winter_dss_del": 426,
    "G1 Spring_theta_del": -0.508973386,
    "G1 Spring_pr_1_99_del": 20,
    "G1 Spring_ss_del": 88,
    "G1 Spring_dss_del": 449,
    "G2 Fall_theta_del": -0.304141596,
    "G2 Fall_pr_1_99_del": 20,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 470,
    "G2 Winter_theta_del": -0.079735167,
    "G2 Winter_pr_1_99_del": 20,
    "G2 Winter_ss_del": 88,
    "G2 Winter_dss_del": 492,
    "G2 Spring_theta_del": 0.219870751,
    "G2 Spring_pr_1_99_del": 20,
    "G2 Spring_ss_del": 88,
    "G2 Spring_dss_del": 522
  },
  {
    "ID": 37,
    "KG Fall_theta_del": -1.878781471,
    "KG Fall_pr_1_99_del": 21,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 312,
    "KG Winter_theta_del": -1.630635007,
    "KG Winter_pr_1_99_del": 21,
    "KG Winter_ss_del": 87,
    "KG Winter_dss_del": 337,
    "KG Spring_theta_del": -1.199315538,
    "KG Spring_pr_1_99_del": 21,
    "KG Spring_ss_del": 88,
    "KG Spring_dss_del": 380,
    "G1 Fall_theta_del": -1.034771796,
    "G1 Fall_pr_1_99_del": 21,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 397,
    "G1 Winter_theta_del": -0.740679083,
    "G1 Winter_pr_1_99_del": 21,
    "G1 Winter_ss_del": 88,
    "G1 Winter_dss_del": 426,
    "G1 Spring_theta_del": -0.492847733,
    "G1 Spring_pr_1_99_del": 21,
    "G1 Spring_ss_del": 88,
    "G1 Spring_dss_del": 451,
    "G2 Fall_theta_del": -0.30052736,
    "G2 Fall_pr_1_99_del": 21,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 470,
    "G2 Winter_theta_del": -0.067818326,
    "G2 Winter_pr_1_99_del": 21,
    "G2 Winter_ss_del": 88,
    "G2 Winter_dss_del": 493,
    "G2 Spring_theta_del": 0.221176566,
    "G2 Spring_pr_1_99_del": 21,
    "G2 Spring_ss_del": 88,
    "G2 Spring_dss_del": 522
  },
  {
    "ID": 132,
    "KG Fall_theta_del": -1.877193989,
    "KG Fall_pr_1_99_del": 21,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 312,
    "KG Winter_theta_del": -1.624940127,
    "KG Winter_pr_1_99_del": 21,
    "KG Winter_ss_del": 87,
    "KG Winter_dss_del": 338,
    "KG Spring_theta_del": -1.198223499,
    "KG Spring_pr_1_99_del": 21,
    "KG Spring_ss_del": 88,
    "KG Spring_dss_del": 380,
    "G1 Fall_theta_del": -1.034722996,
    "G1 Fall_pr_1_99_del": 21,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 397,
    "G1 Winter_theta_del": -0.739987103,
    "G1 Winter_pr_1_99_del": 21,
    "G1 Winter_ss_del": 88,
    "G1 Winter_dss_del": 426,
    "G1 Spring_theta_del": -0.489102983,
    "G1 Spring_pr_1_99_del": 21,
    "G1 Spring_ss_del": 88,
    "G1 Spring_dss_del": 451,
    "G2 Fall_theta_del": -0.293600276,
    "G2 Fall_pr_1_99_del": 21,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 471,
    "G2 Winter_theta_del": -0.062682178,
    "G2 Winter_pr_1_99_del": 21,
    "G2 Winter_ss_del": 88,
    "G2 Winter_dss_del": 494,
    "G2 Spring_theta_del": 0.229016574,
    "G2 Spring_pr_1_99_del": 21,
    "G2 Spring_ss_del": 88,
    "G2 Spring_dss_del": 523
  },
  {
    "ID": 179,
    "KG Fall_theta_del": -1.870570121,
    "KG Fall_pr_1_99_del": 21,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 313,
    "KG Winter_theta_del": -1.620957114,
    "KG Winter_pr_1_99_del": 21,
    "KG Winter_ss_del": 87,
    "KG Winter_dss_del": 338,
    "KG Spring_theta_del": -1.196095862,
    "KG Spring_pr_1_99_del": 21,
    "KG Spring_ss_del": 88,
    "KG Spring_dss_del": 380,
    "G1 Fall_theta_del": -1.018142587,
    "G1 Fall_pr_1_99_del": 21,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 398,
    "G1 Winter_theta_del": -0.734202958,
    "G1 Winter_pr_1_99_del": 21,
    "G1 Winter_ss_del": 88,
    "G1 Winter_dss_del": 427,
    "G1 Spring_theta_del": -0.488034762,
    "G1 Spring_pr_1_99_del": 21,
    "G1 Spring_ss_del": 88,
    "G1 Spring_dss_del": 451,
    "G2 Fall_theta_del": -0.285267399,
    "G2 Fall_pr_1_99_del": 21,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 471,
    "G2 Winter_theta_del": -0.06026541,
    "G2 Winter_pr_1_99_del": 21,
    "G2 Winter_ss_del": 89,
    "G2 Winter_dss_del": 494,
    "G2 Spring_theta_del": 0.233973992,
    "G2 Spring_pr_1_99_del": 21,
    "G2 Spring_ss_del": 89,
    "G2 Spring_dss_del": 523
  },
  {
    "ID": 191,
    "KG Fall_theta_del": -1.869581825,
    "KG Fall_pr_1_99_del": 21,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 313,
    "KG Winter_theta_del": -1.594640467,
    "KG Winter_pr_1_99_del": 21,
    "KG Winter_ss_del": 87,
    "KG Winter_dss_del": 341,
    "KG Spring_theta_del": -1.19327872,
    "KG Spring_pr_1_99_del": 21,
    "KG Spring_ss_del": 88,
    "KG Spring_dss_del": 381,
    "G1 Fall_theta_del": -1.017309841,
    "G1 Fall_pr_1_99_del": 21,
    "G1 Fall_ss_del": 87,
    "G1 Fall_dss_del": 398,
    "G1 Winter_theta_del": -0.732906118,
    "G1 Winter_pr_1_99_del": 21,
    "G1 Winter_ss_del": 88,
    "G1 Winter_dss_del": 427,
    "G1 Spring_theta_del": -0.478948045,
    "G1 Spring_pr_1_99_del": 21,
    "G1 Spring_ss_del": 88,
    "G1 Spring_dss_del": 452,
    "G2 Fall_theta_del": -0.283698074,
    "G2 Fall_pr_1_99_del": 21,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 472,
    "G2 Winter_theta_del": -0.051936527,
    "G2 Winter_pr_1_99_del": 21,
    "G2 Winter_ss_del": 89,
    "G2 Winter_dss_del": 495,
    "G2 Spring_theta_del": 0.240337542,
    "G2 Spring_pr_1_99_del": 21,
    "G2 Spring_ss_del": 89,
    "G2 Spring_dss_del": 524
  },
  {
    "ID": 222,
    "KG Fall_theta_del": -1.863075857,
    "KG Fall_pr_1_99_del": 21,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 314,
    "KG Winter_theta_del": -1.579068715,
    "KG Winter_pr_1_99_del": 21,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 342,
    "KG Spring_theta_del": -1.191669653,
    "KG Spring_pr_1_99_del": 21,
    "KG Spring_ss_del": 88,
    "KG Spring_dss_del": 381,
    "G1 Fall_theta_del": -1.000874759,
    "G1 Fall_pr_1_99_del": 21,
    "G1 Fall_ss_del": 88,
    "G1 Fall_dss_del": 400,
    "G1 Winter_theta_del": -0.727677816,
    "G1 Winter_pr_1_99_del": 21,
    "G1 Winter_ss_del": 88,
    "G1 Winter_dss_del": 427,
    "G1 Spring_theta_del": -0.467473836,
    "G1 Spring_pr_1_99_del": 21,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 453,
    "G2 Fall_theta_del": -0.277458316,
    "G2 Fall_pr_1_99_del": 21,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 472,
    "G2 Winter_theta_del": -0.049889319,
    "G2 Winter_pr_1_99_del": 21,
    "G2 Winter_ss_del": 89,
    "G2 Winter_dss_del": 495,
    "G2 Spring_theta_del": 0.246036713,
    "G2 Spring_pr_1_99_del": 21,
    "G2 Spring_ss_del": 89,
    "G2 Spring_dss_del": 525
  },
  {
    "ID": 294,
    "KG Fall_theta_del": -1.856495494,
    "KG Fall_pr_1_99_del": 22,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 314,
    "KG Winter_theta_del": -1.574198781,
    "KG Winter_pr_1_99_del": 22,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 343,
    "KG Spring_theta_del": -1.187531846,
    "KG Spring_pr_1_99_del": 22,
    "KG Spring_ss_del": 88,
    "KG Spring_dss_del": 381,
    "G1 Fall_theta_del": -0.975735867,
    "G1 Fall_pr_1_99_del": 22,
    "G1 Fall_ss_del": 88,
    "G1 Fall_dss_del": 402,
    "G1 Winter_theta_del": -0.725544705,
    "G1 Winter_pr_1_99_del": 22,
    "G1 Winter_ss_del": 88,
    "G1 Winter_dss_del": 427,
    "G1 Spring_theta_del": -0.466485087,
    "G1 Spring_pr_1_99_del": 22,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 453,
    "G2 Fall_theta_del": -0.277190664,
    "G2 Fall_pr_1_99_del": 22,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 472,
    "G2 Winter_theta_del": -0.047260179,
    "G2 Winter_pr_1_99_del": 22,
    "G2 Winter_ss_del": 89,
    "G2 Winter_dss_del": 495,
    "G2 Spring_theta_del": 0.25759902,
    "G2 Spring_pr_1_99_del": 22,
    "G2 Spring_ss_del": 89,
    "G2 Spring_dss_del": 526
  },
  {
    "ID": 322,
    "KG Fall_theta_del": -1.850885987,
    "KG Fall_pr_1_99_del": 22,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 315,
    "KG Winter_theta_del": -1.56846778,
    "KG Winter_pr_1_99_del": 22,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 343,
    "KG Spring_theta_del": -1.179824823,
    "KG Spring_pr_1_99_del": 22,
    "KG Spring_ss_del": 88,
    "KG Spring_dss_del": 382,
    "G1 Fall_theta_del": -0.9725262,
    "G1 Fall_pr_1_99_del": 22,
    "G1 Fall_ss_del": 88,
    "G1 Fall_dss_del": 403,
    "G1 Winter_theta_del": -0.724653,
    "G1 Winter_pr_1_99_del": 22,
    "G1 Winter_ss_del": 88,
    "G1 Winter_dss_del": 428,
    "G1 Spring_theta_del": -0.465131903,
    "G1 Spring_pr_1_99_del": 22,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 453,
    "G2 Fall_theta_del": -0.275076749,
    "G2 Fall_pr_1_99_del": 22,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 472,
    "G2 Winter_theta_del": -0.040823538,
    "G2 Winter_pr_1_99_del": 22,
    "G2 Winter_ss_del": 89,
    "G2 Winter_dss_del": 496,
    "G2 Spring_theta_del": 0.271768814,
    "G2 Spring_pr_1_99_del": 22,
    "G2 Spring_ss_del": 89,
    "G2 Spring_dss_del": 527
  },
  {
    "ID": 336,
    "KG Fall_theta_del": -1.843203592,
    "KG Fall_pr_1_99_del": 22,
    "KG Fall_ss_del": 87,
    "KG Fall_dss_del": 316,
    "KG Winter_theta_del": -1.563653848,
    "KG Winter_pr_1_99_del": 22,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 344,
    "KG Spring_theta_del": -1.170407966,
    "KG Spring_pr_1_99_del": 22,
    "KG Spring_ss_del": 89,
    "KG Spring_dss_del": 383,
    "G1 Fall_theta_del": -0.968078373,
    "G1 Fall_pr_1_99_del": 22,
    "G1 Fall_ss_del": 88,
    "G1 Fall_dss_del": 403,
    "G1 Winter_theta_del": -0.70712822,
    "G1 Winter_pr_1_99_del": 22,
    "G1 Winter_ss_del": 88,
    "G1 Winter_dss_del": 429,
    "G1 Spring_theta_del": -0.451078528,
    "G1 Spring_pr_1_99_del": 22,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 455,
    "G2 Fall_theta_del": -0.274861556,
    "G2 Fall_pr_1_99_del": 22,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 473,
    "G2 Winter_theta_del": -0.032393143,
    "G2 Winter_pr_1_99_del": 22,
    "G2 Winter_ss_del": 89,
    "G2 Winter_dss_del": 497,
    "G2 Spring_theta_del": 0.276270693,
    "G2 Spring_pr_1_99_del": 22,
    "G2 Spring_ss_del": 89,
    "G2 Spring_dss_del": 528
  },
  {
    "ID": 390,
    "KG Fall_theta_del": -1.833355401,
    "KG Fall_pr_1_99_del": 22,
    "KG Fall_ss_del": 88,
    "KG Fall_dss_del": 317,
    "KG Winter_theta_del": -1.562542889,
    "KG Winter_pr_1_99_del": 22,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 344,
    "KG Spring_theta_del": -1.15994611,
    "KG Spring_pr_1_99_del": 22,
    "KG Spring_ss_del": 89,
    "KG Spring_dss_del": 384,
    "G1 Fall_theta_del": -0.966839367,
    "G1 Fall_pr_1_99_del": 22,
    "G1 Fall_ss_del": 88,
    "G1 Fall_dss_del": 403,
    "G1 Winter_theta_del": -0.69394595,
    "G1 Winter_pr_1_99_del": 22,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 431,
    "G1 Spring_theta_del": -0.447446585,
    "G1 Spring_pr_1_99_del": 22,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 455,
    "G2 Fall_theta_del": -0.271021679,
    "G2 Fall_pr_1_99_del": 22,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 473,
    "G2 Winter_theta_del": -0.028940727,
    "G2 Winter_pr_1_99_del": 22,
    "G2 Winter_ss_del": 89,
    "G2 Winter_dss_del": 497,
    "G2 Spring_theta_del": 0.288651234,
    "G2 Spring_pr_1_99_del": 22,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 529
  },
  {
    "ID": 499,
    "KG Fall_theta_del": -1.832768384,
    "KG Fall_pr_1_99_del": 22,
    "KG Fall_ss_del": 88,
    "KG Fall_dss_del": 317,
    "KG Winter_theta_del": -1.557498103,
    "KG Winter_pr_1_99_del": 22,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 344,
    "KG Spring_theta_del": -1.157313334,
    "KG Spring_pr_1_99_del": 22,
    "KG Spring_ss_del": 89,
    "KG Spring_dss_del": 384,
    "G1 Fall_theta_del": -0.964457623,
    "G1 Fall_pr_1_99_del": 22,
    "G1 Fall_ss_del": 88,
    "G1 Fall_dss_del": 404,
    "G1 Winter_theta_del": -0.691819358,
    "G1 Winter_pr_1_99_del": 22,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 431,
    "G1 Spring_theta_del": -0.442493662,
    "G1 Spring_pr_1_99_del": 22,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 456,
    "G2 Fall_theta_del": -0.26663914,
    "G2 Fall_pr_1_99_del": 22,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 473,
    "G2 Winter_theta_del": 0.001088797,
    "G2 Winter_pr_1_99_del": 22,
    "G2 Winter_ss_del": 90,
    "G2 Winter_dss_del": 500,
    "G2 Spring_theta_del": 0.290090743,
    "G2 Spring_pr_1_99_del": 22,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 529
  },
  {
    "ID": 60,
    "KG Fall_theta_del": -1.814410862,
    "KG Fall_pr_1_99_del": 23,
    "KG Fall_ss_del": 88,
    "KG Fall_dss_del": 319,
    "KG Winter_theta_del": -1.557363358,
    "KG Winter_pr_1_99_del": 23,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 344,
    "KG Spring_theta_del": -1.15493336,
    "KG Spring_pr_1_99_del": 23,
    "KG Spring_ss_del": 89,
    "KG Spring_dss_del": 385,
    "G1 Fall_theta_del": -0.964246371,
    "G1 Fall_pr_1_99_del": 23,
    "G1 Fall_ss_del": 88,
    "G1 Fall_dss_del": 404,
    "G1 Winter_theta_del": -0.6911396,
    "G1 Winter_pr_1_99_del": 23,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 431,
    "G1 Spring_theta_del": -0.439533043,
    "G1 Spring_pr_1_99_del": 23,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 456,
    "G2 Fall_theta_del": -0.265320717,
    "G2 Fall_pr_1_99_del": 23,
    "G2 Fall_ss_del": 88,
    "G2 Fall_dss_del": 473,
    "G2 Winter_theta_del": 0.004388743,
    "G2 Winter_pr_1_99_del": 23,
    "G2 Winter_ss_del": 90,
    "G2 Winter_dss_del": 500,
    "G2 Spring_theta_del": 0.295818513,
    "G2 Spring_pr_1_99_del": 23,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 530
  },
  {
    "ID": 108,
    "KG Fall_theta_del": -1.811708748,
    "KG Fall_pr_1_99_del": 23,
    "KG Fall_ss_del": 88,
    "KG Fall_dss_del": 319,
    "KG Winter_theta_del": -1.556377049,
    "KG Winter_pr_1_99_del": 23,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 344,
    "KG Spring_theta_del": -1.148862805,
    "KG Spring_pr_1_99_del": 23,
    "KG Spring_ss_del": 89,
    "KG Spring_dss_del": 385,
    "G1 Fall_theta_del": -0.932708432,
    "G1 Fall_pr_1_99_del": 23,
    "G1 Fall_ss_del": 89,
    "G1 Fall_dss_del": 407,
    "G1 Winter_theta_del": -0.681337071,
    "G1 Winter_pr_1_99_del": 23,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 432,
    "G1 Spring_theta_del": -0.436787567,
    "G1 Spring_pr_1_99_del": 23,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 456,
    "G2 Fall_theta_del": -0.261845874,
    "G2 Fall_pr_1_99_del": 23,
    "G2 Fall_ss_del": 89,
    "G2 Fall_dss_del": 474,
    "G2 Winter_theta_del": 0.011335568,
    "G2 Winter_pr_1_99_del": 23,
    "G2 Winter_ss_del": 90,
    "G2 Winter_dss_del": 501,
    "G2 Spring_theta_del": 0.300972414,
    "G2 Spring_pr_1_99_del": 23,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 530
  },
  {
    "ID": 259,
    "KG Fall_theta_del": -1.799042805,
    "KG Fall_pr_1_99_del": 23,
    "KG Fall_ss_del": 88,
    "KG Fall_dss_del": 320,
    "KG Winter_theta_del": -1.551423526,
    "KG Winter_pr_1_99_del": 23,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 345,
    "KG Spring_theta_del": -1.144540719,
    "KG Spring_pr_1_99_del": 23,
    "KG Spring_ss_del": 89,
    "KG Spring_dss_del": 386,
    "G1 Fall_theta_del": -0.931282822,
    "G1 Fall_pr_1_99_del": 23,
    "G1 Fall_ss_del": 89,
    "G1 Fall_dss_del": 407,
    "G1 Winter_theta_del": -0.673220355,
    "G1 Winter_pr_1_99_del": 23,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 433,
    "G1 Spring_theta_del": -0.435329647,
    "G1 Spring_pr_1_99_del": 23,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 456,
    "G2 Fall_theta_del": -0.256046252,
    "G2 Fall_pr_1_99_del": 23,
    "G2 Fall_ss_del": 89,
    "G2 Fall_dss_del": 474,
    "G2 Winter_theta_del": 0.014071733,
    "G2 Winter_pr_1_99_del": 23,
    "G2 Winter_ss_del": 90,
    "G2 Winter_dss_del": 501,
    "G2 Spring_theta_del": 0.302751537,
    "G2 Spring_pr_1_99_del": 23,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 530
  },
  {
    "ID": 277,
    "KG Fall_theta_del": -1.778829684,
    "KG Fall_pr_1_99_del": 23,
    "KG Fall_ss_del": 89,
    "KG Fall_dss_del": 322,
    "KG Winter_theta_del": -1.547326255,
    "KG Winter_pr_1_99_del": 23,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 345,
    "KG Spring_theta_del": -1.139845941,
    "KG Spring_pr_1_99_del": 23,
    "KG Spring_ss_del": 89,
    "KG Spring_dss_del": 386,
    "G1 Fall_theta_del": -0.931179504,
    "G1 Fall_pr_1_99_del": 23,
    "G1 Fall_ss_del": 89,
    "G1 Fall_dss_del": 407,
    "G1 Winter_theta_del": -0.665566344,
    "G1 Winter_pr_1_99_del": 23,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 433,
    "G1 Spring_theta_del": -0.434397849,
    "G1 Spring_pr_1_99_del": 23,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 457,
    "G2 Fall_theta_del": -0.247755383,
    "G2 Fall_pr_1_99_del": 23,
    "G2 Fall_ss_del": 89,
    "G2 Fall_dss_del": 475,
    "G2 Winter_theta_del": 0.01984365,
    "G2 Winter_pr_1_99_del": 23,
    "G2 Winter_ss_del": 90,
    "G2 Winter_dss_del": 502,
    "G2 Spring_theta_del": 0.30406941,
    "G2 Spring_pr_1_99_del": 23,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 530
  },
  {
    "ID": 362,
    "KG Fall_theta_del": -1.770228514,
    "KG Fall_pr_1_99_del": 23,
    "KG Fall_ss_del": 89,
    "KG Fall_dss_del": 323,
    "KG Winter_theta_del": -1.542427971,
    "KG Winter_pr_1_99_del": 23,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 346,
    "KG Spring_theta_del": -1.133432003,
    "KG Spring_pr_1_99_del": 23,
    "KG Spring_ss_del": 89,
    "KG Spring_dss_del": 387,
    "G1 Fall_theta_del": -0.930733929,
    "G1 Fall_pr_1_99_del": 23,
    "G1 Fall_ss_del": 89,
    "G1 Fall_dss_del": 407,
    "G1 Winter_theta_del": -0.665317698,
    "G1 Winter_pr_1_99_del": 23,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 433,
    "G1 Spring_theta_del": -0.433060267,
    "G1 Spring_pr_1_99_del": 23,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 457,
    "G2 Fall_theta_del": -0.242134456,
    "G2 Fall_pr_1_99_del": 23,
    "G2 Fall_ss_del": 89,
    "G2 Fall_dss_del": 476,
    "G2 Winter_theta_del": 0.020367515,
    "G2 Winter_pr_1_99_del": 23,
    "G2 Winter_ss_del": 90,
    "G2 Winter_dss_del": 502,
    "G2 Spring_theta_del": 0.304336838,
    "G2 Spring_pr_1_99_del": 23,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 530
  },
  {
    "ID": 103,
    "KG Fall_theta_del": -1.758277692,
    "KG Fall_pr_1_99_del": 24,
    "KG Fall_ss_del": 89,
    "KG Fall_dss_del": 324,
    "KG Winter_theta_del": -1.5409484,
    "KG Winter_pr_1_99_del": 24,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 346,
    "KG Spring_theta_del": -1.120192969,
    "KG Spring_pr_1_99_del": 24,
    "KG Spring_ss_del": 90,
    "KG Spring_dss_del": 388,
    "G1 Fall_theta_del": -0.926082321,
    "G1 Fall_pr_1_99_del": 24,
    "G1 Fall_ss_del": 89,
    "G1 Fall_dss_del": 407,
    "G1 Winter_theta_del": -0.658908882,
    "G1 Winter_pr_1_99_del": 24,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 434,
    "G1 Spring_theta_del": -0.428575467,
    "G1 Spring_pr_1_99_del": 24,
    "G1 Spring_ss_del": 89,
    "G1 Spring_dss_del": 457,
    "G2 Fall_theta_del": -0.240721169,
    "G2 Fall_pr_1_99_del": 24,
    "G2 Fall_ss_del": 89,
    "G2 Fall_dss_del": 476,
    "G2 Winter_theta_del": 0.037106425,
    "G2 Winter_pr_1_99_del": 24,
    "G2 Winter_ss_del": 90,
    "G2 Winter_dss_del": 504,
    "G2 Spring_theta_del": 0.307860529,
    "G2 Spring_pr_1_99_del": 24,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 531
  },
  {
    "ID": 111,
    "KG Fall_theta_del": -1.754446045,
    "KG Fall_pr_1_99_del": 24,
    "KG Fall_ss_del": 89,
    "KG Fall_dss_del": 325,
    "KG Winter_theta_del": -1.539203483,
    "KG Winter_pr_1_99_del": 24,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 346,
    "KG Spring_theta_del": -1.117359379,
    "KG Spring_pr_1_99_del": 24,
    "KG Spring_ss_del": 90,
    "KG Spring_dss_del": 388,
    "G1 Fall_theta_del": -0.920732246,
    "G1 Fall_pr_1_99_del": 24,
    "G1 Fall_ss_del": 89,
    "G1 Fall_dss_del": 408,
    "G1 Winter_theta_del": -0.657223398,
    "G1 Winter_pr_1_99_del": 24,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 434,
    "G1 Spring_theta_del": -0.426065988,
    "G1 Spring_pr_1_99_del": 24,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 457,
    "G2 Fall_theta_del": -0.232697837,
    "G2 Fall_pr_1_99_del": 24,
    "G2 Fall_ss_del": 89,
    "G2 Fall_dss_del": 477,
    "G2 Winter_theta_del": 0.037472113,
    "G2 Winter_pr_1_99_del": 24,
    "G2 Winter_ss_del": 90,
    "G2 Winter_dss_del": 504,
    "G2 Spring_theta_del": 0.309764584,
    "G2 Spring_pr_1_99_del": 24,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 531
  },
  {
    "ID": 249,
    "KG Fall_theta_del": -1.754300565,
    "KG Fall_pr_1_99_del": 24,
    "KG Fall_ss_del": 89,
    "KG Fall_dss_del": 325,
    "KG Winter_theta_del": -1.537093401,
    "KG Winter_pr_1_99_del": 24,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 346,
    "KG Spring_theta_del": -1.112512592,
    "KG Spring_pr_1_99_del": 24,
    "KG Spring_ss_del": 90,
    "KG Spring_dss_del": 389,
    "G1 Fall_theta_del": -0.920553021,
    "G1 Fall_pr_1_99_del": 24,
    "G1 Fall_ss_del": 89,
    "G1 Fall_dss_del": 408,
    "G1 Winter_theta_del": -0.656977762,
    "G1 Winter_pr_1_99_del": 24,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 434,
    "G1 Spring_theta_del": -0.420151358,
    "G1 Spring_pr_1_99_del": 24,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 458,
    "G2 Fall_theta_del": -0.228685902,
    "G2 Fall_pr_1_99_del": 24,
    "G2 Fall_ss_del": 89,
    "G2 Fall_dss_del": 477,
    "G2 Winter_theta_del": 0.04488309,
    "G2 Winter_pr_1_99_del": 24,
    "G2 Winter_ss_del": 90,
    "G2 Winter_dss_del": 504,
    "G2 Spring_theta_del": 0.31072624,
    "G2 Spring_pr_1_99_del": 24,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 531
  },
  {
    "ID": 387,
    "KG Fall_theta_del": -1.750425176,
    "KG Fall_pr_1_99_del": 24,
    "KG Fall_ss_del": 89,
    "KG Fall_dss_del": 325,
    "KG Winter_theta_del": -1.525610946,
    "KG Winter_pr_1_99_del": 24,
    "KG Winter_ss_del": 88,
    "KG Winter_dss_del": 347,
    "KG Spring_theta_del": -1.108929149,
    "KG Spring_pr_1_99_del": 24,
    "KG Spring_ss_del": 90,
    "KG Spring_dss_del": 389,
    "G1 Fall_theta_del": -0.918108746,
    "G1 Fall_pr_1_99_del": 24,
    "G1 Fall_ss_del": 89,
    "G1 Fall_dss_del": 408,
    "G1 Winter_theta_del": -0.653829075,
    "G1 Winter_pr_1_99_del": 24,
    "G1 Winter_ss_del": 89,
    "G1 Winter_dss_del": 435,
    "G1 Spring_theta_del": -0.410036126,
    "G1 Spring_pr_1_99_del": 24,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 459,
    "G2 Fall_theta_del": -0.21779267,
    "G2 Fall_pr_1_99_del": 24,
    "G2 Fall_ss_del": 89,
    "G2 Fall_dss_del": 478,
    "G2 Winter_theta_del": 0.049546728,
    "G2 Winter_pr_1_99_del": 24,
    "G2 Winter_ss_del": 91,
    "G2 Winter_dss_del": 505,
    "G2 Spring_theta_del": 0.310904839,
    "G2 Spring_pr_1_99_del": 24,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 531
  },
  {
    "ID": 426,
    "KG Fall_theta_del": -1.749543189,
    "KG Fall_pr_1_99_del": 24,
    "KG Fall_ss_del": 89,
    "KG Fall_dss_del": 325,
    "KG Winter_theta_del": -1.515709469,
    "KG Winter_pr_1_99_del": 24,
    "KG Winter_ss_del": 89,
    "KG Winter_dss_del": 348,
    "KG Spring_theta_del": -1.108070271,
    "KG Spring_pr_1_99_del": 24,
    "KG Spring_ss_del": 90,
    "KG Spring_dss_del": 389,
    "G1 Fall_theta_del": -0.907621895,
    "G1 Fall_pr_1_99_del": 24,
    "G1 Fall_ss_del": 89,
    "G1 Fall_dss_del": 409,
    "G1 Winter_theta_del": -0.640579749,
    "G1 Winter_pr_1_99_del": 24,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 436,
    "G1 Spring_theta_del": -0.409170401,
    "G1 Spring_pr_1_99_del": 24,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 459,
    "G2 Fall_theta_del": -0.215988088,
    "G2 Fall_pr_1_99_del": 24,
    "G2 Fall_ss_del": 89,
    "G2 Fall_dss_del": 478,
    "G2 Winter_theta_del": 0.058097601,
    "G2 Winter_pr_1_99_del": 24,
    "G2 Winter_ss_del": 91,
    "G2 Winter_dss_del": 506,
    "G2 Spring_theta_del": 0.322514704,
    "G2 Spring_pr_1_99_del": 24,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 532
  },
  {
    "ID": 11,
    "KG Fall_theta_del": -1.742603923,
    "KG Fall_pr_1_99_del": 25,
    "KG Fall_ss_del": 89,
    "KG Fall_dss_del": 326,
    "KG Winter_theta_del": -1.514432789,
    "KG Winter_pr_1_99_del": 25,
    "KG Winter_ss_del": 89,
    "KG Winter_dss_del": 349,
    "KG Spring_theta_del": -1.099046425,
    "KG Spring_pr_1_99_del": 25,
    "KG Spring_ss_del": 90,
    "KG Spring_dss_del": 390,
    "G1 Fall_theta_del": -0.905729571,
    "G1 Fall_pr_1_99_del": 25,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 409,
    "G1 Winter_theta_del": -0.638733035,
    "G1 Winter_pr_1_99_del": 25,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 436,
    "G1 Spring_theta_del": -0.400485344,
    "G1 Spring_pr_1_99_del": 25,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 460,
    "G2 Fall_theta_del": -0.207631478,
    "G2 Fall_pr_1_99_del": 25,
    "G2 Fall_ss_del": 90,
    "G2 Fall_dss_del": 479,
    "G2 Winter_theta_del": 0.059621481,
    "G2 Winter_pr_1_99_del": 25,
    "G2 Winter_ss_del": 91,
    "G2 Winter_dss_del": 506,
    "G2 Spring_theta_del": 0.32383696,
    "G2 Spring_pr_1_99_del": 25,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 532
  },
  {
    "ID": 140,
    "KG Fall_theta_del": -1.736747412,
    "KG Fall_pr_1_99_del": 25,
    "KG Fall_ss_del": 89,
    "KG Fall_dss_del": 326,
    "KG Winter_theta_del": -1.502653924,
    "KG Winter_pr_1_99_del": 25,
    "KG Winter_ss_del": 89,
    "KG Winter_dss_del": 350,
    "KG Spring_theta_del": -1.093306096,
    "KG Spring_pr_1_99_del": 25,
    "KG Spring_ss_del": 90,
    "KG Spring_dss_del": 391,
    "G1 Fall_theta_del": -0.905078518,
    "G1 Fall_pr_1_99_del": 25,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 409,
    "G1 Winter_theta_del": -0.635217017,
    "G1 Winter_pr_1_99_del": 25,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 436,
    "G1 Spring_theta_del": -0.390080701,
    "G1 Spring_pr_1_99_del": 25,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 461,
    "G2 Fall_theta_del": -0.190403423,
    "G2 Fall_pr_1_99_del": 25,
    "G2 Fall_ss_del": 90,
    "G2 Fall_dss_del": 481,
    "G2 Winter_theta_del": 0.065494505,
    "G2 Winter_pr_1_99_del": 25,
    "G2 Winter_ss_del": 91,
    "G2 Winter_dss_del": 507,
    "G2 Spring_theta_del": 0.329779466,
    "G2 Spring_pr_1_99_del": 25,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 533
  },
  {
    "ID": 239,
    "KG Fall_theta_del": -1.71593152,
    "KG Fall_pr_1_99_del": 25,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 328,
    "KG Winter_theta_del": -1.487822338,
    "KG Winter_pr_1_99_del": 25,
    "KG Winter_ss_del": 89,
    "KG Winter_dss_del": 351,
    "KG Spring_theta_del": -1.086245408,
    "KG Spring_pr_1_99_del": 25,
    "KG Spring_ss_del": 90,
    "KG Spring_dss_del": 391,
    "G1 Fall_theta_del": -0.897277231,
    "G1 Fall_pr_1_99_del": 25,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 410,
    "G1 Winter_theta_del": -0.634393732,
    "G1 Winter_pr_1_99_del": 25,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 437,
    "G1 Spring_theta_del": -0.387058823,
    "G1 Spring_pr_1_99_del": 25,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 461,
    "G2 Fall_theta_del": -0.174544048,
    "G2 Fall_pr_1_99_del": 25,
    "G2 Fall_ss_del": 90,
    "G2 Fall_dss_del": 483,
    "G2 Winter_theta_del": 0.073993605,
    "G2 Winter_pr_1_99_del": 25,
    "G2 Winter_ss_del": 91,
    "G2 Winter_dss_del": 507,
    "G2 Spring_theta_del": 0.337330744,
    "G2 Spring_pr_1_99_del": 25,
    "G2 Spring_ss_del": 90,
    "G2 Spring_dss_del": 534
  },
  {
    "ID": 255,
    "KG Fall_theta_del": -1.715926661,
    "KG Fall_pr_1_99_del": 25,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 328,
    "KG Winter_theta_del": -1.487458418,
    "KG Winter_pr_1_99_del": 25,
    "KG Winter_ss_del": 89,
    "KG Winter_dss_del": 351,
    "KG Spring_theta_del": -1.080184054,
    "KG Spring_pr_1_99_del": 25,
    "KG Spring_ss_del": 90,
    "KG Spring_dss_del": 392,
    "G1 Fall_theta_del": -0.88920849,
    "G1 Fall_pr_1_99_del": 25,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 411,
    "G1 Winter_theta_del": -0.633939691,
    "G1 Winter_pr_1_99_del": 25,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 437,
    "G1 Spring_theta_del": -0.38651546,
    "G1 Spring_pr_1_99_del": 25,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 461,
    "G2 Fall_theta_del": -0.160653181,
    "G2 Fall_pr_1_99_del": 25,
    "G2 Fall_ss_del": 90,
    "G2 Fall_dss_del": 484,
    "G2 Winter_theta_del": 0.077177826,
    "G2 Winter_pr_1_99_del": 25,
    "G2 Winter_ss_del": 91,
    "G2 Winter_dss_del": 508,
    "G2 Spring_theta_del": 0.338466855,
    "G2 Spring_pr_1_99_del": 25,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 534
  },
  {
    "ID": 327,
    "KG Fall_theta_del": -1.715332578,
    "KG Fall_pr_1_99_del": 25,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 328,
    "KG Winter_theta_del": -1.474442369,
    "KG Winter_pr_1_99_del": 25,
    "KG Winter_ss_del": 89,
    "KG Winter_dss_del": 353,
    "KG Spring_theta_del": -1.073577325,
    "KG Spring_pr_1_99_del": 25,
    "KG Spring_ss_del": 90,
    "KG Spring_dss_del": 393,
    "G1 Fall_theta_del": -0.888398538,
    "G1 Fall_pr_1_99_del": 25,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 411,
    "G1 Winter_theta_del": -0.629147835,
    "G1 Winter_pr_1_99_del": 25,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 437,
    "G1 Spring_theta_del": -0.386118484,
    "G1 Spring_pr_1_99_del": 25,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 461,
    "G2 Fall_theta_del": -0.156687565,
    "G2 Fall_pr_1_99_del": 25,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 484,
    "G2 Winter_theta_del": 0.092307445,
    "G2 Winter_pr_1_99_del": 25,
    "G2 Winter_ss_del": 91,
    "G2 Winter_dss_del": 509,
    "G2 Spring_theta_del": 0.338888529,
    "G2 Spring_pr_1_99_del": 25,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 534
  },
  {
    "ID": 207,
    "KG Fall_theta_del": -1.713361744,
    "KG Fall_pr_1_99_del": 26,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 329,
    "KG Winter_theta_del": -1.473563081,
    "KG Winter_pr_1_99_del": 26,
    "KG Winter_ss_del": 89,
    "KG Winter_dss_del": 353,
    "KG Spring_theta_del": -1.065341271,
    "KG Spring_pr_1_99_del": 26,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 393,
    "G1 Fall_theta_del": -0.886630798,
    "G1 Fall_pr_1_99_del": 26,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 411,
    "G1 Winter_theta_del": -0.628094065,
    "G1 Winter_pr_1_99_del": 26,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 437,
    "G1 Spring_theta_del": -0.378242782,
    "G1 Spring_pr_1_99_del": 26,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 462,
    "G2 Fall_theta_del": -0.156552873,
    "G2 Fall_pr_1_99_del": 26,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 484,
    "G2 Winter_theta_del": 0.096114735,
    "G2 Winter_pr_1_99_del": 26,
    "G2 Winter_ss_del": 91,
    "G2 Winter_dss_del": 510,
    "G2 Spring_theta_del": 0.340688458,
    "G2 Spring_pr_1_99_del": 26,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 534
  },
  {
    "ID": 237,
    "KG Fall_theta_del": -1.712364361,
    "KG Fall_pr_1_99_del": 26,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 329,
    "KG Winter_theta_del": -1.470636457,
    "KG Winter_pr_1_99_del": 26,
    "KG Winter_ss_del": 89,
    "KG Winter_dss_del": 353,
    "KG Spring_theta_del": -1.058329117,
    "KG Spring_pr_1_99_del": 26,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 394,
    "G1 Fall_theta_del": -0.882980283,
    "G1 Fall_pr_1_99_del": 26,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 412,
    "G1 Winter_theta_del": -0.62491695,
    "G1 Winter_pr_1_99_del": 26,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 438,
    "G1 Spring_theta_del": -0.377539519,
    "G1 Spring_pr_1_99_del": 26,
    "G1 Spring_ss_del": 90,
    "G1 Spring_dss_del": 462,
    "G2 Fall_theta_del": -0.154377774,
    "G2 Fall_pr_1_99_del": 26,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 485,
    "G2 Winter_theta_del": 0.101019756,
    "G2 Winter_pr_1_99_del": 26,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 510,
    "G2 Spring_theta_del": 0.344995477,
    "G2 Spring_pr_1_99_del": 26,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 534
  },
  {
    "ID": 260,
    "KG Fall_theta_del": -1.703801064,
    "KG Fall_pr_1_99_del": 26,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 330,
    "KG Winter_theta_del": -1.466468234,
    "KG Winter_pr_1_99_del": 26,
    "KG Winter_ss_del": 90,
    "KG Winter_dss_del": 353,
    "KG Spring_theta_del": -1.057579863,
    "KG Spring_pr_1_99_del": 26,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 394,
    "G1 Fall_theta_del": -0.881211754,
    "G1 Fall_pr_1_99_del": 26,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 412,
    "G1 Winter_theta_del": -0.615797451,
    "G1 Winter_pr_1_99_del": 26,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 438,
    "G1 Spring_theta_del": -0.371369251,
    "G1 Spring_pr_1_99_del": 26,
    "G1 Spring_ss_del": 91,
    "G1 Spring_dss_del": 463,
    "G2 Fall_theta_del": -0.154216371,
    "G2 Fall_pr_1_99_del": 26,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 485,
    "G2 Winter_theta_del": 0.108034128,
    "G2 Winter_pr_1_99_del": 26,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 511,
    "G2 Spring_theta_del": 0.350474387,
    "G2 Spring_pr_1_99_del": 26,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 535
  },
  {
    "ID": 267,
    "KG Fall_theta_del": -1.685142176,
    "KG Fall_pr_1_99_del": 26,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 331,
    "KG Winter_theta_del": -1.442131519,
    "KG Winter_pr_1_99_del": 26,
    "KG Winter_ss_del": 90,
    "KG Winter_dss_del": 356,
    "KG Spring_theta_del": -1.053876544,
    "KG Spring_pr_1_99_del": 26,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 395,
    "G1 Fall_theta_del": -0.877176493,
    "G1 Fall_pr_1_99_del": 26,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 412,
    "G1 Winter_theta_del": -0.611468027,
    "G1 Winter_pr_1_99_del": 26,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 439,
    "G1 Spring_theta_del": -0.369499089,
    "G1 Spring_pr_1_99_del": 26,
    "G1 Spring_ss_del": 91,
    "G1 Spring_dss_del": 463,
    "G2 Fall_theta_del": -0.143863597,
    "G2 Fall_pr_1_99_del": 26,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 486,
    "G2 Winter_theta_del": 0.10910644,
    "G2 Winter_pr_1_99_del": 26,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 511,
    "G2 Spring_theta_del": 0.355093948,
    "G2 Spring_pr_1_99_del": 26,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 536
  },
  {
    "ID": 371,
    "KG Fall_theta_del": -1.6816509,
    "KG Fall_pr_1_99_del": 26,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 332,
    "KG Winter_theta_del": -1.436069734,
    "KG Winter_pr_1_99_del": 26,
    "KG Winter_ss_del": 90,
    "KG Winter_dss_del": 356,
    "KG Spring_theta_del": -1.052273472,
    "KG Spring_pr_1_99_del": 26,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 395,
    "G1 Fall_theta_del": -0.874686119,
    "G1 Fall_pr_1_99_del": 26,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 413,
    "G1 Winter_theta_del": -0.610422748,
    "G1 Winter_pr_1_99_del": 26,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 439,
    "G1 Spring_theta_del": -0.359494746,
    "G1 Spring_pr_1_99_del": 26,
    "G1 Spring_ss_del": 91,
    "G1 Spring_dss_del": 464,
    "G2 Fall_theta_del": -0.141797835,
    "G2 Fall_pr_1_99_del": 26,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 486,
    "G2 Winter_theta_del": 0.111559613,
    "G2 Winter_pr_1_99_del": 26,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 511,
    "G2 Spring_theta_del": 0.357873884,
    "G2 Spring_pr_1_99_del": 26,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 536
  },
  {
    "ID": 244,
    "KG Fall_theta_del": -1.678868326,
    "KG Fall_pr_1_99_del": 27,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 332,
    "KG Winter_theta_del": -1.435057518,
    "KG Winter_pr_1_99_del": 27,
    "KG Winter_ss_del": 90,
    "KG Winter_dss_del": 356,
    "KG Spring_theta_del": -1.0449037,
    "KG Spring_pr_1_99_del": 27,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 396,
    "G1 Fall_theta_del": -0.873855665,
    "G1 Fall_pr_1_99_del": 27,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 413,
    "G1 Winter_theta_del": -0.608061826,
    "G1 Winter_pr_1_99_del": 27,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 439,
    "G1 Spring_theta_del": -0.358930663,
    "G1 Spring_pr_1_99_del": 27,
    "G1 Spring_ss_del": 91,
    "G1 Spring_dss_del": 464,
    "G2 Fall_theta_del": -0.139749484,
    "G2 Fall_pr_1_99_del": 27,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 486,
    "G2 Winter_theta_del": 0.115428708,
    "G2 Winter_pr_1_99_del": 27,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 512,
    "G2 Spring_theta_del": 0.365448929,
    "G2 Spring_pr_1_99_del": 27,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 537
  },
  {
    "ID": 269,
    "KG Fall_theta_del": -1.678755277,
    "KG Fall_pr_1_99_del": 27,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 332,
    "KG Winter_theta_del": -1.424406145,
    "KG Winter_pr_1_99_del": 27,
    "KG Winter_ss_del": 90,
    "KG Winter_dss_del": 358,
    "KG Spring_theta_del": -1.041844913,
    "KG Spring_pr_1_99_del": 27,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 396,
    "G1 Fall_theta_del": -0.867961789,
    "G1 Fall_pr_1_99_del": 27,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 413,
    "G1 Winter_theta_del": -0.605242754,
    "G1 Winter_pr_1_99_del": 27,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 439,
    "G1 Spring_theta_del": -0.351316082,
    "G1 Spring_pr_1_99_del": 27,
    "G1 Spring_ss_del": 91,
    "G1 Spring_dss_del": 465,
    "G2 Fall_theta_del": -0.130506847,
    "G2 Fall_pr_1_99_del": 27,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 487,
    "G2 Winter_theta_del": 0.125841653,
    "G2 Winter_pr_1_99_del": 27,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 513,
    "G2 Spring_theta_del": 0.371072405,
    "G2 Spring_pr_1_99_del": 27,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 537
  },
  {
    "ID": 330,
    "KG Fall_theta_del": -1.672245168,
    "KG Fall_pr_1_99_del": 27,
    "KG Fall_ss_del": 90,
    "KG Fall_dss_del": 333,
    "KG Winter_theta_del": -1.424343465,
    "KG Winter_pr_1_99_del": 27,
    "KG Winter_ss_del": 90,
    "KG Winter_dss_del": 358,
    "KG Spring_theta_del": -1.038007774,
    "KG Spring_pr_1_99_del": 27,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 396,
    "G1 Fall_theta_del": -0.867204418,
    "G1 Fall_pr_1_99_del": 27,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 413,
    "G1 Winter_theta_del": -0.601849278,
    "G1 Winter_pr_1_99_del": 27,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 440,
    "G1 Spring_theta_del": -0.349350281,
    "G1 Spring_pr_1_99_del": 27,
    "G1 Spring_ss_del": 91,
    "G1 Spring_dss_del": 465,
    "G2 Fall_theta_del": -0.129516646,
    "G2 Fall_pr_1_99_del": 27,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 487,
    "G2 Winter_theta_del": 0.13050575,
    "G2 Winter_pr_1_99_del": 27,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 513,
    "G2 Spring_theta_del": 0.374697063,
    "G2 Spring_pr_1_99_del": 27,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 537
  },
  {
    "ID": 339,
    "KG Fall_theta_del": -1.670683005,
    "KG Fall_pr_1_99_del": 27,
    "KG Fall_ss_del": 91,
    "KG Fall_dss_del": 333,
    "KG Winter_theta_del": -1.423371403,
    "KG Winter_pr_1_99_del": 27,
    "KG Winter_ss_del": 90,
    "KG Winter_dss_del": 358,
    "KG Spring_theta_del": -1.037780144,
    "KG Spring_pr_1_99_del": 27,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 396,
    "G1 Fall_theta_del": -0.86700787,
    "G1 Fall_pr_1_99_del": 27,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 413,
    "G1 Winter_theta_del": -0.599214429,
    "G1 Winter_pr_1_99_del": 27,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 440,
    "G1 Spring_theta_del": -0.347651863,
    "G1 Spring_pr_1_99_del": 27,
    "G1 Spring_ss_del": 91,
    "G1 Spring_dss_del": 465,
    "G2 Fall_theta_del": -0.126730931,
    "G2 Fall_pr_1_99_del": 27,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 487,
    "G2 Winter_theta_del": 0.133542058,
    "G2 Winter_pr_1_99_del": 27,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 513,
    "G2 Spring_theta_del": 0.375523424,
    "G2 Spring_pr_1_99_del": 27,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 538
  },
  {
    "ID": 429,
    "KG Fall_theta_del": -1.650382979,
    "KG Fall_pr_1_99_del": 27,
    "KG Fall_ss_del": 91,
    "KG Fall_dss_del": 335,
    "KG Winter_theta_del": -1.418128593,
    "KG Winter_pr_1_99_del": 27,
    "KG Winter_ss_del": 90,
    "KG Winter_dss_del": 358,
    "KG Spring_theta_del": -1.036041222,
    "KG Spring_pr_1_99_del": 27,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 396,
    "G1 Fall_theta_del": -0.865189135,
    "G1 Fall_pr_1_99_del": 27,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 413,
    "G1 Winter_theta_del": -0.597452491,
    "G1 Winter_pr_1_99_del": 27,
    "G1 Winter_ss_del": 90,
    "G1 Winter_dss_del": 440,
    "G1 Spring_theta_del": -0.346356828,
    "G1 Spring_pr_1_99_del": 27,
    "G1 Spring_ss_del": 91,
    "G1 Spring_dss_del": 465,
    "G2 Fall_theta_del": -0.120412997,
    "G2 Fall_pr_1_99_del": 27,
    "G2 Fall_ss_del": 91,
    "G2 Fall_dss_del": 488,
    "G2 Winter_theta_del": 0.142326418,
    "G2 Winter_pr_1_99_del": 27,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 514,
    "G2 Spring_theta_del": 0.381149838,
    "G2 Spring_pr_1_99_del": 27,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 538
  },
  {
    "ID": 263,
    "KG Fall_theta_del": -1.640520651,
    "KG Fall_pr_1_99_del": 28,
    "KG Fall_ss_del": 91,
    "KG Fall_dss_del": 336,
    "KG Winter_theta_del": -1.415282128,
    "KG Winter_pr_1_99_del": 28,
    "KG Winter_ss_del": 90,
    "KG Winter_dss_del": 358,
    "KG Spring_theta_del": -1.033838076,
    "KG Spring_pr_1_99_del": 28,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 397,
    "G1 Fall_theta_del": -0.860097929,
    "G1 Fall_pr_1_99_del": 28,
    "G1 Fall_ss_del": 90,
    "G1 Fall_dss_del": 414,
    "G1 Winter_theta_del": -0.592931303,
    "G1 Winter_pr_1_99_del": 28,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 441,
    "G1 Spring_theta_del": -0.328699436,
    "G1 Spring_pr_1_99_del": 28,
    "G1 Spring_ss_del": 91,
    "G1 Spring_dss_del": 467,
    "G2 Fall_theta_del": -0.103811839,
    "G2 Fall_pr_1_99_del": 28,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 490,
    "G2 Winter_theta_del": 0.146714031,
    "G2 Winter_pr_1_99_del": 28,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 515,
    "G2 Spring_theta_del": 0.383764626,
    "G2 Spring_pr_1_99_del": 28,
    "G2 Spring_ss_del": 91,
    "G2 Spring_dss_del": 538
  },
  {
    "ID": 286,
    "KG Fall_theta_del": -1.637905479,
    "KG Fall_pr_1_99_del": 28,
    "KG Fall_ss_del": 91,
    "KG Fall_dss_del": 336,
    "KG Winter_theta_del": -1.412781362,
    "KG Winter_pr_1_99_del": 28,
    "KG Winter_ss_del": 91,
    "KG Winter_dss_del": 359,
    "KG Spring_theta_del": -1.027944234,
    "KG Spring_pr_1_99_del": 28,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 397,
    "G1 Fall_theta_del": -0.848163232,
    "G1 Fall_pr_1_99_del": 28,
    "G1 Fall_ss_del": 91,
    "G1 Fall_dss_del": 415,
    "G1 Winter_theta_del": -0.592656554,
    "G1 Winter_pr_1_99_del": 28,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 441,
    "G1 Spring_theta_del": -0.323410684,
    "G1 Spring_pr_1_99_del": 28,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 468,
    "G2 Fall_theta_del": -0.094794568,
    "G2 Fall_pr_1_99_del": 28,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 491,
    "G2 Winter_theta_del": 0.147416157,
    "G2 Winter_pr_1_99_del": 28,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 515,
    "G2 Spring_theta_del": 0.391194553,
    "G2 Spring_pr_1_99_del": 28,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 539
  },
  {
    "ID": 323,
    "KG Fall_theta_del": -1.634742264,
    "KG Fall_pr_1_99_del": 28,
    "KG Fall_ss_del": 91,
    "KG Fall_dss_del": 337,
    "KG Winter_theta_del": -1.403516196,
    "KG Winter_pr_1_99_del": 28,
    "KG Winter_ss_del": 91,
    "KG Winter_dss_del": 360,
    "KG Spring_theta_del": -1.022542891,
    "KG Spring_pr_1_99_del": 28,
    "KG Spring_ss_del": 91,
    "KG Spring_dss_del": 398,
    "G1 Fall_theta_del": -0.846029427,
    "G1 Fall_pr_1_99_del": 28,
    "G1 Fall_ss_del": 91,
    "G1 Fall_dss_del": 415,
    "G1 Winter_theta_del": -0.589686468,
    "G1 Winter_pr_1_99_del": 28,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 441,
    "G1 Spring_theta_del": -0.319315084,
    "G1 Spring_pr_1_99_del": 28,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 468,
    "G2 Fall_theta_del": -0.094563698,
    "G2 Fall_pr_1_99_del": 28,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 491,
    "G2 Winter_theta_del": 0.150738925,
    "G2 Winter_pr_1_99_del": 28,
    "G2 Winter_ss_del": 92,
    "G2 Winter_dss_del": 515,
    "G2 Spring_theta_del": 0.398141146,
    "G2 Spring_pr_1_99_del": 28,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 540
  },
  {
    "ID": 409,
    "KG Fall_theta_del": -1.630545609,
    "KG Fall_pr_1_99_del": 28,
    "KG Fall_ss_del": 91,
    "KG Fall_dss_del": 337,
    "KG Winter_theta_del": -1.401447419,
    "KG Winter_pr_1_99_del": 28,
    "KG Winter_ss_del": 91,
    "KG Winter_dss_del": 360,
    "KG Spring_theta_del": -1.012723366,
    "KG Spring_pr_1_99_del": 28,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 399,
    "G1 Fall_theta_del": -0.844036574,
    "G1 Fall_pr_1_99_del": 28,
    "G1 Fall_ss_del": 91,
    "G1 Fall_dss_del": 416,
    "G1 Winter_theta_del": -0.588812985,
    "G1 Winter_pr_1_99_del": 28,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 441,
    "G1 Spring_theta_del": -0.317954681,
    "G1 Spring_pr_1_99_del": 28,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 468,
    "G2 Fall_theta_del": -0.092265694,
    "G2 Fall_pr_1_99_del": 28,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 491,
    "G2 Winter_theta_del": 0.153104352,
    "G2 Winter_pr_1_99_del": 28,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 515,
    "G2 Spring_theta_del": 0.40030127,
    "G2 Spring_pr_1_99_del": 28,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 540
  },
  {
    "ID": 495,
    "KG Fall_theta_del": -1.627007596,
    "KG Fall_pr_1_99_del": 28,
    "KG Fall_ss_del": 91,
    "KG Fall_dss_del": 337,
    "KG Winter_theta_del": -1.400056277,
    "KG Winter_pr_1_99_del": 28,
    "KG Winter_ss_del": 91,
    "KG Winter_dss_del": 360,
    "KG Spring_theta_del": -1.010085689,
    "KG Spring_pr_1_99_del": 28,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 399,
    "G1 Fall_theta_del": -0.843319337,
    "G1 Fall_pr_1_99_del": 28,
    "G1 Fall_ss_del": 91,
    "G1 Fall_dss_del": 416,
    "G1 Winter_theta_del": -0.585224493,
    "G1 Winter_pr_1_99_del": 28,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 441,
    "G1 Spring_theta_del": -0.317463063,
    "G1 Spring_pr_1_99_del": 28,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 468,
    "G2 Fall_theta_del": -0.09182355,
    "G2 Fall_pr_1_99_del": 28,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 491,
    "G2 Winter_theta_del": 0.154009257,
    "G2 Winter_pr_1_99_del": 28,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 515,
    "G2 Spring_theta_del": 0.40208957,
    "G2 Spring_pr_1_99_del": 28,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 540
  },
  {
    "ID": 12,
    "KG Fall_theta_del": -1.607682684,
    "KG Fall_pr_1_99_del": 29,
    "KG Fall_ss_del": 92,
    "KG Fall_dss_del": 339,
    "KG Winter_theta_del": -1.395135265,
    "KG Winter_pr_1_99_del": 29,
    "KG Winter_ss_del": 91,
    "KG Winter_dss_del": 360,
    "KG Spring_theta_del": -1.008237303,
    "KG Spring_pr_1_99_del": 29,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 399,
    "G1 Fall_theta_del": -0.828416596,
    "G1 Fall_pr_1_99_del": 29,
    "G1 Fall_ss_del": 91,
    "G1 Fall_dss_del": 417,
    "G1 Winter_theta_del": -0.58229612,
    "G1 Winter_pr_1_99_del": 29,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 442,
    "G1 Spring_theta_del": -0.314649151,
    "G1 Spring_pr_1_99_del": 29,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 469,
    "G2 Fall_theta_del": -0.08980719,
    "G2 Fall_pr_1_99_del": 29,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 491,
    "G2 Winter_theta_del": 0.154615052,
    "G2 Winter_pr_1_99_del": 29,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 515,
    "G2 Spring_theta_del": 0.403949762,
    "G2 Spring_pr_1_99_del": 29,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 540
  },
  {
    "ID": 283,
    "KG Fall_theta_del": -1.605610724,
    "KG Fall_pr_1_99_del": 29,
    "KG Fall_ss_del": 92,
    "KG Fall_dss_del": 339,
    "KG Winter_theta_del": -1.392939139,
    "KG Winter_pr_1_99_del": 29,
    "KG Winter_ss_del": 91,
    "KG Winter_dss_del": 361,
    "KG Spring_theta_del": -1.002287638,
    "KG Spring_pr_1_99_del": 29,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 400,
    "G1 Fall_theta_del": -0.821697988,
    "G1 Fall_pr_1_99_del": 29,
    "G1 Fall_ss_del": 91,
    "G1 Fall_dss_del": 418,
    "G1 Winter_theta_del": -0.578926233,
    "G1 Winter_pr_1_99_del": 29,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 442,
    "G1 Spring_theta_del": -0.313719102,
    "G1 Spring_pr_1_99_del": 29,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 469,
    "G2 Fall_theta_del": -0.082663659,
    "G2 Fall_pr_1_99_del": 29,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 492,
    "G2 Winter_theta_del": 0.161143245,
    "G2 Winter_pr_1_99_del": 29,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 516,
    "G2 Spring_theta_del": 0.405493819,
    "G2 Spring_pr_1_99_del": 29,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 541
  },
  {
    "ID": 414,
    "KG Fall_theta_del": -1.597083492,
    "KG Fall_pr_1_99_del": 29,
    "KG Fall_ss_del": 92,
    "KG Fall_dss_del": 340,
    "KG Winter_theta_del": -1.38323643,
    "KG Winter_pr_1_99_del": 29,
    "KG Winter_ss_del": 91,
    "KG Winter_dss_del": 362,
    "KG Spring_theta_del": -0.999317393,
    "KG Spring_pr_1_99_del": 29,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 400,
    "G1 Fall_theta_del": -0.818301482,
    "G1 Fall_pr_1_99_del": 29,
    "G1 Fall_ss_del": 91,
    "G1 Fall_dss_del": 418,
    "G1 Winter_theta_del": -0.572390983,
    "G1 Winter_pr_1_99_del": 29,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 443,
    "G1 Spring_theta_del": -0.312442849,
    "G1 Spring_pr_1_99_del": 29,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 469,
    "G2 Fall_theta_del": -0.081757015,
    "G2 Fall_pr_1_99_del": 29,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 492,
    "G2 Winter_theta_del": 0.163120239,
    "G2 Winter_pr_1_99_del": 29,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 516,
    "G2 Spring_theta_del": 0.407159397,
    "G2 Spring_pr_1_99_del": 29,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 541
  },
  {
    "ID": 434,
    "KG Fall_theta_del": -1.58997835,
    "KG Fall_pr_1_99_del": 29,
    "KG Fall_ss_del": 92,
    "KG Fall_dss_del": 341,
    "KG Winter_theta_del": -1.370528697,
    "KG Winter_pr_1_99_del": 29,
    "KG Winter_ss_del": 91,
    "KG Winter_dss_del": 363,
    "KG Spring_theta_del": -0.997525502,
    "KG Spring_pr_1_99_del": 29,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 400,
    "G1 Fall_theta_del": -0.814459221,
    "G1 Fall_pr_1_99_del": 29,
    "G1 Fall_ss_del": 91,
    "G1 Fall_dss_del": 419,
    "G1 Winter_theta_del": -0.568119072,
    "G1 Winter_pr_1_99_del": 29,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 443,
    "G1 Spring_theta_del": -0.311480554,
    "G1 Spring_pr_1_99_del": 29,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 469,
    "G2 Fall_theta_del": -0.080868435,
    "G2 Fall_pr_1_99_del": 29,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 492,
    "G2 Winter_theta_del": 0.165054295,
    "G2 Winter_pr_1_99_del": 29,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 517,
    "G2 Spring_theta_del": 0.411762879,
    "G2 Spring_pr_1_99_del": 29,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 541
  },
  {
    "ID": 468,
    "KG Fall_theta_del": -1.589022979,
    "KG Fall_pr_1_99_del": 29,
    "KG Fall_ss_del": 92,
    "KG Fall_dss_del": 341,
    "KG Winter_theta_del": -1.370048376,
    "KG Winter_pr_1_99_del": 29,
    "KG Winter_ss_del": 91,
    "KG Winter_dss_del": 363,
    "KG Spring_theta_del": -0.997169367,
    "KG Spring_pr_1_99_del": 29,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 400,
    "G1 Fall_theta_del": -0.809817274,
    "G1 Fall_pr_1_99_del": 29,
    "G1 Fall_ss_del": 91,
    "G1 Fall_dss_del": 419,
    "G1 Winter_theta_del": -0.564306704,
    "G1 Winter_pr_1_99_del": 29,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 444,
    "G1 Spring_theta_del": -0.309724524,
    "G1 Spring_pr_1_99_del": 29,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 469,
    "G2 Fall_theta_del": -0.080786356,
    "G2 Fall_pr_1_99_del": 29,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 492,
    "G2 Winter_theta_del": 0.165113145,
    "G2 Winter_pr_1_99_del": 29,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 517,
    "G2 Spring_theta_del": 0.414776047,
    "G2 Spring_pr_1_99_del": 29,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 541
  },
  {
    "ID": 94,
    "KG Fall_theta_del": -1.563719487,
    "KG Fall_pr_1_99_del": 30,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 344,
    "KG Winter_theta_del": -1.362105373,
    "KG Winter_pr_1_99_del": 30,
    "KG Winter_ss_del": 91,
    "KG Winter_dss_del": 364,
    "KG Spring_theta_del": -0.991635505,
    "KG Spring_pr_1_99_del": 30,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 401,
    "G1 Fall_theta_del": -0.805044806,
    "G1 Fall_pr_1_99_del": 30,
    "G1 Fall_ss_del": 91,
    "G1 Fall_dss_del": 419,
    "G1 Winter_theta_del": -0.559745477,
    "G1 Winter_pr_1_99_del": 30,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 444,
    "G1 Spring_theta_del": -0.305796481,
    "G1 Spring_pr_1_99_del": 30,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 469,
    "G2 Fall_theta_del": -0.070911428,
    "G2 Fall_pr_1_99_del": 30,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 493,
    "G2 Winter_theta_del": 0.165766786,
    "G2 Winter_pr_1_99_del": 30,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 517,
    "G2 Spring_theta_del": 0.417881631,
    "G2 Spring_pr_1_99_del": 30,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 542
  },
  {
    "ID": 143,
    "KG Fall_theta_del": -1.555103743,
    "KG Fall_pr_1_99_del": 30,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 344,
    "KG Winter_theta_del": -1.356754558,
    "KG Winter_pr_1_99_del": 30,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 364,
    "KG Spring_theta_del": -0.990118782,
    "KG Spring_pr_1_99_del": 30,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 401,
    "G1 Fall_theta_del": -0.79758215,
    "G1 Fall_pr_1_99_del": 30,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 420,
    "G1 Winter_theta_del": -0.559347129,
    "G1 Winter_pr_1_99_del": 30,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 444,
    "G1 Spring_theta_del": -0.300179206,
    "G1 Spring_pr_1_99_del": 30,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 470,
    "G2 Fall_theta_del": -0.068205608,
    "G2 Fall_pr_1_99_del": 30,
    "G2 Fall_ss_del": 92,
    "G2 Fall_dss_del": 493,
    "G2 Winter_theta_del": 0.167438652,
    "G2 Winter_pr_1_99_del": 30,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 517,
    "G2 Spring_theta_del": 0.417889315,
    "G2 Spring_pr_1_99_del": 30,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 542
  },
  {
    "ID": 145,
    "KG Fall_theta_del": -1.554098814,
    "KG Fall_pr_1_99_del": 30,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 345,
    "KG Winter_theta_del": -1.355205411,
    "KG Winter_pr_1_99_del": 30,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 364,
    "KG Spring_theta_del": -0.986771003,
    "KG Spring_pr_1_99_del": 30,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 401,
    "G1 Fall_theta_del": -0.796981964,
    "G1 Fall_pr_1_99_del": 30,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 420,
    "G1 Winter_theta_del": -0.553867373,
    "G1 Winter_pr_1_99_del": 30,
    "G1 Winter_ss_del": 91,
    "G1 Winter_dss_del": 445,
    "G1 Spring_theta_del": -0.289614915,
    "G1 Spring_pr_1_99_del": 30,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 471,
    "G2 Fall_theta_del": -0.053429344,
    "G2 Fall_pr_1_99_del": 30,
    "G2 Fall_ss_del": 93,
    "G2 Fall_dss_del": 495,
    "G2 Winter_theta_del": 0.168907529,
    "G2 Winter_pr_1_99_del": 30,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 517,
    "G2 Spring_theta_del": 0.423278813,
    "G2 Spring_pr_1_99_del": 30,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 542
  },
  {
    "ID": 289,
    "KG Fall_theta_del": -1.551566383,
    "KG Fall_pr_1_99_del": 30,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 345,
    "KG Winter_theta_del": -1.354850081,
    "KG Winter_pr_1_99_del": 30,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 365,
    "KG Spring_theta_del": -0.982317369,
    "KG Spring_pr_1_99_del": 30,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 402,
    "G1 Fall_theta_del": -0.796208652,
    "G1 Fall_pr_1_99_del": 30,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 420,
    "G1 Winter_theta_del": -0.540369697,
    "G1 Winter_pr_1_99_del": 30,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 446,
    "G1 Spring_theta_del": -0.281793909,
    "G1 Spring_pr_1_99_del": 30,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 472,
    "G2 Fall_theta_del": -0.052563584,
    "G2 Fall_pr_1_99_del": 30,
    "G2 Fall_ss_del": 93,
    "G2 Fall_dss_del": 495,
    "G2 Winter_theta_del": 0.171523417,
    "G2 Winter_pr_1_99_del": 30,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 517,
    "G2 Spring_theta_del": 0.432772166,
    "G2 Spring_pr_1_99_del": 30,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 543
  },
  {
    "ID": 353,
    "KG Fall_theta_del": -1.550886171,
    "KG Fall_pr_1_99_del": 30,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 345,
    "KG Winter_theta_del": -1.344676517,
    "KG Winter_pr_1_99_del": 30,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 366,
    "KG Spring_theta_del": -0.975744062,
    "KG Spring_pr_1_99_del": 30,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 402,
    "G1 Fall_theta_del": -0.794828489,
    "G1 Fall_pr_1_99_del": 30,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 421,
    "G1 Winter_theta_del": -0.539590401,
    "G1 Winter_pr_1_99_del": 30,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 446,
    "G1 Spring_theta_del": -0.280937495,
    "G1 Spring_pr_1_99_del": 30,
    "G1 Spring_ss_del": 92,
    "G1 Spring_dss_del": 472,
    "G2 Fall_theta_del": -0.048851002,
    "G2 Fall_pr_1_99_del": 30,
    "G2 Fall_ss_del": 93,
    "G2 Fall_dss_del": 495,
    "G2 Winter_theta_del": 0.171580954,
    "G2 Winter_pr_1_99_del": 30,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 517,
    "G2 Spring_theta_del": 0.433020254,
    "G2 Spring_pr_1_99_del": 30,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 543
  },
  {
    "ID": 167,
    "KG Fall_theta_del": -1.547716733,
    "KG Fall_pr_1_99_del": 31,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 345,
    "KG Winter_theta_del": -1.329313019,
    "KG Winter_pr_1_99_del": 31,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 367,
    "KG Spring_theta_del": -0.974658603,
    "KG Spring_pr_1_99_del": 31,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 403,
    "G1 Fall_theta_del": -0.794552941,
    "G1 Fall_pr_1_99_del": 31,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 421,
    "G1 Winter_theta_del": -0.534143613,
    "G1 Winter_pr_1_99_del": 31,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 447,
    "G1 Spring_theta_del": -0.273537934,
    "G1 Spring_pr_1_99_del": 31,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 473,
    "G2 Fall_theta_del": -0.043872354,
    "G2 Fall_pr_1_99_del": 31,
    "G2 Fall_ss_del": 93,
    "G2 Fall_dss_del": 496,
    "G2 Winter_theta_del": 0.173078079,
    "G2 Winter_pr_1_99_del": 31,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 517,
    "G2 Spring_theta_del": 0.435061377,
    "G2 Spring_pr_1_99_del": 31,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 544
  },
  {
    "ID": 195,
    "KG Fall_theta_del": -1.542038862,
    "KG Fall_pr_1_99_del": 31,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 346,
    "KG Winter_theta_del": -1.322309156,
    "KG Winter_pr_1_99_del": 31,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 368,
    "KG Spring_theta_del": -0.971705487,
    "KG Spring_pr_1_99_del": 31,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 403,
    "G1 Fall_theta_del": -0.792811519,
    "G1 Fall_pr_1_99_del": 31,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 421,
    "G1 Winter_theta_del": -0.533959692,
    "G1 Winter_pr_1_99_del": 31,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 447,
    "G1 Spring_theta_del": -0.272417906,
    "G1 Spring_pr_1_99_del": 31,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 473,
    "G2 Fall_theta_del": -0.019203603,
    "G2 Fall_pr_1_99_del": 31,
    "G2 Fall_ss_del": 93,
    "G2 Fall_dss_del": 498,
    "G2 Winter_theta_del": 0.176289685,
    "G2 Winter_pr_1_99_del": 31,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 518,
    "G2 Spring_theta_del": 0.437614203,
    "G2 Spring_pr_1_99_del": 31,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 544
  },
  {
    "ID": 235,
    "KG Fall_theta_del": -1.541874323,
    "KG Fall_pr_1_99_del": 31,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 346,
    "KG Winter_theta_del": -1.322244163,
    "KG Winter_pr_1_99_del": 31,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 368,
    "KG Spring_theta_del": -0.96453792,
    "KG Spring_pr_1_99_del": 31,
    "KG Spring_ss_del": 92,
    "KG Spring_dss_del": 404,
    "G1 Fall_theta_del": -0.789834531,
    "G1 Fall_pr_1_99_del": 31,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 421,
    "G1 Winter_theta_del": -0.525861401,
    "G1 Winter_pr_1_99_del": 31,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 447,
    "G1 Spring_theta_del": -0.265737373,
    "G1 Spring_pr_1_99_del": 31,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 473,
    "G2 Fall_theta_del": -0.016894493,
    "G2 Fall_pr_1_99_del": 31,
    "G2 Fall_ss_del": 93,
    "G2 Fall_dss_del": 498,
    "G2 Winter_theta_del": 0.179378437,
    "G2 Winter_pr_1_99_del": 31,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 518,
    "G2 Spring_theta_del": 0.443596717,
    "G2 Spring_pr_1_99_del": 31,
    "G2 Spring_ss_del": 92,
    "G2 Spring_dss_del": 544
  },
  {
    "ID": 276,
    "KG Fall_theta_del": -1.539736814,
    "KG Fall_pr_1_99_del": 31,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 346,
    "KG Winter_theta_del": -1.320632327,
    "KG Winter_pr_1_99_del": 31,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 368,
    "KG Spring_theta_del": -0.957514699,
    "KG Spring_pr_1_99_del": 31,
    "KG Spring_ss_del": 93,
    "KG Spring_dss_del": 404,
    "G1 Fall_theta_del": -0.785824044,
    "G1 Fall_pr_1_99_del": 31,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 421,
    "G1 Winter_theta_del": -0.524434563,
    "G1 Winter_pr_1_99_del": 31,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 448,
    "G1 Spring_theta_del": -0.265628192,
    "G1 Spring_pr_1_99_del": 31,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 473,
    "G2 Fall_theta_del": -0.016238898,
    "G2 Fall_pr_1_99_del": 31,
    "G2 Fall_ss_del": 93,
    "G2 Fall_dss_del": 498,
    "G2 Winter_theta_del": 0.185469055,
    "G2 Winter_pr_1_99_del": 31,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 519,
    "G2 Spring_theta_del": 0.456357328,
    "G2 Spring_pr_1_99_del": 31,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 546
  },
  {
    "ID": 385,
    "KG Fall_theta_del": -1.534024372,
    "KG Fall_pr_1_99_del": 31,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 347,
    "KG Winter_theta_del": -1.315453971,
    "KG Winter_pr_1_99_del": 31,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 368,
    "KG Spring_theta_del": -0.950857056,
    "KG Spring_pr_1_99_del": 31,
    "KG Spring_ss_del": 93,
    "KG Spring_dss_del": 405,
    "G1 Fall_theta_del": -0.78273167,
    "G1 Fall_pr_1_99_del": 31,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 422,
    "G1 Winter_theta_del": -0.524284489,
    "G1 Winter_pr_1_99_del": 31,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 448,
    "G1 Spring_theta_del": -0.265383729,
    "G1 Spring_pr_1_99_del": 31,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 473,
    "G2 Fall_theta_del": -0.014427127,
    "G2 Fall_pr_1_99_del": 31,
    "G2 Fall_ss_del": 93,
    "G2 Fall_dss_del": 499,
    "G2 Winter_theta_del": 0.192738017,
    "G2 Winter_pr_1_99_del": 31,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 519,
    "G2 Spring_theta_del": 0.466876723,
    "G2 Spring_pr_1_99_del": 31,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 547
  },
  {
    "ID": 210,
    "KG Fall_theta_del": -1.533760951,
    "KG Fall_pr_1_99_del": 32,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 347,
    "KG Winter_theta_del": -1.314758567,
    "KG Winter_pr_1_99_del": 32,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 369,
    "KG Spring_theta_del": -0.940977487,
    "KG Spring_pr_1_99_del": 32,
    "KG Spring_ss_del": 93,
    "KG Spring_dss_del": 406,
    "G1 Fall_theta_del": -0.770209675,
    "G1 Fall_pr_1_99_del": 32,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 423,
    "G1 Winter_theta_del": -0.517094033,
    "G1 Winter_pr_1_99_del": 32,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 448,
    "G1 Spring_theta_del": -0.264363983,
    "G1 Spring_pr_1_99_del": 32,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 474,
    "G2 Fall_theta_del": -0.00124092,
    "G2 Fall_pr_1_99_del": 32,
    "G2 Fall_ss_del": 94,
    "G2 Fall_dss_del": 500,
    "G2 Winter_theta_del": 0.194355988,
    "G2 Winter_pr_1_99_del": 32,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 519,
    "G2 Spring_theta_del": 0.469926526,
    "G2 Spring_pr_1_99_del": 32,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 547
  },
  {
    "ID": 228,
    "KG Fall_theta_del": -1.533150663,
    "KG Fall_pr_1_99_del": 32,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 347,
    "KG Winter_theta_del": -1.306141959,
    "KG Winter_pr_1_99_del": 32,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 369,
    "KG Spring_theta_del": -0.938972807,
    "KG Spring_pr_1_99_del": 32,
    "KG Spring_ss_del": 93,
    "KG Spring_dss_del": 406,
    "G1 Fall_theta_del": -0.770205001,
    "G1 Fall_pr_1_99_del": 32,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 423,
    "G1 Winter_theta_del": -0.51220443,
    "G1 Winter_pr_1_99_del": 32,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 449,
    "G1 Spring_theta_del": -0.261393469,
    "G1 Spring_pr_1_99_del": 32,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 474,
    "G2 Fall_theta_del": 0.002044484,
    "G2 Fall_pr_1_99_del": 32,
    "G2 Fall_ss_del": 94,
    "G2 Fall_dss_del": 500,
    "G2 Winter_theta_del": 0.195807597,
    "G2 Winter_pr_1_99_del": 32,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 520,
    "G2 Spring_theta_del": 0.470725691,
    "G2 Spring_pr_1_99_del": 32,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 547
  },
  {
    "ID": 236,
    "KG Fall_theta_del": -1.527988428,
    "KG Fall_pr_1_99_del": 32,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 347,
    "KG Winter_theta_del": -1.3049649,
    "KG Winter_pr_1_99_del": 32,
    "KG Winter_ss_del": 92,
    "KG Winter_dss_del": 370,
    "KG Spring_theta_del": -0.933594464,
    "KG Spring_pr_1_99_del": 32,
    "KG Spring_ss_del": 93,
    "KG Spring_dss_del": 407,
    "G1 Fall_theta_del": -0.768253571,
    "G1 Fall_pr_1_99_del": 32,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 423,
    "G1 Winter_theta_del": -0.510704756,
    "G1 Winter_pr_1_99_del": 32,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 449,
    "G1 Spring_theta_del": -0.25479473,
    "G1 Spring_pr_1_99_del": 32,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 475,
    "G2 Fall_theta_del": 0.006887125,
    "G2 Fall_pr_1_99_del": 32,
    "G2 Fall_ss_del": 94,
    "G2 Fall_dss_del": 501,
    "G2 Winter_theta_del": 0.195910673,
    "G2 Winter_pr_1_99_del": 32,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 520,
    "G2 Spring_theta_del": 0.473625546,
    "G2 Spring_pr_1_99_del": 32,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 547
  },
  {
    "ID": 321,
    "KG Fall_theta_del": -1.523288169,
    "KG Fall_pr_1_99_del": 32,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 348,
    "KG Winter_theta_del": -1.300154514,
    "KG Winter_pr_1_99_del": 32,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 370,
    "KG Spring_theta_del": -0.921788268,
    "KG Spring_pr_1_99_del": 32,
    "KG Spring_ss_del": 93,
    "KG Spring_dss_del": 408,
    "G1 Fall_theta_del": -0.757030126,
    "G1 Fall_pr_1_99_del": 32,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 424,
    "G1 Winter_theta_del": -0.508763952,
    "G1 Winter_pr_1_99_del": 32,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 449,
    "G1 Spring_theta_del": -0.253329079,
    "G1 Spring_pr_1_99_del": 32,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 475,
    "G2 Fall_theta_del": 0.011445916,
    "G2 Fall_pr_1_99_del": 32,
    "G2 Fall_ss_del": 94,
    "G2 Fall_dss_del": 501,
    "G2 Winter_theta_del": 0.198192288,
    "G2 Winter_pr_1_99_del": 32,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 520,
    "G2 Spring_theta_del": 0.475404751,
    "G2 Spring_pr_1_99_del": 32,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 548
  },
  {
    "ID": 363,
    "KG Fall_theta_del": -1.522425497,
    "KG Fall_pr_1_99_del": 32,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 348,
    "KG Winter_theta_del": -1.296927411,
    "KG Winter_pr_1_99_del": 32,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 370,
    "KG Spring_theta_del": -0.911520602,
    "KG Spring_pr_1_99_del": 32,
    "KG Spring_ss_del": 93,
    "KG Spring_dss_del": 409,
    "G1 Fall_theta_del": -0.755744764,
    "G1 Fall_pr_1_99_del": 32,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 424,
    "G1 Winter_theta_del": -0.507140367,
    "G1 Winter_pr_1_99_del": 32,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 449,
    "G1 Spring_theta_del": -0.250360385,
    "G1 Spring_pr_1_99_del": 32,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 475,
    "G2 Fall_theta_del": 0.01371822,
    "G2 Fall_pr_1_99_del": 32,
    "G2 Fall_ss_del": 94,
    "G2 Fall_dss_del": 501,
    "G2 Winter_theta_del": 0.202959528,
    "G2 Winter_pr_1_99_del": 32,
    "G2 Winter_ss_del": 93,
    "G2 Winter_dss_del": 520,
    "G2 Spring_theta_del": 0.480297229,
    "G2 Spring_pr_1_99_del": 32,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 548
  },
  {
    "ID": 128,
    "KG Fall_theta_del": -1.514026042,
    "KG Fall_pr_1_99_del": 33,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 349,
    "KG Winter_theta_del": -1.296305186,
    "KG Winter_pr_1_99_del": 33,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 370,
    "KG Spring_theta_del": -0.90793455,
    "KG Spring_pr_1_99_del": 33,
    "KG Spring_ss_del": 93,
    "KG Spring_dss_del": 409,
    "G1 Fall_theta_del": -0.751984192,
    "G1 Fall_pr_1_99_del": 33,
    "G1 Fall_ss_del": 92,
    "G1 Fall_dss_del": 425,
    "G1 Winter_theta_del": -0.506581551,
    "G1 Winter_pr_1_99_del": 33,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 449,
    "G1 Spring_theta_del": -0.248722765,
    "G1 Spring_pr_1_99_del": 33,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 475,
    "G2 Fall_theta_del": 0.037742612,
    "G2 Fall_pr_1_99_del": 33,
    "G2 Fall_ss_del": 94,
    "G2 Fall_dss_del": 504,
    "G2 Winter_theta_del": 0.207580589,
    "G2 Winter_pr_1_99_del": 33,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 521,
    "G2 Spring_theta_del": 0.48170328,
    "G2 Spring_pr_1_99_del": 33,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 548
  },
  {
    "ID": 181,
    "KG Fall_theta_del": -1.512610762,
    "KG Fall_pr_1_99_del": 33,
    "KG Fall_ss_del": 93,
    "KG Fall_dss_del": 349,
    "KG Winter_theta_del": -1.291983011,
    "KG Winter_pr_1_99_del": 33,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 371,
    "KG Spring_theta_del": -0.903052785,
    "KG Spring_pr_1_99_del": 33,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 410,
    "G1 Fall_theta_del": -0.746035486,
    "G1 Fall_pr_1_99_del": 33,
    "G1 Fall_ss_del": 93,
    "G1 Fall_dss_del": 425,
    "G1 Winter_theta_del": -0.504040341,
    "G1 Winter_pr_1_99_del": 33,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 450,
    "G1 Spring_theta_del": -0.248686515,
    "G1 Spring_pr_1_99_del": 33,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 475,
    "G2 Fall_theta_del": 0.04491443,
    "G2 Fall_pr_1_99_del": 33,
    "G2 Fall_ss_del": 94,
    "G2 Fall_dss_del": 504,
    "G2 Winter_theta_del": 0.209017106,
    "G2 Winter_pr_1_99_del": 33,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 521,
    "G2 Spring_theta_del": 0.486976941,
    "G2 Spring_pr_1_99_del": 33,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 549
  },
  {
    "ID": 232,
    "KG Fall_theta_del": -1.509765016,
    "KG Fall_pr_1_99_del": 33,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 349,
    "KG Winter_theta_del": -1.287868174,
    "KG Winter_pr_1_99_del": 33,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 371,
    "KG Spring_theta_del": -0.900415148,
    "KG Spring_pr_1_99_del": 33,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 410,
    "G1 Fall_theta_del": -0.74241053,
    "G1 Fall_pr_1_99_del": 33,
    "G1 Fall_ss_del": 93,
    "G1 Fall_dss_del": 426,
    "G1 Winter_theta_del": -0.500473928,
    "G1 Winter_pr_1_99_del": 33,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 450,
    "G1 Spring_theta_del": -0.246888396,
    "G1 Spring_pr_1_99_del": 33,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 475,
    "G2 Fall_theta_del": 0.050861355,
    "G2 Fall_pr_1_99_del": 33,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 505,
    "G2 Winter_theta_del": 0.210863919,
    "G2 Winter_pr_1_99_del": 33,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 521,
    "G2 Spring_theta_del": 0.487297268,
    "G2 Spring_pr_1_99_del": 33,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 549
  },
  {
    "ID": 423,
    "KG Fall_theta_del": -1.508681927,
    "KG Fall_pr_1_99_del": 33,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 349,
    "KG Winter_theta_del": -1.287742725,
    "KG Winter_pr_1_99_del": 33,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 371,
    "KG Spring_theta_del": -0.896509135,
    "KG Spring_pr_1_99_del": 33,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 410,
    "G1 Fall_theta_del": -0.738780494,
    "G1 Fall_pr_1_99_del": 33,
    "G1 Fall_ss_del": 93,
    "G1 Fall_dss_del": 426,
    "G1 Winter_theta_del": -0.498723989,
    "G1 Winter_pr_1_99_del": 33,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 450,
    "G1 Spring_theta_del": -0.243179747,
    "G1 Spring_pr_1_99_del": 33,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 476,
    "G2 Fall_theta_del": 0.050863314,
    "G2 Fall_pr_1_99_del": 33,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 505,
    "G2 Winter_theta_del": 0.211595248,
    "G2 Winter_pr_1_99_del": 33,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 521,
    "G2 Spring_theta_del": 0.490120909,
    "G2 Spring_pr_1_99_del": 33,
    "G2 Spring_ss_del": 93,
    "G2 Spring_dss_del": 549
  },
  {
    "ID": 435,
    "KG Fall_theta_del": -1.504049205,
    "KG Fall_pr_1_99_del": 33,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 350,
    "KG Winter_theta_del": -1.279651999,
    "KG Winter_pr_1_99_del": 33,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 372,
    "KG Spring_theta_del": -0.89408132,
    "KG Spring_pr_1_99_del": 33,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 411,
    "G1 Fall_theta_del": -0.730634886,
    "G1 Fall_pr_1_99_del": 33,
    "G1 Fall_ss_del": 93,
    "G1 Fall_dss_del": 427,
    "G1 Winter_theta_del": -0.498259139,
    "G1 Winter_pr_1_99_del": 33,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 450,
    "G1 Spring_theta_del": -0.237776949,
    "G1 Spring_pr_1_99_del": 33,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 476,
    "G2 Fall_theta_del": 0.051794808,
    "G2 Fall_pr_1_99_del": 33,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 505,
    "G2 Winter_theta_del": 0.220099538,
    "G2 Winter_pr_1_99_del": 33,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 522,
    "G2 Spring_theta_del": 0.502231167,
    "G2 Spring_pr_1_99_del": 33,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 550
  },
  {
    "ID": 38,
    "KG Fall_theta_del": -1.500299883,
    "KG Fall_pr_1_99_del": 34,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 350,
    "KG Winter_theta_del": -1.274541712,
    "KG Winter_pr_1_99_del": 34,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 373,
    "KG Spring_theta_del": -0.892387865,
    "KG Spring_pr_1_99_del": 34,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 411,
    "G1 Fall_theta_del": -0.723948105,
    "G1 Fall_pr_1_99_del": 34,
    "G1 Fall_ss_del": 93,
    "G1 Fall_dss_del": 428,
    "G1 Winter_theta_del": -0.497099186,
    "G1 Winter_pr_1_99_del": 34,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 450,
    "G1 Spring_theta_del": -0.232007052,
    "G1 Spring_pr_1_99_del": 34,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 477,
    "G2 Fall_theta_del": 0.053088999,
    "G2 Fall_pr_1_99_del": 34,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 505,
    "G2 Winter_theta_del": 0.233945503,
    "G2 Winter_pr_1_99_del": 34,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 523,
    "G2 Spring_theta_del": 0.502516511,
    "G2 Spring_pr_1_99_del": 34,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 550
  },
  {
    "ID": 40,
    "KG Fall_theta_del": -1.495210487,
    "KG Fall_pr_1_99_del": 34,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 350,
    "KG Winter_theta_del": -1.273793304,
    "KG Winter_pr_1_99_del": 34,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 373,
    "KG Spring_theta_del": -0.890549463,
    "KG Spring_pr_1_99_del": 34,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 411,
    "G1 Fall_theta_del": -0.723832099,
    "G1 Fall_pr_1_99_del": 34,
    "G1 Fall_ss_del": 93,
    "G1 Fall_dss_del": 428,
    "G1 Winter_theta_del": -0.495219137,
    "G1 Winter_pr_1_99_del": 34,
    "G1 Winter_ss_del": 92,
    "G1 Winter_dss_del": 450,
    "G1 Spring_theta_del": -0.228460445,
    "G1 Spring_pr_1_99_del": 34,
    "G1 Spring_ss_del": 93,
    "G1 Spring_dss_del": 477,
    "G2 Fall_theta_del": 0.053721785,
    "G2 Fall_pr_1_99_del": 34,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 505,
    "G2 Winter_theta_del": 0.236433841,
    "G2 Winter_pr_1_99_del": 34,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 524,
    "G2 Spring_theta_del": 0.504102485,
    "G2 Spring_pr_1_99_del": 34,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 550
  },
  {
    "ID": 69,
    "KG Fall_theta_del": -1.492180339,
    "KG Fall_pr_1_99_del": 34,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 351,
    "KG Winter_theta_del": -1.269858093,
    "KG Winter_pr_1_99_del": 34,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 373,
    "KG Spring_theta_del": -0.8896386,
    "KG Spring_pr_1_99_del": 34,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 411,
    "G1 Fall_theta_del": -0.719192976,
    "G1 Fall_pr_1_99_del": 34,
    "G1 Fall_ss_del": 93,
    "G1 Fall_dss_del": 428,
    "G1 Winter_theta_del": -0.485205697,
    "G1 Winter_pr_1_99_del": 34,
    "G1 Winter_ss_del": 93,
    "G1 Winter_dss_del": 451,
    "G1 Spring_theta_del": -0.223831696,
    "G1 Spring_pr_1_99_del": 34,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 478,
    "G2 Fall_theta_del": 0.054107482,
    "G2 Fall_pr_1_99_del": 34,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 505,
    "G2 Winter_theta_del": 0.242632049,
    "G2 Winter_pr_1_99_del": 34,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 524,
    "G2 Spring_theta_del": 0.505366637,
    "G2 Spring_pr_1_99_del": 34,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 551
  },
  {
    "ID": 184,
    "KG Fall_theta_del": -1.483370738,
    "KG Fall_pr_1_99_del": 34,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 352,
    "KG Winter_theta_del": -1.259389968,
    "KG Winter_pr_1_99_del": 34,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 374,
    "KG Spring_theta_del": -0.888889972,
    "KG Spring_pr_1_99_del": 34,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 411,
    "G1 Fall_theta_del": -0.71770244,
    "G1 Fall_pr_1_99_del": 34,
    "G1 Fall_ss_del": 93,
    "G1 Fall_dss_del": 428,
    "G1 Winter_theta_del": -0.483246417,
    "G1 Winter_pr_1_99_del": 34,
    "G1 Winter_ss_del": 93,
    "G1 Winter_dss_del": 452,
    "G1 Spring_theta_del": -0.221832871,
    "G1 Spring_pr_1_99_del": 34,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 478,
    "G2 Fall_theta_del": 0.060450306,
    "G2 Fall_pr_1_99_del": 34,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 506,
    "G2 Winter_theta_del": 0.243570642,
    "G2 Winter_pr_1_99_del": 34,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 524,
    "G2 Spring_theta_del": 0.507710489,
    "G2 Spring_pr_1_99_del": 34,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 551
  },
  {
    "ID": 379,
    "KG Fall_theta_del": -1.479906945,
    "KG Fall_pr_1_99_del": 34,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 352,
    "KG Winter_theta_del": -1.252645282,
    "KG Winter_pr_1_99_del": 34,
    "KG Winter_ss_del": 93,
    "KG Winter_dss_del": 375,
    "KG Spring_theta_del": -0.881613379,
    "KG Spring_pr_1_99_del": 34,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 412,
    "G1 Fall_theta_del": -0.715515442,
    "G1 Fall_pr_1_99_del": 34,
    "G1 Fall_ss_del": 93,
    "G1 Fall_dss_del": 428,
    "G1 Winter_theta_del": -0.468104949,
    "G1 Winter_pr_1_99_del": 34,
    "G1 Winter_ss_del": 93,
    "G1 Winter_dss_del": 453,
    "G1 Spring_theta_del": -0.22170004,
    "G1 Spring_pr_1_99_del": 34,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 478,
    "G2 Fall_theta_del": 0.065675997,
    "G2 Fall_pr_1_99_del": 34,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 507,
    "G2 Winter_theta_del": 0.252210574,
    "G2 Winter_pr_1_99_del": 34,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 525,
    "G2 Spring_theta_del": 0.520405985,
    "G2 Spring_pr_1_99_del": 34,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 552
  },
  {
    "ID": 48,
    "KG Fall_theta_del": -1.468850658,
    "KG Fall_pr_1_99_del": 35,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 353,
    "KG Winter_theta_del": -1.245813486,
    "KG Winter_pr_1_99_del": 35,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 375,
    "KG Spring_theta_del": -0.878090317,
    "KG Spring_pr_1_99_del": 35,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 412,
    "G1 Fall_theta_del": -0.701499092,
    "G1 Fall_pr_1_99_del": 35,
    "G1 Fall_ss_del": 93,
    "G1 Fall_dss_del": 430,
    "G1 Winter_theta_del": -0.460081432,
    "G1 Winter_pr_1_99_del": 35,
    "G1 Winter_ss_del": 93,
    "G1 Winter_dss_del": 454,
    "G1 Spring_theta_del": -0.21695619,
    "G1 Spring_pr_1_99_del": 35,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 478,
    "G2 Fall_theta_del": 0.070197197,
    "G2 Fall_pr_1_99_del": 35,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 507,
    "G2 Winter_theta_del": 0.25835453,
    "G2 Winter_pr_1_99_del": 35,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 526,
    "G2 Spring_theta_del": 0.525527761,
    "G2 Spring_pr_1_99_del": 35,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 553
  },
  {
    "ID": 187,
    "KG Fall_theta_del": -1.460355395,
    "KG Fall_pr_1_99_del": 35,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 354,
    "KG Winter_theta_del": -1.239722798,
    "KG Winter_pr_1_99_del": 35,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 376,
    "KG Spring_theta_del": -0.873250199,
    "KG Spring_pr_1_99_del": 35,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 413,
    "G1 Fall_theta_del": -0.693030233,
    "G1 Fall_pr_1_99_del": 35,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 431,
    "G1 Winter_theta_del": -0.454380428,
    "G1 Winter_pr_1_99_del": 35,
    "G1 Winter_ss_del": 93,
    "G1 Winter_dss_del": 455,
    "G1 Spring_theta_del": -0.209774059,
    "G1 Spring_pr_1_99_del": 35,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 479,
    "G2 Fall_theta_del": 0.070708505,
    "G2 Fall_pr_1_99_del": 35,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 507,
    "G2 Winter_theta_del": 0.258496615,
    "G2 Winter_pr_1_99_del": 35,
    "G2 Winter_ss_del": 94,
    "G2 Winter_dss_del": 526,
    "G2 Spring_theta_del": 0.526305822,
    "G2 Spring_pr_1_99_del": 35,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 553
  },
  {
    "ID": 272,
    "KG Fall_theta_del": -1.457101787,
    "KG Fall_pr_1_99_del": 35,
    "KG Fall_ss_del": 94,
    "KG Fall_dss_del": 354,
    "KG Winter_theta_del": -1.236604175,
    "KG Winter_pr_1_99_del": 35,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 376,
    "KG Spring_theta_del": -0.864292123,
    "KG Spring_pr_1_99_del": 35,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 414,
    "G1 Fall_theta_del": -0.68411095,
    "G1 Fall_pr_1_99_del": 35,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 432,
    "G1 Winter_theta_del": -0.450746588,
    "G1 Winter_pr_1_99_del": 35,
    "G1 Winter_ss_del": 93,
    "G1 Winter_dss_del": 455,
    "G1 Spring_theta_del": -0.203156308,
    "G1 Spring_pr_1_99_del": 35,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 480,
    "G2 Fall_theta_del": 0.073402775,
    "G2 Fall_pr_1_99_del": 35,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 507,
    "G2 Winter_theta_del": 0.263077068,
    "G2 Winter_pr_1_99_del": 35,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 526,
    "G2 Spring_theta_del": 0.526459677,
    "G2 Spring_pr_1_99_del": 35,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 553
  },
  {
    "ID": 285,
    "KG Fall_theta_del": -1.450323669,
    "KG Fall_pr_1_99_del": 35,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 355,
    "KG Winter_theta_del": -1.23607209,
    "KG Winter_pr_1_99_del": 35,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 376,
    "KG Spring_theta_del": -0.857251591,
    "KG Spring_pr_1_99_del": 35,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 414,
    "G1 Fall_theta_del": -0.682585529,
    "G1 Fall_pr_1_99_del": 35,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 432,
    "G1 Winter_theta_del": -0.448973219,
    "G1 Winter_pr_1_99_del": 35,
    "G1 Winter_ss_del": 93,
    "G1 Winter_dss_del": 455,
    "G1 Spring_theta_del": -0.19699458,
    "G1 Spring_pr_1_99_del": 35,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 480,
    "G2 Fall_theta_del": 0.075477458,
    "G2 Fall_pr_1_99_del": 35,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 508,
    "G2 Winter_theta_del": 0.263846826,
    "G2 Winter_pr_1_99_del": 35,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 526,
    "G2 Spring_theta_del": 0.527050291,
    "G2 Spring_pr_1_99_del": 35,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 553
  },
  {
    "ID": 373,
    "KG Fall_theta_del": -1.447197927,
    "KG Fall_pr_1_99_del": 35,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 355,
    "KG Winter_theta_del": -1.231712368,
    "KG Winter_pr_1_99_del": 35,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 377,
    "KG Spring_theta_del": -0.851475778,
    "KG Spring_pr_1_99_del": 35,
    "KG Spring_ss_del": 94,
    "KG Spring_dss_del": 415,
    "G1 Fall_theta_del": -0.68034652,
    "G1 Fall_pr_1_99_del": 35,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 432,
    "G1 Winter_theta_del": -0.446855242,
    "G1 Winter_pr_1_99_del": 35,
    "G1 Winter_ss_del": 93,
    "G1 Winter_dss_del": 455,
    "G1 Spring_theta_del": -0.19611804,
    "G1 Spring_pr_1_99_del": 35,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 480,
    "G2 Fall_theta_del": 0.08590705,
    "G2 Fall_pr_1_99_del": 35,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 509,
    "G2 Winter_theta_del": 0.267144669,
    "G2 Winter_pr_1_99_del": 35,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 527,
    "G2 Spring_theta_del": 0.529109921,
    "G2 Spring_pr_1_99_del": 35,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 553
  },
  {
    "ID": 67,
    "KG Fall_theta_del": -1.445836669,
    "KG Fall_pr_1_99_del": 36,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 355,
    "KG Winter_theta_del": -1.228165673,
    "KG Winter_pr_1_99_del": 36,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 377,
    "KG Spring_theta_del": -0.832339699,
    "KG Spring_pr_1_99_del": 36,
    "KG Spring_ss_del": 95,
    "KG Spring_dss_del": 417,
    "G1 Fall_theta_del": -0.67722434,
    "G1 Fall_pr_1_99_del": 36,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 432,
    "G1 Winter_theta_del": -0.435279004,
    "G1 Winter_pr_1_99_del": 36,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 456,
    "G1 Spring_theta_del": -0.191666186,
    "G1 Spring_pr_1_99_del": 36,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 481,
    "G2 Fall_theta_del": 0.094828808,
    "G2 Fall_pr_1_99_del": 36,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 509,
    "G2 Winter_theta_del": 0.270242673,
    "G2 Winter_pr_1_99_del": 36,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 527,
    "G2 Spring_theta_del": 0.532297029,
    "G2 Spring_pr_1_99_del": 36,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 553
  },
  {
    "ID": 115,
    "KG Fall_theta_del": -1.445099224,
    "KG Fall_pr_1_99_del": 36,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 355,
    "KG Winter_theta_del": -1.212039087,
    "KG Winter_pr_1_99_del": 36,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 379,
    "KG Spring_theta_del": -0.826474577,
    "KG Spring_pr_1_99_del": 36,
    "KG Spring_ss_del": 95,
    "KG Spring_dss_del": 417,
    "G1 Fall_theta_del": -0.671681274,
    "G1 Fall_pr_1_99_del": 36,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 433,
    "G1 Winter_theta_del": -0.433733915,
    "G1 Winter_pr_1_99_del": 36,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 457,
    "G1 Spring_theta_del": -0.186838542,
    "G1 Spring_pr_1_99_del": 36,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 481,
    "G2 Fall_theta_del": 0.098758098,
    "G2 Fall_pr_1_99_del": 36,
    "G2 Fall_ss_del": 95,
    "G2 Fall_dss_del": 510,
    "G2 Winter_theta_del": 0.273625493,
    "G2 Winter_pr_1_99_del": 36,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 527,
    "G2 Spring_theta_del": 0.537006783,
    "G2 Spring_pr_1_99_del": 36,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 554
  },
  {
    "ID": 204,
    "KG Fall_theta_del": -1.443375624,
    "KG Fall_pr_1_99_del": 36,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 356,
    "KG Winter_theta_del": -1.210630354,
    "KG Winter_pr_1_99_del": 36,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 379,
    "KG Spring_theta_del": -0.821172496,
    "KG Spring_pr_1_99_del": 36,
    "KG Spring_ss_del": 95,
    "KG Spring_dss_del": 418,
    "G1 Fall_theta_del": -0.669262184,
    "G1 Fall_pr_1_99_del": 36,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 433,
    "G1 Winter_theta_del": -0.428505616,
    "G1 Winter_pr_1_99_del": 36,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 457,
    "G1 Spring_theta_del": -0.18612883,
    "G1 Spring_pr_1_99_del": 36,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 481,
    "G2 Fall_theta_del": 0.104956466,
    "G2 Fall_pr_1_99_del": 36,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 510,
    "G2 Winter_theta_del": 0.275889683,
    "G2 Winter_pr_1_99_del": 36,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 528,
    "G2 Spring_theta_del": 0.537119817,
    "G2 Spring_pr_1_99_del": 36,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 554
  },
  {
    "ID": 378,
    "KG Fall_theta_del": -1.432846887,
    "KG Fall_pr_1_99_del": 36,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 357,
    "KG Winter_theta_del": -1.209630604,
    "KG Winter_pr_1_99_del": 36,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 379,
    "KG Spring_theta_del": -0.819863512,
    "KG Spring_pr_1_99_del": 36,
    "KG Spring_ss_del": 95,
    "KG Spring_dss_del": 418,
    "G1 Fall_theta_del": -0.663559886,
    "G1 Fall_pr_1_99_del": 36,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 434,
    "G1 Winter_theta_del": -0.42782283,
    "G1 Winter_pr_1_99_del": 36,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 457,
    "G1 Spring_theta_del": -0.185912138,
    "G1 Spring_pr_1_99_del": 36,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 481,
    "G2 Fall_theta_del": 0.114508862,
    "G2 Fall_pr_1_99_del": 36,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 511,
    "G2 Winter_theta_del": 0.280471194,
    "G2 Winter_pr_1_99_del": 36,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 528,
    "G2 Spring_theta_del": 0.537185707,
    "G2 Spring_pr_1_99_del": 36,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 554
  },
  {
    "ID": 441,
    "KG Fall_theta_del": -1.428673425,
    "KG Fall_pr_1_99_del": 36,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 357,
    "KG Winter_theta_del": -1.205853282,
    "KG Winter_pr_1_99_del": 36,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 379,
    "KG Spring_theta_del": -0.818667803,
    "KG Spring_pr_1_99_del": 36,
    "KG Spring_ss_del": 95,
    "KG Spring_dss_del": 418,
    "G1 Fall_theta_del": -0.66061637,
    "G1 Fall_pr_1_99_del": 36,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 434,
    "G1 Winter_theta_del": -0.426997253,
    "G1 Winter_pr_1_99_del": 36,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 457,
    "G1 Spring_theta_del": -0.183391689,
    "G1 Spring_pr_1_99_del": 36,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 482,
    "G2 Fall_theta_del": 0.115411854,
    "G2 Fall_pr_1_99_del": 36,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 512,
    "G2 Winter_theta_del": 0.285022607,
    "G2 Winter_pr_1_99_del": 36,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 529,
    "G2 Spring_theta_del": 0.537941747,
    "G2 Spring_pr_1_99_del": 36,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 554
  },
  {
    "ID": 15,
    "KG Fall_theta_del": -1.427358263,
    "KG Fall_pr_1_99_del": 37,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 357,
    "KG Winter_theta_del": -1.199813057,
    "KG Winter_pr_1_99_del": 37,
    "KG Winter_ss_del": 94,
    "KG Winter_dss_del": 380,
    "KG Spring_theta_del": -0.815158008,
    "KG Spring_pr_1_99_del": 37,
    "KG Spring_ss_del": 95,
    "KG Spring_dss_del": 418,
    "G1 Fall_theta_del": -0.651620665,
    "G1 Fall_pr_1_99_del": 37,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 435,
    "G1 Winter_theta_del": -0.416810008,
    "G1 Winter_pr_1_99_del": 37,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 458,
    "G1 Spring_theta_del": -0.182394464,
    "G1 Spring_pr_1_99_del": 37,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 482,
    "G2 Fall_theta_del": 0.117513558,
    "G2 Fall_pr_1_99_del": 37,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 512,
    "G2 Winter_theta_del": 0.286011364,
    "G2 Winter_pr_1_99_del": 37,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 529,
    "G2 Spring_theta_del": 0.538080471,
    "G2 Spring_pr_1_99_del": 37,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 554
  },
  {
    "ID": 23,
    "KG Fall_theta_del": -1.426098671,
    "KG Fall_pr_1_99_del": 37,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 357,
    "KG Winter_theta_del": -1.183990773,
    "KG Winter_pr_1_99_del": 37,
    "KG Winter_ss_del": 95,
    "KG Winter_dss_del": 382,
    "KG Spring_theta_del": -0.804703535,
    "KG Spring_pr_1_99_del": 37,
    "KG Spring_ss_del": 95,
    "KG Spring_dss_del": 420,
    "G1 Fall_theta_del": -0.65127287,
    "G1 Fall_pr_1_99_del": 37,
    "G1 Fall_ss_del": 94,
    "G1 Fall_dss_del": 435,
    "G1 Winter_theta_del": -0.414324319,
    "G1 Winter_pr_1_99_del": 37,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 459,
    "G1 Spring_theta_del": -0.179086362,
    "G1 Spring_pr_1_99_del": 37,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 482,
    "G2 Fall_theta_del": 0.118258145,
    "G2 Fall_pr_1_99_del": 37,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 512,
    "G2 Winter_theta_del": 0.289246146,
    "G2 Winter_pr_1_99_del": 37,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 529,
    "G2 Spring_theta_del": 0.539951551,
    "G2 Spring_pr_1_99_del": 37,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 554
  },
  {
    "ID": 110,
    "KG Fall_theta_del": -1.425307731,
    "KG Fall_pr_1_99_del": 37,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 357,
    "KG Winter_theta_del": -1.180785573,
    "KG Winter_pr_1_99_del": 37,
    "KG Winter_ss_del": 95,
    "KG Winter_dss_del": 382,
    "KG Spring_theta_del": -0.800771415,
    "KG Spring_pr_1_99_del": 37,
    "KG Spring_ss_del": 95,
    "KG Spring_dss_del": 420,
    "G1 Fall_theta_del": -0.640405828,
    "G1 Fall_pr_1_99_del": 37,
    "G1 Fall_ss_del": 95,
    "G1 Fall_dss_del": 436,
    "G1 Winter_theta_del": -0.412010817,
    "G1 Winter_pr_1_99_del": 37,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 459,
    "G1 Spring_theta_del": -0.177757166,
    "G1 Spring_pr_1_99_del": 37,
    "G1 Spring_ss_del": 94,
    "G1 Spring_dss_del": 482,
    "G2 Fall_theta_del": 0.119374974,
    "G2 Fall_pr_1_99_del": 37,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 512,
    "G2 Winter_theta_del": 0.290737138,
    "G2 Winter_pr_1_99_del": 37,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 529,
    "G2 Spring_theta_del": 0.546053365,
    "G2 Spring_pr_1_99_del": 37,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 555
  },
  {
    "ID": 211,
    "KG Fall_theta_del": -1.423880285,
    "KG Fall_pr_1_99_del": 37,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 358,
    "KG Winter_theta_del": -1.180029952,
    "KG Winter_pr_1_99_del": 37,
    "KG Winter_ss_del": 95,
    "KG Winter_dss_del": 382,
    "KG Spring_theta_del": -0.790534213,
    "KG Spring_pr_1_99_del": 37,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 421,
    "G1 Fall_theta_del": -0.63899887,
    "G1 Fall_pr_1_99_del": 37,
    "G1 Fall_ss_del": 95,
    "G1 Fall_dss_del": 436,
    "G1 Winter_theta_del": -0.411859061,
    "G1 Winter_pr_1_99_del": 37,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 459,
    "G1 Spring_theta_del": -0.170582102,
    "G1 Spring_pr_1_99_del": 37,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 483,
    "G2 Fall_theta_del": 0.119453266,
    "G2 Fall_pr_1_99_del": 37,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 512,
    "G2 Winter_theta_del": 0.298822474,
    "G2 Winter_pr_1_99_del": 37,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 530,
    "G2 Spring_theta_del": 0.546099001,
    "G2 Spring_pr_1_99_del": 37,
    "G2 Spring_ss_del": 94,
    "G2 Spring_dss_del": 555
  },
  {
    "ID": 310,
    "KG Fall_theta_del": -1.418669696,
    "KG Fall_pr_1_99_del": 37,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 358,
    "KG Winter_theta_del": -1.17743038,
    "KG Winter_pr_1_99_del": 37,
    "KG Winter_ss_del": 95,
    "KG Winter_dss_del": 382,
    "KG Spring_theta_del": -0.786406541,
    "KG Spring_pr_1_99_del": 37,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 421,
    "G1 Fall_theta_del": -0.638415972,
    "G1 Fall_pr_1_99_del": 37,
    "G1 Fall_ss_del": 95,
    "G1 Fall_dss_del": 436,
    "G1 Winter_theta_del": -0.405511061,
    "G1 Winter_pr_1_99_del": 37,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 459,
    "G1 Spring_theta_del": -0.169669714,
    "G1 Spring_pr_1_99_del": 37,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 483,
    "G2 Fall_theta_del": 0.125899079,
    "G2 Fall_pr_1_99_del": 37,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 513,
    "G2 Winter_theta_del": 0.306223436,
    "G2 Winter_pr_1_99_del": 37,
    "G2 Winter_ss_del": 95,
    "G2 Winter_dss_del": 531,
    "G2 Spring_theta_del": 0.555848205,
    "G2 Spring_pr_1_99_del": 37,
    "G2 Spring_ss_del": 95,
    "G2 Spring_dss_del": 556
  },
  {
    "ID": 6,
    "KG Fall_theta_del": -1.414843893,
    "KG Fall_pr_1_99_del": 38,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 359,
    "KG Winter_theta_del": -1.166156523,
    "KG Winter_pr_1_99_del": 38,
    "KG Winter_ss_del": 95,
    "KG Winter_dss_del": 383,
    "KG Spring_theta_del": -0.780857791,
    "KG Spring_pr_1_99_del": 38,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 422,
    "G1 Fall_theta_del": -0.637851826,
    "G1 Fall_pr_1_99_del": 38,
    "G1 Fall_ss_del": 95,
    "G1 Fall_dss_del": 436,
    "G1 Winter_theta_del": -0.402562137,
    "G1 Winter_pr_1_99_del": 38,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 460,
    "G1 Spring_theta_del": -0.16835903,
    "G1 Spring_pr_1_99_del": 38,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 483,
    "G2 Fall_theta_del": 0.129196346,
    "G2 Fall_pr_1_99_del": 38,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 513,
    "G2 Winter_theta_del": 0.31662699,
    "G2 Winter_pr_1_99_del": 38,
    "G2 Winter_ss_del": 96,
    "G2 Winter_dss_del": 532,
    "G2 Spring_theta_del": 0.558821294,
    "G2 Spring_pr_1_99_del": 38,
    "G2 Spring_ss_del": 95,
    "G2 Spring_dss_del": 556
  },
  {
    "ID": 59,
    "KG Fall_theta_del": -1.409361482,
    "KG Fall_pr_1_99_del": 38,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 359,
    "KG Winter_theta_del": -1.161930307,
    "KG Winter_pr_1_99_del": 38,
    "KG Winter_ss_del": 95,
    "KG Winter_dss_del": 384,
    "KG Spring_theta_del": -0.780288558,
    "KG Spring_pr_1_99_del": 38,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 422,
    "G1 Fall_theta_del": -0.636665725,
    "G1 Fall_pr_1_99_del": 38,
    "G1 Fall_ss_del": 95,
    "G1 Fall_dss_del": 436,
    "G1 Winter_theta_del": -0.399132014,
    "G1 Winter_pr_1_99_del": 38,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 460,
    "G1 Spring_theta_del": -0.16733891,
    "G1 Spring_pr_1_99_del": 38,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 483,
    "G2 Fall_theta_del": 0.132218833,
    "G2 Fall_pr_1_99_del": 38,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 513,
    "G2 Winter_theta_del": 0.317829919,
    "G2 Winter_pr_1_99_del": 38,
    "G2 Winter_ss_del": 96,
    "G2 Winter_dss_del": 532,
    "G2 Spring_theta_del": 0.570699226,
    "G2 Spring_pr_1_99_del": 38,
    "G2 Spring_ss_del": 95,
    "G2 Spring_dss_del": 557
  },
  {
    "ID": 213,
    "KG Fall_theta_del": -1.409113616,
    "KG Fall_pr_1_99_del": 38,
    "KG Fall_ss_del": 95,
    "KG Fall_dss_del": 359,
    "KG Winter_theta_del": -1.159977629,
    "KG Winter_pr_1_99_del": 38,
    "KG Winter_ss_del": 95,
    "KG Winter_dss_del": 384,
    "KG Spring_theta_del": -0.779599524,
    "KG Spring_pr_1_99_del": 38,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 422,
    "G1 Fall_theta_del": -0.625928526,
    "G1 Fall_pr_1_99_del": 38,
    "G1 Fall_ss_del": 95,
    "G1 Fall_dss_del": 437,
    "G1 Winter_theta_del": -0.396647814,
    "G1 Winter_pr_1_99_del": 38,
    "G1 Winter_ss_del": 94,
    "G1 Winter_dss_del": 460,
    "G1 Spring_theta_del": -0.158101895,
    "G1 Spring_pr_1_99_del": 38,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 484,
    "G2 Fall_theta_del": 0.13337157,
    "G2 Fall_pr_1_99_del": 38,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 513,
    "G2 Winter_theta_del": 0.318864161,
    "G2 Winter_pr_1_99_del": 38,
    "G2 Winter_ss_del": 96,
    "G2 Winter_dss_del": 532,
    "G2 Spring_theta_del": 0.573219465,
    "G2 Spring_pr_1_99_del": 38,
    "G2 Spring_ss_del": 95,
    "G2 Spring_dss_del": 557
  },
  {
    "ID": 352,
    "KG Fall_theta_del": -1.401447004,
    "KG Fall_pr_1_99_del": 38,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 360,
    "KG Winter_theta_del": -1.158105986,
    "KG Winter_pr_1_99_del": 38,
    "KG Winter_ss_del": 95,
    "KG Winter_dss_del": 384,
    "KG Spring_theta_del": -0.77710233,
    "KG Spring_pr_1_99_del": 38,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 422,
    "G1 Fall_theta_del": -0.625112904,
    "G1 Fall_pr_1_99_del": 38,
    "G1 Fall_ss_del": 95,
    "G1 Fall_dss_del": 437,
    "G1 Winter_theta_del": -0.394084049,
    "G1 Winter_pr_1_99_del": 38,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 461,
    "G1 Spring_theta_del": -0.153490133,
    "G1 Spring_pr_1_99_del": 38,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 485,
    "G2 Fall_theta_del": 0.134261577,
    "G2 Fall_pr_1_99_del": 38,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 513,
    "G2 Winter_theta_del": 0.32110351,
    "G2 Winter_pr_1_99_del": 38,
    "G2 Winter_ss_del": 96,
    "G2 Winter_dss_del": 532,
    "G2 Spring_theta_del": 0.586271723,
    "G2 Spring_pr_1_99_del": 38,
    "G2 Spring_ss_del": 95,
    "G2 Spring_dss_del": 559
  },
  {
    "ID": 487,
    "KG Fall_theta_del": -1.395150429,
    "KG Fall_pr_1_99_del": 38,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 360,
    "KG Winter_theta_del": -1.129430546,
    "KG Winter_pr_1_99_del": 38,
    "KG Winter_ss_del": 96,
    "KG Winter_dss_del": 387,
    "KG Spring_theta_del": -0.776147396,
    "KG Spring_pr_1_99_del": 38,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 422,
    "G1 Fall_theta_del": -0.624741685,
    "G1 Fall_pr_1_99_del": 38,
    "G1 Fall_ss_del": 95,
    "G1 Fall_dss_del": 438,
    "G1 Winter_theta_del": -0.388341333,
    "G1 Winter_pr_1_99_del": 38,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 461,
    "G1 Spring_theta_del": -0.141909372,
    "G1 Spring_pr_1_99_del": 38,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 486,
    "G2 Fall_theta_del": 0.14184586,
    "G2 Fall_pr_1_99_del": 38,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 514,
    "G2 Winter_theta_del": 0.321741216,
    "G2 Winter_pr_1_99_del": 38,
    "G2 Winter_ss_del": 96,
    "G2 Winter_dss_del": 532,
    "G2 Spring_theta_del": 0.587984995,
    "G2 Spring_pr_1_99_del": 38,
    "G2 Spring_ss_del": 95,
    "G2 Spring_dss_del": 559
  },
  {
    "ID": 1,
    "KG Fall_theta_del": -1.393990597,
    "KG Fall_pr_1_99_del": 39,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 361,
    "KG Winter_theta_del": -1.124408732,
    "KG Winter_pr_1_99_del": 39,
    "KG Winter_ss_del": 96,
    "KG Winter_dss_del": 388,
    "KG Spring_theta_del": -0.766146055,
    "KG Spring_pr_1_99_del": 39,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 423,
    "G1 Fall_theta_del": -0.603774443,
    "G1 Fall_pr_1_99_del": 39,
    "G1 Fall_ss_del": 95,
    "G1 Fall_dss_del": 440,
    "G1 Winter_theta_del": -0.385086242,
    "G1 Winter_pr_1_99_del": 39,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 461,
    "G1 Spring_theta_del": -0.140916378,
    "G1 Spring_pr_1_99_del": 39,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 486,
    "G2 Fall_theta_del": 0.142604425,
    "G2 Fall_pr_1_99_del": 39,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 514,
    "G2 Winter_theta_del": 0.32390074,
    "G2 Winter_pr_1_99_del": 39,
    "G2 Winter_ss_del": 96,
    "G2 Winter_dss_del": 532,
    "G2 Spring_theta_del": 0.590243401,
    "G2 Spring_pr_1_99_del": 39,
    "G2 Spring_ss_del": 95,
    "G2 Spring_dss_del": 559
  },
  {
    "ID": 19,
    "KG Fall_theta_del": -1.393883554,
    "KG Fall_pr_1_99_del": 39,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 361,
    "KG Winter_theta_del": -1.117236446,
    "KG Winter_pr_1_99_del": 39,
    "KG Winter_ss_del": 96,
    "KG Winter_dss_del": 388,
    "KG Spring_theta_del": -0.758111031,
    "KG Spring_pr_1_99_del": 39,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 424,
    "G1 Fall_theta_del": -0.600798353,
    "G1 Fall_pr_1_99_del": 39,
    "G1 Fall_ss_del": 95,
    "G1 Fall_dss_del": 440,
    "G1 Winter_theta_del": -0.384719626,
    "G1 Winter_pr_1_99_del": 39,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 462,
    "G1 Spring_theta_del": -0.13813212,
    "G1 Spring_pr_1_99_del": 39,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 486,
    "G2 Fall_theta_del": 0.142639846,
    "G2 Fall_pr_1_99_del": 39,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 514,
    "G2 Winter_theta_del": 0.329751313,
    "G2 Winter_pr_1_99_del": 39,
    "G2 Winter_ss_del": 96,
    "G2 Winter_dss_del": 533,
    "G2 Spring_theta_del": 0.590458334,
    "G2 Spring_pr_1_99_del": 39,
    "G2 Spring_ss_del": 95,
    "G2 Spring_dss_del": 559
  },
  {
    "ID": 303,
    "KG Fall_theta_del": -1.393621661,
    "KG Fall_pr_1_99_del": 39,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 361,
    "KG Winter_theta_del": -1.104420935,
    "KG Winter_pr_1_99_del": 39,
    "KG Winter_ss_del": 96,
    "KG Winter_dss_del": 390,
    "KG Spring_theta_del": -0.749405599,
    "KG Spring_pr_1_99_del": 39,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 425,
    "G1 Fall_theta_del": -0.570826216,
    "G1 Fall_pr_1_99_del": 39,
    "G1 Fall_ss_del": 96,
    "G1 Fall_dss_del": 443,
    "G1 Winter_theta_del": -0.378406768,
    "G1 Winter_pr_1_99_del": 39,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 462,
    "G1 Spring_theta_del": -0.138092222,
    "G1 Spring_pr_1_99_del": 39,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 486,
    "G2 Fall_theta_del": 0.149483845,
    "G2 Fall_pr_1_99_del": 39,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 515,
    "G2 Winter_theta_del": 0.337098833,
    "G2 Winter_pr_1_99_del": 39,
    "G2 Winter_ss_del": 96,
    "G2 Winter_dss_del": 534,
    "G2 Spring_theta_del": 0.591378313,
    "G2 Spring_pr_1_99_del": 39,
    "G2 Spring_ss_del": 95,
    "G2 Spring_dss_del": 559
  },
  {
    "ID": 314,
    "KG Fall_theta_del": -1.386604182,
    "KG Fall_pr_1_99_del": 39,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 361,
    "KG Winter_theta_del": -1.104210974,
    "KG Winter_pr_1_99_del": 39,
    "KG Winter_ss_del": 96,
    "KG Winter_dss_del": 390,
    "KG Spring_theta_del": -0.743644741,
    "KG Spring_pr_1_99_del": 39,
    "KG Spring_ss_del": 96,
    "KG Spring_dss_del": 426,
    "G1 Fall_theta_del": -0.567505451,
    "G1 Fall_pr_1_99_del": 39,
    "G1 Fall_ss_del": 96,
    "G1 Fall_dss_del": 443,
    "G1 Winter_theta_del": -0.37765538,
    "G1 Winter_pr_1_99_del": 39,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 462,
    "G1 Spring_theta_del": -0.12837193,
    "G1 Spring_pr_1_99_del": 39,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 487,
    "G2 Fall_theta_del": 0.150152998,
    "G2 Fall_pr_1_99_del": 39,
    "G2 Fall_ss_del": 96,
    "G2 Fall_dss_del": 515,
    "G2 Winter_theta_del": 0.361225936,
    "G2 Winter_pr_1_99_del": 39,
    "G2 Winter_ss_del": 96,
    "G2 Winter_dss_del": 536,
    "G2 Spring_theta_del": 0.596444781,
    "G2 Spring_pr_1_99_del": 39,
    "G2 Spring_ss_del": 95,
    "G2 Spring_dss_del": 560
  },
  {
    "ID": 453,
    "KG Fall_theta_del": -1.386452272,
    "KG Fall_pr_1_99_del": 39,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 361,
    "KG Winter_theta_del": -1.097894611,
    "KG Winter_pr_1_99_del": 39,
    "KG Winter_ss_del": 96,
    "KG Winter_dss_del": 390,
    "KG Spring_theta_del": -0.737471431,
    "KG Spring_pr_1_99_del": 39,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 426,
    "G1 Fall_theta_del": -0.567036196,
    "G1 Fall_pr_1_99_del": 39,
    "G1 Fall_ss_del": 96,
    "G1 Fall_dss_del": 443,
    "G1 Winter_theta_del": -0.377127102,
    "G1 Winter_pr_1_99_del": 39,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 462,
    "G1 Spring_theta_del": -0.125689472,
    "G1 Spring_pr_1_99_del": 39,
    "G1 Spring_ss_del": 95,
    "G1 Spring_dss_del": 487,
    "G2 Fall_theta_del": 0.152635368,
    "G2 Fall_pr_1_99_del": 39,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 515,
    "G2 Winter_theta_del": 0.364113083,
    "G2 Winter_pr_1_99_del": 39,
    "G2 Winter_ss_del": 96,
    "G2 Winter_dss_del": 536,
    "G2 Spring_theta_del": 0.606393598,
    "G2 Spring_pr_1_99_del": 39,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 561
  },
  {
    "ID": 107,
    "KG Fall_theta_del": -1.385065391,
    "KG Fall_pr_1_99_del": 40,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 361,
    "KG Winter_theta_del": -1.080744496,
    "KG Winter_pr_1_99_del": 40,
    "KG Winter_ss_del": 96,
    "KG Winter_dss_del": 392,
    "KG Spring_theta_del": -0.736726158,
    "KG Spring_pr_1_99_del": 40,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 426,
    "G1 Fall_theta_del": -0.565897418,
    "G1 Fall_pr_1_99_del": 40,
    "G1 Fall_ss_del": 96,
    "G1 Fall_dss_del": 443,
    "G1 Winter_theta_del": -0.372809146,
    "G1 Winter_pr_1_99_del": 40,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 463,
    "G1 Spring_theta_del": -0.111372134,
    "G1 Spring_pr_1_99_del": 40,
    "G1 Spring_ss_del": 96,
    "G1 Spring_dss_del": 489,
    "G2 Fall_theta_del": 0.156859458,
    "G2 Fall_pr_1_99_del": 40,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 516,
    "G2 Winter_theta_del": 0.367407642,
    "G2 Winter_pr_1_99_del": 40,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 537,
    "G2 Spring_theta_del": 0.614070602,
    "G2 Spring_pr_1_99_del": 40,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 561
  },
  {
    "ID": 200,
    "KG Fall_theta_del": -1.371694582,
    "KG Fall_pr_1_99_del": 40,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 363,
    "KG Winter_theta_del": -1.080528797,
    "KG Winter_pr_1_99_del": 40,
    "KG Winter_ss_del": 96,
    "KG Winter_dss_del": 392,
    "KG Spring_theta_del": -0.731609495,
    "KG Spring_pr_1_99_del": 40,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 427,
    "G1 Fall_theta_del": -0.562087335,
    "G1 Fall_pr_1_99_del": 40,
    "G1 Fall_ss_del": 96,
    "G1 Fall_dss_del": 444,
    "G1 Winter_theta_del": -0.371051478,
    "G1 Winter_pr_1_99_del": 40,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 463,
    "G1 Spring_theta_del": -0.109434398,
    "G1 Spring_pr_1_99_del": 40,
    "G1 Spring_ss_del": 96,
    "G1 Spring_dss_del": 489,
    "G2 Fall_theta_del": 0.157065791,
    "G2 Fall_pr_1_99_del": 40,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 516,
    "G2 Winter_theta_del": 0.380669618,
    "G2 Winter_pr_1_99_del": 40,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 538,
    "G2 Spring_theta_del": 0.618953267,
    "G2 Spring_pr_1_99_del": 40,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 562
  },
  {
    "ID": 205,
    "KG Fall_theta_del": -1.369291069,
    "KG Fall_pr_1_99_del": 40,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 363,
    "KG Winter_theta_del": -1.076548234,
    "KG Winter_pr_1_99_del": 40,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 392,
    "KG Spring_theta_del": -0.72948308,
    "KG Spring_pr_1_99_del": 40,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 427,
    "G1 Fall_theta_del": -0.561830154,
    "G1 Fall_pr_1_99_del": 40,
    "G1 Fall_ss_del": 96,
    "G1 Fall_dss_del": 444,
    "G1 Winter_theta_del": -0.36130694,
    "G1 Winter_pr_1_99_del": 40,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 464,
    "G1 Spring_theta_del": -0.101238505,
    "G1 Spring_pr_1_99_del": 40,
    "G1 Spring_ss_del": 96,
    "G1 Spring_dss_del": 490,
    "G2 Fall_theta_del": 0.160389408,
    "G2 Fall_pr_1_99_del": 40,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 516,
    "G2 Winter_theta_del": 0.384375627,
    "G2 Winter_pr_1_99_del": 40,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 538,
    "G2 Spring_theta_del": 0.620435137,
    "G2 Spring_pr_1_99_del": 40,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 562
  },
  {
    "ID": 307,
    "KG Fall_theta_del": -1.368218683,
    "KG Fall_pr_1_99_del": 40,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 363,
    "KG Winter_theta_del": -1.075282324,
    "KG Winter_pr_1_99_del": 40,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 392,
    "KG Spring_theta_del": -0.725870498,
    "KG Spring_pr_1_99_del": 40,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 427,
    "G1 Fall_theta_del": -0.5607882,
    "G1 Fall_pr_1_99_del": 40,
    "G1 Fall_ss_del": 96,
    "G1 Fall_dss_del": 444,
    "G1 Winter_theta_del": -0.358044461,
    "G1 Winter_pr_1_99_del": 40,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 464,
    "G1 Spring_theta_del": -0.085294744,
    "G1 Spring_pr_1_99_del": 40,
    "G1 Spring_ss_del": 96,
    "G1 Spring_dss_del": 491,
    "G2 Fall_theta_del": 0.162806926,
    "G2 Fall_pr_1_99_del": 40,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 516,
    "G2 Winter_theta_del": 0.384505903,
    "G2 Winter_pr_1_99_del": 40,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 538,
    "G2 Spring_theta_del": 0.620572893,
    "G2 Spring_pr_1_99_del": 40,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 562
  },
  {
    "ID": 447,
    "KG Fall_theta_del": -1.362794947,
    "KG Fall_pr_1_99_del": 40,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 364,
    "KG Winter_theta_del": -1.074818416,
    "KG Winter_pr_1_99_del": 40,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 393,
    "KG Spring_theta_del": -0.722968478,
    "KG Spring_pr_1_99_del": 40,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 428,
    "G1 Fall_theta_del": -0.55281284,
    "G1 Fall_pr_1_99_del": 40,
    "G1 Fall_ss_del": 96,
    "G1 Fall_dss_del": 445,
    "G1 Winter_theta_del": -0.351220956,
    "G1 Winter_pr_1_99_del": 40,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 465,
    "G1 Spring_theta_del": -0.065602226,
    "G1 Spring_pr_1_99_del": 40,
    "G1 Spring_ss_del": 97,
    "G1 Spring_dss_del": 493,
    "G2 Fall_theta_del": 0.166427148,
    "G2 Fall_pr_1_99_del": 40,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 517,
    "G2 Winter_theta_del": 0.387636132,
    "G2 Winter_pr_1_99_del": 40,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 539,
    "G2 Spring_theta_del": 0.621610064,
    "G2 Spring_pr_1_99_del": 40,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 562
  },
  {
    "ID": 82,
    "KG Fall_theta_del": -1.361852529,
    "KG Fall_pr_1_99_del": 41,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 364,
    "KG Winter_theta_del": -1.070204644,
    "KG Winter_pr_1_99_del": 41,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 393,
    "KG Spring_theta_del": -0.71433954,
    "KG Spring_pr_1_99_del": 41,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 429,
    "G1 Fall_theta_del": -0.552405141,
    "G1 Fall_pr_1_99_del": 41,
    "G1 Fall_ss_del": 96,
    "G1 Fall_dss_del": 445,
    "G1 Winter_theta_del": -0.349425166,
    "G1 Winter_pr_1_99_del": 41,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 465,
    "G1 Spring_theta_del": -0.061529871,
    "G1 Spring_pr_1_99_del": 41,
    "G1 Spring_ss_del": 97,
    "G1 Spring_dss_del": 494,
    "G2 Fall_theta_del": 0.171159742,
    "G2 Fall_pr_1_99_del": 41,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 517,
    "G2 Winter_theta_del": 0.393022229,
    "G2 Winter_pr_1_99_del": 41,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 539,
    "G2 Spring_theta_del": 0.626684465,
    "G2 Spring_pr_1_99_del": 41,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 563
  },
  {
    "ID": 104,
    "KG Fall_theta_del": -1.358415986,
    "KG Fall_pr_1_99_del": 41,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 364,
    "KG Winter_theta_del": -1.066288321,
    "KG Winter_pr_1_99_del": 41,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 393,
    "KG Spring_theta_del": -0.714165585,
    "KG Spring_pr_1_99_del": 41,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 429,
    "G1 Fall_theta_del": -0.53909128,
    "G1 Fall_pr_1_99_del": 41,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 446,
    "G1 Winter_theta_del": -0.34748369,
    "G1 Winter_pr_1_99_del": 41,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 465,
    "G1 Spring_theta_del": -0.051529434,
    "G1 Spring_pr_1_99_del": 41,
    "G1 Spring_ss_del": 97,
    "G1 Spring_dss_del": 495,
    "G2 Fall_theta_del": 0.172157031,
    "G2 Fall_pr_1_99_del": 41,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 517,
    "G2 Winter_theta_del": 0.401296272,
    "G2 Winter_pr_1_99_del": 41,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 540,
    "G2 Spring_theta_del": 0.636872289,
    "G2 Spring_pr_1_99_del": 41,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 564
  },
  {
    "ID": 425,
    "KG Fall_theta_del": -1.350162947,
    "KG Fall_pr_1_99_del": 41,
    "KG Fall_ss_del": 96,
    "KG Fall_dss_del": 365,
    "KG Winter_theta_del": -1.065122308,
    "KG Winter_pr_1_99_del": 41,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 393,
    "KG Spring_theta_del": -0.713364312,
    "KG Spring_pr_1_99_del": 41,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 429,
    "G1 Fall_theta_del": -0.533252952,
    "G1 Fall_pr_1_99_del": 41,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 447,
    "G1 Winter_theta_del": -0.347024935,
    "G1 Winter_pr_1_99_del": 41,
    "G1 Winter_ss_del": 95,
    "G1 Winter_dss_del": 465,
    "G1 Spring_theta_del": -0.050057146,
    "G1 Spring_pr_1_99_del": 41,
    "G1 Spring_ss_del": 97,
    "G1 Spring_dss_del": 495,
    "G2 Fall_theta_del": 0.173369827,
    "G2 Fall_pr_1_99_del": 41,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 517,
    "G2 Winter_theta_del": 0.401631613,
    "G2 Winter_pr_1_99_del": 41,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 540,
    "G2 Spring_theta_del": 0.647404967,
    "G2 Spring_pr_1_99_del": 41,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 565
  },
  {
    "ID": 474,
    "KG Fall_theta_del": -1.34664372,
    "KG Fall_pr_1_99_del": 41,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 365,
    "KG Winter_theta_del": -1.055112077,
    "KG Winter_pr_1_99_del": 41,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 394,
    "KG Spring_theta_del": -0.71061216,
    "KG Spring_pr_1_99_del": 41,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 429,
    "G1 Fall_theta_del": -0.532287239,
    "G1 Fall_pr_1_99_del": 41,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 447,
    "G1 Winter_theta_del": -0.335763533,
    "G1 Winter_pr_1_99_del": 41,
    "G1 Winter_ss_del": 96,
    "G1 Winter_dss_del": 466,
    "G1 Spring_theta_del": -0.046727983,
    "G1 Spring_pr_1_99_del": 41,
    "G1 Spring_ss_del": 97,
    "G1 Spring_dss_del": 495,
    "G2 Fall_theta_del": 0.175403867,
    "G2 Fall_pr_1_99_del": 41,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 518,
    "G2 Winter_theta_del": 0.402872085,
    "G2 Winter_pr_1_99_del": 41,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 540,
    "G2 Spring_theta_del": 0.651698604,
    "G2 Spring_pr_1_99_del": 41,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 565
  },
  {
    "ID": 481,
    "KG Fall_theta_del": -1.335856973,
    "KG Fall_pr_1_99_del": 41,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 366,
    "KG Winter_theta_del": -1.048571908,
    "KG Winter_pr_1_99_del": 41,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 395,
    "KG Spring_theta_del": -0.710000443,
    "KG Spring_pr_1_99_del": 41,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 429,
    "G1 Fall_theta_del": -0.525232325,
    "G1 Fall_pr_1_99_del": 41,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 447,
    "G1 Winter_theta_del": -0.330396262,
    "G1 Winter_pr_1_99_del": 41,
    "G1 Winter_ss_del": 96,
    "G1 Winter_dss_del": 467,
    "G1 Spring_theta_del": -0.041073048,
    "G1 Spring_pr_1_99_del": 41,
    "G1 Spring_ss_del": 97,
    "G1 Spring_dss_del": 496,
    "G2 Fall_theta_del": 0.175934121,
    "G2 Fall_pr_1_99_del": 41,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 518,
    "G2 Winter_theta_del": 0.406169007,
    "G2 Winter_pr_1_99_del": 41,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 541,
    "G2 Spring_theta_del": 0.65560843,
    "G2 Spring_pr_1_99_del": 41,
    "G2 Spring_ss_del": 96,
    "G2 Spring_dss_del": 566
  },
  {
    "ID": 25,
    "KG Fall_theta_del": -1.330125752,
    "KG Fall_pr_1_99_del": 42,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 367,
    "KG Winter_theta_del": -1.045450625,
    "KG Winter_pr_1_99_del": 42,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 395,
    "KG Spring_theta_del": -0.703442148,
    "KG Spring_pr_1_99_del": 42,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 430,
    "G1 Fall_theta_del": -0.523162748,
    "G1 Fall_pr_1_99_del": 42,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 448,
    "G1 Winter_theta_del": -0.322773836,
    "G1 Winter_pr_1_99_del": 42,
    "G1 Winter_ss_del": 96,
    "G1 Winter_dss_del": 468,
    "G1 Spring_theta_del": -0.036430184,
    "G1 Spring_pr_1_99_del": 42,
    "G1 Spring_ss_del": 97,
    "G1 Spring_dss_del": 496,
    "G2 Fall_theta_del": 0.177944346,
    "G2 Fall_pr_1_99_del": 42,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 518,
    "G2 Winter_theta_del": 0.414691414,
    "G2 Winter_pr_1_99_del": 42,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 541,
    "G2 Spring_theta_del": 0.660108746,
    "G2 Spring_pr_1_99_del": 42,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 566
  },
  {
    "ID": 118,
    "KG Fall_theta_del": -1.329859233,
    "KG Fall_pr_1_99_del": 42,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 367,
    "KG Winter_theta_del": -1.033918177,
    "KG Winter_pr_1_99_del": 42,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 397,
    "KG Spring_theta_del": -0.701997907,
    "KG Spring_pr_1_99_del": 42,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 430,
    "G1 Fall_theta_del": -0.521272283,
    "G1 Fall_pr_1_99_del": 42,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 448,
    "G1 Winter_theta_del": -0.314058448,
    "G1 Winter_pr_1_99_del": 42,
    "G1 Winter_ss_del": 96,
    "G1 Winter_dss_del": 469,
    "G1 Spring_theta_del": -0.035109095,
    "G1 Spring_pr_1_99_del": 42,
    "G1 Spring_ss_del": 97,
    "G1 Spring_dss_del": 496,
    "G2 Fall_theta_del": 0.179900841,
    "G2 Fall_pr_1_99_del": 42,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 518,
    "G2 Winter_theta_del": 0.415748165,
    "G2 Winter_pr_1_99_del": 42,
    "G2 Winter_ss_del": 97,
    "G2 Winter_dss_del": 542,
    "G2 Spring_theta_del": 0.667464721,
    "G2 Spring_pr_1_99_del": 42,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 567
  },
  {
    "ID": 122,
    "KG Fall_theta_del": -1.328049132,
    "KG Fall_pr_1_99_del": 42,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 367,
    "KG Winter_theta_del": -1.029505841,
    "KG Winter_pr_1_99_del": 42,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 397,
    "KG Spring_theta_del": -0.701831774,
    "KG Spring_pr_1_99_del": 42,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 430,
    "G1 Fall_theta_del": -0.51747904,
    "G1 Fall_pr_1_99_del": 42,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 448,
    "G1 Winter_theta_del": -0.311545877,
    "G1 Winter_pr_1_99_del": 42,
    "G1 Winter_ss_del": 96,
    "G1 Winter_dss_del": 469,
    "G1 Spring_theta_del": -0.026108361,
    "G1 Spring_pr_1_99_del": 42,
    "G1 Spring_ss_del": 97,
    "G1 Spring_dss_del": 497,
    "G2 Fall_theta_del": 0.180761154,
    "G2 Fall_pr_1_99_del": 42,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 518,
    "G2 Winter_theta_del": 0.427099015,
    "G2 Winter_pr_1_99_del": 42,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 543,
    "G2 Spring_theta_del": 0.668418929,
    "G2 Spring_pr_1_99_del": 42,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 567
  },
  {
    "ID": 389,
    "KG Fall_theta_del": -1.323987044,
    "KG Fall_pr_1_99_del": 42,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 368,
    "KG Winter_theta_del": -1.028896527,
    "KG Winter_pr_1_99_del": 42,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 397,
    "KG Spring_theta_del": -0.699161704,
    "KG Spring_pr_1_99_del": 42,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 430,
    "G1 Fall_theta_del": -0.516199691,
    "G1 Fall_pr_1_99_del": 42,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 448,
    "G1 Winter_theta_del": -0.309688637,
    "G1 Winter_pr_1_99_del": 42,
    "G1 Winter_ss_del": 96,
    "G1 Winter_dss_del": 469,
    "G1 Spring_theta_del": -0.021825864,
    "G1 Spring_pr_1_99_del": 42,
    "G1 Spring_ss_del": 98,
    "G1 Spring_dss_del": 498,
    "G2 Fall_theta_del": 0.181253617,
    "G2 Fall_pr_1_99_del": 42,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 518,
    "G2 Winter_theta_del": 0.428212341,
    "G2 Winter_pr_1_99_del": 42,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 543,
    "G2 Spring_theta_del": 0.668986487,
    "G2 Spring_pr_1_99_del": 42,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 567
  },
  {
    "ID": 455,
    "KG Fall_theta_del": -1.319920194,
    "KG Fall_pr_1_99_del": 42,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 368,
    "KG Winter_theta_del": -1.027681644,
    "KG Winter_pr_1_99_del": 42,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 397,
    "KG Spring_theta_del": -0.697337892,
    "KG Spring_pr_1_99_del": 42,
    "KG Spring_ss_del": 97,
    "KG Spring_dss_del": 430,
    "G1 Fall_theta_del": -0.50205952,
    "G1 Fall_pr_1_99_del": 42,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 450,
    "G1 Winter_theta_del": -0.302340218,
    "G1 Winter_pr_1_99_del": 42,
    "G1 Winter_ss_del": 96,
    "G1 Winter_dss_del": 470,
    "G1 Spring_theta_del": -0.017784555,
    "G1 Spring_pr_1_99_del": 42,
    "G1 Spring_ss_del": 98,
    "G1 Spring_dss_del": 498,
    "G2 Fall_theta_del": 0.184972928,
    "G2 Fall_pr_1_99_del": 42,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 518,
    "G2 Winter_theta_del": 0.433553541,
    "G2 Winter_pr_1_99_del": 42,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 543,
    "G2 Spring_theta_del": 0.682777587,
    "G2 Spring_pr_1_99_del": 42,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 568
  },
  {
    "ID": 101,
    "KG Fall_theta_del": -1.313132506,
    "KG Fall_pr_1_99_del": 43,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 369,
    "KG Winter_theta_del": -1.024990337,
    "KG Winter_pr_1_99_del": 43,
    "KG Winter_ss_del": 97,
    "KG Winter_dss_del": 398,
    "KG Spring_theta_del": -0.688808542,
    "KG Spring_pr_1_99_del": 43,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 431,
    "G1 Fall_theta_del": -0.495290977,
    "G1 Fall_pr_1_99_del": 43,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 450,
    "G1 Winter_theta_del": -0.301663032,
    "G1 Winter_pr_1_99_del": 43,
    "G1 Winter_ss_del": 96,
    "G1 Winter_dss_del": 470,
    "G1 Spring_theta_del": -0.013319753,
    "G1 Spring_pr_1_99_del": 43,
    "G1 Spring_ss_del": 98,
    "G1 Spring_dss_del": 499,
    "G2 Fall_theta_del": 0.185406312,
    "G2 Fall_pr_1_99_del": 43,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 519,
    "G2 Winter_theta_del": 0.434500077,
    "G2 Winter_pr_1_99_del": 43,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 543,
    "G2 Spring_theta_del": 0.686337032,
    "G2 Spring_pr_1_99_del": 43,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 569
  },
  {
    "ID": 124,
    "KG Fall_theta_del": -1.311347662,
    "KG Fall_pr_1_99_del": 43,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 369,
    "KG Winter_theta_del": -1.01912923,
    "KG Winter_pr_1_99_del": 43,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 398,
    "KG Spring_theta_del": -0.676666907,
    "KG Spring_pr_1_99_del": 43,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 432,
    "G1 Fall_theta_del": -0.492730495,
    "G1 Fall_pr_1_99_del": 43,
    "G1 Fall_ss_del": 97,
    "G1 Fall_dss_del": 451,
    "G1 Winter_theta_del": -0.294962429,
    "G1 Winter_pr_1_99_del": 43,
    "G1 Winter_ss_del": 96,
    "G1 Winter_dss_del": 471,
    "G1 Spring_theta_del": -0.012328189,
    "G1 Spring_pr_1_99_del": 43,
    "G1 Spring_ss_del": 98,
    "G1 Spring_dss_del": 499,
    "G2 Fall_theta_del": 0.18760376,
    "G2 Fall_pr_1_99_del": 43,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 519,
    "G2 Winter_theta_del": 0.438497533,
    "G2 Winter_pr_1_99_del": 43,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 544,
    "G2 Spring_theta_del": 0.687600134,
    "G2 Spring_pr_1_99_del": 43,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 569
  },
  {
    "ID": 196,
    "KG Fall_theta_del": -1.311136498,
    "KG Fall_pr_1_99_del": 43,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 369,
    "KG Winter_theta_del": -1.01798871,
    "KG Winter_pr_1_99_del": 43,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 398,
    "KG Spring_theta_del": -0.670810371,
    "KG Spring_pr_1_99_del": 43,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 433,
    "G1 Fall_theta_del": -0.482863045,
    "G1 Fall_pr_1_99_del": 43,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 452,
    "G1 Winter_theta_del": -0.29002202,
    "G1 Winter_pr_1_99_del": 43,
    "G1 Winter_ss_del": 97,
    "G1 Winter_dss_del": 471,
    "G1 Spring_theta_del": -0.012135784,
    "G1 Spring_pr_1_99_del": 43,
    "G1 Spring_ss_del": 98,
    "G1 Spring_dss_del": 499,
    "G2 Fall_theta_del": 0.190992786,
    "G2 Fall_pr_1_99_del": 43,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 519,
    "G2 Winter_theta_del": 0.440602417,
    "G2 Winter_pr_1_99_del": 43,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 544,
    "G2 Spring_theta_del": 0.68854293,
    "G2 Spring_pr_1_99_del": 43,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 569
  },
  {
    "ID": 445,
    "KG Fall_theta_del": -1.306966373,
    "KG Fall_pr_1_99_del": 43,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 369,
    "KG Winter_theta_del": -1.016227215,
    "KG Winter_pr_1_99_del": 43,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 398,
    "KG Spring_theta_del": -0.670672275,
    "KG Spring_pr_1_99_del": 43,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 433,
    "G1 Fall_theta_del": -0.481603971,
    "G1 Fall_pr_1_99_del": 43,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 452,
    "G1 Winter_theta_del": -0.279542141,
    "G1 Winter_pr_1_99_del": 43,
    "G1 Winter_ss_del": 97,
    "G1 Winter_dss_del": 472,
    "G1 Spring_theta_del": 0.002427947,
    "G1 Spring_pr_1_99_del": 43,
    "G1 Spring_ss_del": 98,
    "G1 Spring_dss_del": 500,
    "G2 Fall_theta_del": 0.19375112,
    "G2 Fall_pr_1_99_del": 43,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 519,
    "G2 Winter_theta_del": 0.441394296,
    "G2 Winter_pr_1_99_del": 43,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 544,
    "G2 Spring_theta_del": 0.693478068,
    "G2 Spring_pr_1_99_del": 43,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 569
  },
  {
    "ID": 488,
    "KG Fall_theta_del": -1.306949449,
    "KG Fall_pr_1_99_del": 43,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 369,
    "KG Winter_theta_del": -1.013552355,
    "KG Winter_pr_1_99_del": 43,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 399,
    "KG Spring_theta_del": -0.670214956,
    "KG Spring_pr_1_99_del": 43,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 433,
    "G1 Fall_theta_del": -0.479040636,
    "G1 Fall_pr_1_99_del": 43,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 452,
    "G1 Winter_theta_del": -0.277206685,
    "G1 Winter_pr_1_99_del": 43,
    "G1 Winter_ss_del": 97,
    "G1 Winter_dss_del": 472,
    "G1 Spring_theta_del": 0.01610097,
    "G1 Spring_pr_1_99_del": 43,
    "G1 Spring_ss_del": 98,
    "G1 Spring_dss_del": 502,
    "G2 Fall_theta_del": 0.195605114,
    "G2 Fall_pr_1_99_del": 43,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 520,
    "G2 Winter_theta_del": 0.441465698,
    "G2 Winter_pr_1_99_del": 43,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 544,
    "G2 Spring_theta_del": 0.699366654,
    "G2 Spring_pr_1_99_del": 43,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 570
  },
  {
    "ID": 188,
    "KG Fall_theta_del": -1.298969319,
    "KG Fall_pr_1_99_del": 44,
    "KG Fall_ss_del": 97,
    "KG Fall_dss_del": 370,
    "KG Winter_theta_del": -1.00475474,
    "KG Winter_pr_1_99_del": 44,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 400,
    "KG Spring_theta_del": -0.669868364,
    "KG Spring_pr_1_99_del": 44,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 433,
    "G1 Fall_theta_del": -0.477406068,
    "G1 Fall_pr_1_99_del": 44,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 452,
    "G1 Winter_theta_del": -0.276015743,
    "G1 Winter_pr_1_99_del": 44,
    "G1 Winter_ss_del": 97,
    "G1 Winter_dss_del": 472,
    "G1 Spring_theta_del": 0.019559268,
    "G1 Spring_pr_1_99_del": 44,
    "G1 Spring_ss_del": 98,
    "G1 Spring_dss_del": 502,
    "G2 Fall_theta_del": 0.200085609,
    "G2 Fall_pr_1_99_del": 44,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 520,
    "G2 Winter_theta_del": 0.445444345,
    "G2 Winter_pr_1_99_del": 44,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 545,
    "G2 Spring_theta_del": 0.69973987,
    "G2 Spring_pr_1_99_del": 44,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 570
  },
  {
    "ID": 190,
    "KG Fall_theta_del": -1.293286186,
    "KG Fall_pr_1_99_del": 44,
    "KG Fall_ss_del": 98,
    "KG Fall_dss_del": 371,
    "KG Winter_theta_del": -1.002728938,
    "KG Winter_pr_1_99_del": 44,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 400,
    "KG Spring_theta_del": -0.664261565,
    "KG Spring_pr_1_99_del": 44,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 434,
    "G1 Fall_theta_del": -0.465095086,
    "G1 Fall_pr_1_99_del": 44,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 453,
    "G1 Winter_theta_del": -0.273795192,
    "G1 Winter_pr_1_99_del": 44,
    "G1 Winter_ss_del": 97,
    "G1 Winter_dss_del": 473,
    "G1 Spring_theta_del": 0.021575086,
    "G1 Spring_pr_1_99_del": 44,
    "G1 Spring_ss_del": 98,
    "G1 Spring_dss_del": 502,
    "G2 Fall_theta_del": 0.200559534,
    "G2 Fall_pr_1_99_del": 44,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 520,
    "G2 Winter_theta_del": 0.450180611,
    "G2 Winter_pr_1_99_del": 44,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 545,
    "G2 Spring_theta_del": 0.701270508,
    "G2 Spring_pr_1_99_del": 44,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 570
  },
  {
    "ID": 333,
    "KG Fall_theta_del": -1.285612314,
    "KG Fall_pr_1_99_del": 44,
    "KG Fall_ss_del": 98,
    "KG Fall_dss_del": 371,
    "KG Winter_theta_del": -1.00174537,
    "KG Winter_pr_1_99_del": 44,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 400,
    "KG Spring_theta_del": -0.648502082,
    "KG Spring_pr_1_99_del": 44,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 435,
    "G1 Fall_theta_del": -0.460282554,
    "G1 Fall_pr_1_99_del": 44,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 454,
    "G1 Winter_theta_del": -0.271188262,
    "G1 Winter_pr_1_99_del": 44,
    "G1 Winter_ss_del": 97,
    "G1 Winter_dss_del": 473,
    "G1 Spring_theta_del": 0.024593444,
    "G1 Spring_pr_1_99_del": 44,
    "G1 Spring_ss_del": 98,
    "G1 Spring_dss_del": 502,
    "G2 Fall_theta_del": 0.201370678,
    "G2 Fall_pr_1_99_del": 44,
    "G2 Fall_ss_del": 97,
    "G2 Fall_dss_del": 520,
    "G2 Winter_theta_del": 0.452845583,
    "G2 Winter_pr_1_99_del": 44,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 545,
    "G2 Spring_theta_del": 0.709549988,
    "G2 Spring_pr_1_99_del": 44,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 571
  },
  {
    "ID": 479,
    "KG Fall_theta_del": -1.285096832,
    "KG Fall_pr_1_99_del": 44,
    "KG Fall_ss_del": 98,
    "KG Fall_dss_del": 371,
    "KG Winter_theta_del": -1.000751891,
    "KG Winter_pr_1_99_del": 44,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 400,
    "KG Spring_theta_del": -0.646427757,
    "KG Spring_pr_1_99_del": 44,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 435,
    "G1 Fall_theta_del": -0.452670463,
    "G1 Fall_pr_1_99_del": 44,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 455,
    "G1 Winter_theta_del": -0.254858551,
    "G1 Winter_pr_1_99_del": 44,
    "G1 Winter_ss_del": 97,
    "G1 Winter_dss_del": 475,
    "G1 Spring_theta_del": 0.032771463,
    "G1 Spring_pr_1_99_del": 44,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 503,
    "G2 Fall_theta_del": 0.204991268,
    "G2 Fall_pr_1_99_del": 44,
    "G2 Fall_ss_del": 98,
    "G2 Fall_dss_del": 520,
    "G2 Winter_theta_del": 0.455647199,
    "G2 Winter_pr_1_99_del": 44,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 546,
    "G2 Spring_theta_del": 0.711974073,
    "G2 Spring_pr_1_99_del": 44,
    "G2 Spring_ss_del": 97,
    "G2 Spring_dss_del": 571
  },
  {
    "ID": 489,
    "KG Fall_theta_del": -1.283039947,
    "KG Fall_pr_1_99_del": 44,
    "KG Fall_ss_del": 98,
    "KG Fall_dss_del": 372,
    "KG Winter_theta_del": -0.99987339,
    "KG Winter_pr_1_99_del": 44,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 400,
    "KG Spring_theta_del": -0.645828966,
    "KG Spring_pr_1_99_del": 44,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 435,
    "G1 Fall_theta_del": -0.451833035,
    "G1 Fall_pr_1_99_del": 44,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 455,
    "G1 Winter_theta_del": -0.24978424,
    "G1 Winter_pr_1_99_del": 44,
    "G1 Winter_ss_del": 97,
    "G1 Winter_dss_del": 475,
    "G1 Spring_theta_del": 0.033838986,
    "G1 Spring_pr_1_99_del": 44,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 503,
    "G2 Fall_theta_del": 0.208955769,
    "G2 Fall_pr_1_99_del": 44,
    "G2 Fall_ss_del": 98,
    "G2 Fall_dss_del": 521,
    "G2 Winter_theta_del": 0.460574827,
    "G2 Winter_pr_1_99_del": 44,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 546,
    "G2 Spring_theta_del": 0.720983738,
    "G2 Spring_pr_1_99_del": 44,
    "G2 Spring_ss_del": 98,
    "G2 Spring_dss_del": 572
  },
  {
    "ID": 77,
    "KG Fall_theta_del": -1.273073172,
    "KG Fall_pr_1_99_del": 45,
    "KG Fall_ss_del": 98,
    "KG Fall_dss_del": 373,
    "KG Winter_theta_del": -0.990998545,
    "KG Winter_pr_1_99_del": 45,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 401,
    "KG Spring_theta_del": -0.642729495,
    "KG Spring_pr_1_99_del": 45,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 436,
    "G1 Fall_theta_del": -0.447019501,
    "G1 Fall_pr_1_99_del": 45,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 455,
    "G1 Winter_theta_del": -0.248372766,
    "G1 Winter_pr_1_99_del": 45,
    "G1 Winter_ss_del": 97,
    "G1 Winter_dss_del": 475,
    "G1 Spring_theta_del": 0.035429094,
    "G1 Spring_pr_1_99_del": 45,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 504,
    "G2 Fall_theta_del": 0.210576833,
    "G2 Fall_pr_1_99_del": 45,
    "G2 Fall_ss_del": 98,
    "G2 Fall_dss_del": 521,
    "G2 Winter_theta_del": 0.463706115,
    "G2 Winter_pr_1_99_del": 45,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 546,
    "G2 Spring_theta_del": 0.721854992,
    "G2 Spring_pr_1_99_del": 45,
    "G2 Spring_ss_del": 98,
    "G2 Spring_dss_del": 572
  },
  {
    "ID": 273,
    "KG Fall_theta_del": -1.264643021,
    "KG Fall_pr_1_99_del": 45,
    "KG Fall_ss_del": 98,
    "KG Fall_dss_del": 374,
    "KG Winter_theta_del": -0.98234841,
    "KG Winter_pr_1_99_del": 45,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 402,
    "KG Spring_theta_del": -0.637812083,
    "KG Spring_pr_1_99_del": 45,
    "KG Spring_ss_del": 98,
    "KG Spring_dss_del": 436,
    "G1 Fall_theta_del": -0.444090654,
    "G1 Fall_pr_1_99_del": 45,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 456,
    "G1 Winter_theta_del": -0.236222344,
    "G1 Winter_pr_1_99_del": 45,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 476,
    "G1 Spring_theta_del": 0.036054796,
    "G1 Spring_pr_1_99_del": 45,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 504,
    "G2 Fall_theta_del": 0.214066951,
    "G2 Fall_pr_1_99_del": 45,
    "G2 Fall_ss_del": 98,
    "G2 Fall_dss_del": 521,
    "G2 Winter_theta_del": 0.464133374,
    "G2 Winter_pr_1_99_del": 45,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 546,
    "G2 Spring_theta_del": 0.724262891,
    "G2 Spring_pr_1_99_del": 45,
    "G2 Spring_ss_del": 98,
    "G2 Spring_dss_del": 572
  },
  {
    "ID": 383,
    "KG Fall_theta_del": -1.261735077,
    "KG Fall_pr_1_99_del": 45,
    "KG Fall_ss_del": 98,
    "KG Fall_dss_del": 374,
    "KG Winter_theta_del": -0.976826568,
    "KG Winter_pr_1_99_del": 45,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 402,
    "KG Spring_theta_del": -0.630447239,
    "KG Spring_pr_1_99_del": 45,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 437,
    "G1 Fall_theta_del": -0.442542659,
    "G1 Fall_pr_1_99_del": 45,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 456,
    "G1 Winter_theta_del": -0.23180907,
    "G1 Winter_pr_1_99_del": 45,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 477,
    "G1 Spring_theta_del": 0.037723762,
    "G1 Spring_pr_1_99_del": 45,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 504,
    "G2 Fall_theta_del": 0.227765038,
    "G2 Fall_pr_1_99_del": 45,
    "G2 Fall_ss_del": 98,
    "G2 Fall_dss_del": 523,
    "G2 Winter_theta_del": 0.46518016,
    "G2 Winter_pr_1_99_del": 45,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 547,
    "G2 Spring_theta_del": 0.73204335,
    "G2 Spring_pr_1_99_del": 45,
    "G2 Spring_ss_del": 98,
    "G2 Spring_dss_del": 573
  },
  {
    "ID": 406,
    "KG Fall_theta_del": -1.245756487,
    "KG Fall_pr_1_99_del": 45,
    "KG Fall_ss_del": 98,
    "KG Fall_dss_del": 375,
    "KG Winter_theta_del": -0.976242081,
    "KG Winter_pr_1_99_del": 45,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 402,
    "KG Spring_theta_del": -0.629548692,
    "KG Spring_pr_1_99_del": 45,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 437,
    "G1 Fall_theta_del": -0.442515272,
    "G1 Fall_pr_1_99_del": 45,
    "G1 Fall_ss_del": 98,
    "G1 Fall_dss_del": 456,
    "G1 Winter_theta_del": -0.228834125,
    "G1 Winter_pr_1_99_del": 45,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 477,
    "G1 Spring_theta_del": 0.042804092,
    "G1 Spring_pr_1_99_del": 45,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 504,
    "G2 Fall_theta_del": 0.229595378,
    "G2 Fall_pr_1_99_del": 45,
    "G2 Fall_ss_del": 98,
    "G2 Fall_dss_del": 523,
    "G2 Winter_theta_del": 0.470674128,
    "G2 Winter_pr_1_99_del": 45,
    "G2 Winter_ss_del": 98,
    "G2 Winter_dss_del": 547,
    "G2 Spring_theta_del": 0.735183595,
    "G2 Spring_pr_1_99_del": 45,
    "G2 Spring_ss_del": 98,
    "G2 Spring_dss_del": 574
  },
  {
    "ID": 413,
    "KG Fall_theta_del": -1.242944706,
    "KG Fall_pr_1_99_del": 45,
    "KG Fall_ss_del": 98,
    "KG Fall_dss_del": 376,
    "KG Winter_theta_del": -0.969548739,
    "KG Winter_pr_1_99_del": 45,
    "KG Winter_ss_del": 98,
    "KG Winter_dss_del": 403,
    "KG Spring_theta_del": -0.628779002,
    "KG Spring_pr_1_99_del": 45,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 437,
    "G1 Fall_theta_del": -0.431994233,
    "G1 Fall_pr_1_99_del": 45,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 457,
    "G1 Winter_theta_del": -0.224442222,
    "G1 Winter_pr_1_99_del": 45,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 478,
    "G1 Spring_theta_del": 0.044449731,
    "G1 Spring_pr_1_99_del": 45,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 504,
    "G2 Fall_theta_del": 0.243597744,
    "G2 Fall_pr_1_99_del": 45,
    "G2 Fall_ss_del": 98,
    "G2 Fall_dss_del": 524,
    "G2 Winter_theta_del": 0.481297818,
    "G2 Winter_pr_1_99_del": 45,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 548,
    "G2 Spring_theta_del": 0.754041848,
    "G2 Spring_pr_1_99_del": 45,
    "G2 Spring_ss_del": 98,
    "G2 Spring_dss_del": 575
  },
  {
    "ID": 76,
    "KG Fall_theta_del": -1.24215069,
    "KG Fall_pr_1_99_del": 46,
    "KG Fall_ss_del": 98,
    "KG Fall_dss_del": 376,
    "KG Winter_theta_del": -0.967496137,
    "KG Winter_pr_1_99_del": 46,
    "KG Winter_ss_del": 99,
    "KG Winter_dss_del": 403,
    "KG Spring_theta_del": -0.627638346,
    "KG Spring_pr_1_99_del": 46,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 437,
    "G1 Fall_theta_del": -0.415340085,
    "G1 Fall_pr_1_99_del": 46,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 458,
    "G1 Winter_theta_del": -0.223850613,
    "G1 Winter_pr_1_99_del": 46,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 478,
    "G1 Spring_theta_del": 0.044596721,
    "G1 Spring_pr_1_99_del": 46,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 504,
    "G2 Fall_theta_del": 0.244868426,
    "G2 Fall_pr_1_99_del": 46,
    "G2 Fall_ss_del": 98,
    "G2 Fall_dss_del": 524,
    "G2 Winter_theta_del": 0.483606827,
    "G2 Winter_pr_1_99_del": 46,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 548,
    "G2 Spring_theta_del": 0.76102598,
    "G2 Spring_pr_1_99_del": 46,
    "G2 Spring_ss_del": 98,
    "G2 Spring_dss_del": 576
  },
  {
    "ID": 81,
    "KG Fall_theta_del": -1.23616204,
    "KG Fall_pr_1_99_del": 46,
    "KG Fall_ss_del": 99,
    "KG Fall_dss_del": 376,
    "KG Winter_theta_del": -0.956771457,
    "KG Winter_pr_1_99_del": 46,
    "KG Winter_ss_del": 99,
    "KG Winter_dss_del": 404,
    "KG Spring_theta_del": -0.627587193,
    "KG Spring_pr_1_99_del": 46,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 437,
    "G1 Fall_theta_del": -0.411308256,
    "G1 Fall_pr_1_99_del": 46,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 459,
    "G1 Winter_theta_del": -0.223784811,
    "G1 Winter_pr_1_99_del": 46,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 478,
    "G1 Spring_theta_del": 0.046247095,
    "G1 Spring_pr_1_99_del": 46,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 505,
    "G2 Fall_theta_del": 0.25064955,
    "G2 Fall_pr_1_99_del": 46,
    "G2 Fall_ss_del": 98,
    "G2 Fall_dss_del": 525,
    "G2 Winter_theta_del": 0.483727582,
    "G2 Winter_pr_1_99_del": 46,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 548,
    "G2 Spring_theta_del": 0.769943682,
    "G2 Spring_pr_1_99_del": 46,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 577
  },
  {
    "ID": 91,
    "KG Fall_theta_del": -1.235337111,
    "KG Fall_pr_1_99_del": 46,
    "KG Fall_ss_del": 99,
    "KG Fall_dss_del": 376,
    "KG Winter_theta_del": -0.951928428,
    "KG Winter_pr_1_99_del": 46,
    "KG Winter_ss_del": 99,
    "KG Winter_dss_del": 405,
    "KG Spring_theta_del": -0.624416358,
    "KG Spring_pr_1_99_del": 46,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 438,
    "G1 Fall_theta_del": -0.408982165,
    "G1 Fall_pr_1_99_del": 46,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 459,
    "G1 Winter_theta_del": -0.222629117,
    "G1 Winter_pr_1_99_del": 46,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 478,
    "G1 Spring_theta_del": 0.049236153,
    "G1 Spring_pr_1_99_del": 46,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 505,
    "G2 Fall_theta_del": 0.253519984,
    "G2 Fall_pr_1_99_del": 46,
    "G2 Fall_ss_del": 98,
    "G2 Fall_dss_del": 525,
    "G2 Winter_theta_del": 0.489576081,
    "G2 Winter_pr_1_99_del": 46,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 549,
    "G2 Spring_theta_del": 0.769967053,
    "G2 Spring_pr_1_99_del": 46,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 577
  },
  {
    "ID": 230,
    "KG Fall_theta_del": -1.233773246,
    "KG Fall_pr_1_99_del": 46,
    "KG Fall_ss_del": 99,
    "KG Fall_dss_del": 377,
    "KG Winter_theta_del": -0.941164318,
    "KG Winter_pr_1_99_del": 46,
    "KG Winter_ss_del": 99,
    "KG Winter_dss_del": 406,
    "KG Spring_theta_del": -0.622495407,
    "KG Spring_pr_1_99_del": 46,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 438,
    "G1 Fall_theta_del": -0.408290125,
    "G1 Fall_pr_1_99_del": 46,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 459,
    "G1 Winter_theta_del": -0.218582785,
    "G1 Winter_pr_1_99_del": 46,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 478,
    "G1 Spring_theta_del": 0.050303334,
    "G1 Spring_pr_1_99_del": 46,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 505,
    "G2 Fall_theta_del": 0.260737962,
    "G2 Fall_pr_1_99_del": 46,
    "G2 Fall_ss_del": 99,
    "G2 Fall_dss_del": 526,
    "G2 Winter_theta_del": 0.495516848,
    "G2 Winter_pr_1_99_del": 46,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 550,
    "G2 Spring_theta_del": 0.772905589,
    "G2 Spring_pr_1_99_del": 46,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 577
  },
  {
    "ID": 347,
    "KG Fall_theta_del": -1.232000355,
    "KG Fall_pr_1_99_del": 46,
    "KG Fall_ss_del": 99,
    "KG Fall_dss_del": 377,
    "KG Winter_theta_del": -0.9326978,
    "KG Winter_pr_1_99_del": 46,
    "KG Winter_ss_del": 99,
    "KG Winter_dss_del": 407,
    "KG Spring_theta_del": -0.610971657,
    "KG Spring_pr_1_99_del": 46,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 439,
    "G1 Fall_theta_del": -0.408183745,
    "G1 Fall_pr_1_99_del": 46,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 459,
    "G1 Winter_theta_del": -0.211740428,
    "G1 Winter_pr_1_99_del": 46,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 479,
    "G1 Spring_theta_del": 0.050816682,
    "G1 Spring_pr_1_99_del": 46,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 505,
    "G2 Fall_theta_del": 0.26529592,
    "G2 Fall_pr_1_99_del": 46,
    "G2 Fall_ss_del": 99,
    "G2 Fall_dss_del": 527,
    "G2 Winter_theta_del": 0.512087255,
    "G2 Winter_pr_1_99_del": 46,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 551,
    "G2 Spring_theta_del": 0.775169946,
    "G2 Spring_pr_1_99_del": 46,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 578
  },
  {
    "ID": 180,
    "KG Fall_theta_del": -1.231284058,
    "KG Fall_pr_1_99_del": 47,
    "KG Fall_ss_del": 99,
    "KG Fall_dss_del": 377,
    "KG Winter_theta_del": -0.931529409,
    "KG Winter_pr_1_99_del": 47,
    "KG Winter_ss_del": 99,
    "KG Winter_dss_del": 407,
    "KG Spring_theta_del": -0.609360746,
    "KG Spring_pr_1_99_del": 47,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 439,
    "G1 Fall_theta_del": -0.405911681,
    "G1 Fall_pr_1_99_del": 47,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 459,
    "G1 Winter_theta_del": -0.203074905,
    "G1 Winter_pr_1_99_del": 47,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 480,
    "G1 Spring_theta_del": 0.05569841,
    "G1 Spring_pr_1_99_del": 47,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 506,
    "G2 Fall_theta_del": 0.270405919,
    "G2 Fall_pr_1_99_del": 47,
    "G2 Fall_ss_del": 99,
    "G2 Fall_dss_del": 527,
    "G2 Winter_theta_del": 0.513614712,
    "G2 Winter_pr_1_99_del": 47,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 551,
    "G2 Spring_theta_del": 0.786297505,
    "G2 Spring_pr_1_99_del": 47,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 579
  },
  {
    "ID": 306,
    "KG Fall_theta_del": -1.227915738,
    "KG Fall_pr_1_99_del": 47,
    "KG Fall_ss_del": 99,
    "KG Fall_dss_del": 377,
    "KG Winter_theta_del": -0.922185751,
    "KG Winter_pr_1_99_del": 47,
    "KG Winter_ss_del": 99,
    "KG Winter_dss_del": 408,
    "KG Spring_theta_del": -0.605809239,
    "KG Spring_pr_1_99_del": 47,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 439,
    "G1 Fall_theta_del": -0.403391587,
    "G1 Fall_pr_1_99_del": 47,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 460,
    "G1 Winter_theta_del": -0.198707168,
    "G1 Winter_pr_1_99_del": 47,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 480,
    "G1 Spring_theta_del": 0.055780322,
    "G1 Spring_pr_1_99_del": 47,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 506,
    "G2 Fall_theta_del": 0.273565476,
    "G2 Fall_pr_1_99_del": 47,
    "G2 Fall_ss_del": 99,
    "G2 Fall_dss_del": 527,
    "G2 Winter_theta_del": 0.523021404,
    "G2 Winter_pr_1_99_del": 47,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 552,
    "G2 Spring_theta_del": 0.787456644,
    "G2 Spring_pr_1_99_del": 47,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 579
  },
  {
    "ID": 399,
    "KG Fall_theta_del": -1.217060947,
    "KG Fall_pr_1_99_del": 47,
    "KG Fall_ss_del": 99,
    "KG Fall_dss_del": 378,
    "KG Winter_theta_del": -0.918213005,
    "KG Winter_pr_1_99_del": 47,
    "KG Winter_ss_del": 99,
    "KG Winter_dss_del": 408,
    "KG Spring_theta_del": -0.600072693,
    "KG Spring_pr_1_99_del": 47,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 440,
    "G1 Fall_theta_del": -0.401846704,
    "G1 Fall_pr_1_99_del": 47,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 460,
    "G1 Winter_theta_del": -0.198225415,
    "G1 Winter_pr_1_99_del": 47,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 480,
    "G1 Spring_theta_del": 0.061942381,
    "G1 Spring_pr_1_99_del": 47,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 506,
    "G2 Fall_theta_del": 0.277654432,
    "G2 Fall_pr_1_99_del": 47,
    "G2 Fall_ss_del": 99,
    "G2 Fall_dss_del": 528,
    "G2 Winter_theta_del": 0.523040329,
    "G2 Winter_pr_1_99_del": 47,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 552,
    "G2 Spring_theta_del": 0.79031237,
    "G2 Spring_pr_1_99_del": 47,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 579
  },
  {
    "ID": 458,
    "KG Fall_theta_del": -1.200152054,
    "KG Fall_pr_1_99_del": 47,
    "KG Fall_ss_del": 99,
    "KG Fall_dss_del": 380,
    "KG Winter_theta_del": -0.912936554,
    "KG Winter_pr_1_99_del": 47,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 409,
    "KG Spring_theta_del": -0.590736017,
    "KG Spring_pr_1_99_del": 47,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 441,
    "G1 Fall_theta_del": -0.39524057,
    "G1 Fall_pr_1_99_del": 47,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 460,
    "G1 Winter_theta_del": -0.194508192,
    "G1 Winter_pr_1_99_del": 47,
    "G1 Winter_ss_del": 98,
    "G1 Winter_dss_del": 481,
    "G1 Spring_theta_del": 0.066108769,
    "G1 Spring_pr_1_99_del": 47,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 507,
    "G2 Fall_theta_del": 0.281593731,
    "G2 Fall_pr_1_99_del": 47,
    "G2 Fall_ss_del": 99,
    "G2 Fall_dss_del": 528,
    "G2 Winter_theta_del": 0.523836909,
    "G2 Winter_pr_1_99_del": 47,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 552,
    "G2 Spring_theta_del": 0.792430816,
    "G2 Spring_pr_1_99_del": 47,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 579
  },
  {
    "ID": 473,
    "KG Fall_theta_del": -1.199695896,
    "KG Fall_pr_1_99_del": 47,
    "KG Fall_ss_del": 99,
    "KG Fall_dss_del": 380,
    "KG Winter_theta_del": -0.910701336,
    "KG Winter_pr_1_99_del": 47,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 409,
    "KG Spring_theta_del": -0.586575134,
    "KG Spring_pr_1_99_del": 47,
    "KG Spring_ss_del": 99,
    "KG Spring_dss_del": 441,
    "G1 Fall_theta_del": -0.385494205,
    "G1 Fall_pr_1_99_del": 47,
    "G1 Fall_ss_del": 99,
    "G1 Fall_dss_del": 461,
    "G1 Winter_theta_del": -0.189854646,
    "G1 Winter_pr_1_99_del": 47,
    "G1 Winter_ss_del": 99,
    "G1 Winter_dss_del": 481,
    "G1 Spring_theta_del": 0.06636518,
    "G1 Spring_pr_1_99_del": 47,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 507,
    "G2 Fall_theta_del": 0.290141385,
    "G2 Fall_pr_1_99_del": 47,
    "G2 Fall_ss_del": 99,
    "G2 Fall_dss_del": 529,
    "G2 Winter_theta_del": 0.525898366,
    "G2 Winter_pr_1_99_del": 47,
    "G2 Winter_ss_del": 99,
    "G2 Winter_dss_del": 553,
    "G2 Spring_theta_del": 0.799346425,
    "G2 Spring_pr_1_99_del": 47,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 580
  },
  {
    "ID": 226,
    "KG Fall_theta_del": -1.198498225,
    "KG Fall_pr_1_99_del": 48,
    "KG Fall_ss_del": 99,
    "KG Fall_dss_del": 380,
    "KG Winter_theta_del": -0.904381839,
    "KG Winter_pr_1_99_del": 48,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 410,
    "KG Spring_theta_del": -0.579461084,
    "KG Spring_pr_1_99_del": 48,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 442,
    "G1 Fall_theta_del": -0.382488958,
    "G1 Fall_pr_1_99_del": 48,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 462,
    "G1 Winter_theta_del": -0.1887081,
    "G1 Winter_pr_1_99_del": 48,
    "G1 Winter_ss_del": 99,
    "G1 Winter_dss_del": 481,
    "G1 Spring_theta_del": 0.069557566,
    "G1 Spring_pr_1_99_del": 48,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 507,
    "G2 Fall_theta_del": 0.293238173,
    "G2 Fall_pr_1_99_del": 48,
    "G2 Fall_ss_del": 99,
    "G2 Fall_dss_del": 529,
    "G2 Winter_theta_del": 0.53581961,
    "G2 Winter_pr_1_99_del": 48,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 554,
    "G2 Spring_theta_del": 0.80121022,
    "G2 Spring_pr_1_99_del": 48,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 580
  },
  {
    "ID": 252,
    "KG Fall_theta_del": -1.182312019,
    "KG Fall_pr_1_99_del": 48,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 382,
    "KG Winter_theta_del": -0.901882021,
    "KG Winter_pr_1_99_del": 48,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 410,
    "KG Spring_theta_del": -0.574718256,
    "KG Spring_pr_1_99_del": 48,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 443,
    "G1 Fall_theta_del": -0.381264948,
    "G1 Fall_pr_1_99_del": 48,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 462,
    "G1 Winter_theta_del": -0.185208191,
    "G1 Winter_pr_1_99_del": 48,
    "G1 Winter_ss_del": 99,
    "G1 Winter_dss_del": 481,
    "G1 Spring_theta_del": 0.072655151,
    "G1 Spring_pr_1_99_del": 48,
    "G1 Spring_ss_del": 99,
    "G1 Spring_dss_del": 507,
    "G2 Fall_theta_del": 0.301928809,
    "G2 Fall_pr_1_99_del": 48,
    "G2 Fall_ss_del": 99,
    "G2 Fall_dss_del": 530,
    "G2 Winter_theta_del": 0.538864342,
    "G2 Winter_pr_1_99_del": 48,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 554,
    "G2 Spring_theta_del": 0.809641664,
    "G2 Spring_pr_1_99_del": 48,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 581
  },
  {
    "ID": 341,
    "KG Fall_theta_del": -1.181389058,
    "KG Fall_pr_1_99_del": 48,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 382,
    "KG Winter_theta_del": -0.895098303,
    "KG Winter_pr_1_99_del": 48,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 410,
    "KG Spring_theta_del": -0.568929963,
    "KG Spring_pr_1_99_del": 48,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 443,
    "G1 Fall_theta_del": -0.380335733,
    "G1 Fall_pr_1_99_del": 48,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 462,
    "G1 Winter_theta_del": -0.179418919,
    "G1 Winter_pr_1_99_del": 48,
    "G1 Winter_ss_del": 99,
    "G1 Winter_dss_del": 482,
    "G1 Spring_theta_del": 0.079611874,
    "G1 Spring_pr_1_99_del": 48,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 508,
    "G2 Fall_theta_del": 0.315856303,
    "G2 Fall_pr_1_99_del": 48,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 532,
    "G2 Winter_theta_del": 0.539870803,
    "G2 Winter_pr_1_99_del": 48,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 554,
    "G2 Spring_theta_del": 0.81402092,
    "G2 Spring_pr_1_99_del": 48,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 581
  },
  {
    "ID": 350,
    "KG Fall_theta_del": -1.178842936,
    "KG Fall_pr_1_99_del": 48,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 382,
    "KG Winter_theta_del": -0.891024506,
    "KG Winter_pr_1_99_del": 48,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 411,
    "KG Spring_theta_del": -0.567598645,
    "KG Spring_pr_1_99_del": 48,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 443,
    "G1 Fall_theta_del": -0.377217109,
    "G1 Fall_pr_1_99_del": 48,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 462,
    "G1 Winter_theta_del": -0.159387901,
    "G1 Winter_pr_1_99_del": 48,
    "G1 Winter_ss_del": 99,
    "G1 Winter_dss_del": 484,
    "G1 Spring_theta_del": 0.079640558,
    "G1 Spring_pr_1_99_del": 48,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 508,
    "G2 Fall_theta_del": 0.31622872,
    "G2 Fall_pr_1_99_del": 48,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 532,
    "G2 Winter_theta_del": 0.54261566,
    "G2 Winter_pr_1_99_del": 48,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 554,
    "G2 Spring_theta_del": 0.81528562,
    "G2 Spring_pr_1_99_del": 48,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 582
  },
  {
    "ID": 381,
    "KG Fall_theta_del": -1.177252806,
    "KG Fall_pr_1_99_del": 48,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 382,
    "KG Winter_theta_del": -0.890148092,
    "KG Winter_pr_1_99_del": 48,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 411,
    "KG Spring_theta_del": -0.566809677,
    "KG Spring_pr_1_99_del": 48,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 443,
    "G1 Fall_theta_del": -0.369039457,
    "G1 Fall_pr_1_99_del": 48,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 463,
    "G1 Winter_theta_del": -0.15314683,
    "G1 Winter_pr_1_99_del": 48,
    "G1 Winter_ss_del": 99,
    "G1 Winter_dss_del": 485,
    "G1 Spring_theta_del": 0.085723081,
    "G1 Spring_pr_1_99_del": 48,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 509,
    "G2 Fall_theta_del": 0.31856664,
    "G2 Fall_pr_1_99_del": 48,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 532,
    "G2 Winter_theta_del": 0.544312413,
    "G2 Winter_pr_1_99_del": 48,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 554,
    "G2 Spring_theta_del": 0.816263502,
    "G2 Spring_pr_1_99_del": 48,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 582
  },
  {
    "ID": 114,
    "KG Fall_theta_del": -1.174156935,
    "KG Fall_pr_1_99_del": 49,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 383,
    "KG Winter_theta_del": -0.889793394,
    "KG Winter_pr_1_99_del": 49,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 411,
    "KG Spring_theta_del": -0.564997595,
    "KG Spring_pr_1_99_del": 49,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 444,
    "G1 Fall_theta_del": -0.368942666,
    "G1 Fall_pr_1_99_del": 49,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 463,
    "G1 Winter_theta_del": -0.13853641,
    "G1 Winter_pr_1_99_del": 49,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 486,
    "G1 Spring_theta_del": 0.092676948,
    "G1 Spring_pr_1_99_del": 49,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 509,
    "G2 Fall_theta_del": 0.321625469,
    "G2 Fall_pr_1_99_del": 49,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 532,
    "G2 Winter_theta_del": 0.546848397,
    "G2 Winter_pr_1_99_del": 49,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 555,
    "G2 Spring_theta_del": 0.816612997,
    "G2 Spring_pr_1_99_del": 49,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 582
  },
  {
    "ID": 206,
    "KG Fall_theta_del": -1.170003778,
    "KG Fall_pr_1_99_del": 49,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 383,
    "KG Winter_theta_del": -0.889613484,
    "KG Winter_pr_1_99_del": 49,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 411,
    "KG Spring_theta_del": -0.561745342,
    "KG Spring_pr_1_99_del": 49,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 444,
    "G1 Fall_theta_del": -0.365288168,
    "G1 Fall_pr_1_99_del": 49,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 463,
    "G1 Winter_theta_del": -0.137892885,
    "G1 Winter_pr_1_99_del": 49,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 486,
    "G1 Spring_theta_del": 0.097852842,
    "G1 Spring_pr_1_99_del": 49,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 510,
    "G2 Fall_theta_del": 0.334691817,
    "G2 Fall_pr_1_99_del": 49,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 533,
    "G2 Winter_theta_del": 0.547571868,
    "G2 Winter_pr_1_99_del": 49,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 555,
    "G2 Spring_theta_del": 0.818791585,
    "G2 Spring_pr_1_99_del": 49,
    "G2 Spring_ss_del": 99,
    "G2 Spring_dss_del": 582
  },
  {
    "ID": 219,
    "KG Fall_theta_del": -1.167366314,
    "KG Fall_pr_1_99_del": 49,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 383,
    "KG Winter_theta_del": -0.886767829,
    "KG Winter_pr_1_99_del": 49,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 411,
    "KG Spring_theta_del": -0.561391266,
    "KG Spring_pr_1_99_del": 49,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 444,
    "G1 Fall_theta_del": -0.35646274,
    "G1 Fall_pr_1_99_del": 49,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 464,
    "G1 Winter_theta_del": -0.133169322,
    "G1 Winter_pr_1_99_del": 49,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 487,
    "G1 Spring_theta_del": 0.098635513,
    "G1 Spring_pr_1_99_del": 49,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 510,
    "G2 Fall_theta_del": 0.339475738,
    "G2 Fall_pr_1_99_del": 49,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 534,
    "G2 Winter_theta_del": 0.550342243,
    "G2 Winter_pr_1_99_del": 49,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 555,
    "G2 Spring_theta_del": 0.822580563,
    "G2 Spring_pr_1_99_del": 49,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 582
  },
  {
    "ID": 229,
    "KG Fall_theta_del": -1.166158625,
    "KG Fall_pr_1_99_del": 49,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 383,
    "KG Winter_theta_del": -0.884375103,
    "KG Winter_pr_1_99_del": 49,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 412,
    "KG Spring_theta_del": -0.561210106,
    "KG Spring_pr_1_99_del": 49,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 444,
    "G1 Fall_theta_del": -0.344204818,
    "G1 Fall_pr_1_99_del": 49,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 466,
    "G1 Winter_theta_del": -0.125780825,
    "G1 Winter_pr_1_99_del": 49,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 487,
    "G1 Spring_theta_del": 0.100064541,
    "G1 Spring_pr_1_99_del": 49,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 510,
    "G2 Fall_theta_del": 0.342666287,
    "G2 Fall_pr_1_99_del": 49,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 534,
    "G2 Winter_theta_del": 0.568133456,
    "G2 Winter_pr_1_99_del": 49,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 557,
    "G2 Spring_theta_del": 0.826285848,
    "G2 Spring_pr_1_99_del": 49,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 583
  },
  {
    "ID": 354,
    "KG Fall_theta_del": -1.162045695,
    "KG Fall_pr_1_99_del": 49,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 384,
    "KG Winter_theta_del": -0.873890735,
    "KG Winter_pr_1_99_del": 49,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 413,
    "KG Spring_theta_del": -0.560867731,
    "KG Spring_pr_1_99_del": 49,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 444,
    "G1 Fall_theta_del": -0.343206955,
    "G1 Fall_pr_1_99_del": 49,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 466,
    "G1 Winter_theta_del": -0.119031622,
    "G1 Winter_pr_1_99_del": 49,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 488,
    "G1 Spring_theta_del": 0.101258985,
    "G1 Spring_pr_1_99_del": 49,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 510,
    "G2 Fall_theta_del": 0.347567542,
    "G2 Fall_pr_1_99_del": 49,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 535,
    "G2 Winter_theta_del": 0.568584387,
    "G2 Winter_pr_1_99_del": 49,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 557,
    "G2 Spring_theta_del": 0.834686966,
    "G2 Spring_pr_1_99_del": 49,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 583
  },
  {
    "ID": 24,
    "KG Fall_theta_del": -1.161463077,
    "KG Fall_pr_1_99_del": 50,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 384,
    "KG Winter_theta_del": -0.87165688,
    "KG Winter_pr_1_99_del": 50,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 413,
    "KG Spring_theta_del": -0.560218198,
    "KG Spring_pr_1_99_del": 50,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 444,
    "G1 Fall_theta_del": -0.336972811,
    "G1 Fall_pr_1_99_del": 50,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 466,
    "G1 Winter_theta_del": -0.117025928,
    "G1 Winter_pr_1_99_del": 50,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 488,
    "G1 Spring_theta_del": 0.106745851,
    "G1 Spring_pr_1_99_del": 50,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 511,
    "G2 Fall_theta_del": 0.350758834,
    "G2 Fall_pr_1_99_del": 50,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 535,
    "G2 Winter_theta_del": 0.577988506,
    "G2 Winter_pr_1_99_del": 50,
    "G2 Winter_ss_del": 100,
    "G2 Winter_dss_del": 558,
    "G2 Spring_theta_del": 0.841417219,
    "G2 Spring_pr_1_99_del": 50,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 584
  },
  {
    "ID": 87,
    "KG Fall_theta_del": -1.153600823,
    "KG Fall_pr_1_99_del": 50,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 385,
    "KG Winter_theta_del": -0.871560305,
    "KG Winter_pr_1_99_del": 50,
    "KG Winter_ss_del": 100,
    "KG Winter_dss_del": 413,
    "KG Spring_theta_del": -0.556713649,
    "KG Spring_pr_1_99_del": 50,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 444,
    "G1 Fall_theta_del": -0.336558051,
    "G1 Fall_pr_1_99_del": 50,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 466,
    "G1 Winter_theta_del": -0.112161551,
    "G1 Winter_pr_1_99_del": 50,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 489,
    "G1 Spring_theta_del": 0.111551005,
    "G1 Spring_pr_1_99_del": 50,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 511,
    "G2 Fall_theta_del": 0.352447858,
    "G2 Fall_pr_1_99_del": 50,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 535,
    "G2 Winter_theta_del": 0.587542689,
    "G2 Winter_pr_1_99_del": 50,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 559,
    "G2 Spring_theta_del": 0.841660621,
    "G2 Spring_pr_1_99_del": 50,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 584
  },
  {
    "ID": 161,
    "KG Fall_theta_del": -1.151984109,
    "KG Fall_pr_1_99_del": 50,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 385,
    "KG Winter_theta_del": -0.852349832,
    "KG Winter_pr_1_99_del": 50,
    "KG Winter_ss_del": 101,
    "KG Winter_dss_del": 415,
    "KG Spring_theta_del": -0.554386272,
    "KG Spring_pr_1_99_del": 50,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 445,
    "G1 Fall_theta_del": -0.33313368,
    "G1 Fall_pr_1_99_del": 50,
    "G1 Fall_ss_del": 100,
    "G1 Fall_dss_del": 467,
    "G1 Winter_theta_del": -0.110206241,
    "G1 Winter_pr_1_99_del": 50,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 489,
    "G1 Spring_theta_del": 0.112458031,
    "G1 Spring_pr_1_99_del": 50,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 511,
    "G2 Fall_theta_del": 0.355822529,
    "G2 Fall_pr_1_99_del": 50,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 536,
    "G2 Winter_theta_del": 0.598661775,
    "G2 Winter_pr_1_99_del": 50,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 560,
    "G2 Spring_theta_del": 0.842740003,
    "G2 Spring_pr_1_99_del": 50,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 584
  },
  {
    "ID": 382,
    "KG Fall_theta_del": -1.15044368,
    "KG Fall_pr_1_99_del": 50,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 385,
    "KG Winter_theta_del": -0.850077958,
    "KG Winter_pr_1_99_del": 50,
    "KG Winter_ss_del": 101,
    "KG Winter_dss_del": 415,
    "KG Spring_theta_del": -0.548041518,
    "KG Spring_pr_1_99_del": 50,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 445,
    "G1 Fall_theta_del": -0.326418621,
    "G1 Fall_pr_1_99_del": 50,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 467,
    "G1 Winter_theta_del": -0.10647057,
    "G1 Winter_pr_1_99_del": 50,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 489,
    "G1 Spring_theta_del": 0.113740648,
    "G1 Spring_pr_1_99_del": 50,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 511,
    "G2 Fall_theta_del": 0.358186626,
    "G2 Fall_pr_1_99_del": 50,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 536,
    "G2 Winter_theta_del": 0.603157857,
    "G2 Winter_pr_1_99_del": 50,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 560,
    "G2 Spring_theta_del": 0.846890106,
    "G2 Spring_pr_1_99_del": 50,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 585
  },
  {
    "ID": 407,
    "KG Fall_theta_del": -1.142658052,
    "KG Fall_pr_1_99_del": 50,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 386,
    "KG Winter_theta_del": -0.839194835,
    "KG Winter_pr_1_99_del": 50,
    "KG Winter_ss_del": 101,
    "KG Winter_dss_del": 416,
    "KG Spring_theta_del": -0.547653238,
    "KG Spring_pr_1_99_del": 50,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 445,
    "G1 Fall_theta_del": -0.316246633,
    "G1 Fall_pr_1_99_del": 50,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 468,
    "G1 Winter_theta_del": -0.100787604,
    "G1 Winter_pr_1_99_del": 50,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 490,
    "G1 Spring_theta_del": 0.124382373,
    "G1 Spring_pr_1_99_del": 50,
    "G1 Spring_ss_del": 100,
    "G1 Spring_dss_del": 512,
    "G2 Fall_theta_del": 0.358222873,
    "G2 Fall_pr_1_99_del": 50,
    "G2 Fall_ss_del": 100,
    "G2 Fall_dss_del": 536,
    "G2 Winter_theta_del": 0.608163014,
    "G2 Winter_pr_1_99_del": 50,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 561,
    "G2 Spring_theta_del": 0.848662325,
    "G2 Spring_pr_1_99_del": 50,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 585
  },
  {
    "ID": 72,
    "KG Fall_theta_del": -1.139487792,
    "KG Fall_pr_1_99_del": 51,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 386,
    "KG Winter_theta_del": -0.835879436,
    "KG Winter_pr_1_99_del": 51,
    "KG Winter_ss_del": 101,
    "KG Winter_dss_del": 416,
    "KG Spring_theta_del": -0.532029724,
    "KG Spring_pr_1_99_del": 51,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 447,
    "G1 Fall_theta_del": -0.315290423,
    "G1 Fall_pr_1_99_del": 51,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 468,
    "G1 Winter_theta_del": -0.099544209,
    "G1 Winter_pr_1_99_del": 51,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 490,
    "G1 Spring_theta_del": 0.127210317,
    "G1 Spring_pr_1_99_del": 51,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 513,
    "G2 Fall_theta_del": 0.365321284,
    "G2 Fall_pr_1_99_del": 51,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 537,
    "G2 Winter_theta_del": 0.610401581,
    "G2 Winter_pr_1_99_del": 51,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 561,
    "G2 Spring_theta_del": 0.850295575,
    "G2 Spring_pr_1_99_del": 51,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 585
  },
  {
    "ID": 134,
    "KG Fall_theta_del": -1.134620681,
    "KG Fall_pr_1_99_del": 51,
    "KG Fall_ss_del": 100,
    "KG Fall_dss_del": 387,
    "KG Winter_theta_del": -0.826799299,
    "KG Winter_pr_1_99_del": 51,
    "KG Winter_ss_del": 101,
    "KG Winter_dss_del": 417,
    "KG Spring_theta_del": -0.529447858,
    "KG Spring_pr_1_99_del": 51,
    "KG Spring_ss_del": 100,
    "KG Spring_dss_del": 447,
    "G1 Fall_theta_del": -0.314471045,
    "G1 Fall_pr_1_99_del": 51,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 469,
    "G1 Winter_theta_del": -0.099476772,
    "G1 Winter_pr_1_99_del": 51,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 490,
    "G1 Spring_theta_del": 0.127917665,
    "G1 Spring_pr_1_99_del": 51,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 513,
    "G2 Fall_theta_del": 0.367372383,
    "G2 Fall_pr_1_99_del": 51,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 537,
    "G2 Winter_theta_del": 0.614199153,
    "G2 Winter_pr_1_99_del": 51,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 561,
    "G2 Spring_theta_del": 0.86067124,
    "G2 Spring_pr_1_99_del": 51,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 586
  },
  {
    "ID": 149,
    "KG Fall_theta_del": -1.126822927,
    "KG Fall_pr_1_99_del": 51,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 387,
    "KG Winter_theta_del": -0.825169107,
    "KG Winter_pr_1_99_del": 51,
    "KG Winter_ss_del": 101,
    "KG Winter_dss_del": 417,
    "KG Spring_theta_del": -0.520665094,
    "KG Spring_pr_1_99_del": 51,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 448,
    "G1 Fall_theta_del": -0.313460144,
    "G1 Fall_pr_1_99_del": 51,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 469,
    "G1 Winter_theta_del": -0.099241677,
    "G1 Winter_pr_1_99_del": 51,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 490,
    "G1 Spring_theta_del": 0.128659027,
    "G1 Spring_pr_1_99_del": 51,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 513,
    "G2 Fall_theta_del": 0.369201046,
    "G2 Fall_pr_1_99_del": 51,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 537,
    "G2 Winter_theta_del": 0.614372668,
    "G2 Winter_pr_1_99_del": 51,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 561,
    "G2 Spring_theta_del": 0.86873852,
    "G2 Spring_pr_1_99_del": 51,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 587
  },
  {
    "ID": 332,
    "KG Fall_theta_del": -1.126504078,
    "KG Fall_pr_1_99_del": 51,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 387,
    "KG Winter_theta_del": -0.821597591,
    "KG Winter_pr_1_99_del": 51,
    "KG Winter_ss_del": 101,
    "KG Winter_dss_del": 418,
    "KG Spring_theta_del": -0.517658133,
    "KG Spring_pr_1_99_del": 51,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 448,
    "G1 Fall_theta_del": -0.31305036,
    "G1 Fall_pr_1_99_del": 51,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 469,
    "G1 Winter_theta_del": -0.096431965,
    "G1 Winter_pr_1_99_del": 51,
    "G1 Winter_ss_del": 100,
    "G1 Winter_dss_del": 490,
    "G1 Spring_theta_del": 0.129479494,
    "G1 Spring_pr_1_99_del": 51,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 513,
    "G2 Fall_theta_del": 0.371201982,
    "G2 Fall_pr_1_99_del": 51,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 537,
    "G2 Winter_theta_del": 0.614519119,
    "G2 Winter_pr_1_99_del": 51,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 561,
    "G2 Spring_theta_del": 0.870551271,
    "G2 Spring_pr_1_99_del": 51,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 587
  },
  {
    "ID": 377,
    "KG Fall_theta_del": -1.119296326,
    "KG Fall_pr_1_99_del": 51,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 388,
    "KG Winter_theta_del": -0.818749943,
    "KG Winter_pr_1_99_del": 51,
    "KG Winter_ss_del": 101,
    "KG Winter_dss_del": 418,
    "KG Spring_theta_del": -0.51729514,
    "KG Spring_pr_1_99_del": 51,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 448,
    "G1 Fall_theta_del": -0.312720174,
    "G1 Fall_pr_1_99_del": 51,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 469,
    "G1 Winter_theta_del": -0.088142734,
    "G1 Winter_pr_1_99_del": 51,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 491,
    "G1 Spring_theta_del": 0.130884653,
    "G1 Spring_pr_1_99_del": 51,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 513,
    "G2 Fall_theta_del": 0.372049069,
    "G2 Fall_pr_1_99_del": 51,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 537,
    "G2 Winter_theta_del": 0.615478716,
    "G2 Winter_pr_1_99_del": 51,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 562,
    "G2 Spring_theta_del": 0.870972824,
    "G2 Spring_pr_1_99_del": 51,
    "G2 Spring_ss_del": 100,
    "G2 Spring_dss_del": 587
  },
  {
    "ID": 58,
    "KG Fall_theta_del": -1.118519923,
    "KG Fall_pr_1_99_del": 52,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 388,
    "KG Winter_theta_del": -0.812216062,
    "KG Winter_pr_1_99_del": 52,
    "KG Winter_ss_del": 101,
    "KG Winter_dss_del": 419,
    "KG Spring_theta_del": -0.516476233,
    "KG Spring_pr_1_99_del": 52,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 448,
    "G1 Fall_theta_del": -0.308905227,
    "G1 Fall_pr_1_99_del": 52,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 469,
    "G1 Winter_theta_del": -0.078148278,
    "G1 Winter_pr_1_99_del": 52,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 492,
    "G1 Spring_theta_del": 0.133029015,
    "G1 Spring_pr_1_99_del": 52,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 513,
    "G2 Fall_theta_del": 0.373097973,
    "G2 Fall_pr_1_99_del": 52,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 537,
    "G2 Winter_theta_del": 0.61705222,
    "G2 Winter_pr_1_99_del": 52,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 562,
    "G2 Spring_theta_del": 0.876608411,
    "G2 Spring_pr_1_99_del": 52,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 588
  },
  {
    "ID": 62,
    "KG Fall_theta_del": -1.118224663,
    "KG Fall_pr_1_99_del": 52,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 388,
    "KG Winter_theta_del": -0.784061407,
    "KG Winter_pr_1_99_del": 52,
    "KG Winter_ss_del": 102,
    "KG Winter_dss_del": 422,
    "KG Spring_theta_del": -0.516440107,
    "KG Spring_pr_1_99_del": 52,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 448,
    "G1 Fall_theta_del": -0.308761063,
    "G1 Fall_pr_1_99_del": 52,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 469,
    "G1 Winter_theta_del": -0.078093361,
    "G1 Winter_pr_1_99_del": 52,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 492,
    "G1 Spring_theta_del": 0.135605987,
    "G1 Spring_pr_1_99_del": 52,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 514,
    "G2 Fall_theta_del": 0.380171195,
    "G2 Fall_pr_1_99_del": 52,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 538,
    "G2 Winter_theta_del": 0.619236596,
    "G2 Winter_pr_1_99_del": 52,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 562,
    "G2 Spring_theta_del": 0.879921605,
    "G2 Spring_pr_1_99_del": 52,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 588
  },
  {
    "ID": 95,
    "KG Fall_theta_del": -1.116668071,
    "KG Fall_pr_1_99_del": 52,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 388,
    "KG Winter_theta_del": -0.783535213,
    "KG Winter_pr_1_99_del": 52,
    "KG Winter_ss_del": 102,
    "KG Winter_dss_del": 422,
    "KG Spring_theta_del": -0.515819889,
    "KG Spring_pr_1_99_del": 52,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 448,
    "G1 Fall_theta_del": -0.30774627,
    "G1 Fall_pr_1_99_del": 52,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 469,
    "G1 Winter_theta_del": -0.075992341,
    "G1 Winter_pr_1_99_del": 52,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 492,
    "G1 Spring_theta_del": 0.140516713,
    "G1 Spring_pr_1_99_del": 52,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 514,
    "G2 Fall_theta_del": 0.380465124,
    "G2 Fall_pr_1_99_del": 52,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 538,
    "G2 Winter_theta_del": 0.6227328,
    "G2 Winter_pr_1_99_del": 52,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 562,
    "G2 Spring_theta_del": 0.882867209,
    "G2 Spring_pr_1_99_del": 52,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 588
  },
  {
    "ID": 154,
    "KG Fall_theta_del": -1.116282237,
    "KG Fall_pr_1_99_del": 52,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 388,
    "KG Winter_theta_del": -0.783191892,
    "KG Winter_pr_1_99_del": 52,
    "KG Winter_ss_del": 102,
    "KG Winter_dss_del": 422,
    "KG Spring_theta_del": -0.515412242,
    "KG Spring_pr_1_99_del": 52,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 448,
    "G1 Fall_theta_del": -0.292148136,
    "G1 Fall_pr_1_99_del": 52,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 471,
    "G1 Winter_theta_del": -0.073435651,
    "G1 Winter_pr_1_99_del": 52,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 493,
    "G1 Spring_theta_del": 0.15527247,
    "G1 Spring_pr_1_99_del": 52,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 516,
    "G2 Fall_theta_del": 0.382915027,
    "G2 Fall_pr_1_99_del": 52,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 538,
    "G2 Winter_theta_del": 0.622794631,
    "G2 Winter_pr_1_99_del": 52,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 562,
    "G2 Spring_theta_del": 0.883633591,
    "G2 Spring_pr_1_99_del": 52,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 588
  },
  {
    "ID": 386,
    "KG Fall_theta_del": -1.114147608,
    "KG Fall_pr_1_99_del": 52,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 389,
    "KG Winter_theta_del": -0.774299984,
    "KG Winter_pr_1_99_del": 52,
    "KG Winter_ss_del": 102,
    "KG Winter_dss_del": 423,
    "KG Spring_theta_del": -0.515043967,
    "KG Spring_pr_1_99_del": 52,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 448,
    "G1 Fall_theta_del": -0.290877041,
    "G1 Fall_pr_1_99_del": 52,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 471,
    "G1 Winter_theta_del": -0.067915193,
    "G1 Winter_pr_1_99_del": 52,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 493,
    "G1 Spring_theta_del": 0.15755192,
    "G1 Spring_pr_1_99_del": 52,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 516,
    "G2 Fall_theta_del": 0.388331047,
    "G2 Fall_pr_1_99_del": 52,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 539,
    "G2 Winter_theta_del": 0.631290468,
    "G2 Winter_pr_1_99_del": 52,
    "G2 Winter_ss_del": 101,
    "G2 Winter_dss_del": 563,
    "G2 Spring_theta_del": 0.883789929,
    "G2 Spring_pr_1_99_del": 52,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 588
  },
  {
    "ID": 52,
    "KG Fall_theta_del": -1.108163199,
    "KG Fall_pr_1_99_del": 53,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 389,
    "KG Winter_theta_del": -0.765387024,
    "KG Winter_pr_1_99_del": 53,
    "KG Winter_ss_del": 102,
    "KG Winter_dss_del": 423,
    "KG Spring_theta_del": -0.509589236,
    "KG Spring_pr_1_99_del": 53,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 449,
    "G1 Fall_theta_del": -0.285606848,
    "G1 Fall_pr_1_99_del": 53,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 471,
    "G1 Winter_theta_del": -0.065641169,
    "G1 Winter_pr_1_99_del": 53,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 493,
    "G1 Spring_theta_del": 0.162863804,
    "G1 Spring_pr_1_99_del": 53,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 516,
    "G2 Fall_theta_del": 0.402564536,
    "G2 Fall_pr_1_99_del": 53,
    "G2 Fall_ss_del": 101,
    "G2 Fall_dss_del": 540,
    "G2 Winter_theta_del": 0.643371667,
    "G2 Winter_pr_1_99_del": 53,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 564,
    "G2 Spring_theta_del": 0.884235492,
    "G2 Spring_pr_1_99_del": 53,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 588
  },
  {
    "ID": 135,
    "KG Fall_theta_del": -1.101306523,
    "KG Fall_pr_1_99_del": 53,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 390,
    "KG Winter_theta_del": -0.755575501,
    "KG Winter_pr_1_99_del": 53,
    "KG Winter_ss_del": 102,
    "KG Winter_dss_del": 424,
    "KG Spring_theta_del": -0.505394479,
    "KG Spring_pr_1_99_del": 53,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 449,
    "G1 Fall_theta_del": -0.283374599,
    "G1 Fall_pr_1_99_del": 53,
    "G1 Fall_ss_del": 101,
    "G1 Fall_dss_del": 472,
    "G1 Winter_theta_del": -0.062966733,
    "G1 Winter_pr_1_99_del": 53,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 494,
    "G1 Spring_theta_del": 0.170631083,
    "G1 Spring_pr_1_99_del": 53,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 517,
    "G2 Fall_theta_del": 0.413183197,
    "G2 Fall_pr_1_99_del": 53,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 541,
    "G2 Winter_theta_del": 0.657680303,
    "G2 Winter_pr_1_99_del": 53,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 566,
    "G2 Spring_theta_del": 0.885564099,
    "G2 Spring_pr_1_99_del": 53,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 589
  },
  {
    "ID": 157,
    "KG Fall_theta_del": -1.095609541,
    "KG Fall_pr_1_99_del": 53,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 390,
    "KG Winter_theta_del": -0.751260295,
    "KG Winter_pr_1_99_del": 53,
    "KG Winter_ss_del": 102,
    "KG Winter_dss_del": 425,
    "KG Spring_theta_del": -0.502535174,
    "KG Spring_pr_1_99_del": 53,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 450,
    "G1 Fall_theta_del": -0.279343138,
    "G1 Fall_pr_1_99_del": 53,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 472,
    "G1 Winter_theta_del": -0.061608285,
    "G1 Winter_pr_1_99_del": 53,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 494,
    "G1 Spring_theta_del": 0.173380554,
    "G1 Spring_pr_1_99_del": 53,
    "G1 Spring_ss_del": 101,
    "G1 Spring_dss_del": 517,
    "G2 Fall_theta_del": 0.413428698,
    "G2 Fall_pr_1_99_del": 53,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 541,
    "G2 Winter_theta_del": 0.663666147,
    "G2 Winter_pr_1_99_del": 53,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 566,
    "G2 Spring_theta_del": 0.887578168,
    "G2 Spring_pr_1_99_del": 53,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 589
  },
  {
    "ID": 168,
    "KG Fall_theta_del": -1.084394647,
    "KG Fall_pr_1_99_del": 53,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 392,
    "KG Winter_theta_del": -0.747838818,
    "KG Winter_pr_1_99_del": 53,
    "KG Winter_ss_del": 102,
    "KG Winter_dss_del": 425,
    "KG Spring_theta_del": -0.496987102,
    "KG Spring_pr_1_99_del": 53,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 450,
    "G1 Fall_theta_del": -0.2791703,
    "G1 Fall_pr_1_99_del": 53,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 472,
    "G1 Winter_theta_del": -0.061546832,
    "G1 Winter_pr_1_99_del": 53,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 494,
    "G1 Spring_theta_del": 0.177648196,
    "G1 Spring_pr_1_99_del": 53,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 518,
    "G2 Fall_theta_del": 0.413549408,
    "G2 Fall_pr_1_99_del": 53,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 541,
    "G2 Winter_theta_del": 0.666093815,
    "G2 Winter_pr_1_99_del": 53,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 567,
    "G2 Spring_theta_del": 0.892802229,
    "G2 Spring_pr_1_99_del": 53,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 589
  },
  {
    "ID": 178,
    "KG Fall_theta_del": -1.07931505,
    "KG Fall_pr_1_99_del": 53,
    "KG Fall_ss_del": 101,
    "KG Fall_dss_del": 392,
    "KG Winter_theta_del": -0.743187289,
    "KG Winter_pr_1_99_del": 53,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 426,
    "KG Spring_theta_del": -0.496537823,
    "KG Spring_pr_1_99_del": 53,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 450,
    "G1 Fall_theta_del": -0.278771569,
    "G1 Fall_pr_1_99_del": 53,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 472,
    "G1 Winter_theta_del": -0.057754332,
    "G1 Winter_pr_1_99_del": 53,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 494,
    "G1 Spring_theta_del": 0.180182753,
    "G1 Spring_pr_1_99_del": 53,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 518,
    "G2 Fall_theta_del": 0.413655865,
    "G2 Fall_pr_1_99_del": 53,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 541,
    "G2 Winter_theta_del": 0.668910121,
    "G2 Winter_pr_1_99_del": 53,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 567,
    "G2 Spring_theta_del": 0.899800565,
    "G2 Spring_pr_1_99_del": 53,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 590
  },
  {
    "ID": 2,
    "KG Fall_theta_del": -1.073266584,
    "KG Fall_pr_1_99_del": 54,
    "KG Fall_ss_del": 102,
    "KG Fall_dss_del": 393,
    "KG Winter_theta_del": -0.73085038,
    "KG Winter_pr_1_99_del": 54,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 427,
    "KG Spring_theta_del": -0.496010303,
    "KG Spring_pr_1_99_del": 54,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 450,
    "G1 Fall_theta_del": -0.277757619,
    "G1 Fall_pr_1_99_del": 54,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 472,
    "G1 Winter_theta_del": -0.055068357,
    "G1 Winter_pr_1_99_del": 54,
    "G1 Winter_ss_del": 101,
    "G1 Winter_dss_del": 494,
    "G1 Spring_theta_del": 0.192406435,
    "G1 Spring_pr_1_99_del": 54,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 519,
    "G2 Fall_theta_del": 0.416001703,
    "G2 Fall_pr_1_99_del": 54,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 542,
    "G2 Winter_theta_del": 0.671829258,
    "G2 Winter_pr_1_99_del": 54,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 567,
    "G2 Spring_theta_del": 0.904942881,
    "G2 Spring_pr_1_99_del": 54,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 590
  },
  {
    "ID": 7,
    "KG Fall_theta_del": -1.070701125,
    "KG Fall_pr_1_99_del": 54,
    "KG Fall_ss_del": 102,
    "KG Fall_dss_del": 393,
    "KG Winter_theta_del": -0.72812434,
    "KG Winter_pr_1_99_del": 54,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 427,
    "KG Spring_theta_del": -0.490774065,
    "KG Spring_pr_1_99_del": 54,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 451,
    "G1 Fall_theta_del": -0.260651424,
    "G1 Fall_pr_1_99_del": 54,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 474,
    "G1 Winter_theta_del": -0.030733467,
    "G1 Winter_pr_1_99_del": 54,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 497,
    "G1 Spring_theta_del": 0.19335729,
    "G1 Spring_pr_1_99_del": 54,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 519,
    "G2 Fall_theta_del": 0.419496486,
    "G2 Fall_pr_1_99_del": 54,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 542,
    "G2 Winter_theta_del": 0.676449175,
    "G2 Winter_pr_1_99_del": 54,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 568,
    "G2 Spring_theta_del": 0.913763927,
    "G2 Spring_pr_1_99_del": 54,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 591
  },
  {
    "ID": 151,
    "KG Fall_theta_del": -1.068544088,
    "KG Fall_pr_1_99_del": 54,
    "KG Fall_ss_del": 102,
    "KG Fall_dss_del": 393,
    "KG Winter_theta_del": -0.722060424,
    "KG Winter_pr_1_99_del": 54,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 428,
    "KG Spring_theta_del": -0.488335996,
    "KG Spring_pr_1_99_del": 54,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 451,
    "G1 Fall_theta_del": -0.256848268,
    "G1 Fall_pr_1_99_del": 54,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 474,
    "G1 Winter_theta_del": -0.028146567,
    "G1 Winter_pr_1_99_del": 54,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 497,
    "G1 Spring_theta_del": 0.19383826,
    "G1 Spring_pr_1_99_del": 54,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 519,
    "G2 Fall_theta_del": 0.420963178,
    "G2 Fall_pr_1_99_del": 54,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 542,
    "G2 Winter_theta_del": 0.677124274,
    "G2 Winter_pr_1_99_del": 54,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 568,
    "G2 Spring_theta_del": 0.916381856,
    "G2 Spring_pr_1_99_del": 54,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 592
  },
  {
    "ID": 355,
    "KG Fall_theta_del": -1.066315219,
    "KG Fall_pr_1_99_del": 54,
    "KG Fall_ss_del": 102,
    "KG Fall_dss_del": 393,
    "KG Winter_theta_del": -0.719767826,
    "KG Winter_pr_1_99_del": 54,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 428,
    "KG Spring_theta_del": -0.484402926,
    "KG Spring_pr_1_99_del": 54,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 452,
    "G1 Fall_theta_del": -0.256381962,
    "G1 Fall_pr_1_99_del": 54,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 474,
    "G1 Winter_theta_del": -0.025285094,
    "G1 Winter_pr_1_99_del": 54,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 497,
    "G1 Spring_theta_del": 0.194019914,
    "G1 Spring_pr_1_99_del": 54,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 519,
    "G2 Fall_theta_del": 0.421676064,
    "G2 Fall_pr_1_99_del": 54,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 542,
    "G2 Winter_theta_del": 0.679730172,
    "G2 Winter_pr_1_99_del": 54,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 568,
    "G2 Spring_theta_del": 0.916725078,
    "G2 Spring_pr_1_99_del": 54,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 592
  },
  {
    "ID": 408,
    "KG Fall_theta_del": -1.049902809,
    "KG Fall_pr_1_99_del": 54,
    "KG Fall_ss_del": 102,
    "KG Fall_dss_del": 395,
    "KG Winter_theta_del": -0.717434515,
    "KG Winter_pr_1_99_del": 54,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 428,
    "KG Spring_theta_del": -0.483834271,
    "KG Spring_pr_1_99_del": 54,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 452,
    "G1 Fall_theta_del": -0.255631209,
    "G1 Fall_pr_1_99_del": 54,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 474,
    "G1 Winter_theta_del": -0.016619584,
    "G1 Winter_pr_1_99_del": 54,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 498,
    "G1 Spring_theta_del": 0.194550462,
    "G1 Spring_pr_1_99_del": 54,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 519,
    "G2 Fall_theta_del": 0.43229772,
    "G2 Fall_pr_1_99_del": 54,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 543,
    "G2 Winter_theta_del": 0.683356068,
    "G2 Winter_pr_1_99_del": 54,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 568,
    "G2 Spring_theta_del": 0.918388089,
    "G2 Spring_pr_1_99_del": 54,
    "G2 Spring_ss_del": 101,
    "G2 Spring_dss_del": 592
  },
  {
    "ID": 3,
    "KG Fall_theta_del": -1.041389641,
    "KG Fall_pr_1_99_del": 55,
    "KG Fall_ss_del": 102,
    "KG Fall_dss_del": 396,
    "KG Winter_theta_del": -0.714166307,
    "KG Winter_pr_1_99_del": 55,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 429,
    "KG Spring_theta_del": -0.478625815,
    "KG Spring_pr_1_99_del": 55,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 452,
    "G1 Fall_theta_del": -0.24096797,
    "G1 Fall_pr_1_99_del": 55,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 476,
    "G1 Winter_theta_del": -0.01496601,
    "G1 Winter_pr_1_99_del": 55,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 499,
    "G1 Spring_theta_del": 0.194952283,
    "G1 Spring_pr_1_99_del": 55,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 519,
    "G2 Fall_theta_del": 0.434001579,
    "G2 Fall_pr_1_99_del": 55,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 543,
    "G2 Winter_theta_del": 0.686499569,
    "G2 Winter_pr_1_99_del": 55,
    "G2 Winter_ss_del": 102,
    "G2 Winter_dss_del": 569,
    "G2 Spring_theta_del": 0.929541533,
    "G2 Spring_pr_1_99_del": 55,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 593
  },
  {
    "ID": 131,
    "KG Fall_theta_del": -1.040734546,
    "KG Fall_pr_1_99_del": 55,
    "KG Fall_ss_del": 102,
    "KG Fall_dss_del": 396,
    "KG Winter_theta_del": -0.712664139,
    "KG Winter_pr_1_99_del": 55,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 429,
    "KG Spring_theta_del": -0.475417855,
    "KG Spring_pr_1_99_del": 55,
    "KG Spring_ss_del": 101,
    "KG Spring_dss_del": 452,
    "G1 Fall_theta_del": -0.240567731,
    "G1 Fall_pr_1_99_del": 55,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 476,
    "G1 Winter_theta_del": -0.011525144,
    "G1 Winter_pr_1_99_del": 55,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 499,
    "G1 Spring_theta_del": 0.199656943,
    "G1 Spring_pr_1_99_del": 55,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 520,
    "G2 Fall_theta_del": 0.440551231,
    "G2 Fall_pr_1_99_del": 55,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 544,
    "G2 Winter_theta_del": 0.688401724,
    "G2 Winter_pr_1_99_del": 55,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 569,
    "G2 Spring_theta_del": 0.931552972,
    "G2 Spring_pr_1_99_del": 55,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 593
  },
  {
    "ID": 305,
    "KG Fall_theta_del": -1.040302669,
    "KG Fall_pr_1_99_del": 55,
    "KG Fall_ss_del": 102,
    "KG Fall_dss_del": 396,
    "KG Winter_theta_del": -0.708461012,
    "KG Winter_pr_1_99_del": 55,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 429,
    "KG Spring_theta_del": -0.473927096,
    "KG Spring_pr_1_99_del": 55,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 453,
    "G1 Fall_theta_del": -0.235547325,
    "G1 Fall_pr_1_99_del": 55,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 476,
    "G1 Winter_theta_del": -0.005184944,
    "G1 Winter_pr_1_99_del": 55,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 499,
    "G1 Spring_theta_del": 0.199881984,
    "G1 Spring_pr_1_99_del": 55,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 520,
    "G2 Fall_theta_del": 0.442781745,
    "G2 Fall_pr_1_99_del": 55,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 544,
    "G2 Winter_theta_del": 0.689681601,
    "G2 Winter_pr_1_99_del": 55,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 569,
    "G2 Spring_theta_del": 0.933171779,
    "G2 Spring_pr_1_99_del": 55,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 593
  },
  {
    "ID": 457,
    "KG Fall_theta_del": -1.037537652,
    "KG Fall_pr_1_99_del": 55,
    "KG Fall_ss_del": 102,
    "KG Fall_dss_del": 396,
    "KG Winter_theta_del": -0.704840916,
    "KG Winter_pr_1_99_del": 55,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 430,
    "KG Spring_theta_del": -0.472971915,
    "KG Spring_pr_1_99_del": 55,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 453,
    "G1 Fall_theta_del": -0.234587002,
    "G1 Fall_pr_1_99_del": 55,
    "G1 Fall_ss_del": 102,
    "G1 Fall_dss_del": 477,
    "G1 Winter_theta_del": -0.002204455,
    "G1 Winter_pr_1_99_del": 55,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 500,
    "G1 Spring_theta_del": 0.20035762,
    "G1 Spring_pr_1_99_del": 55,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 520,
    "G2 Fall_theta_del": 0.443865283,
    "G2 Fall_pr_1_99_del": 55,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 544,
    "G2 Winter_theta_del": 0.697075904,
    "G2 Winter_pr_1_99_del": 55,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 570,
    "G2 Spring_theta_del": 0.942963508,
    "G2 Spring_pr_1_99_del": 55,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 594
  },
  {
    "ID": 491,
    "KG Fall_theta_del": -1.036710642,
    "KG Fall_pr_1_99_del": 55,
    "KG Fall_ss_del": 102,
    "KG Fall_dss_del": 396,
    "KG Winter_theta_del": -0.703788298,
    "KG Winter_pr_1_99_del": 55,
    "KG Winter_ss_del": 103,
    "KG Winter_dss_del": 430,
    "KG Spring_theta_del": -0.472209083,
    "KG Spring_pr_1_99_del": 55,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 453,
    "G1 Fall_theta_del": -0.227242814,
    "G1 Fall_pr_1_99_del": 55,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 477,
    "G1 Winter_theta_del": 0.000251827,
    "G1 Winter_pr_1_99_del": 55,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 500,
    "G1 Spring_theta_del": 0.211917355,
    "G1 Spring_pr_1_99_del": 55,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 521,
    "G2 Fall_theta_del": 0.447822884,
    "G2 Fall_pr_1_99_del": 55,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 545,
    "G2 Winter_theta_del": 0.700640271,
    "G2 Winter_pr_1_99_del": 55,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 570,
    "G2 Spring_theta_del": 0.945348794,
    "G2 Spring_pr_1_99_del": 55,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 595
  },
  {
    "ID": 36,
    "KG Fall_theta_del": -1.022308684,
    "KG Fall_pr_1_99_del": 56,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 398,
    "KG Winter_theta_del": -0.690621836,
    "KG Winter_pr_1_99_del": 56,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 431,
    "KG Spring_theta_del": -0.463659278,
    "KG Spring_pr_1_99_del": 56,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 454,
    "G1 Fall_theta_del": -0.224491269,
    "G1 Fall_pr_1_99_del": 56,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 478,
    "G1 Winter_theta_del": 0.002795358,
    "G1 Winter_pr_1_99_del": 56,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 500,
    "G1 Spring_theta_del": 0.213691719,
    "G1 Spring_pr_1_99_del": 56,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 521,
    "G2 Fall_theta_del": 0.45497797,
    "G2 Fall_pr_1_99_del": 56,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 545,
    "G2 Winter_theta_del": 0.701297356,
    "G2 Winter_pr_1_99_del": 56,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 570,
    "G2 Spring_theta_del": 0.94786012,
    "G2 Spring_pr_1_99_del": 56,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 595
  },
  {
    "ID": 42,
    "KG Fall_theta_del": -1.022286291,
    "KG Fall_pr_1_99_del": 56,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 398,
    "KG Winter_theta_del": -0.686964874,
    "KG Winter_pr_1_99_del": 56,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 431,
    "KG Spring_theta_del": -0.462761412,
    "KG Spring_pr_1_99_del": 56,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 454,
    "G1 Fall_theta_del": -0.223371138,
    "G1 Fall_pr_1_99_del": 56,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 478,
    "G1 Winter_theta_del": 0.003879365,
    "G1 Winter_pr_1_99_del": 56,
    "G1 Winter_ss_del": 102,
    "G1 Winter_dss_del": 500,
    "G1 Spring_theta_del": 0.21607204,
    "G1 Spring_pr_1_99_del": 56,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 522,
    "G2 Fall_theta_del": 0.458937388,
    "G2 Fall_pr_1_99_del": 56,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 546,
    "G2 Winter_theta_del": 0.702248661,
    "G2 Winter_pr_1_99_del": 56,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 570,
    "G2 Spring_theta_del": 0.953744655,
    "G2 Spring_pr_1_99_del": 56,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 595
  },
  {
    "ID": 43,
    "KG Fall_theta_del": -1.020283993,
    "KG Fall_pr_1_99_del": 56,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 398,
    "KG Winter_theta_del": -0.686315424,
    "KG Winter_pr_1_99_del": 56,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 431,
    "KG Spring_theta_del": -0.460345952,
    "KG Spring_pr_1_99_del": 56,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 454,
    "G1 Fall_theta_del": -0.219420028,
    "G1 Fall_pr_1_99_del": 56,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 478,
    "G1 Winter_theta_del": 0.010061298,
    "G1 Winter_pr_1_99_del": 56,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 501,
    "G1 Spring_theta_del": 0.216073637,
    "G1 Spring_pr_1_99_del": 56,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 522,
    "G2 Fall_theta_del": 0.462219393,
    "G2 Fall_pr_1_99_del": 56,
    "G2 Fall_ss_del": 102,
    "G2 Fall_dss_del": 546,
    "G2 Winter_theta_del": 0.703877341,
    "G2 Winter_pr_1_99_del": 56,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 570,
    "G2 Spring_theta_del": 0.957644352,
    "G2 Spring_pr_1_99_del": 56,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 596
  },
  {
    "ID": 298,
    "KG Fall_theta_del": -1.009129377,
    "KG Fall_pr_1_99_del": 56,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 399,
    "KG Winter_theta_del": -0.68602197,
    "KG Winter_pr_1_99_del": 56,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 431,
    "KG Spring_theta_del": -0.460082785,
    "KG Spring_pr_1_99_del": 56,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 454,
    "G1 Fall_theta_del": -0.217394599,
    "G1 Fall_pr_1_99_del": 56,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 478,
    "G1 Winter_theta_del": 0.011102196,
    "G1 Winter_pr_1_99_del": 56,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 501,
    "G1 Spring_theta_del": 0.216713227,
    "G1 Spring_pr_1_99_del": 56,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 522,
    "G2 Fall_theta_del": 0.467289317,
    "G2 Fall_pr_1_99_del": 56,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 547,
    "G2 Winter_theta_del": 0.704969455,
    "G2 Winter_pr_1_99_del": 56,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 570,
    "G2 Spring_theta_del": 0.960097181,
    "G2 Spring_pr_1_99_del": 56,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 596
  },
  {
    "ID": 493,
    "KG Fall_theta_del": -1.008002362,
    "KG Fall_pr_1_99_del": 56,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 399,
    "KG Winter_theta_del": -0.679367227,
    "KG Winter_pr_1_99_del": 56,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 432,
    "KG Spring_theta_del": -0.459995755,
    "KG Spring_pr_1_99_del": 56,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 454,
    "G1 Fall_theta_del": -0.216241274,
    "G1 Fall_pr_1_99_del": 56,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 478,
    "G1 Winter_theta_del": 0.011484183,
    "G1 Winter_pr_1_99_del": 56,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 501,
    "G1 Spring_theta_del": 0.224191343,
    "G1 Spring_pr_1_99_del": 56,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 522,
    "G2 Fall_theta_del": 0.476760797,
    "G2 Fall_pr_1_99_del": 56,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 548,
    "G2 Winter_theta_del": 0.707743942,
    "G2 Winter_pr_1_99_del": 56,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 571,
    "G2 Spring_theta_del": 0.962731612,
    "G2 Spring_pr_1_99_del": 56,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 596
  },
  {
    "ID": 89,
    "KG Fall_theta_del": -1.000612835,
    "KG Fall_pr_1_99_del": 57,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 400,
    "KG Winter_theta_del": -0.673237356,
    "KG Winter_pr_1_99_del": 57,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 433,
    "KG Spring_theta_del": -0.45799739,
    "KG Spring_pr_1_99_del": 57,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 454,
    "G1 Fall_theta_del": -0.21251714,
    "G1 Fall_pr_1_99_del": 57,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 479,
    "G1 Winter_theta_del": 0.012463037,
    "G1 Winter_pr_1_99_del": 57,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 501,
    "G1 Spring_theta_del": 0.225010924,
    "G1 Spring_pr_1_99_del": 57,
    "G1 Spring_ss_del": 102,
    "G1 Spring_dss_del": 523,
    "G2 Fall_theta_del": 0.479378495,
    "G2 Fall_pr_1_99_del": 57,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 548,
    "G2 Winter_theta_del": 0.713200158,
    "G2 Winter_pr_1_99_del": 57,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 571,
    "G2 Spring_theta_del": 0.962771582,
    "G2 Spring_pr_1_99_del": 57,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 596
  },
  {
    "ID": 90,
    "KG Fall_theta_del": -0.999958031,
    "KG Fall_pr_1_99_del": 57,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 400,
    "KG Winter_theta_del": -0.672861508,
    "KG Winter_pr_1_99_del": 57,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 433,
    "KG Spring_theta_del": -0.45486309,
    "KG Spring_pr_1_99_del": 57,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 455,
    "G1 Fall_theta_del": -0.211337871,
    "G1 Fall_pr_1_99_del": 57,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 479,
    "G1 Winter_theta_del": 0.023698365,
    "G1 Winter_pr_1_99_del": 57,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 502,
    "G1 Spring_theta_del": 0.233683361,
    "G1 Spring_pr_1_99_del": 57,
    "G1 Spring_ss_del": 103,
    "G1 Spring_dss_del": 523,
    "G2 Fall_theta_del": 0.480036546,
    "G2 Fall_pr_1_99_del": 57,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 548,
    "G2 Winter_theta_del": 0.714184692,
    "G2 Winter_pr_1_99_del": 57,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 571,
    "G2 Spring_theta_del": 0.975175904,
    "G2 Spring_pr_1_99_del": 57,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 598
  },
  {
    "ID": 138,
    "KG Fall_theta_del": -0.996929882,
    "KG Fall_pr_1_99_del": 57,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 400,
    "KG Winter_theta_del": -0.671299167,
    "KG Winter_pr_1_99_del": 57,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 433,
    "KG Spring_theta_del": -0.450326226,
    "KG Spring_pr_1_99_del": 57,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 455,
    "G1 Fall_theta_del": -0.207594411,
    "G1 Fall_pr_1_99_del": 57,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 479,
    "G1 Winter_theta_del": 0.024129319,
    "G1 Winter_pr_1_99_del": 57,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 502,
    "G1 Spring_theta_del": 0.238822205,
    "G1 Spring_pr_1_99_del": 57,
    "G1 Spring_ss_del": 103,
    "G1 Spring_dss_del": 524,
    "G2 Fall_theta_del": 0.483977104,
    "G2 Fall_pr_1_99_del": 57,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 548,
    "G2 Winter_theta_del": 0.71426848,
    "G2 Winter_pr_1_99_del": 57,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 571,
    "G2 Spring_theta_del": 0.978790726,
    "G2 Spring_pr_1_99_del": 57,
    "G2 Spring_ss_del": 102,
    "G2 Spring_dss_del": 598
  },
  {
    "ID": 248,
    "KG Fall_theta_del": -0.996712302,
    "KG Fall_pr_1_99_del": 57,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 400,
    "KG Winter_theta_del": -0.663952251,
    "KG Winter_pr_1_99_del": 57,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 434,
    "KG Spring_theta_del": -0.445788003,
    "KG Spring_pr_1_99_del": 57,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 455,
    "G1 Fall_theta_del": -0.20517508,
    "G1 Fall_pr_1_99_del": 57,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 479,
    "G1 Winter_theta_del": 0.026690721,
    "G1 Winter_pr_1_99_del": 57,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 503,
    "G1 Spring_theta_del": 0.243472472,
    "G1 Spring_pr_1_99_del": 57,
    "G1 Spring_ss_del": 103,
    "G1 Spring_dss_del": 524,
    "G2 Fall_theta_del": 0.484836216,
    "G2 Fall_pr_1_99_del": 57,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 548,
    "G2 Winter_theta_del": 0.715671588,
    "G2 Winter_pr_1_99_del": 57,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 572,
    "G2 Spring_theta_del": 0.989365831,
    "G2 Spring_pr_1_99_del": 57,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 599
  },
  {
    "ID": 304,
    "KG Fall_theta_del": -0.99517768,
    "KG Fall_pr_1_99_del": 57,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 400,
    "KG Winter_theta_del": -0.656837021,
    "KG Winter_pr_1_99_del": 57,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 434,
    "KG Spring_theta_del": -0.44350339,
    "KG Spring_pr_1_99_del": 57,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 456,
    "G1 Fall_theta_del": -0.203754689,
    "G1 Fall_pr_1_99_del": 57,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 480,
    "G1 Winter_theta_del": 0.031189766,
    "G1 Winter_pr_1_99_del": 57,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 503,
    "G1 Spring_theta_del": 0.245202079,
    "G1 Spring_pr_1_99_del": 57,
    "G1 Spring_ss_del": 103,
    "G1 Spring_dss_del": 525,
    "G2 Fall_theta_del": 0.486869105,
    "G2 Fall_pr_1_99_del": 57,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 549,
    "G2 Winter_theta_del": 0.723586171,
    "G2 Winter_pr_1_99_del": 57,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 572,
    "G2 Spring_theta_del": 0.990051084,
    "G2 Spring_pr_1_99_del": 57,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 599
  },
  {
    "ID": 13,
    "KG Fall_theta_del": -0.990049854,
    "KG Fall_pr_1_99_del": 58,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 401,
    "KG Winter_theta_del": -0.655140815,
    "KG Winter_pr_1_99_del": 58,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 434,
    "KG Spring_theta_del": -0.435144061,
    "KG Spring_pr_1_99_del": 58,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 456,
    "G1 Fall_theta_del": -0.184307533,
    "G1 Fall_pr_1_99_del": 58,
    "G1 Fall_ss_del": 103,
    "G1 Fall_dss_del": 482,
    "G1 Winter_theta_del": 0.038992196,
    "G1 Winter_pr_1_99_del": 58,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 504,
    "G1 Spring_theta_del": 0.257105685,
    "G1 Spring_pr_1_99_del": 58,
    "G1 Spring_ss_del": 103,
    "G1 Spring_dss_del": 526,
    "G2 Fall_theta_del": 0.490491673,
    "G2 Fall_pr_1_99_del": 58,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 549,
    "G2 Winter_theta_del": 0.727034913,
    "G2 Winter_pr_1_99_del": 58,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 573,
    "G2 Spring_theta_del": 0.991848103,
    "G2 Spring_pr_1_99_del": 58,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 599
  },
  {
    "ID": 214,
    "KG Fall_theta_del": -0.987681942,
    "KG Fall_pr_1_99_del": 58,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 401,
    "KG Winter_theta_del": -0.648932005,
    "KG Winter_pr_1_99_del": 58,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 435,
    "KG Spring_theta_del": -0.423603031,
    "KG Spring_pr_1_99_del": 58,
    "KG Spring_ss_del": 102,
    "KG Spring_dss_del": 458,
    "G1 Fall_theta_del": -0.166238626,
    "G1 Fall_pr_1_99_del": 58,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 483,
    "G1 Winter_theta_del": 0.040770553,
    "G1 Winter_pr_1_99_del": 58,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 504,
    "G1 Spring_theta_del": 0.26224098,
    "G1 Spring_pr_1_99_del": 58,
    "G1 Spring_ss_del": 103,
    "G1 Spring_dss_del": 526,
    "G2 Fall_theta_del": 0.493405732,
    "G2 Fall_pr_1_99_del": 58,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 549,
    "G2 Winter_theta_del": 0.72850287,
    "G2 Winter_pr_1_99_del": 58,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 573,
    "G2 Spring_theta_del": 1.002690991,
    "G2 Spring_pr_1_99_del": 58,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 600
  },
  {
    "ID": 320,
    "KG Fall_theta_del": -0.985552744,
    "KG Fall_pr_1_99_del": 58,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 401,
    "KG Winter_theta_del": -0.646396842,
    "KG Winter_pr_1_99_del": 58,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 435,
    "KG Spring_theta_del": -0.417671819,
    "KG Spring_pr_1_99_del": 58,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 458,
    "G1 Fall_theta_del": -0.165055315,
    "G1 Fall_pr_1_99_del": 58,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 483,
    "G1 Winter_theta_del": 0.043816234,
    "G1 Winter_pr_1_99_del": 58,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 504,
    "G1 Spring_theta_del": 0.265240205,
    "G1 Spring_pr_1_99_del": 58,
    "G1 Spring_ss_del": 103,
    "G1 Spring_dss_del": 527,
    "G2 Fall_theta_del": 0.493821925,
    "G2 Fall_pr_1_99_del": 58,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 549,
    "G2 Winter_theta_del": 0.732384829,
    "G2 Winter_pr_1_99_del": 58,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 573,
    "G2 Spring_theta_del": 1.006427614,
    "G2 Spring_pr_1_99_del": 58,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 601
  },
  {
    "ID": 419,
    "KG Fall_theta_del": -0.975269616,
    "KG Fall_pr_1_99_del": 58,
    "KG Fall_ss_del": 103,
    "KG Fall_dss_del": 402,
    "KG Winter_theta_del": -0.642379087,
    "KG Winter_pr_1_99_del": 58,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 436,
    "KG Spring_theta_del": -0.414394865,
    "KG Spring_pr_1_99_del": 58,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 459,
    "G1 Fall_theta_del": -0.164222675,
    "G1 Fall_pr_1_99_del": 58,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 484,
    "G1 Winter_theta_del": 0.044325348,
    "G1 Winter_pr_1_99_del": 58,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 504,
    "G1 Spring_theta_del": 0.269743609,
    "G1 Spring_pr_1_99_del": 58,
    "G1 Spring_ss_del": 103,
    "G1 Spring_dss_del": 527,
    "G2 Fall_theta_del": 0.497370095,
    "G2 Fall_pr_1_99_del": 58,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 550,
    "G2 Winter_theta_del": 0.734513506,
    "G2 Winter_pr_1_99_del": 58,
    "G2 Winter_ss_del": 103,
    "G2 Winter_dss_del": 573,
    "G2 Spring_theta_del": 1.006569191,
    "G2 Spring_pr_1_99_del": 58,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 601
  },
  {
    "ID": 467,
    "KG Fall_theta_del": -0.968267456,
    "KG Fall_pr_1_99_del": 58,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 403,
    "KG Winter_theta_del": -0.635631326,
    "KG Winter_pr_1_99_del": 58,
    "KG Winter_ss_del": 104,
    "KG Winter_dss_del": 436,
    "KG Spring_theta_del": -0.413547292,
    "KG Spring_pr_1_99_del": 58,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 459,
    "G1 Fall_theta_del": -0.15732613,
    "G1 Fall_pr_1_99_del": 58,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 484,
    "G1 Winter_theta_del": 0.04804759,
    "G1 Winter_pr_1_99_del": 58,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 505,
    "G1 Spring_theta_del": 0.281506911,
    "G1 Spring_pr_1_99_del": 58,
    "G1 Spring_ss_del": 104,
    "G1 Spring_dss_del": 528,
    "G2 Fall_theta_del": 0.503632812,
    "G2 Fall_pr_1_99_del": 58,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 550,
    "G2 Winter_theta_del": 0.74151298,
    "G2 Winter_pr_1_99_del": 58,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 574,
    "G2 Spring_theta_del": 1.01057288,
    "G2 Spring_pr_1_99_del": 58,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 601
  },
  {
    "ID": 102,
    "KG Fall_theta_del": -0.964426218,
    "KG Fall_pr_1_99_del": 59,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 404,
    "KG Winter_theta_del": -0.634586163,
    "KG Winter_pr_1_99_del": 59,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 437,
    "KG Spring_theta_del": -0.404148522,
    "KG Spring_pr_1_99_del": 59,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 460,
    "G1 Fall_theta_del": -0.144252524,
    "G1 Fall_pr_1_99_del": 59,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 486,
    "G1 Winter_theta_del": 0.049239859,
    "G1 Winter_pr_1_99_del": 59,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 505,
    "G1 Spring_theta_del": 0.290519424,
    "G1 Spring_pr_1_99_del": 59,
    "G1 Spring_ss_del": 104,
    "G1 Spring_dss_del": 529,
    "G2 Fall_theta_del": 0.507012646,
    "G2 Fall_pr_1_99_del": 59,
    "G2 Fall_ss_del": 103,
    "G2 Fall_dss_del": 551,
    "G2 Winter_theta_del": 0.74631157,
    "G2 Winter_pr_1_99_del": 59,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 575,
    "G2 Spring_theta_del": 1.010975802,
    "G2 Spring_pr_1_99_del": 59,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 601
  },
  {
    "ID": 215,
    "KG Fall_theta_del": -0.964003602,
    "KG Fall_pr_1_99_del": 59,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 404,
    "KG Winter_theta_del": -0.634529642,
    "KG Winter_pr_1_99_del": 59,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 437,
    "KG Spring_theta_del": -0.403508475,
    "KG Spring_pr_1_99_del": 59,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 460,
    "G1 Fall_theta_del": -0.143914232,
    "G1 Fall_pr_1_99_del": 59,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 486,
    "G1 Winter_theta_del": 0.052276538,
    "G1 Winter_pr_1_99_del": 59,
    "G1 Winter_ss_del": 103,
    "G1 Winter_dss_del": 505,
    "G1 Spring_theta_del": 0.291462514,
    "G1 Spring_pr_1_99_del": 59,
    "G1 Spring_ss_del": 104,
    "G1 Spring_dss_del": 529,
    "G2 Fall_theta_del": 0.51703294,
    "G2 Fall_pr_1_99_del": 59,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 552,
    "G2 Winter_theta_del": 0.746386166,
    "G2 Winter_pr_1_99_del": 59,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 575,
    "G2 Spring_theta_del": 1.015010571,
    "G2 Spring_pr_1_99_del": 59,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 602
  },
  {
    "ID": 257,
    "KG Fall_theta_del": -0.963169319,
    "KG Fall_pr_1_99_del": 59,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 404,
    "KG Winter_theta_del": -0.625195345,
    "KG Winter_pr_1_99_del": 59,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 437,
    "KG Spring_theta_del": -0.397424041,
    "KG Spring_pr_1_99_del": 59,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 460,
    "G1 Fall_theta_del": -0.141833115,
    "G1 Fall_pr_1_99_del": 59,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 486,
    "G1 Winter_theta_del": 0.074670171,
    "G1 Winter_pr_1_99_del": 59,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 507,
    "G1 Spring_theta_del": 0.292677055,
    "G1 Spring_pr_1_99_del": 59,
    "G1 Spring_ss_del": 104,
    "G1 Spring_dss_del": 529,
    "G2 Fall_theta_del": 0.521197477,
    "G2 Fall_pr_1_99_del": 59,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 552,
    "G2 Winter_theta_del": 0.747410452,
    "G2 Winter_pr_1_99_del": 59,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 575,
    "G2 Spring_theta_del": 1.015492906,
    "G2 Spring_pr_1_99_del": 59,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 602
  },
  {
    "ID": 361,
    "KG Fall_theta_del": -0.958435139,
    "KG Fall_pr_1_99_del": 59,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 404,
    "KG Winter_theta_del": -0.620933358,
    "KG Winter_pr_1_99_del": 59,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 438,
    "KG Spring_theta_del": -0.395387217,
    "KG Spring_pr_1_99_del": 59,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 460,
    "G1 Fall_theta_del": -0.141676496,
    "G1 Fall_pr_1_99_del": 59,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 486,
    "G1 Winter_theta_del": 0.075521961,
    "G1 Winter_pr_1_99_del": 59,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 508,
    "G1 Spring_theta_del": 0.292718018,
    "G1 Spring_pr_1_99_del": 59,
    "G1 Spring_ss_del": 104,
    "G1 Spring_dss_del": 529,
    "G2 Fall_theta_del": 0.522957404,
    "G2 Fall_pr_1_99_del": 59,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 552,
    "G2 Winter_theta_del": 0.752640358,
    "G2 Winter_pr_1_99_del": 59,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 575,
    "G2 Spring_theta_del": 1.015871979,
    "G2 Spring_pr_1_99_del": 59,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 602
  },
  {
    "ID": 470,
    "KG Fall_theta_del": -0.953057438,
    "KG Fall_pr_1_99_del": 59,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 405,
    "KG Winter_theta_del": -0.619617142,
    "KG Winter_pr_1_99_del": 59,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 438,
    "KG Spring_theta_del": -0.390837461,
    "KG Spring_pr_1_99_del": 59,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 461,
    "G1 Fall_theta_del": -0.135148511,
    "G1 Fall_pr_1_99_del": 59,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 486,
    "G1 Winter_theta_del": 0.076631547,
    "G1 Winter_pr_1_99_del": 59,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 508,
    "G1 Spring_theta_del": 0.310435069,
    "G1 Spring_pr_1_99_del": 59,
    "G1 Spring_ss_del": 104,
    "G1 Spring_dss_del": 531,
    "G2 Fall_theta_del": 0.523520675,
    "G2 Fall_pr_1_99_del": 59,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 552,
    "G2 Winter_theta_del": 0.758551993,
    "G2 Winter_pr_1_99_del": 59,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 576,
    "G2 Spring_theta_del": 1.018162614,
    "G2 Spring_pr_1_99_del": 59,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 602
  },
  {
    "ID": 10,
    "KG Fall_theta_del": -0.952776951,
    "KG Fall_pr_1_99_del": 60,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 405,
    "KG Winter_theta_del": -0.609801131,
    "KG Winter_pr_1_99_del": 60,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 439,
    "KG Spring_theta_del": -0.387980295,
    "KG Spring_pr_1_99_del": 60,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 461,
    "G1 Fall_theta_del": -0.134628333,
    "G1 Fall_pr_1_99_del": 60,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 487,
    "G1 Winter_theta_del": 0.07761711,
    "G1 Winter_pr_1_99_del": 60,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 508,
    "G1 Spring_theta_del": 0.31164696,
    "G1 Spring_pr_1_99_del": 60,
    "G1 Spring_ss_del": 104,
    "G1 Spring_dss_del": 531,
    "G2 Fall_theta_del": 0.533318702,
    "G2 Fall_pr_1_99_del": 60,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 553,
    "G2 Winter_theta_del": 0.760566574,
    "G2 Winter_pr_1_99_del": 60,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 576,
    "G2 Spring_theta_del": 1.026184425,
    "G2 Spring_pr_1_99_del": 60,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 603
  },
  {
    "ID": 93,
    "KG Fall_theta_del": -0.94688966,
    "KG Fall_pr_1_99_del": 60,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 405,
    "KG Winter_theta_del": -0.609334568,
    "KG Winter_pr_1_99_del": 60,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 439,
    "KG Spring_theta_del": -0.38342324,
    "KG Spring_pr_1_99_del": 60,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 462,
    "G1 Fall_theta_del": -0.133582395,
    "G1 Fall_pr_1_99_del": 60,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 487,
    "G1 Winter_theta_del": 0.087833731,
    "G1 Winter_pr_1_99_del": 60,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 509,
    "G1 Spring_theta_del": 0.320014252,
    "G1 Spring_pr_1_99_del": 60,
    "G1 Spring_ss_del": 104,
    "G1 Spring_dss_del": 532,
    "G2 Fall_theta_del": 0.534362757,
    "G2 Fall_pr_1_99_del": 60,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 553,
    "G2 Winter_theta_del": 0.763908122,
    "G2 Winter_pr_1_99_del": 60,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 576,
    "G2 Spring_theta_del": 1.030144749,
    "G2 Spring_pr_1_99_del": 60,
    "G2 Spring_ss_del": 103,
    "G2 Spring_dss_del": 603
  },
  {
    "ID": 275,
    "KG Fall_theta_del": -0.934329973,
    "KG Fall_pr_1_99_del": 60,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 407,
    "KG Winter_theta_del": -0.60654537,
    "KG Winter_pr_1_99_del": 60,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 439,
    "KG Spring_theta_del": -0.382750157,
    "KG Spring_pr_1_99_del": 60,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 462,
    "G1 Fall_theta_del": -0.132729462,
    "G1 Fall_pr_1_99_del": 60,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 487,
    "G1 Winter_theta_del": 0.091851024,
    "G1 Winter_pr_1_99_del": 60,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 509,
    "G1 Spring_theta_del": 0.320014414,
    "G1 Spring_pr_1_99_del": 60,
    "G1 Spring_ss_del": 104,
    "G1 Spring_dss_del": 532,
    "G2 Fall_theta_del": 0.534377258,
    "G2 Fall_pr_1_99_del": 60,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 553,
    "G2 Winter_theta_del": 0.764689405,
    "G2 Winter_pr_1_99_del": 60,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 576,
    "G2 Spring_theta_del": 1.034071992,
    "G2 Spring_pr_1_99_del": 60,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 603
  },
  {
    "ID": 439,
    "KG Fall_theta_del": -0.927253463,
    "KG Fall_pr_1_99_del": 60,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 407,
    "KG Winter_theta_del": -0.604581519,
    "KG Winter_pr_1_99_del": 60,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 440,
    "KG Spring_theta_del": -0.381918973,
    "KG Spring_pr_1_99_del": 60,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 462,
    "G1 Fall_theta_del": -0.128528675,
    "G1 Fall_pr_1_99_del": 60,
    "G1 Fall_ss_del": 104,
    "G1 Fall_dss_del": 487,
    "G1 Winter_theta_del": 0.093134446,
    "G1 Winter_pr_1_99_del": 60,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 509,
    "G1 Spring_theta_del": 0.320472768,
    "G1 Spring_pr_1_99_del": 60,
    "G1 Spring_ss_del": 104,
    "G1 Spring_dss_del": 532,
    "G2 Fall_theta_del": 0.534980225,
    "G2 Fall_pr_1_99_del": 60,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 553,
    "G2 Winter_theta_del": 0.768826163,
    "G2 Winter_pr_1_99_del": 60,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 577,
    "G2 Spring_theta_del": 1.050724651,
    "G2 Spring_pr_1_99_del": 60,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 605
  },
  {
    "ID": 498,
    "KG Fall_theta_del": -0.926326628,
    "KG Fall_pr_1_99_del": 60,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 407,
    "KG Winter_theta_del": -0.604090648,
    "KG Winter_pr_1_99_del": 60,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 440,
    "KG Spring_theta_del": -0.375947772,
    "KG Spring_pr_1_99_del": 60,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 462,
    "G1 Fall_theta_del": -0.119716216,
    "G1 Fall_pr_1_99_del": 60,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 488,
    "G1 Winter_theta_del": 0.099217643,
    "G1 Winter_pr_1_99_del": 60,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 510,
    "G1 Spring_theta_del": 0.329994469,
    "G1 Spring_pr_1_99_del": 60,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 533,
    "G2 Fall_theta_del": 0.536398384,
    "G2 Fall_pr_1_99_del": 60,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 554,
    "G2 Winter_theta_del": 0.769529943,
    "G2 Winter_pr_1_99_del": 60,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 577,
    "G2 Spring_theta_del": 1.055298077,
    "G2 Spring_pr_1_99_del": 60,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 606
  },
  {
    "ID": 33,
    "KG Fall_theta_del": -0.924990987,
    "KG Fall_pr_1_99_del": 61,
    "KG Fall_ss_del": 104,
    "KG Fall_dss_del": 408,
    "KG Winter_theta_del": -0.601142229,
    "KG Winter_pr_1_99_del": 61,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 440,
    "KG Spring_theta_del": -0.373633138,
    "KG Spring_pr_1_99_del": 61,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 463,
    "G1 Fall_theta_del": -0.119329827,
    "G1 Fall_pr_1_99_del": 61,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 488,
    "G1 Winter_theta_del": 0.100740905,
    "G1 Winter_pr_1_99_del": 61,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 510,
    "G1 Spring_theta_del": 0.331832672,
    "G1 Spring_pr_1_99_del": 61,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 533,
    "G2 Fall_theta_del": 0.538808586,
    "G2 Fall_pr_1_99_del": 61,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 554,
    "G2 Winter_theta_del": 0.770504219,
    "G2 Winter_pr_1_99_del": 61,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 577,
    "G2 Spring_theta_del": 1.057683901,
    "G2 Spring_pr_1_99_del": 61,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 606
  },
  {
    "ID": 55,
    "KG Fall_theta_del": -0.911889321,
    "KG Fall_pr_1_99_del": 61,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 409,
    "KG Winter_theta_del": -0.600624977,
    "KG Winter_pr_1_99_del": 61,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 440,
    "KG Spring_theta_del": -0.370923098,
    "KG Spring_pr_1_99_del": 61,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 463,
    "G1 Fall_theta_del": -0.118219964,
    "G1 Fall_pr_1_99_del": 61,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 488,
    "G1 Winter_theta_del": 0.101682036,
    "G1 Winter_pr_1_99_del": 61,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 510,
    "G1 Spring_theta_del": 0.337775031,
    "G1 Spring_pr_1_99_del": 61,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 534,
    "G2 Fall_theta_del": 0.543274341,
    "G2 Fall_pr_1_99_del": 61,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 554,
    "G2 Winter_theta_del": 0.778967182,
    "G2 Winter_pr_1_99_del": 61,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 578,
    "G2 Spring_theta_del": 1.062200628,
    "G2 Spring_pr_1_99_del": 61,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 606
  },
  {
    "ID": 146,
    "KG Fall_theta_del": -0.909588959,
    "KG Fall_pr_1_99_del": 61,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 409,
    "KG Winter_theta_del": -0.597233471,
    "KG Winter_pr_1_99_del": 61,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 440,
    "KG Spring_theta_del": -0.3704225,
    "KG Spring_pr_1_99_del": 61,
    "KG Spring_ss_del": 103,
    "KG Spring_dss_del": 463,
    "G1 Fall_theta_del": -0.118006589,
    "G1 Fall_pr_1_99_del": 61,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 488,
    "G1 Winter_theta_del": 0.103158317,
    "G1 Winter_pr_1_99_del": 61,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 510,
    "G1 Spring_theta_del": 0.33820158,
    "G1 Spring_pr_1_99_del": 61,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 534,
    "G2 Fall_theta_del": 0.54383992,
    "G2 Fall_pr_1_99_del": 61,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 554,
    "G2 Winter_theta_del": 0.780151943,
    "G2 Winter_pr_1_99_del": 61,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 578,
    "G2 Spring_theta_del": 1.065219783,
    "G2 Spring_pr_1_99_del": 61,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 607
  },
  {
    "ID": 421,
    "KG Fall_theta_del": -0.906668862,
    "KG Fall_pr_1_99_del": 61,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 409,
    "KG Winter_theta_del": -0.593487145,
    "KG Winter_pr_1_99_del": 61,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 441,
    "KG Spring_theta_del": -0.362155242,
    "KG Spring_pr_1_99_del": 61,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 464,
    "G1 Fall_theta_del": -0.107503286,
    "G1 Fall_pr_1_99_del": 61,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 489,
    "G1 Winter_theta_del": 0.107625868,
    "G1 Winter_pr_1_99_del": 61,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 511,
    "G1 Spring_theta_del": 0.339603148,
    "G1 Spring_pr_1_99_del": 61,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 534,
    "G2 Fall_theta_del": 0.544575945,
    "G2 Fall_pr_1_99_del": 61,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 554,
    "G2 Winter_theta_del": 0.783826221,
    "G2 Winter_pr_1_99_del": 61,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 578,
    "G2 Spring_theta_del": 1.070083434,
    "G2 Spring_pr_1_99_del": 61,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 607
  },
  {
    "ID": 494,
    "KG Fall_theta_del": -0.906655947,
    "KG Fall_pr_1_99_del": 61,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 409,
    "KG Winter_theta_del": -0.58665087,
    "KG Winter_pr_1_99_del": 61,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 441,
    "KG Spring_theta_del": -0.360764554,
    "KG Spring_pr_1_99_del": 61,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 464,
    "G1 Fall_theta_del": -0.104487324,
    "G1 Fall_pr_1_99_del": 61,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 490,
    "G1 Winter_theta_del": 0.108072148,
    "G1 Winter_pr_1_99_del": 61,
    "G1 Winter_ss_del": 104,
    "G1 Winter_dss_del": 511,
    "G1 Spring_theta_del": 0.342196712,
    "G1 Spring_pr_1_99_del": 61,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 534,
    "G2 Fall_theta_del": 0.546858237,
    "G2 Fall_pr_1_99_del": 61,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 555,
    "G2 Winter_theta_del": 0.789391231,
    "G2 Winter_pr_1_99_del": 61,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 579,
    "G2 Spring_theta_del": 1.070595982,
    "G2 Spring_pr_1_99_del": 61,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 607
  },
  {
    "ID": 16,
    "KG Fall_theta_del": -0.904238272,
    "KG Fall_pr_1_99_del": 62,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 410,
    "KG Winter_theta_del": -0.586131256,
    "KG Winter_pr_1_99_del": 62,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 441,
    "KG Spring_theta_del": -0.358277174,
    "KG Spring_pr_1_99_del": 62,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 464,
    "G1 Fall_theta_del": -0.103526831,
    "G1 Fall_pr_1_99_del": 62,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 490,
    "G1 Winter_theta_del": 0.115925827,
    "G1 Winter_pr_1_99_del": 62,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 512,
    "G1 Spring_theta_del": 0.343820719,
    "G1 Spring_pr_1_99_del": 62,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 534,
    "G2 Fall_theta_del": 0.547532687,
    "G2 Fall_pr_1_99_del": 62,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 555,
    "G2 Winter_theta_del": 0.790247518,
    "G2 Winter_pr_1_99_del": 62,
    "G2 Winter_ss_del": 104,
    "G2 Winter_dss_del": 579,
    "G2 Spring_theta_del": 1.07146473,
    "G2 Spring_pr_1_99_del": 62,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 607
  },
  {
    "ID": 198,
    "KG Fall_theta_del": -0.894007653,
    "KG Fall_pr_1_99_del": 62,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 411,
    "KG Winter_theta_del": -0.586030733,
    "KG Winter_pr_1_99_del": 62,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 441,
    "KG Spring_theta_del": -0.357932754,
    "KG Spring_pr_1_99_del": 62,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 464,
    "G1 Fall_theta_del": -0.096030338,
    "G1 Fall_pr_1_99_del": 62,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 490,
    "G1 Winter_theta_del": 0.118618506,
    "G1 Winter_pr_1_99_del": 62,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 512,
    "G1 Spring_theta_del": 0.348660939,
    "G1 Spring_pr_1_99_del": 62,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 535,
    "G2 Fall_theta_del": 0.551826068,
    "G2 Fall_pr_1_99_del": 62,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 555,
    "G2 Winter_theta_del": 0.794382217,
    "G2 Winter_pr_1_99_del": 62,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 579,
    "G2 Spring_theta_del": 1.081619512,
    "G2 Spring_pr_1_99_del": 62,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 608
  },
  {
    "ID": 282,
    "KG Fall_theta_del": -0.892987459,
    "KG Fall_pr_1_99_del": 62,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 411,
    "KG Winter_theta_del": -0.582694814,
    "KG Winter_pr_1_99_del": 62,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 442,
    "KG Spring_theta_del": -0.357592767,
    "KG Spring_pr_1_99_del": 62,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 464,
    "G1 Fall_theta_del": -0.093708657,
    "G1 Fall_pr_1_99_del": 62,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 491,
    "G1 Winter_theta_del": 0.119157382,
    "G1 Winter_pr_1_99_del": 62,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 512,
    "G1 Spring_theta_del": 0.351090944,
    "G1 Spring_pr_1_99_del": 62,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 535,
    "G2 Fall_theta_del": 0.565615455,
    "G2 Fall_pr_1_99_del": 62,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 557,
    "G2 Winter_theta_del": 0.804619173,
    "G2 Winter_pr_1_99_del": 62,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 580,
    "G2 Spring_theta_del": 1.085815492,
    "G2 Spring_pr_1_99_del": 62,
    "G2 Spring_ss_del": 104,
    "G2 Spring_dss_del": 609
  },
  {
    "ID": 401,
    "KG Fall_theta_del": -0.892837642,
    "KG Fall_pr_1_99_del": 62,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 411,
    "KG Winter_theta_del": -0.582084198,
    "KG Winter_pr_1_99_del": 62,
    "KG Winter_ss_del": 105,
    "KG Winter_dss_del": 442,
    "KG Spring_theta_del": -0.347193297,
    "KG Spring_pr_1_99_del": 62,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 465,
    "G1 Fall_theta_del": -0.092001497,
    "G1 Fall_pr_1_99_del": 62,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 491,
    "G1 Winter_theta_del": 0.1206915,
    "G1 Winter_pr_1_99_del": 62,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 512,
    "G1 Spring_theta_del": 0.352299671,
    "G1 Spring_pr_1_99_del": 62,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 535,
    "G2 Fall_theta_del": 0.566299409,
    "G2 Fall_pr_1_99_del": 62,
    "G2 Fall_ss_del": 104,
    "G2 Fall_dss_del": 557,
    "G2 Winter_theta_del": 0.805583175,
    "G2 Winter_pr_1_99_del": 62,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 581,
    "G2 Spring_theta_del": 1.087582728,
    "G2 Spring_pr_1_99_del": 62,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 609
  },
  {
    "ID": 492,
    "KG Fall_theta_del": -0.891645248,
    "KG Fall_pr_1_99_del": 62,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 411,
    "KG Winter_theta_del": -0.575781055,
    "KG Winter_pr_1_99_del": 62,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 442,
    "KG Spring_theta_del": -0.342084943,
    "KG Spring_pr_1_99_del": 62,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 466,
    "G1 Fall_theta_del": -0.090345354,
    "G1 Fall_pr_1_99_del": 62,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 491,
    "G1 Winter_theta_del": 0.128544327,
    "G1 Winter_pr_1_99_del": 62,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 513,
    "G1 Spring_theta_del": 0.359167083,
    "G1 Spring_pr_1_99_del": 62,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 536,
    "G2 Fall_theta_del": 0.567308915,
    "G2 Fall_pr_1_99_del": 62,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 557,
    "G2 Winter_theta_del": 0.821489596,
    "G2 Winter_pr_1_99_del": 62,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 582,
    "G2 Spring_theta_del": 1.099762298,
    "G2 Spring_pr_1_99_del": 62,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 610
  },
  {
    "ID": 14,
    "KG Fall_theta_del": -0.881529636,
    "KG Fall_pr_1_99_del": 63,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 412,
    "KG Winter_theta_del": -0.573347784,
    "KG Winter_pr_1_99_del": 63,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 443,
    "KG Spring_theta_del": -0.340090966,
    "KG Spring_pr_1_99_del": 63,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 466,
    "G1 Fall_theta_del": -0.08848349,
    "G1 Fall_pr_1_99_del": 63,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 491,
    "G1 Winter_theta_del": 0.134468257,
    "G1 Winter_pr_1_99_del": 63,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 513,
    "G1 Spring_theta_del": 0.363897909,
    "G1 Spring_pr_1_99_del": 63,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 536,
    "G2 Fall_theta_del": 0.572619908,
    "G2 Fall_pr_1_99_del": 63,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 557,
    "G2 Winter_theta_del": 0.824622948,
    "G2 Winter_pr_1_99_del": 63,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 582,
    "G2 Spring_theta_del": 1.10313742,
    "G2 Spring_pr_1_99_del": 63,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 610
  },
  {
    "ID": 63,
    "KG Fall_theta_del": -0.879222038,
    "KG Fall_pr_1_99_del": 63,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 412,
    "KG Winter_theta_del": -0.572235559,
    "KG Winter_pr_1_99_del": 63,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 443,
    "KG Spring_theta_del": -0.336334741,
    "KG Spring_pr_1_99_del": 63,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 466,
    "G1 Fall_theta_del": -0.079822151,
    "G1 Fall_pr_1_99_del": 63,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 492,
    "G1 Winter_theta_del": 0.137832662,
    "G1 Winter_pr_1_99_del": 63,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 514,
    "G1 Spring_theta_del": 0.36711545,
    "G1 Spring_pr_1_99_del": 63,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 537,
    "G2 Fall_theta_del": 0.573289794,
    "G2 Fall_pr_1_99_del": 63,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 557,
    "G2 Winter_theta_del": 0.826260482,
    "G2 Winter_pr_1_99_del": 63,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 583,
    "G2 Spring_theta_del": 1.107260024,
    "G2 Spring_pr_1_99_del": 63,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 611
  },
  {
    "ID": 133,
    "KG Fall_theta_del": -0.875711128,
    "KG Fall_pr_1_99_del": 63,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 412,
    "KG Winter_theta_del": -0.56962418,
    "KG Winter_pr_1_99_del": 63,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 443,
    "KG Spring_theta_del": -0.331241089,
    "KG Spring_pr_1_99_del": 63,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 467,
    "G1 Fall_theta_del": -0.079295388,
    "G1 Fall_pr_1_99_del": 63,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 492,
    "G1 Winter_theta_del": 0.148881813,
    "G1 Winter_pr_1_99_del": 63,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 515,
    "G1 Spring_theta_del": 0.368164322,
    "G1 Spring_pr_1_99_del": 63,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 537,
    "G2 Fall_theta_del": 0.573493039,
    "G2 Fall_pr_1_99_del": 63,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 557,
    "G2 Winter_theta_del": 0.827449255,
    "G2 Winter_pr_1_99_del": 63,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 583,
    "G2 Spring_theta_del": 1.109036768,
    "G2 Spring_pr_1_99_del": 63,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 611
  },
  {
    "ID": 170,
    "KG Fall_theta_del": -0.87299044,
    "KG Fall_pr_1_99_del": 63,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 413,
    "KG Winter_theta_del": -0.561751876,
    "KG Winter_pr_1_99_del": 63,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 444,
    "KG Spring_theta_del": -0.327055287,
    "KG Spring_pr_1_99_del": 63,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 467,
    "G1 Fall_theta_del": -0.078575911,
    "G1 Fall_pr_1_99_del": 63,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 492,
    "G1 Winter_theta_del": 0.150453629,
    "G1 Winter_pr_1_99_del": 63,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 515,
    "G1 Spring_theta_del": 0.371255489,
    "G1 Spring_pr_1_99_del": 63,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 537,
    "G2 Fall_theta_del": 0.582696443,
    "G2 Fall_pr_1_99_del": 63,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 558,
    "G2 Winter_theta_del": 0.830706752,
    "G2 Winter_pr_1_99_del": 63,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 583,
    "G2 Spring_theta_del": 1.109873156,
    "G2 Spring_pr_1_99_del": 63,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 611
  },
  {
    "ID": 460,
    "KG Fall_theta_del": -0.872655443,
    "KG Fall_pr_1_99_del": 63,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 413,
    "KG Winter_theta_del": -0.557301517,
    "KG Winter_pr_1_99_del": 63,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 444,
    "KG Spring_theta_del": -0.326602178,
    "KG Spring_pr_1_99_del": 63,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 467,
    "G1 Fall_theta_del": -0.078051335,
    "G1 Fall_pr_1_99_del": 63,
    "G1 Fall_ss_del": 105,
    "G1 Fall_dss_del": 492,
    "G1 Winter_theta_del": 0.1511242,
    "G1 Winter_pr_1_99_del": 63,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 515,
    "G1 Spring_theta_del": 0.372786644,
    "G1 Spring_pr_1_99_del": 63,
    "G1 Spring_ss_del": 105,
    "G1 Spring_dss_del": 537,
    "G2 Fall_theta_del": 0.583376636,
    "G2 Fall_pr_1_99_del": 63,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 558,
    "G2 Winter_theta_del": 0.832027656,
    "G2 Winter_pr_1_99_del": 63,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 583,
    "G2 Spring_theta_del": 1.114542524,
    "G2 Spring_pr_1_99_del": 63,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 611
  },
  {
    "ID": 250,
    "KG Fall_theta_del": -0.870222213,
    "KG Fall_pr_1_99_del": 64,
    "KG Fall_ss_del": 105,
    "KG Fall_dss_del": 413,
    "KG Winter_theta_del": -0.55499133,
    "KG Winter_pr_1_99_del": 64,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 445,
    "KG Spring_theta_del": -0.322227502,
    "KG Spring_pr_1_99_del": 64,
    "KG Spring_ss_del": 104,
    "KG Spring_dss_del": 468,
    "G1 Fall_theta_del": -0.06015986,
    "G1 Fall_pr_1_99_del": 64,
    "G1 Fall_ss_del": 106,
    "G1 Fall_dss_del": 494,
    "G1 Winter_theta_del": 0.159038257,
    "G1 Winter_pr_1_99_del": 64,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 516,
    "G1 Spring_theta_del": 0.389491443,
    "G1 Spring_pr_1_99_del": 64,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 539,
    "G2 Fall_theta_del": 0.586171555,
    "G2 Fall_pr_1_99_del": 64,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 559,
    "G2 Winter_theta_del": 0.834867242,
    "G2 Winter_pr_1_99_del": 64,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 583,
    "G2 Spring_theta_del": 1.114917239,
    "G2 Spring_pr_1_99_del": 64,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 611
  },
  {
    "ID": 331,
    "KG Fall_theta_del": -0.854702677,
    "KG Fall_pr_1_99_del": 64,
    "KG Fall_ss_del": 106,
    "KG Fall_dss_del": 415,
    "KG Winter_theta_del": -0.551337128,
    "KG Winter_pr_1_99_del": 64,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 445,
    "KG Spring_theta_del": -0.307112656,
    "KG Spring_pr_1_99_del": 64,
    "KG Spring_ss_del": 105,
    "KG Spring_dss_del": 469,
    "G1 Fall_theta_del": -0.056471935,
    "G1 Fall_pr_1_99_del": 64,
    "G1 Fall_ss_del": 106,
    "G1 Fall_dss_del": 494,
    "G1 Winter_theta_del": 0.160278677,
    "G1 Winter_pr_1_99_del": 64,
    "G1 Winter_ss_del": 105,
    "G1 Winter_dss_del": 516,
    "G1 Spring_theta_del": 0.403639083,
    "G1 Spring_pr_1_99_del": 64,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 540,
    "G2 Fall_theta_del": 0.587759741,
    "G2 Fall_pr_1_99_del": 64,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 559,
    "G2 Winter_theta_del": 0.835344872,
    "G2 Winter_pr_1_99_del": 64,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 584,
    "G2 Spring_theta_del": 1.115084074,
    "G2 Spring_pr_1_99_del": 64,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 612
  },
  {
    "ID": 340,
    "KG Fall_theta_del": -0.846673902,
    "KG Fall_pr_1_99_del": 64,
    "KG Fall_ss_del": 106,
    "KG Fall_dss_del": 415,
    "KG Winter_theta_del": -0.548589719,
    "KG Winter_pr_1_99_del": 64,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 445,
    "KG Spring_theta_del": -0.304805282,
    "KG Spring_pr_1_99_del": 64,
    "KG Spring_ss_del": 105,
    "KG Spring_dss_del": 470,
    "G1 Fall_theta_del": -0.045121873,
    "G1 Fall_pr_1_99_del": 64,
    "G1 Fall_ss_del": 106,
    "G1 Fall_dss_del": 495,
    "G1 Winter_theta_del": 0.162528213,
    "G1 Winter_pr_1_99_del": 64,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 516,
    "G1 Spring_theta_del": 0.406612314,
    "G1 Spring_pr_1_99_del": 64,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 541,
    "G2 Fall_theta_del": 0.590519514,
    "G2 Fall_pr_1_99_del": 64,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 559,
    "G2 Winter_theta_del": 0.844923088,
    "G2 Winter_pr_1_99_del": 64,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 584,
    "G2 Spring_theta_del": 1.122360736,
    "G2 Spring_pr_1_99_del": 64,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 612
  },
  {
    "ID": 422,
    "KG Fall_theta_del": -0.846641821,
    "KG Fall_pr_1_99_del": 64,
    "KG Fall_ss_del": 106,
    "KG Fall_dss_del": 415,
    "KG Winter_theta_del": -0.548383739,
    "KG Winter_pr_1_99_del": 64,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 445,
    "KG Spring_theta_del": -0.294796444,
    "KG Spring_pr_1_99_del": 64,
    "KG Spring_ss_del": 105,
    "KG Spring_dss_del": 471,
    "G1 Fall_theta_del": -0.039389515,
    "G1 Fall_pr_1_99_del": 64,
    "G1 Fall_ss_del": 106,
    "G1 Fall_dss_del": 496,
    "G1 Winter_theta_del": 0.167369775,
    "G1 Winter_pr_1_99_del": 64,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 517,
    "G1 Spring_theta_del": 0.40941143,
    "G1 Spring_pr_1_99_del": 64,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 541,
    "G2 Fall_theta_del": 0.591407228,
    "G2 Fall_pr_1_99_del": 64,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 559,
    "G2 Winter_theta_del": 0.846944382,
    "G2 Winter_pr_1_99_del": 64,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 585,
    "G2 Spring_theta_del": 1.122851443,
    "G2 Spring_pr_1_99_del": 64,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 612
  },
  {
    "ID": 496,
    "KG Fall_theta_del": -0.842710396,
    "KG Fall_pr_1_99_del": 64,
    "KG Fall_ss_del": 106,
    "KG Fall_dss_del": 416,
    "KG Winter_theta_del": -0.543581945,
    "KG Winter_pr_1_99_del": 64,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 446,
    "KG Spring_theta_del": -0.293471166,
    "KG Spring_pr_1_99_del": 64,
    "KG Spring_ss_del": 105,
    "KG Spring_dss_del": 471,
    "G1 Fall_theta_del": -0.032604899,
    "G1 Fall_pr_1_99_del": 64,
    "G1 Fall_ss_del": 106,
    "G1 Fall_dss_del": 497,
    "G1 Winter_theta_del": 0.167495831,
    "G1 Winter_pr_1_99_del": 64,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 517,
    "G1 Spring_theta_del": 0.412608489,
    "G1 Spring_pr_1_99_del": 64,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 541,
    "G2 Fall_theta_del": 0.604425168,
    "G2 Fall_pr_1_99_del": 64,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 560,
    "G2 Winter_theta_del": 0.847806018,
    "G2 Winter_pr_1_99_del": 64,
    "G2 Winter_ss_del": 105,
    "G2 Winter_dss_del": 585,
    "G2 Spring_theta_del": 1.132284841,
    "G2 Spring_pr_1_99_del": 64,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 613
  },
  {
    "ID": 29,
    "KG Fall_theta_del": -0.840087309,
    "KG Fall_pr_1_99_del": 65,
    "KG Fall_ss_del": 106,
    "KG Fall_dss_del": 416,
    "KG Winter_theta_del": -0.537531878,
    "KG Winter_pr_1_99_del": 65,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 446,
    "KG Spring_theta_del": -0.286978017,
    "KG Spring_pr_1_99_del": 65,
    "KG Spring_ss_del": 105,
    "KG Spring_dss_del": 471,
    "G1 Fall_theta_del": -0.015209875,
    "G1 Fall_pr_1_99_del": 65,
    "G1 Fall_ss_del": 107,
    "G1 Fall_dss_del": 498,
    "G1 Winter_theta_del": 0.168331313,
    "G1 Winter_pr_1_99_del": 65,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 517,
    "G1 Spring_theta_del": 0.412660062,
    "G1 Spring_pr_1_99_del": 65,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 541,
    "G2 Fall_theta_del": 0.611756473,
    "G2 Fall_pr_1_99_del": 65,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 561,
    "G2 Winter_theta_del": 0.852772933,
    "G2 Winter_pr_1_99_del": 65,
    "G2 Winter_ss_del": 106,
    "G2 Winter_dss_del": 585,
    "G2 Spring_theta_del": 1.135427751,
    "G2 Spring_pr_1_99_del": 65,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 614
  },
  {
    "ID": 50,
    "KG Fall_theta_del": -0.832188426,
    "KG Fall_pr_1_99_del": 65,
    "KG Fall_ss_del": 106,
    "KG Fall_dss_del": 417,
    "KG Winter_theta_del": -0.533918449,
    "KG Winter_pr_1_99_del": 65,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 447,
    "KG Spring_theta_del": -0.279756922,
    "KG Spring_pr_1_99_del": 65,
    "KG Spring_ss_del": 105,
    "KG Spring_dss_del": 472,
    "G1 Fall_theta_del": -0.01389715,
    "G1 Fall_pr_1_99_del": 65,
    "G1 Fall_ss_del": 107,
    "G1 Fall_dss_del": 499,
    "G1 Winter_theta_del": 0.172215886,
    "G1 Winter_pr_1_99_del": 65,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 517,
    "G1 Spring_theta_del": 0.412937318,
    "G1 Spring_pr_1_99_del": 65,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 541,
    "G2 Fall_theta_del": 0.613853067,
    "G2 Fall_pr_1_99_del": 65,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 561,
    "G2 Winter_theta_del": 0.852999379,
    "G2 Winter_pr_1_99_del": 65,
    "G2 Winter_ss_del": 106,
    "G2 Winter_dss_del": 585,
    "G2 Spring_theta_del": 1.136496293,
    "G2 Spring_pr_1_99_del": 65,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 614
  },
  {
    "ID": 326,
    "KG Fall_theta_del": -0.832031686,
    "KG Fall_pr_1_99_del": 65,
    "KG Fall_ss_del": 106,
    "KG Fall_dss_del": 417,
    "KG Winter_theta_del": -0.525922789,
    "KG Winter_pr_1_99_del": 65,
    "KG Winter_ss_del": 106,
    "KG Winter_dss_del": 447,
    "KG Spring_theta_del": -0.266695054,
    "KG Spring_pr_1_99_del": 65,
    "KG Spring_ss_del": 105,
    "KG Spring_dss_del": 473,
    "G1 Fall_theta_del": -0.010908614,
    "G1 Fall_pr_1_99_del": 65,
    "G1 Fall_ss_del": 107,
    "G1 Fall_dss_del": 499,
    "G1 Winter_theta_del": 0.180088356,
    "G1 Winter_pr_1_99_del": 65,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 518,
    "G1 Spring_theta_del": 0.416009587,
    "G1 Spring_pr_1_99_del": 65,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 542,
    "G2 Fall_theta_del": 0.61638982,
    "G2 Fall_pr_1_99_del": 65,
    "G2 Fall_ss_del": 105,
    "G2 Fall_dss_del": 562,
    "G2 Winter_theta_del": 0.856187744,
    "G2 Winter_pr_1_99_del": 65,
    "G2 Winter_ss_del": 106,
    "G2 Winter_dss_del": 586,
    "G2 Spring_theta_del": 1.139384503,
    "G2 Spring_pr_1_99_del": 65,
    "G2 Spring_ss_del": 105,
    "G2 Spring_dss_del": 614
  },
  {
    "ID": 356,
    "KG Fall_theta_del": -0.82637628,
    "KG Fall_pr_1_99_del": 65,
    "KG Fall_ss_del": 106,
    "KG Fall_dss_del": 417,
    "KG Winter_theta_del": -0.52319114,
    "KG Winter_pr_1_99_del": 65,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 448,
    "KG Spring_theta_del": -0.261269608,
    "KG Spring_pr_1_99_del": 65,
    "KG Spring_ss_del": 105,
    "KG Spring_dss_del": 474,
    "G1 Fall_theta_del": -0.006732086,
    "G1 Fall_pr_1_99_del": 65,
    "G1 Fall_ss_del": 107,
    "G1 Fall_dss_del": 499,
    "G1 Winter_theta_del": 0.180460338,
    "G1 Winter_pr_1_99_del": 65,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 518,
    "G1 Spring_theta_del": 0.416369665,
    "G1 Spring_pr_1_99_del": 65,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 542,
    "G2 Fall_theta_del": 0.627396155,
    "G2 Fall_pr_1_99_del": 65,
    "G2 Fall_ss_del": 106,
    "G2 Fall_dss_del": 563,
    "G2 Winter_theta_del": 0.863476797,
    "G2 Winter_pr_1_99_del": 65,
    "G2 Winter_ss_del": 106,
    "G2 Winter_dss_del": 586,
    "G2 Spring_theta_del": 1.150763068,
    "G2 Spring_pr_1_99_del": 65,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 615
  },
  {
    "ID": 440,
    "KG Fall_theta_del": -0.822783983,
    "KG Fall_pr_1_99_del": 65,
    "KG Fall_ss_del": 106,
    "KG Fall_dss_del": 418,
    "KG Winter_theta_del": -0.522318233,
    "KG Winter_pr_1_99_del": 65,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 448,
    "KG Spring_theta_del": -0.259297673,
    "KG Spring_pr_1_99_del": 65,
    "KG Spring_ss_del": 105,
    "KG Spring_dss_del": 474,
    "G1 Fall_theta_del": 0.001048147,
    "G1 Fall_pr_1_99_del": 65,
    "G1 Fall_ss_del": 107,
    "G1 Fall_dss_del": 500,
    "G1 Winter_theta_del": 0.182147245,
    "G1 Winter_pr_1_99_del": 65,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 518,
    "G1 Spring_theta_del": 0.421390373,
    "G1 Spring_pr_1_99_del": 65,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 542,
    "G2 Fall_theta_del": 0.628311328,
    "G2 Fall_pr_1_99_del": 65,
    "G2 Fall_ss_del": 106,
    "G2 Fall_dss_del": 563,
    "G2 Winter_theta_del": 0.865106432,
    "G2 Winter_pr_1_99_del": 65,
    "G2 Winter_ss_del": 106,
    "G2 Winter_dss_del": 587,
    "G2 Spring_theta_del": 1.151442221,
    "G2 Spring_pr_1_99_del": 65,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 615
  },
  {
    "ID": 44,
    "KG Fall_theta_del": -0.81310727,
    "KG Fall_pr_1_99_del": 66,
    "KG Fall_ss_del": 106,
    "KG Fall_dss_del": 419,
    "KG Winter_theta_del": -0.52211304,
    "KG Winter_pr_1_99_del": 66,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 448,
    "KG Spring_theta_del": -0.257908391,
    "KG Spring_pr_1_99_del": 66,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 474,
    "G1 Fall_theta_del": 0.003490291,
    "G1 Fall_pr_1_99_del": 66,
    "G1 Fall_ss_del": 107,
    "G1 Fall_dss_del": 500,
    "G1 Winter_theta_del": 0.183298783,
    "G1 Winter_pr_1_99_del": 66,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 518,
    "G1 Spring_theta_del": 0.426183082,
    "G1 Spring_pr_1_99_del": 66,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 543,
    "G2 Fall_theta_del": 0.628932401,
    "G2 Fall_pr_1_99_del": 66,
    "G2 Fall_ss_del": 106,
    "G2 Fall_dss_del": 563,
    "G2 Winter_theta_del": 0.867537842,
    "G2 Winter_pr_1_99_del": 66,
    "G2 Winter_ss_del": 106,
    "G2 Winter_dss_del": 587,
    "G2 Spring_theta_del": 1.152269186,
    "G2 Spring_pr_1_99_del": 66,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 615
  },
  {
    "ID": 127,
    "KG Fall_theta_del": -0.807309115,
    "KG Fall_pr_1_99_del": 66,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 419,
    "KG Winter_theta_del": -0.518328619,
    "KG Winter_pr_1_99_del": 66,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 448,
    "KG Spring_theta_del": -0.257830025,
    "KG Spring_pr_1_99_del": 66,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 474,
    "G1 Fall_theta_del": 0.011561866,
    "G1 Fall_pr_1_99_del": 66,
    "G1 Fall_ss_del": 107,
    "G1 Fall_dss_del": 501,
    "G1 Winter_theta_del": 0.194356884,
    "G1 Winter_pr_1_99_del": 66,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 519,
    "G1 Spring_theta_del": 0.427348155,
    "G1 Spring_pr_1_99_del": 66,
    "G1 Spring_ss_del": 106,
    "G1 Spring_dss_del": 543,
    "G2 Fall_theta_del": 0.629676834,
    "G2 Fall_pr_1_99_del": 66,
    "G2 Fall_ss_del": 106,
    "G2 Fall_dss_del": 563,
    "G2 Winter_theta_del": 0.888877424,
    "G2 Winter_pr_1_99_del": 66,
    "G2 Winter_ss_del": 106,
    "G2 Winter_dss_del": 589,
    "G2 Spring_theta_del": 1.15603355,
    "G2 Spring_pr_1_99_del": 66,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 616
  },
  {
    "ID": 171,
    "KG Fall_theta_del": -0.781065911,
    "KG Fall_pr_1_99_del": 66,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 422,
    "KG Winter_theta_del": -0.515846963,
    "KG Winter_pr_1_99_del": 66,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 448,
    "KG Spring_theta_del": -0.256328467,
    "KG Spring_pr_1_99_del": 66,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 474,
    "G1 Fall_theta_del": 0.012682013,
    "G1 Fall_pr_1_99_del": 66,
    "G1 Fall_ss_del": 107,
    "G1 Fall_dss_del": 501,
    "G1 Winter_theta_del": 0.195987151,
    "G1 Winter_pr_1_99_del": 66,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 520,
    "G1 Spring_theta_del": 0.429562035,
    "G1 Spring_pr_1_99_del": 66,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 543,
    "G2 Fall_theta_del": 0.633790513,
    "G2 Fall_pr_1_99_del": 66,
    "G2 Fall_ss_del": 106,
    "G2 Fall_dss_del": 563,
    "G2 Winter_theta_del": 0.896803593,
    "G2 Winter_pr_1_99_del": 66,
    "G2 Winter_ss_del": 106,
    "G2 Winter_dss_del": 590,
    "G2 Spring_theta_del": 1.165902014,
    "G2 Spring_pr_1_99_del": 66,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 617
  },
  {
    "ID": 351,
    "KG Fall_theta_del": -0.780334171,
    "KG Fall_pr_1_99_del": 66,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 422,
    "KG Winter_theta_del": -0.513555871,
    "KG Winter_pr_1_99_del": 66,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 449,
    "KG Spring_theta_del": -0.25488988,
    "KG Spring_pr_1_99_del": 66,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 475,
    "G1 Fall_theta_del": 0.024370313,
    "G1 Fall_pr_1_99_del": 66,
    "G1 Fall_ss_del": 107,
    "G1 Fall_dss_del": 502,
    "G1 Winter_theta_del": 0.197816367,
    "G1 Winter_pr_1_99_del": 66,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 520,
    "G1 Spring_theta_del": 0.435289715,
    "G1 Spring_pr_1_99_del": 66,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 544,
    "G2 Fall_theta_del": 0.634211553,
    "G2 Fall_pr_1_99_del": 66,
    "G2 Fall_ss_del": 106,
    "G2 Fall_dss_del": 563,
    "G2 Winter_theta_del": 0.898032363,
    "G2 Winter_pr_1_99_del": 66,
    "G2 Winter_ss_del": 106,
    "G2 Winter_dss_del": 590,
    "G2 Spring_theta_del": 1.178439675,
    "G2 Spring_pr_1_99_del": 66,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 618
  },
  {
    "ID": 388,
    "KG Fall_theta_del": -0.77917137,
    "KG Fall_pr_1_99_del": 66,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 422,
    "KG Winter_theta_del": -0.507045515,
    "KG Winter_pr_1_99_del": 66,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 449,
    "KG Spring_theta_del": -0.249318877,
    "KG Spring_pr_1_99_del": 66,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 475,
    "G1 Fall_theta_del": 0.031873619,
    "G1 Fall_pr_1_99_del": 66,
    "G1 Fall_ss_del": 107,
    "G1 Fall_dss_del": 503,
    "G1 Winter_theta_del": 0.199833005,
    "G1 Winter_pr_1_99_del": 66,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 520,
    "G1 Spring_theta_del": 0.435544794,
    "G1 Spring_pr_1_99_del": 66,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 544,
    "G2 Fall_theta_del": 0.639811631,
    "G2 Fall_pr_1_99_del": 66,
    "G2 Fall_ss_del": 106,
    "G2 Fall_dss_del": 564,
    "G2 Winter_theta_del": 0.903533231,
    "G2 Winter_pr_1_99_del": 66,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 590,
    "G2 Spring_theta_del": 1.186507052,
    "G2 Spring_pr_1_99_del": 66,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 619
  },
  {
    "ID": 136,
    "KG Fall_theta_del": -0.778829833,
    "KG Fall_pr_1_99_del": 67,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 422,
    "KG Winter_theta_del": -0.506515158,
    "KG Winter_pr_1_99_del": 67,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 449,
    "KG Spring_theta_del": -0.24030302,
    "KG Spring_pr_1_99_del": 67,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 476,
    "G1 Fall_theta_del": 0.037338262,
    "G1 Fall_pr_1_99_del": 67,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 504,
    "G1 Winter_theta_del": 0.200451915,
    "G1 Winter_pr_1_99_del": 67,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 520,
    "G1 Spring_theta_del": 0.435883668,
    "G1 Spring_pr_1_99_del": 67,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 544,
    "G2 Fall_theta_del": 0.658262392,
    "G2 Fall_pr_1_99_del": 67,
    "G2 Fall_ss_del": 106,
    "G2 Fall_dss_del": 566,
    "G2 Winter_theta_del": 0.905553216,
    "G2 Winter_pr_1_99_del": 67,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 591,
    "G2 Spring_theta_del": 1.188114013,
    "G2 Spring_pr_1_99_del": 67,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 619
  },
  {
    "ID": 152,
    "KG Fall_theta_del": -0.774321729,
    "KG Fall_pr_1_99_del": 67,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 423,
    "KG Winter_theta_del": -0.504733378,
    "KG Winter_pr_1_99_del": 67,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 450,
    "KG Spring_theta_del": -0.235865901,
    "KG Spring_pr_1_99_del": 67,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 476,
    "G1 Fall_theta_del": 0.044666821,
    "G1 Fall_pr_1_99_del": 67,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 504,
    "G1 Winter_theta_del": 0.200671577,
    "G1 Winter_pr_1_99_del": 67,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 520,
    "G1 Spring_theta_del": 0.437080811,
    "G1 Spring_pr_1_99_del": 67,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 544,
    "G2 Fall_theta_del": 0.659574832,
    "G2 Fall_pr_1_99_del": 67,
    "G2 Fall_ss_del": 106,
    "G2 Fall_dss_del": 566,
    "G2 Winter_theta_del": 0.906113627,
    "G2 Winter_pr_1_99_del": 67,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 591,
    "G2 Spring_theta_del": 1.189791753,
    "G2 Spring_pr_1_99_del": 67,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 619
  },
  {
    "ID": 189,
    "KG Fall_theta_del": -0.767510815,
    "KG Fall_pr_1_99_del": 67,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 423,
    "KG Winter_theta_del": -0.493625575,
    "KG Winter_pr_1_99_del": 67,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 451,
    "KG Spring_theta_del": -0.218170595,
    "KG Spring_pr_1_99_del": 67,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 478,
    "G1 Fall_theta_del": 0.044726113,
    "G1 Fall_pr_1_99_del": 67,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 504,
    "G1 Winter_theta_del": 0.205845125,
    "G1 Winter_pr_1_99_del": 67,
    "G1 Winter_ss_del": 106,
    "G1 Winter_dss_del": 521,
    "G1 Spring_theta_del": 0.437844846,
    "G1 Spring_pr_1_99_del": 67,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 544,
    "G2 Fall_theta_del": 0.661793105,
    "G2 Fall_pr_1_99_del": 67,
    "G2 Fall_ss_del": 106,
    "G2 Fall_dss_del": 566,
    "G2 Winter_theta_del": 0.906372329,
    "G2 Winter_pr_1_99_del": 67,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 591,
    "G2 Spring_theta_del": 1.192090188,
    "G2 Spring_pr_1_99_del": 67,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 619
  },
  {
    "ID": 398,
    "KG Fall_theta_del": -0.767278385,
    "KG Fall_pr_1_99_del": 67,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 423,
    "KG Winter_theta_del": -0.490068146,
    "KG Winter_pr_1_99_del": 67,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 451,
    "KG Spring_theta_del": -0.216324957,
    "KG Spring_pr_1_99_del": 67,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 478,
    "G1 Fall_theta_del": 0.056614163,
    "G1 Fall_pr_1_99_del": 67,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 506,
    "G1 Winter_theta_del": 0.215944436,
    "G1 Winter_pr_1_99_del": 67,
    "G1 Winter_ss_del": 107,
    "G1 Winter_dss_del": 522,
    "G1 Spring_theta_del": 0.438665217,
    "G1 Spring_pr_1_99_del": 67,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 544,
    "G2 Fall_theta_del": 0.675316101,
    "G2 Fall_pr_1_99_del": 67,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 568,
    "G2 Winter_theta_del": 0.907021864,
    "G2 Winter_pr_1_99_del": 67,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 591,
    "G2 Spring_theta_del": 1.192149332,
    "G2 Spring_pr_1_99_del": 67,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 619
  },
  {
    "ID": 411,
    "KG Fall_theta_del": -0.766640914,
    "KG Fall_pr_1_99_del": 67,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 423,
    "KG Winter_theta_del": -0.483967351,
    "KG Winter_pr_1_99_del": 67,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 452,
    "KG Spring_theta_del": -0.215965814,
    "KG Spring_pr_1_99_del": 67,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 478,
    "G1 Fall_theta_del": 0.059862748,
    "G1 Fall_pr_1_99_del": 67,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 506,
    "G1 Winter_theta_del": 0.216360519,
    "G1 Winter_pr_1_99_del": 67,
    "G1 Winter_ss_del": 107,
    "G1 Winter_dss_del": 522,
    "G1 Spring_theta_del": 0.440883196,
    "G1 Spring_pr_1_99_del": 67,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 544,
    "G2 Fall_theta_del": 0.679519278,
    "G2 Fall_pr_1_99_del": 67,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 568,
    "G2 Winter_theta_del": 0.907033783,
    "G2 Winter_pr_1_99_del": 67,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 591,
    "G2 Spring_theta_del": 1.192348078,
    "G2 Spring_pr_1_99_del": 67,
    "G2 Spring_ss_del": 106,
    "G2 Spring_dss_del": 619
  },
  {
    "ID": 147,
    "KG Fall_theta_del": -0.766068539,
    "KG Fall_pr_1_99_del": 68,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 423,
    "KG Winter_theta_del": -0.483126051,
    "KG Winter_pr_1_99_del": 68,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 452,
    "KG Spring_theta_del": -0.207810607,
    "KG Spring_pr_1_99_del": 68,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 479,
    "G1 Fall_theta_del": 0.061543309,
    "G1 Fall_pr_1_99_del": 68,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 506,
    "G1 Winter_theta_del": 0.218586181,
    "G1 Winter_pr_1_99_del": 68,
    "G1 Winter_ss_del": 107,
    "G1 Winter_dss_del": 522,
    "G1 Spring_theta_del": 0.441562041,
    "G1 Spring_pr_1_99_del": 68,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 544,
    "G2 Fall_theta_del": 0.687461776,
    "G2 Fall_pr_1_99_del": 68,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 569,
    "G2 Winter_theta_del": 0.910915851,
    "G2 Winter_pr_1_99_del": 68,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 591,
    "G2 Spring_theta_del": 1.193762671,
    "G2 Spring_pr_1_99_del": 68,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 619
  },
  {
    "ID": 150,
    "KG Fall_theta_del": -0.76583562,
    "KG Fall_pr_1_99_del": 68,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 423,
    "KG Winter_theta_del": -0.481613666,
    "KG Winter_pr_1_99_del": 68,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 452,
    "KG Spring_theta_del": -0.205179513,
    "KG Spring_pr_1_99_del": 68,
    "KG Spring_ss_del": 106,
    "KG Spring_dss_del": 479,
    "G1 Fall_theta_del": 0.062830695,
    "G1 Fall_pr_1_99_del": 68,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 506,
    "G1 Winter_theta_del": 0.224240238,
    "G1 Winter_pr_1_99_del": 68,
    "G1 Winter_ss_del": 107,
    "G1 Winter_dss_del": 522,
    "G1 Spring_theta_del": 0.442865852,
    "G1 Spring_pr_1_99_del": 68,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 544,
    "G2 Fall_theta_del": 0.6908465,
    "G2 Fall_pr_1_99_del": 68,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 569,
    "G2 Winter_theta_del": 0.913133763,
    "G2 Winter_pr_1_99_del": 68,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 591,
    "G2 Spring_theta_del": 1.196527793,
    "G2 Spring_pr_1_99_del": 68,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 620
  },
  {
    "ID": 169,
    "KG Fall_theta_del": -0.764922441,
    "KG Fall_pr_1_99_del": 68,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 424,
    "KG Winter_theta_del": -0.47928702,
    "KG Winter_pr_1_99_del": 68,
    "KG Winter_ss_del": 107,
    "KG Winter_dss_del": 452,
    "KG Spring_theta_del": -0.19546845,
    "KG Spring_pr_1_99_del": 68,
    "KG Spring_ss_del": 107,
    "KG Spring_dss_del": 480,
    "G1 Fall_theta_del": 0.067733968,
    "G1 Fall_pr_1_99_del": 68,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 507,
    "G1 Winter_theta_del": 0.233873282,
    "G1 Winter_pr_1_99_del": 68,
    "G1 Winter_ss_del": 107,
    "G1 Winter_dss_del": 523,
    "G1 Spring_theta_del": 0.445312066,
    "G1 Spring_pr_1_99_del": 68,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 545,
    "G2 Fall_theta_del": 0.693712731,
    "G2 Fall_pr_1_99_del": 68,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 569,
    "G2 Winter_theta_del": 0.913654312,
    "G2 Winter_pr_1_99_del": 68,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 591,
    "G2 Spring_theta_del": 1.197574786,
    "G2 Spring_pr_1_99_del": 68,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 620
  },
  {
    "ID": 176,
    "KG Fall_theta_del": -0.76143738,
    "KG Fall_pr_1_99_del": 68,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 424,
    "KG Winter_theta_del": -0.468714845,
    "KG Winter_pr_1_99_del": 68,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 453,
    "KG Spring_theta_del": -0.189987782,
    "KG Spring_pr_1_99_del": 68,
    "KG Spring_ss_del": 107,
    "KG Spring_dss_del": 481,
    "G1 Fall_theta_del": 0.074638792,
    "G1 Fall_pr_1_99_del": 68,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 507,
    "G1 Winter_theta_del": 0.238242604,
    "G1 Winter_pr_1_99_del": 68,
    "G1 Winter_ss_del": 107,
    "G1 Winter_dss_del": 524,
    "G1 Spring_theta_del": 0.455202398,
    "G1 Spring_pr_1_99_del": 68,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 546,
    "G2 Fall_theta_del": 0.694430297,
    "G2 Fall_pr_1_99_del": 68,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 569,
    "G2 Winter_theta_del": 0.92372726,
    "G2 Winter_pr_1_99_del": 68,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 592,
    "G2 Spring_theta_del": 1.203260675,
    "G2 Spring_pr_1_99_del": 68,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 620
  },
  {
    "ID": 218,
    "KG Fall_theta_del": -0.759505153,
    "KG Fall_pr_1_99_del": 68,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 424,
    "KG Winter_theta_del": -0.464877125,
    "KG Winter_pr_1_99_del": 68,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 454,
    "KG Spring_theta_del": -0.187723812,
    "KG Spring_pr_1_99_del": 68,
    "KG Spring_ss_del": 107,
    "KG Spring_dss_del": 481,
    "G1 Fall_theta_del": 0.076748968,
    "G1 Fall_pr_1_99_del": 68,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 508,
    "G1 Winter_theta_del": 0.238405642,
    "G1 Winter_pr_1_99_del": 68,
    "G1 Winter_ss_del": 107,
    "G1 Winter_dss_del": 524,
    "G1 Spring_theta_del": 0.461937422,
    "G1 Spring_pr_1_99_del": 68,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 546,
    "G2 Fall_theta_del": 0.696807049,
    "G2 Fall_pr_1_99_del": 68,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 570,
    "G2 Winter_theta_del": 0.940878266,
    "G2 Winter_pr_1_99_del": 68,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 594,
    "G2 Spring_theta_del": 1.206279419,
    "G2 Spring_pr_1_99_del": 68,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 621
  },
  {
    "ID": 75,
    "KG Fall_theta_del": -0.757683489,
    "KG Fall_pr_1_99_del": 69,
    "KG Fall_ss_del": 107,
    "KG Fall_dss_del": 424,
    "KG Winter_theta_del": -0.462989563,
    "KG Winter_pr_1_99_del": 69,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 454,
    "KG Spring_theta_del": -0.170608332,
    "KG Spring_pr_1_99_del": 69,
    "KG Spring_ss_del": 107,
    "KG Spring_dss_del": 483,
    "G1 Fall_theta_del": 0.080282935,
    "G1 Fall_pr_1_99_del": 69,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 508,
    "G1 Winter_theta_del": 0.240725641,
    "G1 Winter_pr_1_99_del": 69,
    "G1 Winter_ss_del": 107,
    "G1 Winter_dss_del": 524,
    "G1 Spring_theta_del": 0.468640994,
    "G1 Spring_pr_1_99_del": 69,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 547,
    "G2 Fall_theta_del": 0.704410568,
    "G2 Fall_pr_1_99_del": 69,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 570,
    "G2 Winter_theta_del": 0.945642143,
    "G2 Winter_pr_1_99_del": 69,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 595,
    "G2 Spring_theta_del": 1.214437178,
    "G2 Spring_pr_1_99_del": 69,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 621
  },
  {
    "ID": 302,
    "KG Fall_theta_del": -0.753200723,
    "KG Fall_pr_1_99_del": 69,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 425,
    "KG Winter_theta_del": -0.46017217,
    "KG Winter_pr_1_99_del": 69,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 454,
    "KG Spring_theta_del": -0.164913715,
    "KG Spring_pr_1_99_del": 69,
    "KG Spring_ss_del": 107,
    "KG Spring_dss_del": 484,
    "G1 Fall_theta_del": 0.082239768,
    "G1 Fall_pr_1_99_del": 69,
    "G1 Fall_ss_del": 108,
    "G1 Fall_dss_del": 508,
    "G1 Winter_theta_del": 0.253585128,
    "G1 Winter_pr_1_99_del": 69,
    "G1 Winter_ss_del": 107,
    "G1 Winter_dss_del": 525,
    "G1 Spring_theta_del": 0.472625827,
    "G1 Spring_pr_1_99_del": 69,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 547,
    "G2 Fall_theta_del": 0.704654574,
    "G2 Fall_pr_1_99_del": 69,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 570,
    "G2 Winter_theta_del": 0.946223017,
    "G2 Winter_pr_1_99_del": 69,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 595,
    "G2 Spring_theta_del": 1.219588473,
    "G2 Spring_pr_1_99_del": 69,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 622
  },
  {
    "ID": 395,
    "KG Fall_theta_del": -0.746284016,
    "KG Fall_pr_1_99_del": 69,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 425,
    "KG Winter_theta_del": -0.453767981,
    "KG Winter_pr_1_99_del": 69,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 455,
    "KG Spring_theta_del": -0.155292401,
    "KG Spring_pr_1_99_del": 69,
    "KG Spring_ss_del": 107,
    "KG Spring_dss_del": 484,
    "G1 Fall_theta_del": 0.08621922,
    "G1 Fall_pr_1_99_del": 69,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 509,
    "G1 Winter_theta_del": 0.260603028,
    "G1 Winter_pr_1_99_del": 69,
    "G1 Winter_ss_del": 107,
    "G1 Winter_dss_del": 526,
    "G1 Spring_theta_del": 0.473277227,
    "G1 Spring_pr_1_99_del": 69,
    "G1 Spring_ss_del": 107,
    "G1 Spring_dss_del": 547,
    "G2 Fall_theta_del": 0.711152973,
    "G2 Fall_pr_1_99_del": 69,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 571,
    "G2 Winter_theta_del": 0.947580544,
    "G2 Winter_pr_1_99_del": 69,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 595,
    "G2 Spring_theta_del": 1.22015902,
    "G2 Spring_pr_1_99_del": 69,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 622
  },
  {
    "ID": 424,
    "KG Fall_theta_del": -0.743969688,
    "KG Fall_pr_1_99_del": 69,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 426,
    "KG Winter_theta_del": -0.45305061,
    "KG Winter_pr_1_99_del": 69,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 455,
    "KG Spring_theta_del": -0.155041563,
    "KG Spring_pr_1_99_del": 69,
    "KG Spring_ss_del": 107,
    "KG Spring_dss_del": 484,
    "G1 Fall_theta_del": 0.089641118,
    "G1 Fall_pr_1_99_del": 69,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 509,
    "G1 Winter_theta_del": 0.261567658,
    "G1 Winter_pr_1_99_del": 69,
    "G1 Winter_ss_del": 108,
    "G1 Winter_dss_del": 526,
    "G1 Spring_theta_del": 0.482255434,
    "G1 Spring_pr_1_99_del": 69,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 548,
    "G2 Fall_theta_del": 0.711763941,
    "G2 Fall_pr_1_99_del": 69,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 571,
    "G2 Winter_theta_del": 0.948696182,
    "G2 Winter_pr_1_99_del": 69,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 595,
    "G2 Spring_theta_del": 1.23029508,
    "G2 Spring_pr_1_99_del": 69,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 623
  },
  {
    "ID": 443,
    "KG Fall_theta_del": -0.743782596,
    "KG Fall_pr_1_99_del": 69,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 426,
    "KG Winter_theta_del": -0.450394698,
    "KG Winter_pr_1_99_del": 69,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 455,
    "KG Spring_theta_del": -0.154437514,
    "KG Spring_pr_1_99_del": 69,
    "KG Spring_ss_del": 107,
    "KG Spring_dss_del": 485,
    "G1 Fall_theta_del": 0.090225144,
    "G1 Fall_pr_1_99_del": 69,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 509,
    "G1 Winter_theta_del": 0.262759517,
    "G1 Winter_pr_1_99_del": 69,
    "G1 Winter_ss_del": 108,
    "G1 Winter_dss_del": 526,
    "G1 Spring_theta_del": 0.489923986,
    "G1 Spring_pr_1_99_del": 69,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 549,
    "G2 Fall_theta_del": 0.713103864,
    "G2 Fall_pr_1_99_del": 69,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 571,
    "G2 Winter_theta_del": 0.949548403,
    "G2 Winter_pr_1_99_del": 69,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 595,
    "G2 Spring_theta_del": 1.231510608,
    "G2 Spring_pr_1_99_del": 69,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 623
  },
  {
    "ID": 8,
    "KG Fall_theta_del": -0.737131885,
    "KG Fall_pr_1_99_del": 70,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 426,
    "KG Winter_theta_del": -0.448980461,
    "KG Winter_pr_1_99_del": 70,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 455,
    "KG Spring_theta_del": -0.150504959,
    "KG Spring_pr_1_99_del": 70,
    "KG Spring_ss_del": 108,
    "KG Spring_dss_del": 485,
    "G1 Fall_theta_del": 0.093138753,
    "G1 Fall_pr_1_99_del": 70,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 509,
    "G1 Winter_theta_del": 0.26318219,
    "G1 Winter_pr_1_99_del": 70,
    "G1 Winter_ss_del": 108,
    "G1 Winter_dss_del": 526,
    "G1 Spring_theta_del": 0.490531123,
    "G1 Spring_pr_1_99_del": 70,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 549,
    "G2 Fall_theta_del": 0.715191181,
    "G2 Fall_pr_1_99_del": 70,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 572,
    "G2 Winter_theta_del": 0.952144811,
    "G2 Winter_pr_1_99_del": 70,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 595,
    "G2 Spring_theta_del": 1.231682075,
    "G2 Spring_pr_1_99_del": 70,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 623
  },
  {
    "ID": 45,
    "KG Fall_theta_del": -0.736005935,
    "KG Fall_pr_1_99_del": 70,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 426,
    "KG Winter_theta_del": -0.447264255,
    "KG Winter_pr_1_99_del": 70,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 455,
    "KG Spring_theta_del": -0.149770513,
    "KG Spring_pr_1_99_del": 70,
    "KG Spring_ss_del": 108,
    "KG Spring_dss_del": 485,
    "G1 Fall_theta_del": 0.099949473,
    "G1 Fall_pr_1_99_del": 70,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 510,
    "G1 Winter_theta_del": 0.264191826,
    "G1 Winter_pr_1_99_del": 70,
    "G1 Winter_ss_del": 108,
    "G1 Winter_dss_del": 526,
    "G1 Spring_theta_del": 0.493130749,
    "G1 Spring_pr_1_99_del": 70,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 549,
    "G2 Fall_theta_del": 0.716420477,
    "G2 Fall_pr_1_99_del": 70,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 572,
    "G2 Winter_theta_del": 0.952901079,
    "G2 Winter_pr_1_99_del": 70,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 595,
    "G2 Spring_theta_del": 1.232773249,
    "G2 Spring_pr_1_99_del": 70,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 623
  },
  {
    "ID": 183,
    "KG Fall_theta_del": -0.721605434,
    "KG Fall_pr_1_99_del": 70,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 428,
    "KG Winter_theta_del": -0.445160615,
    "KG Winter_pr_1_99_del": 70,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 455,
    "KG Spring_theta_del": -0.132951203,
    "KG Spring_pr_1_99_del": 70,
    "KG Spring_ss_del": 108,
    "KG Spring_dss_del": 487,
    "G1 Fall_theta_del": 0.105878376,
    "G1 Fall_pr_1_99_del": 70,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 511,
    "G1 Winter_theta_del": 0.271054947,
    "G1 Winter_pr_1_99_del": 70,
    "G1 Winter_ss_del": 108,
    "G1 Winter_dss_del": 527,
    "G1 Spring_theta_del": 0.494016683,
    "G1 Spring_pr_1_99_del": 70,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 549,
    "G2 Fall_theta_del": 0.717680765,
    "G2 Fall_pr_1_99_del": 70,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 572,
    "G2 Winter_theta_del": 0.954686548,
    "G2 Winter_pr_1_99_del": 70,
    "G2 Winter_ss_del": 107,
    "G2 Winter_dss_del": 595,
    "G2 Spring_theta_del": 1.240579057,
    "G2 Spring_pr_1_99_del": 70,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 624
  },
  {
    "ID": 225,
    "KG Fall_theta_del": -0.720703725,
    "KG Fall_pr_1_99_del": 70,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 428,
    "KG Winter_theta_del": -0.442932136,
    "KG Winter_pr_1_99_del": 70,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 456,
    "KG Spring_theta_del": -0.130422225,
    "KG Spring_pr_1_99_del": 70,
    "KG Spring_ss_del": 108,
    "KG Spring_dss_del": 487,
    "G1 Fall_theta_del": 0.116673488,
    "G1 Fall_pr_1_99_del": 70,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 512,
    "G1 Winter_theta_del": 0.271264658,
    "G1 Winter_pr_1_99_del": 70,
    "G1 Winter_ss_del": 108,
    "G1 Winter_dss_del": 527,
    "G1 Spring_theta_del": 0.497767436,
    "G1 Spring_pr_1_99_del": 70,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 550,
    "G2 Fall_theta_del": 0.719932581,
    "G2 Fall_pr_1_99_del": 70,
    "G2 Fall_ss_del": 107,
    "G2 Fall_dss_del": 572,
    "G2 Winter_theta_del": 0.955371409,
    "G2 Winter_pr_1_99_del": 70,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 596,
    "G2 Spring_theta_del": 1.246686946,
    "G2 Spring_pr_1_99_del": 70,
    "G2 Spring_ss_del": 107,
    "G2 Spring_dss_del": 625
  },
  {
    "ID": 394,
    "KG Fall_theta_del": -0.719822396,
    "KG Fall_pr_1_99_del": 70,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 428,
    "KG Winter_theta_del": -0.435683862,
    "KG Winter_pr_1_99_del": 70,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 456,
    "KG Spring_theta_del": -0.127192303,
    "KG Spring_pr_1_99_del": 70,
    "KG Spring_ss_del": 108,
    "KG Spring_dss_del": 487,
    "G1 Fall_theta_del": 0.118508863,
    "G1 Fall_pr_1_99_del": 70,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 512,
    "G1 Winter_theta_del": 0.280349034,
    "G1 Winter_pr_1_99_del": 70,
    "G1 Winter_ss_del": 108,
    "G1 Winter_dss_del": 528,
    "G1 Spring_theta_del": 0.499911656,
    "G1 Spring_pr_1_99_del": 70,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 550,
    "G2 Fall_theta_del": 0.726856693,
    "G2 Fall_pr_1_99_del": 70,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 573,
    "G2 Winter_theta_del": 0.959696999,
    "G2 Winter_pr_1_99_del": 70,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 596,
    "G2 Spring_theta_del": 1.248022888,
    "G2 Spring_pr_1_99_del": 70,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 625
  },
  {
    "ID": 65,
    "KG Fall_theta_del": -0.710827075,
    "KG Fall_pr_1_99_del": 71,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 429,
    "KG Winter_theta_del": -0.427830799,
    "KG Winter_pr_1_99_del": 71,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 457,
    "KG Spring_theta_del": -0.119585542,
    "KG Spring_pr_1_99_del": 71,
    "KG Spring_ss_del": 108,
    "KG Spring_dss_del": 488,
    "G1 Fall_theta_del": 0.119681263,
    "G1 Fall_pr_1_99_del": 71,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 512,
    "G1 Winter_theta_del": 0.288293225,
    "G1 Winter_pr_1_99_del": 71,
    "G1 Winter_ss_del": 108,
    "G1 Winter_dss_del": 529,
    "G1 Spring_theta_del": 0.502038692,
    "G1 Spring_pr_1_99_del": 71,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 550,
    "G2 Fall_theta_del": 0.728157916,
    "G2 Fall_pr_1_99_del": 71,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 573,
    "G2 Winter_theta_del": 0.961015254,
    "G2 Winter_pr_1_99_del": 71,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 596,
    "G2 Spring_theta_del": 1.248329423,
    "G2 Spring_pr_1_99_del": 71,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 625
  },
  {
    "ID": 158,
    "KG Fall_theta_del": -0.710546764,
    "KG Fall_pr_1_99_del": 71,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 429,
    "KG Winter_theta_del": -0.426990781,
    "KG Winter_pr_1_99_del": 71,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 457,
    "KG Spring_theta_del": -0.11932451,
    "KG Spring_pr_1_99_del": 71,
    "KG Spring_ss_del": 108,
    "KG Spring_dss_del": 488,
    "G1 Fall_theta_del": 0.121016777,
    "G1 Fall_pr_1_99_del": 71,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 512,
    "G1 Winter_theta_del": 0.300456378,
    "G1 Winter_pr_1_99_del": 71,
    "G1 Winter_ss_del": 108,
    "G1 Winter_dss_del": 530,
    "G1 Spring_theta_del": 0.506587013,
    "G1 Spring_pr_1_99_del": 71,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 551,
    "G2 Fall_theta_del": 0.735397186,
    "G2 Fall_pr_1_99_del": 71,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 574,
    "G2 Winter_theta_del": 0.968574342,
    "G2 Winter_pr_1_99_del": 71,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 597,
    "G2 Spring_theta_del": 1.253368577,
    "G2 Spring_pr_1_99_del": 71,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 625
  },
  {
    "ID": 227,
    "KG Fall_theta_del": -0.706571236,
    "KG Fall_pr_1_99_del": 71,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 429,
    "KG Winter_theta_del": -0.425321686,
    "KG Winter_pr_1_99_del": 71,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 457,
    "KG Spring_theta_del": -0.11922177,
    "KG Spring_pr_1_99_del": 71,
    "KG Spring_ss_del": 108,
    "KG Spring_dss_del": 488,
    "G1 Fall_theta_del": 0.121340068,
    "G1 Fall_pr_1_99_del": 71,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 512,
    "G1 Winter_theta_del": 0.302083907,
    "G1 Winter_pr_1_99_del": 71,
    "G1 Winter_ss_del": 108,
    "G1 Winter_dss_del": 530,
    "G1 Spring_theta_del": 0.508968141,
    "G1 Spring_pr_1_99_del": 71,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 551,
    "G2 Fall_theta_del": 0.742232311,
    "G2 Fall_pr_1_99_del": 71,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 574,
    "G2 Winter_theta_del": 0.971021725,
    "G2 Winter_pr_1_99_del": 71,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 597,
    "G2 Spring_theta_del": 1.253591656,
    "G2 Spring_pr_1_99_del": 71,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 625
  },
  {
    "ID": 374,
    "KG Fall_theta_del": -0.701837194,
    "KG Fall_pr_1_99_del": 71,
    "KG Fall_ss_del": 108,
    "KG Fall_dss_del": 430,
    "KG Winter_theta_del": -0.420577771,
    "KG Winter_pr_1_99_del": 71,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 458,
    "KG Spring_theta_del": -0.116289934,
    "KG Spring_pr_1_99_del": 71,
    "KG Spring_ss_del": 108,
    "KG Spring_dss_del": 488,
    "G1 Fall_theta_del": 0.122253449,
    "G1 Fall_pr_1_99_del": 71,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 512,
    "G1 Winter_theta_del": 0.322421035,
    "G1 Winter_pr_1_99_del": 71,
    "G1 Winter_ss_del": 109,
    "G1 Winter_dss_del": 532,
    "G1 Spring_theta_del": 0.51169717,
    "G1 Spring_pr_1_99_del": 71,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 551,
    "G2 Fall_theta_del": 0.742297817,
    "G2 Fall_pr_1_99_del": 71,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 574,
    "G2 Winter_theta_del": 0.971765194,
    "G2 Winter_pr_1_99_del": 71,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 597,
    "G2 Spring_theta_del": 1.255479085,
    "G2 Spring_pr_1_99_del": 71,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 626
  },
  {
    "ID": 432,
    "KG Fall_theta_del": -0.697524089,
    "KG Fall_pr_1_99_del": 71,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 430,
    "KG Winter_theta_del": -0.420517541,
    "KG Winter_pr_1_99_del": 71,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 458,
    "KG Spring_theta_del": -0.107603569,
    "KG Spring_pr_1_99_del": 71,
    "KG Spring_ss_del": 108,
    "KG Spring_dss_del": 489,
    "G1 Fall_theta_del": 0.125809489,
    "G1 Fall_pr_1_99_del": 71,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 513,
    "G1 Winter_theta_del": 0.32958669,
    "G1 Winter_pr_1_99_del": 71,
    "G1 Winter_ss_del": 109,
    "G1 Winter_dss_del": 533,
    "G1 Spring_theta_del": 0.513257651,
    "G1 Spring_pr_1_99_del": 71,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 551,
    "G2 Fall_theta_del": 0.744614539,
    "G2 Fall_pr_1_99_del": 71,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 574,
    "G2 Winter_theta_del": 0.973677911,
    "G2 Winter_pr_1_99_del": 71,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 597,
    "G2 Spring_theta_del": 1.257280296,
    "G2 Spring_pr_1_99_del": 71,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 626
  },
  {
    "ID": 30,
    "KG Fall_theta_del": -0.69482026,
    "KG Fall_pr_1_99_del": 72,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 431,
    "KG Winter_theta_del": -0.416477895,
    "KG Winter_pr_1_99_del": 72,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 458,
    "KG Spring_theta_del": -0.09645111,
    "KG Spring_pr_1_99_del": 72,
    "KG Spring_ss_del": 109,
    "KG Spring_dss_del": 490,
    "G1 Fall_theta_del": 0.12779137,
    "G1 Fall_pr_1_99_del": 72,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 513,
    "G1 Winter_theta_del": 0.338591063,
    "G1 Winter_pr_1_99_del": 72,
    "G1 Winter_ss_del": 109,
    "G1 Winter_dss_del": 534,
    "G1 Spring_theta_del": 0.514599221,
    "G1 Spring_pr_1_99_del": 72,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 551,
    "G2 Fall_theta_del": 0.75659706,
    "G2 Fall_pr_1_99_del": 72,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 576,
    "G2 Winter_theta_del": 0.975904052,
    "G2 Winter_pr_1_99_del": 72,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 598,
    "G2 Spring_theta_del": 1.262873429,
    "G2 Spring_pr_1_99_del": 72,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 626
  },
  {
    "ID": 265,
    "KG Fall_theta_del": -0.694792217,
    "KG Fall_pr_1_99_del": 72,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 431,
    "KG Winter_theta_del": -0.414961039,
    "KG Winter_pr_1_99_del": 72,
    "KG Winter_ss_del": 108,
    "KG Winter_dss_del": 459,
    "KG Spring_theta_del": -0.095200908,
    "KG Spring_pr_1_99_del": 72,
    "KG Spring_ss_del": 109,
    "KG Spring_dss_del": 490,
    "G1 Fall_theta_del": 0.13290467,
    "G1 Fall_pr_1_99_del": 72,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 513,
    "G1 Winter_theta_del": 0.344549378,
    "G1 Winter_pr_1_99_del": 72,
    "G1 Winter_ss_del": 109,
    "G1 Winter_dss_del": 534,
    "G1 Spring_theta_del": 0.517262935,
    "G1 Spring_pr_1_99_del": 72,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 552,
    "G2 Fall_theta_del": 0.759416439,
    "G2 Fall_pr_1_99_del": 72,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 576,
    "G2 Winter_theta_del": 0.976274138,
    "G2 Winter_pr_1_99_del": 72,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 598,
    "G2 Spring_theta_del": 1.268294003,
    "G2 Spring_pr_1_99_del": 72,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 627
  },
  {
    "ID": 278,
    "KG Fall_theta_del": -0.68782601,
    "KG Fall_pr_1_99_del": 72,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 431,
    "KG Winter_theta_del": -0.412413263,
    "KG Winter_pr_1_99_del": 72,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 459,
    "KG Spring_theta_del": -0.092462794,
    "KG Spring_pr_1_99_del": 72,
    "KG Spring_ss_del": 109,
    "KG Spring_dss_del": 491,
    "G1 Fall_theta_del": 0.13303241,
    "G1 Fall_pr_1_99_del": 72,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 513,
    "G1 Winter_theta_del": 0.35265278,
    "G1 Winter_pr_1_99_del": 72,
    "G1 Winter_ss_del": 109,
    "G1 Winter_dss_del": 535,
    "G1 Spring_theta_del": 0.522883466,
    "G1 Spring_pr_1_99_del": 72,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 552,
    "G2 Fall_theta_del": 0.760166331,
    "G2 Fall_pr_1_99_del": 72,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 576,
    "G2 Winter_theta_del": 0.981593307,
    "G2 Winter_pr_1_99_del": 72,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 598,
    "G2 Spring_theta_del": 1.270766594,
    "G2 Spring_pr_1_99_del": 72,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 627
  },
  {
    "ID": 318,
    "KG Fall_theta_del": -0.685050595,
    "KG Fall_pr_1_99_del": 72,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 431,
    "KG Winter_theta_del": -0.411840369,
    "KG Winter_pr_1_99_del": 72,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 459,
    "KG Spring_theta_del": -0.086871333,
    "KG Spring_pr_1_99_del": 72,
    "KG Spring_ss_del": 109,
    "KG Spring_dss_del": 491,
    "G1 Fall_theta_del": 0.133629365,
    "G1 Fall_pr_1_99_del": 72,
    "G1 Fall_ss_del": 109,
    "G1 Fall_dss_del": 513,
    "G1 Winter_theta_del": 0.354646668,
    "G1 Winter_pr_1_99_del": 72,
    "G1 Winter_ss_del": 109,
    "G1 Winter_dss_del": 535,
    "G1 Spring_theta_del": 0.527532261,
    "G1 Spring_pr_1_99_del": 72,
    "G1 Spring_ss_del": 108,
    "G1 Spring_dss_del": 553,
    "G2 Fall_theta_del": 0.762009246,
    "G2 Fall_pr_1_99_del": 72,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 576,
    "G2 Winter_theta_del": 0.984617523,
    "G2 Winter_pr_1_99_del": 72,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 598,
    "G2 Spring_theta_del": 1.276687173,
    "G2 Spring_pr_1_99_del": 72,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 628
  },
  {
    "ID": 324,
    "KG Fall_theta_del": -0.68365897,
    "KG Fall_pr_1_99_del": 72,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 432,
    "KG Winter_theta_del": -0.407157575,
    "KG Winter_pr_1_99_del": 72,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 459,
    "KG Spring_theta_del": -0.069456672,
    "KG Spring_pr_1_99_del": 72,
    "KG Spring_ss_del": 109,
    "KG Spring_dss_del": 493,
    "G1 Fall_theta_del": 0.141705269,
    "G1 Fall_pr_1_99_del": 72,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 514,
    "G1 Winter_theta_del": 0.355414642,
    "G1 Winter_pr_1_99_del": 72,
    "G1 Winter_ss_del": 109,
    "G1 Winter_dss_del": 536,
    "G1 Spring_theta_del": 0.533790201,
    "G1 Spring_pr_1_99_del": 72,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 553,
    "G2 Fall_theta_del": 0.765057165,
    "G2 Fall_pr_1_99_del": 72,
    "G2 Fall_ss_del": 108,
    "G2 Fall_dss_del": 577,
    "G2 Winter_theta_del": 0.986208764,
    "G2 Winter_pr_1_99_del": 72,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 599,
    "G2 Spring_theta_del": 1.290897528,
    "G2 Spring_pr_1_99_del": 72,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 629
  },
  {
    "ID": 70,
    "KG Fall_theta_del": -0.669638728,
    "KG Fall_pr_1_99_del": 73,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 433,
    "KG Winter_theta_del": -0.406752817,
    "KG Winter_pr_1_99_del": 73,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 459,
    "KG Spring_theta_del": -0.068763998,
    "KG Spring_pr_1_99_del": 73,
    "KG Spring_ss_del": 109,
    "KG Spring_dss_del": 493,
    "G1 Fall_theta_del": 0.146902912,
    "G1 Fall_pr_1_99_del": 73,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 515,
    "G1 Winter_theta_del": 0.358769297,
    "G1 Winter_pr_1_99_del": 73,
    "G1 Winter_ss_del": 109,
    "G1 Winter_dss_del": 536,
    "G1 Spring_theta_del": 0.533881404,
    "G1 Spring_pr_1_99_del": 73,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 553,
    "G2 Fall_theta_del": 0.775932146,
    "G2 Fall_pr_1_99_del": 73,
    "G2 Fall_ss_del": 109,
    "G2 Fall_dss_del": 578,
    "G2 Winter_theta_del": 0.988194004,
    "G2 Winter_pr_1_99_del": 73,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 599,
    "G2 Spring_theta_del": 1.292735754,
    "G2 Spring_pr_1_99_del": 73,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 629
  },
  {
    "ID": 130,
    "KG Fall_theta_del": -0.669356959,
    "KG Fall_pr_1_99_del": 73,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 433,
    "KG Winter_theta_del": -0.404367975,
    "KG Winter_pr_1_99_del": 73,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 460,
    "KG Spring_theta_del": -0.067574968,
    "KG Spring_pr_1_99_del": 73,
    "KG Spring_ss_del": 109,
    "KG Spring_dss_del": 493,
    "G1 Fall_theta_del": 0.152999624,
    "G1 Fall_pr_1_99_del": 73,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 515,
    "G1 Winter_theta_del": 0.360192806,
    "G1 Winter_pr_1_99_del": 73,
    "G1 Winter_ss_del": 109,
    "G1 Winter_dss_del": 536,
    "G1 Spring_theta_del": 0.535049225,
    "G1 Spring_pr_1_99_del": 73,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 554,
    "G2 Fall_theta_del": 0.779389261,
    "G2 Fall_pr_1_99_del": 73,
    "G2 Fall_ss_del": 109,
    "G2 Fall_dss_del": 578,
    "G2 Winter_theta_del": 0.988586914,
    "G2 Winter_pr_1_99_del": 73,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 599,
    "G2 Spring_theta_del": 1.295555201,
    "G2 Spring_pr_1_99_del": 73,
    "G2 Spring_ss_del": 108,
    "G2 Spring_dss_del": 630
  },
  {
    "ID": 290,
    "KG Fall_theta_del": -0.660497256,
    "KG Fall_pr_1_99_del": 73,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 434,
    "KG Winter_theta_del": -0.396087757,
    "KG Winter_pr_1_99_del": 73,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 460,
    "KG Spring_theta_del": -0.051100661,
    "KG Spring_pr_1_99_del": 73,
    "KG Spring_ss_del": 109,
    "KG Spring_dss_del": 495,
    "G1 Fall_theta_del": 0.157738953,
    "G1 Fall_pr_1_99_del": 73,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 516,
    "G1 Winter_theta_del": 0.365234097,
    "G1 Winter_pr_1_99_del": 73,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 537,
    "G1 Spring_theta_del": 0.536990333,
    "G1 Spring_pr_1_99_del": 73,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 554,
    "G2 Fall_theta_del": 0.783409091,
    "G2 Fall_pr_1_99_del": 73,
    "G2 Fall_ss_del": 109,
    "G2 Fall_dss_del": 578,
    "G2 Winter_theta_del": 0.993784273,
    "G2 Winter_pr_1_99_del": 73,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 599,
    "G2 Spring_theta_del": 1.319245438,
    "G2 Spring_pr_1_99_del": 73,
    "G2 Spring_ss_del": 109,
    "G2 Spring_dss_del": 632
  },
  {
    "ID": 337,
    "KG Fall_theta_del": -0.654962078,
    "KG Fall_pr_1_99_del": 73,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 435,
    "KG Winter_theta_del": -0.381930302,
    "KG Winter_pr_1_99_del": 73,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 462,
    "KG Spring_theta_del": -0.028214053,
    "KG Spring_pr_1_99_del": 73,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 497,
    "G1 Fall_theta_del": 0.160232439,
    "G1 Fall_pr_1_99_del": 73,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 516,
    "G1 Winter_theta_del": 0.365700498,
    "G1 Winter_pr_1_99_del": 73,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 537,
    "G1 Spring_theta_del": 0.542263393,
    "G1 Spring_pr_1_99_del": 73,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 554,
    "G2 Fall_theta_del": 0.796455435,
    "G2 Fall_pr_1_99_del": 73,
    "G2 Fall_ss_del": 109,
    "G2 Fall_dss_del": 580,
    "G2 Winter_theta_del": 1.005863085,
    "G2 Winter_pr_1_99_del": 73,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 601,
    "G2 Spring_theta_del": 1.336212596,
    "G2 Spring_pr_1_99_del": 73,
    "G2 Spring_ss_del": 109,
    "G2 Spring_dss_del": 634
  },
  {
    "ID": 500,
    "KG Fall_theta_del": -0.64757323,
    "KG Fall_pr_1_99_del": 73,
    "KG Fall_ss_del": 109,
    "KG Fall_dss_del": 435,
    "KG Winter_theta_del": -0.380836398,
    "KG Winter_pr_1_99_del": 73,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 462,
    "KG Spring_theta_del": -0.025609916,
    "KG Spring_pr_1_99_del": 73,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 497,
    "G1 Fall_theta_del": 0.164930207,
    "G1 Fall_pr_1_99_del": 73,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 516,
    "G1 Winter_theta_del": 0.369408391,
    "G1 Winter_pr_1_99_del": 73,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 537,
    "G1 Spring_theta_del": 0.54530375,
    "G1 Spring_pr_1_99_del": 73,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 555,
    "G2 Fall_theta_del": 0.806277023,
    "G2 Fall_pr_1_99_del": 73,
    "G2 Fall_ss_del": 109,
    "G2 Fall_dss_del": 581,
    "G2 Winter_theta_del": 1.007558989,
    "G2 Winter_pr_1_99_del": 73,
    "G2 Winter_ss_del": 108,
    "G2 Winter_dss_del": 601,
    "G2 Spring_theta_del": 1.345896123,
    "G2 Spring_pr_1_99_del": 73,
    "G2 Spring_ss_del": 109,
    "G2 Spring_dss_del": 635
  },
  {
    "ID": 274,
    "KG Fall_theta_del": -0.642353096,
    "KG Fall_pr_1_99_del": 74,
    "KG Fall_ss_del": 110,
    "KG Fall_dss_del": 436,
    "KG Winter_theta_del": -0.378307693,
    "KG Winter_pr_1_99_del": 74,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 462,
    "KG Spring_theta_del": -0.024763407,
    "KG Spring_pr_1_99_del": 74,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 498,
    "G1 Fall_theta_del": 0.166022287,
    "G1 Fall_pr_1_99_del": 74,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 517,
    "G1 Winter_theta_del": 0.387533871,
    "G1 Winter_pr_1_99_del": 74,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 539,
    "G1 Spring_theta_del": 0.562020239,
    "G1 Spring_pr_1_99_del": 74,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 556,
    "G2 Fall_theta_del": 0.809578809,
    "G2 Fall_pr_1_99_del": 74,
    "G2 Fall_ss_del": 109,
    "G2 Fall_dss_del": 581,
    "G2 Winter_theta_del": 1.02316163,
    "G2 Winter_pr_1_99_del": 74,
    "G2 Winter_ss_del": 109,
    "G2 Winter_dss_del": 602,
    "G2 Spring_theta_del": 1.349138615,
    "G2 Spring_pr_1_99_del": 74,
    "G2 Spring_ss_del": 109,
    "G2 Spring_dss_del": 635
  },
  {
    "ID": 452,
    "KG Fall_theta_del": -0.641388123,
    "KG Fall_pr_1_99_del": 74,
    "KG Fall_ss_del": 110,
    "KG Fall_dss_del": 436,
    "KG Winter_theta_del": -0.373617817,
    "KG Winter_pr_1_99_del": 74,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 463,
    "KG Spring_theta_del": -0.022664132,
    "KG Spring_pr_1_99_del": 74,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 498,
    "G1 Fall_theta_del": 0.168307456,
    "G1 Fall_pr_1_99_del": 74,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 517,
    "G1 Winter_theta_del": 0.38795081,
    "G1 Winter_pr_1_99_del": 74,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 539,
    "G1 Spring_theta_del": 0.566244152,
    "G1 Spring_pr_1_99_del": 74,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 557,
    "G2 Fall_theta_del": 0.810203924,
    "G2 Fall_pr_1_99_del": 74,
    "G2 Fall_ss_del": 109,
    "G2 Fall_dss_del": 581,
    "G2 Winter_theta_del": 1.028156219,
    "G2 Winter_pr_1_99_del": 74,
    "G2 Winter_ss_del": 109,
    "G2 Winter_dss_del": 603,
    "G2 Spring_theta_del": 1.352589258,
    "G2 Spring_pr_1_99_del": 74,
    "G2 Spring_ss_del": 109,
    "G2 Spring_dss_del": 635
  },
  {
    "ID": 464,
    "KG Fall_theta_del": -0.637124455,
    "KG Fall_pr_1_99_del": 74,
    "KG Fall_ss_del": 110,
    "KG Fall_dss_del": 436,
    "KG Winter_theta_del": -0.369705096,
    "KG Winter_pr_1_99_del": 74,
    "KG Winter_ss_del": 109,
    "KG Winter_dss_del": 463,
    "KG Spring_theta_del": -0.020593635,
    "KG Spring_pr_1_99_del": 74,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 498,
    "G1 Fall_theta_del": 0.178260998,
    "G1 Fall_pr_1_99_del": 74,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 518,
    "G1 Winter_theta_del": 0.394770425,
    "G1 Winter_pr_1_99_del": 74,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 539,
    "G1 Spring_theta_del": 0.567499399,
    "G1 Spring_pr_1_99_del": 74,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 557,
    "G2 Fall_theta_del": 0.811049649,
    "G2 Fall_pr_1_99_del": 74,
    "G2 Fall_ss_del": 109,
    "G2 Fall_dss_del": 581,
    "G2 Winter_theta_del": 1.033944221,
    "G2 Winter_pr_1_99_del": 74,
    "G2 Winter_ss_del": 109,
    "G2 Winter_dss_del": 603,
    "G2 Spring_theta_del": 1.356811978,
    "G2 Spring_pr_1_99_del": 74,
    "G2 Spring_ss_del": 110,
    "G2 Spring_dss_del": 636
  },
  {
    "ID": 478,
    "KG Fall_theta_del": -0.635322274,
    "KG Fall_pr_1_99_del": 74,
    "KG Fall_ss_del": 110,
    "KG Fall_dss_del": 436,
    "KG Winter_theta_del": -0.353069283,
    "KG Winter_pr_1_99_del": 74,
    "KG Winter_ss_del": 110,
    "KG Winter_dss_del": 465,
    "KG Spring_theta_del": -0.020343439,
    "KG Spring_pr_1_99_del": 74,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 498,
    "G1 Fall_theta_del": 0.179398728,
    "G1 Fall_pr_1_99_del": 74,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 518,
    "G1 Winter_theta_del": 0.396344347,
    "G1 Winter_pr_1_99_del": 74,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 540,
    "G1 Spring_theta_del": 0.569092168,
    "G1 Spring_pr_1_99_del": 74,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 557,
    "G2 Fall_theta_del": 0.822156032,
    "G2 Fall_pr_1_99_del": 74,
    "G2 Fall_ss_del": 109,
    "G2 Fall_dss_del": 582,
    "G2 Winter_theta_del": 1.036025955,
    "G2 Winter_pr_1_99_del": 74,
    "G2 Winter_ss_del": 109,
    "G2 Winter_dss_del": 604,
    "G2 Spring_theta_del": 1.359861308,
    "G2 Spring_pr_1_99_del": 74,
    "G2 Spring_ss_del": 110,
    "G2 Spring_dss_del": 636
  },
  {
    "ID": 482,
    "KG Fall_theta_del": -0.624448301,
    "KG Fall_pr_1_99_del": 74,
    "KG Fall_ss_del": 110,
    "KG Fall_dss_del": 438,
    "KG Winter_theta_del": -0.352189515,
    "KG Winter_pr_1_99_del": 74,
    "KG Winter_ss_del": 110,
    "KG Winter_dss_del": 465,
    "KG Spring_theta_del": -0.015549079,
    "KG Spring_pr_1_99_del": 74,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 498,
    "G1 Fall_theta_del": 0.186068058,
    "G1 Fall_pr_1_99_del": 74,
    "G1 Fall_ss_del": 110,
    "G1 Fall_dss_del": 519,
    "G1 Winter_theta_del": 0.403367032,
    "G1 Winter_pr_1_99_del": 74,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 540,
    "G1 Spring_theta_del": 0.569941015,
    "G1 Spring_pr_1_99_del": 74,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 557,
    "G2 Fall_theta_del": 0.822638694,
    "G2 Fall_pr_1_99_del": 74,
    "G2 Fall_ss_del": 109,
    "G2 Fall_dss_del": 582,
    "G2 Winter_theta_del": 1.038024318,
    "G2 Winter_pr_1_99_del": 74,
    "G2 Winter_ss_del": 109,
    "G2 Winter_dss_del": 604,
    "G2 Spring_theta_del": 1.361232591,
    "G2 Spring_pr_1_99_del": 74,
    "G2 Spring_ss_del": 110,
    "G2 Spring_dss_del": 636
  },
  {
    "ID": 105,
    "KG Fall_theta_del": -0.621413891,
    "KG Fall_pr_1_99_del": 75,
    "KG Fall_ss_del": 110,
    "KG Fall_dss_del": 438,
    "KG Winter_theta_del": -0.344543724,
    "KG Winter_pr_1_99_del": 75,
    "KG Winter_ss_del": 110,
    "KG Winter_dss_del": 466,
    "KG Spring_theta_del": -0.015301586,
    "KG Spring_pr_1_99_del": 75,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 498,
    "G1 Fall_theta_del": 0.193700521,
    "G1 Fall_pr_1_99_del": 75,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 519,
    "G1 Winter_theta_del": 0.404053104,
    "G1 Winter_pr_1_99_del": 75,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 540,
    "G1 Spring_theta_del": 0.577696031,
    "G1 Spring_pr_1_99_del": 75,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 558,
    "G2 Fall_theta_del": 0.826824726,
    "G2 Fall_pr_1_99_del": 75,
    "G2 Fall_ss_del": 110,
    "G2 Fall_dss_del": 583,
    "G2 Winter_theta_del": 1.041169777,
    "G2 Winter_pr_1_99_del": 75,
    "G2 Winter_ss_del": 109,
    "G2 Winter_dss_del": 604,
    "G2 Spring_theta_del": 1.368104036,
    "G2 Spring_pr_1_99_del": 75,
    "G2 Spring_ss_del": 110,
    "G2 Spring_dss_del": 637
  },
  {
    "ID": 243,
    "KG Fall_theta_del": -0.613606774,
    "KG Fall_pr_1_99_del": 75,
    "KG Fall_ss_del": 110,
    "KG Fall_dss_del": 439,
    "KG Winter_theta_del": -0.335983211,
    "KG Winter_pr_1_99_del": 75,
    "KG Winter_ss_del": 110,
    "KG Winter_dss_del": 466,
    "KG Spring_theta_del": -0.014213918,
    "KG Spring_pr_1_99_del": 75,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 499,
    "G1 Fall_theta_del": 0.19418866,
    "G1 Fall_pr_1_99_del": 75,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 519,
    "G1 Winter_theta_del": 0.405419088,
    "G1 Winter_pr_1_99_del": 75,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 541,
    "G1 Spring_theta_del": 0.577913104,
    "G1 Spring_pr_1_99_del": 75,
    "G1 Spring_ss_del": 109,
    "G1 Spring_dss_del": 558,
    "G2 Fall_theta_del": 0.830804665,
    "G2 Fall_pr_1_99_del": 75,
    "G2 Fall_ss_del": 110,
    "G2 Fall_dss_del": 583,
    "G2 Winter_theta_del": 1.05017261,
    "G2 Winter_pr_1_99_del": 75,
    "G2 Winter_ss_del": 109,
    "G2 Winter_dss_del": 605,
    "G2 Spring_theta_del": 1.390068919,
    "G2 Spring_pr_1_99_del": 75,
    "G2 Spring_ss_del": 110,
    "G2 Spring_dss_del": 639
  },
  {
    "ID": 418,
    "KG Fall_theta_del": -0.609453283,
    "KG Fall_pr_1_99_del": 75,
    "KG Fall_ss_del": 110,
    "KG Fall_dss_del": 439,
    "KG Winter_theta_del": -0.333685017,
    "KG Winter_pr_1_99_del": 75,
    "KG Winter_ss_del": 110,
    "KG Winter_dss_del": 467,
    "KG Spring_theta_del": -0.008034352,
    "KG Spring_pr_1_99_del": 75,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 499,
    "G1 Fall_theta_del": 0.200158662,
    "G1 Fall_pr_1_99_del": 75,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 520,
    "G1 Winter_theta_del": 0.410379744,
    "G1 Winter_pr_1_99_del": 75,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 541,
    "G1 Spring_theta_del": 0.584012782,
    "G1 Spring_pr_1_99_del": 75,
    "G1 Spring_ss_del": 110,
    "G1 Spring_dss_del": 558,
    "G2 Fall_theta_del": 0.84121465,
    "G2 Fall_pr_1_99_del": 75,
    "G2 Fall_ss_del": 110,
    "G2 Fall_dss_del": 584,
    "G2 Winter_theta_del": 1.064248941,
    "G2 Winter_pr_1_99_del": 75,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 606,
    "G2 Spring_theta_del": 1.39207612,
    "G2 Spring_pr_1_99_del": 75,
    "G2 Spring_ss_del": 110,
    "G2 Spring_dss_del": 639
  },
  {
    "ID": 450,
    "KG Fall_theta_del": -0.601621777,
    "KG Fall_pr_1_99_del": 75,
    "KG Fall_ss_del": 110,
    "KG Fall_dss_del": 440,
    "KG Winter_theta_del": -0.319373894,
    "KG Winter_pr_1_99_del": 75,
    "KG Winter_ss_del": 110,
    "KG Winter_dss_del": 468,
    "KG Spring_theta_del": -0.003411159,
    "KG Spring_pr_1_99_del": 75,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 500,
    "G1 Fall_theta_del": 0.200498879,
    "G1 Fall_pr_1_99_del": 75,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 520,
    "G1 Winter_theta_del": 0.410617939,
    "G1 Winter_pr_1_99_del": 75,
    "G1 Winter_ss_del": 110,
    "G1 Winter_dss_del": 541,
    "G1 Spring_theta_del": 0.585140195,
    "G1 Spring_pr_1_99_del": 75,
    "G1 Spring_ss_del": 110,
    "G1 Spring_dss_del": 559,
    "G2 Fall_theta_del": 0.85461877,
    "G2 Fall_pr_1_99_del": 75,
    "G2 Fall_ss_del": 110,
    "G2 Fall_dss_del": 585,
    "G2 Winter_theta_del": 1.073252369,
    "G2 Winter_pr_1_99_del": 75,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 607,
    "G2 Spring_theta_del": 1.403641511,
    "G2 Spring_pr_1_99_del": 75,
    "G2 Spring_ss_del": 110,
    "G2 Spring_dss_del": 640
  },
  {
    "ID": 466,
    "KG Fall_theta_del": -0.597897932,
    "KG Fall_pr_1_99_del": 75,
    "KG Fall_ss_del": 110,
    "KG Fall_dss_del": 440,
    "KG Winter_theta_del": -0.317142596,
    "KG Winter_pr_1_99_del": 75,
    "KG Winter_ss_del": 110,
    "KG Winter_dss_del": 468,
    "KG Spring_theta_del": 0.007921464,
    "KG Spring_pr_1_99_del": 75,
    "KG Spring_ss_del": 110,
    "KG Spring_dss_del": 501,
    "G1 Fall_theta_del": 0.20358244,
    "G1 Fall_pr_1_99_del": 75,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 520,
    "G1 Winter_theta_del": 0.418176964,
    "G1 Winter_pr_1_99_del": 75,
    "G1 Winter_ss_del": 111,
    "G1 Winter_dss_del": 542,
    "G1 Spring_theta_del": 0.586562024,
    "G1 Spring_pr_1_99_del": 75,
    "G1 Spring_ss_del": 110,
    "G1 Spring_dss_del": 559,
    "G2 Fall_theta_del": 0.861515438,
    "G2 Fall_pr_1_99_del": 75,
    "G2 Fall_ss_del": 110,
    "G2 Fall_dss_del": 586,
    "G2 Winter_theta_del": 1.085163224,
    "G2 Winter_pr_1_99_del": 75,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 609,
    "G2 Spring_theta_del": 1.405694114,
    "G2 Spring_pr_1_99_del": 75,
    "G2 Spring_ss_del": 110,
    "G2 Spring_dss_del": 641
  },
  {
    "ID": 280,
    "KG Fall_theta_del": -0.590814158,
    "KG Fall_pr_1_99_del": 76,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 441,
    "KG Winter_theta_del": -0.316388082,
    "KG Winter_pr_1_99_del": 76,
    "KG Winter_ss_del": 110,
    "KG Winter_dss_del": 468,
    "KG Spring_theta_del": 0.017011237,
    "KG Spring_pr_1_99_del": 76,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 502,
    "G1 Fall_theta_del": 0.209232722,
    "G1 Fall_pr_1_99_del": 76,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 521,
    "G1 Winter_theta_del": 0.43295738,
    "G1 Winter_pr_1_99_del": 76,
    "G1 Winter_ss_del": 111,
    "G1 Winter_dss_del": 543,
    "G1 Spring_theta_del": 0.604834809,
    "G1 Spring_pr_1_99_del": 76,
    "G1 Spring_ss_del": 110,
    "G1 Spring_dss_del": 560,
    "G2 Fall_theta_del": 0.861879887,
    "G2 Fall_pr_1_99_del": 76,
    "G2 Fall_ss_del": 110,
    "G2 Fall_dss_del": 586,
    "G2 Winter_theta_del": 1.090127801,
    "G2 Winter_pr_1_99_del": 76,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 609,
    "G2 Spring_theta_del": 1.408452318,
    "G2 Spring_pr_1_99_del": 76,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 641
  },
  {
    "ID": 360,
    "KG Fall_theta_del": -0.58858759,
    "KG Fall_pr_1_99_del": 76,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 441,
    "KG Winter_theta_del": -0.301020134,
    "KG Winter_pr_1_99_del": 76,
    "KG Winter_ss_del": 111,
    "KG Winter_dss_del": 470,
    "KG Spring_theta_del": 0.0211,
    "KG Spring_pr_1_99_del": 76,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 502,
    "G1 Fall_theta_del": 0.21930538,
    "G1 Fall_pr_1_99_del": 76,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 522,
    "G1 Winter_theta_del": 0.443149286,
    "G1 Winter_pr_1_99_del": 76,
    "G1 Winter_ss_del": 111,
    "G1 Winter_dss_del": 544,
    "G1 Spring_theta_del": 0.622757397,
    "G1 Spring_pr_1_99_del": 76,
    "G1 Spring_ss_del": 110,
    "G1 Spring_dss_del": 562,
    "G2 Fall_theta_del": 0.869535617,
    "G2 Fall_pr_1_99_del": 76,
    "G2 Fall_ss_del": 110,
    "G2 Fall_dss_del": 587,
    "G2 Winter_theta_del": 1.091053267,
    "G2 Winter_pr_1_99_del": 76,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 609,
    "G2 Spring_theta_del": 1.425312276,
    "G2 Spring_pr_1_99_del": 76,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 643
  },
  {
    "ID": 430,
    "KG Fall_theta_del": -0.584967396,
    "KG Fall_pr_1_99_del": 76,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 442,
    "KG Winter_theta_del": -0.298149498,
    "KG Winter_pr_1_99_del": 76,
    "KG Winter_ss_del": 111,
    "KG Winter_dss_del": 470,
    "KG Spring_theta_del": 0.02265548,
    "KG Spring_pr_1_99_del": 76,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 502,
    "G1 Fall_theta_del": 0.222674307,
    "G1 Fall_pr_1_99_del": 76,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 522,
    "G1 Winter_theta_del": 0.444941031,
    "G1 Winter_pr_1_99_del": 76,
    "G1 Winter_ss_del": 111,
    "G1 Winter_dss_del": 544,
    "G1 Spring_theta_del": 0.627545092,
    "G1 Spring_pr_1_99_del": 76,
    "G1 Spring_ss_del": 110,
    "G1 Spring_dss_del": 563,
    "G2 Fall_theta_del": 0.874287485,
    "G2 Fall_pr_1_99_del": 76,
    "G2 Fall_ss_del": 110,
    "G2 Fall_dss_del": 587,
    "G2 Winter_theta_del": 1.092732221,
    "G2 Winter_pr_1_99_del": 76,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 609,
    "G2 Spring_theta_del": 1.427637803,
    "G2 Spring_pr_1_99_del": 76,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 643
  },
  {
    "ID": 448,
    "KG Fall_theta_del": -0.583655613,
    "KG Fall_pr_1_99_del": 76,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 442,
    "KG Winter_theta_del": -0.281176414,
    "KG Winter_pr_1_99_del": 76,
    "KG Winter_ss_del": 111,
    "KG Winter_dss_del": 472,
    "KG Spring_theta_del": 0.023478861,
    "KG Spring_pr_1_99_del": 76,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 502,
    "G1 Fall_theta_del": 0.230661714,
    "G1 Fall_pr_1_99_del": 76,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 523,
    "G1 Winter_theta_del": 0.458801937,
    "G1 Winter_pr_1_99_del": 76,
    "G1 Winter_ss_del": 111,
    "G1 Winter_dss_del": 546,
    "G1 Spring_theta_del": 0.635400587,
    "G1 Spring_pr_1_99_del": 76,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 564,
    "G2 Fall_theta_del": 0.875386708,
    "G2 Fall_pr_1_99_del": 76,
    "G2 Fall_ss_del": 110,
    "G2 Fall_dss_del": 588,
    "G2 Winter_theta_del": 1.093240737,
    "G2 Winter_pr_1_99_del": 76,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 609,
    "G2 Spring_theta_del": 1.431935993,
    "G2 Spring_pr_1_99_del": 76,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 643
  },
  {
    "ID": 497,
    "KG Fall_theta_del": -0.582499146,
    "KG Fall_pr_1_99_del": 76,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 442,
    "KG Winter_theta_del": -0.270860515,
    "KG Winter_pr_1_99_del": 76,
    "KG Winter_ss_del": 111,
    "KG Winter_dss_del": 473,
    "KG Spring_theta_del": 0.023726238,
    "KG Spring_pr_1_99_del": 76,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 502,
    "G1 Fall_theta_del": 0.234341362,
    "G1 Fall_pr_1_99_del": 76,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 523,
    "G1 Winter_theta_del": 0.460459576,
    "G1 Winter_pr_1_99_del": 76,
    "G1 Winter_ss_del": 111,
    "G1 Winter_dss_del": 546,
    "G1 Spring_theta_del": 0.638214492,
    "G1 Spring_pr_1_99_del": 76,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 564,
    "G2 Fall_theta_del": 0.880376419,
    "G2 Fall_pr_1_99_del": 76,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 588,
    "G2 Winter_theta_del": 1.096182869,
    "G2 Winter_pr_1_99_del": 76,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 610,
    "G2 Spring_theta_del": 1.433453578,
    "G2 Spring_pr_1_99_del": 76,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 643
  },
  {
    "ID": 18,
    "KG Fall_theta_del": -0.580989808,
    "KG Fall_pr_1_99_del": 77,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 442,
    "KG Winter_theta_del": -0.233154903,
    "KG Winter_pr_1_99_del": 77,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 477,
    "KG Spring_theta_del": 0.029008933,
    "KG Spring_pr_1_99_del": 77,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 503,
    "G1 Fall_theta_del": 0.236781856,
    "G1 Fall_pr_1_99_del": 77,
    "G1 Fall_ss_del": 111,
    "G1 Fall_dss_del": 524,
    "G1 Winter_theta_del": 0.467831047,
    "G1 Winter_pr_1_99_del": 77,
    "G1 Winter_ss_del": 112,
    "G1 Winter_dss_del": 547,
    "G1 Spring_theta_del": 0.641557724,
    "G1 Spring_pr_1_99_del": 77,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 564,
    "G2 Fall_theta_del": 0.880501287,
    "G2 Fall_pr_1_99_del": 77,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 588,
    "G2 Winter_theta_del": 1.096216706,
    "G2 Winter_pr_1_99_del": 77,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 610,
    "G2 Spring_theta_del": 1.435086583,
    "G2 Spring_pr_1_99_del": 77,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 644
  },
  {
    "ID": 141,
    "KG Fall_theta_del": -0.577363885,
    "KG Fall_pr_1_99_del": 77,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 442,
    "KG Winter_theta_del": -0.227460771,
    "KG Winter_pr_1_99_del": 77,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 477,
    "KG Spring_theta_del": 0.029167589,
    "KG Spring_pr_1_99_del": 77,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 503,
    "G1 Fall_theta_del": 0.246707539,
    "G1 Fall_pr_1_99_del": 77,
    "G1 Fall_ss_del": 112,
    "G1 Fall_dss_del": 525,
    "G1 Winter_theta_del": 0.481391292,
    "G1 Winter_pr_1_99_del": 77,
    "G1 Winter_ss_del": 112,
    "G1 Winter_dss_del": 548,
    "G1 Spring_theta_del": 0.643709673,
    "G1 Spring_pr_1_99_del": 77,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 564,
    "G2 Fall_theta_del": 0.884948874,
    "G2 Fall_pr_1_99_del": 77,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 588,
    "G2 Winter_theta_del": 1.103216243,
    "G2 Winter_pr_1_99_del": 77,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 610,
    "G2 Spring_theta_del": 1.435646047,
    "G2 Spring_pr_1_99_del": 77,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 644
  },
  {
    "ID": 193,
    "KG Fall_theta_del": -0.561322323,
    "KG Fall_pr_1_99_del": 77,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 444,
    "KG Winter_theta_del": -0.226380611,
    "KG Winter_pr_1_99_del": 77,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 477,
    "KG Spring_theta_del": 0.03485899,
    "KG Spring_pr_1_99_del": 77,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 503,
    "G1 Fall_theta_del": 0.248572684,
    "G1 Fall_pr_1_99_del": 77,
    "G1 Fall_ss_del": 112,
    "G1 Fall_dss_del": 525,
    "G1 Winter_theta_del": 0.48289959,
    "G1 Winter_pr_1_99_del": 77,
    "G1 Winter_ss_del": 112,
    "G1 Winter_dss_del": 548,
    "G1 Spring_theta_del": 0.659852264,
    "G1 Spring_pr_1_99_del": 77,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 566,
    "G2 Fall_theta_del": 0.890785531,
    "G2 Fall_pr_1_99_del": 77,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 589,
    "G2 Winter_theta_del": 1.110985817,
    "G2 Winter_pr_1_99_del": 77,
    "G2 Winter_ss_del": 110,
    "G2 Winter_dss_del": 611,
    "G2 Spring_theta_del": 1.437122913,
    "G2 Spring_pr_1_99_del": 77,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 644
  },
  {
    "ID": 358,
    "KG Fall_theta_del": -0.559371306,
    "KG Fall_pr_1_99_del": 77,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 444,
    "KG Winter_theta_del": -0.222577953,
    "KG Winter_pr_1_99_del": 77,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 478,
    "KG Spring_theta_del": 0.045570223,
    "KG Spring_pr_1_99_del": 77,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 505,
    "G1 Fall_theta_del": 0.251397911,
    "G1 Fall_pr_1_99_del": 77,
    "G1 Fall_ss_del": 112,
    "G1 Fall_dss_del": 525,
    "G1 Winter_theta_del": 0.483539137,
    "G1 Winter_pr_1_99_del": 77,
    "G1 Winter_ss_del": 112,
    "G1 Winter_dss_del": 548,
    "G1 Spring_theta_del": 0.66074413,
    "G1 Spring_pr_1_99_del": 77,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 566,
    "G2 Fall_theta_del": 0.897695202,
    "G2 Fall_pr_1_99_del": 77,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 590,
    "G2 Winter_theta_del": 1.116423311,
    "G2 Winter_pr_1_99_del": 77,
    "G2 Winter_ss_del": 111,
    "G2 Winter_dss_del": 612,
    "G2 Spring_theta_del": 1.438225386,
    "G2 Spring_pr_1_99_del": 77,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 644
  },
  {
    "ID": 372,
    "KG Fall_theta_del": -0.551246611,
    "KG Fall_pr_1_99_del": 77,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 445,
    "KG Winter_theta_del": -0.218286238,
    "KG Winter_pr_1_99_del": 77,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 478,
    "KG Spring_theta_del": 0.04742703,
    "KG Spring_pr_1_99_del": 77,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 505,
    "G1 Fall_theta_del": 0.265590088,
    "G1 Fall_pr_1_99_del": 77,
    "G1 Fall_ss_del": 112,
    "G1 Fall_dss_del": 527,
    "G1 Winter_theta_del": 0.492219375,
    "G1 Winter_pr_1_99_del": 77,
    "G1 Winter_ss_del": 112,
    "G1 Winter_dss_del": 549,
    "G1 Spring_theta_del": 0.666173841,
    "G1 Spring_pr_1_99_del": 77,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 567,
    "G2 Fall_theta_del": 0.89866288,
    "G2 Fall_pr_1_99_del": 77,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 590,
    "G2 Winter_theta_del": 1.125138378,
    "G2 Winter_pr_1_99_del": 77,
    "G2 Winter_ss_del": 111,
    "G2 Winter_dss_del": 613,
    "G2 Spring_theta_del": 1.44768967,
    "G2 Spring_pr_1_99_del": 77,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 645
  },
  {
    "ID": 85,
    "KG Fall_theta_del": -0.546918977,
    "KG Fall_pr_1_99_del": 78,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 445,
    "KG Winter_theta_del": -0.214519817,
    "KG Winter_pr_1_99_del": 78,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 479,
    "KG Spring_theta_del": 0.04762597,
    "KG Spring_pr_1_99_del": 78,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 505,
    "G1 Fall_theta_del": 0.266749985,
    "G1 Fall_pr_1_99_del": 78,
    "G1 Fall_ss_del": 112,
    "G1 Fall_dss_del": 527,
    "G1 Winter_theta_del": 0.49356694,
    "G1 Winter_pr_1_99_del": 78,
    "G1 Winter_ss_del": 112,
    "G1 Winter_dss_del": 549,
    "G1 Spring_theta_del": 0.670074502,
    "G1 Spring_pr_1_99_del": 78,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 567,
    "G2 Fall_theta_del": 0.903172998,
    "G2 Fall_pr_1_99_del": 78,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 590,
    "G2 Winter_theta_del": 1.129517718,
    "G2 Winter_pr_1_99_del": 78,
    "G2 Winter_ss_del": 111,
    "G2 Winter_dss_del": 613,
    "G2 Spring_theta_del": 1.454115467,
    "G2 Spring_pr_1_99_del": 78,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 645
  },
  {
    "ID": 119,
    "KG Fall_theta_del": -0.54587304,
    "KG Fall_pr_1_99_del": 78,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 445,
    "KG Winter_theta_del": -0.212314863,
    "KG Winter_pr_1_99_del": 78,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 479,
    "KG Spring_theta_del": 0.048519804,
    "KG Spring_pr_1_99_del": 78,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 505,
    "G1 Fall_theta_del": 0.268177634,
    "G1 Fall_pr_1_99_del": 78,
    "G1 Fall_ss_del": 112,
    "G1 Fall_dss_del": 527,
    "G1 Winter_theta_del": 0.504867885,
    "G1 Winter_pr_1_99_del": 78,
    "G1 Winter_ss_del": 112,
    "G1 Winter_dss_del": 550,
    "G1 Spring_theta_del": 0.670112738,
    "G1 Spring_pr_1_99_del": 78,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 567,
    "G2 Fall_theta_del": 0.903905,
    "G2 Fall_pr_1_99_del": 78,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 590,
    "G2 Winter_theta_del": 1.130906703,
    "G2 Winter_pr_1_99_del": 78,
    "G2 Winter_ss_del": 111,
    "G2 Winter_dss_del": 613,
    "G2 Spring_theta_del": 1.458490156,
    "G2 Spring_pr_1_99_del": 78,
    "G2 Spring_ss_del": 111,
    "G2 Spring_dss_del": 646
  },
  {
    "ID": 142,
    "KG Fall_theta_del": -0.539441203,
    "KG Fall_pr_1_99_del": 78,
    "KG Fall_ss_del": 111,
    "KG Fall_dss_del": 446,
    "KG Winter_theta_del": -0.205190599,
    "KG Winter_pr_1_99_del": 78,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 479,
    "KG Spring_theta_del": 0.062778905,
    "KG Spring_pr_1_99_del": 78,
    "KG Spring_ss_del": 111,
    "KG Spring_dss_del": 506,
    "G1 Fall_theta_del": 0.269402416,
    "G1 Fall_pr_1_99_del": 78,
    "G1 Fall_ss_del": 112,
    "G1 Fall_dss_del": 527,
    "G1 Winter_theta_del": 0.510886035,
    "G1 Winter_pr_1_99_del": 78,
    "G1 Winter_ss_del": 112,
    "G1 Winter_dss_del": 551,
    "G1 Spring_theta_del": 0.673826696,
    "G1 Spring_pr_1_99_del": 78,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 567,
    "G2 Fall_theta_del": 0.907573863,
    "G2 Fall_pr_1_99_del": 78,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 591,
    "G2 Winter_theta_del": 1.138377506,
    "G2 Winter_pr_1_99_del": 78,
    "G2 Winter_ss_del": 111,
    "G2 Winter_dss_del": 614,
    "G2 Spring_theta_del": 1.463394597,
    "G2 Spring_pr_1_99_del": 78,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 646
  },
  {
    "ID": 334,
    "KG Fall_theta_del": -0.537420858,
    "KG Fall_pr_1_99_del": 78,
    "KG Fall_ss_del": 112,
    "KG Fall_dss_del": 446,
    "KG Winter_theta_del": -0.204444749,
    "KG Winter_pr_1_99_del": 78,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 480,
    "KG Spring_theta_del": 0.065968394,
    "KG Spring_pr_1_99_del": 78,
    "KG Spring_ss_del": 112,
    "KG Spring_dss_del": 507,
    "G1 Fall_theta_del": 0.276867796,
    "G1 Fall_pr_1_99_del": 78,
    "G1 Fall_ss_del": 112,
    "G1 Fall_dss_del": 528,
    "G1 Winter_theta_del": 0.513978977,
    "G1 Winter_pr_1_99_del": 78,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 551,
    "G1 Spring_theta_del": 0.677503072,
    "G1 Spring_pr_1_99_del": 78,
    "G1 Spring_ss_del": 111,
    "G1 Spring_dss_del": 568,
    "G2 Fall_theta_del": 0.909539591,
    "G2 Fall_pr_1_99_del": 78,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 591,
    "G2 Winter_theta_del": 1.141076419,
    "G2 Winter_pr_1_99_del": 78,
    "G2 Winter_ss_del": 111,
    "G2 Winter_dss_del": 614,
    "G2 Spring_theta_del": 1.476795586,
    "G2 Spring_pr_1_99_del": 78,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 648
  },
  {
    "ID": 436,
    "KG Fall_theta_del": -0.532971701,
    "KG Fall_pr_1_99_del": 78,
    "KG Fall_ss_del": 112,
    "KG Fall_dss_del": 447,
    "KG Winter_theta_del": -0.203868157,
    "KG Winter_pr_1_99_del": 78,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 480,
    "KG Spring_theta_del": 0.066015467,
    "KG Spring_pr_1_99_del": 78,
    "KG Spring_ss_del": 112,
    "KG Spring_dss_del": 507,
    "G1 Fall_theta_del": 0.290150919,
    "G1 Fall_pr_1_99_del": 78,
    "G1 Fall_ss_del": 112,
    "G1 Fall_dss_del": 529,
    "G1 Winter_theta_del": 0.518428528,
    "G1 Winter_pr_1_99_del": 78,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 552,
    "G1 Spring_theta_del": 0.680257592,
    "G1 Spring_pr_1_99_del": 78,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 568,
    "G2 Fall_theta_del": 0.910158277,
    "G2 Fall_pr_1_99_del": 78,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 591,
    "G2 Winter_theta_del": 1.143481195,
    "G2 Winter_pr_1_99_del": 78,
    "G2 Winter_ss_del": 111,
    "G2 Winter_dss_del": 614,
    "G2 Spring_theta_del": 1.480990143,
    "G2 Spring_pr_1_99_del": 78,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 648
  },
  {
    "ID": 113,
    "KG Fall_theta_del": -0.53243318,
    "KG Fall_pr_1_99_del": 79,
    "KG Fall_ss_del": 112,
    "KG Fall_dss_del": 447,
    "KG Winter_theta_del": -0.203711186,
    "KG Winter_pr_1_99_del": 79,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 480,
    "KG Spring_theta_del": 0.066539949,
    "KG Spring_pr_1_99_del": 79,
    "KG Spring_ss_del": 112,
    "KG Spring_dss_del": 507,
    "G1 Fall_theta_del": 0.290371781,
    "G1 Fall_pr_1_99_del": 79,
    "G1 Fall_ss_del": 112,
    "G1 Fall_dss_del": 529,
    "G1 Winter_theta_del": 0.521325555,
    "G1 Winter_pr_1_99_del": 79,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 552,
    "G1 Spring_theta_del": 0.683023837,
    "G1 Spring_pr_1_99_del": 79,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 568,
    "G2 Fall_theta_del": 0.913147165,
    "G2 Fall_pr_1_99_del": 79,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 591,
    "G2 Winter_theta_del": 1.14802765,
    "G2 Winter_pr_1_99_del": 79,
    "G2 Winter_ss_del": 111,
    "G2 Winter_dss_del": 615,
    "G2 Spring_theta_del": 1.487921221,
    "G2 Spring_pr_1_99_del": 79,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 649
  },
  {
    "ID": 224,
    "KG Fall_theta_del": -0.531636997,
    "KG Fall_pr_1_99_del": 79,
    "KG Fall_ss_del": 112,
    "KG Fall_dss_del": 447,
    "KG Winter_theta_del": -0.198363005,
    "KG Winter_pr_1_99_del": 79,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 480,
    "KG Spring_theta_del": 0.069659594,
    "KG Spring_pr_1_99_del": 79,
    "KG Spring_ss_del": 112,
    "KG Spring_dss_del": 507,
    "G1 Fall_theta_del": 0.30108472,
    "G1 Fall_pr_1_99_del": 79,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 530,
    "G1 Winter_theta_del": 0.521904688,
    "G1 Winter_pr_1_99_del": 79,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 552,
    "G1 Spring_theta_del": 0.685432215,
    "G1 Spring_pr_1_99_del": 79,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 569,
    "G2 Fall_theta_del": 0.918716581,
    "G2 Fall_pr_1_99_del": 79,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 592,
    "G2 Winter_theta_del": 1.167165613,
    "G2 Winter_pr_1_99_del": 79,
    "G2 Winter_ss_del": 111,
    "G2 Winter_dss_del": 617,
    "G2 Spring_theta_del": 1.48877495,
    "G2 Spring_pr_1_99_del": 79,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 649
  },
  {
    "ID": 247,
    "KG Fall_theta_del": -0.521730266,
    "KG Fall_pr_1_99_del": 79,
    "KG Fall_ss_del": 112,
    "KG Fall_dss_del": 448,
    "KG Winter_theta_del": -0.192942883,
    "KG Winter_pr_1_99_del": 79,
    "KG Winter_ss_del": 112,
    "KG Winter_dss_del": 481,
    "KG Spring_theta_del": 0.079961407,
    "KG Spring_pr_1_99_del": 79,
    "KG Spring_ss_del": 112,
    "KG Spring_dss_del": 508,
    "G1 Fall_theta_del": 0.302139626,
    "G1 Fall_pr_1_99_del": 79,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 530,
    "G1 Winter_theta_del": 0.529278352,
    "G1 Winter_pr_1_99_del": 79,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 553,
    "G1 Spring_theta_del": 0.685904601,
    "G1 Spring_pr_1_99_del": 79,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 569,
    "G2 Fall_theta_del": 0.928883657,
    "G2 Fall_pr_1_99_del": 79,
    "G2 Fall_ss_del": 111,
    "G2 Fall_dss_del": 593,
    "G2 Winter_theta_del": 1.175490135,
    "G2 Winter_pr_1_99_del": 79,
    "G2 Winter_ss_del": 112,
    "G2 Winter_dss_del": 618,
    "G2 Spring_theta_del": 1.492057494,
    "G2 Spring_pr_1_99_del": 79,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 649
  },
  {
    "ID": 465,
    "KG Fall_theta_del": -0.498162794,
    "KG Fall_pr_1_99_del": 79,
    "KG Fall_ss_del": 112,
    "KG Fall_dss_del": 450,
    "KG Winter_theta_del": -0.188752029,
    "KG Winter_pr_1_99_del": 79,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 481,
    "KG Spring_theta_del": 0.121440456,
    "KG Spring_pr_1_99_del": 79,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 512,
    "G1 Fall_theta_del": 0.303901322,
    "G1 Fall_pr_1_99_del": 79,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 530,
    "G1 Winter_theta_del": 0.53074797,
    "G1 Winter_pr_1_99_del": 79,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 553,
    "G1 Spring_theta_del": 0.6873175,
    "G1 Spring_pr_1_99_del": 79,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 569,
    "G2 Fall_theta_del": 0.930546965,
    "G2 Fall_pr_1_99_del": 79,
    "G2 Fall_ss_del": 112,
    "G2 Fall_dss_del": 593,
    "G2 Winter_theta_del": 1.177377524,
    "G2 Winter_pr_1_99_del": 79,
    "G2 Winter_ss_del": 112,
    "G2 Winter_dss_del": 618,
    "G2 Spring_theta_del": 1.49325483,
    "G2 Spring_pr_1_99_del": 79,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 649
  },
  {
    "ID": 485,
    "KG Fall_theta_del": -0.496385407,
    "KG Fall_pr_1_99_del": 79,
    "KG Fall_ss_del": 112,
    "KG Fall_dss_del": 450,
    "KG Winter_theta_del": -0.187751153,
    "KG Winter_pr_1_99_del": 79,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 481,
    "KG Spring_theta_del": 0.135197156,
    "KG Spring_pr_1_99_del": 79,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 514,
    "G1 Fall_theta_del": 0.312231187,
    "G1 Fall_pr_1_99_del": 79,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 531,
    "G1 Winter_theta_del": 0.536856091,
    "G1 Winter_pr_1_99_del": 79,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 554,
    "G1 Spring_theta_del": 0.693592573,
    "G1 Spring_pr_1_99_del": 79,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 569,
    "G2 Fall_theta_del": 0.933095687,
    "G2 Fall_pr_1_99_del": 79,
    "G2 Fall_ss_del": 112,
    "G2 Fall_dss_del": 593,
    "G2 Winter_theta_del": 1.180837439,
    "G2 Winter_pr_1_99_del": 79,
    "G2 Winter_ss_del": 112,
    "G2 Winter_dss_del": 618,
    "G2 Spring_theta_del": 1.493730102,
    "G2 Spring_pr_1_99_del": 79,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 649
  },
  {
    "ID": 121,
    "KG Fall_theta_del": -0.484448505,
    "KG Fall_pr_1_99_del": 80,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 452,
    "KG Winter_theta_del": -0.183318868,
    "KG Winter_pr_1_99_del": 80,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 482,
    "KG Spring_theta_del": 0.137136499,
    "KG Spring_pr_1_99_del": 80,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 514,
    "G1 Fall_theta_del": 0.314872559,
    "G1 Fall_pr_1_99_del": 80,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 531,
    "G1 Winter_theta_del": 0.537996247,
    "G1 Winter_pr_1_99_del": 80,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 554,
    "G1 Spring_theta_del": 0.703819531,
    "G1 Spring_pr_1_99_del": 80,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 570,
    "G2 Fall_theta_del": 0.933337856,
    "G2 Fall_pr_1_99_del": 80,
    "G2 Fall_ss_del": 112,
    "G2 Fall_dss_del": 593,
    "G2 Winter_theta_del": 1.192703654,
    "G2 Winter_pr_1_99_del": 80,
    "G2 Winter_ss_del": 112,
    "G2 Winter_dss_del": 619,
    "G2 Spring_theta_del": 1.495223178,
    "G2 Spring_pr_1_99_del": 80,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 650
  },
  {
    "ID": 129,
    "KG Fall_theta_del": -0.479711632,
    "KG Fall_pr_1_99_del": 80,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 452,
    "KG Winter_theta_del": -0.181883613,
    "KG Winter_pr_1_99_del": 80,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 482,
    "KG Spring_theta_del": 0.140419274,
    "KG Spring_pr_1_99_del": 80,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 514,
    "G1 Fall_theta_del": 0.3163937,
    "G1 Fall_pr_1_99_del": 80,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 532,
    "G1 Winter_theta_del": 0.55232025,
    "G1 Winter_pr_1_99_del": 80,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 555,
    "G1 Spring_theta_del": 0.706768847,
    "G1 Spring_pr_1_99_del": 80,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 571,
    "G2 Fall_theta_del": 0.941700722,
    "G2 Fall_pr_1_99_del": 80,
    "G2 Fall_ss_del": 112,
    "G2 Fall_dss_del": 594,
    "G2 Winter_theta_del": 1.196985212,
    "G2 Winter_pr_1_99_del": 80,
    "G2 Winter_ss_del": 112,
    "G2 Winter_dss_del": 620,
    "G2 Spring_theta_del": 1.510369619,
    "G2 Spring_pr_1_99_del": 80,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 651
  },
  {
    "ID": 194,
    "KG Fall_theta_del": -0.478406071,
    "KG Fall_pr_1_99_del": 80,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 452,
    "KG Winter_theta_del": -0.181207067,
    "KG Winter_pr_1_99_del": 80,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 482,
    "KG Spring_theta_del": 0.14308743,
    "KG Spring_pr_1_99_del": 80,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 514,
    "G1 Fall_theta_del": 0.322485335,
    "G1 Fall_pr_1_99_del": 80,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 532,
    "G1 Winter_theta_del": 0.559820956,
    "G1 Winter_pr_1_99_del": 80,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 556,
    "G1 Spring_theta_del": 0.712115742,
    "G1 Spring_pr_1_99_del": 80,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 571,
    "G2 Fall_theta_del": 0.96659929,
    "G2 Fall_pr_1_99_del": 80,
    "G2 Fall_ss_del": 112,
    "G2 Fall_dss_del": 597,
    "G2 Winter_theta_del": 1.200860117,
    "G2 Winter_pr_1_99_del": 80,
    "G2 Winter_ss_del": 112,
    "G2 Winter_dss_del": 620,
    "G2 Spring_theta_del": 1.512266274,
    "G2 Spring_pr_1_99_del": 80,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 651
  },
  {
    "ID": 201,
    "KG Fall_theta_del": -0.470880484,
    "KG Fall_pr_1_99_del": 80,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 453,
    "KG Winter_theta_del": -0.174954786,
    "KG Winter_pr_1_99_del": 80,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 483,
    "KG Spring_theta_del": 0.145508891,
    "KG Spring_pr_1_99_del": 80,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 515,
    "G1 Fall_theta_del": 0.329479672,
    "G1 Fall_pr_1_99_del": 80,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 533,
    "G1 Winter_theta_del": 0.562879549,
    "G1 Winter_pr_1_99_del": 80,
    "G1 Winter_ss_del": 113,
    "G1 Winter_dss_del": 556,
    "G1 Spring_theta_del": 0.718314923,
    "G1 Spring_pr_1_99_del": 80,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 572,
    "G2 Fall_theta_del": 0.98071988,
    "G2 Fall_pr_1_99_del": 80,
    "G2 Fall_ss_del": 112,
    "G2 Fall_dss_del": 598,
    "G2 Winter_theta_del": 1.202436055,
    "G2 Winter_pr_1_99_del": 80,
    "G2 Winter_ss_del": 112,
    "G2 Winter_dss_del": 620,
    "G2 Spring_theta_del": 1.512921476,
    "G2 Spring_pr_1_99_del": 80,
    "G2 Spring_ss_del": 112,
    "G2 Spring_dss_del": 651
  },
  {
    "ID": 245,
    "KG Fall_theta_del": -0.470316333,
    "KG Fall_pr_1_99_del": 80,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 453,
    "KG Winter_theta_del": -0.173976348,
    "KG Winter_pr_1_99_del": 80,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 483,
    "KG Spring_theta_del": 0.156412863,
    "KG Spring_pr_1_99_del": 80,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 516,
    "G1 Fall_theta_del": 0.331854381,
    "G1 Fall_pr_1_99_del": 80,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 533,
    "G1 Winter_theta_del": 0.570725354,
    "G1 Winter_pr_1_99_del": 80,
    "G1 Winter_ss_del": 114,
    "G1 Winter_dss_del": 557,
    "G1 Spring_theta_del": 0.718961823,
    "G1 Spring_pr_1_99_del": 80,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 572,
    "G2 Fall_theta_del": 0.987199755,
    "G2 Fall_pr_1_99_del": 80,
    "G2 Fall_ss_del": 113,
    "G2 Fall_dss_del": 599,
    "G2 Winter_theta_del": 1.209859239,
    "G2 Winter_pr_1_99_del": 80,
    "G2 Winter_ss_del": 112,
    "G2 Winter_dss_del": 621,
    "G2 Spring_theta_del": 1.526251838,
    "G2 Spring_pr_1_99_del": 80,
    "G2 Spring_ss_del": 113,
    "G2 Spring_dss_del": 653
  },
  {
    "ID": 17,
    "KG Fall_theta_del": -0.468017697,
    "KG Fall_pr_1_99_del": 81,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 453,
    "KG Winter_theta_del": -0.156123092,
    "KG Winter_pr_1_99_del": 81,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 484,
    "KG Spring_theta_del": 0.156461264,
    "KG Spring_pr_1_99_del": 81,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 516,
    "G1 Fall_theta_del": 0.338724945,
    "G1 Fall_pr_1_99_del": 81,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 534,
    "G1 Winter_theta_del": 0.575897527,
    "G1 Winter_pr_1_99_del": 81,
    "G1 Winter_ss_del": 114,
    "G1 Winter_dss_del": 558,
    "G1 Spring_theta_del": 0.720253842,
    "G1 Spring_pr_1_99_del": 81,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 572,
    "G2 Fall_theta_del": 0.988004205,
    "G2 Fall_pr_1_99_del": 81,
    "G2 Fall_ss_del": 113,
    "G2 Fall_dss_del": 599,
    "G2 Winter_theta_del": 1.216671059,
    "G2 Winter_pr_1_99_del": 81,
    "G2 Winter_ss_del": 112,
    "G2 Winter_dss_del": 622,
    "G2 Spring_theta_del": 1.530935651,
    "G2 Spring_pr_1_99_del": 81,
    "G2 Spring_ss_del": 113,
    "G2 Spring_dss_del": 653
  },
  {
    "ID": 31,
    "KG Fall_theta_del": -0.464837865,
    "KG Fall_pr_1_99_del": 81,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 454,
    "KG Winter_theta_del": -0.153389373,
    "KG Winter_pr_1_99_del": 81,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 485,
    "KG Spring_theta_del": 0.161558961,
    "KG Spring_pr_1_99_del": 81,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 516,
    "G1 Fall_theta_del": 0.341879337,
    "G1 Fall_pr_1_99_del": 81,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 534,
    "G1 Winter_theta_del": 0.579705636,
    "G1 Winter_pr_1_99_del": 81,
    "G1 Winter_ss_del": 114,
    "G1 Winter_dss_del": 558,
    "G1 Spring_theta_del": 0.728303906,
    "G1 Spring_pr_1_99_del": 81,
    "G1 Spring_ss_del": 112,
    "G1 Spring_dss_del": 573,
    "G2 Fall_theta_del": 0.988197624,
    "G2 Fall_pr_1_99_del": 81,
    "G2 Fall_ss_del": 113,
    "G2 Fall_dss_del": 599,
    "G2 Winter_theta_del": 1.235359431,
    "G2 Winter_pr_1_99_del": 81,
    "G2 Winter_ss_del": 113,
    "G2 Winter_dss_del": 624,
    "G2 Spring_theta_del": 1.535506068,
    "G2 Spring_pr_1_99_del": 81,
    "G2 Spring_ss_del": 113,
    "G2 Spring_dss_del": 654
  },
  {
    "ID": 120,
    "KG Fall_theta_del": -0.454393464,
    "KG Fall_pr_1_99_del": 81,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 455,
    "KG Winter_theta_del": -0.147937593,
    "KG Winter_pr_1_99_del": 81,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 485,
    "KG Spring_theta_del": 0.161848221,
    "KG Spring_pr_1_99_del": 81,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 516,
    "G1 Fall_theta_del": 0.343353827,
    "G1 Fall_pr_1_99_del": 81,
    "G1 Fall_ss_del": 113,
    "G1 Fall_dss_del": 534,
    "G1 Winter_theta_del": 0.589210323,
    "G1 Winter_pr_1_99_del": 81,
    "G1 Winter_ss_del": 114,
    "G1 Winter_dss_del": 559,
    "G1 Spring_theta_del": 0.734571026,
    "G1 Spring_pr_1_99_del": 81,
    "G1 Spring_ss_del": 113,
    "G1 Spring_dss_del": 573,
    "G2 Fall_theta_del": 1.004601864,
    "G2 Fall_pr_1_99_del": 81,
    "G2 Fall_ss_del": 113,
    "G2 Fall_dss_del": 600,
    "G2 Winter_theta_del": 1.237274955,
    "G2 Winter_pr_1_99_del": 81,
    "G2 Winter_ss_del": 113,
    "G2 Winter_dss_del": 624,
    "G2 Spring_theta_del": 1.540488226,
    "G2 Spring_pr_1_99_del": 81,
    "G2 Spring_ss_del": 113,
    "G2 Spring_dss_del": 654
  },
  {
    "ID": 246,
    "KG Fall_theta_del": -0.452810115,
    "KG Fall_pr_1_99_del": 81,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 455,
    "KG Winter_theta_del": -0.146590325,
    "KG Winter_pr_1_99_del": 81,
    "KG Winter_ss_del": 113,
    "KG Winter_dss_del": 485,
    "KG Spring_theta_del": 0.169673587,
    "KG Spring_pr_1_99_del": 81,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 517,
    "G1 Fall_theta_del": 0.347694704,
    "G1 Fall_pr_1_99_del": 81,
    "G1 Fall_ss_del": 114,
    "G1 Fall_dss_del": 535,
    "G1 Winter_theta_del": 0.590857407,
    "G1 Winter_pr_1_99_del": 81,
    "G1 Winter_ss_del": 114,
    "G1 Winter_dss_del": 559,
    "G1 Spring_theta_del": 0.736719483,
    "G1 Spring_pr_1_99_del": 81,
    "G1 Spring_ss_del": 113,
    "G1 Spring_dss_del": 574,
    "G2 Fall_theta_del": 1.004963565,
    "G2 Fall_pr_1_99_del": 81,
    "G2 Fall_ss_del": 113,
    "G2 Fall_dss_del": 600,
    "G2 Winter_theta_del": 1.260918662,
    "G2 Winter_pr_1_99_del": 81,
    "G2 Winter_ss_del": 113,
    "G2 Winter_dss_del": 626,
    "G2 Spring_theta_del": 1.540555575,
    "G2 Spring_pr_1_99_del": 81,
    "G2 Spring_ss_del": 113,
    "G2 Spring_dss_del": 654
  },
  {
    "ID": 343,
    "KG Fall_theta_del": -0.434188192,
    "KG Fall_pr_1_99_del": 81,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 457,
    "KG Winter_theta_del": -0.123190356,
    "KG Winter_pr_1_99_del": 81,
    "KG Winter_ss_del": 114,
    "KG Winter_dss_del": 488,
    "KG Spring_theta_del": 0.170917028,
    "KG Spring_pr_1_99_del": 81,
    "KG Spring_ss_del": 113,
    "KG Spring_dss_del": 517,
    "G1 Fall_theta_del": 0.349841311,
    "G1 Fall_pr_1_99_del": 81,
    "G1 Fall_ss_del": 114,
    "G1 Fall_dss_del": 535,
    "G1 Winter_theta_del": 0.596352286,
    "G1 Winter_pr_1_99_del": 81,
    "G1 Winter_ss_del": 114,
    "G1 Winter_dss_del": 560,
    "G1 Spring_theta_del": 0.741887627,
    "G1 Spring_pr_1_99_del": 81,
    "G1 Spring_ss_del": 113,
    "G1 Spring_dss_del": 574,
    "G2 Fall_theta_del": 1.0194978,
    "G2 Fall_pr_1_99_del": 81,
    "G2 Fall_ss_del": 113,
    "G2 Fall_dss_del": 602,
    "G2 Winter_theta_del": 1.261499651,
    "G2 Winter_pr_1_99_del": 81,
    "G2 Winter_ss_del": 113,
    "G2 Winter_dss_del": 626,
    "G2 Spring_theta_del": 1.542513623,
    "G2 Spring_pr_1_99_del": 81,
    "G2 Spring_ss_del": 113,
    "G2 Spring_dss_del": 654
  },
  {
    "ID": 53,
    "KG Fall_theta_del": -0.434015006,
    "KG Fall_pr_1_99_del": 82,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 457,
    "KG Winter_theta_del": -0.120593617,
    "KG Winter_pr_1_99_del": 82,
    "KG Winter_ss_del": 114,
    "KG Winter_dss_del": 488,
    "KG Spring_theta_del": 0.183984816,
    "KG Spring_pr_1_99_del": 82,
    "KG Spring_ss_del": 114,
    "KG Spring_dss_del": 518,
    "G1 Fall_theta_del": 0.354727092,
    "G1 Fall_pr_1_99_del": 82,
    "G1 Fall_ss_del": 114,
    "G1 Fall_dss_del": 535,
    "G1 Winter_theta_del": 0.598798999,
    "G1 Winter_pr_1_99_del": 82,
    "G1 Winter_ss_del": 114,
    "G1 Winter_dss_del": 560,
    "G1 Spring_theta_del": 0.749717307,
    "G1 Spring_pr_1_99_del": 82,
    "G1 Spring_ss_del": 113,
    "G1 Spring_dss_del": 575,
    "G2 Fall_theta_del": 1.024636058,
    "G2 Fall_pr_1_99_del": 82,
    "G2 Fall_ss_del": 113,
    "G2 Fall_dss_del": 602,
    "G2 Winter_theta_del": 1.270846203,
    "G2 Winter_pr_1_99_del": 82,
    "G2 Winter_ss_del": 113,
    "G2 Winter_dss_del": 627,
    "G2 Spring_theta_del": 1.557305154,
    "G2 Spring_pr_1_99_del": 82,
    "G2 Spring_ss_del": 113,
    "G2 Spring_dss_del": 656
  },
  {
    "ID": 88,
    "KG Fall_theta_del": -0.43184695,
    "KG Fall_pr_1_99_del": 82,
    "KG Fall_ss_del": 113,
    "KG Fall_dss_del": 457,
    "KG Winter_theta_del": -0.119456658,
    "KG Winter_pr_1_99_del": 82,
    "KG Winter_ss_del": 114,
    "KG Winter_dss_del": 488,
    "KG Spring_theta_del": 0.185435466,
    "KG Spring_pr_1_99_del": 82,
    "KG Spring_ss_del": 114,
    "KG Spring_dss_del": 519,
    "G1 Fall_theta_del": 0.365477424,
    "G1 Fall_pr_1_99_del": 82,
    "G1 Fall_ss_del": 114,
    "G1 Fall_dss_del": 537,
    "G1 Winter_theta_del": 0.60124376,
    "G1 Winter_pr_1_99_del": 82,
    "G1 Winter_ss_del": 114,
    "G1 Winter_dss_del": 560,
    "G1 Spring_theta_del": 0.761916444,
    "G1 Spring_pr_1_99_del": 82,
    "G1 Spring_ss_del": 113,
    "G1 Spring_dss_del": 576,
    "G2 Fall_theta_del": 1.03626064,
    "G2 Fall_pr_1_99_del": 82,
    "G2 Fall_ss_del": 114,
    "G2 Fall_dss_del": 604,
    "G2 Winter_theta_del": 1.277582472,
    "G2 Winter_pr_1_99_del": 82,
    "G2 Winter_ss_del": 114,
    "G2 Winter_dss_del": 628,
    "G2 Spring_theta_del": 1.560735532,
    "G2 Spring_pr_1_99_del": 82,
    "G2 Spring_ss_del": 113,
    "G2 Spring_dss_del": 656
  },
  {
    "ID": 117,
    "KG Fall_theta_del": -0.427529473,
    "KG Fall_pr_1_99_del": 82,
    "KG Fall_ss_del": 114,
    "KG Fall_dss_del": 457,
    "KG Winter_theta_del": -0.111359527,
    "KG Winter_pr_1_99_del": 82,
    "KG Winter_ss_del": 114,
    "KG Winter_dss_del": 489,
    "KG Spring_theta_del": 0.204093708,
    "KG Spring_pr_1_99_del": 82,
    "KG Spring_ss_del": 114,
    "KG Spring_dss_del": 520,
    "G1 Fall_theta_del": 0.371370226,
    "G1 Fall_pr_1_99_del": 82,
    "G1 Fall_ss_del": 114,
    "G1 Fall_dss_del": 537,
    "G1 Winter_theta_del": 0.608065601,
    "G1 Winter_pr_1_99_del": 82,
    "G1 Winter_ss_del": 114,
    "G1 Winter_dss_del": 561,
    "G1 Spring_theta_del": 0.774088684,
    "G1 Spring_pr_1_99_del": 82,
    "G1 Spring_ss_del": 113,
    "G1 Spring_dss_del": 577,
    "G2 Fall_theta_del": 1.0365048,
    "G2 Fall_pr_1_99_del": 82,
    "G2 Fall_ss_del": 114,
    "G2 Fall_dss_del": 604,
    "G2 Winter_theta_del": 1.289733257,
    "G2 Winter_pr_1_99_del": 82,
    "G2 Winter_ss_del": 114,
    "G2 Winter_dss_del": 629,
    "G2 Spring_theta_del": 1.582971188,
    "G2 Spring_pr_1_99_del": 82,
    "G2 Spring_ss_del": 114,
    "G2 Spring_dss_del": 658
  },
  {
    "ID": 271,
    "KG Fall_theta_del": -0.41638889,
    "KG Fall_pr_1_99_del": 82,
    "KG Fall_ss_del": 114,
    "KG Fall_dss_del": 458,
    "KG Winter_theta_del": -0.100836348,
    "KG Winter_pr_1_99_del": 82,
    "KG Winter_ss_del": 114,
    "KG Winter_dss_del": 490,
    "KG Spring_theta_del": 0.205417835,
    "KG Spring_pr_1_99_del": 82,
    "KG Spring_ss_del": 114,
    "KG Spring_dss_del": 521,
    "G1 Fall_theta_del": 0.371604807,
    "G1 Fall_pr_1_99_del": 82,
    "G1 Fall_ss_del": 114,
    "G1 Fall_dss_del": 537,
    "G1 Winter_theta_del": 0.609686059,
    "G1 Winter_pr_1_99_del": 82,
    "G1 Winter_ss_del": 114,
    "G1 Winter_dss_del": 561,
    "G1 Spring_theta_del": 0.779981968,
    "G1 Spring_pr_1_99_del": 82,
    "G1 Spring_ss_del": 114,
    "G1 Spring_dss_del": 578,
    "G2 Fall_theta_del": 1.040789436,
    "G2 Fall_pr_1_99_del": 82,
    "G2 Fall_ss_del": 114,
    "G2 Fall_dss_del": 604,
    "G2 Winter_theta_del": 1.293769637,
    "G2 Winter_pr_1_99_del": 82,
    "G2 Winter_ss_del": 114,
    "G2 Winter_dss_del": 629,
    "G2 Spring_theta_del": 1.586362415,
    "G2 Spring_pr_1_99_del": 82,
    "G2 Spring_ss_del": 114,
    "G2 Spring_dss_del": 659
  },
  {
    "ID": 287,
    "KG Fall_theta_del": -0.415284263,
    "KG Fall_pr_1_99_del": 82,
    "KG Fall_ss_del": 114,
    "KG Fall_dss_del": 458,
    "KG Winter_theta_del": -0.09457374,
    "KG Winter_pr_1_99_del": 82,
    "KG Winter_ss_del": 114,
    "KG Winter_dss_del": 491,
    "KG Spring_theta_del": 0.216952642,
    "KG Spring_pr_1_99_del": 82,
    "KG Spring_ss_del": 114,
    "KG Spring_dss_del": 522,
    "G1 Fall_theta_del": 0.371992328,
    "G1 Fall_pr_1_99_del": 82,
    "G1 Fall_ss_del": 114,
    "G1 Fall_dss_del": 537,
    "G1 Winter_theta_del": 0.624614185,
    "G1 Winter_pr_1_99_del": 82,
    "G1 Winter_ss_del": 115,
    "G1 Winter_dss_del": 562,
    "G1 Spring_theta_del": 0.786705921,
    "G1 Spring_pr_1_99_del": 82,
    "G1 Spring_ss_del": 114,
    "G1 Spring_dss_del": 579,
    "G2 Fall_theta_del": 1.061964131,
    "G2 Fall_pr_1_99_del": 82,
    "G2 Fall_ss_del": 114,
    "G2 Fall_dss_del": 606,
    "G2 Winter_theta_del": 1.293911653,
    "G2 Winter_pr_1_99_del": 82,
    "G2 Winter_ss_del": 114,
    "G2 Winter_dss_del": 629,
    "G2 Spring_theta_del": 1.589529122,
    "G2 Spring_pr_1_99_del": 82,
    "G2 Spring_ss_del": 114,
    "G2 Spring_dss_del": 659
  },
  {
    "ID": 5,
    "KG Fall_theta_del": -0.407750222,
    "KG Fall_pr_1_99_del": 83,
    "KG Fall_ss_del": 114,
    "KG Fall_dss_del": 459,
    "KG Winter_theta_del": -0.091736427,
    "KG Winter_pr_1_99_del": 83,
    "KG Winter_ss_del": 114,
    "KG Winter_dss_del": 491,
    "KG Spring_theta_del": 0.217861738,
    "KG Spring_pr_1_99_del": 83,
    "KG Spring_ss_del": 114,
    "KG Spring_dss_del": 522,
    "G1 Fall_theta_del": 0.372272,
    "G1 Fall_pr_1_99_del": 83,
    "G1 Fall_ss_del": 114,
    "G1 Fall_dss_del": 537,
    "G1 Winter_theta_del": 0.625318125,
    "G1 Winter_pr_1_99_del": 83,
    "G1 Winter_ss_del": 115,
    "G1 Winter_dss_del": 563,
    "G1 Spring_theta_del": 0.792217577,
    "G1 Spring_pr_1_99_del": 83,
    "G1 Spring_ss_del": 114,
    "G1 Spring_dss_del": 579,
    "G2 Fall_theta_del": 1.065468097,
    "G2 Fall_pr_1_99_del": 83,
    "G2 Fall_ss_del": 114,
    "G2 Fall_dss_del": 607,
    "G2 Winter_theta_del": 1.300136655,
    "G2 Winter_pr_1_99_del": 83,
    "G2 Winter_ss_del": 114,
    "G2 Winter_dss_del": 630,
    "G2 Spring_theta_del": 1.592132449,
    "G2 Spring_pr_1_99_del": 83,
    "G2 Spring_ss_del": 114,
    "G2 Spring_dss_del": 659
  },
  {
    "ID": 51,
    "KG Fall_theta_del": -0.405317089,
    "KG Fall_pr_1_99_del": 83,
    "KG Fall_ss_del": 114,
    "KG Fall_dss_del": 459,
    "KG Winter_theta_del": -0.073425641,
    "KG Winter_pr_1_99_del": 83,
    "KG Winter_ss_del": 115,
    "KG Winter_dss_del": 493,
    "KG Spring_theta_del": 0.223211596,
    "KG Spring_pr_1_99_del": 83,
    "KG Spring_ss_del": 114,
    "KG Spring_dss_del": 522,
    "G1 Fall_theta_del": 0.375313514,
    "G1 Fall_pr_1_99_del": 83,
    "G1 Fall_ss_del": 114,
    "G1 Fall_dss_del": 538,
    "G1 Winter_theta_del": 0.626087675,
    "G1 Winter_pr_1_99_del": 83,
    "G1 Winter_ss_del": 115,
    "G1 Winter_dss_del": 563,
    "G1 Spring_theta_del": 0.793161141,
    "G1 Spring_pr_1_99_del": 83,
    "G1 Spring_ss_del": 114,
    "G1 Spring_dss_del": 579,
    "G2 Fall_theta_del": 1.066176668,
    "G2 Fall_pr_1_99_del": 83,
    "G2 Fall_ss_del": 114,
    "G2 Fall_dss_del": 607,
    "G2 Winter_theta_del": 1.304189714,
    "G2 Winter_pr_1_99_del": 83,
    "G2 Winter_ss_del": 114,
    "G2 Winter_dss_del": 630,
    "G2 Spring_theta_del": 1.60100953,
    "G2 Spring_pr_1_99_del": 83,
    "G2 Spring_ss_del": 114,
    "G2 Spring_dss_del": 660
  },
  {
    "ID": 197,
    "KG Fall_theta_del": -0.393386772,
    "KG Fall_pr_1_99_del": 83,
    "KG Fall_ss_del": 114,
    "KG Fall_dss_del": 461,
    "KG Winter_theta_del": -0.067308666,
    "KG Winter_pr_1_99_del": 83,
    "KG Winter_ss_del": 115,
    "KG Winter_dss_del": 493,
    "KG Spring_theta_del": 0.224043627,
    "KG Spring_pr_1_99_del": 83,
    "KG Spring_ss_del": 114,
    "KG Spring_dss_del": 522,
    "G1 Fall_theta_del": 0.384198134,
    "G1 Fall_pr_1_99_del": 83,
    "G1 Fall_ss_del": 114,
    "G1 Fall_dss_del": 538,
    "G1 Winter_theta_del": 0.630998705,
    "G1 Winter_pr_1_99_del": 83,
    "G1 Winter_ss_del": 115,
    "G1 Winter_dss_del": 563,
    "G1 Spring_theta_del": 0.796018879,
    "G1 Spring_pr_1_99_del": 83,
    "G1 Spring_ss_del": 114,
    "G1 Spring_dss_del": 580,
    "G2 Fall_theta_del": 1.06748367,
    "G2 Fall_pr_1_99_del": 83,
    "G2 Fall_ss_del": 114,
    "G2 Fall_dss_del": 607,
    "G2 Winter_theta_del": 1.315759973,
    "G2 Winter_pr_1_99_del": 83,
    "G2 Winter_ss_del": 114,
    "G2 Winter_dss_del": 632,
    "G2 Spring_theta_del": 1.609285044,
    "G2 Spring_pr_1_99_del": 83,
    "G2 Spring_ss_del": 114,
    "G2 Spring_dss_del": 661
  },
  {
    "ID": 208,
    "KG Fall_theta_del": -0.380002544,
    "KG Fall_pr_1_99_del": 83,
    "KG Fall_ss_del": 114,
    "KG Fall_dss_del": 462,
    "KG Winter_theta_del": -0.066620075,
    "KG Winter_pr_1_99_del": 83,
    "KG Winter_ss_del": 115,
    "KG Winter_dss_del": 493,
    "KG Spring_theta_del": 0.228114186,
    "KG Spring_pr_1_99_del": 83,
    "KG Spring_ss_del": 115,
    "KG Spring_dss_del": 523,
    "G1 Fall_theta_del": 0.398062799,
    "G1 Fall_pr_1_99_del": 83,
    "G1 Fall_ss_del": 115,
    "G1 Fall_dss_del": 540,
    "G1 Winter_theta_del": 0.641049205,
    "G1 Winter_pr_1_99_del": 83,
    "G1 Winter_ss_del": 115,
    "G1 Winter_dss_del": 564,
    "G1 Spring_theta_del": 0.798031065,
    "G1 Spring_pr_1_99_del": 83,
    "G1 Spring_ss_del": 114,
    "G1 Spring_dss_del": 580,
    "G2 Fall_theta_del": 1.075073127,
    "G2 Fall_pr_1_99_del": 83,
    "G2 Fall_ss_del": 114,
    "G2 Fall_dss_del": 608,
    "G2 Winter_theta_del": 1.327187942,
    "G2 Winter_pr_1_99_del": 83,
    "G2 Winter_ss_del": 114,
    "G2 Winter_dss_del": 633,
    "G2 Spring_theta_del": 1.609784797,
    "G2 Spring_pr_1_99_del": 83,
    "G2 Spring_ss_del": 114,
    "G2 Spring_dss_del": 661
  },
  {
    "ID": 266,
    "KG Fall_theta_del": -0.37806375,
    "KG Fall_pr_1_99_del": 83,
    "KG Fall_ss_del": 114,
    "KG Fall_dss_del": 462,
    "KG Winter_theta_del": -0.065735084,
    "KG Winter_pr_1_99_del": 83,
    "KG Winter_ss_del": 115,
    "KG Winter_dss_del": 493,
    "KG Spring_theta_del": 0.230477355,
    "KG Spring_pr_1_99_del": 83,
    "KG Spring_ss_del": 115,
    "KG Spring_dss_del": 523,
    "G1 Fall_theta_del": 0.411501477,
    "G1 Fall_pr_1_99_del": 83,
    "G1 Fall_ss_del": 115,
    "G1 Fall_dss_del": 541,
    "G1 Winter_theta_del": 0.641229678,
    "G1 Winter_pr_1_99_del": 83,
    "G1 Winter_ss_del": 115,
    "G1 Winter_dss_del": 564,
    "G1 Spring_theta_del": 0.803593233,
    "G1 Spring_pr_1_99_del": 83,
    "G1 Spring_ss_del": 114,
    "G1 Spring_dss_del": 580,
    "G2 Fall_theta_del": 1.095731465,
    "G2 Fall_pr_1_99_del": 83,
    "G2 Fall_ss_del": 115,
    "G2 Fall_dss_del": 610,
    "G2 Winter_theta_del": 1.327769515,
    "G2 Winter_pr_1_99_del": 83,
    "G2 Winter_ss_del": 114,
    "G2 Winter_dss_del": 633,
    "G2 Spring_theta_del": 1.611113586,
    "G2 Spring_pr_1_99_del": 83,
    "G2 Spring_ss_del": 114,
    "G2 Spring_dss_del": 661
  },
  {
    "ID": 27,
    "KG Fall_theta_del": -0.376612243,
    "KG Fall_pr_1_99_del": 84,
    "KG Fall_ss_del": 115,
    "KG Fall_dss_del": 462,
    "KG Winter_theta_del": -0.064591062,
    "KG Winter_pr_1_99_del": 84,
    "KG Winter_ss_del": 115,
    "KG Winter_dss_del": 494,
    "KG Spring_theta_del": 0.232532349,
    "KG Spring_pr_1_99_del": 84,
    "KG Spring_ss_del": 115,
    "KG Spring_dss_del": 523,
    "G1 Fall_theta_del": 0.421070055,
    "G1 Fall_pr_1_99_del": 84,
    "G1 Fall_ss_del": 115,
    "G1 Fall_dss_del": 542,
    "G1 Winter_theta_del": 0.642565285,
    "G1 Winter_pr_1_99_del": 84,
    "G1 Winter_ss_del": 115,
    "G1 Winter_dss_del": 564,
    "G1 Spring_theta_del": 0.81113112,
    "G1 Spring_pr_1_99_del": 84,
    "G1 Spring_ss_del": 114,
    "G1 Spring_dss_del": 581,
    "G2 Fall_theta_del": 1.100002877,
    "G2 Fall_pr_1_99_del": 84,
    "G2 Fall_ss_del": 115,
    "G2 Fall_dss_del": 610,
    "G2 Winter_theta_del": 1.330635107,
    "G2 Winter_pr_1_99_del": 84,
    "G2 Winter_ss_del": 115,
    "G2 Winter_dss_del": 633,
    "G2 Spring_theta_del": 1.612668105,
    "G2 Spring_pr_1_99_del": 84,
    "G2 Spring_ss_del": 114,
    "G2 Spring_dss_del": 661
  },
  {
    "ID": 35,
    "KG Fall_theta_del": -0.37212691,
    "KG Fall_pr_1_99_del": 84,
    "KG Fall_ss_del": 115,
    "KG Fall_dss_del": 463,
    "KG Winter_theta_del": -0.039159557,
    "KG Winter_pr_1_99_del": 84,
    "KG Winter_ss_del": 115,
    "KG Winter_dss_del": 496,
    "KG Spring_theta_del": 0.241135037,
    "KG Spring_pr_1_99_del": 84,
    "KG Spring_ss_del": 115,
    "KG Spring_dss_del": 524,
    "G1 Fall_theta_del": 0.422799308,
    "G1 Fall_pr_1_99_del": 84,
    "G1 Fall_ss_del": 115,
    "G1 Fall_dss_del": 542,
    "G1 Winter_theta_del": 0.65394961,
    "G1 Winter_pr_1_99_del": 84,
    "G1 Winter_ss_del": 115,
    "G1 Winter_dss_del": 565,
    "G1 Spring_theta_del": 0.826840947,
    "G1 Spring_pr_1_99_del": 84,
    "G1 Spring_ss_del": 114,
    "G1 Spring_dss_del": 583,
    "G2 Fall_theta_del": 1.10391788,
    "G2 Fall_pr_1_99_del": 84,
    "G2 Fall_ss_del": 115,
    "G2 Fall_dss_del": 610,
    "G2 Winter_theta_del": 1.343276314,
    "G2 Winter_pr_1_99_del": 84,
    "G2 Winter_ss_del": 115,
    "G2 Winter_dss_del": 634,
    "G2 Spring_theta_del": 1.615299196,
    "G2 Spring_pr_1_99_del": 84,
    "G2 Spring_ss_del": 114,
    "G2 Spring_dss_del": 662
  },
  {
    "ID": 39,
    "KG Fall_theta_del": -0.347904858,
    "KG Fall_pr_1_99_del": 84,
    "KG Fall_ss_del": 115,
    "KG Fall_dss_del": 465,
    "KG Winter_theta_del": -0.034857755,
    "KG Winter_pr_1_99_del": 84,
    "KG Winter_ss_del": 115,
    "KG Winter_dss_del": 497,
    "KG Spring_theta_del": 0.243033315,
    "KG Spring_pr_1_99_del": 84,
    "KG Spring_ss_del": 115,
    "KG Spring_dss_del": 524,
    "G1 Fall_theta_del": 0.439892863,
    "G1 Fall_pr_1_99_del": 84,
    "G1 Fall_ss_del": 115,
    "G1 Fall_dss_del": 544,
    "G1 Winter_theta_del": 0.669652847,
    "G1 Winter_pr_1_99_del": 84,
    "G1 Winter_ss_del": 116,
    "G1 Winter_dss_del": 567,
    "G1 Spring_theta_del": 0.82862843,
    "G1 Spring_pr_1_99_del": 84,
    "G1 Spring_ss_del": 114,
    "G1 Spring_dss_del": 583,
    "G2 Fall_theta_del": 1.114048899,
    "G2 Fall_pr_1_99_del": 84,
    "G2 Fall_ss_del": 115,
    "G2 Fall_dss_del": 611,
    "G2 Winter_theta_del": 1.347573761,
    "G2 Winter_pr_1_99_del": 84,
    "G2 Winter_ss_del": 115,
    "G2 Winter_dss_del": 635,
    "G2 Spring_theta_del": 1.623681146,
    "G2 Spring_pr_1_99_del": 84,
    "G2 Spring_ss_del": 115,
    "G2 Spring_dss_del": 662
  },
  {
    "ID": 313,
    "KG Fall_theta_del": -0.341791235,
    "KG Fall_pr_1_99_del": 84,
    "KG Fall_ss_del": 115,
    "KG Fall_dss_del": 466,
    "KG Winter_theta_del": -0.031915825,
    "KG Winter_pr_1_99_del": 84,
    "KG Winter_ss_del": 115,
    "KG Winter_dss_del": 497,
    "KG Spring_theta_del": 0.249887231,
    "KG Spring_pr_1_99_del": 84,
    "KG Spring_ss_del": 115,
    "KG Spring_dss_del": 525,
    "G1 Fall_theta_del": 0.441617425,
    "G1 Fall_pr_1_99_del": 84,
    "G1 Fall_ss_del": 115,
    "G1 Fall_dss_del": 544,
    "G1 Winter_theta_del": 0.671290458,
    "G1 Winter_pr_1_99_del": 84,
    "G1 Winter_ss_del": 116,
    "G1 Winter_dss_del": 567,
    "G1 Spring_theta_del": 0.831595167,
    "G1 Spring_pr_1_99_del": 84,
    "G1 Spring_ss_del": 115,
    "G1 Spring_dss_del": 583,
    "G2 Fall_theta_del": 1.119610737,
    "G2 Fall_pr_1_99_del": 84,
    "G2 Fall_ss_del": 115,
    "G2 Fall_dss_del": 612,
    "G2 Winter_theta_del": 1.35198955,
    "G2 Winter_pr_1_99_del": 84,
    "G2 Winter_ss_del": 115,
    "G2 Winter_dss_del": 635,
    "G2 Spring_theta_del": 1.625857539,
    "G2 Spring_pr_1_99_del": 84,
    "G2 Spring_ss_del": 115,
    "G2 Spring_dss_del": 663
  },
  {
    "ID": 484,
    "KG Fall_theta_del": -0.337828682,
    "KG Fall_pr_1_99_del": 84,
    "KG Fall_ss_del": 115,
    "KG Fall_dss_del": 466,
    "KG Winter_theta_del": -0.027919346,
    "KG Winter_pr_1_99_del": 84,
    "KG Winter_ss_del": 115,
    "KG Winter_dss_del": 497,
    "KG Spring_theta_del": 0.254417225,
    "KG Spring_pr_1_99_del": 84,
    "KG Spring_ss_del": 115,
    "KG Spring_dss_del": 525,
    "G1 Fall_theta_del": 0.446722925,
    "G1 Fall_pr_1_99_del": 84,
    "G1 Fall_ss_del": 115,
    "G1 Fall_dss_del": 545,
    "G1 Winter_theta_del": 0.672596492,
    "G1 Winter_pr_1_99_del": 84,
    "G1 Winter_ss_del": 116,
    "G1 Winter_dss_del": 567,
    "G1 Spring_theta_del": 0.839371305,
    "G1 Spring_pr_1_99_del": 84,
    "G1 Spring_ss_del": 115,
    "G1 Spring_dss_del": 584,
    "G2 Fall_theta_del": 1.123202703,
    "G2 Fall_pr_1_99_del": 84,
    "G2 Fall_ss_del": 115,
    "G2 Fall_dss_del": 612,
    "G2 Winter_theta_del": 1.35531976,
    "G2 Winter_pr_1_99_del": 84,
    "G2 Winter_ss_del": 115,
    "G2 Winter_dss_del": 636,
    "G2 Spring_theta_del": 1.631333971,
    "G2 Spring_pr_1_99_del": 84,
    "G2 Spring_ss_del": 115,
    "G2 Spring_dss_del": 663
  },
  {
    "ID": 66,
    "KG Fall_theta_del": -0.33519218,
    "KG Fall_pr_1_99_del": 85,
    "KG Fall_ss_del": 115,
    "KG Fall_dss_del": 466,
    "KG Winter_theta_del": -0.02026765,
    "KG Winter_pr_1_99_del": 85,
    "KG Winter_ss_del": 116,
    "KG Winter_dss_del": 498,
    "KG Spring_theta_del": 0.270205863,
    "KG Spring_pr_1_99_del": 85,
    "KG Spring_ss_del": 115,
    "KG Spring_dss_del": 527,
    "G1 Fall_theta_del": 0.449694837,
    "G1 Fall_pr_1_99_del": 85,
    "G1 Fall_ss_del": 116,
    "G1 Fall_dss_del": 545,
    "G1 Winter_theta_del": 0.675695355,
    "G1 Winter_pr_1_99_del": 85,
    "G1 Winter_ss_del": 116,
    "G1 Winter_dss_del": 568,
    "G1 Spring_theta_del": 0.864399905,
    "G1 Spring_pr_1_99_del": 85,
    "G1 Spring_ss_del": 115,
    "G1 Spring_dss_del": 586,
    "G2 Fall_theta_del": 1.123794556,
    "G2 Fall_pr_1_99_del": 85,
    "G2 Fall_ss_del": 115,
    "G2 Fall_dss_del": 612,
    "G2 Winter_theta_del": 1.357201474,
    "G2 Winter_pr_1_99_del": 85,
    "G2 Winter_ss_del": 115,
    "G2 Winter_dss_del": 636,
    "G2 Spring_theta_del": 1.63580819,
    "G2 Spring_pr_1_99_del": 85,
    "G2 Spring_ss_del": 115,
    "G2 Spring_dss_del": 664
  },
  {
    "ID": 78,
    "KG Fall_theta_del": -0.319251957,
    "KG Fall_pr_1_99_del": 85,
    "KG Fall_ss_del": 116,
    "KG Fall_dss_del": 468,
    "KG Winter_theta_del": -0.014101041,
    "KG Winter_pr_1_99_del": 85,
    "KG Winter_ss_del": 116,
    "KG Winter_dss_del": 499,
    "KG Spring_theta_del": 0.273333776,
    "KG Spring_pr_1_99_del": 85,
    "KG Spring_ss_del": 115,
    "KG Spring_dss_del": 527,
    "G1 Fall_theta_del": 0.449736318,
    "G1 Fall_pr_1_99_del": 85,
    "G1 Fall_ss_del": 116,
    "G1 Fall_dss_del": 545,
    "G1 Winter_theta_del": 0.683301308,
    "G1 Winter_pr_1_99_del": 85,
    "G1 Winter_ss_del": 116,
    "G1 Winter_dss_del": 568,
    "G1 Spring_theta_del": 0.868076181,
    "G1 Spring_pr_1_99_del": 85,
    "G1 Spring_ss_del": 115,
    "G1 Spring_dss_del": 587,
    "G2 Fall_theta_del": 1.133185371,
    "G2 Fall_pr_1_99_del": 85,
    "G2 Fall_ss_del": 115,
    "G2 Fall_dss_del": 613,
    "G2 Winter_theta_del": 1.360855711,
    "G2 Winter_pr_1_99_del": 85,
    "G2 Winter_ss_del": 115,
    "G2 Winter_dss_del": 636,
    "G2 Spring_theta_del": 1.662395154,
    "G2 Spring_pr_1_99_del": 85,
    "G2 Spring_ss_del": 115,
    "G2 Spring_dss_del": 666
  },
  {
    "ID": 365,
    "KG Fall_theta_del": -0.312993043,
    "KG Fall_pr_1_99_del": 85,
    "KG Fall_ss_del": 116,
    "KG Fall_dss_del": 469,
    "KG Winter_theta_del": -0.013618002,
    "KG Winter_pr_1_99_del": 85,
    "KG Winter_ss_del": 116,
    "KG Winter_dss_del": 499,
    "KG Spring_theta_del": 0.276167626,
    "KG Spring_pr_1_99_del": 85,
    "KG Spring_ss_del": 115,
    "KG Spring_dss_del": 528,
    "G1 Fall_theta_del": 0.45626213,
    "G1 Fall_pr_1_99_del": 85,
    "G1 Fall_ss_del": 116,
    "G1 Fall_dss_del": 546,
    "G1 Winter_theta_del": 0.697035111,
    "G1 Winter_pr_1_99_del": 85,
    "G1 Winter_ss_del": 116,
    "G1 Winter_dss_del": 570,
    "G1 Spring_theta_del": 0.869229787,
    "G1 Spring_pr_1_99_del": 85,
    "G1 Spring_ss_del": 115,
    "G1 Spring_dss_del": 587,
    "G2 Fall_theta_del": 1.1337791,
    "G2 Fall_pr_1_99_del": 85,
    "G2 Fall_ss_del": 115,
    "G2 Fall_dss_del": 613,
    "G2 Winter_theta_del": 1.374920654,
    "G2 Winter_pr_1_99_del": 85,
    "G2 Winter_ss_del": 115,
    "G2 Winter_dss_del": 637,
    "G2 Spring_theta_del": 1.664200805,
    "G2 Spring_pr_1_99_del": 85,
    "G2 Spring_ss_del": 115,
    "G2 Spring_dss_del": 666
  },
  {
    "ID": 370,
    "KG Fall_theta_del": -0.290274622,
    "KG Fall_pr_1_99_del": 85,
    "KG Fall_ss_del": 116,
    "KG Fall_dss_del": 471,
    "KG Winter_theta_del": -0.007648322,
    "KG Winter_pr_1_99_del": 85,
    "KG Winter_ss_del": 116,
    "KG Winter_dss_del": 499,
    "KG Spring_theta_del": 0.279663201,
    "KG Spring_pr_1_99_del": 85,
    "KG Spring_ss_del": 116,
    "KG Spring_dss_del": 528,
    "G1 Fall_theta_del": 0.474744309,
    "G1 Fall_pr_1_99_del": 85,
    "G1 Fall_ss_del": 116,
    "G1 Fall_dss_del": 547,
    "G1 Winter_theta_del": 0.707513775,
    "G1 Winter_pr_1_99_del": 85,
    "G1 Winter_ss_del": 116,
    "G1 Winter_dss_del": 571,
    "G1 Spring_theta_del": 0.88032265,
    "G1 Spring_pr_1_99_del": 85,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 588,
    "G2 Fall_theta_del": 1.133865865,
    "G2 Fall_pr_1_99_del": 85,
    "G2 Fall_ss_del": 115,
    "G2 Fall_dss_del": 613,
    "G2 Winter_theta_del": 1.387707976,
    "G2 Winter_pr_1_99_del": 85,
    "G2 Winter_ss_del": 116,
    "G2 Winter_dss_del": 639,
    "G2 Spring_theta_del": 1.666934069,
    "G2 Spring_pr_1_99_del": 85,
    "G2 Spring_ss_del": 115,
    "G2 Spring_dss_del": 667
  },
  {
    "ID": 456,
    "KG Fall_theta_del": -0.288299908,
    "KG Fall_pr_1_99_del": 85,
    "KG Fall_ss_del": 116,
    "KG Fall_dss_del": 471,
    "KG Winter_theta_del": -0.003464543,
    "KG Winter_pr_1_99_del": 85,
    "KG Winter_ss_del": 116,
    "KG Winter_dss_del": 500,
    "KG Spring_theta_del": 0.29549734,
    "KG Spring_pr_1_99_del": 85,
    "KG Spring_ss_del": 116,
    "KG Spring_dss_del": 530,
    "G1 Fall_theta_del": 0.479703399,
    "G1 Fall_pr_1_99_del": 85,
    "G1 Fall_ss_del": 116,
    "G1 Fall_dss_del": 548,
    "G1 Winter_theta_del": 0.713802981,
    "G1 Winter_pr_1_99_del": 85,
    "G1 Winter_ss_del": 116,
    "G1 Winter_dss_del": 571,
    "G1 Spring_theta_del": 0.884528996,
    "G1 Spring_pr_1_99_del": 85,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 588,
    "G2 Fall_theta_del": 1.141695689,
    "G2 Fall_pr_1_99_del": 85,
    "G2 Fall_ss_del": 116,
    "G2 Fall_dss_del": 614,
    "G2 Winter_theta_del": 1.38862349,
    "G2 Winter_pr_1_99_del": 85,
    "G2 Winter_ss_del": 116,
    "G2 Winter_dss_del": 639,
    "G2 Spring_theta_del": 1.668444108,
    "G2 Spring_pr_1_99_del": 85,
    "G2 Spring_ss_del": 115,
    "G2 Spring_dss_del": 667
  },
  {
    "ID": 97,
    "KG Fall_theta_del": -0.286213755,
    "KG Fall_pr_1_99_del": 86,
    "KG Fall_ss_del": 116,
    "KG Fall_dss_del": 471,
    "KG Winter_theta_del": -0.002015017,
    "KG Winter_pr_1_99_del": 86,
    "KG Winter_ss_del": 116,
    "KG Winter_dss_del": 500,
    "KG Spring_theta_del": 0.296407088,
    "KG Spring_pr_1_99_del": 86,
    "KG Spring_ss_del": 116,
    "KG Spring_dss_del": 530,
    "G1 Fall_theta_del": 0.481110106,
    "G1 Fall_pr_1_99_del": 86,
    "G1 Fall_ss_del": 116,
    "G1 Fall_dss_del": 548,
    "G1 Winter_theta_del": 0.719559337,
    "G1 Winter_pr_1_99_del": 86,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 572,
    "G1 Spring_theta_del": 0.888292269,
    "G1 Spring_pr_1_99_del": 86,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 589,
    "G2 Fall_theta_del": 1.142298156,
    "G2 Fall_pr_1_99_del": 86,
    "G2 Fall_ss_del": 116,
    "G2 Fall_dss_del": 614,
    "G2 Winter_theta_del": 1.417249127,
    "G2 Winter_pr_1_99_del": 86,
    "G2 Winter_ss_del": 116,
    "G2 Winter_dss_del": 642,
    "G2 Spring_theta_del": 1.668936067,
    "G2 Spring_pr_1_99_del": 86,
    "G2 Spring_ss_del": 115,
    "G2 Spring_dss_del": 667
  },
  {
    "ID": 144,
    "KG Fall_theta_del": -0.283995246,
    "KG Fall_pr_1_99_del": 86,
    "KG Fall_ss_del": 116,
    "KG Fall_dss_del": 472,
    "KG Winter_theta_del": -0.001363228,
    "KG Winter_pr_1_99_del": 86,
    "KG Winter_ss_del": 116,
    "KG Winter_dss_del": 500,
    "KG Spring_theta_del": 0.301660875,
    "KG Spring_pr_1_99_del": 86,
    "KG Spring_ss_del": 116,
    "KG Spring_dss_del": 530,
    "G1 Fall_theta_del": 0.488405559,
    "G1 Fall_pr_1_99_del": 86,
    "G1 Fall_ss_del": 116,
    "G1 Fall_dss_del": 549,
    "G1 Winter_theta_del": 0.722683715,
    "G1 Winter_pr_1_99_del": 86,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 572,
    "G1 Spring_theta_del": 0.891137019,
    "G1 Spring_pr_1_99_del": 86,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 589,
    "G2 Fall_theta_del": 1.163926115,
    "G2 Fall_pr_1_99_del": 86,
    "G2 Fall_ss_del": 116,
    "G2 Fall_dss_del": 616,
    "G2 Winter_theta_del": 1.41727369,
    "G2 Winter_pr_1_99_del": 86,
    "G2 Winter_ss_del": 116,
    "G2 Winter_dss_del": 642,
    "G2 Spring_theta_del": 1.695297634,
    "G2 Spring_pr_1_99_del": 86,
    "G2 Spring_ss_del": 116,
    "G2 Spring_dss_del": 670
  },
  {
    "ID": 233,
    "KG Fall_theta_del": -0.282697137,
    "KG Fall_pr_1_99_del": 86,
    "KG Fall_ss_del": 116,
    "KG Fall_dss_del": 472,
    "KG Winter_theta_del": 0.015486384,
    "KG Winter_pr_1_99_del": 86,
    "KG Winter_ss_del": 116,
    "KG Winter_dss_del": 502,
    "KG Spring_theta_del": 0.31870746,
    "KG Spring_pr_1_99_del": 86,
    "KG Spring_ss_del": 116,
    "KG Spring_dss_del": 532,
    "G1 Fall_theta_del": 0.490992387,
    "G1 Fall_pr_1_99_del": 86,
    "G1 Fall_ss_del": 116,
    "G1 Fall_dss_del": 549,
    "G1 Winter_theta_del": 0.729632443,
    "G1 Winter_pr_1_99_del": 86,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 573,
    "G1 Spring_theta_del": 0.892309027,
    "G1 Spring_pr_1_99_del": 86,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 589,
    "G2 Fall_theta_del": 1.179651577,
    "G2 Fall_pr_1_99_del": 86,
    "G2 Fall_ss_del": 116,
    "G2 Fall_dss_del": 618,
    "G2 Winter_theta_del": 1.422520351,
    "G2 Winter_pr_1_99_del": 86,
    "G2 Winter_ss_del": 116,
    "G2 Winter_dss_del": 642,
    "G2 Spring_theta_del": 1.705403457,
    "G2 Spring_pr_1_99_del": 86,
    "G2 Spring_ss_del": 116,
    "G2 Spring_dss_del": 671
  },
  {
    "ID": 253,
    "KG Fall_theta_del": -0.27935032,
    "KG Fall_pr_1_99_del": 86,
    "KG Fall_ss_del": 116,
    "KG Fall_dss_del": 472,
    "KG Winter_theta_del": 0.021960232,
    "KG Winter_pr_1_99_del": 86,
    "KG Winter_ss_del": 116,
    "KG Winter_dss_del": 502,
    "KG Spring_theta_del": 0.340530949,
    "KG Spring_pr_1_99_del": 86,
    "KG Spring_ss_del": 117,
    "KG Spring_dss_del": 534,
    "G1 Fall_theta_del": 0.497095111,
    "G1 Fall_pr_1_99_del": 86,
    "G1 Fall_ss_del": 116,
    "G1 Fall_dss_del": 550,
    "G1 Winter_theta_del": 0.739855619,
    "G1 Winter_pr_1_99_del": 86,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 574,
    "G1 Spring_theta_del": 0.896343441,
    "G1 Spring_pr_1_99_del": 86,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 590,
    "G2 Fall_theta_del": 1.181749777,
    "G2 Fall_pr_1_99_del": 86,
    "G2 Fall_ss_del": 116,
    "G2 Fall_dss_del": 618,
    "G2 Winter_theta_del": 1.428011246,
    "G2 Winter_pr_1_99_del": 86,
    "G2 Winter_ss_del": 116,
    "G2 Winter_dss_del": 643,
    "G2 Spring_theta_del": 1.709640741,
    "G2 Spring_pr_1_99_del": 86,
    "G2 Spring_ss_del": 116,
    "G2 Spring_dss_del": 671
  },
  {
    "ID": 297,
    "KG Fall_theta_del": -0.271559142,
    "KG Fall_pr_1_99_del": 86,
    "KG Fall_ss_del": 116,
    "KG Fall_dss_del": 473,
    "KG Winter_theta_del": 0.022929509,
    "KG Winter_pr_1_99_del": 86,
    "KG Winter_ss_del": 116,
    "KG Winter_dss_del": 502,
    "KG Spring_theta_del": 0.354938345,
    "KG Spring_pr_1_99_del": 86,
    "KG Spring_ss_del": 117,
    "KG Spring_dss_del": 535,
    "G1 Fall_theta_del": 0.498864459,
    "G1 Fall_pr_1_99_del": 86,
    "G1 Fall_ss_del": 116,
    "G1 Fall_dss_del": 550,
    "G1 Winter_theta_del": 0.743171685,
    "G1 Winter_pr_1_99_del": 86,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 574,
    "G1 Spring_theta_del": 0.902361583,
    "G1 Spring_pr_1_99_del": 86,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 590,
    "G2 Fall_theta_del": 1.185544809,
    "G2 Fall_pr_1_99_del": 86,
    "G2 Fall_ss_del": 116,
    "G2 Fall_dss_del": 619,
    "G2 Winter_theta_del": 1.43256435,
    "G2 Winter_pr_1_99_del": 86,
    "G2 Winter_ss_del": 116,
    "G2 Winter_dss_del": 643,
    "G2 Spring_theta_del": 1.715699819,
    "G2 Spring_pr_1_99_del": 86,
    "G2 Spring_ss_del": 116,
    "G2 Spring_dss_del": 672
  },
  {
    "ID": 221,
    "KG Fall_theta_del": -0.252007739,
    "KG Fall_pr_1_99_del": 87,
    "KG Fall_ss_del": 117,
    "KG Fall_dss_del": 475,
    "KG Winter_theta_del": 0.043449243,
    "KG Winter_pr_1_99_del": 87,
    "KG Winter_ss_del": 117,
    "KG Winter_dss_del": 504,
    "KG Spring_theta_del": 0.355987779,
    "KG Spring_pr_1_99_del": 87,
    "KG Spring_ss_del": 117,
    "KG Spring_dss_del": 536,
    "G1 Fall_theta_del": 0.503469783,
    "G1 Fall_pr_1_99_del": 87,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 550,
    "G1 Winter_theta_del": 0.750971809,
    "G1 Winter_pr_1_99_del": 87,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 575,
    "G1 Spring_theta_del": 0.907537948,
    "G1 Spring_pr_1_99_del": 87,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 591,
    "G2 Fall_theta_del": 1.187465481,
    "G2 Fall_pr_1_99_del": 87,
    "G2 Fall_ss_del": 116,
    "G2 Fall_dss_del": 619,
    "G2 Winter_theta_del": 1.434171501,
    "G2 Winter_pr_1_99_del": 87,
    "G2 Winter_ss_del": 116,
    "G2 Winter_dss_del": 643,
    "G2 Spring_theta_del": 1.716380354,
    "G2 Spring_pr_1_99_del": 87,
    "G2 Spring_ss_del": 116,
    "G2 Spring_dss_del": 672
  },
  {
    "ID": 241,
    "KG Fall_theta_del": -0.251619336,
    "KG Fall_pr_1_99_del": 87,
    "KG Fall_ss_del": 117,
    "KG Fall_dss_del": 475,
    "KG Winter_theta_del": 0.055337144,
    "KG Winter_pr_1_99_del": 87,
    "KG Winter_ss_del": 117,
    "KG Winter_dss_del": 506,
    "KG Spring_theta_del": 0.369721954,
    "KG Spring_pr_1_99_del": 87,
    "KG Spring_ss_del": 117,
    "KG Spring_dss_del": 537,
    "G1 Fall_theta_del": 0.505732247,
    "G1 Fall_pr_1_99_del": 87,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 551,
    "G1 Winter_theta_del": 0.75119297,
    "G1 Winter_pr_1_99_del": 87,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 575,
    "G1 Spring_theta_del": 0.915828205,
    "G1 Spring_pr_1_99_del": 87,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 592,
    "G2 Fall_theta_del": 1.202389305,
    "G2 Fall_pr_1_99_del": 87,
    "G2 Fall_ss_del": 117,
    "G2 Fall_dss_del": 620,
    "G2 Winter_theta_del": 1.437517441,
    "G2 Winter_pr_1_99_del": 87,
    "G2 Winter_ss_del": 117,
    "G2 Winter_dss_del": 644,
    "G2 Spring_theta_del": 1.719622222,
    "G2 Spring_pr_1_99_del": 87,
    "G2 Spring_ss_del": 116,
    "G2 Spring_dss_del": 672
  },
  {
    "ID": 345,
    "KG Fall_theta_del": -0.24612787,
    "KG Fall_pr_1_99_del": 87,
    "KG Fall_ss_del": 117,
    "KG Fall_dss_del": 475,
    "KG Winter_theta_del": 0.056333858,
    "KG Winter_pr_1_99_del": 87,
    "KG Winter_ss_del": 117,
    "KG Winter_dss_del": 506,
    "KG Spring_theta_del": 0.369743119,
    "KG Spring_pr_1_99_del": 87,
    "KG Spring_ss_del": 117,
    "KG Spring_dss_del": 537,
    "G1 Fall_theta_del": 0.512796574,
    "G1 Fall_pr_1_99_del": 87,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 551,
    "G1 Winter_theta_del": 0.751548511,
    "G1 Winter_pr_1_99_del": 87,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 575,
    "G1 Spring_theta_del": 0.916862918,
    "G1 Spring_pr_1_99_del": 87,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 592,
    "G2 Fall_theta_del": 1.206413622,
    "G2 Fall_pr_1_99_del": 87,
    "G2 Fall_ss_del": 117,
    "G2 Fall_dss_del": 621,
    "G2 Winter_theta_del": 1.443467412,
    "G2 Winter_pr_1_99_del": 87,
    "G2 Winter_ss_del": 117,
    "G2 Winter_dss_del": 644,
    "G2 Spring_theta_del": 1.722975889,
    "G2 Spring_pr_1_99_del": 87,
    "G2 Spring_ss_del": 116,
    "G2 Spring_dss_del": 672
  },
  {
    "ID": 349,
    "KG Fall_theta_del": -0.240391305,
    "KG Fall_pr_1_99_del": 87,
    "KG Fall_ss_del": 117,
    "KG Fall_dss_del": 476,
    "KG Winter_theta_del": 0.064650248,
    "KG Winter_pr_1_99_del": 87,
    "KG Winter_ss_del": 117,
    "KG Winter_dss_del": 506,
    "KG Spring_theta_del": 0.370164312,
    "KG Spring_pr_1_99_del": 87,
    "KG Spring_ss_del": 117,
    "KG Spring_dss_del": 537,
    "G1 Fall_theta_del": 0.513344256,
    "G1 Fall_pr_1_99_del": 87,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 551,
    "G1 Winter_theta_del": 0.755695129,
    "G1 Winter_pr_1_99_del": 87,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 576,
    "G1 Spring_theta_del": 0.918844237,
    "G1 Spring_pr_1_99_del": 87,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 592,
    "G2 Fall_theta_del": 1.211560008,
    "G2 Fall_pr_1_99_del": 87,
    "G2 Fall_ss_del": 117,
    "G2 Fall_dss_del": 621,
    "G2 Winter_theta_del": 1.443566761,
    "G2 Winter_pr_1_99_del": 87,
    "G2 Winter_ss_del": 117,
    "G2 Winter_dss_del": 644,
    "G2 Spring_theta_del": 1.723174567,
    "G2 Spring_pr_1_99_del": 87,
    "G2 Spring_ss_del": 116,
    "G2 Spring_dss_del": 672
  },
  {
    "ID": 392,
    "KG Fall_theta_del": -0.228797728,
    "KG Fall_pr_1_99_del": 87,
    "KG Fall_ss_del": 117,
    "KG Fall_dss_del": 477,
    "KG Winter_theta_del": 0.067165019,
    "KG Winter_pr_1_99_del": 87,
    "KG Winter_ss_del": 117,
    "KG Winter_dss_del": 507,
    "KG Spring_theta_del": 0.377014379,
    "KG Spring_pr_1_99_del": 87,
    "KG Spring_ss_del": 117,
    "KG Spring_dss_del": 538,
    "G1 Fall_theta_del": 0.521820749,
    "G1 Fall_pr_1_99_del": 87,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 552,
    "G1 Winter_theta_del": 0.762951006,
    "G1 Winter_pr_1_99_del": 87,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 576,
    "G1 Spring_theta_del": 0.920799135,
    "G1 Spring_pr_1_99_del": 87,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 592,
    "G2 Fall_theta_del": 1.221024047,
    "G2 Fall_pr_1_99_del": 87,
    "G2 Fall_ss_del": 117,
    "G2 Fall_dss_del": 622,
    "G2 Winter_theta_del": 1.460620836,
    "G2 Winter_pr_1_99_del": 87,
    "G2 Winter_ss_del": 117,
    "G2 Winter_dss_del": 646,
    "G2 Spring_theta_del": 1.736393947,
    "G2 Spring_pr_1_99_del": 87,
    "G2 Spring_ss_del": 117,
    "G2 Spring_dss_del": 674
  },
  {
    "ID": 175,
    "KG Fall_theta_del": -0.225957182,
    "KG Fall_pr_1_99_del": 88,
    "KG Fall_ss_del": 117,
    "KG Fall_dss_del": 477,
    "KG Winter_theta_del": 0.086832604,
    "KG Winter_pr_1_99_del": 88,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 509,
    "KG Spring_theta_del": 0.388401587,
    "KG Spring_pr_1_99_del": 88,
    "KG Spring_ss_del": 118,
    "KG Spring_dss_del": 539,
    "G1 Fall_theta_del": 0.528449039,
    "G1 Fall_pr_1_99_del": 88,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 553,
    "G1 Winter_theta_del": 0.763116827,
    "G1 Winter_pr_1_99_del": 88,
    "G1 Winter_ss_del": 117,
    "G1 Winter_dss_del": 576,
    "G1 Spring_theta_del": 0.92964241,
    "G1 Spring_pr_1_99_del": 88,
    "G1 Spring_ss_del": 116,
    "G1 Spring_dss_del": 593,
    "G2 Fall_theta_del": 1.222263256,
    "G2 Fall_pr_1_99_del": 88,
    "G2 Fall_ss_del": 117,
    "G2 Fall_dss_del": 622,
    "G2 Winter_theta_del": 1.464232646,
    "G2 Winter_pr_1_99_del": 88,
    "G2 Winter_ss_del": 117,
    "G2 Winter_dss_del": 646,
    "G2 Spring_theta_del": 1.740771358,
    "G2 Spring_pr_1_99_del": 88,
    "G2 Spring_ss_del": 117,
    "G2 Spring_dss_del": 674
  },
  {
    "ID": 258,
    "KG Fall_theta_del": -0.215490224,
    "KG Fall_pr_1_99_del": 88,
    "KG Fall_ss_del": 117,
    "KG Fall_dss_del": 478,
    "KG Winter_theta_del": 0.093525292,
    "KG Winter_pr_1_99_del": 88,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 509,
    "KG Spring_theta_del": 0.395539306,
    "KG Spring_pr_1_99_del": 88,
    "KG Spring_ss_del": 118,
    "KG Spring_dss_del": 540,
    "G1 Fall_theta_del": 0.530222805,
    "G1 Fall_pr_1_99_del": 88,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 553,
    "G1 Winter_theta_del": 0.766246412,
    "G1 Winter_pr_1_99_del": 88,
    "G1 Winter_ss_del": 118,
    "G1 Winter_dss_del": 577,
    "G1 Spring_theta_del": 0.956991816,
    "G1 Spring_pr_1_99_del": 88,
    "G1 Spring_ss_del": 117,
    "G1 Spring_dss_del": 596,
    "G2 Fall_theta_del": 1.233695832,
    "G2 Fall_pr_1_99_del": 88,
    "G2 Fall_ss_del": 117,
    "G2 Fall_dss_del": 623,
    "G2 Winter_theta_del": 1.499096735,
    "G2 Winter_pr_1_99_del": 88,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 650,
    "G2 Spring_theta_del": 1.742038956,
    "G2 Spring_pr_1_99_del": 88,
    "G2 Spring_ss_del": 117,
    "G2 Spring_dss_del": 674
  },
  {
    "ID": 301,
    "KG Fall_theta_del": -0.208711535,
    "KG Fall_pr_1_99_del": 88,
    "KG Fall_ss_del": 118,
    "KG Fall_dss_del": 479,
    "KG Winter_theta_del": 0.096336401,
    "KG Winter_pr_1_99_del": 88,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 510,
    "KG Spring_theta_del": 0.396865312,
    "KG Spring_pr_1_99_del": 88,
    "KG Spring_ss_del": 118,
    "KG Spring_dss_del": 540,
    "G1 Fall_theta_del": 0.530535924,
    "G1 Fall_pr_1_99_del": 88,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 553,
    "G1 Winter_theta_del": 0.779283665,
    "G1 Winter_pr_1_99_del": 88,
    "G1 Winter_ss_del": 118,
    "G1 Winter_dss_del": 578,
    "G1 Spring_theta_del": 0.966849557,
    "G1 Spring_pr_1_99_del": 88,
    "G1 Spring_ss_del": 117,
    "G1 Spring_dss_del": 597,
    "G2 Fall_theta_del": 1.249404189,
    "G2 Fall_pr_1_99_del": 88,
    "G2 Fall_ss_del": 118,
    "G2 Fall_dss_del": 625,
    "G2 Winter_theta_del": 1.499477274,
    "G2 Winter_pr_1_99_del": 88,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 650,
    "G2 Spring_theta_del": 1.74353561,
    "G2 Spring_pr_1_99_del": 88,
    "G2 Spring_ss_del": 117,
    "G2 Spring_dss_del": 674
  },
  {
    "ID": 344,
    "KG Fall_theta_del": -0.195973714,
    "KG Fall_pr_1_99_del": 88,
    "KG Fall_ss_del": 118,
    "KG Fall_dss_del": 480,
    "KG Winter_theta_del": 0.098581651,
    "KG Winter_pr_1_99_del": 88,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 510,
    "KG Spring_theta_del": 0.398086272,
    "KG Spring_pr_1_99_del": 88,
    "KG Spring_ss_del": 118,
    "KG Spring_dss_del": 540,
    "G1 Fall_theta_del": 0.531729762,
    "G1 Fall_pr_1_99_del": 88,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 553,
    "G1 Winter_theta_del": 0.787845499,
    "G1 Winter_pr_1_99_del": 88,
    "G1 Winter_ss_del": 118,
    "G1 Winter_dss_del": 579,
    "G1 Spring_theta_del": 0.972844075,
    "G1 Spring_pr_1_99_del": 88,
    "G1 Spring_ss_del": 117,
    "G1 Spring_dss_del": 597,
    "G2 Fall_theta_del": 1.254141871,
    "G2 Fall_pr_1_99_del": 88,
    "G2 Fall_ss_del": 118,
    "G2 Fall_dss_del": 625,
    "G2 Winter_theta_del": 1.500909463,
    "G2 Winter_pr_1_99_del": 88,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 650,
    "G2 Spring_theta_del": 1.743720353,
    "G2 Spring_pr_1_99_del": 88,
    "G2 Spring_ss_del": 117,
    "G2 Spring_dss_del": 674
  },
  {
    "ID": 376,
    "KG Fall_theta_del": -0.195336648,
    "KG Fall_pr_1_99_del": 88,
    "KG Fall_ss_del": 118,
    "KG Fall_dss_del": 480,
    "KG Winter_theta_del": 0.105752777,
    "KG Winter_pr_1_99_del": 88,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 511,
    "KG Spring_theta_del": 0.404842709,
    "KG Spring_pr_1_99_del": 88,
    "KG Spring_ss_del": 118,
    "KG Spring_dss_del": 540,
    "G1 Fall_theta_del": 0.535898889,
    "G1 Fall_pr_1_99_del": 88,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 554,
    "G1 Winter_theta_del": 0.790523033,
    "G1 Winter_pr_1_99_del": 88,
    "G1 Winter_ss_del": 118,
    "G1 Winter_dss_del": 579,
    "G1 Spring_theta_del": 0.973463266,
    "G1 Spring_pr_1_99_del": 88,
    "G1 Spring_ss_del": 117,
    "G1 Spring_dss_del": 597,
    "G2 Fall_theta_del": 1.257871409,
    "G2 Fall_pr_1_99_del": 88,
    "G2 Fall_ss_del": 118,
    "G2 Fall_dss_del": 626,
    "G2 Winter_theta_del": 1.513405836,
    "G2 Winter_pr_1_99_del": 88,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 651,
    "G2 Spring_theta_del": 1.759613509,
    "G2 Spring_pr_1_99_del": 88,
    "G2 Spring_ss_del": 117,
    "G2 Spring_dss_del": 676
  },
  {
    "ID": 164,
    "KG Fall_theta_del": -0.190771778,
    "KG Fall_pr_1_99_del": 89,
    "KG Fall_ss_del": 118,
    "KG Fall_dss_del": 481,
    "KG Winter_theta_del": 0.108125995,
    "KG Winter_pr_1_99_del": 89,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 511,
    "KG Spring_theta_del": 0.414222902,
    "KG Spring_pr_1_99_del": 89,
    "KG Spring_ss_del": 118,
    "KG Spring_dss_del": 541,
    "G1 Fall_theta_del": 0.540270313,
    "G1 Fall_pr_1_99_del": 89,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 554,
    "G1 Winter_theta_del": 0.792456805,
    "G1 Winter_pr_1_99_del": 89,
    "G1 Winter_ss_del": 118,
    "G1 Winter_dss_del": 579,
    "G1 Spring_theta_del": 0.97369486,
    "G1 Spring_pr_1_99_del": 89,
    "G1 Spring_ss_del": 117,
    "G1 Spring_dss_del": 597,
    "G2 Fall_theta_del": 1.277776039,
    "G2 Fall_pr_1_99_del": 89,
    "G2 Fall_ss_del": 118,
    "G2 Fall_dss_del": 628,
    "G2 Winter_theta_del": 1.513707511,
    "G2 Winter_pr_1_99_del": 89,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 651,
    "G2 Spring_theta_del": 1.764978742,
    "G2 Spring_pr_1_99_del": 89,
    "G2 Spring_ss_del": 117,
    "G2 Spring_dss_del": 676
  },
  {
    "ID": 177,
    "KG Fall_theta_del": -0.183171297,
    "KG Fall_pr_1_99_del": 89,
    "KG Fall_ss_del": 118,
    "KG Fall_dss_del": 482,
    "KG Winter_theta_del": 0.109808509,
    "KG Winter_pr_1_99_del": 89,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 511,
    "KG Spring_theta_del": 0.414956524,
    "KG Spring_pr_1_99_del": 89,
    "KG Spring_ss_del": 118,
    "KG Spring_dss_del": 541,
    "G1 Fall_theta_del": 0.54625126,
    "G1 Fall_pr_1_99_del": 89,
    "G1 Fall_ss_del": 117,
    "G1 Fall_dss_del": 555,
    "G1 Winter_theta_del": 0.793183261,
    "G1 Winter_pr_1_99_del": 89,
    "G1 Winter_ss_del": 118,
    "G1 Winter_dss_del": 579,
    "G1 Spring_theta_del": 0.982637456,
    "G1 Spring_pr_1_99_del": 89,
    "G1 Spring_ss_del": 118,
    "G1 Spring_dss_del": 598,
    "G2 Fall_theta_del": 1.300214102,
    "G2 Fall_pr_1_99_del": 89,
    "G2 Fall_ss_del": 119,
    "G2 Fall_dss_del": 630,
    "G2 Winter_theta_del": 1.517597939,
    "G2 Winter_pr_1_99_del": 89,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 652,
    "G2 Spring_theta_del": 1.770387319,
    "G2 Spring_pr_1_99_del": 89,
    "G2 Spring_ss_del": 117,
    "G2 Spring_dss_del": 677
  },
  {
    "ID": 220,
    "KG Fall_theta_del": -0.171678408,
    "KG Fall_pr_1_99_del": 89,
    "KG Fall_ss_del": 118,
    "KG Fall_dss_del": 483,
    "KG Winter_theta_del": 0.114117112,
    "KG Winter_pr_1_99_del": 89,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 511,
    "KG Spring_theta_del": 0.429942892,
    "KG Spring_pr_1_99_del": 89,
    "KG Spring_ss_del": 118,
    "KG Spring_dss_del": 543,
    "G1 Fall_theta_del": 0.558358232,
    "G1 Fall_pr_1_99_del": 89,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 556,
    "G1 Winter_theta_del": 0.796730116,
    "G1 Winter_pr_1_99_del": 89,
    "G1 Winter_ss_del": 118,
    "G1 Winter_dss_del": 580,
    "G1 Spring_theta_del": 0.986098505,
    "G1 Spring_pr_1_99_del": 89,
    "G1 Spring_ss_del": 118,
    "G1 Spring_dss_del": 599,
    "G2 Fall_theta_del": 1.302337547,
    "G2 Fall_pr_1_99_del": 89,
    "G2 Fall_ss_del": 119,
    "G2 Fall_dss_del": 630,
    "G2 Winter_theta_del": 1.518154869,
    "G2 Winter_pr_1_99_del": 89,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 652,
    "G2 Spring_theta_del": 1.780909942,
    "G2 Spring_pr_1_99_del": 89,
    "G2 Spring_ss_del": 117,
    "G2 Spring_dss_del": 678
  },
  {
    "ID": 329,
    "KG Fall_theta_del": -0.167809649,
    "KG Fall_pr_1_99_del": 89,
    "KG Fall_ss_del": 118,
    "KG Fall_dss_del": 483,
    "KG Winter_theta_del": 0.117418704,
    "KG Winter_pr_1_99_del": 89,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 512,
    "KG Spring_theta_del": 0.438420098,
    "KG Spring_pr_1_99_del": 89,
    "KG Spring_ss_del": 118,
    "KG Spring_dss_del": 544,
    "G1 Fall_theta_del": 0.558644236,
    "G1 Fall_pr_1_99_del": 89,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 556,
    "G1 Winter_theta_del": 0.805097709,
    "G1 Winter_pr_1_99_del": 89,
    "G1 Winter_ss_del": 118,
    "G1 Winter_dss_del": 581,
    "G1 Spring_theta_del": 1.010675814,
    "G1 Spring_pr_1_99_del": 89,
    "G1 Spring_ss_del": 118,
    "G1 Spring_dss_del": 601,
    "G2 Fall_theta_del": 1.308044868,
    "G2 Fall_pr_1_99_del": 89,
    "G2 Fall_ss_del": 119,
    "G2 Fall_dss_del": 631,
    "G2 Winter_theta_del": 1.519004091,
    "G2 Winter_pr_1_99_del": 89,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 652,
    "G2 Spring_theta_del": 1.784404249,
    "G2 Spring_pr_1_99_del": 89,
    "G2 Spring_ss_del": 118,
    "G2 Spring_dss_del": 678
  },
  {
    "ID": 459,
    "KG Fall_theta_del": -0.165861377,
    "KG Fall_pr_1_99_del": 89,
    "KG Fall_ss_del": 118,
    "KG Fall_dss_del": 483,
    "KG Winter_theta_del": 0.118854964,
    "KG Winter_pr_1_99_del": 89,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 512,
    "KG Spring_theta_del": 0.441648334,
    "KG Spring_pr_1_99_del": 89,
    "KG Spring_ss_del": 119,
    "KG Spring_dss_del": 544,
    "G1 Fall_theta_del": 0.559043832,
    "G1 Fall_pr_1_99_del": 89,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 556,
    "G1 Winter_theta_del": 0.829781871,
    "G1 Winter_pr_1_99_del": 89,
    "G1 Winter_ss_del": 119,
    "G1 Winter_dss_del": 583,
    "G1 Spring_theta_del": 1.033624477,
    "G1 Spring_pr_1_99_del": 89,
    "G1 Spring_ss_del": 119,
    "G1 Spring_dss_del": 603,
    "G2 Fall_theta_del": 1.322933205,
    "G2 Fall_pr_1_99_del": 89,
    "G2 Fall_ss_del": 119,
    "G2 Fall_dss_del": 632,
    "G2 Winter_theta_del": 1.525745084,
    "G2 Winter_pr_1_99_del": 89,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 653,
    "G2 Spring_theta_del": 1.795554247,
    "G2 Spring_pr_1_99_del": 89,
    "G2 Spring_ss_del": 118,
    "G2 Spring_dss_del": 680
  },
  {
    "ID": 21,
    "KG Fall_theta_del": -0.155133232,
    "KG Fall_pr_1_99_del": 90,
    "KG Fall_ss_del": 119,
    "KG Fall_dss_del": 484,
    "KG Winter_theta_del": 0.124142417,
    "KG Winter_pr_1_99_del": 90,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 512,
    "KG Spring_theta_del": 0.453964409,
    "KG Spring_pr_1_99_del": 90,
    "KG Spring_ss_del": 119,
    "KG Spring_dss_del": 545,
    "G1 Fall_theta_del": 0.559115085,
    "G1 Fall_pr_1_99_del": 90,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 556,
    "G1 Winter_theta_del": 0.829993805,
    "G1 Winter_pr_1_99_del": 90,
    "G1 Winter_ss_del": 119,
    "G1 Winter_dss_del": 583,
    "G1 Spring_theta_del": 1.03470232,
    "G1 Spring_pr_1_99_del": 90,
    "G1 Spring_ss_del": 119,
    "G1 Spring_dss_del": 603,
    "G2 Fall_theta_del": 1.32426611,
    "G2 Fall_pr_1_99_del": 90,
    "G2 Fall_ss_del": 119,
    "G2 Fall_dss_del": 632,
    "G2 Winter_theta_del": 1.528829073,
    "G2 Winter_pr_1_99_del": 90,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 653,
    "G2 Spring_theta_del": 1.804976705,
    "G2 Spring_pr_1_99_del": 90,
    "G2 Spring_ss_del": 118,
    "G2 Spring_dss_del": 680
  },
  {
    "ID": 166,
    "KG Fall_theta_del": -0.153072694,
    "KG Fall_pr_1_99_del": 90,
    "KG Fall_ss_del": 119,
    "KG Fall_dss_del": 485,
    "KG Winter_theta_del": 0.137707775,
    "KG Winter_pr_1_99_del": 90,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 514,
    "KG Spring_theta_del": 0.459001185,
    "KG Spring_pr_1_99_del": 90,
    "KG Spring_ss_del": 119,
    "KG Spring_dss_del": 546,
    "G1 Fall_theta_del": 0.560938486,
    "G1 Fall_pr_1_99_del": 90,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 556,
    "G1 Winter_theta_del": 0.832160098,
    "G1 Winter_pr_1_99_del": 90,
    "G1 Winter_ss_del": 119,
    "G1 Winter_dss_del": 583,
    "G1 Spring_theta_del": 1.041491348,
    "G1 Spring_pr_1_99_del": 90,
    "G1 Spring_ss_del": 119,
    "G1 Spring_dss_del": 604,
    "G2 Fall_theta_del": 1.329414498,
    "G2 Fall_pr_1_99_del": 90,
    "G2 Fall_ss_del": 119,
    "G2 Fall_dss_del": 633,
    "G2 Winter_theta_del": 1.533701865,
    "G2 Winter_pr_1_99_del": 90,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 653,
    "G2 Spring_theta_del": 1.812591372,
    "G2 Spring_pr_1_99_del": 90,
    "G2 Spring_ss_del": 118,
    "G2 Spring_dss_del": 681
  },
  {
    "ID": 400,
    "KG Fall_theta_del": -0.136346745,
    "KG Fall_pr_1_99_del": 90,
    "KG Fall_ss_del": 119,
    "KG Fall_dss_del": 486,
    "KG Winter_theta_del": 0.141250131,
    "KG Winter_pr_1_99_del": 90,
    "KG Winter_ss_del": 118,
    "KG Winter_dss_del": 514,
    "KG Spring_theta_del": 0.464684671,
    "KG Spring_pr_1_99_del": 90,
    "KG Spring_ss_del": 119,
    "KG Spring_dss_del": 546,
    "G1 Fall_theta_del": 0.568612393,
    "G1 Fall_pr_1_99_del": 90,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 557,
    "G1 Winter_theta_del": 0.850353009,
    "G1 Winter_pr_1_99_del": 90,
    "G1 Winter_ss_del": 119,
    "G1 Winter_dss_del": 585,
    "G1 Spring_theta_del": 1.051979451,
    "G1 Spring_pr_1_99_del": 90,
    "G1 Spring_ss_del": 119,
    "G1 Spring_dss_del": 605,
    "G2 Fall_theta_del": 1.346964458,
    "G2 Fall_pr_1_99_del": 90,
    "G2 Fall_ss_del": 120,
    "G2 Fall_dss_del": 635,
    "G2 Winter_theta_del": 1.539711711,
    "G2 Winter_pr_1_99_del": 90,
    "G2 Winter_ss_del": 118,
    "G2 Winter_dss_del": 654,
    "G2 Spring_theta_del": 1.825603326,
    "G2 Spring_pr_1_99_del": 90,
    "G2 Spring_ss_del": 118,
    "G2 Spring_dss_del": 683
  },
  {
    "ID": 415,
    "KG Fall_theta_del": -0.135602786,
    "KG Fall_pr_1_99_del": 90,
    "KG Fall_ss_del": 119,
    "KG Fall_dss_del": 486,
    "KG Winter_theta_del": 0.14412301,
    "KG Winter_pr_1_99_del": 90,
    "KG Winter_ss_del": 119,
    "KG Winter_dss_del": 514,
    "KG Spring_theta_del": 0.467042875,
    "KG Spring_pr_1_99_del": 90,
    "KG Spring_ss_del": 119,
    "KG Spring_dss_del": 547,
    "G1 Fall_theta_del": 0.582673861,
    "G1 Fall_pr_1_99_del": 90,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 558,
    "G1 Winter_theta_del": 0.865711502,
    "G1 Winter_pr_1_99_del": 90,
    "G1 Winter_ss_del": 119,
    "G1 Winter_dss_del": 587,
    "G1 Spring_theta_del": 1.069728625,
    "G1 Spring_pr_1_99_del": 90,
    "G1 Spring_ss_del": 119,
    "G1 Spring_dss_del": 607,
    "G2 Fall_theta_del": 1.35343969,
    "G2 Fall_pr_1_99_del": 90,
    "G2 Fall_ss_del": 120,
    "G2 Fall_dss_del": 635,
    "G2 Winter_theta_del": 1.545506165,
    "G2 Winter_pr_1_99_del": 90,
    "G2 Winter_ss_del": 119,
    "G2 Winter_dss_del": 655,
    "G2 Spring_theta_del": 1.842594458,
    "G2 Spring_pr_1_99_del": 90,
    "G2 Spring_ss_del": 119,
    "G2 Spring_dss_del": 684
  },
  {
    "ID": 444,
    "KG Fall_theta_del": -0.134687525,
    "KG Fall_pr_1_99_del": 90,
    "KG Fall_ss_del": 119,
    "KG Fall_dss_del": 487,
    "KG Winter_theta_del": 0.149471893,
    "KG Winter_pr_1_99_del": 90,
    "KG Winter_ss_del": 119,
    "KG Winter_dss_del": 515,
    "KG Spring_theta_del": 0.467065683,
    "KG Spring_pr_1_99_del": 90,
    "KG Spring_ss_del": 119,
    "KG Spring_dss_del": 547,
    "G1 Fall_theta_del": 0.583855814,
    "G1 Fall_pr_1_99_del": 90,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 558,
    "G1 Winter_theta_del": 0.870214718,
    "G1 Winter_pr_1_99_del": 90,
    "G1 Winter_ss_del": 120,
    "G1 Winter_dss_del": 587,
    "G1 Spring_theta_del": 1.075542282,
    "G1 Spring_pr_1_99_del": 90,
    "G1 Spring_ss_del": 119,
    "G1 Spring_dss_del": 608,
    "G2 Fall_theta_del": 1.355054443,
    "G2 Fall_pr_1_99_del": 90,
    "G2 Fall_ss_del": 120,
    "G2 Fall_dss_del": 636,
    "G2 Winter_theta_del": 1.549464638,
    "G2 Winter_pr_1_99_del": 90,
    "G2 Winter_ss_del": 119,
    "G2 Winter_dss_del": 655,
    "G2 Spring_theta_del": 1.846261845,
    "G2 Spring_pr_1_99_del": 90,
    "G2 Spring_ss_del": 119,
    "G2 Spring_dss_del": 685
  },
  {
    "ID": 61,
    "KG Fall_theta_del": -0.13266516,
    "KG Fall_pr_1_99_del": 91,
    "KG Fall_ss_del": 119,
    "KG Fall_dss_del": 487,
    "KG Winter_theta_del": 0.161166928,
    "KG Winter_pr_1_99_del": 91,
    "KG Winter_ss_del": 119,
    "KG Winter_dss_del": 516,
    "KG Spring_theta_del": 0.479959217,
    "KG Spring_pr_1_99_del": 91,
    "KG Spring_ss_del": 119,
    "KG Spring_dss_del": 548,
    "G1 Fall_theta_del": 0.594136096,
    "G1 Fall_pr_1_99_del": 91,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 559,
    "G1 Winter_theta_del": 0.873520551,
    "G1 Winter_pr_1_99_del": 91,
    "G1 Winter_ss_del": 120,
    "G1 Winter_dss_del": 587,
    "G1 Spring_theta_del": 1.091753419,
    "G1 Spring_pr_1_99_del": 91,
    "G1 Spring_ss_del": 120,
    "G1 Spring_dss_del": 609,
    "G2 Fall_theta_del": 1.358722311,
    "G2 Fall_pr_1_99_del": 91,
    "G2 Fall_ss_del": 120,
    "G2 Fall_dss_del": 636,
    "G2 Winter_theta_del": 1.555763033,
    "G2 Winter_pr_1_99_del": 91,
    "G2 Winter_ss_del": 119,
    "G2 Winter_dss_del": 656,
    "G2 Spring_theta_del": 1.84746943,
    "G2 Spring_pr_1_99_del": 91,
    "G2 Spring_ss_del": 119,
    "G2 Spring_dss_del": 685
  },
  {
    "ID": 251,
    "KG Fall_theta_del": -0.1268242,
    "KG Fall_pr_1_99_del": 91,
    "KG Fall_ss_del": 119,
    "KG Fall_dss_del": 487,
    "KG Winter_theta_del": 0.166157647,
    "KG Winter_pr_1_99_del": 91,
    "KG Winter_ss_del": 119,
    "KG Winter_dss_del": 517,
    "KG Spring_theta_del": 0.4803212,
    "KG Spring_pr_1_99_del": 91,
    "KG Spring_ss_del": 119,
    "KG Spring_dss_del": 548,
    "G1 Fall_theta_del": 0.597939336,
    "G1 Fall_pr_1_99_del": 91,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 560,
    "G1 Winter_theta_del": 0.929753465,
    "G1 Winter_pr_1_99_del": 91,
    "G1 Winter_ss_del": 121,
    "G1 Winter_dss_del": 593,
    "G1 Spring_theta_del": 1.091889474,
    "G1 Spring_pr_1_99_del": 91,
    "G1 Spring_ss_del": 120,
    "G1 Spring_dss_del": 609,
    "G2 Fall_theta_del": 1.365469676,
    "G2 Fall_pr_1_99_del": 91,
    "G2 Fall_ss_del": 120,
    "G2 Fall_dss_del": 637,
    "G2 Winter_theta_del": 1.56656094,
    "G2 Winter_pr_1_99_del": 91,
    "G2 Winter_ss_del": 119,
    "G2 Winter_dss_del": 657,
    "G2 Spring_theta_del": 1.884934602,
    "G2 Spring_pr_1_99_del": 91,
    "G2 Spring_ss_del": 119,
    "G2 Spring_dss_del": 688
  },
  {
    "ID": 300,
    "KG Fall_theta_del": -0.111448784,
    "KG Fall_pr_1_99_del": 91,
    "KG Fall_ss_del": 119,
    "KG Fall_dss_del": 489,
    "KG Winter_theta_del": 0.16690536,
    "KG Winter_pr_1_99_del": 91,
    "KG Winter_ss_del": 119,
    "KG Winter_dss_del": 517,
    "KG Spring_theta_del": 0.493560042,
    "KG Spring_pr_1_99_del": 91,
    "KG Spring_ss_del": 119,
    "KG Spring_dss_del": 549,
    "G1 Fall_theta_del": 0.599187633,
    "G1 Fall_pr_1_99_del": 91,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 560,
    "G1 Winter_theta_del": 0.930044269,
    "G1 Winter_pr_1_99_del": 91,
    "G1 Winter_ss_del": 121,
    "G1 Winter_dss_del": 593,
    "G1 Spring_theta_del": 1.102296826,
    "G1 Spring_pr_1_99_del": 91,
    "G1 Spring_ss_del": 120,
    "G1 Spring_dss_del": 610,
    "G2 Fall_theta_del": 1.371164558,
    "G2 Fall_pr_1_99_del": 91,
    "G2 Fall_ss_del": 120,
    "G2 Fall_dss_del": 637,
    "G2 Winter_theta_del": 1.603807539,
    "G2 Winter_pr_1_99_del": 91,
    "G2 Winter_ss_del": 120,
    "G2 Winter_dss_del": 660,
    "G2 Spring_theta_del": 1.918120159,
    "G2 Spring_pr_1_99_del": 91,
    "G2 Spring_ss_del": 120,
    "G2 Spring_dss_del": 692
  },
  {
    "ID": 316,
    "KG Fall_theta_del": -0.097067763,
    "KG Fall_pr_1_99_del": 91,
    "KG Fall_ss_del": 120,
    "KG Fall_dss_del": 490,
    "KG Winter_theta_del": 0.181165343,
    "KG Winter_pr_1_99_del": 91,
    "KG Winter_ss_del": 119,
    "KG Winter_dss_del": 518,
    "KG Spring_theta_del": 0.495921108,
    "KG Spring_pr_1_99_del": 91,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 550,
    "G1 Fall_theta_del": 0.60585061,
    "G1 Fall_pr_1_99_del": 91,
    "G1 Fall_ss_del": 118,
    "G1 Fall_dss_del": 561,
    "G1 Winter_theta_del": 0.940201642,
    "G1 Winter_pr_1_99_del": 91,
    "G1 Winter_ss_del": 121,
    "G1 Winter_dss_del": 594,
    "G1 Spring_theta_del": 1.107583149,
    "G1 Spring_pr_1_99_del": 91,
    "G1 Spring_ss_del": 120,
    "G1 Spring_dss_del": 611,
    "G2 Fall_theta_del": 1.386304062,
    "G2 Fall_pr_1_99_del": 91,
    "G2 Fall_ss_del": 120,
    "G2 Fall_dss_del": 639,
    "G2 Winter_theta_del": 1.623489486,
    "G2 Winter_pr_1_99_del": 91,
    "G2 Winter_ss_del": 120,
    "G2 Winter_dss_del": 662,
    "G2 Spring_theta_del": 1.946312344,
    "G2 Spring_pr_1_99_del": 91,
    "G2 Spring_ss_del": 121,
    "G2 Spring_dss_del": 695
  },
  {
    "ID": 410,
    "KG Fall_theta_del": -0.057349158,
    "KG Fall_pr_1_99_del": 91,
    "KG Fall_ss_del": 120,
    "KG Fall_dss_del": 494,
    "KG Winter_theta_del": 0.181675602,
    "KG Winter_pr_1_99_del": 91,
    "KG Winter_ss_del": 119,
    "KG Winter_dss_del": 518,
    "KG Spring_theta_del": 0.512548775,
    "KG Spring_pr_1_99_del": 91,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 551,
    "G1 Fall_theta_del": 0.620620214,
    "G1 Fall_pr_1_99_del": 91,
    "G1 Fall_ss_del": 119,
    "G1 Fall_dss_del": 562,
    "G1 Winter_theta_del": 0.947015163,
    "G1 Winter_pr_1_99_del": 91,
    "G1 Winter_ss_del": 121,
    "G1 Winter_dss_del": 595,
    "G1 Spring_theta_del": 1.109180473,
    "G1 Spring_pr_1_99_del": 91,
    "G1 Spring_ss_del": 120,
    "G1 Spring_dss_del": 611,
    "G2 Fall_theta_del": 1.38875358,
    "G2 Fall_pr_1_99_del": 91,
    "G2 Fall_ss_del": 120,
    "G2 Fall_dss_del": 639,
    "G2 Winter_theta_del": 1.632760002,
    "G2 Winter_pr_1_99_del": 91,
    "G2 Winter_ss_del": 120,
    "G2 Winter_dss_del": 663,
    "G2 Spring_theta_del": 1.949296179,
    "G2 Spring_pr_1_99_del": 91,
    "G2 Spring_ss_del": 121,
    "G2 Spring_dss_del": 695
  },
  {
    "ID": 47,
    "KG Fall_theta_del": -0.046010163,
    "KG Fall_pr_1_99_del": 92,
    "KG Fall_ss_del": 121,
    "KG Fall_dss_del": 495,
    "KG Winter_theta_del": 0.18568532,
    "KG Winter_pr_1_99_del": 92,
    "KG Winter_ss_del": 119,
    "KG Winter_dss_del": 519,
    "KG Spring_theta_del": 0.522036055,
    "KG Spring_pr_1_99_del": 92,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 552,
    "G1 Fall_theta_del": 0.631990661,
    "G1 Fall_pr_1_99_del": 92,
    "G1 Fall_ss_del": 119,
    "G1 Fall_dss_del": 563,
    "G1 Winter_theta_del": 0.962832295,
    "G1 Winter_pr_1_99_del": 92,
    "G1 Winter_ss_del": 121,
    "G1 Winter_dss_del": 596,
    "G1 Spring_theta_del": 1.124563927,
    "G1 Spring_pr_1_99_del": 92,
    "G1 Spring_ss_del": 120,
    "G1 Spring_dss_del": 612,
    "G2 Fall_theta_del": 1.390873817,
    "G2 Fall_pr_1_99_del": 92,
    "G2 Fall_ss_del": 120,
    "G2 Fall_dss_del": 639,
    "G2 Winter_theta_del": 1.645639493,
    "G2 Winter_pr_1_99_del": 92,
    "G2 Winter_ss_del": 120,
    "G2 Winter_dss_del": 665,
    "G2 Spring_theta_del": 1.955703937,
    "G2 Spring_pr_1_99_del": 92,
    "G2 Spring_ss_del": 121,
    "G2 Spring_dss_del": 696
  },
  {
    "ID": 56,
    "KG Fall_theta_del": -0.04480376,
    "KG Fall_pr_1_99_del": 92,
    "KG Fall_ss_del": 121,
    "KG Fall_dss_del": 496,
    "KG Winter_theta_del": 0.201612354,
    "KG Winter_pr_1_99_del": 92,
    "KG Winter_ss_del": 120,
    "KG Winter_dss_del": 520,
    "KG Spring_theta_del": 0.523268111,
    "KG Spring_pr_1_99_del": 92,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 552,
    "G1 Fall_theta_del": 0.636222088,
    "G1 Fall_pr_1_99_del": 92,
    "G1 Fall_ss_del": 119,
    "G1 Fall_dss_del": 564,
    "G1 Winter_theta_del": 0.973791207,
    "G1 Winter_pr_1_99_del": 92,
    "G1 Winter_ss_del": 122,
    "G1 Winter_dss_del": 597,
    "G1 Spring_theta_del": 1.126773257,
    "G1 Spring_pr_1_99_del": 92,
    "G1 Spring_ss_del": 120,
    "G1 Spring_dss_del": 613,
    "G2 Fall_theta_del": 1.402569591,
    "G2 Fall_pr_1_99_del": 92,
    "G2 Fall_ss_del": 121,
    "G2 Fall_dss_del": 640,
    "G2 Winter_theta_del": 1.648739854,
    "G2 Winter_pr_1_99_del": 92,
    "G2 Winter_ss_del": 120,
    "G2 Winter_dss_del": 665,
    "G2 Spring_theta_del": 1.955709466,
    "G2 Spring_pr_1_99_del": 92,
    "G2 Spring_ss_del": 121,
    "G2 Spring_dss_del": 696
  },
  {
    "ID": 159,
    "KG Fall_theta_del": -0.029312945,
    "KG Fall_pr_1_99_del": 92,
    "KG Fall_ss_del": 121,
    "KG Fall_dss_del": 497,
    "KG Winter_theta_del": 0.214602959,
    "KG Winter_pr_1_99_del": 92,
    "KG Winter_ss_del": 120,
    "KG Winter_dss_del": 521,
    "KG Spring_theta_del": 0.532248793,
    "KG Spring_pr_1_99_del": 92,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 553,
    "G1 Fall_theta_del": 0.636568789,
    "G1 Fall_pr_1_99_del": 92,
    "G1 Fall_ss_del": 119,
    "G1 Fall_dss_del": 564,
    "G1 Winter_theta_del": 0.974202877,
    "G1 Winter_pr_1_99_del": 92,
    "G1 Winter_ss_del": 122,
    "G1 Winter_dss_del": 597,
    "G1 Spring_theta_del": 1.13059122,
    "G1 Spring_pr_1_99_del": 92,
    "G1 Spring_ss_del": 120,
    "G1 Spring_dss_del": 613,
    "G2 Fall_theta_del": 1.422965969,
    "G2 Fall_pr_1_99_del": 92,
    "G2 Fall_ss_del": 121,
    "G2 Fall_dss_del": 642,
    "G2 Winter_theta_del": 1.654027596,
    "G2 Winter_pr_1_99_del": 92,
    "G2 Winter_ss_del": 121,
    "G2 Winter_dss_del": 665,
    "G2 Spring_theta_del": 1.986322887,
    "G2 Spring_pr_1_99_del": 92,
    "G2 Spring_ss_del": 121,
    "G2 Spring_dss_del": 699
  },
  {
    "ID": 212,
    "KG Fall_theta_del": -0.01766149,
    "KG Fall_pr_1_99_del": 92,
    "KG Fall_ss_del": 121,
    "KG Fall_dss_del": 498,
    "KG Winter_theta_del": 0.215730194,
    "KG Winter_pr_1_99_del": 92,
    "KG Winter_ss_del": 120,
    "KG Winter_dss_del": 522,
    "KG Spring_theta_del": 0.53430192,
    "KG Spring_pr_1_99_del": 92,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 553,
    "G1 Fall_theta_del": 0.646615148,
    "G1 Fall_pr_1_99_del": 92,
    "G1 Fall_ss_del": 119,
    "G1 Fall_dss_del": 565,
    "G1 Winter_theta_del": 0.983715158,
    "G1 Winter_pr_1_99_del": 92,
    "G1 Winter_ss_del": 122,
    "G1 Winter_dss_del": 598,
    "G1 Spring_theta_del": 1.131911852,
    "G1 Spring_pr_1_99_del": 92,
    "G1 Spring_ss_del": 121,
    "G1 Spring_dss_del": 613,
    "G2 Fall_theta_del": 1.424696584,
    "G2 Fall_pr_1_99_del": 92,
    "G2 Fall_ss_del": 121,
    "G2 Fall_dss_del": 642,
    "G2 Winter_theta_del": 1.658906757,
    "G2 Winter_pr_1_99_del": 92,
    "G2 Winter_ss_del": 121,
    "G2 Winter_dss_del": 666,
    "G2 Spring_theta_del": 1.996559692,
    "G2 Spring_pr_1_99_del": 92,
    "G2 Spring_ss_del": 122,
    "G2 Spring_dss_del": 700
  },
  {
    "ID": 295,
    "KG Fall_theta_del": -0.009501061,
    "KG Fall_pr_1_99_del": 92,
    "KG Fall_ss_del": 121,
    "KG Fall_dss_del": 499,
    "KG Winter_theta_del": 0.21734349,
    "KG Winter_pr_1_99_del": 92,
    "KG Winter_ss_del": 120,
    "KG Winter_dss_del": 522,
    "KG Spring_theta_del": 0.537161097,
    "KG Spring_pr_1_99_del": 92,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 554,
    "G1 Fall_theta_del": 0.65178573,
    "G1 Fall_pr_1_99_del": 92,
    "G1 Fall_ss_del": 119,
    "G1 Fall_dss_del": 565,
    "G1 Winter_theta_del": 0.99772803,
    "G1 Winter_pr_1_99_del": 92,
    "G1 Winter_ss_del": 122,
    "G1 Winter_dss_del": 600,
    "G1 Spring_theta_del": 1.16208927,
    "G1 Spring_pr_1_99_del": 92,
    "G1 Spring_ss_del": 121,
    "G1 Spring_dss_del": 616,
    "G2 Fall_theta_del": 1.426343632,
    "G2 Fall_pr_1_99_del": 92,
    "G2 Fall_ss_del": 121,
    "G2 Fall_dss_del": 643,
    "G2 Winter_theta_del": 1.661026493,
    "G2 Winter_pr_1_99_del": 92,
    "G2 Winter_ss_del": 121,
    "G2 Winter_dss_del": 666,
    "G2 Spring_theta_del": 2.024439322,
    "G2 Spring_pr_1_99_del": 92,
    "G2 Spring_ss_del": 122,
    "G2 Spring_dss_del": 702
  },
  {
    "ID": 100,
    "KG Fall_theta_del": -0.008224121,
    "KG Fall_pr_1_99_del": 93,
    "KG Fall_ss_del": 121,
    "KG Fall_dss_del": 499,
    "KG Winter_theta_del": 0.218934785,
    "KG Winter_pr_1_99_del": 93,
    "KG Winter_ss_del": 120,
    "KG Winter_dss_del": 522,
    "KG Spring_theta_del": 0.539860661,
    "KG Spring_pr_1_99_del": 93,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 554,
    "G1 Fall_theta_del": 0.656624105,
    "G1 Fall_pr_1_99_del": 93,
    "G1 Fall_ss_del": 119,
    "G1 Fall_dss_del": 566,
    "G1 Winter_theta_del": 1.001725874,
    "G1 Winter_pr_1_99_del": 93,
    "G1 Winter_ss_del": 122,
    "G1 Winter_dss_del": 600,
    "G1 Spring_theta_del": 1.163214686,
    "G1 Spring_pr_1_99_del": 93,
    "G1 Spring_ss_del": 121,
    "G1 Spring_dss_del": 616,
    "G2 Fall_theta_del": 1.429956487,
    "G2 Fall_pr_1_99_del": 93,
    "G2 Fall_ss_del": 121,
    "G2 Fall_dss_del": 643,
    "G2 Winter_theta_del": 1.666464699,
    "G2 Winter_pr_1_99_del": 93,
    "G2 Winter_ss_del": 121,
    "G2 Winter_dss_del": 667,
    "G2 Spring_theta_del": 2.025040371,
    "G2 Spring_pr_1_99_del": 93,
    "G2 Spring_ss_del": 122,
    "G2 Spring_dss_del": 703
  },
  {
    "ID": 261,
    "KG Fall_theta_del": 0.004283744,
    "KG Fall_pr_1_99_del": 93,
    "KG Fall_ss_del": 122,
    "KG Fall_dss_del": 500,
    "KG Winter_theta_del": 0.228973117,
    "KG Winter_pr_1_99_del": 93,
    "KG Winter_ss_del": 120,
    "KG Winter_dss_del": 523,
    "KG Spring_theta_del": 0.540079277,
    "KG Spring_pr_1_99_del": 93,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 554,
    "G1 Fall_theta_del": 0.66789019,
    "G1 Fall_pr_1_99_del": 93,
    "G1 Fall_ss_del": 120,
    "G1 Fall_dss_del": 567,
    "G1 Winter_theta_del": 1.004203563,
    "G1 Winter_pr_1_99_del": 93,
    "G1 Winter_ss_del": 122,
    "G1 Winter_dss_del": 600,
    "G1 Spring_theta_del": 1.208477236,
    "G1 Spring_pr_1_99_del": 93,
    "G1 Spring_ss_del": 122,
    "G1 Spring_dss_del": 621,
    "G2 Fall_theta_del": 1.437201844,
    "G2 Fall_pr_1_99_del": 93,
    "G2 Fall_ss_del": 121,
    "G2 Fall_dss_del": 644,
    "G2 Winter_theta_del": 1.677773735,
    "G2 Winter_pr_1_99_del": 93,
    "G2 Winter_ss_del": 121,
    "G2 Winter_dss_del": 668,
    "G2 Spring_theta_del": 2.06972022,
    "G2 Spring_pr_1_99_del": 93,
    "G2 Spring_ss_del": 123,
    "G2 Spring_dss_del": 707
  },
  {
    "ID": 264,
    "KG Fall_theta_del": 0.018442991,
    "KG Fall_pr_1_99_del": 93,
    "KG Fall_ss_del": 122,
    "KG Fall_dss_del": 502,
    "KG Winter_theta_del": 0.289466182,
    "KG Winter_pr_1_99_del": 93,
    "KG Winter_ss_del": 121,
    "KG Winter_dss_del": 529,
    "KG Spring_theta_del": 0.547815689,
    "KG Spring_pr_1_99_del": 93,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 555,
    "G1 Fall_theta_del": 0.691423297,
    "G1 Fall_pr_1_99_del": 93,
    "G1 Fall_ss_del": 120,
    "G1 Fall_dss_del": 569,
    "G1 Winter_theta_del": 1.01688711,
    "G1 Winter_pr_1_99_del": 93,
    "G1 Winter_ss_del": 122,
    "G1 Winter_dss_del": 602,
    "G1 Spring_theta_del": 1.222987474,
    "G1 Spring_pr_1_99_del": 93,
    "G1 Spring_ss_del": 122,
    "G1 Spring_dss_del": 622,
    "G2 Fall_theta_del": 1.439720281,
    "G2 Fall_pr_1_99_del": 93,
    "G2 Fall_ss_del": 121,
    "G2 Fall_dss_del": 644,
    "G2 Winter_theta_del": 1.682509471,
    "G2 Winter_pr_1_99_del": 93,
    "G2 Winter_ss_del": 121,
    "G2 Winter_dss_del": 668,
    "G2 Spring_theta_del": 2.078307961,
    "G2 Spring_pr_1_99_del": 93,
    "G2 Spring_ss_del": 123,
    "G2 Spring_dss_del": 708
  },
  {
    "ID": 328,
    "KG Fall_theta_del": 0.022382578,
    "KG Fall_pr_1_99_del": 93,
    "KG Fall_ss_del": 122,
    "KG Fall_dss_del": 502,
    "KG Winter_theta_del": 0.291145333,
    "KG Winter_pr_1_99_del": 93,
    "KG Winter_ss_del": 121,
    "KG Winter_dss_del": 529,
    "KG Spring_theta_del": 0.548354438,
    "KG Spring_pr_1_99_del": 93,
    "KG Spring_ss_del": 120,
    "KG Spring_dss_del": 555,
    "G1 Fall_theta_del": 0.697549308,
    "G1 Fall_pr_1_99_del": 93,
    "G1 Fall_ss_del": 120,
    "G1 Fall_dss_del": 570,
    "G1 Winter_theta_del": 1.019091813,
    "G1 Winter_pr_1_99_del": 93,
    "G1 Winter_ss_del": 123,
    "G1 Winter_dss_del": 602,
    "G1 Spring_theta_del": 1.223111676,
    "G1 Spring_pr_1_99_del": 93,
    "G1 Spring_ss_del": 122,
    "G1 Spring_dss_del": 622,
    "G2 Fall_theta_del": 1.443592883,
    "G2 Fall_pr_1_99_del": 93,
    "G2 Fall_ss_del": 121,
    "G2 Fall_dss_del": 644,
    "G2 Winter_theta_del": 1.693275377,
    "G2 Winter_pr_1_99_del": 93,
    "G2 Winter_ss_del": 121,
    "G2 Winter_dss_del": 669,
    "G2 Spring_theta_del": 2.090532317,
    "G2 Spring_pr_1_99_del": 93,
    "G2 Spring_ss_del": 123,
    "G2 Spring_dss_del": 709
  },
  {
    "ID": 486,
    "KG Fall_theta_del": 0.026251472,
    "KG Fall_pr_1_99_del": 93,
    "KG Fall_ss_del": 122,
    "KG Fall_dss_del": 503,
    "KG Winter_theta_del": 0.302489303,
    "KG Winter_pr_1_99_del": 93,
    "KG Winter_ss_del": 121,
    "KG Winter_dss_del": 530,
    "KG Spring_theta_del": 0.563081299,
    "KG Spring_pr_1_99_del": 93,
    "KG Spring_ss_del": 121,
    "KG Spring_dss_del": 556,
    "G1 Fall_theta_del": 0.711642345,
    "G1 Fall_pr_1_99_del": 93,
    "G1 Fall_ss_del": 121,
    "G1 Fall_dss_del": 571,
    "G1 Winter_theta_del": 1.027730258,
    "G1 Winter_pr_1_99_del": 93,
    "G1 Winter_ss_del": 123,
    "G1 Winter_dss_del": 603,
    "G1 Spring_theta_del": 1.249091755,
    "G1 Spring_pr_1_99_del": 93,
    "G1 Spring_ss_del": 123,
    "G1 Spring_dss_del": 625,
    "G2 Fall_theta_del": 1.453423081,
    "G2 Fall_pr_1_99_del": 93,
    "G2 Fall_ss_del": 122,
    "G2 Fall_dss_del": 645,
    "G2 Winter_theta_del": 1.693804551,
    "G2 Winter_pr_1_99_del": 93,
    "G2 Winter_ss_del": 121,
    "G2 Winter_dss_del": 669,
    "G2 Spring_theta_del": 2.092986633,
    "G2 Spring_pr_1_99_del": 93,
    "G2 Spring_ss_del": 123,
    "G2 Spring_dss_del": 709
  },
  {
    "ID": 22,
    "KG Fall_theta_del": 0.033058363,
    "KG Fall_pr_1_99_del": 94,
    "KG Fall_ss_del": 122,
    "KG Fall_dss_del": 503,
    "KG Winter_theta_del": 0.323143143,
    "KG Winter_pr_1_99_del": 94,
    "KG Winter_ss_del": 122,
    "KG Winter_dss_del": 532,
    "KG Spring_theta_del": 0.594365767,
    "KG Spring_pr_1_99_del": 94,
    "KG Spring_ss_del": 121,
    "KG Spring_dss_del": 559,
    "G1 Fall_theta_del": 0.728141902,
    "G1 Fall_pr_1_99_del": 94,
    "G1 Fall_ss_del": 121,
    "G1 Fall_dss_del": 573,
    "G1 Winter_theta_del": 1.033979438,
    "G1 Winter_pr_1_99_del": 94,
    "G1 Winter_ss_del": 123,
    "G1 Winter_dss_del": 603,
    "G1 Spring_theta_del": 1.2620431,
    "G1 Spring_pr_1_99_del": 94,
    "G1 Spring_ss_del": 123,
    "G1 Spring_dss_del": 626,
    "G2 Fall_theta_del": 1.468755354,
    "G2 Fall_pr_1_99_del": 94,
    "G2 Fall_ss_del": 122,
    "G2 Fall_dss_del": 647,
    "G2 Winter_theta_del": 1.705063637,
    "G2 Winter_pr_1_99_del": 94,
    "G2 Winter_ss_del": 122,
    "G2 Winter_dss_del": 671,
    "G2 Spring_theta_del": 2.106976557,
    "G2 Spring_pr_1_99_del": 94,
    "G2 Spring_ss_del": 124,
    "G2 Spring_dss_del": 711
  },
  {
    "ID": 71,
    "KG Fall_theta_del": 0.037113335,
    "KG Fall_pr_1_99_del": 94,
    "KG Fall_ss_del": 122,
    "KG Fall_dss_del": 504,
    "KG Winter_theta_del": 0.324546511,
    "KG Winter_pr_1_99_del": 94,
    "KG Winter_ss_del": 122,
    "KG Winter_dss_del": 532,
    "KG Spring_theta_del": 0.597091923,
    "KG Spring_pr_1_99_del": 94,
    "KG Spring_ss_del": 121,
    "KG Spring_dss_del": 560,
    "G1 Fall_theta_del": 0.74156211,
    "G1 Fall_pr_1_99_del": 94,
    "G1 Fall_ss_del": 121,
    "G1 Fall_dss_del": 574,
    "G1 Winter_theta_del": 1.043555327,
    "G1 Winter_pr_1_99_del": 94,
    "G1 Winter_ss_del": 123,
    "G1 Winter_dss_del": 604,
    "G1 Spring_theta_del": 1.271849634,
    "G1 Spring_pr_1_99_del": 94,
    "G1 Spring_ss_del": 123,
    "G1 Spring_dss_del": 627,
    "G2 Fall_theta_del": 1.482046795,
    "G2 Fall_pr_1_99_del": 94,
    "G2 Fall_ss_del": 122,
    "G2 Fall_dss_del": 648,
    "G2 Winter_theta_del": 1.718945864,
    "G2 Winter_pr_1_99_del": 94,
    "G2 Winter_ss_del": 122,
    "G2 Winter_dss_del": 672,
    "G2 Spring_theta_del": 2.112670489,
    "G2 Spring_pr_1_99_del": 94,
    "G2 Spring_ss_del": 124,
    "G2 Spring_dss_del": 711
  },
  {
    "ID": 223,
    "KG Fall_theta_del": 0.044397671,
    "KG Fall_pr_1_99_del": 94,
    "KG Fall_ss_del": 122,
    "KG Fall_dss_del": 504,
    "KG Winter_theta_del": 0.325785709,
    "KG Winter_pr_1_99_del": 94,
    "KG Winter_ss_del": 122,
    "KG Winter_dss_del": 533,
    "KG Spring_theta_del": 0.603083243,
    "KG Spring_pr_1_99_del": 94,
    "KG Spring_ss_del": 122,
    "KG Spring_dss_del": 560,
    "G1 Fall_theta_del": 0.748228572,
    "G1 Fall_pr_1_99_del": 94,
    "G1 Fall_ss_del": 121,
    "G1 Fall_dss_del": 575,
    "G1 Winter_theta_del": 1.047428175,
    "G1 Winter_pr_1_99_del": 94,
    "G1 Winter_ss_del": 123,
    "G1 Winter_dss_del": 605,
    "G1 Spring_theta_del": 1.283113149,
    "G1 Spring_pr_1_99_del": 94,
    "G1 Spring_ss_del": 124,
    "G1 Spring_dss_del": 628,
    "G2 Fall_theta_del": 1.482637549,
    "G2 Fall_pr_1_99_del": 94,
    "G2 Fall_ss_del": 122,
    "G2 Fall_dss_del": 648,
    "G2 Winter_theta_del": 1.719474135,
    "G2 Winter_pr_1_99_del": 94,
    "G2 Winter_ss_del": 122,
    "G2 Winter_dss_del": 672,
    "G2 Spring_theta_del": 2.11302488,
    "G2 Spring_pr_1_99_del": 94,
    "G2 Spring_ss_del": 124,
    "G2 Spring_dss_del": 711
  },
  {
    "ID": 240,
    "KG Fall_theta_del": 0.078367339,
    "KG Fall_pr_1_99_del": 94,
    "KG Fall_ss_del": 123,
    "KG Fall_dss_del": 508,
    "KG Winter_theta_del": 0.346824305,
    "KG Winter_pr_1_99_del": 94,
    "KG Winter_ss_del": 122,
    "KG Winter_dss_del": 535,
    "KG Spring_theta_del": 0.636214202,
    "KG Spring_pr_1_99_del": 94,
    "KG Spring_ss_del": 122,
    "KG Spring_dss_del": 564,
    "G1 Fall_theta_del": 0.753565158,
    "G1 Fall_pr_1_99_del": 94,
    "G1 Fall_ss_del": 121,
    "G1 Fall_dss_del": 575,
    "G1 Winter_theta_del": 1.057091686,
    "G1 Winter_pr_1_99_del": 94,
    "G1 Winter_ss_del": 123,
    "G1 Winter_dss_del": 606,
    "G1 Spring_theta_del": 1.286308987,
    "G1 Spring_pr_1_99_del": 94,
    "G1 Spring_ss_del": 124,
    "G1 Spring_dss_del": 629,
    "G2 Fall_theta_del": 1.492228843,
    "G2 Fall_pr_1_99_del": 94,
    "G2 Fall_ss_del": 122,
    "G2 Fall_dss_del": 649,
    "G2 Winter_theta_del": 1.735093861,
    "G2 Winter_pr_1_99_del": 94,
    "G2 Winter_ss_del": 122,
    "G2 Winter_dss_del": 674,
    "G2 Spring_theta_del": 2.135726139,
    "G2 Spring_pr_1_99_del": 94,
    "G2 Spring_ss_del": 124,
    "G2 Spring_dss_del": 714
  },
  {
    "ID": 254,
    "KG Fall_theta_del": 0.080048414,
    "KG Fall_pr_1_99_del": 94,
    "KG Fall_ss_del": 123,
    "KG Fall_dss_del": 508,
    "KG Winter_theta_del": 0.347529602,
    "KG Winter_pr_1_99_del": 94,
    "KG Winter_ss_del": 122,
    "KG Winter_dss_del": 535,
    "KG Spring_theta_del": 0.669803081,
    "KG Spring_pr_1_99_del": 94,
    "KG Spring_ss_del": 123,
    "KG Spring_dss_del": 567,
    "G1 Fall_theta_del": 0.771050083,
    "G1 Fall_pr_1_99_del": 94,
    "G1 Fall_ss_del": 122,
    "G1 Fall_dss_del": 577,
    "G1 Winter_theta_del": 1.074882106,
    "G1 Winter_pr_1_99_del": 94,
    "G1 Winter_ss_del": 124,
    "G1 Winter_dss_del": 607,
    "G1 Spring_theta_del": 1.29363947,
    "G1 Spring_pr_1_99_del": 94,
    "G1 Spring_ss_del": 124,
    "G1 Spring_dss_del": 629,
    "G2 Fall_theta_del": 1.502991149,
    "G2 Fall_pr_1_99_del": 94,
    "G2 Fall_ss_del": 123,
    "G2 Fall_dss_del": 650,
    "G2 Winter_theta_del": 1.735139304,
    "G2 Winter_pr_1_99_del": 94,
    "G2 Winter_ss_del": 122,
    "G2 Winter_dss_del": 674,
    "G2 Spring_theta_del": 2.135875111,
    "G2 Spring_pr_1_99_del": 94,
    "G2 Spring_ss_del": 124,
    "G2 Spring_dss_del": 714
  },
  {
    "ID": 96,
    "KG Fall_theta_del": 0.101378604,
    "KG Fall_pr_1_99_del": 95,
    "KG Fall_ss_del": 123,
    "KG Fall_dss_del": 510,
    "KG Winter_theta_del": 0.372081929,
    "KG Winter_pr_1_99_del": 95,
    "KG Winter_ss_del": 123,
    "KG Winter_dss_del": 537,
    "KG Spring_theta_del": 0.670783935,
    "KG Spring_pr_1_99_del": 95,
    "KG Spring_ss_del": 123,
    "KG Spring_dss_del": 567,
    "G1 Fall_theta_del": 0.771245971,
    "G1 Fall_pr_1_99_del": 95,
    "G1 Fall_ss_del": 122,
    "G1 Fall_dss_del": 577,
    "G1 Winter_theta_del": 1.079528133,
    "G1 Winter_pr_1_99_del": 95,
    "G1 Winter_ss_del": 124,
    "G1 Winter_dss_del": 608,
    "G1 Spring_theta_del": 1.293989936,
    "G1 Spring_pr_1_99_del": 95,
    "G1 Spring_ss_del": 124,
    "G1 Spring_dss_del": 629,
    "G2 Fall_theta_del": 1.551238875,
    "G2 Fall_pr_1_99_del": 95,
    "G2 Fall_ss_del": 123,
    "G2 Fall_dss_del": 655,
    "G2 Winter_theta_del": 1.755580466,
    "G2 Winter_pr_1_99_del": 95,
    "G2 Winter_ss_del": 122,
    "G2 Winter_dss_del": 676,
    "G2 Spring_theta_del": 2.141779863,
    "G2 Spring_pr_1_99_del": 95,
    "G2 Spring_ss_del": 124,
    "G2 Spring_dss_del": 714
  },
  {
    "ID": 137,
    "KG Fall_theta_del": 0.110722611,
    "KG Fall_pr_1_99_del": 95,
    "KG Fall_ss_del": 124,
    "KG Fall_dss_del": 511,
    "KG Winter_theta_del": 0.411762966,
    "KG Winter_pr_1_99_del": 95,
    "KG Winter_ss_del": 123,
    "KG Winter_dss_del": 541,
    "KG Spring_theta_del": 0.731852624,
    "KG Spring_pr_1_99_del": 95,
    "KG Spring_ss_del": 124,
    "KG Spring_dss_del": 573,
    "G1 Fall_theta_del": 0.776628257,
    "G1 Fall_pr_1_99_del": 95,
    "G1 Fall_ss_del": 122,
    "G1 Fall_dss_del": 578,
    "G1 Winter_theta_del": 1.084030269,
    "G1 Winter_pr_1_99_del": 95,
    "G1 Winter_ss_del": 124,
    "G1 Winter_dss_del": 608,
    "G1 Spring_theta_del": 1.297102425,
    "G1 Spring_pr_1_99_del": 95,
    "G1 Spring_ss_del": 124,
    "G1 Spring_dss_del": 630,
    "G2 Fall_theta_del": 1.566976589,
    "G2 Fall_pr_1_99_del": 95,
    "G2 Fall_ss_del": 124,
    "G2 Fall_dss_del": 657,
    "G2 Winter_theta_del": 1.761248489,
    "G2 Winter_pr_1_99_del": 95,
    "G2 Winter_ss_del": 123,
    "G2 Winter_dss_del": 676,
    "G2 Spring_theta_del": 2.142512261,
    "G2 Spring_pr_1_99_del": 95,
    "G2 Spring_ss_del": 124,
    "G2 Spring_dss_del": 714
  },
  {
    "ID": 174,
    "KG Fall_theta_del": 0.127202978,
    "KG Fall_pr_1_99_del": 95,
    "KG Fall_ss_del": 124,
    "KG Fall_dss_del": 513,
    "KG Winter_theta_del": 0.478672314,
    "KG Winter_pr_1_99_del": 95,
    "KG Winter_ss_del": 125,
    "KG Winter_dss_del": 548,
    "KG Spring_theta_del": 0.747593058,
    "KG Spring_pr_1_99_del": 95,
    "KG Spring_ss_del": 124,
    "KG Spring_dss_del": 575,
    "G1 Fall_theta_del": 0.797703852,
    "G1 Fall_pr_1_99_del": 95,
    "G1 Fall_ss_del": 122,
    "G1 Fall_dss_del": 580,
    "G1 Winter_theta_del": 1.087465501,
    "G1 Winter_pr_1_99_del": 95,
    "G1 Winter_ss_del": 124,
    "G1 Winter_dss_del": 609,
    "G1 Spring_theta_del": 1.317707312,
    "G1 Spring_pr_1_99_del": 95,
    "G1 Spring_ss_del": 124,
    "G1 Spring_dss_del": 632,
    "G2 Fall_theta_del": 1.588916585,
    "G2 Fall_pr_1_99_del": 95,
    "G2 Fall_ss_del": 124,
    "G2 Fall_dss_del": 659,
    "G2 Winter_theta_del": 1.797843651,
    "G2 Winter_pr_1_99_del": 95,
    "G2 Winter_ss_del": 123,
    "G2 Winter_dss_del": 680,
    "G2 Spring_theta_del": 2.158792755,
    "G2 Spring_pr_1_99_del": 95,
    "G2 Spring_ss_del": 125,
    "G2 Spring_dss_del": 716
  },
  {
    "ID": 182,
    "KG Fall_theta_del": 0.134787447,
    "KG Fall_pr_1_99_del": 95,
    "KG Fall_ss_del": 124,
    "KG Fall_dss_del": 513,
    "KG Winter_theta_del": 0.478865464,
    "KG Winter_pr_1_99_del": 95,
    "KG Winter_ss_del": 125,
    "KG Winter_dss_del": 548,
    "KG Spring_theta_del": 0.753749039,
    "KG Spring_pr_1_99_del": 95,
    "KG Spring_ss_del": 124,
    "KG Spring_dss_del": 575,
    "G1 Fall_theta_del": 0.88368805,
    "G1 Fall_pr_1_99_del": 95,
    "G1 Fall_ss_del": 124,
    "G1 Fall_dss_del": 588,
    "G1 Winter_theta_del": 1.101015773,
    "G1 Winter_pr_1_99_del": 95,
    "G1 Winter_ss_del": 124,
    "G1 Winter_dss_del": 610,
    "G1 Spring_theta_del": 1.326181861,
    "G1 Spring_pr_1_99_del": 95,
    "G1 Spring_ss_del": 124,
    "G1 Spring_dss_del": 633,
    "G2 Fall_theta_del": 1.595919485,
    "G2 Fall_pr_1_99_del": 95,
    "G2 Fall_ss_del": 124,
    "G2 Fall_dss_del": 660,
    "G2 Winter_theta_del": 1.799335244,
    "G2 Winter_pr_1_99_del": 95,
    "G2 Winter_ss_del": 123,
    "G2 Winter_dss_del": 680,
    "G2 Spring_theta_del": 2.159341689,
    "G2 Spring_pr_1_99_del": 95,
    "G2 Spring_ss_del": 125,
    "G2 Spring_dss_del": 716
  },
  {
    "ID": 417,
    "KG Fall_theta_del": 0.14258587,
    "KG Fall_pr_1_99_del": 95,
    "KG Fall_ss_del": 124,
    "KG Fall_dss_del": 514,
    "KG Winter_theta_del": 0.491263898,
    "KG Winter_pr_1_99_del": 95,
    "KG Winter_ss_del": 125,
    "KG Winter_dss_del": 549,
    "KG Spring_theta_del": 0.769141181,
    "KG Spring_pr_1_99_del": 95,
    "KG Spring_ss_del": 125,
    "KG Spring_dss_del": 577,
    "G1 Fall_theta_del": 0.908585655,
    "G1 Fall_pr_1_99_del": 95,
    "G1 Fall_ss_del": 124,
    "G1 Fall_dss_del": 591,
    "G1 Winter_theta_del": 1.117205035,
    "G1 Winter_pr_1_99_del": 95,
    "G1 Winter_ss_del": 124,
    "G1 Winter_dss_del": 612,
    "G1 Spring_theta_del": 1.332927561,
    "G1 Spring_pr_1_99_del": 95,
    "G1 Spring_ss_del": 125,
    "G1 Spring_dss_del": 633,
    "G2 Fall_theta_del": 1.607915701,
    "G2 Fall_pr_1_99_del": 95,
    "G2 Fall_ss_del": 125,
    "G2 Fall_dss_del": 661,
    "G2 Winter_theta_del": 1.82718993,
    "G2 Winter_pr_1_99_del": 95,
    "G2 Winter_ss_del": 124,
    "G2 Winter_dss_del": 683,
    "G2 Spring_theta_del": 2.179745208,
    "G2 Spring_pr_1_99_del": 95,
    "G2 Spring_ss_del": 125,
    "G2 Spring_dss_del": 718
  },
  {
    "ID": 49,
    "KG Fall_theta_del": 0.177638641,
    "KG Fall_pr_1_99_del": 96,
    "KG Fall_ss_del": 125,
    "KG Fall_dss_del": 518,
    "KG Winter_theta_del": 0.515672164,
    "KG Winter_pr_1_99_del": 96,
    "KG Winter_ss_del": 125,
    "KG Winter_dss_del": 552,
    "KG Spring_theta_del": 0.778667029,
    "KG Spring_pr_1_99_del": 96,
    "KG Spring_ss_del": 125,
    "KG Spring_dss_del": 578,
    "G1 Fall_theta_del": 0.919072474,
    "G1 Fall_pr_1_99_del": 96,
    "G1 Fall_ss_del": 125,
    "G1 Fall_dss_del": 592,
    "G1 Winter_theta_del": 1.129314673,
    "G1 Winter_pr_1_99_del": 96,
    "G1 Winter_ss_del": 125,
    "G1 Winter_dss_del": 613,
    "G1 Spring_theta_del": 1.354417951,
    "G1 Spring_pr_1_99_del": 96,
    "G1 Spring_ss_del": 125,
    "G1 Spring_dss_del": 635,
    "G2 Fall_theta_del": 1.609145212,
    "G2 Fall_pr_1_99_del": 96,
    "G2 Fall_ss_del": 125,
    "G2 Fall_dss_del": 661,
    "G2 Winter_theta_del": 1.847599781,
    "G2 Winter_pr_1_99_del": 96,
    "G2 Winter_ss_del": 124,
    "G2 Winter_dss_del": 685,
    "G2 Spring_theta_del": 2.183889365,
    "G2 Spring_pr_1_99_del": 96,
    "G2 Spring_ss_del": 125,
    "G2 Spring_dss_del": 718
  },
  {
    "ID": 125,
    "KG Fall_theta_del": 0.181983044,
    "KG Fall_pr_1_99_del": 96,
    "KG Fall_ss_del": 125,
    "KG Fall_dss_del": 518,
    "KG Winter_theta_del": 0.595523434,
    "KG Winter_pr_1_99_del": 96,
    "KG Winter_ss_del": 127,
    "KG Winter_dss_del": 560,
    "KG Spring_theta_del": 0.792396661,
    "KG Spring_pr_1_99_del": 96,
    "KG Spring_ss_del": 125,
    "KG Spring_dss_del": 579,
    "G1 Fall_theta_del": 0.919506587,
    "G1 Fall_pr_1_99_del": 96,
    "G1 Fall_ss_del": 125,
    "G1 Fall_dss_del": 592,
    "G1 Winter_theta_del": 1.157323164,
    "G1 Winter_pr_1_99_del": 96,
    "G1 Winter_ss_del": 125,
    "G1 Winter_dss_del": 616,
    "G1 Spring_theta_del": 1.356881191,
    "G1 Spring_pr_1_99_del": 96,
    "G1 Spring_ss_del": 125,
    "G1 Spring_dss_del": 636,
    "G2 Fall_theta_del": 1.613823922,
    "G2 Fall_pr_1_99_del": 96,
    "G2 Fall_ss_del": 125,
    "G2 Fall_dss_del": 661,
    "G2 Winter_theta_del": 1.881714569,
    "G2 Winter_pr_1_99_del": 96,
    "G2 Winter_ss_del": 125,
    "G2 Winter_dss_del": 688,
    "G2 Spring_theta_del": 2.207715601,
    "G2 Spring_pr_1_99_del": 96,
    "G2 Spring_ss_del": 125,
    "G2 Spring_dss_del": 721
  },
  {
    "ID": 192,
    "KG Fall_theta_del": 0.191393898,
    "KG Fall_pr_1_99_del": 96,
    "KG Fall_ss_del": 125,
    "KG Fall_dss_del": 519,
    "KG Winter_theta_del": 0.599397639,
    "KG Winter_pr_1_99_del": 96,
    "KG Winter_ss_del": 127,
    "KG Winter_dss_del": 560,
    "KG Spring_theta_del": 0.837403272,
    "KG Spring_pr_1_99_del": 96,
    "KG Spring_ss_del": 126,
    "KG Spring_dss_del": 584,
    "G1 Fall_theta_del": 0.938193069,
    "G1 Fall_pr_1_99_del": 96,
    "G1 Fall_ss_del": 125,
    "G1 Fall_dss_del": 594,
    "G1 Winter_theta_del": 1.207435697,
    "G1 Winter_pr_1_99_del": 96,
    "G1 Winter_ss_del": 126,
    "G1 Winter_dss_del": 621,
    "G1 Spring_theta_del": 1.3578769,
    "G1 Spring_pr_1_99_del": 96,
    "G1 Spring_ss_del": 125,
    "G1 Spring_dss_del": 636,
    "G2 Fall_theta_del": 1.623510194,
    "G2 Fall_pr_1_99_del": 96,
    "G2 Fall_ss_del": 125,
    "G2 Fall_dss_del": 662,
    "G2 Winter_theta_del": 1.911702084,
    "G2 Winter_pr_1_99_del": 96,
    "G2 Winter_ss_del": 125,
    "G2 Winter_dss_del": 691,
    "G2 Spring_theta_del": 2.209596046,
    "G2 Spring_pr_1_99_del": 96,
    "G2 Spring_ss_del": 125,
    "G2 Spring_dss_del": 721
  },
  {
    "ID": 375,
    "KG Fall_theta_del": 0.284750444,
    "KG Fall_pr_1_99_del": 96,
    "KG Fall_ss_del": 127,
    "KG Fall_dss_del": 528,
    "KG Winter_theta_del": 0.600126092,
    "KG Winter_pr_1_99_del": 96,
    "KG Winter_ss_del": 127,
    "KG Winter_dss_del": 560,
    "KG Spring_theta_del": 0.842304749,
    "KG Spring_pr_1_99_del": 96,
    "KG Spring_ss_del": 126,
    "KG Spring_dss_del": 584,
    "G1 Fall_theta_del": 0.979134401,
    "G1 Fall_pr_1_99_del": 96,
    "G1 Fall_ss_del": 126,
    "G1 Fall_dss_del": 598,
    "G1 Winter_theta_del": 1.251127183,
    "G1 Winter_pr_1_99_del": 96,
    "G1 Winter_ss_del": 127,
    "G1 Winter_dss_del": 625,
    "G1 Spring_theta_del": 1.358955355,
    "G1 Spring_pr_1_99_del": 96,
    "G1 Spring_ss_del": 125,
    "G1 Spring_dss_del": 636,
    "G2 Fall_theta_del": 1.634589536,
    "G2 Fall_pr_1_99_del": 96,
    "G2 Fall_ss_del": 125,
    "G2 Fall_dss_del": 663,
    "G2 Winter_theta_del": 1.928612561,
    "G2 Winter_pr_1_99_del": 96,
    "G2 Winter_ss_del": 126,
    "G2 Winter_dss_del": 693,
    "G2 Spring_theta_del": 2.257806848,
    "G2 Spring_pr_1_99_del": 96,
    "G2 Spring_ss_del": 126,
    "G2 Spring_dss_del": 726
  },
  {
    "ID": 384,
    "KG Fall_theta_del": 0.287694576,
    "KG Fall_pr_1_99_del": 96,
    "KG Fall_ss_del": 127,
    "KG Fall_dss_del": 529,
    "KG Winter_theta_del": 0.613979861,
    "KG Winter_pr_1_99_del": 96,
    "KG Winter_ss_del": 127,
    "KG Winter_dss_del": 561,
    "KG Spring_theta_del": 0.934097028,
    "KG Spring_pr_1_99_del": 96,
    "KG Spring_ss_del": 128,
    "KG Spring_dss_del": 593,
    "G1 Fall_theta_del": 0.980093553,
    "G1 Fall_pr_1_99_del": 96,
    "G1 Fall_ss_del": 126,
    "G1 Fall_dss_del": 598,
    "G1 Winter_theta_del": 1.262261734,
    "G1 Winter_pr_1_99_del": 96,
    "G1 Winter_ss_del": 127,
    "G1 Winter_dss_del": 626,
    "G1 Spring_theta_del": 1.362731474,
    "G1 Spring_pr_1_99_del": 96,
    "G1 Spring_ss_del": 125,
    "G1 Spring_dss_del": 636,
    "G2 Fall_theta_del": 1.666643534,
    "G2 Fall_pr_1_99_del": 96,
    "G2 Fall_ss_del": 126,
    "G2 Fall_dss_del": 667,
    "G2 Winter_theta_del": 1.982855193,
    "G2 Winter_pr_1_99_del": 96,
    "G2 Winter_ss_del": 127,
    "G2 Winter_dss_del": 698,
    "G2 Spring_theta_del": 2.261443956,
    "G2 Spring_pr_1_99_del": 96,
    "G2 Spring_ss_del": 126,
    "G2 Spring_dss_del": 726
  },
  {
    "ID": 92,
    "KG Fall_theta_del": 0.300914152,
    "KG Fall_pr_1_99_del": 97,
    "KG Fall_ss_del": 127,
    "KG Fall_dss_del": 530,
    "KG Winter_theta_del": 0.623910886,
    "KG Winter_pr_1_99_del": 97,
    "KG Winter_ss_del": 127,
    "KG Winter_dss_del": 562,
    "KG Spring_theta_del": 0.965464385,
    "KG Spring_pr_1_99_del": 97,
    "KG Spring_ss_del": 128,
    "KG Spring_dss_del": 597,
    "G1 Fall_theta_del": 1.003909573,
    "G1 Fall_pr_1_99_del": 97,
    "G1 Fall_ss_del": 126,
    "G1 Fall_dss_del": 600,
    "G1 Winter_theta_del": 1.293905063,
    "G1 Winter_pr_1_99_del": 97,
    "G1 Winter_ss_del": 128,
    "G1 Winter_dss_del": 629,
    "G1 Spring_theta_del": 1.371390933,
    "G1 Spring_pr_1_99_del": 97,
    "G1 Spring_ss_del": 125,
    "G1 Spring_dss_del": 637,
    "G2 Fall_theta_del": 1.668847499,
    "G2 Fall_pr_1_99_del": 97,
    "G2 Fall_ss_del": 126,
    "G2 Fall_dss_del": 667,
    "G2 Winter_theta_del": 1.990301273,
    "G2 Winter_pr_1_99_del": 97,
    "G2 Winter_ss_del": 127,
    "G2 Winter_dss_del": 699,
    "G2 Spring_theta_del": 2.273333312,
    "G2 Spring_pr_1_99_del": 97,
    "G2 Spring_ss_del": 127,
    "G2 Spring_dss_del": 727
  },
  {
    "ID": 155,
    "KG Fall_theta_del": 0.301001987,
    "KG Fall_pr_1_99_del": 97,
    "KG Fall_ss_del": 127,
    "KG Fall_dss_del": 530,
    "KG Winter_theta_del": 0.628519223,
    "KG Winter_pr_1_99_del": 97,
    "KG Winter_ss_del": 127,
    "KG Winter_dss_del": 563,
    "KG Spring_theta_del": 0.973988279,
    "KG Spring_pr_1_99_del": 97,
    "KG Spring_ss_del": 128,
    "KG Spring_dss_del": 597,
    "G1 Fall_theta_del": 1.008144214,
    "G1 Fall_pr_1_99_del": 97,
    "G1 Fall_ss_del": 126,
    "G1 Fall_dss_del": 601,
    "G1 Winter_theta_del": 1.301520273,
    "G1 Winter_pr_1_99_del": 97,
    "G1 Winter_ss_del": 128,
    "G1 Winter_dss_del": 630,
    "G1 Spring_theta_del": 1.381614312,
    "G1 Spring_pr_1_99_del": 97,
    "G1 Spring_ss_del": 125,
    "G1 Spring_dss_del": 638,
    "G2 Fall_theta_del": 1.673026644,
    "G2 Fall_pr_1_99_del": 97,
    "G2 Fall_ss_del": 126,
    "G2 Fall_dss_del": 667,
    "G2 Winter_theta_del": 2.008726492,
    "G2 Winter_pr_1_99_del": 97,
    "G2 Winter_ss_del": 127,
    "G2 Winter_dss_del": 701,
    "G2 Spring_theta_del": 2.320436401,
    "G2 Spring_pr_1_99_del": 97,
    "G2 Spring_ss_del": 128,
    "G2 Spring_dss_del": 732
  },
  {
    "ID": 172,
    "KG Fall_theta_del": 0.311400027,
    "KG Fall_pr_1_99_del": 97,
    "KG Fall_ss_del": 127,
    "KG Fall_dss_del": 531,
    "KG Winter_theta_del": 0.649564171,
    "KG Winter_pr_1_99_del": 97,
    "KG Winter_ss_del": 128,
    "KG Winter_dss_del": 565,
    "KG Spring_theta_del": 0.985274822,
    "KG Spring_pr_1_99_del": 97,
    "KG Spring_ss_del": 129,
    "KG Spring_dss_del": 599,
    "G1 Fall_theta_del": 1.019128077,
    "G1 Fall_pr_1_99_del": 97,
    "G1 Fall_ss_del": 126,
    "G1 Fall_dss_del": 602,
    "G1 Winter_theta_del": 1.306292239,
    "G1 Winter_pr_1_99_del": 97,
    "G1 Winter_ss_del": 128,
    "G1 Winter_dss_del": 631,
    "G1 Spring_theta_del": 1.402264491,
    "G1 Spring_pr_1_99_del": 97,
    "G1 Spring_ss_del": 126,
    "G1 Spring_dss_del": 640,
    "G2 Fall_theta_del": 1.69679947,
    "G2 Fall_pr_1_99_del": 97,
    "G2 Fall_ss_del": 126,
    "G2 Fall_dss_del": 670,
    "G2 Winter_theta_del": 2.02658721,
    "G2 Winter_pr_1_99_del": 97,
    "G2 Winter_ss_del": 128,
    "G2 Winter_dss_del": 703,
    "G2 Spring_theta_del": 2.341245437,
    "G2 Spring_pr_1_99_del": 97,
    "G2 Spring_ss_del": 128,
    "G2 Spring_dss_del": 734
  },
  {
    "ID": 312,
    "KG Fall_theta_del": 0.322154445,
    "KG Fall_pr_1_99_del": 97,
    "KG Fall_ss_del": 127,
    "KG Fall_dss_del": 532,
    "KG Winter_theta_del": 0.675066732,
    "KG Winter_pr_1_99_del": 97,
    "KG Winter_ss_del": 128,
    "KG Winter_dss_del": 568,
    "KG Spring_theta_del": 1.003986389,
    "KG Spring_pr_1_99_del": 97,
    "KG Spring_ss_del": 129,
    "KG Spring_dss_del": 600,
    "G1 Fall_theta_del": 1.052514923,
    "G1 Fall_pr_1_99_del": 97,
    "G1 Fall_ss_del": 127,
    "G1 Fall_dss_del": 605,
    "G1 Winter_theta_del": 1.309955236,
    "G1 Winter_pr_1_99_del": 97,
    "G1 Winter_ss_del": 128,
    "G1 Winter_dss_del": 631,
    "G1 Spring_theta_del": 1.411291615,
    "G1 Spring_pr_1_99_del": 97,
    "G1 Spring_ss_del": 126,
    "G1 Spring_dss_del": 641,
    "G2 Fall_theta_del": 1.701991742,
    "G2 Fall_pr_1_99_del": 97,
    "G2 Fall_ss_del": 126,
    "G2 Fall_dss_del": 670,
    "G2 Winter_theta_del": 2.027578047,
    "G2 Winter_pr_1_99_del": 97,
    "G2 Winter_ss_del": 128,
    "G2 Winter_dss_del": 703,
    "G2 Spring_theta_del": 2.341372077,
    "G2 Spring_pr_1_99_del": 97,
    "G2 Spring_ss_del": 128,
    "G2 Spring_dss_del": 734
  },
  {
    "ID": 463,
    "KG Fall_theta_del": 0.336932119,
    "KG Fall_pr_1_99_del": 97,
    "KG Fall_ss_del": 128,
    "KG Fall_dss_del": 534,
    "KG Winter_theta_del": 0.676869775,
    "KG Winter_pr_1_99_del": 97,
    "KG Winter_ss_del": 128,
    "KG Winter_dss_del": 568,
    "KG Spring_theta_del": 1.056727964,
    "KG Spring_pr_1_99_del": 97,
    "KG Spring_ss_del": 130,
    "KG Spring_dss_del": 606,
    "G1 Fall_theta_del": 1.055768512,
    "G1 Fall_pr_1_99_del": 97,
    "G1 Fall_ss_del": 127,
    "G1 Fall_dss_del": 606,
    "G1 Winter_theta_del": 1.35392758,
    "G1 Winter_pr_1_99_del": 97,
    "G1 Winter_ss_del": 129,
    "G1 Winter_dss_del": 635,
    "G1 Spring_theta_del": 1.505301627,
    "G1 Spring_pr_1_99_del": 97,
    "G1 Spring_ss_del": 128,
    "G1 Spring_dss_del": 651,
    "G2 Fall_theta_del": 1.719559243,
    "G2 Fall_pr_1_99_del": 97,
    "G2 Fall_ss_del": 127,
    "G2 Fall_dss_del": 672,
    "G2 Winter_theta_del": 2.035688957,
    "G2 Winter_pr_1_99_del": 97,
    "G2 Winter_ss_del": 128,
    "G2 Winter_dss_del": 704,
    "G2 Spring_theta_del": 2.41467715,
    "G2 Spring_pr_1_99_del": 97,
    "G2 Spring_ss_del": 129,
    "G2 Spring_dss_del": 741
  },
  {
    "ID": 112,
    "KG Fall_theta_del": 0.341994019,
    "KG Fall_pr_1_99_del": 98,
    "KG Fall_ss_del": 128,
    "KG Fall_dss_del": 534,
    "KG Winter_theta_del": 0.676981223,
    "KG Winter_pr_1_99_del": 98,
    "KG Winter_ss_del": 128,
    "KG Winter_dss_del": 568,
    "KG Spring_theta_del": 1.061926789,
    "KG Spring_pr_1_99_del": 98,
    "KG Spring_ss_del": 130,
    "KG Spring_dss_del": 606,
    "G1 Fall_theta_del": 1.084967194,
    "G1 Fall_pr_1_99_del": 98,
    "G1 Fall_ss_del": 128,
    "G1 Fall_dss_del": 608,
    "G1 Winter_theta_del": 1.394142077,
    "G1 Winter_pr_1_99_del": 98,
    "G1 Winter_ss_del": 130,
    "G1 Winter_dss_del": 639,
    "G1 Spring_theta_del": 1.507190437,
    "G1 Spring_pr_1_99_del": 98,
    "G1 Spring_ss_del": 128,
    "G1 Spring_dss_del": 651,
    "G2 Fall_theta_del": 1.734228139,
    "G2 Fall_pr_1_99_del": 98,
    "G2 Fall_ss_del": 127,
    "G2 Fall_dss_del": 673,
    "G2 Winter_theta_del": 2.052025435,
    "G2 Winter_pr_1_99_del": 98,
    "G2 Winter_ss_del": 128,
    "G2 Winter_dss_del": 705,
    "G2 Spring_theta_del": 2.428096595,
    "G2 Spring_pr_1_99_del": 98,
    "G2 Spring_ss_del": 130,
    "G2 Spring_dss_del": 743
  },
  {
    "ID": 202,
    "KG Fall_theta_del": 0.423674518,
    "KG Fall_pr_1_99_del": 98,
    "KG Fall_ss_del": 129,
    "KG Fall_dss_del": 542,
    "KG Winter_theta_del": 0.678905527,
    "KG Winter_pr_1_99_del": 98,
    "KG Winter_ss_del": 128,
    "KG Winter_dss_del": 568,
    "KG Spring_theta_del": 1.19491437,
    "KG Spring_pr_1_99_del": 98,
    "KG Spring_ss_del": 133,
    "KG Spring_dss_del": 619,
    "G1 Fall_theta_del": 1.092247926,
    "G1 Fall_pr_1_99_del": 98,
    "G1 Fall_ss_del": 128,
    "G1 Fall_dss_del": 609,
    "G1 Winter_theta_del": 1.399521116,
    "G1 Winter_pr_1_99_del": 98,
    "G1 Winter_ss_del": 130,
    "G1 Winter_dss_del": 640,
    "G1 Spring_theta_del": 1.612271709,
    "G1 Spring_pr_1_99_del": 98,
    "G1 Spring_ss_del": 130,
    "G1 Spring_dss_del": 661,
    "G2 Fall_theta_del": 1.735869617,
    "G2 Fall_pr_1_99_del": 98,
    "G2 Fall_ss_del": 127,
    "G2 Fall_dss_del": 674,
    "G2 Winter_theta_del": 2.060876015,
    "G2 Winter_pr_1_99_del": 98,
    "G2 Winter_ss_del": 128,
    "G2 Winter_dss_del": 706,
    "G2 Spring_theta_del": 2.478886139,
    "G2 Spring_pr_1_99_del": 98,
    "G2 Spring_ss_del": 131,
    "G2 Spring_dss_del": 748
  },
  {
    "ID": 427,
    "KG Fall_theta_del": 0.449165854,
    "KG Fall_pr_1_99_del": 98,
    "KG Fall_ss_del": 130,
    "KG Fall_dss_del": 545,
    "KG Winter_theta_del": 0.695434627,
    "KG Winter_pr_1_99_del": 98,
    "KG Winter_ss_del": 128,
    "KG Winter_dss_del": 570,
    "KG Spring_theta_del": 1.275020146,
    "KG Spring_pr_1_99_del": 98,
    "KG Spring_ss_del": 134,
    "KG Spring_dss_del": 628,
    "G1 Fall_theta_del": 1.112656111,
    "G1 Fall_pr_1_99_del": 98,
    "G1 Fall_ss_del": 128,
    "G1 Fall_dss_del": 611,
    "G1 Winter_theta_del": 1.416122848,
    "G1 Winter_pr_1_99_del": 98,
    "G1 Winter_ss_del": 130,
    "G1 Winter_dss_del": 642,
    "G1 Spring_theta_del": 1.619948427,
    "G1 Spring_pr_1_99_del": 98,
    "G1 Spring_ss_del": 130,
    "G1 Spring_dss_del": 662,
    "G2 Fall_theta_del": 1.801279516,
    "G2 Fall_pr_1_99_del": 98,
    "G2 Fall_ss_del": 128,
    "G2 Fall_dss_del": 680,
    "G2 Winter_theta_del": 2.144078321,
    "G2 Winter_pr_1_99_del": 98,
    "G2 Winter_ss_del": 130,
    "G2 Winter_dss_del": 714,
    "G2 Spring_theta_del": 2.486479176,
    "G2 Spring_pr_1_99_del": 98,
    "G2 Spring_ss_del": 131,
    "G2 Spring_dss_del": 749
  },
  {
    "ID": 438,
    "KG Fall_theta_del": 0.481625676,
    "KG Fall_pr_1_99_del": 98,
    "KG Fall_ss_del": 130,
    "KG Fall_dss_del": 548,
    "KG Winter_theta_del": 0.701203924,
    "KG Winter_pr_1_99_del": 98,
    "KG Winter_ss_del": 129,
    "KG Winter_dss_del": 570,
    "KG Spring_theta_del": 1.298892565,
    "KG Spring_pr_1_99_del": 98,
    "KG Spring_ss_del": 134,
    "KG Spring_dss_del": 630,
    "G1 Fall_theta_del": 1.122550299,
    "G1 Fall_pr_1_99_del": 98,
    "G1 Fall_ss_del": 128,
    "G1 Fall_dss_del": 612,
    "G1 Winter_theta_del": 1.442976223,
    "G1 Winter_pr_1_99_del": 98,
    "G1 Winter_ss_del": 131,
    "G1 Winter_dss_del": 644,
    "G1 Spring_theta_del": 1.624219667,
    "G1 Spring_pr_1_99_del": 98,
    "G1 Spring_ss_del": 130,
    "G1 Spring_dss_del": 662,
    "G2 Fall_theta_del": 1.809590614,
    "G2 Fall_pr_1_99_del": 98,
    "G2 Fall_ss_del": 128,
    "G2 Fall_dss_del": 681,
    "G2 Winter_theta_del": 2.147954506,
    "G2 Winter_pr_1_99_del": 98,
    "G2 Winter_ss_del": 130,
    "G2 Winter_dss_del": 715,
    "G2 Spring_theta_del": 2.523455818,
    "G2 Spring_pr_1_99_del": 98,
    "G2 Spring_ss_del": 131,
    "G2 Spring_dss_del": 752
  },
  {
    "ID": 477,
    "KG Fall_theta_del": 0.484777133,
    "KG Fall_pr_1_99_del": 98,
    "KG Fall_ss_del": 130,
    "KG Fall_dss_del": 548,
    "KG Winter_theta_del": 0.762060132,
    "KG Winter_pr_1_99_del": 98,
    "KG Winter_ss_del": 130,
    "KG Winter_dss_del": 576,
    "KG Spring_theta_del": 1.340013434,
    "KG Spring_pr_1_99_del": 98,
    "KG Spring_ss_del": 135,
    "KG Spring_dss_del": 634,
    "G1 Fall_theta_del": 1.130612043,
    "G1 Fall_pr_1_99_del": 98,
    "G1 Fall_ss_del": 129,
    "G1 Fall_dss_del": 613,
    "G1 Winter_theta_del": 1.44831511,
    "G1 Winter_pr_1_99_del": 98,
    "G1 Winter_ss_del": 131,
    "G1 Winter_dss_del": 645,
    "G1 Spring_theta_del": 1.658906134,
    "G1 Spring_pr_1_99_del": 98,
    "G1 Spring_ss_del": 131,
    "G1 Spring_dss_del": 666,
    "G2 Fall_theta_del": 1.815865559,
    "G2 Fall_pr_1_99_del": 98,
    "G2 Fall_ss_del": 129,
    "G2 Fall_dss_del": 682,
    "G2 Winter_theta_del": 2.284316641,
    "G2 Winter_pr_1_99_del": 98,
    "G2 Winter_ss_del": 132,
    "G2 Winter_dss_del": 728,
    "G2 Spring_theta_del": 2.539726791,
    "G2 Spring_pr_1_99_del": 98,
    "G2 Spring_ss_del": 132,
    "G2 Spring_dss_del": 754
  },
  {
    "ID": 34,
    "KG Fall_theta_del": 0.506408438,
    "KG Fall_pr_1_99_del": 99,
    "KG Fall_ss_del": 131,
    "KG Fall_dss_del": 551,
    "KG Winter_theta_del": 0.82315641,
    "KG Winter_pr_1_99_del": 99,
    "KG Winter_ss_del": 131,
    "KG Winter_dss_del": 582,
    "KG Spring_theta_del": 1.390398568,
    "KG Spring_pr_1_99_del": 99,
    "KG Spring_ss_del": 136,
    "KG Spring_dss_del": 639,
    "G1 Fall_theta_del": 1.247208021,
    "G1 Fall_pr_1_99_del": 99,
    "G1 Fall_ss_del": 131,
    "G1 Fall_dss_del": 625,
    "G1 Winter_theta_del": 1.475255451,
    "G1 Winter_pr_1_99_del": 99,
    "G1 Winter_ss_del": 132,
    "G1 Winter_dss_del": 648,
    "G1 Spring_theta_del": 1.672498145,
    "G1 Spring_pr_1_99_del": 99,
    "G1 Spring_ss_del": 131,
    "G1 Spring_dss_del": 667,
    "G2 Fall_theta_del": 1.9075073,
    "G2 Fall_pr_1_99_del": 99,
    "G2 Fall_ss_del": 130,
    "G2 Fall_dss_del": 691,
    "G2 Winter_theta_del": 2.297803728,
    "G2 Winter_pr_1_99_del": 99,
    "G2 Winter_ss_del": 133,
    "G2 Winter_dss_del": 730,
    "G2 Spring_theta_del": 2.617377411,
    "G2 Spring_pr_1_99_del": 99,
    "G2 Spring_ss_del": 133,
    "G2 Spring_dss_del": 762
  },
  {
    "ID": 54,
    "KG Fall_theta_del": 0.625621711,
    "KG Fall_pr_1_99_del": 99,
    "KG Fall_ss_del": 133,
    "KG Fall_dss_del": 563,
    "KG Winter_theta_del": 0.8367128,
    "KG Winter_pr_1_99_del": 99,
    "KG Winter_ss_del": 131,
    "KG Winter_dss_del": 584,
    "KG Spring_theta_del": 1.392896559,
    "KG Spring_pr_1_99_del": 99,
    "KG Spring_ss_del": 136,
    "KG Spring_dss_del": 639,
    "G1 Fall_theta_del": 1.275598504,
    "G1 Fall_pr_1_99_del": 99,
    "G1 Fall_ss_del": 131,
    "G1 Fall_dss_del": 628,
    "G1 Winter_theta_del": 1.509799234,
    "G1 Winter_pr_1_99_del": 99,
    "G1 Winter_ss_del": 132,
    "G1 Winter_dss_del": 651,
    "G1 Spring_theta_del": 1.748634311,
    "G1 Spring_pr_1_99_del": 99,
    "G1 Spring_ss_del": 133,
    "G1 Spring_dss_del": 675,
    "G2 Fall_theta_del": 2.25778864,
    "G2 Fall_pr_1_99_del": 99,
    "G2 Fall_ss_del": 137,
    "G2 Fall_dss_del": 726,
    "G2 Winter_theta_del": 2.316699043,
    "G2 Winter_pr_1_99_del": 99,
    "G2 Winter_ss_del": 133,
    "G2 Winter_dss_del": 732,
    "G2 Spring_theta_del": 2.62071841,
    "G2 Spring_pr_1_99_del": 99,
    "G2 Spring_ss_del": 133,
    "G2 Spring_dss_del": 762
  },
  {
    "ID": 242,
    "KG Fall_theta_del": 0.811512176,
    "KG Fall_pr_1_99_del": 99,
    "KG Fall_ss_del": 137,
    "KG Fall_dss_del": 581,
    "KG Winter_theta_del": 0.885145497,
    "KG Winter_pr_1_99_del": 99,
    "KG Winter_ss_del": 132,
    "KG Winter_dss_del": 589,
    "KG Spring_theta_del": 1.40951736,
    "KG Spring_pr_1_99_del": 99,
    "KG Spring_ss_del": 136,
    "KG Spring_dss_del": 641,
    "G1 Fall_theta_del": 1.451647283,
    "G1 Fall_pr_1_99_del": 99,
    "G1 Fall_ss_del": 135,
    "G1 Fall_dss_del": 645,
    "G1 Winter_theta_del": 1.525990036,
    "G1 Winter_pr_1_99_del": 99,
    "G1 Winter_ss_del": 133,
    "G1 Winter_dss_del": 653,
    "G1 Spring_theta_del": 1.801912243,
    "G1 Spring_pr_1_99_del": 99,
    "G1 Spring_ss_del": 134,
    "G1 Spring_dss_del": 680,
    "G2 Fall_theta_del": 2.348604241,
    "G2 Fall_pr_1_99_del": 99,
    "G2 Fall_ss_del": 139,
    "G2 Fall_dss_del": 735,
    "G2 Winter_theta_del": 2.366017848,
    "G2 Winter_pr_1_99_del": 99,
    "G2 Winter_ss_del": 134,
    "G2 Winter_dss_del": 737,
    "G2 Spring_theta_del": 2.624375236,
    "G2 Spring_pr_1_99_del": 99,
    "G2 Spring_ss_del": 133,
    "G2 Spring_dss_del": 762
  },
  {
    "ID": 270,
    "KG Fall_theta_del": 0.831393265,
    "KG Fall_pr_1_99_del": 99,
    "KG Fall_ss_del": 137,
    "KG Fall_dss_del": 583,
    "KG Winter_theta_del": 0.934818278,
    "KG Winter_pr_1_99_del": 99,
    "KG Winter_ss_del": 133,
    "KG Winter_dss_del": 593,
    "KG Spring_theta_del": 1.413522001,
    "KG Spring_pr_1_99_del": 99,
    "KG Spring_ss_del": 137,
    "KG Spring_dss_del": 641,
    "G1 Fall_theta_del": 1.551186508,
    "G1 Fall_pr_1_99_del": 99,
    "G1 Fall_ss_del": 137,
    "G1 Fall_dss_del": 655,
    "G1 Winter_theta_del": 1.536168769,
    "G1 Winter_pr_1_99_del": 99,
    "G1 Winter_ss_del": 133,
    "G1 Winter_dss_del": 654,
    "G1 Spring_theta_del": 1.817338144,
    "G1 Spring_pr_1_99_del": 99,
    "G1 Spring_ss_del": 134,
    "G1 Spring_dss_del": 682,
    "G2 Fall_theta_del": 2.381675776,
    "G2 Fall_pr_1_99_del": 99,
    "G2 Fall_ss_del": 139,
    "G2 Fall_dss_del": 738,
    "G2 Winter_theta_del": 2.41034336,
    "G2 Winter_pr_1_99_del": 99,
    "G2 Winter_ss_del": 135,
    "G2 Winter_dss_del": 741,
    "G2 Spring_theta_del": 2.740684136,
    "G2 Spring_pr_1_99_del": 99,
    "G2 Spring_ss_del": 135,
    "G2 Spring_dss_del": 774
  },
  {
    "ID": 296,
    "KG Fall_theta_del": 0.873292558,
    "KG Fall_pr_1_99_del": 99,
    "KG Fall_ss_del": 138,
    "KG Fall_dss_del": 587,
    "KG Winter_theta_del": 0.987522012,
    "KG Winter_pr_1_99_del": 99,
    "KG Winter_ss_del": 134,
    "KG Winter_dss_del": 599,
    "KG Spring_theta_del": 1.450286627,
    "KG Spring_pr_1_99_del": 99,
    "KG Spring_ss_del": 137,
    "KG Spring_dss_del": 645,
    "G1 Fall_theta_del": 1.580618914,
    "G1 Fall_pr_1_99_del": 99,
    "G1 Fall_ss_del": 137,
    "G1 Fall_dss_del": 658,
    "G1 Winter_theta_del": 1.604561945,
    "G1 Winter_pr_1_99_del": 99,
    "G1 Winter_ss_del": 134,
    "G1 Winter_dss_del": 660,
    "G1 Spring_theta_del": 1.910922934,
    "G1 Spring_pr_1_99_del": 99,
    "G1 Spring_ss_del": 136,
    "G1 Spring_dss_del": 691,
    "G2 Fall_theta_del": 2.444050602,
    "G2 Fall_pr_1_99_del": 99,
    "G2 Fall_ss_del": 141,
    "G2 Fall_dss_del": 744,
    "G2 Winter_theta_del": 2.467017446,
    "G2 Winter_pr_1_99_del": 99,
    "G2 Winter_ss_del": 136,
    "G2 Winter_dss_del": 747,
    "G2 Spring_theta_del": 2.975282924,
    "G2 Spring_pr_1_99_del": 99,
    "G2 Spring_ss_del": 140,
    "G2 Spring_dss_del": 798
  },
  {
    "ID": 317,
    "KG Fall_theta_del": 0.92745395,
    "KG Fall_pr_1_99_del": 99,
    "KG Fall_ss_del": 139,
    "KG Fall_dss_del": 593,
    "KG Winter_theta_del": 1.499776201,
    "KG Winter_pr_1_99_del": 99,
    "KG Winter_ss_del": 143,
    "KG Winter_dss_del": 650,
    "KG Spring_theta_del": 1.474370726,
    "KG Spring_pr_1_99_del": 99,
    "KG Spring_ss_del": 138,
    "KG Spring_dss_del": 647,
    "G1 Fall_theta_del": 1.580716457,
    "G1 Fall_pr_1_99_del": 99,
    "G1 Fall_ss_del": 137,
    "G1 Fall_dss_del": 658,
    "G1 Winter_theta_del": 1.688438182,
    "G1 Winter_pr_1_99_del": 99,
    "G1 Winter_ss_del": 136,
    "G1 Winter_dss_del": 669,
    "G1 Spring_theta_del": 2.011593179,
    "G1 Spring_pr_1_99_del": 99,
    "G1 Spring_ss_del": 138,
    "G1 Spring_dss_del": 701,
    "G2 Fall_theta_del": 2.614484691,
    "G2 Fall_pr_1_99_del": 99,
    "G2 Fall_ss_del": 144,
    "G2 Fall_dss_del": 761,
    "G2 Winter_theta_del": 2.748273653,
    "G2 Winter_pr_1_99_del": 99,
    "G2 Winter_ss_del": 141,
    "G2 Winter_dss_del": 775,
    "G2 Spring_theta_del": 3.047030799,
    "G2 Spring_pr_1_99_del": 99,
    "G2 Spring_ss_del": 141,
    "G2 Spring_dss_del": 805
  },
  {
    "ID": 346,
    "KG Fall_theta_del": 1.042949407,
    "KG Fall_pr_1_99_del": 99,
    "KG Fall_ss_del": 141,
    "KG Fall_dss_del": 604,
    "KG Winter_theta_del": 1.585942817,
    "KG Winter_pr_1_99_del": 99,
    "KG Winter_ss_del": 144,
    "KG Winter_dss_del": 659,
    "KG Spring_theta_del": 1.566245875,
    "KG Spring_pr_1_99_del": 99,
    "KG Spring_ss_del": 139,
    "KG Spring_dss_del": 657,
    "G1 Fall_theta_del": 1.614656033,
    "G1 Fall_pr_1_99_del": 99,
    "G1 Fall_ss_del": 138,
    "G1 Fall_dss_del": 661,
    "G1 Winter_theta_del": 1.782118393,
    "G1 Winter_pr_1_99_del": 99,
    "G1 Winter_ss_del": 138,
    "G1 Winter_dss_del": 678,
    "G1 Spring_theta_del": 2.374433584,
    "G1 Spring_pr_1_99_del": 99,
    "G1 Spring_ss_del": 145,
    "G1 Spring_dss_del": 737,
    "G2 Fall_theta_del": 2.658327232,
    "G2 Fall_pr_1_99_del": 99,
    "G2 Fall_ss_del": 145,
    "G2 Fall_dss_del": 766,
    "G2 Winter_theta_del": 2.846928889,
    "G2 Winter_pr_1_99_del": 99,
    "G2 Winter_ss_del": 143,
    "G2 Winter_dss_del": 785,
    "G2 Spring_theta_del": 3.134961768,
    "G2 Spring_pr_1_99_del": 99,
    "G2 Spring_ss_del": 143,
    "G2 Spring_dss_del": 813
  },
  {
    "ID": 428,
    "KG Fall_theta_del": 1.276203491,
    "KG Fall_pr_1_99_del": 99,
    "KG Fall_ss_del": 145,
    "KG Fall_dss_del": 628,
    "KG Winter_theta_del": 1.699722106,
    "KG Winter_pr_1_99_del": 99,
    "KG Winter_ss_del": 147,
    "KG Winter_dss_del": 670,
    "KG Spring_theta_del": 1.59839948,
    "KG Spring_pr_1_99_del": 99,
    "KG Spring_ss_del": 140,
    "KG Spring_dss_del": 660,
    "G1 Fall_theta_del": 2.730196456,
    "G1 Fall_pr_1_99_del": 99,
    "G1 Fall_ss_del": 159,
    "G1 Fall_dss_del": 773,
    "G1 Winter_theta_del": 2.153062661,
    "G1 Winter_pr_1_99_del": 99,
    "G1 Winter_ss_del": 145,
    "G1 Winter_dss_del": 715,
    "G1 Spring_theta_del": 2.846565442,
    "G1 Spring_pr_1_99_del": 99,
    "G1 Spring_ss_del": 155,
    "G1 Spring_dss_del": 785,
    "G2 Fall_theta_del": 2.720751306,
    "G2 Fall_pr_1_99_del": 99,
    "G2 Fall_ss_del": 146,
    "G2 Fall_dss_del": 772,
    "G2 Winter_theta_del": 3.084596581,
    "G2 Winter_pr_1_99_del": 99,
    "G2 Winter_ss_del": 147,
    "G2 Winter_dss_del": 808,
    "G2 Spring_theta_del": 3.490211399,
    "G2 Spring_pr_1_99_del": 99,
    "G2 Spring_ss_del": 149,
    "G2 Spring_dss_del": 849
  }
];

function ClinicianReport({}: ClinicianReportProps) {
  const terms = getUseCaseTerminology();
  const routeParams = useParams();
  const [user, setUser] = useState([] as any);
  const [allTests, setAllTests] = useState([] as any);
  const [tests, setTests] = useState([] as any);
  const [categories, setCategories] = useState([] as any);
  const [scores, setScores] = useState([] as any);
  const [selectedTestIds, setSelectedTestIds] = useState([] as any);
  const [showFilters, setShowFilters] = useState(false);
  const [studentInfo, setStudentInfo] = useState({} as any);

  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(1000);
  const [totalSize, setTotalSize] = useState(0);
  const [sortFieldName, setSortFieldName] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [organizationFilter, setOrganizationFilter] = useState("");
  const [clinicianFilter, setClinicianFilter] = useState("");
  const [studentFilter, setStudentFilter] = useState("");
  const [testFilter, setTestFilter] = useState("");
  const [subtestFilter, setSubtestFilter] = useState("All" as any);
  const [normsFilter, setNormsFilter] = useState("All" as any);
  const [testDateFilter, setTestDateFilter] = useState("All" as any);
  const [completionStatusFilter, setCompletionStatusFilter] = useState("Complete");
  const [allScoreRows, setAllScoreRows] = useState([] as any);
  const [progressSubtest, setProgressSubtest] = useState("" as any);
  const [progressStartDate, setProgressStartDate] = useState("" as any);
  const [progressEndDate, setProgressEndDate] = useState("" as any);

  const normalizeGrade = (grade: string) => {
    if (!grade) {
      return grade;
    }
    if (grade === "K" || grade === "1st" || grade === "2nd" || grade === "3rd") {
      return grade;
    }
    const numericMatch = grade.match(/\d+/);
    if (numericMatch) {
      const numericGrade = parseInt(numericMatch[0], 10);
      if (numericGrade >= 4) {
        return "3rd";
      }
    }
    return grade;
  };

  useEffect(() => {
    if (routeParams.studentId) {
      setStudentFilter(routeParams.studentId);
      let studentInfo: any = {};

      let token = sessionStorage.getItem("token");
      fetch(ventrisConfig.url + '/api/student/' + routeParams.studentId, {
        method: 'get',
        headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
        }
      }).then(function (response) {
        return response.json();
      }).then(async function (data) {
        if (data.student) {
          setStudentInfo(data.student);
          studentInfo = data.student;
          let token = sessionStorage.getItem("token");
          fetch(ventrisConfig.url + '/api/schemas', {
            method: 'get',
            headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token
            }
          }).then(function (response) {
            return response.json();
          }).then(async function (data) {
            for (let i = 0; i < data?.length; i++) {
              if (data[i].name == 'WRA') {
                let groupedCategories: any = [];

                for (let j = 0; j < data[i].questions?.length; j++) {
                  let categoryIndex: number = -1;
                  for (let k = 0; k < groupedCategories.length; k++) {
                    if (groupedCategories[k].name == data[i].questions[j].category) {
                      categoryIndex = k;
                      break;
                    }
                  }

                  if (categoryIndex == -1) {
                    categoryIndex = groupedCategories.length;
                    groupedCategories.push({
                      name: data[i].questions[j].category,
                      subcategories: []
                    });
                  }

                  let subcategoryIndex: number = -1;
                  for (let k = 0; k < groupedCategories[categoryIndex].subcategories.length; k++) {
                    if (groupedCategories[categoryIndex].subcategories[k].name == data[i].questions[j].subcategory) {
                      subcategoryIndex = k;
                      break;
                    }
                  }

                  if (subcategoryIndex == -1) {
                    subcategoryIndex = groupedCategories[categoryIndex].subcategories.length;
                    groupedCategories[categoryIndex].subcategories.push({
                      name: data[i].questions[j].subcategory,
                      questions: []
                    });
                  }

                  if (data[i].questions[j].discrimination && data[i].questions[j].difficulty) {
                    groupedCategories[categoryIndex].subcategories[subcategoryIndex].questions.push(data[i].questions[j]);
                  }
                }

                setCategories(groupedCategories);
                loadTests(1, groupedCategories, studentInfo);
                break;
              }
            }
          });
        }
      });
    }

    let currentUser = sessionStorage.getItem("current_user");
    setUser(JSON.parse(currentUser!));
  }, []);

  useEffect(() => {
      loadTests(page, categories, studentInfo);
  }, [page, sizePerPage, sortFieldName, sortDirection, organizationFilter, clinicianFilter, studentFilter, testFilter, completionStatusFilter, subtestFilter, normsFilter]);

  useEffect(() => {
    if (allTests.length > 0) {
      generateScores(allTests, categories, studentInfo);
    }
  }, [testDateFilter]);

  useEffect(() => {
    const optionKeys = new Set(availableTestDates().map(([key]) => key));
    if (testDateFilter !== "All" && !optionKeys.has(testDateFilter)) {
      setTestDateFilter("All");
    }
  }, [allTests, normsFilter]);

  const loadTests = (page: number, categories: any, studentInfo: any) => {
    setTimeout(() => {
      let token = sessionStorage.getItem("token");
      console.log(page);
      
      fetch(ventrisConfig.url + '/api/tests?page=' + page + '&sizePerPage=' + sizePerPage + '&sortField=' + sortFieldName + '&sortDirection=' + sortDirection + '&organization=' + organizationFilter + '&clinician=' + clinicianFilter + '&student=' + routeParams.studentId + '&test=' + testFilter + '&completionStatus=' + completionStatusFilter + '&includeDetails=true', {
        method: 'get',
        headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
        }
      }).then(function (response) {
        return response.json();
      }).then(async function (data) {
        const filteredTests = [];
        for (let i = 0; i < data.tests?.length; i++) {
          if (!routeParams.testId || routeParams.testId?.toString() === "-1" || routeParams.testId?.toString() === data.tests[i].id?.toString()) {
            filteredTests.push(data.tests[i]);
          }
        }
        setAllTests(filteredTests);
        setTotalSize(data.total_records);
        setSelectedTestIds([]);
        generateScores(filteredTests, categories, studentInfo);
      });
    },
    100);
  }

  const back = () => {
    window.history.back();
  }

  const dateFormatter = (cell: any, row: any) => {
    return (
      <div>
        {(new Date(Date.parse(cell))).toLocaleDateString()}
      </div>
    );
  }

  const formatDateLabel = (cell: any) => {
    if (!cell) {
      return "";
    }

    const parsedDate = new Date(cell);
    if (isNaN(parsedDate.getTime())) {
      return cell;
    }

    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const year = parsedDate.getFullYear();

    return `${month}/${day}/${year}`;
  }

  const testDateKey = (cell: any) => {
    if (!cell) {
      return "";
    }

    const parsedDate = new Date(cell);
    if (isNaN(parsedDate.getTime())) {
      return cell;
    }

    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const year = parsedDate.getFullYear();

    return `${year}-${month}-${day}`;
  }

  const isNumericScoreValue = (value: any) => value !== null && value !== undefined && value !== "" && value !== "N/A" && !isNaN(Number(value));

  const updatedFormatter = (cell: any, row: any) => {
    if (!cell) {
      return "";
    }

    const parsedDate = new Date(cell);
    if (isNaN(parsedDate.getTime())) {
      return cell;
    }

    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const year = parsedDate.getFullYear();

    let hours = parsedDate.getHours();
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const hoursString = String(hours).padStart(2, "0");

    return (
      <div>
        {`${month}/${day}/${year} ${hoursString}:${minutes} ${ampm}`}
      </div>
    );
  }

  const scoreColumns = [
    {
      dataField: 'completed_timestamp',
      text: 'Test Date',
      formatter: dateFormatter
    },
    {
      dataField: 'updated_at',
      text: 'Last Updated',
      formatter: updatedFormatter
    },
    {
      dataField: 'subtest',
      text: 'Subtest'
    },
    {
      dataField: 'norms',
      text: 'Norms'
    },
    {
      dataField: 'flag',
      text: 'Flag'
    },
    /*
    {
      dataField: 'theta',
      text: 'Theta'
    },
    */
    {
      dataField: 'scaleScore',
      text: 'Scale Score'
    },
    {
      dataField: 'scaleScoreCI',
      text: '95% CI'
    },
    {
      dataField: 'standardScore',
      text: 'Standard Score'
    },
    {
      dataField: 'standardScoreCI',
      text: '95% CI'
    },
    {
      dataField: 'percentileRank',
      text: 'Percentile Rank'
    },
    {
      dataField: 'percentileRankCI',
      text: '95% CI'
    },
  ];

  const testColumns = [
    {
      dataField: 'completed_timestamp',
      text: 'Test Date',
      formatter: dateFormatter
    },
    {
      dataField: 'updated_at',
      text: 'Last Updated',
      formatter: updatedFormatter
    },
    /*
    {
      dataField: 'dysFlag',
      text: 'DYS Flag'
    },
    {
      dataField: 'dysCutoff',
      text: 'DYS Cutoff'
    },
    {
      dataField: 'dysInterpretation',
      text: 'DYS Interpretation'
    },
    {
      dataField: 'pwrrFlag',
      text: 'PWRR Flag'
    },
    {
      dataField: 'pwrrCutoff',
      text: 'PWRR Cutoff'
    },
    {
      dataField: 'pwrrInterpretation',
      text: 'PWRR Interpretation'
    },
    */
    {
      dataField: 'finalClassification',
      text: 'Final Classification'
    },
  ];

  const updateFilters = (organizations: any, clinician: string, student: string, test: string, status: string, subtest: string, norms: string) => {
    setSubtestFilter(subtest);
    setNormsFilter(norms);
    setPage(1);
  };

  const handleTableChange = (type: string, { page, sizePerPage, filters, sortField, sortOrder, cellEdit }: any) => {
    if ((sortField && sortFieldName != sortField) || (sortDirection && sortDirection != sortOrder)) {
      setPage(1);
    } else {
      setPage(page);
    }
    setPage(page);
    setSortFieldName(sortField ? sortField : "");
    setSortDirection(sortOrder ? sortOrder : "");
  }

  const getFloorCeilingInfo = (category: any, answers: any) => {
    let consecutiveCorrectCount = 0;
    let consecutiveIncorrectCount = 0;
    const firstEightResults: boolean[] = [];

    for (let j = 0; j < category.subcategories?.length; j++) {
      for (let k = 0; k < category.subcategories[j].questions?.length; k++) {
        const question: any = category.subcategories[j].questions[k];
        if (answers[question.id]) {
          for (let l = 0; l < question.answers.length; l++) {
            if (question.answers[l].id == answers[question.id]) {
              const answer: any = question.answers[l];
              if (firstEightResults.length < 8) {
                firstEightResults.push(!!answer.correct);
              }
              if (answer.correct) {
                if (consecutiveIncorrectCount > 0) {
                  consecutiveIncorrectCount = 0;
                  consecutiveCorrectCount = 1;
                } else {
                  consecutiveCorrectCount++;
                }
              } else {
                if (consecutiveCorrectCount > 0) {
                  consecutiveCorrectCount = 0;
                  consecutiveIncorrectCount = 1;
                } else {
                  consecutiveIncorrectCount++;
                }
              }
            }
          }
        }
      }
    }

    const firstEightAllCorrect = firstEightResults.length >= 8 && firstEightResults.every((result) => result);
    const firstEightAllIncorrect = firstEightResults.length >= 8 && firstEightResults.every((result) => !result);

    let flag = "";
    if (firstEightAllCorrect) {
      flag = "Ceiling";
    } else if (firstEightAllIncorrect) {
      flag = "Floor";
    } else if (consecutiveCorrectCount >= 8) {
      flag = "Ceiling";
    } else if (consecutiveIncorrectCount >= 8) {
      flag = "Floor";
    }

    return {
      isFloorCeiling: flag !== "",
      flag,
      firstEightAllCorrect,
      firstEightAllIncorrect
    };
  }

  const screeningSubtestsFor = (season: string, grade: string) => {
    const normalizedGrade = normalizeGrade(grade);
    if (normalizedGrade === "K" && season === "Fall") {
      return ["Deletion", "Blending"];
    }
    if (normalizedGrade === "K" && season === "Winter") {
      return ["Blending", "Word Reading"];
    }
    return ["Word Reading"];
  };

  const generateScores = (tests: any, categories: any, studentInfo: any) => {
    const scores: any = [];
    const allScores: any = [];
    const testsFiltered: any = [];

    const normalizedGrade = normalizeGrade(studentInfo.grade);
    for (let t = 0; t < tests.length; t++) {
      const flaggedSubtests = new Set<string>();

      if (normsFilter !== "All" && normsFilter !== tests[t].season) {
        continue;
      }

      if (testDateFilter !== "All" && testDateKey(tests[t].completed_timestamp) !== testDateFilter) {
        continue;
      }

      let answers: any = {};
      
      for (let i = 0; i < tests[t].answers?.length; i++) {
        answers[tests[t].answers[i].test_schema_question_id] = tests[t].answers[i].test_schema_question_answer_id;
      }

      const subtestThetas: any = {
        nre: 0,
        wre: 0,
        ble: 0,
        dev: 0
      };

      for (let i = 0; i < categories.length; i++) {
        const floorCeilingInfo = getFloorCeilingInfo(categories[i], answers);
        if (floorCeilingInfo.isFloorCeiling) {
          flaggedSubtests.add(categories[i].name);
        }

        let thetas: any = [];
        let likelihoods: any = {};
        let maxTheta = 0;
        let maxThetaLikelihood = -9999;
        let informationSum = 0;
        let standardError = 0;

        for (let j = 0; j < categories[i].subcategories.length; j++) {
          for (let k = 0; k < categories[i].subcategories[j].questions.length; k++) {
            const question: any = categories[i].subcategories[j].questions[k];
            if (question.id in answers && question.discrimination && question.difficulty) {
              for (let l = 0; l < question.answers.length; l++) {
                if (question.answers[l].id == answers[question.id]) {
                  const answer: any = question.answers[l];
                  let probabilities: any = {};
                  for (let theta = -5; theta <= 5; theta += 0.01) {
                    const probability = 1/(1 + Math.exp(-1.7*question.discrimination*(theta-question.difficulty)));
                    if (answer.correct) {
                      probabilities[theta] = probability;
                    } else {
                      probabilities[theta] = 1 - probability;
                    }
                  }
                  thetas.push(probabilities);
                }
              }
            }
          }
        }

        for (let theta = -5; theta <= 5; theta += 0.01) {
          likelihoods[theta] = 1;
          for (let i = 0; i < thetas.length; i++) {
            likelihoods[theta] *= thetas[i][theta];
          }

          if (likelihoods[theta] > maxThetaLikelihood) {
            maxThetaLikelihood = likelihoods[theta];
            maxTheta = theta;
          }
        }

        if (maxTheta > -5) {
          if (normalizedGrade === "2nd" && tests[t].season === "Winter" && categories[i].name === "Word Reading") {
            maxTheta = 1.04885752*maxTheta - 0.3062846;
          } else if (normalizedGrade === "3rd" && tests[t].season === "Spring" && categories[i].name === "Word Reading") {
            maxTheta = 0.97766434*maxTheta + 0.56662549;
          }

          for (let j = 0; j < categories[i].subcategories.length; j++) {
            for (let k = 0; k < categories[i].subcategories[j].questions.length; k++) {
              const question: any = categories[i].subcategories[j].questions[k];
              if (question.id in answers && question.discrimination && question.difficulty) {
                for (let l = 0; l < question.answers.length; l++) {
                  if (question.answers[l].id == answers[question.id]) {
                    const probability = 1/(1 + Math.exp(-1.7*question.discrimination*(maxTheta-question.difficulty)));
                    const information = (Math.pow(1.7*question.discrimination, 2)*(1 - probability)*probability);
                    informationSum += information;
                  }
                }
              }
            }
          }

          standardError = 1/Math.sqrt(informationSum);

          const score = calculateScore(tests[t], categories[i].name, studentInfo, maxTheta);
          const scoreConfidenceIntervalLower = calculateScore(tests[t], categories[i].name, studentInfo, maxTheta - 1.96*standardError);
          const scoreConfidenceIntervalUpper = calculateScore(tests[t], categories[i].name, studentInfo, maxTheta + 1.96*standardError);

          const scoreRow = {
            id: `${tests[t].id}-${categories[i].name}`,
            testId: tests[t].id,
            completed_timestamp: tests[t].completed_timestamp,
            completed_date_key: testDateKey(tests[t].completed_timestamp),
            updated_at: tests[t].updated_at,
            subtest: categories[i].name,
            norms: tests[t].season,
            flag: floorCeilingInfo.isFloorCeiling ? (floorCeilingInfo.flag || "Floor/Ceiling") : "",
            theta: maxTheta,
            scaleScore: floorCeilingInfo.isFloorCeiling ? "N/A" : score.scaleScore,
            scaleScoreCI: floorCeilingInfo.isFloorCeiling ? "N/A" : scoreConfidenceIntervalLower.scaleScore + " - " + scoreConfidenceIntervalUpper.scaleScore,
            standardScore: floorCeilingInfo.isFloorCeiling ? "N/A" : score.standardScore,
            standardScoreCI: floorCeilingInfo.isFloorCeiling ? "N/A" : scoreConfidenceIntervalLower.standardScore + " - " + scoreConfidenceIntervalUpper.standardScore,
            percentileRank: floorCeilingInfo.isFloorCeiling ? "N/A" : score.percentileRank,
            percentileRankCI: floorCeilingInfo.isFloorCeiling ? "N/A" : scoreConfidenceIntervalLower.percentileRank + " - " + scoreConfidenceIntervalUpper.percentileRank
          };

          allScores.push(scoreRow);

          if (subtestFilter === "All" || subtestFilter === categories[i].name) {
            scores.push(scoreRow);
          }

          if (categories[i].name === "Nonword Reading") {
            subtestThetas.nre = maxTheta;
          } else if (categories[i].name === "Word Reading") {
            subtestThetas.wre = maxTheta;
          } else if (categories[i].name === "Blending") {
            subtestThetas.ble = maxTheta;
          } else if (categories[i].name === "Deletion") {
            subtestThetas.del = maxTheta;
          }
        }
      }

      tests[t].dysFlag = 0;
      tests[t].dysCutoff = 0;
      tests[t].dysInterpretation = 0;
      tests[t].pwrrFlag = 0;
      tests[t].pwrrCutoff = 0;
      tests[t].pwrrInterpretation = 0;
      tests[t].finalClassification = 0;

      if (normalizedGrade === "K") {
        if (tests[t].season === "Fall") {
          tests[t].pwrrFlag = -1.3749 + -0.828*subtestThetas.del + -0.5991*subtestThetas.ble;
          tests[t].pwrrCutoff = -0.478;
          tests[t].dysFlag = -2.9864 + -0.8359*subtestThetas.del + -0.6229*subtestThetas.ble;
          tests[t].dysCutoff = -1.65;
        } else if (tests[t].season === "Winter") {
          tests[t].pwrrFlag = -1.5141 + -0.623*subtestThetas.ble + -1.2998*subtestThetas.wre;
          tests[t].pwrrCutoff = -0.439739;
          tests[t].dysFlag = -3.0957 + -0.4999*subtestThetas.ble + -1.1774*subtestThetas.wre;
          tests[t].dysCutoff = -1.600208;
        } else if (tests[t].season === "Spring") {
          tests[t].pwrrFlag = -2.242 + -2.2315*subtestThetas.wre;
          tests[t].pwrrCutoff = -0.4790195;
          tests[t].dysFlag = -4.4823 + -2.3147*subtestThetas.wre;
          tests[t].dysCutoff = -1.7153412;
        }
      } else if (normalizedGrade === "1st") {
        if (tests[t].season === "Fall") {
          tests[t].pwrrFlag = -1.7147 + -3.1426*subtestThetas.wre;
          tests[t].pwrrCutoff = -0.6280326;
          tests[t].dysFlag = -4.3025 + -3.6139*subtestThetas.wre;
          tests[t].dysCutoff = -1.5756004;
        } else if (tests[t].season === "Winter") {
          tests[t].pwrrFlag = 0.07712 + -4.07885*subtestThetas.wre;
          tests[t].pwrrCutoff = -0.5154523;
          tests[t].dysFlag = -2.4722 + -3.5014*subtestThetas.wre;
          tests[t].dysCutoff = -1.9645652;
        } else if (tests[t].season === "Spring") {
          tests[t].pwrrFlag = 0.7128 + -4.8007*subtestThetas.wre;
          tests[t].pwrrCutoff = -0.4955694;
          tests[t].dysFlag = -2.347 + -2.8805*subtestThetas.wre;
          tests[t].dysCutoff = -1.9294146;
        }
      } else if (normalizedGrade === "2nd") {
        if (tests[t].season === "Fall") {
          tests[t].pwrrFlag = 1.252 + -2.6942*subtestThetas.wre;
          tests[t].pwrrCutoff = -0.4287528;
          tests[t].dysFlag = -0.66558 + -3.1316*subtestThetas.wre;
          tests[t].dysCutoff = -1.7863947;
        } else if (tests[t].season === "Winter") {
          tests[t].pwrrFlag = 2.0116 + -3.9969*subtestThetas.wre;
          tests[t].pwrrCutoff = -0.4559537;
          tests[t].dysFlag = -0.2782 + -4.2165*subtestThetas.wre;
          tests[t].dysCutoff = -1.48187645;
        } else if (tests[t].season === "Spring") {
          tests[t].pwrrFlag = 3.3815 + -4.6224*subtestThetas.wre;
          tests[t].pwrrCutoff = -0.111554;
          tests[t].dysFlag = 0.5267 + -3.9798*subtestThetas.wre;
          tests[t].dysCutoff = -1.6117772;
        }
      } else if (normalizedGrade === "3rd") {
        if (tests[t].season === "Fall") {
          tests[t].pwrrFlag = 2.4348 + -2.8995*subtestThetas.wre;
          tests[t].pwrrCutoff = -0.2824386;
          tests[t].dysFlag = 0.7151 + -3.0383*subtestThetas.wre;
          tests[t].dysCutoff = -1.7789737;
        } else if (tests[t].season === "Winter") {
          tests[t].pwrrFlag = 4.468 + -3.3563*subtestThetas.wre;
          tests[t].pwrrCutoff = -0.2210173;
          tests[t].dysFlag = 2.5387 + -3.1855*subtestThetas.wre;
          tests[t].dysCutoff = -1.5379891;
        } else if (tests[t].season === "Spring") {
          tests[t].pwrrFlag = 9.2099 + -5.0149*subtestThetas.wre;
          tests[t].pwrrCutoff = 0.008115222;
          tests[t].dysFlag = 5.7115 + -4.4963*subtestThetas.wre;
          tests[t].dysCutoff = -1.61687139;
        }
      }

      tests[t].dysInterpretation = (tests[t].dysFlag > tests[t].dysCutoff) ? "True" : "False";
      tests[t].pwrrInterpretation = (tests[t].pwrrFlag > tests[t].pwrrCutoff) ? "True" : "False";

      const isKindergartenFallWinterEdgeCase =
        normalizedGrade === "K" &&
        (tests[t].season === "Fall" || tests[t].season === "Winter") &&
        tests[t].dysInterpretation === "True" &&
        tests[t].pwrrInterpretation === "False";

      // Yaacov noted a Fall/Winter K edge case where DYS can be true while
      // PWRR is false. That combination should default to High Risk.
      if (tests[t].dysInterpretation === "True" && tests[t].pwrrInterpretation === "True") {
        tests[t].finalClassification = "High Risk";
      } else if (isKindergartenFallWinterEdgeCase) {
        tests[t].finalClassification = "High Risk";
      } else if (tests[t].dysInterpretation === "False" && tests[t].pwrrInterpretation === "False") {
        tests[t].finalClassification = "Low Risk";
      } else {
        tests[t].finalClassification = "Moderate Risk";
      }

      const screeningSubtests = screeningSubtestsFor(tests[t].season, studentInfo.grade);
      const hasScreeningFloorCeiling = screeningSubtests.some((subtest) => flaggedSubtests.has(subtest));
      if (hasScreeningFloorCeiling) {
        tests[t].finalClassification = "N/A";
      }

      testsFiltered.push(tests[t]);
    }

    setAllScoreRows(allScores);
    setScores(scores);
    setTests(testsFiltered);
  }

  function availableTestDates() {
    const dateMap = new Map<string, string>();
    for (let i = 0; i < allTests.length; i++) {
      if (normsFilter !== "All" && normsFilter !== allTests[i].season) {
        continue;
      }

      const key = testDateKey(allTests[i].completed_timestamp);
      if (!key) {
        continue;
      }

      if (!dateMap.has(key)) {
        dateMap.set(key, formatDateLabel(allTests[i].completed_timestamp));
      }
    }

    return Array.from(dateMap.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }

  const calculateScore = (test: any, subtest: any, studentInfo: any, maxTheta: any) => {
    let scaleScore = 0;
    let standardScore = 0;
    let percentileRank = 0;

    const normalizedGrade = normalizeGrade(studentInfo.grade);
    let gradeCode = "";
    if (normalizedGrade === "K") {
      gradeCode = "KG";
    } else if (normalizedGrade === "1st") {
      gradeCode = "G1";
    } else if (normalizedGrade === "2nd") {
      gradeCode = "G2";
    } else if (normalizedGrade === "3rd") {
      gradeCode = "G3";
    }

    let scoringData: any = [];
    let scoringCode = "";
    if (subtest === "Nonword Reading") {
      scoringData = nre;
      scoringCode = "nre";
    } else if (subtest === "Word Reading") {
      scoringData = wre;
      scoringCode = "wre";
    } else if (subtest === "Blending") {
      scoringData = ble;
      scoringCode = "ble";
    } else if (subtest === "Deletion") {
      scoringData = del;
      scoringCode = "del";
    }

    if (scoringData.length > 0) {
      let closestIndex = 0;
      let thetaDiff = 9999999;

      for (let j = 0; j < scoringData.length; j++) {
        if (Math.abs(maxTheta - scoringData[j][gradeCode + ' ' + test.season + '_theta_' + scoringCode]) < thetaDiff) {
          thetaDiff = Math.abs(maxTheta - scoringData[j][gradeCode + ' ' + test.season + '_theta_' + scoringCode]);
          closestIndex = j;
        }
      }
      scaleScore = scoringData[closestIndex][gradeCode + ' ' + test.season + '_dss_' + scoringCode];
      standardScore = scoringData[closestIndex][gradeCode + ' ' + test.season + '_ss_' + scoringCode];
      percentileRank = scoringData[closestIndex][gradeCode + ' ' + test.season + '_pr_1_99_' + scoringCode];
    }

    return {
      scaleScore,
      standardScore,
      percentileRank
    }
  }

  const testDateOptions = availableTestDates();
  const selectedTestDateLabel = testDateFilter === "All"
    ? "All"
    : (testDateOptions.find(([key]) => key === testDateFilter)?.[1] || testDateFilter);

  const progressSubtestOptions: string[] = Array.from(
    new Set(
      allScoreRows
        .filter((row: any) => isNumericScoreValue(row.scaleScore) && isNumericScoreValue(row.percentileRank))
        .map((row: any) => row.subtest)
    )
  ).sort() as string[];

  useEffect(() => {
    if (progressSubtestOptions.length === 0) {
      setProgressSubtest("");
      setProgressStartDate("");
      setProgressEndDate("");
      return;
    }

    if (!progressSubtest || !progressSubtestOptions.includes(progressSubtest)) {
      setProgressSubtest(progressSubtestOptions[0]);
    }

    const datedRows = allScoreRows
      .filter((row: any) => isNumericScoreValue(row.scaleScore) && isNumericScoreValue(row.percentileRank) && row.completed_date_key)
      .sort((a: any, b: any) => a.completed_date_key.localeCompare(b.completed_date_key));

    if (datedRows.length === 0) {
      setProgressStartDate("");
      setProgressEndDate("");
      return;
    }

    const minDate = datedRows[0].completed_date_key;
    const maxDate = datedRows[datedRows.length - 1].completed_date_key;

    setProgressStartDate((current: string) => current && current >= minDate && current <= maxDate ? current : minDate);
    setProgressEndDate((current: string) => current && current >= minDate && current <= maxDate ? current : maxDate);
  }, [allScoreRows, progressSubtest, progressSubtestOptions]);

  const progressRows = allScoreRows
    .filter((row: any) => row.subtest === progressSubtest)
    .filter((row: any) => isNumericScoreValue(row.scaleScore) && isNumericScoreValue(row.percentileRank))
    .filter((row: any) => !progressStartDate || row.completed_date_key >= progressStartDate)
    .filter((row: any) => !progressEndDate || row.completed_date_key <= progressEndDate)
    .sort((a: any, b: any) => {
      const dateCompare = a.completed_date_key.localeCompare(b.completed_date_key);
      if (dateCompare !== 0) {
        return dateCompare;
      }
      return a.testId - b.testId;
    });

  const progressChartOptions = {
    chart: {
      zoomType: 'x',
      height: 460
    },
    title: {
      text: progressSubtest ? `Progress Over Time - ${progressSubtest}` : 'Progress Over Time'
    },
    credits: {
      enabled: false
    },
    xAxis: {
      categories: progressRows.map((row: any) => formatDateLabel(row.completed_timestamp)),
      crosshair: true
    },
    yAxis: [
      {
        title: {
          text: 'Scale Score'
        },
        min: 200,
        max: 1000
      },
      {
        title: {
          text: 'Percentile Rank'
        },
        min: 0,
        max: 100,
        opposite: true
      }
    ],
    legend: {
      align: 'center',
      verticalAlign: 'bottom'
    },
    tooltip: {
      shared: true
    },
    plotOptions: {
      column: {
        borderWidth: 1,
        pointPadding: 0.15,
        groupPadding: 0.2
      },
      spline: {
        lineWidth: 3,
        marker: {
          radius: 4,
          fillColor: '#111111',
          lineColor: '#111111',
          lineWidth: 1
        }
      },
      series: {
        animation: false,
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            textOutline: 'none'
          }
        }
      }
    },
    series: [
      {
        type: 'column',
        name: 'Percentile Rank',
        yAxis: 1,
        data: progressRows.map((row: any) => Number(row.percentileRank)),
        color: '#c8d7ea',
        borderColor: '#6f93c5'
      },
      {
        type: 'spline',
        name: 'Scale Score',
        yAxis: 0,
        data: progressRows.map((row: any) => Number(row.scaleScore)),
        color: '#111111',
        zIndex: 3
      }
    ]
  };

  const screeningSelectedTest = tests
    .slice()
    .sort((a: any, b: any) => new Date(b.completed_timestamp).getTime() - new Date(a.completed_timestamp).getTime())[0];

  const screeningGrade = normalizeGrade(screeningSelectedTest?.student?.grade || studentInfo?.grade);
  const screeningConfig = screeningSelectedTest
    ? SCREENING_RISK_CUTPOINTS[`${screeningGrade}|${screeningSelectedTest.season}`]
    : null;

  const screeningScoreRow = screeningSelectedTest
    ? allScoreRows.find((row: any) =>
        row.testId?.toString() === screeningSelectedTest.id?.toString() &&
        row.subtest === "Word Reading" &&
        isNumericScoreValue(row.scaleScore)
      )
    : null;

  const screeningScaleScore = screeningScoreRow && isNumericScoreValue(screeningScoreRow.scaleScore)
    ? Number(screeningScoreRow.scaleScore)
    : null;

  const screeningShouldRender = !!screeningSelectedTest && !!screeningConfig && screeningScaleScore !== null;

  const screeningAxisMin = screeningShouldRender && screeningScaleScore! >= screeningConfig!.lowerBound && screeningScaleScore! <= screeningConfig!.upperBound
    ? screeningConfig!.lowerBound
    : 200;
  const screeningAxisMax = screeningShouldRender && screeningScaleScore! >= screeningConfig!.lowerBound && screeningScaleScore! <= screeningConfig!.upperBound
    ? screeningConfig!.upperBound
    : 1000;

  const screeningRiskCategory = !screeningShouldRender
    ? null
    : (screeningScaleScore! < screeningConfig!.highRiskCutPoint
      ? 'High'
      : screeningScaleScore! < screeningConfig!.moderateRiskCutPoint
        ? 'Moderate'
        : 'Low');

  const screeningRiskContent = screeningRiskCategory ? SCREENING_RISK_CONTENT[screeningRiskCategory] : null;

  const screeningChartOptions = !screeningShouldRender ? null : {
    chart: {
      height: 320,
      spacingTop: 14,
      spacingRight: 22,
      spacingLeft: 22,
      spacingBottom: 18,
      backgroundColor: '#ffffff'
    },
    title: {
      text: `Test Date ${formatDateLabel(screeningSelectedTest.completed_timestamp)}`,
      align: 'left',
      style: {
        fontSize: '20px',
        fontWeight: '700'
      }
    },
    credits: {
      enabled: false
    },
    legend: {
      enabled: false
    },
    xAxis: {
      min: screeningAxisMin,
      max: screeningAxisMax,
      tickPositions: [screeningAxisMin, screeningAxisMax],
      title: {
        text: 'Scale Score',
        style: {
          fontSize: '15px',
          color: '#666666'
        }
      },
      labels: {
        style: {
          fontSize: '15px',
          color: '#333333',
          fontWeight: '700'
        }
      },
      lineWidth: 0,
      gridLineWidth: 0,
      tickLength: 0,
      plotLines: [
        {
          value: screeningAxisMin,
          color: '#333333',
          width: 3,
          zIndex: 5
        },
        {
          value: screeningAxisMax,
          color: '#333333',
          width: 3,
          zIndex: 5
        }
      ]
    },
    yAxis: {
      min: -0.9,
      max: 0.9,
      visible: false,
      title: {
        text: ''
      }
    },
    tooltip: {
      enabled: false
    },
    plotOptions: {
      xrange: {
        borderWidth: 0,
        pointPadding: 0,
        groupPadding: 0,
        dataLabels: {
          enabled: false
        }
      }
    },
    series: [
      {
        type: 'xrange',
        name: 'Risk Bands',
        pointWidth: 32,
        enableMouseTracking: false,
        data: [
          {
            x: screeningAxisMin,
            x2: screeningConfig!.highRiskCutPoint,
            y: 0,
            color: SCREENING_RISK_CONTENT.High.graphColor
          },
          {
            x: screeningConfig!.highRiskCutPoint,
            x2: screeningConfig!.moderateRiskCutPoint,
            y: 0,
            color: SCREENING_RISK_CONTENT.Moderate.graphColor
          },
          {
            x: screeningConfig!.moderateRiskCutPoint,
            x2: screeningAxisMax,
            y: 0,
            color: SCREENING_RISK_CONTENT.Low.graphColor
          }
        ]
      },
      {
        type: 'line',
        name: 'Student Score Line',
        color: '#333333',
        lineWidth: 2,
        enableMouseTracking: false,
        marker: {
          enabled: false
        },
        data: [
          {
            x: screeningScaleScore!,
            y: 0.72
          },
          {
            x: screeningScaleScore!,
            y: 0.18
          }
        ]
      },
      {
        type: 'scatter',
        name: 'Student Score',
        color: '#333333',
        enableMouseTracking: false,
        marker: {
          symbol: 'triangle-down',
          radius: 7
        },
        dataLabels: {
          enabled: true,
          y: -56,
          style: {
            fontSize: '18px',
            fontWeight: '700',
            color: '#333333',
            textOutline: 'none'
          }
        },
        data: [
          {
            x: screeningScaleScore!,
            y: 0.16,
            dataLabels: {
              format: `${screeningScaleScore}`
            }
          }
        ]
      }
    ]
  };

  return (
    <Container fluid className="ventris-content-wrapper">
      <Header portal={sessionStorage.getItem("portal")!} page="results" />
      <Container as="main" id="main-content" tabIndex={-1}>
        <Row>
          <Col>
            <h1 className="page-header-no-margin align-items-center">
              <ReportPageIcon />
              <div className="flex-grow">Test Results</div>
              <Button variant="primary" className="ventris-button-primary ventris-button-primary-teal hidden" onClick={() => {}}><DownloadIcon /> Export Results</Button>
            </h1>
            <Button variant="link" size="sm" className="back-link" onClick={() => back()}>&#x276E; Back</Button>
            <h3>{terms.student} Info</h3>
            <br />
            <ResponsiveTableRegion label={`${terms.student} information`} className="ventris-students">
              <div className="react-bootstrap-table react-bootstrap-table-rounded">
                <table className="table tabled-bordered">
                  <thead>
                    <tr>
                      <th></th>
                      <th>{terms.student} Name</th>
                      <th>{terms.student} ID</th>
                      <th>Grade</th>
                      <th>Birthdate</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td></td>
                      <td>{studentInfo?.first_name} {studentInfo?.last_name}</td>
                      <td>{studentInfo?.student_code}</td>
                      <td>{studentInfo?.grade}</td>
                      <td>{studentInfo?.birthdate}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ResponsiveTableRegion>
            <br />
            <div className="ventris-reports">
              <div className="flex flex-row">
                <h3 className="flex-grow">Screening Results</h3>
              </div>
              {screeningShouldRender ? (
                <div className="screening-risk-section">
                  <div className="screening-risk-chart">
                    <AccessibleChart
                      options={screeningChartOptions!}
                      title="Screening result"
                      description={`Scale score ${screeningScaleScore}, classified as ${screeningRiskCategory} risk, for the test completed ${formatDateLabel(screeningSelectedTest.completed_timestamp)}. Risk band boundaries are included in the data table.`}
                      data={{
                        columns: ['Test date', 'Scale score', 'Risk category', 'High risk range', 'Moderate risk range', 'Low risk range'],
                        rows: [[
                          formatDateLabel(screeningSelectedTest.completed_timestamp),
                          screeningScaleScore,
                          `${screeningRiskCategory} risk`,
                          `${screeningAxisMin} to ${screeningConfig!.highRiskCutPoint}`,
                          `${screeningConfig!.highRiskCutPoint} to ${screeningConfig!.moderateRiskCutPoint}`,
                          `${screeningConfig!.moderateRiskCutPoint} to ${screeningAxisMax}`
                        ]]
                      }}
                    />
                  </div>
                  <div className="screening-risk-legend">
                    <div className="screening-risk-legend-item">
                      <span className="screening-risk-legend-swatch screening-risk-legend-swatch-high" />
                      <span>High risk</span>
                    </div>
                    <div className="screening-risk-legend-item">
                      <span className="screening-risk-legend-swatch screening-risk-legend-swatch-moderate" />
                      <span>Moderate risk</span>
                    </div>
                    <div className="screening-risk-legend-item">
                      <span className="screening-risk-legend-swatch screening-risk-legend-swatch-low" />
                      <span>Low risk</span>
                    </div>
                  </div>
                  <div className="screening-risk-summary">
                    <div
                      className="screening-risk-summary-category"
                      style={{
                        backgroundColor: screeningRiskContent?.panelColor,
                        color: screeningRiskContent?.textColor
                      }}
                    >
                      <div className="screening-risk-summary-title">{screeningRiskContent?.title}</div>
                      <div className="screening-risk-summary-description">{screeningRiskContent?.description}</div>
                    </div>
                    <div className="screening-risk-summary-meaning">
                      <div className="screening-risk-summary-meaning-title">What this means:</div>
                      <div className="screening-risk-summary-meaning-text">{screeningRiskContent?.meaning}</div>
                    </div>
                  </div>
                </div>
              ) : null}
              <ResponsiveTableRegion label="Tests">
                <BootstrapTable keyField='id' data={tests} columns={testColumns} bordered={true} remote={{pagination: true}} pagination={paginationFactory({ page, sizePerPage, totalSize })} onTableChange={handleTableChange} wrapperClasses="react-bootstrap-table-rounded" />
              </ResponsiveTableRegion>
            </div>
            <div className="ventris-reports">
              <div className="flex flex-row">
                <h3 className="flex-grow">Test Results</h3>
                <div className="ventris-action-links">
                  <Button variant="link" size="sm" className="ventris-action-link" onClick={() => setShowFilters(!showFilters)}><FilterIcon /> {showFilters ? 'Hide Filter' : 'Show Filter'}</Button>
                </div>
              </div>
              <div className={showFilters ? '' : 'hidden'}>
                <Filters updateFilters={updateFilters} hideFilters={['Organization', 'Clinician', 'Student', 'Test', 'Completion Status']} />
                <div className="filters">
                  <Container fluid>
                    <Row>
                      <Col className="filter-wrapper">
                        <Dropdown className="filter-dropdown">
                          <Dropdown.Toggle variant="light" id="dropdown-test-date">
                            <span className="filter-value">Test Date: {selectedTestDateLabel}</span>
                          </Dropdown.Toggle>
                          <Dropdown.Menu style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'scroll' }}>
                            <Dropdown.Item onClick={() => { setTestDateFilter("All"); setPage(1); }}>All</Dropdown.Item>
                            {testDateOptions.map(([key, label]) =>
                              <Dropdown.Item key={key} onClick={() => { setTestDateFilter(key); setPage(1); }}>{label}</Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      </Col>
                    </Row>
                  </Container>
                </div>
              </div>
              <br />
              <ResponsiveTableRegion label="Test results">
                <BootstrapTable keyField='id' data={scores} columns={scoreColumns} bordered={true} remote={{pagination: true}} pagination={paginationFactory({ page, sizePerPage, totalSize })} onTableChange={handleTableChange} wrapperClasses="react-bootstrap-table-rounded" />
              </ResponsiveTableRegion>
            </div>
            <div className="ventris-reports">
              <div className="flex flex-row">
                <h3 className="flex-grow">Progress Monitoring</h3>
              </div>
              <div className="report-progress-controls">
                <Row>
                  <Col lg={4} md={12}>
                    <Form.Group className="ventris-form-element" controlId="progressSubtest">
                      <Form.Label>Subtest</Form.Label>
                      <Form.Select
                        value={progressSubtest}
                        onChange={(event) => setProgressSubtest(event.target.value)}
                        disabled={progressSubtestOptions.length === 0}
                      >
                        {progressSubtestOptions.length === 0 ? (
                          <option value="">No subtests available</option>
                        ) : null}
                        {progressSubtestOptions.map((subtestName: string) => (
                          <option key={subtestName} value={subtestName}>{subtestName}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg={4} md={6}>
                    <Form.Group className="ventris-form-element" controlId="progressStartDate">
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={progressStartDate}
                        max={progressEndDate || undefined}
                        onChange={(event) => setProgressStartDate(event.target.value)}
                        disabled={progressSubtestOptions.length === 0}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={4} md={6}>
                    <Form.Group className="ventris-form-element" controlId="progressEndDate">
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={progressEndDate}
                        min={progressStartDate || undefined}
                        onChange={(event) => setProgressEndDate(event.target.value)}
                        disabled={progressSubtestOptions.length === 0}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
              {progressRows.length > 0 ? (
                <div className="report-progress-chart">
                  <AccessibleChart
                    options={progressChartOptions}
                    title={`Progress over time${progressSubtest ? ` for ${progressSubtest}` : ''}`}
                    description="Scale score and national percentile rank for each completed test date. The two measures use different chart shapes as well as different colors."
                  />
                </div>
              ) : (
                <div className="report-progress-empty">
                  No progress monitoring scores are available for the selected subtest and date range.
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

export default ClinicianReport;
